import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, memo, Fragment, Component, createContext, useContext } from "react";
// createPortal: SearchableSelect's panel is rendered into <body> rather than
// next to its trigger, so no ancestor's overflow can clip it. See the panel
// block in SearchableSelect for the full reasoning.
import { createPortal } from "react-dom";

import {
  Search, Bell, Plus, Phone, Building, Users, BarChart3,
  Settings, Home, Briefcase, Target, TrendingUp, UserPlus, CheckCircle,
  Activity, Layers, DollarSign, X, Lock, Globe, LogOut, Eye, EyeOff, User, Mail,
  Trash2, AlertCircle, Menu, Upload, MessageSquare, ChevronRight, ChevronDown,
  ClipboardList, Edit, Archive, Award, Zap, RotateCcw, ExternalLink, KeyRound, FileSpreadsheet, MapPin,
  // Navigation, not a second MapPin: BELL 6 (pending DR locations) already owns
  // an amber MapPin in this header, and the bell removed in 2026-08-26 used a
  // blue one — two pins of different colours meaning different queues is how a
  // header stops being readable at a glance. See OffSiteBell.
  Navigation,
  Monitor, Smartphone, Shield, Clock, RefreshCw,
  // Page-identity icons for the shared header (PAGE_ICONS).
  LayoutGrid, FileText, Calendar, Handshake, Package, Wallet
} from "lucide-react";

// QR code generation for AssetTracker. Static import is fine — ~10 kB
// gzipped and used on every asset detail view. The heavier scanner library
// (html5-qrcode, ~50 kB gz) is dynamic-imported inside the scan sub-view.
import QRCode from "qrcode";

// react-virtuoso — virtualization library used for the LeadsPage and
// DailyRequestsPage tables/cards so only the visible viewport of rows mounts
// in the DOM. TableVirtuoso handles the desktop <table> branches (L1, L3,
// DR2 — landed in STEP 2a/2b). Virtuoso handles the mobile card branches
// (L2 LeadsPage cards, DR1 DailyRequestsPage cards — STEP 2b). All call
// sites use useWindowScroll so the page-level scroll bar is the single
// scroll surface (no double scrollbars). ~25 KB gz total one-time cost.
import { TableVirtuoso, Virtuoso } from "react-virtuoso";
import { initPushNotifications, disposePushNotifications, removeTokenFromBackend, deleteDeviceToken } from "./utils/pushNotifications";
import { isNativePlatformSync, runBiometricAuth, isBiometricAvailable } from "./utils/biometric";
import { getCurrentPosition as geoGetCurrentPosition } from "./utils/geolocation";
// PRINTING FROM THE APPS — all four print surfaces. window.print does not exist
// in either WebView, so every one of those buttons did nothing there; the app
// path rasterises the very node the website prints and hands the PDF to the
// native share sheet. `pdfShareSupport` owns the four-way branch and
// `captureAndShare` owns the one delivery path, both at MODULE SCOPE out there
// so verify-broker-print.js can execute them. See that file's header for why
// this needs a store release: the WebView loads the live site, but a native
// PLUGIN only exists in a new binary.
import { pdfShareSupport, captureAndShare, pdfFilename, PRINT_UNSUPPORTED_MSG } from "./utils/printPdf";
import { TxnDocThumb, DocViewer } from "./components/TxnDocThumb";
import { MobileFab, FilterGrid, ToggleRow, ActiveFilterStrip, ChipRows, MobileSelectBar, MobileSelectBox } from "./components/MobileFilterChrome";
import DashRangePicker from "./components/DashRangePicker";
import DeletionBell from "./components/DeletionBell";
import { periodChipStyle, periodSelectStyle, periodAccentOf, PERIOD_ACCENT_BLUE } from "./lib/periodChip";
import { attendanceDateChip, reportsDateChip, meetingsReportDateChip, assetsHistoryDateChip } from "./lib/dateChipSites";
import { meetingSourceLabel } from "./lib/meetingSource";
import { sourceColor, rowSourceMix, sourceLegend, otherCampaignsRow, campaignDrillSpec } from "./lib/campaignSources";
import { leadSourceNames, literalSourceNames, setLeadSources, useLeadSourcesVersion } from "./lib/leadSources";
import { saveReportReturn, peekReportReturn, clearReportReturn } from "./lib/reportReturn";
import { ReportsCampaignPerformanceSwitch } from "./pages/ReportsCampaignStrength";
import { tileListUrl, dealCountsForTile, callbackSplit, drTileQS } from "./lib/tileClickthrough";
import { lockReplacesTree, lockedShellProps, bioBusyActive } from "./lib/lockShell";
import { callbackStripModel, CallbackStrip, RescheduleSheet, useMinuteTick, CB_COL_PIN } from "./lib/callbackStrip";
import { OriginBlock } from "./lib/txnOrigin";
import { useLiveRev, useLiveFamily } from "./lib/liveFamily";
import { txnLiveUrl, mergeTxnRowsByLead } from "./lib/txnLiveMerge";
import { dealLifecycleActions, canHardDeleteRow, TxnDeleteModal } from "./lib/txnHardDelete";
import { fmtTime12, fmtDateTime12, fmtWallDateTime12, excelWallDate, excelFormatColumn, EXCEL_DATETIME_FMT } from "./lib/timeFormat";
import CommissionCalculatorCard from "./components/CommissionCalculatorCard";
import { CALC_EXEMPT_DEFAULT } from "./lib/commissionCalculator";
import { tierRowStats } from "./lib/rotationRowStats";
import { useRememberedTab } from "./lib/tabMemory";
import { sourceNameStyle, txnChipLabel, eoiTabText, PendingApprovalPill, CancelledDot, StackedCell, dealSourceOptions, APPROVED_COL_PIN, STACKED_ELLIPSIS_STYLE, ProjectWeightsIconBtn, txnTabStyle, txnTotalPillStyle, TXN_HEADER_BTN_STYLE, TXN_ADD_BTN_STYLE } from "./lib/txnTableChrome";
import { SegmentedControl, SegmentedSelect } from "./components/SegmentedControl";
import { canonicalProjectOptions } from "./lib/projectOptions";
import { canonicalProjectName } from "./lib/projectAliases";
import { dealDateScopeOk } from "./lib/txnPeriodScope";

/* ========== CRM ARO v7 — Complete Edition ========== */

const API = process.env.REACT_APP_API_URL || "https://crm-aro-backend-production.up.railway.app";

// ===== SESSION STORAGE (Phase 2) =====
// The JWT session blob lives in localStorage (Remember me / native) OR in
// sessionStorage (web, unchecked → cleared when the tab/browser closes). Every
// reader/clearer must consult BOTH so a session in either store is honored and
// fully cleared on logout / 401.
function sessionStoreWrite(obj, persistent) {
  var raw;
  try { raw = JSON.stringify(obj); } catch(e) { return; }
  try {
    if (persistent) {
      localStorage.setItem('crm_aro_session', raw);
      try { sessionStorage.removeItem('crm_aro_session'); } catch(e){}
    } else {
      sessionStorage.setItem('crm_aro_session', raw);
      try { localStorage.removeItem('crm_aro_session'); } catch(e){}
    }
  } catch(e){}
}
function sessionStoreRead() {
  try {
    var raw = (typeof window !== "undefined" && window.localStorage) ? localStorage.getItem('crm_aro_session') : null;
    if (!raw && typeof window !== "undefined" && window.sessionStorage) raw = sessionStorage.getItem('crm_aro_session');
    if (raw) { var s = JSON.parse(raw); if (s && s.user && s.token) return s; }
  } catch(e){}
  return null;
}
function sessionStoreClear() {
  try { localStorage.removeItem('crm_aro_session'); } catch(e){}
  try { sessionStorage.removeItem('crm_aro_session'); } catch(e){}
}
// Patch the stored user IN PLACE, in whichever store already holds the session.
// Used by the boot identity refresh (P6): the session blob is read synchronously
// at first paint, so writing the fresh role/title/titleRank back is what makes
// the NEXT first paint correct rather than replaying the same stale frame.
// Deliberately store-preserving — re-writing through sessionStoreWrite() with a
// guessed `persistent` flag would silently promote a tab-scoped session to a
// durable one (or demote a "Remember me" session), which is a security-visible
// change nobody asked for.
function sessionStorePatchUser(patch) {
  try {
    var store = null, raw = null;
    if (typeof window !== "undefined" && window.localStorage) {
      raw = localStorage.getItem('crm_aro_session');
      if (raw) store = localStorage;
    }
    if (!raw && typeof window !== "undefined" && window.sessionStorage) {
      raw = sessionStorage.getItem('crm_aro_session');
      if (raw) store = sessionStorage;
    }
    if (!raw || !store) return;
    var s = JSON.parse(raw);
    if (!s || !s.user) return;
    s.user = Object.assign({}, s.user, patch || {});
    store.setItem('crm_aro_session', JSON.stringify(s));
  } catch(e){}
}
// The ACCESS ENVELOPE, stored beside the user in the same blob and read back
// synchronously at first paint — same reason the user is. The envelope decides
// which nav entries and tabs render; restoring it a round-trip late would paint
// one frame of a stripped-down UI to somebody who has full access, and (from
// Phase 3) one frame with the wrong pages in the sidebar.
//
// Store-preserving, exactly like sessionStorePatchUser: rewriting through
// sessionStoreWrite() with a guessed `persistent` flag would silently promote a
// tab-scoped session to a durable one.
function sessionStoreWriteAccess(access) {
  try {
    var store = null, raw = null;
    if (typeof window !== "undefined" && window.localStorage) {
      raw = localStorage.getItem('crm_aro_session');
      if (raw) store = localStorage;
    }
    if (!raw && typeof window !== "undefined" && window.sessionStorage) {
      raw = sessionStorage.getItem('crm_aro_session');
      if (raw) store = sessionStorage;
    }
    if (!raw || !store) return;
    var s = JSON.parse(raw);
    if (!s) return;
    s.access = access || null;
    store.setItem('crm_aro_session', JSON.stringify(s));
  } catch(e){}
}
// Stable per-install device id — SAME key + UUID pattern as pushNotifications.js
// (get-or-create) so login always sends a non-empty, consistent deviceId.
function getOrCreateDeviceId() {
  try {
    var id = localStorage.getItem("crm_device_id");
    if (!id) {
      id = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : ("dev-" + Math.random().toString(36).slice(2) + Date.now().toString(36));
      localStorage.setItem("crm_device_id", id);
    }
    return id;
  } catch(e) { return ""; }
}
// Capacitor platform ("android"/"ios"/"web"); "web" on the browser build.
function getClientPlatform() {
  try {
    if (typeof window !== "undefined" && window.Capacitor && typeof window.Capacitor.getPlatform === "function") {
      return String(window.Capacitor.getPlatform() || "web").toLowerCase();
    }
  } catch(e){}
  return "web";
}
// Extract the session-id (sid) claim from a JWT payload (base64url) WITHOUT
// verifying — used only to tell the backend which session to revoke on logout.
function sidFromToken(tok) {
  try {
    var part = String(tok).split(".")[1];
    if (!part) return null;
    var payload = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return (payload && payload.sid) ? payload.sid : null;
  } catch(e) { return null; }
}

async function apiFetch(path, method, body, token, csrfToken) {
  var opts = { method: method || "GET", headers: { "Content-Type": "application/json" } };
  if (token) opts.headers["Authorization"] = "Bearer " + token;
  if (csrfToken && (method === "POST" || method === "PUT" || method === "DELETE")) {
    opts.headers["X-CSRF-Token"] = csrfToken;
  }
  if (body) opts.body = JSON.stringify(body);
  // 30s hard timeout via AbortController. Without it a hung backend leaves
  // the App-level "Loading..." spinner stuck indefinitely (loadData awaits
  // Promise.all of 5 fetches with no other escape hatch). On timeout we
  // throw the same shape as a network error so callers' error paths fire.
  var controller = (typeof AbortController !== "undefined") ? new AbortController() : null;
  if (controller) opts.signal = controller.signal;
  var timer = controller ? setTimeout(function(){ try { controller.abort(); } catch(e){} }, 30000) : null;
  var res;
  try { res = await fetch(API + path, opts); }
  catch(netErr) {
    if (timer) clearTimeout(timer);
    if (netErr && netErr.name === "AbortError") throw new Error("Request timed out");
    throw new Error("Connection error");
  }
  if (timer) clearTimeout(timer);
  var data;
  try { data = await res.json(); } catch(e) { data = {}; }
  if (res.status === 401) {
    sessionStoreClear();
    // Forced logout (owner ruling 2026-11-03): the bearer is dead, so the server
    // cannot be asked to drop this device's push token — delete it ON the device
    // instead (FirebaseMessaging.deleteToken), bounded so the reload never waits
    // on a hung bridge. The server side already refuses delivery to a token
    // whose session is not live; this removes the token itself as well.
    try { await Promise.race([deleteDeviceToken(), new Promise(function(r){ setTimeout(r, 1500); })]); } catch(e){}
    if (data && data.code === "deactivated") {
      try { alert("Your account has been deactivated. Please contact admin."); } catch(e){}
    }
    window.location.reload();
    return;
  }
  // Prefer the server's human-readable `message` over the machine `error` slug.
  // Most 400s carry both (`{error:"primary_sales_recipient_required", message:"يجب تعيين Agent..."}`);
  // callers universally surface this via `alert(e.message)`, so throwing the
  // slug showed users raw snake_case. Falls back to `error` for the ~1000
  // responses that ship a code only, so no existing message gets worse.
  // Safe to widen: nothing in the app branches on the thrown message's value.
  // The thrown Error also CARRIES the machine code (additive, 2026-08-16). The
  // human `message` above is what every caller alerts, but a caller that needs
  // to BRANCH on a specific failure had no option other than regex-matching the
  // prose — which breaks the moment the wording is edited or translated.
  // `err.code` / `err.status` give those callers something stable. Nothing reads
  // these unless it opts in, so no existing error path changes.
  if (!res.ok) {
    var apiErr = new Error(data.message || data.error || "API Error");
    apiErr.code   = data.code || data.error || "";
    apiErr.status = res.status;
    apiErr.fields = data.fields || null;
    throw apiErr;
  }
  return data;
}

// ===== ONE GET PER URL PER BURST — shared in-flight promise + short result TTL =====
// A WS burst (cbBust) wakes several readers of the SAME URL at once: the
// Sidebar badge and the Leads page both ask /api/leads/counts, and every
// empty-payload lead_updated calls fetchLeadsLight() for the same bootstrap
// window. Measured in the server log for one sales session (2026-10-15): two
// counts requests per tick, 2-5 /api/leads per tick. Each reader keeps its own
// debounce — this does not change WHEN anyone asks, only how many requests the
// same question costs: readers that ask while one is in flight share its
// promise, and readers that ask within SHARED_GET_TTL_MS after it settled get
// the settled result (the burst window the readers' own debounces already
// accept). Only fulfilled results are kept; a rejection is dropped at once so a
// Retry is a real request. apiFetch stays the one network function.
var SHARED_GET_TTL_MS = 1500;
var _sharedGets = {};
var sharedApiGet = function(url, token, now) {
  var t = (typeof now === "number") ? now : Date.now();
  var hit = _sharedGets[url];
  if (hit && hit.token === token && (hit.settledAt === null || (t - hit.settledAt) <= SHARED_GET_TTL_MS)) return hit.promise;
  var entry = { token: token, settledAt: null, promise: null };
  entry.promise = apiFetch(url, "GET", null, token).then(function(r){
    entry.settledAt = (typeof now === "number") ? now : Date.now();
    return r;
  }, function(e){
    if (_sharedGets[url] === entry) delete _sharedGets[url];
    throw e;
  });
  _sharedGets[url] = entry;
  return entry.promise;
};

// ===== A WINDOW REFRESH MUST NOT EVICT ROWS THE WINDOW CANNOT CONTAIN (2026-10-27) =====
//
// p.leads is the createdAt-desc window the bootstrap and loadMoreLeads paged in
// PLUS every lead a WS lead_updated doc UPSERTED since ([lead].concat(prev) in
// the socket handler). An upserted lead is usually OLDER than the window —
// agents work callbacks from days ago — and under the admin's Last Activity
// sort those are exactly the rows at the top of the list. The empty-payload
// refresh (fetchLeadsLight) re-reads page 1 of the window and used to REPLACE
// the held array with it whenever the response was at least as long, so every
// WS-upserted row outside the window was evicted on the next unrelated burst:
// a Call/WhatsApp tap ({recorded} answers with no doc), an inbound webhook, a
// bulk op, anywhere in the company.
//
// Measured in headless Chrome against production (2026-10-27, as the owner,
// Leads / All / All Time / Last Activity): a lead created 2026-10-11 (outside
// the 100-row window) appeared at row 0 on its agent's first write, its Last
// Feedback updated in place on the second, and 1.9 s after a third party's
// empty lead_updated the refresh (`page=1&limit=201`, lead absent) wiped the
// row — 14 rows before, 14 rows after, the lead untouched in the database.
//
// The rule: a refresh REPLACES the window and RE-VERIFIES, BY ID, every row the
// client holds outside it (`GET /api/leads?fields=summary&ids=…`, the same
// scoped, staleness-gated list the window came from). A row the caller may no
// longer see (rotated out of a team leader's subtree, archived by a bulk op,
// gone stale) is absent from that answer and dropped; a row that still belongs
// comes back as the server's own copy and stays — and the page's own sort then
// moves it visibly instead of losing it. Module scope and pure so
// verify-leads-window-refresh.js can RUN it over the reproduced shape.
var LEADS_REFRESH_VERIFY_MAX = 100;
// Ids held but not in the refreshed window, in held order (WS upserts prepend,
// so the rows most recently pushed are asked first and the deep-scroll tail
// falls beyond the cap).
var leadsHeldOutsideWindow = function(held, fresh) {
  var inFresh = {};
  (fresh || []).forEach(function(l){ if (l && l._id) inFresh[String(l._id)] = true; });
  var out = [];
  (held || []).forEach(function(l){
    var id = l && l._id ? String(l._id) : "";
    if (id && !inFresh[id] && out.indexOf(id) === -1) out.push(id);
  });
  return out;
};
// What the verification answered: a map id -> row for the ids that came back,
// or FALSE when a row nobody asked for came back — an older deploy that does
// not read ?ids= answers with the window instead, and an unhonoured parameter
// is never treated as a verification (that path drops, it never ghosts).
var leadsVerifiedById = function(asked, rows) {
  var want = {}; (asked || []).forEach(function(id){ want[String(id)] = true; });
  var map = {};
  var list = Array.isArray(rows) ? rows : [];
  for (var i = 0; i < list.length; i++) {
    var id = list[i] && list[i]._id ? String(list[i]._id) : "";
    if (!id || !want[id]) return false;
    map[id] = list[i];
  }
  return map;
};
// The merge. `verified` is the map above; `undefined` means the verification
// request FAILED (keep what is held — the next burst asks again); `false` means
// it was not honoured (the asked rows are dropped: the pre-2026-10-27
// behaviour, never a ghost). Held rows beyond the cap stay, unverified, as the
// deep-scroll tail always did. Window rows take the server's copy; a verified
// row keeps the held copy only when it is newer than the server's.
var mergeLeadsWindowRefresh = function(prev, fresh, verified, asked) {
  var held = Array.isArray(prev) ? prev : [];
  var next = (fresh || []).slice();
  var have = {}; next.forEach(function(l){ if (l && l._id) have[String(l._id)] = true; });
  var askedSet = {}; (asked || []).forEach(function(id){ askedSet[String(id)] = true; });
  held.forEach(function(l) {
    var id = l && l._id ? String(l._id) : "";
    if (!id || have[id]) return;
    var keep = null;
    if (!askedSet[id]) keep = l;
    else if (verified === undefined) keep = l;
    else if (verified && verified[id]) {
      var srv = verified[id];
      var heldNewer = l.updatedAt && srv.updatedAt && new Date(l.updatedAt).getTime() > new Date(srv.updatedAt).getTime();
      keep = heldNewer ? l : srv;
    }
    if (keep) { next.push(keep); have[id] = true; }
  });
  return next;
};

// ===== TRANSLATIONS =====
var TR = {
  ar: {
    dir: "rtl",
    login: "تسجيل الدخول", loginBtn: "دخول", loginError: "Username أو كلمة المرور غلط",
    username: "Username", password: "Password", logout: "تسجيل خروج", rememberMe: "تذكرني",
    dashboard: "الرئيسية", leads: "الLeads", deals: "الDeals", projects: "المشاريع",
    reports: "التقارير", team: "فريق المبيعات", users: "Users",
    units: "الوحدات", settings: "الإعدادات", channels: "القنوات", dailyReq: "Daily Request",
    archive: "الArchive", brokers: "Brokers",
    search: "Search...",
    all: "الكل", totalLeads: "Total الLeads", newLeads: "جدد",
    activeDeals: "Deals نشطة", doneDeals: "تم البيع",
    addLead: "إضافة leads", addUser: "Add User", addRequest: "طلب جديد",
    name: "Name", phone: "Phone", phone2: "هاتف إضافي", email: "Email", budget: "Budget",
    project: "المشروع", source: "المصدر", agent: "Agent",
    // Helper under the Convert-to-Deal Project field. See the 2026-07-20 ruling
    // recorded at openConvert — the field opens blank on purpose.
    projectPurchasedHint: "اكتب المشروع اللي اتشرى فعلاً — ممكن يكون مختلف عن الـ EOI",
    status: "Status", cancel: "إلغاء", save: "حفظ", add: "إضافة", edit: "تعديل",
    callbackTime: "Callback", notes: "Notes",
    changeStatus: "Change Status", assignTo: "Assign To",
    lastActivity: "Last Activity", title: "Job Title", role: "Role",
    active: "نشط", inactive: "غير نشط",
    admin: "مدير نظام", salesManager: "مدير مبيعات", salesAgent: "موظف مبيعات", viewer: "مشاهد",
    potential: "Potential", hotCase: "Hot Case", callBack: "Call Back", notInterested: "Not Interested",
    noAnswer: "No Answer", doneDeal: "Done Deal", meetingDone: "Meeting Done",
    connected: "Online", disconnected: "غير Online",
    conversionRate: "معدل التحويل", totalCalls: "الCalls",
    todayActivities: "أنشطة Today", callReminder: "تنبيهات",
    available: "متاح", reserved: "محجوز", sold: "مباع",
    language: "اللغة", calls: "Calls", meetings: "اجتماعات", followups: "متابعات",
    relatedLead: "الleads",
    sourcePerf: "أداء المصادر", leadsByStatus: "الLeads حسب Status",
    agentPerf: "أداء Agentين", companyName: "اسم الشركة",
    welcome: "مرحباً", myLeads: "عملائي", allLeads: "كل الLeads",
    pending: "متبقية", ago: "منذ", minutes: "دقيقة", hours: "ساعة", days: "يوم", just: "الآن",
    loading: "Loading...", error: "خطأ في الاتصال", retry: "إعادة المحاولة",
    deleteConfirm: "هل أنت متأكد؟", archiveConfirm: "أرشفة الleads؟ يمكن استعادته لاحقاً",
    logActivity: "تسجيل نشاط",
    statusComment: "Reason for status change (required)", statusCommentPH: "Write a note about this change...",
    commentRequired: "⚠️ A note is required قبل Change Status",
    importExcel: "استيراد Excel", importDone: "تم الاستيراد", importErr: "خطأ — تأكد من الأعمدة: name, phone, source",
    activityLog: "سجل الأنشطة", clientHistory: "تاريخ الleads",
    duplicateFound: "⚠️ الرقم ده موجود بالفعل!", duplicateClient: "leads موجود بنفس الرقم",
    salesDay:"مبيعات Today", salesWeek:"مبيعات This Week", salesMonth:"مبيعات This Month", dealsCount:"deal", newLeadsToday:"Leads جدد Today", bestAgent:"🏆 الأفضل هذا This Month", kpiTitle:"📊 KPIs — المبيعات",
    bulkReassign: "تحويل جماعي", selectAll: "تحديد الكل", reassignTo: "تحويل لـ",
    whatsapp: "واتساب", call: "اتصال",
    propertyType: "Property Type", area: "Location",
    totalRequests: "Total Numbers",
    restore: "استعادة",
    overdue: "متأخرون",
    noActivity: "No Activity +يوم",
    exportExcel: "تصدير Excel",
    vip: "VIP",
    markVip: "تمييز كـ VIP",
    removeVip: "إلغاء VIP",
    quickAdd: "إضافة سريعة",
    bulkWhatsApp: "واتساب جماعي",
    waTemplates: "رسائل جاهزة",
    sendWa: "إرسال واتساب",
    selectAll: "تحديد الكل",
    selected: "محدد",
    msgCopied: "✅ تم نسخ الرسالة",
    changePassword: "تغيير كلمة المرور",
    currentPassword: "كلمة المرور الحالية",
    newPassword: "كلمة المرور الجديدة",
    confirmPassword: "Confirm كلمة المرور الجديدة",
    passwordMismatch: "كلمتا المرور مش متطابقتين",
    passwordSuccess: "✅ تم تغيير كلمة المرور بنجاح",
    passwordError: "❌ خطأ في تغيير كلمة المرور",
    // Callback strip + reschedule sheet (src/lib/callbackStrip.js, 2026-10-26).
    cbToday: "النهاردة", cbTomorrow: "بكرة", cbNow: "دلوقتي", cbIn: "كمان",
    cbOverdueBy: "معاد المكالمة اتأخر", cbLateSince: "فات من", cbReschedule: "أجّل المكالمة", cbWasAt: "كان معاده",
    cbQuickHour: "بعد ساعة", cbQuickTomorrowMorning: "بكرة الصبح 10:00 ص", cbQuickTomorrowSame: "بكرة نفس الوقت",
    // 12-hour clock words (src/lib/timeFormat.js, 2026-10-27).
    am: "ص", pm: "م",
  },
  en: {
    dir: "ltr",
    login: "Login", loginBtn: "Sign In", loginError: "Invalid username or password",
    username: "Username", password: "Password", logout: "Logout", rememberMe: "Remember me",
    dashboard: "Dashboard", leads: "Leads", deals: "Deals", projects: "Projects",
    reports: "Reports", team: "Sales Team", users: "Users",
    units: "Units", settings: "Settings", channels: "Channels", dailyReq: "Daily Request",
    archive: "Archive", brokers: "Brokers",
    search: "Search...",
    all: "All", totalLeads: "Total Leads", newLeads: "New",
    activeDeals: "Active Deals", doneDeals: "Done Deals",
    addLead: "Add Lead", addUser: "Add User", addRequest: "New Request",
    name: "Name", phone: "Phone", phone2: "Alt. Phone", email: "Email", budget: "Budget",
    project: "Project", source: "Source", agent: "Agent",
    projectPurchasedHint: "Type the project actually purchased — may differ from the EOI",
    status: "Status", cancel: "Cancel", save: "Save", add: "Add", edit: "Edit",
    callbackTime: "Callback Time", notes: "Notes",
    changeStatus: "Change Status", assignTo: "Assign To",
    lastActivity: "Last Activity", title: "Job Title", role: "Role",
    active: "Active", inactive: "Inactive",
    admin: "Admin", salesManager: "Sales Manager", salesAgent: "Sales Agent", viewer: "Viewer",
    potential: "Potential", hotCase: "Hot Case", callBack: "Call Back", notInterested: "Not Interested",
    noAnswer: "No Answer", doneDeal: "Done Deal", meetingDone: "Meeting Done",
    connected: "Connected", disconnected: "Disconnected",
    conversionRate: "Conversion Rate", totalCalls: "Total Calls",
    todayActivities: "Today Activities", callReminder: "Notifications",
    available: "Available", reserved: "Reserved", sold: "Sold",
    language: "Language", calls: "Calls", meetings: "Meetings", followups: "Follow-ups",
    relatedLead: "Lead",
    sourcePerf: "Source Performance", leadsByStatus: "Leads by Status",
    agentPerf: "Agent Performance", companyName: "Company Name",
    welcome: "Welcome", myLeads: "My Leads", allLeads: "All Leads",
    pending: "Pending", ago: "ago", minutes: "min", hours: "hr", days: "days", just: "Just now",
    loading: "Loading...", error: "Connection Error", retry: "Retry",
    deleteConfirm: "Are you sure?", archiveConfirm: "Archive this lead? Can be restored later.",
    logActivity: "Log Activity",
    statusComment: "Reason for status change (required)", statusCommentPH: "Write a note about this change...",
    commentRequired: "⚠️ You must write a note before changing status",
    importExcel: "Import Excel", importDone: "Import Done", importErr: "Error — Check columns: name, phone, source",
    activityLog: "Activity Log", clientHistory: "Client History",
    duplicateFound: "⚠️ This number already exists!", duplicateClient: "Existing client with same number",
    salesDay:"Today's Deals", salesWeek:"This Week's Deals", salesMonth:"This Month's Deals", dealsCount:"deal(s)", newLeadsToday:"New Leads Today", bestAgent:"🏆 Top Performer", kpiTitle:"📊 KPIs — Deals",
    bulkReassign: "Bulk Reassign", selectAll: "Select All", reassignTo: "Reassign To",
    whatsapp: "WhatsApp", call: "Call",
    propertyType: "Property Type", area: "Location",
    totalRequests: "Total Numbers",
    restore: "Restore",
    overdue: "Overdue",
    noActivity: "No activity +1 day",
    exportExcel: "Export Excel",
    vip: "VIP",
    markVip: "Mark as VIP",
    removeVip: "Remove VIP",
    quickAdd: "Quick Add",
    bulkWhatsApp: "Bulk WhatsApp",
    waTemplates: "Message Templates",
    sendWa: "Send WhatsApp",
    selectAll: "Select All",
    selected: "Selected",
    msgCopied: "✅ Message Copied",
    changePassword: "Change Password",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmPassword: "Confirm New Password",
    passwordMismatch: "Passwords do not match",
    passwordSuccess: "✅ Password changed successfully",
    passwordError: "❌ Error changing password",
    // Callback strip + reschedule sheet (src/lib/callbackStrip.js, 2026-10-26).
    cbToday: "Today", cbTomorrow: "Tomorrow", cbNow: "Now", cbIn: "in",
    cbOverdueBy: "Callback overdue", cbLateSince: "late", cbReschedule: "Reschedule", cbWasAt: "Was at",
    cbQuickHour: "In 1 hour", cbQuickTomorrowMorning: "Tomorrow 10:00 AM", cbQuickTomorrowSame: "Tomorrow, same time",
    // 12-hour clock words (src/lib/timeFormat.js, 2026-10-27).
    am: "AM", pm: "PM",
  }
};

var C = {
  primary: "#1a2942", primaryLight: "#243752", primaryDark: "#0d1f33",
  accent: "#E8A838", accentLight: "#F5C563",
  success: "#22C55E", danger: "#EF4444", warning: "#F59E0B", info: "#3B82F6",
  bg: "#F0F2F5", text: "#1E293B", textLight: "#64748B", border: "#E2E8F0"
};

var STATUSES = function(t) { return [
  { value: "NewLead", label: "New Lead", bg: "#EEF2FF", color: "#6366F1" },
  { value: "Potential", label: "Potential", bg: "#E0F2FE", color: "#0284C7" },
  { value: "HotCase", label: t.hotCase, bg: "#FEE2E2", color: "#DC2626" },
  { value: "CallBack", label: t.callBack, bg: "#FEF3C7", color: "#B45309" },
  { value: "MeetingDone", label: t.meetingDone, bg: "#F3E8FF", color: "#7C3AED" },
  { value: "EOI", label: "EOI", bg: "#FFF7ED", color: "#EA580C" },
  { value: "NotInterested", label: t.notInterested, bg: "#F1F5F9", color: "#64748B" },
  { value: "NoAnswer", label: t.noAnswer, bg: "#E0E7FF", color: "#4338CA" },
  { value: "DoneDeal", label: t.doneDeal, bg: "#DCFCE7", color: "#15803D" },
  { value: "Deal Cancelled", label: "Deal Cancelled", bg: "#FEE2E2", color: "#B91C1C", adminOnly: true },
]; };

// ===== THE STATUS COLUMN IS PINNED, NOT CAPPED (owner UX ruling 2026-08-21) =====
//
// At narrow viewports the status pill wrapped to two lines ("Not Interested"
// breaking after "Not"), fattening the row and breaking the table's rhythm.
//
// This is the SAME treatment as 01061ef, for a related but distinct reason. There
// the cap was a measure-loop hazard: `max-width` on a wrapping <td> under
// `table-layout:auto` caps a column without flooring it, so width -> wrap ->
// height -> what the virtualiser measures -> width oscillates forever. Here the
// column was UNCAPPED, which the shake audit correctly called loop-safe — an
// uncapped cell takes max-content when it can, so the feedback path has no
// purchase. Uncapped is safe; it is not STABLE. The column still resizes with
// whatever rows happen to be on screen.
//
// width = minWidth = maxWidth is the only form that is both. Pinning can only
// REMOVE degrees of freedom from the layout, so it cannot introduce a loop — it
// is the safe direction by construction, and it is why this needs no re-measure.
//
// THE NUMBER IS MEASURED, NOT GUESSED. Rendered in headless Chrome with the exact
// pill styles and the app's font stack, cell width = pill + 24px td padding, for
// every label each column can actually produce (both pages filter out "Deal
// Cancelled" — App.js:8084 and :18582 — and DR additionally drops "New Lead"):
//
//   DAILY REQUEST (pill fontSize 11)   widest "Not Interested"  136.50px
//   LEADS MAIN    (pill fontSize 12)   widest "Not Interested"  143.61px
//
// NOT "Meeting Done" (133.30 / 140.25) — that reads longer than it measures.
// Sizing to it would have left the real widest label still wrapping.
//
// 152 clears the 143.61 ceiling by 8.4px. The neighbouring resolvable families in
// the stack land at 141.91 (Arial) and 142.89 (Helvetica Neue), so the ceiling is
// a tight cluster rather than a lucky local number. Segoe UI and Roboto could not
// be measured on macOS — they fall back — so the headroom also covers them.
//
// DR needs only 137 and shares 152 anyway. Two constants for the SAME pill would
// drift, and 15px on one column does not buy a second source of truth. (A second
// constant DOES appear below — for a genuinely different pill, which is the case
// where splitting is the honest choice rather than the lazy one.)
//
// ACCEPTED TRADE-OFF (owner's explicit ask): a fixed column no longer shrinks when
// every visible row is a short status, so both tables reach horizontal scroll
// slightly sooner on narrow windows. Stable rhythm was chosen over that.
//
// Degrades gracefully if a platform ever exceeds 152: with nowrap the pill
// overflows its cell rather than wrapping, so the row HEIGHT stays constant and
// the loop still cannot form.
var STATUS_COL_W = 152;
// boxSizing:"border-box" is NOT decoration — without it these numbers are wrong.
// A <td> is content-box by default, so `width:152` plus the cell's own 24px of
// horizontal padding renders a 176px column: 24px wider than intended, spent on
// horizontal scroll. Measured that way first (176 at every viewport), which is
// how it was caught. border-box makes the constant mean the whole cell, which is
// what it was measured as (pill + padding).
var STATUS_COL_PIN = { width: STATUS_COL_W, minWidth: STATUS_COL_W, maxWidth: STATUS_COL_W, boxSizing: "border-box", whiteSpace: "nowrap" };

// ===== THE PHONE COLUMNS ARE PINNED TOO (2026-08-26) ========================
//
// Same mechanism as the status column, different trigger: PhoneCell swaps a
// masked string for the real number on hover, and maskPhone emits EXACTLY 11
// characters whatever the input length. Under table-layout:auto the column is
// the max over its cells, so revealing a 15-char number re-ran the layout for
// the whole table and shifted every column beside it.
//
// The first attempt (f582e02) pinned the WIDTH INSIDE PhoneCell with a `ch`
// floor and a monospace face. It worked and it was the wrong layer: it changed
// the typeface of every phone number in the product — leads table, DR table,
// side panels, cards — to fix two tables. Reverted. The fix belongs on the
// column, which is where the instability actually lives, and which is how the
// status column was already done.
//
// THE NUMBER IS MEASURED, NOT GUESSED. Headless Chrome 151, the app's exact
// body stack from index.css, PhoneCell's span (fontSize 12, letterSpacing 1)
// plus the cell's 24px of horizontal padding:
//
//   17 digits  "88888888888888888"        147.30 span -> 171.30 cell   <- target
//   real widest on these pages today (15) 120.20 span -> 144.20 cell
//   masked (11, every row at rest)         80.72 span -> 104.72 cell
//
// Neighbouring resolvable families land at 154.44 (Helvetica Neue), 154.47
// (Arial), 170.70 (Verdana), 176.97 (Geneva) — so 171.30 sits in a cluster
// rather than being a lucky local number. Segoe UI and Roboto cannot be
// measured on macOS; the headroom covers them.
//
// 152 clears the REAL widest value on these two pages — 15 chars, 144.20px —
// by 7.8px, the same ~5% headroom STATUS_COL_W takes over its own ceiling.
//
// IT WAS 184, SIZED FOR 17 CHARS THAT DO NOT EXIST (corrected 2026-08-26). The
// length histogram over the EOI + Deals population tops out at 15; 17 was a
// requested target, not a measured one. The 32px it cost per column was not
// free: with 11-12 columns at these viewports the surplus came out of Name,
// which wrapped to FOUR lines on EOI and three on Deals. Headers are not
// binding either way ("Alt. Phone" measures 80.50).
//
// ONE constant for both columns: Phone and Alt. Phone render the same
// component at the same size, so two numbers would be two things to drift.
//
// Degrades gracefully. The whole book holds one 27-char value
// ("+201284131211/0 10 01713200", 224.97) — with nowrap and a hard pin it
// OVERFLOWS its cell rather than wrapping, so the row height is unchanged and
// the column still cannot resize. Wide is a cosmetic problem; reflow was the
// bug.
var PHONE_COL_W = 152;
var PHONE_COL_PIN = { width: PHONE_COL_W, minWidth: PHONE_COL_W, maxWidth: PHONE_COL_W, boxSizing: "border-box", whiteSpace: "nowrap" };

// The Leads IMPORTANT table's two status columns render a DIFFERENT pill: no
// dropdown arrow, padding 3px 9px instead of 4px 10px. Measured the same way,
// its widest cell is "Not Interested" at 122.92px, so 130 gives it the same ~6%
// headroom 152 gives the arrow-bearing pill.
//
// A SECOND CONSTANT, not a shared one, and the arithmetic is the argument:
// reusing 152 would leave 29px of dead space in each of that table's TWO status
// columns — 58px on an 8-column table, which is real horizontal scroll spent on
// an arrow that is not drawn. Two constants are only a drift risk when they
// describe the same thing; these describe two different pills.
var STATUS_COL_W_PLAIN = 130;
var STATUS_COL_PIN_PLAIN = { width: STATUS_COL_W_PLAIN, minWidth: STATUS_COL_W_PLAIN, maxWidth: STATUS_COL_W_PLAIN, boxSizing: "border-box", whiteSpace: "nowrap" };

// ===== VIRTUALIZED LIST SCROLL BUDGET (2026-09-02) =========================
//
// WHITE ROWS WHILE SCROLLING, CONTENT BACK WHEN THE SCROLL STOPS. All five
// virtualized lists in this app scroll the WINDOW (`useWindowScroll` on the
// Leads main table, the Leads "Important" table, the Leads mobile cards, the
// DR table and the DR mobile cards), so the scroll container is the document
// and Virtuoso only learns a scroll happened after the browser has already
// painted the new position. With Virtuoso's default overscan of ZERO, a row is
// mounted only once it is inside the viewport — so a fast flick outruns the
// render and the user looks at the list's own background until React catches
// up. Nothing is broken and nothing has crashed; there is simply nothing
// mounted where the eye is looking.
//
// 600px each way is the runway that closes that gap: roughly 13 already-
// painted desktop table rows or ~2.5 mobile cards above and below the
// viewport, which covers a flick at normal thumb/wheel speed. It is a RENDER
// budget, not a fetch budget — `endReached` still fires off the true viewport,
// so paging, the load-more chase and the 150ms re-fire cooldown are all
// unchanged.
var LIST_VIEWPORT_OVERSCAN = { top: 600, bottom: 600 };
//
// The starting height estimate for a row, which lets Virtuoso skip its
// single-row "probe" pass and lay out a full screen on the first frame instead
// of converging on one over several. Read off the row's own box rather than
// guessed: a table row is 10px of padding above and below a ~13px line with a
// 19px status pill in it (~44px), and a mobile card is 16px of padding and a
// 2px border around a header, a chip row, a feedback line and a 40px action
// row, plus the 12px margin below it (~250px). Every row is still MEASURED
// after it mounts exactly as before — this only decides where the layout
// starts, so an estimate that is a few pixels off costs nothing.
var LIST_ROW_H_TABLE = 44;
var LIST_ROW_H_CARD = 250;

// ===== THE DAILY REQUESTS TABLE'S TWO CONTACT COLUMNS (2026-09-01) ==========
//
// Phone and Alt. Phone are ONE renderer, not two similar ones. The owner's ask
// was that they be styled identically; the honest way to guarantee that is for
// there to be nothing to keep in sync. EOI and Deals already ship the pair as
// two columns sharing PHONE_COL_PIN — the DR table was the odd one out, stacking
// the second number as a small grey sub-line inside the Phone cell with no
// header of its own, which is why it read as "no Alt Phone column".
//
// FIXED WIDTH, BOTH CELLS AND BOTH HEADERS. PHONE_COL_PIN is width = minWidth =
// maxWidth + border-box + nowrap, the measured constant already used by the
// other two tables. Under table-layout:auto a max-width only CAPS a column, so a
// cap alone leaves the width a function of whichever rows the virtualiser has
// mounted — column width -> wrap width -> line count -> row height -> what
// Virtuoso measures -> a new window -> a new column width, forever. That is the
// loop 01061ef fixed on this table's Last Feedback column. Pinning min = width =
// max, on the <th> as well (a column is the max over its cells, header
// included), removes the path entirely.
//
// AN EMPTY CELL OCCUPIES EXACTLY THE SPACE OF A FULL ONE, and this is why the
// action row is hidden rather than omitted: `visibility:"hidden"` keeps the box
// in the layout, so the two cells have byte-identical geometry whether or not a
// second number exists. Omitting the row instead would make row height depend on
// the DATA in the row, which is the same class of instability as the width loop
// and the one the owner named. visibility:hidden also takes the links out of the
// tab order and out of hit-testing; pointerEvents is belt-and-braces for the
// href, which is a `tel:` built from an empty string when there is no number.
//
// DEDUPED THROUGH altPhoneCell — the SAME module-scope helper the DR Excel
// export already calls, so the column and the file cannot disagree about what an
// alt phone is. Measured on production 2026-09-01: of 3,872 non-archived
// requests, 60 carry a phone2 and 25 of those repeat `phone` byte for byte. Those
// 25 render "-": a column that shows the same number twice reads as a second
// contact that does not exist. The comparison is EXACT and not normalised, on
// purpose — see altPhoneCell's own note about the 3,005 "p:+2010…" leads.
var DrContactCell = function(q) {
  var num = String(q.phone == null ? "" : q.phone).trim();
  var has = !!num;
  return <td style={Object.assign({ padding:"10px 12px", fontSize:12, direction:"ltr" }, PHONE_COL_PIN)}>
    {has ? <PhoneCell phone={num}/> : <span style={{ color:"#CBD5E1" }}>-</span>}
    <div aria-hidden={!has} style={{ display:"flex", gap:6, marginTop:3, visibility: has ? "visible" : "hidden", pointerEvents: has ? "auto" : "none" }}>
      <a href={"tel:"+cleanPhone(num)} onClick={function(e){e.stopPropagation();}} tabIndex={has?0:-1} style={{ fontSize:q.isMobile?10:12, color:"#60A5FA", textDecoration:"none", display:"flex", alignItems:"center", gap:3, padding:q.isMobile?"0":"2px 6px", borderRadius:6, background:q.isMobile?"transparent":"#EFF6FF" }}><Phone size={q.isMobile?9:12}/>{!q.isMobile&&" Call"}</a>
      <a href={"https://wa.me/"+waPhone(num)} target="_blank" rel="noreferrer" onClick={function(e){e.stopPropagation();}} tabIndex={has?0:-1} style={{ fontSize:q.isMobile?10:12, color:"#25D366", textDecoration:"none", display:"flex", alignItems:"center", gap:3, padding:q.isMobile?"0":"2px 6px", borderRadius:6, background:q.isMobile?"transparent":"#DCFCE720" }}><svg viewBox="0 0 24 24" width={q.isMobile?14:16} height={q.isMobile?14:16} fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>{!q.isMobile&&" WhatsApp"}</a>
    </div>
  </td>;
};

var DR_STATUSES = function(t) { return STATUSES(t).filter(function(s){return s.value!=="NewLead";}); };
// Filter out statuses flagged adminOnly (e.g. "Deal Cancelled") for non-admin users.
var visibleStatuses = function(list, role){ return (list||[]).filter(function(s){ return !s.adminOnly || role==="admin"; }); };

// Closing Company (Feature B) — the company a deal was closed under is visible
// and editable to admin + operation only. Display-only gate (the field may
// ship to other roles in API payloads; UI hides it).
var canSeeClosingCompany = function(u){ return !!(u && (u.role === "admin" || u.role === "operation")); };

// Permanent Lead ID (Feature A) — the ID is just an attribute of a Deal/EOI/
// Commission row the viewer is already authorized to see (page routing + per-role
// server-side scope enforce access upstream). So any authenticated user who can
// render such a row may see its ID; no need to re-gate by role here.
var canSeeLeadIds = function(u){ return !!u; };
// Developer (D4) — like the Lead ID rule above: the developer is just an
// attribute of a Deal/Commission row the viewer is already authorized to see,
// so anyone authenticated may SEE it. EDITING (the side-panel dropdown) is
// admin + operation only, mirroring the closing-company edit gate.
var canSeeDeveloper  = function(u){ return !!u; };
var canEditDeveloper = function(u){ return !!(u && (u.role === "admin" || u.role === "operation")); };
// Developer picker on a deal (R2/Option 1) — any role that can EDIT a deal may
// set it from the pre-vetted dropdown. Mirrors the PUT /api/leads scope gate
// (admin/SA/sales/team_leader/manager/director; viewer/hr are read-only).
var canEditDeals = function(u){ return !!(u && ["admin","operation","sales","team_leader","manager","director"].indexOf(u.role) !== -1); };
// Format an ACTION CODE as "ID #01000" (5-digit zero-padded).
//
// "No code", not "ID pending" (owner ruling 2026-08-28). The old wording dated
// from when every row was expected to acquire a number eventually — a
// placeholder promising one was coming. Under ID RULE 2 that is no longer true
// of anything: a row without a code is a row with no ACTION behind it (the 12
// orphaned commissions, whose leadId matches no lead) and it will never get one.
// "Pending" would be a promise the system has no intention of keeping.
var formatLeadId = function(n){
  if (n === null || n === undefined || n === "") return "No code";
  var num = Number(n);
  if (!isFinite(num)) return "No code";
  return "ID #" + String(Math.trunc(num)).padStart(5, "0");
};
// ── THE ID-QUERY SHAPE — MUST match ID_QUERY_RE in crm-aro-backend/server.js ──
// Everything a person types when they mean an id: "50038", "#50038",
// "ID #50038", "id50038", "ID: 50038", "ID-50038". The optional "id" word and
// any separators are stripped; the digits are captured.
//
// Duplicated rather than imported for the same reason ROLE_DEFINITIONS_FE is:
// Railway deploys only /crm-aro-backend and CRA blocks importing from outside
// src/. If you change one, change the other — a frontend that thinks
// "ID #50038" is a name query while the backend treats it as an id sends the
// request down the wrong path and the DR search runs when it should not.
//
// The digits must END the query and are capped at 9, so an 11-digit phone and
// any name with trailing text keep taking the ordinary regex path.
var ID_QUERY_RE = /^\s*(?:id)?[\s:.\-#]*(\d{1,9})\s*$/i;
var isIdShaped = function(v){ return ID_QUERY_RE.test(String(v == null ? "" : v).trim()); };
// ── THE PHONE-SUBSTRING FLOOR — MUST match PHONE_MIN_DIGITS in server.js ─────
// How many digits a numeric query must carry before it is ALSO asked as "does a
// phone contain this?". Below the floor only the strict id pass runs.
//
// 4, because of what the shorter cases actually return. Egyptian mobiles are
// "+20 1XX XXXXXXX", so every lead in the book contains "01" and "20"; a two- or
// three-digit substring matches a double-digit percentage of it. Four is the
// first width that is normally a real fragment of a number read off a screen.
//
// THE FLOOR IS NOT AN ID GATE. "50" still resolves leadId 50 exactly as before.
//
// Duplicated rather than imported for the same reason ID_QUERY_RE is (Railway
// deploys only /crm-aro-backend; CRA blocks importing from outside src/), and
// gated the same way — a frontend floor of 3 against a backend floor of 4 would
// have the Leads list showing rows the dropdown says do not exist.
var PHONE_MIN_DIGITS = 4;
// ── BARE DIGITS vs EXPLICIT ID SYNTAX — MUST match BARE_DIGITS_RE in server.js ─
// "50038" is ambiguous — id, or a fragment of a phone? The user has not said.
// "#50038" / "ID #50038" / "id50038" are the user saying. Only the bare form
// gets a phone pass; every explicit form behaves exactly as it did before phone
// search existed. "Nothing but digits" is the test, so ":", ".", "-" and spaces
// count as decoration too — anything typed around the number reads as intent.
var BARE_DIGITS_RE = /^\s*\d{1,9}\s*$/;
var isBareDigitQuery = function(v){ return BARE_DIGITS_RE.test(String(v == null ? "" : v).trim()); };
var idQueryDigits = function(v){ var m = ID_QUERY_RE.exec(String(v == null ? "" : v).trim()); return m ? m[1] : ""; };
// THE SINGLE CHOKE POINT for both rules — bare-only and above-the-floor. Every
// phone-substring pass on both halves of the app asks this and nothing else.
var phoneSubstringDigits = function(v){
  if (!isBareDigitQuery(v)) return "";       // explicit id syntax -> id pass only
  var d = idQueryDigits(v);
  return d.length >= PHONE_MIN_DIGITS ? d : "";
};
// Its EXACT COMPLEMENT: the digits to run an ID pass on. Explicit syntax only.
//
// The pair is the whole contract, and it is mutually exclusive by construction —
// both branch on the same isBareDigitQuery, in opposite directions, so for any
// query at most ONE of them is non-empty and never both. Reading either one in
// isolation tells you which of the two questions is being asked.
var idPassDigits = function(v){ return isBareDigitQuery(v) ? "" : idQueryDigits(v); };
// Does this lead answer a numeric query? ONE condition, never two — whichever
// the syntax selected:
//   explicit ("#8220", "ID 8220") -> leadId equals the number. Phone is NOT
//                                    consulted, at any digit width.
//   bare     ("8220")             -> phone/phone2 contains the digits, and only
//                                    above the floor. leadId is NOT consulted.
// This mirrors the backend exactly: the dropdown renders one section for a
// numeric query because the server ran one pass, and this list must narrow to
// the same rows or the two surfaces disagree about what was asked.
var matchIdQuery = function(lead, q) {
  if (!idQueryDigits(q)) return true;        // not numeric at all — caller decides
  var idd = idPassDigits(q);
  if (idd) {
    var n = parseInt(idd, 10);
    if (!(isFinite(n) && n > 0)) return false;
    // R2/R4 — a transaction is searched by its recordNo, so this narrow has to
    // match it too. It used to compare leadId ALONE, which was correct only
    // while a transaction displayed its parent's number: an id read off the
    // Deals page emptied this list, and the comment below called that "the
    // honest answer". It is not honest any more — the row is right there.
    // _recordNo rides on record-backed rows (rowFromTxnRecord / txnTargetRow).
    // ACTIONS ONLY (ID RULE 3). This list narrow exists so the Leads list and the
    // search dropdown answer the SAME question. The dropdown resolves "#N" to an
    // action or to nothing, so matching leadId here would light up a client row
    // for a number the dropdown just said resolves nothing — the two surfaces
    // disagreeing, which is the whole defect this helper was written to prevent.
    // A client has no number to match (RULE 2).
    return Number(lead._recordNo) === n;
  }
  var pd = phoneSubstringDigits(q);
  if (!pd) return false;   // bare but below the floor — nothing is being asked
  if (lead.phone  && String(lead.phone).indexOf(pd)  >= 0) return true;
  if (lead.phone2 && String(lead.phone2).indexOf(pd) >= 0) return true;
  return false;
};

// The identifier a transaction row shows. ONE SHAPE SYSTEM-WIDE: "ID #01024",
// through formatLeadId itself — the same formatter, the same five digits, the
// same string every other row in the app already renders.
//
// OWNER RULING 2026-08-18: a transaction row shows its PARENT LEAD's id, never
// its own recordNo. The reader cutover briefly displayed the record number
// ("ID #50038" for an EOI on lead #01024) and that is reverted here. The client
// is the thing people search for and recognise, so every row belonging to
// #01024 reads #01024 — the EOI, the deal, and the lead itself.
//
// DISPLAY ONLY. `recordNo` is untouched in the database and still uniquely
// numbers every transaction; it stays the internal identity behind _rowKey,
// the per-row React key and the /api/records/:id routes. Nothing routes off the
// number that is rendered here.
//
// THE RULE (owner, 2026-08-18), applied here and nowhere else:
//   - a client with ONE transaction  -> every row shows the LEAD's id (#01024).
//     This is the legacy convention and it is what almost every row is.
//   - a client with TWO OR MORE      -> the OLDEST transaction keeps the lead's
//     id; every later (repeat) transaction shows its OWN recordNo (#50038), so
//     two purchases can never wear the same label.
//
// `_txnCount` / `_txnIndex` are computed server-side by txnGroupsForLeads and
// count TRANSACTIONS, not records — an EOI and the deal it converted into are
// one purchase wearing two record numbers. A row with no record at all (the 53
// historical frozen leads, the whole demo world) carries neither field, reads as
// count 0, and falls through to the lead id exactly as it always did.
var txnRowLabel = function(d){
  if (!d) return "";
  // ID RULE 1/2 (2026-08-28): THE CODE IS THE ACTION'S, ALWAYS. No sibling
  // arithmetic, no parent lookup, no "unless this is the oldest one" — the row
  // renders the number the row owns.
  //
  // What was here counted the client's transactions and, for the oldest, showed
  // the CLIENT's number instead. That rule is what put "#50039" on a deal whose
  // code is #50040 and made a number you can read off the screen one you cannot
  // type into the search box. It also meant a client's first purchase silently
  // changed label the day they bought again.
  //
  // `_recordNo` is absent only where there is genuinely no action record: the
  // demo world's 8 seeded EOI/DoneDeal leads, which carry no code and no client
  // number either. formatLeadId answers "No code" for those — exactly what
  // they rendered before this change, since d.leadId was equally absent.
  return formatLeadId(d._recordNo);
};

var PROJECTS = [
  "العاصمة الإدارية", "المستقبل سيتي", "التجمع الخامس", "الشروق", "6 أكتوبر",
  "بالم هيلز", "ماونتن فيو", "سوديك ايست", "الرحاب", "مدينتي"
];
// Canonical unit types — mirrors the Add-Deal + Daily-Request unit-type dropdowns.
// Drives the Lead form's Unit Type field (fieldsV2 model). "" renders "- Select -".
var UNIT_TYPES = ["", "Apartment", "Duplex", "Townhouse", "Twinhouse", "Standalone", "Commercial", "Admin", "Clinic", "Service Apartment", "Chalet"];
// The lead source list, the ad-platform flag and the source colours are NOT
// here: they are the server's registry (crm-aro-backend/lead-sources.js), held
// client-side by src/lib/leadSources.js (owner decisions 2026-11-02).

// ===== Campaign / Project name normalization (client mirror) =====
// KEEP IN LOCKSTEP with normalizeCampaignName / normalizeProjectName in
// crm-aro-backend/server.js. The server is the source of truth — it builds the
// dropdown options and resolves a selected base back to raw spellings. These
// copies exist only so the client can (a) re-narrow rows for exactness while a
// server fetch is still in flight, and (b) group the Daily Requests project
// options, which are derived from the already-loaded client list. If the two
// drift, the symptom is a row flickering in/out during load — not a wrong
// server result.
var normCampaignFE = function(raw){
  var s = String(raw == null ? "" : raw).trim().replace(/\s+/g, " ");
  if (!s) return "";
  var prev;
  do {
    prev = s;
    s = s.replace(/\s*[-–]\s*copy\s*\d*$/i, "");
    s = s.replace(/\s*\(copy\s*\d*\)$/i, "");
    s = s.replace(/\s+copy\s*\d*$/i, "");
    s = s.replace(/\s+\d+$/, "");
    s = s.replace(/\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)$/i, "");
    s = s.trim();
  } while (s !== prev && s.length > 0);
  return s || String(raw).trim();
};
// Since 2026-10-29 the fold also passes through the owner's project alias table
// (src/lib/projectAliases.js, the twin of the server's): a ruled list, never a
// heuristic — "Dose" keys to "D.O.S.E", "Zed 2" is still not "Zed".
var normProjectFE = function(raw){
  return canonicalProjectName(String(raw == null ? "" : raw).trim().replace(/\s+/g, " "));
};

// Location entity key — the FE twin of the backend's normalizeLocationEntityName.
// Strips invisible bidi/zero-width marks, lowercases, drops every
// non-alphanumeric codepoint (Unicode-aware, so Arabic names survive). Keep the
// two in step: this is what decides whether a row belongs to a managed location.
var normLocationEntityFE = function(raw){
  return String(raw == null ? "" : raw)
    .replace(/[​-‏‪-‮⁦-⁩﻿]/g, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");
};

// ===== DEVELOPER SEARCH — one fold, the SAME fold the add-dedupe uses ========
// Developer name search key: the FE twin of the backend's
// normalizeDeveloperName (server.js) — lowercase, then drop every codepoint
// that is not a letter or a number (Unicode-aware, so Arabic names survive).
// Format/zero-width marks are neither \p{L} nor \p{N}, so they fall out here
// too; that is why this needs no separate bidi strip the way
// normLocationEntityFE does.
//
// WHY THIS FOLD AND NOT A PLAIN LOWERCASE SUBSTRING. The question both search
// surfaces exist to answer is "does this developer already exist under SOME
// spelling?" — and the only authority on that is POST /api/developers, which
// refuses a name whose normalizeDeveloperName key already exists. A search box
// that folded differently would answer a different question than the one the
// admin is about to ask the server: type "elmasria", see nothing, add it, get a
// 400 saying it already exists. So this must stay in step with the backend, and
// verify-developer-search.js runs BOTH functions over a corpus every build.
//
// NOT a client mirror of a server RULE: nothing here decides whether a name is
// acceptable. The server owns that and answers with a 400 the form shows
// verbatim. This only decides which rows to SHOW.
//
// `String(raw || "")`, not the `raw == null ? "" : raw` spelling its location
// twin uses, so parity with the backend is TOTAL rather than total-for-strings:
// the two disagree on 0 / false / NaN otherwise, and the gate asserts parity
// over a corpus that includes them.
var normDeveloperNameFE = function(raw){
  return String(raw || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
};

// The one developer-search decision. Both surfaces route through it: the
// Settings → Developers list and the "Link to existing" picker of the
// Pending-developer resolver (SearchableSelect matchFn). An empty query matches
// everything — a filter with nothing typed is not a filter.
//
// MODULE SCOPE ON PURPOSE. Inside a render closure a build gate cannot execute
// it, and this codebase has shipped three wrong rules that way (CLAUDE.md, "A
// WRITER OR A RESOLVER INSIDE A RENDER CLOSURE CANNOT BE GATED"). Out here
// verify-developer-search.js runs the real function.
var devSearchMatch = function(name, query){
  var q = normDeveloperNameFE(query);
  if (!q) return true;
  return normDeveloperNameFE(name).indexOf(q) !== -1;
};

// List form of the same decision, for the Settings rows + their live count.
// Built on devSearchMatch rather than repeating the fold, so the row list and
// the dropdown can never disagree about what matches.
var filterDevelopersFE = function(list, query){
  return (list || []).filter(function(d){ return devSearchMatch(d && d.name, query); });
};

// Location search matcher — the twin of devSearchMatch, over the entity key
// above. Same reason it exists there: the "Link to existing" picker of the
// Pending-location resolver offers the managed locations, and the question it
// answers is "is this place already in the list under SOME spelling?" — which is
// the question normalizedName decides everywhere else (the unique index, the
// add-dedupe, resolveDrLocationWrite's auto-link). A plain substring filter
// answers a different one: "ras elhekma" would miss "Ras El Hekma", and the
// admin would mint the duplicate the fold exists to prevent.
//
// Module scope, so verify-location-resolution.js can execute it against the
// backend's own normalizeLocationEntityName rather than describe it.
var locSearchMatch = function(name, query){
  var q = normLocationEntityFE(query);
  if (!q) return true;
  return normLocationEntityFE(name).indexOf(q) !== -1;
};

// Location filter matcher. The Location dropdown mixes TWO kinds of option:
//   "loc:<entityKey>" — a MANAGED location; matches any spelling of that place,
//                       because the entity key folds punctuation and spacing
//                       ("Ras el hekma ", "Ras elhekma", "Raselhekma " are one).
//   anything else     — a legacy free-text option, matched on the project-style
//                       key (trim + collapse whitespace, case-insensitive) that
//                       the server also keys those by.
// Mirrors resolveDrLocationVariants server-side. Without the loc: branch this
// re-check would compare "loc:sahel" against "sahel" and hide EVERY row the
// server just returned.
var selMatchLocation = function(arr, raw){
  if (!arr || !arr.length) return true;
  var v = String(raw == null ? "" : raw).trim();
  if (v === "") return arr.indexOf("__none") !== -1;
  var entKey = normLocationEntityFE(v);
  var plainKey = normProjectFE(v).toLowerCase();
  for (var i = 0; i < arr.length; i++) {
    var sel = String(arr[i]);
    if (sel.indexOf("loc:") === 0) { if (sel.slice(4) === entKey) return true; }
    else if (sel.toLowerCase() === plainKey) return true;
  }
  return false;
};

// ===== Multi-select filter helpers (2026-08-05) =====
// Every multi-value filter dropdown holds an ARRAY of selected values.
// THE INVARIANT: [] means NO filter (show everything) — never "match nothing".
// Both helpers below return true on an empty selection for exactly that reason,
// and the server mirrors it (an absent/empty param adds no clause at all).
//
// selKey collapses a selection to a primitive so it can be a useMemo/useEffect
// dependency: a filter array is a fresh identity every time it is re-set, so
// depending on the array itself re-runs the effect (a full refetch) even when
// the user lands back on the same set.
// The separator must be a character that cannot occur in a value: with a plain
// join("") the selections ["ab","c"] and ["a","bc"] collapse to the SAME key and
// the effect would skip a refetch it owed.
var selKey = function(arr){ return (arr || []).join("\u0001"); };

// Id membership (agents, developers, …) — exact string compare, no folding.
var selHasId = function(arr, id){
  if (!arr || !arr.length) return true;
  var v = String(id == null ? "" : id);
  for (var i = 0; i < arr.length; i++) if (String(arr[i]) === v) return true;
  return false;
};

// Free-text name membership (project, location, campaign, …). Compares through
// the SAME fold the server keys its option values by, so one option matches
// every stored spelling. "__none" is the sentinel for "no value".
// normFn defaults to normProjectFE (case + whitespace only). Campaign MUST pass
// normCampaignFE instead — campaigns additionally merge trailing variant tokens
// ("- Copy", a number, a month), and folding them with the project function
// would leave "Sahel - Copy" unreachable behind the "Sahel" option.
var selMatchName = function(arr, raw, normFn){
  if (!arr || !arr.length) return true;
  var v = String(raw == null ? "" : raw).trim();
  if (v === "") return arr.indexOf("__none") !== -1;
  var k = (normFn || normProjectFE)(v).toLowerCase();
  for (var i = 0; i < arr.length; i++) {
    if (String(arr[i]).toLowerCase() === k) return true;
  }
  return false;
};

// Repeated query params (?location=a&location=b) — NOT comma-joined: project,
// location and campaign values are free text and legitimately contain commas,
// which a comma format would split into two bogus values. Express parses
// repeated keys into an array on the other side. Empty selection → "", so the
// param is omitted entirely and the server applies no clause.
var selQS = function(name, arr){
  return (arr || []).map(function(v){ return "&" + name + "=" + encodeURIComponent(v); }).join("");
};

// "Daily Request" (the DR-mirror lead) and "Daily Request → Lead" are stored
// values the linkage between Lead and DailyRequest depends on, so the data stays
// as it is — but they are never offered as a selectable source. They are the
// registry's `literals` (literalSourceNames()), not a list kept here.

// Source filter dropdown — top-level memoized component. Lifted out of
// LeadsPage so frequent WS-driven parent re-renders (lead_updated etc.)
// don't reconcile the underlying native <select> while the user has it
// open — that reconciliation is what dismissed the OS dropdown and
// produced the "black rectangle for ~1s" flicker (memory: ui_bug_dropdown
// _flicker, 2026-05-05). Style is a top-level const so the style prop
// reference is stable across renders. Caller MUST pass a stable onChange
// (useCallback) and a stable legacyList ref (use the string-hash memo
// pattern at LeadsPage:3008) for memo() to actually skip work.
var SourceFilterSelect = memo(function SourceFilterSelect(props){
  useLeadSourcesVersion();   // memo() would otherwise keep the pre-boot (empty) list
  return <Select value={props.value} onChange={props.onChange} searchable width={180} maxWidth={180}
    options={[{ value:"", label:props.emptyLabel || "📡 All sources" }]
      .concat(leadSourceNames().map(function(s){ return { value:s, label:s }; }))
      .concat(props.legacyList.map(function(s){ return { value:s, label:s + " (legacy)" }; }))}/>;
});

// Reusable searchable single-select (D5). Native <select> has no type-to-filter,
// which is painful for long lists (50+ developers). This is a minimal combobox: a
// trigger button that opens a panel with a filter input + a keyboard-navigable
// option list (ArrowUp/Down to move, Enter to pick, Esc to close); closes on
// outside-click. Props:
//   value       — current selected value ("" = the empty row)
//   onChange    — fn(nextValue)
//   options     — [{ value, label }]  (do NOT include the empty row; pass emptyLabel)
//   emptyLabel  — label for the always-present value="" row ("All developers" / "— None —")
//   placeholder — search input placeholder + trigger text when nothing is selected
//   width / maxWidth / fontSize — optional style overrides
// SearchableSelect panel geometry. SS_PANEL_MAX MUST equal the panel's maxHeight
// below — the flip decision compares available space against it, so if they drift
// the dropdown will flip on the wrong threshold.
var SS_PANEL_MAX = 260;
var SS_GAP = 4;
// The panel is NOT pinned to the trigger's width. Inline pickers in table cells
// are deliberately tiny (the Agent column trigger is 110px), and inheriting that
// truncated every option to "Abdelrahman Y…". The panel instead opens at least
// this wide — or the trigger's width when that is larger — and is capped so it
// can never exceed the viewport. SS_EDGE is the margin kept from either edge.
// 170 is snug: it clears the longest agent name in the book without leaving the
// dead space 220 did. Raise it only if names actually start truncating again.
var SS_PANEL_MIN_W = 170;
var SS_EDGE = 8;
// The panel is portaled to <body>, so it is a SIBLING of every overlay in the
// app rather than a descendant — its z-index has to beat all of them or a
// dropdown opened inside a modal would paint behind it. The ceiling in this
// file is BiometricLockScreen at 99999 (six modals sit at 1000, Modal at 600),
// so this must stay above 99999. Raise it if a higher overlay is ever added.
var SS_PANEL_Z = 100000;

// The in-row status pickers (Leads + Daily Requests). Owner prefers the
// ORIGINAL comfortable density — 14px radius, 9px/12px rows, 13px text, 10px
// dots — so only the border and shadow are shared with the SearchableSelect
// panel; the sizing is deliberately roomier than an option list.
//
// HEIGHT IS VIEWPORT-BASED, NOT A FIXED CAP. A previous pass gave this menu
// maxHeight:SS_PANEL_MAX (260px). Nine statuses at comfortable density run
// ~410px, so the menu was silently cut — the screenshot showed it starting at
// "Meeting Done" with the four statuses above it unreachable. calc(100vh - 32px)
// means all nine are visible on any normal screen, and the list only scrolls
// (header + Cancel staying pinned) when the viewport genuinely cannot fit them.
// Never reintroduce a fixed pixel cap here.
var SS_MENU_MAX_H  = "calc(100vh - 32px)";
var SS_MENU_PANEL  = { background:"#fff", border:"1px solid #E2E8F0", borderRadius:14, boxShadow:"0 8px 24px rgba(0,0,0,0.16)", minWidth:180, maxHeight:SS_MENU_MAX_H, overflow:"hidden", display:"flex", flexDirection:"column", padding:8, boxSizing:"border-box" };
var SS_MENU_HEADER = { flexShrink:0, fontSize:12, fontWeight:600, color:C.textLight, padding:"6px 10px 10px", borderBottom:"1px solid #F1F5F9", marginBottom:4 };
var SS_MENU_ITEM   = { padding:"9px 12px", borderRadius:9, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", gap:10, whiteSpace:"nowrap" };
var SS_MENU_DOT    = { width:10, height:10, borderRadius:"50%", flexShrink:0 };
var SS_MENU_FOOTER = { flexShrink:0, borderTop:"1px solid #F1F5F9", marginTop:4, paddingTop:4 };
var SS_MENU_CANCEL = { width:"100%", padding:"7px", borderRadius:8, border:"none", background:"#F1F5F9", cursor:"pointer", fontSize:12, color:C.textLight, fontFamily:"inherit" };
// Hover tint — identical to the SearchableSelect option rows.
var SS_MENU_HOVER  = "#F1F5F9";
// At or below this many real options, an Inp select renders without a search
// box (status, sale type, quarter, yes/no…). Above it the filter box earns its
// place. Call sites that pass `searchable` always keep the box.
var INP_NOSEARCH_MAX = 6;

// Shared open-direction logic for every absolutely-positioned menu in the app.
// Opens BELOW the trigger by default and flips above ONLY when below cannot fit
// the panel AND above is genuinely roomier — so a menu never covers its own
// trigger, and never runs off the bottom of the viewport either.
// `boxRef` must be on the position:relative wrapper. Re-measures on scroll
// (capture, so scrolling containers count) and resize; resets on close so a
// flip can't persist into the next open.
// Returns { dir, alignRight, boxW }:
//   dir        "down" | "up"  — vertical anchor (see above)
//   alignRight true when a left-anchored panel of SS_PANEL_MIN_W would run off
//              the right edge, so the panel should hang from the trigger's RIGHT
//              edge and grow leftwards instead. Needed because the panel is no
//              longer pinned to the trigger's width: a narrow trigger in a
//              right-hand table column (the Agent cell) would otherwise push a
//              220px panel off-screen.
//   boxW       the trigger's measured width, so the panel can be at least as
//              wide as its trigger and never narrower.
//   rect       the trigger's viewport-space box (top/bottom/left/right), plus
//              vw/vh, so a PORTALED panel can anchor itself with position:fixed
//              instead of position:absolute. null while closed. The two callers
//              that only read `dir` are unaffected — these fields are additive.
var DROP_POS_CLOSED = { dir:"down", alignRight:false, boxW:0, rect:null, vw:0, vh:0 };
// Scroll now DRIVES the panel's position (a fixed panel doesn't move with its
// trigger on its own), so `measure` runs on every scroll frame. Bail out of the
// state update when nothing moved, or a long page would re-render the whole
// dropdown on each of those frames.
function sameDropPos(a, b) {
  if (a.dir !== b.dir || a.alignRight !== b.alignRight || a.boxW !== b.boxW || a.vw !== b.vw || a.vh !== b.vh) return false;
  var x = a.rect, y = b.rect;
  if (!x || !y) return x === y;
  return x.top === y.top && x.bottom === y.bottom && x.left === y.left && x.right === y.right;
}
function useDropDirection(open, boxRef, panelMax) {
  var st = useState(DROP_POS_CLOSED), pos = st[0], setPos = st[1];
  // Layout effect, not effect: the portaled panel cannot render until this has
  // measured (it has no position without a rect), so the measurement has to
  // land before paint or the panel would flash in at the wrong place.
  useLayoutEffect(function(){
    if (!open) { setPos(function(prev){ return prev.rect ? DROP_POS_CLOSED : prev; }); return; }
    var measure = function(){
      var el = boxRef && boxRef.current;
      if (!el || !el.getBoundingClientRect) return;
      var r = el.getBoundingClientRect();
      var vw = window.innerWidth, vh = window.innerHeight;
      var below = vh - r.bottom - SS_GAP;
      var above = r.top - SS_GAP;
      var wantW = Math.max(SS_PANEL_MIN_W, r.width);
      var next = {
        dir: (below < panelMax && above > below) ? "up" : "down",
        // Only flip sides when hanging left would actually overflow AND there is
        // room on the other side — otherwise a narrow viewport would bounce it
        // off the left edge instead.
        alignRight: (r.left + wantW > vw - SS_EDGE) && (r.right - wantW >= SS_EDGE),
        boxW: r.width,
        rect: { top:r.top, bottom:r.bottom, left:r.left, right:r.right },
        vw: vw, vh: vh
      };
      setPos(function(prev){ return sameDropPos(prev, next) ? prev : next; });
    };
    measure();
    window.addEventListener("resize", measure);
    // PASSIVE + CAPTURE. capture:true is load-bearing — the panel is position:
    // fixed and must re-measure when ANY ancestor scroller moves, not just the
    // window, and scroll events do not bubble. passive:true tells the browser
    // this handler will never call preventDefault, so it can composite the
    // scroll without waiting on it; measure() only reads a rect and setPos()es,
    // and it already bails when nothing moved (sameDropPos).
    //
    // The options OBJECT must be passed to removeEventListener too, or the
    // listener is not matched and leaks — the boolean-vs-object form is not
    // interchangeable for removal.
    var scrollOpts = { passive: true, capture: true };
    window.addEventListener("scroll", measure, scrollOpts);
    return function(){
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, scrollOpts);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  return pos;
}
// Turn a direction into the CSS anchor for the panel.
function dropAnchor(dir) {
  return dir === "up"
    ? { bottom:"calc(100% + " + SS_GAP + "px)" }
    : { top:"calc(100% + " + SS_GAP + "px)" };
}
var SearchableSelect = function(props){
  var options = props.options || [];
  var emptyLabel = props.emptyLabel != null ? props.emptyLabel : "";
  var stateOpen = useState(false), open = stateOpen[0], setOpen = stateOpen[1];
  var stateQ = useState(""), q = stateQ[0], setQ = stateQ[1];
  var stateHi = useState(0), hi = stateHi[0], setHi = stateHi[1];
  var boxRef = useRef(null);
  var panelRef = useRef(null);
  var inputRef = useRef(null);
  // scrollToSelected: on open, land the list at the CURRENTLY SELECTED row
  // instead of the top — opening the month picker on "August" used to always
  // show January first, forcing a manual scroll every time. listRef is the
  // scrollable option list; selectedRowRef is set by whichever row currently
  // renders isSel (see the row map below). scrolledRef guards the one-shot so
  // a later re-render (hover highlight, a keystroke) never yanks the list back.
  var listRef = useRef(null);
  var selectedRowRef = useRef(null);
  var scrolledRef = useRef(false);

  // hideEmpty: omit the "" row entirely, for required pickers whose owner
  // guarantees a selection (else the user could clear it to "" and downstream
  // consumers would query on an empty id).
  var disabled = !!props.disabled;
  // multi (2026-08-05): opt-in multi-select. `value` becomes an ARRAY and
  // onChange emits an ARRAY; [] means "no filter". Everything below is gated on
  // this flag so the ~20 single-select call sites (every form picker) run the
  // exact same code they did before — this prop is additive, not a rewrite.
  // The "" row is dropped in multi mode: "all" is expressed by selecting
  // nothing, and the header's Clear button is how you get back there.
  var isMulti = !!props.multi;
  var multiVals = isMulti ? (Array.isArray(props.value) ? props.value.map(String) : []) : [];
  var isPicked = function(v){ return multiVals.indexOf(String(v)) !== -1; };
  var allOpts = ((props.hideEmpty || isMulti) ? [] : [{ value: "", label: emptyLabel }]).concat(options);
  // matchFn: OPT-IN replacement for the default lowercase-substring filter, for
  // a list that has a canonical fold of its own. The developer pickers pass
  // devSearchMatch, so typing "elmasria" finds "El Masria Group" — the same
  // answer POST /api/developers' dedupe gives. Every other call site passes
  // nothing and runs the exact filter it ran before; this prop is additive.
  var matchFn = props.matchFn || function(label, query){
    return String(label||"").toLowerCase().indexOf(String(query||"").toLowerCase()) !== -1;
  };
  var ql = q.trim();
  var filtered = ql ? allOpts.filter(function(o){ return matchFn(String(o.label||""), ql); }) : allOpts;
  // With showCount on, a typed query is asking about the REAL options, so the
  // always-present "" row drops out of an active search. Its label is prose
  // ("— Select developer —") and the developer fold matches it on "dev", which
  // would leave a row on screen under a "0 of 505 developers match" count and
  // suppress the "No matches" line. Gated on showCount so no other picker's
  // filtering changes.
  if (props.showCount && ql) filtered = filtered.filter(function(o){ return o.value !== ""; });
  // allowCustom: opt-in free-text mode. The known-values list stays a normal
  // searchable dropdown, but whatever the user types can also be committed
  // verbatim — needed for fields like Project where the canonical list is
  // derived from existing data and a genuinely new development must still be
  // enterable. The custom row is offered only when the typed text doesn't
  // already match an option exactly (case-insensitive), so it never shadows a
  // real choice. Rendered as the FIRST row so Enter commits it by default.
  // allowCustom is single-select only — it exists for form fields (type a brand
  // new development) and has no meaning for a filter over values that exist.
  var customText = q.trim();
  var hasExact = customText && allOpts.some(function(o){ return String(o.label||"").toLowerCase() === customText.toLowerCase(); });
  var showCustom = !!(props.allowCustom && !isMulti && customText && !hasExact);
  if (showCustom) filtered = [{ value: customText, label: '➕ Use "' + customText + '"', __custom: true }].concat(filtered);
  // Object-safe value: a caller may pass a populated ref ({_id,...}) instead of
  // an id string. Coerce to _id so it matches an option AND triggerLabel never
  // ends up a raw object — React refuses to render one as a child, blanking the
  // page. Hardens every searchable dropdown, not just the one that surfaced it.
  var rawVal = (isMulti || (props.value && typeof props.value === "object")) ? ((props.value && props.value._id) || "") : props.value;
  var selected = isMulti ? null : allOpts.filter(function(o){ return String(o.value)===String(rawVal||""); })[0];
  // Multi trigger: nothing → the "all" label; one → that option's own label (so
  // a single pick reads exactly like the old single-select); several → a count.
  // Chips were rejected here: these dropdowns sit in a 200px filter bar and a
  // chip row reflows the whole bar as you tick. The full list is in `title`.
  var multiLabels = !isMulti ? [] : multiVals.map(function(v){
    var hit = allOpts.filter(function(o){ return String(o.value)===String(v); })[0];
    return hit ? String(hit.label) : selectTriggerLabel(null, v, props.unknownLabel);
  });
  // Nothing selected falls back to emptyLabel, NOT to "": single-select gets
  // that label from the "" option row, which multi mode doesn't render, so
  // without this an unfiltered dropdown would read "Search location…" (the
  // placeholder) where it used to read "📍 All locations".
  var triggerLabel = isMulti
    ? (multiLabels.length === 0 ? emptyLabel : multiLabels.length === 1 ? multiLabels[0] : (multiLabels.length + " selected"))
    : selectTriggerLabel(selected, rawVal, props.unknownLabel);

  useEffect(function(){
    if (!open) return;
    // The panel is portaled to <body>, so it is NOT inside boxRef any more —
    // without the panelRef arm every click on an option would read as "outside"
    // and close the dropdown, which would break multi-select (it deliberately
    // stays open across ticks) and the Select all / Clear buttons.
    function onDown(e){
      var inTrigger = boxRef.current && boxRef.current.contains(e.target);
      var inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) { setOpen(false); setQ(""); }
    }
    document.addEventListener("mousedown", onDown);
    return function(){ document.removeEventListener("mousedown", onDown); };
  }, [open]);
  useEffect(function(){ if (open) { setHi(0); if (inputRef.current) { try { inputRef.current.focus(); } catch(e){} } } }, [open]);
  useEffect(function(){ setHi(function(h){ return Math.min(h, Math.max(0, filtered.length-1)); }); }, [ql, filtered.length]);

  // Single: commit and close. Multi: toggle and STAY open (with the search text
  // intact) so several values can be ticked in one visit — closing after each
  // pick is what makes most multi-selects miserable to use.
  function choose(o){
    if (isMulti) {
      var v = String(o.value);
      var next = isPicked(v)
        ? multiVals.filter(function(x){ return x !== v; })
        : multiVals.concat([v]);
      props.onChange(next);
      return;
    }
    props.onChange(o.value); setOpen(false); setQ("");
  }
  // Select all obeys the search box — it adds the CURRENTLY VISIBLE options, so
  // "sahel" + Select all picks the Sahel family and nothing else.
  function selectAllVisible(){
    var next = multiVals.slice();
    filtered.forEach(function(o){ var v=String(o.value); if (next.indexOf(v) === -1) next.push(v); });
    props.onChange(next);
  }
  function clearAll(){ props.onChange([]); }
  function onKey(e){
    if (e.key === "ArrowDown"){ e.preventDefault(); setHi(function(h){ return Math.min(h+1, filtered.length-1); }); }
    else if (e.key === "ArrowUp"){ e.preventDefault(); setHi(function(h){ return Math.max(h-1, 0); }); }
    else if (e.key === "Enter"){ e.preventDefault(); if (filtered[hi]) choose(filtered[hi]); }
    else if (e.key === "Escape"){ e.preventDefault(); setOpen(false); setQ(""); }
  }

  var width = props.width || "100%";
  var maxWidth = props.maxWidth || 260;
  var fontSize = props.fontSize || 12;

  // Open BELOW by default; flip above ONLY when there genuinely isn't room.
  // The panel used to be pinned at top:"calc(100% + 4px)" unconditionally, so a
  // trigger near the viewport bottom (a filter bar low on the page, a picker in
  // a lower table row) pushed its list off-screen with no way to reach the
  // options. Now shared with every other menu via useDropDirection.
  // SS_PANEL_MAX must track the panel's maxHeight below.
  var dropPos = useDropDirection(open && !disabled, boxRef, SS_PANEL_MAX);
  // Fires once the panel has actually measured and mounted (dropPos.rect goes
  // null -> set — see useDropDirection above), not on the raw `open` flip, so
  // the row refs below are guaranteed attached.
  useEffect(function(){
    if (!open) { scrolledRef.current = false; return; }
    if (scrolledRef.current) return;
    var container = listRef.current, row = selectedRowRef.current;
    if (!container || !row) return;
    scrolledRef.current = true;
    container.scrollTop = Math.max(0, row.offsetTop - (container.clientHeight - row.clientHeight) / 2);
  }, [open, !!dropPos.rect]);
  // Vertical anchor + horizontal side + width. The panel deliberately does NOT
  // inherit the trigger's width (see SS_PANEL_MIN_W): a 110px Agent-column
  // trigger would otherwise ellipsise every name. It hangs from the trigger's
  // left edge normally, and from the right edge when hanging left would run
  // off-screen, so right-hand table columns stay fully visible.
  //
  // FIXED + PORTALED, NOT ABSOLUTE — DO NOT REVERT TO position:absolute.
  // An absolutely-positioned panel is clipped by ANY ancestor that establishes
  // a scroll/clip box, and this app wraps nearly every surface in one: the
  // Leads table sits in `Card{overflow:hidden}` > `div{overflowX:auto}` (and
  // overflow-x:auto forces overflow-y to auto, so it clips vertically too), and
  // Modal's inner is `maxHeight:90vh; overflowY:auto`. Reported 2026-08-13: on
  // the Leads page the Agent picker rendered as a bare search box hanging over
  // the table with every option row cut off at the card's edge, and the Split
  // Deal modal cut the list mid-row. It looked like a detached input; it was
  // the panel being sliced by its container.
  // The geometry below reproduces the old anchors exactly (top/bottom
  // `calc(100% + SS_GAP)`, left/right 0) in viewport space instead of relative
  // to the trigger's wrapper. useDropDirection re-measures on scroll and
  // resize, so the panel stays glued to its trigger.
  var anchorRect = dropPos.rect;
  var panelPos = !anchorRect ? null : Object.assign(
    { position:"fixed", zIndex:SS_PANEL_Z },
    dropPos.dir === "up"
      ? { bottom: dropPos.vh - anchorRect.top + SS_GAP }
      : { top: anchorRect.bottom + SS_GAP },
    dropPos.alignRight
      ? { right: dropPos.vw - anchorRect.right }
      : { left: anchorRect.left },
    { minWidth: Math.max(SS_PANEL_MIN_W, dropPos.boxW || 0),
      maxWidth: "calc(100vw - " + (SS_EDGE * 2) + "px)" });

  return <div ref={boxRef} style={{ position:"relative", width:width, maxWidth:maxWidth, boxSizing:"border-box" }}>
    <button type="button" disabled={disabled} title={props.title} onClick={function(){ if (disabled) return; setOpen(function(o){ return !o; }); }}
      style={{ width:"100%", textAlign:"start", padding:"6px 8px", borderRadius:8, border:"1px solid "+C.border, background:(disabled?"#F8FAFC":"#fff"), fontSize:fontSize, cursor:(disabled?"not-allowed":"pointer"), opacity:(disabled?0.7:1), boxSizing:"border-box", display:"flex", justifyContent:"space-between", alignItems:"center", gap:6, color:(triggerLabel?C.text:C.textLight), fontFamily:"inherit" }}>
      <span title={isMulti && multiLabels.length > 1 ? multiLabels.join(", ") : undefined}
        style={Object.assign({ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }, props.triggerStyle || {})}>{triggerLabel || props.placeholder || "Select…"}</span>
      <span style={{ color:C.textLight, flexShrink:0 }}>▾</span>
    </button>
    {/* Portaled to <body> so no ancestor overflow can clip it (see panelPos).
        `data-overlay-above` is load-bearing: the side panels (lead / EOI / deal
        / DR) close themselves via useOutsideClose, which treats a click as
        "inside" only if it lands in the panel or in an element tagged with this
        attribute. Those side panels host an Agent picker, so without the tag,
        picking an agent would close the whole panel out from under the user.
        React routes events through the REACT tree, not the DOM tree, so the
        <td>'s and Modal's stopPropagation handlers still see these clicks —
        row-click and click-outside-to-close behave exactly as before. */}
    {open && !disabled && panelPos && createPortal(<div ref={panelRef} className="crm-ss-panel" data-overlay-above="true"
      style={Object.assign({ background:"#fff", border:"1px solid #E2E8F0", borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,0.16)", maxHeight:SS_PANEL_MAX, overflow:"hidden", display:"flex", flexDirection:"column" }, panelPos)}>
      {/* noSearch: short fixed lists (a 4-option sort picker) don't need a filter
          box, and on mobile the auto-focus would pop the keyboard for nothing.
          The option list below is unchanged; only the search row is omitted. */}
      {!props.noSearch && <input ref={inputRef} value={q} onChange={function(e){ setQ(e.target.value); }} onKeyDown={onKey}
        placeholder={props.placeholder || "Search…"}
        style={{ margin:8, padding:"6px 8px", borderRadius:8, border:"1px solid "+C.border, fontSize:fontSize, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}/>}
      {/* showCount: OPT-IN live match count, for a list long enough that "is it
          in here?" is the actual question — the 505 developers. Counts REAL
          options only: the always-present "" row and the allowCustom row are
          affordances, not matches, and counting them would report 1 match on a
          list that matched nothing. Opt-in so no short picker grows a line of
          chrome it does not need. */}
      {!!props.showCount && !props.noSearch && (function(){
        var noun = props.countNoun || "options";
        var shown = filtered.filter(function(o){ return o.value !== "" && !o.__custom; }).length;
        return <div style={{ flexShrink:0, padding:"0 10px 6px", fontSize:Math.max(9,fontSize-2), color:(ql && shown===0) ? C.danger : C.textLight, fontWeight:(ql?600:400) }}>
          {ql ? (shown + " of " + options.length + " " + noun + " match") : (options.length + " " + noun)}
        </div>;
      })()}
      {/* Multi header — mirrors the Leaderboard "Include managers" panel, the
          multi-select pattern already shipped in this app. Select all follows
          the search box; Clear is how you get back to "all values".
          TWO ROWS (2026-08-08): this was one flex row — label, then the two
          buttons — and in a ~200px panel the label ("📣 All campaigns",
          "🏢 All projects") truncated and ran into the buttons. The label now
          owns a full-width row and the buttons share the next one, so nothing
          collides at any panel width and the buttons stop being cramped. */}
      {isMulti && <div style={{ flexShrink:0, padding:"0 8px 8px", borderBottom:"1px solid #F1F5F9" }}>
        <div style={{ fontSize:Math.max(9,fontSize-2), color:C.textLight, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginBottom:6 }}>
          {multiVals.length ? multiVals.length + " selected" : emptyLabel}
        </div>
        <div style={{ display:"flex", gap:6 }}>
          <button type="button" onMouseDown={function(e){ e.preventDefault(); selectAllVisible(); }}
            style={{ flex:1, padding:"3px 7px", borderRadius:6, border:"1px solid "+C.border, background:"#fff", color:C.accent, fontSize:Math.max(9,fontSize-2), fontWeight:600, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>Select all</button>
          <button type="button" onMouseDown={function(e){ e.preventDefault(); clearAll(); }}
            style={{ flex:1, padding:"3px 7px", borderRadius:6, border:"1px solid "+C.border, background:"#fff", color:C.textLight, fontSize:Math.max(9,fontSize-2), fontWeight:600, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>Clear</button>
        </div>
      </div>}
      <div ref={listRef} style={{ overflowY:"auto" }}>
        {filtered.length===0 && <div style={{ padding:"8px 12px", fontSize:fontSize, color:C.textLight }}>No matches</div>}
        {(function(){
          // Group headers are presentation only — `hi`/ArrowUp/ArrowDown index into
          // `filtered` (selectable options), so headers can never take focus.
          var lastGroup = null;
          return filtered.map(function(o, idx){
            var isSel = isMulti ? isPicked(o.value) : String(o.value)===String(props.value||"");
            var row = <div key={String(o.value)+"|"+idx} ref={function(el){ if (isSel) selectedRowRef.current = el; }} onMouseEnter={function(){ setHi(idx); }} onMouseDown={function(e){ e.preventDefault(); choose(o); }}
              /* centerOptions: opt-in horizontal centering. The panel is wider
                 than its trigger for inline pickers, which left agent names
                 hugging the left with dead space to the right. Opt-in rather
                 than global — a project/campaign list reads better left-aligned,
                 and multi rows have a checkbox that must stay at the start. */
              style={{ padding:(props.renderRow?"0":"7px 12px"), fontSize:fontSize, cursor:"pointer", background:(idx===hi?"#F1F5F9":"#fff"), color:(o.color||(o.value?C.text:C.textLight)), fontWeight:(isSel?700:(o.color?600:400)), whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", display:(isMulti?"flex":"block"), alignItems:"center", gap:8, textAlign:((props.centerOptions && !isMulti) ? "center" : "start"), borderTop:(o.__divider?"1px solid #E8ECF1":"none") }}>
              {/* Styled box rather than a real <input type=checkbox>: the row
                  commits on onMouseDown+preventDefault (so the panel never loses
                  focus), which would fight a native checkbox's own click/change. */}
              {isMulti && <span style={{ flexShrink:0, width:13, height:13, borderRadius:3, boxSizing:"border-box",
                border:"1px solid "+(isSel?C.accent:C.border), background:(isSel?C.accent:"#fff"),
                color:"#fff", fontSize:10, lineHeight:"11px", textAlign:"center", fontWeight:700 }}>{isSel?"✓":""}</span>}
              {props.renderRow
                ? props.renderRow(o, { selected:isSel, active:idx===hi })
                : <span style={{ minWidth:0, overflow:"hidden", textOverflow:"ellipsis" }}>{o.label || (o.value || "")}</span>}
            </div>;
            var g = o.group || null;
            var needsHeader = g && g !== lastGroup;
            lastGroup = g;
            if (!needsHeader) return row;
            return <Fragment key={"g|"+g+"|"+idx}>
              <div style={{ padding:"6px 12px 3px", fontSize:Math.max(9,fontSize-2), fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.4, background:"#F8FAFC" }}>{g}</div>
              {row}
            </Fragment>;
          });
        })()}
      </div>
    </div>, document.body)}
  </div>;
};

// Build the dropdown options for a Lead source <select>. Always prepends a
// disabled-feeling "Select source..." placeholder so the empty value is a
// forced choice (caller validates before submit). If the current value is
// non-empty but not in the source registry (leadSourceNames()) — a stored
// value from before the registry — surface it as a
// "(legacy)" option at the top so editing doesn't silently rewrite it.
// The registry's literals (literalSourceNames()) are intentionally excluded — DR mirrors
// keep their stored source value (no overwrite on save) but no UI option
// appears for them.
var buildSourceOptions = function(currentValue) {
  var opts = [{ value: "", label: "Select source..." }];
  if (currentValue
      && leadSourceNames().indexOf(currentValue) === -1
      && literalSourceNames().indexOf(currentValue) === -1) {
    opts.push({ value: currentValue, label: currentValue + " (legacy)" });
  }
  return opts.concat(leadSourceNames().map(function(x){ return { value: x, label: x }; }));
};
var PROP_TYPES = ["Apartment","Duplex","Townhouse","Twinhouse","Standalone","Commercial","Admin","Clinic","Service Apartment","Chalet"];

// Sort-picker options. These were inline <option> lists inside native <select>s,
// whose popups the OS renders over their own trigger (see the call sites). Now
// fed to SearchableSelect, which opens below and only flips when there is no
// room. Leads offers four sorts; Daily Requests only ever offered two — kept
// exactly as they were so neither picker gains or loses a choice.
var SORT_OPTIONS = [
  { value:"lastActivity", label:"⏱ Last Activity" },
  { value:"newest",       label:"🆕 Newest" },
  { value:"oldest",       label:"📅 Oldest" },
  { value:"name",         label:"🔤 Name" }
];
// Sales sees the same four slots, but the two date sorts mean HIS clock rather
// than the system's: "Newest to me" is the lead that reached him most recently
// (assignments[].assignedAt on his own slice), not the most recently created
// customer. Owner ruling 2026-08-20 — a sales agent's list is relative to when
// he received the lead; admin and operation keep system time because they
// manage total inventory.
//
// FOUR OPTIONS, NOT SIX. Offering `newest` AND `received` side by side would ask
// an agent to distinguish "newest" from "newest to me" in a dropdown, and the
// wrong pick is silent — the list just looks subtly wrong. The two system-time
// sorts are simply not offered to him; nothing he can select maps to createdAt.
var SALES_SORT_OPTIONS = [
  { value:"lastActivity",   label:"⏱ Last Activity" },
  { value:"received",       label:"🆕 Newest to me" },
  { value:"receivedOldest", label:"📅 Longest with me" },
  { value:"name",           label:"🔤 Name" }
];
var DR_SORT_OPTIONS = [
  { value:"lastActivity", label:"⏱ Last Activity" },
  { value:"newest",       label:"🆕 Newest" }
];


// ===== AVATAR COLORS =====
var AVATAR_COLORS = ["#6366F1","#EC4899","#F59E0B","#10B981","#3B82F6","#8B5CF6","#EF4444","#14B8A6","#F97316","#06B6D4"];
var avatarColor = function(name){ var i=0; if(name)for(var j=0;j<name.length;j++)i+=name.charCodeAt(j); return AVATAR_COLORS[i%AVATAR_COLORS.length]; };
var Avatar = function(p){
  var size=p.size||36;
  // `flat` makes the avatar match the sidebar footer style: rgba(255,255,255,0.1) bg, 10px radius, 11px weight-700 text.
  var flat = !!p.flat;
  var bg = flat ? "rgba(255,255,255,0.1)" : avatarColor(p.name);
  var radius = flat ? 10 : (p.round?"50%":Math.round(size*0.28));
  var fontSize = flat ? 11 : Math.round(size*0.38);
  return <div style={{ width:size, height:size, borderRadius:radius, background:bg, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:fontSize, flexShrink:0, position:"relative" }}>{p.name?(p.name[0]+( p.name.split(" ")[1]?p.name.split(" ")[1][0]:"")).toUpperCase():""}{p.online!==undefined&&<span style={{ position:"absolute", bottom:1, right:1, width:Math.round(size*0.28), height:Math.round(size*0.28), borderRadius:"50%", background:p.online?"#22C55E":"#94A3B8", border:"2px solid "+(flat?"rgba(28,30,40,0.95)":"#fff") }}/>}</div>;
};
var gid = function(o) { if(!o) return null; return String(o._id || o.id || ""); };
// A ROW's own identity, as opposed to the lead's. A record-backed row carries
// _rowKey (the record's _id); a plain lead row falls back to gid. Use this for
// React keys and for "is this row selected" — gid alone makes two transactions
// on one client collide as the same key AND select each other, because they
// share the lead's _id by design (every action on these pages still targets
// /api/leads/:id, which is why _id stays the lead's).
var rowKey = function(o) { return (o && o._rowKey) ? String(o._rowKey) : gid(o); };

// ===== ONE RESOLVER PER ENTITY — reference → display name (owner ruling 2026-09-11) =====
//
// EOI #50058's Edit modal showed its agent as "6a40d99b…" while the row beside
// it showed "Jana Mohamed". Two causes, one class: the modal read a DIFFERENT
// field than the row (the lead's live holder, not the record's author), and the
// picker it fed painted the bound value verbatim when no option matched it. The
// owner ruled it system-wide: one source per record, one resolver per entity,
// and NO render path may output a 24-hex ObjectId string.
//
// So every reference is resolved HERE. Populated `.name` first, then a lookup in
// the list the caller holds, then "" — never the id. Call sites keep their own
// empty-answer wording ("—", "-", "Unknown"); what they may not do is fall
// through to the id. verify-reference-render.js runs these over every shape a
// reference can take, renders the picker over an unmatched id, and fails the
// build if an ObjectId ever comes back.
var OBJECT_ID_RE = /^[0-9a-f]{24}$/i;
var looksLikeObjectId = function(v){ return OBJECT_ID_RE.test(String(v == null ? "" : v)); };
// The id string behind a reference — a populated object, a bare id, or nothing.
var refId = function(ref){ if (!ref) return ""; return typeof ref === "object" ? String(ref._id || ref.id || "") : String(ref); };
var refNameOf = function(ref, list){
  if (!ref) return "";
  if (typeof ref === "object" && ref.name) return String(ref.name);
  var id = refId(ref);
  if (!id) return "";
  var hit = (list || []).find(function(x){ return x && gid(x) === id; });
  return (hit && hit.name) ? String(hit.name) : "";
};
// One name per entity. Thin on purpose: grep the entity and you have every site
// that resolves it.
var userNameOf           = function(ref, users)      { return refNameOf(ref, users); };
var developerNameOf      = function(ref, developers) { return refNameOf(ref, developers); };
var closingCompanyNameOf = function(ref, companies)  { return refNameOf(ref, companies); };
var brokerNameOf         = function(ref, brokers)    { return refNameOf(ref, brokers); };
// THE AGENT A TRANSACTION ROW SHOWS — the same answer on the list row, the panel
// and the Edit modal. Per-action attribution: the record's own author
// (eoiAgentId / dealAgentId), a legacy free-text agent on a deal, then for a
// pre-anchor row the holder at the record's date (agentAtTime), else the live
// holder. "" when nothing resolves — never the id. This is what the Edit modal
// reads on a record-backed row instead of the lead's live agentId, which is a
// different person on every rotated lead (#50058: Rokaya, inactive, vs Jana).
var txnAgentNameOf = function(row, users){
  if (!row) return "";
  if (row.legacyAgentName) return String(row.legacyAgentName) + " (legacy)";
  var isDeal = row._txnKind === "deal" || row.status === "DoneDeal" || row.globalStatus === "donedeal";
  var anchored = userNameOf(isDeal ? row.dealAgentId : row.eoiAgentId, users);
  if (anchored) return anchored;
  var live = userNameOf(row.agentId, users);
  var at = agentAtTime(row, live);
  return (!at || at === "-") ? "" : String(at);
};
// WHAT A PICKER SHOWS FOR ITS BOUND VALUE. A matched option shows its label; an
// unmatched plain value (allowCustom text such as a project name) shows itself;
// an unmatched ObjectId shows unknownLabel — NEVER the id. This is the chokepoint
// every select in the app renders through (SearchableSelect ← Select ← Inp
// type="select"), so an id whose option list does not contain it — an inactive
// agent, a deleted broker, a list that has not loaded yet — reads as "Unknown"
// instead of as 24 hex characters.
var selectTriggerLabel = function(selected, rawVal, unknownLabel){
  if (selected) return selected.label;
  var v = (rawVal == null) ? "" : String(rawVal);
  if (!v) return "";
  return looksLikeObjectId(v) ? (unknownLabel || "Unknown") : v;
};
// Pin the CURRENT value into an option list that does not contain it, labelled
// by the shared resolver, so an edit form shows the same name the row showed
// (an inactive agent is not in salesUsers; a deleted broker is in no list).
var withCurrentOption = function(options, value, label){
  var v = refId(value);
  if (!v) return options || [];
  if ((options || []).some(function(o){ return o && String(o.value) === v; })) return options;
  return [{ value: v, label: label || "Unknown", __current: true }].concat(options || []);
};
// A DATE INPUT NEEDS A DATE. eoiDate / dealDate are date-only strings by law
// (CLAUDE.md), but two StatusModal writers stamped `new Date().toISOString()`
// into eoiDate, and <input type="date"> renders a datetime as BLANK — EOI
// #50058's Edit modal showed no date while the list row (fmtDayOnly, UTC) showed
// 09/09/2026. Same instant, same UTC day, so the seed takes the UTC date.
var toDateInputValue = function(v){
  if (v == null || v === "") return "";
  var str = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) return str.slice(0, 10);
  return str;
};
// ===== A RECORD-BACKED ROW NEVER READS THE LEAD'S LIFECYCLE (owner ruling 2026-09-12) =====
// Both panels hydrate by laying the ROW over the FULL lead — right for what the
// CLIENT owns (email, source, campaign, phone2, the rotation history), and the
// only door through which the lead's stage can reach a record-backed row: the
// list row never carries globalStatus (EOI_LIST_FIELDS omits it), so the lead's
// slid into the hole and isConvertedRow read "donedeal" on a Pending EOI. EOI
// #50058 lost its Approve / Cancel buttons to that three times, once per
// surface that was fixed. The rule is enforced HERE, once, instead: on a
// record-backed row every lifecycle key is DELETED from the lead's copy and
// restored from the row's own — which rowFromTxnRecord wrote from the record's
// status — so no surface downstream can reach the lead's. The developer pair
// (owner ruling 2026-09-11) follows the same rule through the same door.
// Mirrors LEAD_LIFECYCLE_KEYS in rowFromTxnRecord (server.js); the gate
// verify-txn-status-home.js runs both lists against each other and renders
// every status-dependent surface over the result.
var TXN_LEAD_LIFECYCLE_KEYS = ["status", "globalStatus", "derivedStatus", "preEoiStatus", "preDealStatus",
                               "eoiStatus", "eoiApproved", "dealStatus", "dealApproved"];
var TXN_RECORD_OWNED_KEYS = TXN_LEAD_LIFECYCLE_KEYS.concat(["developerId", "developerPending"]);
// TWO ORIENTATIONS, ONE RULE. Default (hydration): lead first, ROW second —
// everything the row already knew about its own transaction outranks the
// lead's current values, and on a record-backed row the lead's copy of every
// record-owned key (lifecycle + developer pair) is dropped and restored from
// the row. `opts.leadWins` (a SAVE): the response of PUT /api/leads/:id lays
// over the row, because the edit just changed the client's fields and, on a
// record-backed row, carried txnId so mirrorEditToRecord wrote the same
// mirrored values (project, budget, the developer pair — TXN_EDITABLE_*) onto
// the record. What an edit NEVER mirrors is the lifecycle: approval is its own
// transition. So in leadWins mode the row's lifecycle keys still win, and a
// Pending EOI edited on a DoneDeal client stays Pending on screen — the third
// door (owner, 2026-09-12). Measured before choosing: 0 of 60 live records
// carry no developerId while their lead does, and the writer guarantees one
// at birth, so a mirrored save cannot fill a hole the row left.
var hydrateTxnRow = function(full, row, opts){
  var leadWins = !!(opts && opts.leadWins);
  var merged = leadWins ? Object.assign({}, row || {}, full || {}) : Object.assign({}, full || {}, row || {});
  if (!row || !row._fromRecord) return merged;
  (leadWins ? TXN_LEAD_LIFECYCLE_KEYS : TXN_RECORD_OWNED_KEYS).forEach(function(k){
    delete merged[k];
    if (k in row) merged[k] = row[k];   // absent stays absent (2c) — never the lead's
  });
  return merged;
};
// ===== THE ORDER OF A TRANSACTION LIST IS THE RECORD'S OWN DATE (owner ruling 2026-09-12) =====
// Both pages, both tabs, every role: eoiDate / dealDate descending, then the
// record's createdAt descending. NEVER updatedAt, approvedAt, restoredAt or any
// housekeeping instant — restoring #50058 bumped its updatedAt and lifted a
// 09/09 EOI above one recorded on 11/09, which is what the old book sort
// (byDatesDesc(["updatedAt","createdAt"])) and the deals page's getDealDate
// fallback (…→ updatedAt) both did. A record-backed row's "created" is the
// RECORD's createdAt (_recordedAt); row.createdAt is the client's. A branch-B
// lead row has only the lead's. Module scope so verify-txn-status-home.js §4
// runs it over the real rows.
var TXN_ROW_DATE_KEY = { eoi: "eoiDate", deal: "dealDate" };
var txnRowDateMs = function(row, kind){
  var v = row && row[TXN_ROW_DATE_KEY[kind] || "eoiDate"];
  var t = v ? new Date(v).getTime() : NaN;   // "YYYY-MM-DD" parses as UTC midnight; a Date passes through
  return isNaN(t) ? 0 : t;
};
var txnRowRecordedMs = function(row){
  var v = row && (row._fromRecord ? row._recordedAt : row.createdAt);
  var t = v ? new Date(v).getTime() : NaN;
  return isNaN(t) ? 0 : t;
};
var txnRowOrder = function(kind){
  return function(a, b){
    var d = txnRowDateMs(b, kind) - txnRowDateMs(a, kind);
    if (d) return d;
    var c = txnRowRecordedMs(b) - txnRowRecordedMs(a);
    if (c) return c;
    return String(rowKey(b)).localeCompare(String(rowKey(a)));   // stable, identity-only tiebreak
  };
};
// Is this row a CLOSED transaction? One ending counts now — cancelled. It briefly
// took two: a converted EOI was a row in its own right, and leaving it out here is
// how a converted EOI's edit control took the LIVE path and offered to write
// status:"EOI" onto a closed DoneDeal lead. A conversion deletes that record
// (2026-08-28), so there is no converted row left to classify. Mirrors
// TXN_CLOSED_STATUSES on the server, which made the same move in the same commit.
var isHistoricalRow = function(o) { return !!(o && o._fromRecord && o._txnStatus === "Cancelled"); };

// ===== A NOTIFICATION HANDS OVER A LEAD, NOT A ROW =========================
// EVERY notification door into the EOI / Deals pages resolves its target through
// a LEAD endpoint, so what reaches nav() is a lead document carrying neither
// _rowKey nor _fromRecord:
//   ⏳ pending-approvals bell   GET /api/leads/pending-approvals   (a Lead + recordNo)
//   🏗️ pending-developers bell  GET /api/leads/with-pending-developer (a Lead)
//   📲 native push tap          GET /api/leads/:id                 (a Lead)
// Both pages key their rows by rowKey() — the RECORD's _id — so a lead-shaped
// shell matches NO rendered row, and the two halves of each page then fail
// DIFFERENTLY, which is why one defect was reported as two:
//
//   DESKTOP  the panel is a <tr> rendered under the selected row, gated on
//            `isSel` (rowKey(selected) === rowKey(row)). Nothing matches, so the
//            panel never mounts and never scrolls — the click just lands on the
//            page. (EOIPage § `isSel && !p.isMobile && hasRenderableEOI`,
//            DealsPage § `isSel && !p.isMobile && hasRenderableDeal`.)
//   MOBILE   the panel is a fixed overlay gated only on hasRenderableEOI /
//            hasRenderableDeal, so it DOES open — but the hydration effect builds
//            the artifact owner key as `_fromRecord ? _rowKey : null`, so a shell
//            asks for `lead:<leadId>` where the files live under
//            `eoi:<recordId>` / `deal:<recordId>`. The artifacts collection
//            answers nothing and the documents are missing.
//
// Opening the same record from its own table works because THOSE rows come from
// /api/eois / /api/deals through rowFromTxnRecord, which stamps both fields.
//
// The fix is the shape the Commissions “View Deal” cue already uses
// (selectedDealLeadId): resolve the id against the destination page's OWN book
// and land on the row that book holds. Resolution order:
//   1. _recordNo — the approvals bell sends the action's code, and a code names
//      exactly ONE action (ID RULE 1). It is the only EXACT answer available.
//   2. lead id — all the other doors have only that, and a client may hold
//      several rows. Prefer a LIVE one, so someone who bought, cancelled and
//      bought again lands on the live purchase rather than the dead one.
// Returns null when the book holds no row for this shell; callers FAIL SOFT and
// open the shell, so a click still navigates rather than doing nothing.
//
// `isCancelled` is passed in rather than chosen here: the two pages spell it
// differently (isEoiCancelledRow / isDealCancelledRow) and a third spelling
// inside this function would be a copy that can drift from both.
// verify-deeplink-consumers.js §9 EXECUTES this against a two-row book.
var resolveNotifRow = function(rows, shell, isCancelled) {
  if (!shell || !Array.isArray(rows) || !rows.length) return null;
  if (shell._fromRecord) return null;              // already a row — nothing to resolve
  var rn = (shell.recordNo != null) ? shell.recordNo : shell._recordNo;
  if (rn != null && String(rn) !== "") {
    var byNo = rows.find(function(r){
      return r && r._recordNo != null && String(r._recordNo) === String(rn);
    });
    if (byNo) return byNo;
  }
  var lid = gid(shell);
  if (!lid) return null;
  var mine = rows.filter(function(r){ return gid(r) === lid; });
  if (!mine.length) return null;
  if (typeof isCancelled !== "function") return mine[0];
  var live = mine.find(function(r){ return !isCancelled(r); });
  return live || mine[0];
};

// ===== THE EOI ROW LIFECYCLE — MODULE SCOPE SO A GATE CAN RUN IT =====
// These four decided, inside EOIPage's render body, whether a row was still
// actionable. That is precisely the shape CLAUDE.md says cannot be checked: a
// build gate cannot reach into a closure, so the only available "test" is a
// regex over the component's source, which passes while the function is wrong
// and breaks the moment it moves. They are pure functions of one row, so there
// was never a reason for them to live in there.
// verify-converted-eoi.js EXECUTES all four, against both the correct writer and
// the old one, and fails if the old one stops being detected.

// A cancelled EOI (EOI Cancelled / Deal Cancelled). Once cancelled the old
// Pending/Approved approval state is irrelevant — the row badge shows
// "Cancelled" and the journey strip is hidden. A legacy row carrying both
// eoiStatus="Approved" and status="Deal Cancelled" is cancelled.
var isEoiCancelledRow = function(l){ return !!l && (l.eoiStatus==="EOI Cancelled" || l.eoiStatus==="Deal Cancelled" || l.status==="Deal Cancelled"); };
// LEAD-level "this became a deal". It is the branch-B answer — a lead carrying no
// record — and it was BLIND BY CONSTRUCTION to a record-backed converted row:
// rowFromTxnRecord pins row.status to "EOI" and globalStatus is not in
// EOI_LIST_FIELDS, so #50039 reached Active with a live Convert button while this
// exact test was on screen claiming to prevent it. That blind spot went with the
// row that occupied it; branch B is still real, so this still runs.
var isConvertedDeal = function(l){ return !!(l && (l.status==="DoneDeal" || l.globalStatus==="donedeal")); };
// "This EOI is finished because it became a deal". It used to have two spellings
// — the row's own _txnStatus and the lead's status — and needed both, because a
// converted EOI was a record-backed row that answered false to the lead-level
// test. There is no such row any more, so the lead-level test is the whole answer
// and this name survives as the one every caller already reads.
var isConvertedRow = function(l){ return isConvertedDeal(l); };
// Only offer the click where it can actually succeed — mirrors the hard guards
// at the top of POST /eoi-to-deal. eoiApproved stays true on a converted row (it
// WAS approved), so isConvertedRow is what excludes it, not the badge flag.
// This is the affordance; the server's 409 already_converted is the enforcement.
var isConvertibleEoi = function(l){ return !!(l && l.eoiApproved) && !isEoiCancelledRow(l) && !isConvertedRow(l); };
// THE ONE PLACE A ROW IS FILED. Returns the EOI-page chip a row belongs to, or
// "none" — which is a real answer, not a failure. A converted transaction belongs
// to NO chip on this page: it became a deal and moved to the Deals page (owner
// ruling 2026-08-24). Reachable only as a branch-B LEAD now, the record-backed
// form having been deleted with its record, and the server withholds those too —
// so "none" is the second line of defence, not the first.
// Cancelled is tested BEFORE converted: a row that is somehow both is a failed
// transaction and still belongs in Cancelled.
// R-T7 REVISED — a cancelled row files into its own chip again. "none" stays a
// REAL ANSWER for the one state that has no home here: a CONVERTED row, which is
// a deal and lives on the Deals page. Nothing renders a "none" row.
var eoiRowBucket = function(l){
  if (isEoiCancelledRow(l)) return "cancelled";
  if (isConvertedRow(l))    return "none";
  return "active";
};
// WHICH ACTIONS THE ROW'S LIFECYCLE PERMITS — the other half of "where does this
// row belong". Role is a SEPARATE axis and is applied at the call site
// (isOnlyAdmin / isEoiAuthor); this answers only "is this action meaningful on a
// row in this state at all".
//
// Every EOI-page control derives from this, so verify-converted-eoi.js walks the
// full lifecycle through the SAME function the buttons render from. A gate that
// re-listed the expected buttons would be a model of the UI, and a model of the
// guards is not the guards — that is how the record layer shipped tested at
// birth and untested at conversion.
//
//   Pending    approve · cancel · edit · archive          (nothing to convert yet)
//   Approved   convert · cancel · edit · archive          (approve is done)
//   Cancelled  restore · edit · archive                   (R-T7 revised: the row
//              stays on this page. `restore` is owner/admin only and that gate is
//              applied at the CALL SITE, like every other role test here —
//              lifecycle answers "is this action meaningful in this state", never
//              "may this person do it")
//
// There is no Converted row to list. A conversion deletes the EOI record, so the
// only converted thing this page can meet is a branch-B LEAD sitting at DoneDeal,
// which isConvertedRow still files as "no actions".
//
// CANCELLED IS TESTED BEFORE CONVERTED, and the order is load-bearing (2026-09-11).
// isConvertedRow reads the LEAD's word (status / globalStatus) — the panel
// hydration lays the row over the full lead, and a lead whose deal is approved
// carries globalStatus "donedeal". With converted tested first, EOI #50058
// (cancelled, on a lead at DoneDeal) answered [] the moment hydration landed:
// the Restore button vanished while the refusal sentence beside it stayed, since
// that line never asked the lifecycle. The row's OWN cancellation outranks the
// lead's stage — the same precedence eoiRowBucket already keeps. The gate
// RENDERS the button over that exact shape rather than regexing this order.
var eoiLifecycleActions = function(l){
  if (!l) return [];
  // "delete" — owner-only permanent delete (2026-11-02), on every RECORD-backed
  // row whatever its state; a lead-only legacy row has no record to delete. The
  // render gate is canHardDeleteRow(access,row) (src/lib/txnHardDelete.js).
  // Cancelled is tested BEFORE converted, as before: a cancelled EOI on a
  // DoneDeal lead keeps its own Restore (verify-converted-eoi.js pins the order).
  var del = l._fromRecord ? ["delete"] : [];
  if (isEoiCancelledRow(l)) return ["restore", "edit", "archive"].concat(del);
  if (isConvertedRow(l))    return [];
  return (isConvertibleEoi(l) ? ["convert"] : ["approve"]).concat(["cancel", "edit", "archive"]).concat(del);
};
var eoiAllows = function(l, action){ return eoiLifecycleActions(l).indexOf(action) !== -1; };

// WHY A RESTORE IS REFUSED, phrased for a human. Owner ruling 2026-09-11: the
// Restore button is VISIBLE on every cancelled EOI and DISABLED with the reason
// when the server would refuse it — a button that vanishes teaches nothing, and
// one that 409s after the click teaches it too late.
//
// THE DECISION IS NOT HERE. The server sends `_restoreRefusal` as a CODE,
// computed by `restoreEoiRefusal` — the same function the restore route calls,
// on the same two facts. This map only turns a code into a sentence. That split
// is deliberate: a client copy of the RULE would be a second answer that can
// drift, and it would drift toward enabling a button the server refuses
// (CLAUDE.md, no client mirror of a server rule). Pressing it anyway is still
// refused by the server on the server's own facts.
//
// An UNKNOWN code still disables, and says so. Failing closed here costs a
// restore the owner can retry after a refresh; failing open offers a button that
// cannot work, which is the state this ruling exists to remove.
//
// ONE ENTRY (R-T7 revised, owner ruling 2026-09-11): every recordNo is
// independent, so no other record on the client — not an approved deal, not a
// newer EOI — can refuse a restore. The two sentences that said otherwise are
// gone with the two server arms that produced their codes.
var EOI_RESTORE_REASONS = {
  not_cancelled: function(){ return "This EOI is not cancelled — there is nothing to restore."; }
};
// Returns "" when the row is restorable, else the sentence to show.
var eoiRestoreReason = function(row){
  var code = row && row._restoreRefusal;
  if (!code) return "";
  var f = EOI_RESTORE_REASONS[code];
  return f ? f() : ("This EOI cannot be restored (" + String(code) + ").");
};

// THE ONE PLACE A PANEL EDIT IS ROUTED. Every editable control on the EOI and
// Deals panels goes through this, so the rule lives once instead of at each
// call site.
//
//   historical row  -> PATCH /api/records/:rowKey. Record only; the lead is
//                      never touched.
//   live row        -> PUT /api/leads/:id, exactly as before, plus txnId naming
//                      the row so the server mirrors onto THAT record.
//
// txnId is not cosmetic. Without it the server inferred the target from the
// LEAD's status, and on a lead holding two transactions it wrote the wrong one:
// editing #01024's cancelled June deal changed the lead AND mirrored onto the
// live August EOI, while the row being edited got nothing.
//
// Returns { updated, record } — `updated` is the lead doc when there was one,
// so existing p.setLeads() callers keep working; `record` is the record doc on
// the historical path. Callers must patch their row rather than replacing it
// with `updated`, or they throw the record's own fields away again.
// A SERVER-REPORTED HALF-LANDING MUST REACH THE USER (wave 2b, 2026-08-25).
//
// PUT /api/leads/:id has always returned `_recordMirrorError` when the lead write
// succeeded and the mirror onto the transaction record did not. Its own comment
// says why it exists — "the client can tell the user their edit reached the lead
// but not the transaction record" — and NOTHING IN THIS FILE HAS EVER READ IT.
// The save returned green, the panel patched itself from its own payload, and the
// value reverted on the next refetch with no explanation available to anyone.
//
// Module scope, pure, returns a string: a gate can run it, and the decision of
// WHETHER to warn is separated from the act of warning. Callers alert; this
// decides. `reason` responses (no open record, nothing to mirror) are deliberately
// NOT warnings — they are non-events, and warning about them would train people to
// dismiss the box that matters.
function mirrorWarning(resp) {
  if (!resp || !resp._recordMirrorError) return "";
  return "Saved to the client, but NOT to this transaction row:\n\n" +
         resp._recordMirrorError +
         "\n\nThe row on screen may still show the old value after a refresh. " +
         "Reopen it from the list and save again.";
}

async function saveRowEdit(row, body, token) {
  if (isHistoricalRow(row)) {
    var r = await apiFetch("/api/records/" + rowKey(row), "PATCH", body, token);
    return { updated: null, record: (r && r.record) || null };
  }
  // txnId NAMES A RECORD, so only send one when the row IS a record. rowKey()
  // falls back to the lead's own _id for a branch-B row (rowFromLead sets
  // _rowKey = String(lead._id)), and sending that made the server look up a
  // record id that never existed. The server now recognises the lead-self
  // spelling and infers the target — this half stops producing it at all, so the
  // two agree instead of one compensating for the other.
  var body2 = row && row._fromRecord
    ? Object.assign({}, body, { txnId: rowKey(row) })
    : Object.assign({}, body);
  var updated = await apiFetch("/api/leads/" + gid(row), "PUT", body2, token);
  var warn = mirrorWarning(updated);
  if (warn) alert(warn);
  return { updated: updated, record: null };
}

// ===== ROLE REGISTRY — frontend mirror (2026-08-16) =====
// MUST stay identical to ROLE_DEFINITIONS in crm-aro-backend/server.js.
// verify-role-registry.js compares the two and fails the BUILD on any drift, so
// this is a mirror by construction, not by discipline.
//
// It is duplicated rather than imported because there is no shared-module path:
// Railway deploys only /crm-aro-backend, so a repo-root file never reaches the
// backend, and CRA's ModuleScopePlugin blocks importing from outside src/. The
// same mirror+gate pattern already shipped for HIERARCHY_PARENT_ROLES.
//
// Field meanings are documented once, on the server copy. `department` is a
// DECLARATION ONLY — no department collection, screen or UI exists or should be
// built until the company grows into one; its job is the gate's isolation
// invariant (every role in `parents` must share the role's department).
//
// Mirrors ROLE_DEPARTMENTS on the server. `executive` holds the Owner account
// alone — above every department rather than inside one.
var ROLE_DEPARTMENTS_FE = ["executive", "sales", "operation", "hr", "marketing", "support", "technical", "accounting"];

// BANDED_FE — mirror of the server's BANDED sentinel. Marks a field decided per
// RANK BAND rather than per role, so a role-level read cannot be mistaken for an
// answer. rolesWhereFE() refuses it; read banded fields with positionsWhereFE().
var BANDED_FE = { __banded: true };
var isBandedFE = function(v){ return !!v && v.__banded === true; };

// ORDER IS THE UI ORDER — the Add/Edit role pickers render in this sequence.
var ROLE_DEFINITIONS_FE = {
  admin:       { label:"Admin",          i18nKey:"admin",         colour:"#EF4444",
                 department:"executive", ladder:null,
                 parents:null, hasJobTitles:false, hasTargets:false, inRotation:false,
                 world:"real", creatable:false, readOnly:false, leadScope:"unrestricted" },
  operation:   { label:"Operations",                              colour:"#E8A838",
                 department:"operation", ladder:"operation",
                 parents:null, hasJobTitles:true,  hasTargets:false, inRotation:false,
                 world:"real", creatable:true,  readOnly:false, leadScope:"unrestricted",
                 visibility:"matrix" },
  hr:          { label:"HR",                                      colour:"#94A3B8",
                 department:"hr",        ladder:"hr",
                 parents:null, hasJobTitles:true,  hasTargets:false, inRotation:false,
                 world:"real", creatable:true,  readOnly:false, leadScope:"none",
                 visibility:"matrix" },
  // Mirror of the backend entry. `parents:["marketing"]` since the hierarchy
  // phase; operation/hr stay outside it (see server.js for why).
  marketing:   { label:"Marketing",                               colour:"#EC4899",
                 department:"marketing", ladder:"marketing",
                 parents:["marketing"], hasJobTitles:true, hasTargets:false, inRotation:false,
                 world:"real", creatable:true,  readOnly:false, leadScope:"none",
                 visibility:"matrix" },
  // Mirror of the backend entry (2026-11-01). The three extra flags are read by
  // name on the server; see server.js for what each one means. visibility is
  // page RESOLUTION (any role may carry it); financeReads and customerData are
  // the accountant's alone.
  accountant:  { label:"Accountant",                              colour:"#0D9488",
                 department:"accounting", ladder:"accounting",
                 parents:null, hasJobTitles:true,  hasTargets:false, inRotation:false,
                 world:"real", creatable:true,  readOnly:false, leadScope:"none",
                 visibility:"matrix", customerData:false, financeReads:true },
  // ===== THE SALES DEPARTMENT IS ONE ROLE — mirror of server.js =====
  // "دور واحد والفرق بالمسمى". director / manager / team_leader are BANDS of rank
  // inside this role, resolved through positionOf(). The role-level slots for the
  // three banded fields carry the BANDED_FE sentinel, never the agent band's
  // values: a role-level answer that silently means "rank 1-4 only" is a trap for
  // every rolesWhereFE() caller.
  sales:       { label:"Sales",          i18nKey:"salesAgent",    colour:"#3B82F6",
                 department:"sales",     ladder:"sales",
                 visibility:"matrix",
                 parents:["sales"], hasJobTitles:true,
                 hasTargets:BANDED_FE, inRotation:BANDED_FE, leadScope:BANDED_FE,
                 world:"real", creatable:true,  readOnly:false,
                 bands: [
                   { position:"sales",       minRank:1,  maxRank:4,  label:"Sales Agent",    i18nKey:"salesAgent",   colour:"#3B82F6",
                     leadScope:"own",     hasTargets:true,  inRotation:true  },
                   { position:"team_leader", minRank:5,  maxRank:6,  label:"Team Leader",                            colour:"#0EA5E9",
                     leadScope:"team",    hasTargets:true,  inRotation:true  },
                   { position:"manager",     minRank:7,  maxRank:9,  label:"Sales Manager",  i18nKey:"salesManager", colour:"#8B5CF6",
                     leadScope:"subtree", hasTargets:false, inRotation:true  },
                   { position:"director",    minRank:10, maxRank:10, label:"Sales Director",                         colour:"#DC2626",
                     leadScope:"subtree", hasTargets:false, inRotation:false }
                 ] },
  viewer:      { label:"Viewer",         i18nKey:"viewer",        colour:"#94A3B8",
                 department:"technical", ladder:null,
                 parents:null, hasJobTitles:false, hasTargets:false, inRotation:false,
                 world:"demo", creatable:true,  readOnly:true,  leadScope:"unrestricted" },
  office_boy:  { label:"Office Boy",                              colour:"#94A3B8",
                 department:"support",   ladder:"office",
                 parents:null, hasJobTitles:true,  hasTargets:false, inRotation:false,
                 world:"real", creatable:true,  readOnly:false, leadScope:"none",
                 visibility:"matrix" }
};

// ===== JOB-TITLE LADDERS — frontend mirror (2026-08-16) =====
// MUST stay identical to DEPARTMENT_LADDERS in crm-aro-backend/server.js;
// verify-role-registry.js compares them and fails the build on any drift.
// Field meanings are documented once, on the server copy. The `role` column of
// the `sales` ladder is the frozen equivalence table (ranks 1-4 / 5-6 / 7-9 / 10
// reproduce today's four sales roles exactly) — do not reorder it.
var DEPARTMENT_LADDERS_FE = {
  sales: { label:"Sales", minLeaderRank: 5,
    presets: [{ key:"all", label:"All", minRank:1 }, { key:"senior", label:"Senior", minRank:3 }, { key:"leadership", label:"Leadership", minRank:5 }, { key:"management", label:"Management", minRank:8 }],
    titles: [
    { rank:1,  title:"Property Advisor",        role:"sales"       },
    { rank:2,  title:"Property Consultant",     role:"sales"       },
    { rank:3,  title:"Senior Sales",            role:"sales"       },
    { rank:4,  title:"Supervisor",              role:"sales"       },
    { rank:5,  title:"Team Leader",             role:"team_leader" },
    { rank:6,  title:"Sr. Team Leader",         role:"team_leader" },
    { rank:7,  title:"Associate Sales Manager", role:"manager"     },
    { rank:8,  title:"Sales Manager",           role:"manager"     },
    { rank:9,  title:"Sr. Sales Manager",       role:"manager"     },
    { rank:10, title:"Sales Director",          role:"director"    }
  ]},
  operation: { label:"Operations", minLeaderRank: 4,
    presets: [{ key:"all", label:"All", minRank:1 }, { key:"senior", label:"Senior", minRank:3 }, { key:"leadership", label:"Leadership", minRank:4 }, { key:"management", label:"Management", minRank:6 }],
    titles: [
    { rank:1, title:"Operations Coordinator",         role:"operation" },
    { rank:2, title:"Operations Specialist",          role:"operation" },
    { rank:3, title:"Senior Operations Specialist",   role:"operation" },
    { rank:4, title:"Operations Team Leader",         role:"operation" },
    { rank:5, title:"Assistant Operations Manager",   role:"operation" },
    { rank:6, title:"Operations Manager",             role:"operation" },
    { rank:7, title:"Head of Operations",             role:"operation" }
  ]},
  hr: { label:"HR", minLeaderRank: 4,
    presets: [{ key:"all", label:"All", minRank:1 }, { key:"senior", label:"Senior", minRank:3 }, { key:"leadership", label:"Leadership", minRank:4 }, { key:"management", label:"Management", minRank:6 }],
    titles: [
    { rank:1, title:"HR Coordinator",       role:"hr" },
    { rank:2, title:"HR Specialist",        role:"hr" },
    { rank:3, title:"Senior HR Specialist", role:"hr" },
    { rank:4, title:"HR Team Leader",       role:"hr" },
    { rank:5, title:"Assistant HR Manager", role:"hr" },
    { rank:6, title:"HR Manager",           role:"hr" },
    { rank:7, title:"Head of HR",           role:"hr" }
  ]},
  marketing: { label:"Marketing", minLeaderRank: 4,
    presets: [{ key:"all", label:"All", minRank:1 }, { key:"senior", label:"Senior", minRank:3 }, { key:"leadership", label:"Leadership", minRank:4 }, { key:"management", label:"Management", minRank:6 }],
    titles: [
    { rank:1, title:"Marketing Coordinator",         role:"marketing" },
    { rank:2, title:"Marketing Specialist",          role:"marketing" },
    { rank:3, title:"Senior Marketing Specialist",   role:"marketing" },
    { rank:4, title:"Marketing Team Leader",         role:"marketing" },
    { rank:5, title:"Assistant Marketing Manager",   role:"marketing" },
    { rank:6, title:"Marketing Manager",             role:"marketing" },
    { rank:7, title:"Head of Marketing",             role:"marketing" }
  ]},
  office: { label:"Office", minLeaderRank: 4,
    presets: [{ key:"all", label:"All", minRank:1 }, { key:"senior", label:"Senior", minRank:2 }, { key:"leadership", label:"Leadership", minRank:3 }, { key:"management", label:"Management", minRank:4 }],
    titles: [
    { rank:1, title:"Office Assistant",           role:"office_boy" },
    { rank:2, title:"Senior Office Assistant",    role:"office_boy" },
    { rank:3, title:"Office Services Supervisor", role:"office_boy" },
    { rank:4, title:"Office Services Manager",    role:"office_boy" }
  ]},
  accounting: { label:"Accounting", minLeaderRank: 1,
    presets: [{ key:"all", label:"All", minRank:1 }],
    titles: [
    { rank:1, title:"Accountant", role:"accountant" }
  ]}
};
var LADDER_KEYS_FE = Object.keys(DEPARTMENT_LADDERS_FE);
var ladderOfFE = function(role){
  var d = ROLE_DEFINITIONS_FE[String(role || "")];
  return (d && d.ladder) ? d.ladder : null;
};
var ladderTitlesFE = function(role){
  var k = ladderOfFE(role);
  return k ? DEPARTMENT_LADDERS_FE[k].titles : [];
};
var isLadderRoleFE = function(role){ return ladderOfFE(role) !== null; };
// ===== THE TWO ACCOUNTANT-ONLY FLAGS, read by name (owner decision 2026-11-01) =====
// Mirrors of the server's roleHasFinanceReads / roleHidesCustomerData. Both flags
// sit on the accountant entry alone (the registry gate pins that), so every
// other role answers false here by construction. The FIRST is why the sales
// ranking and the claim-data backfill control are hidden for the role, why the
// Reports/Commissions bodies admit it, and why the withholding toggle, Edit
// Collection, cycle delete/restore, the Ambassador block, the VAT ledger and
// its delete, the Assets money view and Retire admit it beside admin/operation
// (owner rulings 2026-11-02, two passes; the server asks the
// manageCommissionCycle / manageFinanceEntries / manageAssets grant on each
// write); the SECOND is why the header search, the callback bell, the phone
// search and View Deal are hidden. The
// Claims card names the CLIENT for every role since 2026-11-02 — the server
// strips the phone, not the name. Keyed on the flag rather than the role
// literal so the behaviour follows the declaration, not a string somebody has
// to remember to grep for.
//
// NOT keyed on visibility:"matrix" (flag split, 2026-11-01): that flag is page
// RESOLUTION, every ladder role carries it since 2026-11-02, and the client has
// no reader for it at all — the Page Visibility preview resolves a rung from the
// draft's grants, which is the only thing the server resolves it from.
var roleHasFinanceReadsFE = function(cu){
  var d = ROLE_DEFINITIONS_FE[String((cu && cu.role) || "")];
  return !!d && d.financeReads === true;
};
var roleHidesCustomerDataFE = function(cu){
  var d = ROLE_DEFINITIONS_FE[String((cu && cu.role) || "")];
  return !!d && d.customerData === false;
};
// ===== THE DEPARTMENT-TAB LABEL (2026-08-22) =====
// Both matrix screens key their tab row by DEPARTMENT, because department is the
// persisted grant key: grants[department][rank]. What a tab actually LISTS is a
// ladder's titles. Those two names coincide for four of the five departments, so
// rendering the raw key with textTransform:"capitalize" looked correct — and for
// the fifth it was not. office_boy is department "support", ladder "office", so
// the tab read "Support": a department this org does not have (owner report).
// The same raw-key render also printed "Hr" for the HR ladder.
//
// The department key is deliberately NOT renamed. It is written into stored
// matrix documents, ROLE_DEPARTMENTS validates incoming grants against it, and
// capabilityFor()/visibility read grants[rdef.department] — moving it is a data
// migration, not a label change. So the key stays and the label travels beside it.
//
// Resolved from THIS mirror first and the server payload only as a fallback:
// frontend (Vercel) and backend (Railway) deploy independently, so a frontend
// that ships ahead of the server must not quietly print "Support" again while it
// waits. verify-role-registry.js deep-compares this table against the server's,
// so the local answer cannot drift from the one the payload would have carried.
var LADDER_LABEL_BY_DEPT_FE = (function(){
  var m = {};
  Object.keys(ROLE_DEFINITIONS_FE).forEach(function(r){
    var d = ROLE_DEFINITIONS_FE[r];
    if (d && d.ladder && DEPARTMENT_LADDERS_FE[d.ladder]) m[d.department] = DEPARTMENT_LADDERS_FE[d.ladder].label;
  });
  return m;
})();
var deptLabel = function(data, dept){
  return LADDER_LABEL_BY_DEPT_FE[dept] || ((data && data.ladderLabels) || {})[dept] || dept;
};

// ===== THRESHOLD PRESETS — THE TABLE WRITER (2026-08-22) ======================
// Both matrix screens STORE per-rank arrays: grants[dept][rank] = [key, …]. That
// model is unchanged. What changed is the writer: the table offers a threshold
// ("Leadership 5+") instead of one checkbox per rank, and writes the equivalent
// range. Fewer ways to express the same intent means fewer ways to get it wrong.
//
// TOP-ANCHORED, and that word is load-bearing. A threshold is exactly {N…max}.
// {1,2,3} is contiguous but bottom-anchored, so it is NOT a threshold: it must
// render as `custom` with no radio selected, or the radio would claim a grant it
// does not describe. thresholdOfRanks returns null for that, and for any gap.
//
// NOBODY (0) is different from custom: an empty array IS representable, so it is
// a real column and always round-trips.
//
// These live at module scope, not inside the screen, so verify-page-visibility.js
// can simulate every preset on every ladder and diff the resulting draft without
// a browser. A writer that only exists inside a closure cannot be gated.
var VIS_NOBODY = 0;

var thresholdOfRanks = function(rankNums, maxRank) {
  var list = (rankNums || []).slice().sort(function(a, b){ return a - b; });
  if (!list.length) return VIS_NOBODY;
  var n = list[0];
  // THE LENGTH CHECK IS THE TOP-ANCHOR TEST. A run that is contiguous from n and
  // holds exactly (maxRank - n + 1) ranks spans n..maxRank by construction, so it
  // reaches the top; anything shorter does not, and anything with a gap fails the
  // loop below. An explicit `list[last] !== maxRank` early-out was written here
  // first and then removed: checked exhaustively over all 1024 subsets of a
  // 10-rank ladder, it never changed a single answer. Dead branches read as
  // safety and are really just untested code, so this comment carries the
  // guarantee instead — and verify-fe-position.js re-proves it exhaustively on
  // every build rather than trusting this paragraph.
  if (list.length !== (maxRank - n + 1)) return null;
  for (var i = 0; i < list.length; i++) if (list[i] !== n + i) return null;
  return n;
};

var ranksForThreshold = function(minRank, maxRank) {
  var out = [];
  if (!minRank || minRank < 1) return out;                 // NOBODY
  for (var r = minRank; r <= maxRank; r++) out.push(r);
  return out;
};

// What the stored grant for one key currently is, in threshold terms.
// N | VIS_NOBODY | null(custom).
var thresholdOfKey = function(grants, dept, rankNums, key) {
  var d = (grants || {})[dept] || {};
  var all = (rankNums || []).slice().sort(function(a, b){ return a - b; });
  var have = all.filter(function(r){ return (d[String(r)] || []).indexOf(key) !== -1; });
  return thresholdOfRanks(have, all.length ? all[all.length - 1] : 0);
};

// Writes a threshold and returns a NEW grants object. PURE — no state, no draft
// mutation — which is what makes it simulatable by the gate.
//
// The tab/page invariant is enforced with the SAME asymmetry the checkbox grid
// used, because the right answer depends on what the owner just touched:
//   touched a TAB  → grant its page wherever the tab is granted. AUTO-WIDEN,
//                    owner ruling 2026-08-22, matching "ticking a tab ticks its
//                    page". The server's orphan_tab guard stays the backstop; it
//                    is not the mechanism, and this must not rely on it firing.
//   touched a PAGE → drop its tabs wherever the page is not granted, matching
//                    "unticking a page unticks its tabs".
// Applied blind, those two rules contradict each other on the same row; keyed to
// the touched surface, they are exactly today's behaviour lifted to a range.
var applyThresholdToGrants = function(grants, dept, rankNums, key, minRank, surfaces) {
  var all = (rankNums || []).slice().sort(function(a, b){ return a - b; });
  var maxRank = all.length ? all[all.length - 1] : 0;
  var next = JSON.parse(JSON.stringify(grants || {}));
  var d = next[dept] = next[dept] || {};
  var on = {};
  ranksForThreshold(minRank, maxRank).forEach(function(r){ on[r] = true; });
  var def = (surfaces || {})[key] || {};
  var kidsOf = function(pageId){
    return Object.keys(surfaces || {}).filter(function(k){
      return surfaces[k].kind === "tab" && surfaces[k].pageId === pageId;
    });
  };
  all.forEach(function(r){
    var list = (d[String(r)] || []).slice();
    var at = list.indexOf(key);
    if (on[r] && at === -1) list.push(key);
    if (!on[r] && at !== -1) list.splice(at, 1);
    if (def.kind === "tab") {
      if (on[r] && def.parent && list.indexOf(def.parent) === -1) list.push(def.parent);
    } else if (def.kind === "page") {
      if (!on[r]) kidsOf(def.pageId).forEach(function(tk){
        var ti = list.indexOf(tk); if (ti !== -1) list.splice(ti, 1);
      });
    }
    d[String(r)] = list;
  });
  return next;
};

// unmanageMatrix (hand a surface back to the sidebar rule) was retired here on
// 2026-11-02 with the `managed` branch it served: every ladder role resolves from
// grants, so there is no sidebar rule to hand a surface back to. Save is the one
// action on the Page Visibility screen.

// ===== THE BAND WRITE — WHAT ONE TOGGLE ON ONE RUNG MEANS (P5) =============
//
// The owner's ruling, 2026-08-23, as a pure function:
//
//   ON  at rank N -> min(current, N): N and everyone ABOVE gain it.
//   OFF at rank N -> N+1: N and everyone BELOW lose it, seniors keep it.
//   OFF at the top rung -> NOBODY.
//
// OFF RAISES THE BAR rather than clearing the column. That keeps every write
// from the replica TOP-ANCHORED, so applyThresholdToGrants stays the only writer
// and the screen cannot manufacture a `custom` grant nobody asked for. Clearing
// a surface for everyone is what the per-rank drawer is for.
//
// A per-rung write was the alternative and it is the documented trap — "grant
// the RANK BAND, not the one rank, or a promotion silently revokes" — which
// would have become the default gesture.
//
// AT MODULE SCOPE BECAUSE THE SEMANTICS ARE THE RULING. Left inside the render
// closure it was unreachable by the gate: replacing the OFF branch with "clear
// everything" changed the product's behaviour and no check went red. Same
// lesson previewSees taught one phase earlier.
//
// `cur` is N | VIS_NOBODY | null(custom) — the shapes thresholdOfKey returns.
var bandThresholdFor = function(cur, rank, maxRank, on) {
  if (on) {
    // A custom grant has no threshold to take the min of, so ON replaces it with
    // the band from here up. The blast line says so out loud.
    return (cur === null || cur === VIS_NOBODY) ? rank : Math.min(cur, rank);
  }
  return (rank >= maxRank) ? VIS_NOBODY : rank + 1;
};

// The preset columns for one department's ladder, from the registry mirror, with
// NOBODY appended. Registry-driven so a new department is a one-line change.
var presetsForDept = function(data, dept) {
  var fromPayload = ((data && data.ladderPresets) || {})[dept];
  var local = (function(){
    var lk = null;
    Object.keys(ROLE_DEFINITIONS_FE).forEach(function(r){
      var dd = ROLE_DEFINITIONS_FE[r];
      if (dd && dd.department === dept && dd.ladder) lk = dd.ladder;
    });
    return (lk && DEPARTMENT_LADDERS_FE[lk] && DEPARTMENT_LADDERS_FE[lk].presets) || null;
  })();
  return (local || fromPayload || []).slice();
};
// Rank of a title ON THIS ROLE'S LADDER, or null. Role-scoped on purpose — the
// server's rankOfTitle does the same, and a title from another department must
// not resolve here either.
var rankOfTitleFE = function(role, title){
  var t = String(title == null ? "" : title).trim();
  if (!t) return null;
  var list = ladderTitlesFE(role);
  for (var i = 0; i < list.length; i++) if (list[i].title === t) return list[i].rank;
  return null;
};
// ===== positionOf — the frontend half of the sales unification (P6, 2026-08-21) =====
//
// Mirrors server.js's positionOf() exactly. `role` says which ROW of the users
// collection a person is in; `position` says what that role MEANS on the sales
// ladder. Today they are equal for every user (verify-position-equivalence.js,
// re-proven live against the whole roster). After the collapse they are not:
// every sales-ladder user will STORE role "sales", and only the rank
// distinguishes an agent from a director.
//
// So on this side of the wire the rule is the same as the server's: any surface
// that tells the four sales positions apart must ask positionOf(), never .role.
// A surface comparing against the whole ladder at once — or against a non-ladder
// role like admin / operation / hr — is unaffected either way and is left alone.
//
// THE RANK FALLBACK IS DELIBERATE, AND IT IS NOT A SECOND SOURCE OF TRUTH.
// The server derives titleRank from (title, ladder) and persists it. Here, when
// a user object reaches us without titleRank, we derive it from the SAME title
// against the SAME ladder table — DEPARTMENT_LADDERS_FE, which
// verify-role-registry.js compares to the server's copy and fails the build on
// any drift. It is the same function applied to the same input, not a guess.
//
// It exists because of one real window: a session blob written by an older
// build carries no titleRank, and it is read SYNCHRONOUSLY at first paint,
// before the /api/me identity refresh can land. Without the fallback every
// substituted gate would resolve to null for that first render — a sales agent
// would see one frame of an app that thinks they have no position. With it,
// that frame is correct, and the refresh then supplies the stored value.
//
// Rank-less AND title-less still returns null. That is the honest answer and the
// surfaces treat it as "not this position", which is the fail-closed direction.
var RANK_TO_POSITION_FE = (function(){
  var m = {};
  ((DEPARTMENT_LADDERS_FE.sales && DEPARTMENT_LADDERS_FE.sales.titles) || []).forEach(function(t){ m[t.rank] = t.role; });
  return m;
})();
var SALES_LADDER_ROLES_FE = (function(){
  var seen = {}, out = [];
  ((DEPARTMENT_LADDERS_FE.sales && DEPARTMENT_LADDERS_FE.sales.titles) || []).forEach(function(t){
    if (!seen[t.role]) { seen[t.role] = 1; out.push(t.role); }
  });
  return out;
})();
// ===== BAND DESCRIPTORS — label and colour for a POSITION, whichever shape the
// registry is in (P3 Phase B, 2026-08-21) =====
//
// Today the four sales positions ARE roles, each carrying its own label and
// colour. After the registry cleanup they are BANDS on the single `sales` role
// and those role entries are gone. Every badge on the Users page resolves
// rc[positionOf(u)] / rl[positionOf(u)], so reading them straight out of
// ROLE_DEFINITIONS_FE would leave a director's badge undefined the day the
// entries are removed — falling back to u.role, which by then says "sales".
//
// This reads whichever shape exists, so the badge maps answer correctly THROUGH
// the change rather than across it, and Phase B can be verified while the old
// registry is still in place.
var salesBandDescriptorsFE = function(){
  var sd = ROLE_DEFINITIONS_FE.sales;
  if (sd && Array.isArray(sd.bands)) return sd.bands;                     // after the cleanup
  return SALES_LADDER_ROLES_FE.map(function(pos){                         // before it
    var d = ROLE_DEFINITIONS_FE[pos];
    return d ? { position: pos, label: d.label, i18nKey: d.i18nKey, colour: d.colour } : null;
  }).filter(Boolean);
};
var positionOf = function(user){
  if (!user) return null;
  var role = user.role;
  var d = ROLE_DEFINITIONS_FE[role];
  if (!d || d.ladder !== "sales") return role;   // not on the sales ladder: its own position
  var rank = (typeof user.titleRank === "number") ? user.titleRank : rankOfTitleFE(role, user.title);
  if (typeof rank !== "number") return null;     // rank-less: no position
  return RANK_TO_POSITION_FE[rank] || null;
};
// `positionOf(u) === one of these` reads badly at a call site that wants "is this
// person anywhere on the sales ladder". This is that question, and it stays true
// across the collapse for exactly the users it is true for today.
var isSalesLadderFE = function(user){
  return SALES_LADDER_ROLES_FE.indexOf(positionOf(user)) !== -1;
};

// ===== PAGE VISIBILITY — READ FROM THE SERVER (Phase 5, 2026-08-22) =========
//
// A LOOKUP, NOT A DECISION — the same shape canDo() has for capabilities.
//
// Phase 1c put the client's two disagreeing copies (the Sidebar items array and
// the renderPage switch) behind one table, PAGE_ACCESS. Phase 2 transcribed that
// table to the server and gated the two equal. Phase 5 deletes the client's copy:
// the envelope carries `pages`, resolved by surfaceFor(), and this reads it.
//
// WHY THE COPY HAD TO GO, and not just be kept in sync: it was rank-blind in the
// same way hasAttendancePerm was. The moment the owner takes a page over, its
// answer is a RANK question against a matrix the client does not have. A local
// table would have gone quietly wrong for exactly the people a take-over moves —
// which is how Marwa lost her access on 2026-08-22, one layer down.
//
// Keyed by PAGE ID, including the three Attendance deep-link aliases, so a stale
// crm_page value resolves through the same dictionary as everything else. An
// unknown id is absent, reads as undefined, and fails closed.
var canSeePage = function(access, pageId){
  if (!access || !access.pages || !pageId) return false;
  return access.pages[pageId] === true;
};
// True once the server has told us anything at all. Distinguishes "not allowed"
// from "not answered yet" — a session blob written before Phase 5 carries an
// envelope with no `pages`, and rendering that as "sees nothing" would blank the
// nav for one frame after upgrade rather than simply waiting a round-trip.
var accessLoaded = function(access){ return !!(access && access.pages); };

// ===== THE OWNER FLAG — FROM THE ENVELOPE, NEVER FROM p.cu (2026-08-27) =====
//
// Same shape as canDo() and canSeePage(): a dictionary read of an answer the
// SERVER resolved, with no rule in it.
//
// WHY NOT p.cu.isOwner. `currentUser` is restored synchronously from a session
// blob written at LOGIN, and tokens live 30 days. The boot identity refresh
// (loadData's `fresh`) copies role, title and titleRank back — and deliberately
// nothing else, because widening it would overwrite local edits other screens
// own. So the owner flag on p.cu is whatever was true the day that session was
// minted, for up to a month. The envelope is rebuilt on every boot /api/me and
// re-fetched on every access-changing websocket event.
//
// A PAGE MUST NOT USE THIS. Page visibility is canSeePage(); this answers only
// for owner-only CONTROLS that are not pages — the Users-page Devices button and
// the Assets total-value card. A page gated here instead of on the envelope's
// `pages` is a second visibility rule, which is what Phase 5 deleted, and it is
// how MeetingsPage came to refuse people the sidebar and the server both let in.
// verify-page-visibility.js holds that line.
var isOwnerFE = function(access){ return !!(access && access.isOwner === true); };

var ROLE_KEYS_FE = Object.keys(ROLE_DEFINITIONS_FE);
var rolesWhereFE = function(pred){
  return ROLE_KEYS_FE.filter(function(r){
    var v = pred(ROLE_DEFINITIONS_FE[r], r);
    if (isBandedFE(v)) {
      throw new Error("rolesWhereFE: `" + r + "` answers with a BANDED field — that value differs " +
        "per rank band, so there is no role-level answer. Use positionsWhereFE().");
    }
    return v;
  });
};
// The band-level twin: POSITIONS whose band satisfies the predicate. A role with
// no bands answers for itself, so callers never have to branch on shape.
var positionsWhereFE = function(pred){
  var out = [];
  ROLE_KEYS_FE.forEach(function(r){
    var d = ROLE_DEFINITIONS_FE[r];
    var bands = (d && Array.isArray(d.bands)) ? d.bands : [];
    if (!bands.length) { if (pred(d, r)) out.push(r); return; }
    bands.forEach(function(b){ if (pred(b, b.position)) out.push(b.position); });
  });
  return out;
};
// Role label honouring the active translation dictionary, falling back to the
// registry's canonical English. One definition for every label map below.
var roleLabel = function(role, t){
  var d = ROLE_DEFINITIONS_FE[role];
  if (!d) return String(role || "");
  return (d.i18nKey && t && t[d.i18nKey]) || d.label;
};
// Roles whose Job Titles group together on the (future) Job Titles settings
// screen. A new role with hasJobTitles:true appears there automatically.
var rolesWithJobTitles = function(){ return rolesWhereFE(function(d){ return d.hasJobTitles; }); };

// Historical agent for a CANCELLED deal/EOI record. While a lead is a live EOI
// or Done Deal, rotation is hard-blocked server-side; so the FIRST agentHistory
// entry dated AFTER the record was created (dealDate for a cancelled deal, else
// eoiDate) is the post-cancellation auto-rotate that reassigned the lead — and
// its `fromAgent` is exactly who HELD the record when it was cancelled. Reading
// the live agentId is wrong for these rows: rotation overwrote it with the new
// owner once the lead re-entered the pool. Falls back to `liveName` when the
// lead was never rotated away after cancellation (live agent is still the
// holder) or the history/date is too thin to resolve. Derives entirely from
// data already on the lead (agentHistory + eoiDate/dealDate) — no snapshot
// field, no migration.
// Two-party Resale display — the with-us party names joined ("Seller ↔ Buyer"),
// falling back to a single name. Returns null for non-resale leads.
var resalePartyLabel = function(lead){
  if(!lead || lead.saleType!=="Resale" || !lead.resaleParties) return null;
  var rp=lead.resaleParties, s=rp.seller||{}, b=rp.buyer||{};
  var names=[];
  if(s.name) names.push(s.name + (s.withUs?"":" (not ours)"));
  if(b.name) names.push(b.name + (b.withUs?"":" (not ours)"));
  return names.length ? names.join(" ↔ ") : null;
};

// NOTE ON PLACEMENT: the block below sits HERE, after resalePartyLabel, and not
// up beside canSeePage where it belongs by subject. verify-role-registry.js
// `new Function()`-evals the slice of this file from `var ROLE_DEPARTMENTS_FE`
// to the "Historical agent for a CANCELLED" marker, so anything defined inside
// that range must be self-contained — DEALS_TABS reads C.success, and the eval
// has no C. Moving the block out is the fix; inlining the hex would have been a
// second copy of a colour token.
// ===== TAB VISIBILITY — THE SAME DICTIONARY READ, ONE LAYER DOWN ============
//
// WHY THIS LANDED ON 2026-08-23, AND WHY IT LANDED ALONE.
//
// Phase 5 taught the client to read `access.pages` and deleted its copy of the
// page rule. The envelope has ALSO carried `access.tabs` since that same commit
// — all 32 tab surfaces, resolved by surfaceFor, recomputed on every /api/me —
// and NOTHING READ IT. `grep '\.tabs'` over this file returned zero hits.
//
// So a tab surface was governable on paper and nowhere else. The Owner could
// take one over, PATCH /api/settings/visibility-matrix would accept it,
// surfaceFor would change its answer, the audit row would record a holder diff
// by name — and the chip would keep rendering for exactly the people the take-
// over was supposed to hide it from. Seven of the 32 (`settings.campaigns`,
// `capabilities`, `commissions`, `officeLocation`, `rotation`, `visibility`,
// `workShifts`) are declared on a route and would at least have refused the
// fetch; the other 25 were not enforced anywhere at all.
//
// A wire that carries nothing gets connected before a switch is built for it.
//
// FAILS CLOSED on a missing envelope, same as canSeePage. `pages` and `tabs`
// are built in ONE object literal by buildAccessEnvelope, so accessLoaded()
// answers for both — verify-page-visibility.js pins that, because a stored
// session blob carrying one and not the other would hide every tab in the
// product.
var canSeeTab = function(access, pageId, tabId){
  if (!access || !access.tabs || !pageId || !tabId) return false;
  return access.tabs[pageId + "." + tabId] === true;
};

// Filter a page's tab strip through the envelope AND keep the selection valid.
// Returns { tabs, active, empty }.
//
// THE SELECTION IS THE HALF THAT BITES, not the filter. Reports persists its tab
// in localStorage (`crm_reports_tab`) and restored it unvalidated — the same
// shape as `crm_page`, which renderPage checks precisely because it did not
// always. A stored tab the caller can no longer see would render its content
// with no chip left to leave by.
//
// `active` is NULL when nothing is visible, and that is deliberate: every call
// site reads the active tab with `=== "someId"`, so a null answer makes every
// content block false without a second guard anywhere. Unreachable today — the
// legacy rule never hides a whole strip — and correct the day a surface is
// taken over.
var resolveTabStrip = function(access, pageId, list, current) {
  var vis = (list || []).filter(function(tb){ return tb && canSeeTab(access, pageId, tb.id); });
  var ok = vis.some(function(tb){ return tb.id === current; });
  return {
    tabs: vis,
    active: ok ? current : (vis.length ? vis[0].id : null),
    empty: vis.length === 0
  };
};

// ===== "by Name — DD/MM/YYYY h:mm AM/PM" (owner decision 2026-11-01, Phase 4) =====
// ONE spelling for every audit caption: a cycle stage, an expense row, a VAT
// payment, the profit-tax entry. Old records carry no name or no timestamp and
// show "—" for the missing half; nothing is inferred. Goes through fmtDateTime12
// (src/lib/timeFormat.js), so the clock is Cairo and 12-hour like every other
// time on screen. Module scope so a gate can run it.
var auditCaption = function(byName, at){
  var who = byName ? String(byName) : "—";
  // No recording time (every pre-Phase-4 stage and ledger row): just "by Name",
  // no trailing dashes; "by —" only when the name is missing too (owner, 2026-11-01).
  if (!at) return "by " + who;
  return "by " + who + " — " + fmtDateTime12(at, undefined, { numeric: true, sep: " " });
};

// ===== CYCLE HISTORY — every statusLog entry as one line (owner-approved 2026-11-01) =====
// "<action> by <name> — <date 12h>", oldest first, over the data the cycle card
// already holds. Delete entries do not appear: they live in auditlogs, since the
// cycle they belonged to is gone. Pure and at module scope so a gate can run it.
var CYCLE_STATE_LABELS = { pending_claim: "Pending Claim", claim_submitted: "Claim Submitted", invoice_submitted: "Invoice Submitted",
                           received: "Received", paid_to_team: "Paid to Team", cancelled: "Cancelled" };
var cycleHistoryLabel = function(e){
  var a = String((e && e.action) || "");
  var to = CYCLE_STATE_LABELS[e && e.to] || String((e && e.to) || "");
  if (a === "advance")        return "Advanced to " + to;
  if (a.indexOf("edit:") === 0) return "Edited " + (CYCLE_STATE_LABELS[a.slice(5)] || a.slice(5));
  if (a === "restore")        return "Restored to " + to;
  if (a === "cascade_cancel") return "Cancelled with the deal";
  return a || "—";
};
var cycleHistoryLines = function(cycle){
  var log = (cycle && Array.isArray(cycle.statusLog)) ? cycle.statusLog : [];
  return log.map(function(e){ return cycleHistoryLabel(e) + " " + auditCaption(e && e.byName, e && e.at); });
};

// The message a page shows when the matrix has hidden every one of its tabs.
// One sentence, one place — five copies of it would be five things to reword.
var NO_TABS_MSG = "No sections of this page are available to you.";

// ===== THE FIVE TAB STRIPS — ONE REGISTRY EACH, AT MODULE SCOPE =============
//
// They used to be literals inside their components, three of them carrying
// their own role tests: `isOwner &&` on eight Settings tabs, `canManageBranches
// &&` on Branches, `role !== "operation"` on Integrations, `role === "admin"`
// on Commissions › Annual Summary. Those were the last visibility rules the
// client owned, and they were rank-blind in exactly the way hasAttendancePerm
// was — the moment a tab is taken over, its answer is a rank question against a
// matrix this bundle does not have.
//
// Every condition here is now transcribed in LEGACY_TAB_RULES on the server and
// gated equal to what these arrays used to say, subject by subject. Hoisting
// them to module scope is what lets verify-page-visibility.js parse them as
// data instead of hunting conditions inside JSX — and what makes putting a role
// test back a visible act rather than an easy one.
var SETTINGS_TABS = [
  {id:"general",          label:"General"},
  {id:"rotation",         label:"Rotation"},
  {id:"visibility",       label:"Page Visibility"},
  {id:"capabilities",     label:"Capabilities"},
  {id:"workShifts",       label:"Work Shifts"},
  {id:"locations",        label:"Locations"},
  {id:"branches",         label:"Branches"},
  {id:"officeLocation",   label:"Office Location"},
  {id:"developers",       label:"Developers"},
  {id:"closingCompanies", label:"Closing Companies"},
  {id:"commissions",      label:"Commissions"},
  {id:"campaigns",        label:"Campaigns"},
  {id:"rules",            label:"Business Rules"},
  {id:"team",             label:"Team & Roles"},
  {id:"integrations",     label:"Integrations"},
  {id:"audit",            label:"Audit Log"},
  {id:"sysAudit",         label:"System Audit"},
  {id:"rotationDiag",     label:"Rotation Diagnostics"}
];
// `enabled:false` is a SHIPPING state ("Soon"), not a visibility rule — it stays
// on the registry rather than moving into the matrix, which answers a different
// question.
var REPORTS_TABS = [
  {id:"overview",    label:"Overview",       enabled:true},
  {id:"campaigns",   label:"Campaigns",      enabled:true},
  {id:"agents",      label:"Agents",         enabled:true},
  // Sits beside Agents because it answers the same question about the same
  // people, off a different collection: Agents derives meetings from Lead/DR,
  // this one counts the permanent Meeting archive. Mirrored in SURFACES
  // (server.js) — verify-page-visibility parses this literal and fails the
  // build if the two strips disagree.
  {id:"meetings",    label:"Meetings",       enabled:true},
  {id:"leaderboard", label:"🏆 Leaderboard", enabled:true},
  {id:"pipeline",    label:"Pipeline",       enabled:true}
];
var COMMISSIONS_TABS = [
  {id:"deals",      label:"Claims"},
  {id:"calculator", label:"Calculator"},
  {id:"duePayouts", label:"Due Payouts"},
  {id:"payout",     label:"Payout Report"},
  {id:"annual",     label:"Annual Summary"}
];
// EOI and Deals carry their chip colours here too; the COUNT is supplied by the
// component, because it is data and not vocabulary.
// TWO chips, because an EOI is a STAGE and this page is that stage (owner ruling
// 2026-08-24). A converted EOI is not a third kind of EOI — it is a DEAL, and it
// lives on the Deals page. It is not listed here at all, under any chip; the
// server does not even send it (txnStatusesForTab, "all").
// Mirrored in SURFACES (server.js) — verify-page-visibility parses this literal.
// R-T7 REVISED (owner ruling, final, 2026-09-10) — CANCELLED IS A TAB AGAIN.
//
// For a few hours a cancelled EOI was archived and moved to the Archive page.
// The owner withdrew that: a cancelled EOI stays here, under its own tab, where
// the agent who made it and the owner both find it. Restore lives on that tab.
//
// SHOWN IS NOT COUNTED. It is visible here and it enters no total — the page
// header, the dashboard, the KPIs, Reports and the campaign table all count
// through EOI_ACTIVE_STATUSES, which does not name Cancelled. Two constants on
// the server keep those apart on purpose.
// 2026-10-30 (owner task): the EOI Active tab wears the Deals green tokens
// (tab border/bg/text and the header Total pill alike); Cancelled stays red.
var EOI_TABS = [
  {id:"active",    label:"📋 Active",    fg:C.success, bg:"#DCFCE7"},
  {id:"cancelled", label:"❌ Cancelled",       fg:"#B91C1C", bg:"#FEE2E2"}
];
var DEALS_TABS = [
  {id:"active",    label:"💼 Active",        fg:C.success, bg:"#DCFCE7"},
  {id:"cancelled", label:"❌ Deal Cancelled",      fg:"#B91C1C", bg:"#FEE2E2"}
];

// ===== THE PREVIEW'S RESOLUTION (P4) =======================================
//
// What ONE RUNG of ONE DEPARTMENT would see. This is surfaceFor()'s grants branch
// with the Owner short-circuit removed, and removing it is the whole point: the
// subject is a RUNG, not a person. The Owner's own account resolves true on
// everything, so previewing his rung through his own answer would draw a menu
// with nothing hidden in it and look entirely plausible.
//
// GRANTS ONLY (settings-single-source Phase 7, 2026-11-02). Every ladder role
// carries visibility:"matrix", so a rung's answer is exactly matrixOnlySurfaceAllows:
// the draft's grants for (department, rank), a tab never visible where its page is
// not, and a rank-less subject sees nothing. The legacy half this used to layer
// under `managed` (legacyByRank / legacyRankless from the server) is gone from the
// payload and from here — the server's sidebar rule now answers only the rank-less
// viewer, who has no rung on this screen.
//
// verify-page-visibility.js runs this exact function against surfaceFor for every
// rung x every surface, under the live grants and under fabricated ones.
//
// At module scope, not inside the screen, for the same reason
// applyThresholdToGrants is: a writer or a resolver that only exists inside a
// closure cannot be gated.
var previewSees = function(ctx, surfaceKey){
  var def = ctx.surfaces[surfaceKey];
  if (!def) return false;                                   // unknown -> deny, as the server does
  if (ctx.rankless) return false;                           // no rung, no grant — as matrixOnlySurfaceAllows
  // A tab is never visible where its page is not, whatever the grid says. The
  // server refuses that combination at save time (orphan_tab) AND at resolution
  // time; the preview has to refuse it too or it shows a tab the app will not.
  if (def.kind === "tab" && def.parent && !previewSees(ctx, def.parent)) return false;
  var list = (ctx.grants[ctx.dept] || {})[String(ctx.rank)] || [];
  return list.indexOf(surfaceKey) !== -1;
};

// Assemble what previewSees() needs for one (department, rank).
var previewContext = function(data, draft, dept, rank){
  return {
    surfaces: (data && data.surfaces) || {},
    grants:   (draft && draft.grants) || {},
    dept:     dept,
    rank:     rank,
    rankless: (rank === null || rank === undefined)
  };
};

// ===== A CONFIRMED RETRY RESENDS, IT DOES NOT REBUILD =======================
//
// THE BUG THIS EXISTS TO MAKE IMPOSSIBLE (shipped 5eafd74, found on prod the
// same day). "Take over" posted save({[key]:true}) — the draft's managed map
// PLUS the surface being taken over. The endpoint refused it (409
// surface_stranded, correctly: only the Owner would be left). The confirm button
// then called save(undefined, true), which rebuilt the body from `visDraft` —
// and visDraft.managed is only updated on SUCCESS, so it had never gained the
// key. The retry therefore posted the CURRENT managed map with the flag: nothing
// was stranded, the guard passed, the endpoint wrote an unchanged document and
// answered 200. The screen said "Saved", the proposal closed, and the surface
// stayed on the sidebar rule. A silent no-op wearing a success message.
//
// So the refused body is kept verbatim and resent. The retry may add the flag
// and may change NOTHING else — any rebuild from live state can drift from what
// the server actually refused, and that drift is invisible because the second
// request succeeds.
//
// AT MODULE SCOPE so verify-page-visibility.js can RUN it. That is this file's
// standing rule for writers and resolvers (previewSees and
// applyThresholdToGrants live here for the same reason), and it is the rule the
// first version broke: the retry existed only inside a closure, so the gate
// could only assert that `save(undefined, true)` was called — which it was, and
// which was exactly the defect.
var confirmedRetryBody = function(refusedBody){
  return Object.assign({}, refusedBody || {}, { confirmStranded: true });
};

var agentAtTime = function(lead, liveName){
  if(!lead) return liveName || "-";
  // Anchor on the record's own creation date. EOI-cancelled → eoiDate (even if
  // a stale deal flag lingers from an earlier lifecycle); pure deal-cancelled →
  // dealDate. Fallback to the other date if the primary is blank.
  var isEoiCx = lead.eoiStatus==="EOI Cancelled";
  var isDealCx = !isEoiCx && (lead.dealStatus==="Deal Cancelled" || lead.status==="Deal Cancelled");
  var anchorStr = isDealCx ? (lead.dealDate||lead.eoiDate) : (lead.eoiDate||lead.dealDate);
  var anchor = anchorStr ? new Date(anchorStr).getTime() : NaN;
  if(isNaN(anchor)) return liveName || "-";
  var post = (lead.agentHistory||[])
    .filter(function(h){ return h && h.fromAgent && h.fromAgent!=="-" && h.date; })
    .map(function(h){ return { from:h.fromAgent, t:new Date(h.date).getTime() }; })
    .filter(function(h){ return !isNaN(h.t) && h.t > anchor; })
    .sort(function(a,b){ return a.t - b.t; });
  return post.length ? post[0].from : (liveName || "-");
};

// Resolve the sales agents on `lead` that the caller (managerial role) is
// allowed to address via "Send to specific sales". Mirrors the backend
// canSendFeedbackToTarget rule:
//   admin/operation → any sales agent with a slice on this lead
//   manager           → sales whose reportsTo is the manager OR a TL under them
//   team_leader       → sales whose reportsTo is the caller
// Returns [] for every other role / unsupported case.
var getEligibleSalesTargets = function(lead, cu, users) {
  if (!lead || !cu) return [];
  var role = cu.role;
  // operation is read-only on feedback by design — never allowed to address.
  if (role !== "admin" && role !== "manager" && role !== "team_leader") return [];
  var sliceIds = ((lead.assignments || [])
    .map(function(a){ var aid = a && a.agentId && a.agentId._id ? a.agentId._id : (a && a.agentId); return String(aid || ""); })
    .filter(Boolean));
  var sliceSet = {}; sliceIds.forEach(function(id){ sliceSet[id] = true; });
  var sliceUsers = (users || []).filter(function(u){
    if (!u || !u.active || positionOf(u) !== "sales") return false;
    return !!sliceSet[String(gid(u))];
  });
  if (role === "admin") return sliceUsers;
  var cuId = String(cu.id || gid(cu));
  if (role === "team_leader") {
    return sliceUsers.filter(function(u){
      var rt = u.reportsTo && u.reportsTo._id ? u.reportsTo._id : u.reportsTo;
      return String(rt || "") === cuId;
    });
  }
  // manager: direct reports + sales under TLs that report to this manager
  var tlIdsUnderMe = (users || []).filter(function(u){
    if (!u || positionOf(u) !== "team_leader") return false;
    var rt = u.reportsTo && u.reportsTo._id ? u.reportsTo._id : u.reportsTo;
    return String(rt || "") === cuId;
  }).map(function(u){ return String(gid(u)); });
  var allowedRTs = {}; allowedRTs[cuId] = true;
  tlIdsUnderMe.forEach(function(id){ allowedRTs[id] = true; });
  return sliceUsers.filter(function(u){
    var rt = u.reportsTo && u.reportsTo._id ? u.reportsTo._id : u.reportsTo;
    return !!allowedRTs[String(rt || "")];
  });
};

// Inline feedback composer used by the StatusModal (when a managerial role
// is changing status) and by the standalone "Add Feedback" modal. Renders
// the visibility selector + targetAgentId dropdown above a textarea.
// For sales role: renders ONLY the textarea — same UI as before this feature.
// onChange receives ({ text, visibility, targetAgentId }) on every keystroke.
var FeedbackComposer = function(p) {
  var role = p.cu && p.cu.role;
  // operation is excluded — read-only on feedback. They get the same plain
  // textarea sales sees here, but the parent page never sends a save through
  // this component for them (the "Add Feedback" button is hidden too).
  var isManagerial = role === "admin" || role === "manager" || role === "team_leader";
  // Team Leader on their own personal lead is treated as agent (no selector).
  var leadAgentId = p.lead && p.lead.agentId && p.lead.agentId._id ? p.lead.agentId._id : (p.lead && p.lead.agentId);
  var isOwnLead = role === "team_leader" && String(leadAgentId || "") === String(p.cu.id || gid(p.cu));
  var showSelector = isManagerial && !isOwnLead;
  var eligible = showSelector ? getEligibleSalesTargets(p.lead, p.cu, p.users || []) : [];
  var rows = p.rows || 3;
  var placeholder = p.placeholder || "";
  if (!showSelector) {
    return <textarea rows={rows} placeholder={placeholder} value={p.value && p.value.text || ""}
      onChange={function(e){ p.onChange({ text: e.target.value, visibility: "private", targetAgentId: "" }); }}
      style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box", resize:"vertical", fontFamily:"inherit" }}/>;
  }
  var v = p.value || { text:"", visibility:"private", targetAgentId:"" };
  var canSendToSales = eligible.length > 0;
  return <div>
    <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:8, padding:"8px 10px", background:"#F1F5F9", borderRadius:10, border:"1px solid #E2E8F0" }}>
      <div style={{ fontSize:11, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:"0.04em" }}>Visibility</div>
      <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:C.text, cursor:"pointer" }}>
        <input type="radio" name={"vis-"+(p.id||"f")} checked={v.visibility==="private"}
          onChange={function(){ p.onChange({ text:v.text, visibility:"private", targetAgentId:"" }); }}/>
        <span><b>Just me</b> <span style={{ color:C.textLight, fontSize:11 }}>— private note, only you see it</span></span>
      </label>
      <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:canSendToSales?C.text:C.textLight, cursor:canSendToSales?"pointer":"not-allowed" }}>
        <input type="radio" name={"vis-"+(p.id||"f")} checked={v.visibility==="to_sales"} disabled={!canSendToSales}
          onChange={function(){ p.onChange({ text:v.text, visibility:"to_sales", targetAgentId: v.targetAgentId || (eligible[0] ? String(gid(eligible[0])) : "") }); }}/>
        <span><b>Send to specific sales</b>{canSendToSales ? "" : <span style={{ color:C.textLight, fontSize:11 }}> — no eligible sales on this lead</span>}</span>
        {v.visibility==="to_sales" && canSendToSales && <span style={{ display:"inline-block", marginLeft:8, minWidth:150 }}><Select value={v.targetAgentId||""} searchable width={150} maxWidth={150}
          onChange={function(e){ p.onChange({ text:v.text, visibility:"to_sales", targetAgentId:e.target.value }); }}
          options={eligible.map(function(u){ return { value:gid(u), label:u.name }; })}/></span>}
      </label>
    </div>
    <textarea rows={rows} placeholder={placeholder} value={v.text}
      onChange={function(e){ p.onChange(Object.assign({}, v, { text: e.target.value })); }}
      style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box", resize:"vertical", fontFamily:"inherit" }}/>
  </div>;
};

// Returns the set of user IDs a team_leader is allowed to see data for —
// themselves plus active sales/team_leader users whose reportsTo points at
// them. Returns null for every other role; callers treat null as "no scope
// filter, pass data through". Used at the App root to scope leads, users,
// activities, and daily requests before they're handed to pages, so any
// out-of-team data that leaks past the backend (e.g. a WS broadcast — the
// server's per-client filter is sales-only, so team_leader sockets receive
// the unfiltered firehose) is dropped before pages render.
var getTeamScopeIds = function(currentUser, allUsers) {
  if (!currentUser || positionOf(currentUser) !== "team_leader") return null;
  var tlId = String(currentUser.id || currentUser._id || "");
  var ids = new Set([tlId]);
  (allUsers || []).forEach(function(u){
    // Include inactive members of the TL's team: their historical leads must
    // remain visible to the TL, and they must appear in agent filter dropdowns
    // (with "(inactive)" suffix). Active-only filtering for new assignments
    // still happens downstream where assignment lists are built.
    if (!u) return;
    if (positionOf(u) !== "sales" && positionOf(u) !== "team_leader") return;
    var rt = u.reportsTo && u.reportsTo._id ? String(u.reportsTo._id) : String(u.reportsTo || "");
    if (rt === tlId) ids.add(String(u._id));
  });
  return ids;
};

// Client-side mirror of the backend sales list overlay (server.js ~7947-7998).
// WS `lead_updated` broadcasts ship the ORIGINATING request's response body —
// for an admin action (e.g. a rotate) that's the full admin-scoped doc with
// EVERY assignment slice, including the previous owner's feedback. The server's
// per-client WS filter only gates DELIVERY; it does not redact slices, so a
// sales socket that receives such an event would upsert another agent's slice
// into p.leads and briefly leak it in the detail panel (until the corrective
// fetchSingleLead lands). Re-apply the same overlay the REST list endpoint
// applies, BEFORE the doc enters local state.
// Only role "sales" is scoped by the backend list endpoint; every other role
// (admin / operation / manager / team_leader / HR) gets the doc unchanged,
// exactly as the backend's else-branch leaves it. NOTE: the sibling dr_updated
// handler shares this leak pattern and is intentionally NOT touched here.
var scopeWsLeadForViewer = function(rawLead, currentUser) {
  if (!rawLead || !currentUser || positionOf(currentUser) !== "sales") return rawLead;
  var uid = String(currentUser.id || currentUser._id || "");
  var obj = Object.assign({}, rawLead);
  // Same EOI/DoneDeal classification the FE already uses (see buildDealItems).
  var isEoiOrDoneDeal = obj.status === "DoneDeal" || obj.status === "EOI"
      || obj.globalStatus === "donedeal" || obj.globalStatus === "eoi"
      || (obj.eoiStatus && obj.eoiStatus !== "EOI Cancelled" && obj.eoiStatus !== "");
  // "Which slice is mine" — exactly the backend match: own agentId, not removed.
  var myAssign = (obj.assignments || []).find(function(a){
    if (!a || a.removedAt) return false;
    var aid = a.agentId && a.agentId._id ? a.agentId._id : a.agentId;
    return String(aid) === uid;
  });
  var topAgentId = obj.agentId && obj.agentId._id ? obj.agentId._id : obj.agentId;
  var isLegacyOwner = !myAssign && String(topAgentId || "") === uid && isEoiOrDoneDeal;
  // Wipe top-level per-agent text unless this is legacy ownership (then it's
  // the caller's own pre-assignments[] data).
  if (!isLegacyOwner) { obj.notes = ""; obj.lastFeedback = ""; }
  if (myAssign) {
    if (!isEoiOrDoneDeal) {
      var assignStatus = myAssign.status === "New Lead" ? "NewLead" : myAssign.status;
      obj.status = assignStatus || obj.status;
    }
    obj.notes = myAssign.notes !== undefined ? myAssign.notes : "";
    // Mirror the backend guard (server.js list/single): a frozen deal's budget
    // is deal-level, so keep top-level Lead.budget instead of the per-agent
    // slice (which defaults to "" and would render the row as "-"). Uses the
    // same isEoiOrDoneDeal boolean that already gates the status overlay above.
    if (!isEoiOrDoneDeal) {
      obj.budget = myAssign.budget !== undefined ? myAssign.budget : obj.budget;
    }
    obj.callbackTime = myAssign.callbackTime !== undefined ? myAssign.callbackTime : obj.callbackTime;
    obj.lastFeedback = myAssign.lastFeedback !== undefined ? myAssign.lastFeedback : "";
    obj.nextCallAt = myAssign.nextCallAt !== undefined ? myAssign.nextCallAt : obj.nextCallAt;
    if (myAssign.lastActionAt) obj.lastActivityTime = myAssign.lastActionAt;
    if (myAssign.assignedAt) obj.assignedAt = myAssign.assignedAt;
    obj.notesAuthorId   = myAssign.notesAuthorId   || null;
    obj.notesAuthorName = myAssign.notesAuthorName || "";
    obj.notesAuthorRole = myAssign.notesAuthorRole || "";
    // After rotation the top-level agentId points to the NEW owner; the old
    // agent must see themselves as the owner so the view stays clean.
    obj.agentId = myAssign.agentId;
  }
  // Strip every other agent's data + rotation metadata.
  obj.agentHistory = (myAssign && myAssign.agentHistory && myAssign.agentHistory.length > 0) ? myAssign.agentHistory : [];
  obj.assignments = myAssign ? [myAssign] : [];
  obj.previousAgentIds = [];
  obj.rotationCount = 0;
  obj.lastRotationAt = null;
  obj.privateNotes = [];
  // lead.history: drop rotation/assignment events + anything not authored by
  // the caller (mirrors the backend's salesSelfName filter).
  var selfName = String(currentUser.name || "");
  obj.history = (obj.history || []).filter(function(h){
    if (!h) return false;
    var ev = String(h.event || "");
    if (ev === "rotated" || ev === "assigned" || ev === "reassigned") return false;
    if (selfName && String(h.byUser || "") !== selfName) return false;
    return true;
  });
  return obj;
};

// WhatsApp chooser — shows popup to pick WhatsApp or WhatsApp Business
var WaChooser = function(p) {
  if(!p.show) return null;
  var phone = waPhone(p.phone);
  // Android intent URLs to open specific app
  var isAndroid = /android/i.test(navigator.userAgent);
  var waUrl = isAndroid
    ? "intent://send?phone=+"+phone+"#Intent;scheme=whatsapp;package=com.whatsapp;end"
    : "https://wa.me/"+phone;
  var waBizUrl = isAndroid
    ? "intent://send?phone=+"+phone+"#Intent;scheme=whatsapp;package=com.whatsapp.w4b;end"
    : "https://wa.me/"+phone;
  return <div data-overlay-above="true" onClick={p.onClose} style={{ position:"fixed", inset:0, zIndex:999, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
    <div onClick={function(e){e.stopPropagation();}} style={{ background:"#fff", borderRadius:"18px 18px 0 0", padding:"20px 16px 32px", width:"100%", maxWidth:480 }}>
      <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16, textAlign:"center" }}>Open with WhatsApp</div>
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        <a href={waUrl} target="_blank" rel="noreferrer" onClick={p.onClose} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderRadius:12, background:"#DCFCE7", textDecoration:"none" }}>
          <svg viewBox="0 0 24 24" width="28" height="28" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:"#15803D" }}>WhatsApp</div>
            <div style={{ fontSize:11, color:"#6B7280" }}>WhatsApp Personal</div>
          </div>
        </a>
        <a href={waBizUrl} target="_blank" rel="noreferrer" onClick={p.onClose} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderRadius:12, background:"#E8F5E9", textDecoration:"none" }}>
          <svg viewBox="0 0 24 24" width="28" height="28" fill="#1B5E20"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:"#1B5E20" }}>WhatsApp Business</div>
            <div style={{ fontSize:11, color:"#6B7280" }}>WhatsApp for Business</div>
          </div>
        </a>
      </div>
      <button onClick={p.onClose} style={{ width:"100%", marginTop:12, padding:"12px", borderRadius:12, border:"none", background:"#F1F5F9", cursor:"pointer", fontSize:14, fontWeight:600, color:C.textLight }}>Cancel</button>
    </div>
  </div>;
};
var waPhone = function(phone) {
  if (!phone) return "";
  var p = phone.replace(/\s+/g, "").replace(/[^\d+]/g, "");
  if (p.startsWith("+")) return p.slice(1);
  if (p.startsWith("0")) return "20" + p.slice(1);
  return p;
};
var cleanPhone = function(phone) {
  if (!phone) return "";
  var p = phone.replace(/\s+/g, "").replace(/[^\d+]/g, "");
  if (!p) return "";
  if (p.startsWith("+")) return p;
  if (p.startsWith("20") && p.length > 10) return "+" + p;
  if (p.startsWith("0")) return p;
  if (p.length === 10) return "0" + p;
  return p;
};
// Response Watch tap log. A holder tapping Call / WhatsApp on a LEAD is a
// first-response signal; the server records it (holder-only, PII-free) via
// POST /api/leads/:id/contact-tap. Fire-and-forget: a failed log must never
// block the dial / the wa.me open. Every Call / WhatsApp control on a lead
// surface goes through THIS helper (gate: verify-contact-tap.js) — a bare
// tel:/wa.me anchor on a lead surface is invisible to Response Watch.
var logContactTap = function(lead, channel, token, csrfToken) {
  var id = gid(lead);
  if (!id) return;
  apiFetch("/api/leads/" + encodeURIComponent(id) + "/contact-tap", "POST", { channel: channel }, token, csrfToken).catch(function(){});
};
var timeAgo = function(d, t) {
  if (!d) return "-";
  var diff = (Date.now() - new Date(d).getTime()) / 60000;
  // Defensive: future-dated values (data-entry bugs — e.g. a dealDate typed
  // as 2026-12-15 when meant 2026-02-15) make diff negative, which would
  // satisfy `diff < 1` below and render "Just now". Render the ISO date
  // instead so the bad date is visually obvious.
  if (diff < 0) return new Date(d).toISOString().slice(0,10);
  if (diff < 1) return t.just;
  if (diff < 60) return Math.floor(diff) + " " + t.minutes + " " + t.ago;
  if (diff < 1440) return Math.floor(diff / 60) + " " + t.hours + " " + t.ago;
  return Math.floor(diff / 1440) + " " + t.days + " " + t.ago;
};

// Forward-looking, compact time formatter for CallbackBell rows. The bell
// answers "when is the next call due?" — future rows read "in 2h" / "in 3d",
// overdue rows read "Overdue 2d". Compact units (m/h/d) with no space
// between qty and unit keep the row chip readable at 10px font.
var callbackTimeStr = function(d) {
  if (!d) return "-";
  var ts = new Date(d).getTime();
  if (isNaN(ts)) return "-";
  var diffMs = ts - Date.now();
  var absMin = Math.abs(diffMs) / 60000;
  var qty, unit;
  if (absMin < 60) { qty = Math.max(1, Math.round(absMin)); unit = "m"; }
  else if (absMin < 1440) { qty = Math.max(1, Math.round(absMin / 60)); unit = "h"; }
  else { qty = Math.max(1, Math.round(absMin / 1440)); unit = "d"; }
  return diffMs >= 0 ? ("in " + qty + unit) : ("Overdue " + qty + unit);
};

// 2026-05-18 — absolute date+time formatter for notification dropdowns.
// Used by the deal-bell and rotation-bell where every row should read as
// its own discrete event time. Relative "X days ago" hides per-row
// distinction when notifications cluster within the same window (the
// "1 days ago for every row" symptom we shipped to fix in 0e91a2c).
// Format: "15 May 2026 · 2:32 PM", Cairo, via the one clock formatter
// (src/lib/timeFormat.js, 12-hour everywhere — owner brief 2026-10-27).
var absoluteDateTime = function(d) {
  if (!d) return "-";
  // A DATE-ONLY value has no clock to show, and printing one INVENTS data.
  // `new Date("2026-08-11")` is midnight UTC by spec, so getHours() in Cairo
  // reads 03:00 (02:00 in winter) — the offset, not the deal. Measured on
  // production 2026-08-26: all 54 dealDate and all 28 eoiDate values are
  // date-only strings, and the pending-approvals bell was rendering
  // "11 Aug 2026 · 03:00" for every one of them. The Deals page fixed this
  // class in 2de32da via fmtDayOnly; the bell was the site it missed.
  // Guarded HERE rather than at the call site so a future caller cannot
  // reintroduce it. `s.createdAt` (Sessions) is a full ISO instant, does not
  // match, and keeps its time.
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(d))) return fmtDayOnly(d);
  var dt = new Date(d);
  if (isNaN(dt.getTime())) return "-";
  return fmtDateTime12(dt);
};

// Smart search: name, phone, last 4 digits
// ===== fetchAllPages — NO CEILING (owner ruling 2026-08-14) =====
// Walks page=1..totalPages and returns every row, so a list can never silently
// stop at a constant. Termination is server-authoritative: the loop stops when
// the server says there are no more pages, not at a number chosen here.
//
// WHY THIS SHAPE AND NOT loadMore. The four surfaces this replaced (Deals, EOI,
// Archive, Settings audit) render plain tables, not virtualised ones — there is
// no endReached to hang incremental loading off without rewriting each page.
// This removes the ceiling today with no UI change; if any of these books ever
// grows large enough to want incremental loading, the migration is to
// virtualise the table and page this same URL, which is exactly what
// usePagedLeads already does for Leads.
//
// Rejects on failure rather than resolving a partial list, so a caller can never
// mistake a half-loaded set for the whole book (every call site has a .catch).
//
// KEYSET WALK (2026-08-15). This used to page with ?page=N, i.e. skip/limit on
// the server. skip re-runs the query per page, so any write that reorders the
// result mid-walk moves rows across a page boundary — one returned twice, one
// never returned. It is not a rare race: Deals and EOI sort by `updatedAt`, so
// ANY agent editing ANY row during the walk shifts the whole book down one.
//
// So the walk now asks for `?paging=cursor` and follows `nextCursor`, which
// pages by `_id` — immutable and unique, therefore immovable by a concurrent
// write. See the keysetPaging block in server.js for the full reasoning.
//
// THREE compatibility properties, because Vercel and Railway deploy the same
// push independently and either side can be live alone:
//   - ?page= is still sent, so a backend that predates cursor support answers
//     the old walk exactly as before.
//   - Cursor mode is detected by the RESPONSE carrying a `nextCursor` key, not
//     by assuming the request was honoured. An old backend omits it and the
//     loop falls back to the totalPages walk.
//   - Rows are deduped by _id regardless of mode, so even on the legacy path a
//     row that shifts backwards can no longer be delivered twice. (Dedupe
//     cannot recover a SKIPPED row — only the cursor can.)
//
// Termination in cursor mode is `nextCursor === null` and NOT an empty page:
// /api/leads post-filters each page with the staleness gate and can legally
// return a page of zero rows with more book behind it.
// onProgress(loadedSoFar, totalOrNull) fires after every page. Optional and
// purely observational — it exists so an export button can say "Exporting…
// 1,200 / 4,825" instead of freezing for the length of a full-book walk. The
// total comes from the SERVER's own count on each page (it counts the whole
// matching set, not the page), and is null on a response that omits it.
async function fetchAllPages(urlBase, token, pageSize, onProgress) {
  var size = pageSize || 200;
  var sep  = urlBase.indexOf("?") === -1 ? "?" : "&";
  var out  = [];
  var seen = new Set();
  var page = 1;
  var cursor = null;
  while (true) {
    var url = urlBase + sep + "page=" + page + "&limit=" + size + "&paging=cursor";
    if (cursor) url += "&cursor=" + encodeURIComponent(cursor);
    var r = await apiFetch(url, "GET", null, token);
    var rows = (r && Array.isArray(r.data)) ? r.data : (Array.isArray(r) ? r : []);
    for (var i = 0; i < rows.length; i++) {
      // _rowKey FIRST. Two transactions on one client are two rows sharing the
      // lead's _id, so deduping on _id alone silently DROPS the second one —
      // exactly the row the per-transaction design exists to show.
      var k = (rows[i] && rows[i]._rowKey) ? String(rows[i]._rowKey)
            : ((rows[i] && (rows[i]._id || rows[i].id)) ? String(rows[i]._id || rows[i].id) : null);
      if (k) { if (seen.has(k)) continue; seen.add(k); }
      out.push(rows[i]);
    }
    if (onProgress) {
      try {
        onProgress(out.length, (r && typeof r.total === "number") ? r.total : null);
      } catch (e) {}   // a broken reporter must never abort the walk
    }
    var cursorMode = !!(r && typeof r === "object" && !Array.isArray(r)
      && Object.prototype.hasOwnProperty.call(r, "nextCursor"));
    if (cursorMode) {
      // A server that hands back the cursor it was given is not advancing;
      // stop rather than spin. This is a liveness guard, NOT a row ceiling —
      // it can only fire when a page failed to move the walk forward.
      if (!r.nextCursor || r.nextCursor === cursor) break;
      cursor = r.nextCursor;
      page++;                 // cosmetic in cursor mode; the server ignores it
      continue;
    }
    var tp = (r && typeof r.totalPages === "number") ? r.totalPages : 1;
    if (page >= tp || rows.length === 0) break;
    page++;
  }
  return out;
}

// ===== A WS BURST MUST NOT REDOWNLOAD THE BOOK (2026-08-26) =================
//
// The Deals and EOI lists had `p.cbBust` in their fetch deps, so every websocket
// lead burst re-ran fetchAllPages over the entire book. cbBust is already
// debounced 1500ms off the socket, which coalesces ONE burst — it does not
// coalesce a working day. Measured in Railway's own logs: FOUR /api/deals calls
// in 19.2 seconds (gaps 9.3s, 7.1s, 2.8s) for a single page view, each one a
// complete server-side rebuild.
//
// Live row updates do not need this and never did: patchDealRow /
// patchDealRowsByLead (and patchEoiRow / patchEoiRowsByLead) already apply WS
// changes to the row in place. The refetch is only a SAFETY NET against a
// change no patcher covers.
//
// THROTTLE, NOT DEBOUNCE, and the difference is the whole point. A debounce
// resets on every event, so under sustained activity — exactly when the data is
// moving — it would never fire and the list could go stale indefinitely. This
// bumps at most once per `ms`, and at least once within `ms` of any change, so
// "cannot be more than ~30s stale" is actually true rather than true only when
// the office is quiet.
//
// The mount fetch is NOT counted: the effect below already runs once on mount,
// and bumping again would restore the double-fetch this removes.
var useStaleRefetchKey = function(bust, ms){
  var [key, setKey] = useState(0);
  var timer = useRef(null);
  var mounted = useRef(false);
  // The timer lives in a ref, not in this effect's cleanup — a cleanup would
  // clear it on every `bust` change and quietly turn the throttle back into a
  // debounce. Only unmount cancels it.
  useEffect(function(){ return function(){ if (timer.current) { clearTimeout(timer.current); timer.current = null; } }; }, []);
  useEffect(function(){
    if (!mounted.current) { mounted.current = true; return; }
    if (timer.current) return;                       // a bump is already scheduled
    timer.current = setTimeout(function(){
      timer.current = null;
      setKey(function(k){ return k + 1; });
    }, ms);
  }, [bust, ms]);
  return key;
};
// How long a list may lag a websocket change before it re-reads the book.
var STALE_REFETCH_MS = 30000;

// ===== usePageVisible — tab-visibility gate for safety polls (2026-09-13) ===
// A dashboard-style effect that both reacts to WS events (useStaleRefetchKey)
// and runs a periodic safety poll must stop polling a BACKGROUND tab — nobody
// is looking at it, and every open tab in the office polling in lockstep is
// server load nobody spends. Returns the current visibility and re-renders on
// visibilitychange, so a caller gates its effect on this boolean and gets a
// free catch-up fetch the moment the tab is shown again (the effect re-runs).
var usePageVisible = function(){
  var [visible, setVisible] = useState(function(){
    return typeof document === "undefined" || document.visibilityState !== "hidden";
  });
  useEffect(function(){
    if (typeof document === "undefined") return;
    var onVis = function(){ setVisible(document.visibilityState !== "hidden"); };
    document.addEventListener("visibilitychange", onVis);
    return function(){ document.removeEventListener("visibilitychange", onVis); };
  }, []);
  return visible;
};

// ===== TWO-PHASE BOOTSTRAP — THE OPEN PAGE'S FETCH GOES FIRST ==============
//
// A cold load fired 16 GETs inside 5ms of each other, and the fetch that
// actually draws the open page was one of them. Worse, the boot loader
// (08cb739) covers the tree until loadData settles, so the rows could be in the
// DOM and still invisible: measured in this harness against the shipped bundle,
// a Deals cold load had its 54 rows rendered at +585ms and showed them at
// +3,603ms — every one of those 3 seconds spent behind the overlay waiting for
// /api/activities and the 100-row /api/leads bootstrap, neither of which the
// Deals page reads.
//
// PHASE 1 is what the first frame cannot be drawn without: the access envelope
// and the agent names (/api/me, /api/users — a rank-blind first frame is
// forbidden), the OPEN PAGE's own fetch, project weights, and — for a landing
// page whose first paint reads them — the shared lists below. The loader lifts
// when phase 1 settles.
//
// PHASE 2 is everything else: the other shared lists, and the badge/bell counts
// that render 0 until answered and are wrong for nobody while pending. It is
// released by the OPEN PAGE'S OWN LOADED FLAG, never by requestIdleCallback and
// never by a timer — an idle callback fires while the page is still empty,
// which is the state phase 2 exists to stay out of.
var BootPhase2Context = createContext(false);
var useBootPhase2 = function(){ return useContext(BootPhase2Context); };

// WHICH SHARED LISTS A LANDING PAGE'S FIRST PAINT READS — and therefore which
// of them CANNOT be deferred without replacing a number the page already shows
// with a zero.
//
// Read as an allow-list, not a rule: a page named here is one whose first paint
// was READ and found not to touch the list it gives up. Everything absent keeps
// all three in phase 1, i.e. boots exactly as it does today. That default is the
// safe half — a page added to this switch without its first paint being read
// costs nothing until somebody names it here.
//
//   deals / eoi     — dealsSource and eoiScope are server-fetched ONLY (the
//                     `: p.leads` fallbacks were deleted for the cross-agent
//                     flash). The two remaining p.leads reads on each page are
//                     a deep-link resolve off p.initSelected and getStages(),
//                     neither of which runs on a cold load.
//   meetings        — reads none of the three at all.
//   leads           — p.leads IS its list. Its three p.activities reads are the
//                     Reports→Lead-Aging drill-down (p.specialFilter==="aging",
//                     null at boot) and the selected row's activity log.
//
// TWO PAGES ARE DELIBERATELY ABSENT, and both were MEASURED rather than reasoned
// about. Absent means "keeps all three", i.e. boots exactly as it does today.
//
//   dashboard — its first paint reads all three: the KPI strip counts meetings
//               and deals out of p.dailyReqs, Today's Activity reads
//               p.activities, the lead/DR id maps read both. Deferring any of
//               them paints zeros where it paints numbers.
//
//   dailyReq  — NOT because of the shared lists (its only p.leads read is inside
//               the status-change handler) but because of what lifting the
//               loader early UNCOVERS. Its chips and summary cards read
//               `drCounts` from GET /api/daily-requests/counts, and that fetch
//               sits behind its own 1000ms debounce; until it lands they fall
//               back to `requests`, the loaded 100-row window. Measured in the
//               harness: the first uncovered frame went from
//               "All (4162) · Potential (1000) · Had Meeting (462) · Total 4162"
//               to "All (100) · Potential (25) · Had Meeting (0) · Total 100".
//               That is not a badge waiting to be answered, it is a smaller
//               number that reads as a real one — the loader had been hiding
//               the fallback. Making the DR page's counts arrive with its rows
//               is a different change; this one leaves the page alone.
var BOOT_PHASE1_LISTS = {
  deals:    [],
  eoi:      [],
  meetings: [],
  leads:    ["leads"]
};
var BOOT_ALL_LISTS = ["leads","activities","dailyRequests"];
var bootPhase1Lists = function(pageId){
  var v = BOOT_PHASE1_LISTS[pageId];
  return v ? v : BOOT_ALL_LISTS;
};
// Pages whose rows come from their OWN fetch rather than from the bootstrap, so
// phase 2 waits on that page's loaded flag. Every other page's rows ARE phase 1,
// so phase 1 settling is its loaded flag.
var BOOT_PAGE_OWNS_ROWS = { deals:true, eoi:true, meetings:true };

// Display order for a fetchAllPages book. The keyset walk returns `_id` order
// (it must — see fetchAllPages), so any surface that used to inherit the
// server's display sort restores it HERE, over the assembled whole book. That
// is exact by construction, and strictly better than trusting a server sort the
// walk can no longer honour: the order no longer depends on how the rows were
// paged, which is the same "pagination unit" class of bug the leads list hit.
//
// Descending on each date field in turn, `_id` desc last so the order is TOTAL
// — rows sharing a timestamp (bulk imports, one action writing several audit
// rows) then have one defined order instead of an arbitrary one. Matches the
// server's own legacy sort exactly, so nothing visibly moves.
var byDatesDesc = function(fields) {
  return function(a, b) {
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      var av = (a && a[f]) ? new Date(a[f]).getTime() : 0;
      var bv = (b && b[f]) ? new Date(b[f]).getTime() : 0;
      if (av !== bv) return bv - av;
    }
    var ai = String((a && a._id) || ""), bi = String((b && b._id) || "");
    return ai < bi ? 1 : ai > bi ? -1 : 0;
  };
};

// The LeadsPage visibility predicate (allVisible's non-isReq branch), lifted to
// module scope so the App-level pager and every LeadsPage fetch test the SAME
// thing. A row that fails this never renders on the Leads page, so a page made
// entirely of such rows contributes nothing and must not end the chase.
var leadsPageScopeVisible = function(l) {
  if (!l) return false;
  if (l.archived) return false;
  if (l.source === "Daily Request") return false;
  if (l.status === "EOI" || l.status === "DoneDeal") return false;
  if (l.status === "Deal Cancelled" && l.eoiStatus !== "EOI Cancelled") return false;
  return true;
};

// ===== THE DATE AXIS A ROW IS JUDGED ON — client twin of sales-receipt-window.js =====
// The server windows a list on ONE of two fields, by role: a sales caller's
// rows are "reached ME in this period" (the caller's own active slice's
// assignedAt, createdAt when they hold no slice — owner ruling 2026-08-20, R9);
// every other role's are "entered the system" (createdAt, ruling 2026-08-14).
// /api/leads and /api/leads/counts share that clause by import, so the chips
// and the rows agree on membership by construction.
//
// LeadsPage ALSO re-narrows client-side — for rows that came from the
// un-windowed bootstrap (the All tab, the union under a status chip). Until
// 2026-10-15 that re-narrow read createdAt for EVERY role, i.e. the admin axis
// under a sales list. Measured live as Nagy Naeem (sales), Today: the chips
// counted New Lead 6 / Hot Case 5 / No Answer 5 on receipt time, the server
// returned exactly those rows, and the client then dropped every row whose
// CUSTOMER entered the system before today — 0 / 4 / 0 rendered under chips
// reading 6 / 5 / 5. Rotated-in leads are precisely the rows the receipt
// window exists to show.
//
// Mirrors sales-receipt-window.js field for field: the row's `assignedAt` is
// written by the sales overlay from the caller's own slice, and the createdAt
// fallback is that module's second $or arm. CRA cannot import across the
// repo root and Railway ships only /crm-aro-backend, so this is a declared twin,
// gated equal to the server clause by verify-leads-date-axis.js — the same
// discipline as ROLE_DEFINITIONS_FE. Module scope so that gate can RUN it.
var leadDateAxisMs = function(l, receipt) {
  if (!l) return 0;
  if (receipt) return new Date(l.assignedAt || l.createdAt || 0).getTime();
  return l.createdAt ? new Date(l.createdAt).getTime() : 0;
};
var leadInDateWindow = function(l, win, receipt) {
  if (!win) return true;
  var t = leadDateAxisMs(l, receipt);
  return t >= win[0] && t <= win[1];
};

// ===== WHEN THE CHIP-COUNTS FETCH WAITS — a new question goes out at once ====
// The Leads page's /api/leads/counts fetch coalesces WS bursts (cbBust) with a
// 1000ms debounce. That wait must apply ONLY to a refresh of the question
// already on screen: applied to a period or filter change as well, every
// change showed "…" for a second before the request was even sent, and a
// second click inside that second cancelled the first request outright — the
// All Time strip Nagy Naeem saw on 2026-10-15 had never sent its request.
// `lastAskedKey` is the key of the last request actually ISSUED; `key` is the
// one the current filters build. Module scope so verify-leads-date-axis.js can
// run both branches.
var COUNTS_REFRESH_DEBOUNCE_MS = 1000;
var countsFetchDelayMs = function(lastAskedKey, key) {
  return lastAskedKey === key ? COUNTS_REFRESH_DEBOUNCE_MS : 0;
};

// ===== WHAT THE CHIP STRIP SHOWS WHILE COUNTS ARE IN FLIGHT (2026-10-17) =====
// Never "(…)" when a number we can legitimately show exists. f4a4625 made every
// first paint and every period change read "(…)" on all chips. The four states:
//   1. nothing ever received  -> label only (the strip reserves the width)
//   2. same question refreshed -> its own numbers stay, never blanked
//   3. new question, unseen    -> the PREVIOUS numbers, muted — a narrowed period
//                                 never shows another period's numbers un-muted
//   4. the request FAILED      -> no body; the "Counts unavailable" notice shows
// `prevBody` is the last body that was shown for any question. Module scope so
// verify-leads-date-axis.js can run every state.
var leadsChipCountsView = function(byUrl, url, errUrl, prevBody) {
  if (errUrl === url) return { body:null, muted:false, failed:true };
  var own = byUrl && byUrl[url];
  if (own) return { body:own, muted:false, failed:false };
  if (prevBody) return { body:prevBody, muted:true, failed:false };
  return { body:null, muted:false, failed:false };
};
// The text after a chip label. null count = no number yet: nothing is printed
// (the caller reserves the width), never a placeholder glyph.
var leadsChipCountText = function(cnt, isMobile) {
  if (cnt === null || cnt === undefined) return "";
  return isMobile ? " " + cnt : " (" + cnt + ")";
};
// Per-session memory of received counts, keyed by token then URL, so a remount
// of the page (navigate away and back) paints the numbers it already had.
var COUNTS_MEMO = {};
var COUNTS_LAST = {};

// Has this lead ever left a previous holder? THE basis for the 🌱 Not Rotated
// chip, on every side (badge fallback, row render predicate) — and it must stay
// in lockstep with the server's { "previousAgentIds.0": { $exists: false } }
// (server.js ~L12316 filter / ~L13888 counts facet).
//
// NOT `rotationCount` (2026-08-14): bulk-reassign and bulk-redistribute-backlog
// `$inc` rotationCount unconditionally while pushing previousAgentIds only when
// a prior holder existed, so a lead's FIRST assignment counts as a rotation.
// Measured on prod: 70 of the 92 leads that have never left their first agent
// carried rotationCount:1 and were hidden from the chip, which read 22 of 92.
// previousAgentIds is pushed ONLY on a real hand-off, so empty == never rotated.
var leadEverRotated = function(l) {
  return !!(l && l.previousAgentIds && l.previousAgentIds.length > 0);
};

var matchSearch = function(lead, q) {
  if (!q) return true;
  q = q.toLowerCase().trim();
  if (lead.name.toLowerCase().includes(q)) return true;
  if (lead.phone.includes(q)) return true;
  if (lead.phone.slice(-4).includes(q)) return true;
  if (lead.phone2 && lead.phone2.includes(q)) return true;
  return false;
};

// Excel/CSV import
var loadXLSX = function() {
  return new Promise(function(resolve) {
    if (window.XLSX) { resolve(window.XLSX); return; }
    var s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    s.onload = function() { resolve(window.XLSX); };
    document.head.appendChild(s);
  });
};
// Map an import row (keys already lowercased by handleImport) to a lead payload.
// `newLayout` is decided ONCE from the header set (reliable, unlike per-cell
// guessing): a sheet carrying a Unit Type / Development column is the corrected
// 3-field template → campaign = ad campaign, project = development, unitType =
// unit, marked fieldsV2. A legacy sheet (only a campaign/project column, the old
// inverted convention) keeps the prior behaviour: that column → project, v1.
var rowToLead = function(row, newLayout) {
  var g = function() { for (var i = 0; i < arguments.length; i++) { var v = row[arguments[i]]; if (v) return String(v).trim(); } return ""; };
  var base = { name: g("name","اسم الleads"), phone: g("phone","phone number","موبايل","رقم"), phone2: g("phone2","phone2 ","phone 2","هاتف إضافي","هاتف2","رقم2","موبايل2"), email: g("email"), budget: g("budget"), source: g("source","المصدر"), notes: g("notes") };
  if (newLayout) {
    base.campaign = g("campaign","ad campaign","campaign name","الكامبين");
    base.project  = g("development","project","المشروع","التطوير");
    base.unitType = g("unit type","unittype","unit_type","نوع الوحدة","الوحدة");
    base.fieldsV2 = true;
  } else {
    base.project = g("project","campaign","المشروع","الكامبين") || "";
  }
  return base;
};

// ===== UI COMPONENTS =====
var Badge = function(p) { return <span style={{ background: p.bg||"#F1F5F9", color: p.color||C.text, padding:"4px 12px", borderRadius:20, fontSize:12, fontWeight:600, whiteSpace:"nowrap", cursor:p.onClick?"pointer":"default", border:p.dashed?"1px dashed "+(p.color||C.text):"none", display:"inline-flex", alignItems:"center", gap:4 }} onClick={p.onClick}>{p.children}</span>; };
var Card = function(p) { return <div ref={p.innerRef} style={Object.assign({ background:"#fff", borderRadius:14, padding:p.p!==undefined?p.p:22, boxShadow:"0 1px 4px rgba(0,0,0,0.07)", border:"1px solid #E8ECF1" }, p.style||{})}>{p.children}</div>; };

// Shared helper for closing any side panel when the user clicks outside it.
// Strategy: Option A — a document-level mousedown listener that closes when
// the target is not inside the panel AND is not inside a higher-stacking
// overlay (Modal at z:600, status dropdowns at z:500, etc). Walks ancestors
// and bails out if any is a fixed/absolute element at zIndex >= 400 other
// than the panel itself — this keeps the Edit Modal, status picker, and
// confirm dialogs from accidentally closing the panel underneath. Esc also
// closes. Pair this with ref={outsideCloseRef} on the panel's outermost node.
function useOutsideClose(open, onClose, ignoreSelector){
  var ref = useRef(null);
  var cbRef = useRef(onClose);
  cbRef.current = onClose;
  useEffect(function(){
    if (!open) return;
    // Overlay-above check (2026-05-06): any ancestor of the click
    // target tagged data-overlay-above="true" (Modal, status dropdowns,
    // search overlays, etc.) means the click happened in something
    // stacked above the panel — don't close. closest() is O(depth)
    // with no layout recalc; the previous getComputedStyle ancestor
    // walk caused ~3.5s of layout thrashing per click on busy pages
    // (root cause of the reported "black dropdown" symptom). Every
    // overlay-style element with z-index >= 400 carries the
    // data-overlay-above attribute — see grep across this file.
    function isInOverlayAbove(target, panel){
      if (panel.contains(target)) return false;
      return !!(target.closest && target.closest('[data-overlay-above="true"]'));
    }
    function onMouseDown(e){
      var panel = ref.current;
      if (!panel) return;
      if (panel.contains(e.target)) return;
      if (isInOverlayAbove(e.target, panel)) return;
      // Opt-in (2026-06-06): ignore mousedowns on the panel's own trigger
      // element(s). Without this, a re-click on the trigger closes here on
      // mousedown, then the trigger's React onClick re-opens on click (the
      // mousedown-driven re-render flipped its isSel) — a close-then-reopen
      // double-toggle. Backward-compatible: no-op when ignoreSelector is
      // undefined (the existing two-arg call sites behave exactly as before).
      if (ignoreSelector && e.target.closest && e.target.closest(ignoreSelector)) return;
      cbRef.current && cbRef.current();
    }
    function onKey(e){ if (e.key === "Escape") { cbRef.current && cbRef.current(); } }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return function(){
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return ref;
}
var StatCard = function(p) {
  var I = p.icon;
  return <div onClick={p.onClick} style={{ background:"#fff", borderRadius:14, padding:"16px 18px", flex:1, minWidth:150, boxShadow:"0 1px 4px rgba(0,0,0,0.07)", border:"1px solid #E8ECF1", display:"flex", alignItems:"center", gap:13, cursor:p.onClick?"pointer":"default", transition:"transform 0.15s,box-shadow 0.15s" }}
    onMouseEnter={function(e){if(p.onClick){e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 6px 18px rgba(0,0,0,0.12)";}}}
    onMouseLeave={function(e){e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,0.07)";}}>
    <div style={{ width:44, height:44, borderRadius:12, background:p.c+"18", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><I size={20} color={p.c}/></div>
    <div><div style={{ fontSize:11, color:C.textLight, marginBottom:3 }}>{p.label}</div><div style={{ fontSize:22, fontWeight:700, color:C.text }}>{p.value}</div></div>
  </div>;
};
var Modal = function(p) {
  if (!p.show) return null;
  // className hooks so the global mobile stylesheet can make the overlay
  // full-screen on phones without disturbing desktop centering.
  return <div className="crm-modal" data-overlay-above="true" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.52)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:600, padding:16 }} onClick={p.onClose}>
    <div className="crm-modal-inner" style={{ background:"#fff", borderRadius:18, padding:26, width:"100%", maxWidth:p.w||500, maxHeight:"90vh", overflowY:"auto", overflowX:"hidden", boxSizing:"border-box" }} onClick={function(e){e.stopPropagation();}}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
        <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:C.text }}>{p.title}</h2>
        <button onClick={p.onClose} style={{ width:28, height:28, borderRadius:7, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><X size={14}/></button>
      </div>
      {p.children}
    </div>
  </div>;
};
// Full-screen biometric lock overlay. Rendered only when the app is locked
// (native + backgrounded ≥1 min, or cold launch with a session). Never shows
// on web. The button re-fires the native prompt — there is NO bypass: a device
// with a lock can only open by passing device auth. Failure never logs out.
var BiometricLockScreen = function(p) {
  return <div style={{ position:"fixed", inset:0, zIndex:99999, overflow:"hidden", background:"linear-gradient(170deg,#fbfbfd,#f4f1ec)",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      gap:18, fontFamily:"Cairo,sans-serif", color:C.text }}>
    {/* Soft decorative background accents — behind the content, partially off-screen */}
    <div style={{ position:"absolute", top:-160, left:-160, width:460, height:460, borderRadius:"50%", background:"radial-gradient(circle, rgba(205,169,104,0.15), transparent 70%)", pointerEvents:"none" }}/>
    <div style={{ position:"absolute", bottom:-180, right:-180, width:520, height:520, borderRadius:"50%", background:"radial-gradient(circle, rgba(28,42,69,0.05), transparent 70%)", pointerEvents:"none" }}/>
    <div style={{ position:"relative", textAlign:"center", marginBottom:6 }}>
      <div style={{ fontFamily:"Georgia, 'Times New Roman', serif", fontSize:42, fontWeight:400, color:"#1a1a1a", letterSpacing:"0.38em", paddingLeft:"0.38em", lineHeight:1 }}>ARO</div>
      <div style={{ width:60, height:2, margin:"18px auto 0", background:"linear-gradient(90deg, transparent, #c19a59, transparent)" }}/>
    </div>
    <div style={{ position:"relative", width:72, height:72, borderRadius:20, background:"rgba(193,154,89,0.10)",
        display:"flex", alignItems:"center", justifyContent:"center" }}>
      <Lock size={34} color="#c19a59"/>
    </div>
    <div style={{ position:"relative", fontSize:18, fontWeight:700 }}>ARO CRM is locked</div>
    <div style={{ position:"relative", fontSize:13, color:C.textLight, maxWidth:280, textAlign:"center" }}>
      {p.failed ? "Couldn't verify your identity. Tap Retry to authenticate." : "Verify your identity to continue."}
    </div>
    {/* Single action — Unlock / Retry. Never permanently disabled (busy always
        clears via the native-call timeout), so the spinner can't get stuck. */}
    <button onClick={p.onUnlock} style={{ position:"relative", padding:"0 30px", height:54, borderRadius:14, border:"none",
        background:"linear-gradient(135deg,#d4af6e,#c19a59,#b08842)", color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer", boxShadow:"0 8px 20px rgba(193,154,89,0.32)" }}>
      {p.busy ? "Authenticating…" : (p.failed ? "Retry" : "Unlock")}
    </button>
  </div>;
};
var Inp = function(p) {
  // Required marker is owned by THIS component, never by the caller's label.
  // Several call sites historically passed a label that already ended in "*"
  // AND set `req`, rendering a double asterisk ("Status **"). Those labels are
  // cleaned up at the call sites, and this strip makes the component immune to
  // the mistake recurring — the asterisk below is the single source of truth.
  var labelText = (p.req && typeof p.label === "string") ? p.label.replace(/\s*\*+\s*$/, "") : p.label;
  return <div style={{ marginBottom:13 }}>
    <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>{labelText}{p.req&&<span style={{ color:C.danger, marginRight:3 }}>*</span>}</label>
    {p.type==="select"
      // EVERY select renders as SearchableSelect (2026-08-08). It used to be
      // opt-in via `searchable`, and everything else fell through to a native
      // <select> — whose popup the OS draws over its own trigger (and upward
      // near the viewport bottom). Routing all of them through one component
      // makes the whole app open below the trigger, flipping only when there is
      // genuinely no room. This ONE change converts every `Inp type="select"`
      // call site at once, so no option list is retyped and none can drift.
      //
      // SearchableSelect renders its own empty row, so hoist any existing
      // "- Select -" placeholder into emptyLabel (else it renders twice).
      // onChange is re-wrapped as a synthetic event so call sites keep reading
      // e.target.value unchanged — this stays a component swap, not a contract
      // change, and every caller's validation keeps working untouched.
      ? (function(){
          var norm = (p.options||[]).map(function(o){ return o!==null && typeof o==="object" ? { value:o.value, label:o.label||String(o.value), group:o.group } : { value:o, label:String(o) }; });
          var blank = norm.filter(function(o){ return String(o.value||"")===""; })[0];
          var rest = norm.filter(function(o){ return String(o.value||"")!==""; });
          // Short fixed lists get no filter box (and no mobile keyboard pop).
          // An explicit `searchable` forces the box on regardless of length —
          // that is what the long pickers (agents, projects, developers) pass.
          var noSearch = !p.searchable && rest.length <= INP_NOSEARCH_MAX;
          return <SearchableSelect value={p.value} onChange={function(v){ p.onChange({ target:{ value:v } }); }}
            options={rest} emptyLabel={blank?blank.label:(p.emptyLabel||"— Select —")} allowCustom={p.allowCustom}
            noSearch={noSearch} disabled={p.disabled}
            placeholder={p.searchPlaceholder||"Search…"} width="100%" maxWidth={9999} fontSize={14}/>;
        })()
      : p.type==="textarea"
      ? <textarea rows={3} placeholder={p.placeholder||""} value={p.value} onChange={p.onChange} style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, outline:"none", boxSizing:"border-box", resize:"vertical", fontFamily:"inherit" }}/>
      // readOnly is OPT-IN and absent by default, so every existing caller is
      // byte-identical. It carries its own look because an input that accepts no
      // typing while looking exactly like one that does is a worse control than
      // no control — the user finds out by trying. readOnly rather than disabled:
      // a disabled input is skipped by keyboard navigation and is not readable by
      // some screen readers, and this field exists to BE READ.
      : <input type={p.type||"text"} placeholder={p.placeholder||""} value={p.value} onChange={p.onChange} min={p.min} max={p.max}
          readOnly={p.readOnly || undefined} title={p.title || undefined}
          style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid " + (p.readOnly ? "#E8ECF1" : "#E2E8F0"), fontSize:14, outline:"none", boxSizing:"border-box",
                   background: p.readOnly ? "#F8FAFC" : undefined, color: p.readOnly ? C.textLight : undefined, cursor: p.readOnly ? "not-allowed" : undefined }}/>}
  </div>;
};
// ===== UNIFIED DATE FILTER =====
// ONE definition of the date-filter bar, shared by every page that has one, so
// Leads and Daily Requests (which had drifted to different chip sets) can never
// diverge again. Chips are fixed and identical everywhere; "Quarter" opens the
// same year + Q1-Q4 picker the dashboard uses; "📅 Date" is the dashboard's
// calendar chip (DashRangePicker), which writes the same from/to "custom" reads.
// Nothing here is role-gated — every user sees the same bar.
// Phone-only short forms so every status chip fits in three rows (MobileFilterChrome). Desktop never calls it.
// Phone chips are text-only (a leading emoji is dropped); every label otherwise stays EXACTLY as on desktop. The single
// permitted abbreviation ("Rotation Stopped" -> "Rot. Stopped", only if it saves a row) lives in ChipRows' `abbreviate`.
// Whether a role gets the floating ＋ on Leads (same gate as the header ＋ had: the buttons the page renders).
var leadsPhoneHasFab = function(isOnlyAdmin, cu, nSelected){ return !!(isOnlyAdmin || (cu && cu.role === "admin") || nSelected > 0); };
var mobShort = function(l){ return String(l == null ? "" : l).replace(/^(?:\p{Extended_Pictographic}|\uFE0F|\s)+/u, ""); };
var DATE_FILTER_CHIPS = [
  { v:"all",       l:"All Time" },
  { v:"today",     l:"Today" },
  { v:"yesterday", l:"Yesterday" },
  { v:"week",      l:"This Week" },
  { v:"month",     l:"This Month" },
  { v:"quarter",   l:"Quarter" },
  { v:"custom",    l:"Date" }
];
// A typed date range as text — "from → to", "…" for an open end — or null when
// NEITHER end is set: an empty range filters nothing, so it is never named
// (owner 2026-10-31; it used to read "… → …"). Deals' period label reads it.
var dateRangeTagText = function(from, to){
  return (from || to) ? (from || "…") + " → " + (to || "…") : null;
};
// The phone filter strip's 📅 tag for a date-filter period, or null for no tag.
// A "custom" period with neither end set gets NO tag. Leads and Daily Request
// both build their tag here; verify-mobile-filter-chrome.js §DATE runs it.
var dateFilterPillLabel = function(range, from, to, quarter){
  if (!range || range === "all") return null;
  if (range === "custom") { var rt = dateRangeTagText(from, to); return rt ? "📅 " + rt : null; }
  if (range === "quarter") return "📅 Q" + ((quarter && quarter.q) || "") + " " + ((quarter && quarter.year) || "");
  var dc = DATE_FILTER_CHIPS.filter(function(c){ return c.v === range; })[0];
  return "📅 " + (dc ? dc.l : range);
};
// ===== Africa/Cairo day boundaries =====
// These used to be new Date(y, m, d, ...), i.e. the DEVICE timezone: a phone on
// UTC and a laptop on Cairo clicking "Today" got different windows, and after
// 22:00 Cairo (= 19:00 UTC in summer) a UTC device was still on the previous
// calendar day. Measured 2026-08-09 22:22 UTC / 2026-08-10 01:22 Cairo: "Today"
// matched 0 leads on the Cairo day but 61 on the UTC day. The company operates
// on Cairo time and the backend already reasons in it (getCairoDateParts,
// server.js), so the chips do too now — independently of where the device is.
// Intl only, no new dependency. Egypt observes DST (re-introduced 2023), so the
// offset is resolved per-instant rather than hard-coded to +02:00/+03:00.
var DF_TZ = "Africa/Cairo";
// Cairo's calendar day as "YYYY-MM-DD". en-CA gives ISO-ordered parts and DF_TZ
// is a NAMED zone, so DST is resolved per instant. Replaces a hardcoded +3h,
// which named TOMORROW between 23:00 and midnight Cairo for the ~177 days a
// year Egypt sits at UTC+2 — stamping a deal with the wrong business date.
var cairoTodayIso = function(d){
  return new Date(d === undefined ? Date.now() : d).toLocaleDateString("en-CA", { timeZone: DF_TZ });
};
// Calendar Y/M/D that the instant `d` falls on in Cairo.
var dfCairoParts = function(d){
  var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(new Date(d).toLocaleDateString("en-CA", { timeZone: DF_TZ }));
  return m ? { y:+m[1], m:+m[2], d:+m[3] } : null;
};
// Cairo calendar year / 0-based month / day / "YYYY-MM" for an instant. Thin
// readers over dfCairoParts, so no caller re-derives them from a fixed offset.
// The month is 0-BASED to match the getUTCMonth() calls these replaced.
var dfCairoYear     = function(d){ var p = dfCairoParts(d === undefined ? Date.now() : d); return p ? p.y : NaN; };
var dfCairoMonthIdx = function(d){ var p = dfCairoParts(d === undefined ? Date.now() : d); return p ? p.m - 1 : NaN; };
var dfCairoDay      = function(d){ var p = dfCairoParts(d === undefined ? Date.now() : d); return p ? p.d : NaN; };
var dfCairoMonthKey = function(d){ var p = dfCairoParts(d === undefined ? Date.now() : d); return p ? p.y + "-" + String(p.m).padStart(2, "0") : ""; };
// Cairo's UTC offset in ms at instant `ts` — the Cairo wall-clock reading of
// `ts` minus `ts` itself.
var dfCairoOffsetMs = function(ts){
  // Intl exposes no millisecond field, so probe on a whole second and diff
  // against that same whole second — otherwise a ts ending in .999 reports an
  // offset 999 ms short and end-of-day lands at 00:00:00.997 the NEXT day.
  // Zone offsets are whole minutes, so truncating the probe is lossless.
  var base = Math.floor(ts / 1000) * 1000;
  var p = {};
  new Intl.DateTimeFormat("en-US", { timeZone: DF_TZ, hour12:false,
    year:"numeric", month:"2-digit", day:"2-digit",
    hour:"2-digit", minute:"2-digit", second:"2-digit" })
    .formatToParts(new Date(base)).forEach(function(x){ p[x.type] = x.value; });
  // hourCycle h23 vs h24: some engines render midnight as "24".
  var hh = p.hour === "24" ? 0 : +p.hour;
  return Date.UTC(+p.year, +p.month-1, +p.day, hh, +p.minute, +p.second) - base;
};
// UTC instant of the Cairo wall-clock time y-mo-d hh:mi:ss.ms. Resolved twice
// so a DST boundary inside the guess window can't leave it an hour off.
var dfCairoInstant = function(y, mo, d, hh, mi, ss, ms){
  var guess = Date.UTC(y, mo, d, hh, mi, ss, ms);
  var t = guess - dfCairoOffsetMs(guess);
  var t2 = guess - dfCairoOffsetMs(t);
  return new Date(t2);
};
var dfStartOfParts = function(p){ return dfCairoInstant(p.y, p.m-1, p.d, 0,0,0,0); };
var dfEndOfParts   = function(p){ return dfCairoInstant(p.y, p.m-1, p.d, 23,59,59,999); };
// Calendar arithmetic on Cairo days — never on the device clock, so subtracting
// a day near a DST shift can't land two days back (or none).
var dfCairoShiftDays = function(d, delta){
  var p = dfCairoParts(d);
  var u = new Date(Date.UTC(p.y, p.m-1, p.d) + delta*86400000);
  return { y:u.getUTCFullYear(), m:u.getUTCMonth()+1, d:u.getUTCDate() };
};
var dfStart = function(d){ return dfStartOfParts(dfCairoParts(d)); };
var dfEnd   = function(d){ return dfEndOfParts(dfCairoParts(d)); };
// The Cairo calendar day of an instant as "YYYY-MM-DD" — a day KEY, never an
// instant (see the Cairo day KEY vs INSTANT rule). The sparkline's week starts
// travel to the server in this spelling.
var dfYmd = function(d){ var p = dfCairoParts(d);
  return p.y + "-" + (p.m<10?"0":"") + p.m + "-" + (p.d<10?"0":"") + p.d; };
// A typed YYYY-MM-DD is a Cairo calendar day. Anchored at midday so the
// dfStart/dfEnd round-trip below can't be pushed onto a neighbouring day.
var dfParseYmd = function(v){ var m=/^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v||"")); return m?dfCairoInstant(+m[1],+m[2]-1,+m[3],12,0,0,0):null; };
// Selectable years for the Quarter picker — current plus the two prior, enough
// to audit history without becoming the unbounded list the dashboard once had.
function dfYears(){ var y=new Date().getFullYear(); return [y, y-1, y-2]; }

// Month names for a month label, in the picker and in every summary line that
// names a specific month. Index 0 is unused so the array is indexed by the
// 1-BASED Cairo month dfCairoParts returns — the same number the resolver and
// the "YYYY-MM" wire key carry, so no caller converts between two conventions.
var DF_MONTH_NAMES = ["", "January","February","March","April","May","June",
                      "July","August","September","October","November","December"];
// "YYYY-MM" <-> {year, m}. The dropdown, the resolver and the backend floor all
// speak this one spelling of a month.
function dfMonthKey(y, m){ return String(y) + "-" + (m<10?"0":"") + m; }
function dfParseMonthKey(v){
  var mm = /^(\d{4})-(\d{2})$/.exec(String(v||""));
  if (!mm) return null;
  var m = +mm[2];
  return (m>=1 && m<=12) ? { year:+mm[1], m:m } : null;
}

// ===== THE ONE PERIOD RESOLVER =====
// Every date chip, every dashboard card, every month and every quarter
// selection resolves HERE. Day boundaries are Africa/Cairo (see dfStart/dfEnd
// above), so a period is the same period on every device.
//
// It returns the period's real boundaries PLUS the two caps a caller can
// legitimately want, so a card picks a NAMED end instead of re-deriving one:
//
//   from       period start — Cairo 00:00 of the first day
//   periodEnd  period end   — Cairo 23:59:59.999 of the last day, UNCAPPED
//   toDay      min(periodEnd, end of TODAY) — a list bounded by the calendar
//   toNow      min(periodEnd, now)          — "so far", for a period in progress
//   label      what the picker and the summary lines show
//
// Why the named ends exist: DashboardPage carried EIGHT copies of this
// arithmetic and they had drifted — the same "This Week" ended at now in three
// of them and at end-of-week in two others. Centralising the maths without
// naming the ends would have forced one of those readings onto every card
// silently. Now the choice is visible at the call site.
//
// WEEK RUNS SATURDAY -> FRIDAY (owner ruling 2026-08-30). That is ARO's work
// week; Friday is the universal off-day. Until this ruling the week started on
// MONDAY here — so the Leads and Daily Requests chips answered a different
// question from the dashboard's own "This Week", which had always been
// Saturday. Measured on production the day it changed (Sunday 2026-08-30):
// Leads "This Week" 268 -> 67 by createdAt and 1,089 -> 605 on a sales
// agent's receipt window, Daily Requests 319 -> 74. The 201 leads and 245
// daily requests in the dropped Monday..Friday sliver belong to the PREVIOUS
// work week, which is what "Last Week" now answers.
//
// `o` carries the quarter selection ({year,q}), the month selection ({year,m})
// and the custom from/to strings.
// "5 – 7 Oct 2026" / "5 Oct – 7 Nov 2026" / "5 Dec 2025 – 7 Jan 2026" for a
// dashboard range; the cohort sentence turns the dash into "to".
function dashRangeLabel(a, b){
  var mon = function(p){ return DF_MONTH_NAMES[p.m].slice(0,3); };
  if (a.y === b.y && a.m === b.m) return a.d + " \u2013 " + b.d + " " + mon(b) + " " + b.y;
  if (a.y === b.y) return a.d + " " + mon(a) + " \u2013 " + b.d + " " + mon(b) + " " + b.y;
  return a.d + " " + mon(a) + " " + a.y + " \u2013 " + b.d + " " + mon(b) + " " + b.y;
}
// Calendar days a range covers (both ends inclusive) — the factor the sales
// target pro-rates by ("daily target x days in range").
function dashRangeDays(o){
  return Math.round((Date.UTC(o.to.y, o.to.m-1, o.to.d) - Date.UTC(o.from.y, o.from.m-1, o.from.d)) / 86400000) + 1;
}
function dateFilterRange(key, o){
  o = o || {};
  if (key === "all") return null;
  var now    = new Date();
  var nowP   = dfCairoParts(now);
  var dayEnd = dfEnd(now).getTime();   // end of TODAY in Cairo
  var nowMs  = now.getTime();
  var out = function(fromD, endD, label){
    var f = fromD.getTime(), pe = endD.getTime();
    return { from:f, periodEnd:pe, toDay:Math.min(pe, dayEnd), toNow:Math.min(pe, nowMs), label:label };
  };
  // Days back from `p` to the most recent Saturday. Cairo weekday, not the
  // device's: getUTCDay() on a UTC-midnight date built from the CAIRO calendar
  // parts names the Cairo day. Sat->0, Sun->1, ... Fri->6.
  var satBack = function(p){ return (new Date(Date.UTC(p.y, p.m-1, p.d)).getUTCDay() + 1) % 7; };
  if (key === "today")     return out(dfStart(now), dfEnd(now), "Today");
  if (key === "yesterday"){ var y = dfCairoShiftDays(now,-1); return out(dfStartOfParts(y), dfEndOfParts(y), "Yesterday"); }
  if (key === "day"){
    // ONE arbitrary Cairo calendar day (the dashboard's date picker). `o.day`
    // is {y,m,d}; it is built by dashPeriodOf from a validated "D<yyyy-mm-dd>"
    // and is never today or yesterday (those keep their own keys). Same shape
    // as "yesterday" — a whole day, so cohort semantics are identical.
    if (!o.day) return null;
    return out(dfStartOfParts(o.day), dfEndOfParts(o.day), o.day.d + " " + DF_MONTH_NAMES[o.day.m].slice(0,3) + " " + o.day.y);
  }
  if (key === "range"){
    // A from -> to range of Cairo calendar days (the dashboard's date control),
    // both ends INCLUSIVE; o.from / o.to are {y,m,d}. Built by dashPeriodOf from
    // a validated "R<from>_<to>" and never a day, a shortcut or a whole month /
    // quarter (those resolve to their own keys). The end may lie in the future
    // (a range over the rest of the current week), exactly as This Week's does.
    if (!o.from || !o.to) return null;
    return out(dfStartOfParts(o.from), dfEndOfParts(o.to), dashRangeLabel(o.from, o.to));
  }
  if (key === "week"){
    // `o.anchor` (ms) asks for the Sat->Fri week CONTAINING that instant rather
    // than the current one. Added 2026-09-03 for the quarter sparkline, which
    // needs each of its weeks' start days: enumerating them anywhere else would
    // have meant a second Saturday derivation, which is the 2026-08-30 defect.
    // Absent, this is byte-for-byte the previous behaviour.
    var wAnchored = o.anchor != null;
    var wDate = wAnchored ? new Date(o.anchor) : now;
    var wP    = wAnchored ? dfCairoParts(wDate) : nowP;
    var b = satBack(wP);
    return out(dfStartOfParts(dfCairoShiftDays(wDate, -b)),
               dfEndOfParts(dfCairoShiftDays(wDate, 6-b)),
               wAnchored ? "Week of " + dfYmd(dfStartOfParts(dfCairoShiftDays(wDate, -b))) : "This Week");
  }
  if (key === "lastWeek"){
    // The seven days ending on the Friday BEFORE this week's Saturday.
    var b2 = satBack(nowP);
    return out(dfStartOfParts(dfCairoShiftDays(now, -(b2+7))),
               dfEndOfParts(dfCairoShiftDays(now, -(b2+1))), "Last Week");
  }
  if (key === "month"){
    // A SPECIFIC calendar month when o.month is given, otherwise the current
    // one. Whole-month range; the caller decides whether to cap it.
    var my = (o.month && o.month.year) || nowP.y;
    var mo = (o.month && o.month.m)    || nowP.m;
    // Day 0 of the month after = this month's last day (28/29/30/31, resolved).
    var mLast = new Date(Date.UTC(my, mo, 0)).getUTCDate();
    return out(dfStartOfParts({ y:my, m:mo, d:1 }),
               dfEndOfParts({ y:my, m:mo, d:mLast }),
               (my===nowP.y && mo===nowP.m) ? "This Month" : (DF_MONTH_NAMES[mo]+" "+my));
  }
  if (key === "quarter"){
    // A SPECIFIC quarter, not just the current one.
    var qy = (o.quarter && o.quarter.year) || nowP.y;
    var qq = (o.quarter && o.quarter.q)    || (Math.floor((nowP.m-1)/3)+1);
    var first = dfStartOfParts({ y:qy, m:(qq-1)*3+1, d:1 });
    // Day 0 of the month after the quarter = its last day.
    var lastD = new Date(Date.UTC(qy, qq*3, 0));
    var last  = dfEndOfParts({ y:lastD.getUTCFullYear(), m:lastD.getUTCMonth()+1, d:lastD.getUTCDate() });
    return out(first, last, "Q"+qq+" "+qy);
  }
  if (key === "custom"){
    var f=dfParseYmd(o.from), t=dfParseYmd(o.to);
    if (!f && !t) return null;                       // nothing entered yet -> no filter
    // End stays INCLUSIVE — dfEnd is 23:59:59.999 of the `to` day in Cairo.
    // A TYPED range is the user's own boundary, so it is never capped further:
    // all three ends are the same instant. Someone who types a future `to`
    // asked for it.
    var cs = f ? dfStart(f).getTime() : -Infinity;
    var ce = t ? dfEnd(t).getTime()   : dayEnd;
    return { from:cs, periodEnd:ce, toDay:ce, toNow:ce, label:(o.from||"…")+" → "+(o.to||"…") };
  }
  return null;
}
// [startMs, endMs] for a filter key, or null for "no filter" — the shape the
// date-filter bar's callers have always consumed. A thin reader over the
// resolver, NOT a second copy of it: the bar bounds lists by the calendar, so
// it takes toDay.
function dateFilterWindow(key, o){
  var r = dateFilterRange(key, o);
  return r ? [ r.from, r.toDay ] : null;
}
// Human label for the active selection (used by the pages' summary lines).
// "All Time" has no range, so it is the one label that comes from the chips.
function dateFilterLabel(key, o){
  var r = dateFilterRange(key, o);
  if (r && r.label) return r.label;
  var hit = DATE_FILTER_CHIPS.filter(function(c){ return c.v===key; })[0];
  return hit ? hit.l : "";
}

// ===== THE DASHBOARD'S PERIOD FILTER IS A STRING =====
// DashboardPage keeps its whole selection in ONE string — "today",
// "yesterday", "week", "lastWeek", "month", "M2026-07", "Q3 2026" — because
// that is what its picker buttons write and what every active-state check
// reads. dashPeriodOf() is the ONLY place that turns it back into the shared
// resolver's (key, options) pair, and dashPeriod() is the only period maths
// the dashboard has left: it replaced EIGHT inline copies, one per fetch or
// memo, which had drifted on both the week anchor and the range end.
//
// "month" (current) and "M<yyyy>-<mm>" (a specific month) are the SAME key —
// so every consumer that pro-rates or labels by month covers both without
// asking which spelling it got.
var DASH_PERIOD_PRESETS = ["today", "yesterday", "week", "lastWeek", "month"];
function dashPeriodOf(filter){
  var f = (typeof filter === "string") ? filter : "";
  var q = /^Q([1-4])\s+(\d{4})$/.exec(f);
  if (q) return { key:"quarter", o:{ quarter:{ year:+q[2], q:+q[1] } } };
  if (f.charAt(0) === "M") {
    var mo = dfParseMonthKey(f.slice(1));
    if (mo) return { key:"month", o:{ month:mo } };
  }
  if (DASH_PERIOD_PRESETS.indexOf(f) >= 0) return { key:f, o:{} };
  // "D<yyyy-mm-dd>" — a day picked from the date control. Today and Yesterday
  // resolve to their OWN keys (so picking yesterday's date is the Yesterday
  // shortcut by construction, and every branch keyed on those two follows). A
  // malformed, impossible (2026-02-31) or future date falls through to the
  // fallback below, like any string the picker cannot write.
  var dm = /^D(\d{4}-\d{2}-\d{2})$/.exec(f);
  if (dm && dfParseYmd(dm[1]) && dfYmd(dfParseYmd(dm[1])) === dm[1]) {
    var dNow = dfYmd(new Date());
    if (dm[1] === dNow) return { key:"today", o:{} };
    if (dm[1] === dfYmd(dfStartOfParts(dfCairoShiftDays(new Date(), -1)))) return { key:"yesterday", o:{} };
    if (dm[1] < dNow) return { key:"day", o:{ day: dfCairoParts(dfParseYmd(dm[1])) } };
  }
  // "R<from>_<to>" — a range from the date control. Same validity rules as a
  // day (real dates, the start no later than today); an end before the start
  // is refused. from == to is a single day and takes the D path above. A range
  // that IS a shortcut's range (Today, Yesterday, This / Last Week, a whole
  // month or quarter) resolves to that shortcut's key, so the chips light by
  // the d81e465 convention and every branch keyed on the shortcut follows.
  var rm = /^R(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})$/.exec(f);
  if (rm && rm[1] <= rm[2]) {
    var rf = dfParseYmd(rm[1]), rt = dfParseYmd(rm[2]);
    if (rf && rt && dfYmd(rf) === rm[1] && dfYmd(rt) === rm[2] && rm[1] <= dfYmd(new Date())) {
      if (rm[1] === rm[2]) return dashPeriodOf("D" + rm[1]);
      var rO = { from: dfCairoParts(rf), to: dfCairoParts(rt) };
      var rFrom = dfStartOfParts(rO.from).getTime(), rTo = dfEndOfParts(rO.to).getTime();
      var same = function(k, o2){ var x = dateFilterRange(k, o2); return x.from === rFrom && x.periodEnd === rTo; };
      var sc = ["week", "lastWeek", "month"];
      for (var si = 0; si < sc.length; si++) if (same(sc[si], {})) return { key: sc[si], o: {} };
      if (rO.from.d === 1 && rO.from.y === rO.to.y && rO.from.m === rO.to.m && same("month", { month: { year: rO.from.y, m: rO.from.m } }))
        return { key: "month", o: { month: { year: rO.from.y, m: rO.from.m } } };
      if (rO.from.d === 1 && (rO.from.m - 1) % 3 === 0) {
        var rq = { year: rO.from.y, q: (rO.from.m - 1) / 3 + 1 };
        if (same("quarter", { quarter: rq })) return { key: "quarter", o: { quarter: rq } };
      }
      return { key: "range", o: rO };
    }
  }
  // Unreachable from the picker — every button writes one of the forms above,
  // and verify-week-boundaries.js runs the picker's own vocabulary through
  // here to keep that true. It is a fallback, not a default anyone selects.
  return { key:"today", o:{} };
}
// The resolver's output plus the two things only the dashboard needs: which
// key it resolved (so a card can branch on the KIND of period rather than
// re-parse the string) and `soFar`.
function dashPeriod(filter){
  var p = dashPeriodOf(filter);
  var r = dateFilterRange(p.key, p.o);
  r.key = p.key;
  // `soFar` is the end the KPI strip, the sales cards, Agent Performance and
  // the callbacks card have always used: a DAY period runs to the end of that
  // day, a longer period still in progress stops at NOW. Named here so those
  // cards state which end they take instead of each deriving one — that is how
  // the eight copies drifted. For a period wholly in the past (Last Week, an
  // earlier month, a finished quarter) toNow IS periodEnd, so the distinction
  // only ever bites the period in progress.
  r.soFar = (p.key === "today" || p.key === "yesterday" || p.key === "day") ? r.periodEnd : r.toNow;
  return r;
}
// Is the active filter a month / a quarter? Both spellings of a month answer
// yes to the first. Used for target pro-rating and for the pickers' own
// active state, so no screen re-parses the string.
function dashIsMonth(filter){   return dashPeriodOf(filter).key === "month"; }
// The months the Month dropdown offers: CURRENT FIRST, back to `floorKey`.
// The floor is DERIVED from the caller's own data (GET /api/dashboard/
// period-bounds) rather than hardcoded, so the list can never offer a month
// the CRM has nothing in, and never stops short of one it does — the same
// reasoning as the campaign screen's month floor.
// `floorKey` null (not answered yet, or the request failed) falls back to a
// year back: bounded, never empty, and replaced the moment the floor lands.
function dashMonthOptions(floorKey){
  var nowP = dfCairoParts(Date.now());
  var f = dfParseMonthKey(floorKey);
  if (!f) { var b = new Date(Date.UTC(nowP.y, nowP.m - 1 - 11, 1)); f = { year:b.getUTCFullYear(), m:b.getUTCMonth()+1 }; }
  var out = [], y = nowP.y, m = nowP.m, guard = 0;
  // guard: 50 years. A floor that is somehow AHEAD of today would otherwise
  // walk backwards forever; the break below stops it at the current month.
  while (guard++ < 600) {
    out.push({ value:"M"+dfMonthKey(y,m), label:DF_MONTH_NAMES[m]+" "+y, isCurrent:(y===nowP.y && m===nowP.m) });
    if (y === f.year && m === f.m) break;
    if (y < f.year || (y === f.year && m < f.m)) break;
    m--; if (m === 0) { m = 12; y--; }
  }
  return out;
}
// The wire key for the CURRENT Cairo month — what the Month button selects on
// its first click, so the chip keeps its one-click behaviour.
function dashCurrentMonthValue(){ var p = dfCairoParts(Date.now()); return "M" + dfMonthKey(p.y, p.m); }
function dashIsQuarter(filter){ return dashPeriodOf(filter).key === "quarter"; }
// Does the shortcut chip `chip` ("today" | "yesterday" | "week" | "lastWeek")
// light for this filter? By the RESOLVED KEY, not the string — so a day picked
// from the date control that IS yesterday lights Yesterday too (the d81e465
// convention: a chip lights when the active dates are that shortcut's range).
function dashChipLit(filter, chip){ return dashPeriodOf(filter).key === chip; }
// The date control's own state: the picked [from, to] ("yyyy-mm-dd", "" when
// unset) for a "D…" / "R…" filter, and the filter string a pick writes. One day
// (to empty, or to == from) is "D<day>"; two days is "R<from>_<to>", swapped if
// entered backwards; nothing set returns to the default, Today. A lone `to` is
// taken as the day.
function dashRangeValue(filter){
  var f = typeof filter === "string" ? filter : "";
  var d = /^D(\d{4}-\d{2}-\d{2})$/.exec(f); if (d) return { from: d[1], to: "" };
  var r = /^R(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})$/.exec(f); if (r) return { from: r[1], to: r[2] };
  return { from: "", to: "" };
}
function dashRangeFilterOf(from, to){
  var a = from || "", b = to || "";
  if (!a && !b) return "today";
  if (!a || !b || a === b) return "D" + (a || b);
  return a < b ? ("R" + a + "_" + b) : ("R" + b + "_" + a);
}

// ===== A SHARE, AS A STRING — ONE RENDERING RULE (item 4, 2026-09-03) ======
// The VALUE rule lives once on the server (sharePct): whole percent, falling to
// ONE decimal only when whole would be 0 while the count is not. This is the
// other half — turning that number into the text on the tile — and it is one
// function so a percentage reads the same on the admin tiles, the sales strip
// and every campaign row.
//
// `pct === 0` is AMBIGUOUS on its own, which is why `count` and `base` are
// both parameters: the server returns 0 for "nothing happened", for a share too
// small for even one decimal (1 of 5,062 leads is 0.02%), AND for a period with
// no leads at all. Those are three different statements — "0%", "<0.1%", "0%" —
// and the response already carries the two numbers that tell them apart, so
// there is no third field and no second copy of the value rule here. Reading
// only `count` would have printed "<0.1%" for a metric with no denominator.
//
// null / undefined pct renders EMPTY, never "0%": a tile still loading has no
// share to report, and 0% would be a claim.
function pctLabelOf(pct, count, base){
  if (pct === null || pct === undefined || isNaN(Number(pct))) return "";
  var v = Number(pct);
  if (v === 0) return (Number(count) > 0 && Number(base) > 0) ? "<0.1%" : "0%";
  return (v < 1 ? v.toFixed(1) : String(Math.round(v))) + "%";
}

// ===== THE SPARKLINE STRIP FOLLOWS THE SELECTED PERIOD (2026-09-03) ========
// It used to be seven days ending today, always, whatever the tiles showed — so
// the bars under a "Q2 2026" tile drew last week. The grain per period, as
// ruled: Today / Yesterday keep the 7 days ending on that day; This / Last Week
// show the 7 days OF that week; a month shows its days; a quarter shows its
// WEEKS; All shows the months.
//
// A PURE FUNCTION OF (filter, monthFloor), at module scope, so
// verify-dashboard-matrix.js RUNS it for every period the picker can write
// instead of reading the fetch effect and believing it.
//
// The quarter case sends the week START DAYS rather than letting the server
// find them: ARO's Saturday anchor lives in dateFilterRange and nowhere else,
// and re-deriving it in MQL would be the two-week-anchors defect again, one
// language further away. Each start comes from the resolver itself, via
// `{anchor}`.
var DASH_SERIES_MAX_WEEKS = 20;      // a quarter is 13-14; the cap stops a loop
function dashSeriesRequest(filter, monthFloor){
  // ALL TIME -> the months, from the earliest month the CALLER can see
  // (GET /api/dashboard/period-bounds) to this one. No floor answered yet ->
  // let the server fall back to its 7-day contract rather than invent a start.
  //
  // TESTED ON THE RAW STRING, ahead of dashPeriodOf, and deliberately:
  // **the dashboard's period picker does not offer All Time today** (its
  // buttons are Today / Yesterday / This Week / Last Week + the Month and
  // Quarter dropdowns, and `dashPeriodOf` maps any other string to `today`).
  // Wiring a chip would mean teaching `dashPeriod` to answer for a period with
  // NO window and auditing all six of its consumers — `new Date(null)` is the
  // epoch, so a null `from` would silently request 1970 rather than fail. That
  // is a separate, owner-scoped change. This branch exists so the grain is
  // ready and gated the day it lands, and it is exercised directly by
  // verify-dashboard-matrix.js rather than left unreachable.
  if (filter === "all" || filter === "alltime") {
    var f = dfParseMonthKey(monthFloor);
    if (!f) return { grain: "day" };
    return { grain: "month",
             from: dfStartOfParts({ y:f.year, m:f.m, d:1 }).getTime(),
             to:   dfEnd(new Date()).getTime() };
  }
  var p = dashPeriodOf(filter);
  var k = p.key;
  var r = dateFilterRange(k, p.o);
  if (!r) return { grain: "day" };
  if (k === "today" || k === "yesterday" || k === "day") {
    // The 7 days ENDING on that day — the strip this replaced, kept exactly.
    return { grain: "day",
             from: dfStartOfParts(dfCairoShiftDays(new Date(r.from), -(KPI_STRIP_DAYS - 1))).getTime(),
             to:   r.periodEnd };
  }
  if (k === "week" || k === "lastWeek" || k === "month") {
    return { grain: "day", from: r.from, to: r.periodEnd };
  }
  if (k === "range") {
    // The range's OWN days, bucketed weekly once it passes 14 days (owner
    // ruling 2026-10-14). Weeks are the resolver's Saturday weeks, walked the
    // way the quarter is; a range too long for DASH_SERIES_MAX_WEEKS weeks
    // falls to months rather than truncating its tail.
    var rDays = dashRangeDays(p.o);
    if (rDays <= 14) return { grain: "day", from: r.from, to: r.periodEnd };
    if (rDays <= DASH_SERIES_MAX_WEEKS * 7) {
      var rStarts = [], rCursor = r.from, rGuard = 0;
      while (rCursor <= r.periodEnd && rGuard++ < DASH_SERIES_MAX_WEEKS + 2) {
        var rw = dateFilterRange("week", { anchor: rCursor });
        rStarts.push(dfYmd(new Date(rw.from)));
        rCursor = rw.periodEnd + 1;
      }
      return { grain: "week", from: r.from, to: r.periodEnd, weekStarts: rStarts };
    }
    return { grain: "month", from: r.from, to: r.periodEnd };
  }
  if (k === "quarter") {
    // Walk the quarter one resolver-answered week at a time.
    var starts = [], cursor = r.from, guard = 0;
    while (cursor <= r.periodEnd && guard++ < DASH_SERIES_MAX_WEEKS) {
      var w = dateFilterRange("week", { anchor: cursor });
      starts.push(dfYmd(new Date(w.from)));
      cursor = w.periodEnd + 1;
    }
    return { grain: "week", from: r.from, to: r.periodEnd, weekStarts: starts };
  }
  return { grain: "day", from: r.from, to: r.periodEnd };
}
// The strip's length for a day-anchored period. Named so the client and
// GET /api/dashboard/kpi-series' own KPI_SERIES_DAYS are visibly the same 7.
var KPI_STRIP_DAYS = 7;
// The Followups card has no series — it is the notification bell's all-time
// count and follows no period (R4). A flat, unlabelled strip says so, and it is
// a named constant rather than an inline array so it cannot be mistaken for a
// series that failed to load.
var KPI_SPARK_NONE = { v:[0,0,0,0,0,0,0], labels:["","","","","","",""] };
// The query string for that request, so the fetch effect carries no period
// logic of its own.
function dashSeriesQuery(filter, monthFloor){
  var s = dashSeriesRequest(filter, monthFloor);
  var parts = ["grain=" + s.grain];
  if (s.from != null) parts.push("from=" + encodeURIComponent(new Date(s.from).toISOString()));
  parts.push("to=" + encodeURIComponent(new Date(s.to != null ? s.to : Date.now()).toISOString()));
  if (s.weekStarts && s.weekStarts.length) parts.push("weekStarts=" + s.weekStarts.join(","));
  return parts.join("&");
}

// ===== THE DASHBOARD'S PERIOD LABEL COMES FROM THE RESOLVER =================
// It used to come from `periodLabelForFilter`, a five-case if-chain inside
// DashboardPage that knew "today" / "yesterday" / "week" / "month" / a quarter
// and RETURNED "Today" for everything else. So with Last Week selected, or any
// month picked from the Month dropdown — both of which the picker writes and
// neither of which that chain had a case for — the Campaign & Source
// Performance card read "Cohort: leads created Today" over August's 1,255-lead
// cohort. Measured on production 2026-09-03: the card's DATA was correct for
// every period (it takes the window from dashPeriod like every other card);
// only the sentence describing it was stuck.
//
// dateFilterRange already answers this, for every key, in one place, and the
// month case already distinguishes the current month ("This Month") from a
// specific one ("August 2026"). A second vocabulary was the whole bug.
function dashPeriodLabel(filter){ return dashPeriod(filter).label || ""; }
// "leads created <phrase>" — a relative label reads correctly bare ("created
// Today", "created This Week"), a named month or quarter needs the preposition
// ("created in August 2026"). Module scope, and a pure function of the label,
// so verify-metric-definitions.js RUNS it over the picker's whole vocabulary
// instead of pattern-matching the JSX it feeds.
function dashCohortPhrase(filter){
  var l = dashPeriodLabel(filter);
  if (!l) return "";
  if (dashPeriodOf(filter).key === "day") return "on " + l;
  if (dashPeriodOf(filter).key === "range") return "from " + l.replace(" \u2013 ", " to ");
  return /^(Today|Yesterday|This |Last )/.test(l) ? l : ("in " + l);
}

// The year-select + four fixed quarter buttons, shared by the dashboard header
// picker and the date-filter bar so the two can never diverge.
var QuarterPickerPanel = function(p){
  var nowD2 = new Date(), nowQ = Math.floor(nowD2.getMonth()/3)+1, nowY = nowD2.getFullYear();
  var years = p.years || dfYears();
  var yr = (years.indexOf(p.year) >= 0) ? p.year : years[0];
  var lbl = { fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.3px" };
  return <div style={{ padding:10, minWidth:200 }}>
    <div style={Object.assign({ marginBottom:5 }, lbl)}>Year</div>
    <SearchableSelect noSearch hideEmpty value={String(yr)}
      onChange={function(v){ p.onYear(Number(v)); }}
      options={years.map(function(y){ return { value:String(y), label:String(y) }; })}
      width="100%" maxWidth={9999} fontSize={12}/>
    <div style={Object.assign({ margin:"10px 0 5px" }, lbl)}>Quarter</div>
    <div style={{ display:"flex", gap:5 }}>
      {[1,2,3,4].map(function(q){
        var isActive  = !!p.isActive && p.isActive(q, yr);
        var isCurrent = (q===nowQ && yr===nowY);
        return <button key={q} type="button" onClick={function(){ p.onPick(q, yr); }}
          style={{ flex:1, position:"relative", padding:"7px 0", borderRadius:8, cursor:"pointer",
            fontSize:12, fontWeight:isActive?700:600, fontFamily:"inherit",
            border:"1px solid "+(isActive?"#3B82F6":"#E2E8F0"),
            background:isActive?"#EFF6FF":"#fff", color:isActive?"#1D4ED8":"#334155" }}>
          {"Q"+q}
          {isCurrent&&<span title="Current quarter" style={{ position:"absolute", top:3, right:3, width:6, height:6, borderRadius:"50%", background:"#22C55E" }}/>}
        </button>;
      })}
    </div>
  </div>;
};

// The bar itself. Presentational — the page owns the state and passes it down,
// so each page keeps its own filter while the CHIPS and the resolver stay shared.
var DateFilterBar = function(p){
  var st = useState(false), qOpen = st[0], setQOpen = st[1];
  var boxRef = useRef(null);
  var anchor = dropAnchor(useDropDirection(qOpen, boxRef, 200).dir);
  useEffect(function(){
    if (!qOpen) return;
    var fn = function(e){ if (boxRef.current && !boxRef.current.contains(e.target)) setQOpen(false); };
    document.addEventListener("mousedown", fn);
    return function(){ document.removeEventListener("mousedown", fn); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qOpen]);
  var q = p.quarter || {};
  // The period before "📅 Date" was chosen in the phone dropdown, so closing the
  // calendar without a pick returns to it (owner 2026-10-31) instead of leaving
  // an empty "custom" period behind.
  var prevRef = useRef("all");
  if (p.value !== "custom") prevRef.current = p.value;
  // Every chip in the bar is the shared period chip (src/lib/periodChip.js, owner 2026-10-31) — the Dashboard's box.
  var chip = function(on){ return periodChipStyle(on, PERIOD_ACCENT_BLUE, p.isMobile); };
  // compact (phone toolbar): the period itself is chosen in a dropdown elsewhere; this bar then shows ONLY what a
  // dropdown cannot — the Quarter picker or the 📅 Date calendar chip — and nothing for the other periods.
  var shownChips = p.compact ? DATE_FILTER_CHIPS.filter(function(c){ return c.v === p.value && (c.v === "quarter" || c.v === "custom"); }) : DATE_FILTER_CHIPS;
  if (p.compact && !shownChips.length) return null;
  return <div data-period-row={p.compact ? undefined : "datefilter"} style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
    {/* Optional leading label. Absent by default, so Daily Requests renders
        exactly as before. The Leads page passes one because the SAME chips now
        mean different things by role: "Received" for a sales agent (his own
        slice's assignedAt) and "Date Added" for admin/operation (createdAt).
        Without it the chips would silently answer a different question for
        different people — the one thing a shared control must not do. Plain
        text, no emoji, so it reads as a label for the chips rather than
        competing with them. */}
    {p.label && !p.compact && <span style={{ fontSize:11, fontWeight:600, color:C.textLight, whiteSpace:"nowrap" }}>{p.label}</span>}
    {shownChips.map(function(c){
      var on = p.value === c.v;
      if (c.v === "quarter") {
        return <span key={c.v} ref={boxRef} style={{ position:"relative", display:"inline-block" }}>
          <button type="button" onClick={function(){ if(!on) p.onChange("quarter"); setQOpen(!qOpen); }}
            style={chip(on)}>{on ? dateFilterLabel("quarter", p) : c.l} {"\u25be"}</button>
          {qOpen && <div style={Object.assign({ position:"absolute", left:0, background:"#fff", border:"1px solid #E2E8F0", borderRadius:10, zIndex:99, boxShadow:"0 4px 16px rgba(0,0,0,0.08)" }, anchor)}>
            <QuarterPickerPanel years={dfYears()} year={q.year || new Date().getFullYear()}
              onYear={function(y){ p.onQuarter({ year:y, q:q.q || (Math.floor(new Date().getMonth()/3)+1) }); }}
              isActive={function(qq, yy){ return p.value==="quarter" && q.q===qq && q.year===yy; }}
              onPick={function(qq, yy){ p.onQuarter({ year:yy, q:qq }); p.onChange("quarter"); setQOpen(false); }}/>
          </div>}
        </span>;
      }
      if (c.v === "custom") {
        // 📅 Date (owner 2026-10-31) — the Dashboard's calendar chip in place of the
        // Custom Range inputs: one click = that day, a later second click = the range,
        // an earlier one restarts it. It writes the SAME from/to the inputs wrote (a
        // single day is from == to) and selects "custom", so the resolver, counts,
        // exports and deep links read exactly what they did. Clear = All Time.
        return <DashRangePicker key={c.v} isMobile={p.isMobile} iconOnly={p.dateIconOnly} autoOpen={p.compact && !p.from && !p.to}
          value={on ? { from:p.from||"", to:(p.to && p.to !== p.from) ? p.to : "" } : { from:"", to:"" }}
          active={on && !!(p.from || p.to)} min="" todayYmd={cairoTodayIso()}
          triggerStyle={p.compact ? undefined : Object.assign(chip(on && !!(p.from || p.to)), { width:"auto" })}
          onPick={function(a, b){
            if (!a) { p.onFrom(""); p.onTo(""); p.onChange("all"); return; }
            p.onFrom(a); p.onTo(b || a); p.onChange("custom");
          }}
          onDismiss={function(){ if (p.value === "custom" && !p.from && !p.to) p.onChange(prevRef.current || "all"); }}/>;
      }
      return <button key={c.v} type="button" onClick={function(){ p.onChange(c.v); }} style={chip(on)}>{c.l}</button>;
    })}
  </div>;
};

// ===== AGENT-REASSIGN PICKER =====
// The three agent pickers (Leads row, Leads side panel, Daily Requests row) are
// a load-balancing decision, not a plain list, so they get a purpose-built
// layout: current owner pinned on top, everyone else least-loaded-first with
// today's receipt count, "— No Agent —" last.

// Today's per-agent assignment counts. Cached module-wide for 60s so the two
// pages that render a picker don't each hit the endpoint on every open.
var _assignedTodayCache = { at:0, data:null, inflight:null };
function useAssignedToday(token){
  var st = useState(_assignedTodayCache.data || {}), counts = st[0], setCounts = st[1];
  useEffect(function(){
    if (!token) return;
    if (_assignedTodayCache.data && (Date.now() - _assignedTodayCache.at < 60000)) {
      setCounts(_assignedTodayCache.data); return;
    }
    if (!_assignedTodayCache.inflight) {
      _assignedTodayCache.inflight = apiFetch("/api/leads/assigned-today","GET",null,token)
        .then(function(d){ _assignedTodayCache.data = d||{}; _assignedTodayCache.at = Date.now(); _assignedTodayCache.inflight = null; return _assignedTodayCache.data; })
        .catch(function(){ _assignedTodayCache.inflight = null; return {}; });
    }
    var cancelled = false;
    _assignedTodayCache.inflight.then(function(d){ if(!cancelled) setCounts(d||{}); });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  return counts;
}

// Build the ordered option list:
//   1. current owner, pinned first, __current (highlight + ✓)
//   2. divider, then the rest LEAST LOADED FIRST (today's receipts, then name)
//   3. divider, then "— No Agent —" (admin only), muted
// An owner who is not in the pool (inactive, or outside every tier) is still
// pinned at the top so the control always shows who actually holds the lead.
function buildAgentOptions(pool, currentId, counts, includeNoAgent, ownerName){
  var cid = String(currentId||"");
  var cnt = function(id){ var n = counts && counts[String(id)]; return typeof n === "number" ? n : 0; };
  var cur = null, rest = [];
  (pool||[]).forEach(function(u){
    var id = gid(u);
    var o = { value:id, label:u.name, __count:cnt(id) };
    if (id === cid) { o.__current = true; cur = o; } else rest.push(o);
  });
  if (!cur && cid) cur = { value:cid, label:(ownerName||"Unknown"), __count:cnt(cid), __current:true };
  rest.sort(function(a,b){ return (a.__count - b.__count) || String(a.label||"").localeCompare(String(b.label||"")); });
  if (cur && rest.length) rest[0].__divider = true;
  var out = (cur ? [cur] : []).concat(rest);
  if (includeNoAgent) out.push({ value:"", label:"— No Agent —", __none:true, __divider:true });
  return out;
}

// One row. Avatar uses avatarColor(name) — keyed to the NAME, not the list
// index, so an agent keeps the same colour as the list re-sorts by load.
function agentOptionRow(o){
  if (o.__none) {
    return <div style={{ padding:"9px 12px", fontSize:13, color:C.textLight, fontStyle:"italic" }}>{o.label}</div>;
  }
  var initials = String(o.label||"?").trim().split(/\s+/).slice(0,2).map(function(w){ return (w[0]||"").toUpperCase(); }).join("") || "?";
  return <div style={{ display:"flex", alignItems:"center", gap:9, padding:"8px 12px", background:(o.__current?"#F0F7FF":"transparent") }}>
    <span style={{ width:26, height:26, borderRadius:"50%", background:avatarColor(o.label), color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, flexShrink:0 }}>{initials}</span>
    {/* The current holder is 700 against 400 for everyone else. It was already
        700, but the others sat at 500 — too close for the bold to register at
        13px, so the pin read as highlight-only. 400 is the real contrast. */}
    <span style={{ flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:13, fontWeight:(o.__current?700:400), color:C.text, textAlign:"start" }}>{o.label}</span>
    {o.__current
      ? <span style={{ flexShrink:0, fontSize:12, fontWeight:700, color:"#0F6E56" }}>✓</span>
      : <span title="Leads received today" style={{ flexShrink:0, fontSize:11, fontWeight:600, color:C.textLight, background:"#F1F5F9", borderRadius:6, padding:"1px 6px", minWidth:18, textAlign:"center" }}>{o.__count}</span>}
  </div>;
}

// Drop-in replacement for a native <select>, so the ~52 hand-rolled selects
// scattered across dashboards/reports/admin panels could be converted without
// touching a single handler.
//
// CONTRACT IS THE NATIVE ONE: onChange receives a synthetic { target:{ value } },
// so every existing handler keeps working verbatim — including the async ones
// with early returns, and the ones that switch on sentinel values like
// "__add_new__". Only the markup changes: <option> children become an `options`
// array of { value, label } (or bare strings).
//
// EMPTY-ROW PARITY is the subtle part. A native select shows exactly the options
// it is given. So: if the caller's list contains a blank-valued option (the
// familiar <option value="">All years</option>), that becomes SearchableSelect's
// empty row with the SAME label; if it does not, hideEmpty suppresses the row
// entirely so no phantom "— Select —" appears where the native control had none.
var Select = function(p) {
  var norm = (p.options || []).map(function(o){
    return (o !== null && typeof o === "object")
      ? { value:o.value, label:(o.label !== undefined && o.label !== null) ? o.label : String(o.value) }
      : { value:o, label:String(o) };
  });
  var isBlank = function(o){ return String(o.value == null ? "" : o.value) === ""; };
  // hideEmpty from the caller = "my list is already exactly what I want, in the
  // order I want it". Skip the blank-hoisting entirely so a ""-valued row stays
  // where the caller put it — the agent pickers place "— No Agent —" LAST.
  var blank = p.hideEmpty ? null : norm.filter(isBlank)[0];
  var rest  = p.hideEmpty ? norm : norm.filter(function(o){ return !isBlank(o); });
  return <SearchableSelect
    value={p.value == null ? "" : p.value}
    onChange={function(v){ if (p.onChange) p.onChange({ target:{ value:v } }); }}
    options={rest}
    hideEmpty={!blank}
    emptyLabel={blank ? blank.label : ""}
    noSearch={p.searchable ? false : (rest.length <= INP_NOSEARCH_MAX)}
    disabled={p.disabled} title={p.title} centerOptions={p.centerOptions} renderRow={p.renderRow}
    placeholder={p.placeholder || "Search…"}
    width={p.width || "100%"} maxWidth={p.maxWidth || 9999} fontSize={p.fontSize || 12}/>;
};
var Btn = function(p) { return <button onClick={p.onClick} disabled={p.loading||p.disabled} title={p.title} style={Object.assign({ padding:"9px 18px", borderRadius:10, border:p.outline?"1px solid #E2E8F0":"none", background:p.outline?"#fff":p.danger?C.danger:p.success?C.success:"linear-gradient(135deg,"+C.accent+","+C.accentLight+")", color:p.outline?C.textLight:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:7, justifyContent:"center", opacity:p.loading||p.disabled?0.65:1 }, p.style||{})}>{p.children}</button>; };
var Loader = function() { return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:80 }}><div style={{ width:36, height:36, borderRadius:"50%", border:"3px solid #E8ECF1", borderTopColor:C.accent, animation:"spin 0.8s linear infinite" }}/></div>; };

// ===== BULK REASSIGN — multi-agent distribution editor (Leads + Daily Requests) =====
// One row per target agent: searchable agent picker + integer count + remove.
// The page owns the rows state; this component only edits it and renders the
// running total. An agent chosen on one row disappears from every other row's
// picker, so the same agent cannot be listed twice. The last row cannot be
// removed. `roster` is [{value,label}] of eligible agents; `selectedCount` is
// the number of ticked rows the distribution is sliced over.
function bulkDistributionTotals(rows, selectedCount) {
  var sum = 0, incomplete = false;
  (rows || []).forEach(function(r){
    var n = parseInt(r.count, 10);
    if (!r.agentId || !(n > 0)) incomplete = true;
    if (n > 0) sum += n;
  });
  return { sum: sum, incomplete: incomplete, over: sum > selectedCount, canSubmit: !incomplete && sum > 0 && sum <= selectedCount };
}
var BulkDistributionEditor = function(p) {
  var rows = p.rows, setRows = p.setRows, roster = p.roster || [], selectedCount = p.selectedCount || 0, disabled = !!p.disabled;
  var noun = p.noun || "leads";
  var tot = bulkDistributionTotals(rows, selectedCount);
  var canRemove = rows.length > 1;
  var canAdd = rows.length < roster.length;
  var update = function(i, patch){ setRows(function(prev){ return prev.map(function(r, j){ return j === i ? Object.assign({}, r, patch) : r; }); }); };
  return <div>
    <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:6 }}>Distribution</div>
    {rows.map(function(row, i){
      var taken = {};
      rows.forEach(function(r, j){ if (j !== i && r.agentId) taken[r.agentId] = true; });
      var opts = roster.filter(function(o){ return !taken[o.value]; });
      return <div key={i} style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <SearchableSelect value={row.agentId} onChange={function(v){ update(i, { agentId:v }); }} options={opts}
            emptyLabel="- Select Agent -" disabled={disabled} placeholder="Search…" width="100%" maxWidth={9999} fontSize={14}/>
        </div>
        <input type="number" inputMode="numeric" min={1} max={selectedCount} value={row.count} disabled={disabled} placeholder="Count"
          onChange={function(e){ update(i, { count: String(e.target.value || "").replace(/[^0-9]/g, "") }); }}
          style={{ width:84, padding:"9px 10px", borderRadius:10, border:"1px solid " + (parseInt(row.count,10) > selectedCount ? C.danger : "#E2E8F0"), fontSize:14, outline:"none", boxSizing:"border-box" }}/>
        <button type="button" disabled={disabled || !canRemove} title={canRemove ? "Remove row" : "At least one agent is required"}
          onClick={function(){ setRows(function(prev){ return prev.filter(function(_, j){ return j !== i; }); }); }}
          style={{ background:"none", border:"none", padding:6, cursor:(disabled || !canRemove) ? "not-allowed" : "pointer", color:canRemove ? C.danger : C.border, display:"flex", alignItems:"center" }}>
          <Trash2 size={16}/>
        </button>
      </div>;
    })}
    <button type="button" disabled={disabled || !canAdd} title={canAdd ? "Add another agent" : "Every eligible agent already has a row"}
      onClick={function(){ setRows(function(prev){ return prev.concat([{ agentId:"", count:"" }]); }); }}
      style={{ background:"none", border:"1px dashed " + C.border, borderRadius:10, padding:"7px 12px", fontSize:12, fontWeight:600, color:(disabled || !canAdd) ? C.textLight : C.info, cursor:(disabled || !canAdd) ? "not-allowed" : "pointer", display:"flex", alignItems:"center", gap:5, opacity:(disabled || !canAdd) ? 0.6 : 1 }}>
      <Plus size={14}/> Add Agent
    </button>
    <div style={{ marginTop:12, marginBottom:12, fontSize:13 }}>
      <div style={{ fontWeight:600, color:tot.over ? C.danger : C.text }}>Total: {tot.sum} / {selectedCount}</div>
      {tot.over && <div style={{ color:C.danger, fontSize:12, marginTop:3 }}>Exceeds selected {noun}</div>}
      {!tot.over && tot.sum < selectedCount && <div style={{ color:C.textLight, fontSize:12, marginTop:3 }}>({selectedCount - tot.sum} {noun} will stay with their current agents)</div>}
      {!tot.over && tot.sum > 0 && tot.sum === selectedCount && <div style={{ color:C.success, fontSize:12, marginTop:3 }}>✓ All selected {noun} distributed</div>}
    </div>
  </div>;
};
// Ticked ids in DISPLAY order. Select-all already follows `displayList`; a
// hand-ticked set is in click order, so re-sort by the current display index.
// Ids no longer displayed (filter changed after ticking) trail in tick order.
function orderSelectedByDisplay(selectedIds, displayList) {
  var idx = {}; displayList.forEach(function(row, i){ idx[gid(row)] = i; });
  var tick = {}; selectedIds.forEach(function(id, i){ tick[id] = i; });
  return selectedIds.slice().sort(function(a, b){
    var ia = idx[a], ib = idx[b];
    if (ia == null && ib == null) return tick[a] - tick[b];
    if (ia == null) return 1;
    if (ib == null) return -1;
    return ia - ib;
  });
}
// Summary line for the post-reassign alert, from the server's byAgent[].
function bulkReassignSummary(res, noun) {
  if (!res || !Array.isArray(res.byAgent)) return null;
  var parts = res.byAgent.map(function(s){ return s.agentName + " (" + s.reassigned + ")"; });
  var msg = "Reassigned " + (res.reassigned || 0) + " " + noun + ": " + parts.join(", ") + ".";
  var skips = [];
  if (res.skippedSelf) skips.push(res.skippedSelf + " same-agent");
  if (res.skippedFrozen) skips.push(res.skippedFrozen + " frozen (DoneDeal/EOI)");
  if (res.notFound) skips.push(res.notFound + " not found");
  if (skips.length) msg += " Skipped: " + skips.join(", ") + ".";
  return msg;
}

// ===== TEMPORARY SHAKE DIAGNOSTIC — LEADS (?diagShake=1, admin only) =====
// REMOVE ONCE THE LEADS SHAKE IS DIAGNOSED, exactly as its DR ancestor was
// removed in 2ba49d3. Nothing below runs unless the query param is present AND
// the caller is an admin; the component is not mounted otherwise, so normal
// sessions execute none of this.
//
// This is a rebuild of the probe that caught the DR measure loop (01061ef),
// re-scoped to the Leads page. The DR chase produced four signals and the
// reasons each one is load-bearing — all four are reproduced verbatim, because
// each of them earned its place by catching something the others were blind to:
//
//   1. getAnimations() — every running CSS animation AND transition, with its
//      target. layout-shift is BLIND to transform/opacity animation: the
//      callback bell painted at ~24fps forever while logging CLS 0.000, so a
//      CLS-only probe reports a perfectly still page while it visibly shakes.
//   2. rect/transform sampler — elements whose box actually moves, which catches
//      JS-driven motion owning no Animation object. KEYED BY ELEMENT VIA
//      WeakMap, never by selector path: path keys carry no nth-child, so
//      siblings collapse onto one key and the sampler compares different
//      elements to each other. That bug manufactured 4,499 phantom sidebar
//      "moves" in the owner's first DR capture.
//   3. layout-shift with sources[] — genuine reflow, and it NAMES the nodes.
//   4. MutationObserver — DOM/attribute churn, which is both what retriggers a
//      transition and what a re-render loop looks like from the DOM side.
//
// Observer attachment is RECORDED, not assumed: an observer that silently failed
// to attach reports zero forever, which reads exactly like a clean result.
//
// ONE SIGNAL IS NEW, and it exists because Leads is not DR. DR had a single
// wrapping column, so "row height flips by ~36px" was enough to identify the
// culprit by inference. The Leads main table has ~15 columns, so the probe
// measures COLUMN WIDTHS directly (signal 5 below) and names the offending
// column instead of leaving it to be deduced. Under table-layout:auto the
// mechanism IS a column width flip — measuring it removes the last inference
// step from the diagnosis.
var shakeSelectorPath = function(n) {
  if (!n) return "(null)";
  if (n.nodeType === 3) n = n.parentElement;
  if (!n || !n.tagName) return "(non-element)";
  var parts = [], el = n, hops = 0;
  while (el && el.tagName && hops < 6) {
    var s = el.tagName.toLowerCase();
    if (el.id) s += "#" + el.id;
    var cls = (typeof el.className === "string") ? el.className.trim() : "";
    if (cls) s += "." + cls.split(/\s+/).slice(0, 3).join(".");
    if (el.attributes) {
      for (var a = 0; a < el.attributes.length; a++) {
        var at = el.attributes[a];
        if (at.name.indexOf("data-") === 0) s += "[" + at.name + "]";
      }
    }
    parts.unshift(s);
    el = el.parentElement; hops++;
  }
  var txt = "";
  try { txt = (n.textContent || "").trim().replace(/\s+/g, " ").slice(0, 40); } catch (e) {}
  return parts.join(" > ") + (txt ? '  "' + txt + '"' : "");
};

// Identify a virtualised table by its own header row rather than by a marker
// attribute, so the probe needs NO product markup to label its findings. Leads
// mounts two tables (main list, Important tab) plus, on mobile, two plain
// Virtuoso card lists; the header signature tells them apart in the report and
// survives the diagnostic being deleted again.
var shakeTableLabel = function(tbl) {
  if (!tbl) return "(no table)";
  try {
    var ths = tbl.querySelectorAll("thead th");
    var names = [];
    for (var i = 0; i < ths.length && i < 4; i++) {
      names.push((ths[i].textContent || "").trim().replace(/\s+/g, " ").slice(0, 14) || "?");
    }
    return "table[" + names.join("|") + "] (" + ths.length + " cols)";
  } catch (e) { return "(table ?)"; }
};

// Render counters for the Leads page. A no-op unless the diagnostic has created
// window.__LEADSPROF, so a normal session pays one property lookup per call.
//
// This is the half of the question the DOM cannot answer. A page whose rows
// re-lay-out on every animation frame has two very different possible causes:
//
//   A) an unconditional RE-RENDER loop — React re-renders LeadsPage forever, so
//      Virtuoso is handed new children continuously. Then `renders` climbs with
//      the shift count.
//   B) a measure -> layout -> re-measure OSCILLATION inside Virtuoso — React is
//      idle, but a row's measured height keeps flipping, so Virtuoso repositions
//      rows forever. Then `renders` stays flat while data-known-size flips
//      between two values.
//
// Counting renders separates them without guessing. DR's probe carried three
// counters; Leads gets five, because this page has two independent table
// wirings and a two-stage filter chain (allVisible -> filtered), and a rebuild
// of either components memo remounts a whole <table> subtree (ba6b70d).
var LEADSPROF = function(k) {
  var d = (typeof window !== "undefined") && window.__LEADSPROF;
  if (d) d[k] = (d[k] || 0) + 1;
};

var ShakeDiagnostic = function(p) {
  var [running, setRunning] = useState(false);
  var [left, setLeft] = useState(0);
  var [report, setReport] = useState(null);
  var stateRef = useRef(null);

  var DURATION = 30000;

  var start = function() {
    var S = {
      startedAt: new Date().toISOString(),
      page: (function(){ try { return localStorage.getItem("crm_page") || "?"; } catch (e) { return "?"; } })(),
      bundle: (function(){
        try {
          var el = document.querySelector('script[src*="/static/js/main."]');
          return el ? el.src.split("/").pop() : "(unknown)";
        } catch (e) { return "(unknown)"; }
      })(),
      viewport: window.innerWidth + "x" + window.innerHeight + " dpr" + (window.devicePixelRatio || 1),
      scrollYStart: Math.round(window.scrollY),
      ua: navigator.userAgent,
      anims: {}, motion: {}, shifts: {}, mutations: {},
      shiftCount: 0, cls: 0, frames: 0, longFrames: 0, samples: 0,
      observersAttached: { layoutShift: false, mutation: false },
      // Per-row height history, keyed by Virtuoso's data-item-index (stable per
      // item, unlike data-index which is positional). A measure -> layout ->
      // re-measure loop shows up here as one row flipping between exactly two
      // sizes, over and over.
      rowSizes: {}, rowSizeEvents: 0,
      // SIGNAL 5 — per-column width history, keyed by table label + column
      // index. This is the direct observation of the DR mechanism rather than an
      // inference from row height: under table-layout:auto a capped, wrapping
      // cell lets the column be sized from whichever rows are currently
      // rendered, so the COLUMN ITSELF flips width. A column with exactly two
      // observed widths and many changes is the culprit, named outright.
      colWidths: {}, colWidthEvents: 0
    };
    stateRef.current = S;

    // Baseline the Leads render counters, then let LEADSPROF accumulate.
    window.__LEADSPROF = {
      renders: 0, allVisibleComputes: 0, filteredComputes: 0,
      mainComponentsBuilds: 0, importantComponentsBuilds: 0
    };

    try {
      S._po = new PerformanceObserver(function(list) {
        list.getEntries().forEach(function(e) {
          S.shiftCount++; S.cls += e.value;
          (e.sources || []).forEach(function(src) {
            var k = shakeSelectorPath(src.node);
            if (!S.shifts[k]) S.shifts[k] = { n: 0, maxDy: 0, maxDx: 0 };
            S.shifts[k].n++;
            if (src.previousRect && src.currentRect) {
              var dy = Math.round(src.currentRect.y - src.previousRect.y);
              var dx = Math.round(src.currentRect.x - src.previousRect.x);
              if (Math.abs(dy) > Math.abs(S.shifts[k].maxDy)) S.shifts[k].maxDy = dy;
              if (Math.abs(dx) > Math.abs(S.shifts[k].maxDx)) S.shifts[k].maxDx = dx;
            }
          });
        });
      });
      S._po.observe({ type: "layout-shift", buffered: false });
      S.observersAttached.layoutShift = true;
    } catch (e) { S.layoutShiftError = String(e); }

    // Attachment is RECORDED, not assumed. An observer that silently failed to
    // attach reports zero forever, which reads exactly like a clean result.
    try {
      var root = document.documentElement || document.body;
      if (root) {
        S._mo = new MutationObserver(function(muts) {
          for (var i = 0; i < muts.length; i++) {
            var m = muts[i];
            var k = shakeSelectorPath(m.target) + "  [" + m.type + (m.attributeName ? ":" + m.attributeName : "") + "]";
            if (!S.mutations[k]) S.mutations[k] = 0;
            S.mutations[k]++;
            // Virtuoso rewrites data-known-size whenever it re-measures a row.
            // Catching the ATTRIBUTE WRITE (rather than sampling) records every
            // change with no aliasing, which is what an oscillation needs.
            if (m.type === "attributes" && m.attributeName === "data-known-size" && m.target && m.target.getAttribute) {
              try {
                var idx = m.target.getAttribute("data-item-index") || m.target.getAttribute("data-index") || "?";
                var sz = m.target.getAttribute("data-known-size");
                // Which of the two Leads tables this row belongs to. Only one is
                // mounted at a time today, but labelling costs nothing and stops
                // the report being ambiguous if that ever changes.
                var tlab = "?";
                try { tlab = shakeTableLabel(m.target.closest ? m.target.closest("table") : null); } catch (e0) {}
                var rk = tlab + " #" + idx;
                if (!S.rowSizes[rk]) S.rowSizes[rk] = { seq: [], counts: {}, changes: 0 };
                var rec = S.rowSizes[rk];
                var last = rec.seq.length ? rec.seq[rec.seq.length - 1] : null;
                if (sz !== last) {
                  if (rec.seq.length < 60) rec.seq.push(sz);
                  rec.counts[sz] = (rec.counts[sz] || 0) + 1;
                  rec.changes++; S.rowSizeEvents++;
                }
              } catch (e) {}
            }
          }
        });
        S._mo.observe(root, { childList: true, subtree: true, attributes: true, characterData: false, attributeOldValue: false });
        S.observersAttached.mutation = true;
      }
    } catch (e) { S.mutationError = String(e); }

    // Sampler: running animations + element motion + column widths. 10Hz —
    // frequent enough to catch a 0.6s animation many times over, cheap enough
    // not to distort the page it is measuring.
    // KEYED BY ELEMENT, NOT BY SELECTOR PATH (see the WeakMap note above).
    var prevRects = new WeakMap();
    S._tick = setInterval(function() {
      S.samples++;
      try {
        var anims = document.getAnimations ? document.getAnimations() : [];
        for (var i = 0; i < anims.length; i++) {
          var a = anims[i];
          if (a.playState !== "running") continue;
          var target = a.effect && a.effect.target;
          var nm = a.animationName || a.transitionProperty || (a.constructor && a.constructor.name) || "?";
          var iter = "?";
          try { iter = String(a.effect.getTiming().iterations); } catch (e2) {}
          var key = nm + " {" + iter + "}  " + shakeSelectorPath(target);
          if (!S.anims[key]) S.anims[key] = { n: 0, onscreen: null };
          S.anims[key].n++;
          try {
            var b = target.getBoundingClientRect();
            S.anims[key].onscreen = b.height > 0 && b.bottom > 0 && b.top < window.innerHeight;
          } catch (e3) {}
        }
      } catch (e) {}

      // Motion sampler over visible elements, capped so the probe stays cheap.
      try {
        var all = document.querySelectorAll(".crm-app *");
        var checked = 0;
        for (var j = 0; j < all.length && checked < 400; j++) {
          var el = all[j];
          var r = el.getBoundingClientRect();
          if (r.width === 0 || r.height === 0 || r.bottom < 0 || r.top > window.innerHeight) continue;
          checked++;
          var path = shakeSelectorPath(el);
          var tf = "";
          try { tf = getComputedStyle(el).transform; } catch (e4) {}
          var sig = Math.round(r.x) + "," + Math.round(r.y) + "," + Math.round(r.width) + "," + Math.round(r.height) + "," + tf;
          var was = prevRects.get(el);
          if (was !== undefined && was !== sig) {
            if (!S.motion[path]) S.motion[path] = { n: 0, sample: "" };
            S.motion[path].n++;
            if (!S.motion[path].sample) S.motion[path].sample = was + "  ->  " + sig;
          }
          prevRects.set(el, sig);
        }
        S.elementsSampled = checked;
      } catch (e) {}

      // SIGNAL 5 — column widths, per table, per column, with the header name.
      // Sampled from <th> rects (already laid out; no extra reflow beyond the
      // motion sampler's, which runs in the same tick). Only header cells are
      // read, so this is ~15 rects per table, not one per body cell.
      try {
        var tables = document.querySelectorAll(".crm-app table");
        for (var ti = 0; ti < tables.length && ti < 4; ti++) {
          var tbl = tables[ti];
          var lab = shakeTableLabel(tbl);
          var ths = tbl.querySelectorAll("thead th");
          for (var ci = 0; ci < ths.length; ci++) {
            var th = ths[ci];
            var tr2 = th.getBoundingClientRect();
            if (tr2.width === 0) continue;
            var name = (th.textContent || "").trim().replace(/\s+/g, " ").slice(0, 18) || ("col" + ci);
            var ckey = lab + "  col " + ci + " \"" + name + "\"";
            var w = String(Math.round(tr2.width));
            if (!S.colWidths[ckey]) S.colWidths[ckey] = { seq: [], counts: {}, changes: 0 };
            var crec = S.colWidths[ckey];
            var clast = crec.seq.length ? crec.seq[crec.seq.length - 1] : null;
            if (w !== clast) {
              if (crec.seq.length < 40) crec.seq.push(w);
              crec.counts[w] = (crec.counts[w] || 0) + 1;
              // The very first observation is not a change, only a baseline.
              if (clast !== null) { crec.changes++; S.colWidthEvents++; }
            }
          }
        }
      } catch (e) {}
    }, 100);

    // Frame cadence — an idle page should be near zero.
    var lastT = 0;
    var raf = function(ts) {
      if (lastT) { var d = ts - lastT; S.frames++; if (d > 50) S.longFrames++; }
      lastT = ts;
      S._raf = requestAnimationFrame(raf);
    };
    S._raf = requestAnimationFrame(raf);

    setRunning(true); setReport(null); setLeft(Math.round(DURATION / 1000));
    S._countdown = setInterval(function(){ setLeft(function(x){ return x > 0 ? x - 1 : 0; }); }, 1000);
    S._end = setTimeout(function(){ stop(); }, DURATION);
  };

  var stop = function() {
    var S = stateRef.current;
    if (!S) return;
    try { S._po && S._po.disconnect(); } catch (e) {}
    try { S._mo && S._mo.disconnect(); } catch (e) {}
    clearInterval(S._tick); clearInterval(S._countdown); clearTimeout(S._end);
    cancelAnimationFrame(S._raf);

    var byCount = function(obj, mapFn) {
      return Object.keys(obj).map(function(k){ return mapFn(k, obj[k]); })
        .sort(function(a, b){ return b.n - a.n; }).slice(0, 25);
    };
    var out = {
      startedAt: S.startedAt, durationSec: 30, page: S.page, bundle: S.bundle,
      viewport: S.viewport, scrollYStart: S.scrollYStart, scrollYEnd: Math.round(window.scrollY),
      ua: S.ua,
      observersAttached: S.observersAttached,
      layoutShiftError: S.layoutShiftError || null,
      mutationError: S.mutationError || null,
      totals: {
        rafFrames: S.frames, longFrames: S.longFrames,
        layoutShiftEntries: S.shiftCount, cls: Number(S.cls.toFixed(4)),
        samples: S.samples, elementsSampledPerTick: S.elementsSampled || 0
      },
      // THE DECISIVE PAIR. Read them together:
      //   renders high + rowOscillators ~0  -> unconditional re-render loop
      //   renders ~0  + rowOscillators high -> Virtuoso measure/re-measure loop
      //   both high                          -> renders are driving re-measure
      leadsRenders: (function(){
        var d = window.__LEADSPROF || {};
        return {
          renders: d.renders || 0,
          allVisibleComputes: d.allVisibleComputes || 0,
          filteredComputes: d.filteredComputes || 0,
          mainComponentsBuilds: d.mainComponentsBuilds || 0,
          importantComponentsBuilds: d.importantComponentsBuilds || 0
        };
      })(),
      rowSizeSummary: (function(){
        var keys = Object.keys(S.rowSizes);
        var osc = keys.filter(function(i){
          var c = Object.keys(S.rowSizes[i].counts);
          return c.length === 2 && S.rowSizes[i].changes >= 4;   // flipping between exactly two heights
        });
        return {
          totalSizeWrites: S.rowSizeEvents,
          rowsThatChangedSize: keys.length,
          rowsOscillatingBetweenTwoSizes: osc.length,
          worstOffenders: keys.map(function(i){
            return { row: i, changes: S.rowSizes[i].changes, sizes: S.rowSizes[i].counts, firstSeq: S.rowSizes[i].seq.slice(0, 12) };
          }).sort(function(a, b){ return b.changes - a.changes; }).slice(0, 10)
        };
      })(),
      // Names the column outright when a column width is the thing oscillating.
      // "two widths, many changes" is the exact signature of a capped, wrapping
      // cell under table-layout:auto.
      columnWidthSummary: (function(){
        var keys = Object.keys(S.colWidths);
        var osc = keys.filter(function(k){
          return Object.keys(S.colWidths[k].counts).length >= 2 && S.colWidths[k].changes >= 4;
        });
        return {
          totalWidthChanges: S.colWidthEvents,
          columnsThatChangedWidth: keys.filter(function(k){ return S.colWidths[k].changes > 0; }).length,
          columnsOscillating: osc.length,
          worstOffenders: keys.map(function(k){
            return { column: k, changes: S.colWidths[k].changes, widths: S.colWidths[k].counts, firstSeq: S.colWidths[k].seq.slice(0, 12) };
          }).filter(function(x){ return x.changes > 0; })
            .sort(function(a, b){ return b.changes - a.changes; }).slice(0, 10)
        };
      })(),
      runningAnimations: byCount(S.anims, function(k, v){ return { what: k, n: v.n, onscreen: v.onscreen }; }),
      movingElements:    byCount(S.motion, function(k, v){ return { what: k, n: v.n, sample: v.sample }; }),
      layoutShiftSources:byCount(S.shifts, function(k, v){ return { what: k, n: v.n, maxDy: v.maxDy, maxDx: v.maxDx }; }),
      domChurn:          byCount(S.mutations, function(k, v){ return { what: k, n: v }; })
    };
    // Plain-language verdict, so the report answers the question on its face
    // rather than requiring someone to correlate counters by eye. The column
    // clause comes first when it fires, because it is the only line that names
    // the thing to change.
    var rr = out.leadsRenders.renders;
    var osc = out.rowSizeSummary.rowsOscillatingBetweenTwoSizes;
    var cosc = out.columnWidthSummary.columnsOscillating;
    var worstCol = out.columnWidthSummary.worstOffenders[0];
    out.verdict =
      (cosc > 0)            ? "MEASURE LOOP — COLUMN NAMED: " + cosc + " column(s) flipping width, worst = " + (worstCol ? worstCol.column + " " + JSON.stringify(worstCol.widths) : "?") + " (" + rr + " renders, " + osc + " oscillating rows). Pin that column: width = minWidth = maxWidth, or whiteSpace:nowrap." :
      (rr > 200 && osc > 0) ? "BOTH: " + rr + " Leads re-renders AND " + osc + " rows flipping between two heights — re-render loop driving re-measure" :
      (rr > 200)            ? "RE-RENDER LOOP: LeadsPage re-rendered " + rr + " times in 30s while idle" :
      (osc > 0)             ? "MEASURE LOOP: React idle (" + rr + " renders) but " + osc + " row(s) flipping between two heights, with no column width change recorded — read rowSizeSummary.worstOffenders" :
      (out.totals.layoutShiftEntries > 100) ? "NEITHER counter fired, yet " + out.totals.layoutShiftEntries + " layout shifts — cause is outside Leads render + row sizing" :
      "quiet: " + out.totals.layoutShiftEntries + " shifts, " + rr + " renders";

    try { delete window.__LEADSPROF; } catch (e) { window.__LEADSPROF = null; }
    stateRef.current = null;
    setRunning(false);
    setReport(out);
  };

  var download = function() {
    try {
      var blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url; a.download = "leads-shake-report-" + Date.now() + ".json";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(function(){ URL.revokeObjectURL(url); }, 2000);
    } catch (e) { alert("Download failed: " + e); }
  };
  var copy = function() {
    try { navigator.clipboard.writeText(JSON.stringify(report, null, 2)); alert("Report copied to clipboard"); }
    catch (e) { alert("Copy failed: " + e); }
  };

  var top = function(arr, n) { return (arr || []).slice(0, n); };

  return <div style={{ position:"fixed", right:12, bottom:12, zIndex:99999, width:390, maxWidth:"94vw",
      background:"#0F172A", color:"#E2E8F0", borderRadius:12, padding:14, fontSize:12,
      fontFamily:"ui-monospace,SFMono-Regular,Menlo,monospace", boxShadow:"0 10px 40px rgba(0,0,0,0.45)",
      maxHeight:"80vh", overflowY:"auto" }}>
    <div style={{ fontWeight:700, marginBottom:8, color:"#38BDF8" }}>Shake diagnostic — Leads (temporary)</div>
    <div style={{ color:"#94A3B8", lineHeight:1.5, marginBottom:10 }}>
      Open the Leads page and set it to the tab/filters where you see the shake,
      leave the mouse still, then press Start and do nothing for 30 seconds.
    </div>
    {!running && !report && <button onClick={start} style={{ width:"100%", padding:"9px", borderRadius:8, border:"none", background:"#0EA5E9", color:"#fff", fontWeight:700, cursor:"pointer" }}>Start 30s capture</button>}
    {running && <div style={{ padding:"9px", textAlign:"center", background:"#1E293B", borderRadius:8 }}>Capturing… {left}s left — stay idle</div>}
    {report && <div>
      <div style={{ marginBottom:8, color:"#F8FAFC", background:"#1E293B", padding:"7px 8px", borderRadius:6, lineHeight:1.4 }}>{report.verdict}</div>
      <div style={{ marginBottom:8, color:"#94A3B8" }}>
        frames {report.totals.rafFrames} · shifts {report.totals.layoutShiftEntries} · CLS {report.totals.cls} · anims {report.runningAnimations.length}
      </div>
      <div style={{ marginBottom:8, color:"#7DD3FC" }}>
        Leads renders {report.leadsRenders.renders} · allVisible {report.leadsRenders.allVisibleComputes} · filtered {report.leadsRenders.filteredComputes} · builds {report.leadsRenders.mainComponentsBuilds}/{report.leadsRenders.importantComponentsBuilds}
      </div>
      <div style={{ marginBottom:8, color:"#FCA5A5" }}>
        row size writes {report.rowSizeSummary.totalSizeWrites} · rows changed {report.rowSizeSummary.rowsThatChangedSize} · oscillating {report.rowSizeSummary.rowsOscillatingBetweenTwoSizes}
      </div>
      <div style={{ marginBottom:8, color:"#FDBA74" }}>
        column width changes {report.columnWidthSummary.totalWidthChanges} · columns oscillating {report.columnWidthSummary.columnsOscillating}
      </div>
      {top(report.columnWidthSummary.worstOffenders, 3).map(function(c, i){
        return <div key={"c"+i} style={{ color:"#FDBA74", wordBreak:"break-all", marginBottom:4 }}>col {c.changes}x {c.column} {JSON.stringify(c.widths)}</div>;
      })}
      {top(report.runningAnimations, 3).map(function(a, i){
        return <div key={"a"+i} style={{ color:"#FDE68A", wordBreak:"break-all", marginBottom:4 }}>anim {a.n}x {a.what}</div>;
      })}
      {top(report.movingElements, 3).map(function(m, i){
        return <div key={"m"+i} style={{ color:"#FCA5A5", wordBreak:"break-all", marginBottom:4 }}>moves {m.n}x {m.what}</div>;
      })}
      <div style={{ display:"flex", gap:8, marginTop:10 }}>
        <button onClick={download} style={{ flex:1, padding:"8px", borderRadius:8, border:"none", background:"#22C55E", color:"#fff", fontWeight:700, cursor:"pointer" }}>Download JSON</button>
        <button onClick={copy} style={{ flex:1, padding:"8px", borderRadius:8, border:"none", background:"#64748B", color:"#fff", fontWeight:700, cursor:"pointer" }}>Copy</button>
      </div>
      <button onClick={function(){ setReport(null); }} style={{ width:"100%", marginTop:8, padding:"7px", borderRadius:8, border:"1px solid #334155", background:"transparent", color:"#94A3B8", cursor:"pointer" }}>Run again</button>
    </div>}
  </div>;
};

var DocumentsUpload = function(p) {
  var files = p.files || [];
  var label = p.label || "📎 Upload Documents";
  var onPick = function(e){
    var picked = Array.from(e.target.files||[]);
    if (!picked.length) return;
    var tooBig = picked.find(function(f){return f.size>6*1024*1024;});
    if (tooBig) { if(p.onError) p.onError("Each file must be under 6MB ("+tooBig.name+")"); else alert("Each file must be under 6MB ("+tooBig.name+")"); e.target.value=""; return; }
    Promise.all(picked.map(function(f){
      return new Promise(function(resolve,reject){
        var r=new FileReader();
        r.onload=function(ev){resolve({fileData:ev.target.result, fileName:f.name});};
        r.onerror=function(){reject(new Error("Read failed: "+f.name));};
        r.readAsDataURL(f);
      });
    })).then(function(loaded){
      p.onChange(files.concat(loaded));
      if(p.onError) p.onError("");
    }).catch(function(ex){ if(p.onError) p.onError(ex.message||"Failed to read files"); else alert(ex.message||"Failed to read files"); });
    try{ e.target.value=""; }catch(er){}
  };
  var removeAt = function(idx){ p.onChange(files.filter(function(_,i){return i!==idx;})); };
  return <div style={Object.assign({ marginBottom:11 }, p.wrapperStyle||{})}>
    <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>{label}</label>
    <label style={{ display:"block", padding:"9px 12px", borderRadius:10, border:"1px dashed "+C.accent, background:C.accent+"08", color:C.accent, fontSize:13, fontWeight:600, cursor:"pointer", textAlign:"center" }}>
      Select files (images or PDF)
      <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple style={{ display:"none" }} onChange={onPick}/>
    </label>
    {files.length>0&&<div style={{ marginTop:8, padding:"8px 10px", background:"#F8FAFC", borderRadius:8, border:"1px solid #E2E8F0" }}>
      <div style={{ fontSize:11, fontWeight:700, color:C.textLight, marginBottom:6 }}>{files.length} file{files.length===1?"":"s"} selected — uploaded on Save</div>
      {files.map(function(f,idx){
        var isPdf = f.fileData && f.fileData.indexOf("application/pdf")>=0;
        return <div key={idx} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, padding:"4px 0", borderTop:idx>0?"1px solid #F1F5F9":"none" }}>
          <div style={{ fontSize:12, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1, minWidth:0 }}>{isPdf?"📕":"🖼️"} {f.fileName}</div>
          <button onClick={function(){removeAt(idx);}} style={{ background:"none", border:"none", color:C.danger, fontSize:14, cursor:"pointer", padding:"0 4px", lineHeight:1 }} title="Remove">×</button>
        </div>;
      })}
    </div>}
  </div>;
};

// Shared quick-pick row for the upcoming-callback datetime field. Rendered in
// every status that binds cbTime (CallBack / NoAnswer / Potential / HotCase /
// MeetingDone). Each button writes a NAIVE-LOCAL "YYYY-MM-DDTHH:MM" string built
// from the browser-local clock — byte-identical to what the datetime-local input
// emits — so the save path and the BE's lexicographic range comparisons stay
// untouched. NO .toISOString()/UTC/timezone math (see storage contract above).
// p.value = current cbTime (for active highlight); p.onPick(v) sets it.
var QuickCbButtons = function(p) {
  var pad2 = function(n){return n<10?"0"+n:""+n;};
  var fmt = function(d){return d.getFullYear()+"-"+pad2(d.getMonth()+1)+"-"+pad2(d.getDate())+"T"+pad2(d.getHours())+":"+pad2(d.getMinutes());};
  var quick = [
    { label:"After 1hr",      make:function(){var d=new Date();d.setHours(d.getHours()+1);return d;} },
    { label:"After 2hr",      make:function(){var d=new Date();d.setHours(d.getHours()+2);return d;} },
    { label:"After 3hr",      make:function(){var d=new Date();d.setHours(d.getHours()+3);return d;} },
    { label:"8 PM",           make:function(){var d=new Date();d.setHours(20,0,0,0);return d;} },
    { label:"Tomorrow 12 PM", make:function(){var d=new Date();d.setDate(d.getDate()+1);d.setHours(12,0,0,0);return d;} },
    { label:"Tomorrow 3 PM",  make:function(){var d=new Date();d.setDate(d.getDate()+1);d.setHours(15,0,0,0);return d;} },
    { label:"Next week",      make:function(){var d=new Date();d.setDate(d.getDate()+7);return d;} }
  ];
  return <div style={{ display:"flex", gap:6, marginTop:8, flexWrap:"wrap" }}>
    {quick.map(function(q){
      var v = fmt(q.make());
      var active = p.value===v;
      return <button key={q.label} type="button" onClick={function(){p.onPick(v);}}
        style={{ flex:"1 1 0", minWidth:88, padding:"7px 10px", borderRadius:9, border:"1px solid "+(active?"#3B82F6":"#E2E8F0"), background:active?"#EFF6FF":"#fff", color:active?"#1D4ED8":"#334155", fontSize:12, fontWeight:600, cursor:"pointer" }}>{q.label}</button>;
    })}
  </div>;
};

// Status change requires mandatory comment
var StatusModal = function(p) {
  var [comment, setComment] = useState("");
  // Managerial visibility for the comment. For sales role / TL on own lead,
  // these are unused (FeedbackComposer renders only the textarea); but tracked
  // here in one shape so the parent's onConfirm always receives the same args.
  var [fbVisibility, setFbVisibility] = useState("private");
  var [fbTargetAgentId, setFbTargetAgentId] = useState("");
  var [cbTime, setCbTime] = useState("");
  var [dealProject, setDealProject] = useState("");
  var [dealUnitType, setDealUnitType] = useState("");
  var [dealUnitCode, setDealUnitCode] = useState("");
  var [dealBudget, setDealBudget] = useState("");
  var [eoiDeposit, setEoiDeposit] = useState("");
  var [eoiDateInput, setEoiDateInput] = useState("");
  var [eoiDocFiles, setEoiDocFiles] = useState([]); // array of {fileData, fileName}
  var [rejectNote, setRejectNote] = useState("");
  var [potBudget, setPotBudget] = useState("");
  var [err, setErr] = useState("");
  var [saving, setSaving] = useState(false);
  // Developer (N2) — REQUIRED for EOI/DoneDeal on the LEADS flow only (gated by
  // p.requireDeveloper; the Daily-Request StatusModal does not pass it, so DRs
  // are unaffected). devSelId = chosen developerId (dropdown); devPendName =
  // typed new-developer name. Mutually exclusive (one is always ""). devList =
  // the 505 managed developers.
  var [devSelId, setDevSelId] = useState("");
  var [devPendName, setDevPendName] = useState("");
  var [devPendOpen, setDevPendOpen] = useState(false);
  var [devList, setDevList] = useState([]);
  var sc = STATUSES(p.t);
  // ===== SAME GRID AS THE DAILY-REQUEST FORM (2026-09-07) ====================
  // This modal is ONE component mounted on TWO pages (LeadsPage ~L12137,
  // DailyRequestsPage ~L22877), so the pairing lands on both doors at once —
  // which is the point: the EOI/Deal fields an agent fills here are the same
  // fields, and they had drifted into a different shape on each form.
  //
  // p.isMobile is passed by BOTH mounts. It is not optional: a mount that
  // forgets it makes isMobile undefined, the ternary picks two columns, and the
  // phone gets 218px controls with no error anywhere — so verify-fe-state.js
  // asserts both mounts pass it rather than trusting the next person to notice.
  var smGrid = { display:"grid", gridTemplateColumns: p.isMobile ? "minmax(0,1fr)" : "minmax(0,1fr) minmax(0,1fr)", gap:"0 12px" };
  var smFull = { gridColumn:"1/-1" };
  var ns = sc.find(function(s){return s.value===p.newStatus;});
  var st = p.newStatus;
  var isNewLead    = st==="NewLead";
  var isDoneDeal   = st==="DoneDeal";
  var isEOI        = st==="EOI";
  var isReject     = st==="NotInterested";
  var needsComment = st==="Potential"||st==="HotCase"||st==="MeetingDone";
  var needsCb      = st==="CallBack"||st==="NoAnswer";
  var needsPotFields = st==="Potential"||st==="HotCase";
  var hasBudget = p.lead&&p.lead.budget&&p.lead.budget.trim&&p.lead.budget.trim()!=="";
  var needsBudgetFields = needsPotFields&&!hasBudget;

  useEffect(function(){
    setComment(""); setCbTime(""); setDealProject(""); setDealUnitType(""); setDealUnitCode(""); setDealBudget(""); setEoiDeposit(""); setEoiDateInput(""); setEoiDocFiles([]); setRejectNote("");
    setPotBudget(""); setErr("");
    setFbVisibility("private"); setFbTargetAgentId("");
    setDevSelId(""); setDevPendName(""); setDevPendOpen(false);   // N2
  },[p.show]);
  // Developer list (N2) — load the 505 managed developers when the modal opens
  // for an EOI/DoneDeal transition on the leads flow (requireDeveloper + token).
  var liveDevRev = useLiveRev("developer");
  useEffect(function(){
    if (!p.show || !(isEOI||isDoneDeal) || !p.requireDeveloper || !p.token) return;
    var cancelled = false;
    apiFetch("/api/developers","GET",null,p.token).then(function(data){
      if (cancelled) return;
      var rows = (data && Array.isArray(data.data)) ? data.data : (Array.isArray(data) ? data : []);
      setDevList(rows);
    }).catch(function(){ /* non-fatal: dropdown shows no options */ });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.show, isEOI, isDoneDeal, p.requireDeveloper, p.token, liveDevRev]);

  var submit = async function() {
    if (needsComment && !cbTime)         { setErr("Please select a Callback time"); return; }
    if (needsComment && !comment.trim()) { setErr("A note is required"); return; }
    if (needsCb && !cbTime)              { setErr("Please select a time"); return; }
    if (isReject && !comment.trim())     { setErr("Please select a rejection reason"); return; }
    if (isReject && !rejectNote.trim())  { setErr("Feedback is required"); return; }
    if ((isDoneDeal||isEOI) && !dealBudget.trim()){ setErr("Please enter the amount"); return; }
    if ((isDoneDeal||isEOI) && !dealProject.trim()){ setErr("Please enter the Project"); return; }
    // Unit Code required on the DoneDeal status-transition door (gated to isDoneDeal).
    if (isDoneDeal && !dealUnitCode.trim()){ setErr("Please enter the Unit Code (e.g. B1-204)"); return; }
    if ((isDoneDeal||isEOI) && p.requireDeveloper && !devSelId && !devPendName.trim()){ setErr("Please pick a developer or enter a new name"); return; }   // N2
    if (needsBudgetFields && !potBudget.trim()){ setErr("Please enter the Budget"); return; }
    setSaving(true);
    var extra = (isDoneDeal||isEOI)
      ? { project: dealProject, unitType: dealUnitType, unitCode: dealUnitCode.trim(), budget: dealBudget, eoiDeposit: eoiDeposit, eoiDate: eoiDateInput, eoiDocumentFiles: (isEOI||isDoneDeal) ? eoiDocFiles : [],
          developerId: (p.requireDeveloper && devSelId) ? devSelId : "", developerPending: (p.requireDeveloper && !devSelId) ? devPendName.trim() : "" }   // N2
      : (needsPotFields && potBudget)
        ? { budget: potBudget }   // budget capture stays; deposit/instalment removed (never stored)
        : {};
    var finalComment = isReject&&rejectNote.trim() ? comment.trim()+" — "+rejectNote.trim() : comment.trim();
    var fb = { text: finalComment, visibility: fbVisibility, targetAgentId: fbTargetAgentId };
    // Validation: managerial role choosing "Send to specific sales" must pick
    // a target. operation excluded — they're read-only on feedback.
    var role = p.cu && p.cu.role;
    var isMgr = role === "admin" || role === "manager" || role === "team_leader";
    var leadAgentId = p.lead && p.lead.agentId && p.lead.agentId._id ? p.lead.agentId._id : (p.lead && p.lead.agentId);
    var isOwnLead = role === "team_leader" && String(leadAgentId || "") === String((p.cu && p.cu.id) || "");
    if (isMgr && !isOwnLead && finalComment && fb.visibility === "to_sales" && !fb.targetAgentId) {
      setErr("Pick a sales agent to send the feedback to"); setSaving(false); return;
    }
    await p.onConfirm(finalComment, cbTime, extra, fb);
    setSaving(false);
  };

  return <Modal show={p.show} onClose={p.onClose} title={p.t.changeStatus}>
    {ns&&<div style={{ marginBottom:14, padding:"10px 14px", background:ns.bg, borderRadius:10, display:"flex", alignItems:"center", gap:8 }}>
      <span style={{ width:10, height:10, borderRadius:"50%", background:ns.color }}/>
      <span style={{ fontSize:14, fontWeight:600, color:ns.color }}>{ns.label}</span>
    </div>}

    {/* CallBack / NoAnswer: date required */}
    {needsCb&&<div style={{ marginBottom:12 }}>
      <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>📅 Callback <span style={{color:C.danger}}>*</span></label>
      <input type="datetime-local" value={cbTime} onChange={function(e){setCbTime(e.target.value);setErr("");}}
        style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>
      {/* Shared quick-pick row — shown for both statuses that bind cbTime here
          (CallBack / NoAnswer). Fills cbTime in the same naive-local
          "YYYY-MM-DDTHH:MM" format the manual picker emits, so the existing
          submit / save path is untouched. The manual picker stays visible above
          for any timing the quick options don't cover. */}
      <QuickCbButtons value={cbTime} onPick={function(v){setCbTime(v);setErr("");}}/>
    </div>}

    {/* Potential / HotCase / MeetingDone: date + comment required */}
    {needsComment&&<div style={{ marginBottom:12 }}>
      <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>📅 Upcoming Callback <span style={{color:C.danger}}>*</span></label>
      <input type="datetime-local" value={cbTime} onChange={function(e){setCbTime(e.target.value);setErr("");}} style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>
      {/* Same shared quick-pick row — Potential / HotCase / MeetingDone all bind cbTime. */}
      <QuickCbButtons value={cbTime} onPick={function(v){setCbTime(v);setErr("");}}/>
    </div>}
    {needsComment&&<div style={{ marginBottom:12 }}>
      <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>💬 Feedback <span style={{color:C.danger}}>*</span></label>
      <FeedbackComposer id="needsComment" cu={p.cu} lead={p.lead} users={p.users}
        value={{text:comment, visibility:fbVisibility, targetAgentId:fbTargetAgentId}} rows={3}
        onChange={function(v){ setComment(v.text); setFbVisibility(v.visibility); setFbTargetAgentId(v.targetAgentId); setErr(""); }}/>
    </div>}

    {/* Potential / HotCase: budget only (and only when no budget is set yet).
        The Down Payment / Installments inputs that used to sit here were removed
        2026-08-13: they were REQUIRED but their values were never stored. On the
        Leads path the concatenated "Down Payment: … | Installments: …" string was
        written to `notes` and then overwritten one line later by the (also
        required) comment, so nothing survived; 4 of the only 6 rows that ever
        captured it read "0 EGP | 0 EGP" — agents typing zeros to clear a required
        field with no consumer. Note also that "Installments" here meant an EGP
        amount, while the deal form's `installmentYears` means YEARS — same word,
        different unit, which read as deal fields leaking into Hot Case/Potential.
        The real sale fields stay gated to the DoneDeal form. */}
    {needsBudgetFields&&<div style={{ background:"#F0F9FF", borderRadius:10, padding:"12px 14px", marginBottom:12, border:"1px solid #BAE6FD" }}>
      <div style={{ fontSize:12, fontWeight:700, color:"#0284C7", marginBottom:10 }}>💰 Budget Details</div>
      <div>
        <label style={{ display:"block", fontSize:12, fontWeight:600, color:C.text, marginBottom:4 }}>Budget (EGP) <span style={{color:C.danger}}>*</span></label>
          <input type="text" placeholder="e.g. 10,000,000" value={potBudget} onChange={function(e){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");setPotBudget(r?Number(r).toLocaleString():"");setErr("");}}
            style={{ width:"100%", padding:"8px 12px", borderRadius:9, border:"1px solid #E2E8F0", fontSize:13, boxSizing:"border-box", direction:"ltr" }}/>
      </div>
    </div>}

    {/* CallBack / NoAnswer: optional comment */}
    {needsCb&&<div style={{ marginBottom:12 }}>
      <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>💬 Feedback (optional)</label>
      <FeedbackComposer id="needsCb" cu={p.cu} lead={p.lead} users={p.users}
        value={{text:comment, visibility:fbVisibility, targetAgentId:fbTargetAgentId}} rows={2}
        onChange={function(v){ setComment(v.text); setFbVisibility(v.visibility); setFbTargetAgentId(v.targetAgentId); }}/>
    </div>}

    {/* NotInterested: reason required */}
    {isReject&&<div style={{ marginBottom:12 }}>
      <div style={{ fontSize:13, fontWeight:600, marginBottom:8, color:"#EF4444" }}>Rejection Reason <span style={{color:C.danger}}>*</span></div>
      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        {["Price too high","Area not suitable","Bought elsewhere","Not ready yet","Not interested at all","Other reason"].map(function(r){
          return <button key={r} onClick={function(){setComment(r);setErr("");}}
            style={{ padding:"8px 12px", borderRadius:8, border:"1px solid", borderColor:comment===r?"#EF4444":"#E2E8F0",
              background:comment===r?"#FEF2F2":"#fff", color:comment===r?"#EF4444":"#64748B", fontSize:12, cursor:"pointer", textAlign:"right" }}>{r}</button>;
        })}
      </div>
      <div style={{ marginTop:10 }}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>💬 Feedback <span style={{color:C.danger}}>*</span></label>
        <FeedbackComposer id="rejectNote" cu={p.cu} lead={p.lead} users={p.users}
          value={{text:rejectNote, visibility:fbVisibility, targetAgentId:fbTargetAgentId}} rows={2}
          onChange={function(v){ setRejectNote(v.text); setFbVisibility(v.visibility); setFbTargetAgentId(v.targetAgentId); setErr(""); }}/>
      </div>
    </div>}

    {/* DoneDeal / EOI: project + unit type + budget. TWO PER ROW on a desktop,
        one on a phone (smGrid). Only this section pairs: every other branch of
        this modal is a single control per row already (a callback picker with
        its quick-pick row, a FeedbackComposer, the rejection-reason list), and
        halving those would squeeze the controls rather than tidy them. */}
    {(isDoneDeal||isEOI)&&<div style={smGrid}>
      <div style={{ marginBottom:11 }}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>🏠 Project <span style={{color:C.danger}}>*</span></label>
        <input type="text" placeholder="" value={dealProject} onChange={function(e){setDealProject(e.target.value);}}
          style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>
      </div>
      <div style={{ marginBottom:11 }}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>🏷️ Unit Type</label>
        <Select value={dealUnitType} onChange={function(e){setDealUnitType(e.target.value);}} fontSize={14}
          options={["","Apartment","Duplex","Townhouse","Twinhouse","Standalone","Commercial","Admin","Clinic","Service Apartment","Chalet"].map(function(x){return { value:x, label:x||"- Select -" };})}/>
      </div>
      {/* Unit Code — required for the DoneDeal status-transition door only. */}
      {isDoneDeal&&<div style={{ marginBottom:11 }}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>🔖 Unit Code <span style={{color:C.danger}}>*</span></label>
        <input type="text" placeholder="e.g. B1-204" value={dealUnitCode} onChange={function(e){setDealUnitCode(e.target.value);setErr("");}}
          style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>
      </div>}
      {/* Amount pairs with Unit Code on a Done Deal; on an EOI (no unit code)
          it owns the row rather than sitting half-width beside nothing. */}
      <div style={Object.assign({ marginBottom:11 }, isDoneDeal?null:smFull)}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>💰 Amount (EGP) <span style={{color:C.danger}}>*</span></label>
        <input type="text" placeholder="" value={dealBudget}
          onChange={function(e){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");setDealBudget(r?Number(r).toLocaleString():"");setErr("");}}
          style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box", direction:"ltr" }}/>
      </div>
      {/* Developer (N2) — REQUIRED. Pick from the 505 managed developers OR type a
          new name (mutually exclusive). Leads flow only (p.requireDeveloper). */}
      {p.requireDeveloper&&<div style={Object.assign({ marginBottom:11 }, smFull)}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>🏗️ Developer <span style={{color:C.danger}}>*</span></label>
        {!devPendOpen && <SearchableSelect
          value={devSelId}
          emptyLabel="— Select developer —"
          placeholder="Search developers…"
          width="100%" maxWidth={9999} fontSize={13}
          options={(devList||[]).map(function(dv){ return { value:String(dv._id), label:dv.name }; })}
          onChange={function(val){ setDevSelId(val); if(val) setDevPendName(""); setErr(""); }}/>}
        {devPendOpen && <input type="text" value={devPendName} placeholder="New developer name"
          onChange={function(e){ setDevPendName(e.target.value); setDevSelId(""); setErr(""); }}
          style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>}
        <button type="button" onClick={function(){ if(devPendOpen){ setDevPendOpen(false); setDevPendName(""); } else { setDevPendOpen(true); setDevSelId(""); } setErr(""); }}
          style={{ marginTop:6, background:"none", border:"none", padding:0, color:C.info, fontSize:12, cursor:"pointer", textDecoration:"underline" }}>
          {devPendOpen ? "← Pick from the list instead" : "Can't find the developer? Enter a new name"}
        </button>
      </div>}
      {isEOI&&<div style={{ marginBottom:11 }}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>📅 EOI Date</label>
        <input type="date" value={eoiDateInput} onChange={function(e){setEoiDateInput(e.target.value);}}
          style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>
      </div>}
      {isEOI&&<div style={{ marginBottom:11 }}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>💵 Deposit (EGP)</label>
        <input type="text" placeholder="" value={eoiDeposit}
          onChange={function(e){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");setEoiDeposit(r?Number(r).toLocaleString():"");}}
          style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box", direction:"ltr" }}/>
      </div>}
      {(isEOI||isDoneDeal)&&<DocumentsUpload
        files={eoiDocFiles}
        onChange={setEoiDocFiles}
        onError={setErr}
        label={isEOI?"📎 Upload EOI Documents":"📎 Upload Deal Documents"}
        wrapperStyle={Object.assign({}, smFull, { marginBottom:11 })}
      />}
    </div>}

    {err&&<div style={{ color:C.danger, fontSize:12, marginBottom:12, padding:"8px 12px", background:"#FEF2F2", borderRadius:8 }}>⚠️ {err}</div>}
    <div style={{ display:"flex", gap:10 }}>
      <Btn outline onClick={p.onClose} style={{ flex:1 }}>{p.t.cancel}</Btn>
      <Btn onClick={submit} loading={saving} disabled={(isDoneDeal||isEOI) && p.requireDeveloper && !devSelId && !devPendName.trim()} style={{ flex:1 }}>{p.t.save}</Btn>
    </div>
  </Modal>;
};

// ===== SESSIONS / DEVICES (Phase 2 — device & session management) =====
// Coarse userAgent -> { browser, os } for display. Deliberately simple: enough
// to tell devices apart in the list, not a full UA-parsing library.
function parseUserAgentLabel(ua) {
  ua = String(ua || "");
  if (!ua) return { browser: "Unknown device", os: "" };
  var browser = /Edg\//.test(ua) ? "Edge"
    : /OPR\/|Opera/.test(ua) ? "Opera"
    : (/Chrome\//.test(ua) && !/Chromium/.test(ua)) ? "Chrome"
    : /Firefox\//.test(ua) ? "Firefox"
    : (/Safari\//.test(ua) && /Version\//.test(ua)) ? "Safari"
    : /Capacitor/i.test(ua) ? "ARO App"
    : "Browser";
  var os = /Windows NT/.test(ua) ? "Windows"
    : /iPhone|iPad|iPod/.test(ua) ? "iOS"
    : /Android/.test(ua) ? "Android"
    : /Mac OS X|Macintosh/.test(ua) ? "macOS"
    : /Linux/.test(ua) ? "Linux"
    : "";
  return { browser: browser, os: os };
}
// 2-letter ISO country code -> flag emoji (regional-indicator letters). "" when missing.
function countryFlagEmoji(cc) {
  try {
    if (!cc || String(cc).length !== 2) return "";
    return String(cc).toUpperCase().replace(/[A-Z]/g, function(c){ return String.fromCodePoint(127397 + c.charCodeAt(0)); });
  } catch(e) { return ""; }
}
// One device/session row. Presentational — parent injects the revoke handler.
var SessionRow = function(p) {
  var s = p.session; var t = p.t;
  var ua = parseUserAgentLabel(s.userAgent);
  var isMobile = s.deviceType === "mobile" || s.platform === "android" || s.platform === "ios";
  var DevIcon = isMobile ? Smartphone : Monitor;
  var platLabel = s.platform === "android" ? "Android app"
    : s.platform === "ios" ? "iOS app"
    : s.platform === "web" ? "Web"
    : (ua.os || "Web");
  var loc = [countryFlagEmoji(s.geoCountry), s.geoCity, s.geoCountry].filter(Boolean).join(" ");
  return <div style={{ display:"flex", alignItems:"center", gap:14, padding:"13px 15px", border:"1px solid "+(s.current?"#BBF7D0":C.border), borderRadius:12, background:s.current?"#F0FDF4":"#fff", marginBottom:10 }}>
    <div style={{ width:42, height:42, borderRadius:10, background:"#F1F5F9", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><DevIcon size={20} color={C.primary}/></div>
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
        <span style={{ fontWeight:700, color:C.text, fontSize:14 }}>{ua.browser}{ua.os?(" · "+ua.os):""}</span>
        {s.current ? <Badge bg="#DCFCE7" color="#15803D"><CheckCircle size={11}/> This device</Badge> : null}
        <Badge bg="#F1F5F9" color={C.textLight}>{platLabel}</Badge>
      </div>
      <div style={{ fontSize:12.5, color:C.textLight, marginTop:5, display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
        {loc ? <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}><MapPin size={12}/>{loc}</span> : null}
        {s.ip ? <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}><Globe size={12}/>{s.ip}</span> : null}
        <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}><Clock size={12}/>{timeAgo(s.lastActive, t)}</span>
      </div>
      <div style={{ fontSize:11.5, color:"#94A3B8", marginTop:3 }}>Signed in {absoluteDateTime(s.createdAt)}</div>
    </div>
    <button onClick={function(){ p.onRevoke(s); }} disabled={p.busy} title={s.current?"Sign out this device":"Sign out"} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 12px", borderRadius:9, border:"1px solid "+C.danger, background:"#fff", color:C.danger, fontSize:12.5, fontWeight:600, cursor:p.busy?"not-allowed":"pointer", opacity:p.busy?0.55:1, flexShrink:0, whiteSpace:"nowrap" }}><LogOut size={13}/> Sign out</button>
  </div>;
};
// Presentational list + toolbar. Data + handlers injected so the same panel
// serves both the self-view page and the admin per-user modal.
var SessionsPanel = function(p) {
  var t = p.t;
  var active = (p.sessions || []).filter(function(s){ return !s.revoked; });
  var others = p.selfView ? active.filter(function(s){ return !s.current; }).length : active.length;
  if (p.loading) return <div style={{ padding:"38px 0", textAlign:"center" }}><Loader/></div>;
  if (p.error) return <div style={{ padding:"20px", textAlign:"center", color:C.danger, fontSize:13 }}>{p.error}</div>;
  return <div>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, gap:10, flexWrap:"wrap" }}>
      <div style={{ fontSize:13, color:C.textLight }}>{active.length} active {active.length===1?"device":"devices"}</div>
      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
        <button onClick={p.onRefresh} disabled={p.busy} title="Refresh" style={{ width:34, height:34, borderRadius:9, border:"1px solid "+C.border, background:"#fff", cursor:p.busy?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><RefreshCw size={15} color={C.textLight}/></button>
        {p.onRevokeAll && others > 0 ? <Btn danger onClick={p.onRevokeAll} loading={p.busy}><LogOut size={13}/> {p.revokeAllLabel||"Sign out all other devices"}</Btn> : null}
      </div>
    </div>
    {active.length === 0
      ? <div style={{ padding:"36px 16px", textAlign:"center", color:C.textLight, fontSize:13 }}><Shield size={30} color={C.border} style={{ marginBottom:10 }}/><div>{p.emptyLabel||"No active sessions."}</div></div>
      : active.map(function(s){ return <SessionRow key={s.sid} session={s} t={t} busy={p.busy} onRevoke={p.onRevoke}/>; })
    }
  </div>;
};
// Owner-only per-user view, rendered inside the Users-page Devices modal — the
// single place device/session management lives (the standalone self-view page was
// removed). Reads/revokes are enforced owner-only on the backend (requireOwner).
var UserDevicesPanel = function(p) {
  var liveSessionRev = useLiveRev("session");
  var t = p.t;
  var [sessions, setSessions] = useState([]);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState("");
  var [busy, setBusy] = useState(false);
  var load = useCallback(function() {
    setLoading(true); setErr("");
    apiFetch("/api/sessions?userId=" + encodeURIComponent(p.userId), "GET", null, p.token).then(function(rows){
      setSessions(Array.isArray(rows) ? rows : []); setLoading(false);
    }).catch(function(e){ setErr((e && e.message) || "Failed to load sessions"); setLoading(false); });
  }, [p.userId, p.token, liveSessionRev]);
  useEffect(function(){ load(); }, [load]);
  var revokeOne = function(s) {
    if (!window.confirm("Sign out this device for " + (p.userName || "this user") + "?")) return;
    setBusy(true);
    apiFetch("/api/sessions/" + encodeURIComponent(s.sid) + "/revoke", "POST", {}, p.token, p.csrfToken).then(function(){
      setBusy(false); load();
    }).catch(function(e){ setBusy(false); alert((e && e.message) || "Failed"); });
  };
  var revokeAll = function() {
    var act = (sessions || []).filter(function(s){ return !s.revoked; });
    if (!act.length) return;
    if (!window.confirm("Sign out ALL " + act.length + " device(s) for " + (p.userName || "this user") + "?")) return;
    setBusy(true);
    Promise.all(act.map(function(s){
      return apiFetch("/api/sessions/" + encodeURIComponent(s.sid) + "/revoke", "POST", {}, p.token, p.csrfToken).catch(function(){});
    })).then(function(){ setBusy(false); load(); });
  };
  return <SessionsPanel t={t} sessions={sessions} loading={loading} error={err} busy={busy} selfView={false} onRefresh={load} onRevoke={revokeOne} onRevokeAll={revokeAll} revokeAllLabel="Sign out all devices" emptyLabel="No sessions recorded for this user yet."/>;
};

// ===== LOGIN =====
var LoginPage = function(p) {
  var t = p.t;
  var [user, setUser] = useState(""); var [pass, setPass] = useState(""); var [err, setErr] = useState(""); var [showPass, setShowPass] = useState(false); var [loading, setLoading] = useState(false);
  var [remember, setRemember] = useState(true);
  var go = async function() {
    if (!user||!pass) return; setLoading(true); setErr("");
    var platform = getClientPlatform();
    var isNative = platform === "android" || platform === "ios";
    try {
      var data = await apiFetch("/api/login","POST",{ username:user, password:pass, rememberMe:remember, platform:platform, deviceId:getOrCreateDeviceId() });
      p.onLogin(data.user, data.token, data.csrfToken, remember || isNative, data.access);
    }
    catch(e) { setErr(t.loginError); } setLoading(false);
  };
  return <div style={{ position:"relative", minHeight:"100vh", background:"linear-gradient(170deg,#fbfbfd,#f4f1ec)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Cairo','Segoe UI',sans-serif", padding:16, overflow:"hidden" }}>
    {/* Soft decorative background accents — behind the card, partially off-screen */}
    <div style={{ position:"absolute", top:-160, left:-160, width:460, height:460, borderRadius:"50%", background:"radial-gradient(circle, rgba(205,169,104,0.15), transparent 70%)", pointerEvents:"none" }}/>
    <div style={{ position:"absolute", bottom:-180, right:-180, width:520, height:520, borderRadius:"50%", background:"radial-gradient(circle, rgba(28,42,69,0.05), transparent 70%)", pointerEvents:"none" }}/>
    <div style={{ position:"relative", background:"transparent", borderRadius:24, padding:"40px 36px", width:"100%", maxWidth:420, boxSizing:"border-box" }}>
      <div style={{ textAlign:"center", marginBottom:36 }}>
        <div style={{ fontFamily:"Georgia, 'Times New Roman', serif", fontSize:42, fontWeight:400, color:"#1a1a1a", letterSpacing:"0.38em", paddingLeft:"0.38em", lineHeight:1 }}>ARO</div>
        <div style={{ width:60, height:2, margin:"18px auto 0", background:"linear-gradient(90deg, transparent, #c19a59, transparent)" }}/>
      </div>
      {err && <div style={{ background:"#FEE2E2", color:"#B91C1C", padding:"10px 16px", borderRadius:10, fontSize:13, marginBottom:18, textAlign:"center" }}>{err}</div>}
      <div style={{ marginBottom:14 }}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, marginBottom:6, color:C.text }}>{t.username}</label>
        <div style={{ position:"relative" }}>
          <span style={{ position:"absolute", top:"50%", left:16, transform:"translateY(-50%)", color:"#c4bdb0", display:"flex", pointerEvents:"none" }}><User size={18}/></span>
          <input value={user} onChange={function(e){setUser(e.target.value);}} placeholder={t.username} style={{ width:"100%", height:52, padding:"0 16px 0 46px", borderRadius:14, border:"1px solid #ece9e2", background:"#fff", fontSize:15, outline:"none", boxSizing:"border-box", color:C.text }} onKeyDown={function(e){if(e.key==="Enter")go();}}/>
        </div>
      </div>
      <div style={{ marginBottom:26 }}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, marginBottom:6, color:C.text }}>{t.password}</label>
        <div style={{ position:"relative" }}>
          <span style={{ position:"absolute", top:"50%", left:16, transform:"translateY(-50%)", color:"#c4bdb0", display:"flex", pointerEvents:"none" }}><Lock size={18}/></span>
          <input type={showPass?"text":"password"} value={pass} onChange={function(e){setPass(e.target.value);}} placeholder={t.password} style={{ width:"100%", height:52, padding:"0 46px 0 46px", borderRadius:14, border:"1px solid #ece9e2", background:"#fff", fontSize:15, outline:"none", boxSizing:"border-box", color:C.text }} onKeyDown={function(e){if(e.key==="Enter")go();}}/>
          <button onClick={function(){setShowPass(!showPass);}} style={{ position:"absolute", top:"50%", right:14, transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"#c4bdb0", display:"flex" }}>{showPass?<EyeOff size={18}/>:<Eye size={18}/>}</button>
        </div>
      </div>
      <label style={{ display:"flex", alignItems:"center", gap:9, marginBottom:22, cursor:"pointer", userSelect:"none" }}>
        <input type="checkbox" checked={remember} onChange={function(e){ setRemember(e.target.checked); }} style={{ width:17, height:17, accentColor:"#c19a59", cursor:"pointer" }}/>
        <span style={{ fontSize:13.5, color:C.text, fontWeight:500 }}>{t.rememberMe||"Remember me"}</span>
      </label>
      <button onClick={go} disabled={loading} style={{ width:"100%", height:54, borderRadius:14, border:"none", background:"linear-gradient(135deg,#d4af6e,#c19a59,#b08842)", color:"#fff", fontSize:16, fontWeight:700, cursor:"pointer", boxShadow:"0 8px 20px rgba(193,154,89,0.32)", opacity:loading?0.75:1 }}>{loading?t.loading:t.loginBtn}</button>
      <div style={{ textAlign:"center", marginTop:16 }}>
        <a href="/forgot-password" onClick={function(e){ e.preventDefault(); window.history.pushState({}, "", "/forgot-password"); window.dispatchEvent(new PopStateEvent("popstate")); }} style={{ color:"#b08842", fontSize:13, fontWeight:500, textDecoration:"none" }}>Forgot Password?</a>
      </div>
    </div>
  </div>;
};

// ===== FORGOT PASSWORD =====
// Owner-only flow on the backend. The UI is identical for any email so the
// page never reveals whether an address is registered or owner-flagged.
var ForgotPasswordPage = function() {
  var [email, setEmail] = useState("");
  var [submitted, setSubmitted] = useState(false);
  var [loading, setLoading] = useState(false);
  var [err, setErr] = useState("");
  var go = async function() {
    if (!email) return;
    setLoading(true); setErr("");
    try {
      await apiFetch("/api/auth/forgot-password", "POST", { email: email });
      setSubmitted(true);
    } catch(e) {
      // Backend always returns success; only network errors land here.
      setErr("Connection error. Please try again.");
    }
    setLoading(false);
  };
  var backToLogin = function(e) {
    if (e) e.preventDefault();
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  return <div style={{ position:"relative", minHeight:"100vh", background:"linear-gradient(170deg,#fbfbfd,#f4f1ec)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Cairo','Segoe UI',sans-serif", padding:16, overflow:"hidden" }}>
    {/* Soft decorative background accents — behind the card, partially off-screen */}
    <div style={{ position:"absolute", top:-160, left:-160, width:460, height:460, borderRadius:"50%", background:"radial-gradient(circle, rgba(205,169,104,0.15), transparent 70%)", pointerEvents:"none" }}/>
    <div style={{ position:"absolute", bottom:-180, right:-180, width:520, height:520, borderRadius:"50%", background:"radial-gradient(circle, rgba(28,42,69,0.05), transparent 70%)", pointerEvents:"none" }}/>
    <div style={{ position:"relative", background:"transparent", borderRadius:24, padding:"40px 36px", width:"100%", maxWidth:420, boxSizing:"border-box" }}>
      <div style={{ textAlign:"center", marginBottom:36 }}>
        <div style={{ fontFamily:"Georgia, 'Times New Roman', serif", fontSize:42, fontWeight:400, color:"#1a1a1a", letterSpacing:"0.38em", paddingLeft:"0.38em", lineHeight:1 }}>ARO</div>
        <div style={{ width:60, height:2, margin:"18px auto 0", background:"linear-gradient(90deg, transparent, #c19a59, transparent)" }}/>
        <h1 style={{ margin:"22px 0 0", fontSize:24, fontWeight:800, color:C.text }}>Forgot Password</h1>
      </div>
      {submitted ? (
        <div>
          <div style={{ background:"#DCFCE7", color:"#166534", padding:"14px 16px", borderRadius:10, fontSize:14, marginBottom:18, textAlign:"center", lineHeight:1.5 }}>
            If this email is registered, a reset link has been sent.
          </div>
          <a href="/" onClick={backToLogin} style={{ display:"block", textAlign:"center", color:"#b08842", fontSize:14, fontWeight:500, textDecoration:"none" }}>Back to Login</a>
        </div>
      ) : (
        <div>
          {err && <div style={{ background:"#FEE2E2", color:"#B91C1C", padding:"10px 16px", borderRadius:10, fontSize:13, marginBottom:18, textAlign:"center" }}>{err}</div>}
          <div style={{ marginBottom:24 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:600, marginBottom:6, color:C.text }}>Email</label>
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", top:"50%", left:16, transform:"translateY(-50%)", color:"#c4bdb0", display:"flex", pointerEvents:"none" }}><Mail size={18}/></span>
              <input type="email" value={email} onChange={function(e){setEmail(e.target.value);}} placeholder="Email" style={{ width:"100%", height:52, padding:"0 16px 0 46px", borderRadius:14, border:"1px solid #ece9e2", background:"#fff", fontSize:15, outline:"none", boxSizing:"border-box", color:C.text }} onKeyDown={function(e){if(e.key==="Enter")go();}}/>
            </div>
          </div>
          <button onClick={go} disabled={loading} style={{ width:"100%", height:54, borderRadius:14, border:"none", background:"linear-gradient(135deg,#d4af6e,#c19a59,#b08842)", color:"#fff", fontSize:16, fontWeight:700, cursor:"pointer", boxShadow:"0 8px 20px rgba(193,154,89,0.32)", opacity:loading?0.75:1 }}>{loading?"Sending...":"Send Reset Link"}</button>
          <div style={{ textAlign:"center", marginTop:16 }}>
            <a href="/" onClick={backToLogin} style={{ color:"#b08842", fontSize:13, fontWeight:500, textDecoration:"none" }}>Back to Login</a>
          </div>
        </div>
      )}
    </div>
  </div>;
};

// ===== RESET PASSWORD =====
// Reads the token from ?token=... in the URL. No token → bounce to /login
// before render. On success, redirects after 3s.
var ResetPasswordPage = function() {
  // Read token once on mount; subsequent state changes shouldn't re-parse it.
  var token = useMemo(function(){
    try { return new URLSearchParams(window.location.search).get("token") || ""; }
    catch(e) { return ""; }
  }, []);

  var [pw, setPw] = useState("");
  var [pw2, setPw2] = useState("");
  var [showPw, setShowPw] = useState(false);
  var [loading, setLoading] = useState(false);
  var [err, setErr] = useState("");
  var [done, setDone] = useState(false);
  var [tokenInvalid, setTokenInvalid] = useState(false);

  useEffect(function(){
    if (!token) {
      window.history.replaceState({}, "", "/");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  }, [token]);

  useEffect(function(){
    if (!done) return;
    var to = setTimeout(function(){
      window.history.replaceState({}, "", "/");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }, 3000);
    return function(){ clearTimeout(to); };
  }, [done]);

  var validate = function() {
    if (pw.length < 8) return "Password must be at least 8 characters.";
    if (!/[A-Za-z]/.test(pw) || !/[0-9]/.test(pw)) return "Password must contain at least one letter and one number.";
    if (pw !== pw2) return "Passwords do not match.";
    return "";
  };

  var go = async function() {
    var v = validate();
    if (v) { setErr(v); return; }
    setLoading(true); setErr("");
    try {
      // Bypass the global apiFetch 401 handler — its window.location.reload()
      // would clobber the page on an invalid/expired token.
      var res = await fetch(API + "/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: token, newPassword: pw })
      });
      var data;
      try { data = await res.json(); } catch(e){ data = {}; }
      if (!res.ok) {
        if (res.status === 400) setTokenInvalid(true);
        else setErr(data.error || "Failed to reset password.");
      } else {
        setDone(true);
      }
    } catch(e) {
      setErr("Connection error. Please try again.");
    }
    setLoading(false);
  };

  if (!token) return null;

  return <div style={{ position:"relative", minHeight:"100vh", background:"linear-gradient(170deg,#fbfbfd,#f4f1ec)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Cairo','Segoe UI',sans-serif", padding:16, overflow:"hidden" }}>
    {/* Soft decorative background accents — behind the card, partially off-screen */}
    <div style={{ position:"absolute", top:-160, left:-160, width:460, height:460, borderRadius:"50%", background:"radial-gradient(circle, rgba(205,169,104,0.15), transparent 70%)", pointerEvents:"none" }}/>
    <div style={{ position:"absolute", bottom:-180, right:-180, width:520, height:520, borderRadius:"50%", background:"radial-gradient(circle, rgba(28,42,69,0.05), transparent 70%)", pointerEvents:"none" }}/>
    <div style={{ position:"relative", background:"transparent", borderRadius:24, padding:"40px 36px", width:"100%", maxWidth:420, boxSizing:"border-box" }}>
      <div style={{ textAlign:"center", marginBottom:32 }}>
        <div style={{ fontFamily:"Georgia, 'Times New Roman', serif", fontSize:42, fontWeight:400, color:"#1a1a1a", letterSpacing:"0.38em", paddingLeft:"0.38em", lineHeight:1 }}>ARO</div>
        <div style={{ width:60, height:2, margin:"18px auto 0", background:"linear-gradient(90deg, transparent, #c19a59, transparent)" }}/>
        <h1 style={{ margin:"22px 0 0", fontSize:24, fontWeight:800, color:C.text }}>Reset Password</h1>
      </div>
      {tokenInvalid ? (
        <div>
          <div style={{ background:"#FEE2E2", color:"#B91C1C", padding:"14px 16px", borderRadius:10, fontSize:14, marginBottom:18, textAlign:"center", lineHeight:1.5 }}>
            This link has expired or is invalid. Please request a new one.
          </div>
          <a href="/forgot-password" onClick={function(e){ e.preventDefault(); window.history.pushState({}, "", "/forgot-password"); window.dispatchEvent(new PopStateEvent("popstate")); }} style={{ display:"block", textAlign:"center", color:"#b08842", fontSize:14, fontWeight:500, textDecoration:"none" }}>Request a new reset link</a>
        </div>
      ) : done ? (
        <div style={{ background:"#DCFCE7", color:"#166534", padding:"14px 16px", borderRadius:10, fontSize:14, textAlign:"center", lineHeight:1.5 }}>
          Password reset successfully. Redirecting to login...
        </div>
      ) : (
        <div>
          {err && <div style={{ background:"#FEE2E2", color:"#B91C1C", padding:"10px 16px", borderRadius:10, fontSize:13, marginBottom:18, textAlign:"center" }}>{err}</div>}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:600, marginBottom:6, color:C.text }}>New Password</label>
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", top:"50%", left:16, transform:"translateY(-50%)", color:"#c4bdb0", display:"flex", pointerEvents:"none" }}><Lock size={18}/></span>
              <input type={showPw?"text":"password"} value={pw} onChange={function(e){setPw(e.target.value);setErr("");}} placeholder="New Password" style={{ width:"100%", height:52, padding:"0 46px 0 46px", borderRadius:14, border:"1px solid #ece9e2", background:"#fff", fontSize:15, outline:"none", boxSizing:"border-box", color:C.text }}/>
              <button onClick={function(){setShowPw(!showPw);}} style={{ position:"absolute", top:"50%", right:14, transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"#c4bdb0", display:"flex" }}>{showPw?<EyeOff size={18}/>:<Eye size={18}/>}</button>
            </div>
          </div>
          <div style={{ marginBottom:24 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:600, marginBottom:6, color:C.text }}>Confirm Password</label>
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", top:"50%", left:16, transform:"translateY(-50%)", color:"#c4bdb0", display:"flex", pointerEvents:"none" }}><Lock size={18}/></span>
              <input type={showPw?"text":"password"} value={pw2} onChange={function(e){setPw2(e.target.value);setErr("");}} placeholder="Confirm Password" style={{ width:"100%", height:52, padding:"0 16px 0 46px", borderRadius:14, border:"1px solid #ece9e2", background:"#fff", fontSize:15, outline:"none", boxSizing:"border-box", color:C.text }} onKeyDown={function(e){if(e.key==="Enter")go();}}/>
            </div>
          </div>
          <button onClick={go} disabled={loading} style={{ width:"100%", height:54, borderRadius:14, border:"none", background:"linear-gradient(135deg,#d4af6e,#c19a59,#b08842)", color:"#fff", fontSize:16, fontWeight:700, cursor:"pointer", boxShadow:"0 8px 20px rgba(193,154,89,0.32)", opacity:loading?0.75:1 }}>{loading?"Resetting...":"Reset Password"}</button>
        </div>
      )}
    </div>
  </div>;
};

// ===== PRIVACY POLICY (public, no auth) =====
// Rendered for authPath === "/privacy" before the auth gate, so it is reachable
// directly via URL whether logged in or out (app stores fetch this during review).
// Self-contained static content: no API calls, no token, no hooks — renders
// identically regardless of session state. English-only legal text → forced LTR.
var PrivacyPolicyPage = function() {
  var h2 = { margin:"28px 0 8px", fontSize:17, fontWeight:800, color:C.text };
  var p  = { margin:"0 0 12px", fontSize:14.5, lineHeight:1.7, color:C.text };
  var ul = { margin:"0 0 12px", padding:"0 0 0 20px", fontSize:14.5, lineHeight:1.7, color:C.text };
  var li = { marginBottom:6 };
  return <div dir="ltr" style={{ direction:"ltr", minHeight:"100vh", background:"#F0F2F5", fontFamily:"'Cairo','Segoe UI',sans-serif", padding:"32px 16px", boxSizing:"border-box" }}>
    <div style={{ background:"#fff", borderRadius:16, padding:"40px 40px 48px", width:"100%", maxWidth:760, margin:"0 auto", boxShadow:"0 8px 32px rgba(0,0,0,0.08)" }}>
      <div style={{ borderBottom:"1px solid #E2E8F0", paddingBottom:18, marginBottom:24 }}>
        <div style={{ fontFamily:"Georgia, 'Times New Roman', serif", fontSize:34, fontWeight:400, color:"#1a1a1a", letterSpacing:"0.38em", lineHeight:1 }}>ARO</div>
        <div style={{ width:60, height:2, margin:"14px 0 18px", background:"linear-gradient(90deg, #c19a59, transparent)" }}/>
        <h1 style={{ margin:0, fontSize:26, fontWeight:800, color:C.text }}>Privacy Policy — ARO CRM</h1>
        <div style={{ marginTop:8, fontSize:13, color:C.textLight }}>Last updated: 3 June 2026</div>
      </div>

      <p style={p}>ARO CRM ("the App") is operated by AROG For Real Estate Development and Marketing ("we", "us", "our"), a limited liability company. This Privacy Policy explains what information the App collects, how we use it, and your rights.</p>

      <h2 style={h2}>1. Who This App Is For</h2>
      <p style={p}>ARO CRM is an internal business application for our authorized employees and sales staff to manage real estate sales leads, deals, and related workflows. Access requires an account issued by us.</p>

      <h2 style={h2}>2. Information We Collect</h2>
      <ul style={ul}>
        <li style={li}><strong>Account information:</strong> your name, email, role, and login credentials.</li>
        <li style={li}><strong>Usage data:</strong> actions you take in the app (leads assigned, deals, status changes, callbacks) for business operations.</li>
        <li style={li}><strong>Customer/lead data:</strong> contact details and notes about prospective real estate clients, entered by staff for business purposes.</li>
        <li style={li}><strong>Device &amp; push token:</strong> when you enable notifications, we store a Firebase Cloud Messaging (FCM) device token to deliver push notifications to your device.</li>
        <li style={li}><strong>Technical data:</strong> basic device/connection information necessary to operate the service.</li>
      </ul>

      <h2 style={h2}>3. How We Use Information</h2>
      <ul style={ul}>
        <li style={li}>To provide and operate the CRM (assign leads, track deals, send work notifications).</li>
        <li style={li}>To send push notifications about leads, deals, approvals, and callback reminders relevant to your role.</li>
        <li style={li}>To secure accounts and prevent unauthorized access.</li>
      </ul>

      <h2 style={h2}>4. Push Notifications</h2>
      <p style={p}>We use Firebase Cloud Messaging (a Google service) to deliver notifications. A device token is associated with your account while you are logged in, and is removed or reassigned when you log out or another user logs in on the same device. You can disable notifications at any time in your device settings.</p>

      <h2 style={h2}>5. Data Sharing</h2>
      <p style={p}>We do not sell your personal information. Data is accessible only to authorized personnel within our organization according to their role. We use Google Firebase to deliver notifications, subject to Google's privacy practices.</p>

      <h2 style={h2}>6. Data Retention</h2>
      <p style={p}>We retain information for as long as necessary to operate the CRM and meet legal and business obligations.</p>

      <h2 style={h2}>7. Security</h2>
      <p style={p}>We apply reasonable technical and organizational measures to protect data, including access controls and rate limiting. No system is completely secure.</p>

      <h2 style={h2}>8. Your Rights</h2>
      <p style={p}>You may request access to, correction of, or deletion of your personal data by contacting us. Customer/lead data is managed by our organization as the data controller for business purposes.</p>

      <h2 style={h2}>9. Account and Data Deletion</h2>
      <p style={p}>You can request deletion of your ARO CRM account and all associated personal data — including your name, email, device/push token, location and attendance records, and activity history — by emailing <a href="mailto:amgad.mohamed@aro-investment.com" style={{ color:C.primary, fontWeight:600 }}>amgad.mohamed@aro-investment.com</a> from your registered email address with the subject "Delete my account". Because ARO CRM is an internal company tool, accounts are managed by your company administrator; employees may also request deletion directly from their administrator.</p>
      <p style={p}>We process deletion requests within 30 days. On deletion, we remove your personal account data from the active system. Customer/lead records created for business purposes, and any records we are required to keep to meet legal, tax, or accounting obligations, may be retained by AROG For Real Estate Development and Marketing as the data controller for the period required by law.</p>

      <h2 style={h2}>10. Contact</h2>
      <p style={p}>AROG For Real Estate Development and Marketing<br/>Email: <a href="mailto:amgad.mohamed@aro-investment.com" style={{ color:C.primary, fontWeight:600 }}>amgad.mohamed@aro-investment.com</a></p>

      <h2 style={h2}>11. Changes</h2>
      <p style={p}>We may update this Policy; the "Last updated" date reflects the latest version.</p>
    </div>
  </div>;
};

// ===== SIDEBAR =====
// `colOverride` (P4) — an explicit ink, used by the Page Visibility replica for
// a menu entry a rank CANNOT see. Kept as its own parameter rather than smuggled
// through `accent` with active:true, which would have worked and read as a bug.
var SidebarIcon = function(id, active, accent, colOverride){
  var col = colOverride || (active ? (accent || "#fff") : "rgba(255,255,255,0.4)");
  var sw = 1.5;
  var base = { width:18, height:18, display:"block" };
  switch(id){
    case "dashboard":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><rect x="1" y="1" width="7" height="7" rx="2" fill={col}/><rect x="10" y="1" width="7" height="7" rx="2" fill={col} opacity=".4"/><rect x="1" y="10" width="7" height="7" rx="2" fill={col} opacity=".4"/><rect x="10" y="10" width="7" height="7" rx="2" fill={col} opacity=".4"/></svg>;
    case "leads":
    case "users":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><circle cx="9" cy="6" r="3.5" stroke={col} strokeWidth={sw}/><path d="M2 16c0-3.9 3.1-7 7-7s7 3.1 7 7" stroke={col} strokeWidth={sw} strokeLinecap="round"/></svg>;
    case "dailyReq":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><rect x="2" y="2" width="14" height="14" rx="3" stroke={col} strokeWidth={sw}/><path d="M5 9h8M5 6h8M5 12h5" stroke={col} strokeWidth={sw} strokeLinecap="round"/></svg>;
    case "deals":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><path d="M15 6L9 2.5 3 6v7l6 3.5 6-3.5V6z" stroke={col} strokeWidth={sw} strokeLinejoin="round"/></svg>;
    case "eoi":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke={col} strokeWidth={sw}/><path d="M9 6v3.5l2.5 2.5" stroke={col} strokeWidth={sw} strokeLinecap="round"/></svg>;
    case "kpis":
      if (id==="kpis") return <svg style={base} viewBox="0 0 18 18" fill="none"><path d="M2 14l4-5 3 3 4-7 3 4" stroke={col} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"/></svg>;
      return <svg style={base} viewBox="0 0 18 18" fill="none"><path d="M3 5h12M3 9h8M3 13h10" stroke={col} strokeWidth={sw} strokeLinecap="round"/></svg>;
    case "reports":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><path d="M2 14l4-5 3 3 4-7 3 4" stroke={col} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "team":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><circle cx="5.5" cy="6" r="2.5" stroke={col} strokeWidth={sw}/><circle cx="12.5" cy="6" r="2.5" stroke={col} strokeWidth={sw}/><path d="M1 15c0-2.5 2-4.5 4.5-4.5M17 15c0-2.5-2-4.5-4.5-4.5M9 15c0-2.5 2-4.5 4.5-4.5" stroke={col} strokeWidth={sw} strokeLinecap="round"/></svg>;
    case "archive":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><rect x="2" y="2" width="14" height="14" rx="3" stroke={col} strokeWidth={sw}/><path d="M6 9h6" stroke={col} strokeWidth={sw} strokeLinecap="round"/></svg>;
    case "brokers":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><circle cx="9" cy="6" r="3" stroke={col} strokeWidth={sw}/><path d="M2 16c0-3.5 3-6 7-6s7 2.5 7 6" stroke={col} strokeWidth={sw} strokeLinecap="round"/><path d="M14 3l2 1.5L14 6" stroke={col} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "commissions":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><path d="M9 2v14M12.5 5.5C12 4.2 10.7 3.5 9 3.5S6 4.2 6 5.5c0 1.3 1 2 3 2.5s3 1.2 3 2.5-1.3 2-3 2-3-.7-3.5-2" stroke={col} strokeWidth={sw} strokeLinecap="round"/></svg>;
    case "attendance":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><path d="M9 3c-3.3 0-6 2.7-6 6c0 1.5.5 2.8 1.3 3.8M9 5c-2.2 0-4 1.8-4 4c0 1 .4 2 1 2.7M9 7c-1.1 0-2 .9-2 2c0 .5.2 1 .5 1.4M9 9v3M11 7c.6.6 1 1.4 1 2.4c0 1.8-.5 3.5-1.4 5M14 5.3c1 1 1.5 2.4 1.5 4c0 .7-.1 1.4-.3 2" stroke={col} strokeWidth={sw} strokeLinecap="round" fill="none"/></svg>;
    case "settings":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="2.5" stroke={col} strokeWidth={sw}/><path d="M9 2v1.5M9 14.5V16M2 9h1.5M14.5 9H16M4.1 4.1l1.1 1.1M12.8 12.8l1.1 1.1M4.1 13.9l1.1-1.1M12.8 5.2l1.1-1.1" stroke={col} strokeWidth={sw} strokeLinecap="round"/></svg>;
    case "assets":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><path d="M9 1.5L2 5v8l7 3.5L16 13V5L9 1.5z" stroke={col} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round"/><path d="M2 5l7 3.5L16 5M9 8.5V16" stroke={col} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round"/></svg>;
    case "meetings":
      return <svg style={base} viewBox="0 0 18 18" fill="none"><rect x="2.5" y="3.5" width="13" height="12" rx="2.5" stroke={col} strokeWidth={sw}/><path d="M2.5 7.5h13M6 2v3M12 2v3" stroke={col} strokeWidth={sw} strokeLinecap="round"/><circle cx="9" cy="11.5" r="1.6" fill={col}/></svg>;
    default:
      return <svg style={base} viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="3" stroke={col} strokeWidth={sw}/></svg>;
  }
};

// ===== THE NAV LIST — ONE ARRAY, AND ONE ROW THAT DRAWS IT =================
//
// P2 of the sidebar-preview editor. Nothing about the menu changes here: the
// array and the row stop being local to Sidebar so that a SECOND surface can
// draw the same menu instead of a copy of it.
//
// WHY IT MATTERS THAT THERE IS EXACTLY ONE. Page Visibility is about to render a
// replica of a rank's sidebar, and a replica assembled from its own list is a
// list that drifts — silently, and in the worst direction: the preview says a
// rank sees something the real menu never offered, or misses something it does.
// Fidelity by construction beats fidelity by gate, so the gate's job here is to
// keep the construction honest rather than to compare two lists: it asserts
// there is exactly ONE nav literal in this file, that Sidebar reads it rather
// than carrying its own, and that its ids are precisely the SURFACES pages with
// a menu entry.
//
// VISIBILITY IS STILL NOT DECIDED HERE. Every entry is filtered through
// canSeePage(), which since Phase 5 reads the SERVER's answer off the access
// envelope. Before Phase 1c this array carried its own boolean per entry, which
// is how it drifted from the route guard on seven cases without anybody
// noticing; before Phase 5 it read a local table, which would have gone
// rank-blind the moment a page was taken over.
//
// `t` is a PARAMETER, not a module read. The labels are translated, and the
// replica renders in the OWNER's language while the real menu renders in the
// viewer's — one array, two callers, two languages, no second list.
var NAV_ITEMS = function(t){
  return [
    {id:"dashboard",   label:t.dashboard},
    {id:"leads",       label:t.leads},
    {id:"dailyReq",    label:t.dailyReq},
    {id:"meetings",    label:"Meetings"},
    {id:"eoi",         label:"EOI"},
    {id:"deals",       label:t.deals},
    {id:"kpis",        label:"KPIs"},
    {id:"reports",     label:t.reports,      adminSection:true},
    {id:"commissions", label:"Commissions",  adminSection:true},
    {id:"team",        label:t.team,         adminSection:true},
    {id:"attendance",  label:"Attendance",   adminSection:true},
    {id:"users",       label:t.users,        adminSection:true},
    {id:"brokers",     label:t.brokers,      adminSection:true},
    {id:"archive",     label:t.archive,      adminSection:true},
    {id:"assets",      label:"Assets",       adminSection:true},
    {id:"settings",    label:t.settings,     adminSection:true}
  ];
};

// The sidebar's row. PRESENTATION ONLY — active state, the badge and the click
// handler arrive as arguments, so the row can be drawn by something that is not
// a navigation bar without that caller reimplementing icon, label or spacing.
//
// Called as a plain function rather than mounted as <NavRow/>, matching
// SidebarIcon just above it; the `key` therefore lives on the element this
// returns, which is what the caller pushes into its array.
//
// ===== THE REPLICA'S TWO INKS (recalibrated 2026-08-23, owner) =============
//
// A hidden entry must read as "present but OFF", not "barely there". The first
// pass put hidden at 0.16, which was legible as absence rather than as a state —
// at arm's length the row looked like a rendering artefact.
//
// BOTH STATES HAD TO MOVE, and that is the part worth writing down. The replica
// used to spend the sidebar's own INACTIVE ink (0.4) on "visible", so raising
// hidden to a legible 0.65 would have made hidden BRIGHTER than lit. So the
// affirmative state takes the sidebar's ACTIVE ink instead — which the replica
// has spare, because a preview has no current page.
//
// THE COST, STATED: the replica's ink no longer matches the live sidebar's,
// where an inactive entry renders at 0.4. The fidelity gate asserts the item
// LIST, its order and its icons — never the ink — so nothing there breaks, and
// the design note never claimed ink parity. This is a deliberate trade of
// pixel-fidelity for legibility in an editor, made once, here.
//
// AND DIMNESS IS NOT THE ONLY SIGNAL. Opacity alone fails at a meter and fails
// entirely for anyone who cannot separate two greys, so OFF also carries a
// strikethrough and a HOLLOW toggle against the lit state's FILLED one. Filled
// vs hollow survives distance, greyscale and a bad screen; 0.95 vs 0.65 does
// not have to carry the meaning on its own.
var NAV_LIT_INK = "rgba(255,255,255,0.95)";
var NAV_DIM_INK = "rgba(255,255,255,0.65)";
// The strike is deliberately weaker than the label it crosses — it should read
// as "switched off", not as "deleted".
var NAV_STRIKE   = "rgba(255,255,255,0.42)";

// The replica's switch. FILLED = on, HOLLOW = off, at both sizes.
var previewSwitch = function(on, big, onClick, title){
  var w = big ? 22 : 20, h = big ? 12 : 11, k = big ? 9 : 8;
  return <span onClick={onClick} title={title}
    style={{ width:w, height:h, borderRadius:h/2, flexShrink:0, position:"relative",
             cursor: onClick ? "pointer" : "default", boxSizing:"border-box",
             background: on ? "rgba(255,255,255,0.88)" : "transparent",
             border: on ? "1px solid rgba(255,255,255,0.88)" : "1px solid rgba(255,255,255,0.45)" }}>
    <span style={{ position:"absolute", top:(h-k)/2 - 1, width:k, height:k, borderRadius:"50%",
                   left: on ? "auto" : 1.5, right: on ? 1.5 : "auto",
                   background: on ? "#1C1E28" : "rgba(255,255,255,0.55)" }}/>
  </span>;
};

var NavRow = function(o){
  var item = o.item, act = !!o.active, isRTL = !!o.isRTL, dim = !!o.dim;
  var badge = (typeof o.badge === "number" && o.badge > 0) ? o.badge : 0;
  // `preview` scopes the recalibrated inks to the REPLICA. The live sidebar is
  // untouched: it keeps #fff for the current page and 0.4 for the rest, which is
  // what it has always rendered and what the fidelity work was about preserving.
  var ink = o.preview ? (dim ? NAV_DIM_INK : NAV_LIT_INK)
                      : (act ? "#fff" : "rgba(255,255,255,0.4)");
  // `selected` is the replica's "the pane is describing this row" state. It is
  // NOT `active`: a preview has no current page, and reusing the active
  // treatment would read as "you are here" on somebody else's menu.
  var selBg = o.selected ? "rgba(255,255,255,0.07)" : null;
  return <button key={item.id} className="crm-nav-item" onClick={o.onClick}
    onMouseEnter={function(e){ if(!act) e.currentTarget.style.background="rgba(255,255,255,0.05)"; }}
    onMouseLeave={function(e){ if(!act) e.currentTarget.style.background=selBg || "transparent"; }}
    style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"7px 12px", borderRadius:12, border:"none",
      cursor: o.onClick ? "pointer" : "default",
      background: act ? (item.accent ? item.accent+"33" : "rgba(255,255,255,0.1)") : (selBg || "transparent"),
      color: ink,
      fontSize:13, fontWeight: act?500:400, marginBottom:2, textAlign:isRTL?"right":"left",
      fontFamily:"inherit"
    }}>
    {/* The icon stays legible when hidden — it is what makes the row read as an
        entry that is switched off rather than as an entry that failed to draw. */}
    {SidebarIcon(item.id, act, item.accent, o.preview ? ink : null)}
    <span style={{ flex:1, textAlign:isRTL?"right":"left",
                   textDecoration: dim ? "line-through" : "none",
                   textDecorationColor: dim ? NAV_STRIKE : undefined,
                   textDecorationThickness: dim ? "1px" : undefined }}>{item.label}</span>
    {badge>0 && <div style={{ marginLeft:"auto", background:"#EF4444", borderRadius:10, padding:"1px 7px", fontSize:10, fontWeight:700, color:"#fff", display:"flex", alignItems:"center", gap:3 }}>
      <div style={{ width:4, height:4, borderRadius:"50%", background:"#fff" }}/>
      {badge}
    </div>}
    {o.trailing || null}
  </button>;
};

// ===== THE SIDEBAR REPLICA (P4) ============================================
//
// A rank's actual menu, drawn from NAV_ITEMS and NavRow — the SAME array and the
// SAME row the live sidebar uses, which is the point of P2. Lit = that rung sees
// it, dimmed = it does not.
//
// IT IS THE EDITOR (P5). Clicking a row switches that entry for the rung in the
// dropdown; the chevron opens its tabs, which switch the same way. Nothing is
// written to the server until Save, and a surface still on the sidebar rule needs
// its take-over confirmed on top of that — the two-step contract is unchanged,
// only the control that starts it moved.
//
// WHAT IS DELIBERATELY NOT REPLICATED, and each omission is a decision:
//   - the leads badge, because it is the VIEWING owner's count and would attach
//     a live number to a hypothetical person;
//   - the active-page highlight, because a preview has no current page — which
//     is what frees the sidebar's white-10% active treatment for hover;
//   - the footer (avatar, role line, logout), none of which the matrix governs;
//   - the language: this renders in the OWNER's `t`. An Arabic-locale user's real
//     menu carries Arabic labels and this preview will not, so the screen says so
//     rather than leaving it as a silent difference.
//
// THE TAB ROWS ARE EDITOR CHROME, NOT SIDEBAR CHROME. The real sidebar has no
// sub-items, so they are drawn deliberately unlike a nav row — indented, on a
// dashed rail, smaller, no icon — rather than as convincing fake menu entries.
var SidebarReplicaTabRow = function(o){
  var dim = !!o.dim, sel = !!o.selected;
  // Same two inks and the same filled/hollow switch as the page rows. A tab that
  // reads differently from the page above it makes the reader work out which
  // convention applies where, twice per row.
  return <div key={o.surfaceKey} onClick={o.onClick}
    style={{ display:"flex", alignItems:"center", gap:8, padding:"4px 10px 4px 30px",
             borderRadius:8, fontSize:11.5, cursor: o.onClick ? "pointer" : "default",
             background: sel ? "rgba(255,255,255,0.07)" : "transparent",
             color: dim ? NAV_DIM_INK : NAV_LIT_INK }}>
    <span style={{ flex:1, textDecoration: dim ? "line-through" : "none",
                   textDecorationColor: dim ? NAV_STRIKE : undefined,
                   textDecorationThickness: dim ? "1px" : undefined }}>{o.label}</span>
    {o.onToggle && previewSwitch(!dim, false,
      function(e){ e.stopPropagation(); o.onToggle(); },
      dim ? "Show this tab from this rank up" : "Hide it from this rank down")}
    {o.trailing || null}
  </div>;
};

var SidebarReplica = function(p){
  var t = p.t, ctx = p.ctx, surfaces = ctx.surfaces;
  var tabsOf = function(pageId){
    return Object.keys(surfaces).filter(function(k){
      return surfaces[k].kind === "tab" && surfaces[k].pageId === pageId;
    });
  };
  var rows = [];
  var dividerDrawn = false;
  NAV_ITEMS(t).forEach(function(item){
    // The divider sits before the first adminSection entry, exactly as the live
    // sidebar draws it — and unlike the sidebar, the replica shows every entry
    // whether or not the rung sees it, so the divider always appears.
    if (item.adminSection && !dividerDrawn) {
      rows.push(<div key="rep-divider" style={{ height:1, background:"rgba(255,255,255,0.06)", margin:"6px 4px" }}/>);
      dividerDrawn = true;
    }
    var pageKey = "page:" + item.id;
    var visible = previewSees(ctx, pageKey);
    var kids = tabsOf(item.id);
    var open = !!p.expanded[item.id];
    rows.push(NavRow({
      item: item,
      // The replica never renders an ACTIVE row — a preview has no current page.
      // `selected` is a different thing (which surface the pane is describing)
      // and gets its own, quieter treatment so it cannot be read as "you are
      // here".
      active: false,
      isRTL: false,
      dim: !visible,
      preview: true,
      selected: p.selected === pageKey,
      onClick: p.onSelect ? function(){ p.onSelect(pageKey); } : null,
      trailing: <span style={{ display:"flex", alignItems:"center", gap:7, flexShrink:0 }}>
        {kids.length > 0 &&
          <span onClick={function(e){ e.stopPropagation(); p.onToggleExpand(item.id); }}
                title={kids.length + " tab" + (kids.length===1?"":"s")}
                style={{ fontSize:10, color: visible ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.5)",
                         cursor:"pointer", userSelect:"none", fontVariantNumeric:"tabular-nums" }}>
            {open ? "▾" : "▸"}{kids.length}
          </span>}
        {p.onToggle && previewSwitch(visible, true,
          function(e){ e.stopPropagation(); p.onToggle(pageKey, !visible); },
          visible ? "Hide it from this rank down" : "Show it from this rank up")}
      </span>
    }));
    if (kids.length && open) {
      rows.push(<div key={"rep-rail-"+item.id}
        style={{ borderLeft:"1px dashed rgba(255,255,255,0.14)", marginLeft:18, paddingLeft:2 }}>
        {kids.map(function(tk){
          var tvis = previewSees(ctx, tk);
          return SidebarReplicaTabRow({
            surfaceKey: tk,
            // The registry label is "Settings › General"; the parent row already
            // says Settings.
            label: (surfaces[tk].label || tk).replace(/^.*›\s*/, ""),
            dim: !tvis,
            selected: p.selected === tk,
            onClick: p.onSelect ? function(){ p.onSelect(tk); } : null,
            onToggle: p.onToggle ? function(){ p.onToggle(tk, !tvis); } : null
          });
        })}
      </div>);
    }
  });

  return <div style={{ background:"#1C1E28", borderRadius:10, padding:"14px 12px", width:"100%", maxWidth:262,
                       border:"1px solid rgba(255,255,255,0.08)", boxSizing:"border-box" }}>
    <div style={{ color:"rgba(255,255,255,0.62)", fontSize:17, fontWeight:800, letterSpacing:"-1.5px", lineHeight:1,
                  padding:"0 6px 12px", borderBottom:"1px solid rgba(255,255,255,0.07)", marginBottom:8 }}>
      ARO<span style={{ color:"rgba(255,255,255,0.25)", fontSize:11, fontWeight:400, letterSpacing:0 }}> CRM</span>
    </div>
    <div style={{ display:"flex", flexDirection:"column" }}>{rows}</div>
  </div>;
};

// Sidebar drawer width (px) — the fixed panel width AND the swipe-gesture travel
// distance. Kept as a module constant so the render helper and the touch effect
// agree without threading it through deps.
var SIDEBAR_W = 240;
var Sidebar = function(p) {
  var t = p.t;
  // THE menu, filtered by the envelope. The array itself is NAV_ITEMS at module
  // scope — see the note there for why there may only ever be one of it.
  var items = NAV_ITEMS(t).filter(function(it){ return canSeePage(p.access, it.id); });
  var isRTL = t.dir==="rtl";
  // ===== Interactive edge-swipe drawer (Option 2 — finger-tracking) =====
  // The panel follows the finger during an edge drag, then flicks/settles open or
  // closed on release. Mobile-only; desktop skips the whole effect. Reuses the
  // panel's own translateX + "transform 0.28s ease" so there is a single animation
  // system — during the drag we swap transition to none and drive transform/opacity
  // imperatively, then restore the transition for the settle. A tiny phase machine
  // (idle | drag | settle) keeps React renders in sync with the imperative writes so
  // a mid-gesture parent re-render can't clobber the panel position.
  var panelRef = useRef(null);
  var overlayRef = useRef(null);
  var dragRef = useRef({ tx:0 });
  var settleRef = useRef({ tx:0, open:false });
  var liveRef = useRef({});
  var settleTimerRef = useRef(null);
  var [dragPhase, setDragPhase] = useState("idle");
  liveRef.current = { open:p.open, onOpen:p.onOpen, onClose:p.onClose };
  // Openness fraction (0 closed → 1 open) for a given translateX; used by the
  // render path so the overlay mounts at the correct dim even before the first
  // imperative paint lands.
  var openFracOf = function(tx){ var o = isRTL ? (SIDEBAR_W-tx)/SIDEBAR_W : (SIDEBAR_W+tx)/SIDEBAR_W; return o<0?0:(o>1?1:o); };
  useEffect(function(){
    if (!p.isMobile) return;
    var W=SIDEBAR_W, EDGE=24, INTENT=6, FLICK=0.5;
    var rtl = isRTL;
    var closedTx = rtl ? W : -W;
    var g = { active:false, confirmed:false, startX:0, startY:0, baseTx:0, lastX:0, lastT:0, vx:0 };
    var clamp = function(tx){ return rtl ? Math.max(0,Math.min(W,tx)) : Math.max(-W,Math.min(0,tx)); };
    var frac = function(tx){ var o = rtl ? (W-tx)/W : (W+tx)/W; return o<0?0:(o>1?1:o); };
    var paint = function(tx){
      dragRef.current.tx = tx;
      var el=panelRef.current;
      if (el){ el.style.transition="none"; el.style.transform="translateX("+tx+"px)"; }
      var ov=overlayRef.current;
      if (ov){ ov.style.transition="none"; ov.style.opacity=String(frac(tx)); }
    };
    var onStart, onMove, onEnd;
    var cleanupMove = function(){
      document.removeEventListener("touchmove", onMove, { passive:false });
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
    onStart = function(e){
      if (g.active) return;
      if (!e.touches || e.touches.length!==1) return;
      var x=e.touches[0].clientX, y=e.touches[0].clientY;
      var open = !!liveRef.current.open;
      if (!open){
        var atEdge = rtl ? (x >= window.innerWidth - EDGE) : (x <= EDGE);
        if (!atEdge) return;
      }
      g.active=true; g.confirmed=false;
      g.startX=x; g.startY=y; g.baseTx = open ? 0 : closedTx;
      g.lastX=x; g.lastT=e.timeStamp; g.vx=0;
      document.addEventListener("touchmove", onMove, { passive:false });
      document.addEventListener("touchend", onEnd);
      document.addEventListener("touchcancel", onEnd);
    };
    onMove = function(e){
      if (!g.active || !e.touches || e.touches.length!==1) return;
      var x=e.touches[0].clientX, y=e.touches[0].clientY;
      var dx=x-g.startX, dy=y-g.startY;
      if (!g.confirmed){
        if (Math.abs(dx)<INTENT && Math.abs(dy)<INTENT) return;
        if (Math.abs(dy) > Math.abs(dx)){ g.active=false; cleanupMove(); return; }
        g.confirmed=true;
        if (settleTimerRef.current){ clearTimeout(settleTimerRef.current); settleTimerRef.current=null; }
        setDragPhase("drag");
      }
      if (e.cancelable) e.preventDefault();
      var tx=clamp(g.baseTx+dx);
      var now=e.timeStamp, dt=now-g.lastT;
      if (dt>0) g.vx=(x-g.lastX)/dt;
      g.lastX=x; g.lastT=now;
      paint(tx);
    };
    onEnd = function(){
      if (!g.active){ cleanupMove(); return; }
      g.active=false;
      cleanupMove();
      if (!g.confirmed) return;
      var f=frac(dragRef.current.tx);
      var openingVx = rtl ? -g.vx : g.vx;
      var toOpen = openingVx > FLICK ? true : (openingVx < -FLICK ? false : f >= 0.5);
      var targetTx = toOpen ? 0 : closedTx;
      settleRef.current = { tx:targetTx, open:toOpen };
      var el=panelRef.current;
      if (el){ el.style.transition="transform 0.28s ease"; void el.offsetWidth; el.style.transform="translateX("+targetTx+"px)"; }
      var ov=overlayRef.current;
      if (ov){ ov.style.transition="opacity 0.28s ease"; void ov.offsetWidth; ov.style.opacity=String(toOpen?1:0); }
      setDragPhase("settle");
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      settleTimerRef.current = setTimeout(function(){ settleTimerRef.current=null; setDragPhase("idle"); }, 300);
      var wasOpen=!!liveRef.current.open;
      if (toOpen && !wasOpen && liveRef.current.onOpen) liveRef.current.onOpen();
      else if (!toOpen && wasOpen && liveRef.current.onClose) liveRef.current.onClose();
    };
    document.addEventListener("touchstart", onStart, { passive:true });
    return function(){
      document.removeEventListener("touchstart", onStart);
      cleanupMove();
      if (settleTimerRef.current){ clearTimeout(settleTimerRef.current); settleTimerRef.current=null; }
    };
  }, [p.isMobile, isRTL]);
  // Badge source priority — must agree with the LeadsPage "All" tab:
  //  1. p.sidebarLeadsTotal — historical-scope count from /api/leads/counts
  //     (for sales: ?scope=historical, which mirrors the LeadsPage filters
  //     exactly; for other roles: the strict-current-owner /counts that
  //     also matches what they see). Same fetch shape both pages use → one
  //     number everywhere.
  //  2. p.leadsTotal — bootstrap /api/leads.total. Coarser fallback while
  //     #1 is still in flight (1s debounce window on first paint).
  //  3. p.leads.filter — last-resort if neither has resolved.
  // typeof === "number" (not >0) so a legitimate 0 from the server still
  // renders the hidden-badge state rather than falling back.
  var leadsCount = (typeof p.sidebarLeadsTotal === "number")
    ? p.sidebarLeadsTotal
    : (typeof p.leadsTotal === "number" && p.leadsTotal > 0)
      ? p.leadsTotal
      : (Array.isArray(p.leads) ? p.leads.filter(function(l){return !l.archived;}).length : 0);
  var userName = p.cu.username==="amgad" ? "Amgad Mohamed" : p.cu.name;
  var userInitial = (userName||"?")[0];
  var userRole = p.cu.title || ({admin:"Admin",operation:"Operations",manager:"Manager",team_leader:"Team Leader",sales:"Sales",viewer:"Viewer",office_boy:"Office Boy"}[p.cu.role]||"");
  var st = {
    width:SIDEBAR_W, height:"100vh",
    background:"rgba(28, 30, 40, 0.95)",
    borderRight:"1px solid rgba(255,255,255,0.07)",
    padding:"calc(24px + env(safe-area-inset-top, 0px)) 14px 16px",
    fontFamily:"-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
    display:"flex", flexDirection:"column",
    position:"fixed", top:0, zIndex:150,
    transition:"transform 0.28s ease",
    boxSizing:"border-box"
  };
  if (isRTL) { st.right=0; st.borderRight="none"; st.borderLeft="1px solid rgba(255,255,255,0.07)"; } else { st.left=0; }
  if (p.isMobile) {
    if (dragPhase==="drag") { st.transition="none"; st.transform="translateX("+dragRef.current.tx+"px)"; }
    else if (dragPhase==="settle") { st.transform="translateX("+settleRef.current.tx+"px)"; }
    else if (!p.open) { st.transform=isRTL?"translateX(100%)":"translateX(-100%)"; }
  }
  // The row moved to NavRow at module scope. What stays here is what is TRUE OF
  // THE NAVIGATION and not of a menu row in general: which page is current, what
  // clicking does, and that the leads count is the one badge the nav carries.
  var renderItem = function(item){
    return NavRow({
      item: item,
      active: p.active===item.id,
      isRTL: isRTL,
      badge: item.id==="leads" ? leadsCount : 0,
      onClick: function(){
        p.setActive(item.id);
        try{localStorage.setItem("crm_page",item.id);}catch(e){}
        if(p.isMobile) p.onClose();
      }
    });
  };
  return <>
    {p.isMobile&&(p.open||dragPhase!=="idle")&&<div ref={overlayRef} data-m-drawer="1" onClick={p.onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.48)", zIndex:140,
      opacity: dragPhase==="drag" ? openFracOf(dragRef.current.tx) : (dragPhase==="settle" ? (settleRef.current.open?1:0) : 1),
      pointerEvents: (p.open&&dragPhase!=="drag") ? "auto" : "none",
      transition: dragPhase==="drag" ? "none" : "opacity 0.28s ease" }}/>}
    <div ref={panelRef} style={st}>
      {/* Header */}
      <div style={{ paddingBottom:18, borderBottom:"1px solid rgba(255,255,255,0.06)", marginBottom:8 }}>
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", gap:10 }}>
          <div style={{ minWidth:0 }}>
            <div style={{ color:"rgba(255,255,255,0.7)", fontSize:22, fontWeight:800, letterSpacing:"-1.5px", lineHeight:1 }}>
              ARO<span style={{ color:"rgba(255,255,255,0.25)", fontSize:14, fontWeight:400, letterSpacing:0 }}> CRM</span>
            </div>
            <div style={{ width:32, height:2, background:"rgba(255,255,255,0.15)", borderRadius:2, marginTop:5 }}/>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
            {p.isMobile&&<button onClick={p.onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.6)", display:"flex", padding:0 }}><X size={18}/></button>}
          </div>
        </div>
      </div>

      {/* Nav items */}
      <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column" }}>
        {(function(){
          var out = [];
          var dividerInserted = false;
          items.forEach(function(item){
            if (item.adminSection && !dividerInserted) {
              out.push(<div key="divider-admin" style={{ height:1, background:"rgba(255,255,255,0.06)", margin:"6px 4px" }}/>);
              dividerInserted = true;
            }
            out.push(renderItem(item));
          });
          return out;
        })()}
      </div>

      {/* Footer */}
      <div style={{ paddingTop:14, marginTop:8, borderTop:"1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"0 4px", marginBottom:10 }}>
          <div style={{ width:32, height:32, borderRadius:10, background:"rgba(255,255,255,0.1)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:600, fontSize:13, flexShrink:0 }}>{userInitial}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:"rgba(255,255,255,0.8)", fontSize:11, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{userName}</div>
            {p.cu.role!=="office_boy" && <div style={{ color:"rgba(255,255,255,0.25)", fontSize:9, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{userRole}</div>}
          </div>
          <div style={{ width:7, height:7, borderRadius:"50%", background:"#22C55E", flexShrink:0 }}/>
        </div>
        <button onClick={p.onLogout}
          onMouseEnter={function(e){ e.currentTarget.style.background="rgba(239,68,68,0.08)"; }}
          onMouseLeave={function(e){ e.currentTarget.style.background="transparent"; }}
          style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"7px 12px", background:"transparent", border:"none", borderRadius:12, color:"#EF4444", fontSize:13, fontWeight:500, cursor:"pointer", textAlign:isRTL?"right":"left", fontFamily:"inherit" }}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{display:"block"}}><path d="M7 3H3a1 1 0 00-1 1v10a1 1 0 001 1h4M12 13l4-4-4-4M16 9H7" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span>{t.logout}</span>
        </button>
      </div>
    </div>
  </>;
};

// ===== CALLBACK BELL (isolated for performance) =====
var CB_COLORS = {overdue:{bg:"#FEF2F2",border:"#EF4444",icon:"#FEE2E2",text:"#DC2626"},now:{bg:"#FFF7ED",border:"#F97316",icon:"#FFEDD5",text:"#EA580C"},upcoming:{bg:"#F0FDF4",border:"#22C55E",icon:"#DCFCE7",text:"#16A34A"},nocontact:{bg:"#FFFBEB",border:"#EAB308",icon:"#FEF3C7",text:"#B45309"}};
// THE BELL SHAKE IS FINITE (2026-08-15). It used to be
// `animation:cbBellShake 0.6s ease-in-out infinite`, and `.cb-bell-shake` is
// applied whenever `totalCount > 0` — which on a live CRM is essentially
// always. A never-ending transform animation keeps the compositor painting for
// as long as the header is on screen, forever.
//
// MEASURED (headless-free Chrome, CPU throttled 4x, live WS traffic, 20s at a
// forced scrollY=0): 480 screencast frames with the bell active vs 1 with it
// inactive on Daily Requests, and 467 vs 1 on Leads. ~24fps of permanent
// repainting, on EVERY page, entirely from this one line.
//
// This is why it never showed up in the earlier hunt: `transform` animations
// produce ZERO layout-shift entries (measured: 0 shifts, CLS 0.000) and zero
// React re-renders, so neither a PerformanceObserver on layout-shift nor a
// remount counter can see it. It also explains "stops only when I scroll" —
// the header scrolls out of view and the compositor goes quiet (measured: 480
// frames at the top -> 24 at scrollY 1400 -> 1 deeper).
//
// 3 iterations keeps the attention-grab (1.8s of shaking) and then stops. It
// replays whenever the count CHANGES, via the key on the Bell below, so a NEW
// overdue callback still announces itself. prefers-reduced-motion opts out.
var CB_CSS = "@keyframes cbBellShake{0%,100%{transform:rotate(0)}15%{transform:rotate(12deg)}30%{transform:rotate(-10deg)}45%{transform:rotate(8deg)}60%{transform:rotate(-6deg)}75%{transform:rotate(3deg)}}.cb-bell-shake{animation:cbBellShake 0.6s ease-in-out 3}@media (prefers-reduced-motion:reduce){.cb-bell-shake{animation:none}}.cb-dropdown-enter{animation:cbSlideDown 0.2s ease-out}@keyframes cbSlideDown{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}.cb-card:hover{box-shadow:0 4px 16px rgba(0,0,0,0.08)!important;transform:translateY(-1px)}";
var cbStyleInjected = false;

var CallbackBell = function(p) {
  var cbStaleKey = useStaleRefetchKey(p.cbBust, STALE_REFETCH_MS);
  var cbAfterPaint = useBootPhase2();          // badges are phase 2
  var [tab, setTab] = useState("all");
  // (the old client-side `limit` + Show More slice lived here; paging is
  // server-side now — see bellPage / bellPages and fetchTabPage)
  // STEP 4-2 — own state populated from /api/leads/callbacks +
  // /api/daily-requests/callbacks + /api/leads/no-contact. Rows are already
  // role-scoped + archived-filtered + status-excluded server-side. Replaces
  // the previous p.leads.concat(p.dailyRequests) scan which would silently
  // miss callbacks once STEP 4-5 shrinks the bootstrap.
  // PARTITION-SCOPED (2026-08-14). The bell used to pull the ENTIRE 9-day
  // window of rows every 60s per agent — measured 384 KB per poll per agent
  // (1,068 leads + 585 DRs) to render 30 of them — and then split that pooled
  // set into overdue / now / upcoming in JS.
  //
  // Two problems, one fix. (1) Paging the pooled window would have emptied a
  // partition: sorted ascending, page 1 is all overdue and "Upcoming" renders
  // blank. (2) The poll was pulling rows nobody was looking at.
  //
  // Now: the POLL fetches counts only (a number per partition, no rows), and
  // ROWS load when the dropdown opens or the tab changes. Each partition is its
  // own complete, independently paged set — its boundaries are pure time ranges,
  // so they ride the from/to params the endpoints already had.
  var [cbCounts, setCbCounts] = useState({ overdue:0, now:0, upcoming:0, nocontact:0 });
  // Rows are cached PER TAB, in memory, for the life of the session
  // (stale-while-revalidate): a tab switch or a re-open paints that tab's last
  // rows at once and refetches page 1 behind them. An entry exists only once a
  // fetch for that tab has SETTLED — so "no entry" means never fetched
  // ("Loading…"), and an entry with zero rows is a real, fetched emptiness
  // ("No items"). Entry: { rows, page, pages, busy }.
  var [bellCache, setBellCache] = useState({});
  // Latest-request token PER TAB, so a slow response cannot overwrite a newer
  // one for the same tab (a WS-triggered refetch overlapping the open fetch).
  var bellSeq = useRef({});
  var ref = useRef(null);

  // Partition windows. NOW_DELAY_MS / NOW_UPCOMING_MS are the SAME thresholds
  // the old client-side split used, so no row changes bucket — verified on prod:
  // the three ranges sum to exactly the pooled window (1,653 == 1,653), i.e. no
  // row falls between them and none is counted twice.
  // Same thresholds the old client-side split used; cbWindows() and
  // rowPartition() must stay in step with each other.
  var NOW_DELAY_MS    = 30 * 60 * 1000;   // 30min overdue still counts as Now
  var NOW_UPCOMING_MS = 30 * 60 * 1000;   // up to 30min ahead still counts as Now
  var cbWindows = function(){
    var nowMs = Date.now();
    var naive = function(ms){
      var d = new Date(ms);
      return new Date(d.getTime() - d.getTimezoneOffset()*60000).toISOString().slice(0,-1);
    };
    var HALF_BACK = 30*60*1000, HALF_FWD = 30*60*1000;
    return {
      overdue:  { from: naive(nowMs - 7*24*3600*1000), to: naive(nowMs - HALF_BACK), order: "desc" },
      now:      { from: naive(nowMs - HALF_BACK),      to: naive(nowMs + HALF_FWD),  order: "asc"  },
      upcoming: { from: naive(nowMs + HALF_FWD),       to: naive(nowMs + 48*3600*1000), order: "asc" }
    };
  };

  useEffect(function(){
    if(!p.showNotif) return;
    var fn=function(e){if(ref.current&&!ref.current.contains(e.target))p.setShowNotif(false);};
    document.addEventListener("mousedown",fn);
    return function(){document.removeEventListener("mousedown",fn);};
  },[p.showNotif]);

  useEffect(function(){
    if(cbStyleInjected) return;
    var s=document.createElement("style"); s.textContent=CB_CSS; document.head.appendChild(s); cbStyleInjected=true;
  },[]);

  // ---- POLL: counts only. No rows cross the wire on a tick. ----
  useEffect(function(){
    if(!p.token || !cbAfterPaint) return;       // phase 2 — see useBootPhase2
    var cancelled = false;
    var loadCounts = function(){
      var W = cbWindows();
      var one = function(url){
        return apiFetch(url, "GET", null, p.token)
          .then(function(r){ return (r && typeof r.count === "number") ? r.count : 0; })
          .catch(function(){ return 0; });
      };
      // SIX COUNTS, ONE REQUEST. This used to issue six count_only calls per
      // tick — three windows x two collections. Measured in Railway's logs:
      // 34.9 calls/min to /api/leads/callbacks alone across 11 live sessions,
      // in bursts of three. The server does the same six queries together now
      // and hands back one object; the windows are still computed here and sent
      // verbatim, so the numbers are identical to what the six calls returned.
      var cq = "?overdueFrom=" + encodeURIComponent(W.overdue.from) + "&overdueTo=" + encodeURIComponent(W.overdue.to)
             + "&nowFrom="     + encodeURIComponent(W.now.from)     + "&nowTo="     + encodeURIComponent(W.now.to)
             + "&upcomingFrom="+ encodeURIComponent(W.upcoming.from)+ "&upcomingTo="+ encodeURIComponent(W.upcoming.to);
      Promise.all([
        apiFetch("/api/leads/callback-counts" + cq, "GET", null, p.token).catch(function(){ return null; }),
        one("/api/leads/no-contact?stale_hours=24&count_only=true")
      ]).then(function(res){
        if (cancelled) return;
        var cc = res[0], nc = res[1];
        // A failed combined call leaves the previous badge rather than zeroing
        // it — the old code's per-call .catch(0) would have shown "0 overdue"
        // on a blip, which reads as "nothing to do" and is the wrong lie.
        if (!cc || !cc.leads || !cc.dailyRequests) { setCbCounts(function(prev){ return Object.assign({}, prev, { nocontact: nc }); }); return; }
        var sum = function(k){ return (cc.leads[k] || 0) + (cc.dailyRequests[k] || 0); };
        setCbCounts({ overdue: sum("overdue"), now: sum("now"), upcoming: sum("upcoming"), nocontact: nc });
      });
    };
    loadCounts();
    var t = setInterval(loadCounts, 60000);
    return function(){ cancelled = true; clearInterval(t); };
  // cbStaleKey, NOT p.cbBust. cbBust fires per websocket burst (debounced
  // 1500ms), and this effect re-runs the whole poll on every change — so a busy
  // afternoon multiplied a 60s poll into a burst-rate one. Throttled to at most
  // one extra refresh per 30s; the 60s interval is unchanged.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, cbStaleKey, cbAfterPaint]);

  // ---- ROWS: only for the tab being looked at, and only while open. ----
  var CB_PAGE = 30;
  var fetchTabPage = function(which, pageNo, append){
    if(!p.token) return;
    var W = cbWindows();
    var reqs;
    if (which === "nocontact") {
      reqs = [ apiFetch("/api/leads/no-contact?stale_hours=24&page=" + pageNo + "&limit=" + CB_PAGE,
                        "GET", null, p.token).catch(function(){ return null; }) ];
    } else {
      var w2 = W[which];
      reqs = [
        apiFetch("/api/leads/callbacks?from=" + encodeURIComponent(w2.from) + "&to=" + encodeURIComponent(w2.to) +
                 "&order=" + w2.order + "&page=" + pageNo + "&limit=" + CB_PAGE, "GET", null, p.token).catch(function(){ return null; }),
        apiFetch("/api/daily-requests/callbacks?from=" + encodeURIComponent(w2.from) + "&to=" + encodeURIComponent(w2.to) +
                 "&order=" + w2.order + "&page=" + pageNo + "&limit=" + CB_PAGE, "GET", null, p.token).catch(function(){ return null; })
      ];
    }
    var setEntry = function(fn){
      setBellCache(function(c){ var n = Object.assign({}, c); n[which] = fn(c[which]); return n; });
    };
    // Mark busy on an EXISTING entry only; creating one here would read as
    // "fetched, empty" and flash the empty state before the first fetch settles.
    setBellCache(function(c){
      if (!c[which]) return c;
      var n = Object.assign({}, c); n[which] = Object.assign({}, c[which], { busy:true }); return n;
    });
    var mySeq = (bellSeq.current[which] = (bellSeq.current[which] || 0) + 1);
    Promise.all(reqs).then(function(res){
      if (mySeq !== bellSeq.current[which]) return;
      // Every request failed: keep whatever this tab already showed rather than
      // replacing real rows with an empty list. (.finally still settles it.)
      if (res.every(function(r){ return !r; })) return;
      var rows = [], maxPages = 1;
      res.forEach(function(r, idx){
        if(!r) return;
        var d = Array.isArray(r.data) ? r.data : (Array.isArray(r) ? r : []);
        // tag the source so the row click routes to the right page
        var kind = (which === "nocontact") ? "lead"
                 : (idx === 1 ? "dr" : "lead");
        // _seg: which partition fetched this row. The All view is DERIVED from
        // the per-tab caches, and a lead that is both overdue AND stale appears
        // in two segments (kept, owner ruling) — _seg is what labels each copy.
        d.forEach(function(x){ x._kind = kind; x._seg = which; rows.push(x); });
        if (r && typeof r.totalPages === "number" && r.totalPages > maxPages) maxPages = r.totalPages;
      });
      setEntry(function(prev){
        var merged = rows;
        if (append && prev) {
          var seen = {};
          (prev.rows||[]).forEach(function(x){ if(x && x._id) seen[String(x._id)] = 1; });
          merged = (prev.rows||[]).concat(rows.filter(function(x){ return x && x._id && !seen[String(x._id)]; }));
        }
        return { rows: merged, page: pageNo, pages: maxPages, busy: true };
      });
    }).finally(function(){
      if (mySeq !== bellSeq.current[which]) return;
      // Settles the tab on success AND failure: a failed first fetch ends in
      // "No items", never an endless "Loading…".
      setEntry(function(prev){ return Object.assign({ rows:[], page:1, pages:1 }, prev, { busy:false }); });
    });
  };

  // A different session must never see the previous one's rows.
  useEffect(function(){
    bellSeq.current = {};
    setBellCache({});
  },[p.token]);

  // The four FETCHED partitions, in the order the All tab lists them. "All" has
  // no cache entry and no request of its own: it is these four, concatenated.
  var CB_SEGS = ["overdue","now","upcoming","nocontact"];
  var segsOf = function(t){ return t === "all" ? CB_SEGS : [t]; };

  // OPEN (or a new session): fetch page 1 of every partition in parallel, so each
  // tab is in the cache after the one initial "Loading…". Each partition writes
  // its own cache entry as it lands — the slow No Contact aggregation therefore
  // never holds back the callbacks partitions.
  // A tab change or a WS bust while open refetches only the ACTIVE tab's
  // partitions (all four when that is All), behind their cached rows.
  var bellOpenKey = useRef("");
  useEffect(function(){
    if(!p.showNotif) { bellOpenKey.current = ""; return; }
    var key = String(p.token);
    if (bellOpenKey.current !== key) {
      bellOpenKey.current = key;
      CB_SEGS.forEach(function(k){ fetchTabPage(k, 1, false); });
      return;
    }
    segsOf(tab).forEach(function(k){ fetchTabPage(k, 1, false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.showNotif, tab, p.token, p.cbBust]);

  // The server already returned exactly the rows for the active tab, in that
  // partition's own order. No client-side re-partitioning, and NO re-sort: the
  // ordering is the endpoint's (`order=` per partition), so it holds across page
  // boundaries instead of only within whatever happened to be loaded.
  // A single tab reads its own entry. ALL is the four partitions concatenated in
  // listing order (Delay -> Now -> Upcoming -> No Contact): the list therefore
  // holds exactly what the All badge sums — callbacks (leads + DRs) plus No
  // Contact — and a lead in both a callbacks window and No Contact is listed in
  // both, as the badge counts it twice.
  var segEntries = segsOf(tab).map(function(k){ return bellCache[k]; });
  var landed = segEntries.filter(Boolean);
  // "Loaded" = every partition this tab needs has SETTLED. Rows from partitions
  // that have landed show immediately; "No items" waits for all of them.
  var bellLoaded = landed.length === segEntries.length;
  var allItems = [];
  landed.forEach(function(e){ allItems = allItems.concat(e.rows); });
  var bellBusy = landed.some(function(e){ return e.busy; });
  // Next page to fetch: the first partition (in listing order) that has more.
  // A partition that has not landed yet blocks the ones after it — paging past
  // an unlanded Delay would list Upcoming rows ahead of Delay rows.
  var bellNext = null;
  for (var si = 0; si < segEntries.length && !bellNext; si++) {
    if (!segEntries[si]) break;
    if (segEntries[si].page < segEntries[si].pages) bellNext = { seg: segsOf(tab)[si], page: segEntries[si].page };
  }

  // Per-tab membership, used only by getType() for the row's colour/label. Rows
  // carry the tab they were fetched for, so this is a lookup, not a re-derivation.
  var rowPartition = function(l){
    if (tab !== "all") return tab;
    if (l && l._seg) return l._seg;
    if (!l || !l.callbackTime) return "nocontact";
    var diff = new Date(l.callbackTime).getTime() - Date.now();
    if (isNaN(diff)) return "nocontact";
    if (diff < -NOW_DELAY_MS) return "overdue";
    if (diff <= NOW_UPCOMING_MS) return "now";
    return "upcoming";
  };

  var visible = allItems;
  var totalCount = cbCounts.overdue + cbCounts.now + cbCounts.upcoming + cbCounts.nocontact;

  // Counts come from the server's count_only queries — the SAME predicate and
  // window the row fetches use, so a tab's number and its rows cannot disagree.
  var tabs = [
    {key:"all", label:"All", count:totalCount},
    {key:"overdue", label:"Delay", count:cbCounts.overdue},
    {key:"now", label:"Now", count:cbCounts.now},
    {key:"upcoming", label:"Upcoming", count:cbCounts.upcoming},
    {key:"nocontact", label:"No Contact", count:cbCounts.nocontact}
  ];

  var getType = function(l){ return rowPartition(l); };

  return <div style={{ position:"relative" }} ref={ref}>
    <button onClick={function(){
      var opening=!p.showNotif;
      p.setShowNotif(opening);
      if(opening){if(p.setShowPendingApproval)p.setShowPendingApproval(false);if(p.setShowRotNotif)p.setShowRotNotif(false);setTab("all");}
    }} style={{ width:36, height:36, borderRadius:9, border:"1px solid #E8ECF1", background:totalCount>0?"#FEF2F2":"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", position:"relative", transition:"all 0.2s" }}>
      {/* key on the count so a CHANGE remounts the icon and replays the (now
          finite) shake — that is what makes a new overdue callback still grab
          attention without painting forever. See CB_CSS. */}
      <Bell key={"cb-bell-"+totalCount} size={16} color={totalCount>0?C.danger:C.textLight} className={totalCount>0?"cb-bell-shake":""}/>
      {totalCount>0&&<span style={{ position:"absolute", top:-2, right:-2, minWidth:17, height:17, borderRadius:9, background:C.danger, color:"#fff", fontSize:9, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px", border:"2px solid #fff" }}>{totalCount>99?"99+":totalCount}</span>}
    </button>
    {p.showNotif&&<div className="cb-dropdown-enter crm-notif-panel" style={{ position:"absolute", top:46, right:0, width:440,maxWidth:"95vw", background:"#fff", borderRadius:16, boxShadow:"0 8px 32px rgba(0,0,0,0.15)", zIndex:200, maxHeight:520, display:"flex", flexDirection:"column" }}>
      <div style={{ padding:"16px 20px 12px", borderBottom:"1px solid #F1F5F9", flexShrink:0 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:18 }}>🔔</span>
            <span style={{ fontWeight:700, fontSize:15, color:C.text }}>Callbacks</span>
            {totalCount>0&&<span style={{ background:"#FEF2F2", color:C.danger, padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:700 }}>{totalCount}</span>}
          </div>
          <div style={{ display:"flex", gap:4, alignItems:"center" }}>
            <button onClick={function(){p.setShowNotif(false);}} style={{ background:"none", border:"none", cursor:"pointer", color:C.textLight, display:"flex", padding:4 }}><X size={15}/></button>
          </div>
        </div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:3, rowGap:4 }}>
          {tabs.map(function(t2){var active=tab===t2.key;return <button key={t2.key} onClick={function(){setTab(t2.key);}} style={{ padding:"4px 6px", borderRadius:6, border:"none", background:active?"#111827":"#F8FAFC", color:active?"#fff":"#374151", fontSize:10, fontWeight:active?700:600, cursor:"pointer", whiteSpace:"nowrap", transition:"all 0.15s", display:"flex", alignItems:"center", gap:3, flexShrink:0 }}>
            {t2.label}
            {t2.count>0&&<span style={{ background:active?"rgba(255,255,255,0.25)":"#E5E7EB", color:active?"#fff":"#374151", padding:"0 4px", borderRadius:4, fontSize:8, fontWeight:700, lineHeight:"14px" }}>{t2.count}</span>}
          </button>;})}
        </div>
      </div>
      <div style={{ overflowY:"auto", flex:1, padding:"8px 12px" }}>
        {totalCount===0&&<div style={{ padding:"40px 20px", textAlign:"center" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🎉</div>
          <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:4 }}>All clear!</div>
          <div style={{ fontSize:12, color:C.textLight }}>No pending callbacks</div>
        </div>}
        {allItems.length===0&&totalCount>0&&!bellLoaded&&<div style={{ padding:"32px 20px", textAlign:"center", color:C.textLight, fontSize:13 }}>Loading…</div>}
        {allItems.length===0&&totalCount>0&&bellLoaded&&<div style={{ padding:"32px 20px", textAlign:"center", color:C.textLight, fontSize:13 }}>No items in this category</div>}
        {visible.map(function(l){
          var agName=userNameOf(l.agentId);
          var lType=getType(l);
          var cc=CB_COLORS[lType]||CB_COLORS.now;
          var cbTypeLabel=lType==="overdue"?"Overdue":lType==="now"?"Callback Now":lType==="upcoming"?"Upcoming":"No Contact";
          // Every bell row — overdue / now / upcoming / no-contact — reads
          // the same forward-looking direction off lead.callbackTime. The
          // No Contact tab used to fall back to timeAgo(lastActivityTime)
          // which inverted the direction ("15 days ago") on rows visible
          // alongside future-direction siblings in the All tab.
          var timeStr;
          if(l.callbackTime){ timeStr=callbackTimeStr(l.callbackTime); }
          else if(l.lastContactAt||l.lastActivityTime){
            // No Contact caption: age from the faithful lastContactAt (the
            // server's real-action clock — status change / feedback / call),
            // NOT the polluted lastActivityTime (rotation / edits / archive
            // reset it, causing the wrong "0 days"). Fall back to
            // lastActivityTime for older rows / payloads without lastContactAt.
            var ncBasis=l.lastContactAt||l.lastActivityTime;
            var ncDays=Math.floor((Date.now()-new Date(ncBasis).getTime())/86400000);
            timeStr=ncDays+(ncDays===1?" day":" days");
          } else { timeStr=""; }
          return <div key={gid(l)+"|"+(l._seg||l._kind)} className="cb-card" onClick={function(){p.setShowNotif(false);var isDR=l._kind==="dr";setTimeout(function(){if(isDR){p.onDRClick&&p.onDRClick();return;}/* BUG #6 — /api/leads/callbacks returns only LEAD_CALLBACK_FIELDS (no project/campaign/budget). Fetch full doc before opening the panel; fail-soft to the bell's lite copy. */apiFetch("/api/leads/"+gid(l),"GET",null,p.token).then(function(fresh){p.onLeadClick((fresh&&fresh._id)?fresh:l);}).catch(function(err){console.warn("[CALLBACK_BELL_FETCH_FAILED]",{leadId:gid(l),row:l,error:err&&err.message});p.onLeadClick(l);});},50);}} style={{ background:cc.bg, borderLeft:"4px solid "+cc.border, borderRadius:12, padding:"14px 16px", marginBottom:8, cursor:"pointer", boxShadow:"0 1px 4px rgba(0,0,0,0.04)", transition:"all 0.2s", display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:36, height:36, borderRadius:"50%", background:cc.icon, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:16 }}>{lType==="overdue"?"⏰":lType==="now"?"📞":lType==="upcoming"?"🔔":"😴"}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:700, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.name}</div>
              <div style={{ fontSize:13, fontWeight:500, color:C.textLight, marginTop:2 }}>{agName||"Unassigned"}</div>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:3 }}>
                <span style={{ fontSize:10, fontWeight:600, color:cc.text, background:cc.icon, padding:"1px 6px", borderRadius:4 }}>{cbTypeLabel}</span>
                <span style={{ fontSize:11, color:C.textLight }}>{timeStr}</span>
              </div>
            </div>
            {l.phone&&<a href={"tel:"+cleanPhone(l.phone)} onClick={function(e){e.stopPropagation();logContactTap(l,"call",p.token,p.csrfToken);}} style={{ width:40, height:40, borderRadius:10, background:cc.icon, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, textDecoration:"none", boxShadow:"0 2px 8px rgba(0,0,0,0.15)" }} title="Call"><Phone size={16} color={cc.text}/></a>}
          </div>;
        })}
        {/* Show More pages the SERVER now. It used to reveal more of an
            already-loaded array, which meant it could only ever show what the
            one pooled fetch had happened to include. */}
        {!bellLoaded && allItems.length>0 && <div style={{ padding:"10px", textAlign:"center", color:C.textLight, fontSize:12 }}>Loading…</div>}
        {bellNext && <button disabled={bellBusy} onClick={function(e){e.stopPropagation();fetchTabPage(bellNext.seg, bellNext.page+1, true);}} style={{ width:"100%", padding:"10px", border:"none", borderRadius:8, background:"#F1F5F9", color:"#374151", fontSize:12, fontWeight:600, cursor:bellBusy?"default":"pointer", marginTop:4, opacity:bellBusy?0.6:1 }}>{bellBusy ? "Loading…" : ("Show More (" + Math.max(0, (tabs.filter(function(t2){return t2.key===tab;})[0]||{}).count - allItems.length) + " remaining)")}</button>}
      </div>
    </div>}
  </div>;
};

// Status to render for a row that came from a SLIM lead endpoint
// (/api/leads/search, /api/leads/by-phone, /api/leads/callbacks,
// /api/leads/no-contact) rather than from the paginated list.
//
// Those payloads carry no assignments[], so the LeadsPage currentStatus()
// walker has nothing to replay. The backend instead resolves the value for us:
// for a SALES caller applySalesSliceOverlay() overlays `status` from that
// agent's own slice and precomputes `_currentStatus` (the same agentHistory
// replay the Leads page uses) — so the two surfaces can never disagree. For
// admin / operation / managerial roles both fields are the global status.
//
// Prefer _currentStatus, fall back to status. NEVER read `.status` directly on
// these rows: before 2026-08-02 that rendered another agent's status for a
// shared lead (agent A's NewLead showed as agent B's "Potential" in search).
var slimRowStatus = function(item) {
  if (!item) return "";
  if (typeof item._currentStatus === "string" && item._currentStatus) return item._currentStatus;
  return item.status || "";
};

// ===== GLOBAL HEADER SEARCH =====
var HeaderSearch = function(p) {
  var t = p.t;
  var wrapRef = useRef(null);
  var [open, setOpen] = useState(false);
  // STEP 4-1 — server-side search replaces the previous in-memory
  // p.leads.filter / p.dailyRequests.filter scans. Those scans worked only
  // because the bootstrap shipped the full collection; once STEP 4-5
  // shrinks the bootstrap to ~100 rows, in-memory search silently misses
  // every lead beyond the first page. Debounced (250ms) so rapid typing
  // doesn't flood the BE with one request per keystroke; the server
  // endpoints (/api/leads/search, /api/daily-requests/search) cap at
  // limit=10 to match the previous .slice(0,10) UX and apply the same
  // role-scoped visibility as the list endpoints.
  var [leadResults, setLeadResults] = useState([]);
  var [drResults, setDrResults] = useState([]);
  var [searching, setSearching] = useState(false);
  // True when the last attempt could not reach the server. Distinct from "no
  // results" — see the sequencing block below.
  var [searchErr, setSearchErr] = useState(false);
  useEffect(function(){
    var onDown = function(e){ if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return function(){ document.removeEventListener("mousedown", onDown); };
  },[]);
  var q = (p.search||"").trim();
  // Mirrors the backend's ID test in /api/leads/search. A numeric query comes
  // back tagged with the ONE pass the server ran — `_matchKind:"id"` for an
  // explicit query ("#50038"), `_matchKind:"phone"` for a bare one ("50038") —
  // never both, because the two branches are mutually exclusive server-side. The
  // dropdown heads whichever group arrived; the other is simply absent.
  //
  // Daily Requests join only the BARE case, and only above the floor: their
  // schema carries no leadId and no recordNo (mirrorLeadId / movedToLeadId are
  // ObjectId refs, not typeable numbers), so an explicit id query asks them a
  // question they cannot have an answer to. Their section stays separately
  // headed because it is a different collection, not a different pass.
  var isIdQuery = isIdShaped(q);
  // ── RESPONSE SEQUENCING ────────────────────────────────────────────────────
  // The debounce cancels the TIMER, never the REQUEST. Once a fetch is in flight
  // nothing stopped it, and the old `.then` called setLeadResults
  // unconditionally — so the LAST RESPONSE TO ARRIVE won, whatever query it
  // answered. Every intermediate query on the way to "#50038" ("#500", "#5003")
  // returns [], so a stale one landing late blanked a correct result into "No
  // matching results". Measured on production: a hit takes ~1.57s and a miss
  // ~0.98s, so the two are routinely in flight together and their arrival order
  // is network luck. That is the whole "works sometimes, no pattern" report.
  //
  // Every run of this effect claims the next generation. A response may only
  // touch state if its generation is still current; otherwise the user has typed
  // again and the answer is about a question they are no longer asking, so it is
  // dropped. Claimed at EFFECT time rather than fetch time so that editing the
  // box invalidates an outstanding request immediately, not 250ms later.
  var searchGen = useRef(0);
  useEffect(function(){
    var gen = ++searchGen.current;
    if (q.length < 2) { setLeadResults([]); setDrResults([]); setSearching(false); setSearchErr(false); return; }
    setSearching(true);
    var timer = setTimeout(function(){
      var qParam = encodeURIComponent(q);
      var idQuery = isIdShaped(q);
      // The DR request is skipped whenever there is nothing for it to answer:
      // an EXPLICIT id query ("#50038" — the user asked for an id, and a DR has
      // none), or a bare one below the floor. Otherwise it runs and comes back
      // phone-matched (the backend narrows its $or to
      // phone/phone2 for exactly these queries — the mixed regex would otherwise
      // match the digits inside a DR's NOTES and file it under a heading that
      // claims a phone was found). The backend re-checks the floor itself; this
      // is here to save the round trip, not to be the rule.
      var skipDr = idQuery && !phoneSubstringDigits(q);
      // A FAILURE IS NOT AN EMPTY RESULT. `.catch(() => [])` rendered a dropped
      // connection, a 30s abort and a genuine miss as the same "No matching
      // results". The sentinel keeps them apart.
      var failed = function(){ return { __searchFailed: true }; };
      Promise.all([
        apiFetch("/api/leads/search?q=" + qParam + "&limit=10", "GET", null, p.token).catch(failed),
        skipDr ? Promise.resolve([])
               : apiFetch("/api/daily-requests/search?q=" + qParam + "&limit=10", "GET", null, p.token).catch(failed)
      ]).then(function(results){
        if (gen !== searchGen.current) return;          // superseded — drop it
        var leadRes = results[0], drRes = results[1];
        if ((leadRes && leadRes.__searchFailed) || (drRes && drRes.__searchFailed)) {
          // Hold whatever was on screen and say so. Blanking the list would
          // assert "nothing matches", which is a claim this response cannot make.
          setSearchErr(true);
          setSearching(false);
          return;
        }
        setSearchErr(false);
        setLeadResults(Array.isArray(leadRes) ? leadRes : []);
        setDrResults(Array.isArray(drRes) ? drRes : []);
        setSearching(false);
      });
    }, 250);
    return function(){ clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  var totalResults = leadResults.length + drResults.length;
  // THE TWO SECTIONS. For a numeric query the backend fills exactly ONE of these
  // — explicit fills idRows, bare fills phoneRows — so only one heading ever
  // renders and the other group is empty. A name/phone/email query tags nothing,
  // so every one of its rows falls in the first group and renders under "Leads"
  // exactly as before; this split is inert for it.
  var idRows    = leadResults.filter(function(l){ return l._matchKind !== "phone"; });
  var phoneRows = leadResults.filter(function(l){ return l._matchKind === "phone"; });
  var showDropdown = open && q.length>=2;
  var sc = STATUSES(t);
  var drSc = typeof DR_STATUSES==="function" ? DR_STATUSES(t) : sc;
  var sectionLabel = function(label,count){ return <div style={{ padding:"8px 14px 6px", fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:1, background:"#FAFBFC" }}>{label} ({count})</div>; };
  // Build the row shape the EOI / Deals deep-link expects from one _txnMatches
  // entry. `_rowKey` + `_fromRecord` are the load-bearing pair: they are what
  // make the destination page open THAT transaction instead of whichever of the
  // client's rows it happens to find first.
  var txnTargetRow = function(lead, m){
    var row = {
      _id: gid(lead), _rowKey: m.recordId, _recordNo: m.recordNo,
      _fromRecord: true, _txnKind: m.kind, _txnStatus: m.status,
      // Sibling context travels WITH the row so the side panel's header renders
      // the same label the list row did — the rule lives in txnRowLabel only.
      _txnCount: m.txnCount, _txnIndex: m.txnIndex,
      name: lead.name, phone: lead.phone, leadId: lead.leadId, project: m.project
    };
    if (m.kind === "eoi") {
      row.status      = "EOI";
      row.eoiStatus   = m.status === "Cancelled" ? "EOI Cancelled" : m.status;
      row.eoiApproved = m.status === "Approved";
      row.eoiDate     = m.date || "";
    } else {
      row.status       = "DoneDeal";
      row.dealStatus   = m.status === "Cancelled" ? "Deal Cancelled" : "";
      row.dealApproved = m.status === "Approved";
      row.dealDate     = m.date || "";
    }
    return row;
  };
  // One transaction of an id-matched client. Every row of a client now renders
  // the SAME "ID #01024" label, so what distinguishes them here is the kind,
  // project, status and date — which is exactly what _txnMatches carries.
  var renderTxnRow = function(lead, m){
    var kindLabel = m.kind === "deal" ? "Deal" : "EOI";
    var dt = m.date ? String(m.date).slice(0, 10) : "";
    var stColor = m.status === "Cancelled" ? "#B91C1C" : m.status === "Approved" ? "#15803D" : "#B45309";
    var stBg    = m.status === "Cancelled" ? "#FEE2E2" : m.status === "Approved" ? "#DCFCE7" : "#FEF3C7";
    return <div key={"txn-" + m.recordId} onClick={function(){
      setOpen(false);
      p.onTxnClick && p.onTxnClick(m.kind === "deal" ? "deals" : "eoi", txnTargetRow(lead, m));
    }} style={{ padding:"9px 14px 9px 26px", borderBottom:"1px solid #F1F5F9", cursor:"pointer", display:"flex", alignItems:"center", gap:10, background:"#FCFDFE" }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12.5, fontWeight:600, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          <span style={{ color:C.textLight, fontWeight:700, fontSize:10, letterSpacing:.5, marginRight:6 }}>{kindLabel.toUpperCase()}</span>
          {m.project || "—"}
        </div>
        <div style={{ fontSize:11, color:C.textLight, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {/* the id this row actually WEARS on the list pages, so the search
              result and the row it lands on read identically */}
          {txnRowLabel(txnTargetRow(lead, m))}{" · "}{lead.name || "—"}{dt ? " · " + dt : ""}
        </div>
      </div>
      <span style={{ background:stBg, color:stColor, fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:6, flexShrink:0 }}>{m.status}</span>
    </div>;
  };
  // In a Phone match row, show the number that actually CARRIES the typed
  // digits. Half of these hits are on phone2, and rendering the primary number —
  // which visibly does not contain what was typed — reads as a wrong result.
  var matchedPhone = function(item){
    var d = phoneSubstringDigits(q);
    if (d && item.phone2 && String(item.phone2).indexOf(d) >= 0
          && !(item.phone && String(item.phone).indexOf(d) >= 0)) return item.phone2;
    return item.phone || "";
  };
  var renderRow = function(item, onClick, statuses, isDR, phoneOverride){
    // DRs carry a single agentId and no assignments[] — their top-level status
    // IS the per-agent status. Leads go through the slim-row resolver.
    var rowSt = isDR ? item.status : slimRowStatus(item);
    var so = statuses.find(function(s){return s.value===rowSt;})||statuses[0];
    // phoneOverride is the number the row MATCHED on; it has to survive both
    // branches below or a phone2 hit reverts to the primary number.
    var shownPhone = phoneOverride || item.phone || "";
    var sub = shownPhone;
    if (isDR) {
      // project FIRST — a DR has carried its own development since 2026-08-04
      // and it is required on create, so it is the most specific thing this row
      // can say. propertyType (unit type) / area / notes stay as the fallbacks
      // for rows created before the field existed.
      var drDesc = item.project || item.propertyType || item.area || item.notes || "";
      if (drDesc) sub = shownPhone + (shownPhone?" \u00b7 ":"") + (drDesc.length>32?drDesc.slice(0,32)+"\u2026":drDesc);
    } else if (item.project) {
      sub = shownPhone + " \u00b7 " + item.project;
    }
    return <div key={(isDR?"dr-":"lead-")+gid(item)} onClick={function(){
      setOpen(false);
      if (isDR) { onClick && onClick(item); return; }
      apiFetch("/api/leads/"+gid(item),"GET",null,p.token)
        .then(function(fresh){ onClick && onClick((fresh && fresh._id) ? fresh : item); })
        .catch(function(){ onClick && onClick(item); });
    }} style={{ padding:"10px 14px", borderBottom:"1px solid #F1F5F9", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:600, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.name||"\u2014"}</div>
        <div style={{ fontSize:11, color:C.textLight, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{sub}</div>
      </div>
      {so && <span style={{ background:so.bg, color:so.color, fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:6, flexShrink:0 }}>{so.label}</span>}
    </div>;
  };
  // ONE lead-result renderer, used by BOTH sections. A phone hit is a different
  // ANSWER, not a different kind of row: it still has to land on the EOI/Deals
  // page when the lead is frozen, exactly as an id or a name hit does. Sharing
  // the body is what guarantees the two sections can never drift apart in
  // behaviour while staying distinct in labelling.
  //
  // `isPhoneHit` changes exactly one thing — which of the lead's two numbers the
  // sub-line shows. It is never used to route.
  var renderLeadResult = function(l, isPhoneHit){
    var ph = isPhoneHit ? matchedPhone(l) : null;
    // Only an id query's ID-match rows carry _txnMatches; a name/phone/email
    // search and every Phone-match row are rendered exactly as they always were.
    var ms = Array.isArray(l._txnMatches) ? l._txnMatches : [];
    // THE COUNT COMES FROM THE BACKEND, AND IS NEVER RENDERED (ID RULE 3).
    // `ms.length` is a RECORD count: for one purchase wearing an EOI record and
    // a Deal record it reads 2. The server folds records into actions
    // (txnGroupsForLeads) and sends the result as _txnOffered. It survives here
    // for exactly one job — deciding whether "#N" resolved a single action and
    // should open it directly — and for nothing on screen. Fall back to the
    // array length ONLY for a payload from an older server, so a version skew
    // degrades to the previous behaviour instead of never opening anything.
    var txnOffered = (typeof l._txnOffered === "number") ? l._txnOffered : ms.length;
    // A number that named a TRANSACTION resolves to that transaction, not to a
    // menu (R4). The server says so; the client does not re-decide it.
    var isDirect = l._txnDirect === true;
    // NO RECORD — but that does NOT mean "no transaction". The 54 frozen
    // leads the forward-only ruling never migrated hold their transaction
    // in their OWN fields and render on the EOI / Deals pages as the row
    // builder's branch B. Routing them to the Lead card was wrong: the id
    // was read off the EOI page, so it has to land back there.
    //
    // Status is the only thing that can decide it, and for a frozen lead
    // it is reliable — EOI and DoneDeal are lead-level FINAL states that
    // the sales slice overlay deliberately does not touch, so the value is
    // the same for every role. Anything else really is just a lead, and
    // keeps the Lead card exactly as before.
    if (ms.length === 0) {
      var frozenPg = ({ EOI: "eoi", DoneDeal: "deals" })[slimRowStatus(l)] || null;
      if (frozenPg) {
        return renderRow(l, function(fresh){
          p.onTxnClick && p.onTxnClick(frozenPg, fresh);
        }, sc, false, ph);
      }
      return renderRow(l, p.onLeadClick, sc, false, ph);
    }
    // THE QUERY NAMED A TRANSACTION -> go straight to it. The client row is
    // deliberately not offered: it would be a second, worse answer to a question
    // that has exactly one. Keyed on the SERVER's flag, not on `ms.length === 1`
    // — a client with a single transaction is still a CLIENT hit, and answering
    // "#N" and "0100…" identically is what made a phone search jump into a deal.
    if (isDirect && txnOffered === 1) {
      return <div key={"leadgrp-"+gid(l)}>{renderTxnRow(l, ms[0])}</div>;
    }
    // Otherwise the answer is the CLIENT plus their actions, each wearing its own
    // code and its own status badge (ID RULE 3). Shown for a name, a phone and an
    // email alike — a phone hit used to render the bare client row and stop, so
    // the most common way to find someone was the one way that never showed you
    // their actions.
    //
    // NO HEADER LINE. There was a "{n} transactions — pick one" caption here and
    // it is gone by owner ruling: the actions sit directly beneath their client,
    // indented, each already carrying its kind, code, project, date and status.
    // The caption added a count nobody asked for and a word ("transactions") the
    // product does not use — and getting that count wrong is the whole reason
    // this file was opened. `txnOffered` is still read from the server for the
    // direct-hit decision above; it is never rendered.
    return <div key={"leadgrp-"+gid(l)}>
      {renderRow(l, p.onLeadClick, sc, false, ph)}
      {ms.map(function(m){ return renderTxnRow(l, m); })}
    </div>;
  };
  return <div ref={wrapRef} style={{ position:"relative", width:(p.width||260) }}>
    <div style={{ display:"flex", alignItems:"center", gap:7, background:"#F5F7FA", border:"1px solid #EEF2F6", borderRadius:999, padding:"8px 15px", width:"100%", boxSizing:"border-box" }}>
      <Search size={14} color={C.textLight}/>
      <input autoFocus={!!p.autoFocus} placeholder={t.search} value={p.search||""} onFocus={function(){setOpen(true);}} onChange={function(e){p.setSearch(e.target.value);setOpen(true);}} style={{ border:"none", background:"transparent", outline:"none", fontSize:13, color:C.text, width:"100%" }}/>
      {q.length>0&&<button onClick={function(){p.setSearch("");setOpen(false);}} style={{ background:"none", border:"none", cursor:"pointer", color:C.textLight, padding:0, display:"flex" }} title="Clear"><X size={14}/></button>}
    </div>
    {showDropdown && <div data-overlay-above="true" style={{ position:"absolute", top:"calc(100% + 6px)", left:0, right:0, background:"#fff", border:"1px solid #E2E8F0", borderRadius:12, boxShadow:"0 8px 32px rgba(0,0,0,0.12)", zIndex:500, maxHeight:420, overflowY:"auto" }}>
      {searchErr && <div style={{ padding:"9px 14px", fontSize:11.5, color:"#B45309", background:"#FEF3C7", borderBottom:"1px solid #FDE68A", textAlign:"center" }}>Couldn't reach the server — showing the last result. Edit the search to try again.</div>}
      {searching && totalResults===0 && !searchErr && <div style={{ padding:"14px 16px", fontSize:12, color:"#94A3B8", textAlign:"center" }}>Searching…</div>}
      {!searching && totalResults===0 && !searchErr && <div style={{ padding:"14px 16px", fontSize:12, color:"#94A3B8", textAlign:"center" }}>No matching results</div>}
      {idRows.length>0 && <>
        {sectionLabel(isIdQuery ? "ID match" : "Leads", idRows.length)}
        {idRows.map(function(l){ return renderLeadResult(l, false); })}
      </>}
      {phoneRows.length>0 && <>
        {/* A SEPARATE HEADING, ALWAYS. These leads matched on digits inside a
            phone number, not on an id — and for a numeric query the ID match
            section above is empty by construction, because a bare query never
            ran an id pass. The heading is what tells the reader WHICH question
            the rows answer, which is the whole point of the split. */}
        {sectionLabel("Phone match", phoneRows.length)}
        {phoneRows.map(function(l){ return renderLeadResult(l, true); })}
      </>}
      {drResults.length>0 && <>
        {/* A THIRD HEADING for a numeric query, never folded into either lead
            section. These rows matched a phone substring on a Daily Request —
            a different collection AND a different question from the id, so the
            label says which. Non-numeric queries keep the old heading and the
            old mixed-regex behaviour exactly. */}
        {sectionLabel(isIdQuery ? "Daily Request match" : "Daily Requests", drResults.length)}
        {drResults.map(function(r){ return renderRow(r, p.onDRClick, drSc, true, isIdQuery ? matchedPhone(r) : null); })}
      </>}
    </div>}
  </div>;
};

// Live EOI/DoneDeal items, shared by the Deal bell badge (unseen count) and
// the Deal bell panel (rendered list). Keeping both on one source prevents the
// badge from drifting out of sync with the panel contents.
var buildDealItems = function(leads, dailyRequests, cu, myTeamUsers) {
  var agentNameOf = function(entry){ return entry ? userNameOf(entry.agentId, myTeamUsers) : ""; };
  var seenLeadKeys = {};
  var items = [];
  (leads||[]).filter(function(l){return !l.archived;}).forEach(function(l){
    var isEOI = l.status==="EOI" || l.globalStatus==="eoi" || (l.eoiStatus && l.eoiStatus!=="EOI Cancelled" && l.eoiStatus!=="");
    var isDeal = l.status==="DoneDeal" || l.globalStatus==="donedeal";
    if (!isEOI && !isDeal) return;
    if (l.eoiStatus==="EOI Cancelled" && !isDeal) return;
    var kind = isDeal ? "DoneDeal" : (l.eoiStatus || "Pending");
    // Use the closure date (dealDate / eoiDate) so editing a closed deal
    // doesn't bump it back to the top of the notification panel via updatedAt.
    // createdAt fallback covers legacy rows from before dealDate/eoiDate were
    // captured — they sort lower than recent closures, never resurface.
    var leadTime = isDeal ? (l.dealDate || l.createdAt) : (l.eoiDate || l.createdAt);
    items.push({
      _id: gid(l), leadId: gid(l), leadName: l.name,
      agentName: agentNameOf(l), budget: l.budget || "",
      kind: kind, time: leadTime,
      phone: l.phone || ""
    });
    if (l.phone) seenLeadKeys[l.phone] = true;
  });
  (dailyRequests||[]).forEach(function(r){
    if (r.archived) return;
    if (r.phone && seenLeadKeys[r.phone]) return;
    var isEOI = r.status==="EOI" || (r.eoiStatus && r.eoiStatus!=="EOI Cancelled" && r.eoiStatus!=="");
    var isDeal = r.status==="DoneDeal";
    if (!isEOI && !isDeal) return;
    if (r.eoiStatus==="EOI Cancelled" && !isDeal) return;
    var kind = isDeal ? "DoneDeal" : (r.eoiStatus || "Pending");
    var drTime = isDeal ? (r.dealDate || r.createdAt) : (r.eoiDate || r.createdAt);
    items.push({
      _id: gid(r), leadId: gid(r), leadName: r.name,
      agentName: agentNameOf(r), budget: r.budget || "",
      kind: kind, isDR: true, time: drTime,
      phone: r.phone || ""
    });
  });
  if (cu && positionOf(cu)==="team_leader") {
    var teamNames=new Set((myTeamUsers||[]).map(function(u){return u.name;}));
    teamNames.add(cu.name);
    items = items.filter(function(it){ return !it.agentName || teamNames.has(it.agentName); });
  }
  items.sort(function(a,b){ return new Date(b.time||0) - new Date(a.time||0); });
  return items;
};

// ===== THERE IS NO PENDING DEVELOPER, AND THEREFORE NO BANNER ==============
// OWNER RULING 2026-09-11, permanent, system-wide.
//
// `PendingDevResolver` lived here: a link / "choose another" / reject block the
// EOI and Deal side panels rendered whenever a row carried a typed developer
// name with no `developerId`. It is DELETED, along with both of its mounts. The
// state it existed to resolve cannot occur any more — every write path runs the
// typed name through `resolveDeveloperPair` (server.js) and persists a
// `developerId`, folding it onto an existing Developer or minting one — so a
// banner here could only ever describe a row that predates the ruling.
//
// DO NOT RE-ADD IT, and do not re-add a softer version of it. Its last act was
// EOI #50058: the panel opened showing "Salam Properties" and then flipped to
// "Salam already exists — Link to Salam", because the row and the lead each
// carried one half of the pair and the banner's condition read one field from
// each. The defect was never the banner's wording or its match rule — it was
// that a reader had two sources to choose between. A renderer cannot fix that;
// only the writer can, and now does.
//
// The developer block in both panels is two branches now: the value (read-only
// roles, and a converted EOI) or the picker. verify-developer-resolution.js
// fails the build if a third one appears.

// ===== PENDING LOCATION RESOLVER (2026-08-07) =====
// Twin of PendingDevResolver above, for a Daily Request whose agent typed a
// location that isn't in the managed collection yet. Rendered in the DR side
// panel for admin/SA only. Three actions hit POST
// /api/daily-requests/:id/resolve-location { action: link | add | reject }.
//
// One deliberate difference from the developer copy: "Reject" here does NOT
// blank the field. area keeps the agent's typed text (it reverts to a legacy
// free-text value the filter still matches) — a Daily Request must never end up
// with no location at all, whereas a deal can sit with no developer.
var PendingLocationResolver = function(p){
  var [mode, setMode] = useState("");       // "" | "link" | "reject"
  var [linkId, setLinkId] = useState("");
  var [busy, setBusy] = useState(false);
  var [err, setErr] = useState("");
  var pendingName = (p.request && p.request.locationPending) || "";
  var locOpts = (p.locations||[]).map(function(l){ return { value:String(l._id), label:l.name }; });

  var run = function(body){
    setBusy(true); setErr("");
    apiFetch("/api/daily-requests/"+gid(p.request)+"/resolve-location","POST",body,p.token)
      .then(function(r){
        setBusy(false); setMode(""); setLinkId("");
        if(p.onResolved) p.onResolved((r&&r.request)||null, (r&&r.location)||null);
      })
      .catch(function(ex){ setBusy(false); setErr((ex&&ex.message)||"Action failed"); });
  };

  return <div style={{ marginTop:2, padding:12, background:"#FEF3C7", border:"1px solid "+C.warning, borderRadius:8 }}>
    <div style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:10 }}>
      <AlertCircle size={16} color="#B45309" style={{ flexShrink:0, marginTop:1 }}/>
      <div style={{ fontSize:12.5, color:"#92400E", lineHeight:1.4 }}>
        <span style={{ fontWeight:700 }}>Pending location:</span> "{pendingName}" <span style={{ color:"#B45309" }}>(typed by sales)</span>
        <div style={{ fontSize:11, color:"#B45309", marginTop:2 }}>Link it to a managed location, or add it as a new one.</div>
      </div>
    </div>
    {err && <div style={{ fontSize:12, color:"#B91C1C", background:"#FEE2E2", padding:"6px 10px", borderRadius:8, marginBottom:8 }}>{err}</div>}

    {mode==="" && <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
      <button type="button" disabled={busy} onClick={function(){ setErr(""); setLinkId(""); setMode("link"); }} style={{ flex:"1 1 auto", padding:"7px 10px", borderRadius:8, border:"none", background:C.info, color:"#fff", fontSize:12, fontWeight:600, cursor:busy?"wait":"pointer", opacity:busy?0.6:1 }}>🔗 Link to existing</button>
      <button type="button" disabled={busy} onClick={function(){ run({ action:"add", newName: pendingName }); }} style={{ flex:"1 1 auto", padding:"7px 10px", borderRadius:8, border:"none", background:C.success, color:"#fff", fontSize:12, fontWeight:600, cursor:busy?"wait":"pointer", opacity:busy?0.6:1 }}>{busy?"Working…":"➕ Add as new"}</button>
      <button type="button" disabled={busy} onClick={function(){ setErr(""); setMode("reject"); }} style={{ flex:"1 1 auto", padding:"7px 10px", borderRadius:8, border:"1px solid "+C.danger, background:"#fff", color:C.danger, fontSize:12, fontWeight:600, cursor:busy?"wait":"pointer", opacity:busy?0.6:1 }}>✕ Dismiss</button>
    </div>}

    {mode==="link" && <div>
      <div style={{ fontSize:11, fontWeight:700, color:"#92400E", marginBottom:6 }}>Pick the matching location</div>
      {/* Same managed list, same search as the developer picker: matchFn folds
          case, spacing and punctuation exactly the way normalizeLocationEntityName
          does, so "raselhekma" reaches "Ras El Hekma" — the row this resolver
          exists to find. The count says at a glance whether anything matched. */}
      <SearchableSelect value={linkId} onChange={setLinkId} options={locOpts} matchFn={locSearchMatch} showCount countNoun="locations"
        emptyLabel="— Select location —" placeholder="Search locations…" width="100%" maxWidth={9999} fontSize={12}/>
      <div style={{ display:"flex", gap:8, marginTop:8 }}>
        <button type="button" disabled={busy||!linkId} onClick={function(){ run({ action:"link", locationId: linkId }); }} style={{ flex:1, padding:"7px 12px", borderRadius:8, border:"none", background:C.info, color:"#fff", fontSize:12, fontWeight:600, cursor:(busy||!linkId)?"not-allowed":"pointer", opacity:(busy||!linkId)?0.6:1 }}>{busy?"Linking…":"Confirm link"}</button>
        <button type="button" disabled={busy} onClick={function(){ setMode(""); setLinkId(""); setErr(""); }} style={{ padding:"7px 12px", borderRadius:8, border:"1px solid "+C.border, background:"#fff", fontSize:12, cursor:"pointer", color:C.textLight }}>Cancel</button>
      </div>
    </div>}

    {mode==="reject" && <div>
      <div style={{ fontSize:12, color:"#92400E", marginBottom:8, lineHeight:1.4 }}>Dismiss this suggestion? "{pendingName}" stays on the request as free text — it just stops asking to be approved.</div>
      <div style={{ display:"flex", gap:8 }}>
        <button type="button" disabled={busy} onClick={function(){ run({ action:"reject" }); }} style={{ flex:1, padding:"7px 12px", borderRadius:8, border:"none", background:C.danger, color:"#fff", fontSize:12, fontWeight:600, cursor:busy?"wait":"pointer", opacity:busy?0.6:1 }}>{busy?"Dismissing…":"Yes, dismiss"}</button>
        <button type="button" disabled={busy} onClick={function(){ setMode(""); setErr(""); }} style={{ padding:"7px 12px", borderRadius:8, border:"1px solid "+C.border, background:"#fff", fontSize:12, cursor:"pointer", color:C.textLight }}>Cancel</button>
      </div>
    </div>}
  </div>;
};

// ===== PENDING LOCATION BELL (2026-08-07) =====
// Admin/SA discoverability bell, structurally identical to the PendingDevBell
// it was modelled on (retired 2026-09-11 — see the note where it stood, below):
// self-fetches on mount, every 60s, on cbBust, on locBust (a resolution
// just made in the DR side panel), and on open; renders NO chrome at all while
// the queue is empty. Each row routes to the request's Daily Requests side
// panel via p.onRowClick(request).
var PendingLocationBell = function(p){
  var afterPaint = useBootPhase2();        // badge counts are phase 2
  var [rows, setRows] = useState([]);
  var ref = useRef(null);

  useEffect(function(){
    if(!p.show) return;
    var fn=function(e){ if(ref.current&&!ref.current.contains(e.target)) p.setShow(false); };
    document.addEventListener("mousedown",fn);
    return function(){ document.removeEventListener("mousedown",fn); };
  },[p.show]);

  useEffect(function(){
    if(!p.token || !afterPaint) return;   // phase 2 — badge, see useBootPhase2
    var cancelled=false;
    var load=function(){
      apiFetch("/api/daily-requests/with-pending-location","GET",null,p.token)
        .then(function(r){ if(!cancelled) setRows((r&&Array.isArray(r.data))?r.data:[]); })
        .catch(function(){});
    };
    load();
    var t=setInterval(load,60000);
    return function(){ cancelled=true; clearInterval(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, p.cbBust, p.locBust, afterPaint]);

  // Open-refetch — fresh count the moment the admin opens the bell.
  useEffect(function(){
    if(!p.show||!p.token) return;
    var cancelled=false;
    apiFetch("/api/daily-requests/with-pending-location","GET",null,p.token)
      .then(function(r){ if(!cancelled) setRows((r&&Array.isArray(r.data))?r.data:[]); })
      .catch(function(){});
    return function(){ cancelled=true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.show]);

  var count = rows.length;

  // Empty queue -> no chrome, as the retired PendingDevBell did. The component stays
  // MOUNTED behind the null render so its polls keep running and the icon
  // appears on its own the moment an agent types a new location name. Close any
  // open popover first so the parent's show-flag can't strand it open.
  useEffect(function(){
    if(count===0 && p.show && p.setShow) p.setShow(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[count, p.show]);
  if(count===0) return null;

  return <div style={{ position:"relative" }} ref={ref}>
    <button onClick={function(){
      var opening=!p.show;
      p.setShow(opening);
      if(opening && p.closeOthers) p.closeOthers();
    }} style={{ width:36, height:36, borderRadius:9, border:"1px solid #E8ECF1", background:count>0?"#FEF3C7":"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", position:"relative", transition:"all 0.2s" }}>
      <MapPin size={15} color={count>0?"#B45309":C.textLight}/>
      {count>0&&<span style={{ position:"absolute", top:-2, right:-2, minWidth:17, height:17, borderRadius:9, background:C.warning, color:"#fff", fontSize:9, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px", border:"2px solid #fff" }}>{count>9?"9+":count}</span>}
    </button>
    {p.show&&<div className="crm-notif-panel" style={{ position:"absolute", top:46, right:0, width:360, maxWidth:"95vw", background:"#fff", borderRadius:16, boxShadow:"0 16px 48px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.04)", zIndex:200, maxHeight:460, display:"flex", flexDirection:"column" }}>
      <div style={{ padding:"14px 18px", borderBottom:"1px solid #F1F5F9", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <MapPin size={16} color="#B45309"/>
          <span style={{ fontWeight:700, fontSize:14, color:C.text }}>Pending locations</span>
          {count>0&&<span style={{ background:"#FEF3C7", color:"#B45309", padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600 }}>{count}</span>}
        </div>
        <button onClick={function(){ p.setShow(false); }} style={{ background:"none", border:"none", cursor:"pointer", color:C.textLight, display:"flex", padding:4 }}><X size={15}/></button>
      </div>
      <div style={{ overflowY:"auto", flex:1 }}>
        {/* No empty state — the bell unmounts its chrome at count===0. */}
        {rows.map(function(n){
          var agName = userNameOf(n.agentId) || "—";
          return <div key={String(n._id)} onClick={function(){
            p.setShow(false);
            if(p.onRowClick) p.onRowClick(n);
          }} style={{ padding:"11px 18px", borderBottom:"1px solid #F8FAFC", cursor:"pointer", background:"#fff", transition:"background 0.15s" }}
            onMouseEnter={function(e){ e.currentTarget.style.background="#FFFBEB"; }}
            onMouseLeave={function(e){ e.currentTarget.style.background="#fff"; }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:13, fontWeight:600, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{n.name||"(no name)"}</span>
              <span style={{ flexShrink:0, fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:8, background:"#FFF7ED", color:"#EA580C" }}>Request</span>
            </div>
            <div style={{ fontSize:12, color:"#B45309", marginTop:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>📍 "{n.locationPending}"</div>
            <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>👤 {agName}</div>
          </div>;
        })}
      </div>
    </div>}
  </div>;
};

// ===== BELL 4 — OFF-SITE APPROVALS (rebuilt 2026-08-30) =====
//
// NOT A REVERT of the bell removed on 2026-08-26 (b765bcd). That one was removed
// because it lingered, and it lingered for two reasons that a restore would have
// brought back with it:
//
//   1. It also rendered `offsite_approved` / `offsite_rejected` rows to the
//      REQUESTER, and nothing on earth deleted those. 52 of them were still in
//      production. Those types are now retired at the writer; this bell asks for
//      one type, and it is the queue.
//   2. It carried seen-state — a badge counting unseen rows, a "Mark Read"
//      button, a mark-seen round trip. A row that is merely SEEN is still in the
//      list, so the bell kept showing work that was already done.
//
// SO THERE IS NO SEEN-STATE HERE AT ALL, and that is the design, not an omission.
// The queue is the state: a row exists while the request is pending and is
// deleted by every terminal transition (approve, reject, cancel, expire — all
// four gated in verify-attendance-write-safety.js). Empty queue -> the whole
// bell unrenders, exactly like BELL 5 and BELL 6. Nothing to mark, nothing to
// clear, nothing that can disagree with the Off-site Requests tab.
//
// NO POLL, unlike BELL 5/6. Those two watch routes that emit no websocket event.
// This one has two — `notification_updated` fires when the row is written or
// deleted, `offsite_request_updated` fires on all four transitions — so a timer
// would only add latency-shaped noise. It stays mounted behind the null render
// so those listeners keep running and the icon appears on its own.
//
// The client capability gate is belt-and-braces: getVisibleNotifications already
// returns offsite_pending to approvers ONLY, so a non-approver's fetch comes back
// empty and the bell would unrender anyway. Checking canDo as well means a
// non-approver never issues the request in the first place.
// ===== ACTING ON AN OFF-SITE REQUEST — ONE AUTHORITY, TWO SURFACES ==========
//
// Owner ruling 2026-08-30: the bell's rows carry Approve / Reject / Open in Maps,
// and they must go through the SAME handler and the SAME capability gate as the
// Attendance page. Two surfaces reaching the same endpoints by two spellings is
// how they drift — and the one that drifts is always the one fewer people open.
//
// These live at MODULE SCOPE so a build gate can execute them. A rule about who
// may do what, sitting inside a render closure, is a rule nobody can check; the
// off-site page and this bell are exactly the "one component, two consumers" shape
// that hid the StatusModal picker for 124 days.

// WHO MAY DECIDE, AND ON WHICH ROW. One predicate, both surfaces, and the gate
// runs it in every shape.
//
// Called with ONE argument it answers the capability question — "may this account
// decide off-site requests at all" — which is what mounts the bell and what the
// Attendance page gates its whole panel on.
//
// Called with THREE it also answers "…this one?", and the extra rule is the owner's
// 2026-08-30 ruling: NOBODY DECIDES THEIR OWN REQUEST. The server refuses it with
// 403 self_decision (selfDecisionRefusal, server.js) and is the actual gate; this
// exists so the UI never offers a button the server will refuse. An approver who
// files a request sees their own row in the queue — they should, it is pending —
// with no Approve and no Reject on it.
//
// `userId` arrives POPULATED from GET /api/offsite/pending ({_id, name, role,…})
// and raw from anywhere else, so both shapes are normalised here rather than at two
// call sites, which is how the two surfaces would come to disagree.
var canDecideOffSite = function(access, request, selfUserId){
  if (!canDo(access, "approveOffSiteRequests")) return false;
  if (!request || !selfUserId) return true;          // capability-only question
  var owner = request.userId;
  var ownerId = (owner && typeof owner === "object") ? (owner._id || owner.id) : owner;
  if (!ownerId) return true;                          // unknown requester: server still decides
  return String(ownerId) !== String(selfUserId);
};

// THE MAPS LINK. Same URL on both surfaces because it is built in one place.
// Returns null when the request carries no fix, so neither surface renders a link
// to "undefined,undefined" — an off-site request can be filed with the browser
// refusing geolocation.
var offSiteMapsUrl = function(r){
  if (!r || r.latitude == null || r.longitude == null) return null;
  return "https://www.google.com/maps?q=" + r.latitude + "," + r.longitude;
};

// THE DECISION ITSELF. Approve and reject differ only by verb and by whether notes
// ride along, so they are one function — a second copy would be a second answer to
// "what does approving mean", and the copy that rots is the one on the surface
// nobody uses. Rejects with no `notes` send an empty string, exactly as the
// Attendance page has always done.
var offSiteDecision = function(action, id, opts){
  var o = opts || {};
  if (action !== "approve" && action !== "reject") {
    return Promise.reject(new Error("offSiteDecision: unknown action " + action));
  }
  var body = action === "reject" ? { notes: o.notes || "" } : null;
  return apiFetch("/api/offsite/" + id + "/" + action, "POST", body, o.token, o.csrfToken);
};

// ===== BELL 4 — OFF-SITE APPROVALS ==========================================
//
// NOT the bell removed on 2026-08-26 (b765bcd), and no longer the notification-row
// version that replaced it either. It reads GET /api/offsite/pending — the REQUEST
// COLLECTION, filtered `status:"pending"` — which is the queue rather than a mirror
// of it.
//
// THAT CHANGE IS WHY THE LINGERING CLASS IS GONE, not just its instances. The
// mirror needed every terminal transition to remember to delete a row, and the day
// a fourth one arrived (`expired`) it did not. Reading the queue means a request
// leaves this bell the instant its status changes — for approve, reject, cancel,
// expire, and for any terminal state nobody has written yet.
//
// It also carries no seen-state, deliberately: a row that is merely SEEN is still
// in the list, which is how the original bell kept showing finished work. Empty
// queue -> the whole bell unrenders, like BELL 5 and BELL 6.
//
// NO POLL. `offsite_request_updated` fires on create, cancel, approve and reject,
// and clientShouldReceive lets it through to every role, so a timer would only add
// latency. The component stays mounted behind the null render so the listener keeps
// running and the icon reappears on its own.
var OffSiteBell = function(p){
  var afterPaint = useBootPhase2();        // badge counts are phase 2
  var [rows, setRows] = useState([]);
  var [busyId, setBusyId] = useState(null);
  var [rejectingId, setRejectingId] = useState(null);
  var [rejectNotes, setRejectNotes] = useState("");
  var ref = useRef(null);
  var canApprove = canDecideOffSite(p.access);

  useEffect(function(){
    if(!p.show) return;
    var fn=function(e){ if(ref.current&&!ref.current.contains(e.target)) p.setShow(false); };
    document.addEventListener("mousedown",fn);
    return function(){ document.removeEventListener("mousedown",fn); };
  },[p.show]);

  var load = useCallback(function(){
    if(!p.token || !canApprove) return;
    apiFetch("/api/offsite/pending","GET",null,p.token)
      .then(function(r){ setRows(Array.isArray(r) ? r : []); })
      .catch(function(){});
  },[p.token, canApprove]);

  useEffect(function(){
    if(!p.token || !canApprove || !afterPaint) return;
    load();
    window.addEventListener("crm:offsite_request_updated", load);
    return function(){ window.removeEventListener("crm:offsite_request_updated", load); };
  },[p.token, canApprove, afterPaint, load]);

  // THE QUEUE IS THE STATE, so a decided row leaves the popover at once rather
  // than waiting for the websocket to come back round. The refetch behind it is
  // what makes the list authoritative again; this only removes the latency.
  var decide = function(action, id){
    setBusyId(String(id));
    offSiteDecision(action, id, { token: p.token, csrfToken: p.csrfToken, notes: rejectNotes })
      .then(function(){
        setRows(function(prev){ return (prev||[]).filter(function(x){ return String(x._id) !== String(id); }); });
        setRejectingId(null); setRejectNotes("");
        load();
      })
      .catch(function(err){ window.alert((err && err.message) || (action === "approve" ? "Approve failed" : "Reject failed")); })
      .finally(function(){ setBusyId(null); });
  };

  var count = rows.length;

  useEffect(function(){
    if(count===0 && p.show && p.setShow) p.setShow(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[count, p.show]);
  if(!canApprove || count===0) return null;

  var stop = function(e){ e.stopPropagation(); };

  return <div style={{ position:"relative" }} ref={ref}>
    <button onClick={function(){
      var opening=!p.show;
      p.setShow(opening);
      if(opening && p.closeOthers) p.closeOthers();
    }} style={{ width:36, height:36, borderRadius:9, border:"1px solid #E8ECF1", background:"#EFF6FF", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", position:"relative", transition:"all 0.2s" }}>
      <Navigation size={15} color="#1D4ED8"/>
      <span style={{ position:"absolute", top:-2, right:-2, minWidth:17, height:17, borderRadius:9, background:"#1D4ED8", color:"#fff", fontSize:9, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px", border:"2px solid #fff" }}>{count>9?"9+":count}</span>
    </button>
    {p.show&&<div className="crm-notif-panel" style={{ position:"absolute", top:46, right:0, width:360, maxWidth:"95vw", background:"#fff", borderRadius:16, boxShadow:"0 16px 48px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.04)", zIndex:200, maxHeight:460, display:"flex", flexDirection:"column" }}>
      <div style={{ padding:"14px 18px", borderBottom:"1px solid #F1F5F9", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <Navigation size={16} color="#1D4ED8"/>
          <span style={{ fontWeight:700, fontSize:14, color:C.text }}>Off-site requests</span>
          <span style={{ background:"#EFF6FF", color:"#1D4ED8", padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600 }}>{count}</span>
        </div>
        <button onClick={function(){ p.setShow(false); }} style={{ background:"none", border:"none", cursor:"pointer", color:C.textLight, display:"flex", padding:4 }}><X size={15}/></button>
      </div>
      <div style={{ overflowY:"auto", flex:1 }}>
        {/* No empty state — the bell unmounts its chrome at count===0. */}
        {rows.map(function(r){
          var emp = r.userId || {};
          var kind = r.type === "check_out" ? "Off-site check-out" : "Off-site check-in";
          var mapsUrl = offSiteMapsUrl(r);
          var busy = busyId === String(r._id);
          var rejecting = rejectingId === String(r._id);
          // NOBODY DECIDES THEIR OWN REQUEST. An approver's own row still appears
          // — it is genuinely pending — but carries no Approve and no Reject. The
          // server refuses it anyway (403 self_decision); this is so the UI never
          // offers the button.
          var mayDecide = canDecideOffSite(p.access, r, gid(p.cu));
          return <div key={String(r._id)} onClick={function(){
            p.setShow(false);
            if(p.onRowClick) p.onRowClick(r);
          }} style={{ padding:"11px 18px", borderBottom:"1px solid #F8FAFC", cursor:"pointer", background:"#fff", transition:"background 0.15s" }}
            onMouseEnter={function(e){ e.currentTarget.style.background="#F8FBFF"; }}
            onMouseLeave={function(e){ e.currentTarget.style.background="#fff"; }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:13, fontWeight:600, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{userNameOf(emp)||"(employee)"}</span>
              <span style={{ flexShrink:0, fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:8, background:"#EFF6FF", color:"#1D4ED8" }}>{kind}</span>
            </div>
            {r.reason&&<div style={{ fontSize:12, color:C.textLight, marginTop:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.reason}</div>}
            <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>{timeAgo(r.requestedAt, p.t)}</div>
            {/* THE ACTION ROW STOPS PROPAGATION — the row itself still navigates to
                Attendance, which is where the full request (coordinates, team, the
                whole reason text) is rendered. */}
            <div onClick={stop} style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, flexWrap:"wrap" }}>
              {!mayDecide && <span style={{ fontSize:10, color:C.textLight, fontStyle:"italic" }}>Your own request — another approver reviews it</span>}
              {mayDecide && <button type="button" disabled={busy} onClick={function(){ decide("approve", r._id); }}
                style={{ fontSize:11, padding:"5px 12px", border:"none", background:"#0F766E", color:"#fff", borderRadius:7, cursor: busy?"wait":"pointer", fontWeight:600, fontFamily:"inherit", opacity: busy?0.6:1 }}>
                {busy?"…":"Approve"}
              </button>}
              {mayDecide && !rejecting && <button type="button" disabled={busy} onClick={function(){ setRejectingId(String(r._id)); setRejectNotes(""); }}
                style={{ fontSize:11, padding:"5px 12px", border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent", borderRadius:7, cursor: busy?"wait":"pointer", fontFamily:"inherit", color:C.text }}>
                Reject
              </button>}
              {mapsUrl && <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                style={{ color:"#185FA5", textDecoration:"none", fontSize:11, fontWeight:500, marginLeft:"auto" }}>📍 Open in Maps ↗</a>}
            </div>
            {/* Same two-step as the Attendance page. A rejection the employee gets
                no reason for is worse than one extra tap, and copying the page's
                shape is half of what "the same handler" is supposed to mean. */}
            {mayDecide && rejecting && <div onClick={stop} style={{ marginTop:8, padding:10, background:"#FEF9F0", border:"0.5px solid #FDE68A", borderRadius:8 }}>
              <label style={{ fontSize:10, color:C.textLight, display:"block", marginBottom:5 }}>Rejection notes (optional)</label>
              <textarea value={rejectNotes} onChange={function(e){ setRejectNotes(e.target.value); }} rows={2}
                style={{ width:"100%", padding:7, border:"0.5px solid rgba(0,0,0,0.15)", borderRadius:6, fontSize:11, fontFamily:"inherit", resize:"vertical", boxSizing:"border-box" }}/>
              <div style={{ display:"flex", gap:6, marginTop:7, justifyContent:"flex-end" }}>
                <button type="button" onClick={function(){ setRejectingId(null); setRejectNotes(""); }}
                  style={{ fontSize:11, padding:"5px 10px", border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent", borderRadius:6, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                <button type="button" disabled={busy} onClick={function(){ decide("reject", r._id); }}
                  style={{ fontSize:11, padding:"5px 10px", border:"none", background:"#B91C1C", color:"#fff", borderRadius:6, cursor: busy?"wait":"pointer", fontWeight:600, fontFamily:"inherit", opacity: busy?0.6:1 }}>
                  {busy?"…":"Confirm reject"}
                </button>
              </div>
            </div>}
          </div>;
        })}
      </div>
    </div>}
  </div>;
};

// ===== THE PENDING-DEVELOPER BELL IS RETIRED (2026-09-11) ==================
// `PendingDevBell` lived here — an admin/operation queue of leads carrying a
// typed developer name with no developerId, each row routing to its side panel
// to be resolved by hand. It is DELETED with its endpoint
// (GET /api/leads/with-pending-developer), its route declaration and the
// resolver endpoint the rows led to.
//
// Nothing is pending any more. A developer is resolved at WRITE time
// (server.js, resolveDeveloperPair): every save folds the typed name onto an
// existing Developer or mints one, so the query behind this bell can only ever
// return zero rows. It hid itself at count===0, so what remained was a bell that
// could never ring — and a queue nobody can enter is worse than no queue,
// because its silence reads as "nothing to do" rather than "nothing to do this
// with".
//
// The twin below, PendingLocationBell, STAYS. Locations are a different rule: a
// DR's area is an owner-ordered managed list with an "Other" fallback, and an
// unrecognised place there IS a decision somebody has to make.

// ===== PENDING APPROVALS BELL =====
// Admin/SA approvals queue. Self-fetches GET /api/leads/pending-approvals
// (mirroring CallbackBell) on mount, every 60s, on cbBust (WS
// lead events), and on open. Shows every LIVE deal + EOI awaiting sign-off,
// company-wide — not just the ones the viewer submitted.
//
// This REPLACES the old Notification-fed "Deals & EOI" bell. That bell read
// type:"deal" Notification rows, which failed two structural ways: those rows
// shared one limit=600 window with rotation (~450 rows/day vs 38 deal rows
// total, so a deal fell out of the window ~32h after creation), and
// /eoi-to-deal never wrote a row at all, so a converted EOI showed as a pending
// EOI forever. A derived queue has neither failure mode: it is correct for
// historic rows, needs no backfill, and clears itself the moment an item is
// approved. See CLAUDE.md "Pending Approvals Bell — DO NOT BREAK".
//
// Unlike the retired PendingDevBell, this bell does NOT hide at count===0 — it is a standing
// workflow surface, and an honest "all caught up" state is the whole point
// after the old bell spent months claiming "No deals yet" over a real queue.
// ===== A PENDING-APPROVAL ROW'S KIND IS ITS COLLECTION (owner ruling 2026-09-11) =====
// The server stamps every queue row with _txnKind — the collection the row was
// built from (pendingApprovalRows). Kind, label and click target are read from
// THAT and nothing else: no fallback to the lead's status, which can read
// DoneDeal beside a live EOI record (#50058 rendered as "Deal" that way). A row
// with no stamp is rendered as unknown and is not routed — a guess would be a
// fallback by another name. Module scope so verify-approval-mirror.js runs it.
var PENDING_ROW_KINDS = { eoi: { label: "📋 EOI", page: "eoi" }, deal: { label: "🎉 Deal", page: "deals" } };
var pendingRowKind = function(n){ var k = n && n._txnKind; return PENDING_ROW_KINDS[k] ? k : null; };
var PendingApprovalsBell = function(p){
  var afterPaint = useBootPhase2();        // badge counts are phase 2
  var [rows, setRows] = useState([]);
  var ref = useRef(null);

  useEffect(function(){
    if(!p.show) return;
    var fn=function(e){ if(ref.current&&!ref.current.contains(e.target)) p.setShow(false); };
    document.addEventListener("mousedown",fn);
    return function(){ document.removeEventListener("mousedown",fn); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.show]);

  useEffect(function(){
    if(!p.token || !afterPaint) return;   // phase 2 — badge, see useBootPhase2
    var cancelled=false;
    var load=function(){
      apiFetch("/api/leads/pending-approvals","GET",null,p.token)
        .then(function(r){ if(!cancelled) setRows((r&&Array.isArray(r.data))?r.data:[]); })
        .catch(function(err){ console.warn("[PENDING_APPROVALS_FETCH_FAILED]", err&&err.message); });
    };
    load();
    var t=setInterval(load,60000);
    return function(){ cancelled=true; clearInterval(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, p.cbBust, afterPaint]);

  // Open-refetch — cbBust is debounced 1500ms off the WS burst, so this
  // guarantees a fresh count at the instant the admin opens the bell.
  useEffect(function(){
    if(!p.show||!p.token) return;
    var cancelled=false;
    apiFetch("/api/leads/pending-approvals","GET",null,p.token)
      .then(function(r){ if(!cancelled) setRows((r&&Array.isArray(r.data))?r.data:[]); })
      .catch(function(){});
    return function(){ cancelled=true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.show]);

  var count = rows.length;
  // The stamped kind, and nothing else — see pendingRowKind.
  var isDealRow = function(n){ return pendingRowKind(n) === "deal"; };
  // Local copy — the pages each define their own parseBudget inside the
  // component body, so there is no module-scope one to reuse here.
  var budgetNum = function(b){ return parseFloat((b||"0").toString().replace(/,/g,"")) || 0; };
  // Submitter = the frozen per-action author (who MADE the EOI / CLOSED the
  // deal), not the lead's live holder — those diverge after any rotation.
  var submitterOf = function(n){
    var anchor = isDealRow(n) ? n.dealAgentId : n.eoiAgentId;
    return userNameOf(anchor) || userNameOf(n.agentId) || "—";
  };

  return <div style={{ position:"relative" }} ref={ref}>
    <button onClick={function(){
      var opening=!p.show;
      p.setShow(opening);
      if(opening && p.closeOthers) p.closeOthers();
    }} style={{ width:36, height:36, borderRadius:9, border:"1px solid #E8ECF1", background:count>0?"#F0FDF4":"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", position:"relative", transition:"all 0.2s" }}>
      <DollarSign size={16} color={count>0?"#15803D":C.textLight}/>
      {count>0&&!p.show&&<span style={{ position:"absolute", top:-2, right:-2, minWidth:17, height:17, borderRadius:9, background:"#15803D", color:"#fff", fontSize:9, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px", border:"2px solid #fff" }}>{count>9?"9+":count}</span>}
    </button>
    {p.show&&<div className="crm-notif-panel" style={{ position:"absolute", top:46, right:0, width:360, maxWidth:"95vw", background:"#fff", borderRadius:16, boxShadow:"0 16px 48px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.04)", zIndex:200, maxHeight:460, display:"flex", flexDirection:"column" }}>
      <div style={{ padding:"14px 18px", borderBottom:"1px solid #F1F5F9", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:16 }}>⏳</span>
          <span style={{ fontWeight:700, fontSize:14, color:C.text }}>Pending approvals</span>
          {count>0&&<span style={{ background:"#F0FDF4", color:"#15803D", padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600 }}>{count}</span>}
        </div>
        <button onClick={function(){ p.setShow(false); }} style={{ background:"none", border:"none", cursor:"pointer", color:C.textLight, display:"flex", padding:4 }}><X size={15}/></button>
      </div>
      <div style={{ overflowY:"auto", flex:1 }}>
        {count===0&&<div style={{ padding:32, textAlign:"center", color:C.textLight, fontSize:13 }}>
          <div style={{ fontSize:28, marginBottom:8 }}>✅</div>All caught up — nothing awaiting approval
        </div>}
        {rows.map(function(n){
          var kind = pendingRowKind(n);
          var isDeal = kind === "deal";
          var kindMeta = PENDING_ROW_KINDS[kind] || { label: "Unknown", page: null };
          var bv = budgetNum(n.budget);
          // WHEN IT WAS SUBMITTED: the record's own createdAt (_recordedAt,
          // rowFromTxnRecord). eoiDate / dealDate are date-only by law and carry
          // no instant — reading them here printed midnight for every row. A
          // lead holding no record (branch B) is its own row, so its createdAt
          // is the only instant it has.
          var when = n._recordedAt || n.createdAt;
          return <div key={rowKey(n)} onClick={function(){
            p.setShow(false);
            if(p.onRowClick && kindMeta.page) p.onRowClick(kindMeta.page, n);
          }} style={{ padding:"11px 18px", borderBottom:"1px solid #F8FAFC", cursor:"pointer", background:"#fff", transition:"background 0.15s" }}
            onMouseEnter={function(e){ e.currentTarget.style.background="#F0FDF4"; }}
            onMouseLeave={function(e){ e.currentTarget.style.background="#fff"; }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:13, fontWeight:700, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{n.name||"(no name)"}</span>
              <span style={{ flexShrink:0, fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:8, background:isDeal?"#DCFCE7":"#EDE9FE", color:isDeal?"#15803D":"#6D28D9" }}>{kindMeta.label}</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginTop:3 }}>
              {n.project&&<span style={{ fontSize:11, color:"#6D28D9" }}>🏠 {n.project}</span>}
              {bv>0&&<span style={{ fontSize:11, color:C.success, fontWeight:700 }}>💰 {bv.toLocaleString()}</span>}
            </div>
            <div style={{ fontSize:11, color:C.textLight, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {/* WHICH action is waiting. This queue is worked row by row, so the
                  code is the most useful thing a row can carry — and it is the
                  one thing it could not say while a row was labelled by its
                  client. Server-supplied (r.recordNo); "No code" cannot appear
                  here, because a pending approval is by definition an action. */}
              {canSeeLeadIds(p.cu) && <span style={{ fontWeight:700 }}>{formatLeadId(n.recordNo)} · </span>}
              👤 {submitterOf(n)}{when?" · "+absoluteDateTime(when):""}
            </div>
          </div>;
        })}
      </div>
    </div>}
  </div>;
};

// ===== HEADER =====
// Page identity for the shared header: one lucide icon COMPONENT per page,
// rendered at a consistent size in a soft brand-tinted tile beside the title.
// (Was a set of Unicode glyphs, which render at inconsistent weights and
// baselines across platforms. The sidebar is TEXT-ONLY, so there was no
// existing per-page icon set to inherit — this map is the first one.) Keyed by the same page ids the `titles`
// map uses, so a new page only needs an entry here to inherit the treatment
// (and simply gets no tile if it is missed — never a broken header).
var PAGE_ICONS = {
  dashboard:LayoutGrid, kpis:Target, leads:User, dailyReq:FileText, meetings:Calendar,
  eoi:Clock, deals:Handshake, projects:Building, reports:BarChart3, commissions:DollarSign,
  team:Users, attendance:CheckCircle, users:UserPlus, brokers:Briefcase, archive:Archive,
  queue:Layers, offsiteRequests:MapPin, salaries:Wallet, companyOffDays:Calendar,
  assets:Package, settings:Settings
};

var Header = function(p) {
  var t = p.t; var isOnlyAdmin = p.cu&&(p.cu.role==="admin"||p.cu.role==="operation");
  // Leads / Daily Request on a phone: the search bar collapses to an icon (tap to open) and the row gets a slot the page
  // mounts its ＋ actions menu into (a portal — the buttons stay the page's own). Every other page/width is untouched.
  var mCompact = !!p.isMobile && (p.page === "leads" || p.page === "dailyReq");
  var [mSearchOpen, setMSearchOpen] = useState(false);

  // Close the rotation notif on outside click. BELL 3 (pending approvals) and
  // BELL 5 (pending developers) own their own outside-click handlers inside
  // their components, so they are absent here; BELL 4 (off-site) was removed
  // 2026-08-26 and took its ref and its half of this handler with it.
  var rotNotifRef = useRef(null);
  useEffect(function(){
    if(!p.showRotNotif) return;
    var fn=function(e){
      if(p.showRotNotif&&rotNotifRef.current&&!rotNotifRef.current.contains(e.target)){
        if(p.setShowRotNotif)p.setShowRotNotif(false);
      }
    };
    setTimeout(function(){document.addEventListener("mousedown",fn);},0);
    return function(){document.removeEventListener("mousedown",fn);};
  },[p.showRotNotif]);
  // Top bar. The shadow lives on the WRAPPER, not the inner row, so it sits
  // above page content as it scrolls under and does not get clipped by the
  // row's own layout.
  // NOT sticky, on purpose (2026-10-16): this was position:sticky for years but
  // its scrollport was .crm-app (overflow-x:hidden made it a scroll container
  // that never scrolls), so sticky was inert and the bar scrolled away with the
  // page. 8ea78ed removed that accidental scroller, the bar started pinning, and
  // on the phone it covered the top of every page. "relative" is what it always
  // actually did. A real sticky bar is a product decision, not a CSS side effect.
  return <div style={{ position:"relative", zIndex:100, background:"#fff", paddingTop:"env(safe-area-inset-top, 0px)", boxShadow:"0 1px 3px rgba(15,23,42,0.06), 0 4px 12px rgba(15,23,42,0.04)" }}>
  <div style={{ height:p.isMobile?60:72, background:"#fff", borderBottom:"1px solid #EEF2F6", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 18px", gap:10 }}>
    <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
      {p.isMobile&&<button onClick={p.onMenu} style={{ width:36, height:36, borderRadius:9, border:"1px solid #E8ECF1", background:"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0 }}><Menu size={18} color={C.text}/></button>}
      {/* Icon tile + title (+ optional muted subtitle). The tile is skipped when
          the page has no glyph, so an unmapped page degrades to title-only. */}
      {!!PAGE_ICONS[p.page] && !mCompact && (function(){
        var PageIcon = PAGE_ICONS[p.page];
        return <span aria-hidden="true" style={{ flexShrink:0, width:p.isMobile?30:34, height:p.isMobile?30:34, borderRadius:10, background:C.accent+"14", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <PageIcon size={p.isMobile?16:18} color={C.accent} strokeWidth={2.2}/>
        </span>;
      })()}
      <div style={{ minWidth:0 }}>
        <h1 style={{ fontSize:p.isMobile?16:22, fontWeight:800, letterSpacing:"-0.01em", color:C.text, margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", lineHeight:1.2 }}>{p.title}</h1>
      </div>
    </div>
    {/* Right cluster — one row, even 6px spacing. The action buttons each
        carry .crm-hdr-ico so hover/active states come from one CSS rule
        instead of per-button inline handlers. */}
    <div className="crm-hdr-cluster" style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
      {mCompact&&p.cu.role!=="hr"&&p.cu.role!=="office_boy"&&!roleHidesCustomerDataFE(p.cu)&&<button type="button" aria-label="Search" onClick={function(){ setMSearchOpen(!mSearchOpen); }} style={{ width:36, height:36, borderRadius:9, border:"1px solid "+((mSearchOpen||p.search)?C.accent:"#E8ECF1"), background:(mSearchOpen||p.search)?"#FFF8E8":"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", padding:0 }}><Search size={16} color={(mSearchOpen||p.search)?C.accent:C.textLight}/></button>}
      {!p.isMobile&&p.cu.role!=="hr"&&p.cu.role!=="office_boy"&&!roleHidesCustomerDataFE(p.cu)&&<HeaderSearch t={t} token={p.token} search={p.search} setSearch={p.setSearch} leads={p.leads} dailyRequests={p.dailyRequests} onLeadClick={p.onLeadClick} onDRClick={p.onDRItemClick||p.onDRClick} onTxnClick={p.onTxnClick}/>}
      
      {/* BELL 3 — Pending approvals (isolated component). Admin/SA only.
          Self-fetches /api/leads/pending-approvals; rows route to the item's
          Deals/EOI side panel. team_leader was dropped here on purpose: the
          feed is company-wide and approval is an admin/SA action, so a TL
          could previously see a badge for work they cannot act on. */}
      {(p.cu&&(p.cu.role==="admin"||p.cu.role==="operation")) && <PendingApprovalsBell
        token={p.token} cbBust={p.cbBust}
        show={p.showPendingApproval} setShow={p.setShowPendingApproval}
        onRowClick={p.onPendingApprovalClick}
        closeOthers={function(){ if(p.setShowNotif)p.setShowNotif(false); if(p.setShowRotNotif)p.setShowRotNotif(false); if(p.setShowOffSite)p.setShowOffSite(false); }}/>}

      {/* BELL 2 — Rotation notifications: admin + operation only */}
      {(p.cu&&(p.cu.role==="admin"||p.cu.role==="operation"))&&<div ref={rotNotifRef} style={{ position:"relative" }}>
        <button onClick={function(){var next=!p.showRotNotif;if(p.setShowRotNotif)p.setShowRotNotif(next);if(next){p.setShowNotif(false);if(p.setShowPendingApproval)p.setShowPendingApproval(false);if(p.setShowOffSite)p.setShowOffSite(false);if(p.onRotNotifSeen)p.onRotNotifSeen();}}} style={{ width:36, height:36, borderRadius:9, border:"1px solid #E8ECF1", background:p.unseenRot>0?"#FFF7ED":"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", position:"relative", transition:"all 0.2s" }}>
          <RotateCcw size={15} color={p.unseenRot>0?"#EA580C":C.textLight}/>
          {p.unseenRot>0&&!p.showRotNotif&&<span style={{ position:"absolute", top:-2, right:-2, minWidth:17, height:17, borderRadius:9, background:"#EA580C", color:"#fff", fontSize:9, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px", border:"2px solid #fff" }}>{p.unseenRot>9?"9+":p.unseenRot}</span>}
        </button>
        {p.showRotNotif&&<div className="crm-notif-panel" style={{ position:"absolute", top:46, right:0, width:340, background:"#fff", borderRadius:16, boxShadow:"0 16px 48px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.04)", zIndex:200, maxHeight:440, display:"flex", flexDirection:"column" }}>
          <div style={{ padding:"14px 18px", borderBottom:"1px solid #F1F5F9", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <RotateCcw size={16} color="#EA580C"/>
              <span style={{ fontWeight:700, fontSize:14, color:C.text }}>Rotations</span>
              {p.rotNotifs&&p.rotNotifs.length>0&&<span style={{ background:"#FFF7ED", color:"#EA580C", padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600 }}>{p.rotNotifs.length}</span>}
            </div>
            <div style={{ display:"flex", gap:4, alignItems:"center" }}>
              {p.rotNotifs&&p.rotNotifs.length>0&&<button onClick={function(){if(p.onRotClearAll)p.onRotClearAll();}} style={{ background:"#FFF7ED", border:"none", borderRadius:6, cursor:"pointer", fontSize:11, color:"#EA580C", fontWeight:600, padding:"4px 10px" }}>Clear all</button>}
              <button onClick={function(){if(p.setShowRotNotif)p.setShowRotNotif(false);}} style={{ background:"none", border:"none", cursor:"pointer", color:C.textLight, display:"flex", padding:4 }}><X size={15}/></button>
            </div>
          </div>
          <div style={{ overflowY:"auto", flex:1 }}>
            {(!p.rotNotifs||p.rotNotifs.length===0)&&<div style={{ padding:32, textAlign:"center", color:C.textLight, fontSize:13 }}>
              <div style={{ fontSize:28, marginBottom:8 }}>🔄</div>No rotations
            </div>}
            {p.rotNotifs&&p.rotNotifs.map(function(n){
              var canNav = !!n.leadId;
              var openItem = function(){
                if (p.setShowRotNotif) p.setShowRotNotif(false);
                if (!canNav) return;
                var route = function(target){
                  if (p.onRotNotifClick) p.onRotNotifClick(target);
                  else if (p.onLeadClick) p.onLeadClick(target);
                };
                // BUG #6 — always fetch /api/leads/:id; the ?fields=summary
                // slice in p.leads omits Project/Campaign and post-shrink
                // may not even contain the lead. Fail-soft to a name shell.
                apiFetch("/api/leads/" + String(n.leadId), "GET", null, p.token).then(function(fresh){
                  route((fresh && fresh._id) ? fresh : { _id: n.leadId, name: n.leadName||"" });
                }).catch(function(err){
                  console.warn("[ROTATION_BELL_FETCH_FAILED]", { notification: n, error: err && err.message });
                  route({ _id: n.leadId, name: n.leadName||"" });
                });
              };
              return <div key={n._id||n.id} onClick={canNav?openItem:undefined} style={{ padding:"12px 18px", borderBottom:"1px solid #F8FAFC", display:"flex", alignItems:"center", gap:12, background:n.seen?"#fff":"#FFFBF5", transition:"background 0.2s", cursor:canNav?"pointer":"default" }}>
                <div style={{ width:38, height:38, borderRadius:10, background:"linear-gradient(135deg,#FFF7ED,#FFEDD5)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><RotateCcw size={16} color="#EA580C"/></div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{n.leadName}</div>
                  <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>{n.fromName} → {n.toName}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:2 }}>
                    <span style={{ fontSize:10, color:"#EA580C", fontWeight:600, background:"#FFF7ED", padding:"1px 6px", borderRadius:4 }}>{(function(){ if(n.reason==="Manual reassign") return "Manual reassign"; var labels={no_answer_streak:"No Answer",callback_overdue:"Callback overdue",not_interested_return:"Not Interested return",no_action_timeout:"No action timeout",hot_no_action:"Hot Case +1 no contact"}; return "Auto rotation — "+(labels[n.reason]||n.reason||"auto"); })()}</span>
                    <span style={{ fontSize:10, color:C.textLight }}>{timeAgo(n.createdAt||n.eventTime||n.time, t)}</span>
                  </div>
                </div>
                {!n.seen&&<div style={{ width:8, height:8, borderRadius:"50%", background:"#EA580C", flexShrink:0 }}/>}
              </div>;
            })}
            {/* "Load older" — the bell shows the newest NOTIF_PAGE rotation rows
                and pages back from there. Before 2026-08-14 the four bell types
                shared ONE limit=600 and rotation (~422/day, 35,434 rows) took
                599 of those slots, so older rotation rows were unreachable and
                the off-site types were starved out entirely. Rendered only when
                the server says more pages exist, so it never appears on a bell
                that is already showing everything. */}
            {p.rotHasMore && <div style={{ padding:"10px 18px", borderTop:"1px solid #F1F5F9", textAlign:"center" }}>
              <button onClick={function(){ if(p.onLoadOlderRot) p.onLoadOlderRot(); }}
                style={{ background:"#FFF7ED", border:"1px solid #FFEDD5", borderRadius:8, cursor:"pointer",
                         fontSize:11, color:"#EA580C", fontWeight:600, padding:"6px 14px" }}>
                Load older
              </button>
            </div>}
          </div>
        </div>}
      </div>}

      {/* BELL 4 (📍 off-site) WAS HERE — REMOVED FOR ALL ROLES, owner rule
          2026-08-26. The pin, its unseen badge and its popover are gone; nothing
          replaced them in the header.

          The two things it did are both still done, better, by surfaces that own
          the data instead of mirroring it:
            - APPROVERS act from Attendance › Off-site Requests, which reads the
              request collection itself rather than a notification row.
            - REQUESTERS see their own state in the Check-in widget, which is
              driven by data.pendingOffSite — the live request, not a copy of it.
          Push notifications are untouched, and so is the general 🔔 bell.

          The notification ROWS are still written server-side and the routes that
          serve them are unchanged (GET /api/notifications and
          PUT /api/notifications/mark-seen are shared with the rotation bell) —
          this only stopped ASKING for the offsite_* types. See loadNotifications. */}

      {/* BELL 6 — Pending locations (isolated component). Admin/SA only.
          Self-fetches /api/daily-requests/with-pending-location; each row routes
          to that request's Daily Requests side panel, where the resolver sits.
          Hides entirely when the queue is empty. (It used to say "same as
          BELL 5" — the pending-developer bell, which is retired; this is the
          only self-hiding bell left.) */}
      {(p.cu&&(p.cu.role==="admin"||p.cu.role==="operation")) && <PendingLocationBell
        token={p.token} cbBust={p.cbBust} locBust={p.pendingLocRev}
        show={p.showPendingLoc} setShow={p.setShowPendingLoc}
        onRowClick={p.onPendingLocClick}
        closeOthers={function(){ if(p.setShowNotif)p.setShowNotif(false); if(p.setShowPendingApproval)p.setShowPendingApproval(false); if(p.setShowRotNotif)p.setShowRotNotif(false); if(p.setShowOffSite)p.setShowOffSite(false); }}/>}

      {/* BELL 4 — Off-site approvals (rebuilt 2026-08-30, NOT the one removed in
          b765bcd). Self-fetches the pending queue, gates itself on the
          approveOffSiteRequests capability, carries no seen-state, and unrenders
          when the queue empties. Rows route to Attendance › Off-site Requests,
          which is where the approve/reject buttons live. */}
      <OffSiteBell
        token={p.token} csrfToken={p.csrfToken} access={p.access} t={p.t} cu={p.cu}
        show={p.showOffSite} setShow={p.setShowOffSite}
        onRowClick={p.onOffSiteClick}
        closeOthers={function(){ if(p.setShowNotif)p.setShowNotif(false); if(p.setShowPendingApproval)p.setShowPendingApproval(false); if(p.setShowRotNotif)p.setShowRotNotif(false); if(p.setShowPendingLoc)p.setShowPendingLoc(false); }}/>

      {/* BELL 1 — Callbacks (isolated component). Not rendered for HR — never mounts, so no callbacks fetch/poll. */}
      {p.cu.role!=="hr" && p.cu.role!=="office_boy" && !roleHidesCustomerDataFE(p.cu) && <CallbackBell t={p.t} token={p.token} cbBust={p.cbBust} cu={p.cu} myTeamUsers={p.myTeamUsers} showNotif={p.showNotif} setShowNotif={p.setShowNotif} setShowPendingApproval={p.setShowPendingApproval} setShowRotNotif={p.setShowRotNotif} onLeadClick={p.onLeadClick} onDRClick={p.onDRClick}/>}
      {/* DELETIONS — the owner's record of every delete / cancel / restore (owner rule 2026-11-02).
          getVisibleNotifications answers type "delete" to isOwner accounts alone; mounted for the same flag. */}
      {isOwnerFE(p.access) && <DeletionBell token={p.token} csrfToken={p.csrfToken} apiFetch={apiFetch}/>}
    </div>
  </div>
  {p.isMobile&&p.cu.role!=="hr"&&p.cu.role!=="office_boy"&&!roleHidesCustomerDataFE(p.cu)&&(!mCompact||mSearchOpen||!!p.search)&&<div style={{ padding:"8px", background:"#fff", borderBottom:"1px solid #E5E7EB" }}>
    <HeaderSearch autoFocus={mCompact&&mSearchOpen&&!p.search} width="100%" t={t} token={p.token} search={p.search} setSearch={p.setSearch} leads={p.leads} dailyRequests={p.dailyRequests} onLeadClick={p.onLeadClick} onDRClick={p.onDRItemClick||p.onDRClick} onTxnClick={p.onTxnClick}/>
  </div>}
  </div>;
};

// ===== LEAD FORM (shared for add/edit) =====
// Add-form draft autosave — guards against mobile WebView background-reload data
// loss. Persists serializable text/scalar fields to localStorage (survives a
// WebView reload) so an OS-killed Add EOI / Add Deal / Add Lead form can restore
// on reopen. Attachments are intentionally excluded (too large for localStorage)
// and re-picked after restore. Add mode only.
var LEAD_DRAFT_PREFIX = "crm_lead_draft_v1_";
var readLeadDraft = function(key){ try { return JSON.parse(localStorage.getItem(key) || "null"); } catch(e){ return null; } };
var writeLeadDraft = function(key, data){ try { localStorage.setItem(key, JSON.stringify(data)); } catch(e){} };
var clearLeadDraft = function(key){ try { localStorage.removeItem(key); } catch(e){} };

var LeadForm = function(p) {
  useLeadSourcesVersion();   // re-render when the lead source registry lands (src/lib/leadSources.js)
  var t = p.t; var isAdmin = p.cu.role==="admin"||p.cu.role==="operation"||positionOf(p.cu)==="director"||positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader";
  var isOnlyAdmin = p.cu.role==="admin"||p.cu.role==="operation";
  var salesUsers = p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;});
  // Draft autosave (add mode only): key scoped per form purpose so Add EOI / Add
  // Deal / Add Lead / Add Request don't clobber each other. Read once via a ref.
  var draftKey = LEAD_DRAFT_PREFIX + (p.isReq ? "req" : (p.initialStatus || "lead"));
  var draftRef = useRef(false);
  if (draftRef.current === false) draftRef.current = (!p.editId ? readLeadDraft(draftKey) : null) || null;
  var initialDraft = draftRef.current;
  var [form, setForm] = useState((function(){
    var base = p.initial||{ name:"", phone:"", phone2:"", email:"", budget:"", project:"", source:p.isReq?"Daily Request":"", agentId:"", callbackTime:"", notes:"", status:"NewLead", dealDate:"", eoiDate:"", eoiDeposit:"", downPaymentPct:"", installmentYears:"" };
    // Full sale form (2026-07-05) — ensure the sale-form fields are always present
    // so the inputs stay controlled even when p.initial (Add Deal / Add EOI) omits
    // them. Server values (p.initial) win; these only fill gaps.
    //
    // CREATE MODE ONLY (2026-08-08). On an EDIT these "gap fillers" were the bug:
    // when the list projection didn't ship a field, the default became the form's
    // value and was SENT on save (the Done-Deal branch doesn't strip them), so
    // saving an untouched deal rewrote saleType to "Primary" — which changes
    // commission economics — and blanked the four date/amount fields. On edit the
    // stored record is the only legitimate source; a field that is genuinely
    // absent must stay `undefined` so JSON drops it and the server's partial
    // merge leaves the stored value alone.
    if (!p.editId) {
      base=Object.assign({ saleType:"Primary", downPayment:"", downPaymentDate:"", reservationDate:"", contractionDate:"" }, base);
    }
    // Two-party Resale — seller + buyer, each optionally "with us" (own agent +
    // commission → own Commission doc). Default seller with-us; only used when
    // saleType==="Resale". Normalize an existing lead's resaleParties (agentId
    // ObjectId → string, numbers → strings) so the inputs bind cleanly on edit.
    var mkParty=function(p){ p=p||{}; return { withUs:!!p.withUs, name:p.name||"", phone:p.phone||"", agentId:p.agentId?String(p.agentId._id||p.agentId):"", commissionAmount:(p.commissionAmount!=null&&p.commissionAmount!=="")?String(p.commissionAmount):"", commissionRate:(p.commissionRate!=null&&p.commissionRate!=="")?String(p.commissionRate):"" }; };
    if(base.resaleParties){ base=Object.assign({},base,{ resaleParties:{ seller:mkParty(base.resaleParties.seller), buyer:mkParty(base.resaleParties.buyer) } }); }
    else { base=Object.assign({},base,{ resaleParties:{ seller:{ withUs:true, name:"", phone:"", agentId:"", commissionAmount:"", commissionRate:"" }, buyer:{ withUs:false, name:"", phone:"", agentId:"", commissionAmount:"", commissionRate:"" } } }); }
    // Load saved extra fields from localStorage if editing a deal. Now that the
    // sale fields persist server-side (downPayment/downPaymentPct/installmentYears/
    // dealDate on the Lead), the SERVER value wins — the old localStorage "extra"
    // only backfills a legacy deal that predates server persistence.
    if(p.editId){
      var extra=getDealExtra(String(p.editId));
      if(extra) base=Object.assign({},base,{downPaymentPct:base.downPaymentPct||extra.downPaymentPct||"",installmentYears:base.installmentYears||extra.installmentYears||"",dealDate:base.dealDate||extra.dealDate||""});
      // Also read dealDate from lead object if available
      if(p.initial&&p.initial.dealDate&&!base.dealDate) base=Object.assign({},base,{dealDate:p.initial.dealDate});
    }
    // Phase R-12 Part 3 — seed external-deal fields from initial. dealType
    // defaults to "internal" so existing add-deal flows are unchanged. The
    // sub-doc shape mirrors the schema (Part 2): { commissionTaxPct, brokerSharePct }.
    base = Object.assign({
      dealType: "internal",
      externalBrokerId: "",
      externalDealConfig: { commissionTaxPct: 0, brokerSharePct: 0 },
      // Phase R-14 — ambassador-deal config. developerTaxRate is a string in the
      // controlled input; coerced to number on submit. Cleared by the backend
      // validator whenever dealType !== "ambassador".
      ambassadorConfig: { developerTaxEnabled: false, developerTaxRate: "" },
      commissionRate: "",
      externalSalesAgentEnabled: false,
      externalSalesAgentId: "",
      externalSalesAgentManualAmount: "",
      externalSalesAgentPaidBy: "company"
    }, base);
    if (base.externalBrokerId && typeof base.externalBrokerId === "object") {
      base.externalBrokerId = String(base.externalBrokerId._id || "");
    }
    if (base.externalSalesAgentId && typeof base.externalSalesAgentId === "object") {
      base.externalSalesAgentId = String(base.externalSalesAgentId._id || "");
    }
    // agentId comes back POPULATED ({_id,name,title,...}) from the leads-list
    // query. Coerce to its id string so the searchable Agent picker binds to an
    // option value — a raw object would render as a React child and blank the
    // whole form. Mirrors the externalBrokerId / externalSalesAgentId normalize.
    if (base.agentId && typeof base.agentId === "object") {
      base.agentId = String(base.agentId._id || "");
    }
    // A DATE INPUT NEEDS A DATE (toDateInputValue). A datetime-shaped eoiDate
    // rendered the EOI Date input BLANK on #50058's Edit modal while the row
    // showed 09/09/2026 — the same value, two renderers. Normalised here, once,
    // for every date-typed field the form binds.
    ["eoiDate", "dealDate", "reservationDate", "downPaymentDate", "contractionDate"].forEach(function (k) {
      if (k in base && base[k] != null && base[k] !== "") base[k] = toDateInputValue(base[k]);
    });
    // developerId and closingCompanyId arrive POPULATED ({_id,name}) from
    // /api/leads and from the record rows, and were the only id fields on this
    // form that nobody normalised — agentId, externalBrokerId and
    // externalSalesAgentId all did, three lines apart. The object survived normId
    // on the server by luck (it reads ._id), so the omission never showed;
    // treating them like their siblings makes the form hold an id string like
    // every other reference and removes the luck from the path.
    //
    // A key that is genuinely ABSENT stays absent — `in` rather than truthiness.
    // rowFromTxnRecord now omits these when the record holds none, and coercing
    // a missing key to "" here would re-manufacture on the client the exact clear
    // the server just stopped manufacturing.
    ["developerId", "closingCompanyId"].forEach(function (k) {
      if (!(k in base)) return;
      if (base[k] && typeof base[k] === "object") base[k] = String(base[k]._id || "");
      else if (base[k] != null) base[k] = String(base[k]);
    });
    // Defensive normalize: an existing lead may come back with externalDealConfig
    // as an empty object (or with string-typed pcts from old payloads). Coerce
    // to numeric so the controlled inputs below don't render NaN.
    var seedCfg = (base.externalDealConfig && typeof base.externalDealConfig === "object") ? base.externalDealConfig : {};
    base.externalDealConfig = {
      commissionTaxPct: Number(seedCfg.commissionTaxPct) || 0,
      brokerSharePct:   Number(seedCfg.brokerSharePct)   || 0
    };
    // Phase R-14 — normalize ambassadorConfig from an existing lead. developerTaxRate
    // is a Number on the DB but a string in the controlled input (allows decimals).
    var seedAmb = (base.ambassadorConfig && typeof base.ambassadorConfig === "object") ? base.ambassadorConfig : {};
    base.ambassadorConfig = {
      developerTaxEnabled: !!seedAmb.developerTaxEnabled,
      developerTaxRate: (typeof seedAmb.developerTaxRate === "number" && isFinite(seedAmb.developerTaxRate) && seedAmb.developerTaxRate > 0)
        ? String(seedAmb.developerTaxRate)
        : ""
    };
    // commissionRate is a Number on the DB (percent), but a string in the
    // controlled input (allows decimals). Leave empty for old deals that
    // predate this field so the field appears empty + required.
    if (typeof base.commissionRate === "number" && isFinite(base.commissionRate) && base.commissionRate > 0) {
      base.commissionRate = String(base.commissionRate);
    } else {
      base.commissionRate = "";
    }
    if (typeof base.externalSalesAgentManualAmount === "number" && isFinite(base.externalSalesAgentManualAmount) && base.externalSalesAgentManualAmount > 0) {
      base.externalSalesAgentManualAmount = base.externalSalesAgentManualAmount.toLocaleString(undefined, { maximumFractionDigits: 2 });
    } else {
      base.externalSalesAgentManualAmount = "";
    }
    // Draft restore (add mode only) — overlay saved text/scalar fields onto the
    // normalized base. Attachments were never persisted, so documentFiles stays.
    if (initialDraft && initialDraft.form) base = Object.assign({}, base, initialDraft.form);
    return base;
  })());
  var [dupWarning, setDupWarning] = useState(null);
  var [saving, setSaving] = useState(false);
  // Draft banner (add mode) — shown when a saved draft was restored on open.
  var [draftRestored, setDraftRestored] = useState(!!initialDraft);
  var [draftHadAttach, setDraftHadAttach] = useState(!!(initialDraft && initialDraft.hadAttachments));
  // Phase R-12 Part 3 — brokers state for the External-deal dropdown.
  // Self-fetched on mount when admin AND the form is for a Done Deal (the
  // only context that surfaces the toggle). Fetches with includeArchived=1
  // so an existing lead's archived broker still renders correctly.
  var [brokers, setBrokers] = useState([]);
  var [brokersLoaded, setBrokersLoaded] = useState(false);
  var [showAddBroker, setShowAddBroker] = useState(false);
  var [newBrokerName, setNewBrokerName] = useState("");
  var [savingBroker, setSavingBroker] = useState(false);
  // Developer (N3) — REQUIRED for Add Deal / Add EOI (create mode, EOI/DoneDeal).
  // Same pattern as StatusModal: devSelId (dropdown) XOR devPendName (typed new
  // name). Create-only — edit mode never shows it (developer edits go through the
  // deal side-panel dropdown, which carries the N1 approval lock).
  var [devSelId, setDevSelId] = useState((initialDraft && initialDraft.devSelId) || "");
  var [devPendName, setDevPendName] = useState((initialDraft && initialDraft.devPendName) || "");
  var [devPendOpen, setDevPendOpen] = useState(false);
  var [devList, setDevList] = useState([]);
  // Legacy/former-agent toggle (Add Deal, admin only) — mirrors the developer-
  // pending pattern. Open = the Agent SearchableSelect is replaced by a free-text
  // input; filling it clears form.agentId so NO real user is credited.
  var [legacyAgentOpen, setLegacyAgentOpen] = useState(!!(initialDraft && initialDraft.legacyAgentName) || !!(p.initial && p.initial.legacyAgentName));
  // Phase R-12 Part 5 — commission lock. When editing an existing deal whose
  // active commission already exists, the dealType / broker / pct fields are
  // frozen on the FE (matched by the backend lock at PUT /api/leads/:id).
  // Cancelled commissions don't lock — admin can re-tag a rolled-back deal.
  // null = not yet checked (or no editId / not DoneDeal); false = unlocked;
  // true = locked.
  var [commissionLock, setCommissionLock] = useState(null);
  // Synchronous inflight guard — the button's disabled attribute depends on
  // React state, which isn't committed to the DOM until after the current
  // event loop tick, so a second click that fires before React commits could
  // still slip through. A ref gives us a same-tick check that blocks the
  // second submission before it ever hits the network.
  var inflight = useRef(false);
  var isReq = p.isReq||false;
  // Effective status: forced initialStatus (EOI / Deals pages) wins, else edited lead's status,
  // else the form's own status picker (Add Lead modal). Lets EOI/DoneDeal-gated UI react live.
  var effectiveStatus = p.initialStatus || (p.editId&&p.initial ? p.initial.status : form.status) || "";
  var isEOIForm = effectiveStatus==="EOI";
  var isDoneDealForm = effectiveStatus==="DoneDeal";

  // Phase R-12 Part 3 — fetch brokers when the form is in a Done Deal context
  // for an admin user. Skip otherwise to avoid wasted API calls (regular Add
  // Lead / Edit Lead never surfaces the External toggle). includeArchived=1
  // so editing an old deal whose broker has since been archived still renders.
  // Hooks-above-guards: this useEffect must precede any conditional return.
  useEffect(function(){
    if (!isOnlyAdmin) return;
    if (!isDoneDealForm) return;
    if (brokersLoaded) return;
    apiFetch("/api/brokers?includeArchived=1", "GET", null, p.token).then(function(resp){
      setBrokers((resp && resp.data) || []);
      setBrokersLoaded(true);
    }).catch(function(){
      // Silent — dropdown will be empty + "+ Add Broker" still works.
      setBrokersLoaded(true);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnlyAdmin, isDoneDealForm]);

  // Phase R-12 Part 5 — commission-lock probe. Only when EDITING an existing
  // deal as admin (not Add Deal — no commission can exist on a brand-new
  // lead). 404 from /api/commissions/by-lead/:leadId means no commission =
  // unlocked. 200 with status !== "cancelled" means active/fully_paid =
  // locked. Mirrors the backend gate at PUT /api/leads/:id.
  useEffect(function(){
    if (!isOnlyAdmin || !isDoneDealForm || !p.editId) {
      setCommissionLock(false);
      return;
    }
    apiFetch("/api/commissions/by-lead/" + p.editId, "GET", null, p.token).then(function(c){
      if (c && c.status && c.status !== "cancelled") setCommissionLock(true);
      else setCommissionLock(false);
    }).catch(function(){
      // 404 (no commission yet) or any other error → unlocked. The backend
      // is the source of truth and will re-reject on PUT if the state changed
      // between probe and submit.
      setCommissionLock(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnlyAdmin, isDoneDealForm, p.editId]);
  // Developer list (N3) — load the 505 managed developers for the create-mode
  // EOI/DoneDeal developer picker (edit mode never shows the field).
  var liveDevRev = useLiveRev("developer");
  useEffect(function(){
    if (p.editId || !(isEOIForm||isDoneDealForm) || !p.token) return;
    var cancelled = false;
    apiFetch("/api/developers","GET",null,p.token).then(function(data){
      if (cancelled) return;
      var rows = (data && Array.isArray(data.data)) ? data.data : (Array.isArray(data) ? data : []);
      setDevList(rows);
    }).catch(function(){ /* non-fatal: dropdown shows no options */ });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEOIForm, isDoneDealForm, p.editId, p.token, liveDevRev]);

  // Inline broker creation. Triggered when the user picks "+ Add new broker"
  // in the dropdown OR clicks the inline + button. Posts to /api/brokers,
  // appends the result to the local list, and auto-selects it. Kept inline
  // (no nested Modal) because the form is already inside one.
  var addBrokerInline = async function() {
    var nm = String(newBrokerName || "").trim();
    if (!nm) { alert("Broker name required"); return; }
    setSavingBroker(true);
    try {
      var doc = await apiFetch("/api/brokers", "POST", { name: nm }, p.token, p.csrfToken);
      if (doc && doc._id) {
        setBrokers(function(prev){ return [doc].concat(prev || []); });
        upd("externalBrokerId", String(doc._id));
        setShowAddBroker(false);
        setNewBrokerName("");
      }
    } catch(e) { alert(e.message || "Failed to add broker"); }
    setSavingBroker(false);
  };
  // Only Add Lead (no forced status, not editing) shows a status picker.
  var showStatusPicker = !p.initialStatus && !p.editId;

  var checkDup = async function(phone) {
    if (phone.length < 8) { setDupWarning(null); return; }
    try {
      var res = await apiFetch("/api/leads/check-duplicate/"+encodeURIComponent(phone), "GET", null, p.token);
      if (res.exists && (!p.editId || gid(res.lead) !== p.editId)) setDupWarning(res.lead);
      else setDupWarning(null);
    } catch(e) { setDupWarning(null); }
  };

  var upd = function(k, v) { setForm(function(f){return Object.assign({},f,{[k]:v});}); };
  // Nested updater for the two-party Resale seller/buyer sub-objects.
  var updRP = function(side, field, v) { setForm(function(f){ var rp=Object.assign({ seller:{}, buyer:{} }, f.resaleParties); rp[side]=Object.assign({}, rp[side], {[field]:v}); return Object.assign({},f,{ resaleParties:rp }); }); };

  // (The Done-Deal campaign auto-clear was REMOVED with the layout unification.
  // Ad Campaign Name is a base field now — rendered in every status, exactly like
  // the other base fields — so an effect that wiped it whenever the source was
  // not a paid-ad one would silently erase what the user had just typed into a
  // visible input. It existed only because the field used to HIDE on that
  // condition; the condition is gone, so the clear goes with it. isAdSource is
  // still read elsewhere and is untouched.)

  // Resale hides the down-payment + installment fields (direct full-payment) —
  // clear any stale values when the deal form switches to Resale so they aren't
  // submitted. dealDate (the single Resale Deal Date) is intentionally kept.
  useEffect(function(){
    if (isDoneDealForm && form.saleType === "Resale") {
      ["downPayment","downPaymentPct","downPaymentDate","reservationDate","contractionDate","installmentYears"].forEach(function(k){
        if (form[k]) upd(k, "");
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.saleType, isDoneDealForm]);

  // LEAVING A STATUS BRANCH CLEARS THAT BRANCH'S OWN FIELDS.
  //
  // The submit payload is `Object.assign({}, form, …)` — EVERY key in `form`
  // is sent, whether or not the branch that owns it is still on screen. So a
  // user who filled the Done Deal sale form, changed their mind and saved a
  // New Lead was posting a unit code, a down payment, four dates and a deal
  // type along with it; the fields were unmounted, not unset. Unmounting a
  // React input does not clear the state behind it, and that is the whole bug
  // class here — the same one the Resale effect above already handles for one
  // sub-branch, and the campaign effect handles for one field.
  //
  // GATED ON showStatusPicker, which is the exact condition under which the
  // status can change at all (`!p.initialStatus && !p.editId`). The Add Deal /
  // Add EOI pages force a status and Edit Lead loads one, so on those the flags
  // never flip and this must never fire — it would wipe fields the form was
  // opened with, or a restored draft. Inert there by construction, not by luck.
  //
  // Keys are set to `undefined` rather than "": JSON.stringify omits an
  // undefined value, so the key leaves the request body entirely instead of
  // arriving as an empty string the server would have to interpret. Every
  // reader in this form already reads them as `form.x || ""`.
  //
  // One setForm, not a loop of upd() calls — these fire together on a single
  // status change and should be one render.
  useEffect(function(){
    if (!showStatusPicker) return;
    var drop = [];
    // The sale form's own fields. `dealDate` included: submit mirrors
    // contractionDate into it, so a stale one would date a lead that is not a deal.
    if (!isDoneDealForm) drop = drop.concat(["unitCode","downPayment","downPaymentPct","downPaymentDate",
      "reservationDate","contractionDate","installmentYears","dealDate","saleType",
      "commissionRate","commissionAmount","dealType","externalBrokerId","externalDealConfig",
      "externalSalesAgentEnabled","externalSalesAgentId","externalSalesAgentManualAmount",
      "externalSalesAgentPaidBy","ambassadorConfig","resaleParties","legacyAgentName"]);
    if (!isEOIForm) drop = drop.concat(["eoiDate","eoiDeposit"]);
    // Attachments ride a SEPARATE upload loop after create (payload.documentFiles
    // is deleted before the POST), and that loop is not status-gated — so files
    // attached on a Done Deal form would upload to a New Lead. The picker only
    // exists for EOI / Done Deal, so leaving both is what clears them.
    if (!isEOIForm && !isDoneDealForm) drop.push("documentFiles");
    // The callback field is the mirror image: it is hidden ON the EOI form, so
    // entering EOI is what strands a value.
    if (isEOIForm) drop.push("callbackTime");
    setForm(function(f){
      var hit = drop.filter(function(k){
        var v = f[k];
        return v !== undefined && v !== "" && v !== null &&
               !(Array.isArray(v) && v.length === 0);
      });
      if (!hit.length) return f;                 // no-op: never re-render for nothing
      var next = Object.assign({}, f);
      hit.forEach(function(k){ next[k] = undefined; });
      return next;
    });
    if (!isDoneDealForm) setLegacyAgentOpen(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoneDealForm, isEOIForm, showStatusPicker]);

  // Draft autosave (add mode only) — debounced persist of text/scalar fields.
  // Attachments (documentFiles) are excluded; we only record whether any exist so
  // the restore banner can prompt a re-pick. An emptied form clears its draft.
  useEffect(function(){
    if (p.editId) return;
    var handle = setTimeout(function(){
      var meaningful = !!(form.name||form.phone||form.phone2||form.email||form.budget||form.project||form.notes||form.eoiDeposit||form.eoiDate||form.dealDate||form.callbackTime||devSelId||devPendName);
      if (!meaningful) { clearLeadDraft(draftKey); return; }
      var toSave = Object.assign({}, form);
      delete toSave.documentFiles;
      writeLeadDraft(draftKey, { form: toSave, devSelId: devSelId, devPendName: devPendName, hadAttachments: !!(form.documentFiles && form.documentFiles.length) });
    }, 400);
    return function(){ clearTimeout(handle); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, devSelId, devPendName]);

  var submit = async function() {
    // Block any second entry within the same async submission — ref check
    // runs synchronously so it wins races that the React state can't.
    if (inflight.current) return;
    if (!form.name||!form.phone) return;
    if (!isReq && !form.source) { alert("Please select a source"); return; }
    if (isEOIForm && !form.budget) { alert("Please enter the Amount (EGP)"); return; }
    if (isEOIForm && !String(form.project||"").trim()) { alert("Please enter the Project"); return; }
    if (isEOIForm && !form.eoiDeposit) { alert("Please enter the Deposit (EGP)"); return; }
    if (isEOIForm && !String(form.eoiDate || "").trim()) { alert("EOI Date is required"); return; }
    if (!p.editId && (isEOIForm || isDoneDealForm) && !devSelId && !devPendName.trim()) { alert("Please pick a developer or enter a new name"); return; }   // N3
    // Phase R-13 — broker split is now informal (commissionTaxPct may be 0).
    // Only brokerSharePct is required > 0 for External; broker dropdown still
    // required. State tax is handled by the canonical preview flow off
    // commissionAmount = budget × commissionRate / 100, not these fields.
    if (isDoneDealForm && form.dealType === "external") {
      if (!form.externalBrokerId) { alert("Please select a broker for this external deal"); return; }
      var taxPct = Number((form.externalDealConfig && form.externalDealConfig.commissionTaxPct) || 0);
      var brkPct = Number((form.externalDealConfig && form.externalDealConfig.brokerSharePct)   || 0);
      if (!(taxPct >= 0 && taxPct <= 100)) { alert("Broker pre-deduction % must be between 0 and 100"); return; }
      if (!(brkPct > 0 && brkPct <= 100)) { alert("Broker Share % must be between 0 and 100"); return; }
      if (form.externalSalesAgentEnabled) {
        if (!form.externalSalesAgentId) { alert("Please select the second sales agent (Sales Agent involved toggle is ON)"); return; }
        var manualRaw = String(form.externalSalesAgentManualAmount || "").replace(/,/g, "").trim();
        var manualNum = Number(manualRaw);
        if (!manualRaw || !isFinite(manualNum) || manualNum <= 0) {
          alert("Please enter the manual commission amount for the second sales agent");
          return;
        }
      }
    }
    // Phase R-14 — ambassador validation. Commission Rate is enforced by the
    // shared DoneDeal check below; here we only validate the developer-tax rate
    // when the toggle is ON (mirrors the backend validator).
    if (isDoneDealForm && form.dealType === "ambassador") {
      var ambCfg = form.ambassadorConfig || {};
      if (ambCfg.developerTaxEnabled) {
        var dtRaw = String(ambCfg.developerTaxRate || "").trim();
        var dtNum = Number(dtRaw);
        if (!dtRaw || !isFinite(dtNum) || dtNum <= 0 || dtNum > 100) {
          alert("Please enter a valid Developer Tax Rate (%) between 0 and 100, or uncheck the developer-tax option");
          return;
        }
      }
    }
    // [F1] Deal Date ≡ Contraction Date (unified — Yosra). There is no separate
    // Deal Date input anymore; the Contraction Date is the single source and is
    // validated with the other sale fields on create (below). On submit we
    // mirror it into dealDate (the backbone), so no standalone check is needed.
    // Unit Price (budget) + Unit Code — required for the Add-Deal (Done Deal) CREATE
    // path (PATH A). Not enforced on edit: existing deals aren't force-backfilled
    // (the admin commission block below still re-checks budget > 0 on any admin save).
    if (isDoneDealForm && !p.editId) {
      var unitPriceNum = (function(){ var s = String(form.budget || "").replace(/,/g, "").replace(/[^0-9.]/g, ""); var n = parseFloat(s); return isFinite(n) ? n : 0; })();
      if (!(unitPriceNum > 0)) { alert("Please enter a valid Unit Price (EGP) greater than 0"); return; }
      if (!String(form.unitCode || "").trim()) { alert("Please enter the Unit Code (e.g. B1-204)"); return; }
      // Full sale form — ALL REQUIRED on create (Yosra). Mirrors the backend guards.
      if (!String(form.project || "").trim()) { alert("Please enter the Project"); return; }
      if (!String(form.unitType || "").trim()) { alert("Please select a Unit Type"); return; }
      if (form.saleType !== "Primary" && form.saleType !== "Resale") { alert("Please select a Sale Type"); return; }
      if (form.saleType === "Resale") {
        // Resale — direct full-payment: no down payment / installments; a single Deal Date.
        if (!String(form.dealDate || "").trim()) { alert("Please enter the Deal Date"); return; }
        // Two-party — at least one with us; each with-us party needs name/phone/agent/commission ≤ price.
        var rpForm = form.resaleParties || {};
        var withCount = 0;
        var chkParty = function(side, lbl){
          var pty = rpForm[side] || {};
          if (!pty.withUs) return true;
          withCount++;
          if (!String(pty.name||"").trim())    { alert(lbl+" name is required (it's with us)"); return false; }
          if (!String(pty.phone||"").trim())   { alert(lbl+" phone is required (it's with us)"); return false; }
          if (!String(pty.agentId||"").trim()) { alert(lbl+" sales agent is required (it's with us)"); return false; }
          var ca=(function(){var s=String(pty.commissionAmount||"").replace(/,/g,"").replace(/[^0-9.]/g,"");var n=parseFloat(s);return isFinite(n)?n:0;})();
          if (!(ca>0))              { alert(lbl+" commission amount is required (it's with us)"); return false; }
          if (ca>unitPriceNum)      { alert(lbl+" commission cannot exceed the Unit Price"); return false; }
          return true;
        };
        if (!chkParty("seller","Seller")) return;
        if (!chkParty("buyer","Buyer")) return;
        if (withCount < 1) { alert("At least one party (seller or buyer) must be with us"); return; }
      } else {
        var dpNum = (function(){ var s = String(form.downPayment || "").replace(/,/g, "").replace(/[^0-9.]/g, ""); var n = parseFloat(s); return isFinite(n) ? n : 0; })();
        if (!(dpNum > 0)) { alert("Please enter the Down Payment (EGP)"); return; }
        if (!String(form.downPaymentDate || "").trim()) { alert("Please enter the Down Payment Date"); return; }
        if (!String(form.reservationDate || "").trim()) { alert("Please enter the Reservation Date"); return; }
        if (!String(form.contractionDate || "").trim()) { alert("Please enter the Contract Date"); return; }
        if (!String(form.installmentYears || "").trim()) { alert("Please enter the Installments (years)"); return; }
      }
    }
    // Commission Rate (%) — required only for the deal types that DEFINE
    // themselves with it (owner ruling 2026-09-02).
    //
    // INTERNAL NO LONGER ASKS. The field is not rendered for an internal deal
    // (see the block below the Deal Type selector), and a check for a field the
    // form does not show is a refusal the user cannot act on — which is exactly
    // what this was: it fired on EDIT too, on a value seeded from the Deal
    // record's unmaintained birth snapshot, blank on half of production.
    // Internal commissions are created at close and completed on the Commissions
    // page, the same path a sales-closed deal has always taken.
    //
    // EXTERNAL and AMBASSADOR still require it, because the broker split is a
    // percentage of it and the ambassador gross is computed from it — neither
    // deal can be described without it. Unit Price stays required for both, for
    // the same reason: it is the base the rate multiplies.
    // AND NOT WHILE IT IS LOCKED. Once a commission exists the field is
    // read-only, so demanding a value the user cannot type is a refusal with no
    // available remedy — the form would simply stop saving. The stored value
    // rides along unchanged and the server writes nothing into the commission
    // from here regardless.
    var rateDefinesDeal = form.dealType === "external" || form.dealType === "ambassador";
    if (isDoneDealForm && isOnlyAdmin && rateDefinesDeal && form.saleType !== "Resale" && commissionLock !== true) {
      var budgetCheck = (function(){
        var s = String(form.budget || "").replace(/,/g, "").replace(/[^0-9.]/g, "");
        var n = parseFloat(s);
        return isFinite(n) ? n : 0;
      })();
      if (!(budgetCheck > 0)) {
        alert("Please enter a Unit Price (EGP) greater than 0 first — the commission is computed against it");
        return;
      }
      var crInputStr = String(form.commissionRate || "").trim();
      var crInputNum = Number(crInputStr);
      if (!crInputStr || !isFinite(crInputNum) || crInputNum <= 0 || crInputNum > 100) {
        alert("Please enter a valid Commission Rate (%) between 0 and 100");
        return;
      }
    }
    inflight.current = true;
    setSaving(true);
    try {
      // Legacy/former-agent: a typed name means NO real user is credited — send
      // legacyAgentName and force agentId empty. Otherwise send the picked agentId
      // and an empty legacyAgentName (so switching back to a real agent clears it).
      var legacyNm = (legacyAgentOpen ? String(form.legacyAgentName||"").trim() : "");
      var payload = Object.assign({}, form, { source: isReq?"Daily Request":form.source, agentId: legacyNm ? null : (form.agentId||""), legacyAgentName: legacyNm, status: p.editId ? (form.status||"Potential") : (p.initialStatus||form.status||"NewLead"), phone2: form.phone2||"" });
      // A record-backed row shows the RECORD's agent (read-only, above) and
      // must not carry the lead's live holder back to the server as if it were
      // an edit — see recordAgentField.
      if (p.txnRow && p.txnRow._fromRecord) { delete payload.agentId; delete payload.legacyAgentName; }
      // Field unification (Yosra): Deal Date ≡ Contraction Date. The Contraction
      // Date input is the single source — mirror it into dealDate (the backbone
      // read by quarter filters / TargetPeriods / leaderboard / commission
      // snapshot). Only overwrite when present so a legacy deal edited without a
      // contraction date keeps its existing dealDate. The backend future-check on
      // dealDate still fires (dealDate is populated = the contraction value).
      if (isDoneDealForm && String(form.contractionDate||"").trim()) {
        payload.dealDate = String(form.contractionDate).trim();
      }
      // New leads created here use the corrected field model (project = development,
      // unitType = unit, campaign = ad campaign) → mark v2. Edits leave the existing
      // flag untouched (not sent) so legacy leads stay v1 until the bulk migration.
      if (!p.editId) payload.fieldsV2 = true;
      // Developer (N3) — create-mode EOI/DoneDeal only: send exactly one of
      // developerId / developerPending (N1's backend require accepts either).
      if (!p.editId && (isEOIForm || isDoneDealForm)) {
        if (devSelId) payload.developerId = devSelId;
        else if (devPendName.trim()) payload.developerPending = devPendName.trim();
      }
      // Commission normalization. Primary: FE sends commissionRate only; BE
      // recomputes commissionAmount = budget × rate/100 (client amount stripped).
      // Resale: amount (EGP) is canonical (no tax) — send it; BE derives the rate.
      // The resale branch is the ONLY place a client commissionAmount is sent.
      (function(){
        if (isDoneDealForm && form.saleType === "Resale") {
          // Two-party resale — normalize resaleParties (agentId string, numeric
          // commission) and derive the required Lead name/phone from the first
          // with-us party. Deal Type is locked to Internal; the single lead-level
          // commission fields are cleared (commissions are per-party).
          var rpF = form.resaleParties || {};
          var mnum = function(v){ var s=String(v||"").replace(/,/g,"").replace(/[^0-9.]/g,""); var n=parseFloat(s); return isFinite(n)?n:0; };
          var normP = function(pty){ pty=pty||{}; var withUs=!!pty.withUs; return { withUs:withUs, name:String(pty.name||"").trim(), phone:String(pty.phone||"").trim(), agentId:withUs?(pty.agentId||""):"", commissionAmount:withUs?mnum(pty.commissionAmount):0, commissionRate:withUs?mnum(pty.commissionRate):0 }; };
          var seller = normP(rpF.seller), buyer = normP(rpF.buyer);
          payload.resaleParties = { seller: seller, buyer: buyer };
          var lead1 = seller.withUs ? seller : (buyer.withUs ? buyer : seller);
          payload.name = lead1.name || form.name || "Resale";
          payload.phone = lead1.phone || form.phone || "-";
          payload.dealType = "internal";
          payload.commissionRate = null; payload.commissionAmount = null;
          return;
        }
        var s = String(payload.commissionRate || "").replace(/,/g, "").trim();
        var n = Number(s);
        payload.commissionRate = (s && isFinite(n) && n > 0 && n <= 100) ? n : null;
        delete payload.commissionAmount;
      })();
      // External sales agent — coerce manual amount + agent id + paidBy.
      (function(){
        if (payload.externalSalesAgentEnabled) {
          var s = String(payload.externalSalesAgentManualAmount || "").replace(/,/g, "").trim();
          var n = Number(s);
          payload.externalSalesAgentManualAmount = (s && isFinite(n) && n > 0) ? n : null;
          // Phase R-13.2 — paidBy enum normalize. Default "company"; "broker"
          // routes the sales-agent amount out of the broker's share.
          var pb = String(payload.externalSalesAgentPaidBy || "company");
          payload.externalSalesAgentPaidBy = (pb === "broker") ? "broker" : "company";
        } else {
          payload.externalSalesAgentId           = null;
          payload.externalSalesAgentManualAmount = null;
          payload.externalSalesAgentPaidBy       = "company";
        }
      })();
      // Phase R-14 — ambassadorConfig coercion. developerTaxRate is a string in
      // the input; send a number (or null when disabled). The backend validator
      // re-normalizes + mirrors commissionRate into ambassadorConfig.commissionRate,
      // and clears the whole sub-config when dealType !== "ambassador".
      (function(){
        var amb = payload.ambassadorConfig || {};
        var on = !!amb.developerTaxEnabled;
        var r = Number(String(amb.developerTaxRate || "").replace(/,/g, "").trim());
        payload.ambassadorConfig = {
          // THE MIRROR RIDES ALONG, OR THE COMMISSION LOCK FIRES ON EVERY SAVE.
          //
          // The server's lock deep-compares this object against the stored one
          // (ambCfgChanged in PUT /api/leads/:id): it reads
          // `Number(n.commissionRate || 0) !== Number(o.commissionRate || 0)`.
          // This block used to omit the key entirely, so the left side was always
          // 0 while the right side was the mirror the server's OWN validator had
          // written — the two could never agree on an Ambassador deal that has a
          // rate, hasNoiseInBody was true on every request, and EVERY admin save
          // was refused 409 deal_type_locked before validateAndNormalizeExternalDeal
          // (which would have re-derived the mirror) ever ran. Nothing on lead
          // #1003 could be edited: not a note, not a date, not the client's name.
          //
          // SENT ONLY FOR AN AMBASSADOR DEAL, and that condition is the whole
          // correctness argument. The server clears this sub-config for internal
          // and external (clearAmbassador sets commissionRate:null), so mirroring
          // the canonical rate unconditionally would invert the bug rather than
          // fix it: an internal deal carrying a 4.25% rate would submit 4.25
          // against a stored null and 409 every internal edit instead. What makes
          // the compare honest is that this expression reproduces the server's own
          // normalization exactly, not that it carries a value.
          //
          // payload.commissionRate is already coerced to a number-or-null by the
          // commission block above, and is the same field the server mirrors from.
          commissionRate: (payload.dealType === "ambassador" && payload.commissionRate != null)
            ? payload.commissionRate
            : null,
          developerTaxEnabled: on,
          developerTaxRate: (on && isFinite(r) && r > 0) ? r : null
        };
      })();
      // Strip client-only fields the API doesn't need
      delete payload.documentFiles;
      // Sale-form fields belong ONLY to Done-Deal forms. Strip them on every
      // other form (regular Lead / EOI / Daily Request) so an edit or create
      // there never persists a stray saleType/down-payment onto a non-deal lead.
      // (form defaults saleType:"Primary", so without this a plain lead edit
      // would silently write it.) Now that these are real schema fields, the
      // strict-mode drop no longer protects us.
      if (!isDoneDealForm) {
        ["saleType","downPayment","downPaymentPct","downPaymentDate","reservationDate","contractionDate","installmentYears"].forEach(function(k){ delete payload[k]; });
      }
      // resaleParties is a Resale-only structure. The form always seeds a skeleton
      // (agentId:"") for controlled inputs; sending it on a Primary/plain lead makes
      // the backend CastError on the empty agentId (BSONError). Only send it for an
      // actual Resale deal — the backend also sanitizes as defense-in-depth.
      if (!(isDoneDealForm && form.saleType === "Resale")) delete payload.resaleParties;
      // Managerial roles editing an existing lead: strip notes/lastFeedback
      // — the backend now hard-rejects those fields via PUT for these roles
      // (admin/operation/manager). They use the dedicated feedback flow on
      // the lead detail panel instead. operation is fully read-only on
      // feedback so this strip applies to all three. team_leader is allowed
      // through PUT only when the lead is their own personal book; otherwise
      // they should use the feedback button as well — strip here to avoid
      // hitting the backend 400 on team-lead edits.
      if (p.editId && p.cu) {
        var cuRole = p.cu.role;
        var leadAgentId = (p.initial && p.initial.agentId && p.initial.agentId._id ? p.initial.agentId._id : (p.initial && p.initial.agentId)) || "";
        var tlOnOwn = cuRole === "team_leader" && String(leadAgentId) === String(p.cu.id || "");
        var stripFeedback = (cuRole === "admin" || cuRole === "operation" || cuRole === "manager")
          || (cuRole === "team_leader" && !tlOnOwn);
        if (stripFeedback) {
          delete payload.notes;
          delete payload.lastFeedback;
        }
      }
      // BUG #5 — pre-submit guard on create. The BE returns 400 anyway
      // (deal_date_future), but surfacing it before the round-trip is
      // nicer UX. Skipped on edit: admin/operation are allowed to
      // correct dealDate to any value (incl. future) via PUT; non-admin
      // edits are stripped silently on the server.
      if (!p.editId && payload.dealDate) {
        var cairoToday5 = cairoTodayIso();   // must agree with the server guard (server.js cairoTodayCreate)
        if (String(payload.dealDate).slice(0,10) > cairoToday5) {
          alert("Deal date cannot be in the future");
          inflight.current = false;
          setSaving(false);
          return;
        }
      }
      // Keep deal metadata in payload so it saves to DB
      var result;
      if (p.editId) {
        // The ✏️ form is seeded from a list ROW, so it must save to that row's
        // transaction. A historical (cancelled) record goes to the record-only
        // route; a live one carries txnId so the server mirrors the right record
        // instead of guessing from the lead's status. p.txnRow is the row the
        // form was opened from.
        if (isHistoricalRow(p.txnRow)) {
          // SPLIT THE SAVE — identity to the lead, the transaction to the record.
          //
          // This used to POST the whole payload to PATCH /api/records/:id and
          // then merge that same payload into the row. But that route writes
          // ONLY record fields, and no record has a home for name/phone (see the
          // birth-provenance comment on Eoi.clientName). So a rename on a
          // cancelled row was dropped server-side, rendered as saved by the merge
          // below, survived until the next refetch, and had been written nowhere.
          // The response said ok:true the whole time.
          //
          // Identity is the CLIENT's and lives on the lead, so it goes to the
          // lead — with NO txnId, because a rename is not scoped to a
          // transaction and naming a closed record on that route is a deliberate
          // 409. An identity-only body mirrors nothing (txnPatchFromLeadBody
          // returns {}), so this cannot disturb the lead's live transaction.
          //
          // What gets merged into the row afterwards is then exactly what was
          // written — which is the property the old code lacked.
          //
          // Only the keys that actually CHANGED are sent. Posting all four
          // unconditionally would blank a field the form never loaded, and
          // comparing against the row first makes an unloaded field a non-change
          // so it is never sent at all.
          //
          // The original reason given here — "the EOI / Deal list projections
          // carry no email or phone2" — is no longer true: EOI_LIST_FIELDS
          // selects both, and DEAL_LIST_FIELDS extends it. The compare-first
          // rule stays anyway, because it is the general guard against writing
          // "" over a stored value from any input the form did not load, and it
          // must not depend on which fields a projection happens to carry today.
          var IDENT_KEYS = ["name","phone","phone2","email"];
          var identPatch = {}, recBody = {};
          Object.keys(payload).forEach(function(k){
            // A lifecycle key is never a save's to write onto a record row —
            // the route ignores it and the merge must not see it either.
            if (TXN_LEAD_LIFECYCLE_KEYS.indexOf(k) !== -1) return;
            if (IDENT_KEYS.indexOf(k) === -1) { recBody[k] = payload[k]; return; }
            if (String(payload[k]==null?"":payload[k]) !==
                String(p.txnRow[k]==null?"":p.txnRow[k])) identPatch[k] = payload[k];
          });
          var identChanged = Object.keys(identPatch).length > 0;
          await apiFetch("/api/records/"+rowKey(p.txnRow), "PATCH", recBody, p.token, p.csrfToken);
          if (identChanged) {
            await apiFetch("/api/leads/"+p.editId, "PUT", identPatch, p.token, p.csrfToken);
          }
          inflight.current = false; setSaving(false);
          if (p.onSave) p.onSave(Object.assign({}, p.txnRow, recBody,
            identPatch, { _recordPatched: true }));
          if (p.onClose) p.onClose();
          return;
        }
        // Same rule as saveRowEdit: txnId names a RECORD, so only a record row
        // may set one. A branch-B row's rowKey() is its lead's own _id.
        if (p.txnRow && p.txnRow._fromRecord) payload = Object.assign({}, payload, { txnId: rowKey(p.txnRow) });
        try {
          result = await apiFetch("/api/leads/"+p.editId, "PUT", payload, p.token, p.csrfToken);
          var mw1 = mirrorWarning(result); if (mw1) alert(mw1);
        } catch (lockErr) {
          // MONEY LOCK (BE: deal_fields_locked). Editing budget / project /
          // unitType on an ALREADY-CLOSED deal is refused unless an admin
          // confirms, so a second purchase can never silently overwrite the
          // first. Re-submit once with the intent flag if the user confirms;
          // a non-admin gets the server's message and no retry.
          if (!lockErr || lockErr.code !== "deal_fields_locked") throw lockErr;
          var lockMsg = String(lockErr.message || "This deal is closed.");
          if (!isOnlyAdmin) { alert(lockMsg); inflight.current = false; setSaving(false); return; }
          if (!window.confirm(lockMsg + "\n\nContinue and change this closed deal anyway?")) {
            inflight.current = false; setSaving(false); return;
          }
          result = await apiFetch("/api/leads/"+p.editId, "PUT",
            Object.assign({}, payload, { confirmDealFieldEdit: true }), p.token, p.csrfToken);
          var mw2 = mirrorWarning(result); if (mw2) alert(mw2);
        }
      } else {
        result = await apiFetch("/api/leads", "POST", payload, p.token, p.csrfToken);
      }
      // (Removed the localStorage "deal extra" backup — downPayment / downPaymentPct
      // / installmentYears / dealDate now persist server-side on the Lead, so the
      // old per-browser cache was a duplicate parallel store.)
      if (payload.phone2) {
        result.phone2 = payload.phone2;
        // Cache phone2 in localStorage
        try {
          var cache = JSON.parse(localStorage.getItem('phone2_cache')||'{}');
          if (result._id) cache[String(result._id)] = payload.phone2;
          localStorage.setItem('phone2_cache', JSON.stringify(cache));
        } catch(e) {}
      }
      // Upload any documents attached in the form (EOI or DoneDeal).
      //
      // THE BUCKET IS THE LABEL (owner ruling 2026-08-24). This picker renders as
      // "📎 Upload Deal Documents" on a Done-Deal form and every one of those
      // files used to be POSTed to /eoi-documents, so it was stored bucket:"eoi"
      // — a write that says one thing and means another. Nothing was corrupt; the
      // file simply belonged to a panel it had not been filed under, and stayed
      // reachable only because reownStrandedArtifacts rescues it at record
      // creation. A write that depends on a later rescue is the architecture
      // inverted: the rescue is the safety net, not the contract. Route by what
      // the form actually is and the net stops being load-bearing.
      var docEndpoint = isDoneDealForm ? "deal-documents" : "eoi-documents";
      if (result && result._id && Array.isArray(form.documentFiles) && form.documentFiles.length>0) {
        for (var di=0; di<form.documentFiles.length; di++) {
          var ff = form.documentFiles[di];
          if (!ff || !ff.fileData) continue;
          try {
            var withDocs = await apiFetch("/api/leads/"+gid(result)+"/"+docEndpoint,"POST",{fileData:ff.fileData, fileName:ff.fileName||""},p.token);
            if (withDocs && withDocs._id) result = withDocs;
          } catch(docErr) { console.error("Document upload failed:", docErr.message); }
        }
      }
      if (!p.editId) clearLeadDraft(draftKey); // draft consumed on successful add
      p.onSave(result);
    } catch(e) { alert(e.message); }
    inflight.current = false;
    setSaving(false);
  };

  // Field blocks shared between the Done-Deal sale form (sectioned Client /
  // Property / Sales layout) and the regular Add-Lead / EOI form. Extracted so
  // both can place them without duplicating the JSX.
  var sourceField = !isReq ? <Inp label={t.source} req type="select" value={form.source} onChange={function(e){upd("source",e.target.value);}} options={buildSourceOptions(form.source)}/> : null;
  // ONE SOURCE FOR THE AGENT ON A RECORD-BACKED ROW (owner ruling 2026-09-11).
  // The picker below is bound to form.agentId — the LEAD's live holder, which on
  // a rotated lead is a different person from the record's author the list row
  // shows (#50058: Rokaya Swidan, inactive, vs Jana Mohamed). The record's agent
  // is not an editable field of this form (TXN_EDITABLE_* carries no agentId and
  // an author is per-action attribution, frozen at creation), so on a record row
  // the control is the SAME RESOLVED NAME the row shows, read-only, and the
  // payload carries no agentId at all — this form does not reassign a lead
  // through a transaction's edit. A plain lead keeps the picker.
  var recordAgentField = (p.txnRow && p.txnRow._fromRecord)
    ? <Inp label={t.agent} readOnly value={txnAgentNameOf(p.txnRow, p.users) || "—"}
        title="The agent who made this record. Attribution is per action and is not edited here."/>
    : null;
  var agentField = isAdmin ? (recordAgentField || <div style={{ marginBottom:13 }}>
    {!legacyAgentOpen && <Inp label={t.agent} searchable type="select" value={form.agentId} onChange={function(e){upd("agentId",e.target.value);}} options={[{value:"",label:"- Select -"}].concat(withCurrentOption((function(){
      var agentList = salesUsers;
      if (isEOIForm || isDoneDealForm) {
        var amgadUsers = (p.users || []).filter(function(u){ return u.username === "amgad" && u.active; });
        amgadUsers.forEach(function(a){
          if (!agentList.some(function(u){ return gid(u) === gid(a); })) agentList = agentList.concat([a]);
        });
      }
      return agentList;
    })().map(function(u){return{value:gid(u),label:u.name+" - "+u.title};}), form.agentId, (userNameOf(form.agentId, p.users) ? userNameOf(form.agentId, p.users) + " (inactive)" : "Unknown")))}/>}
    {/* Legacy/former-agent free-text input — for historical deals whose agent has
        NO user account. Filling it credits no real user (agentId stays empty). */}
    {legacyAgentOpen && <Inp label={t.agent + " — legacy name"} value={form.legacyAgentName||""}
      onChange={function(e){ upd("legacyAgentName", e.target.value); }}
      placeholder="Type the former agent's name (no user account)"/>}
    {/* Toggle — only on the Add Deal form (mirrors the developer-pending toggle). */}
    {isDoneDealForm && <button type="button" onClick={function(){
      if (legacyAgentOpen) { setLegacyAgentOpen(false); upd("legacyAgentName", ""); }
      else { setLegacyAgentOpen(true); upd("agentId", ""); }
    }} style={{ marginTop:6, background:"none", border:"none", padding:0, color:C.info, fontSize:12, cursor:"pointer", textDecoration:"underline", textAlign:"left" }}>
      {legacyAgentOpen ? "← Pick an existing agent instead" : "Agent not in the list? Enter name manually"}
    </button>}
  </div>) : null;
  var developerBlock = ((isEOIForm||isDoneDealForm)&&!p.editId) ? <div style={{ marginBottom:13 }}>
    <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>🏗️ Developer <span style={{color:C.danger}}>*</span></label>
    {!devPendOpen && <SearchableSelect
      value={devSelId}
      emptyLabel="— Select developer —"
      placeholder="Search developers…"
      width="100%" maxWidth={9999} fontSize={13}
      options={(devList||[]).map(function(dv){ return { value:String(dv._id), label:dv.name }; })}
      onChange={function(val){ setDevSelId(val); if(val) setDevPendName(""); }}/>}
    {devPendOpen && <input type="text" value={devPendName} placeholder="New developer name"
      onChange={function(e){ setDevPendName(e.target.value); setDevSelId(""); }}
      style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>}
    <button type="button" onClick={function(){ if(devPendOpen){ setDevPendOpen(false); setDevPendName(""); } else { setDevPendOpen(true); setDevSelId(""); } }}
      style={{ marginTop:6, background:"none", border:"none", padding:0, color:C.info, fontSize:12, cursor:"pointer", textDecoration:"underline", maxWidth:"100%", textAlign:"left", whiteSpace:"normal" }}>
      {devPendOpen ? "← Pick from the list instead" : "Can't find the developer? Enter a new name"}
    </button>
  </div> : null;
  var campaignField = <Inp label="📣 Ad Campaign Name" value={form.campaign||""} onChange={function(e){upd("campaign",e.target.value);}} placeholder="e.g. Cali Coast – June – Lookalike"/>;
  // THE STATUS PICKER IS DEFINED ONCE AND RENDERED BY BOTH BRANCHES.
  //
  // Picking "Done Deal" flips isDoneDealForm, and the whole form below is a
  // ternary on that flag — so the entire Add-Lead layout is replaced by the
  // sale form. The Status select lived only in the else branch, which meant
  // choosing Done Deal DELETED THE CONTROL THAT CHOSE IT: no way back to any
  // other status, and no way to see what was selected. Not an early return —
  // a layout swap that swallowed the field.
  //
  // Defined as a variable rather than duplicated, matching this form's own
  // idiom (campaignField / sourceField / agentField / developerBlock are all
  // one definition placed in both branches). A second copy of the options list
  // is a second answer to "what statuses can be chosen".
  var statusField = showStatusPicker ? <Inp label="Status" type="select" value={form.status||"NewLead"} onChange={function(e){upd("status",e.target.value);}} options={[
    {value:"NewLead",label:"New Lead"},
    {value:"Potential",label:"Potential"},
    {value:"HotCase",label:"Hot Case"},
    {value:"EOI",label:"EOI"},
    {value:"DoneDeal",label:"Done Deal"}
  ]}/> : null;

  // ===== ONE FORM LANGUAGE — the grid the DR New Request form uses ==========
  // Same template, same gap, same collapse as drFormGrid (DailyRequestsPage):
  // minmax(0,1fr) so a long option label cannot push a column past its share and
  // overflow the modal, gap "0 12px" because every field already carries its own
  // 13px bottom margin, and a single column under p.isMobile — two 218px columns
  // inside a 500px modal is the squeezed layout this pattern exists to avoid.
  var lfGrid = { display:"grid", gridTemplateColumns: p.isMobile ? "minmax(0,1fr)" : "minmax(0,1fr) minmax(0,1fr)", gap:"0 12px" };
  var lfFull = { gridColumn:"1/-1" };
  // A status section is a full-span nested grid, so its own fields pair with each
  // other and never with a base field above them.
  var lfSub  = Object.assign({}, lfGrid, lfFull);
  // A PAIR THAT SURVIVES ITS OWN CONDITIONS. Half of these fields are optional
  // (source hides on a Daily-Request add, status on the forced-status pages, the
  // agent picker for a sales user), and a pair whose partner is absent used to
  // leave a lone half-width control beside dead space. Given one, it spans.
  // Fragments are not DOM nodes, so both children stay direct grid items.
  var lfPair = function(a, b){
    if (a && b) return <>{a}{b}</>;
    var only = a || b;
    return only ? <div style={lfFull}>{only}</div> : null;
  };
  var lfHdr = function(txt){ return <div style={Object.assign({}, lfFull, { fontSize:12, fontWeight:800, color:C.textLight, textTransform:"uppercase", letterSpacing:".4px", margin:"4px 0 8px" })}>{txt}</div>; };

  // ===== VALUES THE STATUS SECTIONS SHARE ==================================
  // Lifted out of the Done-Deal IIFE that used to own them, because the sections
  // are no longer a separate form — they append to the one base form.
  var lfNum = function(v){ var s=String(v||"").replace(/[^0-9.]/g,""); var n=parseFloat(s); return isFinite(n)?n:0; };
  var isResale = form.saleType === "Resale";
  // A Done Deal's close date can't be in the future (dealDate feeds quarter
  // attribution). Capped on create; edit is unconstrained so admins can correct.
  var maxDay = p.editId ? undefined : cairoTodayIso();
  var dpPriceN = lfNum(form.budget), dpPaidN = lfNum(form.downPayment);
  var dpPct = (dpPriceN>0&&dpPaidN>0) ? (Math.round((dpPaidN/dpPriceN)*10000)/100) : null;
  // ONE Amount field for every status — the label is what changes, not the field.
  // It is bound to form.budget in all three states, so the base form can keep it
  // (issue 2: it must never disappear) without the sale section duplicating it.
  var amountLabel = isDoneDealForm ? "💰 Unit Price (EGP)" : (isEOIForm ? "💰 Amount (EGP)" : t.budget);

  // TWO-PARTY RESALE — seller and buyer, each with a with-us toggle and, when
  // with us, its own agent and its own linked commission amount⇄%. Lifted out of
  // the Done-Deal IIFE unchanged apart from the grid it lays out on; each party
  // is a full-span card whose own fields pair inside it.
  var resaleAgentOpts = [{value:"",label:"- Select -"}].concat((salesUsers||[]).map(function(u){return {value:gid(u),label:u.name+" - "+u.title};}));
  var renderResaleParty = function(side, title, color){
    var party=(form.resaleParties&&form.resaleParties[side])||{};
    var withUs=!!party.withUs;
    var amtDisp=party.commissionAmount?Number(String(party.commissionAmount).replace(/,/g,"")).toLocaleString():"";
    return <div style={{ border:"1px solid "+color+"33", borderRadius:12, padding:"12px 14px", marginBottom:12, background:color+"08" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <div style={{ fontSize:13, fontWeight:800, color:color }}>{title}</div>
        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:600, cursor:"pointer" }}>
          <input type="checkbox" checked={withUs} onChange={function(e){updRP(side,"withUs",e.target.checked);}}/> With us?
        </label>
      </div>
      <div style={lfGrid}>
        <Inp label="Name" req={withUs} value={party.name||""} onChange={function(e){updRP(side,"name",e.target.value);}}/>
        <Inp label="Phone" req={withUs} value={party.phone||""} onChange={function(e){updRP(side,"phone",e.target.value);}}/>
      </div>
      {withUs && <>
        {isAdmin && <Inp label="👤 Agent" req searchable type="select" value={party.agentId||""} onChange={function(e){updRP(side,"agentId",e.target.value);}} options={withCurrentOption(resaleAgentOpts, party.agentId, userNameOf(party.agentId, p.users) || "Unknown")}/>}
        <div style={lfGrid}>
          <Inp label="💰 Commission (EGP)" req value={amtDisp} onChange={function(e){
            var raw=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");
            updRP(side,"commissionAmount",raw?Number(raw).toLocaleString():"");
            var a=lfNum(raw), b=lfNum(form.budget);
            updRP(side,"commissionRate",(a>0&&b>0)?String(Math.round(a/b*10000)/100):"");
          }} placeholder="e.g. 60,000"/>
          <Inp label="Commission %" value={party.commissionRate||""} onChange={function(e){
            var raw=e.target.value.replace(/[^0-9.]/g,"");
            var fd=raw.indexOf("."); if(fd>=0) raw=raw.slice(0,fd+1)+raw.slice(fd+1).replace(/\./g,"");
            updRP(side,"commissionRate",raw);
            var r=lfNum(raw), b=lfNum(form.budget);
            updRP(side,"commissionAmount",(r>0&&b>0)?Number(Math.round(b*r)/100).toLocaleString():"");
          }} placeholder="e.g. 2"/>
        </div>
        <div style={{ marginTop:-4, fontSize:11, color:"#059669" }}>Tax-free · own agent's tier chain · collected in full (direct).</div>
      </>}
    </div>;
  };

  return <div>
    {draftRestored&&<div style={{ marginBottom:14, padding:"10px 14px", background:"#EFF6FF", borderRadius:10, fontSize:13, fontWeight:500, color:"#1E40AF", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
      <span>↩︎ Unsaved draft restored{draftHadAttach?" — re-attach any documents/images":""}</span>
      <button type="button" onClick={function(){ clearLeadDraft(draftKey); setDevSelId(""); setDevPendName(""); setLegacyAgentOpen(false); setDraftRestored(false); setDraftHadAttach(false); setForm(function(f){ return Object.assign({},f,{ name:"", phone:"", phone2:"", email:"", budget:"", project:"", unitCode:"", notes:"", eoiDeposit:"", eoiDate:"", dealDate:"", callbackTime:"", source:p.isReq?"Daily Request":"", agentId:"", legacyAgentName:"", downPaymentPct:"", installmentYears:"", commissionRate:"" }); }); }} style={{ flexShrink:0, background:"none", border:"none", padding:"2px 6px", color:C.danger, fontSize:12, fontWeight:700, cursor:"pointer", textDecoration:"underline" }}>Discard</button>
    </div>}
    {dupWarning&&<div style={{ marginBottom:14, padding:"10px 14px", background:"#FEF3C7", borderRadius:10, fontSize:13, fontWeight:500, color:"#B45309", display:"flex", alignItems:"center", gap:8 }}>
      <AlertCircle size={16}/> {t.duplicateFound} — <b>{dupWarning.name}</b>
    </div>}
    {/* ===== ONE FORM, ONE ORDER, IN EVERY STATUS =============================
        This was a ternary on isDoneDealForm: picking Done Deal REPLACED the
        whole form with a separate sale layout. Three things went wrong at once.
        The base form lost fields that have nothing to do with a deal (Email, the
        Amount input, Ad Campaign Name). The Status picker sat mid-form in every
        other state and was prepended to the top in Done Deal, so the control
        MOVED depending on what it was set to — you had to hunt for the field you
        had just used. And the two layouts had drifted into different form
        languages, neither of which matched the DR New Request modal.

        Now there is ONE base grid, always rendered, in a fixed order, and the
        status sections APPEND to it. Nothing a status adds can remove or move
        anything the base owns.

          Name                              (full)
          Phone            | Alt Phone
          Email            | Amount          <- label changes, field never moves
          Ad Campaign                       (full)
          Status           | Source          <- FIXED position, every state
          Project          | Unit Type
          Agent                             (full)
          [ EOI section    — appended       ]
          [ Deal section   — appended       ]
          Callback Time                     (full, pipeline statuses only)
          Notes                             (full)

        WHERE THIS DEPARTS FROM THE PAIRS AS DICTATED, and why: "Unit Price |
        Down Payment" and "Unit Type | Unit Code" cannot both hold while the base
        stays intact, because Amount (= Unit Price, the same form.budget) and
        Unit Type are BASE fields that must render in every state. Duplicating
        them in the sale section would be two inputs bound to one value. So Unit
        Code pairs with Down Payment, and the pairs shift by one. Everything
        else is as specified. ===================================================== */}
    <div style={lfGrid}>
      <div style={lfFull}><Inp label={t.name} req value={form.name} onChange={function(e){upd("name",e.target.value);}}/></div>
      <Inp label={t.phone} req value={form.phone} onChange={function(e){upd("phone",e.target.value);checkDup(e.target.value);}} placeholder=""/>
      <Inp label={t.phone2} value={form.phone2||""} onChange={function(e){upd("phone2",e.target.value);}} placeholder=""/>
      <Inp label={t.email} value={form.email} onChange={function(e){upd("email",e.target.value);}}/>
      <Inp label={amountLabel} req={isEOIForm||isDoneDealForm} value={form.budget} onChange={function(e){var raw=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");upd("budget",raw?Number(raw).toLocaleString():"");}}/>
      {/* Ad Campaign is UNCONDITIONAL now. It used to be hidden on the sale form
          unless the source was a paid-ad one, with an effect that wiped the value
          when it hid — so the field the owner expects on every add was missing in
          exactly the state where the campaign that produced the deal matters. */}
      <div style={lfFull}>{campaignField}</div>
      {/* THE FIXED SEAT. Status is the 5th row in every state; the sections below
          append, they never insert above it. lfPair spans it when there is no
          Source beside it (a Daily-Request add) so it is never a lone half. */}
      {lfPair(statusField, sourceField)}
      <Inp label={"🏢 " + t.project} req={isEOIForm || (isDoneDealForm && !p.editId)} value={form.project||""} onChange={function(e){upd("project",e.target.value);}} placeholder="e.g. Cali Coast"/>
      <Inp label="🏷️ Unit Type" type="select" value={form.unitType||""} onChange={function(e){upd("unitType",e.target.value);}} options={UNIT_TYPES.map(function(x){return {value:x, label:x||"- Select -"};})}/>
      {agentField ? <div style={lfFull}>{agentField}</div> : null}

      {/* ===== EOI SECTION — appended, never replacing ===== */}
      {isEOIForm&&<div style={lfSub}>
        {lfHdr("📄 EOI")}
        <Inp label="📅 EOI Date" type="date" req value={form.eoiDate||""} onChange={function(e){upd("eoiDate",e.target.value);}} max={p.editId?undefined:cairoTodayIso()}/>
        <Inp label="💵 Deposit (EGP)" req value={form.eoiDeposit||""} onChange={function(e){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");upd("eoiDeposit",r?Number(r).toLocaleString():"");}} placeholder=""/>
        {developerBlock ? <div style={lfFull}>{developerBlock}</div> : null}
      </div>}

      {/* ===== DEAL SECTION — appended, never replacing ===== */}
      {isDoneDealForm&&<div style={lfSub}>
        {lfHdr("🏠 Unit")}
        {isResale
          ? <div style={lfFull}><Inp label="🔖 Unit Code" req={!p.editId} value={form.unitCode||""} onChange={function(e){upd("unitCode",e.target.value);}} placeholder="e.g. B1-204"/></div>
          : <>
              <Inp label="🔖 Unit Code" req={!p.editId} value={form.unitCode||""} onChange={function(e){upd("unitCode",e.target.value);}} placeholder="e.g. B1-204"/>
              <Inp label="💵 Down Payment (EGP)" req={!p.editId} value={form.downPayment||""} onChange={function(e){var raw=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");upd("downPayment",raw?Number(raw).toLocaleString():"");}} placeholder="e.g. 500,000"/>
              <div style={Object.assign({}, lfFull, { marginTop:-6, marginBottom:12, fontSize:12, fontWeight:600, color:dpPct!=null?C.success:C.textLight })}>{dpPct!=null?("= "+dpPct+"% of unit price"):"= —% (enter Unit Price + Down Payment)"}</div>
            </>}
        {developerBlock ? <div style={lfFull}>{developerBlock}</div> : null}
        {lfHdr("💰 Sale")}
        <div style={lfFull}><Inp label="🏷️ Sale Type" req={!p.editId} type="select" value={form.saleType||"Primary"} onChange={function(e){upd("saleType",e.target.value);}} options={[{value:"Primary",label:"Primary"},{value:"Resale",label:"Resale"}]}/></div>
        {isResale
          ? <div style={lfFull}><Inp label="📅 Deal Date" req={!p.editId} type="date" value={form.dealDate||""} onChange={function(e){upd("dealDate",e.target.value);}} max={maxDay}/></div>
          : <>
              <Inp label="📅 Reservation Date" req={!p.editId} type="date" value={form.reservationDate||""} onChange={function(e){upd("reservationDate",e.target.value);}}/>
              <Inp label="📅 Down Payment Date" req={!p.editId} type="date" value={form.downPaymentDate||""} onChange={function(e){upd("downPaymentDate",e.target.value);}}/>
              <Inp label="📅 Contract Date" req={!p.editId} type="date" value={form.contractionDate||""} onChange={function(e){upd("contractionDate",e.target.value);}} max={maxDay}/>
              <Inp label="📆 Installments (years)" req={!p.editId} value={form.installmentYears||""} onChange={function(e){upd("installmentYears",e.target.value.replace(/[^0-9]/g,""));}} placeholder="e.g. 8"/>
            </>}
        {isResale&&<div style={Object.assign({}, lfFull, { fontSize:11, color:C.textLight, margin:"2px 0 10px" })}>A resale has two parties — set who's with us; each with-us party gets its own agent + commission. At least one must be with us.</div>}
        {isResale&&<div style={lfFull}>{renderResaleParty("seller","🔴 Seller","#B91C1C")}</div>}
        {isResale&&<div style={lfFull}>{renderResaleParty("buyer","🔵 Buyer","#1D4ED8")}</div>}
      </div>}

      {/* Callback is a pipeline field: meaningless once the lead is an EOI or a
          closed deal, which is exactly the condition it carried before. */}
      {(!isEOIForm&&!isDoneDealForm)&&<div style={lfFull}><Inp label={t.callbackTime} type="datetime-local" value={form.callbackTime} onChange={function(e){upd("callbackTime",e.target.value);}}/></div>}
      <div style={lfFull}><Inp label={t.notes} type="textarea" value={form.notes} onChange={function(e){upd("notes",e.target.value);}}/></div>
    </div>
    {/* Phase R-12 Part 3 — Deal Type toggle (Internal / External). Surfaces
        only on Done-Deal forms for admin/operation (matches backend gate).
        Edit-Deal can flip mid-stream; the backend write-site validator clears
        external fields when flipping back to internal so no stale config
        lingers on the lead. */}
    {isDoneDealForm&&isOnlyAdmin&&form.saleType!=="Resale"&&<div style={{ marginBottom:13, padding:"12px 14px", background:"#F8FAFC", borderRadius:10, border:"1px solid #E8ECF1" }}>
      <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:8 }}>Deal Type</label>
      {/* Phase R-12 Part 5 — commission-lock banner. Mirrors the backend
          409 from PUT /api/leads/:id when an active commission exists. */}
      {commissionLock===true&&<div style={{ marginBottom:10, padding:"8px 12px", background:"#FEF3C7", border:"1px solid #FDE68A", borderRadius:8, fontSize:12, color:"#92400E", lineHeight:1.5 }}>
        🔒 <b>Locked.</b> An active commission exists for this deal. Cancel the commission first if you need to change the deal type, broker, or split percentages.
      </div>}
      <div style={{ display:"flex", gap:8, marginBottom: (form.dealType==="external"||form.dealType==="ambassador") ? 14 : 0 }}>
        {/* Phase R-14 — third option "Ambassador" (amber). Per-option theme
            color: Internal blue, External violet, Ambassador amber. Switching
            type clears the other type's sub-config so the live preview + payload
            stay clean (the backend validator re-normalizes regardless). The
            Ambassador radio is gated to admin/operation by isOnlyAdmin, which
            already wraps this whole block. */}
        {[["internal","🏢 Internal","Managed by ARO team",C.accent],["external","🤝 External","Brought in by a broker","#7C3AED"],["ambassador","🎯 Ambassador","Manual team commissions","#D97706"]].map(function(opt){
          var active = form.dealType === opt[0];
          var locked = commissionLock===true;
          var theme = opt[3];
          return <button key={opt[0]} type="button" disabled={locked}
            title={locked ? "Locked — active commission exists" : ""}
            onClick={function(){
              if (locked) return;
              upd("dealType", opt[0]);
              if (opt[0] !== "external") {
                upd("externalBrokerId", "");
                upd("externalDealConfig", { commissionTaxPct: 0, brokerSharePct: 0 });
                upd("externalSalesAgentEnabled", false);
                upd("externalSalesAgentId", "");
                upd("externalSalesAgentManualAmount", "");
                upd("externalSalesAgentPaidBy", "company");
              }
              if (opt[0] !== "ambassador") {
                upd("ambassadorConfig", { developerTaxEnabled: false, developerTaxRate: "" });
              }
            }}
            style={{ flex:1, minWidth:0, padding:"10px 12px", borderRadius:9, border:"1px solid", borderColor: active ? theme : "#E2E8F0", background: active ? theme+"12" : "#fff", cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.55 : 1, textAlign:"left" }}>
            <div style={{ fontSize:13, fontWeight:600, color: active ? theme : C.text }}>{opt[1]}</div>
            <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>{opt[2]}</div>
          </button>;
        })}
      </div>

      {/* ── THE RATE BELONGS TO THE TYPES THAT DEFINE IT (owner ruling 2026-09-02) ──

          An INTERNAL deal has no commission rate on this form at all. Its
          commission is created at close and completed on the Commissions page —
          the same path a sales-closed deal has always taken. That is not a
          simplification of the old behaviour, it is the removal of a field that
          could not do its job: it was seeded from the Deal record's birth
          snapshot, which nothing maintains, so it rendered EMPTY on 25 of 50
          production deals while being marked required — an admin editing a date
          had to type a rate they could not see, and whatever they typed was
          written to the lead and propagated into cycle 1.

          EXTERNAL and AMBASSADOR keep it, because for them the rate is not a
          bookkeeping figure that lives downstream — it DEFINES the deal. The
          broker split is a percentage of it, and the ambassador gross is
          computed from it, so neither type can be recorded without it.

          IT MOVED BELOW THE TYPE SELECTOR, and that is the whole reason it can
          be conditional at all: the field now depends on an answer the user
          gives in this same block, so it has to come after the question. Left
          where it was, choosing External would have made an input appear ABOVE
          the radio just clicked. */}
      {(form.dealType === "external" || form.dealType === "ambassador") && <div style={{ marginTop:14 }}>
        {/* Commission input — saleType-aware. Primary: Rate (%) canonical, amount =
            budget × rate/100 (state-tax model applies downstream). Resale: linked
            amount ⇄ percent pair (BOTH stored; amount canonical) and the commission
            enters WHOLE — no VAT, no withholding. */}
        {(function(){
          var isResaleC = form.saleType === "Resale";
          var numC = function(v){ var s=String(v||"").replace(/,/g,"").replace(/[^0-9.]/g,""); var n=parseFloat(s); return isFinite(n)?n:0; };
          var budgetNum = numC(form.budget);
          var fmtMoney = function(n){ return n.toLocaleString(undefined, { maximumFractionDigits: 2 }); };
          if (isResaleC) {
            var g2C = { display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)", gap:"0 12px" };
            var amtDisplay = form.commissionAmount ? Number(String(form.commissionAmount).replace(/,/g,"")).toLocaleString() : "";
            return <>
              <div style={g2C}>
                <Inp label="💰 Commission Amount (EGP)" req value={amtDisplay} onChange={function(e){
                  var raw = e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");
                  upd("commissionAmount", raw ? Number(raw).toLocaleString() : "");
                  var a = numC(raw), b = numC(form.budget);
                  upd("commissionRate", (a>0 && b>0) ? String(Math.round(a/b*10000)/100) : "");
                }} placeholder="e.g. 150,000"/>
                <Inp label="Commission %" value={form.commissionRate || ""} onChange={function(e){
                  var raw = e.target.value.replace(/[^0-9.]/g,"");
                  var fd = raw.indexOf("."); if (fd>=0) raw = raw.slice(0,fd+1) + raw.slice(fd+1).replace(/\./g,"");
                  var parts = raw.split("."); var dp = parts.length>1 ? parts[1].slice(0,4) : "";
                  var formatted = parts[0]==="" ? (parts.length>1?"0."+dp:"") : (parts.length>1?parts[0]+"."+dp:parts[0]);
                  upd("commissionRate", formatted);
                  var r = numC(formatted), b = numC(form.budget);
                  upd("commissionAmount", (r>0 && b>0) ? Number(Math.round(b*r)/100).toLocaleString() : "");
                }} placeholder="e.g. 3"/>
              </div>
              <div style={{ marginTop:-4, marginBottom:12, padding:"6px 10px", background:"#ECFDF5", border:"1px solid #A7F3D0", borderRadius:6, fontSize:11, color:"#065F46" }}>
                🏷️ Resale — commission enters WHOLE (no VAT, no withholding). {budgetNum>0 && numC(form.commissionAmount)>0 ? <b>Net = {fmtMoney(numC(form.commissionAmount))} EGP</b> : "Enter Unit Price + an amount or %."}
              </div>
            </>;
          }
          return <>
            {/* READ-ONLY ONCE A COMMISSION EXISTS (owner ruling 2026-09-02). The
                rate DEFINES an External or Ambassador deal, so it is typed at
                creation — but once the commission exists the money is the
                Commission's, and nothing on this form writes into it any more.
                An input that still accepted typing would be offering an edit the
                server no longer performs, which is worse than no input: the user
                would believe they had changed something.
                `req` drops with it — a required field nobody can fill is a save
                the form refuses for a reason the user cannot act on. */}
            <Inp label="💰 Commission Rate (%)" req={commissionLock !== true}
              readOnly={commissionLock === true}
              title={commissionLock === true ? "Locked — an active commission exists. Change it on the Commissions page." : ""}
              value={form.commissionRate || ""} onChange={function(e){
              if (commissionLock === true) return;
              var raw = e.target.value.replace(/[^0-9.]/g, "");
              var firstDot = raw.indexOf("."); if (firstDot >= 0) raw = raw.slice(0, firstDot+1) + raw.slice(firstDot+1).replace(/\./g, "");
              var parts = raw.split("."); var intPart = parts[0] || ""; var hasDec = parts.length > 1; var decPart = hasDec ? parts[1].slice(0, 4) : "";
              upd("commissionRate", intPart === "" ? (hasDec ? "0." + decPart : "") : (hasDec ? intPart + "." + decPart : intPart));
            }} placeholder="e.g. 3"/>
          </>;
        })()}
      </div>}

      {form.dealType==="external"&&<div>
        <div style={{ marginBottom:13 }}>
          <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>Broker<span style={{ color:C.danger, marginLeft:3 }}>*</span></label>
          <Select value={form.externalBrokerId||""} disabled={commissionLock===true} searchable fontSize={14}
            title={commissionLock===true ? "Locked — active commission exists" : ""}
            onChange={function(e){
              if (e.target.value === "__add_new__") {
                setShowAddBroker(true);
                setNewBrokerName("");
                return;
              }
              upd("externalBrokerId", e.target.value);
            }}
            options={[{ value:"", label:(brokersLoaded ? "- Select Broker -" : "Loading brokers...") }]
              .concat(withCurrentOption(brokers.map(function(b){ return { value:gid(b), label:b.name + (b.archived?" (archived)":"") }; }),
                                        form.externalBrokerId, brokerNameOf(form.externalBrokerId, brokers) || (brokersLoaded ? "Unknown broker" : "Loading…")))
              .concat(commissionLock!==true ? [{ value:"__add_new__", label:"+ Add new broker" }] : [])}/>
        </div>

        {/* Phase R-13 — "Sales Agent involved" toggle (External only). When ON,
            a second sales agent (picked here) is paid a manual EGP amount on
            this deal. The Lead's primary agent is NOT paid commission on
            External deals regardless of this toggle (per-1000 chain disabled). */}
        <div style={{ marginBottom:13, padding:"10px 12px", background:"#fff", border:"1px solid #E2E8F0", borderRadius:10 }}>
          <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, fontWeight:600, color:C.text, cursor:"pointer" }}>
            <input type="checkbox" checked={!!form.externalSalesAgentEnabled}
              onChange={function(e){
                var on = e.target.checked;
                upd("externalSalesAgentEnabled", on);
                if (!on) {
                  upd("externalSalesAgentId", "");
                  upd("externalSalesAgentManualAmount", "");
                  upd("externalSalesAgentPaidBy", "company");
                }
              }}/>
            Sales Agent involved
          </label>
          <div style={{ fontSize:11, color:C.textLight, marginTop:4, marginInlineStart:24 }}>
            When ON, pick the second sales agent and enter the manual EGP commission they receive.
            The Internal team chain (TL / manager / director / owner) is never paid on External deals.
          </div>
          {form.externalSalesAgentEnabled && <div style={{ marginTop:10, display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)", gap:"0 12px" }}>
            <div>
              <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>Second Sales Agent<span style={{ color:C.danger, marginLeft:3 }}>*</span></label>
              <SearchableSelect value={form.externalSalesAgentId || ""}
                onChange={function(v){ upd("externalSalesAgentId", v); }}
                options={withCurrentOption(salesUsers.map(function(u){ return { value:gid(u), label:u.name+(u.title?" — "+u.title:"") }; }),
                                           form.externalSalesAgentId, userNameOf(form.externalSalesAgentId, p.users) || "Unknown")}
                emptyLabel="- Select -" placeholder="Search agent…" width="100%" maxWidth={9999} fontSize={14}/>
            </div>
            <Inp label="Manual Commission (EGP)" req
              value={form.externalSalesAgentManualAmount || ""}
              onChange={function(e){
                var raw = e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "");
                var firstDot = raw.indexOf(".");
                if (firstDot >= 0) raw = raw.slice(0, firstDot+1) + raw.slice(firstDot+1).replace(/\./g, "");
                var parts = raw.split(".");
                var intPart = parts[0] || "";
                var hasDec = parts.length > 1;
                var decPart = hasDec ? parts[1].slice(0, 2) : "";
                var formatted = intPart === ""
                  ? (hasDec ? "0." + decPart : "")
                  : Number(intPart).toLocaleString() + (hasDec ? "." + decPart : "");
                upd("externalSalesAgentManualAmount", formatted);
              }}
              placeholder="e.g. 50,000"/>
            {/* Phase R-13.2 — Paid by radio. Company (default) deducts the
                sales-agent amount from ARO's net. Broker deducts it from the
                broker's share (broker's net take-home shrinks). */}
            <div style={{ gridColumn:"1 / span 2", marginTop:8 }}>
              <div style={{ fontSize:11, fontWeight:600, color:C.text, marginBottom:6 }}>Paid by:</div>
              <div style={{ display:"flex", gap:18, alignItems:"center", flexWrap:"wrap" }}>
                <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:C.text, cursor:"pointer" }}>
                  <input type="radio" name="extSalesPaidBy"
                    checked={String(form.externalSalesAgentPaidBy || "company") === "company"}
                    onChange={function(){ upd("externalSalesAgentPaidBy", "company"); }}/>
                  Company
                </label>
                <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:C.text, cursor:"pointer" }}>
                  <input type="radio" name="extSalesPaidBy"
                    checked={String(form.externalSalesAgentPaidBy || "company") === "broker"}
                    onChange={function(){ upd("externalSalesAgentPaidBy", "broker"); }}/>
                  Broker
                </label>
              </div>
              <div style={{ fontSize:10, color:C.textLight, marginTop:4 }}>
                Company (default): sales amount comes out of ARO's net. Broker: sales amount comes out of the broker's share.
              </div>
            </div>
          </div>}
        </div>

        {/* Phase R-13 — External Broker Split (informal, for bookkeeping).
            Separate from the canonical state-tax flow (14% VAT + 5% withholding),
            which is computed on the Commissions page. commissionTaxPct here is an informal
            pre-broker deduction; 0 is allowed (broker takes their share from
            the full gross). */}
        <div style={{ fontSize:11, fontWeight:700, color:"#5B21B6", textTransform:"uppercase", letterSpacing:0.3, marginBottom:6 }}>
          External Broker Split (informal — for bookkeeping)
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)", gap:"0 12px" }}>
          <Inp label="Broker pre-deduction %"
            value={(form.externalDealConfig && form.externalDealConfig.commissionTaxPct) || ""}
            onChange={function(e){
              if (commissionLock===true) return;
              var raw = e.target.value.replace(/[^0-9.]/g,"");
              var n = raw === "" ? 0 : Number(raw);
              upd("externalDealConfig", Object.assign({}, form.externalDealConfig||{}, { commissionTaxPct: n }));
            }}
            placeholder="e.g. 0"/>
          <Inp label="Broker Share %" req
            value={(form.externalDealConfig && form.externalDealConfig.brokerSharePct) || ""}
            onChange={function(e){
              if (commissionLock===true) return;
              var raw = e.target.value.replace(/[^0-9.]/g,"");
              var n = raw === "" ? 0 : Number(raw);
              upd("externalDealConfig", Object.assign({}, form.externalDealConfig||{}, { brokerSharePct: n }));
            }}
            placeholder="e.g. 80"/>
          <div style={{ marginBottom:13 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>ARO Share %</label>
            <input type="text" disabled
              value={(function(){
                var bp = Number((form.externalDealConfig && form.externalDealConfig.brokerSharePct) || 0);
                if (!isFinite(bp) || bp <= 0) return "";
                return Math.max(0, 100 - bp).toString();
              })()}
              style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, background:"#F1F5F9", color:C.textLight, boxSizing:"border-box", cursor:"not-allowed" }}/>
          </div>
        </div>

      </div>}

      {/* Phase R-14 — Ambassador config block. Amber-themed. Reuses the canonical
          top-level Commission Rate (%) input above (no separate rate field).
          Ambassador deals pay NO VAT and NO withholding; the only deduction is
          the optional developer tax (a % of gross commission). Team commissions
          are assigned manually later in Edit Collection — not here. */}
      {form.dealType==="ambassador"&&<div style={{ marginBottom:13, padding:"12px 14px", background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:10 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#B45309", textTransform:"uppercase", letterSpacing:0.3, marginBottom:8 }}>
          Ambassador Deal
        </div>
        <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, fontWeight:600, color:C.text, cursor:"pointer" }}>
          <input type="checkbox" checked={!!(form.ambassadorConfig && form.ambassadorConfig.developerTaxEnabled)}
            onChange={function(e){
              var on = e.target.checked;
              upd("ambassadorConfig", Object.assign({}, form.ambassadorConfig||{}, {
                developerTaxEnabled: on,
                developerTaxRate: on ? (form.ambassadorConfig && form.ambassadorConfig.developerTaxRate) || "" : ""
              }));
            }}/>
          Developer takes tax from commission
        </label>
        {(form.ambassadorConfig && form.ambassadorConfig.developerTaxEnabled) && <div style={{ marginTop:10, maxWidth:220 }}>
          <Inp label="Developer Tax Rate (%)" req
            value={(form.ambassadorConfig && form.ambassadorConfig.developerTaxRate) || ""}
            onChange={function(e){
              var raw = e.target.value.replace(/[^0-9.]/g, "");
              var firstDot = raw.indexOf(".");
              if (firstDot >= 0) raw = raw.slice(0, firstDot+1) + raw.slice(firstDot+1).replace(/\./g, "");
              upd("ambassadorConfig", Object.assign({}, form.ambassadorConfig||{}, { developerTaxRate: raw }));
            }}
            placeholder="e.g. 10"/>
        </div>}
        {/* Ambassador live preview — Gross − developer tax = ARO Net. */}
        {(function(){
          var budgetNum = (function(){
            var s = String(form.budget || "").replace(/,/g, "").replace(/[^0-9.]/g, "");
            var n = parseFloat(s);
            return isFinite(n) ? n : 0;
          })();
          var rateNum = Number(form.commissionRate);
          var fmt = function(n){ return n.toLocaleString(undefined, { maximumFractionDigits: 2 }); };
          if (!(budgetNum > 0) || !(isFinite(rateNum) && rateNum > 0)) {
            return <div style={{ marginTop:10, padding:"6px 10px", background:"#FEF9C3", border:"1px solid #FDE68A", borderRadius:6, fontSize:11, color:"#92400E" }}>
              Set Budget (EGP) + Commission Rate (%) above to preview the ambassador commission.
            </div>;
          }
          var gross = budgetNum * rateNum / 100;
          var taxOn = !!(form.ambassadorConfig && form.ambassadorConfig.developerTaxEnabled);
          var taxRate = Number(form.ambassadorConfig && form.ambassadorConfig.developerTaxRate) || 0;
          var taxAmt = taxOn ? gross * taxRate / 100 : 0;
          var aroNet = gross - taxAmt;
          return <div style={{ marginTop:10, padding:"10px 12px", background:"#FEF3C7", border:"1px solid #FCD34D", borderRadius:8, fontSize:12, color:"#92400E", lineHeight:1.6 }}>
            <div style={{ fontWeight:600, marginBottom:4 }}>🎯 Ambassador Commission:</div>
            <div>Deal Total ({fmt(budgetNum)}) × {rateNum}% = <b>{fmt(gross)} EGP</b> gross</div>
            {taxOn && <div>− Developer Tax ({taxRate}%) = <b>{fmt(taxAmt)} EGP</b></div>}
            <div style={{ marginTop:4, paddingTop:4, borderTop:"1px solid #FCD34D", color:"#B45309", fontWeight:700 }}>ARO Net = {fmt(aroNet)} EGP</div>
            <div style={{ fontSize:10, color:"#A16207", marginTop:6, fontStyle:"italic" }}>Team commissions are assigned manually in Edit Collection.</div>
          </div>;
        })()}
      </div>}

    </div>}
    {/* Inline Add Broker — quick-add modal launched from the "+ Add new broker"
        option in the dropdown above. Single name field for speed; full edit
        (phone/email/notes) happens on the Brokers page. */}
    {showAddBroker&&<Modal show={true} onClose={function(){ setShowAddBroker(false); }} title="➕ Add New Broker" w={420}>
      <Inp label="Name" req value={newBrokerName} onChange={function(e){ setNewBrokerName(e.target.value); }}/>
      <div style={{ fontSize:11, color:C.textLight, marginBottom:14 }}>You can add phone, email, and notes later from the Brokers page.</div>
      <div style={{ display:"flex", gap:10 }}>
        <Btn outline onClick={function(){ setShowAddBroker(false); }} style={{ flex:1 }}>Cancel</Btn>
        <Btn onClick={addBrokerInline} loading={savingBroker} style={{ flex:1 }}>{savingBroker ? "Saving..." : "Save & Select"}</Btn>
      </div>
    </Modal>}
    {(isEOIForm||isDoneDealForm)&&<DocumentsUpload
      files={form.documentFiles||[]}
      onChange={function(next){upd("documentFiles",next);}}
      label={isEOIForm?"📎 Upload EOI Documents":"📎 Upload Deal Documents"}
    />}
    <div style={{ display:"flex", gap:10 }}>
      <Btn outline onClick={function(){ if(!p.editId) clearLeadDraft(draftKey); p.onClose(); }} style={{ flex:1 }}>{t.cancel}</Btn>
      <Btn onClick={submit} loading={saving} disabled={!p.editId && (isEOIForm||isDoneDealForm) && !devSelId && !devPendName.trim()} style={{ flex:1 }}>{p.editId?t.save:t.add}</Btn>
    </div>
  </div>;
};



var playAlertSound = function() {
  try {
    var ctx = new (window.AudioContext||window.webkitAudioContext)();
    var o = ctx.createOscillator(); var g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.setValueAtTime(660, ctx.currentTime+0.1);
    o.frequency.setValueAtTime(880, ctx.currentTime+0.2);
    g.gain.setValueAtTime(0.3, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime+0.4);
    o.start(ctx.currentTime); o.stop(ctx.currentTime+0.4);
  } catch(e) {}
};


// ===== WHATSAPP TEMPLATES =====
var WA_TEMPLATES_AR = [
  { id:1, label:"ترحيب", text:"أهلاً {name} 👋\nأنا {agent} من شركة ARO Investment\nشكراً لاهتمامك بمشروع {project}\nمتى يناسبك نتكلم؟" },
  { id:2, label:"متابعة", text:"أهلاً {name}\nبتواصل معاكم بخصوص عرضنا على {project}\nهل عندك وقت نتكلم؟ 🏠" },
  { id:3, label:"عرض خاص", text:"🎯 عرض خاص لـ {name}\nلدينا وحدات محدودة في {project}\nبسعر مميز - تواصل معنا الآن! 📞" },
  { id:4, label:"Confirm موعد", text:"أهلاً {name} 😊\nبتذكيرك بموعدنا غداً\nنتطلع لنراك! ✅" },
  { id:5, label:"بعد الاجتماع", text:"شكراً {name} على وقتك الكريم 🙏\nيسعدني خدمتك دائماً\nلو عندك أي استفسار أنا دايماً موجود" },
];

var fillTemplate = function(template, lead, agentName) {
  return template
    .replace(/{name}/g, lead.name || "")
    .replace(/{agent}/g, agentName || "")
    .replace(/{project}/g, lead.project || "");
};

// ===== THE ALT. PHONE CELL — ONE RULE, EVERY EXPORT (2026-08-26) ============
//
// Every export that prints a phone column prints this beside it, and blanks it
// in the same two cases: no second number, or a second number that is the SAME
// STRING as the primary. Measured on production 2026-08-26, 834 of the 4,310
// leads that carry a phone2 store it byte-identical to phone — without this the
// column repeats the number next to it on one row in five and reads as a second
// contact that does not exist.
//
// EXACT (trimmed), NOT NORMALISED, and the reason is a defect this is refusing
// to paper over: 3,859 of the 4,310 are the same NUMBER once punctuation and
// country code are ignored, because 3,005 leads store `phone` as "p:+2010…" —
// a prefix arriving from the Facebook lead-ads intake — with the clean copy in
// phone2. A normalising comparison would blank the only dialable copy on those
// 3,005 rows and leave the file showing "p:+2010…" alone. That prefix wants a
// fix on the write path, not a workaround inside an export.
//
// MODULE SCOPE ON PURPOSE: a rule that decides what lands in a file must be
// executable by a build gate (CLAUDE.md — a resolver inside a render closure
// cannot be gated). server.js carries the identical function for the two
// server-side exports, and verify-export-completeness.js §4 fails the build if
// the two spellings diverge.
var altPhoneCell = function(phone, phone2) {
  var alt = String(phone2 == null ? "" : phone2).trim();
  if (!alt) return "";
  return alt === String(phone == null ? "" : phone).trim() ? "" : alt;
};

// ===== EXPORT EXCEL =====
var exportLeadsToExcel = async function(leads, users, filename, includeClosingCompany, includeLeadId, includeDeveloper) {
  var XLSX = await new Promise(function(resolve) {
    if (window.XLSX) { resolve(window.XLSX); return; }
    var s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    s.onload = function() { resolve(window.XLSX); };
    document.head.appendChild(s);
  });
  var getAgentName = function(lead) { return userNameOf(lead && lead.agentId, users); };
  // Feature B — resolve a lead's closing company name from the populated
  // closingCompanyId ({_id,name}); empty for non-deal leads / unpopulated ids.
  var ccName = function(l){ return closingCompanyNameOf(l && l.closingCompanyId); };
  // Developer (D5) — developerId arrives populated ({_id,name}) from /api/leads.
  var devName = function(l){ return developerNameOf(l && l.developerId); };
  var rows = leads.map(function(l) {
    var row = {
      "Name": l.name,
      "Phone": l.phone,
      "Alt. Phone": altPhoneCell(l.phone, l.phone2),
      "Email": l.email || "",
      "Project": l.project || "",
      "Status": l.status || "",
      "Source": l.source || "",
      "Budget": l.budget || "",
      "Agent": getAgentName(l),
      "VIP": l.isVIP ? "Yes" : "",
      "Notes": l.notes || "",
      "Callback": excelWallDate(l.callbackTime),   // a real Excel datetime cell (sorts); 12h number format set below
      "Last Activity": l.lastActivityTime ? new Date(l.lastActivityTime).toLocaleDateString("en-GB") : "",
      "Date Added": l.createdAt ? new Date(l.createdAt).toLocaleDateString("en-GB") : "",
    };
    // Admin/SA only (caller passes the gate); skipped for the daily-requests export.
    // Feature A — ID column (admin/SA only). Populated for leads that have a
    // permanent id (EOI/DoneDeal); blank for the rest (the blank is meaningful:
    // those leads were never EOI/DoneDeal). Matches the on-screen "ID #01000".
    // THE "ID" COLUMN ANSWERS A DIFFERENT QUESTION NOW (owner ruling 2026-08-28).
    // This is a CLIENT export — one row per client — and a client has no number
    // (ID RULE 2). So the column no longer prints the client's id; it prints the
    // codes of the ACTIONS that client holds, comma-separated, blank when none.
    // A client with two purchases shows both. The server supplies them already
    // de-duplicated and sorted (actionCodesByLead), because an EOI and the deal
    // it became share one code and must not print twice.
    // Formatted, not raw: a cell must read "ID #50040" so it can be pasted straight
    // back into the search box. Bare digits are a PHONE query in this app.
    if (includeLeadId) row["ID"] = (l._actionCodes || []).map(formatLeadId).join(", ");
    if (includeClosingCompany) row["Closing Company"] = ccName(l);
    if (includeDeveloper) row["Developer"] = devName(l);
    return row;
  });
  var ws = XLSX.utils.json_to_sheet(rows);
  var wb = XLSX.utils.book_new();
  excelFormatColumn(XLSX, ws, "Callback", EXCEL_DATETIME_FMT);
  XLSX.utils.book_append_sheet(wb, ws, "Leads");
  XLSX.writeFile(wb, (filename || "leads") + "_" + new Date().toISOString().slice(0,10) + ".xlsx");
};


var callbackColor = function(cbTime) {
  if (!cbTime) return null;
  var diff = new Date(cbTime).getTime() - Date.now();
  var mins = diff / 60000;
  if (mins < 0) return { bg:"#FEE2E2", color:"#DC2626", label:"⚠️ Overdue" };
  if (mins < 60) return { bg:"#FEF3C7", color:"#D97706", label:"🔔 Within 1hr" };
  if (mins < 1440) return { bg:"#DCFCE7", color:"#15803D", label:"✅ Today" };
  return null;
};

// ===== A CLOSED RECORD HAS NO CALLBACK ON SCREEN (owner rule, 2026-09-08) =====
// DISPLAY ONLY. Nothing here writes, clears or reschedules anything: the stored
// callbackTime stays exactly as it is on both collections, and every counter and
// both push sweeps were already correct before this (the lead-level pair in
// derived.js returns null on a closed lead; the sweeps exclude the same
// statuses). This closes the gap between what those numbers count and what the
// tables were painting.
//
// Three modes, because "closed" is not one behaviour:
//   "hide"    - reached Done Deal or EOI. The cell renders EMPTY - which is NOT
//               the "-" an open row with no callback scheduled renders. That
//               difference is the point: "none scheduled" is an open state a
//               reader may need to act on; a closed record has no callback to
//               have scheduled at all.
//   "neutral" - Not Interested. The date still renders, in the neutral grey the
//               call sites already use for a callback more than 24h out, and
//               NEVER in overdue red. Returning null from callbackColorFor is
//               the whole mechanism: every site already falls back to
//               `ci ? ci.bg : "transparent"` / `ci ? ci.color : C.textLight`,
//               so this adds no style and no new branch.
//   "normal"  - unchanged.
//
// EOI CANCELLED IS DELIBERATELY NOT CLOSED. Cancelling an EOI un-freezes the
// lead (status -> HotCase, globalStatus -> active) and it auto-rotates again, so
// it is an open lead and keeps its callback. Same carve-out the EOI test in
// buildDealItems already makes. Two leads are on this branch today.
//
// Reads top-level `status`, which is the right field on BOTH record kinds: a
// DailyRequest has no assignments[] so its status IS its whole state, and a Lead
// arrives here already overlaid for a sales viewer (their own slice status) -
// except on a frozen deal/EOI, where the overlay deliberately keeps the
// lead-level value, which is exactly the one "hide" needs to see.
var callbackDisplayMode = function(r) {
  if (!r) return "normal";
  var st = r.status || "";
  if (st === "DoneDeal" || st === "Done Deal" || st === "EOI") return "hide";
  if (r.globalStatus === "donedeal" || r.globalStatus === "eoi") return "hide";
  if (r.eoiStatus && r.eoiStatus !== "EOI Cancelled") return "hide";
  if (st === "NotInterested" || st === "Not Interested") return "neutral";
  return "normal";
};
// callbackColor, gated by the rule above. Call sites that render a coloured
// callback chip use THIS, never callbackColor directly, so the "never red on a
// Not Interested row" half cannot be reintroduced by copying an existing site.
var callbackColorFor = function(r) {
  if (!r) return null;
  if (callbackDisplayMode(r) === "neutral") return null;
  return callbackColor(r.callbackTime);
};


// ===== QUICK PHONE SEARCH =====
var QuickPhoneSearch = function(p) {
  var [show,setShow]=useState(false);
  var [q,setQ]=useState("");
  // STEP 4-1 — server-side by-phone lookup replaces the in-memory
  // p.leads / p.dailyReqs scans. Once bootstrap shrinks to ~100 rows the
  // old scan would silently skip the rest of the collection; the BE
  // endpoints honor the same role-scope as the list and apply
  // substring + endsWith semantics matching the previous filter.
  // Debounced 250ms.
  var [leadResults,setLeadResults]=useState([]);
  var [drResults,setDrResults]=useState([]);
  var [searching,setSearching]=useState(false);
  useEffect(function(){
    if (q.length < 4) { setLeadResults([]); setDrResults([]); setSearching(false); return; }
    setSearching(true);
    var timer = setTimeout(function(){
      var qParam = encodeURIComponent(q);
      Promise.all([
        apiFetch("/api/leads/by-phone?phone=" + qParam + "&limit=20", "GET", null, p.token).catch(function(){ return []; }),
        apiFetch("/api/daily-requests/by-phone?phone=" + qParam + "&limit=20", "GET", null, p.token).catch(function(){ return []; })
      ]).then(function(results){
        setLeadResults(Array.isArray(results[0]) ? results[0] : []);
        setDrResults(Array.isArray(results[1]) ? results[1] : []);
        setSearching(false);
      });
    }, 250);
    return function(){ clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  var sc=STATUSES(p.t);
  var drSc=DR_STATUSES(p.t);
  var totalResults=leadResults.length+drResults.length;
  var renderCard=function(l,onClick,statuses,isDR){
    // See renderRow in HeaderSearch — DR status is per-agent by construction,
    // lead status must come from the caller's own slice via slimRowStatus.
    var cardSt = isDR ? l.status : slimRowStatus(l);
    var so=statuses.find(function(s){return s.value===cardSt;})||statuses[0];
    return <div key={gid(l)} onClick={function(){
      setShow(false);setQ("");
      if (isDR) { onClick(l); return; }
      apiFetch("/api/leads/"+gid(l),"GET",null,p.token)
        .then(function(fresh){ onClick((fresh && fresh._id) ? fresh : l); })
        .catch(function(){ onClick(l); });
    }} style={{ padding:"12px 14px", borderRadius:12, border:"1px solid #E8ECF1", marginBottom:8, cursor:"pointer", background:"#FAFBFC" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ fontWeight:700, fontSize:14 }}>{l.name}</div>
        <span style={{ background:so.bg, color:so.color, padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:600 }}>{so.label}</span>
      </div>
      <div style={{ fontSize:12, color:"#64748B", marginTop:4, direction:"ltr" }}>{l.phone}</div>
      <div style={{ display:"flex", gap:8, marginTop:8 }}>
        <a href={"tel:"+cleanPhone(l.phone)} onClick={function(e){e.stopPropagation();logContactTap(l,"call",p.token,p.csrfToken);}} style={{ flex:1, padding:"6px", borderRadius:8, background:"#EFF6FF", color:"#60A5FA", fontSize:12, fontWeight:600, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:4 }}>📞 Call</a>
        <a href={"https://wa.me/"+waPhone(l.phone)} target="_blank" rel="noreferrer" onClick={function(e){e.stopPropagation();logContactTap(l,"whatsapp",p.token,p.csrfToken);}} style={{ flex:1, padding:"6px", borderRadius:8, background:"#DCFCE7", color:"#25D366", fontSize:12, fontWeight:600, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:4 }}><svg viewBox="0 0 24 24" width="14" height="14" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> WhatsApp</a>
      </div>
    </div>;
  };
  if(!show)return <button className={p.isMobile?"crm-qps-btn":undefined} onClick={function(){setShow(true);}} style={{ position:"fixed", bottom:24, right:24, zIndex:300, width:46, height:46, borderRadius:"50%", background:"rgba(255,255,255,0.15)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)", border:"1.5px solid rgba(255,255,255,0.35)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 16px rgba(0,0,0,0.18)" }} title="Quick Phone Search"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={C.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="22" y2="22"/></svg></button>;
  return <div data-overlay-above="true" style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center" }} onClick={function(){setShow(false);setQ("");}}>
    <div style={{ background:"#fff", borderRadius:18, padding:20, width:340, maxWidth:"90vw", maxHeight:"80vh", overflow:"auto" }} onClick={function(e){e.stopPropagation();}}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ fontSize:15, fontWeight:700 }}>📞 Quick Phone Search</div>
        <button onClick={function(){setShow(false);setQ("");}} style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:"#94A3B8" }}>✕</button>
      </div>
      <input autoFocus value={q} onChange={function(e){setQ(e.target.value);}} placeholder="Enter last 4 digits or full number..." style={{ width:"100%", padding:"10px 14px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box", direction:"ltr", marginBottom:12 }}/>
      {q.length>0&&q.length<4&&<div style={{ fontSize:12, color:"#94A3B8", textAlign:"center", marginBottom:10 }}>Type at least 4 digits</div>}
      {searching&&totalResults===0&&q.length>=4&&<div style={{ fontSize:13, color:"#94A3B8", textAlign:"center", padding:20 }}>Searching…</div>}
      {!searching&&totalResults===0&&q.length>=4&&<div style={{ fontSize:13, color:"#94A3B8", textAlign:"center", padding:20 }}>No results</div>}
      {leadResults.length>0&&<div>
        <div style={{ fontSize:12, fontWeight:700, color:"#64748B", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Leads ({leadResults.length})</div>
        {leadResults.map(function(l){return renderCard(l,p.onSelect,sc,false);})}
      </div>}
      {drResults.length>0&&<div style={{ marginTop:leadResults.length>0?12:0 }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#8B5CF6", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Daily Requests ({drResults.length})</div>
        {drResults.map(function(r){return renderCard(r,p.onSelectDR,drSc,true);})}
      </div>}
    </div>
  </div>;
};


// Phone cell with hover reveal
// Mask a phone number for display — reveal first 3 + middle 2, hide the rest.
// Single source of truth: used by PhoneCell (header hover-reveal) AND by
// maskPhonesInText below, so every surface masks identically.
var maskPhone = function(ph){
  ph = String(ph == null ? "" : ph);
  if (!ph) return ph;
  if (ph.length < 7) return ph.slice(0,2) + "****";
  // show first 3, mask 2, show 2, mask last 4
  return ph.slice(0,3) + "**" + ph.slice(5,7) + "****";
};
// Scrub any phone-like run (9+ contiguous digits, optional leading +) out of a
// free-text string, so stored notes / activity descriptions / call logs can
// never leak a full number in any history / timeline / activity view. Values
// that contain separators or are shorter (dates, times, budgets) stay intact.
var maskPhonesInText = function(text){
  if (text == null) return text;
  return String(text).replace(/\+?\d{9,}/g, function(m){ return maskPhone(m); });
};
// PhoneCell owns NO width and NO font. The hover jitter it used to cause is
// fixed at the COLUMN, by PHONE_COL_PIN — see that constant. A per-cell width
// here was the wrong layer twice over: it changed the typeface of every phone
// in the product to fix a table-layout problem, and it could not help the
// surfaces that are not tables (cards, side panels) because they have no
// column to be stable within.
var PhoneCell = function(p) {
  var [show, setShow] = useState(false);
  if (!p.phone) return <span style={{ color:"#CBD5E1" }}>-</span>;
  var masked = maskPhone(p.phone);
  return <span
    onMouseEnter={function(){setShow(true);}}
    onMouseLeave={function(){setShow(false);}}
    style={{ cursor:"pointer", direction:"ltr", letterSpacing:1, userSelect:show?"text":"none" }}
    title="Hover to show"
  >{show ? p.phone : masked}</span>;
};

// Status → pill background color. Hoisted to module scope so both
// LeadJourney (Phase 4 timeline rows) and LeadsPage (Phase 2 per-slice
// feedback list + Phase 3 latest-feedback banner + agent-row pills) use
// byte-identical colors across the side panel. Keep in sync with the
// `sColor` palette inside LeadJourney — these are the new (post-Phase 2)
// status colors used across the redesigned panel surfaces.
var sliceStatusPillColor = function(s){
  var map = {
    NewLead:"#DCFCE7", "New Lead":"#DCFCE7",
    Potential:"#DBEAFE",
    HotCase:"#FED7AA", "Hot Case":"#FED7AA",
    CallBack:"#FEF3C7", "Call Back":"#FEF3C7",
    MeetingDone:"#E9D5FF", "Meeting Done":"#E9D5FF",
    EOI:"#CCFBF1",
    NotInterested:"#FEE2E2", "Not Interested":"#FEE2E2",
    NoAnswer:"#FFEDD5", "No Answer":"#FFEDD5",
    DoneDeal:"#D1FAE5", "Done Deal":"#D1FAE5",
    "Deal Cancelled":"#F1F5F9"
  };
  return map[s] || "#F1F5F9";
};

// Parallel text-color map for the pastel-bg status pills above. Same key shape
// as sliceStatusPillColor so consumers can do background:sliceStatusPillColor(s)
// + color:sliceStatusPillTextColor(s) for a readable pastel pill with no border.
var sliceStatusPillTextColor = function(s){
  var map = {
    NewLead:"#166534", "New Lead":"#166534",
    Potential:"#1E40AF",
    HotCase:"#9A3412", "Hot Case":"#9A3412",
    CallBack:"#92400E", "Call Back":"#92400E",
    MeetingDone:"#6B21A8", "Meeting Done":"#6B21A8",
    EOI:"#115E59",
    NotInterested:"#991B1B", "Not Interested":"#991B1B",
    NoAnswer:"#9A3412", "No Answer":"#9A3412",
    DoneDeal:"#065F46", "Done Deal":"#065F46",
    "Deal Cancelled":"#475569"
  };
  return map[s] || "#475569";
};

// ===== LEAD JOURNEY =====
// Unified grouped-by-agent-era view of a lead's audit trail. Replaces the old
// rotation-history card, side-panel activity list, and full-history modal body.
var LeadJourney = function(p) {
  var events = p.events || [];
  var lead = p.lead;
  var isAdmin = p.isAdminRole;
  var currentUser = p.currentUser;
  var allUsers = p.allUsers || [];
  var variant = p.variant || "panel";
  var isPanel = variant === "panel";
  var setShowCompare = p.setShowCompare || function(){};

  var bodyFs = isPanel ? 11 : 12;
  var metaFs = isPanel ? 10 : 11;
  var eraPad = isPanel ? "10px 12px" : "14px 16px";
  var maxW = isPanel ? "100%" : 620;

  var statusLabelMap = {
    NewLead:"New Lead", NoAnswer:"No Answer", NotInterested:"Not Interested",
    CallBack:"Call Back", HotCase:"Hot Case", MeetingDone:"Meeting Done", DoneDeal:"Done Deal"
  };
  var statusColorMap = {
    NewLead:"#5F5E5A", Potential:"#185FA5", NoAnswer:"#854F0B", NotInterested:"#A32D2D",
    CallBack:"#BA7517", HotCase:"#D85A30", MeetingDone:"#0F6E56", DoneDeal:"#04342C", EOI:"#04342C",
    "New Lead":"#5F5E5A","No Answer":"#854F0B","Not Interested":"#A32D2D","Call Back":"#BA7517",
    "Hot Case":"#D85A30","Meeting Done":"#0F6E56","Done Deal":"#04342C","Deal Cancelled":"#A32D2D"
  };
  var sLabel = function(s){ return statusLabelMap[s] || s || "New Lead"; };
  var sColor = function(s){ return statusColorMap[s] || statusColorMap[sLabel(s)] || "#5F5E5A"; };
  var extractStatus = function(ev){
    if (!ev) return null;
    if (ev.toStatus) return ev.toStatus;
    if (ev.status) return ev.status;
    var note = ev.note || "";
    var m = note.match(/^\s*\[([^\]]+)\]/);
    if (m) return m[1].trim();
    // History description form: "Status changed X → Y by Z" (X may be "—"
    // when no prior status). Captures the destination status only.
    var m2 = note.match(/Status\s+changed\s+\S+\s+(?:→|->)\s+(\S+?)(?:\s+by\s+|\s*$)/i);
    if (m2) return m2[1].trim();
    // Per-slice agentHistory form written by PUT /api/leads/:id slice-sync at
    // server.js:10223 ("Status: " + req.body.status). Without this match the
    // dedup's special status-change merger sees null from one side and falls
    // through to the content-equality check, which fails because the three
    // status-row note shapes differ in surface text — so the same status save
    // surfaces two rows in the timeline.
    var m3 = note.match(/^\s*Status\s*:\s*(\S+)\s*$/i);
    if (m3) return m3[1].trim();
    return null;
  };
  var extractFromStatus = function(ev){
    if (!ev) return null;
    if (ev.fromStatus) return ev.fromStatus;
    var note = ev.note || "";
    var m = note.match(/Status\s+changed\s+(\S+?)\s+(?:→|->)\s+\S+/i);
    if (m && m[1] !== "—") return m[1].trim();
    return null;
  };
  // Feedback text saved together with a status change, in priority order:
  //   (1) the explicit `feedback` field now stamped onto the per-slice
  //       agentHistory status_change entry (BE PUT slice-sync), forwarded by
  //       /full-history; also set by the dedup carry-over below.
  //   (2) the Activity status_change note shape "[Status] feedback text" the FE
  //       writes on a combined save — the text after the leading [bracket].
  // The bare "Status: X" and "Status changed X → Y by Z" shapes carry no
  // feedback and return "". Used to render the mandatory feedback beside a
  // status row when no separate feedback event accompanies it.
  var extractEntryFeedback = function(ev){
    if (!ev) return "";
    if (ev.feedback != null && String(ev.feedback).trim()) return String(ev.feedback).trim();
    var note = String(ev.note || "");
    var m = note.match(/^\s*\[[^\]]+\]\s*([\s\S]+)$/);
    if (m && m[1] && m[1].trim()) return m[1].trim();
    return "";
  };
  var parseReason = function(note){
    if (!note) return null;
    var m = note.match(/\(([^)]+)\)\s*$/);
    return m ? m[1].trim() : null;
  };
  var isAutoReason = function(note){ return /by\s+System|\bauto\b/i.test(note||""); };
  // Strip "Feedback by <name>: " / "Note by <name>: " prefixes the backend
  // bakes into history-source descriptions. The era header already shows
  // the agent's name and avatar, so the prefix is redundant noise inside
  // the muted body panel. Display-only — the stored value is unchanged.
  var stripActorPrefix = function(text){
    if (text == null) return text;
    return String(text)
      .replace(/^Feedback\s+by\s+[^:]+:\s*/i, "")
      .replace(/^Note\s+by\s+[^:]+:\s*/i, "");
  };
  var fmtTs = function(d){
    if (!d) return "";
    var dt = new Date(d);
    return dt.toLocaleString("en-US",{month:"short",day:"numeric"}) + " · " +
      fmtTime12(dt);
  };
  var fmtShort = function(d){
    if (!d) return "";
    return new Date(d).toLocaleString("en-US",{month:"short",day:"numeric"});
  };
  var fmtRange = function(start, end, isCurrent){
    var s = fmtShort(start);
    var e = isCurrent ? "now" : fmtShort(end);
    if (s && e) return s + " – " + e;
    return s || e || "";
  };

  // Per-era expand/collapse state. Keyed by the era's index in the rendered
  // order. Default empty → all eras start collapsed (showing newest 5).
  var [expandedEras, setExpandedEras] = useState({});

  // Two events share an actor when their userId._ids match (preferred) or,
  // when _ids are missing on either side, when their userId.names match.
  var sameActor = function(a, b){
    if (!a || !b || !a.userId || !b.userId) return false;
    if (a.userId._id && b.userId._id) return String(a.userId._id) === String(b.userId._id);
    if (a.userId.name && b.userId.name) return a.userId.name === b.userId.name;
    return false;
  };

  // Parse a rotation event into { isAuto, actor, reason }. Note formats
  // emitted by the backend look like:
  //   "Rotated from X to Y by System (3x No Answer)"            (auto, explicit)
  //   "Rotated from X to Y by <outgoing agent> (3x_no_answer)"  (auto, cron impersonates outgoing)
  //   "Rotated from X to Y by <admin name>"                     (manual)
  //   "Rotated from X to Y by <admin name> (<reason>)"          (manual)
  //   "Bulk redistribution — reassigned to <name> by <name>"    (manual bulk)
  //   "Auto Rotation | From: X → To: Y | Reason: <reason>"      (auto, alt)
  // The cron runs as the OUTGOING sales agent, so notes can read "by <agent>"
  // even on auto rotations. Cover that with: any auto-reason marker in the
  // note OR the actor's role being sales/team_leader (neither can manually
  // rotate, so a rotation logged with that actor must be the cron).
  var parseRotation = function(ev){
    var note = (ev && ev.note) || "";
    if (!note) return { isAuto: false, actor: null, reason: null };
    var hasAutoRot = /auto\s*rotation/i.test(note);
    // Auto markers — explicit "by System" wording AND every reason code/keyword
    // that translateAutoReason recognizes (timeout, 3x no answer, not
    // interested streak, callback overdue, cooldown), plus rotation-cron
    // wording. Whitespace, hyphen, and underscore variants all match.
    var hasAutoMarker = /by\s+system|auto[\s\-_]?rotat|auto[\s\-_]?timeout|rotation[\s\-_]?cron|3x[\s\-_]?no[\s\-_]?answer|not[\s\-_]?interested[\s\-_]?streak|callback[\s\-_]?overdue|cooldown/i.test(note);
    // Resolve the actor's role. Activity / history rows don't populate
    // userId.role, so fall back to looking up the actor by name in allUsers.
    var actorRole = "";
    if (ev && ev.userId && ev.userId.role) {
      actorRole = String(ev.userId.role);
    } else if (ev && ev.userId && ev.userId.name) {
      var u = (allUsers || []).find(function(x){ return x && x.name === ev.userId.name; });
      if (u && u.role) actorRole = String(u.role);
    }
    var actorIsSales = actorRole === "sales" || actorRole === "team_leader";
    var isAuto = hasAutoMarker || actorIsSales;
    var actor = null;
    if (hasAutoRot) {
      actor = "System";
    } else if (!isAuto) {
      var byMatch = note.match(/by\s+(.+?)(?:\s*\(|$)/i);
      if (byMatch) actor = byMatch[1].trim();
    }
    var reason = null;
    var lastParen = note.match(/\(([^)]+)\)\s*$/);
    if (lastParen) {
      reason = lastParen[1].trim();
    } else {
      var rMatch = note.match(/Reason:\s*(.+?)(?:\s*\||$)/i);
      if (rMatch) reason = rMatch[1].trim();
    }
    return { isAuto: isAuto, actor: actor, reason: reason };
  };

  // Translate raw auto-rotation reason codes (the parenthesized substring of
  // the rotation note) into human-readable English. Substring + case-
  // insensitive so we match whether the backend writes "no_action_timeout",
  // "No Action Timeout", or "auto_timeout (legacy)". Empty input returns
  // the "no reason recorded" fallback used when an auto rotation didn't
  // supply a reason. Manual reasons are NOT translated — they're admin
  // free-text.
  // Order matters: the rule-specific codes are checked before the legacy
  // generic ones because "no_action_timeout" contains "timeout" and would
  // otherwise be swallowed by the legacy fallback.
  var translateAutoReason = function(raw){
    if (!raw || !String(raw).trim()) return "No reason recorded";
    var s = String(raw).toLowerCase();
    // Rule-specific codes — the actual rule that fired (Phase BB).
    if (s.indexOf("no_answer_streak") !== -1)      return "No-Answer threshold reached";
    if (s.indexOf("not_interested_return") !== -1) return "Not Interested cooldown ended";
    if (s.indexOf("no_action_timeout") !== -1)     return "No action taken on the lead";
    if (s.indexOf("callback_overdue") !== -1 || s.indexOf("callback overdue") !== -1) return "Callback time passed";
    if (s.indexOf("hot_no_action") !== -1)         return "No action on Hot/Potential/Meeting lead";
    // Legacy fallbacks — pre-Phase BB history entries used generic codes.
    // Map them to the same human-readable strings so old rotations render
    // consistently with new ones.
    if (s.indexOf("3x no answer") !== -1 || s.indexOf("3x_no_answer") !== -1)               return "No-Answer threshold reached";
    if (s.indexOf("not interested streak") !== -1 || s.indexOf("not_interested_streak") !== -1) return "Not Interested cooldown ended";
    if (s.indexOf("auto_timeout") !== -1 || s.indexOf("timeout") !== -1)                    return "No action taken on the lead";
    if (s.indexOf("cooldown") !== -1) return "Cooldown expired, redistributed";
    return raw;
  };

  // Map a User.role enum value to a display label. Returns null for unknown
  // or missing roles — callers omit the parenthesized role suffix in that
  // case rather than guessing.
  var roleLabel = function(role){
    if (!role) return null;
    var map = { admin:"Admin", operation:"Operations", manager:"Manager", director:"Director", team_leader:"Team Leader", sales:"Sales" };
    return map[role] || null;
  };

  var sorted = events.slice().sort(function(a,b){
    return new Date(a.createdAt) - new Date(b.createdAt);
  });

  // Resolve the actual lead-holder for an assignment event. The backend
  // stamps userId.name with the ACTOR (could be an admin pressing "rotate"),
  // and agentName with h.toAgent (the sales person actually receiving the
  // lead). Era ownership must follow agentName, not userId.name.
  var resolveAgentName = function(ev){
    if (ev.agentName && String(ev.agentName).trim()) return String(ev.agentName).trim();
    if (ev.toAgent && String(ev.toAgent).trim()) return String(ev.toAgent).trim();
    if (ev.source !== "history" && userNameOf(ev.userId, allUsers)) return userNameOf(ev.userId, allUsers);
    return "Unknown";
  };

  // Resolve the receiving agent's _id for an assignment event. History rows
  // only carry the receiver's NAME (h.toAgent), so we look up the _id via
  // allUsers by name. Activity-source assignment rows often hold the actor
  // (admin) on userId, not the receiver — same name-lookup path is safer.
  var resolveAgentId = function(ev){
    var n = (ev.agentName && String(ev.agentName).trim()) ||
            (ev.toAgent && String(ev.toAgent).trim()) ||
            (ev.source !== "history" && userNameOf(ev.userId, allUsers)) || "";
    if (n) {
      var u = (allUsers || []).find(function(x){ return x && x.name === n; });
      if (u && u._id) return String(u._id);
    }
    if (ev.source !== "history" && ev.userId && ev.userId._id) return String(ev.userId._id);
    return "";
  };

  // Resolve the AUTHOR _id of any event. Activity rows populate userId._id;
  // history rows carry only userId.name (= h.byUser) → name lookup; synthesized
  // assignmentEntries already include _id. Returns "" when the author can't be
  // identified (e.g. system-only rows with no actor name).
  var resolveAuthorId = function(ev){
    if (!ev || !ev.userId) return "";
    if (ev.userId._id) return String(ev.userId._id);
    if (typeof ev.userId === "string") return ev.userId;
    if (ev.userId.name) {
      var u = (allUsers || []).find(function(x){ return x && x.name === ev.userId.name; });
      if (u && u._id) return String(u._id);
    }
    return "";
  };

  // Normalize a person name for tolerant matching — Unicode NFC, collapsed
  // whitespace, trimmed, lowercased. Era ownership is recovered from the
  // rotation marker's h.toAgent, which is stored as a NAME string (server.js
  // schema toAgent:String). resolveAgentId resolves it to an id via an EXACT
  // allUsers.find(x => x.name === n), so any leading/trailing/doubled
  // whitespace, case difference, Arabic diacritic / NFC-vs-NFD mismatch, or a
  // name edited after the rotation was logged leaves era.agentId === "" — the
  // Path-B cause of the cross-slice leak. Matching by normName tolerates all of
  // these so an event still finds its author's era even when ids don't line up.
  var normName = function(s){
    return String(s == null ? "" : s).normalize("NFC").replace(/\s+/g, " ").trim().toLowerCase();
  };
  // Display name of an event's author. Slice-synth entries carry the slice
  // agent on userId (and agentName); history / Activity rows carry the actor on
  // userId.name. Used to match an event to its author's era by name when the
  // ids don't line up (see normName).
  var resolveAuthorName = function(ev){
    if (!ev) return "";
    if (userNameOf(ev.userId, allUsers)) return userNameOf(ev.userId, allUsers);
    if (ev.agentName) return ev.agentName;
    return "";
  };

  var eras = [];
  var cur = null;
  sorted.forEach(function(ev){
    var type = ev.type;
    var isAssign = type==="assigned" || type==="first_assigned" || type==="rotated" || type==="reassigned";
    if (isAssign) {
      var note = ev.note || "";
      var rotMeta = parseRotation(ev);
      cur = {
        agentName: resolveAgentName(ev),
        agentId: resolveAgentId(ev),
        fromAgent: ev.fromAgent || null,
        assignType: type,
        isRotation: type==="rotated" || type==="reassigned",
        reason: parseReason(note),
        auto: rotMeta.isAuto ? "auto" : "manual",
        rotationMeta: rotMeta,
        actor: ev.userId ? { name: userNameOf(ev.userId, allUsers) || null, role: ev.userId.role || null } : null,
        source: ev.source || ((type==="first_assigned" && lead) ? lead.source : null),
        startedAt: ev.createdAt,
        endedAt: ev.createdAt,
        events: [ev]
      };
      eras.push(cur);
    } else if (cur) {
      // Route the event to the era of the agent who actually AUTHORED it, not
      // whichever era happens to be open at its timestamp. Match against every
      // era (current or past) by author id first, then by normalized author
      // name — the name fallback is essential because a rotation marker's
      // agent is a NAME string (h.toAgent), and resolveAgentId's exact lookup
      // can leave era.agentId === "" (Path B); without it an event with a
      // resolvable author would never match such an era and would leak.
      var evUid  = resolveAuthorId(ev);
      var evName = normName(resolveAuthorName(ev));
      var targetEra = null;
      for (var bi = eras.length - 1; bi >= 0; bi--) {
        var e = eras[bi];
        var eId = e.agentId ? String(e.agentId) : "";
        var eName = normName(e.agentName);
        if ((evUid && eId && eId === evUid) || (evName && eName && eName === evName)) { targetEra = e; break; }
      }
      if (targetEra) {
        targetEra.events.push(ev);
        // Only extend the CURRENT era's boundary; past eras keep theirs.
        if (targetEra === cur) cur.endedAt = ev.createdAt;
      } else if (ev.source === "assignment") {
        // A slice-OWNED entry we couldn't attribute to any era's agent. It
        // belongs to a specific sales slice, NOT the current holder — dumping
        // it into `cur` is exactly the sales-to-sales rotation leak. Drop it
        // rather than misattribute it. (With the backend always stamping the
        // slice agent id, real previous holders match above; only a genuinely
        // orphaned slice entry — e.g. a removed user with no resolvable era —
        // reaches here.)
      } else {
        // A GLOBAL event (lead.history / Activity) we couldn't tie to an era —
        // a manager / admin / TL / director action (they never own a slice) or
        // a system row. Keep the active-holder fallback: it lands in whichever
        // slice was active when it occurred (we process chronologically, so
        // `cur` IS that holder). This preserves manager/admin visibility in the
        // holder's slice exactly as before.
        cur.events.push(ev);
        cur.endedAt = ev.createdAt;
      }
    } else {
      cur = {
        agentName: userNameOf(ev.userId, allUsers) || userNameOf(lead && lead.agentId, allUsers) || "Unknown",
        agentId: resolveAuthorId(ev),
        fromAgent: null,
        assignType: null,
        isRotation: false,
        reason: null,
        auto: null,
        source: lead ? lead.source : null,
        startedAt: ev.createdAt,
        endedAt: ev.createdAt,
        events: [ev]
      };
      eras.push(cur);
    }
  });

  // Map an event type to its logical category. The backend writes a single
  // user gesture as several typed rows (status_change in Activity vs.
  // status_changed in history; feedback in Activity vs. feedback_added in
  // history). Bucketing by category lets dedup collapse those duplicates.
  var eventCategory = function(t){
    if (t === "feedback" || t === "feedback_added") return "feedback";
    if (t === "note") return "note";
    if (t === "status_change" || t === "status_changed") return "status_change";
    if (t === "callback_scheduled") return "callback";
    if (t === "call") return "call";
    if (t === "meeting") return "meeting";
    return t || "other";
  };
  // Cross-category whitelist: when the same actor writes the same non-empty
  // content as both a note and a feedback (or status_change), within 10s,
  // it's the same logical gesture surfaced through different sinks. Other
  // category pairings stay segregated.
  var ALLOWED_CROSS_CAT = {
    "note|feedback":1, "feedback|note":1,
    "note|status_change":1, "status_change|note":1,
    "feedback|status_change":1, "status_change|feedback":1
  };

  // Dedupe events within each era. Backend writes some logical actions to
  // BOTH the Activity collection and lead.history; the merged feed surfaces
  // both copies. Match: same logical category (or one of the allowed
  // cross-category pairs above), same actor, same non-empty content,
  // timestamps within 10s. Keep the earliest occurrence.
  // Status-change special case: two status_change rows with the same
  // target status, same actor, within 10s collapse even when their note
  // text differs (Activity row writes "[CallBack] note" while history
  // row writes "Status changed X → Y by Z"). Prefer the row that carries
  // BOTH from and to states — it shows the transition.
  eras.forEach(function(era){
    var keep = [];
    for (var di = 0; di < era.events.length; di++) {
      var dEv = era.events[di];
      var dContent = String(dEv.note || dEv.feedback || "").trim();
      var dT = new Date(dEv.createdAt).getTime();
      var dCat = eventCategory(dEv.type);
      var isDup = false;
      var replaceIdx = -1;
      for (var dj = 0; dj < keep.length; dj++) {
        var pe = keep[dj];
        var cA = eventCategory(pe.type), cB = dCat;
        var peT = new Date(pe.createdAt).getTime();
        // Status-change merger — runs before the content-based path so it
        // catches Activity ↔ history pairs whose note strings don't match.
        if (cA === "status_change" && cB === "status_change") {
          var dStatus = extractStatus(dEv);
          var peStatus = extractStatus(pe);
          if (dStatus && peStatus && dStatus === peStatus &&
              sameActor(pe, dEv) && Math.abs(dT - peT) <= 10000) {
            // Preserve feedback across the collapse. The row we keep is the one
            // that shows the from→to transition (history "X → Y" shape), but the
            // feedback the agent typed often lives on the OTHER row (the Activity
            // "[Y] feedback" note, or the per-slice entry's feedback field).
            // Union it onto whichever row survives so the status renders WITH its
            // feedback instead of losing it to dedup.
            var mergedFb = extractEntryFeedback(pe) || extractEntryFeedback(dEv);
            var dHasFrom = !!extractFromStatus(dEv);
            var peHasFrom = !!extractFromStatus(pe);
            if (dHasFrom && !peHasFrom) {
              if (mergedFb && (dEv.feedback == null || !String(dEv.feedback).trim())) dEv.feedback = mergedFb;
              replaceIdx = dj; break;
            }
            if (mergedFb && (pe.feedback == null || !String(pe.feedback).trim())) pe.feedback = mergedFb;
            isDup = true; break;
          }
        }
        if (cA !== cB) {
          var pair1 = cA + "|" + cB;
          if (!ALLOWED_CROSS_CAT[pair1]) continue;
        }
        var peContent = String(pe.note || pe.feedback || "").trim();
        // Normalize note prefixes BEFORE the equality check. The same logical
        // feedback/note write surfaces in multiple stores with subtly different
        // shapes — slice.lastFeedback / slice.agentHistory carry the raw text,
        // but lead.history's audit row wraps it as "Feedback by NAME: text" or
        // "Note by NAME: text" (server.js:10301-10310). The display renderer
        // already runs stripActorPrefix at render time so both rows look
        // identical visually; matching the same patterns here lets dedup
        // collapse them as duplicates instead of letting both survive.
        var dContentN  = stripActorPrefix(dContent);
        var peContentN = stripActorPrefix(peContent);
        if (!dContentN || !peContentN || dContentN !== peContentN) continue;
        if (!sameActor(pe, dEv)) continue;
        if (Math.abs(dT - peT) <= 10000) { isDup = true; break; }
      }
      if (replaceIdx >= 0) keep[replaceIdx] = dEv;
      else if (!isDup) keep.push(dEv);
    }
    era.events = keep;
  });

  // EVERY agent who held the lead must appear in the journey, regardless of
  // whether they did any work during their tenure. Earlier code here dropped
  // "phantom" eras whose only event was the rotation marker itself (i.e. an
  // intermediate agent received the lead and then was rotated away with no
  // status change / feedback / call / callback / note in between). That hid
  // legitimate handoffs from admin view — a 4-agent chain where the middle
  // agent did nothing would only show 3 eras, missing the silent middle hop.
  // The current Phase 5 spec requires a 1:1 mapping between rotation events
  // and visible eras; any noise reduction beyond that needs to be surgical
  // (e.g. by actor role) and is deferred. Do not reintroduce a length-based
  // or work-type-based filter without revisiting the spec.

  // Team_leader scope: drop any era whose agent isn't on this TL's team. The
  // backend already scopes leads/users to the TL's team, but the journey
  // panel renders the lead's full history — including eras held by agents
  // outside the team — and that history must be hidden too.
  // Match the era's agent (era.agentName, the receiving sales person) against
  // allUsers by name to recover an _id, then test it against the scope set.
  // No exception for the current (last) era — the rule is strict.
  var teamScope = getTeamScopeIds(currentUser, allUsers);
  if (teamScope) {
    eras = eras.filter(function(era){
      var agentMatch = (allUsers || []).find(function(u){ return u && u.name === era.agentName; });
      var agentId = agentMatch ? String(agentMatch._id) : "";
      return teamScope.has(agentId);
    });
  }

  // After dropping phantom eras (and any out-of-team eras for team_leader),
  // point each surviving era's fromAgent at the previous SURVIVING era's
  // agent — so the rotation separator reads "Rotated <prevSurvivor> →
  // <nextSurvivor>", skipping the admin hop and any out-of-team eras.
  eras.forEach(function(era, i){
    if (i > 0) era.fromAgent = eras[i-1].agentName;
  });

  // Phase 6 — merge tenures: collapse multiple eras owned by the same agent
  // into a single card. Bucket by agentId (fallback to agentName) preserving
  // first-touch order. Each bucket unions every tenure's events[] back into
  // a single chronologically-sorted array so the downstream action-grouping
  // pass produces correct 60-second clusters spanning all tenures.
  //
  // The header chrome (fromAgent at first-touch, assignType, source, reason,
  // rotationMeta, actor) comes from the FIRST tenure — that's the predecessor
  // who handed the lead in originally. Subsequent tenures' rotation metadata
  // is preserved on tenureMeta[] so renderEra can render inline re-assignment
  // markers at the chronological boundary between tenures.
  var bucketOrder = [];
  var bucketByKey = {};
  eras.forEach(function(era){
    var key = era.agentId ? ("id:" + era.agentId) : ("name:" + (era.agentName || "Unknown"));
    if (!bucketByKey[key]) {
      bucketOrder.push(key);
      bucketByKey[key] = { first: era, tenures: [era] };
    } else {
      bucketByKey[key].tenures.push(era);
    }
  });
  eras = bucketOrder.map(function(key){
    var b = bucketByKey[key];
    var first = b.first;
    if (b.tenures.length === 1) {
      first.tenureCount = 1;
      first.tenureStarts = [first.startedAt];
      first.tenureEnds = [first.endedAt];
      first.tenureMeta = [];
      return first;
    }
    var allEvents = [];
    b.tenures.forEach(function(t){ allEvents = allEvents.concat(t.events); });
    allEvents.sort(function(x, y){ return new Date(x.createdAt) - new Date(y.createdAt); });
    // tenureMeta[i] describes the i-th re-takeover (i >= 1). tenureMeta[0]
    // is intentionally empty — the first tenure's handoff is shown via the
    // outer renderSeparator above the merged card, not inline.
    var tenureMeta = b.tenures.map(function(t, ti){
      if (ti === 0) return null;
      return { startedAt: t.startedAt, fromAgent: t.fromAgent, rotationMeta: t.rotationMeta, actor: t.actor };
    });
    return {
      agentName: first.agentName,
      agentId:   first.agentId,
      fromAgent: first.fromAgent,
      assignType: first.assignType,
      isRotation: first.isRotation,
      reason: first.reason,
      auto: first.auto,
      rotationMeta: first.rotationMeta,
      actor: first.actor,
      source: first.source,
      startedAt: first.startedAt,
      endedAt:   b.tenures[b.tenures.length - 1].endedAt,
      events: allEvents,
      tenureCount: b.tenures.length,
      tenureStarts: b.tenures.map(function(t){ return t.startedAt; }),
      tenureEnds: b.tenures.map(function(t){ return t.endedAt; }),
      tenureMeta: tenureMeta
    };
  });
  // Re-thread fromAgent across merged eras — the rewire upstream may now
  // point at an era that got absorbed into another bucket.
  eras.forEach(function(era, i){ if (i > 0) era.fromAgent = eras[i-1].agentName; });

  // ── De-duplicate feedback within each era (BUG 1 + BUG 2 fix) ────────────
  // A status_change agentHistory entry now carries the feedback the agent
  // wrote with it (commit 4f28831 stamps it on the slice entry), and the
  // renderer shows that inline beneath the status pill. The SAME text ALSO
  // arrives as a standalone "feedback" row — the slice.lastFeedback synth
  // (server.js full-history, stamped at lastActionAt) and/or a lead.history
  // "feedback_added" audit row. Left alone the feedback renders TWICE: once
  // inline on its real status, and once as a floating row that grouping +
  // whileStatusFor pair with whatever status is current — which is exactly the
  // duplicate-on-a-second-pill (BUG 1) and, as its mirror image, a real status
  // left showing no feedback (BUG 2). Render it ONCE: drop the standalone row
  // when its text matches a status_change's own feedback in the SAME era.
  // Managerial /feedback rows (to_sales / private writes carry a non-sales
  // authorRole) are NEVER dropped — they're a distinct action and must stay
  // visible (manager/admin/TL/director visibility in the slice).
  eras.forEach(function(era){
    var inlineFb = {};
    (era.events || []).forEach(function(ev){
      if ((ev.type === "status_change" || ev.type === "status_changed") && ev.feedback) {
        var t = String(ev.feedback).trim();
        if (t) inlineFb[t] = true;
      }
    });
    if (!Object.keys(inlineFb).length) return;
    era.events = (era.events || []).filter(function(ev){
      if (ev.type !== "feedback" && ev.type !== "feedback_added") return true;
      if (ev.authorRole && ev.authorRole !== "sales") return true; // keep managerial feedback
      var txt = stripActorPrefix(String(ev.feedback || ev.note || "")).trim();
      return !inlineFb[txt]; // drop the standalone duplicate of an inline status feedback
    });
  });

  // Group consecutive same-actor events written within 60s of each other into
  // a single composite "action". Backend frequently writes one user action as
  // 4-6 history rows (status_change + callback_scheduled + feedback_added +
  // mirror copies in Activity). Grouping collapses them into one row, and
  // shrinks the era's "X actions" count to match logical user activity.
  // Era boundaries (assigned/first_assigned/rotated/reassigned) are NEVER
  // groupable — they always render as their own row.
  var GRP_GAP_MS = 60 * 1000;
  var GROUPABLE_TYPES = { status_change:1, feedback:1, feedback_added:1, callback_scheduled:1, note:1, call:1, meeting:1 };
  var isGroupable = function(t){ return !!GROUPABLE_TYPES[t]; };
  eras.forEach(function(era){
    var groups = [];
    era.events.forEach(function(ev){
      var last = groups[groups.length - 1];
      var canJoin = false;
      if (last) {
        var lastEv = last.subEvents[last.subEvents.length - 1];
        var tDelta = Math.abs(new Date(ev.createdAt) - new Date(lastEv.createdAt));
        canJoin = isGroupable(lastEv.type) && isGroupable(ev.type) && sameActor(lastEv, ev) && tDelta <= GRP_GAP_MS;
      }
      if (canJoin) {
        last.subEvents.push(ev);
      } else {
        groups.push({
          _id: (ev._id ? String(ev._id) + "-grp" : ("grp-" + groups.length)),
          createdAt: ev.createdAt,
          actor: ev.userId || null,
          subEvents: [ev]
        });
      }
    });
    groups.forEach(function(g){ g.isGroup = g.subEvents.length > 1; });
    era.actions = groups;
  });

  // Knock-on #1: after merge, bucket order is first-touch order, NOT last-
  // touch. The agent currently holding the lead is the one whose LATEST
  // tenureStart is the maximum across all eras, which may not be eras[-1].
  var __latestStartMs = -Infinity;
  var __latestIdx = -1;
  eras.forEach(function(era, i){
    var starts = era.tenureStarts || [era.startedAt];
    var maxStart = -Infinity;
    for (var si = 0; si < starts.length; si++) {
      var t = new Date(starts[si]).getTime();
      if (!isNaN(t) && t > maxStart) maxStart = t;
    }
    if (maxStart > __latestStartMs) { __latestStartMs = maxStart; __latestIdx = i; }
  });
  eras.forEach(function(era, i){
    era.isCurrent = (i === __latestIdx);
    var last = null;
    for (var j = era.events.length - 1; j >= 0; j--) {
      if (era.events[j].type === "status_change") { last = extractStatus(era.events[j]); break; }
    }
    era.endedAtStatus = last || "NewLead";
  });

  // Conflict detection: flag a later era when previous era ended in strong engagement
  // but current era reaches a negative outcome.
  var strongStatuses = { HotCase:1, MeetingDone:1, DoneDeal:1, EOI:1, "Hot Case":1, "Meeting Done":1, "Done Deal":1 };
  var negativeStatuses = { NotInterested:1, NoAnswer:1, "Not Interested":1, "No Answer":1 };
  for (var ci = 1; ci < eras.length; ci++) {
    var prevEra = eras[ci-1];
    var thisEra = eras[ci];
    if (!strongStatuses[prevEra.endedAtStatus]) continue;
    var hitsNegative = thisEra.events.some(function(ev){
      if (ev.type !== "status_change") return false;
      var s = extractStatus(ev);
      return !!negativeStatuses[s];
    });
    if (hitsNegative) {
      thisEra.isConflict = true;
      thisEra.conflictPrevStatus = prevEra.endedAtStatus;
    }
  }

  // Sales: keep only the caller's current era.
  if (!isAdmin) {
    eras = eras.filter(function(e){ return e.isCurrent; });
  }

  if (eras.length === 0) {
    return <div style={{ fontSize:bodyFs, color:C.textLight, textAlign:"center", padding:14 }}>No history</div>;
  }

  // Both panel and modal variants render newest era at the top → oldest at
  // the bottom. The separator between adjacent eras is placed AFTER the
  // newer one (`orderedEras[i]`) and BEFORE the older one, and shows the
  // rotation that produced that newer era.
  var orderedEras = eras.slice().reverse();

  // Compute the status the lead held when a feedback row was written. Used
  // by the "while X" badge on feedback rows. Scans status_change events
  // across ALL eras (not just the era of `ev`) — a lead inherited via
  // rotation may have had its status set by a previous agent, and that
  // event lives in the previous agent's era. The badge reflects lead
  // status at time of write, not era-scoped slice status.
  // Type-name compatibility:
  //   - Activity records and per-slice agentHistory entries use "status_change".
  //   - lead.history entries pushed by PUT use "status_changed" (with -ed).
  // Both spellings are matched.
  // Fallback when nothing matches: lead.status (the current top-level value)
  // when available, else "NewLead". Privacy is preserved: this function only
  // reads events the client already received from /full-history (server-side
  // role-filtered) and only outputs a status string — never an author, note,
  // or any other slice/private content.
  var whileStatusFor = function(ev, era){
    var tFb = new Date(ev.createdAt).getTime();
    var best = null; var bestT = -Infinity;
    // SAME-ERA ONLY (BUG 3 fix). The "while X" pill on a feedback row must
    // reflect the status THIS agent set during THIS tenure — never a status
    // from another era. The old all-eras scan picked the most-recent
    // status_change across the whole lead, so one agent's strong status (e.g.
    // a MeetingDone) leaked onto every later slice's feedback rows, making it
    // look as if every agent had that status.
    var scan = (era && era.events) ? era.events : [];
    for (var k = 0; k < scan.length; k++) {
      var ek = scan[k];
      var ekType = ek && ek.type;
      if (ekType !== "status_change" && ekType !== "status_changed") continue;
      var t = new Date(ek.createdAt).getTime();
      if (!isFinite(t) || t > tFb) continue;
      if (t > bestT) {
        var s = extractStatus(ek);
        if (s) { best = s; bestT = t; }
      }
    }
    if (best) return best;
    // No status set yet in this tenure → the era's starting status. Rotated
    // eras begin at NewLead; do NOT fall back to lead.status (the current
    // top-level value), which would itself be a cross-slice leak.
    return "NewLead";
  };

  // Layout B — labeled multi-line block rendering for one sub-event inside a
  // composite group row. Each sub-event becomes a block with a "Category:"
  // label; feedback / note bodies sit in a muted panel below the label.
  // The `group` arg gives access to sibling sub-events so we can suppress
  // the "while X" badge when a status_change in the same group already
  // shows the matching destination status (badge would be redundant).
  // Phase 4: per-event-type body renderer. Inside an action group with 2+ sub-
  // events. Visual conventions:
  //   Feedback   — yellow box, left-border accent, status pill on left
  //   Note       — slate/white box, left-border slate, 📝 icon
  //   Status     — inline 🔄 + colored from→to labels
  //   Callback   — inline 📅 + scheduled-for label
  //   Call       — inline 📞 + text
  //   Meeting    — inline 🤝 + text
  // The icon identifies the row type, so no "Status:" / "Call:" / etc. labels.
  // Notes may arrive with leading emoji already baked into the text (e.g. the
  // Call activity row writes "📞 Call initiated — +20..."); stripLeadingIcon
  // removes that so the row doesn't double up its icon visually.
  var ROW_ICON_STYLE = { fontSize:12, lineHeight:1, flexShrink:0, width:14, textAlign:"center", display:"inline-block" };
  var stripLeadingIcon = function(text){
    if (text == null) return "";
    // Drop any leading emoji-ish run (variation selectors, ZWJ joins) plus
    // following whitespace. Covers 📞 📅 📝 🤝 🔄 ⭐ 🛑 etc. Anything that
    // isn't ASCII at the start of the string is treated as an icon char.
    return String(text).replace(/^[^\x20-\x7E]+\s*/, "").trim();
  };
  var renderSubLine = function(ev, era, group){
    var type = ev.type;
    if (type === "status_change" || type === "status_changed") {
      var to = extractStatus(ev) || "NewLead";
      // Show ONLY the new (target) status — the "from" pill and arrow were
      // dropped (read confusingly, e.g. "New Lead → Call Back"). The mandatory
      // feedback saved with this status change still renders beneath the pill,
      // UNLESS a sibling feedback row in the same action group already renders
      // the same text (avoid showing it twice). Single-event rows
      // (group == null) always show it.
      var groupHasFb = !!(group && group.subEvents && group.subEvents.some(function(s){
        return s && (s.type === "feedback" || s.type === "feedback_added") &&
               String(s.feedback || s.note || "").trim();
      }));
      var scFb = groupHasFb ? "" : maskPhonesInText(extractEntryFeedback(ev));
      return <div style={{ fontSize:bodyFs, color:C.text }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
          <span style={ROW_ICON_STYLE}>🔄</span>
          <span style={{ color:sColor(to), fontWeight:700 }}>{sLabel(to)}</span>
        </div>
        {scFb ? <div style={{ marginTop:4, padding:"6px 9px", background:"#F8FAFC", borderRadius:6, border:"1px solid #EEF1F5", fontSize:bodyFs, color:C.text, lineHeight:1.4, wordBreak:"break-word" }}>💬 {scFb}</div> : null}
      </div>;
    }
    if (type === "feedback" || type === "feedback_added") {
      var ws = whileStatusFor(ev, era);
      var fbText = maskPhonesInText(stripActorPrefix(ev.feedback || ev.note || ""));
      // Slate panel + soft shadow. Box is explicit display:block + width:100%
      // + boxSizing:border-box so it always fills the body column (defensive
      // against any future flex parent that might shrink-to-fit). Pill is
      // inline-block at the start of the text so wrapped lines flow under
      // the pill using the full width of the box. marginRight:8 + a leading
      // space character in the text guarantees a visible gap between pill
      // and content even on subpixel rounding.
      return <div style={{ display:"block", width:"100%", boxSizing:"border-box", padding:"7px 10px", background:"#F8FAFC", borderRadius:6, border:"1px solid #EEF1F5", boxShadow:"0 1px 3px rgba(15, 23, 42, 0.06)", fontSize:bodyFs, color:C.text, lineHeight:1.45, wordBreak:"break-word" }}>
        <span style={{ display:"inline-block", verticalAlign:"baseline", fontSize:9, fontWeight:600, color:sliceStatusPillTextColor(ws), background:sliceStatusPillColor(ws), padding:"2px 6px", borderRadius:4, marginRight:8, whiteSpace:"nowrap", lineHeight:1.2 }}>{sLabel(ws)}</span>
        <span>{fbText || <span style={{ color:C.textLight, fontStyle:"italic" }}>(empty feedback)</span>}</span>
      </div>;
    }
    if (type === "callback_scheduled") {
      var cbTime = ev.scheduledFor || ev.time || ev.callbackTime || null;
      var cbLabel = cbTime ? fmtDateTime12(cbTime, undefined, { year:false, sep:", " }) : (ev.note || "");
      return <div style={{ fontSize:bodyFs, color:C.text, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
        <span style={ROW_ICON_STYLE}>📅</span>
        <span style={{ fontWeight:600 }}>{cbLabel}</span>
      </div>;
    }
    if (type === "note") {
      var noteText = maskPhonesInText(stripActorPrefix(ev.note || ""));
      // Same inline-flow + full-width treatment as feedback so wrapped lines
      // use the full box width. Differentiation from feedback comes via the
      // 📝 icon (vs the colored status pill on feedback).
      return <div style={{ display:"block", width:"100%", boxSizing:"border-box", padding:"7px 10px", background:"#F8FAFC", borderRadius:6, border:"1px solid #EEF1F5", boxShadow:"0 1px 3px rgba(15, 23, 42, 0.06)", fontSize:bodyFs, color:C.text, lineHeight:1.45, wordBreak:"break-word" }}>
        <span style={{ display:"inline-block", verticalAlign:"baseline", marginRight:8, fontSize:12, lineHeight:1.2 }}>📝</span>
        <span>{noteText || <span style={{ color:C.textLight, fontStyle:"italic" }}>(empty note)</span>}</span>
      </div>;
    }
    if (type === "call") {
      var callText = maskPhonesInText(stripLeadingIcon(ev.note || ""));
      return <div style={{ fontSize:bodyFs, color:C.text, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
        <span style={ROW_ICON_STYLE}>📞</span>
        <span>{callText || "Call initiated"}</span>
      </div>;
    }
    if (type === "meeting") {
      var meetText = maskPhonesInText(stripLeadingIcon(ev.note || ""));
      return <div style={{ fontSize:bodyFs, color:C.text, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
        <span style={ROW_ICON_STYLE}>🤝</span>
        <span>{meetText || "Meeting"}</span>
      </div>;
    }
    if (type === "assigned" || type === "first_assigned") {
      var src = (type === "first_assigned" && era.source) ? " · source: " + era.source : "";
      return <div style={{ fontSize:bodyFs, color:C.text }}>Assigned fresh as <span style={{ color:sColor("NewLead"), fontWeight:700 }}>New Lead</span>{src}</div>;
    }
    if (type === "rotated" || type === "reassigned") {
      return <div style={{ fontSize:bodyFs, color:C.text }}>Assigned fresh as <span style={{ color:sColor("NewLead"), fontWeight:700 }}>New Lead</span></div>;
    }
    return <div style={{ fontSize:bodyFs, color:C.text }}>{maskPhonesInText(ev.note || "") || type}</div>;
  };

  // Phase 4: single-event row. Renders the typed body via renderSubLine (the
  // unified per-type renderer above) and wraps it in a 2-col row whose left
  // column carries the timestamp. When `suppressTs` is true (cluster-by-minute
  // case — previous row already displayed the same fmtTs), the left column
  // renders empty space to keep body alignment intact.
  //
  // FULL-WIDTH GUARANTEE (Fix 4, third attempt): the outer wrapper uses CSS
  // grid with a fixed-width timestamp column + a `1fr` body column. Grid's
  // `1fr` is deterministic — it always grabs the remaining space — whereas
  // `flex:1` can collapse to content under certain flex-basis interpretations.
  // The body cell has minWidth:0 (so child text + ellipsis can shrink below
  // intrinsic width) but no width:auto fallback that would let the box
  // shrink-to-fit. Result: the feedback / note box below ALWAYS spans from
  // the right edge of the timestamp column to the right edge of the era
  // card's inner padding. Do not switch this back to flex without verifying.
  var renderEvent = function(ev, era, idx, noTopBorder, suppressTs){
    var body = renderSubLine(ev, era, null);
    var hideTop = (typeof noTopBorder === "boolean") ? noTopBorder : (idx === 0);
    return <div key={ev._id || (era.agentName+"-"+idx)} style={{ display:"grid", gridTemplateColumns:(isPanel?"84px":"96px")+" 1fr", columnGap:10, padding:"6px 0", borderTop:hideTop?"none":"1px solid rgba(0,0,0,0.04)" }}>
      <div style={{ fontSize:metaFs, color:C.textLight, paddingTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{suppressTs ? "" : fmtTs(ev.createdAt)}</div>
      <div style={{ minWidth:0 }}>{body}</div>
    </div>;
  };

  // Render one action (a group of 1+ events). Single-event groups fall
  // through to the existing renderEvent layout. Multi-event groups render as
  // a single row whose right column is a stack of labeled sub-event blocks
  // ordered by category (Status → Feedback → Callback → Note → Call →
  // Meeting), with chronological order preserved within each category.
  var SUB_CATEGORY_ORDER = { status_change:0, status_changed:0, feedback:1, feedback_added:1, callback_scheduled:2, note:3, call:4, meeting:5 };
  var renderAction = function(action, era, chronoIdx, noTopBorder, suppressTs){
    if (!action.isGroup) {
      return renderEvent(action.subEvents[0], era, chronoIdx, noTopBorder, suppressTs);
    }
    var hideTop = (typeof noTopBorder === "boolean") ? noTopBorder : (chronoIdx === 0);
    var ordered = action.subEvents.slice().sort(function(a, b){
      var ca = (SUB_CATEGORY_ORDER[a.type] != null) ? SUB_CATEGORY_ORDER[a.type] : 99;
      var cb = (SUB_CATEGORY_ORDER[b.type] != null) ? SUB_CATEGORY_ORDER[b.type] : 99;
      if (ca !== cb) return ca - cb;
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
    // Same CSS-grid 2-col layout as renderEvent (see FULL-WIDTH GUARANTEE
    // comment there). The body cell uses `1fr` to deterministically fill the
    // remaining space after the fixed-width timestamp column.
    return <div key={action._id} style={{ display:"grid", gridTemplateColumns:(isPanel?"84px":"96px")+" 1fr", columnGap:10, padding:"6px 0", borderTop:hideTop?"none":"1px solid rgba(0,0,0,0.04)" }}>
      <div style={{ fontSize:metaFs, color:C.textLight, paddingTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{suppressTs ? "" : fmtTs(action.createdAt)}</div>
      <div style={{ minWidth:0 }}>
        {ordered.map(function(sub, si){
          return <div key={si} style={{ padding: si === 0 ? "0" : "4px 0 0", lineHeight:1.4, width:"100%", boxSizing:"border-box" }}>
            {renderSubLine(sub, era, action)}
          </div>;
        })}
      </div>
    </div>;
  };

  var renderEra = function(era, idx){
    // Knock-on #3: key expandedEras by agentId/name (stable across merges)
    // rather than bucket index, so toggle state survives re-renders after
    // the tenure-merge pass reorders eras.
    var eraKey = era.agentId ? String(era.agentId) : ("name:" + (era.agentName || "Unknown"));
    var initials = (era.agentName||"?").split(/\s+/).filter(Boolean).map(function(w){return w[0]||"";}).slice(0,2).join("").toUpperCase() || "?";
    // Phase 4: era cards now match the "Agents on this lead" card chrome —
    // pure white background, 1px outer border, 3px left accent stripe whose
    // color carries the current/previous distinction. Conflict styling
    // unchanged (red border + ⚠ Conflict tag from the existing logic below).
    var leftAccent = era.isCurrent ? "#1D9E75" : "#94A3B8";
    var outerBorder = era.isCurrent ? "#1D9E75" : C.border;
    var conflict = era.isConflict && isAdmin;
    var cardStyle = conflict ? {
      border:"2px solid #A32D2D",
      borderLeft:"4px solid #A32D2D",
      background:"#FCEBEB",
      borderRadius:10,
      padding:eraPad,
      marginBottom:10,
      position:"relative"
    } : {
      border:"1px solid "+outerBorder,
      borderLeft:"3px solid "+leftAccent,
      background:"#fff",
      borderRadius:10,
      padding:eraPad,
      marginBottom:10,
      position:"relative"
    };
    var actions = era.actions || [];
    // Collapse repeated NewLead displays inside one era. The era starts as
    // NewLead by definition (the assignment row reads "Assigned fresh as
    // New Lead"); every subsequent status_change to NewLead is redundant
    // noise. Walk chronologically, keep the first event whose displayed
    // status resolves to NewLead, drop later ones. Other eras keep their
    // own first NewLead entry independently.
    var resolvesToNewLead = function(ev){
      var t = ev && ev.type;
      if (t === "status_change" || t === "status_changed") {
        var s = extractStatus(ev) || "NewLead";
        return s === "NewLead" || s === "New Lead";
      }
      if (t === "assigned" || t === "first_assigned" || t === "rotated" || t === "reassigned") return true;
      return false;
    };
    var seenNewLead = false;
    var filteredActions = [];
    actions.forEach(function(action){
      var keptSubs = [];
      action.subEvents.forEach(function(se){
        if (resolvesToNewLead(se)) {
          if (!seenNewLead) { seenNewLead = true; keptSubs.push(se); }
        } else {
          keptSubs.push(se);
        }
      });
      if (keptSubs.length === 0) return;
      if (keptSubs.length === action.subEvents.length) {
        filteredActions.push(action);
      } else {
        filteredActions.push(Object.assign({}, action, {
          subEvents: keptSubs,
          isGroup: keptSubs.length > 1
        }));
      }
    });
    actions = filteredActions;
    var actionsCount = actions.length;
    // Build rows in chronological order (actions + silence pills computed
    // from chrono gaps), then reverse for display so newest actions sit on
    // top. Silence pills are placed between consecutive groups based on the
    // gap from the previous group's last sub-event to this group's first.
    var chronoItems = [];
    var GAP = 48 * 60 * 60 * 1000;
    // Knock-on #5: inline tenure markers. Tenure 0's start is the era's own
    // startedAt (rendered via the outer renderSeparator above the card), so
    // we only need markers for tenures 1..N. Walk pendingTenureStarts in
    // parallel with the actions loop; emit a marker BEFORE any action whose
    // timestamp crosses the next pending tenure boundary.
    var pendingTenureStarts = (era.tenureStarts || []).slice(1);
    var pendingTenureMeta = (era.tenureMeta || []).slice(1);
    var __tiCounter = 0;
    actions.forEach(function(action, i){
      while (pendingTenureStarts.length > 0 && new Date(action.createdAt) >= new Date(pendingTenureStarts[0])) {
        var markerStart = pendingTenureStarts.shift();
        pendingTenureMeta.shift();
        var ti = __tiCounter++;
        chronoItems.push({ kind: "tenureMarker", node:
          <div key={"tenure-"+idx+"-"+i+"-"+ti} style={{ display:"flex", justifyContent:"center", margin:"6px 0" }}>
            <div style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 10px", borderRadius:12, background:"#FAEEDA", color:"#633806", fontSize:10, fontWeight:600 }}>
              ↻ Re-assigned · {new Date(markerStart).toLocaleString("en-US",{month:"short",day:"numeric"})}
            </div>
          </div>
        });
      }
      if (i > 0) {
        var prevAction = actions[i-1];
        var prevLast = prevAction.subEvents[prevAction.subEvents.length - 1];
        var prevType = prevLast.type;
        var prevIsAssign = prevType==="assigned" || prevType==="first_assigned" || prevType==="rotated" || prevType==="reassigned";
        if (!prevIsAssign) {
          var gapMs = new Date(action.createdAt) - new Date(prevLast.createdAt);
          if (gapMs > GAP) {
            var days = Math.round(gapMs / (24*60*60*1000));
            chronoItems.push({ kind:"silence", node:
              <div key={"silence-"+idx+"-"+i} style={{ display:"flex", justifyContent:"center", margin:"8px 0" }}>
                <div style={{
                  display:"inline-flex", alignItems:"center", gap:6,
                  padding:"6px 12px", borderRadius:14,
                  border:"1px dashed #BA7517", background:"#FAEEDA",
                  color:"#633806", fontStyle:"italic", fontSize:10
                }}>
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#BA7517" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  — {days} day{days===1?"":"s"} of silence —
                </div>
              </div>
            });
          }
        }
      }
      chronoItems.push({ kind:"action", action: action, chronoIdx: i });
    });
    var displayItems = chronoItems.slice().reverse();

    // Collapse to the newest 5 actions when the era has more than 5. Silence
    // pills that sit between two visible actions stay; pills that would land
    // adjacent to a hidden action are dropped (achieved naturally by slicing
    // up to and including the 5th action seen in display order).
    var isExpanded = !!expandedEras[eraKey];
    var visibleItems;
    if (actionsCount > 5 && !isExpanded) {
      visibleItems = [];
      var actionsSeen = 0;
      for (var vi = 0; vi < displayItems.length; vi++) {
        var di = displayItems[vi];
        if (di.kind === "action") {
          actionsSeen++;
          if (actionsSeen > 5) break;
        }
        visibleItems.push(di);
      }
    } else {
      visibleItems = displayItems;
    }

    // Phase 4: cluster-by-minute — when two consecutive visible action rows
    // would display the same fmtTs string, hide the timestamp on the second
    // (and subsequent) so the user sees the date+time once per cluster.
    // Silence pills reset the tracker because they themselves delimit a time
    // jump; the post-silence row always shows its timestamp.
    var rows = [];
    var firstEventSeen = false;
    var lastShownTs = null;
    visibleItems.forEach(function(item){
      if (item.kind === "silence") { rows.push(item.node); lastShownTs = null; return; }
      if (item.kind === "tenureMarker") { rows.push(item.node); lastShownTs = null; return; }
      var noTopBorder = !firstEventSeen;
      firstEventSeen = true;
      var thisTs = fmtTs(item.action.createdAt);
      var suppressTs = (thisTs && thisTs === lastShownTs);
      if (!suppressTs) lastShownTs = thisTs;
      rows.push(renderAction(item.action, era, item.chronoIdx, noTopBorder, suppressTs));
    });

    var toggleBtn = null;
    if (actionsCount > 5) {
      toggleBtn = <button key={"toggle-"+idx} onClick={function(){
        setExpandedEras(function(prev){
          var next = Object.assign({}, prev);
          next[eraKey] = !prev[eraKey];
          return next;
        });
      }} onMouseEnter={function(e){ e.currentTarget.style.background = "#F8FAFC"; }}
         onMouseLeave={function(e){ e.currentTarget.style.background = "transparent"; }}
         style={{
        display:"block", width:"100%", marginTop:8,
        padding:8, border:"1px dashed #888780", background:"transparent",
        color:"#5F5E5A", fontSize:11, borderRadius:8, cursor:"pointer",
        fontFamily:"inherit"
      }}>
        {isExpanded ? "Show less ▴" : ("Show all " + actionsCount + " actions ▾")}
      </button>;
    }

    return <div key={"era-"+idx} style={cardStyle}>
      {conflict && <div style={{
        position:"absolute", top:-10, left:8,
        background:"#A32D2D", color:"#fff", fontSize:10,
        padding:"2px 8px", borderRadius:10, fontWeight:700
      }}>⚠ Conflict</div>}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
        <div style={{ width:28, height:28, borderRadius:"50%", background:avatarColor(era.agentName), color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, flexShrink:0 }}>{initials}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:bodyFs, fontWeight:700, color:C.text }}>{era.agentName}</div>
          <div style={{ fontSize:metaFs, color:C.textLight, marginTop:1 }}>
            {fmtRange(era.startedAt, era.endedAt, era.isCurrent)} · {actionsCount} action{actionsCount===1?"":"s"}{era.isCurrent ? "" : <> · ended at <span style={{ color:sColor(era.endedAtStatus), fontWeight:600 }}>{sLabel(era.endedAtStatus)}</span></>}{(era.tenureCount > 1) ? (" · Held the lead " + era.tenureCount + " times") : ""}
          </div>
        </div>
        <div style={{ flexShrink:0, fontSize:metaFs, fontWeight:700, padding:"2px 8px", borderRadius:10, background:era.isCurrent?"#1D9E75":"#888780", color:"#fff" }}>{era.isCurrent?"Current":"Previous"}</div>
      </div>
      {conflict && <div style={{ fontSize:10, color:"#A32D2D", fontStyle:"italic", marginBottom:8 }}>
        Previous agent had {sLabel(era.conflictPrevStatus)} — investigate
      </div>}
      <div>{rows}</div>
      {toggleBtn}
    </div>;
  };

  var renderSeparator = function(era, key){
    var meta = era.rotationMeta || { isAuto: era.auto === "auto", actor: null, reason: era.reason };
    var from = era.fromAgent || "Unassigned";
    var to = era.agentName || "Unknown";
    var ts = fmtTs(era.startedAt);
    var line2;
    if (meta.isAuto) {
      // Auto rotations have no meaningful actor — the rotation cron runs as
      // the outgoing agent. Skip the "by X" segment entirely.
      line2 = "Auto-rotated · " + translateAutoReason(meta.reason) + " · " + ts;
    } else {
      // Manual rotations: prefer era.actor (captured at era creation, has
      // structured name + role). Fall back to the parsed note actor when the
      // userId wasn't populated. Role label is omitted when unknown rather
      // than guessed.
      var actorName = (era.actor && era.actor.name) || meta.actor || null;
      var actorRoleLabel = era.actor ? roleLabel(era.actor.role) : null;
      var prefix;
      if (actorName) {
        prefix = "Manually rotated by " + actorName + (actorRoleLabel ? (" (" + actorRoleLabel + ")") : "");
      } else {
        prefix = "Manually rotated";
      }
      line2 = prefix + " · " + ts;
    }
    return <div key={key} style={{ margin:"6px 0 12px", padding:"8px 12px", background:"#FAEEDA", color:"#633806", borderRadius:10, fontSize:metaFs, lineHeight:1.45 }}>
      <div style={{ fontWeight:700 }}>↻ Rotated {from} → {to} · status reset to New Lead</div>
      <div style={{ marginTop:3, opacity:0.92 }}>{line2}</div>
    </div>;
  };

  var rankOf = function(s){
    var r = { DoneDeal:8, "Done Deal":8, EOI:7, MeetingDone:6, "Meeting Done":6, HotCase:5, "Hot Case":5, CallBack:4, "Call Back":4, Potential:3, NewLead:2, "New Lead":2, NoAnswer:1, "No Answer":1, NotInterested:0, "Not Interested":0 };
    return (r[s] == null) ? -1 : r[s];
  };
  var buildSummary = function(){
    if (!eras.length) return null;
    // Knock-on #2a: count rotations as the sum of tenures minus 1, NOT
    // eras.length - 1. After merge a 3-rotation chain that revisits an
    // agent collapses to 2 eras but still represents 2 rotations.
    var totalTenures = 0;
    eras.forEach(function(e){ totalTenures += (e.tenureCount || 1); });
    var rotations = totalTenures - 1;
    var firstTs = new Date(eras[0].startedAt).getTime();
    // Knock-on #2b: max endedAt across ALL eras — the "current" era may not
    // be the last in bucket order after merge.
    var lastTs = -Infinity;
    eras.forEach(function(e){
      var t = new Date(e.endedAt).getTime();
      if (!isNaN(t) && t > lastTs) lastTs = t;
    });
    if (!isFinite(lastTs)) lastTs = firstTs;
    var spanDays = Math.max(1, Math.round((lastTs - firstTs) / (24*60*60*1000)));
    // Knock-on #2c: bestEra scans events[] for highest-rank status, NOT
    // just the era's endedAtStatus. After merge an agent who hit HotCase
    // in tenure 1 then dropped to NoAnswer in tenure 2 would otherwise
    // lose their HotCase peak from the summary.
    var bestEra = eras[0];
    var bestRank = -1;
    eras.forEach(function(e){
      var localBest = -1;
      (e.events || []).forEach(function(ev){
        if (ev.type !== "status_change" && ev.type !== "status_changed") return;
        var s = extractStatus(ev);
        if (!s) return;
        var r = rankOf(s);
        if (r > localBest) localBest = r;
      });
      // Fallback: if this era has no status_change events, use endedAtStatus.
      if (localBest < 0) localBest = rankOf(e.endedAtStatus);
      if (localBest > bestRank) { bestRank = localBest; bestEra = e; }
    });
    var curStatus = (lead && lead.status) || "NewLead";
    if (rotations === 0) {
      return "Never rotated. Held by " + eras[0].agentName + " for " + spanDays + " day" + (spanDays===1?"":"s") + " — currently " + sLabel(curStatus) + ".";
    }
    var bestLabel = bestEra.isCurrent ? bestEra.agentName + " (current)" : bestEra.agentName + " (" + sLabel(bestEra.endedAtStatus) + ")";
    return "Rotated " + rotations + " time" + (rotations===1?"":"s") + " in " + spanDays + " day" + (spanDays===1?"":"s") + ". Best engagement with " + bestLabel + " — currently " + sLabel(curStatus) + ".";
  };
  var summary = isAdmin ? buildSummary() : null;
  var canCompare = isAdmin && eras.length >= 2;

  var out = [];
  if (canCompare) {
    out.push(<div key="cmp-row" style={{ display:"flex", justifyContent:"flex-end", marginBottom:8 }}>
      <button onClick={function(){ setShowCompare(lead, eras); }} style={{
        display:"inline-flex", alignItems:"center", gap:6,
        padding:"4px 10px", border:"1px solid #CBD5E1", borderRadius:8,
        background:"#fff", cursor:"pointer", fontSize:metaFs, color:C.text, fontWeight:600
      }}>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
        </svg>
        Compare agents
      </button>
    </div>);
  }
  if (summary) {
    out.push(<div key="summary" style={{
      background:"linear-gradient(135deg, #E6F1FB 0%, #F0F7FF 100%)",
      borderLeft:"3px solid #185FA5",
      borderRadius:10, padding:"10px 12px", marginBottom:10
    }}>
      <div style={{ fontSize:9, fontWeight:700, color:"#185FA5", letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>Summary</div>
      <div style={{ fontSize:bodyFs, color:C.text, lineHeight:1.5 }}>{summary}</div>
    </div>);
  }
  orderedEras.forEach(function(era, i){
    out.push(renderEra(era, i));
    if (i < orderedEras.length - 1) {
      var newer = orderedEras[i];
      if (newer && newer.isRotation) {
        out.push(renderSeparator(newer, "sep-"+i));
      }
    }
  });

  return <div style={{ maxWidth:maxW }}>{out}</div>;
};

// ===== PAGED LEADS FETCH =====
// One paged fetcher shared by every targeted Leads-page fetch (status chip,
// agent filter, campaign/project, inactive-agent, 🛑, 🌱).
//
// WHY: those six fetches each pulled `&limit=2000` in one shot with no paging.
// The cap silently truncated, and because ?status= is a SUPERSET predicate
// (status OR any assignments[] slice status) the fetch matched far more rows
// than the tab renders — NoAnswer matched 3,894 docs to render 1,575. Newest-
// first truncation therefore dropped real rows: measured 2026-08-11, 1,616 rows
// across four chips were unreachable, the oldest reachable row on NoAnswer
// being 2026-06-20 with April rows invisible. Raising the cap would not have
// fixed it; the fetch has to page.
//
// `qs` is the FULL query string minus paging (e.g. "/api/leads?status=NoAnswer
// &fields=summary"). Changing it — chip switch, agent change, date change —
// resets to page 1. `active` false means this fetcher owns nothing right now:
// it clears its rows and issues no request, so the six can coexist with only
// the relevant one in flight.
//
// Returns rows:null while unloaded (callers distinguish "not loaded" from
// "loaded, empty" — allVisible falls back to p.leads on null, not on []).
// `survives` (2026-08-14) — the caller's "would this row actually appear in the
// rendered list?" test. The chase loop below uses it to decide whether a page
// CONTRIBUTED, instead of asking whether the response was empty. Optional; when
// omitted every row counts as surviving, which is the old behaviour exactly.
function usePagedLeads(qs, token, bust, active, survives) {
  var [rows, setRows] = useState(null);
  var [page, setPage] = useState(1);
  var [totalPages, setTotalPages] = useState(1);
  var [total, setTotal] = useState(0);
  var [loading, setLoading] = useState(false);
  // Monotonic request id. A page-1 fetch bumps it, which invalidates every
  // in-flight response from a previous query string — without this, switching
  // chips quickly lets a slow earlier response land on top of the newer rows.
  // loadMore deliberately does NOT bump it: an append belongs to the query that
  // is already loaded, and must be discarded if that query has since changed.
  var reqRef = useRef(0);
  var pageRef = useRef(1);
  // In-flight guard as a REF, not the `loading` state. Virtuoso can fire
  // endReached twice before a re-render lands, so the guard has to be readable
  // and writable synchronously. It must also NOT live inside a setState updater:
  // index.js renders under React.StrictMode, which invokes updaters twice in
  // development, so a fetch started inside one would be issued twice per scroll.
  var busyRef = useRef(false);
  // How many rows are currently held, and the query they belong to. These let
  // the effect below tell a WS-driven REFRESH apart from a real query change.
  var loadedRef = useRef(0);
  var qsRef = useRef(null);
  // Held in a ref, not a useCallback dep: the caller rebuilds this closure every
  // render, and putting it in the dep array would rebuild loadMore every render
  // too — churning the identity Virtuoso holds onto.
  var survivesRef = useRef(survives);
  survivesRef.current = survives;

  useEffect(function(){
    if (!active || !token) {
      reqRef.current++;                       // cancel anything in flight
      busyRef.current = false;                // ...and release the loadMore guard
      setRows(null); setPage(1); pageRef.current = 1;
      loadedRef.current = 0; qsRef.current = null;
      setTotalPages(1); setTotal(0); setLoading(false);
      return;
    }
    // Reset the guard on every query change too: an in-flight loadMore from the
    // previous query is now orphaned by the reqRef bump, and its .finally would
    // otherwise be the only thing that could clear it.
    busyRef.current = false;
    // REFRESH vs NEW QUERY. `bust` is in this effect's deps and ticks 1500ms
    // after ANY WS lead/DR burst (App.js bumpCbBust) — measured median gap ~8.6s
    // during active use. Until 2026-08-14 every one of those ticks refetched
    // page 1 and did `setRows(data); pageRef.current = 1`, throwing away every
    // page the user had scrolled to load. On a large chip tab (NoAnswer ~1.6k)
    // that collapsed the list to 100 rows underneath the scroll position, so
    // Virtuoso re-anchored, endReached refired, and loadMore re-fetched pages
    // the client had already had — the "freezes, then resumes" the owner
    // reported. Same query + rows already held = refresh the LOADED WINDOW in
    // one call and leave pagination where it is.
    //
    // The refresh deliberately re-reads ONE PAGE, not the whole held window.
    // The App-level fetchLeadsLight precedent (~L34379) re-reads up to 250 rows
    // on the theory that 250 stays inside a ~1.5 MB budget — but that was
    // measured on the NEWEST leads (0.44 MB per 100). Chip tabs are not the
    // newest leads: measured on prod 2026-08-14, one 100-row NoAnswer page is
    // 913.5 KB and 250 rows is 2,390 KB, because older leads carry more
    // assignment slices. Refreshing 250 here would triple the payload of a tick
    // that fires every ~8.6s, which is a real regression for a user who is
    // parked on a deep-scrolled tab and not scrolling at all.
    //
    // One page costs exactly what today's reset already costs (913.5 KB, 1 RTT),
    // so this fix is free on the wire: the merge below keeps the rows the reset
    // used to discard. TRADE-OFF, stated plainly: rows past the first page are
    // not re-read on a bust tick, so they can go stale until the query changes.
    // That is strictly better than the current behaviour, which does not leave
    // them stale — it deletes them outright on every tick.
    var isRefresh = qsRef.current === qs && loadedRef.current > 0;
    qsRef.current = qs;
    var want = 100;
    var myId = ++reqRef.current;
    // A background refresh must not flip `loading` — the list stays on screen
    // and nothing should flash. Only a real query change is a load.
    if (!isRefresh) setLoading(true);
    // Page 1 through sharedApiGet: a burst that refreshes this query while an
    // identical request is in flight (or just settled) rides that one. loadMore
    // stays on apiFetch — a scroll is never a duplicate of anything.
    sharedApiGet(qs + "&page=1&limit=" + want, token)
      .then(function(r){
        if (reqRef.current !== myId) return;
        var data = (r && Array.isArray(r.data)) ? r.data : (Array.isArray(r) ? r : []);
        if (isRefresh) {
          setRows(function(prev){
            // Refetched at least as many rows as we held → the window is fully
            // covered, take it as-is (the common case).
            if (!Array.isArray(prev) || prev.length <= data.length) return data;
            // Scrolled past page 1: refresh the head, keep the tail. Dedup by
            // _id so a lead created since the last load (which shifts the whole
            // list by one) can't appear twice.
            var seen = {};
            data.forEach(function(l){ if (l && l._id) seen[String(l._id)] = true; });
            return data.concat(prev.filter(function(l){ return l && l._id && !seen[String(l._id)]; }));
          });
          // page / pageRef deliberately UNTOUCHED — this is the whole point of
          // the fix. The held window did not shrink, so the next loadMore must
          // continue from where the user actually is, not from page 2.
          //
          // totalPages IS safe to refresh: the request above uses limit=100, the
          // same unit pageRef counts in. It would NOT be safe under a larger
          // refresh limit — the server computes ceil(pageableTotal / limit), so
          // a limit=250 response states pages in units of 250 and writing that
          // back would mix units and re-create the stop-early bug 5985044 fixed.
          // If the refresh limit ever changes, this line has to move with it.
          // Functional update, NOT a read of `totalPages` from the closure:
          // capturing it would put it in this effect's dep array, and the effect
          // refetches page 1 — so every page load would retrigger it.
          setTotalPages(function(prevTp){
            return (r && typeof r.totalPages === "number") ? r.totalPages : prevTp;
          });
          setTotal((r && typeof r.total === "number") ? r.total : data.length);
        } else {
          setRows(data);
          setPage(1); pageRef.current = 1;
          setTotalPages((r && typeof r.totalPages === "number") ? r.totalPages : 1);
          setTotal((r && typeof r.total === "number") ? r.total : data.length);
        }
      })
      // A failed refresh must leave the loaded rows alone; only a failed
      // first load renders the empty state.
      .catch(function(){ if (reqRef.current === myId && !isRefresh) setRows([]); })
      .finally(function(){ if (reqRef.current === myId && !isRefresh) setLoading(false); });
  }, [qs, token, bust, active]);

  // Keep the held-row count fresh for the refresh branch above. Same shape as
  // the App-level leadsLoadedCountRef (~L33915).
  useEffect(function(){
    loadedRef.current = Array.isArray(rows) ? rows.length : 0;
  }, [rows]);

  var loadMore = useCallback(function(){
    if (!active || !token) return;
    if (busyRef.current) return;                    // a page is already in flight
    if (pageRef.current >= totalPages) return;      // nothing left to load
    var myId = reqRef.current;
    busyRef.current = true;
    setLoading(true);
    // A page is a slice of the RAW query result: the server applies the
    // per-slice visibility gate AFTER skip/limit, so a page can legitimately
    // come back EMPTY with matching rows still further down (a full-book agent
    // has ~90% of their scope hidden by staleness, so a run of 100 raw docs
    // yielding nothing is ordinary, not pathological). Stopping there would
    // stall the list for good: the rows never grow, so Virtuoso never fires
    // endReached again, so loadMore is never called again — the badge would go
    // on counting leads no amount of scrolling could reach. Keep pulling pages
    // until one contributes rows or the pages run out.
    var MAX_CHAIN = 30;   // ~the largest raw page count in the book today (29)
    var chase = function(hops){
      var next = pageRef.current + 1;
      pageRef.current = next;                       // claim it synchronously
      return apiFetch(qs + "&page=" + next + "&limit=100", "GET", null, token)
        .then(function(r){
          if (reqRef.current !== myId) return;       // query changed mid-flight
          var data = (r && Array.isArray(r.data)) ? r.data : (Array.isArray(r) ? r : []);
          setRows(function(prev){
            var seen = {};
            (prev || []).forEach(function(l){ if (l && l._id) seen[String(l._id)] = 1; });
            // Dedup on append: a lead mutated between pages can shift across the
            // skip boundary and arrive twice, which would render a duplicate row.
            return (prev || []).concat(data.filter(function(l){ return l && l._id && !seen[String(l._id)]; }));
          });
          setPage(next);
          var tp = (r && typeof r.totalPages === "number") ? r.totalPages : totalPages;
          if (r && typeof r.totalPages === "number") setTotalPages(r.totalPages);
          if (r && typeof r.total === "number") setTotal(r.total);
          // CONTRIBUTION, not raw length (fixed 2026-08-14). This used to test
          // `!data.length`, i.e. "did the server return nothing?". But the stall
          // it was written to prevent is "did the LIST grow?", and those differ
          // whenever a full page is dropped by the client-side predicate: the
          // rows arrive, none of them render, `filtered` does not change length,
          // Virtuoso never re-fires endReached — and because endReached is the
          // only thing that calls loadMore, the list is stuck there permanently
          // with pages still unread.
          //
          // Reproduced on a scratch collection 2026-08-14 (250 docs whose middle
          // page is entirely excluded): the old rule reached 100 of 150
          // reachable rows and stopped at page 2 of 3; this rule reaches all 150.
          //
          // Still read off the RESPONSE, not off the dedup result: the append
          // happens inside a setState updater which React may run twice
          // (StrictMode) and after this line, so a count taken there could not be
          // trusted.
          var sFn = survivesRef.current;
          var contributed = sFn ? data.filter(sFn).length : data.length;
          if (!contributed && next < tp && hops + 1 < MAX_CHAIN) return chase(hops + 1);
        });
    };
    chase(0)
      .catch(function(){ if (reqRef.current === myId) pageRef.current = Math.max(1, pageRef.current - 1); })
      .finally(function(){
        busyRef.current = false;
        if (reqRef.current === myId) setLoading(false);
      });
  }, [qs, token, active, totalPages]);

  return {
    rows: rows, loading: loading, total: total,
    hasMore: page < totalPages, loadMore: loadMore
  };
}

// ===== LEADS PAGE =====
var LeadsPage = function(p) {
  var leadSourcesVersion = useLeadSourcesVersion();   // re-render when the lead source registry lands (src/lib/leadSources.js)
  LEADSPROF("renders"); // diagnostic only — see LEADSPROF
  var t = p.t;
  // Today's per-agent receipts — feeds the load column in the agent pickers.
  var assignedToday = useAssignedToday(p.token);
  // Dropdown (Change Status) options — excludes "Deal Cancelled" everywhere on the Leads page.
  var sc = visibleStatuses(STATUSES(t), p.cu&&p.cu.role).filter(function(s){ return s.value!=="Deal Cancelled"; });
  // Top filter-tab options — also hide EOI and DoneDeal (they have their own pages).
  var tabSc = sc.filter(function(s){ return s.value!=="EOI" && s.value!=="DoneDeal"; });
  var isAdmin = p.cu.role==="admin"||p.cu.role==="operation"||positionOf(p.cu)==="director"||positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader"; var isOnlyAdmin = p.cu.role==="admin"||p.cu.role==="operation";
  var salesUsers = p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;});
  // Filter dropdown only — includes inactive agents (active first, then inactive)
  // so admins can still filter to historical leads owned by deactivated users.
  // Never use for assignment selects: inactive users must stay excluded there.
  var salesUsersForFilter = (function(){var act=p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;});var ina=p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&!u.active;});return act.concat(ina);})();
  var agentFilterOpts = salesUsersForFilter.map(function(u){ return { value:gid(u), label:u.name+(u.active?"":" (inactive)") }; });
  var isManager = positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader";
  var myTeamUsers = p.myTeamUsers || salesUsers;
  var isReq = !!p.isRequest;

  // ---- State declarations (must be before filter logic) ----
  var [selected, setSelected] = useState(null);
  // Defensive belt-and-suspenders on top of Fix A + Fix B (commit c34cb44).
  // After the STEP 4-5 bootstrap shrink, some users continued to see a
  // blank panel on the right even with the gid(selected) guard. The
  // hypothesis: `selected` is set to a degenerate value (e.g. a search-
  // projection rehydration that ships only {_id} with no name/phone/etc.)
  // and the gid() check passes — but the panel body has nothing to render.
  // We now require a renderable identity field (name or phone) AND clear
  // the degenerate state via the useEffect below so the page recovers on
  // the next paint.
  var hasRenderableSelected = !!(selected && gid(selected) && (selected.name || selected.phone));
  useEffect(function(){
    if (selected && !hasRenderableSelected) {
      // eslint-disable-next-line no-console
      console.warn("[LeadsPage] Clearing degenerate `selected`:", selected);
      setSelected(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selected, hasRenderableSelected]);

  // ===== TEMPORARY DEBUG — slice.status diagnosis (admin-only, deletable) =====
  // When an admin opens a lead detail panel, fetch the FULL doc via
  // GET /api/leads/:id (the list object has agentHistory[] stripped by the
  // summary projection) and dump each slice's real .status alongside its
  // agentHistory so we can see whether a "MeetingDone" slice carries a
  // matching status_change entry (and whether that entry has author fields,
  // distinguishing a genuine agent action from a holder-sync mirror write).
  // Read-only (GET). Remove this whole block after diagnosis.
  var debugLeadIdRef = useRef("");
  useEffect(function(){
    var isAdminOnly = p.cu && (p.cu.role === "admin" || p.cu.role === "operation");
    if (!isAdminOnly || !selected || !gid(selected) || !p.token) return;
    var lid = gid(selected);
    if (debugLeadIdRef.current === lid) return;   // only once per opened lead
    debugLeadIdRef.current = lid;
    apiFetch("/api/leads/" + lid, "GET", null, p.token).then(function(full){
      if (!full || !full._id) return;
      var holderId = full.agentId && full.agentId._id ? String(full.agentId._id) : String(full.agentId || "");
      var canon = function(s){ return String(s || "").replace(/\s/g, ""); };
      // Top-level lead.history "status_changed" rows — these ARE author-tagged
      // (byUser = the name of whoever made the PUT). A genuine self-action by
      // agent X produces a row with byUser === X. A pre-703ef2f mirror write
      // onto X's slice produced its only history row under the ROTATED-OFF
      // caller's name, NOT X — so X's mirror entry has no matching row here.
      var histSC = (full.history || []).filter(function(h){ return h && h.event === "status_changed"; }).map(function(h){
        var nm = String(h.description || "").match(/→\s*(.+?)\s+by\s+/i);
        return {
          byUser: String(h.byUser || ""),
          newStatus: nm && nm[1] ? nm[1].trim() : "",
          ts: new Date(h.timestamp || h.time || h.createdAt || 0).getTime(),
          description: String(h.description || "")
        };
      });
      var rows = (full.assignments || []).map(function(a){
        var aId = a.agentId && a.agentId._id ? String(a.agentId._id) : String(a.agentId || "");
        var aName = userNameOf(a.agentId, p.users) || "Unknown";
        var hist = Array.isArray(a.agentHistory) ? a.agentHistory : [];
        var statusChanges = hist.filter(function(h){ return h && h.type === "status_change"; }).map(function(h){
          var m = String(h.note || "").match(/Status:\s*(.+)$/i);
          var st = (m && m[1] ? m[1].trim() : (h.status || "?"));
          var fb = (h.feedback != null && String(h.feedback).trim() !== "") ? String(h.feedback).trim() : "";
          var entryTs = new Date(h.createdAt || h.at || 0).getTime();
          // Cross-reference: is there an author-tagged top-level history row for
          // THIS slice's own agent, same target status, within ±2 min?
          var match = histSC.find(function(H){
            return H.byUser === aName && canon(H.newStatus) === canon(st) && Math.abs(H.ts - entryTs) <= 120000;
          });
          return {
            status: st,
            createdAt: h.createdAt || h.at || null,
            note: h.note != null ? String(h.note) : null,
            hasAuthorId: !!h.authorId,
            hasAuthorName: !!h.authorName,
            hasAuthorRole: !!h.authorRole,
            hasAuthor: !!(h.authorId || h.authorName || h.authorRole),   // (all false on this lead — unreliable)
            hasFeedback: !!fb,                                            // mirror writes carry no feedback
            feedback: fb,
            // Author-tagged history row by THIS agent for this status+time?
            historyMatchByOwnAgent: !!match,
            historyMatchBy: match ? match.byUser : "",
            // Refined mirror signature: feedback-less AND no self-authored
            // history row corroborating it.
            looksLikeMirror: !fb && !match
          };
        });
        var matchesOwnStatus = statusChanges.some(function(e){ return canon(e.status) === canon(a.status); });
        return {
          agent: aName,
          agentId: aId,
          sliceStatus: a.status || "(none)",
          isCurrentHolder: !!(aId && holderId && aId === holderId),
          removed: !!a.removedAt,
          lastFeedback: a.lastFeedback != null ? String(a.lastFeedback) : "",
          notes: a.notes != null ? String(a.notes) : "",
          notesAuthorName: a.notesAuthorName || "",
          notesAuthorRole: a.notesAuthorRole || "",
          lastActionAt: a.lastActionAt || null,
          statusChangedAt: a.statusChangedAt || null,
          assignedAt: a.assignedAt || null,
          historyStatusChangeCount: statusChanges.length,
          historyHasMatchingStatusChange: matchesOwnStatus,
          statusChanges: statusChanges
        };
      });
      /* eslint-disable no-console */
      console.log("%c[SLICE-DEBUG] " + (full.name || "(no name)") + " · " + lid, "color:#B91C1C;font-weight:700");
      console.log("[SLICE-DEBUG] current holder agentId:", holderId);
      console.table(rows.map(function(r){ return {
        agent: r.agent, sliceStatus: r.sliceStatus, holder: r.isCurrentHolder,
        removed: r.removed, statusChanges: r.historyStatusChangeCount,
        matchesOwnStatus: r.historyHasMatchingStatusChange
      }; }));
      console.log("[SLICE-DEBUG] full per-slice detail (with status_change author info):", rows);
      // Focused view: strong-status slices (MeetingDone/DoneDeal/EOI) only —
      // dump each of their status_change entries' raw fields so we can read
      // the author/feedback signal that distinguishes a genuine agent action
      // from a holder-sync mirror write.
      var STRONG = { MeetingDone:1, "Meeting Done":1, DoneDeal:1, "Done Deal":1, EOI:1 };
      var strongRows = rows.filter(function(r){ return STRONG[r.sliceStatus]; });
      console.log("%c[SLICE-DEBUG] STRONG-status slices: " + strongRows.length, "color:#9A3412;font-weight:700");
      strongRows.forEach(function(r){
        var matchEntries = r.statusChanges.filter(function(e){ return canon(e.status) === canon(r.sliceStatus); });
        // Polluted IFF every same-status entry is feedback-less AND has no
        // self-authored history row — i.e. NO genuine evidence the agent set it.
        var noGenuineEvidence = matchEntries.length > 0 && matchEntries.every(function(e){ return e.looksLikeMirror; });
        console.log(
          "%c  • " + r.agent + " → " + r.sliceStatus +
          "  | same-status entries: " + matchEntries.length +
          " | feedback-bearing: " + matchEntries.filter(function(e){return e.hasFeedback;}).length +
          " | self-authored history rows: " + matchEntries.filter(function(e){return e.historyMatchByOwnAgent;}).length +
          " | NO genuine evidence (→ polluted): " + noGenuineEvidence,
          "color:" + (noGenuineEvidence ? "#B91C1C" : "#15803D") + ";font-weight:600"
        );
        console.log("      lastFeedback:", JSON.stringify(r.lastFeedback), "| notes:", JSON.stringify(r.notes), "| notesAuthor:", r.notesAuthorName || "(none)", r.notesAuthorRole || "");
        console.table(r.statusChanges.map(function(e){ return {
          status: e.status, createdAt: e.createdAt,
          hasFeedback: e.hasFeedback,
          selfAuthoredHistory: e.historyMatchByOwnAgent,
          looksLikeMirror: e.looksLikeMirror,
          feedback: e.feedback, note: e.note
        }; }));
      });
      console.log("[SLICE-DEBUG] raw top-level lead.history:", full.history);
      console.log("[SLICE-DEBUG] parsed status_changed history rows (author-tagged):", histSC);
      console.log("[SLICE-DEBUG] raw assignments (full doc):", full.assignments);
      /* eslint-enable no-console */
    }).catch(function(err){
      /* eslint-disable-next-line no-console */
      console.warn("[SLICE-DEBUG] fetch failed:", err && err.message);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selected, p.token]);

  // Close the side panel when the user clicks anywhere outside of it.
  var panelRef = useOutsideClose(hasRenderableSelected, function(){ setSelected(null); });
  // Tab-badge counts — server-side aggregation against the same scope the
  // rendered table uses. Sales role passes ?scope=historical, which routes
  // to the fetch-then-tally branch that mirrors GET /api/leads's sales
  // overlay + Phase P staleness filter + LeadsPage allVisible exclusions,
  // grouped by Jana's per-slice status. Other roles use the existing
  // strict-current-owner /counts response. In both cases byStatus[k]
  // matches what allVisible.filter(currentStatus===k) would compute, but
  // server-side so badges are correct from first paint regardless of how
  // many pages of /api/leads the FE has loaded so far.
  //
  // Debounced 1000ms so a burst of WS lead_updated events (cbBust) doesn't
  // flood the endpoint — same pattern as the Sidebar fetch.
  //
  // Responses are kept PER QUESTION — keyed by the exact URL the filters build —
  // and the badges read only the entry for the URL the current filters build
  // (`leadsPageCounts`, derived below the effect). Storing one bare body is how
  // a narrowed period showed the previous period's numbers: the old body stayed
  // on screen through the debounce + request, and forever when the request
  // failed, because the failure was swallowed. Keyed per URL, a period the user
  // has already seen shows its own numbers again at once while a refresh runs,
  // and a period never asked keeps the previous numbers muted until its first answer lands (leadsChipCountsView). countsErrUrl
  // marks the URL whose latest request failed; countsRetry re-issues it.
  var [countsByUrl, setCountsByUrl] = useState(function(){ return COUNTS_MEMO[p.token] || {}; });
  var lastShownCountsRef = useRef(COUNTS_LAST[p.token] || null);
  var [countsErrUrl, setCountsErrUrl] = useState(null);
  var [countsRetry, setCountsRetry] = useState(0);
  // Monotonic sequence for the badge-counts fetch below. countsReqRef is bumped
  // when a request is ISSUED; the two per-URL maps hold, for each question, the
  // newest sequence issued and the newest applied. A response is applied
  // whenever it is newer than the last one applied FOR ITS OWN URL — so a
  // completed response is never thrown away, only an out-of-order one is, and
  // an All-Time answer landing after a newer Today answer is still the newest
  // answer to the All-Time question. countsAskedRef holds the key of the last
  // request actually issued, which is what decides whether the next one is a
  // refresh (debounced) or a new question (immediate) — see countsFetchDelayMs.
  var countsReqRef = useRef(0);
  var countsLatestByUrlRef = useRef({});
  var countsAppliedByUrlRef = useRef({});
  var countsAskedRef = useRef(null);
  var isSalesRole = p.cu && positionOf(p.cu) === "sales";
  // Rotation thresholds used by the client-side "Rotation Stopped" mirror
  // (leadRotationStopped below). The broadened chip counts age- and cap-based
  // stops, which need rotationStopAfterDays + maxRotationsPerLead. The server
  // count/list filter is the source of truth; these hydrate the FE fallback +
  // rsLeads re-filter so rendered rows match the badge. Defaults (45 / 0) match
  // ROTATION_DEFAULTS so the mirror is correct even before the fetch lands.
  var [rotStopDays, setRotStopDays] = useState(45);
  var [rotMaxCap, setRotMaxCap] = useState(0);
  useEffect(function(){
    if (!p.token || !isOnlyAdmin) return;
    var cancelled = false;
    apiFetch("/api/settings/rotation", "GET", null, p.token)
      .then(function(s){
        if (cancelled || !s || typeof s !== "object") return;
        setRotStopDays(Number(s.rotationStopAfterDays) || 45);
        setRotMaxCap(Number(s.maxRotationsPerLead) || 0);
      })
      .catch(function(){});
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, isOnlyAdmin]);
  // NOTE: the badge-counts fetch that used to live here now sits below the
  // dateRange/dateQuarter/dateFrom/dateTo useState calls (~L7030). It has to:
  // its dep array reads those vars, and at THIS point in the render they are
  // still the hoisted `undefined`, which would freeze the deps and stop the
  // badges refetching when the date chip changes. Same reason the statusLeads
  // effect is declared after lockedOnly.

  // ===== Campaign / Project dropdown filters (2026-08-04) — ALL ROLES =====
  // Server-side, so they work against the full book rather than the paginated
  // bootstrap slice. Values are the NORMALIZED base names returned by
  // /api/filter-options (variant-merged for campaign, case-folded for project);
  // the backend expands a base back to every raw spelling. "__none" selects
  // records with no value — rendered as the "(No campaign)" / "(No project)"
  // row and the sole replacement for the removed admin-only campaignMode probe
  // (2026-08-05): its "No campaign" mode folds into this sentinel, "Contains
  // text…" into SearchableSelect's search box, and "Has campaign" into picking
  // any named option (each already carries its own count).
  // MULTI-SELECT (2026-08-05): both hold ARRAYS of normalized bases; [] = no
  // filter. Several values ride as repeated ?campaign= / ?project= params and
  // come back as one $in per field, so "three campaigns" is one query.
  var [campaignFilter, setCampaignFilter] = useState([]);
  var [projectFilter, setProjectFilter] = useState([]);
  // Unit Type filter (2026-09-16) — rides the SAME "cp" bundle as campaign/project
  // (cpQS/cpActive/cpVariantMatch below) rather than its own machinery, since all
  // three compose as one AND'd narrow and every downstream consumer (tailQS,
  // cpPaged, badge counts, export) already keys off cpQS/cpActive. Options are
  // data-derived from filterOpts.leadUnitTypes (/api/filter-options), same as
  // campaign/project — see the SearchableSelect render below for the owner's
  // "every raw value is its own option" ruling.
  var [unitTypeFilter, setUnitTypeFilter] = useState([]);
  var [filterOpts, setFilterOpts] = useState(null); // {leadCampaigns,leadProjects,drProjects}
  useEffect(function(){
    if (!p.token) return;
    var cancelled = false;
    apiFetch("/api/filter-options", "GET", null, p.token)
      .then(function(r){ if (!cancelled && r) setFilterOpts(r); })
      .catch(function(){});
    return function(){ cancelled = true; };
  },[p.token]);
  // Query-string fragment carrying the two filters. Appended to EVERY targeted
  // side-fetch below (status / agent / inactive / rotation chips) as well as the
  // standalone cpLeads fetch, so the server AND-composes them with whatever
  // else is active — without this, selecting a status chip would refetch that
  // status unfiltered and silently widen the campaign/project narrow.
  // Each selected value is its own repeated param (see selQS) — never
  // comma-joined, because campaign and project names contain commas.
  var cpQS = selQS("campaign", campaignFilter) + selQS("project", projectFilter) + selQS("unitType", unitTypeFilter);
  var cpActive = !!(campaignFilter.length || projectFilter.length || unitTypeFilter.length);
  // Client-side re-narrow, so the rows stay exact while p.leads (which has no
  // filter applied) is still the base during the brief pre-fetch window.
  // Campaign folds with normCampaignFE (variant merge) and project with
  // normProjectFE — the same split the server uses; passing one function for
  // both would either lose "- Copy" campaigns or collapse "Zed 2" into "Zed".
  // Unit Type folds with normProjectFE too (case + whitespace only, no variant
  // merge) — mirrors the backend's resolveFieldVariants("unitType", v), which
  // reuses normalizeProjectName because prod data has no equivalent of the
  // campaign "- Copy" suffix pattern to strip.
  var cpVariantMatch = function(lead){
    if (!selMatchName(campaignFilter, lead.campaign, normCampaignFE)) return false;
    if (!selMatchName(projectFilter,  lead.project)) return false;
    if (!selMatchName(unitTypeFilter, lead.unitType)) return false;
    return true;
  };

  // The six targeted Leads fetches (🛑 / 🌱 / status chip / agent / campaign+
  // project / inactive-agent) now live in ONE block further down, after the
  // date-filter state they depend on — search "PAGED TARGETED FETCHES". They
  // used to sit here as six near-identical one-shot `&limit=2000` effects.

  var [statusDrop, setStatusDrop] = useState(null);
  var [showAdd, setShowAdd] = useState(false);
  var [editLead, setEditLead] = useState(null);
  // Standalone managerial feedback modal — open from the lead detail panel
  // for admin/SA/manager/TL (TL only when not on their own personal lead) so
  // they can write a private note or send-to-specific-sales without changing
  // status. Sales role uses the existing inline path (StatusModal).
  var [feedbackModal, setFeedbackModal] = useState(null); // {lead}
  var [fbForm, setFbForm] = useState({ text:"", visibility:"private", targetAgentId:"" });
  var [fbSaving, setFbSaving] = useState(false);
  var [fbErr, setFbErr] = useState("");
  var [showStatusPicker, setShowStatusPicker] = useState(false);
  var [showStatusComment, setShowStatusComment] = useState(false);
  var [pendingStatus, setPendingStatus] = useState(null);
  // Callback strip clock + reschedule sheet (src/lib/callbackStrip.js, 2026-10-26).
  // One 60 s tick per page; every strip reads it through props.
  var cbNow = useMinuteTick();
  var [cbResched, setCbResched] = useState(null);       // { leadId, status, callbackTime }
  var [cbReschedBusy, setCbReschedBusy] = useState(false);
  var [waChooser, setWaChooser] = useState(null); // phone number for WA chooser
  var [actNote, setActNote] = useState(""); var [actType, setActType] = useState("call"); var [showActForm, setShowActForm] = useState(false);
  var [saving, setSaving] = useState(false);
  var [importing, setImporting] = useState(false); var [importMsg, setImportMsg] = useState("");
  var [selected2, setSelected2] = useState([]);
  var [showBulk, setShowBulk] = useState(false); var [bulkRows, setBulkRows] = useState([{ agentId:"", count:"" }]);
  var [bulkReassignBusy, setBulkReassignBusy] = useState(false); var [bulkReassignErr, setBulkReassignErr] = useState("");
  // Phase A4-main (2026-05-05): bulk source-change modal state. Admin
  // selects leads via the existing multi-select, opens via the new
  // "Change Source" toolbar button, picks a canonical source, confirms.
  var [showBulkSource, setShowBulkSource] = useState(false);
  var [bulkSource, setBulkSource] = useState("");
  var [bulkSourceSaving, setBulkSourceSaving] = useState(false);
  var [showWaTemplates, setShowWaTemplates] = useState(false);
  var [waLead, setWaLead] = useState(null);
  var [showBulkWa, setShowBulkWa] = useState(false);
  var [bulkWaTemplate, setBulkWaTemplate] = useState(null);
  var [showQuickAdd, setShowQuickAdd] = useState(false);
  var [showHistory, setShowHistory] = useState(false);
  var [historyLead, setHistoryLead] = useState(null);
  var [fullHistory, setFullHistory] = useState([]);
  var [historyLoading, setHistoryLoading] = useState(false);
  var [showCompare, setShowCompare] = useState(false);
  var [compareLead, setCompareLead] = useState(null);
  var [compareEras, setCompareEras] = useState([]);
  var openCompare = function(leadObj, erasArr){
    setCompareLead(leadObj || null);
    setCompareEras(erasArr || []);
    setShowCompare(true);
  };
  var closeCompare = function(){
    setShowCompare(false);
    setCompareLead(null);
    setCompareEras([]);
  };
  var [quickForm, setQuickForm] = useState({name:"",phone:"",project:PROJECTS[0],source:""});
  var [quickSaving, setQuickSaving] = useState(false);
  var [vipFilter, setVipFilter] = useState(false);
  var [noAgentFilter, setNoAgentFilter] = useState(false);
  // MULTI-SELECT (2026-08-06): an ARRAY of user ids; [] = all agents. Sent as
  // repeated ?agentId= params and unioned into ONE $in server-side, so picking
  // three agents is a single query against the full book — not three.
  var [agentFilter, setAgentFilter] = useState([]);
  // SALES DEFAULTS TO RECEIPT TIME (2026-08-20). Everyone else keeps
  // lastActivity, which is what this has always been.
  //
  // This is what makes the tabs agree for a sales agent. The All tab renders the
  // App-level bootstrap, which sends no ?sort= and therefore gets the server's
  // role default (`received` for sales); every other tab rides a targeted fetch
  // carrying ?sort=<sortBy>. With sortBy left at "lastActivity" the two would
  // order differently — All by receipt time, every chip by last activity — and
  // the same lead would change rank when the agent clicked a chip. That split
  // already existed before this work (All on createdAt, chips on
  // lastActivityTime); it is being closed rather than inherited.
  var [sortBy, setSortBy] = useState(isSalesRole ? "received" : "lastActivity");
  var [lockedOnly, setLockedOnly] = useState(false);
  // rotation_stopped chip results. The paginated /api/leads bootstrap only
  // contains the loaded page slice, so rotation-stopped leads deeper in the
  // DB never appear in allVisible. Targeted fetch (gated to admin/SA via the
  // BE ?rotationStopped=true param) populates this with all matching leads
  // in scope; null = not loaded yet, [] = loaded with no matches.
  // not_rotated chip results — same paginated-bootstrap problem as rsLeads.
  // Targeted fetch (?notRotated=true, admin/SA only) populates this with
  // every first-assignment lead in scope.
  // Status-chip results — same paginated-bootstrap problem as rsLeads/nrLeads,
  // but for the ordinary status tabs (Meeting Done, Hot Case, Call Back, Not
  // Interested, New Lead, …). Before this, clicking a status chip filtered only
  // the loaded page slice of p.leads, so the chip looked empty until the All
  // bucket scrolled in. The effect below fetches that one status's leads
  // directly from BE (?status=…) for EVERY role (the chips aren't admin-gated,
  // unlike rotation_stopped/not_rotated). null = not loaded / not a status chip;
  // [] = loaded with no matches. The filtered memo uses this as the status
  // base, re-applying allVisible exclusions + currentStatus() for exactness.
  // Agent-filter results — same paginated-bootstrap problem as statusLeads:
  // before this, the "All Agents" dropdown filtered only the loaded ~100
  // leads client-side (5270), so an agent whose leads weren't in that page
  // showed "No data". null = no agent selected / not loaded; [] = loaded with
  // no matches. The allVisible memo uses this as its base when an agent is
  // selected (with the same exclusions), so every downstream filter + the tab
  // counts operate on the agent's full set instead of the bootstrap slice.
  // "Inactive Agent" filter — leads whose CURRENT holder is a deactivated user.
  // The page is server-paginated, so a toggle drives a targeted fetch
  // (inactiveLeads, ?inactiveAgent=true) that becomes the base for accuracy +
  // Bulk Reassign select-all. Admin/operation only (matches the No Agent
  // toggle + the BE gate). null = not loaded; [] = loaded, no matches.
  var [inactiveAgentFilter, setInactiveAgentFilter] = useState(false);
  // rsLeads / nrLeads / statusLeads / agentLeads / inactiveLeads / cpLeads are
  // supplied by the PAGED TARGETED FETCHES block below. Each is null until its
  // fetch owns the view, [] when it owns it and matched nothing — allVisible and
  // the filtered memo rely on that distinction, so do not collapse null to [].
  // Deactivated-agent id set (from p.users) + row predicate — mirrors DailyRequestsPage.
  var inactiveAgentIds = useMemo(function(){
    var s = {};
    (p.users||[]).forEach(function(u){ if(u && u.active===false) s[String(gid(u))]=true; });
    return s;
  }, [p.users]);
  var isInactiveAgentRow = function(l){
    var a = l.agentId && l.agentId._id ? String(l.agentId._id) : (l.agentId ? String(l.agentId) : "");
    return !!(a && inactiveAgentIds[a]);
  };
  // Phase A4-prep (2026-05-05): Source + Campaign filters for cohort
  // identification before the bulk source editor (next slice). Admin /
  // operation only — gated in the UI. Apply additively on top of the
  // existing filter pipeline at the bottom of the main branch.
  var [sourceFilter, setSourceFilter] = useState("");
  // Two-step memo for the legacy-source list. Step 1 collapses p.leads to a
  // stable join-key string; step 2 derives the array only when that key
  // changes. Without the indirection, a plain useMemo([p.leads]) returns a
  // NEW array reference on every WS-driven leads update even when the
  // legacy set is unchanged — that ref churn breaks SourceFilterSelect's
  // memo() and reconciles the native <select> while it's open, producing
  // the dropdown-flicker symptom (ui_bug_dropdown_flicker, 2026-05-05).
  var sourceFilterLegacyKey = useMemo(function(){
    var seenS = {};
    leadSourceNames().forEach(function(s){ seenS[s] = true; });
    literalSourceNames().forEach(function(s){ seenS[s] = true; });
    var legacyMap = {};
    (p.leads || []).forEach(function(l){ if (l.source && !seenS[l.source]) legacyMap[l.source] = true; });
    return Object.keys(legacyMap).sort().join("|");
  // leadSourcesVersion IS needed: leadSourceNames() reads the registry store, which the linter cannot see.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.leads, leadSourcesVersion]);
  var sourceFilterLegacyList = useMemo(function(){
    return sourceFilterLegacyKey ? sourceFilterLegacyKey.split("|") : [];
  }, [sourceFilterLegacyKey]);
  // Stable onChange ref so SourceFilterSelect's memo() actually skips
  // re-render. useState setters are stable, so deps are empty.
  var onSourceFilterChange = useCallback(function(e){
    setLockedOnly(false);
    setSourceFilter(e.target.value);
  }, []);
  var [panelHistory, setPanelHistory] = useState([]);
  // Unified date filter (shared DATE_FILTER_CHIPS / dateFilterWindow).
  var [dateRange, setDateRange] = useState("all");
  // Phone-only chrome (MobileFilterChrome): whether the floating ＋ speed-dial is open. Presentation state only.
  var [mActionsOpen, setMActionsOpen] = useState(false);
  // Select mode (Bulk Reassign on a phone, where cards have no checkboxes): reuses the desktop selection (selected2) and the
  // existing Bulk Reassign modal (showBulk). The mode ends when that modal closes with nothing left selected (a successful reassign).
  var [mSelectMode, setMSelectMode] = useState(false);
  var mBulkWasOpen = useRef(false);
  useEffect(function(){ if (mSelectMode && !showBulk && mBulkWasOpen.current && selected2.length === 0) setMSelectMode(false); mBulkWasOpen.current = showBulk; }, [showBulk, selected2.length, mSelectMode]);
  var [dateQuarter, setDateQuarter] = useState({ year:new Date().getFullYear(), q:Math.floor(new Date().getMonth()/3)+1 });
  var [dateFrom, setDateFrom] = useState("");
  var [dateTo, setDateTo] = useState("");

  // ===== PAGED TARGETED FETCHES =====
  // The six fetches that bypass the p.leads bootstrap, all through
  // usePagedLeads (page=1&limit=100 + loadMore) instead of the one-shot
  // `&limit=2000` each used to issue.
  //
  // The cap was not theoretical: on 2026-08-11 the four busiest status chips
  // truncated, hiding 1,616 rows the tabs should have rendered — NoAnswer
  // showed a badge of 1,575 and could reach 719, with everything created
  // before 2026-06-20 invisible. It bit hardest on the status chips because
  // ?status= is a SUPERSET predicate (3,894 docs matched to render 1,575), so
  // newest-first truncation dropped real rows well before the badge count.
  //
  // Placed HERE, not with the other Leads state, because the query strings
  // embed the date window declared just above; computing them earlier would
  // read `dateRange`/`dateFrom`/`dateTo` before useState had assigned them and
  // silently bake `undefined` into the first fetch of every page load.
  //
  // Exactly one of these is active at a time (the `active` argument), so only
  // the fetch that owns the current view is in flight; the rest hold rows:null
  // and issue nothing.

  // Date window → ?from/?to. The server reproduces max(lastActivityTime,
  // createdAt) exactly (see the $expr on GET /api/leads), so moving this off
  // the client changes no row. It HAS to move: a client-side window filters the
  // loaded page rather than the book, which is the same class of bug as the cap.
  var dateWinQS = (function(){
    var w = dateFilterWindow(dateRange, { quarter:dateQuarter, from:dateFrom, to:dateTo });
    if (!w) return "";
    return "&from=" + encodeURIComponent(new Date(w[0]).toISOString())
         + "&to="   + encodeURIComponent(new Date(w[1]).toISOString());
  })();
  var cpAgentQS = selQS("agentId", agentFilter) + (inactiveAgentFilter ? "&inactiveAgent=true" : "");
  // Shared tail: campaign/project + date window ride on EVERY targeted fetch, so
  // a chip and a date chip and a campaign filter narrow the same query instead
  // of being intersected client-side across three partial result sets.
  // ⭐ VIP rides on the SHARED TAIL, not on a branch of its own, so it narrows
  // every targeted fetch (status chips, agent, inactive agent, campaign/project)
  // server-side — the same way the campaign/project and date narrows compose
  // across every tab. Before 2026-08-14 it was a pure in-memory predicate over
  // the 100-row bootstrap window (the old line at the end of the status branch),
  // and measured on prod that window contained ZERO of the 100 page-visible VIP
  // leads: the first one sits at rank 391, i.e. bootstrap page 4. So the chip
  // returned 0 rows on every fresh load regardless of how many VIPs existed.
  var vipQS = vipFilter ? "&vip=true" : "";
  // 📣 Source rides the tail for exactly the same reason ⭐ VIP does. It was a
  // client-side predicate over the 100-row bootstrap until 2026-08-14, and
  // measured on prod that day 11 of 16 sources rendered ZERO rows — including
  // "Daily Request → Lead" with 503 leads behind it.
  var srcQS = sourceFilter ? "&source=" + encodeURIComponent(sourceFilter) : "";
  // Header search — server-side since 2026-08-14. It was a client predicate over
  // the 100-row bootstrap, so typing searched 99 of 4,253 page-visible leads
  // (2.3%): a lead the header quick-jump COULD find was missing from the list it
  // was supposedly filtering. Trimmed and length-gated to match the server, which
  // ignores an empty/whitespace value.
  var searchStr = String(p.search || "").trim();
  var searchQS = searchStr ? "&search=" + encodeURIComponent(searchStr) : "";
  // Sort — rides every targeted fetch so a chip/agent/campaign view orders the
  // WHOLE matching set, not the rows that happen to be loaded. See the All-tab
  // caveat on `narrowActive` below.
  var sortQS = sortBy ? "&sort=" + encodeURIComponent(sortBy) : "";
  var tailQS = cpQS + dateWinQS + vipQS + srcQS + searchQS + sortQS;

  // Which chip owns the view. "all"/"important" have no targeted fetch — they
  // render allVisible off the p.leads bootstrap and its own paging.
  var chipIsStatus = !isReq && !lockedOnly && !!p.leadFilter
    && p.leadFilter !== "all" && p.leadFilter !== "important"
    && p.leadFilter !== "rotation_stopped" && p.leadFilter !== "not_rotated";
  // untouched_new is not a stored status — it is derived NewLead MINUS the
  // genuine ones, so it fetches NewLead and the chip narrows within that set.
  // Both halves are reachable inside the same paged fetch; neither is hidden.
  // untouched_new no longer maps onto a derivedStatus — it has its own stored
  // predicate and its own param. Sending derivedStatus=NewLead alongside it would
  // AND the two and return the auto-rotated subset again, which is the bug.
  var chipDerived = p.leadFilter === "untouched_new" ? "" : p.leadFilter;

  // Each fetch passes the SAME test its render branch applies, so "did this page
  // contribute?" means what the user actually sees. Without this the chase can
  // stop on a page whose rows all arrived but none rendered.
  var rsPaged = usePagedLeads("/api/leads?rotationStopped=true&fields=summary" + tailQS,
                              p.token, p.cbBust, p.leadFilter === "rotation_stopped" && isOnlyAdmin,
                              function(l){ return leadsPageScopeVisible(l) && leadRotationStopped(l); });
  // Render predicate reads previousAgentIds, NOT rotationCount — the SAME basis
  // the server filter and the badge facet use since 2026-08-14. rotationCount is
  // incremented by a lead's first assignment out of the queue, so it reported 70
  // never-rotated leads as rotated. Leaving this half on rotationCount while the
  // server moved would re-create the 52% class: the badge would read 92 and the
  // chase loop would render 22. previousAgentIds survives the admin list
  // projection (GET /api/leads does a bare find(); only the SALES overlay strips
  // it, and this chip is admin/SA-only).
  var nrPaged = usePagedLeads("/api/leads?notRotated=true&fields=summary" + tailQS,
                              p.token, p.cbBust, p.leadFilter === "not_rotated" && isOnlyAdmin,
                              function(l){ return leadsPageScopeVisible(l) && !leadEverRotated(l) && l.agentId; });
  // ?derivedStatus= (not ?status=) — the exact persisted field the badge groups
  // on, so badge == reachable rows by construction and no client-side re-narrow
  // can hide a row inside a loaded page. Verified 0 mismatches against both the
  // badge and the rendered _currentStatus over the whole collection.
  // ?untouchedBacklog=1 rides ONLY on the 🕓 Untouched chip: the backlog chip's
  // purpose is surfacing stale-held leads, and the manager/TL staleness gate was
  // hiding exactly those, so the badge counted leads the list would not open
  // (manager 575 vs 395). The server honours it for manager/team_leader/director
  // only and ignores it for every other role, so the New Lead chip — same
  // derivedStatus=NewLead fetch, no param — stays gated as before.
  // ?untouchedOpen=1 is the ROW filter (the stored predicate). untouchedBacklog=1
  // is the separate, older staleness CARVE-OUT that lets a manager actually open
  // the neglected rows the badge counts. Both ride this chip; they are not the
  // same thing — see server.js on each.
  var untouchedQS = p.leadFilter === "untouched_new" ? "&untouchedOpen=1&untouchedBacklog=1" : "";
  var stPaged = usePagedLeads("/api/leads?derivedStatus=" + encodeURIComponent(chipDerived || "") + "&fields=summary" + untouchedQS + tailQS,
                              p.token, p.cbBust, chipIsStatus,
                              // THE SALES CASE. The server filters on lead-level
                              // derivedStatus; a sales caller's rows are narrowed
                              // client-side by currentStatus(), which reads their
                              // OWN slice (CLAUDE.md: the two key on different
                              // fields, and that divergence once made 52% of badge
                              // rows unreachable). So a page can arrive full and
                              // render nothing — exactly the stall condition.
                              function(l){
                                if (!leadsPageScopeVisible(l)) return false;
                                if (p.leadFilter === "untouched_new") return currentStatus(l) === "NewLead";
                                return currentStatus(l) === p.leadFilter;
                              });
  // 🔒 Locked Only — the BE has served ?lockedOnly=true since the chip shipped
  // (server.js ~L12119) but NOTHING ever sent it: the chip filtered allVisible
  // in memory instead. Measured on prod 2026-08-14: 10 locked leads, 6 of them
  // page-visible, earliest at rank 1287 = bootstrap page 13 — so the 100-row
  // window held none of them and both the rows AND the "(n)" badge (which is
  // literally filtered.length) read 0. Standalone chip: it clears every other
  // filter when switched on, so it owns the view outright.
  var lkPaged = usePagedLeads("/api/leads?lockedOnly=true&fields=summary" + tailQS,
                              p.token, p.cbBust, lockedOnly && isOnlyAdmin,
                              function(l){ return leadsPageScopeVisible(l) && l.locked === true; });
  // DASHBOARD TILE CLICK-THROUGHS (Leads / Interested / Callbacks / Response Watch):
  // ONE targeted server fetch, built by tileListUrl (src/lib/tileClickthrough.js)
  // from the spec the tile's click carried, and it is the page's ONLY row source
  // while the spec is active (allVisible below). Until 2026-10-07 each of these
  // had its own fetch that nothing rendered: allVisible fell through to the
  // 100-row bootstrap, so every tile opened the same newest 100 leads under its
  // own banner (Interested 25 -> 100 rows, Callbacks 96 -> 40, measured). The
  // spec carries the TILE's window, so the date chip and the other query-string
  // narrows deliberately do not ride this query: the list is the tile's set.
  var tlUrl = tileListUrl(p.specialFilter);
  var tlActive = !!tlUrl;
  var tlPaged = usePagedLeads((tlUrl || "") + sortQS,
                              p.token, p.cbBust, tlActive,
                              // The server already applied the tile's own base, so every
                              // row it returns is a row the tile counted.
                              function(){ return true; });
  var agPaged = usePagedLeads("/api/leads?fields=summary" + selQS("agentId", agentFilter) + tailQS,
                              p.token, p.cbBust, !!agentFilter.length && !cpActive, leadsPageScopeVisible);
  var inPaged = usePagedLeads("/api/leads?inactiveAgent=true&fields=summary" + tailQS,
                              p.token, p.cbBust, inactiveAgentFilter && !cpActive, leadsPageScopeVisible);
  var cpPaged = usePagedLeads("/api/leads?fields=summary" + tailQS + cpAgentQS,
                              p.token, p.cbBust, cpActive, leadsPageScopeVisible);
  // ⭐ Important — its own targeted fetch, the last chip that had none. The
  // server predicate (?important=true) is the twin of the qualifyingMarks()
  // walk below; verified on prod 2026-08-14 to return exactly the 1,985
  // page-visible leads the JS walk finds over the whole collection, against the
  // 15 the bootstrap window was showing. tailQS rides along so the tab composes
  // with the date / campaign / project / VIP / source narrows like every other.
  var impPaged = usePagedLeads("/api/leads?important=true&fields=summary" + tailQS,
                               p.token, p.cbBust, p.leadFilter === "important" && isAdmin,
                               function(l){ return leadsPageScopeVisible(l) && qualifyingMarks(l).length > 0; });
  // Cross-cutting narrows (VIP, source) on a tab that has NO targeted fetch of
  // its own — i.e. "All". Every other combination is already covered: a status
  // chip rides stPaged, Important rides impPaged, an agent narrow rides agPaged,
  // campaign/project rides cpPaged, and all of them carry tailQS. Kept off
  // whenever one of those owns the view so two partial result sets are never
  // intersected client-side. ONE fetch covers both narrows rather than one per
  // filter, because they compose in the query string.
  // Search joins VIP/source as a cross-cutting narrow that can own the All tab.
  //
  // SORT DELIBERATELY DOES NOT — OWNER RULING 2026-08-14, DO NOT "FIX" THIS.
  // Adding sortBy here looks like the obvious completion of the server-sort work
  // and is the wrong move. sortBy ALWAYS has a value (it defaults to
  // "lastActivity"), so including it would make narrowOwnsView permanently true
  // on the All tab, and that tab would then request a targeted page ON TOP OF
  // the App-level bootstrap it already loads — two ~900 KB requests at idle, on
  // the one tab people leave open to browse.
  //
  // The accepted consequence: on the All tab with no other filter, ordering is
  // applied to the LOADED WINDOW, not the whole book. That is a known, ruled-on
  // trade — All is the browse tab and doubling its idle payload to server-order
  // it is not worth it. Every other view (status chips, agent, campaign/project,
  // VIP, source, search, Important) rides a targeted fetch and IS server-ordered.
  //
  // If this ever genuinely needs fixing, the answer is to change the shared
  // bootstrap's sort — NOT to add a second fetch here.
  var narrowActive = !!(vipFilter || sourceFilter || searchStr);
  var narrowOwnsView = narrowActive && !lockedOnly && !chipIsStatus && !cpActive
                    && !inactiveAgentFilter && !agentFilter.length
                    && p.leadFilter !== "important";
  var vipPaged = usePagedLeads("/api/leads?fields=summary" + cpQS + dateWinQS + vipQS + srcQS + searchQS + sortQS,
                               p.token, p.cbBust, narrowOwnsView, leadsPageScopeVisible);

  var rsLeads       = rsPaged.rows;
  var nrLeads       = nrPaged.rows;
  var statusLeads   = stPaged.rows;
  var agentLeads    = agPaged.rows;
  var inactiveLeads = inPaged.rows;
  var cpLeads       = cpPaged.rows;
  var lockedLeads   = lkPaged.rows;
  var vipLeads      = vipPaged.rows;
  var importantLeads= impPaged.rows;

  // The fetcher that owns the rendered rows — endReached must page THIS one.
  // Precedence mirrors allVisible's `src` chain exactly (cp > inactive > agent)
  // with the two chips and the status chip layered on top, because the filtered
  // memo prefers their arrays over allVisible. When nothing targeted is active
  // the page falls back to the App-level bootstrap paging (p.loadMoreLeads).
  // lkPaged sits FIRST: Locked Only is standalone (switching it on clears every
  // other filter), so when it is active it owns the rendered rows outright.
  // vipPaged sits LAST, mirroring its activation guard above — on any tab where
  // another fetch owns the view, that fetch already carries &vip=true via tailQS
  // and is therefore the array to page.
  var activePaged = (lockedOnly && isOnlyAdmin)                          ? lkPaged
                  : (p.leadFilter === "rotation_stopped" && isOnlyAdmin) ? rsPaged
                  : (p.leadFilter === "not_rotated" && isOnlyAdmin)      ? nrPaged
                  : (p.leadFilter === "important" && isAdmin)             ? impPaged
                  : tlActive                                             ? tlPaged
                  : chipIsStatus                                         ? stPaged
                  : cpActive                                             ? cpPaged
                  : inactiveAgentFilter                                  ? inPaged
                  : agentFilter.length                                   ? agPaged
                  : narrowOwnsView                                       ? vipPaged
                  : null;
  // THE dead-scroll fix. Every endReached used to call p.loadMoreLeads(), which
  // pages the App-level bootstrap — an UNFILTERED query. Whenever a targeted
  // fetch owned the rendered rows, scrolling therefore appended rows to a
  // completely different array than the one on screen: the table never grew, and
  // the extra pages were wasted bandwidth. Route to whichever fetcher actually
  // produced the rows, and fall back to the bootstrap only when none did.
  var loadMoreRendered = function(){
    if (activePaged) { activePaged.loadMore(); return; }
    if (p.loadMoreLeads) p.loadMoreLeads();
  };


  var fileRef = useRef(null);

  // Tab-badge counts — moved down from ~L6675 so the dep array can read the
  // date-filter state above (see the note there). Debounced 1000ms so a burst
  // of WS lead_updated events (cbBust) doesn't flood the endpoint.
  //
  // The URL is built in render, not inside the timer, so the badges can tell
  // whether the response they hold answers the CURRENT filters (see
  // leadsPageCounts below). It is also the effect's whole dependency: every
  // filter that changes the question changes this string.
  var countsUrl = (function(){
      var url = "/api/leads/counts?excludeSource=" + encodeURIComponent("Daily Request");
      // derivedStatus=1 makes byStatus key on the SAME derived status the rows
      // are filtered by (currentStatus(l) below), so a chip's badge equals its
      // rendered row count. The sales branch already tallies that way, so the
      // flag is only meaningful — and only sent — for the strict-scope roles.
      if (isSalesRole) url += "&scope=historical"; else url += "&derivedStatus=1";
      // Honour the active source filter. /api/leads/counts has accepted ?source=
      // (as `sourceOnly`) since it was written; the FE simply never sent it, so
      // picking a source narrowed the rows while every chip badge kept showing
      // whole-book totals. Same class as the date-window fix below it.
      if (sourceFilter) url += "&source=" + encodeURIComponent(sourceFilter);
      // Every other cross-cutting narrow the ROWS honour. Without these the strip
      // reported whole-book totals under a filtered list — campaign, project,
      // agent, VIP and search all narrowed the rows and none of them narrowed a
      // badge. Same param names and same values the targeted row fetches send
      // (tailQS / selQS), so the two queries cannot describe different sets.
      url += cpQS + selQS("agentId", agentFilter) + vipQS + searchQS;
      if (inactiveAgentFilter) url += "&inactiveAgent=true";
      // Honour the active date chip. Without this the rows shrank with the
      // filter while the badges kept showing all-time totals ("5 rows" under a
      // "(684)" badge). The window is the SAME dateFilterWindow the rows use,
      // so both move together — and since the 2026-08-14 field ruling BOTH sides
      // window on createdAt, so they now describe the same set exactly. (This
      // comment used to record the opposite: counts on createdAt vs rows on
      // max(lastActivityTime, createdAt), a gap measured at 579 vs 2,208 rows
      // for "this month". That split is gone; do not reintroduce it.)
      var win = dateFilterWindow(dateRange, { quarter:dateQuarter, from:dateFrom, to:dateTo });
      if (win) {
        if (win[0] !== -Infinity) url += "&from=" + encodeURIComponent(new Date(win[0]).toISOString());
        if (win[1] !== Infinity)  url += "&to="   + encodeURIComponent(new Date(win[1]).toISOString());
      }
      return url;
  })();
  useEffect(function(){
    if (!p.token) return;
    var cancelled = false;
    var url = countsUrl;
    // A NEW QUESTION goes out at once; only a REFRESH of the question already on
    // screen (a cbBust tick, same URL) waits the debounce that coalesces WS
    // bursts. The wait used to apply to both, so every period change showed "…"
    // for a second before the request was even sent, and a second click inside
    // that second cancelled the first request outright: measured as Nagy Naeem
    // on 2026-10-15, no All-Time counts request left the browser between
    // 14:42:44Z and 14:43:32Z while the sidebar's did. The retry tick is part of
    // the key so a Retry is immediate too.
    var askKey = url + "#" + countsRetry;
    var timer = setTimeout(function(){
      if (cancelled) return;
      countsAskedRef.current = askKey;
      // The response is deliberately NOT gated on `cancelled`. It used to be, and
      // that was the 2026-08-11 regression: this cleanup runs on EVERY dep change,
      // p.cbBust ticks on every WS lead/DR event, and since 92af97c the request
      // takes 4.7-8.2s in production (derivedStatus=1 fetches every in-scope lead
      // with its agentHistory). A completed 200 was therefore discarded again and
      // again, leadsPageCounts stayed null, and every badge fell back to counting
      // the 100 bootstrap-loaded rows instead of the ~4.1k scope. Simulated against
      // the real prod WS timeline (measured cbBust gaps: p10 1.8s, median 8.6s):
      // median 9s to a correct badge, p99 65s, worst observed 127s.
      // Applying by sequence instead means a finished response is always used; only
      // a stale one (an older request landing after a newer one already applied)
      // is dropped. The badge can be a few seconds behind, but it is never a
      // page-slice count.
      var seq = ++countsReqRef.current;
      countsLatestByUrlRef.current[url] = seq;
      // A failure is RECORDED, never swallowed. It used to be `.catch(function(){})`,
      // which left whatever body was last applied on the chips — under a
      // narrowed period that is the previous period's numbers, indefinitely.
      // Only the newest request FOR THIS URL may report a failure: an older one
      // failing after a newer one was issued says nothing about the screen.
      var fail = function(){
        if (countsLatestByUrlRef.current[url] !== seq) return;
        setCountsErrUrl(url);
      };
      sharedApiGet(url, p.token)
        .then(function(r){
          if (!r || typeof r !== "object") { fail(); return; }
          if (seq <= (countsAppliedByUrlRef.current[url] || 0)) return;
          countsAppliedByUrlRef.current[url] = seq;
          setCountsByUrl(function(prev){
            var next = Object.assign({}, prev);
            next[url] = r;
            // Bounded: a session asks a handful of questions, not thousands.
            var keys = Object.keys(next);
            if (keys.length > 24) delete next[keys[0]];
            COUNTS_MEMO[p.token] = next;
            COUNTS_LAST[p.token] = r;
            return next;
          });
          setCountsErrUrl(function(e){ return e === url ? null : e; });
        })
        .catch(fail);
    }, countsFetchDelayMs(countsAskedRef.current, askKey));
    // clearTimeout still cancels a request that hasn't been ISSUED yet, so the
    // refresh debounce keeps coalescing WS bursts exactly as before. `cancelled`
    // only short-circuits the timer body — never a response already in flight.
    return function(){ cancelled = true; clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, p.cbBust, countsUrl, countsRetry]);
  // The counts the badges may show: only the answer to the CURRENT question,
  // and only while that question's latest request has not failed. Otherwise
  // null — the chip row then renders via chipCountsView (previous numbers muted, or
  // label only) or the retry notice (failed); another question's numbers only ever appear muted.
  var countsFailed = countsErrUrl === countsUrl;
  var leadsPageCounts = (!countsFailed && countsByUrl[countsUrl]) || null;
  // The chip strip reads this view, not leadsPageCounts: it may carry the
  // previous question's numbers, muted, where the strict value above is null.
  var chipCountsView = leadsChipCountsView(countsByUrl, countsUrl, countsErrUrl, lastShownCountsRef.current);
  if (chipCountsView.body && !chipCountsView.muted) lastShownCountsRef.current = chipCountsView.body;

  // ---- Helpers: read status / feedback / activity from the CURRENT holder's
  // assignment slice (not stale top-level fields). Defined once here, reused
  // everywhere the table needs to show what the present holder sees.
  var currentHolderSlice = function(lead) {
    if (!lead || !lead.agentId) return null;
    var holderId = String(lead.agentId._id || lead.agentId);
    var arr = lead.assignments || [];
    for (var i = 0; i < arr.length; i++) {
      var a = arr[i];
      if (!a) continue;
      var aid = a.agentId && a.agentId._id ? String(a.agentId._id) : String(a.agentId || "");
      if (aid === holderId) return a;
    }
    return null;
  };
  // Phase OO-3: latest-wins. Walk every assignment slice's agentHistory and
  // pick the most recent status_change / feedback event across all sales who
  // ever held the lead. No slice prioritization, no fallback chains beyond
  // top-level lead.status when nothing has ever been recorded.
  var currentStatus = function(lead) {
    if (!lead) return "NewLead";
    // STEP 3 — when the BE sent ?fields=summary, agentHistory[] has been
    // stripped but `_currentStatus` is precomputed by summaryComputeCurrentStatus.
    // Fall back to it before the original walk so list-view callers don't
    // see "NewLead" everywhere just because agentHistory is empty.
    if (typeof lead._currentStatus === "string" && lead._currentStatus) return lead._currentStatus;
    var latestStatus = null;
    var latestStatusTs = 0;
    (lead.assignments || []).forEach(function(a){
      if (!a) return;
      var hist = Array.isArray(a.agentHistory) ? a.agentHistory : [];
      hist.forEach(function(h){
        if (!h || h.type !== "status_change") return;
        var ts = new Date(h.createdAt || h.at || h.timestamp || 0).getTime();
        if (ts <= latestStatusTs) return;
        var s = h.status || h.toStatus;
        if (!s && h.note) {
          var m = String(h.note).match(/Status:\s*(\w+)/i);
          if (m) s = m[1];
        }
        if (s) {
          latestStatus = s;
          latestStatusTs = ts;
        }
      });
      if (a.status && a.lastActionAt) {
        var t = new Date(a.lastActionAt).getTime();
        if (t > latestStatusTs) {
          latestStatus = a.status;
          latestStatusTs = t;
        }
      }
    });
    if (latestStatus) return latestStatus;
    return lead.status || "NewLead";
  };
  var currentFeedback = function(lead) {
    if (!lead) return "";
    // STEP 3 — summary precompute fallback. `_currentFeedback` is the BE
    // mirror of this walk; when present, agentHistory was stripped so we
    // can't reproduce the search here without it.
    if (typeof lead._currentFeedback === "string") return lead._currentFeedback;
    var latestFeedback = "";
    var latestFeedbackTs = 0;
    (lead.assignments || []).forEach(function(a){
      if (!a) return;
      var hist = Array.isArray(a.agentHistory) ? a.agentHistory : [];
      hist.forEach(function(h){
        if (!h) return;
        if (h.type !== "feedback" && h.type !== "feedback_added") return;
        var content = String(h.note || h.feedback || "").trim();
        if (!content) return;
        var ts = new Date(h.createdAt || h.at || h.timestamp || 0).getTime();
        if (ts > latestFeedbackTs) {
          latestFeedbackTs = ts;
          latestFeedback = content;
        }
      });
      if (a.lastFeedback && String(a.lastFeedback).trim() && a.lastActionAt) {
        var t = new Date(a.lastActionAt).getTime();
        if (t > latestFeedbackTs) {
          latestFeedbackTs = t;
          latestFeedback = String(a.lastFeedback).trim();
        }
      }
    });
    return latestFeedback;
  };
  // Phase 2: full feedback history for ONE slice (assignments[] entry), newest
  // first. Walks slice.agentHistory chronologically and replays status_change
  // events to derive the slice's status AT THE MOMENT each feedback was
  // written. Returns [{ text, at, status, authorName, authorRole, isVirtual }].
  //
  // Two agentHistory write paths exist (see BE server.js):
  //   - Path A (managerial to_sales, /api/leads/:id/feedback): pushes a clean
  //     { type:"feedback", note, createdAt, authorId, authorName, authorRole }.
  //   - Path B (sales/TL PUT /api/leads/:id slice-sync): pushes ONE entry per
  //     PUT with priority status_change > note > feedback. So when an agent
  //     saves status + feedback together, only a status_change is logged — the
  //     feedback text only lives in slice.lastFeedback. The virtual-entry
  //     fallback below covers that case + legacy leads with no agentHistory.
  //
  // Visibility scoping is already handled BE-side (single-lead GET per-role
  // overlay): sales gets only their own slice, managers/TL get subtree slices,
  // admin gets all. Private feedback (visibility="private") lives in
  // lead.privateNotes[] and never lands in agentHistory — so it can't leak
  // through this helper.
  // sliceStatusPillColor hoisted to module scope above LeadJourney so both
  // surfaces (timeline rows + agent-row pills) use byte-identical colors.
  var feedbacksForSlice = function(slice) {
    if (!slice) return [];
    var hist = Array.isArray(slice.agentHistory) ? slice.agentHistory : [];
    // Sort ascending for chronological replay.
    var asc = hist.slice().sort(function(a, b){
      var ta = new Date((a && (a.createdAt || a.at || a.timestamp)) || 0).getTime();
      var tb = new Date((b && (b.createdAt || b.at || b.timestamp)) || 0).getTime();
      return ta - tb;
    });
    // Replay status: rotation seeds every new slice with "NewLead" (see BE
    // newAssignment shapes). For lead-create first slices the initial status
    // may differ — if it does, the slice.status reflects the LATEST value
    // (after all replayed transitions), so we can't trivially recover it
    // from slice.status alone. NewLead is the right starting point for the
    // common rotation case; for the lead-create case the first status_change
    // (if any) will correct it.
    var st = "NewLead";
    var out = [];
    asc.forEach(function(h){
      if (!h || !h.type) return;
      if (h.type === "status_change") {
        var m = String(h.note || "").match(/Status:\s*(\S+)/i);
        if (m && m[1]) st = m[1];
        return;
      }
      if (h.type !== "feedback" && h.type !== "feedback_added") return;
      var text = String(h.note || h.feedback || "").trim();
      if (!text) return;
      out.push({
        text: text,
        at: h.createdAt || h.at || h.timestamp || null,
        status: st,
        authorName: h.authorName || "",
        authorRole: h.authorRole || ""
      });
    });
    // Path-B undercount fallback + legacy fallback: when no clean feedback
    // entries exist but slice.lastFeedback is non-empty, surface it as one
    // virtual entry stamped at the slice's last action time.
    if (out.length === 0 && slice.lastFeedback && String(slice.lastFeedback).trim()) {
      out.push({
        text: String(slice.lastFeedback).trim(),
        at: slice.lastActionAt || slice.assignedAt || null,
        status: slice.status || "NewLead",
        authorName: slice.notesAuthorName || "",
        authorRole: slice.notesAuthorRole || "",
        isVirtual: true
      });
    }
    // Newest first for display.
    out.sort(function(a, b){
      var ta = new Date(a.at || 0).getTime();
      var tb = new Date(b.at || 0).getTime();
      return tb - ta;
    });
    return out;
  };
  // Per-slice shown status for the in-card agents-block badge. Mirrors backend
  // af54b52 (summaryComputeCurrentStatus): the latest status_change whose
  // canonical status is NOT "NewLead" wins. NewLead is the seed state, and
  // stale tools wrote bogus trailing "Status: NewLead" status_change entries on
  // top of genuine ones — feedbacksForSlice replays them without skipping, so it
  // resolves to NewLead. Falls back to the feedbacksForSlice replay / raw
  // slice.status only when no non-NewLead status_change exists. Display-only;
  // no write. Regex-free string parse (matches feedbacksForSlice's first-token
  // behavior); collapsed-note check also catches the legacy spaced "New Lead".
  var shownSliceStatus = function(slice) {
    if (!slice) return "NewLead";
    var sh = Array.isArray(slice.agentHistory) ? slice.agentHistory : [];
    var best = null, bestTs = -1;
    for (var hi = 0; hi < sh.length; hi++) {
      var h = sh[hi];
      if (!h || h.type !== "status_change") continue;
      var note = String(h.note || "");
      var ix = note.toLowerCase().indexOf("status:");
      var parsed = "";
      if (ix >= 0) {
        parsed = note.slice(ix + 7).trim();
        var sp = parsed.indexOf(" ");
        if (sp >= 0) parsed = parsed.slice(0, sp);
      }
      if (!parsed) parsed = String(h.status || "").trim();
      if (!parsed) continue;
      var collapsedNote = note.toLowerCase().split(" ").join("");
      if (parsed === "NewLead" || collapsedNote.indexOf("status:newlead") >= 0) continue;
      var ts = new Date(h.createdAt || h.at || h.timestamp || 0).getTime();
      if (ts >= bestTs) { bestTs = ts; best = parsed; }
    }
    // Reactivation/reset override (mirrors currentStatus 4837-4843): when the
    // slice's raw status was set by a genuine status transition MORE RECENTLY
    // than the latest worked status_change, the raw status is authoritative —
    // surfaces a soft-remove reactivation reset (status→NewLead, statusChangedAt→now)
    // instead of the preserved older worked status. statusChangedAt is only ever
    // advanced alongside a co-written assignments[].status (BE invariant), so the
    // raw value is never stale here. The NewLead-skip in the scan above is kept.
    if (slice.statusChangedAt) {
      var scTs = new Date(slice.statusChangedAt).getTime();
      if (!isNaN(scTs) && scTs > bestTs && slice.status) return slice.status;
    }
    return best || (feedbacksForSlice(slice)[0] || {}).status || slice.status || "NewLead";
  };
  // Phase 3: pick the lead's single most-recent feedback entry across every
  // slice the caller can see (BE overlay already scopes which slices reach the
  // FE per role). Returns null if no slice has any feedback (or virtual-
  // fallback) entry. Reuses feedbacksForSlice for the chronological replay
  // (so the returned entry's `status` is correct for the moment it was
  // written, not the slice's current status).
  var latestFeedbackForPanel = function(lead) {
    if (!lead || !Array.isArray(lead.assignments)) return null;
    var best = null;
    lead.assignments.forEach(function(a){
      var top = feedbacksForSlice(a)[0];
      if (!top) return;
      var ts = new Date(top.at || 0).getTime();
      if (!best || ts > new Date(best.at || 0).getTime()) {
        best = Object.assign({}, top, {
          agentName: userNameOf(a.agentId, p.users) || "Unknown",
          agentId:   a.agentId && a.agentId._id ? a.agentId._id : a.agentId
        });
      }
    });
    return best;
  };
  // "New Lead" tab is for first-time leads only — current status NewLead AND
  // never rotated AND no action yet taken on the slice. A lead with 2+
  // assignment slices, any non-zero rotationCount, or any feedback / notes /
  // callback / status_change / note / feedback / call entry on the holder's
  // slice is hidden even though it may still read as NewLead.
  var isGenuineNewLead = function(lead) {
    if (!lead) return false;
    if (currentStatus(lead) !== "NewLead") return false;

    // STEP 3 — when ?fields=summary was sent, `_hasAction` (true if ANY
    // slice has had a user-visible action recorded) replaces the agentHistory
    // walk below. _hasAction === undefined means the BE didn't precompute,
    // so fall through to the original walk.
    if (typeof lead._hasAction === "boolean") return !lead._hasAction;

    // Walk every slice (current and historical). If any slice shows any
    // action, the lead has been worked on and is no longer "new".
    var slices = (lead && lead.assignments) || [];
    for (var i = 0; i < slices.length; i++) {
      var s = slices[i];
      if (!s) continue;
      if (s.lastFeedback && String(s.lastFeedback).trim()) return false;
      if (s.notes && String(s.notes).trim()) return false;
      if (s.callbackTime) return false;
      var hist = Array.isArray(s.agentHistory) ? s.agentHistory : [];
      var hasAction = hist.some(function(h){
        if (!h || !h.type) return false;
        var t = h.type;
        return t === "status_change"
            || t === "feedback_added" || t === "feedback"
            || t === "note"
            || t === "call"
            || t === "callback_scheduled";
      });
      if (hasAction) return false;
    }
    return true;
  };
  // lastActivityAt() lived here until the 2026-08-14 field ruling. It returned
  // max(lastActivityTime, createdAt) and was what the date chip filtered on;
  // the chip now filters on createdAt alone ("Date Added" = entered the system),
  // matching /api/leads and /api/leads/counts, so it had no callers left.
  //
  // The history it carried is worth keeping: it deliberately did NOT read
  // updatedAt, because the rotation sweep bumps updatedAt on thousands of
  // untouched leads (CLAUDE.md, "updatedAt sweep pollution") and that used to
  // drag them into Today/Yesterday. createdAt is immune to that, so the new
  // field does not walk back into the old bug. The "Last Activity" COLUMN and
  // the lastActivity SORT still read lead.lastActivityTime — unchanged; only
  // the date-range chip moved.
  // Build the [start, end] window the date-filter chip represents in local time.
  // Delegates to the ONE shared resolver so Leads and Daily Requests can never
  // interpret the same chip differently again.
  var dateRangeWindow = function(key) {
    return dateFilterWindow(key, { quarter:dateQuarter, from:dateFrom, to:dateTo });
  };
;
  // Detect every qualifying mark (HotCase / Potential / MeetingDone) ever
  // recorded against any assignment slice — current status OR historical
  // status_change in agentHistory[]. Returns array sorted oldest-first.
  var QUALIFYING = { HotCase:1, Potential:1, MeetingDone:1 };
  var qualifyingMarks = function(lead) {
    // STEP 3 — when ?fields=summary was sent, the BE precomputed:
    //   _qualifyingMarksCount: total count (number)
    //   _qualifyingMarksFirst: the oldest mark object (or null)
    // The Important tab filter at App.js below reads `.length > 0`; the
    // table cell only ever renders `marks[0]`. So a one-element array
    // built from the precomputed first mark is functionally equivalent.
    if (typeof lead._qualifyingMarksCount === "number") {
      return lead._qualifyingMarksFirst ? [lead._qualifyingMarksFirst] : [];
    }
    var marks = [];
    var assignments = (lead && lead.assignments) || [];
    assignments.forEach(function(a){
      if (!a) return;
      var aName = userNameOf(a.agentId, p.users);
      var aId = a.agentId && a.agentId._id ? String(a.agentId._id) : String(a.agentId || "");
      // Signal 1: the slice currently carries a qualifying status.
      if (QUALIFYING[a.status]) {
        var ts = a.lastActionAt ? new Date(a.lastActionAt).getTime()
               : a.assignedAt ? new Date(a.assignedAt).getTime() : 0;
        marks.push({ status:a.status, agentName:aName, agentId:aId, date:ts, feedback:a.lastFeedback||"" });
      }
      // Signal 2: historical status_change entries logged in this slice.
      (a.agentHistory || []).forEach(function(h){
        if (!h) return;
        var note = String(h.note || "");
        Object.keys(QUALIFYING).forEach(function(qs){
          var spaced = qs.replace(/([A-Z])/g, " $1").trim(); // "HotCase" → "Hot Case"
          if (note.indexOf(qs) >= 0 || note.indexOf(spaced) >= 0) {
            var ts2 = h.createdAt ? new Date(h.createdAt).getTime() : 0;
            marks.push({ status:qs, agentName:aName, agentId:aId, date:ts2, feedback:a.lastFeedback||"" });
          }
        });
      });
    });
    return marks.sort(function(x,y){ return x.date - y.date; });
  };

  // ---- Filter logic (uses state values above) ----
  // Memoized so a fresh array reference isn't produced on every parent
  // re-render (cbBust ticks, leadsPageCounts setState, etc.). Stable
  // identity here lets the downstream `filtered` useMemo skip recomputing
  // when nothing relevant changed, which in turn keeps Virtuoso's `data`
  // prop reference stable and stops the row-remount → height-remeasure →
  // window-scroll-anchor jitter during progressive load.
  var allVisible = useMemo(function(){
    LEADSPROF("allVisibleComputes"); // diagnostic only — see LEADSPROF
    // When a specific agent is selected, base the visible set on that agent's
    // FULL server-fetched list (agentLeads) instead of the bootstrapped ~100
    // (p.leads) — the agent dropdown's targeted fetch above. The SAME
    // exclusions below (archived / Daily Request / EOI / DoneDeal) are applied
    // to it, so deals/EOIs still don't show on the Leads page. Falls back to
    // p.leads when no agent is selected OR during the brief load window
    // (agentLeads === null), where the client-side intersect at 5270 still
    // narrows the bootstrap slice correctly.
    // cpLeads (campaign/project fetch) takes precedence: its request already
    // carries the agentId / inactiveAgent narrows, so it is the most-filtered
    // server result available and using it avoids intersecting two partial
    // fetches client-side. Falls through to the existing bases when no
    // campaign/project filter is set, or during the brief pre-fetch window.
    // vipLeads is the lowest-precedence targeted base, matching vipPaged's
    // activation guard: it is non-null ONLY on the tabs where no other fetch
    // owns the view, and on every other tab the winning fetch above already
    // carries &vip=true. Falling through to p.leads (the 100-row bootstrap) is
    // what made ⭐ VIP Only render empty before 2026-08-14.
    // RESPONSE WATCH owns the view outright: its rows are the card's own id set
    // (?responseWatch=, resolved by the card's loader). Without this the page fell
    // through to p.leads and both badges opened the same 100-row bootstrap. While
    // the fetch is in flight the list is EMPTY, never the bootstrap.
    var src = tlActive ? (tlPaged.rows || [])
            : (cpActive && cpLeads !== null) ? cpLeads
            : (inactiveAgentFilter && inactiveLeads !== null) ? inactiveLeads
            : (agentFilter.length && agentLeads !== null) ? agentLeads
            : (narrowOwnsView && vipLeads !== null) ? vipLeads
            : p.leads;
    // A tile list is the tile's own set (the server applied its base): the page's
    // open-only exclusions below would drop the closed leads the tile counted.
    if (tlActive) return src;
    return src.filter(function(l){
      if(l.archived) return false;
      var matchSource = isReq?l.source==="Daily Request":l.source!=="Daily Request";
      if(!matchSource) return false;
      // Hide EOI and DoneDeal from Leads page — they have their own pages.
      // Also hide Cancelled leads that came from EOI (they live in the EOI Cancelled tab).
      if(!isReq && (l.status==="EOI"||l.status==="DoneDeal")) return false;
      // Leads that were cancelled from EOI keep their restored status and reappear here for the rotated agent.
      // Leads that were status-cancelled via the Deals/status dropdown stay hidden from the Leads page as before.
      if(!isReq && l.status==="Deal Cancelled" && !(l.eoiStatus==="EOI Cancelled")) return false;
      // Manager: hide leads with no agent in daily request
      if(isReq && (positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader") && !l.agentId) return false;
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.leads, agentLeads, selKey(agentFilter), inactiveLeads, inactiveAgentFilter, cpLeads, cpActive, vipLeads, narrowOwnsView, isReq, p.cu && p.cu.role, tlActive, tlPaged.rows]);
  // Memoized for the same reason as `allVisible` above. Without this,
  // every parent re-render (every cbBust tick, every leadsPageCounts
  // setState) recomputed and re-sorted the array, handing Virtuoso a
  // fresh `data` reference and forcing it to re-evaluate row layout.
  // Combined with the new `computeItemKey` on each Virtuoso, this keeps
  // row identity AND data reference stable across cosmetic re-renders.
  // Client mirror of the backend buildRotationStoppedOr — "rotation stopped for
  // ANY per-lead reason". Kept in lockstep with the server $or so rendered rows
  // equal the badge. The backend list query is the source of truth (rsLeads are
  // already stop-filtered); this re-applies the same logic for the pre-fetch
  // fallback on allVisible and as a belt-and-braces re-filter of rsLeads.
  // The current-slice noRotation check ($expr on the server) is approximated in
  // JS against the current holder's active assignment slice.
  var leadRotationStopped = function(l){
    if (!l) return false;
    if (l.rotationStopped === true) return true;
    if (l.locked === true) return true;
    if (l.createdAt && (Date.now() - new Date(l.createdAt).getTime()) > (Number(rotStopDays) || 45) * 86400000) return true;
    if (rotMaxCap > 0 && (Number(l.rotationCount) || 0) >= rotMaxCap) return true;
    var curId = l.agentId && l.agentId._id ? String(l.agentId._id) : String(l.agentId || "");
    var cur = (l.assignments || []).find(function(a){
      if (!a || a.removedAt) return false;
      var aid = a.agentId && a.agentId._id ? String(a.agentId._id) : String(a.agentId || "");
      return aid === curId;
    });
    if (cur && cur.noRotation === true) return true;
    return false;
  };
  // ===== ONE FILTER PIPELINE, TWO CONSUMERS (2026-08-26) ====================
  // `base` is null for the on-screen memo — every branch then reads its own
  // targeted fetch exactly as it always has, so the rendered list is unchanged
  // to the byte. Export Excel passes the FULLY WALKED book for the active view
  // instead, so the file contains every row this page would render if the user
  // scrolled to the end, narrowed by the same predicates in the same order.
  //
  // The alternative — a second copy of these rules written for the export —
  // is the shape this codebase keeps getting burned by: two answers that agree
  // on the day they are written. There is one pipeline; the export is a caller.
  var passesLeadsPageScope = function(l){
    if (l.archived) return false;
    var matchSource = isReq ? l.source==="Daily Request" : l.source!=="Daily Request";
    if (!matchSource) return false;
    if (!isReq && (l.status==="EOI" || l.status==="DoneDeal")) return false;
    if (!isReq && l.status==="Deal Cancelled" && !(l.eoiStatus==="EOI Cancelled")) return false;
    if (isReq && (positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader") && !l.agentId) return false;
    return true;
  };
  var buildFilteredLeads = function(base){
  var filtered;
  if (lockedOnly) {
    // Standalone filter: ignore every other user-applied filter and show only
    // leads where the rotation-lock flag is set.
    // Targeted BE fetch (admin/SA via ?lockedOnly=true) holds the full in-scope
    // list, bypassing the paginated p.leads — same pattern as rotation_stopped
    // below. Falls back to a client-side filter on allVisible during the brief
    // window before the fetch lands. The same exclusions allVisible applies
    // (archived / Daily Request / EOI / DoneDeal / unresolved Deal Cancelled)
    // are re-applied to BE-fetched rows so they match LeadsPage scope — that
    // gap is real, not theoretical: of the 10 locked leads on prod only 6 are
    // page-visible, so rendering the raw fetch would put the badge 4 above the
    // rows.
    if (base || lockedLeads !== null) {
      filtered = (base || lockedLeads).filter(function(l){
        if (l.archived) return false;
        if (l.source === "Daily Request") return false;
        if (l.status === "EOI" || l.status === "DoneDeal") return false;
        if (l.status === "Deal Cancelled" && l.eoiStatus !== "EOI Cancelled") return false;
        return l.locked === true;
      });
    } else {
      filtered = allVisible.filter(function(l){return l.locked===true;});
    }
  } else if (p.leadFilter === "rotation_stopped") {
    // Cross-cut filter (independent of status): leads whose rotation has stopped
    // for ANY per-lead reason (3× NI halt / locked / >45d age / max-rotations /
    // current-slice noRotation) — mirrors the broadened chip counter.
    // Targeted BE fetch (admin/SA via ?rotationStopped=true) holds the
    // full in-scope list, bypassing the paginated p.leads. Falls back to
    // a client-side filter on allVisible during the brief window before
    // the fetch lands. The same exclusions allVisible applies (archived /
    // Daily Request / EOI / DoneDeal / unresolved Deal Cancelled) are
    // re-applied to BE-fetched rows so they match LeadsPage scope.
    if (base || rsLeads !== null) {
      filtered = (base || rsLeads).filter(function(l){
        if (l.archived) return false;
        if (l.source === "Daily Request") return false;
        if (l.status === "EOI" || l.status === "DoneDeal") return false;
        if (l.status === "Deal Cancelled" && l.eoiStatus !== "EOI Cancelled") return false;
        return leadRotationStopped(l);
      });
    } else {
      filtered = allVisible.filter(leadRotationStopped);
    }
  } else if (p.leadFilter === "not_rotated") {
    // Same pattern as rotation_stopped above. Predicate is "never left a prior
    // holder AND agentId set" — leads still with their original (first-assigned)
    // agent. leadEverRotated reads previousAgentIds, matching the server filter
    // and the badge facet exactly; see its definition (~L1305) for why
    // rotationCount is NOT the basis (a first assignment inflates it).
    if (base || nrLeads !== null) {
      filtered = (base || nrLeads).filter(function(l){
        if (l.archived) return false;
        if (l.source === "Daily Request") return false;
        if (l.status === "EOI" || l.status === "DoneDeal") return false;
        if (l.status === "Deal Cancelled" && l.eoiStatus !== "EOI Cancelled") return false;
        return !leadEverRotated(l) && l.agentId;
      });
    } else {
      filtered = allVisible.filter(function(l){ return !leadEverRotated(l) && l.agentId; });
    }
  } else if (p.leadFilter === "important") {
    // Important tab: every lead that EVER had a qualifying mark on any slice.
    // Targeted BE fetch (?important=true) holds the full in-scope list — same
    // pattern as rotation_stopped / not_rotated above. Falls back to the
    // client-side walk over allVisible only until that fetch lands. The
    // LeadsPage exclusions are re-applied to fetched rows so badge == rows.
    //
    // The client-side branch is NOT a redundant safety net here, it is a
    // fallback for a genuinely different data shape: on a ?fields=summary
    // payload agentHistory[] is stripped, so qualifyingMarks() reads the
    // precomputed _qualifyingMarksCount instead of walking. Both paths agree —
    // verified on prod 2026-08-14, the server predicate and the JS walk both
    // return 1,985.
    if (base || importantLeads !== null) {
      filtered = (base || importantLeads).filter(function(l){
        if (l.archived) return false;
        if (l.source === "Daily Request") return false;
        if (l.status === "EOI" || l.status === "DoneDeal") return false;
        if (l.status === "Deal Cancelled" && l.eoiStatus !== "EOI Cancelled") return false;
        return qualifyingMarks(l).length > 0;
      });
    } else {
      filtered = allVisible.filter(function(l){ return qualifyingMarks(l).length > 0; });
    }
  } else {
    // Status tabs filter by the CURRENT holder's slice status, not the stale
    // top-level field. "Meeting Done" tab now strictly means "current holder's
    // slice is MeetingDone" per Phase S spec.
    //
    // statusBase: for a plain status chip, prefer the targeted BE fetch
    // (statusLeads) over the paginated allVisible slice so the chip shows every
    // matching lead in scope, not just those on the loaded page. The BE
    // ?status= filter is a superset (top-level OR per-slice), so the same
    // exclusions allVisible applies (archived / Daily Request / EOI / DoneDeal /
    // unresolved Deal Cancelled) are re-applied here, and currentStatus() ===
    // filter below re-narrows for exactness. "all" always uses allVisible
    // (bootstrap + infinite scroll); the fetch effect doesn't populate it.
    // The export walk already IS the whole matching set for this view, so it
    // needs no union with the loaded window — that union exists only to rescue
    // rows the paged fetch could not reach.
    var statusBase = base ? base.filter(passesLeadsPageScope) : allVisible;
    if (!base && p.leadFilter !== "all" && statusLeads !== null) {
      var passesStatusScope = passesLeadsPageScope;
      var rawBase = statusLeads.filter(passesStatusScope);
      // The ?status= fetch (statusLeads) matches RAW status/assignments.status
      // on the backend, so a lead whose DERIVED currentStatus matches the chip
      // but whose raw status does NOT (e.g. raw NewLead → derived Potential
      // after the af54b52 trailing-NewLead rule) never enters statusLeads.
      // Union the already-loaded visible set so those leads become candidates;
      // the currentStatus(l)===chip narrow below makes final membership match
      // the rows AND the /api/leads/counts (_currentStatus) count. Deduped by id.
      var seenIds = {};
      rawBase.forEach(function(l){ seenIds[gid(l)] = true; });
      statusBase = rawBase.concat(allVisible.filter(function(l){ return !seenIds[gid(l)] && passesStatusScope(l); }));
    }
    filtered = p.leadFilter==="all"
      ? statusBase
      : p.leadFilter==="NewLead"
        ? statusBase.filter(isGenuineNewLead)
        // Derived status is NewLead but the lead HAS been worked before — i.e.
        // it reached its current holder (usually by rotation, which seeds the
        // new slice as NewLead) and they haven't touched it yet. These match no
        // other chip and are not "new" either, so before this tab existed they
        // were reachable only from All: 526 leads on the admin scope, measured
        // 2026-08-10. Exactly the complement of isGenuineNewLead within derived
        // NewLead, so NewLead + this = byStatus.NewLead and the chips now sum
        // to All.
        : p.leadFilter==="untouched_new"
          // Reads the SAME stored field the badge counts. It used to be "derived
          // NewLead minus genuine", which only ever matched AUTO-rotated leads:
          // the manual reassign path writes no statusChangedAt on the new slice,
          // so those never collapsed to NewLead. 678 rows by the old rule, 1,088
          // by this one — measured on production 2026-09-01.
          ? statusBase.filter(function(l){ return l.derivedUntouchedBacklog === true; })
          : statusBase.filter(function(l){ return currentStatus(l) === p.leadFilter; });
    // Management-alerts special filter (from dashboard)
    if (p.specialFilter && p.specialFilter.type) {
      var spT = p.specialFilter.type;
      var nowMs = Date.now();
      var monthStartMs = new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0).getTime();
      filtered = filtered.filter(function(l){
        // Reads the SAME stored deadline the server filtered on. Deliberately not
        // re-derived from callbackTime + an action scan: that is the expression
        // that already exists twice and disagreed with itself by the Cairo offset.
        if (spT==="overdue") {
          // Same stored field, same window as the fetch above. Without the
          // window the banner count would be the whole backlog while the rows
          // were the period's — the tile/list disagreement, moved one layer in.
          if (!l.derivedOverdueAt) return false;
          var ovMs = new Date(l.derivedOverdueAt).getTime();
          if (!(ovMs < nowMs)) return false;
          if (p.specialFilter.dueFrom && ovMs < new Date(p.specialFilter.dueFrom).getTime()) return false;
          if (p.specialFilter.dueTo   && ovMs > new Date(p.specialFilter.dueTo).getTime())   return false;
          return true;
        }
        if (spT==="untouched") {
          var asgn = l.assignments||[];
          if (asgn.length===0) return true;
          return asgn.every(function(a){
            if (!a.lastActionAt) return true;
            if (a.assignedAt && new Date(a.lastActionAt).getTime()===new Date(a.assignedAt).getTime()) return true;
            return false;
          });
        }
        if (spT==="missingFeedback") return (l.assignments||[]).some(function(a){ return !a.notes || String(a.notes).trim()===""; });
        if (spT==="stale48h") return (l.assignments||[]).some(function(a){ return a.lastActionAt && (nowMs - new Date(a.lastActionAt).getTime()) > 48*3600*1000; });
        if (spT==="noRotation") return (l.assignments||[]).some(function(a){ return a.noRotation===true; });
        if (spT==="rotatedThisMonth") return (l.agentHistory||[]).some(function(h){ return h && h.date && new Date(h.date).getTime() >= monthStartMs; });
        // The rows are the server's (?interested=1); this mirror only keeps a
        // row whose derived status the payload carries and that has moved on.
        // The rows are the server's (?cohortInterested=1) and are the whole
        // answer: a cohort lead that once became interested and has since moved
        // on to NoAnswer is STILL in the tile's set. The old mirror re-tested
        // the CURRENT derived status here and would have hidden exactly those
        // rows, so the page would not have matched its tile.
        if (spT==="interested") return true;
        if (spT==="reportDrill") return true;   // the server already resolved the row (?devGroup= / ?campaignGroup=)
        if (spT==="responseWatch" || spT==="periodLeads") return true;
        if (spT==="project") return (l.project || "") === (p.specialFilter.value || "");
        if (spT==="aging") {
          // Reports → Lead Aging drill-down. UNION of two real-contact
          // sources, mirrors the backend pipeline exactly:
          //   (1) lead.history[] entries for status_changed /
          //       feedback_added / callback_scheduled — diff-gated by the
          //       server PUT handler so admin-saves-without-changes don't
          //       falsely refresh the clock.
          //   (2) p.activities entries for call/meeting/followup/email/note
          //       by the current holder against this lead.
          // Take the max across both. Fallback A = current holder's
          // slice.assignedAt, fallback B = lead.createdAt.
          var historyContactEvents = { status_changed:1, feedback_added:1, callback_scheduled:1 };
          var contactTypes = { call:1, meeting:1, followup:1, email:1, note:1 };
          var aid = l.agentId && l.agentId._id ? l.agentId._id : l.agentId;
          var aidStr = aid ? String(aid) : "";
          var lidStr = String(gid(l) || "");
          var maxContact = 0;
          // Source 1: lead.history events
          var hist = l.history || [];
          for (var hi = 0; hi < hist.length; hi++) {
            var h = hist[hi];
            if (!h || !historyContactEvents[h.event]) continue;
            var th = h.timestamp ? new Date(h.timestamp).getTime() : 0;
            if (th > maxContact) maxContact = th;
          }
          // Source 2: Activity entries (already loaded in props)
          if (aidStr && lidStr) {
            var acts = p.activities || [];
            for (var i2 = 0; i2 < acts.length; i2++) {
              var act = acts[i2];
              if (!act || !contactTypes[act.type]) continue;
              var alid = act.leadId && act.leadId._id ? act.leadId._id : act.leadId;
              var auid = act.userId && act.userId._id ? act.userId._id : act.userId;
              if (!alid || !auid) continue;
              if (String(alid) !== lidStr) continue;
              if (String(auid) !== aidStr) continue;
              var ta = act.createdAt ? new Date(act.createdAt).getTime() : 0;
              if (ta > maxContact) maxContact = ta;
            }
          }
          var lastT = maxContact;
          if (!lastT && aidStr) {
            // Fallback A: current holder's slice.assignedAt
            var asg = l.assignments || [];
            for (var j2 = asg.length - 1; j2 >= 0; j2--) {
              var sa = asg[j2] && asg[j2].agentId;
              var sid = sa && sa._id ? sa._id : sa;
              if (sid && String(sid) === aidStr && asg[j2].assignedAt) {
                lastT = new Date(asg[j2].assignedAt).getTime();
                break;
              }
            }
          }
          if (!lastT) lastT = l.createdAt ? new Date(l.createdAt).getTime() : 0;
          var ageDays = (nowMs - lastT) / 86400000;
          var aMin = (typeof p.specialFilter.ageMin === "number") ? p.specialFilter.ageMin : -Infinity;
          var aMax = (typeof p.specialFilter.ageMax === "number") ? p.specialFilter.ageMax : Infinity;
          return ageDays >= aMin && ageDays < aMax;
        }
        return true;
      });
    }
    // A NUMERIC QUERY ASKS THIS LIST THE SAME ONE QUESTION THE DROPDOWN ASKS.
    // The header search box and this list share one `search` state (CRMApp -> sp
    // -> LeadsPage), so whatever "8220" means up there it means down here too —
    // and since the syntax now selects exactly one pass, so does matchIdQuery:
    // "#8220" narrows by leadId alone, "8220" by phone/phone2 alone. Same
    // ID_QUERY_RE, same BARE_DIGITS_RE, same PHONE_MIN_DIGITS floor the backend
    // used to decide which section to fill.
    //
    // It replaces a blanket skip. matchSearch matches name/phone/phone2 as
    // TEXT and never an id, so running it on "50038" emptied the list; skipping
    // it instead left every row visible, which said "all of these match" and was
    // no more true. Both halves are now answered properly.
    //
    // SUPERSEDED 2026-08-28 (R2/R4). This used to say that an id belonging to an
    // EOI/Deal RECORD "legitimately matches no lead", so the list going empty
    // was "the honest answer". That reasoning held only while a transaction
    // displayed its PARENT's number and a recordNo was an internal id nobody
    // could read off a screen. Under R2 the recordNo IS what every surface
    // renders, so an id typed from the Deals page must narrow this list to the
    // row it names — matchIdQuery now matches _recordNo as well as leadId.
    //
    // matchSearch itself is UNTOUCHED — name/phone/phone2 behaviour is byte for
    // byte what it was for every non-id query.
    if (isIdShaped(p.search)) {
      filtered = filtered.filter(function(l){ return matchIdQuery(l, p.search); });
    } else {
      filtered = filtered.filter(function(l){return matchSearch(l,p.search);});
    }
    // Belt-and-braces exact re-narrow. The server has already applied ?vip=true
    // (it rides tailQS on whichever fetch owns the view, or vipPaged on the All
    // tab), so this only matters while a fetch is in flight and p.leads is still
    // the base — same role the cpVariantMatch / agentFilter re-narrows play.
    if (vipFilter) filtered = filtered.filter(function(l){return l.isVIP;});
    if (noAgentFilter) filtered = filtered.filter(function(l){ var aid=l.agentId&&l.agentId._id?l.agentId._id:l.agentId; return !aid; });
    // Holder OR split partner, against the whole selection — same predicate the
    // server applies, so the client re-narrow can't disagree with the fetch.
    if (agentFilter.length) filtered = filtered.filter(function(l){ var aid=l.agentId&&l.agentId._id?l.agentId._id:l.agentId; var sid=l.splitAgent2Id&&l.splitAgent2Id._id?l.splitAgent2Id._id:l.splitAgent2Id; return selHasId(agentFilter,aid)||selHasId(agentFilter,sid); });
    // Phase A4-prep filter. Source matches exactly against the lead's
    // stored source value (legacy values like "Snap Chat" / lowercase
    // "facebook" remain selectable from the dropdown). The campaign
    // has/none/contains probe that used to sit here was removed 2026-08-05 —
    // the normalized campaignFilter dropdown (applied below, server-side and
    // on every tab) covers all three modes.
    if (sourceFilter) filtered = filtered.filter(function(l){ return (l.source||"") === sourceFilter; });
  }
  // Inactive Agent — narrow to leads whose CURRENT holder is deactivated.
  // Applied after the branch selection (like the date-range chip below) so it
  // composes with every tab. When active, the base set is the inactiveLeads
  // targeted fetch (via allVisible) for accuracy; this predicate keeps it exact
  // and lets it layer on top of a status/search/date filter. isInactiveAgentRow
  // is off unless the toggle is on, so this is a no-op otherwise.
  if (inactiveAgentFilter) filtered = filtered.filter(isInactiveAgentRow);
  // Campaign / Project — applied AFTER the branch selection (like the
  // inactiveAgent and date-range narrows below) so they compose with EVERY tab,
  // including Locked Only / Rotation Stopped / Not Rotated / Important. The
  // legacy source narrow lives inside the status-tab branch only and therefore
  // silently no-ops on those tabs; these deliberately do not.
  // The server has already applied the same predicate — this re-narrow keeps
  // rows exact while a fetch is in flight and p.leads is still the base.
  if (cpActive) filtered = filtered.filter(cpVariantMatch);
  // Date-range chips. For the Important tab the chip filters by the
  // qualifying-mark date; for every other tab it filters by the lead's most
  // recent activity time.
  (function(){
    var win = dateRangeWindow(dateRange);
    if (!win) return;
    // Response Watch carries its OWN window (assigned in the card's period). The
    // date chip windows on createdAt — a different axis — so it must not narrow
    // a set the badge counted on assignedAt.
    if (tlActive) return;
    // THE AXIS IS THE SERVER'S, BY ROLE — see leadInDateWindow (module scope).
    // "Date Added" (every non-sales role) windows on createdAt, the 2026-08-14
    // ruling; "Received" (sales) windows on the caller's own receipt time, the
    // 2026-08-20 ruling. This re-narrow used to read createdAt for every role,
    // so under a sales list it dropped every rotated-in lead the chips had
    // counted (Nagy Naeem, Today, 2026-10-15: New Lead chip 6, list "No data").
    //
    // The Important tab is included deliberately. It used to window on the
    // first qualifying-mark date, and leaving it that way would have created a
    // NEW badge-vs-rows split the moment the server started windowing its
    // ?important=true fetch — the exact bug class this whole batch closed.
    //
    // Do NOT restore lastActivityAt() here: it reads max(lastActivityTime,
    // createdAt), which made a lead created in March but worked yesterday count
    // as "added" this week. Lists under a date filter are SHORTER now, by intent.
    //
    // ROWS THE SERVER ALREADY WINDOWED ARE NOT RE-JUDGED. Every targeted fetch
    // and the export walk carry ?from/?to (tailQS), and the server clause is the
    // exact one the chips count on — including a receipt on ANY of the caller's
    // active slices, which a row (overlaid to its first slice) cannot see. Only
    // rows from the un-windowed bootstrap (the All tab, the union under a status
    // chip) are judged here, on the same axis. `base` is the export walk of the
    // windowed URL, so it is windowed in full.
    var preWindowed = base ? null : (activePaged && Array.isArray(activePaged.rows) ? activePaged.rows : null);
    var preWindowedIds = {};
    (preWindowed || []).forEach(function(l){ preWindowedIds[gid(l)] = true; });
    if (!base) filtered = filtered.filter(function(l){
      if (preWindowedIds[gid(l)]) return true;
      return leadInDateWindow(l, win, isSalesRole);
    });
  })();
  // When a targeted fetch owns the rows, the SERVER has already ordered the whole
  // matching set — re-sorting here would only reorder within the loaded prefix
  // and, for `name`, would fight the server's ICU collation with the browser's
  // localeCompare, making order differ across a page boundary. The Important tab
  // is the exception: it orders by qualifying-mark date, which is derived from
  // agentHistory and not expressible as a server sort, so it keeps sorting here.
  // A walked book arrives in `_id` order (the keyset walk must — see
  // fetchAllPages), so the export ALWAYS re-sorts here; the server sort it
  // would otherwise inherit does not survive cursor paging.
  var serverSorted = !base && !!activePaged && p.leadFilter !== "important";
  filtered = serverSorted ? filtered : filtered.slice().sort(function(a,b){
    if (p.leadFilter === "important") {
      // Important tab sorts by qualifying date, newest first.
      var aMarks = qualifyingMarks(a); var bMarks = qualifyingMarks(b);
      var aTs = aMarks.length ? aMarks[0].date : 0;
      var bTs = bMarks.length ? bMarks[0].date : 0;
      return bTs - aTs;
    }
    if (sortBy==="lastActivity") return new Date(b.lastActivityTime||0)-new Date(a.lastActivityTime||0);
    if (sortBy==="newest") return new Date(b.createdAt||0)-new Date(a.createdAt||0);
    if (sortBy==="oldest") return new Date(a.createdAt||0)-new Date(b.createdAt||0);
    // Receipt time — the sales default. `assignedAt` is set per row by the sales
    // overlay in GET /api/leads from the caller's OWN slice, so this reads the
    // same value the server sorted the whole book by. The createdAt fallback
    // mirrors buildReceiptKeyExpr's $ifNull exactly: the split-agent and
    // legacy-frozen scope branches can admit a lead the caller holds no slice
    // on, and those rows have no receipt time.
    //
    // This branch only runs on the All tab with no other filter, where the
    // server has NOT ordered the set (see serverSorted above) — so without it
    // the agent's browse tab would silently fall through to `return 0` and
    // render in whatever order the bootstrap happened to arrive in.
    if (sortBy==="received") return new Date(b.assignedAt||b.createdAt||0)-new Date(a.assignedAt||a.createdAt||0);
    if (sortBy==="receivedOldest") return new Date(a.assignedAt||a.createdAt||0)-new Date(b.assignedAt||b.createdAt||0);
    if (sortBy==="name") return a.name.localeCompare(b.name,"ar");
    return 0;
  });
  return filtered;
  };
  var filtered = useMemo(function(){
    LEADSPROF("filteredComputes"); // diagnostic only — see LEADSPROF
    return buildFilteredLeads(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allVisible, p.leadFilter, p.specialFilter, p.activities, p.search, rsLeads, nrLeads, statusLeads, lockedLeads, vipLeads, lockedOnly, vipFilter, noAgentFilter, selKey(agentFilter), inactiveAgentFilter, inactiveAgentIds, sourceFilter, selKey(campaignFilter), selKey(projectFilter), selKey(unitTypeFilter), cpActive, dateRange, dateQuarter, dateFrom, dateTo, sortBy]);

  // ===== EXPORT EXCEL — THE WHOLE FILTERED SET, NOT THE LOADED WINDOW =======
  //
  // It used to hand `filtered` straight to the workbook, i.e. whatever the
  // browser had downloaded. Measured on production 2026-08-26: a fresh load of
  // the All tab holds the 100-row bootstrap against 4,825 page-visible leads, so
  // the file was 2.1% of the list the admin was looking at — and silently, since
  // an .xlsx with 100 correct rows looks exactly like an .xlsx with all of them.
  //
  // THE URL IS activePaged's PRECEDENCE, LINE FOR LINE. Whichever fetch owns the
  // rendered rows is the fetch this walks; anything else would export a
  // different question than the one on screen. When no targeted fetch owns the
  // view (the All tab), the same tailQS narrows still apply — they ride every
  // query — so the walk is the All tab's own book.
  //
  // ?fields=export, not full and not summary: 15.38 MB against 89.09 / 41.07 for
  // this scope. See applyExportProjection in server.js for what it keeps and the
  // rule for keeping it correct.
  var [exporting, setExporting] = useState(false);
  var [exportDone, setExportDone] = useState(0);
  var [exportTotal, setExportTotal] = useState(0);
  var leadsExportUrl = function(){
    var F = "&fields=export";
    if (lockedOnly && isOnlyAdmin)                          return "/api/leads?lockedOnly=true" + F + tailQS;
    if (p.leadFilter === "rotation_stopped" && isOnlyAdmin)  return "/api/leads?rotationStopped=true" + F + tailQS;
    if (p.leadFilter === "not_rotated" && isOnlyAdmin)       return "/api/leads?notRotated=true" + F + tailQS;
    if (p.leadFilter === "important" && isAdmin)             return "/api/leads?important=true" + F + tailQS;
    if (chipIsStatus)                                        return "/api/leads?derivedStatus=" + encodeURIComponent(chipDerived || "") + F + untouchedQS + tailQS;
    if (cpActive)                                            return "/api/leads?fields=export" + tailQS + cpAgentQS;
    if (inactiveAgentFilter)                                 return "/api/leads?inactiveAgent=true" + F + tailQS;
    if (agentFilter.length)                                  return "/api/leads?fields=export" + selQS("agentId", agentFilter) + tailQS;
    return "/api/leads?fields=export" + tailQS;
  };
  var runLeadsExport = async function(){
    if (exporting) return;
    setExporting(true); setExportDone(0); setExportTotal(0);
    try {
      // 500/page, not the 200 default: this walk is a deliberate one-shot, and
      // 10 round-trips beat 25 for the same bytes.
      var book = await fetchAllPages(leadsExportUrl(), p.token, 500, function(done, total){
        setExportDone(done);
        if (typeof total === "number") setExportTotal(total);
      });
      // THE SAME PIPELINE THE ROWS GO THROUGH. Not a re-implementation of it —
      // see buildFilteredLeads. The server predicate is a superset for several
      // chips, so this is what makes the file equal the list rather than the
      // query.
      var rows = buildFilteredLeads(book);
      await exportLeadsToExcel(rows, p.users, isReq ? "daily_requests" : "leads",
        canSeeClosingCompany(p.cu) && !isReq, canSeeLeadIds(p.cu) && !isReq, canEditDeveloper(p.cu) && !isReq);
    } catch (e) {
      alert((e && e.message) || "Export failed");
    } finally {
      setExporting(false); setExportDone(0); setExportTotal(0);
    }
  };

  // Fix A — clear p.initSelected after consuming it (matches EOI/Deals/DR
  // patterns at L9246 / L9903 / L10911). Without this, the deep-link state
  // sticks across page navigations: any remount of LeadsPage re-fires the
  // setSelected, painting a side panel for a stale lead — the visible
  // symptom is the post-STEP-4-5 "blank panel on the right" bug since the
  // shrunk bootstrap may no longer carry the deep-linked lead.
  useEffect(function(){
    if(p.initSelected){
      setSelected(p.initSelected);
      if (p.setInitSelected) p.setInitSelected(null);
    }
  },[p.initSelected]);

  // Consume one-shot agent filter from Admin Dashboard drill-down clicks.
  useEffect(function(){
    if (p.initAgentFilter) {
      setAgentFilter(p.initAgentFilter);
      setNoAgentFilter(false);
      if (p.setInitAgentFilter) p.setInitAgentFilter(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.initAgentFilter]);

  // Locked Only is mutually exclusive with search: if the user types anything
  // in the header search while it's active, drop out of Locked Only so the
  // search result is applied against the full list.
  useEffect(function(){
    if (lockedOnly && p.search && String(p.search).trim().length>0) {
      setLockedOnly(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.search]);

  // Fetch full history when a lead is selected
  useEffect(function(){
    if(!selected){setPanelHistory([]);return;}
    var lid=gid(selected);
    apiFetch("/api/leads/"+lid+"/full-history","GET",null,p.token).then(function(hist){
      var all=hist||[];
      var isAdminRole=p.cu.role==="admin"||p.cu.role==="operation"||positionOf(p.cu)==="director"||positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader";
      if(!isAdminRole){
        var rotTime=selected.lastRotationAt?new Date(selected.lastRotationAt).getTime():0;
        all=all.filter(function(a){
          var auid=String(a.userId&&a.userId._id?a.userId._id:a.userId||"");
          return auid===String(p.cu.id||"");
        });
      }
      setPanelHistory(all.slice().sort(function(a,b){return new Date(b.createdAt)-new Date(a.createdAt);}));
    }).catch(function(){setPanelHistory([]);});
  },[selected]);

  var getAgentName = function(l){ return userNameOf(l && l.agentId, p.users) || "-"; };

  var reqStatus = function(lid, st) {
    if (st === "DoneDeal") {
      if (!window.confirm("⚠️ Are you sure this deal is done? This cannot be undone!")) return;
    }
    setPendingStatus({leadId:lid,newStatus:st}); setShowStatusComment(true);
  };

  // THE RESCHEDULE SHEET RIDES THIS HANDLER (2026-10-26). psOverride names the
  // record and its CURRENT status ({ leadId, newStatus }), so a reschedule is
  // byte-for-byte the write that re-picking that status in the modal makes:
  // same PUT, same activity row, same server audit, same slice stamp. The
  // modal's pendingStatus is shadowed on purpose — nothing below may read the
  // modal's state when the sheet is the caller.
  var pendingStatusOfRender = pendingStatus;
  var confirmStatus = async function(comment, cbTime, extra, fb, psOverride) {
    var pendingStatus = psOverride || pendingStatusOfRender;
    if(!pendingStatus) return;
    // NO client-side DoneDeal recipient pre-check here — deliberately removed
    // 2026-08-13. The old guard read the agent off the CLIENT'S cached copy of
    // the lead (`selected`, else the ~100-row `p.leads` bootstrap) and failed
    // CLOSED when it could not find the lead at all, so "lead missing from
    // client state" rendered the same "no agent" alert as a genuinely
    // unassigned lead. It blocked a correctly-assigned lead (leadId 50033,
    // agentId + eoiAgentId both set, live slice) that the server would have
    // accepted, and it was strictly more restrictive than the server in three
    // ways: fail-closed on a cache miss, no legacyAgentName exemption, no
    // external/broker exemption.
    // The rule now lives ONLY in the backend guard (primary_sales_recipient_required,
    // server.js ~16176), which reads the STORED field and applies the per-dealType
    // rules. apiFetch surfaces its Arabic `message` verbatim, so the user still
    // gets a readable sentence rather than a raw error code.
    // SCOPE, precisely: that guard sits inside the admin/operation branch
    // (server.js ~16114), so it does NOT run for a `sales` caller. That is not a
    // hole this removal opened — a sales user cannot reach an agent-less lead in
    // the first place: GET /api/leads/:id 404s without a slice / ownership, and
    // the list overlay sets agentId from the caller's own slice. The
    // role-agnostic DoneDeal guards (unit_code_required, developer-required)
    // still fire for sales and now surface readably via the apiFetch change.
    try {
      // Determine whether feedback should be routed through the managerial
      // feedback endpoint (visibility selector) or saved inline via PUT
      // (existing sales / TL-on-own-lead path).
      // operation is read-only on feedback by design — comment is dropped
      // (the textarea is rendered with no selector for them; the backend
      // would reject both PUT and feedback writes anyway).
      var role = p.cu && p.cu.role;
      var leadDoc = selected;
      var leadAgentId = leadDoc && leadDoc.agentId && leadDoc.agentId._id ? leadDoc.agentId._id : (leadDoc && leadDoc.agentId);
      var isWriteMgrRole = role === "admin" || role === "manager" || role === "team_leader";
      var isOwnLead = role === "team_leader" && String(leadAgentId || "") === String(p.cu.id || "");
      var routeFeedbackViaApi = isWriteMgrRole && !isOwnLead && comment;
      var dropFeedback = role === "operation";
      var upData = { status: pendingStatus.newStatus };
      // For sales / TL-on-own-lead: keep inline notes/lastFeedback in PUT.
      // For admin/manager/TL on a team lead: omit from PUT (backend 400s),
      //                                      route via /feedback below.
      // For operation: drop entirely — read-only on feedback.
      if(comment && !routeFeedbackViaApi && !dropFeedback) upData.lastFeedback = comment;
      if(cbTime) upData.callbackTime = cbTime;
      else upData.callbackTime = "";
      if(extra) {
        if(extra.budget)     upData.budget     = extra.budget;
        if(extra.project)    upData.project    = extra.project;
        if(extra.unitType)   upData.unitType   = extra.unitType;
        if(extra.unitCode)   upData.unitCode   = extra.unitCode;
        if(extra.eoiDeposit) upData.eoiDeposit = extra.eoiDeposit;
        if(extra.developerId)      upData.developerId      = extra.developerId;      // N2
        if(extra.developerPending) upData.developerPending = extra.developerPending; // N2
      }
      if(comment && !routeFeedbackViaApi && !dropFeedback) upData.notes = comment;
      if(pendingStatus.newStatus === "EOI") {
        // DATE ONLY (owner ruling 2026-09-11), the same form every other
        // eoiDate / dealDate writer stamps. This used to append the current UTC
        // time-of-day so the approvals bell's "X hr ago" would not read the TZ
        // offset — and that datetime rendered the Edit modal's EOI Date input
        // BLANK (EOI #50058) and would sort outside every lexicographic
        // date-only window, exactly as verify-dealdate-render.js records for
        // dealDate. The picked day as picked; otherwise today's Cairo day.
        upData.eoiDate = (extra && extra.eoiDate) ? String(extra.eoiDate).slice(0,10) : cairoTodayIso();
      }
      // Stamp dealDate on close as date-only Cairo-local (YYYY-MM-DD), unified
      // with every other dealDate write path.
      if(pendingStatus.newStatus === "DoneDeal") upData.dealDate = cairoTodayIso();
      // Notify admin when DoneDeal or EOI
      if(pendingStatus.newStatus==="DoneDeal"||pendingStatus.newStatus==="EOI"){
        var notifEntry={leadName:selected?selected.name:"",leadId:pendingStatus.leadId,agentName:p.cu.name,status:pendingStatus.newStatus,budget:extra&&extra.budget?extra.budget:""};
        if(p.addDealNotif) p.addDealNotif(notifEntry);
      }
      var updated = await apiFetch("/api/leads/"+pendingStatus.leadId,"PUT",upData,p.token);
      var mwCS = mirrorWarning(updated); if (mwCS) alert(mwCS);
      // Immediate UI update from PUT response
      if(updated&&updated._id){p.setLeads(function(prev){return prev.map(function(l){return gid(l)===pendingStatus.leadId?updated:l;});});if(selected&&gid(selected)===pendingStatus.leadId)setSelected(updated);}
      // Managerial feedback routing — separate POST to /feedback because
      // PUT silently stripped notes/lastFeedback for these roles before the
      // visibility model existed (and now hard-rejects them with 400).
      if (routeFeedbackViaApi && fb && fb.text) {
        try {
          var feedbackBody = { text: fb.text, visibility: fb.visibility || "private" };
          if (feedbackBody.visibility === "to_sales") feedbackBody.targetAgentId = fb.targetAgentId;
          var fbResp = await apiFetch("/api/leads/"+pendingStatus.leadId+"/feedback","POST",feedbackBody,p.token);
          if (fbResp && fbResp.lead && fbResp.lead._id) {
            updated = fbResp.lead;
            p.setLeads(function(prev){return prev.map(function(l){return gid(l)===pendingStatus.leadId?fbResp.lead:l;});});
            if (selected && gid(selected)===pendingStatus.leadId) setSelected(fbResp.lead);
          }
        } catch(fbErr) { alert("Feedback save failed: "+(fbErr.message||"Unknown error")); }
      }
      // Upload any EOI documents picked in the status modal
      if (extra && Array.isArray(extra.eoiDocumentFiles) && extra.eoiDocumentFiles.length>0) {
        for (var i=0; i<extra.eoiDocumentFiles.length; i++) {
          var f = extra.eoiDocumentFiles[i];
          if (!f || !f.fileData) continue;
          try {
            var withDocs = await apiFetch("/api/leads/"+pendingStatus.leadId+"/"+(pendingStatus.newStatus==="DoneDeal"?"deal-documents":"eoi-documents"),"POST",{fileData:f.fileData, fileName:f.fileName||""},p.token);
            if (withDocs && withDocs._id) { updated = withDocs; p.setLeads(function(prev){return prev.map(function(l){return gid(l)===pendingStatus.leadId?withDocs:l;});}); if(selected&&gid(selected)===pendingStatus.leadId) setSelected(withDocs); }
          } catch(docErr) { console.error("EOI document upload failed:", docErr.message); }
        }
      }
      try { await apiFetch("/api/activities","POST",{leadId:pendingStatus.leadId,type:"status_change",note:"["+pendingStatus.newStatus+"] "+comment},p.token); } catch(actE){ console.error("activity log error:",actE.message); }
      // Background re-fetch for correct per-agent overlay
      apiFetch("/api/leads/"+pendingStatus.leadId,"GET",null,p.token).then(function(freshLead){if(freshLead&&freshLead._id){p.setLeads(function(prev){return prev.map(function(l){return gid(l)===pendingStatus.leadId?freshLead:l;});});if(selected&&gid(selected)===pendingStatus.leadId)setSelected(freshLead);}}).catch(function(){});
      p.setActivities(function(prev){return [{_id:Date.now(),userId:{name:p.cu.name},leadId:{_id:pendingStatus.leadId,name:selected?selected.name:""},type:"status_change",note:"["+pendingStatus.newStatus+"] "+comment,createdAt:new Date().toISOString()}].concat(prev);});
      // Track NoAnswer count for rotation
      if(pendingStatus.newStatus==="NoAnswer"){
        var naKey="crm_na_count_"+pendingStatus.leadId;
        var naTimeKey="crm_na_time_"+pendingStatus.leadId;
        var naCount=0; try{naCount=Number(localStorage.getItem(naKey)||0);}catch(e){}
        naCount+=1; try{localStorage.setItem(naKey,String(naCount));localStorage.setItem(naTimeKey,String(Date.now()));}catch(e){}
      } else {
        // Reset NoAnswer count if status changed to something else
        try{localStorage.removeItem("crm_na_count_"+pendingStatus.leadId);localStorage.removeItem("crm_na_time_"+pendingStatus.leadId);}catch(e){}
        // Reset no-activity rotation flags
        try{localStorage.removeItem("crm_noact2_"+pendingStatus.leadId);localStorage.removeItem("crm_hotrot_"+pendingStatus.leadId);localStorage.removeItem("crm_cbrot_"+pendingStatus.leadId);}catch(e){}
      }
      if(pendingStatus.newStatus==="DoneDeal") p.nav("deals");
      else if(pendingStatus.newStatus==="EOI") p.nav("eoi");
    } catch(e){alert(e.message);}
    setShowStatusComment(false); setPendingStatus(null); setShowStatusPicker(false);
  };
  var saveReschedule = async function(v) {
    if (!cbResched || !v) return;
    setCbReschedBusy(true);
    try { await confirmStatus("", v, null, null, { leadId: cbResched.leadId, newStatus: cbResched.status }); }
    finally { setCbReschedBusy(false); setCbResched(null); }
  };

  var openHistory = async function(lead) {
    setHistoryLead(lead); setShowHistory(true); setFullHistory([]); setHistoryLoading(true);
    var isAdminRole = p.cu.role==="admin"||p.cu.role==="operation"||positionOf(p.cu)==="director"||positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader";
    try {
      var hist = await apiFetch("/api/leads/"+gid(lead)+"/full-history","GET",null,p.token);
      var all = hist||[];
      if(!isAdminRole) {
        // Sales sees only their own activities
        all = all.filter(function(a){
          var auid = String(a.userId&&a.userId._id?a.userId._id:a.userId||"");
          return auid===String(p.cu.id||"");
        });
      }
      // Sort oldest to newest
      all = all.slice().sort(function(a,b){return new Date(a.createdAt)-new Date(b.createdAt);});
      setFullHistory(all);
    } catch(e){ setFullHistory([]); }
    setHistoryLoading(false);
  };

  var archiveLead = async function(lid) {
    if(!window.confirm(t.archiveConfirm)) return;
    try {
      await apiFetch("/api/leads/"+lid+"/archive","PUT",null,p.token,p.csrfToken);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===lid?Object.assign({},l,{archived:true}):l;});});
      if(selected&&gid(selected)===lid) setSelected(null);
    } catch(e){alert(e.message);}
  };

  var logActivity = async function() {
    if(!actNote.trim()||!selected) return;
    setSaving(true);
    try {
      var act = await apiFetch("/api/activities","POST",{leadId:gid(selected),type:actType,note:actNote},p.token);
      p.setActivities(function(prev){return [act].concat(prev);});
      var updated = await apiFetch("/api/leads/"+gid(selected),"PUT",{lastActivityTime:new Date()},p.token);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(selected)?updated:l;});});
      setSelected(updated); setActNote(""); setShowActForm(false);
    } catch(e){alert(e.message);}
    setSaving(false);
  };

  var handleImport = async function(e) {
    var file=e.target.files[0]; if(!file)return;
    setImporting(true); setImportMsg("");
    try {
      var rows=[];
      if(file.name.endsWith(".csv")){var txt=await file.text();var lines=txt.split(/\r?\n/).filter(Boolean);if(lines.length<2){setImportMsg(t.importErr);setImporting(false);return;}var hdrs=lines[0].split(",").map(function(h){return h.trim().replace(/['"]/g,"").toLowerCase();});rows=lines.slice(1).map(function(line){var vals=line.split(",").map(function(v){return v.trim().replace(/['"]/g,"");});var obj={};hdrs.forEach(function(h,i){obj[h]=vals[i]||"";});return obj;});}
      else{var XLSX=await loadXLSX();var wb=XLSX.read(await file.arrayBuffer(),{type:"array"});var ws=wb.Sheets[wb.SheetNames[0]];var raw=XLSX.utils.sheet_to_json(ws,{defval:""});rows=raw.map(function(r){var o={};Object.keys(r).forEach(function(k){o[k.toLowerCase().trim()]=String(r[k]);});return o;});}
      // Phase A2.5 (2026-05-05): pre-flight check that the file actually
      // carries a source column. The server now requires non-empty source
      // on every POST /api/leads, and per-row errors in the import loop
      // below are silently swallowed — without this check, a CSV missing
      // the source column would just produce "Import Done: 0" with no
      // explanation. DR imports skip the check (source is forced to
      // "Daily Request" at the POST site).
      if (!isReq && rows.length > 0) {
        var firstKeys = Object.keys(rows[0]);
        if (firstKeys.indexOf("source") === -1 && firstKeys.indexOf("المصدر") === -1) {
          setImportMsg("Your file is missing a 'source' column. Add a column named 'source' with one of: " + leadSourceNames().join(", "));
          setImporting(false);
          return;
        }
      }
      // Decide the field layout once from the header set (see rowToLead): a Unit
      // Type / Development column means the corrected 3-field template (v2).
      var newLayout = rows.length>0 && (function(){ var k=Object.keys(rows[0]); return k.indexOf("unit type")>=0||k.indexOf("unittype")>=0||k.indexOf("unit_type")>=0||k.indexOf("development")>=0||k.indexOf("نوع الوحدة")>=0; })();
      var toImport=rows.map(function(r){return rowToLead(r,newLayout);}).filter(function(l){return l.name&&l.phone;});
      if(!toImport.length){setImportMsg(t.importErr);setImporting(false);return;}
      var agId="";
      var created=[]; for(var i=0;i<toImport.length;i++){try{var lead=await apiFetch("/api/leads","POST",Object.assign({},toImport[i],{agentId:agId,source:isReq?"Daily Request":toImport[i].source,status:"NewLead",phone2:toImport[i].phone2||""}),p.token);if(toImport[i].phone2){try{var cache=JSON.parse(localStorage.getItem("phone2_cache")||"{}");if(lead._id)cache[String(lead._id)]=toImport[i].phone2;localStorage.setItem("phone2_cache",JSON.stringify(cache));}catch(e){}}created.push(lead);}catch(ex){}}
      p.setLeads(function(prev){return created.concat(prev);});
      setImportMsg("✅ "+t.importDone+": "+created.length);
    } catch(ex){setImportMsg(t.importErr+": "+ex.message);}
    setImporting(false); e.target.value="";
  };

  // Phase A4-main (2026-05-05): bulk source change handler. Validates
  // selection + chosen source, POSTs to /api/admin/bulk-update-source,
  // then mirrors the modifiedCount into local state via setLeads (matches
  // the existing doBulkReassign in-place-update pattern). Server returns
  // { updated, skippedNoOp, notFound, invalidIds } so the toast can give
  // an accurate breakdown when partial.
  var doBulkSourceChange = async function() {
    if (!bulkSource) { alert("Please select a source"); return; }
    if (!selected2.length) return;
    setBulkSourceSaving(true);
    try {
      var res = await apiFetch("/api/admin/bulk-update-source","POST",
        { ids: selected2, newSource: bulkSource }, p.token, p.csrfToken);
      // Mirror in local state — only IDs we sent get updated. Server-side
      // skipped/not-found rows leave their local state untouched (which is
      // already correct).
      var newSrc = bulkSource;
      p.setLeads(function(prev){
        return prev.map(function(l){
          return selected2.indexOf(gid(l)) >= 0
            ? Object.assign({}, l, { source: newSrc })
            : l;
        });
      });
      var msg = "✓ Updated " + (res.updated || 0) + " lead" + ((res.updated||0)===1?"":"s") + " to " + newSrc;
      if (res.skippedNoOp)  msg += " · " + res.skippedNoOp + " already on this source";
      if (res.notFound)     msg += " · " + res.notFound + " not found";
      if (res.invalidIds)   msg += " · " + res.invalidIds + " invalid IDs";
      alert(msg);
      setSelected2([]);
      setShowBulkSource(false);
      setBulkSource("");
    } catch (e) {
      alert("Failed: " + (e && e.message ? e.message : "Unknown error"));
    }
    setBulkSourceSaving(false);
  };

  var doBulkReassign = async function() {
    if(selected2.length===0) return;
    var tot = bulkDistributionTotals(bulkRows, selected2.length);
    if(!tot.canSubmit) return;
    setBulkReassignBusy(true); setBulkReassignErr("");
    try {
      var orderedIds = orderSelectedByDisplay(selected2, filtered);
      var distribution = bulkRows.map(function(r){ return { agentId:r.agentId, count:parseInt(r.count,10) }; });
      var res = await apiFetch("/api/leads/bulk-reassign","PUT",{ leadIds:orderedIds, distribution:distribution },p.token,p.csrfToken);
      // Backend returns { total, reassigned, skippedSelf, skippedFrozen, notFound,
      // byAgent:[{agentId, agentName, count, reassigned, skippedSelf, skippedFrozen, notFound}] }.
      // Mirror the server's positional slicing locally: slot i of the ordered
      // ids belongs to the slice whose cumulative count covers i. A same-agent
      // slot is a no-op either way; ids past the distributed total stay put.
      var agentBySlot = {};
      var off = 0;
      (Array.isArray(res && res.byAgent) ? res.byAgent : distribution).forEach(function(s){
        var n = typeof s.count === "number" ? s.count : 0;
        var u = p.users.find(function(x){ return gid(x)===String(s.agentId); });
        orderedIds.slice(off, off + n).forEach(function(id){ agentBySlot[id] = u || s.agentId; });
        off += n;
      });
      p.setLeads(function(prev){ return prev.map(function(l){ var a = agentBySlot[gid(l)]; return a ? Object.assign({},l,{agentId:a}) : l; }); });
      setSelected2([]); setShowBulk(false); setBulkRows([{ agentId:"", count:"" }]);
      var summary = bulkReassignSummary(res, "leads");
      if (summary) alert(summary);
    } catch(e){
      // In-modal error (no raw browser alert). Bulk reassign is safe to retry —
      // re-running only pushes a fresh slice; a timeout likely means the server
      // finished, so guide the admin to refresh and check before retrying.
      var msg=(e&&e.message)||"Something went wrong";
      if(/tim(e|ed)\s*out|timeout/i.test(msg)){
        setBulkReassignErr("This is taking longer than expected. The reassignment may have already completed on the server — close this, refresh, and check the agents' leads before retrying.");
      }else{
        setBulkReassignErr(msg);
      }
    }
    setBulkReassignBusy(false);
  };

  var leadActs = selected ? p.activities.filter(function(a){ var lid=gid(selected); return a.leadId&&(gid(a.leadId)===lid||a.leadId===lid); }) : [];

  // ===== STEP 2a — TableVirtuoso wiring for the main desktop table (L3). =====
  // Defined per-page (closures over selected/selected2/isOnlyAdmin) and memoized
  // so a stable identity is passed to Virtuoso's `components` prop — fresh
  // identities every render would force Virtuoso to re-mount the list and lose
  // scroll position. Deps cover every piece of state the render reads.
  var leadsMainTableComponents = useMemo(function(){
    LEADSPROF("mainComponentsBuilds"); // diagnostic only — a rebuild here remounts the whole <table> (ba6b70d)
    return {
      Table: function TableEl(props){
        return <table {...props} data-table-align="sheet" style={{ width:"100%", borderCollapse:"collapse", minWidth: p.isMobile?600:900 }}/>;
      },
      TableRow: function TableRowEl(props){
        var lead = props.item;
        if (!lead) return <tr {...props}/>;
        var lid = gid(lead);
        var isSel = selected && gid(selected) === lid;
        var isChk = selected2.includes(lid);
        var isVIP = lead.isVIP;
        var isRotated = isOnlyAdmin && lead.previousAgentIds && lead.previousAgentIds.filter(function(x){ return x != null; }).length > 0;
        // Strip Virtuoso-only props ("item", "context") before spreading to <tr>;
        // React 19 warns on unknown DOM attributes.
        var trProps = {};
        Object.keys(props).forEach(function(k){ if (k !== "item" && k !== "context") trProps[k] = props[k]; });
        return <tr {...trProps} onClick={function(){setSelected(lead);}} style={{ borderBottom:"1px solid #F1F5F9", cursor:"pointer", background:isSel?"#EFF6FF":isVIP?"#FFFBEB":isChk?"#F0FDF4":isRotated?"#FFF7ED":"transparent", transition:"background 0.12s", borderRight:isVIP?"3px solid #F59E0B":"3px solid transparent" }}/>;
      }
    };
  }, [selected, selected2, isOnlyAdmin, p.isMobile, setSelected]);

  // ===== STEP 2b — TableVirtuoso wiring for the "Important leads" tab (L1). =====
  // Simpler than the main table — no checkbox column, no bulk-select, no
  // VIP/rotated highlights. Single click → setSelected(lead). Background only
  // toggles on the currently-selected row.
  var leadsImportantTableComponents = useMemo(function(){
    LEADSPROF("importantComponentsBuilds"); // diagnostic only — a rebuild here remounts the whole <table> (ba6b70d)
    return {
      Table: function TableEl(props){
        return <table {...props} data-table-align="sheet" style={{ width:"100%", borderCollapse:"collapse", minWidth: p.isMobile?720:900 }}/>;
      },
      TableRow: function TableRowEl(props){
        var lead = props.item;
        if (!lead) return <tr {...props}/>;
        var lid = gid(lead);
        var isSel = selected && gid(selected) === lid;
        var trProps = {};
        Object.keys(props).forEach(function(k){ if (k !== "item" && k !== "context") trProps[k] = props[k]; });
        return <tr {...trProps} onClick={function(){setSelected(lead);}} style={{ borderBottom:"1px solid #F1F5F9", cursor:"pointer", background:isSel?"#EFF6FF":"transparent" }}/>;
      }
    };
  }, [selected, p.isMobile, setSelected]);

  var specialFilterLabel = (function(){
    if (!p.specialFilter||!p.specialFilter.type) return "";
    if (p.specialFilter.type === "aging") {
      var aMin = p.specialFilter.ageMin, aMax = p.specialFilter.ageMax;
      if (typeof aMin === "number" && typeof aMax === "number") return "Aging: " + aMin + "\u2013" + aMax + " days no contact";
      if (typeof aMin === "number") return "Aging: " + aMin + "+ days no contact";
      if (typeof aMax === "number") return "Aging: under " + aMax + " days no contact";
      return "Aging filter";
    }
    if (p.specialFilter.type === "project") {
      return "Project: " + (p.specialFilter.value || "(no project)");
    }
    // The overdue banner names its window, because the same filter serves two
    // sources now: the CALLBACKS tile (a period) and the bell / a bookmark (all
    // time). "Showing: Overdue callbacks" alone cannot tell them apart.
    if (p.specialFilter.type === "overdue") {
      return "Overdue callbacks" +
        (p.specialFilter.periodLabel ? " \u2014 " + p.specialFilter.periodLabel : "");
    }
    if (p.specialFilter.type === "reportDrill") {
      return (p.specialFilter.label || "Report row") + (p.specialFilter.periodLabel ? " \u2014 " + p.specialFilter.periodLabel : "");
    }
    if (p.specialFilter.type === "responseWatch") {
      return (p.specialFilter.state === "tapped" ? "Called \u2014 awaiting feedback" : "No action yet") +
        (p.specialFilter.periodLabel ? " \u2014 " + p.specialFilter.periodLabel : "");
    }
    if (p.specialFilter.type === "periodLeads") {
      return "Leads received" + (p.specialFilter.periodLabel ? " \u2014 " + p.specialFilter.periodLabel : "");
    }
    if (p.specialFilter.type === "interested") {
      // The tile's COHORT: created in the period and ever interested, met, or
      // closed. A lead that has since moved on (No Answer, Not Interested) is
      // still in it, so the banner must not claim a CURRENT status.
      return "Interested (became interested, had a meeting or closed)" +
        (p.specialFilter.periodLabel ? " \u2014 " + p.specialFilter.periodLabel : "");
    }
    var m = {untouched:"Untouched leads (no activity since assignment)",missingFeedback:"Missing feedback (empty notes)",stale48h:"Stale leads \u2014 no activity 48h+",noRotation:"Locked leads (noRotation flag)",rotatedThisMonth:"Rotated this month",interested:"Interested leads (HotCase + Potential)"};
    return m[p.specialFilter.type]||p.specialFilter.type;
  })();
  return <div style={{ padding:"18px 16px 40px" }}>
    {p.specialFilter&&p.specialFilter.type&&<div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, padding:"10px 14px", background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:10, marginBottom:12 }}>
      <div style={{ fontSize:13, color:"#1D4ED8", fontWeight:600, minWidth:0, overflow:"hidden", textOverflow:"ellipsis" }}>Showing: {specialFilterLabel} <span style={{ color:"#64748B", fontWeight:500 }}>({(tlActive && tlPaged.hasMore && tlPaged.total) ? tlPaged.total : filtered.length})</span>
        {p.specialFilter.back && <button onClick={function(){ saveReportReturn(p.specialFilter.back); if(p.setSpecialFilter)p.setSpecialFilter(null); if(p.nav)p.nav("reports"); }} style={{ marginInlineStart:10, background:"#fff", border:"1px solid #BFDBFE", color:"#1D4ED8", fontSize:12, fontWeight:600, padding:"3px 10px", borderRadius:8, cursor:"pointer" }}>{"\u2190 Back to report"}</button>}</div>
      <button onClick={function(){if(p.setSpecialFilter)p.setSpecialFilter(null);}} style={{ background:"#fff", border:"1px solid #BFDBFE", color:"#1D4ED8", fontSize:12, fontWeight:600, padding:"4px 10px", borderRadius:8, cursor:"pointer", display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>Clear filter <X size={13}/></button>
    </div>}
    <WaChooser show={!!waChooser} phone={waChooser} onClose={function(){setWaChooser(null);}}/>
    {showStatusPicker&&selected&&!showStatusComment&&<Modal show={true} onClose={function(){setShowStatusPicker(false);}} title={t.changeStatus}>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:16 }}>
        {sc.map(function(s){return <button key={s.value} onClick={function(){reqStatus(gid(selected),s.value);}} style={{ padding:"8px 14px", borderRadius:9, border:"1px solid "+s.color, background:currentStatus(selected)===s.value?s.bg:"#fff", color:s.color, fontSize:13, fontWeight:600, cursor:"pointer" }}>{s.label}</button>;})}
      </div>
      <Btn outline onClick={function(){setShowStatusPicker(false);}} style={{ width:"100%" }}>{t.cancel}</Btn>
    </Modal>}
    <StatusModal show={showStatusComment} t={t} token={p.token} isMobile={p.isMobile} requireDeveloper={true} newStatus={pendingStatus?pendingStatus.newStatus:null} lead={selected} cu={p.cu} users={p.users} onClose={function(){setShowStatusComment(false);}} onConfirm={confirmStatus}/>
    <RescheduleSheet open={!!cbResched} t={t} nowMs={cbNow} current={cbResched?cbResched.callbackTime:""} busy={cbReschedBusy} onClose={function(){if(!cbReschedBusy)setCbResched(null);}} onSave={saveReschedule}/>

    {feedbackModal && <Modal show={true} onClose={function(){ if(!fbSaving) setFeedbackModal(null); }} title="📝 Add Feedback">
      <FeedbackComposer id="standalone" cu={p.cu} lead={feedbackModal.lead} users={p.users}
        value={fbForm} rows={4}
        onChange={function(v){ setFbForm(v); setFbErr(""); }}/>
      {(function(){
        // Show this manager's own existing private notes for context.
        var pn = (feedbackModal.lead && Array.isArray(feedbackModal.lead.privateNotes)) ? feedbackModal.lead.privateNotes : [];
        if (!pn.length) return null;
        return <div style={{ marginTop:14, padding:"10px 12px", background:"#F8FAFC", borderRadius:10, border:"1px solid #E2E8F0" }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#475569", marginBottom:6 }}>YOUR PRIVATE NOTES ({pn.length})</div>
          {pn.slice().reverse().slice(0,5).map(function(n,i){
            return <div key={i} style={{ fontSize:12, color:C.text, marginBottom:6, paddingLeft:8, borderLeft:"2px solid #CBD5E1" }}>
              {n.text}
              <div style={{ fontSize:10, color:C.textLight, marginTop:2 }}>{fmtDateTime12(n.createdAt, t, { numeric:true, sep:", " })}</div>
            </div>;
          })}
        </div>;
      })()}
      {fbErr && <div style={{ fontSize:12, color:C.danger, marginTop:8 }}>{fbErr}</div>}
      <div style={{ display:"flex", gap:10, marginTop:14 }}>
        <Btn outline onClick={function(){ setFeedbackModal(null); }} disabled={fbSaving} style={{ flex:1 }}>Cancel</Btn>
        <Btn loading={fbSaving} onClick={async function(){
          var text = (fbForm.text || "").trim();
          if (!text) { setFbErr("Please type the feedback text"); return; }
          if (fbForm.visibility === "to_sales" && !fbForm.targetAgentId) { setFbErr("Pick a sales agent to send the feedback to"); return; }
          setFbSaving(true);
          try {
            var body = { text: text, visibility: fbForm.visibility };
            if (fbForm.visibility === "to_sales") body.targetAgentId = fbForm.targetAgentId;
            var res = await apiFetch("/api/leads/"+gid(feedbackModal.lead)+"/feedback","POST",body,p.token);
            if (res && res.lead && res.lead._id) {
              p.setLeads(function(prev){ return prev.map(function(l){ return gid(l)===gid(res.lead) ? res.lead : l; }); });
              if (selected && gid(selected) === gid(res.lead)) setSelected(res.lead);
            }
            setFeedbackModal(null);
          } catch(e) { setFbErr(e.message || "Failed to save feedback"); }
          finally { setFbSaving(false); }
        }} style={{ flex:1 }}>{fbSaving ? "Saving…" : "Save feedback"}</Btn>
      </div>
    </Modal>}

    {/* Bulk Reassign Modal — multi-agent distribution (2026-10-26) */}
    <Modal show={showBulk} onClose={function(){ if(!bulkReassignBusy){ setShowBulk(false); setBulkReassignErr(""); } }} title={t.bulkReassign}>
      <div style={{ marginBottom:14, padding:"10px 14px", background:"#F0F9FF", borderRadius:10, fontSize:13 }}>{selected2.length} leads selected</div>
      {selected2.length>1000&&<div style={{ marginBottom:10, padding:"8px 10px", background:"#FEF3F2", color:C.danger, borderRadius:8, fontSize:12 }}>⚠️ Max 1000 per batch — please select 1000 or fewer.</div>}
      <BulkDistributionEditor rows={bulkRows} setRows={setBulkRows} selectedCount={selected2.length} disabled={bulkReassignBusy} noun="leads"
        roster={(isOnlyAdmin?p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;}):myTeamUsers).map(function(u){return{value:gid(u),label:u.name+" - "+u.title};})}/>
      <div style={{ display:"flex", gap:10 }}><Btn outline disabled={bulkReassignBusy} onClick={function(){setShowBulk(false);setBulkReassignErr("");}} style={{ flex:1 }}>{t.cancel}</Btn><Btn loading={bulkReassignBusy} disabled={bulkReassignBusy||selected2.length>1000||!bulkDistributionTotals(bulkRows, selected2.length).canSubmit} onClick={function(){doBulkReassign();}} style={{ flex:1 }}>{t.bulkReassign}</Btn></div>
      {bulkReassignBusy&&<div style={{ marginTop:10, fontSize:12, color:C.textLight }}>⏳ Reassigning {selected2.length} lead{selected2.length>1?"s":""}… this can take a few seconds. Please keep this window open.</div>}
      {bulkReassignErr&&<div style={{ marginTop:10, padding:"8px 10px", background:"#FEF3F2", color:C.danger, borderRadius:8, fontSize:12, lineHeight:1.5 }}>{bulkReassignErr}</div>}
    </Modal>

    {/* Phase A4-main bulk source change modal — opens from the toolbar's
        "📡 Change Source" button. Lists current-source breakdown of the
        selected leads so admin can sanity-check what they're about to
        retag, picks a canonical source from the dropdown, confirms. */}
    <Modal show={showBulkSource} onClose={function(){setShowBulkSource(false);setBulkSource("");}} title={"Change source for " + selected2.length + " lead" + (selected2.length===1?"":"s")}>
      <div style={{ marginBottom:14, padding:"10px 14px", background:"#F5F3FF", borderRadius:10, fontSize:13, color:"#5B21B6" }}>{selected2.length} lead{selected2.length===1?"":"s"} selected</div>
      {/* Defensive guard (2026-05-06): IIFE only runs when modal is open.
          React evaluates JSX children expressions to compute the children
          prop even when Modal returns null, so without this guard the
          O(N×M) scan over p.leads × selected2 ran on every parent render. */}
      {showBulkSource && (function(){
        var counts = {};
        var selectedLeads = (p.leads || []).filter(function(l){return selected2.indexOf(gid(l)) >= 0;});
        selectedLeads.forEach(function(l){
          var s = l.source || "(empty)";
          counts[s] = (counts[s] || 0) + 1;
        });
        var rows = Object.keys(counts).map(function(s){return {source: s, count: counts[s]};}).sort(function(a,b){return b.count-a.count;});
        if (rows.length === 0) return null;
        return <div style={{ marginBottom:14, padding:"10px 14px", background:"#FAFAFA", borderRadius:10, fontSize:12, color:C.textLight, border:"1px solid #E8ECF1" }}>
          <div style={{ fontWeight:600, marginBottom:6, color:C.text, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px" }}>Current breakdown</div>
          {rows.map(function(r){return <div key={r.source} style={{ marginBottom:2 }}>{r.count}× {r.source}</div>;})}
        </div>;
      })()}
      <Inp label="New source" req type="select" value={bulkSource} onChange={function(e){setBulkSource(e.target.value);}} options={buildSourceOptions(bulkSource)}/>
      <div style={{ display:"flex", gap:10 }}>
        <Btn outline onClick={function(){setShowBulkSource(false);setBulkSource("");}} style={{ flex:1 }}>{t.cancel}</Btn>
        <Btn loading={bulkSourceSaving} onClick={doBulkSourceChange} style={{ flex:1 }}>Change Source</Btn>
      </div>
    </Modal>

    {/* Toolbar — NOT sticky (see the top bar's note: sticky was inert until 8ea78ed, and pinned this 390px block under the header on a phone). */}
    {(function(){
    // The toolbar's four pieces are built once, then arranged: desktop returns the
    // ORIGINAL tree (same nodes, same order); a phone gets MobileFilterChrome.
    var clearAll = function(){
      setCampaignFilter([]); setProjectFilter([]); setUnitTypeFilter([]); setSourceFilter("");
      setAgentFilter([]); setVipFilter(false); setNoAgentFilter(false);
      setInactiveAgentFilter(false); setDateRange("all");
      if (p.setSearch) p.setSearch("");
    };
    var chipsNode = <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
        {(function(){
          var base = [{v:"all",l:t.all}].concat(tabSc.map(function(s){return{v:s.value,l:s.label};}));
          // Sits next to New Lead because it is its complement: derived NewLead
          // that HAS been worked before, i.e. handed to a new holder who hasn't
          // touched it.
          //
          // NOT FOR SALES (2026-08-13). "Worked before" is lead-level for every
          // other role, but a sales row carries only the caller's own slice
          // (the overlay narrows assignments[] before applySummaryProjection),
          // so _hasAction reads "did I act" and the chip inverted: Jana had 69
          // rotated-in leads and the chip showed 0, with all 69 sitting under
          // New Lead instead. Splitting them correctly for sales would mean
          // telling an agent a lead had been worked by someone else, which the
          // rotation-invisibility invariant forbids (CLAUDE.md "Per-Agent Lead
          // State"). The New Lead chip already answers their question — theirs
          // is "nothing from me yet" — so the chip is simply not offered.
          if (!isSalesRole) base.push({v:"untouched_new",l:"🕓 Untouched"});
          if (isAdmin) base.push({v:"important",l:"⭐ Important"});
          if (isOnlyAdmin) base.push({v:"rotation_stopped",l:"🛑 Rotation Stopped"});
          if (isOnlyAdmin) base.push({v:"not_rotated",l:"🌱 Not Rotated"});
          return base;
        })().map(function(s){
          // Prefer the server-aggregated counts when loaded (matches the
          // rendered table's scope exactly — see leadsPageCounts effect
          // above). Until the response for the CURRENT filters lands the chip keeps
          // the previous numbers MUTED (or label only on first load); a failed
          // request shows no number at all — never another period's figures
          // un-muted, never a loaded-page count.
          //   - sales:    /counts?scope=historical → byStatus keyed by the
          //               caller's per-slice status, total already excludes
          //               closed states (EOI/DoneDeal/Deal Cancelled).
          //   - other:    strict /counts → byStatus keyed by top-level
          //               status; "All" sums byStatus excluding closed.
          //   - Important: no server equivalent (qualifyingMarks() walks
          //               per-doc; admin-only tab). Always in-memory.
          //   - NewLead:  server returns newLeadCount for sales scope=
          //               historical (matches isGenuineNewLead); other
          //               roles fall back to in-memory.
          var cnt;
          // Deliberately shadows the strict outer leadsPageCounts: inside the chip
          // strip the body may be the PREVIOUS question's, shown muted.
          var leadsPageCounts = chipCountsView.body;
          var bs = leadsPageCounts && leadsPageCounts.byStatus;
          if (leadsPageCounts) {
            if (s.v === "all") {
              if (isSalesRole) {
                cnt = leadsPageCounts.total || 0;
              } else {
                cnt = 0;
                Object.keys(bs || {}).forEach(function(k){
                  if (k === "EOI" || k === "DoneDeal" || k === "Deal Cancelled") return;
                  cnt += bs[k] || 0;
                });
              }
            } else if (s.v === "important") {
              // Server total from the targeted ?important=true fetch — the whole
              // in-scope list, not the loaded page. Until 2026-08-14 this line
              // counted allVisible and read 15 against a true 1,985. The
              // allVisible fallback only runs before that fetch lands.
              cnt = (impPaged.total > 0 || importantLeads !== null)
                ? impPaged.total
                : allVisible.filter(function(l){ return qualifyingMarks(l).length > 0; }).length;
            } else if (s.v === "rotation_stopped") {
              // Server-aggregated total (rotationStoppedCount) reflects every
              // matching lead in scope, not just the paginated p.leads slice.
              // Falls back to the in-memory mirror (any per-lead stop reason) if
              // the BE field is absent (sales-historical branch / pre-deploy FE).
              cnt = (typeof leadsPageCounts.rotationStoppedCount === "number")
                ? leadsPageCounts.rotationStoppedCount
                : allVisible.filter(leadRotationStopped).length;
            } else if (s.v === "not_rotated") {
              // Server-aggregated notRotatedCount, same precedent as the
              // rotation_stopped branch above. The in-memory fallback uses
              // leadEverRotated (previousAgentIds), the same basis as the server
              // facet — never rotationCount, which a first assignment inflates.
              cnt = (typeof leadsPageCounts.notRotatedCount === "number")
                ? leadsPageCounts.notRotatedCount
                : allVisible.filter(function(l){ return !leadEverRotated(l) && l.agentId; }).length;
            } else if (s.v === "NewLead") {
              // newLeadCount is now returned for EVERY role — sales via
              // scope=historical, the rest via derivedStatus=1 — and mirrors
              // isGenuineNewLead over the WHOLE scope. The allVisible fallback
              // only runs pre-deploy / if the field is missing; it counts the
              // loaded page slice, so the badge used to grow while scrolling.
              cnt = (typeof leadsPageCounts.newLeadCount === "number")
                ? leadsPageCounts.newLeadCount
                : allVisible.filter(isGenuineNewLead).length;
            } else if (s.v === "untouched_new") {
              // Complement of NewLead inside derived NewLead, so the two chips
              // add up to byStatus.NewLead and the whole strip sums to All.
              // Both operands are whole-scope server figures — no page slice.
              // ONE number from the server, not arithmetic on two others. The
              // subtraction this replaces is how the badge and the rows came to
              // describe different sets.
              cnt = (typeof leadsPageCounts.untouchedBacklogCount === "number")
                ? leadsPageCounts.untouchedBacklogCount
                : allVisible.filter(function(l){ return l.derivedUntouchedBacklog === true; }).length;
            } else {
              cnt = (bs && bs[s.v]) || 0;
            }
          } else if (s.v==="important") {
            // Its own targeted fetch, independent of the counts response.
            cnt = (importantLeads !== null)
              ? impPaged.total
              : allVisible.filter(function(l){ return qualifyingMarks(l).length > 0; }).length;
          } else {
            // No counts at all (first load) or the request failed: no number —
            // label only, width reserved below (the retry notice says why). This used to count the
            // loaded ≤100-row page instead — a number for a different set.
            cnt = null;
          }
          return <button key={s.v} onClick={function(){setLockedOnly(false);p.setFilter(s.v);}} style={{ padding:"5px 10px", borderRadius:7, border:"1px solid", borderColor:p.leadFilter===s.v?C.accent:"#E8ECF1", background:p.leadFilter===s.v?C.accent+"12":"#fff", color:p.leadFilter===s.v?C.accent:C.textLight, fontSize:11, fontWeight:500, cursor:"pointer", opacity:(chipCountsView.muted && s.v!=="important")?0.55:1 }}>{p.isMobile?mobShort(s.l):s.l}{leadsChipCountText(cnt, p.isMobile)}{cnt === null && !countsFailed && <span aria-hidden="true" style={{visibility:"hidden"}}>{p.isMobile?" 0000":" (0000)"}</span>}</button>;
        })}
        {countsFailed && <span data-counts-unavailable="1" style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 10px", borderRadius:7, border:"1px solid #FCA5A5", background:"#FEF2F2", color:"#B91C1C", fontSize:11, fontWeight:500 }}>
          Counts unavailable
          <button onClick={function(){ setCountsErrUrl(null); setCountsRetry(function(n){ return n + 1; }); }} style={{ padding:"2px 8px", borderRadius:5, border:"1px solid #B91C1C", background:"#fff", color:"#B91C1C", fontSize:11, fontWeight:600, cursor:"pointer" }}>Retry</button>
        </span>}
      </div>;
    // Unified date filter — ALL ROLES. The isAdmin gate that used to wrap
    // this was removed: every user filters their own scoped list, so the
    // bar exposes nothing role-specific.
    var dateNode = <DateFilterBar isMobile={p.isMobile} value={dateRange} onChange={setDateRange}
        label={isSalesRole ? "Received" : "Date Added"}
        quarter={dateQuarter} onQuarter={setDateQuarter}
        from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo}/>;
    var actionsNode = <div style={{ display:"flex", gap:7, flexWrap:"wrap" }}>
        {selected2.length>0&&isOnlyAdmin&&<Btn outline onClick={function(){setBulkReassignErr("");setShowBulk(true);}} style={{ padding:"7px 11px", fontSize:12, color:C.info, borderColor:C.info }}><RotateCcw size={13}/> {t.bulkReassign} ({selected2.length})</Btn>}
        {selected2.length>0&&isOnlyAdmin&&<Btn outline onClick={async function(){
          if(!window.confirm("Archive "+selected2.length+" leads?"))return;
          var ids=[...selected2];
          for(var i=0;i<ids.length;i++){
            try{await apiFetch("/api/leads/"+ids[i]+"/archive","PUT",null,p.token);}catch(e){}
          }
          p.setLeads(function(prev){return prev.map(function(l){return ids.includes(gid(l))?Object.assign({},l,{archived:true}):l;});});
          setSelected2([]);
          if(selected&&ids.includes(gid(selected)))setSelected(null);
        }} style={{ padding:"7px 11px", fontSize:12, color:C.warning, borderColor:C.warning }}><Archive size={13}/> Archive ({selected2.length})</Btn>}
        {selected2.length>0&&isOnlyAdmin&&<Btn outline onClick={function(){setShowBulkSource(true);}} style={{ padding:"7px 11px", fontSize:12, color:"#8B5CF6", borderColor:"#8B5CF6" }}>📡 Change Source ({selected2.length})</Btn>}
        {selected2.length>0&&<Btn outline onClick={function(){setShowBulkWa(true);}} style={{ padding:"7px 11px", fontSize:12, color:"#25D366", borderColor:"#25D366" }}>💬 {t.bulkWhatsApp} ({selected2.length})</Btn>}
        <input type="file" ref={fileRef} accept=".xlsx,.xls,.csv" onChange={handleImport} style={{ display:"none" }}/>
        {isOnlyAdmin&&<Btn outline onClick={function(){fileRef.current.click();}} loading={importing} style={{ padding:"7px 11px", fontSize:12 }}><Upload size={13}/> {t.importExcel}</Btn>}
        {isOnlyAdmin&&<Btn onClick={function(){setShowAdd(true);}} style={{ padding:"7px 11px", fontSize:12 }}><Plus size={14}/> {isReq?t.addRequest:t.addLead}</Btn>}
        {p.cu&&p.cu.role==="admin"&&<Btn outline onClick={runLeadsExport} disabled={exporting} style={{ padding:"7px 11px", fontSize:12, color:C.success, borderColor:C.success }}><FileSpreadsheet size={13}/> {exporting ? ("Exporting… " + exportDone.toLocaleString() + (exportTotal ? " / " + exportTotal.toLocaleString() : "")) : t.exportExcel}</Btn>}
        {!p.isMobile&&isOnlyAdmin&&<Btn outline onClick={function(){setShowQuickAdd(true);}} style={{ padding:"7px 11px", fontSize:12, color:C.info, borderColor:C.info }}><Zap size={13}/> {t.quickAdd}</Btn>}
      </div>;
    /* Was a native <select>. A native popup is rendered by the OS, not the
                page: on macOS it opens OVER its own trigger (and upward near the
                viewport bottom), which is the reported "opens upward covering the
                button". CSS cannot position it. SearchableSelect is the app's own
                dropdown — it opens below the trigger and only flips when there is
                genuinely no room, matching the Agents filter beside it. */
    var sortNode = <SearchableSelect noSearch hideEmpty value={sortBy}
          onChange={function(v){setLockedOnly(false);setSortBy(v);}}
          options={isSalesRole ? SALES_SORT_OPTIONS : SORT_OPTIONS} width={150} maxWidth={150} fontSize={12}/>;
    var agentNode = isAdmin&&<SearchableSelect multi value={agentFilter} onChange={function(v){setLockedOnly(false);setAgentFilter(v);setNoAgentFilter(false);}}
          options={agentFilterOpts} emptyLabel={p.isMobile?"👤 Agent":"👤 All Agents"} placeholder="Search agent…" width={200} maxWidth={200} fontSize={12}/>;
    var noAgentBtn = isOnlyAdmin&&<button onClick={function(){setLockedOnly(false);setInactiveAgentFilter(false);setNoAgentFilter(!noAgentFilter);setAgentFilter("");}} style={{ padding:"5px 12px", borderRadius:7, border:"1px solid", borderColor:noAgentFilter?"#EF4444":"#E8ECF1", background:noAgentFilter?"#FEE2E2":"#fff", color:noAgentFilter?"#B91C1C":C.textLight, fontSize:11, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>🚫 {p.isMobile?"No agent":"No Agent"} {noAgentFilter?"✓":""}</button>;
    var inactiveAgentN = (leadsPageCounts&&typeof leadsPageCounts.inactiveAgentCount==="number")?leadsPageCounts.inactiveAgentCount:(inactiveLeads!==null?inactiveLeads.length:allVisible.filter(isInactiveAgentRow).length);
    var inactiveBtn = isOnlyAdmin&&<button aria-label={p.isMobile?"Inactive agent leads ("+inactiveAgentN+")":undefined} onClick={function(){
          var turningOn = !inactiveAgentFilter;
          setInactiveAgentFilter(turningOn);
          if (turningOn) {
            // Turning ON — clear the competing base/scope filters (mirror
            // Locked Only / No Agent) so the view is a clean "leads owned by a
            // deactivated agent" set. Status/search/date can be layered after.
            setLockedOnly(false);
            p.setFilter("all");
            setAgentFilter("");
            setNoAgentFilter(false);
            setVipFilter(false);
          }
        }} style={{ padding:"5px 12px", borderRadius:7, border:"1px solid", borderColor:inactiveAgentFilter?"#D97706":"#E8ECF1", background:inactiveAgentFilter?"#FEF3C7":"#fff", color:inactiveAgentFilter?"#92400E":C.textLight, fontSize:11, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:5 }} title={p.isMobile?"Inactive agent leads: "+inactiveAgentN:"Show only leads whose assigned agent is deactivated"}>⛔ {p.isMobile?"Inactive":"Inactive Agent"} {p.isMobile?"":"("+inactiveAgentN+")"}{inactiveAgentFilter?" ✓":""}</button>;
    /* Both counts read `filtered.length` — the array the table actually
                renders — so the badge can never claim rows the list won't open.
                That is the whole point here: the old Locked badge read the same
                expression but over an in-memory slice of the 100-row bootstrap, so
                it agreed with the rows at 0 while 6 locked leads sat unreachable. */
    var vipBtn = <button onClick={function(){setLockedOnly(false);setVipFilter(!vipFilter);}} style={{ padding:"5px 12px", borderRadius:7, border:"1px solid", borderColor:vipFilter?"#F59E0B":"#E8ECF1", background:vipFilter?"#FEF3C7":"#fff", color:vipFilter?"#B45309":C.textLight, fontSize:11, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>⭐ {p.isMobile?"VIP":"VIP Only"}{vipFilter?" ("+filtered.length+") ✓":""}</button>;
    var lockedBtn = isOnlyAdmin&&<button onClick={function(){
          if (!lockedOnly) {
            // Turning ON — clear every other user-applied filter.
            p.setFilter("all");
            setAgentFilter("");
            setNoAgentFilter(false);
            setInactiveAgentFilter(false);
            setVipFilter(false);
            setSortBy("lastActivity");
            setSourceFilter("");
            setCampaignFilter("");
            setProjectFilter("");
            setUnitTypeFilter([]);
            if (p.setSearch) p.setSearch("");
          }
          setLockedOnly(!lockedOnly);
        }} style={{ padding:"5px 12px", borderRadius:7, border:"1px solid", borderColor:lockedOnly?"#EC4899":"#E8ECF1", background:lockedOnly?"#FCE7F3":"#fff", color:lockedOnly?"#BE185D":C.textLight, fontSize:11, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>🔒 {p.isMobile?"Locked":"Locked Only"}{lockedOnly?" ("+filtered.length+")":""}</button>;
    /* Source filter — extracted to top-level memo() component (see
                SourceFilterSelect definition near buildSourceOptions) so WS-driven
                parent re-renders don't reconcile the open native <select> and
                dismiss the OS dropdown. legacyList ref is stable thanks to the
                two-step string-hash memo above; onChange is wrapped in
                useCallback. Both stability properties are load-bearing for
                memo() to actually skip work. */
    var sourceNode = isOnlyAdmin && <SourceFilterSelect value={sourceFilter} onChange={onSourceFilterChange} legacyList={sourceFilterLegacyList} emptyLabel={p.isMobile?"📡 Source":undefined} />;
    /* The admin-only "📣 Campaign: any" presence/text probe that used to sit
                here was REMOVED 2026-08-05 — the normalized dropdown below replaces
                it outright and is strictly better (server-side, all tabs, all roles,
                per-option counts). Its three modes map over cleanly: "No campaign" →
                the "(No campaign)" row that /api/filter-options already emits,
                "Contains text…" → the dropdown's own search box, "Has campaign" →
                pick any named option. Do not reintroduce a second campaign control. */
    /* Campaign / Project dropdowns — ALL ROLES (no isOnlyAdmin gate). The
                Leads table already renders both columns to every role, so these
                expose nothing new; options come from /api/filter-options, which is
                scoped to the caller's own visible leads. Selecting one clears
                lockedOnly because that chip is a standalone filter that ignores
                everything else. */
    var campaignNode = <div style={{ minWidth:170 }}>
          <SearchableSelect multi value={campaignFilter} onChange={function(v){ setLockedOnly(false); setCampaignFilter(v); }}
            options={((filterOpts&&filterOpts.leadCampaigns)||[]).map(function(o){ return { value:o.value, label:o.label+" ("+o.count+")" }; })}
            emptyLabel={p.isMobile?"📣 Campaign":"📣 All campaigns"} placeholder="Search campaign…" width="100%" maxWidth={9999} fontSize={12}/>
        </div>;
    var projectNode = <div style={{ minWidth:170 }}>
          <SearchableSelect multi value={projectFilter} onChange={function(v){ setLockedOnly(false); setProjectFilter(v); }}
            options={((filterOpts&&filterOpts.leadProjects)||[]).map(function(o){ return { value:o.value, label:o.label+" ("+o.count+")" }; })}
            emptyLabel={p.isMobile?"🏢 Project":"🏢 All projects"} placeholder="Search project…" width="100%" maxWidth={9999} fontSize={12}/>
        </div>;
    /* Unit Type dropdown (2026-09-16) — data-derived from /api/filter-options,
                same as Campaign/Project, NOT the fixed UNIT_TYPES list. Owner ruling:
                every distinct raw unitType value is its own option under its actual
                stored spelling — no semantic mapping, no Arabic translation, no
                snake_case→Title Case conversion. The only merging is exact
                case/whitespace duplicates (server's collapse(), same as campaign/
                project), so "chalet"/"Chalet" is one option but "twin_house" and
                "Twinhouse" stay two. Cross-field junk ("call", "Tiktok", …) shows up
                as its own option too, deliberately — that's a data-quality question,
                not something this filter hides. */
    var unitNode = <div style={{ minWidth:170 }}>
          <SearchableSelect multi value={unitTypeFilter} onChange={function(v){ setLockedOnly(false); setUnitTypeFilter(v); }}
            options={((filterOpts&&filterOpts.leadUnitTypes)||[]).map(function(o){ return { value:o.value, label:o.label+" ("+o.count+")" }; })}
            emptyLabel={p.isMobile?"🏠 Unit type":"🏠 All unit types"} placeholder="Search unit type…" width="100%" maxWidth={9999} fontSize={12}/>
        </div>;
    var clearBtn = !p.isMobile&&(campaignFilter.length||projectFilter.length||unitTypeFilter.length||sourceFilter||agentFilter.length||vipFilter||noAgentFilter||inactiveAgentFilter||dateRange!=="all"||p.search)&&
          <button onClick={clearAll} style={{ padding:"5px 12px", borderRadius:7, border:"1px solid #E2E8F0", background:"#fff", fontSize:11, fontWeight:600, cursor:"pointer", color:C.danger }}>✕ Clear All</button>;
    if (!p.isMobile) {
      var selectsNode = <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
        {sortNode}{agentNode}{noAgentBtn}{inactiveBtn}{vipBtn}{lockedBtn}{sourceNode}{campaignNode}{projectNode}{unitNode}{clearBtn}
      </div>;
      return <div style={{ position:"relative", zIndex:90, background:"#F8FAFC", margin:"0 -16px 14px", padding:"8px 16px 8px", borderBottom:"1px solid #E8ECF1", boxShadow:"0 2px 8px rgba(0,0,0,0.04)" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:8 }}>
          {chipsNode}
          {dateNode}
          {actionsNode}
        </div>
        {selectsNode}
      </div>;
    }
    // ----- phone: ONE compact card — chips, 3x2 filter grid, toggle pills, active summary; ＋ lives in the header row -----
    var asArr = function(v){ return Array.isArray(v) ? v : (v ? [v] : []); };
    var many = function(v, icon, more){ var a = asArr(v); return a.length === 1 ? (icon+" "+a[0]) : (icon+" "+a.length+" "+more); };
    var pills = [];
    var dl = dateFilterPillLabel(dateRange, dateFrom, dateTo, dateQuarter);
    if (dl) pills.push({ key:"date", label:dl, onRemove:function(){ setDateRange("all"); } });
    if (asArr(agentFilter).length) {
      var agNames = asArr(agentFilter).map(function(id){ var o = agentFilterOpts.filter(function(x){ return String(x.value) === String(id); })[0]; return o ? o.label : ""; }).filter(Boolean);
      pills.push({ key:"agent", label: agNames.length === 1 ? ("👤 "+agNames[0]) : ("👤 "+asArr(agentFilter).length+" agents"), onRemove:function(){ setAgentFilter([]); } });
    }
    if (noAgentFilter) pills.push({ key:"noagent", label:"No agent", onRemove:function(){ setNoAgentFilter(false); } });
    if (inactiveAgentFilter) pills.push({ key:"inactive", label:"Inactive agent", onRemove:function(){ setInactiveAgentFilter(false); } });
    if (vipFilter) pills.push({ key:"vip", label:"⭐ VIP", onRemove:function(){ setVipFilter(false); } });
    if (lockedOnly) pills.push({ key:"locked", label:"🔒 Locked", onRemove:function(){ setLockedOnly(false); } });
    if (sourceFilter) pills.push({ key:"source", label:"📡 "+sourceFilter, onRemove:function(){ setSourceFilter(""); } });
    if (asArr(campaignFilter).length) pills.push({ key:"campaign", label:many(campaignFilter,"📣","campaigns"), onRemove:function(){ setCampaignFilter([]); } });
    if (asArr(projectFilter).length) pills.push({ key:"project", label:many(projectFilter,"🏢","projects"), onRemove:function(){ setProjectFilter([]); } });
    if (asArr(unitTypeFilter).length) pills.push({ key:"unit", label:many(unitTypeFilter,"🏠","unit types"), onRemove:function(){ setUnitTypeFilter([]); } });
    var hasActions = leadsPhoneHasFab(isOnlyAdmin, p.cu, selected2.length);
    // Date as a compact dropdown over the SAME setter and the SAME option list the chip bar uses;
    // Quarter / 📅 Date open the existing quarter picker / the calendar chip inline below the grid (DateFilterBar, compact).
    var dateSel = <SearchableSelect noSearch hideEmpty value={dateRange} onChange={setDateRange}
      options={DATE_FILTER_CHIPS.map(function(c){ return { value:c.v, label:"📅 "+(c.v === "all" ? (isSalesRole ? "Received" : "Date added") : c.v === "custom" ? "Date" : c.l) }; })}
      width="100%" maxWidth={9999} fontSize={12}/>;
    var dateInline = <DateFilterBar compact isMobile={p.isMobile} value={dateRange} onChange={setDateRange}
        quarter={dateQuarter} onQuarter={setDateQuarter}
        from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo}/>;
    // Bulk Reassign on a phone enters SELECT MODE (cards get checkboxes + a sticky bar); the desktop button only opens the modal.
    var selectEntry = isOnlyAdmin && <Btn outline onClick={function(){ setMActionsOpen(false); setMSelectMode(true); }}>Bulk Reassign</Btn>;
    var selSet = {}; selected2.forEach(function(id){ selSet[id] = 1; });
    var allSelectedHere = filtered.length > 0 && filtered.every(function(l){ return selSet[gid(l)]; });
    return <>
      {hasActions && <MobileFab open={mActionsOpen} hidden={hasRenderableSelected || mSelectMode} onToggle={function(){ setMActionsOpen(!mActionsOpen); }} onClose={function(){ setMActionsOpen(false); }}>{actionsNode.props.children}{selectEntry}</MobileFab>}
      {mSelectMode && <MobileSelectBar count={selected2.length} allSelected={allSelectedHere}
        onToggleAll={function(){ setSelected2(allSelectedHere ? [] : filtered.map(gid)); }}
        onReassign={function(){ setBulkReassignErr(""); setShowBulk(true); }}
        onCancel={function(){ setSelected2([]); setMSelectMode(false); }}/>}
      <div style={{ position:"relative", zIndex:90, background:"#F8FAFC", margin:"0 -16px 8px", padding:"8px 12px 8px", borderBottom:"1px solid #E8ECF1", display:"flex", flexDirection:"column", gap:8 }}>
        <ChipRows abbreviate={[["Rotation Stopped","Rot. Stopped"]]}>{chipsNode.props.children}</ChipRows>
        {isSalesRole && dateNode}
        <FilterGrid>{!isSalesRole && dateSel}{agentNode}{sourceNode}{campaignNode}{projectNode}{unitNode}</FilterGrid>
        {!isSalesRole && dateInline}
        <ToggleRow items={[{ node:noAgentBtn, on:noAgentFilter }, { node:inactiveBtn, on:inactiveAgentFilter }, { node:vipBtn, on:vipFilter }, { node:lockedBtn, on:lockedOnly }]}>{sortNode}</ToggleRow>
        <ActiveFilterStrip pills={pills} onClearAll={function(){ clearAll(); setLockedOnly(false); }}/>
      </div>
    </>;
    })()}
    {importMsg&&<div style={{ marginBottom:10, padding:"9px 14px", background:importMsg.startsWith("✅")?"#DCFCE7":"#FEE2E2", color:importMsg.startsWith("✅")?"#15803D":"#B91C1C", borderRadius:9, fontSize:13 }}>{importMsg}</div>}

    <div style={{ display:"flex", gap:14, paddingRight:!p.isMobile&&hasRenderableSelected?330:0, transition:"padding-right 0.25s" }}>
      {/* Status dropdown overlay */}
      {statusDrop&&<div data-overlay-above="true" style={{ position:"fixed", inset:0, zIndex:499 }} onClick={function(){setStatusDrop(null);}}/>}
    {/* Table */}
      {p.leadFilter==="important"?<Card style={{ flex:1, padding:0, overflow:"hidden", minWidth:0 }}>
        <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
          {/* STEP 2b — empty state renders the same chrome ("No important leads")
              as before; non-empty state hands off to TableVirtuoso so the
              "Important" tab also virtualizes when the qualifying-mark filter
              admits hundreds of rows (rare, but the path exists). */}
          {filtered.length === 0 ? (
            <table data-table-align="sheet" style={{ width:"100%", borderCollapse:"collapse", minWidth:p.isMobile?720:900 }}>
              <thead><tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120 }}>{t.name}</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120 }}>{t.phone}</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120 }}>First Marked By</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:110 }}>First Mark Date</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:110 }}>First Status</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:160 }}>Feedback</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:110 }}>Current Status</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120 }}>Current Agent</th>
              </tr></thead>
              <tbody>
                <tr><td colSpan={8} style={{ padding:40, textAlign:"center", color:C.textLight, fontSize:13 }}>No important leads</td></tr>
              </tbody>
            </table>
          ) : (
            <TableVirtuoso
              useWindowScroll
              increaseViewportBy={LIST_VIEWPORT_OVERSCAN}
              defaultItemHeight={LIST_ROW_H_TABLE}
              data={filtered}
              components={leadsImportantTableComponents}
              computeItemKey={function(idx, lead){ return (lead && lead._id) ? String(lead._id) : idx; }}
              endReached={loadMoreRendered}
              fixedHeaderContent={function(){
                return <tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120, background:"#F8FAFC" }}>{t.name}</th>
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120, background:"#F8FAFC" }}>{t.phone}</th>
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120, background:"#F8FAFC" }}>First Marked By</th>
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:110, background:"#F8FAFC" }}>First Mark Date</th>
                  <th style={Object.assign({ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, background:"#F8FAFC" }, STATUS_COL_PIN_PLAIN)}>First Status</th>
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:160, background:"#F8FAFC" }}>Feedback</th>
                  <th style={Object.assign({ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, background:"#F8FAFC" }, STATUS_COL_PIN_PLAIN)}>Current Status</th>
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120, background:"#F8FAFC" }}>Current Agent</th>
                </tr>;
              }}
              itemContent={function(idx, lead){
                var marks=qualifyingMarks(lead);
                var first=marks[0]||{};
                var curSt=currentStatus(lead);
                var curSo=sc.find(function(s){return s.value===curSt;})||sc[0];
                var firstSo=sc.find(function(s){return s.value===first.status;})||{label:first.status||"",bg:"#F1F5F9",color:"#64748B"};
                var firstDate=first.date?new Date(first.date).toLocaleDateString("en-GB"):"—";
                var curAgentName=userNameOf(lead.agentId, p.users)||"—";
                return <Fragment>
                  <td style={{ padding:"10px 12px", fontSize:13, fontWeight:600, color:C.text, textAlign:"left", whiteSpace:"nowrap" }}>{lead.name}</td>
                  <td style={{ padding:"10px 12px", fontSize:13, fontWeight:600, textAlign:"left", whiteSpace:"nowrap", direction:"ltr" }}><PhoneCell phone={lead.phone}/></td>
                  <td style={{ padding:"10px 12px", fontSize:12, color:C.text, textAlign:"left", whiteSpace:"nowrap" }}>{first.agentName||"—"}</td>
                  <td style={{ padding:"10px 12px", fontSize:12, color:C.textLight, textAlign:"left", whiteSpace:"nowrap" }}>{firstDate}</td>
                  {/* Both status columns in this table pinned 2026-08-21, same ruling
                      as DR/Leads-main. These pills already carried nowrap, so they never
                      WRAPPED — what they did was let the column resize with whichever
                      rows were on screen. Pinning buys the visual stability; it cannot
                      cost a measure loop, since a pin only removes freedom. */}
                  <td style={Object.assign({ padding:"10px 12px", textAlign:"left" }, STATUS_COL_PIN_PLAIN)}>
                    <span style={{ background:firstSo.bg, color:firstSo.color, padding:"3px 9px", borderRadius:14, fontSize:11, fontWeight:600, whiteSpace:"nowrap" }}>{firstSo.label||first.status||"—"}</span>
                  </td>
                  {/* PINNED, NOT CAPPED — preventive, same bug class as the DR
                      Last Feedback cell (01061ef). This is the only other
                      wrapping-and-capped cell in a virtualised table: max-width
                      with whiteSpace:normal inside a TableVirtuoso.

                      Under `table-layout:auto`, max-width CAPS a column but does
                      not FLOOR it, so the column is sized from whichever rows
                      are currently rendered. In a virtualiser that set changes
                      constantly, and wrap width -> line count -> row height ->
                      what Virtuoso measures -> new window. On Daily Requests
                      that loop ran at one layout shift per animation frame.

                      Not reported as shaking here — the Leads feedback column is
                      wider (240) and its text shorter, so the loop evidently
                      never caught. Pinned anyway: the difference between the two
                      tables is data, not structure, and data changes. */}
                  <td style={{ padding:"10px 12px", fontSize:12, color:C.text, textAlign:"left", width:240, minWidth:240, maxWidth:240, wordBreak:"break-word", whiteSpace:"normal", lineHeight:1.4 }}>{first.feedback||<span style={{color:"#CBD5E1"}}>—</span>}</td>
                  <td style={Object.assign({ padding:"10px 12px", textAlign:"left" }, STATUS_COL_PIN_PLAIN)}>
                    <span style={{ background:curSo.bg, color:curSo.color, padding:"3px 9px", borderRadius:14, fontSize:11, fontWeight:600, whiteSpace:"nowrap", border:"1px dashed "+curSo.color }}>{curSo.label}</span>
                  </td>
                  <td style={{ padding:"10px 12px", fontSize:12, color:C.text, textAlign:"left", whiteSpace:"nowrap" }}>{curAgentName}</td>
                </Fragment>;
              }}
            />
          )}
        </div>
      </Card>:p.isMobile?<div style={{ padding:leadsPhoneHasFab(isOnlyAdmin, p.cu, selected2.length)?"4px 16px 170px":"4px 16px", maxWidth:480, margin:"0 auto", width:"100%", boxSizing:"border-box" }}>
        {/* STEP 2b — mobile cards virtualized via Virtuoso (not TableVirtuoso,
            since these are divs not <tr>). useWindowScroll keeps page-level
            scroll. The 12px vertical gap previously provided by flex `gap:12`
            on the wrapper is rebuilt as `marginBottom:12` on each card item
            (Virtuoso uses absolute positioning internally; flex gap doesn't
            apply across virtualized siblings). */}
        {filtered.length === 0 ? (
          <div style={{ textAlign:"center", padding:40, color:C.textLight }}>No data</div>
        ) : (
          <Virtuoso
            useWindowScroll
            increaseViewportBy={LIST_VIEWPORT_OVERSCAN}
            defaultItemHeight={LIST_ROW_H_CARD}
            data={filtered}
            computeItemKey={function(idx, lead){ return (lead && lead._id) ? String(lead._id) : idx; }}
            endReached={loadMoreRendered}
            itemContent={function(idx, lead){
              var curSt=currentStatus(lead); var lf=latestFeedbackForPanel(lead); var so=sc.find(function(s){return s.value===curSt;})||sc[0]; var isVIP=lead.isVIP;
              var lastAct=lead.lastActivityTime?timeAgo(lead.lastActivityTime,t):"—";
              var actColor=lead.lastActivityTime&&(Date.now()-new Date(lead.lastActivityTime).getTime())>3*24*60*60*1000?C.danger:C.accent;
              var cbModel=(callbackDisplayMode(lead)!=="hide"&&lead.callbackTime)?callbackStripModel(lead.callbackTime,t,cbNow,callbackDisplayMode(lead)==="neutral"):null;
              var borderCol=(cbModel&&cbModel.style.border)||(isVIP?"#F59E0B":so.color||"#E8ECF1");
              var isRotated = isOnlyAdmin && lead.previousAgentIds && lead.previousAgentIds.filter(function(x){ return x != null; }).length > 0;
              var selOn = selected2.indexOf(gid(lead)) >= 0;
              return <div onClick={function(){ if (mSelectMode) { setSelected2(function(prev){ return prev.indexOf(gid(lead)) >= 0 ? prev.filter(function(x){ return x !== gid(lead); }) : prev.concat(gid(lead)); }); } else setSelected(lead); }}
                style={{ position:"relative", background:isRotated?"#FFF7ED":"#fff", borderRadius:16, padding:mSelectMode?"16px 16px 16px 50px":"16px",
                  border:"2px solid "+borderCol,
                  cursor:"pointer", boxShadow:"0 3px 12px "+borderCol+"35", marginBottom:12 }}>
            {mSelectMode&&<MobileSelectBox on={selOn}/>}
            {/* Header row */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:16, fontWeight:700, color:isVIP?C.accent:C.text, marginBottom:3 }}>{isVIP?"⭐ ":""}{lead.name}</div>
                <div style={{ fontSize:12, fontWeight:700, color:C.text, direction:"ltr" }}><PhoneCell phone={lead.phone}/></div>
                {(function(){var agName=userNameOf(lead.agentId, p.users);return agName?<div style={{ fontSize:11, color:C.accent, fontWeight:600, marginTop:2 }}>👤 {agName}{isInactiveAgentRow(lead)&&<span style={{ marginLeft:6, background:"#FEF3C7", color:"#92400E", border:"1px solid #FDE68A", borderRadius:10, padding:"1px 6px", fontSize:9, fontWeight:700 }}>Inactive</span>}</div>:null;})()}
              </div>
              <span style={{ background:so.bg, color:so.color, padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:700, whiteSpace:"nowrap", marginLeft:8 }}>{so.label}</span>
            </div>
            {/* Phone2 */}
            {lead.phone2&&<div style={{ fontSize:12, fontWeight:700, color:C.text, direction:"ltr", marginBottom:4 }}><PhoneCell phone={lead.phone2}/></div>}
            {/* Campaign + Project + Last Activity — order mirrors the table (Campaign → Project → Status). */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, flexWrap:"wrap", gap:4 }}>
              <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                {lead.campaign&&<span style={{ fontSize:11, color:"#0369A1", fontWeight:700, background:"#E0F2FE", padding:"2px 8px", borderRadius:6 }}>📣 {lead.campaign}</span>}
                {lead.project?<span style={{ fontSize:11, color:"#6D28D9", fontWeight:700, background:"#EDE9FE", padding:"2px 8px", borderRadius:6 }}>📍 {lead.project}</span>:<span style={{ color:C.textLight, fontSize:11 }}>—</span>}
                {lead.unitType&&<span style={{ fontSize:11, color:"#B45309", fontWeight:700, background:"#FEF3C7", padding:"2px 8px", borderRadius:6 }}>🏷️ {lead.unitType}</span>}
                {lead.unitCode&&<span style={{ fontSize:11, color:"#0F766E", fontWeight:700, background:"#CCFBF1", padding:"2px 8px", borderRadius:6 }}>🔖 {lead.unitCode}</span>}
              </div>
              <span style={{ fontSize:11, color:actColor, fontWeight:600 }}>🕐 {lastAct}</span>
            </div>
            {/* Last Feedback (slice that owns the most-recent action) */}
            {lf&&lf.text&&<div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:8, padding:"6px 10px", background:"#F8FAFC", borderRadius:8, borderLeft:"3px solid "+C.accent }}>💬 {lf.text}{lf.agentName&&positionOf(p.cu)!=="sales"&&<div style={{ fontSize:10, fontWeight:500, color:C.textLight, marginTop:3 }}>— by {lf.agentName}</div>}</div>}
            {/* Callback strip — CLOSED RECORD = NO CALLBACK (owner rule, 2026-09-08).
                The mobile twin of the desktop cell above it, and of the Daily
                Requests card: a Done Deal / EOI lead renders no strip at all (cbModel
                is null), and a Not Interested lead keeps its date in neutral grey —
                never an overdue level. Wording, levels and the 24 h+ Reschedule pill
                live in src/lib/callbackStrip.js; the card border above follows the
                level. Display only here — the sheet writes through confirmStatus. */}
            <CallbackStrip model={cbModel} variant="card" t={t} onReschedule={function(){setCbResched({leadId:gid(lead),status:curSt,callbackTime:lead.callbackTime});}}/>
            {/* Action buttons */}
            <div style={{ display:"flex", gap:8 }}>
              <a href={"tel:"+cleanPhone(lead.phone)} onClick={function(e){e.stopPropagation();logContactTap(lead,"call",p.token,p.csrfToken);}}
                style={{ flex:1, padding:"11px", borderRadius:10, background:"#EFF6FF", color:"#1D4ED8", fontSize:13, fontWeight:700, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:6, border:"1px solid #BFDBFE" }}>
                <Phone size={14} color="#1D4ED8"/> Call
              </a>
              <a href={"https://wa.me/"+waPhone(lead.phone)} target="_blank" rel="noreferrer" onClick={function(e){e.stopPropagation();logContactTap(lead,"whatsapp",p.token,p.csrfToken);}}
                style={{ flex:1, padding:"11px", borderRadius:10, background:"#DCFCE7", color:"#15803D", fontSize:13, fontWeight:700, border:"1px solid #22C55E60", cursor:"pointer", textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="#15803D"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> WhatsApp
              </a>
            </div>
          </div>;
            }}
          />
        )}
      </div>:<Card style={{ flex:1, padding:0, overflow:"hidden", minWidth:0 }}>
        <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
          {/* STEP 2a — empty state renders as a plain table so the "No data"
              placeholder shows the same chrome (thead + colSpan cell) as before.
              Non-empty state hands off to TableVirtuoso, which mounts only the
              visible viewport of rows (~20 vs 1115) and uses useWindowScroll so
              the page-level scroll bar remains the single scroll surface.
              Wrapper `<div style="overflowX:auto">` is preserved so narrow
              viewports still scroll the table horizontally without changing
              the page width. */}
          {filtered.length === 0 ? (
            <table data-table-align="sheet" style={{ width:"100%", borderCollapse:"collapse", minWidth:p.isMobile?600:900 }}>
              <thead><tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
                <th style={{ padding:"10px 8px", width:32 }}><input type="checkbox" disabled/></th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:100 }}>{t.name}</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120 }}>{t.phone}</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:110 }}>{t.phone2}</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:110 }}>Campaign</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:100 }}>{t.project}</th>
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:110 }}>{t.status}</th>
                {!p.isMobile&&<th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120 }}>Last Feedback</th>}
                {!p.isMobile&&isAdmin&&<th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:90 }}>{t.source}</th>}
                {isAdmin&&<th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:100 }}>{t.agent}</th>}
                <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:90 }}>{t.lastActivity}</th>
                {!p.isMobile&&<th style={Object.assign({ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight }, CB_COL_PIN)}>{t.callbackTime}</th>}
              </tr></thead>
              <tbody>
                <tr><td colSpan={10} style={{ padding:40, textAlign:"center", color:C.textLight, fontSize:13 }}>No data</td></tr>
              </tbody>
            </table>
          ) : (
            <TableVirtuoso
              useWindowScroll
              increaseViewportBy={LIST_VIEWPORT_OVERSCAN}
              defaultItemHeight={LIST_ROW_H_TABLE}
              data={filtered}
              components={leadsMainTableComponents}
              computeItemKey={function(idx, lead){ return (lead && lead._id) ? String(lead._id) : idx; }}
              endReached={loadMoreRendered}
              fixedHeaderContent={function(){
                // Header "select all" must reflect the real selection and toggle OFF
                // when everything is already selected. It was uncontrolled (no
                // `checked`), so after the virtualized header re-rendered the DOM box
                // desynced from selected2 and a 2nd click re-selected instead of
                // clearing. Derive `allSel` from selected2 and toggle on it.
                var selSet2 = new Set(selected2);
                var allSel = filtered.length>0 && filtered.every(function(l){return selSet2.has(gid(l));});
                return <tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
                  <th style={{ padding:"10px 8px", width:32, background:"#F8FAFC" }}><input type="checkbox" checked={allSel} onChange={function(){setSelected2(allSel?[]:filtered.map(function(l){return gid(l);}));}}/></th>
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:100, background:"#F8FAFC" }}>{t.name}</th>
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120, background:"#F8FAFC" }}>{t.phone}</th>
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:110, background:"#F8FAFC" }}>{t.phone2}</th>
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:110, background:"#F8FAFC" }}>Campaign</th>
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:100, background:"#F8FAFC" }}>{t.project}</th>
                  {/* minWidth:110 REPLACED by the pin rather than left beside it. 110 < 152
                      so the pin would have dominated anyway, but leaving both means the
                      header and the body state two different contracts for one column and
                      the next reader has to work out which wins. */}
                  <th style={Object.assign({ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, background:"#F8FAFC" }, STATUS_COL_PIN)}>{t.status}</th>
                  {!p.isMobile&&<th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:120, background:"#F8FAFC" }}>Last Feedback</th>}
                  {!p.isMobile&&isAdmin&&<th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:90, background:"#F8FAFC" }}>{t.source}</th>}
                  {isAdmin&&<th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:100, background:"#F8FAFC" }}>{t.agent}</th>}
                  <th style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, minWidth:90, background:"#F8FAFC" }}>{t.lastActivity}</th>
                  {!p.isMobile&&<th style={Object.assign({ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:600, color:C.textLight, background:"#F8FAFC" }, CB_COL_PIN)}>{t.callbackTime}</th>}
                </tr>;
              }}
              itemContent={function(idx, lead){
                var lid=gid(lead); var curSt=currentStatus(lead); var lf=latestFeedbackForPanel(lead); var so=sc.find(function(s){return s.value===curSt;})||sc[0];
                var isChk=selected2.includes(lid);
                return <Fragment>
                  <td style={{ padding:"10px 8px" }} onClick={function(e){e.stopPropagation();setSelected2(function(prev){return prev.includes(lid)?prev.filter(function(x){return x!==lid;}):[...prev,lid];});}}><input type="checkbox" checked={isChk} readOnly/></td>
                  <td style={{ padding:"10px 12px", textAlign:"left" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                      {lead.isVIP&&<span style={{ fontSize:14 }} title="VIP">⭐</span>}
                      {lead.locked&&<span style={{ fontSize:12 }} title="Locked — no rotation">🔒</span>}
                      {lead.rotationStopped&&<span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:5, background:"#FEE2E2", color:"#991B1B", whiteSpace:"nowrap" }} title="Rotation permanently stopped — 3 consecutive Not Interested">🛑 Rotation Stopped</span>}
                      <div style={{ fontSize:13, fontWeight:600, color:lead.isVIP?C.accent:C.text, whiteSpace:"nowrap" }}>{lead.name}</div>
                    </div>
                    <div style={{ fontSize:10, color:C.textLight }}>{lead.email}</div>
                  </td>
                  <td style={{ padding:"10px 12px", whiteSpace:"nowrap", textAlign:"left" }}>
                    <div style={{ fontSize:14, fontWeight:600, direction:"ltr", display:"inline-block" }}><PhoneCell phone={lead.phone}/></div>
                    <div style={{ display:"flex", gap:6, marginTop:3, justifyContent:"flex-start" }}>
                      <a href={"tel:"+cleanPhone(lead.phone)} onClick={function(e){e.stopPropagation();logContactTap(lead,"call",p.token,p.csrfToken);}} style={{ fontSize:12, color:"#60A5FA", textDecoration:"none", display:"flex", alignItems:"center", gap:3, padding:"2px 6px", borderRadius:6, background:"#EFF6FF" }}><Phone size={12}/> {t.call}</a>
                      <a href={"https://wa.me/"+waPhone(lead.phone)} target="_blank" rel="noreferrer" onClick={function(e){e.stopPropagation();logContactTap(lead,"whatsapp",p.token,p.csrfToken);}} style={{ fontSize:12, color:"#25D366", textDecoration:"none", display:"flex", alignItems:"center", gap:3, padding:"2px 6px", borderRadius:6, background:"#DCFCE720" }}><svg viewBox="0 0 24 24" width="16" height="16" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> {t.whatsapp}</a>
                    </div>
                  </td>
                  <td style={{ padding:"10px 12px", whiteSpace:"nowrap", textAlign:"left" }}>
                    {lead.phone2&&lead.phone2!==lead.phone?(function(){
                      return <div>
                        <div style={{ fontSize:14, fontWeight:600, direction:"ltr" }}><PhoneCell phone={lead.phone2}/></div>
                        <div style={{ display:"flex", gap:6, marginTop:3 }}>
                          <a href={"tel:"+cleanPhone(lead.phone2)} onClick={function(e){e.stopPropagation();logContactTap(lead,"call",p.token,p.csrfToken);}} style={{ fontSize:p.isMobile?10:12, color:"#60A5FA", textDecoration:"none", display:"flex", alignItems:"center", gap:3, padding:p.isMobile?"0":"2px 6px", borderRadius:6, background:p.isMobile?"transparent":"#EFF6FF" }}><Phone size={p.isMobile?9:12}/>{!p.isMobile&&" Call"}</a>
                          <a href={"https://wa.me/"+waPhone(lead.phone2)} target="_blank" rel="noreferrer" onClick={function(e){e.stopPropagation();logContactTap(lead,"whatsapp",p.token,p.csrfToken);}} style={{ fontSize:p.isMobile?10:12, color:"#25D366", textDecoration:"none", display:"flex", alignItems:"center", gap:3, padding:p.isMobile?"0":"2px 6px", borderRadius:6, background:p.isMobile?"transparent":"#DCFCE720" }}><svg viewBox="0 0 24 24" width={p.isMobile?14:16} height={p.isMobile?14:16} fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>{!p.isMobile&&" WhatsApp"}</a>
                        </div>
                      </div>;
                    })():<span style={{ fontSize:12, direction:"ltr", display:"inline-block" }}>{lead.phone2?<PhoneCell phone={lead.phone2}/>:<span style={{color:"#CBD5E1"}}>-</span>}</span>}
                  </td>
                  <td style={{ padding:"10px 12px", fontSize:12, color:C.textLight, textAlign:"left", maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead.campaign||<span style={{color:"#CBD5E1"}}>-</span>}</td>
                  <td style={{ padding:"10px 12px", fontSize:12, color:C.textLight, textAlign:"left", maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead.project}</td>
                  {/* Pinned 2026-08-21. The shake audit left this column UNCAPPED and
                      called it loop-safe, which was right — uncapped takes max-content
                      and the width->height path never closes. The owner now wants it
                      visually stable too, and a pin is loop-safe by construction, so
                      this needs no re-measure. The pill already carried nowrap here
                      (unlike DR's), so only the column contract was missing.

                      `overflow:hidden` added 2026-08-22 with the "was:" chip fix.
                      The chip used to be an INLINE sibling of the pill, and this
                      cell's pin carries whiteSpace:"nowrap" — so the two could not
                      share a line and could not wrap either; the chip simply ran
                      out of the 152px cell and over Last Feedback. It is now a
                      block below the pill (see below), and the cell clips, so a
                      status/chip pair that ever exceeds 152 is truncated rather
                      than drawn on top of the next column.

                      This costs no measure loop: the chip is nowrap and the pill
                      is nowrap, so neither one's line count depends on the column
                      width. Row height moves with CONTENT (chip present or not),
                      which is stable per row — the width->wrap->height->virtualiser
                      cycle needs a width-sensitive wrap, and there is none here.

                      The status dropdown is position:fixed, so its containing block
                      is the viewport and this overflow does NOT clip it. Anything
                      that later re-anchors that menu to the cell (position:absolute,
                      or a transform/filter/will-change on any ancestor) would make
                      it clippable — that change has to move the menu out of this
                      subtree, not delete this overflow. */}
                  <td style={Object.assign({ padding:"10px 12px", position:"relative", overflow:"hidden" }, STATUS_COL_PIN)} onClick={function(e){e.stopPropagation();}}>
                    <div style={{ position:"relative", display:"inline-block" }}>
                      <span style={{ background:so.bg, color:so.color, padding:"4px 10px", borderRadius:20, fontSize:12, fontWeight:600, whiteSpace:"nowrap", border:"1px dashed "+so.color, display:"inline-flex", alignItems:"center", gap:4, cursor:"pointer" }}
                        onClick={function(e){e.stopPropagation();setStatusDrop(statusDrop===lid?null:lid);}}>
                        {so.label} ▼
                      </span>
                      {statusDrop===lid&&<div data-overlay-above="true" style={Object.assign({ position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)", zIndex:500 }, SS_MENU_PANEL)} onClick={function(e){e.stopPropagation();}}>
                        <div style={SS_MENU_HEADER}>{t.changeStatus}</div>
                        <div style={{ overflowY:"auto" }}>
                        {sc.map(function(s){return <div key={s.value} onClick={function(e){e.stopPropagation();setSelected(lead);reqStatus(lid,s.value);setStatusDrop(null);}} style={Object.assign({}, SS_MENU_ITEM, { background:curSt===s.value?s.bg:"transparent", fontWeight:curSt===s.value?600:400 })}
                          onMouseEnter={function(e){if(curSt!==s.value)e.currentTarget.style.background=SS_MENU_HOVER;}}
                          onMouseLeave={function(e){if(curSt!==s.value)e.currentTarget.style.background=curSt===s.value?s.bg:"transparent";}}>
                          <span style={Object.assign({}, SS_MENU_DOT, { background:s.color })}/><span style={{ color:s.color }}>{s.label}</span>
                        </div>;})}
                        </div>
                        <div style={SS_MENU_FOOTER}><button onClick={function(e){e.stopPropagation();setStatusDrop(null);}} style={SS_MENU_CANCEL}>{t.cancel}</button></div>
                      </div>}
                    </div>
                    {(function(){if(!isOnlyAdmin||!lead.assignments||lead.assignments.length<=1)return null;var SP=["MeetingDone","HotCase","Potential","CallBack","NoAnswer","NotInterested","NewLead"];var SL={"MeetingDone":"Meeting Done","HotCase":"Hot Case","Potential":"Potential","CallBack":"Call Back","NoAnswer":"No Answer","NotInterested":"Not Interested","NewLead":"New Lead"};var curIdx=SP.indexOf(lead.status);var bestIdx=SP.length;for(var ai=0;ai<lead.assignments.length;ai++){var si=SP.indexOf(lead.assignments[ai].status);if(si>=0&&si<bestIdx)bestIdx=si;}if(bestIdx>=curIdx||bestIdx>=SP.length)return null;return <div style={{marginTop:4}}><span style={{background:"#F1F5F9",color:"#64748B",padding:"2px 6px",borderRadius:8,fontSize:10,fontWeight:500,display:"inline-block",maxWidth:"100%",boxSizing:"border-box",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>was: {SL[SP[bestIdx]]||SP[bestIdx]}</span></div>;})()}
                  </td>
                  {/* WIDTH IS PINNED, NOT CAPPED — same fix as the DR table's
                      Last Feedback column (01061ef), applied here on the owner's
                      call rather than after a capture: this cell carried the
                      identical fingerprint, `maxWidth:160` + `whiteSpace:normal`.

                      Under `table-layout:auto` a cell's max-width only CAPS the
                      column; it does not floor it, so the column is sized from the
                      content of whichever rows are currently rendered — and
                      Virtuoso keeps changing that set. Wrap width decides line
                      count, line count decides row height, and row height is what
                      Virtuoso measures: measure -> layout -> re-measure, forever.

                      This column had a real 120-160 oscillation band, because the
                      matching <th> above declares minWidth:120 against this cell's
                      160 cap. Pinning min = width = max makes the wrap width
                      constant, so row height becomes a pure function of the text
                      and the loop has nothing to feed on. 160 also dominates the
                      header's 120, so the two constraints agree instead of fighting.

                      NOTE this cell can render a second line ("— by {agent}") for
                      non-sales roles, so its height already varies BY ROLE. That is
                      fine and is not the loop: it is constant per row once the wrap
                      width is constant. TRADE-OFF, same as DR: this column can no
                      longer be squeezed below 160px, so a very narrow desktop
                      window reaches horizontal scroll slightly sooner. Mobile is
                      unaffected — it renders the card list, not this table. */}
                  {!p.isMobile&&<td style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:C.text, textAlign:"left", width:160, minWidth:160, maxWidth:160, wordBreak:"break-word", whiteSpace:"normal", lineHeight:1.4 }}>{lf&&lf.text?<Fragment>{lf.text}{lf.agentName&&positionOf(p.cu)!=="sales"&&<div style={{ fontSize:10, fontWeight:500, color:C.textLight, marginTop:3 }}>— by {lf.agentName}</div>}</Fragment>:<span style={{color:"#CBD5E1", fontWeight:400}}>-</span>}</td>}
                  {!p.isMobile&&isAdmin&&<td style={{ padding:"10px 12px", fontSize:11, color:C.textLight, textAlign:"left", whiteSpace:"nowrap" }}>{lead.source}</td>}
                  {isAdmin&&<td style={{ padding:"10px 12px", fontSize:11, whiteSpace:"nowrap" }} onClick={function(e){e.stopPropagation();}}>
                    {isInactiveAgentRow(lead)&&<div style={{ marginBottom:3 }}><span style={{ background:"#FEF3C7", color:"#92400E", border:"1px solid #FDE68A", borderRadius:10, padding:"1px 6px", fontSize:9, fontWeight:700 }}>Inactive</span></div>}
                    <Select value={lead.agentId&&lead.agentId._id?lead.agentId._id:(lead.agentId||"")} onChange={async function(e){
                      var newAgent=e.target.value;
                      if(!newAgent)return;
                      var oldAgName=userNameOf(lead.agentId, p.users);
                      var newAgUser=p.users.find(function(u){return gid(u)===newAgent;});
                      try{var rotRes=await apiFetch("/api/leads/"+gid(lead)+"/rotate","POST",{targetAgentId:newAgent,reason:"manual"},p.token);try{var freshLead=await apiFetch("/api/leads/"+gid(lead),"GET",null,p.token);if(freshLead&&freshLead._id){p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(lead)?freshLead:l;});});if(selected&&gid(selected)===gid(lead))setSelected(freshLead);}}catch(fe){}if(rotRes.firstAssignment)return;if(oldAgName&&p.notifyRotation)p.notifyRotation(lead,oldAgName,newAgUser?newAgUser.name:"","Manual reassign");}catch(ex){/* Phase R-13.3 — surface server errors so the user doesn't see a phantom hang. Refetch the lead so the dropdown reverts to the actual owner. */alert((ex&&ex.message)||"Rotation failed");try{var revert=await apiFetch("/api/leads/"+gid(lead),"GET",null,p.token);if(revert&&revert._id){p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(lead)?revert:l;});});}}catch(_){}}
                    }} searchable hideEmpty renderRow={agentOptionRow} width={170} maxWidth={170} fontSize={13}
                      options={(function(){
                        var pool = (p.myTeamUsers||salesUsers).filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="team_leader"||positionOf(u)==="manager"||positionOf(u)==="director")&&u.active;});
                        var ownerId = lead.agentId&&lead.agentId._id ? String(lead.agentId._id) : (lead.agentId ? String(lead.agentId) : "");
                        var ownerName = userNameOf(lead.agentId, p.users) || userNameOf(ownerId, p.users) || "Unknown";
                        return buildAgentOptions(pool, ownerId, assignedToday, isOnlyAdmin, ownerName);
                      })()}/>
                  </td>}
                  <td style={{ padding:"10px 12px", fontSize:11, color:C.accent, textAlign:"left", whiteSpace:"nowrap" }}>{timeAgo(lead.lastActivityTime,t)}</td>
                  {/* CLOSED RECORD = NO CALLBACK (owner rule, 2026-09-08). "hide" (Done
                      Deal / EOI) renders an EMPTY cell. That is NOT the same as the "-"
                      below it, and the difference is deliberate: "-" means "open, no
                      callback scheduled", which is a real state a reader may need to
                      act on, while a closed record has no callback to have scheduled.
                      The <td> itself stays so the column keeps its alignment. Not
                      Interested is handed to callbackStripModel as neutral and renders
                      grey. Display only — lead.callbackTime is not touched here; the 24 h+
                      Reschedule pill writes through confirmStatus. */}
                  {/* Pinned column (CB_COL_PIN) + compact desktop wording — see
                      callbackStrip.js. Empty = muted centred "—". */}
                  {!p.isMobile&&<td style={Object.assign({ padding:"10px 12px", fontSize:11, textAlign:"center" }, CB_COL_PIN)}>
                    {callbackDisplayMode(lead)==="hide" ? null : lead.callbackTime ?
                      <CallbackStrip model={callbackStripModel(lead.callbackTime,t,cbNow,callbackDisplayMode(lead)==="neutral",true)} variant="cell" t={t}
                        onReschedule={function(){setCbResched({leadId:lid,status:curSt,callbackTime:lead.callbackTime});}}/>
                      : <span style={{ color:C.textLight }}>—</span>}
                  </td>}
                </Fragment>;
              }}
            />
          )}
        </div>
      </Card>}

      {/* Side Panel — Fix B: require gid(selected) so a stale or partial
          `selected` value (e.g. the deep-link initSelected projection that
          shipped only {_id, phone, name}) doesn't paint an apparently-
          blank Card. The paddingRight rule above uses the same guard so
          the wrapper offset matches the panel's actual rendering. */}
      {hasRenderableSelected&&<Card innerRef={panelRef} style={p.isMobile?{ position:"fixed", inset:0, zIndex:300, borderRadius:0, overflowY:"auto", padding:0, margin:0 }:{ position:"fixed", top:0, right:0, bottom:0, width:320, zIndex:300, borderRadius:0, overflowY:"auto", padding:0, boxShadow:"-4px 0 24px rgba(0,0,0,0.12)" }}>
        {/* ─── Phase 3 redesign ─── Section 1: HEADER (dark navy, kept) ─── */}
        <div style={{ background:"linear-gradient(135deg,"+C.primary+","+C.primaryLight+")", padding:"calc(16px + env(safe-area-inset-top, 0px)) 18px 16px", position:"sticky", top:0, zIndex:5 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
            <button onClick={function(){setSelected(null);}} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:8, width:30, height:30, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff" }} title={t.close||"Close"}><X size={14}/></button>
            <div style={{ display:"flex", gap:6 }}>
              {isOnlyAdmin&&<button onClick={function(){setEditLead(selected);}} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:8, width:30, height:30, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff" }} title={t.edit}><Edit size={13}/></button>}
              {isOnlyAdmin&&<button onClick={function(){archiveLead(gid(selected));}} style={{ background:"rgba(255,165,0,0.35)", border:"none", borderRadius:8, width:30, height:30, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff" }} title={t.archive}><Archive size={13}/></button>}
            </div>
          </div>
          <div style={{ color:"#fff", fontSize:17, fontWeight:700, lineHeight:1.25, wordBreak:"break-word" }}>{selected.name}</div>
          {isOnlyAdmin&&selected.rotationStopped&&<div style={{ marginTop:6, display:"inline-block", fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:5, background:"rgba(255,255,255,0.95)", color:"#991B1B" }} title="Rotation permanently stopped — 3 consecutive Not Interested">🛑 Rotation Stopped</div>}
          <div style={{ color:"rgba(255,255,255,0.75)", fontSize:12, marginTop:4, direction:"ltr" }}>
            <PhoneCell phone={selected.phone}/>{selected.phone2?<>{" / "}<PhoneCell phone={selected.phone2}/></>:""}
          </div>
          {/* Call + WhatsApp — equal-width full-width row at bottom of header */}
          <div style={{ display:"flex", gap:8, marginTop:14 }}>
            <a href={"tel:"+cleanPhone(selected.phone)} onClick={async function(){try{logContactTap(selected,"call",p.token,p.csrfToken);p.setActivities&&p.setActivities(function(prev){return [{_id:Date.now(),type:"call",note:"📞 Call initiated",leadId:selected,userId:p.cu,createdAt:new Date().toISOString()}].concat(prev);});}catch(ex){}}} style={{ flex:1, padding:"7px 10px", borderRadius:8, background:"#fff", color:C.primary, fontSize:12, fontWeight:600, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:5 }}><Phone size={11}/> {t.call}</a>
            <a href={"https://wa.me/"+waPhone(selected.phone)} target="_blank" rel="noreferrer" onClick={function(){logContactTap(selected,"whatsapp",p.token,p.csrfToken);}} style={{ flex:1, padding:"7px 10px", borderRadius:8, background:"#25D366", color:"#fff", fontSize:12, fontWeight:600, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:5 }}><svg viewBox="0 0 24 24" width="11" height="11" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> {t.whatsapp}</a>
          </div>
        </div>
        {/* ─── PANEL BODY ─── */}
        <div style={{ padding:"14px 16px" }}>
          {/* ─── Section 2: LATEST FEEDBACK banner (replaces the yellow Notes card) ───
              Walks every slice the BE sent us (sales = own slice; admin/TL/manager = all
              visible slices) and surfaces the single most-recent feedback entry with the
              status replayed from agentHistory at that moment. */}
          {(function(){
            var lf = latestFeedbackForPanel(selected);
            if (!lf) return null;
            var pillColor = sliceStatusPillColor(lf.status);
            var ts = lf.at ? timeAgo(lf.at, t) : "";
            var initial = String(lf.agentName || "?").charAt(0).toUpperCase();
            return <div style={{ background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:12, padding:"11px 13px", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                  <div style={{ width:30, height:30, borderRadius:"50%", background:C.accent, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, flexShrink:0 }}>{initial}</div>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{lf.agentName}</div>
                    <div style={{ fontSize:10, color:"#92400E", fontWeight:600 }}>💬 Latest feedback{ts?" · "+ts:""}</div>
                  </div>
                </div>
                <span style={{ fontSize:10, fontWeight:600, color:sliceStatusPillTextColor(lf.status), background:pillColor, padding:"3px 8px", borderRadius:6, whiteSpace:"nowrap" }}>{lf.status||"NewLead"}</span>
              </div>
              <div style={{ background:"#fff", borderRadius:8, padding:"8px 11px", fontSize:13, color:C.text, lineHeight:1.45, wordBreak:"break-word" }}>
                {lf.text}
                {lf.authorRole && lf.authorRole !== "sales" && lf.authorName && lf.authorName !== lf.agentName && <span style={{ marginLeft:6, fontSize:10, color:"#6D28D9", fontWeight:700 }}>· from {lf.authorName}</span>}
              </div>
            </div>;
          })()}
          {/* ─── Section 3: CHANGE STATUS pill row ─── */}
          <div style={{ marginBottom:12, padding:11, background:"#F8FAFC", borderRadius:10, border:"1px solid "+C.border }}>
            <div style={{ fontSize:11, color:C.textLight, marginBottom:8, fontWeight:600 }}>{t.changeStatus}</div>
            <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
              {sc.map(function(s){var on=currentStatus(selected)===s.value;return <button key={s.value} onClick={function(){reqStatus(gid(selected),s.value);}} style={{ padding:"5px 11px", borderRadius:14, border:"1px solid "+(on?s.color:C.border), background:on?s.bg:"#fff", color:on?s.color:C.textLight, fontSize:11, fontWeight:on?700:500, cursor:"pointer", transition:"all .15s" }}>{s.label}</button>;})}
            </div>
          </div>
          {/* ─── Section 4: ASSIGN TO dropdown ─── */}
          {isAdmin&&<div style={{ marginBottom:12, padding:11, background:"#F8FAFC", borderRadius:10, border:"1px solid "+C.border }}>
            <div style={{ fontSize:11, color:C.textLight, marginBottom:7, fontWeight:600 }}>{t.assignTo}</div>
            <Select value={selected.agentId&&selected.agentId._id?selected.agentId._id:(selected.agentId||"")} onChange={async function(e){
              var newAgent=e.target.value;
              if(!newAgent)return;
              var isManagerUser=positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader";
              if(isManagerUser&&p.cu.teamId){var tgt=p.users.find(function(u){return gid(u)===newAgent;});if(tgt&&tgt.teamId!==p.cu.teamId)return;}
              var oldAgName=userNameOf(selected.agentId, p.users);
              var newAgUser=p.users.find(function(u){return gid(u)===newAgent;});
              try{var rotRes=await apiFetch("/api/leads/"+gid(selected)+"/rotate","POST",{targetAgentId:newAgent,reason:"manual"},p.token);try{var freshLead=await apiFetch("/api/leads/"+gid(selected),"GET",null,p.token);if(freshLead&&freshLead._id){p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(selected)?freshLead:l;});});setSelected(freshLead);}}catch(fe){}if(rotRes.firstAssignment)return;if(oldAgName&&p.notifyRotation)p.notifyRotation(selected,oldAgName,newAgUser?newAgUser.name:"","Manual reassign");}catch(ex){/* Phase R-13.3 — surface server errors instead of swallowing them. Refetch the lead so the assign-select reverts to the actual owner. */alert((ex&&ex.message)||"Rotation failed");try{var revert=await apiFetch("/api/leads/"+gid(selected),"GET",null,p.token);if(revert&&revert._id){p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(selected)?revert:l;});});setSelected(revert);}}catch(_){}}
            }} searchable hideEmpty renderRow={agentOptionRow} fontSize={13}
              options={(function(){
                var poolSel = (p.myTeamUsers||salesUsers).filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="team_leader"||positionOf(u)==="manager"||positionOf(u)==="director")&&u.active;});
                var ownerId = selected.agentId&&selected.agentId._id ? String(selected.agentId._id) : (selected.agentId ? String(selected.agentId) : "");
                var ownerName = userNameOf(selected.agentId, p.users) || userNameOf(ownerId, p.users) || "Unknown";
                return buildAgentOptions(poolSel, ownerId, assignedToday, isOnlyAdmin, ownerName);
              })()}/>
          </div>}
          {/* ─── Section 5: LEAD DETAILS — unified 2-col grid ───
              Field order per Phase 3 spec: Campaign, Project, Source*, Budget,
              Callback Time, Last Contact, Date Added*. Source admin-gated; Date
              Added admin/operation-only. Filter removes empty/null rows; row
              borders are computed off the filtered array length. */}
          <div style={{ background:"#fff", border:"1px solid "+C.border, borderRadius:10, marginBottom:12, overflow:"hidden" }}>
            {(function(){
              var fields = [
                { l:"Campaign",       v: selected.campaign },
                { l:t.project,         v: selected.project },
                { l:"Unit Type",      v: selected.unitType },
                { l:"Unit Code",      v: (selected.status==="DoneDeal"||selected.globalStatus==="donedeal") ? (selected.unitCode||"—") : null },
                { l:t.source,          v: isAdmin ? selected.source : null },
                { l:t.budget,          v: selected.budget },
                /* CLOSED RECORD = NO CALLBACK. null drops the field out of the grid
                   entirely (the .filter below removes null/""), which is the panel's
                   own way of saying "this record has no such value". */
                { l:t.callbackTime,    v: (callbackDisplayMode(selected)!=="hide" && selected.callbackTime) ? fmtWallDateTime12(selected.callbackTime, t) : null },
                { l:"Last Contact",   v: selected.lastActivityTime ? timeAgo(selected.lastActivityTime, t) : null },
                { l:"Date Added",     v: isOnlyAdmin && selected.createdAt ? new Date(selected.createdAt).toLocaleDateString("en-GB") : null }
              ].filter(function(f){ return f.v != null && f.v !== ""; });
              if (!fields.length) return null;
              return <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr" }}>
                {fields.map(function(f, fi){
                  var isRightCol = fi % 2 === 1;
                  var rowOfLast  = Math.floor((fields.length - 1) / 2);
                  var isLastRow  = Math.floor(fi / 2) === rowOfLast;
                  return <div key={f.l} style={{
                    padding:"10px 12px",
                    borderRight: isRightCol ? "none" : "1px solid #F1F5F9",
                    borderBottom: isLastRow ? "none" : "1px solid #F1F5F9"
                  }}>
                    <div style={{ fontSize:10, color:C.textLight, fontWeight:600, marginBottom:3, textTransform:"uppercase", letterSpacing:".3px" }}>{f.l}</div>
                    <div style={{ fontSize:12, fontWeight:700, color:C.text, wordBreak:"break-word", lineHeight:1.35 }}>{f.v}</div>
                  </div>;
                })}
              </div>;
            })()}
          </div>
          {/* ─── Section 6: ACTION ROW — 3 squares (Important, Lock, Notes) ───
              Important (⭐) gated to admin/operation (was: always visible —
              see below); Lock (🔒) gated to admin/operation/manager/team_leader
              (sales hidden); Notes (📝) gated to admin/manager/team_leader with
              TL-on-own excluded. WaTemplates + History modals are still defined
              elsewhere; we just removed their entry buttons. */}
          <div style={{ display:"flex", gap:8, marginBottom:14 }}>
            {/* VIP is a lead-level visibility decision (row highlight, ⭐ badge,
                VIP list filter), not a per-agent working note — so it is
                admin/operation only, same as Deal Date and Closing Company.
                It was open to every role with a slice until 2026-08-11. The
                server strips isVIP from any other role's PUT regardless; this
                just stops showing a control that would silently do nothing. */}
            {(p.cu && ["admin","operation"].indexOf(p.cu.role) >= 0) &&
            <button onClick={async function(){
              try{var newVip=!selected.isVIP;var upd=await apiFetch("/api/leads/"+gid(selected),"PUT",{isVIP:newVip},p.token);var merged=Object.assign({},selected,{isVIP:newVip});p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(selected)?Object.assign({},l,{isVIP:newVip}):l;});});setSelected(merged);}catch(e){console.error("VIP error",e);}
            }} style={{ width:42, height:42, borderRadius:10, border:"1px solid "+(selected.isVIP?"#F59E0B":C.border), background:selected.isVIP?"#FEF3C7":"#fff", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }} title={selected.isVIP?t.removeVip:t.markVip}>⭐</button>}
            {(p.cu && ["admin","operation","team_leader","manager"].indexOf(positionOf(p.cu)) >= 0) && (function(){
              var lid=gid(selected);
              var isLocked=!!selected.locked;
              return <button onClick={async function(){
                try{
                  var updLock=await apiFetch("/api/leads/"+lid,"PUT",{locked:!isLocked},p.token);
                  p.setLeads(function(prev){return prev.map(function(l){return gid(l)===lid?updLock:l;});});
                  setSelected(updLock);
                }catch(e){console.error("lock error",e);}
              }} style={{ width:42, height:42, borderRadius:10, border:"1px solid "+(isLocked?"#EF4444":C.border), background:isLocked?"#FEE2E2":"#fff", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }} title={isLocked?"Unlock — allow rotation":"Lock — prevent rotation"}>
                {isLocked?"🔒":"🔓"}
              </button>;
            })()}
            {(function(){
              // Notes / Add Feedback — same gate as before (admin/manager/TL only;
              // TL-on-own excluded). Visual rename: button now labelled Notes via
              // tooltip; opens the existing FeedbackModal verbatim. Count badge
              // sums feedbacksForSlice across every visible slice.
              if (!p.cu) return null;
              var role = p.cu.role;
              if (role !== "admin" && role !== "manager" && role !== "team_leader") return null;
              var leadAgentId = selected.agentId && selected.agentId._id ? selected.agentId._id : selected.agentId;
              if (role === "team_leader" && String(leadAgentId || "") === String(p.cu.id || "")) return null;
              var fbCount = 0;
              (selected.assignments || []).forEach(function(a){ fbCount += feedbacksForSlice(a).length; });
              return <button onClick={function(){ setFbForm({ text:"", visibility:"private", targetAgentId:"" }); setFbErr(""); setFeedbackModal({lead:selected}); }}
                style={{ width:42, height:42, borderRadius:10, border:"1px solid "+C.border, background:"#FEF3C7", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", position:"relative" }} title="Notes — view & add feedback">
                📝
                {fbCount > 0 && <span style={{ position:"absolute", top:-5, right:-5, background:C.accent, color:"#fff", fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:8, minWidth:14, textAlign:"center", lineHeight:"12px", boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }}>{fbCount}</span>}
              </button>;
            })()}
          </div>
          {/* ─── Section 7: AGENTS ON THIS LEAD — Currently / Previous split ───
              Same outer gate as before (isOnlyAdmin && >1 slices). Each slice's
              feedback list comes from Phase 2's feedbacksForSlice (chronological
              replay with status-at-the-moment). CURRENT badge goes to the slice
              with the most-recent feedback in the active set. Removed slices
              render in a separate Previous sub-section without a Remove button. */}
          {isOnlyAdmin && selected.assignments && (function(){
            var active  = selected.assignments.filter(function(a){ return !a.removedAt; });
            if (active.length <= 1) return null;
            var topTs = function(a){ var top = feedbacksForSlice(a)[0]; return top && top.at ? new Date(top.at).getTime() : 0; };
            // Slice CREATION timestamp — primary key for the Currently Assigned
            // sort. Reads the Mongoose subdoc _id's embedded ObjectId
            // timestamp (first 4 bytes = seconds since epoch). This is the
            // slice's BIRTH time, immutable across reactivations. We avoid
            // a.assignedAt because the Phase 1 soft-remove reactivation path
            // RESETS assignedAt to "now" — which would put a recently-
            // reactivated old slice above a never-removed newer slice.
            // Fallback: a.assignedAt if _id is somehow missing.
            var sliceCreatedTs = function(a){
              if (a && a._id) {
                var oidStr = String(a._id);
                if (oidStr.length >= 8) {
                  var sec = parseInt(oidStr.slice(0, 8), 16);
                  if (!isNaN(sec) && sec > 0) return sec * 1000;
                }
              }
              if (a && a.assignedAt) {
                var t = new Date(a.assignedAt).getTime();
                if (!isNaN(t) && t > 0) return t;
              }
              return 0;
            };
            // Currently Assigned: sort by slice creation timestamp desc (newest
            // rotated/assigned at top, oldest at bottom).
            active.sort(function(x,y){ return sliceCreatedTs(y) - sliceCreatedTs(x); });
            var renderSlice = function(a, i, arr, isActiveSection){
              var aName = userNameOf(a.agentId, p.users) || "Unknown";
              var aId   = a.agentId && a.agentId._id ? a.agentId._id : a.agentId;
              var initial = String(aName || "?").charAt(0).toUpperCase();
              // Display the per-agent EFFECTIVE status via the existing
              // feedbacksForSlice agentHistory-replay (same value the card's
              // Latest Feedback banner derives), NOT the raw stored slice.status —
              // which can lag (e.g. a reactivated/leaked slice stuck at "NewLead").
              // Falls back to the raw status only when the slice has no replayable
              // history/feedback (a genuinely fresh slice).
              // af54b52 parity: ignore status_change entries whose canonical status
              // is "NewLead" (seed state + bogus trailing cleanup writes); take the
              // latest non-NewLead status. Fall back to the feedbacksForSlice replay
              // / raw a.status only when none exists. Display-only; no write.
              var sliceShownStatus = shownSliceStatus(a);
              var statusColor = sliceStatusPillColor(sliceShownStatus);
              return <div key={i} style={{ padding:"9px 0", borderBottom: i<arr.length-1 ? "1px solid #F1F5F9" : "none" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6, gap:8 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0, flex:1, overflow:"hidden" }}>
                    <div style={{ width:26, height:26, borderRadius:"50%", background: a.removedAt ? "#CBD5E1" : C.accent, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, flexShrink:0 }}>{initial}</div>
                    <div style={{ minWidth:0, flex:1, overflow:"hidden", display:"flex", alignItems:"center", gap:5 }}>
                      <span style={{ fontSize:12, fontWeight:600, color: a.removedAt ? C.textLight : C.text, textDecoration:a.removedAt?"line-through":"none", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:0, flex:"0 1 auto" }} title={aName}>{aName}</span>
                      {a.removedAt && <span style={{ fontSize:9, background:"#FEE2E2", color:"#B91C1C", padding:"1px 6px", borderRadius:5, fontWeight:600, flexShrink:0, whiteSpace:"nowrap" }} title={"Removed "+(new Date(a.removedAt).toLocaleDateString("en-GB"))}>removed</span>}
                    </div>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
                    <span style={{ fontSize:9, fontWeight:600, color:sliceStatusPillTextColor(sliceShownStatus), background:statusColor, padding:"2px 7px", borderRadius:5, whiteSpace:"nowrap" }}>{sliceShownStatus}</span>
                    {!a.removedAt && <button onClick={async function(){
                      if (!window.confirm("Remove "+aName+" from this lead?")) return;
                      try{var upd=await apiFetch("/api/leads/"+gid(selected)+"/assignment/"+aId,"DELETE",null,p.token);p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(selected)?upd:l;});});setSelected(upd);}catch(ex){alert(ex.message||"Failed");}
                    }} style={{ background:"none", border:"none", cursor:"pointer", color:"#EF4444", fontSize:14, padding:"2px 6px", borderRadius:6 }} title="Remove agent">🗑</button>}
                  </div>
                </div>
                {/* Phase 2: per-slice feedback list — chronological replay with
                    status-at-the-moment pill + relative timestamp. The branch
                    is now dead in the rendered tree (the only caller passes
                    isActiveSection=true), kept as a guard so a future
                    Previous-section reintroduction can flip the call site
                    without re-wiring the renderer. The Latest Feedback banner
                    at the top of the panel + LeadJourney below already cover
                    every visible feedback for active agents. */}
                {!isActiveSection && (function(){
                  var fbs = feedbacksForSlice(a);
                  if (!fbs.length) return null;
                  return <div style={{ marginLeft:34 }}>
                    {fbs.map(function(fb, fbi){
                      var stColor = sliceStatusPillColor(fb.status);
                      var fbts = fb.at ? timeAgo(fb.at, t) : "";
                      return <div key={fbi} style={{ display:"flex", alignItems:"flex-start", gap:6, padding:"4px 7px", marginTop: fbi>0?3:0, background:"#FFFBEB", borderRadius:6, borderLeft:"2px solid "+C.accent }}>
                        <span style={{ fontSize:9, fontWeight:600, color:sliceStatusPillTextColor(fb.status), background:stColor, padding:"1px 5px", borderRadius:4, whiteSpace:"nowrap", marginTop:1, flexShrink:0 }}>{fb.status||"NewLead"}</span>
                        <span style={{ flex:1, fontSize:11, color:C.text, wordBreak:"break-word" }}>{fb.text}{fb.authorRole && fb.authorRole !== "sales" && fb.authorName && <span style={{ marginLeft:6, fontSize:10, color:"#6D28D9", fontWeight:600 }}>· from {fb.authorName}</span>}</span>
                        {fbts && <span style={{ fontSize:9, color:C.textLight, whiteSpace:"nowrap", marginTop:2, flexShrink:0 }}>{fbts}</span>}
                      </div>;
                    })}
                  </div>;
                })()}
              </div>;
            };
            return <div style={{ background:"#fff", border:"1px solid "+C.border, borderRadius:10, padding:"11px 13px", marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:600, color:C.textLight, marginBottom:10, padding:"5px 9px", background:"#F8FAFC", borderRadius:6, boxShadow:"0 1px 2px rgba(15, 23, 42, 0.04)", display:"inline-block", textTransform:"uppercase", letterSpacing:".4px" }}>👥 Agents on this lead</div>
              {active.length > 0 && <div>
                <div style={{ fontSize:10, fontWeight:700, color:"#15803D", marginBottom:4, display:"flex", alignItems:"center", gap:6, textTransform:"uppercase", letterSpacing:".3px" }}>
                  <span style={{ width:7, height:7, borderRadius:"50%", background:"#22C55E" }}/>
                  Currently assigned · {active.length}
                </div>
                {active.map(function(a,i){ return renderSlice(a,i,active,true); })}
              </div>}
            </div>;
          })()}
          {/* ─── Section 8: LeadJourney — UNCHANGED (out of scope) ─── */}
          <div style={{ marginTop:14 }}>
            <LeadJourney events={panelHistory} lead={selected} currentUser={p.cu} allUsers={p.users} isAdminRole={isAdmin} variant="panel" setShowCompare={openCompare} />
          </div>
        </div>
      </Card>}
    </div>

    {/* Full History Modal */}
    {showHistory&&historyLead&&<Modal show={true} onClose={function(){setShowHistory(false);setHistoryLead(null);}} title={"📋 Lead History — "+historyLead.name} w={520}>
      {historyLoading ? <div style={{ textAlign:"center", padding:30, color:C.textLight }}>Loading...</div>
        : <div style={{ maxHeight:500, overflowY:"auto" }}>
          <LeadJourney events={fullHistory} lead={historyLead} currentUser={p.cu} allUsers={p.users} isAdminRole={isAdmin} variant="modal" setShowCompare={openCompare} />
        </div>}
    </Modal>}

    {/* Agent Comparison Modal */}
    {showCompare&&compareLead&&(function(){
      var cmpStatusColor = function(s){
        var m = { NewLead:"#5F5E5A", Potential:"#185FA5", NoAnswer:"#854F0B", NotInterested:"#A32D2D", CallBack:"#BA7517", HotCase:"#D85A30", MeetingDone:"#0F6E56", DoneDeal:"#04342C", EOI:"#04342C", "New Lead":"#5F5E5A","No Answer":"#854F0B","Not Interested":"#A32D2D","Call Back":"#BA7517","Hot Case":"#D85A30","Meeting Done":"#0F6E56","Done Deal":"#04342C" };
        return m[s] || "#5F5E5A";
      };
      var cmpStatusLabel = function(s){
        var m = { NewLead:"New Lead", NoAnswer:"No Answer", NotInterested:"Not Interested", CallBack:"Call Back", HotCase:"Hot Case", MeetingDone:"Meeting Done", DoneDeal:"Done Deal" };
        return m[s] || s || "New Lead";
      };
      return <Modal show={true} onClose={closeCompare} title={"Agent Comparison — "+compareLead.name} w={520}>
        {compareEras.length===0 ? <div style={{ textAlign:"center", padding:30, color:C.textLight }}>No eras to compare</div> : <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
                <th style={{ textAlign:"left", padding:"8px 10px", fontWeight:700, color:C.text, fontSize:11 }}>Agent</th>
                <th style={{ textAlign:"right", padding:"8px 10px", fontWeight:700, color:C.text, fontSize:11 }}>Days held</th>
                <th style={{ textAlign:"right", padding:"8px 10px", fontWeight:700, color:C.text, fontSize:11 }}>Actions</th>
                <th style={{ textAlign:"left", padding:"8px 10px", fontWeight:700, color:C.text, fontSize:11 }}>Ended at</th>
              </tr>
            </thead>
            <tbody>
              {compareEras.map(function(era, i){
                // Knock-on #4: after merge, an era can span multiple non-
                // contiguous tenures. Sum each tenure's individual span
                // rather than computing earliest-start to latest-end (which
                // would falsely count gaps when another agent held the lead).
                var daysHeld = (function(){
                  if (era.tenureStarts && era.tenureStarts.length > 1 && era.tenureEnds && era.tenureEnds.length === era.tenureStarts.length) {
                    var totalMs = 0;
                    for (var ti = 0; ti < era.tenureStarts.length; ti++) {
                      var s = new Date(era.tenureStarts[ti]).getTime();
                      var e = new Date(era.tenureEnds[ti]).getTime();
                      if (!isNaN(s) && !isNaN(e) && e >= s) totalMs += (e - s);
                    }
                    return Math.max(1, Math.ceil(totalMs / (24*60*60*1000)));
                  }
                  return Math.max(1, Math.ceil((new Date(era.endedAt) - new Date(era.startedAt)) / (24*60*60*1000)));
                })();
                // Match the era header: count groups, including era-start
                // assign groups. The number tracks distinct user actions.
                var actions = (era.actions||[]).length;
                var rowBg = era.isCurrent ? "#E1F5EE" : (i%2===0 ? "#fff" : "#FBFBFA");
                var agentColor = era.isCurrent ? "#04342C" : C.text;
                return <tr key={"cmp-"+i} style={{ background:rowBg, borderBottom:"1px solid #F1F5F9" }}>
                  <td style={{ padding:"10px", color:agentColor, fontWeight:era.isCurrent?700:600 }}>
                    {era.agentName}
                    {era.isCurrent && <span style={{ marginLeft:6, fontWeight:700, fontSize:10, color:"#04342C" }}>· current</span>}
                  </td>
                  <td style={{ padding:"10px", textAlign:"right", color:C.text }}>{daysHeld}</td>
                  <td style={{ padding:"10px", textAlign:"right", color:C.text }}>{actions}</td>
                  <td style={{ padding:"10px", color:cmpStatusColor(era.endedAtStatus), fontWeight:600 }}>{cmpStatusLabel(era.endedAtStatus)}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>}
      </Modal>;
    })()}

    {/* Bulk WhatsApp Templates Modal */}
    {showBulkWa&&<Modal show={true} onClose={function(){setShowBulkWa(false);setBulkWaTemplate(null);}} title={"💬 "+t.bulkWhatsApp+" ("+selected2.length+")"}>
      <div style={{ marginBottom:12, padding:"10px 14px", background:"#F0FDF4", borderRadius:10, fontSize:12, color:"#15803D" }}>
        اختار رسالة وبعدين اضغط "إرسال للكل"
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:14 }}>
        {WA_TEMPLATES_AR.map(function(tmpl){
          var isSelected=bulkWaTemplate&&bulkWaTemplate.id===tmpl.id;
          var previewLead=filtered.find(function(l){return selected2.includes(gid(l));});
          var preview=previewLead?fillTemplate(tmpl.text,previewLead,p.cu.name):tmpl.text;
          return <div key={tmpl.id} onClick={function(){setBulkWaTemplate(tmpl);}} style={{ border:"2px solid", borderColor:isSelected?"#25D366":"#E8ECF1", borderRadius:12, padding:14, cursor:"pointer", background:isSelected?"#F0FDF4":"#fff" }}>
            <div style={{ fontSize:12, fontWeight:700, marginBottom:6, color:isSelected?"#15803D":C.text }}>{tmpl.label} {isSelected?"✓":""}</div>
            <div style={{ fontSize:11, color:C.textLight, lineHeight:1.6, whiteSpace:"pre-line" }}>{preview}</div>
          </div>;
        })}
      </div>
      <div style={{ display:"flex", gap:10 }}>
        <Btn outline onClick={function(){setShowBulkWa(false);setBulkWaTemplate(null);}} style={{ flex:1 }}>{t.cancel}</Btn>
        <Btn onClick={function(){
          if(!bulkWaTemplate){alert("اختار رسالة الأول");return;}
          var selectedLeads=filtered.filter(function(l){return selected2.includes(gid(l));});
          selectedLeads.forEach(function(l){
            var msg=fillTemplate(bulkWaTemplate.text,l,p.cu.name);
            logContactTap(l,"whatsapp",p.token,p.csrfToken);window.open("https://wa.me/"+waPhone(l.phone)+"?text="+encodeURIComponent(msg),"_blank");
          });
          setShowBulkWa(false);setBulkWaTemplate(null);
        }} style={{ flex:2, background:"#25D366", borderColor:"#25D366" }}>💬 إرسال للكل ({selected2.length})</Btn>
      </div>
    </Modal>}

    {/* WhatsApp Templates Modal */}
    {showWaTemplates&&waLead&&<Modal show={true} onClose={function(){setShowWaTemplates(false);setWaLead(null);}} title={"💬 WhatsApp Messages — "+waLead.name}>
      <div style={{ marginBottom:14, padding:"10px 14px", background:"#F0FDF4", borderRadius:10, fontSize:12, color:"#15803D", display:"flex", alignItems:"center", gap:8 }}>
        <Phone size={14}/> {waLead.phone}{waLead.phone2?" / "+waLead.phone2:""}
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {WA_TEMPLATES_AR.map(function(tmpl){
          var agentName=p.cu.name;
          var msg=fillTemplate(tmpl.text,waLead,agentName);
          var waUrl="https://wa.me/"+waPhone(waLead.phone)+"?text="+encodeURIComponent(msg);
          return <div key={tmpl.id} style={{ border:"1px solid #E8ECF1", borderRadius:12, padding:14 }}>
            <div style={{ fontSize:12, fontWeight:700, marginBottom:6, color:C.text }}>{tmpl.label}</div>
            <div style={{ fontSize:11, color:C.textLight, marginBottom:10, lineHeight:1.6, whiteSpace:"pre-line" }}>{msg}</div>
            <div style={{ display:"flex", gap:8 }}>
              <a href={waUrl} target="_blank" rel="noreferrer" onClick={function(){logContactTap(waLead,"whatsapp",p.token,p.csrfToken);}} style={{ flex:1, padding:"8px", borderRadius:8, background:"#25D366", color:"#fff", fontSize:12, fontWeight:600, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:5 }}>💬 Send</a>
              <button onClick={function(){navigator.clipboard.writeText(msg);}} style={{ padding:"8px 12px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", fontSize:12, cursor:"pointer", color:C.textLight }}>📋 Copy</button>
            </div>
          </div>;
        })}
      </div>
    </Modal>}

    {/* Quick Add Modal */}
    <Modal show={showQuickAdd} onClose={function(){setShowQuickAdd(false);}} title={"⚡ "+t.quickAdd} w={360}>
      <Inp label={t.name} req value={quickForm.name} onChange={function(e){setQuickForm(function(f){return Object.assign({},f,{name:e.target.value});});}}/>
      <Inp label={t.phone} req value={quickForm.phone} onChange={function(e){setQuickForm(function(f){return Object.assign({},f,{phone:e.target.value});});}} placeholder=""/>
      <Inp label={t.project} value={quickForm.project||""} onChange={function(e){setQuickForm(function(f){return Object.assign({},f,{project:e.target.value});});}} placeholder=""/>
      <Inp label={t.source} req type="select" value={quickForm.source} onChange={function(e){setQuickForm(function(f){return Object.assign({},f,{source:e.target.value});});}} options={buildSourceOptions(quickForm.source)}/>
      <div style={{ display:"flex", gap:10 }}>
        <Btn outline onClick={function(){setShowQuickAdd(false);}} style={{ flex:1 }}>{t.cancel}</Btn>
        <Btn loading={quickSaving} onClick={async function(){
          if(!quickForm.name||!quickForm.phone)return;
          if(!quickForm.source){ alert("Please select a source"); return; }
          setQuickSaving(true);
          try{
            var salesUsers=p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;});
            var lead=await apiFetch("/api/leads","POST",Object.assign({},quickForm,{agentId:quickForm.agentId||"",fieldsV2:true}),p.token);
            p.setLeads(function(prev){return [lead].concat(prev);});
            setShowQuickAdd(false);
            setQuickForm({name:"",phone:"",project:PROJECTS[0],source:""});
          }catch(e){alert(e.message);}
          setQuickSaving(false);
        }} style={{ flex:2 }}>⚡ {t.quickAdd}</Btn>
      </div>
    </Modal>

    {/* Add Modal */}
    <Modal show={showAdd} onClose={function(){setShowAdd(false);}} title={isReq?t.addRequest:t.addLead}>
      <LeadForm t={t} cu={p.cu} users={p.users} token={p.token} isMobile={p.isMobile} isReq={isReq} onClose={function(){setShowAdd(false);}} onSave={function(lead){p.setLeads(function(prev){var nid=String(lead&&lead._id||"");if(!nid)return[lead].concat(prev);if(prev.some(function(l){return gid(l)===nid;}))return prev.map(function(l){return gid(l)===nid?lead:l;});return [lead].concat(prev);});setShowAdd(false);}}/>
    </Modal>
    {/* Edit Modal */}
    {editLead&&<Modal show={true} onClose={function(){setEditLead(null);}} title={t.edit}>
      <LeadForm t={t} cu={p.cu} users={p.users} token={p.token} isMobile={p.isMobile} isReq={isReq} editId={gid(editLead)} initial={editLead} onClose={function(){setEditLead(null);}} onSave={function(updated){p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(updated)?updated:l;});});setSelected(updated);setEditLead(null);}}/>
    </Modal>}
  </div>;
};


// =====================================================================
// ATTENDANCE — Phase 3
// CheckInWidget is the single source of truth for the daily check-in flow.
// mode="compact" pins it as a one-row card on dashboards. mode="full" is the
// same card on /attendance with a monthly history table below.
// =====================================================================

// Format helpers shared by the widget and the page.
var fmtCairoTime = function(d){
  if (!d) return "—";
  var x = new Date(d);
  return fmtTime12(x) || "—";
};
var fmtCairoDate = function(d){
  if (!d) return "—";
  var x = new Date(d);
  return x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "Africa/Cairo" });
};
var fmtDuration = function(ms){
  if (!ms || ms < 0) return "0m";
  var mins = Math.floor(ms / 60000);
  var h = Math.floor(mins / 60);
  var m = mins % 60;
  if (h <= 0) return m + "m";
  return h + "h " + m + "m";
};

// Status badge → label + colors. Used by both widget (current state) and
// monthly log rows.
var attStatusBadge = function(status, off){
  if (off === "friday")  return { label:"Friday off",   bg:"#F1F5F9", color:"#64748B" };
  if (off === "saturday") return { label:"Saturday off", bg:"#F1F5F9", color:"#64748B" };
  if (off && off.indexOf("company:") === 0) return { label:"Off: " + off.slice(8), bg:"#F1F5F9", color:"#64748B" };
  switch (status) {
    case "in_progress":      return { label:"In progress",  bg:"#DCFCE7", color:"#15803D" };
    case "present":          return { label:"Present",      bg:"#DCFCE7", color:"#15803D" };
    case "late_quarter":     return { label:"Late · ¼ day", bg:"#FEF3C7", color:"#B45309" };
    case "late_half":        return { label:"Late · ½ day", bg:"#FED7AA", color:"#C2410C" };
    case "late_full":        return { label:"Late · 1 day", bg:"#FEE2E2", color:"#B91C1C" };
    case "absent":           return { label:"Absent",       bg:"#FEE2E2", color:"#B91C1C" };
    case "pending_offsite":  return { label:"Off-site (pending)", bg:"#FEF9C3", color:"#A16207" };
    case "override":         return { label:"Override",     bg:"#F3E8FF", color:"#7C3AED" };
    default:                 return { label:"—",            bg:"#F1F5F9", color:"#64748B" };
  }
};

// Check-out cell for an attendance row. When the nightly auto-checkout job
// closed the day (a.autoClosed), the time shown is the role's OFFICIAL shift
// end, not a real check-out — so it carries an "Auto" chip. That chip is the
// signal HR/Owner use to spot who forgets to check out repeatedly; it never
// implies a deduction (the job leaves deductionFraction alone).
var attCheckOutCell = function(a){
  // A day whose check-out was undone (POST /api/attendance/undo-check-out) has no
  // check-out and is open again. The chip says so, because an empty cell on a PAST
  // day is indistinguishable from someone who never checked out at all — and those
  // two want opposite things from whoever is reading the row.
  if (a && a.reopenedAt && !(a.checkOut && a.checkOut.timestamp)) {
    return <span title="A check-out was recorded and then undone — the day is open again"
      style={{display:"inline-block", padding:"2px 7px", borderRadius:5, fontSize:10, fontWeight:600, background:"#FEF3C7", color:"#854F0B", whiteSpace:"nowrap"}}>Reopened</span>;
  }
  if (!a || !a.checkOut || !a.checkOut.timestamp) return "—";
  var t = fmtCairoTime(a.checkOut.timestamp);
  if (!a.autoClosed) return t;
  return <span style={{display:"inline-flex", alignItems:"center", gap:6, flexWrap:"wrap"}}>
    <span>{t}</span>
    <span title="Forgot to check out — auto-closed at the shift end"
      style={{display:"inline-block", padding:"2px 7px", borderRadius:5, fontSize:10, fontWeight:600, background:"#E0E7FF", color:"#4338CA", whiteSpace:"nowrap"}}>Auto</span>
  </span>;
};

// ===== READ A CAPABILITY FROM THE SERVER-RESOLVED ACCESS ENVELOPE ===========
//
// A LOOKUP, NOT A DECISION. This replaced hasAttendancePerm(role, action,
// settings) — a client-side reimplementation of the server's legacy permission
// table that was called from 12 places and was keyed on ROLE, with no notion of
// RANK. It agreed with the server only for as long as the rule it copied stayed
// role-keyed. Two ways that ended, both silent:
//
//   - a capability taken over by the rank matrix is a RANK question, and the
//     copy had no rank to answer it with;
//   - once the legacy permissions document stops being maintained, the copy
//     resolves everything to false for non-admins.
//
// Neither throws. Tabs just stop appearing for one person.
//
// There is deliberately NO admin short-circuit here. The server already returns
// true for admin in every capability (capabilityFor short-circuits it), so
// re-stating that rule client-side would be a second copy of the exact thing
// this function exists to delete.
var canDo = function(access, key){
  return !!(access && access.capabilities && access.capabilities[key] === true);
};

var CheckInWidget = function(p) {
  // p: { token, cu, mode: "compact"|"full", onNavigate?, onCheckedIn? }
  var [data, setData] = useState(null);
  var [coords, setCoords] = useState(null);
  var [coordsErr, setCoordsErr] = useState("");
  var [busy, setBusy] = useState(false);
  var [error, setError] = useState("");
  var [tick, setTick] = useState(0);
  // Phase 4 — off-site request modal state.
  var [modalOpen, setModalOpen] = useState(false);
  var [modalType, setModalType] = useState("check_in");
  var [modalReason, setModalReason] = useState("");
  var [modalErr, setModalErr] = useState("");
  var [skewMs, setSkewMs] = useState(0);
  var [modalBusy, setModalBusy] = useState(false);

  var refetch = useCallback(function(){
    if (!p.token || !p.cu || p.cu.role === "admin") return;
    apiFetch("/api/attendance/today", "GET", null, p.token).then(function(r){
      setData(r);
      // Clock skew against the SERVER, measured once per fetch. A device whose
      // clock is ten minutes fast would otherwise light the check-in button up
      // ten minutes early and hand the person a 400 — which is the exact
      // dishonesty this whole change exists to remove. serverTime has been in
      // this payload all along and was never read.
      if (r && r.serverTime) {
        var st = new Date(r.serverTime).getTime();
        if (isFinite(st)) setSkewMs(st - Date.now());
      }
      setError("");
    }).catch(function(err){
      setError((err && err.message) || "Failed to load");
    });
  }, [p.token, p.cu]);

  useEffect(function(){ refetch(); }, [refetch]);

  // Geolocation poll: once on mount, then every 60s. enableHighAccuracy is on
  // because the geofence radius is typically 50-150m — coarse cell-tower fixes
  // produce false positives/negatives. Re-runs only when the underlying set of
  // active locations changes (count is the cheapest stable key).
  var geofenceCount = (function(){
    if (data && Array.isArray(data.geofences)) return data.geofences.length;
    if (data && data.geofence) return 1;
    return 0;
  })();
  useEffect(function(){
    if (geofenceCount === 0) return;
    var stopped = false;
    var poll = function(){
      if (stopped) return;
      geoGetCurrentPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 })
        .then(function(c){
          if (stopped) return;
          setCoords({ latitude: c.latitude, longitude: c.longitude, accuracy: c.accuracy });
          setCoordsErr("");
        })
        .catch(function(err){ if (!stopped) setCoordsErr((err && err.message) || "Could not get location"); });
    };
    poll();
    var interval = setInterval(poll, 60000);
    return function(){ stopped = true; clearInterval(interval); };
  }, [geofenceCount]);

  // Live duration tick (every 30s) for State 3. Same Mongoose `{}` gotcha as
  // the state machine — gate on the actual checkOut timestamp.
  useEffect(function(){
    var a = data && data.attendance;
    var inProgress = !!(a && a.checkIn && a.checkIn.timestamp && !(a.checkOut && a.checkOut.timestamp));
    if (!inProgress) return;
    var t = setInterval(function(){ setTick(function(x){return x+1;}); }, 30000);
    return function(){ clearInterval(t); };
  }, [data]);

  // Cross-tab / cross-device sync. The App-level WS handler re-dispatches
  // attendance_updated as a window CustomEvent — we listen here and patch
  // local state when the event is for the current user. Avoids the user
  // having to refresh tab B after checking in on tab A.
  useEffect(function(){
    if (!p.cu || p.cu.role === "admin") return;
    var handler = function(e){
      if (!e || !e.detail) return;
      var uid = String(p.cu.id || (p.cu._id || ""));
      if (!uid || String(e.detail.userId) !== uid) return;
      if (e.detail.attendance) {
        setData(function(prev){ return Object.assign({}, prev || {}, { attendance: e.detail.attendance }); });
      } else {
        refetch();
      }
    };
    window.addEventListener("crm:attendance_updated", handler);
    return function(){ window.removeEventListener("crm:attendance_updated", handler); };
  }, [p.cu, refetch]);

  // Multi-branch: data.geofences is the full list of active locations.
  // (data.geofence is kept by the server for one deploy window — we fall
  // back to it as a single-item list if the new field hasn't landed yet.)
  // We compute haversine to every location and pick the closest matching
  // (inside-radius) one for State 1; if none match, State 2 reports the
  // distance to the nearest one regardless of radius.
  var geofences = (function(){
    if (data && Array.isArray(data.geofences)) return data.geofences;
    if (data && data.geofence) return [data.geofence];
    return [];
  })();
  var closestMatch = null;     // closest location whose radius the user is inside
  var closestMatchDist = null;
  var nearest = null;          // nearest by distance, regardless of radius (for State 2)
  var nearestDist = null;
  if (coords && geofences.length > 0) {
    var R = 6371000;
    var toRad = function(d){ return d * Math.PI / 180; };
    for (var gi = 0; gi < geofences.length; gi++) {
      var loc = geofences[gi];
      var phi1 = toRad(coords.latitude), phi2 = toRad(loc.latitude);
      var dPhi = toRad(loc.latitude - coords.latitude);
      var dLam = toRad(loc.longitude - coords.longitude);
      var a = Math.sin(dPhi/2)**2 + Math.cos(phi1)*Math.cos(phi2)*Math.sin(dLam/2)**2;
      var d = Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
      if (nearest === null || d < nearestDist) { nearest = loc; nearestDist = d; }
      if (d <= loc.radiusMeters && (closestMatch === null || d < closestMatchDist)) {
        closestMatch = loc; closestMatchDist = d;
      }
    }
  }
  var insideGeofence = closestMatch !== null;

  // ===== THE CHECK-IN WINDOW — A LOOKUP, NOT A RULE ==========================
  //
  // The server resolves it (checkInWindow(), the same function both write
  // endpoints gate on) and ships the ANSWER in /api/attendance/today. Nothing
  // here subtracts an hour, knows what an hour is, or reads workShift.startTime.
  // That is the standing rule in this codebase and it was learned the hard way:
  // a client copy of a server rule is a second authority that disagrees the
  // moment the first one moves.
  //
  // `isOpen` is true-as-of-the-fetch. `opensAt` is an absolute instant, so a tab
  // left open across opening time crosses over on the next 30s tick instead of
  // staying stale — compared against the SERVER's clock via skewMs, because the
  // device's own clock is not evidence. isOpen is the fallback when a server
  // predating this field answers.
  var ciWin = (data && data.checkInWindow) || null;
  var windowOpen = (function(){
    if (!ciWin || ciWin.known === false) return true;       // no declared shift -> no window
    if (ciWin.opensAt) {
      var t = new Date(ciWin.opensAt).getTime();
      if (isFinite(t)) return (Date.now() + skewMs) >= t;
    }
    return ciWin.isOpen !== false;
  })();
  var opensLabel = (ciWin && ciWin.opensAtLabel) || null;
  // One sentence, used by every disabled control and by the modal, so the
  // person is told the same thing wherever they meet the rule.
  var earlyNote = opensLabel ? ("Opens at " + opensLabel) : "Not open yet";

  // Re-render across the opening moment. The duration tick above only runs while
  // a day is IN PROGRESS, so without this a widget sitting on "Opens at 10:00"
  // would still say so at 10:05 and the button would stay disabled until
  // something else happened to re-render it — a disabled control that has
  // quietly become wrong, which is the same class of dishonesty as the visible
  // one that was guaranteed to be refused. Runs ONLY while the window is shut,
  // and stops itself the moment it opens.
  useEffect(function(){
    if (windowOpen) return;
    var t = setInterval(function(){ setTick(function(x){return x+1;}); }, 30000);
    return function(){ clearInterval(t); };
  }, [windowOpen]);
  // Display values mirror previous single-loc semantics so downstream JSX
  // doesn't change shape: distanceMeters = "what you'd report to the user
  // right now"; activeLocName = "which location's name to show".
  var distanceMeters = insideGeofence ? closestMatchDist : nearestDist;
  var activeLocName = insideGeofence ? closestMatch.name : (nearest ? nearest.name : null);

  // State machine resolution. Order matters — pending > done > in-progress >
  // off-day > inside/outside.
  // IMPORTANT: Mongoose 7 hydrates a doc with nested sub-object schemas as
  // `{ checkOut: {} }` even when the field was never written. So we MUST
  // gate on the actual `.timestamp` presence, not the bare object truthiness,
  // or "checked in but not checked out" misclassifies as "done" → State 4
  // → no Check-out button (the bug fix that motivated these helpers).
  var hasCheckIn  = !!(data && data.attendance && data.attendance.checkIn  && data.attendance.checkIn.timestamp);
  var hasCheckOut = !!(data && data.attendance && data.attendance.checkOut && data.attendance.checkOut.timestamp);
  var widgetState = (function(){
    if (!data) return "loading";
    if (data.pendingOffSite) return "pending_offsite";
    if (hasCheckIn && hasCheckOut) return "done";
    if (hasCheckIn && !hasCheckOut) return "in_progress";
    if (data.isOffDay) return "off_day";
    if (coordsErr) return "no_coords";
    if (coords == null) return "locating";
    return insideGeofence ? "ready_inside" : "ready_outside";
  })();

  var requestPosition = function(){
    // web → navigator.geolocation; native → Capacitor Geolocation plugin.
    // Same { latitude, longitude, accuracy } shape; always settles (timeout).
    return geoGetCurrentPosition({ enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };

  var doCheckIn = async function(){
    setError(""); setBusy(true);
    try {
      var pos = await requestPosition();
      var resp = await apiFetch("/api/attendance/check-in", "POST", pos, p.token, p.csrfToken);
      if (resp && resp.attendance) {
        setData(function(prev){ return Object.assign({}, prev || {}, { attendance: resp.attendance }); });
      } else {
        refetch();
      }
      if (p.onCheckedIn) p.onCheckedIn();
    } catch (err) {
      setError((err && err.message) || "Check-in failed");
    } finally {
      setBusy(false);
    }
  };

  var doCheckOut = async function(){
    setError(""); setBusy(true);
    try {
      var pos = await requestPosition();
      var resp = await apiFetch("/api/attendance/check-out", "POST", pos, p.token, p.csrfToken);
      if (resp && resp.attendance) {
        setData(function(prev){ return Object.assign({}, prev || {}, { attendance: resp.attendance }); });
      } else {
        refetch();
      }
    } catch (err) {
      var msg = (err && err.message) || "";
      // Phase 4: backend returns 403 "Outside office geofence" when checking
      // out from outside the office. Auto-open the off-site modal so the
      // user doesn't need a second click. apiFetch loses the structured code
      // field — match on the message text. Stable as long as the backend
      // string doesn't change.
      if (/outside/i.test(msg)) {
        openOffSiteModal("check_out");
      } else {
        setError(msg || "Check-out failed");
      }
    } finally {
      setBusy(false);
    }
  };

  var openOffSiteModal = function(type){
    // ROW 3 OF THE TRUTH TABLE, CLOSED AT THE DOOR. The old order was: open the
    // modal, make the person compose ten-plus characters of justification, POST
    // it, and only then tell them they were too early. The refusal was correct
    // and the sequence was backwards. A check_in before opening time never gets
    // as far as a text box now.
    //
    // check_out is untouched: you finish work at the end of a shift, not the
    // start, and the window has no opinion about it.
    if (type === "check_in" && !windowOpen) {
      setError(earlyNote + (ciWin && ciWin.shiftStart ? " · your shift starts at " + ciWin.shiftStart : ""));
      return;
    }
    setModalType(type);
    setModalReason("");
    setModalErr("");
    setModalOpen(true);
  };

  var submitOffSite = async function(){
    // Belt and braces, and the ORDER is the point: the window is answered before
    // the reason length. A modal that was opened while the window was still open
    // and sat there past a state change must not first complain about the text.
    if (modalType === "check_in" && !windowOpen) {
      setModalErr(earlyNote + (ciWin && ciWin.shiftStart ? " · your shift starts at " + ciWin.shiftStart : ""));
      return;
    }
    var trimmed = (modalReason || "").trim();
    if (trimmed.length < 10) { setModalErr("Reason must be at least 10 characters"); return; }
    setModalErr(""); setModalBusy(true);
    try {
      var pos = await requestPosition();
      await apiFetch("/api/offsite/request", "POST", {
        type: modalType,
        latitude: pos.latitude,
        longitude: pos.longitude,
        accuracy: pos.accuracy,
        reason: trimmed
      }, p.token, p.csrfToken);
      setModalOpen(false);
      refetch();
    } catch (err) {
      setModalErr((err && err.message) || "Submit failed");
    } finally {
      setModalBusy(false);
    }
  };

  var doCancelOffSite = async function(){
    if (!data || !data.pendingOffSite) return;
    setError(""); setBusy(true);
    try {
      await apiFetch("/api/offsite/" + data.pendingOffSite._id, "DELETE", null, p.token, p.csrfToken);
      refetch();
    } catch (err) {
      setError((err && err.message) || "Cancel failed");
    } finally {
      setBusy(false);
    }
  };

  // Owner is hidden entirely.
  if (!p.cu || p.cu.role === "admin") return null;

  // Visual primitives.
  var pad = p.mode === "compact" ? "12px 16px" : "18px 22px";
  var btnPrimary = function(bg){ return {
    fontSize: 13, padding: "10px 18px", border: "none", borderRadius: 10,
    background: bg, color: "#fff", cursor: "pointer", fontWeight: 600,
    fontFamily: "inherit", whiteSpace: "nowrap", minHeight: 40
  };};
  var btnGhost = {
    fontSize: 13, padding: "9px 16px", border: "0.5px solid rgba(0,0,0,0.15)",
    background: "transparent", color: "#1a1a1a", borderRadius: 10, cursor: "pointer",
    fontWeight: 500, fontFamily: "inherit", whiteSpace: "nowrap", minHeight: 40
  };
  // Reads as unavailable rather than broken: not-allowed cursor, muted, still
  // legible. It carries the opening time as its label, so the control itself is
  // the answer instead of a thing you press to receive an error.
  var btnDisabled = {
    fontSize: 13, padding: "9px 16px", border: "0.5px solid rgba(0,0,0,0.10)",
    background: "#F1F5F9", color: C.textLight, borderRadius: 10, cursor: "not-allowed",
    fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap", minHeight: 40
  };

  // Color accents per state.
  var accent = (function(){
    if (widgetState === "ready_inside" || widgetState === "in_progress" || widgetState === "done") return C.success;
    if (widgetState === "ready_outside" || widgetState === "no_coords") return C.danger;
    if (widgetState === "pending_offsite") return C.warning;
    if (widgetState === "off_day") return C.textLight;
    return C.textLight;
  })();

  // Headline text + sublabel + button per state.
  var headline = "";
  var sub = "";
  var btn = null;
  if (widgetState === "loading") {
    headline = "Loading…"; sub = "Checking your attendance status";
  } else if (widgetState === "off_day") {
    if (data.offReason === "friday")  { headline = "Friday";   sub = "Enjoy your day off"; }
    else if (data.offReason === "saturday") { headline = "Saturday"; sub = "Enjoy your day off"; }
    else if (data.offReason && data.offReason.indexOf("company:") === 0) {
      headline = "Company off-day"; sub = data.offReason.slice(8);
    } else { headline = "Day off"; sub = "Enjoy your day"; }
  } else if (widgetState === "pending_offsite") {
    headline = "Off-site " + (data.pendingOffSite.type === "check_in" ? "check-in" : "check-out") + " pending review";
    sub = "Submitted " + fmtCairoTime(data.pendingOffSite.requestedAt);
    btn = <button type="button" onClick={doCancelOffSite} disabled={busy} style={btnGhost}>{busy?"…":"Cancel request"}</button>;
  } else if (widgetState === "done") {
    var ci = data.attendance.checkIn, co = data.attendance.checkOut;
    var hours = (new Date(co.timestamp) - new Date(ci.timestamp)) / 3600000;
    headline = data.attendance.autoClosed ? "Closed automatically" : "Done for today";
    sub = fmtCairoTime(ci.timestamp) + " → " + fmtCairoTime(co.timestamp) + " · " + hours.toFixed(1) + "h"
        + (data.attendance.autoClosed ? " · you forgot to check out — closed at your shift end" : "");
    if (p.onNavigate) btn = <button type="button" onClick={function(){p.onNavigate("attendance");}} style={btnGhost}>View details</button>;
  } else if (widgetState === "in_progress") {
    var ts = new Date(data.attendance.checkIn.timestamp);
    var elapsedMs = Date.now() - ts.getTime();
    headline = "Checked in at " + fmtCairoTime(ts);
    // If user is currently outside every active branch's radius, surface
    // the off-site path up front instead of waiting for the 403 from
    // /check-out. Inside (or when we don't yet know) → normal Check out.
    if (coords && geofences.length > 0 && !insideGeofence) {
      sub = (distanceMeters != null ? distanceMeters + "m from " + (activeLocName || "office") : "Outside office") + " · request off-site check-out";
      btn = <button type="button" onClick={function(){openOffSiteModal("check_out");}} disabled={busy} style={btnGhost}>Request off-site check-out</button>;
    } else {
      sub = "You've been working for " + fmtDuration(elapsedMs);
      btn = <button type="button" onClick={doCheckOut} disabled={busy} style={btnPrimary("#EA580C")}>{busy?"…":"Check out"}</button>;
    }
  } else if (widgetState === "ready_inside") {
    if (!windowOpen) {
      // Disabled and SAYING WHY, rather than clickable-then-refused.
      headline = "Too early to check in";
      sub = earlyNote + (ciWin && ciWin.shiftStart ? " · your shift starts at " + ciWin.shiftStart : "");
      btn = <button type="button" disabled style={btnDisabled}>{earlyNote}</button>;
    } else {
      headline = "Ready to check in";
      sub = "Inside " + (activeLocName || "office") + " · " + (distanceMeters != null ? distanceMeters + "m" : "—");
      btn = <button type="button" onClick={doCheckIn} disabled={busy} style={btnPrimary("#0F766E")}>{busy?"…":"Check in"}</button>;
    }
  } else if (widgetState === "ready_outside") {
    if (!windowOpen) {
      // THE PATH THAT MATTERED. At 00:42 this is the only control the widget has
      // ever offered — being outside the geofence after hours is a certainty, so
      // ready_outside is the state every post-midnight attempt lands in. It is
      // the door Ahmed Kassem walked through.
      headline = "Too early to check in";
      sub = earlyNote + (ciWin && ciWin.shiftStart ? " · your shift starts at " + ciWin.shiftStart : "");
      btn = <button type="button" disabled style={btnDisabled}>{earlyNote}</button>;
    } else {
      headline = activeLocName ? ("Outside " + activeLocName) : "Outside office";
      sub = (distanceMeters != null ? distanceMeters + "m away" : "Out of range") + " · In a meeting? Request off-site approval";
      btn = <button type="button" onClick={function(){openOffSiteModal("check_in");}} disabled={busy} style={btnGhost}>Request off-site</button>;
    }
  } else if (widgetState === "no_coords") {
    headline = "Location unavailable";
    sub = coordsErr || "Enable location in browser settings to check in";
    btn = <button type="button" onClick={refetch} style={btnGhost}>Retry</button>;
  } else if (widgetState === "locating") {
    headline = "Locating…";
    sub = "Waiting for your GPS fix";
  }
  // null branches: nothing to show

  // No active office locations configured — Owner hasn't set any yet.
  if (data && geofences.length === 0 && widgetState !== "off_day" && widgetState !== "in_progress" && widgetState !== "done") {
    headline = "Office locations not configured";
    sub = "Ask the Owner to add a branch in Settings → Office Location";
    btn = null;
  }

  // Card variant (HR dashboard): a richer navy "Total working hours" card that
  // REUSES all the check-in/out logic + today's-hours computation above — only
  // the layout + button colors differ. Non-actionable states (loading / off-day
  // / done / office-not-configured) degrade to a graceful status note.
  var cardEl = null;
  if (p.variant === "card") {
    var cCiTs = hasCheckIn ? data.attendance.checkIn.timestamp : null;
    var cCoTs = hasCheckOut ? data.attendance.checkOut.timestamp : null;
    var cHours = (hasCheckIn && hasCheckOut)
      ? ((new Date(cCoTs) - new Date(cCiTs)) / 3600000).toFixed(1) + "h"
      : (hasCheckIn ? fmtDuration(Date.now() - new Date(cCiTs).getTime()) : "—");
    // Per-state colors mirror the default banner exactly: btnPrimary(bg) is
    // {bg, #fff text, no border}; btnGhost is {transparent, #1a1a1a text,
    // 0.5px rgba(0,0,0,0.15) border}. Card keeps its own geometry — only colors match.
    var cPrimary = function(bg){ return { background:bg, color:"#fff", border:"none", borderRadius:10, padding:"11px 22px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit", minHeight:44, whiteSpace:"nowrap" }; };
    var cGhost = { background:"transparent", color:"#1a1a1a", border:"0.5px solid rgba(0,0,0,0.15)", borderRadius:10, padding:"10px 18px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit", minHeight:44, whiteSpace:"nowrap" };
    var cDisabled = { background:"#F1F5F9", color:C.textLight, border:"0.5px solid rgba(0,0,0,0.10)", borderRadius:10, padding:"10px 18px", fontSize:13, fontWeight:700, cursor:"not-allowed", fontFamily:"inherit", minHeight:44, whiteSpace:"nowrap" };
    var cBtn = null, cNote = null;
    if (data && geofences.length === 0 && widgetState !== "off_day" && widgetState !== "in_progress" && widgetState !== "done") {
      cNote = "Office not configured";
    } else if (widgetState === "in_progress")    { cBtn = <button type="button" onClick={doCheckOut} disabled={busy} style={cPrimary("#EA580C")}>{busy?"…":"Check out"}</button>; }
    else if (widgetState === "ready_inside")      { cBtn = windowOpen
        ? <button type="button" onClick={doCheckIn}  disabled={busy} style={cPrimary("#0F766E")}>{busy?"…":"Check in"}</button>
        : <button type="button" disabled style={cDisabled}>{earlyNote}</button>; }
    else if (widgetState === "ready_outside")     { cBtn = windowOpen
        ? <button type="button" onClick={function(){openOffSiteModal("check_in");}} disabled={busy} style={cGhost}>Request off-site</button>
        : <button type="button" disabled style={cDisabled}>{earlyNote}</button>; }
    else if (widgetState === "pending_offsite")   { cBtn = <button type="button" onClick={doCancelOffSite} disabled={busy} style={cGhost}>{busy?"…":"Cancel request"}</button>; }
    else if (widgetState === "no_coords")         { cBtn = <button type="button" onClick={refetch} style={cGhost}>Retry</button>; }
    else if (widgetState === "done")              { cNote = "Done for today"; }
    else                                          { cNote = sub || headline; }
    cardEl = <div style={{ background:"#fff", borderRadius:14, border:"1px solid #E8ECF1", overflow:"hidden", marginBottom:16, fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <div style={{ background:"#1C2A47", color:"#fff", padding:"12px 18px", display:"flex", alignItems:"center", gap:10 }}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
        <div style={{ fontSize:15, fontWeight:700 }}>Total working hours</div>
      </div>
      <div style={{ padding:"20px 22px", display:"flex", flexWrap:"wrap", alignItems:"center", gap:24 }}>
        <div style={{ flex:"1 1 120px", minWidth:120 }}>
          <div style={{ fontSize:12, color:C.textLight, marginBottom:4 }}>Total hours</div>
          <div style={{ fontSize:24, fontWeight:800, color:"#1C2A47" }}>{cHours}</div>
        </div>
        <div style={{ flex:"1 1 160px", minWidth:160 }}>
          <div style={{ fontSize:12, color:C.textLight, marginBottom:4 }}>Check in &amp; out</div>
          <div style={{ fontSize:16, fontWeight:700, color:C.text }}>{hasCheckIn ? fmtCairoTime(cCiTs) : "—"} <span style={{ color:C.textLight, fontWeight:500 }}>→</span> {hasCheckOut ? fmtCairoTime(cCoTs) : "—"}</div>
        </div>
        <div style={{ flexShrink:0 }}>{cBtn || (cNote && <span style={{ fontSize:13, color:C.textLight, fontWeight:600 }}>{cNote}</span>)}</div>
      </div>
      {error && <div style={{ padding:"0 22px 14px", fontSize:12, color:C.danger }}>{error}</div>}
    </div>;
  }

  return <>
    {cardEl || (<div style={{
      background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)",
      borderLeft:"3px solid "+accent, padding:pad, display:"flex", alignItems:"center",
      gap:14, fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      marginBottom:16, opacity: widgetState==="off_day" ? 0.85 : 1
    }}>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:14, fontWeight:600, color:C.text, marginBottom:2}}>{headline}</div>
        <div style={{fontSize:12, color:C.textLight, lineHeight:1.4}}>{sub}</div>
        {error && <div style={{fontSize:11, color:C.danger, marginTop:4}}>{error}</div>}
      </div>
      {btn}
    </div>)}

    {modalOpen && <div className="crm-modal" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 }}>
      <div className="crm-modal-inner" style={{ background:"#fff", borderRadius:12, maxWidth:480, width:"100%", padding:24, fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
        <div style={{ fontSize:16, fontWeight:600, marginBottom:6, color:C.text }}>Request off-site {modalType === "check_in" ? "check-in" : "check-out"}</div>
        <div style={{ fontSize:12, color:C.textLight, marginBottom:16, lineHeight:1.5 }}>
          Your timestamp and GPS coordinates are captured automatically. The {modalType === "check_in" ? "check-in" : "check-out"} is recorded immediately and reviewed by an admin.
        </div>
        <label style={{ fontSize:12, color:C.textLight, display:"block", marginBottom:6 }}>Reason (min 10 characters)</label>
        <textarea value={modalReason} onChange={function(e){setModalReason(e.target.value);}}
          rows={4} placeholder="e.g. Site visit at New Cairo with Mr. Ahmed…"
          style={{ width:"100%", padding:10, border:"0.5px solid rgba(0,0,0,0.15)", borderRadius:8, fontSize:13, fontFamily:"inherit", resize:"vertical", boxSizing:"border-box" }}/>
        {modalErr && <div style={{ fontSize:12, color:C.danger, marginTop:8 }}>{modalErr}</div>}
        <div style={{ display:"flex", gap:8, marginTop:16, justifyContent:"flex-end" }}>
          <button type="button" onClick={function(){setModalOpen(false);}} disabled={modalBusy}
            style={{ fontSize:13, padding:"9px 16px", border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent", borderRadius:8, cursor:"pointer", fontFamily:"inherit", color:C.text }}>Cancel</button>
          <button type="button" onClick={submitOffSite} disabled={modalBusy}
            style={{ fontSize:13, padding:"9px 16px", border:"none", background:"#185FA5", color:"#fff", borderRadius:8, cursor:"pointer", fontWeight:600, fontFamily:"inherit" }}>
            {modalBusy ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </div>
    </div>}
  </>;
};

// Standalone monthly attendance log — extracted from AttendancePage's "My
// Attendance" tab so the HR dashboard can reuse the exact same markup. Owns its
// own month navigation + /api/attendance/my-month fetch. Props: { token }.
var MonthlyAttendanceLog = function(p) {
  var now = new Date();
  var [year,setYear]   = useState(now.getFullYear());
  var [month,setMonth] = useState(now.getMonth() + 1);
  var [rows,setRows]   = useState([]);
  var [loading,setLoading] = useState(false);

  useEffect(function(){
    var cancelled = false;
    setLoading(true);
    apiFetch("/api/attendance/my-month?year="+year+"&month="+month, "GET", null, p.token).then(function(r){
      if (!cancelled) setRows(Array.isArray(r) ? r : []);
    }).catch(function(){ if (!cancelled) setRows([]); })
      .finally(function(){ if (!cancelled) setLoading(false); });
    return function(){ cancelled = true; };
  }, [year, month, p.token]);

  // Generate every Cairo day in the selected month to render absent rows for
  // workdays that have no Attendance doc (visual completeness — server doesn't
  // create absent rows until salary calc in Phase 5).
  var monthLabel = new Date(year, month-1, 1).toLocaleDateString("en-GB", { month:"long", year:"numeric" });
  var daysInMonth = new Date(year, month, 0).getDate();
  var rowsByDate = {};
  rows.forEach(function(r){
    var dd = new Date(r.date);
    rowsByDate[dd.getUTCFullYear()+"-"+(dd.getUTCMonth()+1)+"-"+dd.getUTCDate()] = r;
  });
  var monthDays = [];
  for (var di = 1; di <= daysInMonth; di++) {
    var iso = year+"-"+month+"-"+di;
    monthDays.push({ date: new Date(Date.UTC(year, month-1, di)), attendance: rowsByDate[iso] || null });
  }
  var prevMonth = function(){ if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  var nextMonth = function(){ if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  return <div style={{background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)", overflow:"hidden"}}>
    <div style={{padding:"14px 20px", borderBottom:"0.5px solid rgba(0,0,0,0.06)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10}}>
      <div style={{fontSize:14, fontWeight:600, color:C.text}}>Monthly log</div>
      <div style={{display:"flex", alignItems:"center", gap:8}}>
        <button type="button" onClick={prevMonth} style={{border:"0.5px solid rgba(0,0,0,0.1)", background:"transparent", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontFamily:"inherit"}}>‹</button>
        <div style={{fontSize:13, fontWeight:500, color:C.text, minWidth:120, textAlign:"center"}}>{monthLabel}</div>
        <button type="button" onClick={nextMonth} style={{border:"0.5px solid rgba(0,0,0,0.1)", background:"transparent", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontFamily:"inherit"}}>›</button>
      </div>
    </div>

    {loading ? <div style={{padding:24, textAlign:"center", color:C.textLight, fontSize:13}}>Loading…</div>
      : <div style={{overflowX:"auto"}}>
        <table style={{width:"100%", borderCollapse:"collapse", fontSize:13}}>
          <thead>
            <tr style={{background:"#F7F7F5"}}>
              <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Date</th>
              <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Day</th>
              <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Check-in</th>
              <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Check-out</th>
              <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Status</th>
            </tr>
          </thead>
          <tbody>
            {monthDays.map(function(d){
              var weekday = d.date.toLocaleDateString("en-GB", { weekday:"short", timeZone:"UTC" });
              var a = d.attendance;
              // Off reason: backend off rows (synthetic off-Saturdays, or real
              // mark_off_day rows tagged offReason) → "Saturday off"/"Off";
              // Friday stays frontend-derived (no row); worked days use a.status.
              var off = (a && a.offReason) ? a.offReason : (!a && weekday === "Fri" ? "friday" : null);
              var badge = attStatusBadge(a && a.status, off);
              return <tr key={d.date.toISOString()} style={{borderTop:"0.5px solid rgba(0,0,0,0.05)"}}>
                <td style={{padding:"10px 16px"}}>{d.date.toLocaleDateString("en-GB", { day:"2-digit", month:"short", timeZone:"UTC" })}</td>
                <td style={{padding:"10px 16px", color:C.textLight}}>{weekday}</td>
                <td style={{padding:"10px 16px"}}>{a && a.checkIn ? fmtCairoTime(a.checkIn.timestamp) : "—"}</td>
                <td style={{padding:"10px 16px"}}>{attCheckOutCell(a)}</td>
                <td style={{padding:"10px 16px"}}>
                  <span style={{display:"inline-block", padding:"3px 10px", borderRadius:6, fontSize:11, fontWeight:500, background:badge.bg, color:badge.color}}>{badge.label}</span>
                </td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>}
  </div>;
};

var AttendancePage = function(p) {
  var isOwner          = p.cu && p.cu.role === "admin";
  var canManage        = canDo(p.access, "manageAttendance");
  var canViewSalaries  = canDo(p.access, "manageSalaries");
  var canApproveOffSite= canDo(p.access, "approveOffSiteRequests");
  var canManageOffDays = canDo(p.access, "manageCompanyOffDays");

  // Default tab — landings (in priority order):
  //   1. p.initTab (deep-link intent, e.g. 5-lates click → salaries)
  //   2. canManage → team    (admin / operation / hr if granted manageAttendance)
  //   3. canViewSalaries → salaries
  //   4. canApproveOffSite → offsiteRequests
  //   5. canManageOffDays → companyOffDays
  //   6. fallback: mine (employee personal view)
  // Order changed Phase R-attendance so non-admin roles with manageAttendance
  // (operation / hr) also default to the Team tab when they have access,
  // matching admin behavior. Was: !isOwner gate forced everyone to "mine".
  // The tabs this caller may see, in landing priority — handed to the shared
  // tab memory as the page's registry (Attendance's tabs are the four
  // capabilities, not surfaces), so a remembered tab that this role can no
  // longer see falls through to the first allowed one. p.initTab is the deep
  // link and wins over the remembered tab.
  var attendanceTabList = [];
  if (canManage)         attendanceTabList.push("team");
  if (canViewSalaries)   attendanceTabList.push("salaries");
  if (canApproveOffSite) attendanceTabList.push("offsiteRequests");
  if (canManageOffDays)  attendanceTabList.push("companyOffDays");
  if (!isOwner)          attendanceTabList.push("mine");
  var defaultTab = attendanceTabList.length ? attendanceTabList[0] : "mine";
  var [activeTab, setActiveTab, setActiveTabQuiet] = useRememberedTab("attendance", gid(p.cu), attendanceTabList, p.initTab, defaultTab);

  // Real-time permission revoke. If the current tab's permission is taken
  // away mid-session, fall back to the first available tab the user can see.
  useEffect(function(){
    var allowed = (
      (activeTab === "mine"            && !isOwner) ||
      (activeTab === "team"             && canManage) ||
      (activeTab === "salaries"         && canViewSalaries) ||
      (activeTab === "offsiteRequests"  && canApproveOffSite) ||
      (activeTab === "companyOffDays"   && canManageOffDays)
    );
    if (allowed) return;
    // Quiet: the stand-in is not remembered, so the revoked tab returns the day
    // the permission does.
    if (canManage)         { setActiveTabQuiet("team");            return; }
    if (canViewSalaries)   { setActiveTabQuiet("salaries");        return; }
    if (canApproveOffSite) { setActiveTabQuiet("offsiteRequests"); return; }
    if (canManageOffDays)  { setActiveTabQuiet("companyOffDays");  return; }
    if (!isOwner)          { setActiveTabQuiet("mine");            return; }
  }, [activeTab, isOwner, canManage, canViewSalaries, canApproveOffSite, canManageOffDays, setActiveTabQuiet]);

  // ----- "My" tab: monthly history now lives in <MonthlyAttendanceLog/>
  // (extracted so the HR dashboard can reuse it). It owns its own month nav +
  // /api/attendance/my-month fetch, so AttendancePage no longer holds that state.

  // ----- "Team" tab: roster — Phase R-attendance: now date-picker driven.
  // dateMode = "single" (default, today) or "range" (from + to).
  // Each row carries `date` from the backend so range mode can group per day.
  var [team,setTeam] = useState([]);
  var [teamMeta,setTeamMeta] = useState({ totalRows: 0, capped: false, mode: "single" });
  var [loadingTeam,setLoadingTeam] = useState(false);
  var todayIsoLocal = (function(){ var d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0"); })();
  var weekAgoIsoLocal = (function(){ var d = new Date(Date.now() - 6*86400000); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0"); })();
  var [dateMode, setDateMode] = useState("single");
  var [singleDate, setSingleDate] = useState(todayIsoLocal);
  var [rangeFrom, setRangeFrom] = useState(weekAgoIsoLocal);
  var [rangeTo, setRangeTo]     = useState(todayIsoLocal);
  // Phase 6 — 5-lates notifications surfaced above the roster.
  var [lateAlerts,setLateAlerts] = useState([]);
  // Undo check-out (2026-08-24) — keyed by rowKey so only the row being undone
  // shows a busy state, and a double-tap on a slow connection cannot fire twice.
  var [undoBusy,setUndoBusy] = useState(null);

  // UNDO A CHECK-OUT THAT SHOULD NEVER HAVE BEEN RECORDED.
  //
  // Rokaya Swidan, 2026-08-24: in at 11:09, out at 11:11 by mistake, day closed
  // while she was still at her desk. The confirm names the person and the exact
  // time being cleared, because "are you sure?" on a roster of 25 rows is how the
  // wrong row gets reopened.
  //
  // No local optimism: the server broadcasts attendance_updated and the handler
  // below patches this row, which is also what makes the button disappear.
  var undoCheckOut = async function(row, rowKey){
    var a = row.attendance;
    if (!a || !a.checkOut || !a.checkOut.timestamp) return;
    var when = fmtCairoTime(a.checkOut.timestamp);
    var who  = row.user.name;
    var msg  = "Undo " + who + "'s check-out on " + (row.date || "today") + "?\n\n"
             + "The recorded check-out of " + when + (a.autoClosed ? " (auto-closed)" : "")
             + " will be cleared and the day returns to In progress."
             + (a.autoClosed ? "\n\nThe automatic close will be cleared too." : "")
             + "\n\nNothing is deleted — the cleared time stays in the audit log.";
    if (!window.confirm(msg)) return;
    setUndoBusy(rowKey);
    try {
      var res = await apiFetch("/api/attendance/undo-check-out", "POST",
        { userId: String(row.user._id), date: row.date }, p.token, p.csrfToken);
      // Patch THIS row from the response rather than leaning on the broadcast. The
      // WS handler below only patches rows dated today, so an Owner reopening an
      // earlier day would otherwise watch a stale check-out sit there until the
      // next refetch.
      if (res && res.attendance) {
        setTeam(function(prev){
          return prev.map(function(r){
            if (String(r.user._id) !== String(row.user._id) || r.date !== row.date) return r;
            return Object.assign({}, r, { attendance: res.attendance, canUndoCheckOut: false });
          });
        });
      }
    } catch (err) {
      // apiFetch surfaces the server's `message` — the refusals each explain what
      // to do instead (reject the off-site request, unlock the month, ask the Owner).
      window.alert((err && err.message) || "Could not undo the check-out.");
    } finally {
      setUndoBusy(null);
    }
  };
  useEffect(function(){
    if (activeTab !== "team") return;
    if (!canManage) return;
    var cancelled = false;
    setLoadingTeam(true);
    var qs;
    if (dateMode === "range") {
      qs = "from=" + encodeURIComponent(rangeFrom) + "&to=" + encodeURIComponent(rangeTo);
    } else {
      qs = "date=" + encodeURIComponent(singleDate);
    }
    apiFetch("/api/attendance/today/all?" + qs, "GET", null, p.token).then(function(r){
      if (cancelled) return;
      // Backend now returns { data, mode, totalRows, capped, rowCap }. Old
      // (pre-Phase-R-attendance) shape was a flat array — fallback for safety.
      var rows = (r && Array.isArray(r.data)) ? r.data : (Array.isArray(r) ? r : []);
      setTeam(rows);
      setTeamMeta({
        totalRows: (r && typeof r.totalRows === "number") ? r.totalRows : rows.length,
        capped: !!(r && r.capped),
        mode: (r && r.mode) || dateMode
      });
    }).catch(function(){ if (!cancelled) { setTeam([]); setTeamMeta({ totalRows: 0, capped: false, mode: dateMode }); } })
      .finally(function(){ if (!cancelled) setLoadingTeam(false); });
    // Fetch this month's 5-lates notifications. Backend filters by createdAt
    // implicitly via the once-per-month creation rule.
    //
    // limit=200, not 20. The headline is lateAlerts.length, so the limit IS the
    // headline — and on 2026-08-26 production held exactly 20 rows against a
    // limit of 20. It read "20 late-attendance alerts" correctly by luck; the
    // 21st person would have been invisible and the headline would still have
    // said 20. One row per person per month bounds this naturally.
    apiFetch("/api/notifications?type=attendance_late_5&limit=200", "GET", null, p.token).then(function(r){
      if (cancelled) return;
      var monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0,0,0,0);
      var rows = (Array.isArray(r) ? r : []).filter(function(n){
        return new Date(n.createdAt || 0) >= monthStart;
      });
      setLateAlerts(rows);
    }).catch(function(){ if (!cancelled) setLateAlerts([]); });
    var refreshAlerts = function(){
      apiFetch("/api/notifications?type=attendance_late_5&limit=200", "GET", null, p.token).then(function(r){
        var monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0,0,0,0);
        var rows = (Array.isArray(r) ? r : []).filter(function(n){ return new Date(n.createdAt || 0) >= monthStart; });
        setLateAlerts(rows);
      }).catch(function(){});
    };
    window.addEventListener("crm:notification_updated", refreshAlerts);
    return function(){ cancelled = true; window.removeEventListener("crm:notification_updated", refreshAlerts); };
  }, [activeTab, canManage, p.token, dateMode, singleDate, rangeFrom, rangeTo]);

  // Real-time team updates — patches the matching row when any employee
  // checks in/out anywhere. Doesn't add new rows (the roster set is fixed
  // for a given day). Only active while Team tab is mounted.
  useEffect(function(){
    if (activeTab !== "team") return;
    var handler = function(e){
      if (!e || !e.detail) return;
      var uid = String(e.detail.userId);
      setTeam(function(prev){
        return prev.map(function(row){
          if (String(row.user._id) !== uid) return row;
          // In range mode the same user appears multiple times (one row per
          // day). The live check-in/out event only describes today's record,
          // so only patch the row whose date is today (or the row with no
          // explicit date in single-day mode).
          if (row.date && row.date !== todayIsoLocal) return row;
          return Object.assign({}, row, { attendance: e.detail.attendance || row.attendance });
        });
      });
    };
    window.addEventListener("crm:attendance_updated", handler);
    return function(){ window.removeEventListener("crm:attendance_updated", handler); };
  }, [activeTab, todayIsoLocal]);

  var tabBtn = function(id, label){
    var act = activeTab === id;
    return <div key={id} onClick={function(){setActiveTab(id);}}
      style={{padding:"8px 14px", fontSize:13, cursor:"pointer", borderRadius:8, whiteSpace:"nowrap", userSelect:"none", flexShrink:0,
        color:act?"#1a1a1a":"#666", fontWeight:act?500:400,
        background:act?"#fff":"transparent",
        border:"0.5px solid "+(act?"rgba(0,0,0,0.1)":"transparent")}}>{label}</div>;
  };

  return <div style={{padding:"24px 16px 40px", fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
    <div style={{maxWidth:1200, margin:"0 auto"}}>

      {/* Tab bar — every visible tab is gated by an attendance permission. */}
      <div style={{display:"flex", gap:6, marginBottom:14, padding:6, background:"#F7F7F5", borderRadius:10, width:"fit-content", border:"0.5px solid rgba(0,0,0,0.05)", flexWrap:"wrap"}}>
        {!isOwner            && tabBtn("mine",            "My Attendance")}
        {canManage           && tabBtn("team",            "Team Today")}
        {canViewSalaries     && tabBtn("salaries",        "Salaries")}
        {canApproveOffSite   && tabBtn("offsiteRequests", "Off-site Requests")}
        {canManageOffDays    && tabBtn("companyOffDays",  "Company Off-Days")}
      </div>

      {activeTab === "mine" && !isOwner && <div>
        <CheckInWidget token={p.token} cu={p.cu} csrfToken={p.csrfToken} mode="full"/>

        <MonthlyAttendanceLog token={p.token}/>
      </div>}

      {activeTab === "team" && canManage && <>
        {lateAlerts.length > 0 && <div style={{background:"#FEF9F0", border:"0.5px solid #FDE68A", borderRadius:12, padding:"12px 16px", marginBottom:14}}>
          <div style={{fontSize:13, fontWeight:600, color:"#92400E", marginBottom:8}}>⚠ {lateAlerts.length} late-attendance alert{lateAlerts.length === 1 ? "" : "s"} this month</div>
          <div style={{display:"flex", flexDirection:"column", gap:4}}>
            {/* THE COUNT IS THE SERVER'S LIVE ONE, NOT THE ROW'S TEXT.
                `n.reason` was rendered here and it is a CROSSING RECORD frozen at
                the moment the person hit the threshold — so it read "5 lates this
                month" for every single person, all month. Measured on production
                2026-08-26: 20 rows, all storing that exact string, real counts
                running from 5 to 21 (Mohamed Mostafa 21, Nagy Naeem 19).
                `lateCount` / `lateLastAt` / `lateThreshold` are resolved at read
                time by attachLateCounts (server.js) from the attendance rows
                themselves, so a repaired attendance day moves this line too.
                A row whose count cannot be resolved falls back to the threshold
                wording rather than printing a confident wrong number. */}
            {lateAlerts.slice().sort(function(a,b){
              return (Number(b.lateCount) || 0) - (Number(a.lateCount) || 0);
            }).map(function(n){
              var canDrill = canViewSalaries && p.setSalaryViewUserId && n.leadId;
              var open = function(){
                if (!canDrill) return;
                p.setSalaryViewUserId(String(n.leadId));
                setActiveTab("salaries");
              };
              var thr = Number(n.lateThreshold) || 5;
              var cnt = (typeof n.lateCount === "number") ? n.lateCount : null;
              var lastAt = n.lateLastAt || n.createdAt;
              return <div key={String(n._id)} onClick={open}
                style={{cursor: canDrill ? "pointer" : "default", fontSize:12, color:C.text, padding:"6px 8px", borderRadius:6}}
                onMouseEnter={function(e){ if (canDrill) e.currentTarget.style.background = "#FEF3C7"; }}
                onMouseLeave={function(e){ e.currentTarget.style.background = "transparent"; }}>
                <b>{n.agentName || "Employee"}</b>
                {cnt !== null
                  ? <> · <b>{cnt}</b> late{cnt === 1 ? "" : "s"} this month
                      <span style={{color:C.textLight, fontSize:11}}> (threshold {thr})</span></>
                  : <> · reached the {thr}-late threshold this month</>}
                <span style={{color:C.textLight, marginLeft:8, fontSize:11}}>· last {lastAt ? new Date(lastAt).toLocaleDateString("en-GB", { day:"2-digit", month:"short" }) : "—"}</span>
              </div>;
            })}
          </div>
        </div>}
        <div style={{background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)", overflow:"visible"}}>{/* visible: the 📅 calendar opens below the header (owner 2026-10-31) */}
        <div style={{padding:"14px 20px", borderBottom:"0.5px solid rgba(0,0,0,0.06)", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:14, fontWeight:600, color:C.text}}>
              {dateMode === "single"
                ? "Team — " + (singleDate === todayIsoLocal ? "today" : singleDate)
                : "Team — " + rangeFrom + " to " + rangeTo}
            </div>
            <div style={{fontSize:12, color:C.textLight, marginTop:2}}>
              {teamMeta.totalRows.toLocaleString()} row{teamMeta.totalRows === 1 ? "" : "s"}
              {teamMeta.capped ? " (capped at 500 — narrow the range to see more)" : ""}
            </div>
          </div>
          {/* 📅 Date (owner 2026-10-31): the shared calendar chip in place of the
              Single Day / Date Range toggle and its date inputs. One click = that
              day (single mode), a later second click = the range (range mode);
              attendanceDateChip writes the same dateMode / singleDate / rangeFrom /
              rangeTo the inputs wrote. */}
          <div style={{display:"flex", gap:6, alignItems:"center", flexWrap:"wrap"}}>
            <DashRangePicker isMobile={p.isMobile} todayYmd={todayIsoLocal} min=""
              value={attendanceDateChip.value({ dateMode:dateMode, singleDate:singleDate, rangeFrom:rangeFrom, rangeTo:rangeTo })}
              active={attendanceDateChip.active({ dateMode:dateMode, singleDate:singleDate, todayIso:todayIsoLocal })}
              triggerStyle={periodChipStyle(attendanceDateChip.active({ dateMode:dateMode, singleDate:singleDate, todayIso:todayIsoLocal }), PERIOD_ACCENT_BLUE, p.isMobile)}
              onPick={function(a, b){ attendanceDateChip.pick(a, b, { setDateMode:setDateMode, setSingleDate:setSingleDate, setRangeFrom:setRangeFrom, setRangeTo:setRangeTo, todayIso:todayIsoLocal }); }}/>
          </div>
        </div>
        {loadingTeam ? <div style={{padding:24, textAlign:"center", color:C.textLight, fontSize:13}}>Loading…</div>
          : <div style={{overflowX:"auto"}}>
            <table style={{width:"100%", borderCollapse:"collapse", fontSize:13}}>
              <thead>
                <tr style={{background:"#F7F7F5"}}>
                  {dateMode === "range" && <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Date</th>}
                  <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Employee</th>
                  <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Role</th>
                  <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Check-in</th>
                  <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Check-out</th>
                  <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Status</th>
                  <th style={{textAlign:"right", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}></th>
                </tr>
              </thead>
              <tbody>
                {team.map(function(row, i){
                  var a = row.attendance;
                  var off = row.isOffDay ? row.offReason : null;
                  var badge = attStatusBadge(a && a.status, off);
                  // Key by user+date in range mode (multiple rows per user); user-only in single.
                  var rowKey = dateMode === "range" ? (String(row.user._id) + "|" + (row.date || i)) : String(row.user._id);
                  return <tr key={rowKey} style={{borderTop:"0.5px solid rgba(0,0,0,0.05)"}}>
                    {dateMode === "range" && <td style={{padding:"10px 16px", color:C.textLight, whiteSpace:"nowrap"}}>{row.date || "—"}</td>}
                    <td style={{padding:"10px 16px"}}>{row.user.name}</td>
                    <td style={{padding:"10px 16px", color:C.textLight}}>{row.user.role}</td>
                    <td style={{padding:"10px 16px"}}>{a && a.checkIn ? fmtCairoTime(a.checkIn.timestamp) : "—"}</td>
                    <td style={{padding:"10px 16px"}}>{attCheckOutCell(a)}</td>
                    <td style={{padding:"10px 16px"}}>
                      <span style={{display:"inline-block", padding:"3px 10px", borderRadius:6, fontSize:11, fontWeight:500, background:badge.bg, color:badge.color}}>{badge.label}</span>
                    </td>
                    <td style={{padding:"10px 16px", textAlign:"right", whiteSpace:"nowrap"}}>
                      {/* THE SERVER DECIDED; THIS ONLY DRAWS. row.canUndoCheckOut is
                          resolved by /api/attendance/today/all against the same rules
                          the endpoint enforces — same-day (or Owner), not your own day,
                          no pending off-site request, no override, month not finalized.
                          The `a.checkOut` half is not a second copy of the rule: it is
                          what makes the button vanish the instant the WS patch lands,
                          without waiting for a refetch. */}
                      {row.canUndoCheckOut && a && a.checkOut && a.checkOut.timestamp &&
                        <button type="button" disabled={undoBusy === rowKey}
                          onClick={function(){ undoCheckOut(row, rowKey); }}
                          style={{fontSize:11, padding:"4px 10px", borderRadius:6,
                                  border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent",
                                  cursor: undoBusy === rowKey ? "wait" : "pointer",
                                  fontFamily:"inherit", color:C.text, opacity: undoBusy === rowKey ? 0.5 : 1}}>
                          {undoBusy === rowKey ? "Undoing…" : "Undo check-out"}
                        </button>}
                    </td>
                  </tr>;
                })}
                {team.length === 0 && <tr><td colSpan={dateMode === "range" ? 7 : 6} style={{padding:24, textAlign:"center", color:C.textLight, fontSize:13}}>No rows for this {dateMode === "range" ? "range" : "date"}</td></tr>}
              </tbody>
            </table>
          </div>}
        </div>
      </>}

      {activeTab === "team" && !canManage && <div style={{padding:24, textAlign:"center", color:C.textLight, fontSize:13, background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)"}}>
        You no longer have permission to view the team attendance.
      </div>}

      {/* Salaries tab — list view with row-click drilldown to per-employee
          sheet. salaryViewUserId is App-level state so the sheet survives
          across tab switches. */}
      {activeTab === "salaries" && canViewSalaries && (
        p.salaryViewUserId
          ? <SalarySheetPage  {...p} embedded={true}/>
          : <SalariesListPage {...p} embedded={true}/>
      )}

      {activeTab === "offsiteRequests" && canApproveOffSite && <OffSiteRequestsPage {...p} embedded={true}/>}
      {activeTab === "companyOffDays"  && canManageOffDays  && <CompanyOffDaysPage  {...p} embedded={true}/>}
    </div>
  </div>;
};

// =====================================================================
// OFF-SITE REQUESTS INBOX — Phase 4
// Admin / operation / hr (gated by approveOffSiteRequests permission).
// Shows raw lat/lng + an Open in Maps link (no third-party API key needed).
// =====================================================================
var OffSiteRequestsPage = function(p) {
  // Same predicate the bell reads — see canDecideOffSite. One capability answer,
  // two surfaces, and a gate that can run it.
  var canApprove = canDecideOffSite(p.access);
  var [rows,setRows] = useState([]);
  var [loading,setLoading] = useState(false);
  var [error,setError] = useState("");
  var [rejectingId,setRejectingId] = useState(null);
  var [rejectNotes,setRejectNotes] = useState("");

  var refetch = useCallback(function(){
    if (!p.token) return;
    setLoading(true);
    apiFetch("/api/offsite/pending","GET",null,p.token).then(function(r){
      setRows(Array.isArray(r) ? r : []);
      setError("");
    }).catch(function(err){
      setError((err && err.message) || "Failed to load");
      setRows([]);
    }).finally(function(){ setLoading(false); });
  },[p.token]);

  useEffect(function(){
    if (!canApprove) return;
    refetch();
    var handler = function(){ refetch(); };
    window.addEventListener("crm:offsite_request_updated", handler);
    return function(){ window.removeEventListener("crm:offsite_request_updated", handler); };
  },[canApprove, refetch]);

  // BOTH SURFACES CALL offSiteDecision. This page used to build the URL itself and
  // so did the bell; two spellings of one endpoint is how the surface fewer people
  // open ends up being the one that drifts.
  var doApprove = async function(id){
    try {
      await offSiteDecision("approve", id, { token: p.token, csrfToken: p.csrfToken });
      refetch();
    } catch (err) { alert((err && err.message) || "Approve failed"); }
  };
  var doReject = async function(id){
    try {
      await offSiteDecision("reject", id, { token: p.token, csrfToken: p.csrfToken, notes: rejectNotes });
      setRejectingId(null); setRejectNotes("");
      refetch();
    } catch (err) { alert((err && err.message) || "Reject failed"); }
  };

  if (!canApprove) {
    return <div style={{padding:"24px 16px"}}>
      <div style={{maxWidth:1200, margin:"0 auto", background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)", padding:24, textAlign:"center", color:C.textLight, fontSize:13, fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
        You do not have permission to approve off-site requests.
      </div>
    </div>;
  }

  return <div style={p.embedded ? {} : {padding:"24px 16px 40px", fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
    <div style={{maxWidth:1200, margin:"0 auto"}}>
      <div style={{background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)", overflow:"hidden"}}>
        <div style={{padding:"16px 20px", borderBottom:"0.5px solid rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:15, fontWeight:600, color:C.text}}>Off-site requests</div>
          <div style={{fontSize:12, color:C.textLight, marginTop:2}}>{rows.length} pending</div>
        </div>

        {loading ? <div style={{padding:32, textAlign:"center", color:C.textLight, fontSize:13}}>Loading…</div>
          : error ? <div style={{padding:32, textAlign:"center", color:C.danger, fontSize:13}}>{error}</div>
          : rows.length === 0 ? <div style={{padding:32, textAlign:"center", color:C.textLight, fontSize:13}}>No pending requests</div>
          : <div>
            {rows.map(function(r){
              var emp = r.userId || {};
              var mapsUrl = offSiteMapsUrl(r);   // one builder, so the bell links to the same pin
              // Same rule as the bell, same predicate: an approver's own request
              // is listed but not decidable here either. The server refuses it
              // (403 self_decision) whatever this renders.
              var mayDecide = canDecideOffSite(p.access, r, gid(p.cu));
              var coordStr = (r.latitude != null ? Number(r.latitude).toFixed(4) : "—") + ", " + (r.longitude != null ? Number(r.longitude).toFixed(4) : "—");
              return <div key={String(r._id)} style={{padding:"16px 20px", borderTop:"0.5px solid rgba(0,0,0,0.05)"}}>
                <div style={{display:"flex", gap:12, alignItems:"flex-start", flexWrap:"wrap"}}>
                  <div style={{flex:"1 1 280px", minWidth:0}}>
                    <div style={{fontSize:14, fontWeight:600, color:C.text}}>{userNameOf(emp) || "—"}</div>
                    <div style={{fontSize:12, color:C.textLight, marginTop:2}}>
                      {emp.role || ""} {emp.teamName ? "· " + emp.teamName : ""}
                    </div>
                    <div style={{fontSize:12, color:C.text, marginTop:8, fontWeight:500}}>
                      {r.type === "check_in" ? "Off-site check-in" : "Off-site check-out"}
                      <span style={{color:C.textLight, fontWeight:400, marginLeft:8}}>· submitted {fmtCairoTime(r.requestedAt)} · {fmtCairoDate(r.requestedAt)}</span>
                    </div>
                    <div style={{fontSize:12, color:C.text, marginTop:6, lineHeight:1.5, background:"#F7F7F5", padding:"8px 10px", borderRadius:6}}>
                      {r.reason}
                    </div>
                    <div style={{fontSize:11, color:C.textLight, marginTop:8, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap"}}>
                      <span style={{fontFamily:"ui-monospace, Menlo, monospace"}}>{coordStr}</span>
                      {mapsUrl && <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{color:"#185FA5", textDecoration:"none", fontSize:11, fontWeight:500}}>📍 Open in Maps ↗</a>}
                    </div>
                  </div>
                  <div style={{display:"flex", gap:8, alignItems:"flex-start"}}>
                    {!mayDecide && <span style={{fontSize:11, color:C.textLight, fontStyle:"italic", maxWidth:200}}>Your own request — another approver reviews it</span>}
                    {mayDecide && <button type="button" onClick={function(){doApprove(r._id);}}
                      style={{fontSize:12, padding:"8px 16px", border:"none", background:"#0F766E", color:"#fff", borderRadius:8, cursor:"pointer", fontWeight:600, fontFamily:"inherit"}}>Approve</button>}
                    {mayDecide && (rejectingId === String(r._id)
                      ? null
                      : <button type="button" onClick={function(){setRejectingId(String(r._id)); setRejectNotes("");}}
                          style={{fontSize:12, padding:"8px 16px", border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent", borderRadius:8, cursor:"pointer", fontFamily:"inherit", color:C.text}}>Reject</button>)}
                  </div>
                </div>
                {mayDecide && rejectingId === String(r._id) && <div style={{marginTop:12, padding:12, background:"#FEF9F0", border:"0.5px solid #FDE68A", borderRadius:8}}>
                  <label style={{fontSize:11, color:C.textLight, display:"block", marginBottom:6}}>Rejection notes (optional)</label>
                  <textarea value={rejectNotes} onChange={function(e){setRejectNotes(e.target.value);}}
                    rows={2} style={{width:"100%", padding:8, border:"0.5px solid rgba(0,0,0,0.15)", borderRadius:6, fontSize:12, fontFamily:"inherit", resize:"vertical", boxSizing:"border-box"}}/>
                  <div style={{display:"flex", gap:8, marginTop:8, justifyContent:"flex-end"}}>
                    <button type="button" onClick={function(){setRejectingId(null); setRejectNotes("");}}
                      style={{fontSize:12, padding:"6px 12px", border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent", borderRadius:6, cursor:"pointer", fontFamily:"inherit"}}>Cancel</button>
                    <button type="button" onClick={function(){doReject(r._id);}}
                      style={{fontSize:12, padding:"6px 12px", border:"none", background:"#B91C1C", color:"#fff", borderRadius:6, cursor:"pointer", fontWeight:600, fontFamily:"inherit"}}>Confirm reject</button>
                  </div>
                </div>}
              </div>;
            })}
          </div>}
      </div>
    </div>
  </div>;
};

// =====================================================================
// COMPANY OFF-DAYS — Phase 4 (doc §11)
// Admin / operation / hr (gated by manageCompanyOffDays).
// =====================================================================
var CompanyOffDaysPage = function(p) {
  var canManage = canDo(p.access, "manageCompanyOffDays");
  var now = new Date();
  var [year,setYear] = useState(now.getFullYear());
  var [rows,setRows] = useState([]);
  var [loading,setLoading] = useState(false);
  var [newDateFrom,setNewDateFrom] = useState("");
  var [newDateTo,setNewDateTo] = useState("");
  var [newName,setNewName] = useState("");
  var [adding,setAdding] = useState(false);
  var [error,setError] = useState("");

  var refetch = useCallback(function(){
    if (!p.token) return;
    setLoading(true);
    apiFetch("/api/company-off-days?year="+year,"GET",null,p.token).then(function(r){
      setRows(Array.isArray(r) ? r : []);
    }).catch(function(){ setRows([]); })
      .finally(function(){ setLoading(false); });
  },[year, p.token]);

  useEffect(function(){
    refetch();
    var handler = function(){ refetch(); };
    window.addEventListener("crm:company_offdays_updated", handler);
    return function(){ window.removeEventListener("crm:company_offdays_updated", handler); };
  },[refetch]);

  var doAdd = async function(){
    if (!newDateFrom || !newName) { setError("From date and name required"); return; }
    if (newDateTo && newDateTo < newDateFrom) { setError("To must be on or after From"); return; }
    setError(""); setAdding(true);
    try {
      await apiFetch("/api/company-off-days","POST",{
        from: newDateFrom,
        to: newDateTo || newDateFrom,
        name: newName
      }, p.token, p.csrfToken);
      setNewDateFrom(""); setNewDateTo(""); setNewName("");
      refetch();
    } catch (err) {
      setError((err && err.message) || "Add failed");
    } finally { setAdding(false); }
  };

  var doDelete = async function(group){
    var msg = group.days > 1
      ? "Delete this " + group.days + "-day off-day range (" + group.name + ")?"
      : "Delete this off-day?";
    if (!window.confirm(msg)) return;
    try {
      var url = group.rangeId
        ? "/api/company-off-days/range/" + group.rangeId
        : "/api/company-off-days/" + group._id;
      await apiFetch(url,"DELETE",null,p.token,p.csrfToken);
      refetch();
    } catch (err) { alert((err && err.message) || "Delete failed"); }
  };

  if (!canManage) {
    return <div style={{padding:"24px 16px"}}>
      <div style={{maxWidth:1200, margin:"0 auto", background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)", padding:24, textAlign:"center", color:C.textLight, fontSize:13, fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
        You do not have permission to manage company off-days.
      </div>
    </div>;
  }

  var monthFloor = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
  var todayKey = function(d){ var x = new Date(d); return new Date(Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate())); };

  // Group rows by rangeId. Legacy single-day docs (rangeId=null) render as
  // their own one-day group keyed by _id so they keep working unchanged.
  var groups = (function(){
    var byRange = {};
    var singles = [];
    rows.forEach(function(r){
      var rid = r.rangeId ? String(r.rangeId) : null;
      if (rid) {
        if (!byRange[rid]) byRange[rid] = [];
        byRange[rid].push(r);
      } else {
        singles.push(r);
      }
    });
    var ranges = Object.keys(byRange).map(function(k){
      var sorted = byRange[k].slice().sort(function(a,b){ return new Date(a.date) - new Date(b.date); });
      return {
        key: "r:"+k,
        rangeId: k,
        from: sorted[0].date,
        to: sorted[sorted.length-1].date,
        name: sorted[0].name,
        days: sorted.length,
        _id: null
      };
    });
    var solos = singles.map(function(s){
      return {
        key: "s:"+s._id,
        rangeId: null,
        from: s.date,
        to: s.date,
        name: s.name,
        days: 1,
        _id: s._id
      };
    });
    return ranges.concat(solos).sort(function(a,b){ return new Date(a.from) - new Date(b.from); });
  })();

  var fmtDate = function(d){
    return new Date(d).toLocaleDateString("en-GB", { weekday:"short", day:"2-digit", month:"short", year:"numeric", timeZone:"UTC" });
  };

  return <div style={p.embedded ? {} : {padding:"24px 16px 40px", fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
    <div style={{maxWidth:900, margin:"0 auto"}}>
      <div style={{background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)", overflow:"hidden"}}>
        <div style={{padding:"16px 20px", borderBottom:"0.5px solid rgba(0,0,0,0.06)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:15, fontWeight:600, color:C.text}}>Company Off-Days</div>
            <div style={{fontSize:12, color:C.textLight, marginTop:2}}>Holidays and non-working days for everyone — removed from the working-days count in salaries.</div>
          </div>
          <div style={{display:"flex", gap:8, alignItems:"center"}}>
            <button type="button" onClick={function(){setYear(year - 1);}} style={{border:"0.5px solid rgba(0,0,0,0.1)", background:"transparent", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontFamily:"inherit"}}>‹</button>
            <div style={{fontSize:13, fontWeight:500, color:C.text, minWidth:60, textAlign:"center"}}>{year}</div>
            <button type="button" onClick={function(){setYear(year + 1);}} style={{border:"0.5px solid rgba(0,0,0,0.1)", background:"transparent", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontFamily:"inherit"}}>›</button>
          </div>
        </div>

        <div style={{padding:"14px 20px", borderBottom:"0.5px solid rgba(0,0,0,0.06)", background:"#FAFAF7", display:"flex", gap:8, alignItems:"flex-end", flexWrap:"wrap"}}>
          <div style={{flex:"1 1 140px", minWidth:130}}>
            <label style={{fontSize:11, color:C.textLight, display:"block", marginBottom:4}}>From</label>
            <input type="date" value={newDateFrom} onChange={function(e){setNewDateFrom(e.target.value);}}
              style={{padding:"7px 10px", border:"0.5px solid rgba(0,0,0,0.15)", borderRadius:6, fontSize:13, fontFamily:"inherit", width:"100%", boxSizing:"border-box"}}/>
          </div>
          <div style={{flex:"1 1 140px", minWidth:130}}>
            <label style={{fontSize:11, color:C.textLight, display:"block", marginBottom:4}}>To <span style={{fontSize:10, color:C.textLight}}>(optional)</span></label>
            <input type="date" value={newDateTo} min={newDateFrom || undefined} onChange={function(e){setNewDateTo(e.target.value);}}
              style={{padding:"7px 10px", border:"0.5px solid rgba(0,0,0,0.15)", borderRadius:6, fontSize:13, fontFamily:"inherit", width:"100%", boxSizing:"border-box"}}/>
          </div>
          <div style={{flex:"2 1 220px", minWidth:180}}>
            <label style={{fontSize:11, color:C.textLight, display:"block", marginBottom:4}}>Name</label>
            <input type="text" value={newName} onChange={function(e){setNewName(e.target.value);}} placeholder="e.g. Eid al-Adha"
              style={{padding:"7px 10px", border:"0.5px solid rgba(0,0,0,0.15)", borderRadius:6, fontSize:13, fontFamily:"inherit", width:"100%", boxSizing:"border-box"}}/>
          </div>
          <button type="button" onClick={doAdd} disabled={adding}
            style={{fontSize:12, padding:"8px 16px", border:"none", background:"#185FA5", color:"#fff", borderRadius:6, cursor:"pointer", fontWeight:600, fontFamily:"inherit", height:32, opacity:adding?0.6:1}}>
            {adding ? "Adding…" : "+ Add off-day"}
          </button>
          {error && <div style={{flex:"1 1 100%", fontSize:12, color:C.danger}}>{error}</div>}
        </div>

        {loading ? <div style={{padding:32, textAlign:"center", color:C.textLight, fontSize:13}}>Loading…</div>
          : groups.length === 0 ? <div style={{padding:32, textAlign:"center", color:C.textLight, fontSize:13}}>No off-days for {year}</div>
          : <table style={{width:"100%", borderCollapse:"collapse", fontSize:13}}>
            <thead>
              <tr style={{background:"#F7F7F5"}}>
                <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Date</th>
                <th style={{textAlign:"left", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Name</th>
                <th style={{textAlign:"right", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px", width:100}}></th>
              </tr>
            </thead>
            <tbody>
              {groups.map(function(g){
                var dateLabel = g.days > 1
                  ? fmtDate(g.from) + "  →  " + fmtDate(g.to)
                  : fmtDate(g.from);
                var inPast = todayKey(g.from) < monthFloor;
                return <tr key={g.key} style={{borderTop:"0.5px solid rgba(0,0,0,0.05)"}}>
                  <td style={{padding:"10px 16px"}}>
                    {dateLabel}
                    {g.days > 1 && <span style={{fontSize:11, color:C.textLight, marginLeft:8}}>({g.days} days)</span>}
                  </td>
                  <td style={{padding:"10px 16px"}}>{g.name}</td>
                  <td style={{padding:"10px 16px", textAlign:"right"}}>
                    {inPast
                      ? <span style={{fontSize:11, color:C.textLight}}>locked</span>
                      : <button type="button" onClick={function(){doDelete(g);}}
                          style={{fontSize:11, padding:"4px 10px", border:"0.5px solid rgba(0,0,0,0.1)", background:"transparent", borderRadius:6, cursor:"pointer", color:C.danger, fontFamily:"inherit"}}>Delete</button>}
                  </td>
                </tr>;
              })}
            </tbody>
          </table>}
      </div>
    </div>
  </div>;
};

// =====================================================================
// SALARIES — Phase 5B (doc §14)
// SalariesListPage = grid view of all employees for the selected month.
// SalarySheetPage  = per-employee header + 4 metric cards + daily log.
// Action-bar buttons (Edit base / Audit / Export / Finalize) and the
// Day Override modal are wired in Phase 5C–5E.
// =====================================================================

var fmtEGP = function(n){
  if (n == null || !isFinite(Number(n))) return "—";
  return Math.round(Number(n)).toLocaleString("en-US") + " EGP";
};
var fmtEGPRaw = function(n){
  if (n == null || !isFinite(Number(n))) return "—";
  return Math.round(Number(n)).toLocaleString("en-US");
};

var SalariesListPage = function(p) {
  var canViewAny = canDo(p.access, "manageSalaries");
  var now = new Date();
  var [year,setYear]   = useState(now.getFullYear());
  var [month,setMonth] = useState(now.getMonth() + 1);
  var [rows,setRows]   = useState([]);
  var [loading,setLoading] = useState(false);
  var [error,setError] = useState("");
  var [bulkBusy,setBulkBusy] = useState(false);
  var [bulkMsg,setBulkMsg]   = useState("");

  var refetch = useCallback(function(){
    if (!canViewAny || !p.token) return;
    setLoading(true); setError("");
    apiFetch("/api/salary/list/"+year+"/"+month,"GET",null,p.token).then(function(r){
      setRows(Array.isArray(r) ? r : []);
    }).catch(function(err){
      setError((err && err.message) || "Failed to load");
      setRows([]);
    }).finally(function(){ setLoading(false); });
  },[canViewAny, p.token, year, month]);

  useEffect(function(){ refetch(); },[refetch]);

  // Refetch when any individual finalize/unlock fires elsewhere — keeps the
  // FINALIZED badge in sync with the per-employee sheet.
  useEffect(function(){
    var handler = function(){ refetch(); };
    window.addEventListener("crm:salary_finalized", handler);
    return function(){ window.removeEventListener("crm:salary_finalized", handler); };
  }, [refetch]);

  var doBulkFinalize = async function(){
    var monthLbl = new Date(year, month-1, 1).toLocaleDateString("en-GB", { month:"long", year:"numeric" });
    var pending = rows.filter(function(r){ return !r.finalized; }).length;
    if (pending === 0) { setBulkMsg("Nothing to finalize — every visible employee is already finalized."); return; }
    if (!window.confirm("Finalize " + monthLbl + " for " + pending + " employee" + (pending === 1 ? "" : "s") + "?\n\nThis locks each salary calculation and prevents further attendance, override, and finalize edits for the month.")) return;
    setBulkBusy(true); setBulkMsg("");
    try {
      var resp = await apiFetch("/api/salary/list/"+year+"/"+month+"/finalize-all", "POST", null, p.token, p.csrfToken);
      setBulkMsg("Finalized " + (resp.finalized || 0) + " · skipped " + (resp.skipped || 0));
      refetch();
    } catch (err) {
      setBulkMsg("Failed: " + ((err && err.message) || "unknown"));
    } finally { setBulkBusy(false); }
  };

  if (!canViewAny) {
    return <div style={{padding:"24px 16px"}}>
      <div style={{maxWidth:1200, margin:"0 auto", background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)", padding:24, textAlign:"center", color:C.textLight, fontSize:13, fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
        You do not have permission to view salaries.
      </div>
    </div>;
  }

  var monthLabel = new Date(year, month-1, 1).toLocaleDateString("en-GB", { month:"long", year:"numeric" });
  var prevMonth = function(){ if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  var nextMonth = function(){ if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  return <div style={p.embedded ? {} : {padding:"24px 16px 40px", fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
    <div style={{maxWidth:1200, margin:"0 auto"}}>
      <div style={{background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)", overflow:"hidden"}}>
        <div style={{padding:"16px 20px", borderBottom:"0.5px solid rgba(0,0,0,0.06)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:15, fontWeight:600, color:C.text}}>Salaries</div>
            <div style={{fontSize:12, color:C.textLight, marginTop:2}}>{rows.length} employees · click a row to open the salary sheet</div>
          </div>
          <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
            <button type="button" onClick={doBulkFinalize} disabled={bulkBusy || rows.length === 0}
              style={{fontSize:12, padding:"6px 12px", border:"0.5px solid rgba(24,95,165,0.3)", background:"#E6F1FB", color:"#185FA5", borderRadius:8, cursor: (bulkBusy || rows.length === 0) ? "not-allowed" : "pointer", fontWeight:500, fontFamily:"inherit", opacity: (bulkBusy || rows.length === 0) ? 0.6 : 1}}>
              {bulkBusy ? "Finalizing…" : "Finalize all"}
            </button>
            <button type="button" onClick={prevMonth} style={{border:"0.5px solid rgba(0,0,0,0.1)", background:"transparent", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontFamily:"inherit"}}>‹</button>
            <div style={{fontSize:13, fontWeight:500, color:C.text, minWidth:120, textAlign:"center"}}>{monthLabel}</div>
            <button type="button" onClick={nextMonth} style={{border:"0.5px solid rgba(0,0,0,0.1)", background:"transparent", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontFamily:"inherit"}}>›</button>
          </div>
        </div>

        {bulkMsg && <div style={{padding:"8px 20px", fontSize:12, color: bulkMsg.indexOf("Failed") === 0 ? C.danger : "#0F6E56", background: bulkMsg.indexOf("Failed") === 0 ? "#FCEBEB" : "#EAF6F0", borderBottom:"0.5px solid rgba(0,0,0,0.06)"}}>{bulkMsg}</div>}

        {loading ? <div style={{padding:32, textAlign:"center", color:C.textLight, fontSize:13}}>Loading…</div>
          : error ? <div style={{padding:32, textAlign:"center", color:C.danger, fontSize:13}}>{error}</div>
          : rows.length === 0 ? <div style={{padding:32, textAlign:"center", color:C.textLight, fontSize:13}}>No employees</div>
          : <div style={{overflowX:"auto"}}>
            <table style={{width:"100%", borderCollapse:"collapse", fontSize:13}}>
              <thead>
                <tr style={{background:"#F7F7F5"}}>
                  <th style={{textAlign:"left",  padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Employee</th>
                  <th style={{textAlign:"left",  padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Role</th>
                  <th style={{textAlign:"right", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Working days</th>
                  <th style={{textAlign:"right", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Deductions</th>
                  <th style={{textAlign:"right", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Net salary</th>
                  <th style={{textAlign:"left",  padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px", width:100}}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(function(row){
                  var s = row.salary || {};
                  var open = function(){ if (p.setSalaryViewUserId) p.setSalaryViewUserId(String(row.user._id)); };
                  return <tr key={String(row.user._id)} onClick={open}
                    style={{borderTop:"0.5px solid rgba(0,0,0,0.05)", cursor:"pointer"}}
                    onMouseEnter={function(e){ e.currentTarget.style.background = "#FAFAFA"; }}
                    onMouseLeave={function(e){ e.currentTarget.style.background = "transparent"; }}>
                    <td style={{padding:"10px 16px"}}>{row.user.name}</td>
                    <td style={{padding:"10px 16px", color:C.textLight}}>{row.user.role}</td>
                    <td style={{padding:"10px 16px", textAlign:"right"}}>{s.workingDays != null ? s.workingDays : "—"}</td>
                    <td style={{padding:"10px 16px", textAlign:"right", color: s.totalDeductions > 0 ? C.danger : C.text}}>
                      {s.totalDeductions > 0 ? fmtEGPRaw(s.totalDeductions) : "0"} <span style={{color:C.textLight, fontSize:11}}>EGP</span>
                    </td>
                    <td style={{padding:"10px 16px", textAlign:"right", color:"#15803D", fontWeight:500}}>
                      {fmtEGPRaw(s.netSalary)} <span style={{color:C.textLight, fontSize:11, fontWeight:400}}>EGP</span>
                    </td>
                    <td style={{padding:"10px 16px"}}>
                      {row.finalized && <span style={{fontSize:10, padding:"2px 8px", borderRadius:6, background:"#F3E8FF", color:"#7C3AED", fontWeight:500}}>FINALIZED</span>}
                    </td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>}
      </div>
    </div>
  </div>;
};

var SalarySheetPage = function(p) {
  // p.salaryViewUserId set by SalariesListPage row click; back button clears it.
  var [year,setYear]   = useState(new Date().getFullYear());
  var [month,setMonth] = useState(new Date().getMonth() + 1);
  var [data,setData]   = useState(null);
  var [attendance,setAttendance] = useState([]);
  var [companyOffDays,setCompanyOffDays] = useState([]);
  var [loading,setLoading] = useState(false);
  var [error,setError] = useState("");
  // Phase 5C — Day Override modal state.
  var [ovrOpen,setOvrOpen]     = useState(false);
  var [ovrDate,setOvrDate]     = useState(null);   // Date object for the row clicked
  var [ovrCurrent,setOvrCurrent] = useState(null); // existing attendance doc for that day
  var [ovrAction,setOvrAction] = useState("cancel_deduction");
  var [ovrFraction,setOvrFraction] = useState(0.25);
  var [ovrReason,setOvrReason] = useState("");
  var [ovrSaving,setOvrSaving] = useState(false);
  var [ovrError,setOvrError]   = useState("");
  // Phase 5D — Edit Base Salary modal state.
  var [bsOpen,setBsOpen]       = useState(false);
  var [bsValue,setBsValue]     = useState("");
  var [bsEffective,setBsEffective] = useState("");
  var [bsReason,setBsReason]   = useState("");
  var [bsSaving,setBsSaving]   = useState(false);
  var [bsError,setBsError]     = useState("");
  // Phase 5E — Finalize / Unlock state.
  var [finBusy,setFinBusy] = useState(false);
  var [finError,setFinError] = useState("");
  // Phase 6 — Audit log modal state.
  var [auditOpen,setAuditOpen] = useState(false);
  var [auditRows,setAuditRows] = useState([]);
  var [auditLoading,setAuditLoading] = useState(false);
  var [auditError,setAuditError] = useState("");

  var refetch = useCallback(function(){
    if (!p.salaryViewUserId || !p.token) return;
    setLoading(true); setError("");
    Promise.all([
      apiFetch("/api/salary/users/"+p.salaryViewUserId+"/"+year+"/"+month, "GET", null, p.token),
      apiFetch("/api/attendance/users/"+p.salaryViewUserId+"/month?year="+year+"&month="+month, "GET", null, p.token)
    ]).then(function(results){
      setData(results[0]);
      var monthData = results[1] || {};
      setAttendance(Array.isArray(monthData.attendance) ? monthData.attendance : []);
      setCompanyOffDays(Array.isArray(monthData.companyOffDays) ? monthData.companyOffDays : []);
    }).catch(function(err){
      setError((err && err.message) || "Failed to load");
    }).finally(function(){ setLoading(false); });
  },[p.salaryViewUserId, p.token, year, month]);

  useEffect(function(){ refetch(); },[refetch]);

  // Live update on WS attendance_updated for the displayed user.
  useEffect(function(){
    var handler = function(e){
      if (!e || !e.detail) return;
      if (String(e.detail.userId) !== String(p.salaryViewUserId)) return;
      // Refetch — the salary engine + per-day log both depend on attendance.
      refetch();
    };
    window.addEventListener("crm:attendance_updated", handler);
    return function(){ window.removeEventListener("crm:attendance_updated", handler); };
  }, [p.salaryViewUserId, refetch]);

  // Phase 5E — refetch when the salary is finalized/unlocked elsewhere.
  useEffect(function(){
    var handler = function(e){
      if (!e || !e.detail) return;
      if (String(e.detail.userId) !== String(p.salaryViewUserId)) return;
      refetch();
    };
    window.addEventListener("crm:salary_finalized", handler);
    return function(){ window.removeEventListener("crm:salary_finalized", handler); };
  }, [p.salaryViewUserId, refetch]);

  var monthLabel = new Date(year, month-1, 1).toLocaleDateString("en-GB", { month:"long", year:"numeric" });
  var prevMonth = function(){ if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  var nextMonth = function(){ if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  var goBack = function(){ if (p.setSalaryViewUserId) p.setSalaryViewUserId(null); };

  if (loading && !data) {
    return <div style={{padding:32, textAlign:"center", color:C.textLight, fontSize:13}}>Loading salary sheet…</div>;
  }
  if (error) {
    return <div style={{padding:24, fontFamily:"inherit"}}>
      <div style={{maxWidth:1200, margin:"0 auto"}}>
        <button type="button" onClick={goBack} style={{fontSize:12, padding:"6px 12px", border:"0.5px solid rgba(0,0,0,0.1)", background:"transparent", borderRadius:8, cursor:"pointer", marginBottom:12, fontFamily:"inherit"}}>‹ Salaries</button>
        <div style={{background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)", padding:24, textAlign:"center", color:C.danger, fontSize:13}}>
          {error}
        </div>
      </div>
    </div>;
  }
  if (!data) return null;

  var s = data.salary || {};
  var t = data.target || {};
  var finalized = !!data.finalized;

  // Build daily log: every Cairo day in the month + lookup of attendance.
  var daysInMonth = new Date(year, month, 0).getDate();
  var attByKey = {};
  attendance.forEach(function(a){
    var d = new Date(a.date);
    attByKey[d.getUTCFullYear()+"-"+(d.getUTCMonth()+1)+"-"+d.getUTCDate()] = a;
  });
  // Same key shape (year-month-day, NOT zero-padded) as attByKey so a single
  // dayKey looks both up. Mirrors the server-side coffSet in salary calc.
  var coffMap = {};
  companyOffDays.forEach(function(o){
    var d = new Date(o.date);
    coffMap[d.getUTCFullYear()+"-"+(d.getUTCMonth()+1)+"-"+d.getUTCDate()] = o.name || "Off Day";
  });

  // Mirror of server-side isSaturdayWorkday (server.js around line 4676) so
  // the calendar can label off-Saturdays without an extra round-trip. Saved
  // saturdayPatternStartDate is a YYYY-MM-DD string → UTC midnight Date, and
  // row.date is also UTC midnight, so UTC parts on both line up with Cairo.
  var isSaturdayWorkdayFE = function(dateUTC){
    var sched = (t && t.saturdaySchedule) || "always_work";
    if (sched === "always_work") return true;
    if (sched === "always_off")  return false;
    if (sched === "alternating") {
      if (!t.saturdayPatternStartDate) return true;
      var anchor = new Date(t.saturdayPatternStartDate);
      var anchorUTC  = Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), anchor.getUTCDate());
      var weeksDiff = Math.round((dateUTC.getTime() - anchorUTC) / (7 * 86400000));
      return Math.abs(weeksDiff) % 2 === 0;
    }
    return true;
  };

  var rows = [];
  for (var d = 1; d <= daysInMonth; d++) {
    var dateUTC = new Date(Date.UTC(year, month - 1, d));
    var dayKey  = year + "-" + month + "-" + d;
    var att = attByKey[dayKey] || null;
    rows.push({ date: dateUTC, dayKey: dayKey, attendance: att });
  }

  var deductionFor = function(att){
    if (!att) return null; // computed at row level using off-day detection
    return Number(att.deductionFraction || 0) * Number(s.dailyRate || 0);
  };

  // Floor of today (UTC midnight) — rows with row.date > this are future and
  // not clickable for override. Re-derived on every render so it stays fresh
  // around the day boundary.
  var todayUTCKey = (function(){
    var n = new Date();
    return Date.UTC(n.getFullYear(), n.getMonth(), n.getDate());
  })();

  // Sick-warning preview (Phase 5C). Counts existing override rows in this
  // month with a sick keyword. The 3rd one tips the modal into a red banner.
  var SICK_RE_FE = /sick|مرض|illness/i;
  var sickPreviewCount = (attendance || []).filter(function(a){
    if (!a.override || !a.override.action || !a.override.reason) return false;
    if (ovrDate && a.date && new Date(a.date).getTime() === ovrDate.getTime()) return false;
    return SICK_RE_FE.test(a.override.reason);
  }).length;
  var sickWarning = SICK_RE_FE.test(ovrReason || "") && sickPreviewCount >= 2;

  var pad2 = function(n){ return n < 10 ? "0" + n : "" + n; };

  // Phase 6 — open audit log modal and fetch rows for this user.
  var openAuditLog = function(){
    setAuditOpen(true);
    setAuditError("");
    setAuditLoading(true);
    // NO CEILING — a per-user audit trail that stops at 200 hides the oldest
    // salary changes, which are exactly the ones a dispute reaches back for.
    fetchAllPages("/api/audit?targetUserId=" + p.salaryViewUserId, p.token)
      // timestamp desc = the endpoint's own order, restored over the whole book
      // (the keyset walk delivers _id order). See byDatesDesc.
      .then(function(r){ setAuditRows(Array.isArray(r) ? r.slice().sort(byDatesDesc(["timestamp"])) : []); })
      .catch(function(err){ setAuditError((err && err.message) || "Failed to load"); setAuditRows([]); })
      .finally(function(){ setAuditLoading(false); });
  };

  // Phase 6 — CSV export of the daily log + summary metrics for the displayed
  // (user, month). Fully client-side — no extra round-trip, no PDF library.
  var doExportCsv = function(){
    var fmtDateUTC = function(d){
      return d.toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric", timeZone:"UTC" });
    };
    var lines = [];
    lines.push("Employee,Role,Year,Month,Base salary (EGP),Working days,Daily rate (EGP),Deduction days,Total deductions (EGP),Net salary (EGP),Finalized");
    lines.push([
      JSON.stringify(t.name || ""),
      JSON.stringify(t.role || ""),
      year, month,
      Math.round(s.baseSalary || 0),
      s.workingDays != null ? s.workingDays : "",
      Math.round(s.dailyRate || 0),
      (s.deductionDays != null ? Number(s.deductionDays).toFixed(2) : ""),
      Math.round(s.totalDeductions || 0),
      Math.round(s.netSalary || 0),
      finalized ? "yes" : "no"
    ].join(","));
    lines.push("");
    lines.push("Date,Day,Check-in,Check-out,Auto-closed,Status,Override reason,Deduction (EGP)");
    rows.forEach(function(row){
      var weekday = row.date.toLocaleDateString("en-GB", { weekday:"short", timeZone:"UTC" });
      var a = row.attendance;
      var hasOverride = a && a.override && a.override.action;
      var status;
      if (hasOverride) status = "Override";
      else if (a) status = a.status;
      else if (weekday === "Fri") status = "friday_off";
      else if (weekday === "Sat" && !isSaturdayWorkdayFE(row.date)) status = "saturday_off";
      else if (coffMap[row.dayKey]) status = "company_off:" + coffMap[row.dayKey];
      else status = "absent";
      var ovReason = hasOverride ? (a.override.reason || "") : "";
      var ded = a ? Math.round((Number(a.deductionFraction) || 0) * Number(s.dailyRate || 0)) : 0;
      lines.push([
        fmtDateUTC(row.date),
        weekday,
        a && a.checkIn ? fmtCairoTime(a.checkIn.timestamp) : "",
        a && a.checkOut ? fmtCairoTime(a.checkOut.timestamp) : "",
        // Auto-closed days carry the role's shift end, not a real check-out.
        a && a.autoClosed ? "yes" : "",
        status,
        JSON.stringify(ovReason),
        ded
      ].join(","));
    });
    var csv = lines.join("\n");
    var blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url;
    a.download = "salary-" + (t.name || "employee").replace(/\s+/g,"_") + "-" + year + "-" + (month < 10 ? "0" + month : month) + ".csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(function(){ URL.revokeObjectURL(url); }, 1000);
  };

  var doFinalize = async function(){
    var monthLabelLocal = new Date(year, month-1, 1).toLocaleDateString("en-GB", { month:"long", year:"numeric" });
    var msg = "Finalize " + monthLabelLocal + " for " + (t.name || "this employee") + "?\n\nThis locks the salary calculation and prevents further attendance, override, and finalize edits for the month. Owner can unlock if needed.";
    if (!window.confirm(msg)) return;
    setFinBusy(true); setFinError("");
    try {
      await apiFetch("/api/salary/"+p.salaryViewUserId+"/"+year+"/"+month+"/finalize", "POST", null, p.token, p.csrfToken);
      refetch();
    } catch (err) {
      setFinError((err && err.message) || "Finalize failed");
    } finally { setFinBusy(false); }
  };
  var doUnlock = async function(){
    var monthLabelLocal = new Date(year, month-1, 1).toLocaleDateString("en-GB", { month:"long", year:"numeric" });
    if (!window.confirm("Unlock " + monthLabelLocal + " for " + (t.name || "this employee") + "?\n\nLive computation will resume and admins can edit attendance again.")) return;
    setFinBusy(true); setFinError("");
    try {
      await apiFetch("/api/salary/"+p.salaryViewUserId+"/"+year+"/"+month+"/unlock", "POST", null, p.token, p.csrfToken);
      refetch();
    } catch (err) {
      setFinError((err && err.message) || "Unlock failed");
    } finally { setFinBusy(false); }
  };

  var openBaseSalaryModal = function(){
    setBsValue(s.baseSalary != null ? String(s.baseSalary) : "");
    setBsEffective("");
    setBsReason("");
    setBsError("");
    setBsOpen(true);
  };
  var submitBaseSalary = async function(){
    var newSalary = Number(bsValue);
    if (!isFinite(newSalary) || newSalary < 0) {
      setBsError("Enter a non-negative number");
      return;
    }
    setBsSaving(true); setBsError("");
    try {
      var body = { newSalary: newSalary };
      if (bsEffective) body.effectiveDate = bsEffective;
      if (bsReason)    body.reason = bsReason.trim();
      await apiFetch("/api/users/"+p.salaryViewUserId+"/base-salary", "PATCH", body, p.token, p.csrfToken);
      setBsOpen(false);
      refetch();
    } catch (err) {
      setBsError((err && err.message) || "Save failed");
    } finally {
      setBsSaving(false);
    }
  };

  var submitOverride = async function(){
    if ((ovrReason || "").trim().length < 10) {
      setOvrError("Reason must be at least 10 characters");
      return;
    }
    setOvrSaving(true); setOvrError("");
    try {
      var dateStr = ovrDate.getUTCFullYear() + "-" + pad2(ovrDate.getUTCMonth() + 1) + "-" + pad2(ovrDate.getUTCDate());
      var body = {
        userId: p.salaryViewUserId,
        date: dateStr,
        action: ovrAction,
        reason: (ovrReason || "").trim()
      };
      if (ovrAction === "apply_fraction") body.fraction = Number(ovrFraction);
      await apiFetch("/api/attendance/override", "POST", body, p.token, p.csrfToken);
      setOvrOpen(false);
      // WS event will refetch, but call directly for snappy local update too.
      refetch();
    } catch (err) {
      setOvrError((err && err.message) || "Save failed");
    } finally {
      setOvrSaving(false);
    }
  };

  var actionBtn = {
    fontSize:12, padding:"8px 14px", border:"0.5px solid rgba(0,0,0,0.15)",
    background:"transparent", borderRadius:8, cursor:"not-allowed",
    fontFamily:"inherit", color:C.textLight, opacity:0.5
  };

  return <div style={p.embedded ? {} : {padding:"24px 16px 40px", fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
    <div style={{maxWidth:1200, margin:"0 auto"}}>
      <button type="button" onClick={goBack} style={{fontSize:12, padding:"6px 12px", border:"0.5px solid rgba(0,0,0,0.1)", background:"transparent", borderRadius:8, cursor:"pointer", marginBottom:12, fontFamily:"inherit", color:C.text}}>‹ Salaries</button>

      {/* Header */}
      <div style={{background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)", overflow:"hidden", marginBottom:14}}>
        <div style={{padding:"18px 22px", display:"flex", alignItems:"center", gap:14, flexWrap:"wrap", borderBottom:"0.5px solid rgba(0,0,0,0.06)"}}>
          <div style={{width:48, height:48, borderRadius:24, background:"#1a2942", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:600}}>
            {(t.name || "?")[0]}
          </div>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:16, fontWeight:600, color:C.text}}>{t.name || "—"}</div>
            <div style={{fontSize:12, color:C.textLight, marginTop:2}}>
              {t.role || ""}{t.teamName ? " · " + t.teamName : ""}{t.startingDate ? " · started " + new Date(t.startingDate).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" }) : ""}
            </div>
          </div>
          <div style={{display:"flex", gap:8, alignItems:"center"}}>
            <button type="button" onClick={prevMonth} style={{border:"0.5px solid rgba(0,0,0,0.1)", background:"transparent", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontFamily:"inherit"}}>‹</button>
            <div style={{fontSize:13, fontWeight:500, color:C.text, minWidth:120, textAlign:"center"}}>{monthLabel}</div>
            <button type="button" onClick={nextMonth} style={{border:"0.5px solid rgba(0,0,0,0.1)", background:"transparent", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontFamily:"inherit"}}>›</button>
            {finalized && <span style={{fontSize:10, padding:"3px 10px", borderRadius:6, background:"#F3E8FF", color:"#7C3AED", fontWeight:600}}>FINALIZED</span>}
          </div>
        </div>

        {/* 4 metric cards */}
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:1, background:"rgba(0,0,0,0.06)"}}>
          <div style={{background:"#fff", padding:"16px 20px"}}>
            <div style={{fontSize:11, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.3px", marginBottom:6}}>Base salary</div>
            <div style={{fontSize:18, fontWeight:600, color:C.text}}>{fmtEGPRaw(s.baseSalary)} <span style={{fontSize:12, color:C.textLight, fontWeight:400}}>EGP</span></div>
          </div>
          <div style={{background:"#fff", padding:"16px 20px"}}>
            <div style={{fontSize:11, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.3px", marginBottom:6}}>Working days</div>
            <div style={{fontSize:18, fontWeight:600, color:C.text}}>{s.workingDays != null ? s.workingDays : "—"}</div>
            <div style={{fontSize:11, color:C.textLight, marginTop:2}}>Daily rate · {fmtEGPRaw(s.dailyRate)} EGP</div>
          </div>
          <div style={{background:"#fff", padding:"16px 20px"}}>
            <div style={{fontSize:11, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.3px", marginBottom:6}}>Deductions</div>
            <div style={{fontSize:18, fontWeight:600, color: s.totalDeductions > 0 ? C.danger : C.text}}>
              {fmtEGPRaw(s.totalDeductions)} <span style={{fontSize:12, color:C.textLight, fontWeight:400}}>EGP</span>
            </div>
            <div style={{fontSize:11, color:C.textLight, marginTop:2}}>{(s.deductionDays || 0).toFixed(2).replace(/\.00$/, "")} days</div>
          </div>
          <div style={{background:"#fff", padding:"16px 20px"}}>
            <div style={{fontSize:11, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.3px", marginBottom:6}}>Net salary</div>
            <div style={{fontSize:18, fontWeight:600, color:"#15803D"}}>{fmtEGPRaw(s.netSalary)} <span style={{fontSize:12, color:C.textLight, fontWeight:400}}>EGP</span></div>
          </div>
        </div>

        {/* Action bar — Edit base salary wired in Phase 5D; Audit + Export
            land in Phase 6; Finalize in Phase 5E */}
        <div style={{padding:"14px 22px", display:"flex", gap:8, flexWrap:"wrap", background:"#FAFAF7", borderTop:"0.5px solid rgba(0,0,0,0.06)"}}>
          {data.access && data.access.canEdit && !finalized
            ? <button type="button" onClick={openBaseSalaryModal}
                style={{fontSize:12, padding:"8px 14px", border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent", borderRadius:8, cursor:"pointer", fontFamily:"inherit", color:C.text}}>Edit base salary</button>
            : <button type="button" disabled style={actionBtn} title={finalized ? "Month finalized — unlock first" : "You don't have permission to edit this user's salary"}>Edit base salary</button>}
          <button type="button" onClick={openAuditLog}
            style={{fontSize:12, padding:"8px 14px", border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent", borderRadius:8, cursor:"pointer", fontFamily:"inherit", color:C.text}}>Audit log</button>
          <button type="button" onClick={doExportCsv}
            style={{fontSize:12, padding:"8px 14px", border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent", borderRadius:8, cursor:"pointer", fontFamily:"inherit", color:C.text}}>Export CSV</button>
          <div style={{flex:1}}/>
          {finError && <span style={{fontSize:11, color:C.danger, alignSelf:"center"}}>{finError}</span>}
          {finalized
            ? <>
                <span style={{fontSize:12, color:"#7C3AED", fontWeight:600, alignSelf:"center"}}>
                  ✓ Finalized {data.finalizedAt ? "· " + fmtCairoDate(data.finalizedAt) : ""}
                </span>
                {p.cu && p.cu.role === "admin" && <button type="button" onClick={doUnlock} disabled={finBusy}
                  style={{fontSize:12, padding:"8px 14px", border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent", borderRadius:8, cursor:finBusy?"not-allowed":"pointer", fontFamily:"inherit", color:C.text, opacity:finBusy?0.6:1}}>
                  {finBusy ? "Unlocking…" : "Unlock"}
                </button>}
              </>
            : (data.access && data.access.canEdit
              ? <button type="button" onClick={doFinalize} disabled={finBusy}
                  style={{fontSize:12, padding:"8px 16px", border:"none", background:"#185FA5", color:"#fff", borderRadius:8, cursor:finBusy?"not-allowed":"pointer", fontWeight:600, fontFamily:"inherit", opacity:finBusy?0.6:1}}>
                  {finBusy ? "Finalizing…" : "Finalize " + monthLabel}
                </button>
              : <button type="button" disabled style={Object.assign({}, actionBtn, { background:"#185FA5", color:"#fff", borderColor:"#185FA5", opacity:0.45 })} title="You don't have permission to finalize this user's salary">
                  Finalize {monthLabel}
                </button>)}
        </div>
      </div>

      {/* Daily log */}
      <div style={{background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)", overflow:"hidden"}}>
        <div style={{padding:"14px 20px", borderBottom:"0.5px solid rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:14, fontWeight:600, color:C.text}}>Daily log</div>
          <div style={{fontSize:12, color:C.textLight, marginTop:2}}>Click any row to open the override modal (wired in Phase 5C)</div>
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%", borderCollapse:"collapse", fontSize:13}}>
            <thead>
              <tr style={{background:"#F7F7F5"}}>
                <th style={{textAlign:"left",  padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Date</th>
                <th style={{textAlign:"left",  padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Day</th>
                <th style={{textAlign:"left",  padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Check-in</th>
                <th style={{textAlign:"left",  padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Check-out</th>
                <th style={{textAlign:"left",  padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Status</th>
                <th style={{textAlign:"right", padding:"10px 16px", fontWeight:500, color:C.textLight, fontSize:11, textTransform:"uppercase", letterSpacing:"0.3px"}}>Deduction</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(function(row){
                var weekday = row.date.toLocaleDateString("en-GB", { weekday:"short", timeZone:"UTC" });
                var a = row.attendance;
                var hasOverride = a && a.override && a.override.action;
                var off = null;
                if (!a) {
                  if (weekday === "Fri") off = "friday";
                  else if (weekday === "Sat" && !isSaturdayWorkdayFE(row.date)) off = "saturday";
                  else if (coffMap[row.dayKey]) off = "company:" + coffMap[row.dayKey];
                }
                var badge = attStatusBadge(a && a.status, off);
                if (hasOverride) badge = { label:"Override", bg:"#F3E8FF", color:"#7C3AED" };
                var ded = deductionFor(a);
                var rowClickable = data.access && data.access.canEdit && !finalized && row.date.getTime() <= todayUTCKey;
                var openModalForThisRow = function(){
                  if (!rowClickable) return;
                  setOvrDate(row.date);
                  setOvrCurrent(a);
                  setOvrAction(a && a.override && a.override.action ? a.override.action : "cancel_deduction");
                  setOvrFraction(a && a.override && a.override.action === "apply_fraction" && a.deductionFraction ? Number(a.deductionFraction) : 0.25);
                  setOvrReason(a && a.override && a.override.reason ? a.override.reason : "");
                  setOvrError("");
                  setOvrOpen(true);
                };
                return <tr key={row.date.toISOString()} onClick={openModalForThisRow}
                  style={{borderTop:"0.5px solid rgba(0,0,0,0.05)", cursor: rowClickable ? "pointer" : "default", background: hasOverride ? "#FFFBEB" : "transparent"}}>
                  <td style={{padding:"10px 16px"}}>{row.date.toLocaleDateString("en-GB", { day:"2-digit", month:"short", timeZone:"UTC" })}</td>
                  <td style={{padding:"10px 16px", color:C.textLight}}>{weekday}</td>
                  <td style={{padding:"10px 16px"}}>{a && a.checkIn ? fmtCairoTime(a.checkIn.timestamp) : "—"}</td>
                  <td style={{padding:"10px 16px"}}>{attCheckOutCell(a)}</td>
                  <td style={{padding:"10px 16px"}}>
                    <span style={{display:"inline-block", padding:"3px 10px", borderRadius:6, fontSize:11, fontWeight:500, background:badge.bg, color:badge.color}}>{badge.label}</span>
                    {hasOverride && a.override.reason && <div style={{fontSize:11, color:C.textLight, marginTop:3}}>{a.override.reason}</div>}
                  </td>
                  <td style={{padding:"10px 16px", textAlign:"right", color: ded > 0 ? C.danger : C.text}}>
                    {ded != null && ded > 0 ? fmtEGP(ded) : (a ? "0 EGP" : "—")}
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </div>

      {ovrOpen && <div className="crm-modal" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 }}>
        <div className="crm-modal-inner" style={{ background:"#fff", borderRadius:12, maxWidth:520, width:"100%", padding:24, fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", maxHeight:"90vh", overflowY:"auto" }}>
          <div style={{ fontSize:16, fontWeight:600, marginBottom:6, color:C.text }}>Override day</div>
          <div style={{ fontSize:12, color:C.textLight, marginBottom:16 }}>
            <div><b>{t.name || "—"}</b></div>
            <div>{ovrDate ? ovrDate.toLocaleDateString("en-GB", { weekday:"long", day:"2-digit", month:"long", year:"numeric", timeZone:"UTC" }) : ""}</div>
            <div style={{marginTop:4}}>
              Current: {ovrCurrent
                ? (ovrCurrent.status + (ovrCurrent.deductionFraction ? " · " + ovrCurrent.deductionFraction + " day deduction" : ""))
                : "Absent (full day deduction)"}
            </div>
          </div>

          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, color:C.textLight, marginBottom:8, fontWeight:500 }}>Action</div>
            {[
              { value:"cancel_deduction",   label:"Cancel deduction (no charge)" },
              { value:"add_full_deduction", label:"Add full day deduction" },
              { value:"mark_off_day",       label:"Mark as off-day" },
              { value:"apply_fraction",     label:"Apply specific deduction fraction" }
            ].map(function(opt){
              return <label key={opt.value} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", cursor:"pointer", fontSize:13, color:C.text }}>
                <input type="radio" name="ovrAction" value={opt.value}
                  checked={ovrAction === opt.value}
                  onChange={function(e){ setOvrAction(e.target.value); }}/>
                {opt.label}
              </label>;
            })}
            {ovrAction === "apply_fraction" && <div style={{ display:"flex", gap:8, marginTop:6, marginLeft:24 }}>
              {[0, 0.25, 0.5, 1].map(function(f){
                return <label key={f} style={{ display:"flex", alignItems:"center", gap:4, fontSize:12, color:C.text, cursor:"pointer" }}>
                  <input type="radio" name="ovrFraction" value={f}
                    checked={Number(ovrFraction) === f}
                    onChange={function(){ setOvrFraction(f); }}/>
                  {f === 0.25 ? "¼" : f === 0.5 ? "½" : f === 1 ? "1 day" : "0"}
                </label>;
              })}
            </div>}
          </div>

          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:12, color:C.textLight, display:"block", marginBottom:6 }}>Reason (min 10 characters)</label>
            <textarea value={ovrReason} onChange={function(e){ setOvrReason(e.target.value); }}
              rows={3} placeholder="e.g. Sick — prescription received on WhatsApp"
              style={{ width:"100%", padding:10, border:"0.5px solid rgba(0,0,0,0.15)", borderRadius:8, fontSize:13, fontFamily:"inherit", resize:"vertical", boxSizing:"border-box" }}/>
          </div>

          {sickWarning && <div style={{ background:"#FEE2E2", border:"0.5px solid #FCA5A5", borderRadius:8, padding:"10px 12px", marginBottom:12, fontSize:12, color:"#991B1B", lineHeight:1.5 }}>
            ⚠ This would be {t.name}'s 3rd+ sick day this month — exceeds the 2-day limit. You can save anyway, but it will be flagged in the audit log.
          </div>}

          {ovrError && <div style={{ fontSize:12, color:C.danger, marginBottom:12 }}>{ovrError}</div>}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button type="button" onClick={function(){ setOvrOpen(false); }} disabled={ovrSaving}
              style={{ fontSize:13, padding:"9px 16px", border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent", borderRadius:8, cursor:"pointer", fontFamily:"inherit", color:C.text }}>Cancel</button>
            <button type="button" onClick={submitOverride} disabled={ovrSaving}
              style={{ fontSize:13, padding:"9px 16px", border:"none", background:"#185FA5", color:"#fff", borderRadius:8, cursor:"pointer", fontWeight:600, fontFamily:"inherit" }}>
              {ovrSaving ? "Saving…" : "Save override"}
            </button>
          </div>
        </div>
      </div>}

      {bsOpen && <div className="crm-modal" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 }}>
        <div className="crm-modal-inner" style={{ background:"#fff", borderRadius:12, maxWidth:480, width:"100%", padding:24, fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
          <div style={{ fontSize:16, fontWeight:600, marginBottom:6, color:C.text }}>Edit base salary</div>
          <div style={{ fontSize:12, color:C.textLight, marginBottom:16 }}>
            <div><b>{t.name || "—"}</b> · current: {fmtEGP(s.baseSalary)}</div>
          </div>

          <label style={{ fontSize:12, color:C.textLight, display:"block", marginBottom:6 }}>New base salary (EGP)</label>
          <input type="number" min="0" step="1" value={bsValue}
            onChange={function(e){ setBsValue(e.target.value); }}
            style={{ width:"100%", padding:10, border:"0.5px solid rgba(0,0,0,0.15)", borderRadius:8, fontSize:13, fontFamily:"inherit", boxSizing:"border-box", marginBottom:12 }}/>

          <label style={{ fontSize:12, color:C.textLight, display:"block", marginBottom:6 }}>Effective date <span style={{ color:C.textLight, fontWeight:400 }}>(audit only)</span></label>
          <input type="date" value={bsEffective}
            onChange={function(e){ setBsEffective(e.target.value); }}
            style={{ width:"100%", padding:10, border:"0.5px solid rgba(0,0,0,0.15)", borderRadius:8, fontSize:13, fontFamily:"inherit", boxSizing:"border-box", marginBottom:12 }}/>
          <div style={{ fontSize:11, color:C.textLight, marginTop:-8, marginBottom:12, lineHeight:1.4 }}>
            Stored in the audit log. Salary calculations use the latest value immediately — phased effective dates are not yet implemented.
          </div>

          <label style={{ fontSize:12, color:C.textLight, display:"block", marginBottom:6 }}>Reason <span style={{ color:C.textLight, fontWeight:400 }}>(optional)</span></label>
          <textarea value={bsReason} onChange={function(e){ setBsReason(e.target.value); }}
            rows={2} placeholder="e.g. Promotion to senior; quarterly raise"
            style={{ width:"100%", padding:10, border:"0.5px solid rgba(0,0,0,0.15)", borderRadius:8, fontSize:13, fontFamily:"inherit", resize:"vertical", boxSizing:"border-box" }}/>

          {bsError && <div style={{ fontSize:12, color:C.danger, marginTop:8 }}>{bsError}</div>}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
            <button type="button" onClick={function(){ setBsOpen(false); }} disabled={bsSaving}
              style={{ fontSize:13, padding:"9px 16px", border:"0.5px solid rgba(0,0,0,0.15)", background:"transparent", borderRadius:8, cursor:"pointer", fontFamily:"inherit", color:C.text }}>Cancel</button>
            <button type="button" onClick={submitBaseSalary} disabled={bsSaving}
              style={{ fontSize:13, padding:"9px 16px", border:"none", background:"#185FA5", color:"#fff", borderRadius:8, cursor:"pointer", fontWeight:600, fontFamily:"inherit" }}>
              {bsSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>}

      {auditOpen && <div className="crm-modal" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 }}>
        <div className="crm-modal-inner" style={{ background:"#fff", borderRadius:12, maxWidth:720, width:"100%", padding:0, fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", maxHeight:"85vh", display:"flex", flexDirection:"column" }}>
          <div style={{ padding:"16px 22px", borderBottom:"0.5px solid rgba(0,0,0,0.06)", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
            <div>
              <div style={{ fontSize:15, fontWeight:600, color:C.text }}>Audit log · {t.name || "—"}</div>
              <div style={{ fontSize:12, color:C.textLight, marginTop:2 }}>Last 200 entries · newest first</div>
            </div>
            <button type="button" onClick={function(){ setAuditOpen(false); }}
              style={{ background:"transparent", border:"none", fontSize:20, cursor:"pointer", color:C.textLight, padding:4, lineHeight:1, fontFamily:"inherit" }}>×</button>
          </div>
          <div style={{ flex:1, overflowY:"auto", padding:"8px 22px 16px" }}>
            {auditLoading ? <div style={{ padding:32, textAlign:"center", color:C.textLight, fontSize:13 }}>Loading…</div>
              : auditError ? <div style={{ padding:24, textAlign:"center", color:C.danger, fontSize:13 }}>{auditError}</div>
              : auditRows.length === 0 ? <div style={{ padding:24, textAlign:"center", color:C.textLight, fontSize:13 }}>No audit entries for this employee yet.</div>
              : auditRows.map(function(r){
                  var actor = userNameOf(r.performedBy) || "—";
                  var when  = r.timestamp || r.createdAt;
                  var typeLabels = {
                    BASE_SALARY_CHANGED: "Base salary changed",
                    DAY_OVERRIDDEN: "Day overridden",
                    MONTH_FINALIZED: "Month finalized",
                    MONTH_UNLOCKED: "Month unlocked",
                    OFFSITE_APPROVED: "Off-site approved",
                    OFFSITE_REJECTED: "Off-site rejected",
                    ATTENDANCE_AUTO_CLOSED: "Day auto-closed",
                    ATTENDANCE_CHECKOUT_UNDONE: "Check-out undone",
                    ATTENDANCE_RESTORED: "Day restored"
                  };
                  var label = typeLabels[r.type] || r.type;
                  var detail = "";
                  if (r.type === "BASE_SALARY_CHANGED") {
                    detail = fmtEGPRaw((r.before || {}).baseSalary) + " EGP → " + fmtEGPRaw((r.after || {}).baseSalary) + " EGP";
                  } else if (r.type === "DAY_OVERRIDDEN") {
                    var act = (r.after || {}).override && r.after.override.action;
                    detail = act ? ("action: " + act) : "";
                  } else if (r.type === "ATTENDANCE_CHECKOUT_UNDONE") {
                    // THE CLEARED TIME IS THE POINT OF THIS ROW. Reading it must not
                    // mean opening the database — the whole cleared check-out lives in
                    // `before`, and the flags carry it pre-formatted for exactly here.
                    var f = r.flags || {};
                    detail = (f.date ? f.date + " · " : "")
                      + "cleared check-out " + (f.clearedCheckOutLabel || "—")
                      + (f.wasAutoClosed ? " (was auto-closed)" : "")
                      + (f.byOwner && !f.sameDay ? " · earlier day, by the Owner" : "");
                  } else if (r.type === "MONTH_FINALIZED" || r.type === "MONTH_UNLOCKED") {
                    var y = (r.after && r.after.year) || (r.before && r.before.year);
                    var m = (r.after && r.after.month) || (r.before && r.before.month);
                    if (y && m) detail = new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month:"long", year:"numeric" });
                  }
                  return <div key={String(r._id)} style={{ padding:"12px 0", borderBottom:"0.5px solid rgba(0,0,0,0.05)" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", gap:12, alignItems:"flex-start" }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:500, color:C.text }}>
                          {label}
                          {r.flags && r.flags.sickLimitExceeded && <span style={{ marginLeft:8, fontSize:10, padding:"2px 6px", borderRadius:4, background:"#FEE2E2", color:"#B91C1C", fontWeight:600 }}>SICK LIMIT</span>}
                          {r.flags && r.flags.bulk && <span style={{ marginLeft:8, fontSize:10, padding:"2px 6px", borderRadius:4, background:"#EEF2FF", color:"#4338CA", fontWeight:600 }}>BULK</span>}
                        </div>
                        {detail && <div style={{ fontSize:12, color:C.textLight, marginTop:2 }}>{detail}</div>}
                        {r.reason && <div style={{ fontSize:12, color:C.text, marginTop:4, padding:"6px 10px", background:"#F7F7F5", borderRadius:6, lineHeight:1.4 }}>{r.reason}</div>}
                      </div>
                      <div style={{ flexShrink:0, fontSize:11, color:C.textLight, textAlign:"right" }}>
                        <div>{actor}</div>
                        <div style={{ marginTop:2 }}>{when ? fmtDateTime12(when, undefined, { sep:", " }) : ""}</div>
                      </div>
                    </div>
                  </div>;
                })}
          </div>
        </div>
      </div>}
    </div>
  </div>;
};

// ===== DASHBOARD =====

// Isolated 1 Hz clock — owns its own setInterval so DashboardPage's heavy
// derivations don't recompute every second. Returns a string; the parent
// keeps its container styling.
var DashboardClock = function(p) {
  // eslint-disable-next-line no-unused-vars
  var [tick, setTick] = useState(0);
  useEffect(function(){
    var id = setInterval(function(){ setTick(function(t){ return t+1; }); }, 1000);
    return function(){ clearInterval(id); };
  },[]);
  var d = new Date();
  if (p.variant === "admin") {
    var dayNames = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
    var months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return dayNames[d.getDay()]+" "+d.getDate()+" "+months[d.getMonth()]+" "+d.getFullYear()+" — "+fmtTime12(d, p.t, { withSeconds: true });
  }
  return fmtTime12(d);
};

// Activity-row time column. toLocaleTimeString() constructs a fresh formatter
// on every call; on a Quarter filter the activity feed can render ~1000 rows and
// the formatter construction alone accounted for ~10s of a 26s click freeze
// (flamegraph: "exactTime" at App.js:7512). fmtTime12 keeps ONE module-level
// Intl instance (src/lib/timeFormat.js), so the same economy holds.
var _dashboardExactTime = function(d){
  if (!d) return "";
  return fmtTime12(d);
};

// Scroll-inside-a-card style. HOISTED to module scope 2026-08-29 when
// CallbackHandlingCard moved out of DashboardPage and took a reference to it
// with it. One definition, two scopes — a second copy is a second answer the
// day one of them is tuned.
var fillList = {flex:1,minHeight:0,overflowY:"auto",WebkitOverflowScrolling:"touch",marginRight:-6,paddingRight:6};

// ===== THE CALLBACK HANDLING CARD — ONE RENDERER, TWO DASHBOARDS ===========
//
// Lifted to module scope 2026-08-29. It had lived inline in the admin branch of
// DashboardPage, which returns before the agent branch ever reaches it — so the
// owner's ruling ("the card is for everyone, each person sees their own numbers")
// could not be satisfied by repointing the fetch alone. 26 of 29 accounts would
// have kept seeing nothing.
//
// The DATA decides what a person sees, not this component: the server scopes rows
// to the caller (callbackScopeIds), so an agent gets a one-row leaderboard that is
// their own and a manager gets their subtree. Nothing here branches on role, which
// is the point — a role test in the renderer would be a second visibility rule,
// and it would disagree with the server the first time the scope changed.
//
// Module scope so the gate can find it, same reason previewSees and
// confirmedRetryBody live out here.
// ===== THE KEY METRICS SUB-LINES (owner rulings R2 / R3 revised, 2026-09-02) =====
// Every count tile says "in period" — no percentages, no denominators, no
// derived numbers. Callbacks alone keeps "N overdue · N pending", because those
// are its own components, not a derivation.
// The Callbacks tile reads the merged trio (leads + Daily Requests) and shows
// handled / pending / overdue. `value` is "handled / due", the sub-line the
// remainder. null (no lead scope, failed fetch) renders an em dash, never 0.
function callbackTrioLabel(cb) {
  if (!cb || typeof cb !== "object") return { value: "—", sub: "" };
  var due = cb.due || 0, done = cb.done || 0, overdue = cb.overdue || 0;
  var pending = (typeof cb.pending === "number") ? cb.pending : Math.max(0, due - done - overdue);
  return { value: done + " / " + due, sub: overdue + " overdue · " + pending + " pending" };
}

var CallbackHandlingCard = function(p) {
  return <>
        <div style={{fontSize:15,fontWeight:700,color:"#0F172A",marginBottom:12}}>Callback Handling</div>
        {(function(){
        // STEP 4-2 — aggregation moved to /api/reports/callback-compliance
        // (fetched in the useEffect above into p.data). The previous
        // in-memory loop scanned every assignment of every lead in p.leads
        // plus every DR in p.dailyReqs — once STEP 4-5 shrinks the bootstrap
        // those scans would silently miss most rows. Server-side aggregation
        // owns the current-assignment-only rule (see server.js
        // /api/reports/callback-compliance handler).
        if (!p.data) return <div style={{fontSize:12,color:"#94A3B8",padding:"10px 0",textAlign:"center"}}>Loading…</div>;
        var sumScheduled = p.data.sumScheduled || 0;
        var sumHandled = p.data.sumHandled || 0;
        var sumMissed = p.data.sumMissed || 0;
        var sumDue = p.data.sumDue || 0;
        var sumPending = Math.max(0, sumScheduled - sumDue);
        var complianceRate = p.data.complianceRate || 0;
        var leaderboard = p.data.leaderboard || [];
        var THIN_DUE = 5;      // rates on < 5 due callbacks are too thin to trust (greyed + "thin")
        var BADGE_MIN_DUE = 10; // the ⚠ "highest risk" badge needs a solid sample, not just worst position
        // The loudest warning must not sit on the weakest data. Fire ⚠ ONLY on a
        // genuinely poor rate (<60%, the red band) backed by a solid sample
        // (>= BADGE_MIN_DUE due), and only on the worst such agent — never merely
        // because someone is at position 1 on a thin/borderline sample. If no
        // agent qualifies (e.g. today's worst solid-sample agent is at 81%), no
        // badge shows at all.
        var riskUid = (function(){ var w=null; leaderboard.forEach(function(x){ if(x.rate!==null && x.due>=BADGE_MIN_DUE && x.rate<60 && x.missed>0 && (!w||x.rate<w.rate)) w=x; }); return w?w.uid:null; })();
        var rateColor = function(rate){ return rate>=80?"#10B981":rate>=60?"#F59E0B":"#DC2626"; };
        var initialsOfName = function(n){return (n||"?").split(" ").slice(0,2).map(function(x){return x[0];}).join("").toUpperCase();};
        // "Due " + the resolver's own label, so a month picked from the
        // dropdown reads "Due in July 2026" instead of the catch-all.
        var pk = dashPeriodOf(p.filter);
        var pLbl = dateFilterLabel(pk.key, pk.o) || "Period";
        // "Today" / "This Week" / "Last Week" / "This Month" already read as a
        // time phrase and take a bare "Due"; a NAMED month or quarter needs the
        // preposition ("Due in July 2026"). Before the month dropdown those
        // both fell through to the catch-all "Due in Period".
        var filterLabel = pk.key==="day" ? ("Due on " + pLbl) : pk.key==="range" ? ("Due from " + pLbl.replace(" \u2013 ", " to ")) : /^(Today|Yesterday|This |Last )/.test(pLbl) ? ("Due " + pLbl) : ("Due in " + pLbl);
        return <>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
            <div style={{background:"#EFF6FF",borderRadius:10,padding:10,textAlign:"center"}}><div style={{fontSize:20,fontWeight:800,color:"#1D4ED8"}}>{sumDue}</div><div style={{fontSize:10,fontWeight:600,color:"#3B82F6"}}>{filterLabel}</div><div style={{fontSize:9,color:"#93C5FD",marginTop:1}}>of {sumScheduled} scheduled</div></div>
            <div style={{background:"#F0FDF4",borderRadius:10,padding:10,textAlign:"center"}}><div style={{fontSize:20,fontWeight:800,color:"#15803D"}}>{complianceRate}%</div><div style={{fontSize:10,fontWeight:600,color:"#22C55E"}}>Handled</div></div>
          </div>
          <div style={{display:"flex",gap:6,marginBottom:12,fontSize:11}}>
            <div style={{flex:1,padding:"6px 8px",background:"#F0FDF4",borderRadius:8,display:"flex",justifyContent:"space-between"}}><span style={{color:"#15803D"}}>Handled</span><span style={{fontWeight:700,color:"#15803D"}}>{sumHandled}</span></div>
            <div style={{flex:1,padding:"6px 8px",background:"#FEF2F2",borderRadius:8,display:"flex",justifyContent:"space-between"}}><span style={{color:"#991B1B"}}>Missed</span><span style={{fontWeight:700,color:"#DC2626"}}>{sumMissed}</span></div>
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginBottom:12}}><span style={{fontSize:10,color:"#94A3B8"}}>Worked vs missed — not timeliness</span><span title={"Based on "+sumDue+" due of "+sumScheduled+" scheduled; handled calls that clear their callback time drop out, so true scheduled is higher."} style={{fontSize:10,fontWeight:700,color:"#94A3B8",cursor:"help"}}>ⓘ</span></div>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}><span style={{fontSize:11,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:1}}>Worst first</span><span title="This leaderboard is sorted by handled rate, worst agent first." style={{fontSize:11,fontWeight:700,color:"#94A3B8",cursor:"help"}}>ⓘ</span></div>
          {leaderboard.length===0 ? <div style={{fontSize:12,color:"#94A3B8",padding:"10px 0",textAlign:"center",flex:1,minHeight:0}}>No agents to show</div> : <div style={fillList}>
            {leaderboard.map(function(x,i){
              var avBg=["#DBEAFE","#DCFCE7","#FEF3C7","#EDE9FE","#FFE4E6"][i%5];
              var avC=["#1D4ED8","#166534","#92400E","#5B21B6","#9F1239"][i%5];
              var thin = x.rate!==null && x.due < THIN_DUE;
              var rc = (x.rate===null || thin) ? "#94A3B8" : rateColor(x.rate);
              var isWorst = !!x.uid && x.uid===riskUid; // ⚠ only on the worst poor-rate, solid-sample agent
              return <div key={x.uid||i} onClick={function(){ if(p.onPickAgent) p.onPickAgent(x.uid); }} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:i<leaderboard.length-1?"1px solid #F8FAFC":"none",cursor:"pointer"}}>
                <div style={{fontSize:11,color:"#888",width:16,textAlign:"center",fontWeight:600,flexShrink:0}}>{i+1}</div>
                <div style={{width:28,height:28,borderRadius:"50%",background:avBg,color:avC,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>{initialsOfName(x.name)}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:600,color:"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{x.name}{isWorst?<span style={{marginLeft:6,fontSize:10,fontWeight:700,color:"#DC2626"}}>⚠ Highest risk</span>:null}</div>
                  <div style={{height:4,borderRadius:2,background:"#F1F5F9",marginTop:3}}><div style={{height:"100%",width:(x.rate===null?0:Math.min(100,x.rate))+"%",background:rc,borderRadius:2}}/></div>
                </div>
                <div style={{textAlign:"right",flexShrink:0,minWidth:64}}>
                  <div style={{fontSize:13,fontWeight:700,color:rc}}>{x.rate===null?"—":x.rate+"%"}{x.rate!==null?<span style={{fontSize:9,fontWeight:600,color:"#94A3B8"}}> n={x.due}</span>:null}{thin?<span style={{fontSize:9,fontWeight:700,color:"#B45309"}}> thin</span>:null}</div>
                  <div style={{fontSize:10,color:"#94A3B8"}}>{x.rate===null?(x.pending?x.pending+" pending":"no due callbacks"):(x.handled+" handled · "+x.missed+" missed"+(x.pending?" · "+x.pending+" pending":""))}</div>
                </div>
              </div>;
            })}
          </div>}
        </>;
        })()}
  </>;
};


var DashboardPage = function(p) {
  useLeadSourcesVersion();   // re-render when the lead source registry lands (src/lib/leadSources.js)
  // Live-refresh wave (2026-09-13): Key Metrics tiles, sparklines, Campaign &
  // Source, Untouched Leads and the sales-side callback strip used to need a
  // full page reload (or, for the callback strip, up to STALE_REFETCH_MS/30s)
  // to notice a lead/DR/EOI/deal/meeting/callback change — all of those write
  // through PUT/POST /api/leads* or /api/daily-requests*, which already
  // broadcast lead_updated/dr_updated (see server.js's broadcast middleware),
  // so no new WS event is needed. dashStaleKey throttles that burst to one
  // bump per 2s (see useStaleRefetchKey) — a dashboard card is a cheap
  // single-scope read, not the full-book refetch STALE_REFETCH_MS's 30s
  // throttle exists to guard on Deals/EOI/Leads, so it can afford to be this
  // much faster.
  var dashStaleKey = useStaleRefetchKey(p.cbBust, 2000);
  // P5 (2026-11-02): the 60s / 90s safety polls on the live-refresh cards are
  // gone. Lead / DR bursts still arrive through dashStaleKey; everything else
  // the cards derive from — deal and EOI records, commissions, meetings, the
  // activity feed — now arrives as an id-only frame and bumps this rev, and a
  // reconnect / seq gap / dead-socket fallback resyncs it (crm:live_resync).
  // The 300s agent-perf poll stays: that aggregate is deliberately off cbBust.
  var dashLiveRev = useLiveRev(["deal", "eoi", "commission", "meeting", "activity"]);
  // Shared visibility gate for the effects below: no fetch (WS-driven or
  // poll) while the tab is hidden.
  var pageVisible = usePageVisible();
  // Ref mirror of pageVisible, for the one effect (kpi-series, below) whose
  // dependency array is pinned byte-for-byte by verify-dashboard-matrix.js
  // and cannot take a new dependency — see the comment at that effect.
  var pageVisibleRef = useRef(pageVisible);
  useEffect(function(){ pageVisibleRef.current = pageVisible; }, [pageVisible]);
  // viewer gets the ADMIN-shaped dashboard, not the agent one. The agent shape
  // renders "My Leads"/"My Rank vs Team", which for the store-review account is
  // 0 and meaningless — it owns no leads, the demo agents do. Admin shape over
  // the sealed demo world shows the 38 demo leads and populated revenue cards.
  var isOnlyAdmin = p.cu.role==="admin"||p.cu.role==="operation"||p.cu.role==="viewer";
  // ===== FINANCE-ONLY DASHBOARD (owner ruling 2026-11-02) =====
  // A financeReads role (the accountant) gets greeting + date + the check-in
  // widget and NOTHING else: no tiles, Urgent, Schedule, status, funnel, feed,
  // callbacks or period chips — and none of the fetches behind them, so no 403
  // noise (kpi-series refuses the role; agent-perf / callback-compliance now
  // refuse it too). Flag-keyed: roleHasFinanceReadsFE is true for exactly one
  // role (verify-role-registry pins it), so every other role's effects and
  // render are byte-identical — verify-dashboard-finance-only.js renders both
  // trees per role to prove it.
  var isFinanceOnlyDash = roleHasFinanceReadsFE(p.cu);
  var [filter, setFilter] = useState("today");
  var [qOpen, setQOpen] = useState(false);
  var quarterDropdownRef = useRef(null);
  // The Month dropdown — its own open state and ref, mirroring the Quarter one
  // above rather than sharing it, so opening either closes nothing by accident.
  var [mOpen, setMOpen] = useState(false);
  var monthDropdownRef = useRef(null);
  // Earliest month the dropdown may offer, derived from the caller's own data.
  // null until the request lands — dashMonthOptions falls back to a year back.
  var [monthFloor, setMonthFloor] = useState(null);
  // Same flip-aware positioning as every other menu (both render branches below
  // share this one state/ref). ~5 rows at ~33px, so 200 is the real panel height.
  // .dir only — the quarter panels set their own left/right and width.
  var qAnchor = dropAnchor(useDropDirection(qOpen, quarterDropdownRef, 200).dir);
  // 264 is the month panel's own max height (see renderMonthPanel).
  var mAnchor = dropAnchor(useDropDirection(mOpen, monthDropdownRef, 264).dir);
  // Year being browsed inside the quarter panel. Seeded from the active filter
  // each time the panel opens (see the toggle buttons), so reopening lands on
  // the year you are actually looking at rather than snapping back to today.
  var [qYear, setQYear] = useState(new Date().getFullYear());
  // Quarter picker panel — a compact YEAR select plus four FIXED quarter
  // buttons, replacing a flat "Q1 2026 … Q4 2025" list that grew by four rows
  // every year and would have been 12 entries next year. Selection still writes
  // the same "Q<n> <year>" filter string, so every downstream consumer (range
  // math, labels, the active-state check on the trigger) is untouched.
  // The green dot marks the CURRENT quarter, so it only appears while the
  // selected year is the current one.
  var renderQuarterPanel = function(years, onPick){
    var nowD2 = new Date(), nowQ = Math.floor(nowD2.getMonth()/3)+1, nowY = nowD2.getFullYear();
    var yr = (years.indexOf(qYear) >= 0) ? qYear : years[0];
    var lbl = { fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.3px" };
    return <div style={{ padding:10, minWidth:200 }}>
      <div style={Object.assign({ marginBottom:5 }, lbl)}>Year</div>
      <SearchableSelect noSearch hideEmpty value={String(yr)}
        onChange={function(v){ setQYear(Number(v)); }}
        options={years.map(function(y){ return { value:String(y), label:String(y) }; })}
        width="100%" maxWidth={9999} fontSize={12}/>
      <div style={Object.assign({ margin:"10px 0 5px" }, lbl)}>Quarter</div>
      <div style={{ display:"flex", gap:5 }}>
        {[1,2,3,4].map(function(q){
          var label = "Q"+q+" "+yr;
          var isActive = filter===label;
          var isCurrent = (q===nowQ && yr===nowY);
          return <button key={q} type="button" onClick={function(){ onPick(label); }}
            style={{ flex:1, position:"relative", padding:"7px 0", borderRadius:8, cursor:"pointer",
              fontSize:12, fontWeight:isActive?700:600, fontFamily:"inherit",
              border:"1px solid "+(isActive?"#3B82F6":"#E2E8F0"),
              background:isActive?"#EFF6FF":"#fff", color:isActive?"#1D4ED8":"#334155" }}>
            {"Q"+q}
            {isCurrent&&<span title="Current quarter" style={{ position:"absolute", top:3, right:3, width:6, height:6, borderRadius:"50%", background:"#22C55E" }}/>}
          </button>;
        })}
      </div>
    </div>;
  };
  // Seed qYear from the active filter whenever the panel is opened.
  var openQuarterPanel = function(){
    var m = /^Q[1-4] (\d{4})$/.exec(String(filter||""));
    if (m) setQYear(Number(m[1]));
    setQOpen(!qOpen);
  };
  // Month picker panel — a flat list, CURRENT FIRST, back to the derived floor.
  // Flat rather than the Quarter panel's year-select + fixed buttons because a
  // month list is what the owner asked for and the data floor bounds it (five
  // entries on 2026-08-30; the earliest lead is 2026-04). It scrolls once it
  // outgrows the box, so it cannot push the header off screen.
  var renderMonthPanel = function(){
    return <div style={{ padding:8, minWidth:170, maxHeight:264, overflowY:"auto" }}>
      {dashMonthOptions(monthFloor).map(function(o){
        var isActive = (filter === o.value);
        return <button key={o.value} type="button"
          onClick={function(){ setFilter(o.value); setMOpen(false); }}
          style={{ display:"block", width:"100%", textAlign:"left", position:"relative",
            padding:"7px 10px", marginBottom:2, borderRadius:8, cursor:"pointer",
            fontSize:12, fontWeight:isActive?700:600, fontFamily:"inherit",
            border:"1px solid "+(isActive?"#3B82F6":"transparent"),
            background:isActive?"#EFF6FF":"#fff", color:isActive?"#1D4ED8":"#334155" }}>
          {o.label}
          {o.isCurrent&&<span title="Current month" style={{ position:"absolute", top:"50%", right:8, marginTop:-3, width:6, height:6, borderRadius:"50%", background:"#22C55E" }}/>}
        </button>;
      })}
    </div>;
  };
  // First click selects the CURRENT month and opens the list — so the control
  // still answers "this month" in one click, the way the chip it replaced did,
  // while the list is right there to pick an earlier one. Same shape as the
  // shared date-filter bar's Quarter chip.
  var openMonthPanel = function(){
    if (!dashIsMonth(filter)) setFilter(dashCurrentMonthValue());
    setMOpen(!mOpen);
  };
  var [todayActivities, setTodayActivities] = useState(null);
  var [rwData, setRwData] = useState(null);
  var [seeAllOpen, setSeeAllOpen] = useState(false);
  // Mobile layout flag — updates on resize. Desktop styles are untouched; mobile just overrides specific rules.
  var [isMobile, setIsMobile] = useState(typeof window!=="undefined" && window.innerWidth<768);
  // The date control (one day, or from -> to), rendered in BOTH headers (sales + admin). Pickable days
  // run from the caller's data floor (first of the earliest month with data, the
  // same floor the Month dropdown uses) to today, Cairo.
  var dayPickerEl = <DashRangePicker isMobile={isMobile}
    value={dashRangeValue(filter)} active={dashRangeValue(filter).from !== ""}
    triggerStyle={Object.assign(periodChipStyle(dashRangeValue(filter).from !== "", PERIOD_ACCENT_BLUE, isMobile), { width:isMobile?"100%":"auto" })}
    min={monthFloor ? (monthFloor + "-01") : ""} todayYmd={dfYmd(new Date())}
    onPick={function(a, b){ setFilter(dashRangeFilterOf(a, b)); }}/>;
  // STEP 4-2 — callback-card state. Sales-side (urgent / schedule / overdueCount)
  // for the !isOnlyAdmin branch; compliance leaderboard for the admin branch.
  // Fetched from /api/leads/callbacks + /api/reports/callback-compliance so the
  // cards don't depend on the in-memory p.leads + p.dailyReqs scans which
  // shrink under STEP 4-5.
  var [cbSalesState, setCbSalesState] = useState({ overdue: [], scheduled: [] });
  var [cbCompliance, setCbCompliance] = useState(null);
  // STEP 4-3 — admin dashboard tile counts. null until first fetch resolves;
  // the destructuring at the render site falls back to the in-memory
  // adminMetrics values while loading, so existing numbers stay visible
  // through the first paint. Once STEP 4-5 shrinks the bootstrap, the
  // in-memory fallback becomes wrong but this state stays authoritative.
  // Management Alerts, server-side. GET /api/dashboard/alerts counts over the
  // full Lead collection; the rows below used to scan `leads` (= p.leads),
  // which since the STEP 4-5 X3 bootstrap shrink holds only the 100
  // newest-created leads — so every row undercounted heavily and drifted as
  // the user scrolled LeadsPage. Current-state counts: NOT bounded by the
  // page's period filter, matching the prior client behaviour.
  var [alertsData, setAlertsData] = useState(null);
  // True untouched total. /api/leads/untouched caps its list at 50, so the
  // "untouched leads" row can't be derived from that list's length.
  var [untouchedTotal, setUntouchedTotal] = useState(null);
  // Leads by Status — full-collection byStatus from /api/leads/counts. Its own
  // fetch rather than reusing statsCounts because this card's lead pool
  // excludes Daily Request mirror rows (the `leads` memo filters them out) and
  // adding excludeSource to the shared statsCounts call would silently change
  // the Campaign & Source Performance card, which reads byCampaign from it.
  var [statusDist, setStatusDist] = useState(null);
  // Campaign & Source Performance rows from /api/reports/campaigns/performance:
  // resolves development/unit per lead vintage (v1 keeps the development in
  // `campaign`, v2 in `project`) and merges name variants (" - Copy", trailing
  // numbers, month suffixes) so one campaign is one row. The old source
  // (/api/leads/counts byCampaign) grouped the raw campaign|project|source
  // triple, so "Naia Sahel", "Naia Sahel - Copy" and "Naia Sahel 2" were three
  // separate low-count rows and the two columns meant different things
  // depending on which vintage a lead was created under.
  var [campaignPerf, setCampaignPerf] = useState(null);
  // The card's cohort-wide source totals (the legend), straight from the endpoint.
  var [campaignSources, setCampaignSources] = useState(null);
  // The "Other campaigns (N)" roll-up row: expanded inline to list the rolled-up campaigns.
  var [otherOpen, setOtherOpen] = useState(false);
  // STEP 4-5 X2 — server-side AgentPerf table from /api/dashboard/agent-perf.
  // Sole source for the card: the in-memory agentPerfMemo fallback is gone,
  // so null means "not resolved yet" and the card renders a loading row
  // rather than flashing the old pre-fix numbers.
  var [agentPerfState, setAgentPerfState] = useState(null);
  useEffect(function(){
    var onResize = function(){ setIsMobile(window.innerWidth<768); };
    window.addEventListener("resize", onResize);
    return function(){ window.removeEventListener("resize", onResize); };
  },[]);
  // Close the Quarter dropdown when clicking outside its wrapper
  useEffect(function(){
    var handleClickOutside = function(e){
      if (quarterDropdownRef.current && !quarterDropdownRef.current.contains(e.target)) {
        setQOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return function(){ document.removeEventListener("mousedown", handleClickOutside); };
  },[]);
  // Same, for the Month dropdown.
  useEffect(function(){
    var handleClickOutside = function(e){
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(e.target)) {
        setMOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return function(){ document.removeEventListener("mousedown", handleClickOutside); };
  },[]);
  // The month floor. Current-state, not period-bound, so it takes no filter
  // dependency and is fetched once per token.
  useEffect(function(){
    if (!p.token) return;
    // HR and office_boy get the attendance-only dashboard, which has no period
    // picker at all — same early return the other period fetches take.
    if (p.cu.role === "hr" || p.cu.role === "office_boy") return;
    if (isFinanceOnlyDash) return;   // finance-only dashboard: no dashboard fetch (owner ruling 2026-11-02)
    var cancelled = false;
    apiFetch("/api/dashboard/period-bounds","GET",null,p.token)
      .then(function(r){ if (!cancelled && r && r.earliestMonth) setMonthFloor(r.earliestMonth); })
      .catch(function(){ /* keep the fallback list — never leave the picker empty */ });
    return function(){ cancelled = true; };
  },[p.token]);

  // STEP 4-2 — fetch sales-side callback cards (urgent / schedule /
  // overdueCount). Range is computed from `filter` to match the legacy
  // rangeStartS / rangeEndS expressions at App.js (now-replaced section).
  // Gated on !isOnlyAdmin so admin's dashboard doesn't issue these fetches
  // (admin renders the CallbackCompliance card instead — separate fetch
  // below).
  useEffect(function(){
    if (p.cu.role === "hr") return;
    if (!p.token) return;
    if (isOnlyAdmin) return;
    if (isFinanceOnlyDash) return;   // finance-only dashboard: no dashboard fetch (owner ruling 2026-11-02)

    var cancelled = false;
    var load = function(){
      // Range from the ONE resolver (dashPeriod). This block used to derive it
      // inline off the DEVICE clock; it is Cairo now, which for this staff is
      // the same wall clock and therefore the same window.
      var w = dashPeriod(filter);
      var fromIso = new Date(w.from).toISOString();
      var toIso   = new Date(w.soFar).toISOString();
      var nowIso  = new Date().toISOString();
      // Two ROW fetches only. The third request — an all-time count_only for
      // the status card's "Overdue" — is gone: that number is the tile's own
      // period-scoped overdue now (R4), already in myStats.
      Promise.all([
        apiFetch("/api/leads/callbacks?to="+encodeURIComponent(nowIso)+"&limit=5&order=asc","GET",null,p.token).catch(function(){return [];}),
        apiFetch("/api/leads/callbacks?from="+encodeURIComponent(fromIso)+"&to="+encodeURIComponent(toIso)+"&limit=7&order=asc","GET",null,p.token).catch(function(){return [];})
      ]).then(function(r){
        if (cancelled) return;
        setCbSalesState({
          overdue:    Array.isArray(r[0]) ? r[0] : [],
          scheduled:  Array.isArray(r[1]) ? r[1] : []
        });
      });
    };
    load();
    return function(){ cancelled = true; };
  // dashStaleKey, NOT p.cbBust — see useStaleRefetchKey. Every websocket burst
  // used to re-run all three requests.
  //
  // NOT FOLDED INTO /api/leads/callback-counts, deliberately. Only ONE of these
  // three is a count; the other two fetch the ROWS this card renders (limit=5
  // overdue, limit=7 scheduled). The combined endpoint answers six counts, so
  // routing a single count through it would run six queries to get one number —
  // more server work, not less. The win here is the burst throttle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, dashStaleKey, filter, isOnlyAdmin]);

  // STEP 4-2 — the CallbackCompliance leaderboard, for EVERY dashboard. It hits
  // /api/dashboard/callback-compliance, which scopes rows to the caller
  // (callbackScopeIds: admin/operation see all, a leader sees their subtree, an
  // agent sees themselves), so one fetch serves both cards.
  //
  // IT WAS GATED `if (!isOnlyAdmin) return;` UNTIL NOW, AND THAT MADE THE CARD
  // BLANK FOR EVERY NON-ADMIN. The 2026-08-29 ruling — "the card is for
  // everyone, each person sees their own numbers" — shipped its server door
  // (the scoped route) and its renderer (lifted to module scope, rendered in
  // both dashboards), and this line quietly withheld the data: cbCompliance
  // stayed null, so CallbackHandlingCard rendered its "Loading…" branch forever
  // on the sales dashboard. Half a ruling reads exactly like a slow network.
  //
  // hr is the one role skipped, matching the sibling my-stats / sales-ranking
  // effect above: it renders neither dashboard, so the request would be spent on
  // a card nobody is looking at.
  useEffect(function(){
    if (!p.token) return;
    if (p.cu.role === "hr") return;
    if (isFinanceOnlyDash) return;   // finance-only dashboard: no dashboard fetch (owner ruling 2026-11-02)
    var cancelled = false;
    var load = function(){
      // The compliance card asks "of the callbacks DUE in this period, how many
      // were handled" — a question about the WHOLE period, including the rest
      // of today and the rest of the week. So it takes periodEnd, not soFar.
      var w = dashPeriod(filter);
      var fromIso = new Date(w.from).toISOString();
      var toIso   = new Date(w.periodEnd).toISOString();
      apiFetch("/api/dashboard/callback-compliance?from="+encodeURIComponent(fromIso)+"&to="+encodeURIComponent(toIso),"GET",null,p.token)
        .then(function(r){ if (!cancelled) setCbCompliance(r||null); })
        .catch(function(){ if (!cancelled) setCbCompliance(null); });
    };
    load();
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, p.cbBust, filter]);

  // The /api/leads/counts + /api/daily-requests/counts fetch that lived here is
  // gone. It ran 14 countDocuments + 3 aggregations on EVERY filter change and
  // nothing rendered the result: the Key Metrics tiles read adminStats
  // (/api/dashboard/admin-stats), and byCampaign — its last real consumer — was
  // replaced by /api/reports/campaigns/performance.

  // Management Alerts + the true untouched total. Both are current-state, so
  // neither takes the period filter — only cbBust, so lead/DR mutations
  // refresh the badges in real time.
  useEffect(function(){
    if (!p.token) return;
    if (!isOnlyAdmin) return;
    var cancelled = false;
    Promise.all([
      apiFetch("/api/dashboard/alerts","GET",null,p.token).catch(function(){return null;}),
      apiFetch("/api/leads/untouched?count_only=true","GET",null,p.token).catch(function(){return null;})
    ]).then(function(r){
      if (cancelled) return;
      setAlertsData(r[0] || {});
      setUntouchedTotal(r[1] && typeof r[1].count === "number" ? r[1].count : null);
    });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, p.cbBust, isOnlyAdmin]);

  // Leads by Status — full-collection distribution for the active period.
  // Same range derivation as the statsCounts fetch above so the card keeps the
  // period behaviour it already had; excludeSource drops the Daily Request
  // mirror rows, matching the card's existing lead pool.
  useEffect(function(){
    if (!p.token) return;
    if (!isOnlyAdmin) return;
    var cancelled = false;
    setStatusDist(null);
    // Whole period (periodEnd), matching what this card has always sent: a
    // distribution over the period, not over "so far".
    var w = dashPeriod(filter);
    var qs = "excludeSource=" + encodeURIComponent("Daily Request") +
             "&from=" + encodeURIComponent(new Date(w.from).toISOString()) +
             "&to="   + encodeURIComponent(new Date(w.periodEnd).toISOString());
    apiFetch("/api/leads/counts?" + qs, "GET", null, p.token)
      .then(function(r){ if (!cancelled) setStatusDist((r && r.byStatus) ? r.byStatus : {}); })
      .catch(function(){ if (!cancelled) setStatusDist({}); });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, p.cbBust, filter, isOnlyAdmin]);

  // Fetch activities since the active period's start (not just today's 00:00)
  // — so Today's Activities / This Week's Activities / etc all have enough
  // data to render without another round-trip. Refreshes every 30s, and
  // re-fires whenever the filter changes so the card always matches the
  // Key Metrics row above it.
  useEffect(function(){
    if (!p.token) return;
    if (isFinanceOnlyDash) return;   // finance-only dashboard: no dashboard fetch (owner ruling 2026-11-02)
    var cancelled = false;
    var load = function(){
      // Only a START is sent — the feed is "everything since the period began".
      var ts = new Date(dashPeriod(filter).from);
      apiFetch("/api/activities?since="+encodeURIComponent(ts.toISOString())+"&limit=1000","GET",null,p.token)
        .then(function(d){ if(cancelled) return; var arr = (d&&d.data)||(Array.isArray(d)?d:[]); setTodayActivities(arr); })
        .catch(function(){ if(!cancelled && todayActivities===null) setTodayActivities([]); });
    };
    load();
    // 90s cadence (was 30s). The dashboard activity card doesn't need
    // sub-minute freshness; the parent's WebSocket activity_created
    // handler already replaces p.activities in real time when a new
    // activity is logged. The polling here is a safety-net refresh.
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, filter, dashLiveRev]);
  // RESPONSE WATCH (replaces the Untouched Leads card). Server derivation in
  // response-watch.js; takes the dashboard period (assignedAt window from..now)
  // and refreshes on the same WS-driven stale key + 60s safety net as the card
  // it replaced. Old backlog stays in the Leads-page Untouched chip.
  useEffect(function(){
    if (!p.token) return;
    if (!isOnlyAdmin && positionOf(p.cu) !== "manager") return;
    if (!pageVisible) return;
    var cancelled = false;
    var w = dashPeriod(filter);
    var load = function(){
      apiFetch("/api/dashboard/response-watch?from="+encodeURIComponent(new Date(w.from).toISOString())+"&to="+encodeURIComponent(new Date(Math.max(w.toNow, w.from + 1)).toISOString()),"GET",null,p.token)
        .then(function(d){ if(cancelled) return; setRwData(d&&d.counts?d:{counts:{none:0,tapped:0},none:[],tapped:[],agents:[]}); })
        .catch(function(){ if(!cancelled && rwData===null) setRwData({counts:{none:0,tapped:0},none:[],tapped:[],agents:[]}); });
    };
    load();
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, dashStaleKey, pageVisible, filter, dashLiveRev]);
  // ── CRM-wide Sales ranking + per-agent KPI counts — both come from the
  //    backend so the dashboard works for any sales user (local p.leads only
  //    contains their own rows). Re-fetch whenever the period filter changes.
  var [salesRanking, setSalesRanking] = useState(null);
  var [myStats, setMyStats] = useState(null);
  // Admin-only Key Metrics row — backend returns the 6 KPI numbers so the
  // KPI cards render without waiting for the full /api/leads payload.
  // null = loading (show skeleton), {} = fetch failed or non-admin.
  var [adminStats, setAdminStats] = useState(null);
  // THE SPARKLINES ARE A SERVER SERIES. GET /api/dashboard/kpi-series answers
  // one bucket array per Key Metrics card with the tiles' own helpers and the
  // caller's own scope. null = not loaded (bars render flat). This replaced a
  // client scan that bucketed the 100-row bootstrap page by lead createdAt and
  // handed that single array to all six cards.
  //
  // KEYED ON THE PERIOD PICKER SINCE 2026-09-03. It used to be keyed only on
  // dashStaleKey and always asked for the last seven days ending today, so the
  // bars under a "Q2 2026" tile drew last week — a strip describing a different
  // period from the number above it. The window and the grain now come from
  // dashSeriesQuery, which is a pure function of (filter, monthFloor) and is
  // run by verify-dashboard-matrix.js for every period the picker can write.
  var [kpiSeries, setKpiSeries] = useState(null);
  useEffect(function(){
    if (p.cu.role === "hr" || p.cu.role === "office_boy") return;
    if (!p.token) return;
    if (isFinanceOnlyDash) return;   // finance-only dashboard: no dashboard fetch (owner ruling 2026-11-02)
    var cancelled = false;
    var load = function(){
      // pageVisible is read from the ref, not the dependency array, here:
      // verify-dashboard-matrix.js pins this effect's closing
      // `},[p.token, dashStaleKey, filter, monthFloor]);` byte-for-byte (it
      // proved a real bug where the strip kept the previous period's buckets
      // once dashStaleKey was the only WS-driven dependency), so a new
      // dependency can't be added without breaking that gate. The other
      // three live-refresh effects below take pageVisible directly and get
      // an instant catch-up fetch on refocus; this one catches up on its
      // next dashStaleKey bump or within the 60s poll.
      if (!pageVisibleRef.current) return;
      apiFetch("/api/dashboard/kpi-series?" + dashSeriesQuery(filter, monthFloor), "GET", null, p.token)
        .then(function(d){ if (!cancelled && d && Array.isArray(d.days)) setKpiSeries(d); })
        .catch(function(){ /* keep the last series; flat bars beat fabricated ones */ });
    };
    load();
    // 60s safety net — dashStaleKey (WS-driven, throttled to 2s) is the
    // primary trigger; this is only the fallback for a missed/lost event.
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, dashStaleKey, filter, monthFloor, dashLiveRev]);
  // One reader for every card: the bucket values plus the tiny labels the strip
  // under the bars renders, both from the server so the two cannot disagree
  // about how many buckets there are. Absent -> a flat 7-bar row.
  var kpiSpark = function(key){
    var v = kpiSeries && kpiSeries[key];
    var b = kpiSeries && kpiSeries.buckets;
    if (!Array.isArray(v) || !v.length) return { v:[0,0,0,0,0,0,0], labels:["","","","","","",""] };
    var labels = (Array.isArray(b) && b.length === v.length)
      ? b.map(function(x){ return (x && x.label) || ""; })
      : v.map(function(){ return ""; });
    return { v: v, labels: labels };
  };
  // Tracks the filter this effect last ran with, so a WS-driven re-run
  // (dashStaleKey below) can tell "the period changed" from "a lead moved" —
  // only the former should blank adminStats to a skeleton.
  var prevDashFilterRef = useRef(filter);
  useEffect(function(){
    if (p.cu.role === "hr") return;
    if (!p.token) return;
    if (!pageVisible) return;
    if (isFinanceOnlyDash) return;   // finance-only dashboard: no dashboard fetch (owner ruling 2026-11-02)

    var filterChanged = prevDashFilterRef.current !== filter;
    prevDashFilterRef.current = filter;
    var cancelled = false;
    var load = function(){
      // The active range, from the ONE resolver. Only `filter` is a dependency
      // because dashPeriod is pure in it.
      var w = dashPeriod(filter);
      var qs = "from="+encodeURIComponent(new Date(w.from).toISOString())+"&to="+encodeURIComponent(new Date(w.soFar).toISOString());
      // If the period is a Quarter, tell the backend which quarter so Target
      // lines up with the selected range; otherwise fall through to current Q.
      var statsQs = qs + (w.key==="quarter" ? "&quarter=Q"+dashPeriodOf(filter).o.quarter.q : "");
      apiFetch("/api/dashboard/sales-ranking?"+qs, "GET", null, p.token)
        .then(function(d){ if(!cancelled) setSalesRanking(Array.isArray(d)?d:[]); })
        .catch(function(){ if(!cancelled) setSalesRanking([]); });
      apiFetch("/api/dashboard/my-stats?"+statsQs, "GET", null, p.token)
        .then(function(d){ if(!cancelled) setMyStats(d||{}); })
        .catch(function(){ if(!cancelled) setMyStats({}); });
      // Admin Key Metrics: backend returns the 6 KPI numbers so the cards render
      // without depending on the full /api/leads array being parsed/derived.
      if (isOnlyAdmin) {
        apiFetch("/api/dashboard/admin-stats?"+qs, "GET", null, p.token)
          .then(function(d){ if(!cancelled) setAdminStats(d||{}); })
          .catch(function(){ if(!cancelled) setAdminStats({}); });
      }
    };
    // Cleared back to null only on a genuine period change, so the cards show
    // a skeleton while the new range loads (avoids stale numbers from the
    // prior filter) — a WS-driven re-run (dashStaleKey) must NOT blank the
    // Key Metrics row on every lead/DR event.
    if (isOnlyAdmin && filterChanged) setAdminStats(null);
    load();
    // 60s safety net, same reasoning as the other three live-refresh fetches.
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, filter, dashStaleKey, pageVisible, dashLiveRev]);
  var now = Date.now();
  // (`rangeMs`, a second filter -> duration map, stood here with NO reader. It
  // was the last period derivation outside dashPeriod and would have read as
  // the authority next time someone needed one. WEEK/MONTH went with it; DAY
  // still measures sparkline buckets and lead age below.)
  var DAY=86400000;

  var leads = useMemo(function(){
    return (p.leads||[]).filter(function(l){return !l.archived&&l.source!=="Daily Request";});
  },[p.leads]);

  // Active range used by the Agent Performance card. Both team-scope and admin
  // paths previously derived this inline with slightly different range-end
  // values (admin used end-of-period, team-scope used now). For past events —
  // which is everything on a CRM — the two derivations produce identical event
  // sets (filtering t<=reA where reA>=now is equivalent to filtering t<=now).
  // We derive once here so renderAgentPerformanceCard can consume a memoized
  // result instead of recomputing on every render.
  var agentPerfRange = useMemo(function(){
    var w = dashPeriod(filter);
    return { rsA: w.from, reA: w.soFar };
  },[filter]);

  // THE CALLBACKS TILE'S WINDOW — the deadline range it counts, derived ONCE
  // for the admin tile, the Management Alerts row and the sales KPI strip, from
  // the same dashPeriod resolver the stats fetches use. Both ends matter: `from`
  // is the period start, `soFar` is the end the whole strip takes (a day period
  // runs to the end of that day, a longer one in progress stops at now), so the
  // tile and the list it opens describe the same window as its siblings.
  var callbackWindow = useMemo(function(){
    var w = dashPeriod(filter);
    return { dueFrom: new Date(w.from).toISOString(),
             dueTo:   new Date(w.soFar).toISOString(),
             label:   w.label };
  },[filter]);
  // THE TILE OPENS WHAT IT COUNTS. ?overdue=1 narrowed to the SAME deadline
  // window — carried on the special filter so the Leads page can put it on the
  // query string (the rows are a server fetch; a client predicate over the
  // loaded window would render a handful of thousands) and apply it to its own
  // row predicate. p.setFilter("all") clears any status chip that would fight it.
  var openOverdueForPeriod = function(){
    if (p.setSpecialFilter) p.setSpecialFilter({ type: "overdue",
      dueFrom: callbackWindow.dueFrom, dueTo: callbackWindow.dueTo,
      periodLabel: callbackWindow.label });
    if (p.setFilter) p.setFilter("all");
    p.nav("leads");
  };
  // THE INTERESTED TILE OPENS WHAT IT COUNTS (R1): the Leads page with the ONE
  // Interested predicate (?interested=1, derived status HotCase / Potential read
  // per viewer) over the SAME period window the tile was counted on. Carried on
  // the special filter, exactly like the overdue window above.
  var openInterestedForPeriod = function(){
    if (p.setSpecialFilter) p.setSpecialFilter({ type: "interested",
      periodFrom: callbackWindow.dueFrom, periodTo: callbackWindow.dueTo,
      periodLabel: callbackWindow.label });
    if (p.setFilter) p.setFilter("all");
    p.nav("leads");
  };
  // THE MEETINGS TILE OPENS THE MEETINGS PAGE (R7): the archive is the
  // definition, so the click lands on its rows for the same window. Who may
  // open that page is the visibility matrix's answer (owner ruling 2026-09-02:
  // page:meetings stays owner-only) — so for a role the envelope refuses the
  // tile carries NO click-through at all: null here, and kpiCard renders no
  // pointer and navigates nowhere, rather than a click that lands on the
  // dashboard.
  var openMeetingsForPeriod = canSeePage(p.access, "meetings") ? function(){
    if (p.setMeetingsInit) p.setMeetingsInit({ from: callbackWindow.dueFrom, to: callbackWindow.dueTo, label: callbackWindow.label });
    p.nav("meetings");
  } : null;

  // THE DEALS TILE OPENS THE DEALS IT COUNTED (ruling 2026-09-07). It used to
  // nav("deals") bare, landing on the page's own default — the current QUARTER —
  // so a tile reading "This Month: 2" opened a list of 11. The tile counts deals
  // whose deal date falls in the selected period, and the Deals page's manual
  // From/To range filters on exactly that date (getDealDate) and takes
  // precedence over its Quarter + Year gates, so handing it the period's Cairo
  // day keys makes the click open the tile's own set.
  //
  // dfYmd, not toISOString().slice(0,10): the page compares date-only Cairo
  // days, and a period ending at 22:0x Cairo is already tomorrow in UTC — the
  // ISO form would hand the list a `to` a day past the one the tile counted.
  var dealsWindow = useMemo(function(){
    var w = dashPeriod(filter);
    return { from: dfYmd(new Date(w.from)), to: dfYmd(new Date(w.soFar)) };
  },[filter]);
  var openDealsForPeriod = function(){
    if (p.setDealsInit) p.setDealsInit({ from: dealsWindow.from, to: dealsWindow.to, approvedOnly: true });
    p.nav("deals");
  };
  // THE DAILY REQUESTS TILE OPENS THE REQUESTS IT COUNTED. It navigated bare, to
  // the page's "All" date range — a tile reading 89 (Today) opened 5,509. Same
  // one-shot hand-over as the Deals tile, over the same Cairo day keys.
  var openDrsForPeriod = function(){
    if (p.setDrInit) p.setDrInit({ from: dealsWindow.from, to: dealsWindow.to });
    p.nav("dailyReq");
  };
  // THE CALLBACKS TILE OPENS BOTH HALVES IT COUNTED. Leads -> the opener above
  // (the Leads page's overdue tile list); Daily Requests -> this: the Daily
  // Requests page narrowed to the same overdue-in-period set (?cbOverdue=1), over
  // the SAME window the tile counted on.
  var openDrOverdueForPeriod = function(){
    if (p.setDrInit) p.setDrInit({ cbOverdue: { type: "cbOverdue", from: callbackWindow.dueFrom, to: callbackWindow.dueTo,
                                                label: callbackWindow.label } });
    p.nav("dailyReq");
  };
  // The tile's sub-line: "Leads: N · DR: M" (overdue in the period), each count
  // tappable and opening exactly its own list; the pending remainder stays as
  // plain text. ONE node builder for the admin tile and the sales strip, so the
  // two dashboards cannot describe the same tile two ways. stopPropagation: the
  // card itself still opens the lead list, as it always did.
  var cbSplitNode = function(cb){
    var sp2 = callbackSplit(cb);
    var chip = function(label, n, open){
      var live = n !== null && open;
      return <span onClick={live ? function(e){ e.stopPropagation(); open(); } : undefined}
                   title={live ? "Open the " + label + " list" : ""}
                   style={{cursor:live?"pointer":"default",textDecoration:live?"underline":"none",textUnderlineOffset:2,fontWeight:700}}>
        {label}: {n === null ? "\u2014" : n}</span>;
    };
    return <span style={{whiteSpace:"normal"}}>
      {chip("Leads", sp2.leads, openOverdueForPeriod)}{" \u00b7 "}{chip("DR", sp2.dr, openDrOverdueForPeriod)}
      {sp2.pending !== null && <span style={{opacity:0.85}}>{" \u00b7 " + sp2.pending + " pending"}</span>}
    </span>;
  };
  // THE LEADS TILE OPENS THE LEADS IT COUNTED (the cohort created in the period).
  // It navigated bare, to the newest 100 of the whole book (40 -> 100, measured).
  var openLeadsForPeriod = function(){
    if (p.setSpecialFilter) p.setSpecialFilter({ type: "periodLeads",
      periodFrom: callbackWindow.dueFrom, periodTo: callbackWindow.dueTo,
      periodLabel: callbackWindow.label });
    if (p.setFilter) p.setFilter("all");
    p.nav("leads");
  };

  // MUST stay below the agentPerfRange assignment above: a dependency
  // array is evaluated at the useEffect CALL SITE on every render, so
  // reading agentPerfRange.rsA before the memo is assigned throws
  // (var hoists the declaration, not the value) and takes the whole
  // DashboardPage render down for EVERY role — the guards inside the
  // callback do not protect the deps.
  // period question, unlike the current-state alerts above).
  useEffect(function(){
    if (!p.token) return;
    if (!isOnlyAdmin) return;
    if (!pageVisible) return;
    var cancelled = false;
    var load = function(){
      // Do NOT reset to null here: the effect re-runs on every dashStaleKey
      // bump (WS-driven lead/DR event, throttled to 2s), and a null reset
      // blanks the card to "Loading…" between fetches — the flicker. Keep the
      // previous rows visible and swap them in on success, matching the Agent
      // Performance effect below. The initial load still shows "Loading…"
      // because campaignPerf starts null.
      apiFetch("/api/reports/campaigns/performance?from=" + agentPerfRange.rsA + "&to=" + agentPerfRange.reA, "GET", null, p.token)
        .then(function(r){ if (!cancelled) { setCampaignPerf((r && Array.isArray(r.campaigns)) ? r.campaigns : []); setCampaignSources((r && Array.isArray(r.sources)) ? r.sources : null); } })
        // On a FAILED refetch keep the rows we already have — a transient network
        // error must not blank the card. Only clear to [] if there was no data
        // yet (initial load), so the card leaves "Loading…" for "No campaign
        // data yet" instead of hanging. Functional updater avoids a stale
        // campaignPerf closure.
        .catch(function(){ if (!cancelled) setCampaignPerf(function(prev){ return prev === null ? [] : prev; }); });
    };
    load();
    // 60s safety net, same reasoning as kpi-series/untouched above.
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, dashStaleKey, agentPerfRange.rsA, agentPerfRange.reA, isOnlyAdmin, pageVisible, dashLiveRev]);

  // STEP 4-5 X2 — server-driven AgentPerf rows. Replaces the agentPerfMemo
  // in-memory scans (which would silently undercount post-X3 bootstrap
  // shrink). Endpoint requires admin/operation/TL/manager/director —
  // sales gets a 403 and we silently fall back to the in-memory result
  // (which never renders for sales anyway). Keyed on cbBust so any lead/DR
  // mutation refreshes the per-agent rows in real time.
  useEffect(function(){
    if (!p.token) return;
    if (p.cu && positionOf(p.cu) === "sales") return;
    if (isFinanceOnlyDash) return;   // finance-only dashboard: no dashboard fetch (owner ruling 2026-11-02)
    var cancelled = false;
    var rsA = agentPerfRange.rsA, reA = agentPerfRange.reA;
    var load = function(){
      apiFetch("/api/dashboard/agent-perf?from=" + rsA + "&to=" + reA, "GET", null, p.token)
        .then(function(r){ if (!cancelled && r && Array.isArray(r.rows)) setAgentPerfState(r.rows); })
        .catch(function(){});
    };
    load();
    // Deliberately NOT keyed on cbBust: this endpoint is heavy and agent
    // performance over a period needs no sub-minute freshness. Refresh on its
    // own every 5 minutes (mirrors the Untouched-leads card); the token and the
    // period filter (rsA/reA) in the deps still force an immediate refetch. A WS
    // lead event no longer re-runs it.
    var id = setInterval(load, 300000);
    return function(){ cancelled = true; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, agentPerfRange.rsA, agentPerfRange.reA]);

  // The in-memory agentPerfMemo that used to live here is gone. It scanned
  // `leads` (= p.leads, only the 100 newest-created leads since the STEP 4-5
  // X3 bootstrap shrink) and reproduced the OLD metric definitions: rotation
  // tallies matched by agent NAME and ignoring the period, plus a "response
  // time" that was really lastActionAt - lead.createdAt. It rendered in the
  // gap before /api/dashboard/agent-perf resolved, so the pre-fix numbers
  // flashed on every filter change. The card now waits for the server rows.

  var hourNow = new Date().getHours();
  var greeting = hourNow<6 ? "Good Night \ud83d\ude34" : hourNow<12 ? "Good Morning \u2600\ufe0f" : hourNow<18 ? "Good Afternoon \ud83c\udf24\ufe0f" : hourNow<24 ? "Good Evening \ud83c\udf19" : "Good Night \ud83d\ude34";

  // The overdueUntouched memo that scanned `leads` for the Management Alerts
  // "overdue callbacks" / "untouched leads" rows is gone: both rows now read
  // server counts (adminStats.overdue and /api/leads/untouched?count_only).

  var kpiCard=function(label,value,sub,bg,vc,onClick,spark){
    // spark: { v: number[], labels: string[] } from kpiSpark — the SELECTED
    // period's buckets, however many there are (7 days, a month's 31, a
    // quarter's 13 weeks, All's months). The strip below the bars is the
    // server's own bucket labels; it used to be a hardcoded weekday run off
    // `new Date().getDay()`, which named the wrong days the moment the series
    // stopped ending today. All-zero → a flat min-height row so the card never
    // renders a collapsed sparkline. The decorative preset is reached only by a
    // caller that passes nothing.
    var vals   = (spark && Array.isArray(spark.v) && spark.v.length) ? spark.v : null;
    var labels = (spark && Array.isArray(spark.labels)) ? spark.labels : [];
    var bars;
    if (vals) {
      var _mx = Math.max.apply(null, vals);
      bars = _mx>0 ? vals.map(function(n){return Math.max(12, Math.round(n/_mx*100));})
                   : vals.map(function(){return 12;});
    } else {
      bars = [30,45,55,40,65,70,85];
    }
    // The LAST bucket is the current one — it used to be hardcoded to index 6.
    var lastIdx = bars.length - 1;
    // A month or a quarter draws more bars than the card is wide; drop the
    // per-bucket labels rather than render an illegible smear.
    var showLabels = labels.length === bars.length && bars.length <= 16;
    // bg is now a CSS gradient string. vc is kept for the small spark-bar
    // and weekday strip below the number, since both inherit from the card
    // accent. Title/value/sub follow the new white-on-gradient spec.
    return <div onClick={onClick} style={{background:bg,borderRadius:16,padding:isMobile?"14px":"18px",cursor:onClick?"pointer":"default",minWidth:0,overflow:"hidden",boxShadow:"0 2px 10px rgba(0,0,0,0.08)"}}>
      <div style={{fontSize:13,fontWeight:600,color:"rgba(255,255,255,0.9)",textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</div>
      <div style={{fontSize:isMobile?28:36,fontWeight:800,color:"#ffffff",letterSpacing:"-0.02em",lineHeight:1,overflow:"hidden",textOverflow:"ellipsis"}}>{value}</div>
      <div style={{fontSize:12,color:"rgba(255,255,255,0.75)",fontWeight:500,marginTop:6,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{sub}</div>
      <div style={{display:"flex",alignItems:"flex-end",gap:2,height:20,marginTop:10}}>
        {bars.map(function(h,i){return <div key={i} style={{flex:1,borderRadius:2,height:h+"%",background:"#ffffff",opacity:i===lastIdx?0.85:0.28}}/>;})}</div>
      <div style={{display:"flex",gap:2,marginTop:3,minHeight:8}}>
        {showLabels && labels.map(function(d,i){return <div key={i} style={{flex:1,fontSize:"6px",textAlign:"center",color:"#ffffff",opacity:0.55}}>{d}</div>;})}</div>
    </div>;
  };

  var bRow=function(label,count,total2,color,onClick){
    var pct=total2>0?Math.max(2,Math.round(count/total2*100)):0;
    return <div onClick={onClick} style={{display:"flex",alignItems:"center",gap:8,marginBottom:7,cursor:onClick?"pointer":"default"}}>
      <div style={{fontSize:12,color:"#64748B",width:82,flexShrink:0}}>{label}</div>
      <div style={{flex:1,height:5,background:"#F1F5F9",borderRadius:3,overflow:"hidden"}}>
        <div style={{height:"100%",width:pct+"%",background:color,borderRadius:3}}/></div>
      <div style={{fontSize:12,fontWeight:600,color:"#334155",width:28,textAlign:"right"}}>{count}</div>
    </div>;
  };

  var card=function(children,extra){return <div className="crm-dash-card" style={Object.assign({background:"#fff",border:"1px solid #E2E8F0",borderRadius:16,padding:isMobile?"14px 14px":"20px 22px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",minWidth:0},extra||{})}>{children}</div>;};
  var sec=function(label){return <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",letterSpacing:"0.1em",textTransform:"uppercase",margin:"24px 0 12px"}}>{label}</div>;};
  // OUTER height of the bottom 4-card row (Management Alerts / Callback
  // Compliance / Rank Team / Leads by Status), applied via grid-auto-rows.
  //
  // Why a fixed row height is REQUIRED, not just inner max-heights: the row is
  // auto-sized, so each card's grid area is indefinite. A card whose list is
  // `flex:1; min-height:0; overflow-y:auto` (Rank Team) still contributes its
  // FULL content height to intrinsic row sizing — `min-height:0` only permits
  // shrinking into a DEFINITE height, and `height:100%` against an indefinite
  // grid area resolves as `auto`. So Rank Team's 19 agents defined the row and
  // its scrollbar never engaged, while cards whose inner lists WERE capped
  // ended short and opened a white gap underneath. Giving the row a definite
  // height makes every grid area definite, which is what finally lets each
  // card's list shrink and scroll.
  var ROW4_H = isMobile ? 380 : 420;
  // Cards in that row: flex columns so the list child can take the slack.
  // No explicit height — grid stretch sizes the border box to the row exactly,
  // which stays correct regardless of box-sizing.
  var fillCard = {display:"flex",flexDirection:"column",minHeight:0,boxSizing:"border-box"};
  // The one scrollable region inside each of those cards.
  // Conversion pill. Same geometry as qBadge (the "Quality" badge it replaces)
  // so the Campaign card's last column keeps its shape and alignment; only the
  // meaning changes, from a 30/15-threshold interested-% label that could not
  // read anything but "Low" on a short range, to the actual deals/leads rate.
  // `pct` decides the COLOUR, `label` is the text (item 4, 2026-09-03): the
  // badge used to print `pct+"%"` itself, so a campaign with real deals on a
  // large cohort read a green-less "0%". The label carries the one-decimal /
  // "<0.1%" rendering; a missing label falls back to the number so a caller
  // that passes only a pct still shows something honest.
  var convBadge=function(pct,label){
    var bg = pct === null ? "#F1F5F9" : pct > 0 ? "#DCFCE7" : "#F1F5F9";
    var fg = pct === null ? "#94A3B8" : pct > 0 ? "#166534" : "#64748B";
    return <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:6,background:bg,color:fg}}>{pct===null?"\u2014":(label||(pct+"%"))}</span>;
  };
  var qBadge=function(q){var m2={High:["#DCFCE7","#166534"],Medium:["#FEF3C7","#92400E"],Low:["#FEE2E2","#991B1B"]};var c2=m2[q]||m2.Low;return <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:6,background:c2[0],color:c2[1]}}>{q}</span>;};

  // Campaign Quality grade — restored as a column, but NOT with the old
  // formula. That one graded interested-% against fixed 30/15 cutoffs on every
  // range, so on Today/Yesterday (where almost nothing has progressed yet)
  // essentially every campaign read "Low" — which is why it was dropped in
  // 2ce138a. A single formula cannot be honest across a 1-day and a 90-day
  // window, so this grades in two modes:
  //   SHORT (Today / Yesterday / This Week) — deals and EOIs are simply too
  //     slow to have landed yet, so judging on them would grade every fresh
  //     campaign "Low" regardless of how good it is. Grade the early signal
  //     instead: did it deliver people who ANSWER (contact rate) and who show
  //     INTEREST (interested rate)?
  //   LONG (This Month / Quarter) — enough time has passed for real funnel
  //     progression, so grade what actually matters commercially: EOI rate
  //     (reached the EOI stage, including the ones already won) + conversion %.
  // Both of a mode's rates must clear the bar, so one strong number cannot
  // carry a campaign that is weak on the other.
  //
  // THRESHOLDS ARE PROVISIONAL — estimates, NOT yet calibrated against the live
  // collection. They were verified only against fixtures, which proves the two
  // modes grade on different evidence but NOT that a real campaign clears the
  // High bar. If the real rates run lower than these, the column repeats the
  // exact failure that got the old badge deleted: everything reads "Low".
  // Retuning is a two-line change — adjust the two constants below and nothing
  // else; the grading logic is threshold-agnostic. Worth a read-only pass over
  // leads grouped by campaign (interested/contacted/eoisReached/deals over
  // leads, on a 7-day and a quarter window) to set them from real data.
  //
  // NO MINIMUM SAMPLE SIZE — owner decision. Every campaign is graded, however
  // few leads it has, so a 1-lead campaign gets a real badge instead of a "—".
  // The tradeoff is deliberate and worth knowing when reading the column: on a
  // handful of leads a single lead moves a rate by 100/leads points, so a
  // 1-lead campaign can only ever score 0% or 100% on each rate and will read
  // High or Low with nothing in between. Small-sample rows are therefore noisy
  // by construction — the grade is honest about the data it has, not a claim
  // that the sample is sufficient. Sort by Leads to see which grades carry
  // weight. (A previous revision withheld a grade below 5 leads; removed here.)
  var CAMPAIGN_Q_SHORT = { high: { int: 25, con: 55 }, med: { int: 10, con: 30 } };
  var CAMPAIGN_Q_LONG  = { high: { eoi: 6,  conv: 3 }, med: { eoi: 2,  conv: 1 } };
  // Short modes are the ranges where the funnel has not had time to move. Keyed
  // on the KIND of period: "Last Week" is seven days and grades short exactly
  // like "This Week", which a string comparison would have graded LONG and so
  // marked most campaigns Low against EOI/conversion thresholds a week cannot
  // meet.
  var campaignQIsShort = (function(){ var k = dashPeriodOf(filter).key;
    return k === "today" || k === "yesterday" || k === "day" || k === "range" || k === "week" || k === "lastWeek"; })();
  var campaignQuality = function(c){
    // Guard only against divide-by-zero, NOT against small samples: a 0-lead
    // row (which the grouping should never emit) scores every rate at 0 and
    // grades Low rather than producing NaN comparisons.
    var leads = c.leads || 0;
    if (leads <= 0) return "Low";
    if (campaignQIsShort) {
      // Reads the fCamps row's own field names (int / contacted / eoi / deals),
      // NOT the raw endpoint names — the row renames interested -> int.
      var intPct = (c.int       || 0) / leads * 100;
      var conPct = (c.contacted || 0) / leads * 100;
      var s = CAMPAIGN_Q_SHORT;
      if (intPct >= s.high.int && conPct >= s.high.con) return "High";
      if (intPct >= s.med.int  && conPct >= s.med.con)  return "Medium";
      return "Low";
    }
    var eoiPct  = (c.eoi   || 0) / leads * 100;
    var convPct = (c.deals || 0) / leads * 100;
    var l = CAMPAIGN_Q_LONG;
    if (eoiPct >= l.high.eoi && convPct >= l.high.conv) return "High";
    if (eoiPct >= l.med.eoi  && convPct >= l.med.conv)  return "Medium";
    return "Low";
  };

  // Shared period labels used by the Campaign & Source Performance cohort line,
  // the Today's Activities card and the admin Rank Team widget. Now a thin
  // reader over the ONE resolver (dashPeriodLabel) rather than a second,
  // shorter vocabulary that answered "Today" for Last Week and for every month
  // the Month dropdown can write. See dashPeriodLabel at module scope.
  var periodLabelForFilter = dashPeriodLabel;
  // The Activities card's TITLE had the same fall-through, and it sits directly
  // above a subtitle this change corrects — leaving it would read
  // "Today's Activities" over "No activity in August 2026". Derived from the
  // resolver's label so the pair can only ever agree: a possessive for the
  // relative labels, "Activities <in month/quarter>" otherwise.
  var activitiesTitleForFilter = function(f){
    var l = dashPeriodLabel(f);
    if (!l) return "Activities";
    if (dashPeriodOf(f).key === "day") return "Activities on " + l;
    if (dashPeriodOf(f).key === "range") return "Activities from " + l.replace(" \u2013 ", " to ");
    return /^(Today|Yesterday|This |Last )/.test(l) ? (l + "'s Activities")
                                                    : ("Activities in " + l);
  };
  // Reusable Rank widget — used by BOTH sales ("My Rank vs Team") and
  // admin ("Rank Team", replacing the old Call Outcomes card). Takes a
  // mode because admin has no personal rank / score block; otherwise
  // identical data (salesRanking comes from the same /api/dashboard/
  // sales-ranking endpoint for both roles).
  var rankWidget = function(opts){
    // A financeReads role (the accountant) has no sales ranking to see (owner
    // decision 2026-11-01). Flag-keyed; every other role renders exactly as before.
    if (roleHasFinanceReadsFE(p.cu)) return null;
    var mode = opts && opts.mode;
    var compact = opts && opts.compact === true;
    var rangeLabel = (opts && opts.rangeLabel) || "";
    var rankRows = Array.isArray(salesRanking) ? salesRanking : [];
    var loading = salesRanking === null;
    var myUid = mode === "sales" ? String(p.cu._id || p.cu.id) : "";
    var myRankIdx = mode === "sales" ? rankRows.findIndex(function(r){return String(r.uid)===myUid;}) : -1;
    var myRank = myRankIdx >= 0 ? myRankIdx+1 : 0;
    var myRankTotal = rankRows.length;
    var myRankRow = myRankIdx >= 0 ? rankRows[myRankIdx] : {deals:0,meetings:0,score:0};
    var title = mode === "admin" ? "Rank Team" : "My Rank vs Team";
    // Admin mode (non-compact) shares a grid row with other cards that stretch
    // to the tallest sibling (Management Alerts / Callback Compliance / Leads
    // by Status). The card is a flex column so the rank list can flex:1 and
    // fill the full card height with internal scrolling.
    // Compact mode (admin content + sales-style sizing) is for cases where the
    // widget sits as one of several similarly-sized cards in a wrapping grid
    // (TL/manager/director dashboard row with Status/Funnel/Recent Activity):
    // card is content-sized, list capped with internal scroll, matching the
    // 228px inner-scroll pattern used by Recent Activity.
    var isAdminMode = mode === "admin";
    var stretch = isAdminMode && !compact;
    var cardStyle = {background:"#fff",border:"1px solid #E2E8F0",borderRadius:16,padding:isMobile?"14px":"20px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",minWidth:0,boxSizing:"border-box"};
    if (stretch) { cardStyle.display = "flex"; cardStyle.flexDirection = "column"; cardStyle.height = "100%"; }
    var listStyle = stretch
      ? {display:"flex",flexDirection:"column",gap:6,flex:1,minHeight:0,overflowY:"auto"}
      : compact
        ? {display:"flex",flexDirection:"column",gap:6,maxHeight:228,overflowY:"auto"}
        : {display:"flex",flexDirection:"column",gap:6,maxHeight:360,overflowY:"auto"};
    return <div className="crm-dash-card" style={cardStyle}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:isMobile?10:14}}>
        <div style={{fontSize:isMobile?14:15,fontWeight:700,color:"#0F172A"}}>{title}</div>
        <div style={{fontSize:10,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>{rangeLabel}</div>
      </div>
      {mode === "sales" && <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:12}}>
        <span style={{fontSize:34,fontWeight:800,color:myRank===1?"#15803D":myRank>0&&myRank<=3?"#1D4ED8":"#334155",lineHeight:1}}>{myRank>0?"#"+myRank:"\u2014"}</span>
        <span style={{fontSize:13,color:"#64748B"}}>out of {myRankTotal} sales agents</span>
      </div>}
      <div style={listStyle}>
        {loading && <div style={{fontSize:12,color:"#94A3B8",padding:"6px 0"}}>Loading ranking\u2026</div>}
        {rankRows.map(function(r,i){
          var isMe = mode === "sales" && String(r.uid)===myUid;
          // Sales mode: rows are clickable. Own row opens own leads unfiltered;
          // others route through initAgentFilter (the leads page will show an
          // empty set for a sales user since the backend scopes leads to self,
          // but admin/TL/manager viewing this same widget get the expected drill-in).
          var onRowClick = mode === "sales" ? function(){
            if (isMe) { p.setFilter && p.setFilter("all"); p.nav("leads"); return; }
            if (p.setInitAgentFilter) p.setInitAgentFilter(r.uid);
            p.setFilter && p.setFilter("all");
            p.nav("leads");
          } : null;
          return <div key={r.uid} onClick={onRowClick} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:8,background:isMe?"#EFF6FF":"transparent",border:isMe?"1px solid #BFDBFE":"1px solid transparent",cursor:onRowClick?"pointer":"default"}}>
            <div style={{width:22,height:22,borderRadius:"50%",background:i===0?"#FBBF24":i===1?"#E2E8F0":i===2?"#F59E0B":"#F1F5F9",color:i<=2?"#0F172A":"#64748B",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,flexShrink:0}}>{i+1}</div>
            <div style={{flex:1,minWidth:0,fontSize:12,fontWeight:isMe?700:500,color:isMe?"#1D4ED8":"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{isMe?(r.name||p.cu.name)+" (you)":r.name}</div>
            <div title={"Activities "+(r.activities||0)+" \u00b7 Calls "+(r.calls||0)+" \u00b7 Meetings "+(r.meetings||0)+" \u00b7 DRs "+(r.dailyRequests||0)} style={{fontSize:12,fontWeight:800,color:"#1D4ED8",minWidth:28,textAlign:"right"}}>{r.score||0}</div>
          </div>;
        })}
        {!loading && !rankRows.length && <div style={{fontSize:12,color:"#94A3B8",padding:"6px 0"}}>No sales agents found</div>}
      </div>
      {mode === "sales" && <div style={{borderTop:"1px solid #F1F5F9",marginTop:10,paddingTop:10,display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:20}}>{myRank===1?"\ud83e\udd47":myRank===2?"\ud83e\udd48":myRank===3?"\ud83e\udd49":"\ud83d\udcca"}</span>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:"#0F172A"}}>Your score: {myRankRow.score||0}</div>
          <div style={{fontSize:11,color:"#94A3B8"}}>{(myRankRow.activities||0)} activities \u00b7 {(myRankRow.calls||0)} calls \u00b7 {(myRankRow.meetings||0)} meetings \u00b7 {(myRankRow.dailyRequests||0)} DRs in {rangeLabel}</div>
        </div>
      </div>}
    </div>;
  };

  // Agent Performance widget — used by both the admin dashboard and the
  // team-scoped (TL/manager/director) dashboard. Rows come exclusively from
  // /api/dashboard/agent-perf (already sorted by quality desc). The rsA/reA
  // params are accepted for backward call-site compatibility but don't drive
  // anything; both call sites map to the same fetched range (agentPerfRange).
  var renderAgentPerformanceCard = function(/* rsA, reA */){
    // null = the fetch hasn't resolved (or failed). Render a placeholder row
    // rather than an in-memory approximation — the old fallback showed
    // name-matched, period-ignoring rotations and a lead-age "Resp.", which
    // flashed as real numbers before the server rows landed.
    // Restored operational card (the previous layout). The composite Quality
    // score, rank number, medals and quality bar are GONE. A single ⚑ "worth a
    // look" review flag is added as the far-right column (fires on never-touched
    // ≥40% of the -3d book, or overdue-untouched ≥100 / ≥25% of book — full books
    // only; see tooltip). Sorted by book size (Leads desc), NOT flagged-first, so
    // no ranking is implied; the ⚑ column surfaces reviews on its own. "Meet" is
    // the corrected Lead+DR count; "Overdue" is the canonical overdue-&-CallBack;
    // "Resp." shows the median with its logged-call sample in the tooltip and
    // renders "—" only when there is no sample.
    var fAgentPerfA = agentPerfState;
    var agentPerfLoading = fAgentPerfA === null;
    var rows = (fAgentPerfA || []).slice().sort(function(a, b){ return (b.leads||0) - (a.leads||0); });
    var gridCols = isMobile ? "150px repeat(14, 56px)" : "160px repeat(14, minmax(0, 1fr))";
    var minW = isMobile ? 980 : undefined;
    var head = ["Agent","Leads","DR","Total","Calls","Follow","Overdue","Int","Meet","Deals","Rot OUT","Rot IN","No Ans","Resp.","⚑"];
    return card(<>
      <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:6}}>
        <div style={{fontSize:15,fontWeight:700,color:"#0F172A"}}>Agent Performance</div>
        <div style={{fontSize:10,color:"#94A3B8"}}>Raw counts this period · ⚑ = worth a review (see tooltip)</div>
      </div>
      <div className="crm-dash-scroll" style={{overflowX:"auto",overflowY:"auto",maxHeight:360,WebkitOverflowScrolling:"touch",width:"100%"}}>
      <div style={{display:"grid",gridTemplateColumns:gridCols,gap:4,paddingTop:4,paddingBottom:8,borderBottom:"1px solid #F1F5F9",marginBottom:4,width:isMobile?"max-content":"100%",minWidth:minW,position:"sticky",top:0,zIndex:10,background:"#fff"}}>
        {head.map(function(h,idx){return <div key={h+idx} style={{fontSize:11,fontWeight:700,color:"#94A3B8",textAlign:idx===0?"left":"center",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{h}</div>;})}
      </div>
      {agentPerfLoading && <div style={{fontSize:12,color:"#94A3B8",padding:"14px 0",textAlign:"center"}}>Loading agent performance…</div>}
      {!agentPerfLoading && rows.length===0 && <div style={{fontSize:12,color:"#94A3B8",padding:"14px 0",textAlign:"center"}}>No agent activity in this period</div>}
      {rows.map(function(a,i){
        var avBg=["#DBEAFE","#DCFCE7","#FEF3C7","#EDE9FE","#FFE4E6"][i%5];
        var avC=["#1D4ED8","#166534","#92400E","#5B21B6","#9F1239"][i%5];
        var initials=(a.name||"?").split(" ").slice(0,2).map(function(x){return x[0];}).join("").toUpperCase();
        var hasResp = a.respTime && a.respTime !== "—";
        var respN = a.contactLeads || 0;
        var respTip = hasResp ? (a.respTime+"h · from "+respN+" logged call"+(respN===1?"":"s")) : "No response-time data this period";
        var why = [a.neglectFlag?"never-touched ≥40% (−3d book)":null, a.overdueFlag?"overdue-untouched ≥100 or ≥25% of book":null].filter(Boolean).join("; ");
        return <div key={a.uid} onClick={function(){ if(p.setInitAgentFilter) p.setInitAgentFilter(a.uid); if(p.setFilter) p.setFilter("all"); if(p.nav) p.nav("leads"); }} style={{display:"grid",gridTemplateColumns:gridCols,gap:4,alignItems:"center",padding:"10px 0",borderBottom:"1px solid #F8FAFC",width:isMobile?"max-content":"100%",minWidth:minW,cursor:"pointer"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
            <div style={{width:30,height:30,borderRadius:"50%",background:avBg,color:avC,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0}}>{initials}</div>
            <div style={{fontSize:13,fontWeight:600,color:"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0}}>{a.name}</div>
          </div>
          <div style={{fontSize:13,fontWeight:700,textAlign:"center",color:"#334155"}}>{a.leads}</div>
          <div style={{fontSize:13,fontWeight:600,textAlign:"center",color:"#0F172A"}}>{a.dr}</div>
          <div style={{fontSize:13,fontWeight:700,textAlign:"center",color:"#334155"}}>{a.total}</div>
          <div style={{fontSize:13,fontWeight:600,textAlign:"center",color:a.calls>0?"#166534":"#94A3B8"}}>{a.calls}</div>
          <div style={{fontSize:13,fontWeight:600,textAlign:"center",color:"#334155"}}>{a.followups}</div>
          <div style={{fontSize:13,fontWeight:600,textAlign:"center",color:a.overdue>0?"#DC2626":"#94A3B8"}}>{a.overdue}</div>
          <div style={{fontSize:12,fontWeight:600,textAlign:"center",color:a.interested>0?"#15803D":"#94A3B8"}}>{a.interested}</div>
          <div style={{fontSize:12,fontWeight:600,textAlign:"center",color:a.meetings>0?"#6D28D9":"#94A3B8"}}>{a.meetings}</div>
          <div style={{fontSize:13,fontWeight:700,textAlign:"center",color:"#065F46"}}>{a.deals}</div>
          <div style={{fontSize:12,fontWeight:600,textAlign:"center",color:a.rotOut>0?"#B45309":"#94A3B8"}} title="Leads rotated away from this agent">{a.rotOut||0}</div>
          <div style={{fontSize:12,fontWeight:600,textAlign:"center",color:a.rotIn>0?"#0F766E":"#94A3B8"}} title="Leads rotated to this agent">{a.rotIn||0}</div>
          <div style={{fontSize:12,fontWeight:600,textAlign:"center",color:a.noAnswer>0?"#64748B":"#94A3B8"}}>{a.noAnswer||0}</div>
          <div style={{fontSize:12,fontWeight:600,textAlign:"center",color:"#334155"}} title={respTip}>{hasResp ? a.respTime+"h" : "—"}</div>
          <div style={{textAlign:"center"}}>
            {a.worthLook
              ? <span title={"Worth a look — "+why} style={{fontSize:14,color:"#B45309",cursor:"help"}}>⚑</span>
              : <span style={{fontSize:12,color:"#E2E8F0"}}>·</span>}
          </div>
        </div>;
      })}
      </div>
    </>);
  };

  // Admin-only heavy derivations memoized so the dropdown click and the
  // 30s poll cascade don't re-run the same filter-bound counts on every
  // parent render. Returns {} for non-admin so the hook still fires
  // unconditionally above the sales early return (rules-of-hooks).
  var adminMetrics = useMemo(function(){
    if (!isOnlyAdmin) return {};
    // Capture `now` inside the memo so a fresh Date.now() on every parent
    // render doesn't invalidate this (~9s O(N²)) computation. The threshold
    // checks (callback < now) shift in seconds, not milliseconds, so
    // recomputing only when leads/filter actually change is correct.
    var now = Date.now();
    var nowD = new Date();
    var w = dashPeriod(filter);
    var rangeStart = w.from, periodEnd = w.periodEnd;
    // adminMetrics' OWN end, preserved exactly as it was measured before this
    // wave: `today` and a month stop at NOW; `yesterday`, a week and a quarter
    // run to the end of the period. Nobody would write that rule down — it is
    // simply what these counts have been doing, and normalising it here would
    // move Key-Metrics numbers this wave was not asked to move. It is called
    // out in the report instead. For a period wholly in the past the two ends
    // are the same instant, so this only bites the period in progress.
    var rangeEnd = (w.key==="today" || w.key==="month") ? w.toNow : w.periodEnd;
    var fLeads = leads.filter(function(l){var ct=l.createdAt?new Date(l.createdAt).getTime():0;return ct>=rangeStart&&ct<=rangeEnd;});
    var fTotal = fLeads.length||1;
    var fSC={}; fLeads.forEach(function(l){fSC[l.status]=(fSC[l.status]||0)+1;});
    var fDR = (p.dailyReqs||[]).filter(function(r){ if (r.archived) return false; var rt=r.createdAt?new Date(r.createdAt).getTime():0;return rt>=rangeStart&&rt<=rangeEnd;});
    var statusChangedInRange = function(l, targetStatus){
      var assignMatch = (l.assignments||[]).some(function(a){
        if (a.status!==targetStatus) return false;
        var t = a.lastActionAt?new Date(a.lastActionAt).getTime():0;
        return t>=rangeStart && t<=rangeEnd;
      });
      if (assignMatch) return true;
      return (l.assignments||[]).some(function(a){
        return (a.agentHistory||[]).some(function(h){
          var t = h.createdAt?new Date(h.createdAt).getTime():0;
          if (t<rangeStart||t>rangeEnd) return false;
          var note = (h.note||"").toLowerCase();
          return note.indexOf(targetStatus.toLowerCase())>=0;
        });
      });
    };
    var interestedStatuses = ["Interested","Hot Case","HotCase","Potential"];
    var allLeadsUntimed = leads;
    var meetingsFromLeads = allLeadsUntimed.filter(function(l){
      if (!filter || filter === "all" || filter === "alltime") {
        return (l.assignments||[]).some(function(a){
          return a.status==="Meeting Done"||a.status==="MeetingDone";
        }) || l.status==="MeetingDone";
      }
      var foundInAssignments = (l.assignments || []).some(function(a){
        return (a.agentHistory || []).some(function(h){
          if (!h) return false;
          var note = String(h.note || "");
          var isStatusChange = h.type === "status_change"
            || /status\s*changed/i.test(note)
            || /^Status:\s*Meeting/i.test(note);
          if (!isStatusChange) return false;
          var hasMeetingDone = /meeting\s*done|meetingdone/i.test(note);
          if (!hasMeetingDone) return false;
          var t = new Date(h.createdAt || h.at || h.date || 0).getTime();
          return t >= rangeStart && t <= rangeEnd;
        });
      });
      if (foundInAssignments) return true;
      var foundInHistory = (l.history || []).some(function(h){
        if (!h) return false;
        var note = String(h.note || h.description || "");
        var isStatusChange = h.event === "status_change"
          || h.type === "status_change"
          || /status\s*changed/i.test(note);
        if (!isStatusChange) return false;
        var hasMeetingDone = /meeting\s*done|meetingdone/i.test(note);
        if (!hasMeetingDone) return false;
        var t = new Date(h.createdAt || h.at || h.date || 0).getTime();
        return t >= rangeStart && t <= rangeEnd;
      });
      if (foundInHistory) return true;
      return false;
    }).length;
    var meetingsFromDR = (p.dailyReqs||[]).filter(function(r){
      if (r.archived) return false;
      var rt=r.createdAt?new Date(r.createdAt).getTime():0;
      return (r.status==="Meeting"||r.status==="MeetingDone") && rt>=rangeStart && rt<=rangeEnd;
    }).length;
    var meetingsFiltered = meetingsFromLeads + meetingsFromDR;
    var interestedFiltered = fLeads.filter(function(l){return (l.assignments||[]).some(function(a){return interestedStatuses.includes(a.status);})||interestedStatuses.includes(l.status);}).length;
    var dealsFromLeads = leads.filter(function(l){
      if (l.globalStatus!=="donedeal" && l.status!=="DoneDeal") return false;
      var dealT = l.dealDate ? new Date(l.dealDate).getTime() : 0;
      if (!dealT || isNaN(dealT)) {
        dealT = 0;
        (l.assignments||[]).forEach(function(a){ if(a.lastActionAt){ var t=new Date(a.lastActionAt).getTime(); if(!isNaN(t)&&t>dealT) dealT=t; } });
      }
      if (!dealT && l.updatedAt) dealT = new Date(l.updatedAt).getTime();
      return dealT>=rangeStart && dealT<=rangeEnd;
    }).length;
    var dealsFromDR = (p.dailyReqs||[]).filter(function(r){
      if (r.archived) return false;
      if (r.status!=="DoneDeal" && r.status!=="Done Deal" && r.status!=="Deal") return false;
      var t = r.lastActivityTime ? new Date(r.lastActivityTime).getTime() : (r.updatedAt ? new Date(r.updatedAt).getTime() : (r.createdAt ? new Date(r.createdAt).getTime() : 0));
      return t>=rangeStart && t<=rangeEnd;
    }).length;
    var dealsFiltered = dealsFromLeads + dealsFromDR;
    var overdueLeads = allLeadsUntimed.filter(function(l){
      if (!l.callbackTime) return false;
      if (["MeetingDone","DoneDeal","EOI"].includes(l.status)) return false;
      var cb = new Date(l.callbackTime).getTime();
      if (isNaN(cb)) return false;
      return cb < now && cb >= rangeStart && cb <= rangeEnd;
    }).length;
    var overdueDR = (p.dailyReqs||[]).filter(function(r){
      if (r.archived) return false;
      var d = r.dueDate||r.callbackTime;
      if (!d) return false;
      if (r.status==="Meeting"||r.status==="MeetingDone"||r.status==="DoneDeal") return false;
      var cb = new Date(d).getTime();
      if (isNaN(cb)) return false;
      return cb < now && cb >= rangeStart && cb <= rangeEnd;
    }).length;
    var overdueFiltered = overdueLeads + overdueDR;
    var callbacksFiltered = fLeads.filter(function(l){return l.callbackTime&&new Date(l.callbackTime).toDateString()===nowD.toDateString();}).length;
    var drFiltered = (p.dailyReqs||[]).filter(function(r){var rt=r.createdAt?new Date(r.createdAt).getTime():0;return rt>=rangeStart&&rt<=rangeEnd;}).length;
    // (todayDay / daysSinceSat / weekStart / weekEnd / todayStart / yestStart /
    // monthStart were returned here. They were the inline period maths this
    // memo no longer does, and nothing outside it read one of them.)
    return {
      nowD:nowD,
      rangeStart:rangeStart, rangeEnd:rangeEnd, periodEnd:periodEnd,
      fLeads:fLeads, fTotal:fTotal, fSC:fSC, fDR:fDR,
      statusChangedInRange:statusChangedInRange,
      interestedStatuses:interestedStatuses, allLeadsUntimed:allLeadsUntimed,
      meetingsFromLeads:meetingsFromLeads, meetingsFromDR:meetingsFromDR, meetingsFiltered:meetingsFiltered,
      interestedFiltered:interestedFiltered,
      dealsFromLeads:dealsFromLeads, dealsFromDR:dealsFromDR, dealsFiltered:dealsFiltered,
      overdueLeads:overdueLeads, overdueDR:overdueDR, overdueFiltered:overdueFiltered,
      callbacksFiltered:callbacksFiltered, drFiltered:drFiltered
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[leads, p.dailyReqs, filter, isOnlyAdmin]);

  var isHR = p.cu.role === "hr";
  var isOfficeBoy = p.cu.role === "office_boy";
  // office_boy is maximally restricted to attendance only — render the exact
  // same attendance-only dashboard as HR (check-in card + own monthly log).
  // The finance-only shape (owner ruling 2026-11-02) is this same branch minus the
  // monthly log: greeting + date + the check-in card, nothing else.
  if (isHR || isOfficeBoy || isFinanceOnlyDash) {
    return <div className="crm-dash" style={{ padding:isMobile?16:32, maxWidth:1200, margin:"0 auto", width:"100%", boxSizing:"border-box" }}>
      <div style={{ marginBottom:20 }}>
        <div style={{fontSize:isMobile?16:22,fontWeight:700,color:"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{greeting+" "+p.cu.name}</div>
        <div style={{fontSize:isMobile?11:12,color:"#94A3B8",marginTop:2,fontVariantNumeric:"tabular-nums",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}><DashboardClock variant="admin" isMobile={isMobile} t={p.t}/></div>
      </div>
      <CheckInWidget variant="card" token={p.token} cu={p.cu} csrfToken={p.csrfToken}/>
      {!isFinanceOnlyDash && <MonthlyAttendanceLog token={p.token}/>}
    </div>;
  }

  if(!isOnlyAdmin) {
    // ============ DATE RANGE ============
    // One resolver, same window the two useEffects above send to the backend,
    // so the rendered range and the fetched range cannot drift. Every stat
    // below filters through inRangeS().
    var curYS = dfCairoYear();
    var wS = dashPeriod(filter);
    var rangeStartS = wS.from, rangeEndS = wS.soFar, rangeLabelS = wS.label;
    var inRangeS = function(d){ if(!d) return false; var t = new Date(d).getTime(); return !isNaN(t) && t>=rangeStartS && t<=rangeEndS; };

    // (quarterOptionsS lived here — the flat "Q1 2026 … Q4 2025" list the picker
    // used to render. Removed 2026-08-09: the panel now composes the label from
    // a year select + four fixed quarter buttons, so the list had no consumer.
    // The selectable years it encoded, [curYS, curYS-1], are passed to
    // renderQuarterPanel at the call site instead.)

    // ============ MY DATA ============
    var myUidS = String(p.cu._id||p.cu.id);
    // Team-scoped roles (team_leader / manager / director): server already
    // narrowed p.leads (and p.users / p.dailyReqs) to the caller's reportsTo
    // subtree via getScopedUserIds, so the visible set IS "my" data for
    // dashboard purposes. Filtering further by an assignments.some(===myUidS)
    // check would drop every subordinate's lead (these roles don't hold their
    // own assignment slice) and leave the dashboard empty.
    var isTeamScope = positionOf(p.cu) === "team_leader" || positionOf(p.cu) === "manager" || positionOf(p.cu) === "director";
    var allMyLeads = isTeamScope ? leads.slice() : leads.filter(function(l){
      return l.assignments && l.assignments.some(function(a){
        var aid = a.agentId&&a.agentId._id?a.agentId._id:a.agentId;
        return String(aid)===myUidS;
      });
    });
    // Leads created inside the active range — used for all counts on the dashboard.
    var myLeads2 = allMyLeads.filter(function(l){ return inRangeS(l.createdAt); });
    // Per-status counts come from the backend (myStats.byStatus). Falls back
    // to a local recomputation while the first request is still in flight so
    // the cards never render undefined.
    var localSC = {}; myLeads2.forEach(function(l){ localSC[l.status] = (localSC[l.status]||0)+1; });
    var mySC2 = (myStats && myStats.byStatus) ? myStats.byStatus : localSC;
    // myTotal2: prefer the backend "My Leads" count (matches Card 1);
    // otherwise fall back to the local filtered set.
    var myTotal2 = (myStats && typeof myStats.myLeads === "number") ? myStats.myLeads : myLeads2.length;
    var myInt2  = (mySC2.HotCase||0) + (mySC2.Potential||0) + (mySC2.MeetingDone||0) + (mySC2.DoneDeal||0);
    var myMeet2 = (myStats && typeof myStats.meetings === "number") ? myStats.meetings : ((mySC2.MeetingDone||0) + (mySC2.DoneDeal||0));
    // STEP 4-2 — callbacks come from /api/leads/callbacks (fetched in the
    // useEffect above into cbSalesState). The previous allMyLeads.filter scan
    // worked only because the bootstrap shipped every lead; once STEP 4-5
    // shrinks the bootstrap it would silently miss callbacks outside the
    // page slice. urgent2 + schedule2 + myOv2 stay role-scoped server-side.
    // The status card's "Overdue" is the TILE's own number — callbacks overdue
    // in the selected period, leads + Daily Requests, the same scope (R4). The
    // header bell keeps its all-time backlog and says so in its title.
    var myOv2   = (myStats && myStats.callbacks && typeof myStats.callbacks.overdue === "number") ? myStats.callbacks.overdue : "\u2014";
    var urgent2 = cbSalesState.overdue;
    var schedule2 = cbSalesState.scheduled;
    // urgentNew2 stays in-memory — it's about NewLead-status leads with no
    // recent activity, which isn't a callback query. Migrates with STEP 4-5.
    var urgentNew2 = allMyLeads.filter(function(l){return l.status==="NewLead"&&l.createdAt&&(now-new Date(l.createdAt).getTime())>2*3600000;}).slice(0,3);
    // "This Week's Schedule", "Last Week's Schedule", "July 2026's Schedule".
    // The label comes from the resolver, so a new period never lands here as a
    // bare "Schedule" the way "M2026-07" would have.
    var scheduleTitle2 = rangeLabelS ? (rangeLabelS + "'s Schedule") : "Schedule";
    var statusColors2={"NewLead":"#1565C0","Potential":"#00796B","HotCase":"#E65100","CallBack":"#6A1B9A","MeetingDone":"#2E7D32","NotInterested":"#EF4444","NoAnswer":"#94A3B8","DoneDeal":"#065F46"};

    // ============ MY RANK VS TEAM (CRM-wide, from backend) ============
    // salesRanking is populated by the useEffect above that hits /api/dashboard/sales-ranking.
    // Empty array while the first request is in flight (shows the "loading" affordance below).
    var rankRowsS      = Array.isArray(salesRanking) ? salesRanking : [];
    var myRankIdxS     = rankRowsS.findIndex(function(r){return String(r.uid)===myUidS;});
    var myRankS        = myRankIdxS>=0 ? myRankIdxS+1 : 0;
    var myRankTotalS   = rankRowsS.length;
    var myRankRowS     = myRankIdxS>=0 ? rankRowsS[myRankIdxS] : {deals:0,meetings:0,score:0};
    var rankLoadingS   = salesRanking===null;

    // ============ CONVERSION FUNNEL (real, range-scoped, backend-sourced) ============
    // Five pipeline stages, each counting leads currently in that stage (snapshot,
    // not lifetime). Stage 1 denominator drives the right-column %.
    // - Total      = myTotal2 (backend myLeads, range-scoped)
    // - Interested = byStatus.HotCase + byStatus.Potential
    // - Meeting    = byStatus.MeetingDone (snapshot, not backend "meetings" which also counts scheduled Tasks)
    // - EOI        = byStatus.EOI
    // - Deal       = byStatus.DoneDeal
    var funnelRowsS = [
      { l:"Total",        v:myTotal2,                                                    c:"#DBEAFE", tc:"#1E40AF", nav:function(){p.setFilter&&p.setFilter("all");p.nav("leads");} },
      { l:"Interested",   v:(myStats && typeof myStats.interested === "number") ? myStats.interested : ((mySC2["HotCase"]||0)+(mySC2["Potential"]||0)), c:"#FEF3C7", tc:"#92400E", nav:openInterestedForPeriod },
      { l:"Meeting Done", v:(mySC2["MeetingDone"]||0),                                   c:"#D1FAE5", tc:"#065F46", nav:function(){p.setFilter&&p.setFilter("MeetingDone");p.nav("leads");} },
      { l:"EOI",          v:(mySC2["EOI"]||0),                                           c:"#FAE8FF", tc:"#6B21A8", nav:function(){p.setFilter&&p.setFilter("EOI");p.nav("leads");} },
      { l:"Deal",         v:(mySC2["DoneDeal"]||0),                                      c:"#FFE4E6", tc:"#9F1239", nav:function(){p.nav("deals");} }
    ];
    var funnelMaxS = Math.max.apply(null, funnelRowsS.map(function(r){return r.v;}).concat([1]));

    // ============ RECENT ACTIVITY (real, from lead.history entries by me) ============
    var myNameS = p.cu.name||"";
    var fmtActTime = function(d){
      var dt = new Date(d);
      var dayStart = new Date(); dayStart.setHours(0,0,0,0);
      var yest = new Date(dayStart); yest.setDate(yest.getDate()-1);
      var tStr = fmtTime12(dt);
      if (dt>=dayStart) return "Today "+tStr;
      if (dt>=yest) return "Yesterday "+tStr;
      return dt.toLocaleDateString("en-GB") + " " + tStr;
    };
    var eventIconS = function(e){
      var ev = String(e||"").toLowerCase();
      if (ev.indexOf("status")>=0) return "\ud83d\udd04";
      if (ev.indexOf("feedback")>=0||ev.indexOf("note")>=0) return "\ud83d\udcdd";
      if (ev.indexOf("meeting")>=0) return "\ud83e\udd1d";
      if (ev.indexOf("callback")>=0) return "\ud83d\udcde";
      if (ev.indexOf("call")>=0) return "\u260e\ufe0f";
      if (ev.indexOf("assign")>=0||ev.indexOf("rotat")>=0) return "\u21aa\ufe0f";
      if (ev.indexOf("created")>=0||ev.indexOf("create")>=0) return "\u2728";
      return "\ud83d\udd14";
    };
    // For team-scoped roles (TL / manager / director): accept history entries
    // authored by any user in the caller's myTeamUsers set (self + subtree).
    var teamMemberNames = isTeamScope ? new Set((p.myTeamUsers||[]).map(function(u){return String(u.name||"");}).filter(Boolean)) : null;
    var myRecentActsS = [];
    allMyLeads.forEach(function(l){
      (l.history||[]).forEach(function(h){
        if (!h || !h.timestamp) return;
        var by = String(h.byUser||"");
        if (isTeamScope ? !teamMemberNames.has(by) : by!==myNameS) return;
        if (!inRangeS(h.timestamp)) return;
        myRecentActsS.push({
          lead: l,
          leadName: l.name||"Lead",
          event: h.event||"",
          description: h.description||"",
          timestamp: h.timestamp
        });
      });
    });
    myRecentActsS.sort(function(a,b){return new Date(b.timestamp).getTime()-new Date(a.timestamp).getTime();});
    myRecentActsS = myRecentActsS.slice(0,20);

    // ============ 7-DAY SPARKLINES ============
    // From the server series (kpiSpark), one per metric, scoped to this caller,
    // Cairo-bucketed. The client scans that lived here (spark7 over the
    // bootstrap page) drew at most the four days that page spanned.
    // Reusable card JSX — defined once and arranged differently per role
    // (team-scope arm groups them with Rank Team in a 4-col row; sales arm
    // keeps the original 3+3 row split).
    var urgentCardS = (
      <div className="crm-dash-card" style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:16,padding:isMobile?"14px":"20px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",minWidth:0,boxSizing:"border-box"}}>
        <div style={{fontSize:isMobile?14:15,fontWeight:700,color:"#0F172A",marginBottom:isMobile?10:14}}>{"🚨"} Urgent {"—"} Action Needed</div>
        {urgent2.length===0&&urgentNew2.length===0&&<div style={{fontSize:12,color:"#94A3B8",padding:"10px 0"}}>{"✅"} No urgent items</div>}
        {urgent2.map(function(l,i){var mins=Math.round((now-new Date(l.callbackTime).getTime())/60000);return <div key={i} onClick={function(){p.nav("leads",l);}} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderBottom:"1px solid #F8FAFC",minWidth:0,cursor:"pointer"}}>
          <div style={{width:8,height:8,borderRadius:"50%",background:"#EF4444",flexShrink:0}}/>
          <div style={{flex:1,minWidth:0}}><div style={{fontSize:13,fontWeight:600,color:"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.name}</div><div style={{fontSize:11,color:"#94A3B8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Overdue {mins>60?Math.round(mins/60)+"h":mins+"min"} {"·"} {slimRowStatus(l)}</div></div>
          <span style={{fontSize:11,fontWeight:700,color:"#DC2626",flexShrink:0}}>LATE</span>
        </div>;})}
        {urgentNew2.map(function(l,i){var hrs=Math.round((now-new Date(l.createdAt).getTime())/3600000);return <div key={"n"+i} onClick={function(){p.nav("leads",l);}} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderBottom:"1px solid #F8FAFC",minWidth:0,cursor:"pointer"}}>
          <div style={{width:8,height:8,borderRadius:"50%",background:"#3B82F6",flexShrink:0}}/>
          <div style={{flex:1,minWidth:0}}><div style={{fontSize:13,fontWeight:600,color:"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.name}</div><div style={{fontSize:11,color:"#94A3B8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>New lead {"—"} no action {hrs}h</div></div>
          <span style={{fontSize:11,fontWeight:700,color:"#1D4ED8",flexShrink:0}}>NEW</span>
        </div>;})}
      </div>
    );
    var scheduleCardS = (
      <div className="crm-dash-card" style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:16,padding:isMobile?"14px":"20px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",minWidth:0,boxSizing:"border-box"}}>
        <div style={{fontSize:isMobile?14:15,fontWeight:700,color:"#0F172A",marginBottom:isMobile?10:14}}>{"📅"} {scheduleTitle2}</div>
        {schedule2.length===0&&<div style={{fontSize:12,color:"#94A3B8",padding:"10px 0"}}>No callbacks scheduled in this range</div>}
        {schedule2.map(function(l,i){
          var t2=l.callbackTime?(fmtTime12(l.callbackTime)||"—"):"—";
          var isLate2=l.callbackTime&&new Date(l.callbackTime).getTime()<now;
          return <div key={i} onClick={function(){p.nav("leads",l);}} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderBottom:"1px solid #F8FAFC",minWidth:0,cursor:"pointer"}}>
            <div style={{fontSize:11,color:isLate2?"#DC2626":"#64748B",width:58,flexShrink:0,fontWeight:600}}>{t2}</div>
            <div style={{flex:1,minWidth:0}}><div style={{fontSize:13,color:"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.name}</div><div style={{fontSize:11,color:isLate2?"#DC2626":"#94A3B8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{isLate2?"Overdue":"Callback scheduled"}</div></div>
            <div style={{width:8,height:8,borderRadius:"50%",background:isLate2?"#EF4444":"#10B981",flexShrink:0}}/>
          </div>;
        })}
      </div>
    );
    var statusCardS = (
      <div className="crm-dash-card" style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:16,padding:isMobile?"14px":"20px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",minWidth:0,boxSizing:"border-box"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:isMobile?10:14}}>
          <div style={{fontSize:isMobile?14:15,fontWeight:700,color:"#0F172A"}}>My Leads {"—"} Status</div>
          <div style={{fontSize:10,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>{rangeLabelS}</div>
        </div>
        {[
          ["New Lead","NewLead","#3B82F6"],
          ["Potential","Potential","#10B981"],
          ["Hot Case","HotCase","#F59E0B"],
          ["Call Back","CallBack","#EF4444"],
          ["Meeting","MeetingDone","#8B5CF6"],
          ["EOI","EOI","#A855F7"],
          ["Done Deal","DoneDeal","#059669"],
          ["No Answer","NoAnswer","#64748B"],
          ["Not Int.","NotInterested","#94A3B8"]
        ].map(function(s){return bRow(s[0],mySC2[s[1]]||0,myTotal2,s[2],function(){p.setFilter&&p.setFilter(s[1]);p.nav("leads");});})}
        <div style={{borderTop:"1px solid #F1F5F9",marginTop:8,paddingTop:8,display:"flex",gap:14,alignItems:"center",fontSize:11,flexWrap:"wrap"}}>
          <span title="Current state — ignores the date filter above" style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.08em",background:"#F1F5F9",padding:"2px 6px",borderRadius:4}}>NOW</span>
          <span onClick={openOverdueForPeriod} title={"Callbacks overdue \u2014 " + rangeLabelS + ", same scope as the tile"} style={{color:"#64748B",cursor:"pointer"}}>Overdue ({rangeLabelS}): <span style={{color:"#EF4444",fontWeight:700}}>{myOv2}</span></span>
          <span onClick={function(){p.setFilter&&p.setFilter("NewLead");p.nav("leads");}} style={{color:"#64748B",cursor:"pointer"}}>Untouched: <span style={{color:"#3B82F6",fontWeight:700}}>{allMyLeads.filter(function(l){return l.status==="NewLead"&&l.createdAt&&(now-new Date(l.createdAt).getTime())>2*DAY;}).length}</span></span>
        </div>
      </div>
    );
    var funnelCardS = (
      <div className="crm-dash-card" style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:16,padding:isMobile?"14px":"20px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",minWidth:0,boxSizing:"border-box",overflow:"hidden"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:isMobile?10:14}}>
          <div style={{fontSize:isMobile?14:15,fontWeight:700,color:"#0F172A"}}>My Conversion Funnel</div>
          <div style={{fontSize:10,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>{rangeLabelS}</div>
        </div>
        {funnelRowsS.map(function(row,i){
          var pct = Math.max(6, Math.round((row.v/funnelMaxS)*100));
          return <div key={i} onClick={row.nav} style={{display:"flex",alignItems:"center",gap:isMobile?6:8,marginBottom:8,minWidth:0,cursor:"pointer"}}>
            <div style={{fontSize:11,color:"#64748B",width:isMobile?70:90,flexShrink:0,textAlign:"right",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{row.l}</div>
            <div style={{flex:1,minWidth:0,height:22,borderRadius:4,background:"#F8FAFC",position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",inset:0,height:"100%",borderRadius:4,background:row.c,width:pct+"%",display:"flex",alignItems:"center",padding:"0 8px",minWidth:0,boxSizing:"border-box"}}>
                <span style={{fontSize:11,fontWeight:700,color:row.tc,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row.v}</span>
              </div>
            </div>
            <div style={{fontSize:10,color:"#94A3B8",width:isMobile?36:46,textAlign:"right",flexShrink:0}}>{funnelRowsS[0].v>0?Math.round(row.v/funnelRowsS[0].v*100)+"%":"0%"}</div>
          </div>;
        })}
      </div>
    );
    var recentActivityCardS = (
      <div className="crm-dash-card" style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:16,padding:isMobile?"14px":"20px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",minWidth:0,boxSizing:"border-box"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:isMobile?10:14}}>
          <div style={{fontSize:isMobile?14:15,fontWeight:700,color:"#0F172A"}}>Recent Activity</div>
          <div style={{fontSize:10,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Last 20 · {rangeLabelS}</div>
        </div>
        {/* Fixed-height list — first ~4 visible (~57px each), the rest scroll inside the card. */}
        <div style={{maxHeight:228,overflowY:"auto",paddingRight:4}}>
          {myRecentActsS.length===0&&<div style={{fontSize:12,color:"#94A3B8",padding:"10px 0"}}>No activity in this range</div>}
          {myRecentActsS.map(function(h,i){
            var stC = statusColors2[h.lead&&h.lead.status]||"#94A3B8";
            return <div key={i} onClick={function(){ if(h.lead) p.nav("leads",h.lead); }} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"8px 0",borderBottom:"1px solid #F8FAFC",minWidth:0,cursor:h.lead?"pointer":"default"}}>
              <span style={{fontSize:14,lineHeight:"18px",flexShrink:0}}>{eventIconS(h.event)}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.leadName} {"—"} <span style={{color:stC,fontWeight:600}}>{h.lead&&h.lead.status}</span></div>
                <div style={{fontSize:11,color:"#64748B",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.description||h.event}</div>
                <div style={{fontSize:10,color:"#94A3B8",marginTop:2}}>{fmtActTime(h.timestamp)}</div>
              </div>
            </div>;
          })}
        </div>
      </div>
    );

    // THE SAME CARD THE ADMIN DASHBOARD RENDERS, with the same component and
    // the same endpoint. What differs is the DATA: the server scopes rows to
    // the caller, so this is one row — their own — for an agent, and a subtree
    // for a leader. No role test here; that would be a second visibility rule.
    var callbackCardS = card(<CallbackHandlingCard data={cbCompliance} filter={filter}
      onPickAgent={function(uid){ if(p.setInitAgentFilter) p.setInitAgentFilter(uid);
        if(p.setFilter) p.setFilter("CallBack"); if(p.nav) p.nav("leads"); }}/>, fillCard);

    return <div className="crm-dash crm-dash-sales" style={{padding:isMobile?"12px 10px 32px":"16px 12px 40px",background:"#F1F5F9",width:"100%",maxWidth:"100%",boxSizing:"border-box",overflowX:"hidden"}}>
      {/* Header — clock now sits to the RIGHT of the date on the same line. */}
      <div className="crm-dash-header" style={{display:"flex",alignItems:isMobile?"stretch":"center",justifyContent:"space-between",marginBottom:isMobile?14:20,flexWrap:"wrap",gap:isMobile?10:8,flexDirection:isMobile?"column":"row"}}>
        <div style={{minWidth:0,width:isMobile?"100%":"auto"}}>
          <div style={{fontSize:isMobile?16:20,fontWeight:700,color:"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{greeting} {p.cu.name}</div>
          <div style={{fontSize:isMobile?11:12,color:"#94A3B8",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{new Date().toDateString()} {"\u00b7"} <DashboardClock variant="sales" isMobile={isMobile}/></div>
        </div>
        <div className="crm-dash-filters" data-period-row="dashboard" style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",width:isMobile?"100%":"auto"}}>
          {[["today","Today"],["yesterday","Yesterday"],["week","This Week"],["lastWeek","Last Week"]].map(function(f){return <button key={f[0]} onClick={function(){setFilter(f[0]);}} style={Object.assign(periodChipStyle(dashChipLit(filter,f[0]), PERIOD_ACCENT_BLUE, isMobile), {flex:isMobile?"1 1 auto":"0 0 auto",flexShrink:0})}>{f[1]}</button>;})}
          {/* Month — a dropdown since 2026-08-30, the same shape as Quarter. */}
          <div ref={monthDropdownRef} style={{position:"relative",flex:isMobile?"1 1 auto":"0 0 auto"}}>
            {(function(){
              var mActive = dashIsMonth(filter);
              return <button onClick={openMonthPanel} style={Object.assign(periodChipStyle(mActive, PERIOD_ACCENT_BLUE, isMobile), {width:isMobile?"100%":"auto"})}>{mActive?dateFilterLabel("month",dashPeriodOf(filter).o):"This Month"} {"\u25be"}</button>;
            })()}
            {mOpen&&<div style={Object.assign({position:"absolute",right:0,background:"#fff",border:"1px solid #E2E8F0",borderRadius:10,zIndex:99,boxShadow:"0 4px 16px rgba(0,0,0,0.08)"},mAnchor)}>
              {renderMonthPanel()}
            </div>}
          </div>
          <div ref={quarterDropdownRef} style={{position:"relative",flex:isMobile?"1 1 auto":"0 0 auto"}}>
            {(function(){
              var qActive = dashIsQuarter(filter);
              return <button onClick={openQuarterPanel} style={Object.assign(periodChipStyle(qActive, PERIOD_ACCENT_BLUE, isMobile), {width:isMobile?"100%":"auto"})}>{qActive?filter:"Quarter"} {"\u25be"}</button>;
            })()}
            {qOpen&&<div style={Object.assign({position:"absolute",right:0,background:"#fff",border:"1px solid #E2E8F0",borderRadius:10,zIndex:99,boxShadow:"0 4px 16px rgba(0,0,0,0.08)"},qAnchor)}>
              {renderQuarterPanel([curYS, curYS-1], function(lbl){ setFilter(lbl); setQOpen(false); })}
            </div>}
          </div>
          {/* 📅 Date sits LAST (owner 2026-10-31): Today · Yesterday · This Week · Last Week · This Month · Quarter · 📅 Date. */}
          {dayPickerEl}
        </div>
      </div>

      {/* KPI strip — real backend counts from /api/dashboard/my-stats, re-fetched on filter change. */}
      {(function(){
        var st = myStats || {};
        var myLeadsCnt    = Number(st.myLeads||0);
        var myDrsCnt      = Number(st.dailyRequests||0);
        var myFupsCnt     = Number(st.followups||0);
        var myIntCnt      = Number(st.interested||0);
        var myMeetCnt     = Number(st.meetings||0);
        var myTargetQ     = Number(st.target||0);      // quarterly target from backend
        var myAchieved    = Number(st.achieved||0);    // achieved in active range (backend scopes deals by from/to)
        // Pro-rate target to the active filter. Backend only knows the quarter's
        // target; we scale it here so progress % is meaningful for Today/Week/Month.
        // 22 working days/month, 4 weeks/month — standard Egypt sales planning.
        var monthlyT = myTargetQ/3;
        var scaledTarget, scaledLabel;
        // Scale by the KIND of period, not by the filter string: "Last Week"
        // scales like a week and "July 2026" like a month, which a string
        // comparison against "week"/"month" would have missed — both would
        // have fallen through to the QUARTERLY target and shown a progress bar
        // roughly a twelfth of its true value.
        var pKey = dashPeriodOf(filter).key;
        if (pKey==="today" || pKey==="yesterday" || pKey==="day") { scaledTarget = monthlyT/22; scaledLabel = "Daily"; }
        else if (pKey==="week" || pKey==="lastWeek")   { scaledTarget = monthlyT/4;  scaledLabel = "Weekly"; }
        else if (pKey==="month")                       { scaledTarget = monthlyT;    scaledLabel = "Monthly"; }
        else if (pKey==="range")                       { var rN = dashRangeDays(dashPeriodOf(filter).o); scaledTarget = monthlyT/22*rN; scaledLabel = rN+" days"; }
        else                                            { scaledTarget = myTargetQ;   scaledLabel = "Quarterly"; }
        var scaledProgress = scaledTarget>0 ? Math.min(999, Math.round((myAchieved/scaledTarget)*100)) : 0;
        var fmtT = function(n){ if (n>=1000000) return (n/1000000).toFixed(1)+"M"; if (n>=1000) return Math.round(n/1000)+"K"; return Math.round(n); };
        var targetSub = scaledTarget>0 ? (scaledLabel+" \u00b7 of "+fmtT(scaledTarget)) : "Not set";
        // CALLBACKS — the SAME tile the admin dashboard shows, off the SAME
        // server function (countCallbackPeriod); only the scope differs, and the
        // backend scopes it with the same builder GET /api/leads uses, so this
        // agent's tile counts exactly the rows their own click opens. Absent
        // (still loading, or a role with no lead scope) renders an em dash.
        var cbS   = myStats && myStats.callbacks ? myStats.callbacks : null;
        var cbLblS = callbackTrioLabel(cbS);
        var v_cbS = cbLblS.value;
        var s_cbS = cbLblS.sub;
        // R3 amended: Interested and Meetings carry a bare "N%" of My Leads in
        // the same window; no denominator wording. The SHARE now comes from the
        // server (my-stats' interestedPct / meetingsPct, the same sharePct the
        // admin tiles use) and the STRING from the one client rule — this line
        // used to be a second Math.round in a render body, which is both a
        // second answer to an R8 question and the copy that printed "0%" over a
        // real count.
        var pctOfKey = function(k){ return pctLabelOf(st[k + "Pct"], Number(st[k] || 0), myLeadsCnt); };
        return <div className="crm-dash-kpi" style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2, minmax(0, 1fr))":"repeat(auto-fit,minmax(130px,1fr))",gap:isMobile?8:10,marginBottom:isMobile?14:20}}>
          {kpiCard("My Leads",       myLeadsCnt, rangeLabelS,                         "#1565C0","#ffffff",function(){p.setFilter&&p.setFilter("all");p.nav("leads");}, kpiSpark("leads"))}
          {kpiCard("Daily Requests", myDrsCnt,   rangeLabelS,                         "#00796B","#ffffff",function(){if(p.setDrInitFilter)p.setDrInitFilter("all");p.nav("dailyReq");}, kpiSpark("dailyRequests"))}
          {kpiCard("Followups",      myFupsCnt,  "Stale > 2d",                        "#E65100","#ffffff",function(){p.setFilter&&p.setFilter("CallBack");p.nav("leads");}, KPI_SPARK_NONE)}
          {kpiCard("Interested",     myIntCnt,   pctOfKey("interested"),                     "#6A1B9A","#ffffff",openInterestedForPeriod, kpiSpark("interested"))}
          {kpiCard("Meetings",       myMeetCnt,  "",                                      "#2E7D32","#ffffff",openMeetingsForPeriod, kpiSpark("meetings"))}
          {kpiCard("Callbacks",      v_cbS,      (cbS ? cbSplitNode(cbS) : s_cbS),    "#B71C1C","#ffffff",openOverdueForPeriod, kpiSpark("callbacks"))}
          {kpiCard("Target",         scaledProgress+"%", targetSub,                   "#AD1457","#ffffff",function(){p.nav(positionOf(p.cu)==="sales"?"kpis":"team");}, kpiSpark("deals"))}
        </div>;
      })()}

      {/* Layout splits by role:
          Team-scope (TL/manager/director): 2-col Urgent+Schedule, then 4-col
            Rank Team (compact) + Status + Funnel + Recent Activity (auto-fit
            grid wraps to 2-col on narrower screens), then full-width Agent
            Performance.
          Sales: original 3-col Rank+Urgent+Schedule, then 3-col
            Status+Funnel+Recent Activity. No Agent Performance. */}
      {isTeamScope ? <>
        <div className="crm-dash-row" style={{display:"grid",gridTemplateColumns:isMobile?"minmax(0, 1fr)":"repeat(auto-fit,minmax(280px,1fr))",gap:isMobile?10:14,marginBottom:14}}>
          {urgentCardS}
          {scheduleCardS}
        </div>

        <div className="crm-dash-row" style={{display:"grid",gridTemplateColumns:isMobile?"minmax(0, 1fr)":"repeat(auto-fit,minmax(280px,1fr))",gap:isMobile?10:14,marginBottom:14}}>
          {rankWidget({ mode: "admin", compact: true, rangeLabel: rangeLabelS })}
          {statusCardS}
          {funnelCardS}
          {recentActivityCardS}
          {callbackCardS}
        </div>

        {sec("Team Performance")}
        <div style={{marginBottom:14}}>
          {renderAgentPerformanceCard(rangeStartS, rangeEndS)}
        </div>
      </> : <>
        <div className="crm-dash-row" style={{display:"grid",gridTemplateColumns:isMobile?"minmax(0, 1fr)":"repeat(auto-fit,minmax(280px,1fr))",gap:isMobile?10:14,marginBottom:14}}>
          {rankWidget({ mode: "sales", rangeLabel: rangeLabelS })}
          {urgentCardS}
          {scheduleCardS}
        </div>

        <div className="crm-dash-row" style={{display:"grid",gridTemplateColumns:isMobile?"minmax(0, 1fr)":"repeat(auto-fit,minmax(280px,1fr))",gap:isMobile?10:14,marginBottom:14}}>
          {statusCardS}
          {funnelCardS}
          {recentActivityCardS}
          {callbackCardS}
        </div>
      </>}
    </div>;
  }

  // Pull memoized values out of adminMetrics — see the useMemo above.
  var nowD = adminMetrics.nowD;
  var rangeStart = adminMetrics.rangeStart, rangeEnd = adminMetrics.rangeEnd, periodEnd = adminMetrics.periodEnd;
  var fLeads = adminMetrics.fLeads, fTotal = adminMetrics.fTotal, fSC = adminMetrics.fSC, fDR = adminMetrics.fDR;
  var statusChangedInRange = adminMetrics.statusChangedInRange;
  var interestedStatuses = adminMetrics.interestedStatuses, allLeadsUntimed = adminMetrics.allLeadsUntimed;
  // meetings* / interested* / deals* / overdue* / callbacksFiltered / drFiltered
  // were destructured out of adminMetrics here and then overridden from
  // statsCounts. Neither the derivation nor the override was ever rendered —
  // the Key Metrics tiles read adminStats — so both are gone. adminMetrics
  // still computes the underlying fields; it is shared with the team-scoped
  // render and is left untouched.

  // Campaign performance (filtered). STEP 4-5 X2 \u2014 prefers
  // statsCounts.leads.byCampaign when resolved (server aggregates top 12
  // campaign|project|source buckets in range). Falls back to the in-memory
  // fLeads scan during the first paint so existing numbers stay visible.
  // Mapped onto the same row shape the markup already reads, so the card's
  // layout is untouched:
  //   campaign -> vintage-resolved DEVELOPMENT (was the raw `campaign` field,
  //               which on legacy leads already held the development)
  //   project  -> the group's top unit type (legacy `project` held the unit)
  //   source   -> dominant source by lead volume, for the coloured dot
  //   conv     -> deals / leads, replacing the old "Quality" badge, which was
  //               interested-% under fixed 30/15 thresholds and therefore
  //               pinned to "Low" on any short range regardless of performance.
  var toCampRow = function(c){
    // THE FOUR SHARES COME FROM THE SERVER (item 4, 2026-09-03) — the same
    // sharePct the tiles use. They were three inline Math.rounds here, which is
    // both a second answer to an R8 question and the copy that rendered "0%"
    // for a campaign with real deals on a large cohort.
    // eoisReached (ever reached the EOI stage, incl. cancelled and the ones
    // already converted to deals) — NOT the endpoint's `eois`, which is
    // Pending|Approved only and would drop every EOI that has since been won.
    var eoi = c.eoisReached || 0;
    var row = {
      campaign: c.campaign || "",
      project:  (c.unitTypes && c.unitTypes[0]) || "",
      source:   c.topSource || "",
      manual:   !!c.manual,
      mix:      rowSourceMix(c),
      leads:    c.leads || 0,
      int:      c.interested || 0,
      meet:     c.meetings || 0,
      eoi:      eoi,
      deals:    c.deals || 0,
      contacted: c.contacted || 0,
      // The server's shares, rendered by the one string rule. `conv` stays a
      // NUMBER because convBadge colours on it; `convLabel` is its text.
      ip: pctLabelOf(c.intPct,  c.interested, c.leads),
      mp: pctLabelOf(c.meetPct, c.meetings,   c.leads),
      ep: pctLabelOf(c.eoiPct,  eoi,          c.leads),
      conv: c.leads > 0 ? (c.convPct || 0) : null,
      convLabel: c.leads > 0 ? pctLabelOf(c.convPct, c.deals, c.leads) : null
    };
    row.quality = campaignQuality(row);
    return row;
  };
  // The 8 biggest campaigns by lead volume, then ONE roll-up row for the rest so
  // the columns add up to the cohort (conversions sit in the small campaigns).
  // The roll-up carries no quality grade: a grade of a mixed bag grades nothing.
  var fCamps = (campaignPerf || []).slice(0, 8).map(toCampRow);
  var otherRaw = otherCampaignsRow((campaignPerf || []).slice(8));
  if (otherRaw) { var otherRow = toCampRow(otherRaw); otherRow.isRollup = true; otherRow.quality = null; fCamps.push(otherRow); }
  // The rolled-up campaigns themselves (shown when the roll-up row is open), and the
  // whole-cohort TOTALS row: every row summed, Conv. through the same share rule.
  var otherChildren = (campaignPerf || []).slice(8).map(function(c){ var r = toCampRow(c); r.isChild = true; r.quality = null; return r; });
  var totalsRaw = otherCampaignsRow(campaignPerf || [], "Total");
  var campTotals = null;
  if (totalsRaw) { campTotals = toCampRow(totalsRaw); campTotals.isTotal = true; campTotals.quality = null; }
  // Clicks: the roll-up row toggles; any real campaign name opens the Leads page on
  // that campaign + the page period (the same cohort the row counted).
  var openCampaignLeads = function(c){
    if (p.setSpecialFilter) p.setSpecialFilter(campaignDrillSpec(c, { from: agentPerfRange.rsA, to: agentPerfRange.reA }, dashPeriodLabel(filter)));
    if (p.setFilter) p.setFilter("all");
    p.nav("leads");
  };
  var campLabel = function(c){
    if (c.isRollup) return (otherOpen ? "\u25be " : "\u25b8 ") + c.campaign;
    if (c.isTotal) return c.campaign;
    return (c.campaign||"\u2014") + " \u00b7 " + (c.project||"\u2014");
  };
  var campClickable = function(c){ return !c.isTotal; };
  var campAct = function(c){ if (c.isRollup) setOtherOpen(function(o){ return !o; }); else if (!c.isTotal) openCampaignLeads(c); };
  var campNameAttrs = function(c){
    if (!campClickable(c)) return {};
    return { role:"button", tabIndex:0, "aria-expanded": c.isRollup ? otherOpen : undefined,
             onClick:function(){ campAct(c); }, onKeyDown:function(e){ if (e.key==="Enter"||e.key===" ") { e.preventDefault(); campAct(c); } } };
  };
  var campNameStyle = function(c){
    if (c.isTotal) return { fontWeight:800 };
    return { cursor:"pointer", textDecoration: c.isRollup ? "none" : "underline", textDecorationColor:"#CBD5E1", textUnderlineOffset:2 };
  };
  var campRowStyle = function(c, base){
    return Object.assign({}, base, c.isTotal ? { borderTop:"1px solid #E2E8F0", background:"#F8FAFC", marginTop:4 } : c.isChild ? { background:"#FBFBFD" } : {});
  };

  // Agent Performance compute moved to renderAgentPerformanceCard() helper
  // above so it can be shared between the admin and team-scoped dashboards.

  // Untouched leads are served by GET /api/leads/untouched — the local
  // leads[] is paginated, so any client-side filter here was capped to the
  // current page (which is why the card used to read 0). See the
  // rwData state + polling effect above (Response Watch card).

  // Today's activities feed — Activity collection (covers leads + DRs) merged
  // with lead.history entries (captures status_change / feedback_added /
  // callback_scheduled / rotated / assigned events the Activity collection
  // doesn't track natively).
  var _actsPool = (todayActivities && todayActivities.length) ? todayActivities : (p.activities||[]);
  // Range-scoped (Change 1) — no longer hardcoded to today. Bounds come
  // from the admin rangeStart/rangeEnd that the Key Metrics cards use.
  var _actsToday = _actsPool.filter(function(a){
    if (!a.createdAt) return false;
    var t = new Date(a.createdAt).getTime();
    return t >= rangeStart && t <= rangeEnd;
  });
  _actsToday.sort(function(a,b){return new Date(b.createdAt)-new Date(a.createdAt);});
  // Dedupe: first by _id (Map), then collapse near-duplicate legacy entries created by old server auto-log
  // (same user+lead+type within 10 seconds => keep only one)
  var _byId = new Map();
  _actsToday.forEach(function(a){ var k = a._id ? String(a._id) : ("no-id-"+Math.random()); if (!_byId.has(k)) _byId.set(k,a); });
  var _uniqueById = Array.from(_byId.values());
  var _seenContent = {};
  var todayActsAll = [];
  _uniqueById.forEach(function(a){
    var uid = String(a.userId&&a.userId._id?a.userId._id:a.userId||"");
    var lid = String(a.leadId&&a.leadId._id?a.leadId._id:a.leadId||"");
    var bucket = a.createdAt ? Math.floor(new Date(a.createdAt).getTime()/10000) : 0;
    var key = uid+"|"+lid+"|"+(a.type||"")+"|"+bucket;
    if (_seenContent[key]) return;
    _seenContent[key] = true;
    todayActsAll.push(a);
  });

  // Merge lead.history entries dated today (same lead pool already loaded).
  // Spec: "Pull from the lead history array and daily requests activity".
  // history entries carry {event, description, byUser, toAgent, timestamp};
  // we adapt them to the activity shape expected by the renderer below.
  var _nameToUid = {};
  (p.users||[]).forEach(function(u){ if (u && u.name) _nameToUid[u.name] = String(u._id||gid(u)); });
  var mapHistoryEvent = function(evt){
    var e = String(evt||"").toLowerCase();
    if (e.indexOf("status")>=0)   return "status_change";
    if (e.indexOf("feedback")>=0) return "note";
    if (e.indexOf("callback")>=0) return "note";
    if (e.indexOf("rotat")>=0)    return "reassign";
    if (e.indexOf("assign")>=0)   return "reassign";
    if (e.indexOf("meet")>=0)     return "meeting";
    return "note";
  };
  leads.forEach(function(l){
    (l.history||[]).forEach(function(h){
      if (!h || !h.timestamp) return;
      var t = new Date(h.timestamp).getTime();
      if (!t || t < rangeStart || t > rangeEnd) return;
      var mappedType = mapHistoryEvent(h.event);
      var byName = h.byUser || "";
      var byUid = _nameToUid[byName] || null;
      // Dedupe against Activity-collection entries already captured for the same lead+type+10s bucket.
      var lid = String(gid(l)||"");
      var bucket = Math.floor(t/10000);
      var key = (byUid||"")+"|"+lid+"|"+mappedType+"|"+bucket;
      if (_seenContent[key]) return;
      _seenContent[key] = true;
      todayActsAll.push({
        _id: "hist:"+lid+":"+t+":"+(h.event||""),
        type: mappedType,
        note: h.description || h.event || "",
        userId: byUid ? { _id: byUid, name: byName } : (byName ? { name: byName } : null),
        leadId: { _id: gid(l), name: l.name },
        createdAt: h.timestamp,
        _fromHistory: true
      });
    });
  });
  todayActsAll.sort(function(a,b){return new Date(b.createdAt)-new Date(a.createdAt);});

  // Mask phone
  var maskPh = function(ph){if(!ph)return "";if(ph.length<5)return ph;return ph.slice(0,3)+"****"+ph.slice(-2);};

  // Click lead navigation
  var openLead = function(l){ if(p.nav)p.nav("leads",l); };
  // Untouched-leads card click. GET /api/leads/untouched answers a COMPUTED VIEW
  // (_id, name, phone, agentId, agentName, lastActivityAt, hoursSinceActivity,
  // assignedOnly) — not a Lead — and LeadsPage consumes p.initSelected verbatim
  // with no re-hydration of its own, unlike DailyRequestsPage. So whatever is
  // handed over here IS what the side panel renders.
  //
  // The card used to hand over `{_id, name}` when the lead was not in the
  // bootstrap page, which dropped the phone the server had already sent. That is
  // not a rare path: the untouched list is sorted OLDEST-ACTIVITY-FIRST and the
  // bootstrap page is NEWEST-FIRST, so the two sets are structurally disjoint —
  // measured on production 2026-09-01, 0 of the 50 untouched rows were in the
  // 100-row bootstrap, i.e. the fallback fired on 100% of clicks. The panel came
  // up with "-" for the phone and a tel: link built from an empty string.
  //
  // Fetching the real document fixes the whole panel, not just the phone: the
  // shell also had no status, budget, agent, notes or callback time. This is the
  // SAME STEP 4-1 pattern openActivity already uses a few lines below — that fix
  // landed on Today's Activities and missed this card.
  //
  // Fail-soft: on a network error, open the shell so the click still navigates.
  // The shell now carries u.phone, which the server sends and which costs
  // nothing to pass on, so even the degraded path can dial.
  var openUntouchedLead = function(u){
    if (!u || !u._id) return;
    var id = String(u._id);
    var inMemory = (leads||[]).find(function(x){ return String(gid(x)) === id; });
    if (inMemory) { openLead(inMemory); return; }
    var shell = { _id: u._id, name: u.name, phone: u.phone || "" };
    apiFetch("/api/leads/" + id, "GET", null, p.token).then(function(fresh){
      openLead((fresh && fresh._id) ? fresh : shell);
    }).catch(function(){ openLead(shell); });
  };
  // Click alert navigation to filtered leads
  var gotoFilter = function(status){ if(p.setFilter)p.setFilter(status||"all"); if(p.nav)p.nav("leads"); };
  // Activity-row click router: Activity.leadId can point at either a Lead or
  // a DailyRequest (same Activity model, different collections). The server
  // only .populate()s the Lead side, so DR-owned activities arrive with an
  // unresolved id — we handle both the click-through and the displayed
  // client name via these local indexes.
  var _leadsById = {}; (p.leads||[]).forEach(function(l){ _leadsById[String(gid(l))] = l; });
  var _drsById   = {}; (p.dailyReqs||[]).forEach(function(r){ _drsById[String(gid(r))] = r; });
  // Phone-indexed lead lookup — DR rows are linked to leads by phone number
  // (same convention the server uses for DR ↔ Lead sync). We normalize to
  // digits only so "+20 010..." and "010..." resolve the same lead.
  var _normPh = function(p){ return String(p||"").replace(/\D+/g,""); };
  var _leadsByPhone = {};
  (p.leads||[]).forEach(function(l){
    var k1 = _normPh(l.phone);   if (k1) _leadsByPhone[k1] = l;
    var k2 = _normPh(l.phone2);  if (k2 && !_leadsByPhone[k2]) _leadsByPhone[k2] = l;
  });
  // Phone-indexed DR lookup — when Activity.populate("leadId") misses (the
  // referenced id is a DailyRequest, not a Lead), a.leadId comes back null
  // and we lose the original id. The Activity's clientPhone snapshot lets
  // us recover the DR by phone, which in turn gives us back the DR's _id
  // for click routing and its name for the row label.
  var _drsByPhone = {};
  (p.dailyReqs||[]).forEach(function(r){
    var k1 = _normPh(r.phone);   if (k1 && !_drsByPhone[k1]) _drsByPhone[k1] = r;
    var k2 = _normPh(r.phone2);  if (k2 && !_drsByPhone[k2]) _drsByPhone[k2] = r;
  });
  var _drFromActivityPhone = function(a){
    var ph = _normPh(a && a.clientPhone);
    return ph ? (_drsByPhone[ph] || null) : null;
  };
  // Source inference from the raw activity doc:
  //   - Lead-backed:  a.leadId is the populated object { _id, name }
  //   - DR-backed:    a.leadId is an unpopulated ObjectId (string after JSON)
  //                   OR null (populate miss — recover via clientPhone)
  var activityIsLead = function(a){ return !!(a && a.leadId && typeof a.leadId === "object" && a.leadId.name); };
  var activityLeadIdStr = function(a){
    if (!a) return "";
    if (a.leadId) {
      if (typeof a.leadId === "object") return a.leadId._id ? String(a.leadId._id) : "";
      return String(a.leadId);
    }
    // Populate miss — DR-backed. Resolve the DR through the phone snapshot
    // and return its _id so the click router can find it in _drsById.
    var dr = _drFromActivityPhone(a);
    return dr ? String(gid(dr)) : "";
  };
  // Router:
  //   - Lead-backed activity → open the lead directly.
  //   - DR-backed activity   → look up a Lead by phone (cached DR's phone
  //     OR the activity's own clientPhone snapshot, so we don't depend on
  //     p.dailyReqs being loaded). Found → open that Lead's detail page.
  //     Not found → open the DR detail (cached doc when available, shell
  //     {_id, name, phone} otherwise — DailyRequestsPage re-hydrates from
  //     its own fetch on mount).
  var openActivity = function(a){
    var id = activityLeadIdStr(a);
    if (!id) return;
    if (activityIsLead(a)) {
      var inMemoryLead = _leadsById[id];
      if (inMemoryLead) { if (p.nav) p.nav("leads", inMemoryLead); return; }
      // STEP 4-1 — fetch the lead from /api/leads/:id when it isn't in the
      // bootstrap page slice. Same fail-soft fallback as the notification
      // bells: on error, navigate to the shell so the page still opens.
      apiFetch("/api/leads/" + id, "GET", null, p.token).then(function(fresh){
        var target = (fresh && fresh._id) ? fresh : { _id: id, name: (a.leadId && a.leadId.name) || "" };
        if (p.nav) p.nav("leads", target);
      }).catch(function(){
        if (p.nav) p.nav("leads", { _id: id, name: (a.leadId && a.leadId.name) || "" });
      });
      return;
    }
    // DR-backed.
    var drObj = _drsById[id];
    var drPhone = (drObj && (_normPh(drObj.phone) || _normPh(drObj.phone2))) || _normPh(a && a.clientPhone);
    var linkedLead = drPhone ? _leadsByPhone[drPhone] : null;
    if (linkedLead) {
      if (p.nav) p.nav("leads", linkedLead);
      return;
    }
    if (drObj) { if (p.nav) p.nav("dailyReq", drObj); return; }
    // STEP 4-1 — DR not in p.dailyReqs and no linked lead in memory either.
    // Try the by-phone fallback to recover the linked Lead first (preferred
    // routing for DR-backed activities is to open the lead, not the DR
    // mirror). If that also fails, fall back to fetching the DR by phone
    // and opening that. Final fallback: shell open as before.
    var doShellOpen = function(){
      var shell = { _id: id, name: resolveClientName(a), phone: (a && a.clientPhone) || "" };
      if (p.nav) p.nav("dailyReq", shell);
    };
    if (!drPhone) { doShellOpen(); return; }
    apiFetch("/api/leads/by-phone?phone=" + encodeURIComponent(drPhone), "GET", null, p.token).then(function(leads){
      if (Array.isArray(leads) && leads.length > 0) {
        if (p.nav) p.nav("leads", leads[0]);
        return;
      }
      apiFetch("/api/daily-requests/by-phone?phone=" + encodeURIComponent(drPhone), "GET", null, p.token).then(function(drs){
        if (Array.isArray(drs) && drs.length > 0) {
          if (p.nav) p.nav("dailyReq", drs[0]);
        } else { doShellOpen(); }
      }).catch(doShellOpen);
    }).catch(doShellOpen);
  };
  // Is the originating Lead/DR for this activity archived? Archived records
  // stay in the Activities card as a read-only history, so the row must be
  // non-clickable even though we could resolve the source. Lead-backed ⇒
  // check the Lead's archived flag; DR-backed ⇒ check the DR's archived flag
  // (via id or phone snapshot). Linked-Lead-by-phone is NOT consulted here:
  // if the activity was logged on an archived DR, it's history either way.
  var isActivityArchived = function(a){
    if (!a) return false;
    var id = activityLeadIdStr(a);
    if (activityIsLead(a)) {
      var l = id ? _leadsById[id] : null;
      return !!(l && l.archived === true);
    }
    var dr = (id ? _drsById[id] : null) || _drFromActivityPhone(a);
    return !!(dr && dr.archived === true);
  };
  // Can we actually navigate to the source record of this activity? Rows
  // where we genuinely can't (DR deleted, no Lead, no phone to fall back to)
  // must render as visually non-clickable — don't pretend a dead click is
  // a real one. Also: archived records are read-only history — no click.
  var canOpenActivity = function(a){
    if (!a) return false;
    if (isActivityArchived(a)) return false;
    if (activityIsLead(a)) return true;
    var id = activityLeadIdStr(a);
    if (id && (_leadsById[id] || _drsById[id])) return true;
    if (_drFromActivityPhone(a)) return true;
    return false;
  };
  // Kept for backward compatibility with any older call sites (single-arg id).
  var openActivityClient = function(leadId){
    if (!leadId) return;
    var key = String(leadId);
    if (_leadsById[key]) { if(p.nav) p.nav("leads", _leadsById[key]); return; }
    if (_drsById[key])   { if(p.nav) p.nav("dailyReq", _drsById[key]); return; }
    if (p.nav) p.nav("dailyReq", { _id: key });
  };
  // Resolve a display label for the client behind an activity row. Order:
  //   1. a.clientName    — server snapshot (new — covers DR-backed activities
  //                        the populate can't resolve, no matter what's cached)
  //   2. a.leadId.name   — populate result (Lead-backed only)
  //   3. local indexes   — _leadsById / _drsById fallback
  //   4. a.clientPhone   — phone snapshot from server (digits as a last resort)
  //   5. "Unknown client"
  // Never returns the empty string; never lets the row render "(no client)".
  var resolveClientName = function(a){
    if (a && a.clientName) return a.clientName;
    if (a && a.leadId && typeof a.leadId === "object" && a.leadId.name) return a.leadId.name;
    var lid = a && a.leadId ? (a.leadId._id ? String(a.leadId._id) : String(a.leadId)) : "";
    if (lid) {
      if (_leadsById[lid] && _leadsById[lid].name) return _leadsById[lid].name;
      if (_drsById[lid]   && _drsById[lid].name)   return _drsById[lid].name;
      // Last-ditch: phone from local indexes if the doc exists but is unnamed.
      if (_leadsById[lid] && _leadsById[lid].phone) return maskPh(_leadsById[lid].phone);
      if (_drsById[lid]   && _drsById[lid].phone)   return maskPh(_drsById[lid].phone);
    }
    // Populate miss — DR-backed activity. Pull the DR via phone snapshot so
    // the row renders the real client name (or phone) instead of "Unknown
    // client". Spec: DR rows must fall back to phone if name is empty and
    // must never render "Unknown client".
    var dr = _drFromActivityPhone(a);
    if (dr) return dr.name || maskPh(dr.phone) || maskPh(a && a.clientPhone) || "";
    if (a && a.clientPhone) return maskPh(a.clientPhone);
    return "Unknown client";
  };
  // Activity source badge: "DR" for DailyRequest-backed rows, nothing for Leads.
  // Lead-backed activities arrive populated (a.leadId is an object with name);
  // everything else is either a DR or an unknown id — either way the row
  // should route to the Daily Requests page when clicked.
  var activitySource = function(a){
    if (!a || !a.leadId) return "";
    if (activityIsLead(a)) return "";
    return "DR";
  };
  // Exact-time formatter for the activity rows — "3:45 PM". Delegates to the
  // module-scope cached Intl.DateTimeFormat (see _dashboardExactTime above).
  var exactTime = _dashboardExactTime;

  // lockedCount / missingFBCount / stale48Count / rotationsMonth (+ auto-manual
  // split) / rotationsTotal used to be derived here by scanning `leads` and
  // every lead's agentHistory on EVERY render. All five now come from
  // /api/dashboard/alerts, counted over the full collection.

  // Action label for activities
  var actLabel = function(a){
    if(a.type==="status_change") return "Status change";
    if(a.type==="note") return "Note added";
    if(a.type==="call") return "Call logged";
    if(a.type==="meeting") return "Meeting booked";
    if(a.type==="reassign") return "Reassign";
    if((a.note||"").toLowerCase().includes("callback")) return "Callback set";
    return "Activity";
  };
  var timeAgoShort = function(d){
    var diff=Math.max(0,(now-new Date(d).getTime())/60000);
    if(diff<1) return "just now";
    if(diff<60) return Math.round(diff)+" min ago";
    if(diff<1440) return Math.round(diff/60)+"h ago";
    return Math.round(diff/1440)+"d ago";
  };
  var agentName = function(uid){ return userNameOf(uid, p.users) || "Unknown"; };
  var initialsOf = function(n){return (n||"?").split(" ").slice(0,2).map(function(x){return x[0];}).join("").toUpperCase();};

  return <div className="crm-dash crm-dash-admin" style={{padding:isMobile?"12px 10px 32px":"16px 12px 40px",background:"#F1F5F9",width:"100%",maxWidth:"100%",boxSizing:"border-box",overflowX:"hidden"}}>
    {/* Mobile safety-net CSS lives once in the app root — see CRMApp. */}
    <div className="crm-dash-header" style={{display:"flex",alignItems:isMobile?"flex-start":"center",justifyContent:"space-between",marginBottom:isMobile?16:24,flexWrap:"wrap",gap:isMobile?10:8,flexDirection:isMobile?"column":"row"}}>
      <div style={{minWidth:0,width:isMobile?"100%":"auto"}}>
        <div style={{fontSize:isMobile?16:22,fontWeight:700,color:"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{greeting+" "+p.cu.name}</div>
        <div style={{fontSize:isMobile?11:12,color:"#94A3B8",marginTop:2,fontVariantNumeric:"tabular-nums",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}><DashboardClock variant="admin" isMobile={isMobile} t={p.t}/></div>
      </div>
      <div className="crm-dash-filters" data-period-row="dashboard" style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",width:isMobile?"100%":"auto",overflowX:isMobile?"auto":"visible",WebkitOverflowScrolling:"touch"}}>
        {[["today","Today"],["yesterday","Yesterday"],["week","This Week"],["lastWeek","Last Week"]].map(function(f){
          return <button key={f[0]} onClick={function(){setFilter(f[0]);}} style={Object.assign(periodChipStyle(dashChipLit(filter,f[0]), PERIOD_ACCENT_BLUE, isMobile), {flexShrink:0})}>{f[1]}</button>;
        })}
        {/* Month — a dropdown since 2026-08-30, the same shape as Quarter. */}
        <div ref={monthDropdownRef} className="crm-dash-quarter-wrap" style={{position:"relative"}}>
          <button onClick={openMonthPanel} style={Object.assign(periodChipStyle(dashIsMonth(filter), PERIOD_ACCENT_BLUE, isMobile), {flexShrink:0})}>{dashIsMonth(filter)?dateFilterLabel("month",dashPeriodOf(filter).o):"This Month"} &#9662;</button>
          {mOpen&&<div style={Object.assign({position:"absolute",left:isMobile?0:"auto",right:isMobile?"auto":0,background:"#fff",border:"1px solid #E2E8F0",borderRadius:10,zIndex:99,boxShadow:"0 4px 16px rgba(0,0,0,0.08)"},mAnchor)}>
            {renderMonthPanel()}
          </div>}
        </div>
        <div ref={quarterDropdownRef} className="crm-dash-quarter-wrap" style={{position:"relative"}}>
          <button onClick={openQuarterPanel} style={Object.assign(periodChipStyle(dashIsQuarter(filter), PERIOD_ACCENT_BLUE, isMobile), {flexShrink:0})}>{dashIsQuarter(filter)?filter:"Quarter"} &#9662;</button>
          {qOpen&&<div style={Object.assign({position:"absolute",left:isMobile?0:"auto",right:isMobile?"auto":0,background:"#fff",border:"1px solid #E2E8F0",borderRadius:10,zIndex:99,boxShadow:"0 4px 16px rgba(0,0,0,0.08)"},qAnchor)}>
            {renderQuarterPanel([nowD.getFullYear()], function(lbl){ setFilter(lbl); setQOpen(false); })}
          </div>}
        </div>
        {/* 📅 Date sits LAST (owner 2026-10-31), same order as the sales header. */}
        {dayPickerEl}
      </div>
    </div>

    {sec("Key Metrics")}
    {(function(){
      // Sparkline bars come from the server series (kpiSpark), one per metric,
      // Cairo-bucketed. Position 6 = today, position 0 = 6 days ago — matches
      // the weekday strip rendered under each card.
      // KPI values come from /api/dashboard/admin-stats so the cards render
      // without depending on p.leads being parsed + derived. While the request
      // is in flight (adminStats === null) cards show a "—" placeholder so the
      // user gets immediate feedback after a filter change. Fetch failure
      // returns {} → counts read as 0.
      var as = adminStats;
      var loadingKpi = as === null;
      var as_int   = (as && as.interested) || {};
      var as_meet  = (as && as.meetings)   || {};
      var as_deals = (as && as.deals)      || {};
      var v_leads  = loadingKpi ? "—" : (as.leads || 0);
      var v_dr     = loadingKpi ? "—" : (as.dailyRequests || 0);
      var v_int    = loadingKpi ? "—" : (as_int.count || 0);
      var v_meet   = loadingKpi ? "—" : (as_meet.count || 0);
      // CALLBACKS — leads + Daily Requests, the merged trio the server built
      // with the Callback Handling card's own predicates (R2): "<handled> / <due>"
      // for the SELECTED period, "N overdue · N pending" on the sub-line.
      // null (a role with no lead scope, or a failed fetch) renders an em dash,
      // never 0: "nothing was due" and "we do not know" are different answers.
      var as_cb    = (as && as.callbacks) || null;
      var cbLbl    = loadingKpi ? { value: "\u2014", sub: "" } : callbackTrioLabel(as_cb);
      var v_cb     = cbLbl.value;
      var s_cb     = cbLbl.sub;
      var v_deals  = loadingKpi ? "—" : (as_deals.count || 0);
      // R3 amended (2026-09-02): Interested, Meetings and Deals carry a bare
      // "N%" — the server's share of the Leads tile — with no denominator
      // wording; Leads and Daily Requests read "in period". Rendered through
      // the ONE string rule since item 4, so a real-but-small share reads
      // "0.5%" instead of the "0%" it used to print beside a non-zero count.
      var s_int    = loadingKpi ? "" : pctLabelOf(as_int.pct,   as_int.count,   as.leads);
      // Meetings: ONE number, every meeting held in the period (lead + Daily
      // Request), no sub-line — ruling 2026-09-05 (second).
      var s_meet   = "";
      // Deals: ONE number too, every approved deal closed in the period (lead +
      // DR), no sub-line — ruling 2026-09-07. Its share of Leads compared a
      // period's closings against that period's INTAKE, two different
      // populations, so the server sends pct null and there is nothing to render.
      var s_deals  = "";
      return <div className="crm-dash-kpi" style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(auto-fit,minmax(160px,1fr))",gap:isMobile?10:14,marginBottom:0}}>
        {kpiCard("Leads",v_leads,"in period","linear-gradient(135deg, #43c6db, #3b5cb8)","#ffffff",openLeadsForPeriod,kpiSpark("leads"))}
        {kpiCard("Daily Requests",v_dr,"in period","linear-gradient(135deg, #56ab2f, #a8e063)","#ffffff",openDrsForPeriod,kpiSpark("dailyRequests"))}
        {kpiCard("Interested",v_int,s_int,"linear-gradient(135deg, #f46b45, #eea849)","#ffffff",openInterestedForPeriod,kpiSpark("interested"))}
        {kpiCard("Meetings",v_meet,s_meet,"linear-gradient(135deg, #a18cd1, #e8a4c8)","#ffffff",openMeetingsForPeriod,kpiSpark("meetings"))}
        {kpiCard("Callbacks",v_cb,(as_cb ? cbSplitNode(as_cb) : s_cb),"linear-gradient(135deg, #e52d27, #b31217)","#ffffff",openOverdueForPeriod,kpiSpark("callbacks"))}
        {kpiCard("Deals",v_deals,s_deals,"linear-gradient(135deg, #f953c6, #b91d73)","#ffffff",openDealsForPeriod,kpiSpark("deals"))}
      </div>;
    })()}

    {sec("Campaigns & Pipeline")}
    <div className="crm-dash-row" style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fit, minmax(300px, 1fr))",gap:isMobile?10:14,marginBottom:14}}>
      {card(<>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{minWidth:0}}>
            <div style={{fontSize:15,fontWeight:700,color:"#0F172A"}}>Campaign &amp; Source Performance</div>
            {/* D3: a COHORT view — leads created in the period; Int / Meet / Deals
                are the cumulative funnel on that cohort (a Meeting record, an
                approved dated deal), never a status proxy. */}
            <div style={{fontSize:10,fontWeight:600,color:"#94A3B8",marginTop:2}}>Cohort: leads created {dashCohortPhrase(filter)} &middot; deals on this cohort</div>
          </div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6,minWidth:0}}>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
              {sourceLegend(campaignSources, campaignPerf).map(function(s){return <span key={s.source} style={{fontSize:10,color:"#64748B",display:"flex",alignItems:"center",gap:3}}><span style={{width:6,height:6,borderRadius:"50%",background:s.color,display:"inline-block"}}/>{s.source} {s.leads}</span>;})}
            </div>
          </div>
        </div>
        {isMobile ? (
          // MOBILE: stacked card-per-campaign layout. The grid table (desktop
          // path below) tried to cram 6 columns into a phone-width container;
          // the name column clipped, headers and values drifted out of
          // alignment, and rows rendered inconsistently once any cell's
          // content shrank. A card per campaign removes the alignment burden
          // entirely — each card is full-width, with the campaign name
          // wrapping freely on top and a 5-stat mini-grid (Leads / Int. /
          // Meet. / EOI / Deals) below where every number sits right under
          // its own label, plus the Conv. and Quality badges on the header row.
          <div style={{display:"flex",flexDirection:"column",gap:10,width:"100%",minWidth:0}}>
            {fCamps.length===0 && <div style={{fontSize:12,color:"#94A3B8",padding:"10px 0"}}>{campaignPerf===null?"Loading\u2026":"No campaign data yet"}</div>}
            {(function(){
              var renderM = function(c,i){
              var srcC=sourceColor(c.source);
              return <div key={i} style={campRowStyle(c,{border:"1px solid #F1F5F9",borderRadius:12,padding:"10px 12px",background:"#FBFBFD",minWidth:0,boxSizing:"border-box"})}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:8,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,minWidth:0,flex:1}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:srcC,display:"inline-block",flexShrink:0}}/>
                    <div style={{minWidth:0,flex:1}}>
                      <div {...campNameAttrs(c)} style={Object.assign({fontSize:13,fontWeight:700,color:"#0F172A",lineHeight:1.25,overflowWrap:"anywhere",wordBreak:"break-word"}, campNameStyle(c))}>{campLabel(c)}</div>
                      <div style={{fontSize:11,color:"#94A3B8",marginTop:2,display:"flex",flexWrap:"wrap",gap:"2px 8px"}}>{c.mix.length===0?"\u2014":c.mix.map(function(m){return <span key={m.source} style={{display:"inline-flex",alignItems:"center",gap:3}}><span style={{width:6,height:6,borderRadius:"50%",background:m.color,display:"inline-block"}}/>{m.source} {m.leads}</span>;})}</div>
                    </div>
                  </div>
                  <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:5}}>{convBadge(c.conv,c.convLabel)}{c.isRollup?null:qBadge(c.quality)}</div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(5, minmax(0, 1fr))",gap:6,paddingTop:8,borderTop:"1px solid #F1F5F9"}}>
                  {[
                    {l:"Leads", v:c.leads, sub:null,         vc:"#334155"},
                    {l:"Int.",  v:c.int,   sub:c.ip,         vc:"#15803D"},
                    {l:"Meet.", v:c.meet,  sub:c.mp,         vc:"#6D28D9"},
                    {l:"EOI",   v:c.eoi,   sub:c.ep,         vc:"#B45309"},
                    {l:"Deals", v:c.deals, sub:null,         vc:"#065F46"}
                  ].map(function(s,idx){return <div key={idx} style={{textAlign:"center",minWidth:0,padding:"2px 0"}}>
                    <div style={{fontSize:10,fontWeight:600,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:2,whiteSpace:"nowrap"}}>{s.l}</div>
                    <div style={{fontSize:15,fontWeight:800,color:s.vc,lineHeight:1}}>{s.v}</div>
                    {s.sub?<div style={{fontSize:10,color:"#94A3B8",marginTop:2}}>{s.sub}</div>:null}
                  </div>;})}
                </div>
              </div>;
              };
              return <>
                {fCamps.map(function(c,i){
                  return <Fragment key={i}>{renderM(c,i)}
                    {c.isRollup && otherOpen ? <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:320,overflowY:"auto",marginTop:8}}>{otherChildren.map(function(k,j){ return renderM(k,"k"+j); })}</div> : null}
                  </Fragment>;
                })}
                {campTotals ? renderM(campTotals,"tot") : null}
              </>;
            })()}
          </div>
        ) : (
        <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
        {(function(){
          // Desktop: grid-based table, sized to fit the card with NO horizontal
          // scroll \u2014 all eight tracks visible at once.
          //
          // The EOI + Quality columns originally kept every old track at its
          // former width and just added 90px + 70px on top of a 660px minWidth.
          // Measured at a 1440px viewport that put 668px of content into a 533px
          // container: 135px of overflow with Quality clipped off the right edge.
          //
          // Refit, in order of how much width each move bought back:
          //   - numeric tracks cut to just fit their content (a 3-4 digit count
          //     over a 2-3 char percentage), not the old uniform 90px;
          //   - headers abbreviated to the same short labels the mobile card
          //     already uses (Interested -> Int., Meetings -> Meet.) \u2014 the
          //     header text was what forced those tracks wide, since it cannot
          //     wrap. Full names live on the title attribute for hover;
          //   - grid gap 4px -> 3px across eight tracks;
          //   - number 13px -> 12px, percentage subline 10px -> 9px;
          //   - the fixed 660px minWidth removed entirely, so the name column
          //     absorbs whatever is left and truncates with an ellipsis rather
          //     than pushing the table wider than its card.
          // Fixed tracks total 314px + 21px of gaps; the name column takes the
          // remainder (~198px at a 1440px viewport) and never less than 72px.
          // That 72px floor only binds once the card drops under ~410px (a
          // sub-1200px window), where it buys a clean fit instead of a
          // scrollbar; at every normal desktop width the name column is far
          // wider than its floor, so the floor costs nothing there.
          var cols = "minmax(72px, 1fr) 38px 42px 42px 38px 40px 46px 68px";
          var heads = [["Campaign \u00b7 Project","Campaign \u00b7 Project"],["Leads","Leads"],
                       ["Int.","Interested"],["Meet.","Meetings"],["EOI","Reached EOI"],
                       ["Deals","Deals"],["Conv.","Conversion (deals / leads)"],["Quality","Campaign quality grade"]];
          return <>
            <div style={{display:"grid",gridTemplateColumns:cols,gap:3,paddingBottom:8,borderBottom:"1px solid #F1F5F9",marginBottom:4}}>
              {heads.map(function(h){return <div key={h[0]} title={h[1]} style={{fontSize:11,fontWeight:700,color:"#94A3B8",textAlign:h[0]==="Campaign \u00b7 Project"?"left":"center",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{h[0]}</div>;})}
            </div>
            {fCamps.length===0 && <div style={{fontSize:12,color:"#94A3B8",padding:"10px 0"}}>{campaignPerf===null?"Loading\u2026":"No campaign data yet"}</div>}
            {(function(){
              var renderD = function(c,i){
              var srcC=sourceColor(c.source);
              // Full "Campaign \u00b7 Project \u00b7 Source" on the title attribute, since
              // the visible label is now free to truncate at the column edge.
              var fullName = campLabel(c) + (c.mix.length ? (" \u00b7 " + c.mix.map(function(m){return m.source + " " + m.leads;}).join(", ")) : "");
              return <div key={i} style={campRowStyle(c,{display:"grid",gridTemplateColumns:cols,gap:3,alignItems:"center",padding:"8px 0",borderBottom:"1px solid #F8FAFC"})}>
                <div style={{minWidth:0,overflow:"hidden"}} title={fullName}>
                  <div style={{display:"flex",alignItems:"center",gap:5,minWidth:0}}>
                    <span style={{width:7,height:7,borderRadius:"50%",background:srcC,display:"inline-block",flexShrink:0}}/>
                    <span {...campNameAttrs(c)} style={Object.assign({fontSize:12,fontWeight:600,color:"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0}, campNameStyle(c))}>{campLabel(c)}</span>
                  </div>
                  <div style={{fontSize:10,color:"#94A3B8",paddingLeft:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.mix.map(function(m,i){return <span key={m.source}>{i>0?" \u00b7 ":""}<span style={{display:"inline-block",width:5,height:5,borderRadius:"50%",background:m.color,marginRight:3,verticalAlign:"middle"}}/>{m.source} {m.leads}</span>;})}</div>
                </div>
                <div style={{fontSize:12,fontWeight:700,textAlign:"center",color:"#334155"}}>{c.leads}</div>
                <div style={{textAlign:"center"}}><div style={{fontSize:12,fontWeight:700,color:"#15803D"}}>{c.int}</div><div style={{fontSize:9,color:"#94A3B8"}}>{c.ip}</div></div>
                <div style={{textAlign:"center"}}><div style={{fontSize:12,fontWeight:700,color:"#6D28D9"}}>{c.meet}</div><div style={{fontSize:9,color:"#94A3B8"}}>{c.mp}</div></div>
                <div style={{textAlign:"center"}}><div style={{fontSize:12,fontWeight:700,color:"#B45309"}}>{c.eoi}</div><div style={{fontSize:9,color:"#94A3B8"}}>{c.ep}</div></div>
                <div style={{fontSize:12,fontWeight:700,textAlign:"center",color:"#065F46"}}>{c.deals}</div>
                <div style={{textAlign:"center",minWidth:0}}>{convBadge(c.conv,c.convLabel)}</div>
                <div style={{textAlign:"center",minWidth:0}}>{c.isRollup?null:qBadge(c.quality)}</div>
              </div>;
              };
              return <>
                {fCamps.map(function(c,i){
                  return <Fragment key={i}>{renderD(c,i)}
                    {c.isRollup && otherOpen ? <div style={{maxHeight:320,overflowY:"auto"}}>{otherChildren.map(function(k,j){ return renderD(k,"k"+j); })}</div> : null}
                  </Fragment>;
                })}
                {campTotals ? renderD(campTotals,"tot") : null}
              </>;
            })()}
          </>;
        })()}
        </div>
        )}
      </>)}
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {card(<>
          {/* RESPONSE WATCH. First response = the EARLIER of a Call/WhatsApp tap and any
              write, per holder, from that holder's assignedAt (response-watch.js). Top:
              this period's assigned leads still waiting. Bottom: per-agent MEDIAN
              first-response time (no-action leads excluded). */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
            <div style={{fontSize:15,fontWeight:700,color:"#0F172A"}}>Response Watch</div>
            <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:6,background:"#FEE2E2",color:"#991B1B"}}>{rwData===null?"\u2026":(rwData.counts.none+rwData.counts.tapped)}</span>
          </div>
          <div style={{fontSize:11,color:"#94A3B8",marginBottom:10}}>{periodLabelForFilter(filter)}{" \u00b7 leads assigned in the period"}</div>
          {rwData===null ? <div style={{fontSize:12,color:"#94A3B8",padding:"10px 0"}}>Loading\u2026</div> : <>
            {(function(){
              var w = dashPeriod(filter);
              var openRw = function(state){
                if (p.setSpecialFilter) p.setSpecialFilter({ type:"responseWatch", state:state,
                  rwFrom:new Date(w.from).toISOString(), rwTo:new Date(Math.max(w.toNow, w.from+1)).toISOString(), periodLabel:w.label });
                if (p.setFilter) p.setFilter("all");
                p.nav("leads");
              };
              var fmtH = function(h){ return h < 1 ? "<1h" : h < 48 ? h+"h" : Math.floor(h/24)+"d"; };
              var rows = rwData.none.map(function(r){ return Object.assign({kind:"none"}, r); })
                .concat(rwData.tapped.map(function(r){ return Object.assign({kind:"tapped"}, r); }))
                .sort(function(a,b){ return new Date(b.assignedAt) - new Date(a.assignedAt); });
              var now0 = Date.now();
              return <>
                <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                  <button onClick={function(){openRw("none");}} disabled={!rwData.counts.none} style={{fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:8,border:"1px solid #FECACA",background:"#FEF2F2",color:"#991B1B",cursor:rwData.counts.none?"pointer":"default",opacity:rwData.counts.none?1:0.5}}>{"No action \u00b7 "+rwData.counts.none}</button>
                  <button onClick={function(){openRw("tapped");}} disabled={!rwData.counts.tapped} style={{fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:8,border:"1px solid #BFDBFE",background:"#EFF6FF",color:"#1D4ED8",cursor:rwData.counts.tapped?"pointer":"default",opacity:rwData.counts.tapped?1:0.5}}>{"\ud83d\udcde Called, no feedback \u00b7 "+rwData.counts.tapped}</button>
                </div>
                {rows.length===0 ? <div style={{fontSize:12,color:"#94A3B8",padding:"8px 0"}}>{"\u2705"} Every lead assigned in this period has a first response</div>
                 : <div style={{maxHeight:260,overflowY:"auto",WebkitOverflowScrolling:"touch",marginRight:-6,paddingRight:6}}>
                  {rows.map(function(r,i){
                    var waitH = r.kind==="tapped" && r.firstTapAt ? Math.floor((now0-new Date(r.firstTapAt).getTime())/3600000) : r.waitingHours;
                    return <div key={r.kind+String(r.leadId)+String(r.agentId)} onClick={function(){openUntouchedLead(r);}} style={{padding:"7px 0",borderBottom:i<rows.length-1?"1px solid #F8FAFC":"none",cursor:"pointer"}}>
                      <div style={{fontSize:13,fontWeight:600,color:"#0F172A"}}>{r.name||"\u2014"} <span style={{color:"#94A3B8",fontWeight:500}}>{"\u2192"} {r.agentName}</span></div>
                      <div style={{fontSize:11,color:r.kind==="none"&&waitH>=24?"#DC2626":"#92400E"}}>{r.kind==="tapped" ? "\ud83d\udcde called \u00b7 awaiting feedback "+fmtH(waitH) : "waiting "+fmtH(waitH)}</div>
                    </div>;
                  })}
                </div>}
                <div style={{fontSize:12,fontWeight:700,color:"#0F172A",margin:"14px 0 6px"}}>Median first response <span style={{fontSize:10,fontWeight:500,color:"#94A3B8"}}>{"\u00b7 slowest first \u00b7 no-action leads excluded"}</span></div>
                {rwData.agents.length===0 ? <div style={{fontSize:12,color:"#94A3B8"}}>No responses yet in this period</div>
                 : <div style={{maxHeight:200,overflowY:"auto",marginRight:-6,paddingRight:6}}>
                  {rwData.agents.map(function(a,i){
                    var m = a.medianMinutes;
                    var txt = m < 60 ? Math.round(m)+" min" : m < 2880 ? (Math.round(m/6)/10)+"h" : (Math.round(m/144)/10)+"d";
                    return <div key={a.agentId} style={{display:"flex",justifyContent:"space-between",gap:8,padding:"5px 0",borderBottom:i<rwData.agents.length-1?"1px solid #F8FAFC":"none"}}>
                      <span style={{fontSize:12,color:"#334155"}}>{a.agentName} <span style={{color:"#94A3B8",fontSize:10}}>{"("+a.n+")"}</span></span>
                      <span style={{fontSize:12,fontWeight:700,color:m>=240?"#DC2626":m>=60?"#92400E":"#15803D"}}>{txt}</span>
                    </div>;
                  })}
                </div>}
              </>;
            })()}
          </>}
        </>)}
        {card(<>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{fontSize:15,fontWeight:700,color:"#0F172A"}}>{activitiesTitleForFilter(filter)}</div>
            <span style={{fontSize:11,fontWeight:600,color:"#1D4ED8",cursor:"pointer"}} onClick={function(){setSeeAllOpen(true);}}>View All ({todayActsAll.length})</span>
          </div>
          <div style={{maxHeight:420,overflowY:"auto",WebkitOverflowScrolling:"touch",marginRight:-6,paddingRight:6}}>
          {/* Cap the inline card at the 30 most-recent rows. todayActsAll can
              hold ~1000 entries on Quarter filter; rendering them all (5 nested
              styled divs each) was the dominant Layout + getHostSibling cost on
              filter click (~11s + 8s per flamegraph). The card has maxHeight:420
              so only ~8 rows are ever visible at once; the rest were behind a
              scroll inside the card. The full list is still reachable via the
              "View All (N)" button — N still reflects the true count. */}
          {todayActsAll.length===0 ? <div style={{fontSize:12,color:"#94A3B8",padding:"10px 0"}}>No activity in {periodLabelForFilter(filter)}</div> : todayActsAll.slice(0,30).map(function(a,i,arr){
            var aid = a.userId&&a.userId._id?a.userId._id:a.userId;
            var aName = userNameOf(a.userId, p.users) || agentName(aid);
            var lName = resolveClientName(a);
            var srcTag = activitySource(a);
            var aNote = a.note||a.notes||a.feedback||a.details||"";
            var actionLabel = actLabel(a);
            var feedbackText = aNote;
            if (a.type==="status_change") {
              var bracketM = aNote.match(/^\s*\[([^\]]+)\]\s*([\s\S]*)$/);
              var colonM = aNote.match(/^\s*Status\s*:\s*([^|]+?)(?:\s*\|\s*([\s\S]*))?$/i);
              var statusName = "", fb = "";
              if (bracketM) { statusName = bracketM[1].trim(); fb = (bracketM[2]||"").trim(); }
              else if (colonM) { statusName = colonM[1].trim(); fb = (colonM[2]||"").trim(); }
              else { statusName = "changed"; fb = aNote; }
              actionLabel = "Status: "+(statusName||"changed");
              feedbackText = fb;
            } else if (a.type==="call") {
              actionLabel = "Call initiated";
              feedbackText = aNote.replace(/^\s*[\ud83d\udcde\ud83d\udcde]+\s*/,"").replace(/^Call initiated\s*[-\u2014:]?\s*/i,"").trim();
            } else if (a.type==="note") {
              actionLabel = "Note added";
            } else if (a.type==="reassign") {
              actionLabel = "Reassign";
              feedbackText = "";
            } else if (a.type==="meeting") {
              actionLabel = "Meeting booked";
            } else if (a.type==="daily_request" || (aNote.toLowerCase().indexOf("daily")>=0)) {
              actionLabel = "DailyReq: "+((aNote.split(":")[1]||"").trim().split("|")[0].trim()||"updated");
              feedbackText = "";
            }
            var noteLc = aNote.toLowerCase();
            var ic;
            if (a.type==="call") ic={icon:"\ud83d\udcde",bg:"#DCFCE7",fg:"#166534"};
            else if (a.type==="meeting" || noteLc.indexOf("deal")>=0) ic={icon:"\ud83c\udfc6",bg:"#FEF3C7",fg:"#92400E"};
            else if (a.type==="status_change") ic={icon:"\u2197",bg:"#EDE9FE",fg:"#5B21B6"};
            else if (a.type==="note") ic={icon:"\ud83d\udcdd",bg:"#FFE4E6",fg:"#9F1239"};
            else if (noteLc.indexOf("callback")>=0) ic={icon:"\ud83d\udcc5",bg:"#DBEAFE",fg:"#1D4ED8"};
            else ic={icon:"\u2022",bg:"#F1F5F9",fg:"#64748B"};
            var actLeadId = activityLeadIdStr(a);
            // Gate the click on canOpenActivity — not just on whether we have
            // an id string. Rows for deleted DRs (id exists but no doc in
            // p.dailyReqs and no phone to fall back to) are truly dead and
            // should render as cursor:default with no handler.
            var onActClick = canOpenActivity(a) ? function(){ openActivity(a); } : null;
            // Archived source records stay in the history but must not be
            // clickable — match the existing archived visual style used on
            // the Archive page rows (opacity 0.7, cursor default).
            var isArchivedRow = isActivityArchived(a);
            // Spec: "client name, action type, agent name, exact time" — show client first, agent on the subtitle.
            var clientName = lName || "Unknown client";
            // Daily Request rows show the full feedback text (spec — no truncation).
            // Lead rows stay single-line with the existing 80-char cap so long notes
            // don't blow up the dashboard.
            var isDrRow = srcTag === "DR";
            var feedbackTextDisplay = maskPhonesInText(feedbackText);
            if (!isDrRow && feedbackTextDisplay && feedbackTextDisplay.length>80) {
              feedbackTextDisplay = feedbackTextDisplay.slice(0,80)+"\u2026";
            }
            var subtitleStyle = isDrRow
              ? { fontSize:11, color:"#64748B", marginTop:1, wordBreak:"break-word", whiteSpace:"normal" }
              : { fontSize:11, color:"#64748B", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" };
            return <div key={String(a._id||("k"+i))} onClick={onActClick} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"9px 0",borderBottom:i<arr.length-1?"1px solid #F1F5F9":"none",cursor:onActClick?"pointer":"default",opacity:isArchivedRow?0.7:1}}>
              <div style={{width:34,height:34,borderRadius:"50%",background:ic.bg,color:ic.fg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0,marginTop:2}}>{ic.icon}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:"#0F172A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  <span style={{fontWeight:600,color:ic.fg}}>{actionLabel}</span>
                  {" \u2014 "}{clientName}
                  {srcTag&&<span style={{marginLeft:6,fontSize:9,fontWeight:700,padding:"1px 5px",borderRadius:4,background:"#DBEAFE",color:"#1D4ED8",verticalAlign:"middle"}} title="Daily Request">{srcTag}</span>}
                </div>
                <div style={subtitleStyle}>
                  {aName}{feedbackTextDisplay?<span> {"\u00b7"} <span style={{fontWeight:700,color:"#334155"}}>{feedbackTextDisplay}</span></span>:null}
                </div>
              </div>
              <div style={{fontSize:11,color:"#94A3B8",flexShrink:0,fontWeight:600,marginTop:2}}>{exactTime(a.createdAt)}</div>
            </div>;
          })}
          </div>
        </>)}
      </div>
    </div>

    {sec("Team Performance")}
    <div style={{marginBottom:14}}>
      {renderAgentPerformanceCard(rangeStart, rangeEnd)}
    </div>

    <div className="crm-dash-row" style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fit,minmax(260px,1fr))",gap:isMobile?10:14,gridAutoRows:ROW4_H+"px"}}>
      {card(<>
        <div style={{fontSize:15,fontWeight:700,color:"#0F172A",marginBottom:12}}>Management Alerts</div>
        {(function(){
          var gotoSpecial = function(type){
            if (p.setFilter) p.setFilter("all");
            if (p.setSpecialFilter) p.setSpecialFilter({type:type});
            if (p.nav) p.nav("leads");
          };
          // All eight counts come from GET /api/dashboard/alerts (full Lead
          // collection) instead of scanning `leads`, which post-bootstrap-shrink
          // is only the 100 newest-created leads. `null`/undefined means the
          // fetch hasn't resolved or failed — render an em dash, never 0, so
          // "loading" and "genuinely zero" can't look the same.
          var A = alertsData;
          var loadingA = A === null;
          var n = function(v){ return (loadingA || v === null || v === undefined) ? "\u2014" : v; };
          // Overdue reads the SAME period trio the Key Metrics tile does — the
          // row and the tile are one number, and both follow the period picker.
          // It is the `overdue` third of it: callbacks that came due inside the
          // period and still have no action after the deadline.
          var overdueN = (adminStats && adminStats.callbacks && typeof adminStats.callbacks.overdue === "number")
            ? adminStats.callbacks.overdue : null;
          var activeAgentsN = (!loadingA && A.activeAgentCount) ? A.activeAgentCount : null;
          var rows=[
            {dot:"#F97316",n:n(overdueN),t:"overdue callbacks",s:"past scheduled, no action since \u00b7 "+callbackWindow.label,onClick:openOverdueForPeriod},
            {dot:"#DC2626",n:n(loadingA?null:A.stale48h),t:"no update in 48h",s:"last action > 48h ago · incl. EOI",onClick:function(){gotoSpecial("stale48h");}},
            {dot:"#6366F1",n:n(loadingA?null:A.heavyRotNoDeal),t:"rotated > 3\u00d7 no deal",s:"churning without closing",onClick:function(){gotoSpecial("rotatedThisMonth");}},
            {dot:"#0EA5E9",n:n(loadingA?null:A.inactiveAgentsToday),t:"agents no activity today",s:activeAgentsN===null?"active sales staff":("of "+activeAgentsN+" active sales staff"),onClick:function(){if(p.nav) p.nav("team");}},
            {dot:"#EF4444",n:n(untouchedTotal),t:"idle over 24h",s:"open leads, idle 24h+ · excludes closed",onClick:function(){gotoSpecial("untouched");}},
            {dot:"#F59E0B",n:n(loadingA?null:A.missingFeedback),t:"missing feedback",s:"no notes",onClick:function(){gotoSpecial("missingFeedback");}},
            {dot:"#6366F1",n:n(loadingA?null:A.rotationsMonth),t:"rotations this month",s:loadingA?"auto \u00b7 manual":((A.rotationsAuto||0)+" auto \u00b7 "+(A.rotationsManual||0)+" manual"),onClick:function(){gotoSpecial("rotatedThisMonth");}},
            {dot:"#7C3AED",n:n(loadingA?null:A.lockedNoRotation),t:"leads locked",s:"noRotation flag",onClick:function(){gotoSpecial("noRotation");}}
          ];
          // Takes whatever height is left under the title and scrolls inside it.
          return <div style={fillList}>
            {rows.map(function(a,i){
            return <div key={i} onClick={a.onClick} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:i<rows.length-1?"1px solid #F8FAFC":"none",cursor:"pointer"}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:a.dot,flexShrink:0}}/>
              <div style={{flex:1,minWidth:0}}><div style={{fontSize:13,fontWeight:600,color:"#0F172A"}}><span style={{fontSize:18,fontWeight:800,color:a.dot,marginRight:6}}>{a.n}</span>{a.t}</div><div style={{fontSize:11,color:"#94A3B8"}}>{a.s}</div></div>
            </div>;
          })}
          </div>;
        })()}
      </>, fillCard)}
        {card(<CallbackHandlingCard data={cbCompliance} filter={filter}
          onPickAgent={function(uid){ if(p.setInitAgentFilter) p.setInitAgentFilter(uid);
            if(p.setFilter) p.setFilter("CallBack"); if(p.nav) p.nav("leads"); }}/>, fillCard)}
      {/* Change 2 — Call Outcomes widget replaced with the same Rank Team
          widget the sales view uses. Admin mode hides the personal rank /
          score blocks; data feed (salesRanking) is already re-fetched when
          the global filter changes, so the ranking reflects the selected
          period. */}
      {rankWidget({ mode: "admin", rangeLabel: periodLabelForFilter(filter) })}
      {card(<>
        <div style={{fontSize:15,fontWeight:700,color:"#0F172A",marginBottom:12}}>Leads by Status</div>
        {(function(){
          // Server-sourced: /api/leads/counts byStatus over the FULL collection
          // (statusDist), replacing the scan of `leads` — which post-bootstrap-
          // shrink is only the 100 newest-created leads, and was additionally
          // filtered by the current assignment's assignedAt, so this card read
          // as mostly zeros. Range behaviour is unchanged: the fetch is keyed on
          // the same period filter. NOTE the window is now the lead's createdAt
          // (what /api/leads/counts scopes by) rather than its assignment date.
          //
          // byStatus keys are raw Lead.status values, so fold the spaced
          // variants onto the canonical keys the rows below use.
          var VARIANTS = {
            "New Lead":"NewLead", "Meeting Done":"MeetingDone", "No Answer":"NoAnswer",
            "Hot Case":"HotCase", "Not Interested":"NotInterested", "Call Back":"CallBack",
            "Done Deal":"DoneDeal"
          };
          var loadingS = statusDist === null;
          var sc = {};
          var assignTotal = 0;
          Object.keys(statusDist || {}).forEach(function(k){
            var key = VARIANTS[k] || k;
            var v = statusDist[k] || 0;
            sc[key] = (sc[key] || 0) + v;
            assignTotal += v;   // denominator counts EVERY status, including any
                                // outside the nine rows, so the bars stay honest
          });
          var denom = Math.max(1,assignTotal);
          var rows=[["New Lead","NewLead","#3B82F6"],["Potential","Potential","#10B981"],["Hot Case","HotCase","#F59E0B"],["Call Back","CallBack","#EF4444"],["Meeting","MeetingDone","#8B5CF6"],["Not Int.","NotInterested","#94A3B8"],["No Answer","NoAnswer","#CBD5E1"],["EOI","EOI","#0EA5E9"],["Done Deal","DoneDeal","#065F46"]];
          // Nine fixed-height bars can't fill a fixed-height card on their own,
          // so distribute them down the available space instead of leaving the
          // remainder as white space under the last bar. marginBottom:-7 cancels
          // the trailing bRow margin so the last bar sits flush with the bottom.
          // This is the only card in the row with no scrollable list.
          var bars = loadingS
            ? rows.map(function(s){return bRow(s[0],"\u2014",0,s[2],null);})
            : rows.map(function(s){return bRow(s[0],sc[s[1]]||0,denom,s[2],function(){ if(p.setFilter) p.setFilter(s[1]); if(p.nav) p.nav("leads"); });});
          return <div style={{flex:1,minHeight:0,display:"flex",flexDirection:"column",justifyContent:"space-between",marginBottom:-7}}>{bars}</div>;
        })()}
      </>, fillCard)}
    </div>
    {seeAllOpen && <div data-overlay-above="true" onClick={function(){setSeeAllOpen(false);}} style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(15,23,42,0.55)",zIndex:1000,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"calc(40px + env(safe-area-inset-top, 0px)) 16px",overflowY:"auto"}}>
      <div onClick={function(e){e.stopPropagation();}} style={{background:"#fff",borderRadius:16,maxWidth:640,width:"100%",padding:"20px 22px",boxShadow:"0 10px 40px rgba(0,0,0,0.2)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{fontSize:17,fontWeight:700,color:"#0F172A"}}>{activitiesTitleForFilter(filter)} ({todayActsAll.length})</div>
          <span style={{fontSize:13,fontWeight:600,color:"#64748B",cursor:"pointer",padding:"4px 10px"}} onClick={function(){setSeeAllOpen(false);}}>{"\u2715"} Close</span>
        </div>
        <div style={{maxHeight:"70vh",overflowY:"auto",paddingRight:4}}>
          {todayActsAll.length===0 ? <div style={{fontSize:13,color:"#94A3B8",padding:"20px 0",textAlign:"center"}}>No activity in {periodLabelForFilter(filter)}</div> : todayActsAll.map(function(a,i){
            var aid = a.userId&&a.userId._id?a.userId._id:a.userId;
            var aName = userNameOf(a.userId, p.users) || agentName(aid);
            var lName = resolveClientName(a);
            var srcTagM = activitySource(a);
            var aNote = a.note||a.notes||a.feedback||a.details||"";
            var actionLabel = actLabel(a);
            var feedbackText = aNote;
            if (a.type==="status_change") {
              var bracketM = aNote.match(/^\s*\[([^\]]+)\]\s*([\s\S]*)$/);
              var colonM = aNote.match(/^\s*Status\s*:\s*([^|]+?)(?:\s*\|\s*([\s\S]*))?$/i);
              var statusName = "", fb = "";
              if (bracketM) { statusName = bracketM[1].trim(); fb = (bracketM[2]||"").trim(); }
              else if (colonM) { statusName = colonM[1].trim(); fb = (colonM[2]||"").trim(); }
              else { statusName = "changed"; fb = aNote; }
              actionLabel = "Status: "+(statusName||"changed");
              feedbackText = fb;
            } else if (a.type==="call") { actionLabel = "Call initiated"; feedbackText = aNote.replace(/^\s*[\ud83d\udcde\ud83d\udcde]+\s*/,"").replace(/^Call initiated\s*[-\u2014:]?\s*/i,"").trim(); }
            else if (a.type==="note") { actionLabel = "Note added"; }
            else if (a.type==="reassign") { actionLabel = "Reassign"; feedbackText = ""; }
            else if (a.type==="meeting") { actionLabel = "Meeting booked"; }
            // Daily Request rows: show full feedback; Lead rows keep the 80-char cap.
            var isDrRowM = srcTagM === "DR";
            var feedbackTextDisplayM = maskPhonesInText(feedbackText);
            if (!isDrRowM && feedbackTextDisplayM && feedbackTextDisplayM.length>80) feedbackTextDisplayM = feedbackTextDisplayM.slice(0,80)+"\u2026";
            var noteLc = aNote.toLowerCase();
            var ic;
            if (a.type==="call") ic={icon:"\ud83d\udcde",bg:"#DCFCE7",fg:"#166534"};
            else if (a.type==="meeting" || noteLc.indexOf("deal")>=0) ic={icon:"\ud83c\udfc6",bg:"#FEF3C7",fg:"#92400E"};
            else if (a.type==="status_change") ic={icon:"\u2197",bg:"#EDE9FE",fg:"#5B21B6"};
            else if (a.type==="note") ic={icon:"\ud83d\udcdd",bg:"#FFE4E6",fg:"#9F1239"};
            else if (noteLc.indexOf("callback")>=0) ic={icon:"\ud83d\udcc5",bg:"#DBEAFE",fg:"#1D4ED8"};
            else ic={icon:"\u2022",bg:"#F1F5F9",fg:"#64748B"};
            var actLeadIdM = activityLeadIdStr(a);
            // Same orphan-safe gate as the compact card above.
            var onActClickM = canOpenActivity(a) ? function(){ setSeeAllOpen(false); openActivity(a); } : null;
            var isArchivedRowM = isActivityArchived(a);
            var clientNameM = lName || "Unknown client";
            var subtitleStyleM = isDrRowM
              ? { fontSize:12, color:"#64748B", marginTop:2, wordBreak:"break-word", whiteSpace:"normal" }
              : { fontSize:12, color:"#64748B", marginTop:2 };
            return <div key={a._id ? String(a._id) : ("k"+i)} onClick={onActClickM} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 0",borderBottom:i<todayActsAll.length-1?"1px solid #F1F5F9":"none",cursor:onActClickM?"pointer":"default",opacity:isArchivedRowM?0.7:1}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:ic.bg,color:ic.fg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0,marginTop:2}}>{ic.icon}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:"#0F172A"}}>
                  <span style={{fontWeight:600,color:ic.fg}}>{actionLabel}</span> {"\u2014 "}{clientNameM}
                  {srcTagM&&<span style={{marginLeft:6,fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:4,background:"#DBEAFE",color:"#1D4ED8",verticalAlign:"middle"}} title="Daily Request">{srcTagM}</span>}
                </div>
                <div style={subtitleStyleM}>{aName}{feedbackTextDisplayM?<span> {"\u00b7"} <span style={{fontWeight:700,color:"#334155"}}>{feedbackTextDisplayM}</span></span>:null}</div>
              </div>
              <div style={{fontSize:11,color:"#94A3B8",flexShrink:0,fontWeight:600,marginTop:2}}>{exactTime(a.createdAt)}</div>
            </div>;
          })}
        </div>
      </div>
    </div>}
  </div>;
};


// ===== MEETINGS ARCHIVE (owner-only) =====
// Permanent archive of every meeting. Owner-only at three layers: the sidebar
// item, the renderPage route guard (falls back to DashboardPage), and the
// backend requireOwner gate.
//
// ALL THREE NOW READ THE SAME ANSWER (2026-08-27). This page used to gate on
// p.cu.isOwner while the sidebar and the router asked the envelope, so the two
// could disagree — and did: the envelope grants `meetings` to the app-store
// demo `viewer` (LEGACY_SIDEBAR_RULES: isOwnerFlag || isViewer) and requireOwner
// admits that account on purpose, so the reviewer got a menu entry, clicked it,
// and was told "Not available." by the one layer that had its own copy of the
// rule. It also deadlocked the phase-2 bootstrap for anyone in that state: the
// page refused before fetching, so it never reported its rows and the deferred
// half of the boot never went out (measured: 4 GETs instead of 13).
//
// canSeePage() is the same predicate the sidebar filters NAV_ITEMS with and the
// same one renderPage guards on, so a disagreement is no longer expressible. Loads ALL rows by default — fetches EVERY page,
// newest first — and shows a "Showing N of N" count so the rendered rows provably
// equal `total` from the API (stays correct after the Phase-4 backfill). Filters
// (date presets + custom range + agent) are optional and combinable, never a
// precondition for seeing data. No WebSocket — refetches via REST. Export is
// Phase 3b (not here yet).
var MeetingsPage = function(p) {
  // P5 (2026-11-02): a meeting recorded anywhere (lead or DR, by anyone the
  // caller may see) re-runs the fetch below with the SAME filters — the
  // team-as-of scoping lives in the route, so the live re-read keeps it.
  var liveMeetingRev = useLiveRev("meeting");
  var t = p.t;
  var maySee = canSeePage(p.access, "meetings");
  // MeetingDone status colour (reused constant, no new hex) — the page accent.
  var md = STATUSES(t).find(function(s){ return s.value==="MeetingDone"; }) || { color:C.primary, bg:"#F3E8FF" };
  var mdColor = md.color;

  // Local copy of the Reports date-preset helper (a pattern, not a shared component).
  var presetRange = function(preset){
    var now=new Date(); var y=now.getFullYear(), m=now.getMonth(), d=now.getDate();
    var todayStart=new Date(y,m,d,0,0,0,0).getTime();
    if(preset==="today")     return { from:todayStart, to:now.getTime() };
    if(preset==="yesterday") return { from:new Date(y,m,d-1,0,0,0,0).getTime(), to:todayStart };
    if(preset==="thisWeek"){ var dow=now.getDay(); var since=(dow-6+7)%7; return { from:new Date(y,m,d-since,0,0,0,0).getTime(), to:now.getTime() }; }
    if(preset==="thisMonth") return { from:new Date(y,m,1,0,0,0,0).getTime(), to:now.getTime() };
    if(preset==="thisQuarter"){ var qs=m<3?0:m<6?3:m<9?6:9; return { from:new Date(y,qs,1,0,0,0,0).getTime(), to:now.getTime() }; }
    return null;
  };
  var presets=[
    { id:"today", label:"Today" }, { id:"yesterday", label:"Yesterday" },
    { id:"thisWeek", label:"This Week" }, { id:"thisMonth", label:"This Month" }
  ];
  var fmtDateInput = function(ms){ if(!ms) return ""; var dt=new Date(ms); var z=function(n){return (n<10?"0":"")+n;}; return dt.getFullYear()+"-"+z(dt.getMonth()+1)+"-"+z(dt.getDate()); };
  var fmtMeetingDate = function(v){ return fmtDateTime12(v, undefined, { sep:", " }); };
  // Table-only short form (no year) so the pinned date column stays narrow; the cell's title keeps the full form.
  var fmtMeetingDateShort = function(v){ return fmtDateTime12(v, undefined, { year:false, sep:", " }); };

  // Agent filter — active first, then inactive (labeled), like the leads filter.
  var agentFilterOpts = (function(){
    var us=p.users||[];
    var roleOk=function(u){ return positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader"||positionOf(u)==="director"; };
    var act=us.filter(function(u){ return roleOk(u)&&u.active; });
    var ina=us.filter(function(u){ return roleOk(u)&&!u.active; });
    return act.concat(ina).map(function(u){ return { value:gid(u), label:u.name+(u.active?"":" (inactive)") }; });
  })();

  // MULTI-SELECT (2026-08-06): agentId is an ARRAY of user ids; [] = all agents.
  // The dashboard Meetings tile hands its period in (p.meetingsInit) so the
  // click lands on exactly the rows it counted (R7); otherwise unfiltered.
  // sourceType "" = All; "lead" | "dailyRequest" narrow to the record's origin,
  // server-side (buildMeetingsFilter) so the list and the export agree (2026-10-01).
  var fSt=useState(p.meetingsInit && p.meetingsInit.from
    ? { preset:"custom", from:new Date(p.meetingsInit.from).getTime(), to:new Date(p.meetingsInit.to).getTime(), agentId:[], sourceType:"" }
    : { preset:"", from:null, to:null, agentId:[], sourceType:"" }); var filters=fSt[0], setFilters=fSt[1];
  useEffect(function(){
    if (p.meetingsInit && p.meetingsInit.from) {
      setFilters(function(f){ return Object.assign({}, f, { preset:"custom", from:new Date(p.meetingsInit.from).getTime(), to:new Date(p.meetingsInit.to).getTime() }); });
      if (p.setMeetingsInit) p.setMeetingsInit(null);   // consumed: the page owns its filters from here
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.meetingsInit]);
  var rSt=useState([]); var rows=rSt[0], setRows=rSt[1];
  var tSt=useState(0); var total=tSt[0], setTotal=tSt[1];
  var lSt=useState(true); var loading=lSt[0], setLoading=lSt[1];
  var eSt=useState(""); var err=eSt[0], setErr=eSt[1];
  var xSt=useState(false); var exporting=xSt[0], setExporting=xSt[1];
  var nowY=new Date().getFullYear();
  var ySt=useState(nowY); var yearSel=ySt[0], setYearSel=ySt[1];
  var yrsSt=useState([nowY]); var years=yrsSt[0], setYears=yrsSt[1];
  // Feedback cells render on a single truncated line; clicking one toggles it
  // open (full text) — keyed by meeting _id so multiple rows can be expanded.
  var fbSt=useState({}); var fbOpen=fbSt[0], setFbOpen=fbSt[1];
  var toggleFb=function(id){ setFbOpen(function(o){ var n=Object.assign({},o); if(n[id]) delete n[id]; else n[id]=1; return n; }); };

  // Quarter (Q1-Q4) of the selected year → a from/to range. Year list is derived
  // from the archive's actual date span (see the fetch effect), never hardcoded.
  var quarterRange=function(year,q){ var sm=(q-1)*3; return { from:new Date(year,sm,1,0,0,0,0).getTime(), to:new Date(year,sm+3,0,23,59,59,999).getTime() }; };
  var setQuarter=function(q){ var r=quarterRange(yearSel,q); setFilters(function(f){ return Object.assign({},f,{preset:"q"+q, from:r.from, to:r.to}); }); };
  var changeYear=function(v){ var y=parseInt(v,10)||nowY; setYearSel(y); setFilters(function(f){ if(f.preset && /^q[1-4]$/.test(f.preset)){ var q=parseInt(f.preset.charAt(1),10); var r=quarterRange(y,q); return Object.assign({},f,{from:r.from,to:r.to}); } return f; }); };
  // Filter chip — MeetingDone-coloured when active (accent), neutral otherwise.
  var chipStyle=function(active){ return { padding:"5px 10px", borderRadius:7, border:"1px solid", borderColor:active?mdColor:C.border, background:active?mdColor+"14":"#fff", color:active?mdColor:C.textLight, fontSize:11, fontWeight:600, cursor:"pointer" }; };

  var setPreset=function(id){ var r=presetRange(id); if(!r){ setFilters(function(f){return Object.assign({},f,{preset:"custom"});}); return; } setFilters(function(f){ return Object.assign({},f,{preset:id, from:r.from, to:r.to}); }); };

  // Excel export — same active filters as the list, so the file matches exactly
  // what's shown (no filter = everything). Binary download (not apiFetch, which
  // parses JSON): fetch the blob and save via a temporary <a>.
  var exportExcel=function(){
    if(exporting) return;
    setExporting(true);
    var qs=[];
    if(filters.from) qs.push("from="+encodeURIComponent(new Date(filters.from).toISOString()));
    if(filters.to)   qs.push("to="+encodeURIComponent(new Date(filters.to).toISOString()));
    filters.agentId.forEach(function(a){ qs.push("agentId="+encodeURIComponent(a)); });
    if(filters.sourceType) qs.push("sourceType="+encodeURIComponent(filters.sourceType));
    fetch(API + "/api/meetings/export"+(qs.length?"?"+qs.join("&"):""), { headers:{ "Authorization":"Bearer "+p.token } })
      .then(function(res){
        if(!res.ok){ return res.json().then(function(b){ throw new Error((b&&b.error)||("Export failed ("+res.status+")")); }, function(){ throw new Error("Export failed ("+res.status+")"); }); }
        return res.blob();
      })
      .then(function(blob){
        var url=URL.createObjectURL(blob);
        var a=document.createElement("a"); a.href=url;
        a.download="meetings_"+new Date().toISOString().slice(0,10)+".xlsx";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url); setExporting(false);
      })
      .catch(function(ex){ alert((ex&&ex.message)||"Export failed"); setExporting(false); });
  };

  useEffect(function(){
    // THE REFUSAL IS AN ANSWER, and it now agrees with the envelope. While this
    // page gated on p.cu.isOwner it could refuse someone `access.pages` had
    // admitted, mount without fetching, and never report its rows — which held
    // the deferred half of the bootstrap for the full 30s apiFetch abort
    // (measured: 4 GETs instead of 13). The predicate is the router's now, so
    // this branch is unreachable by navigation; the report stays because an
    // unreachable branch that goes quiet is exactly what stopped being true once
    // before.
    if(!maySee){ if(p.onBootRows) p.onBootRows(); return; }
    var cancelled=false;
    setLoading(true); setErr("");
    var qs=[];
    if(filters.from) qs.push("from="+encodeURIComponent(new Date(filters.from).toISOString()));
    if(filters.to)   qs.push("to="+encodeURIComponent(new Date(filters.to).toISOString()));
    filters.agentId.forEach(function(a){ qs.push("agentId="+encodeURIComponent(a)); });
    if(filters.sourceType) qs.push("sourceType="+encodeURIComponent(filters.sourceType));
    var base="/api/meetings?limit=1000"+(qs.length?"&"+qs.join("&"):"");
    var all=[], grand=0;
    var fetchPage=function(page){
      return apiFetch(base+"&page="+page, "GET", null, p.token, p.csrfToken).then(function(r){
        if(!r || !Array.isArray(r.data)) throw new Error("Unexpected response");
        grand=r.total||0; all=all.concat(r.data);
        var totalPages=r.totalPages||1;
        if(page<totalPages) return fetchPage(page+1);
        return null;
      });
    };
    var wasUnfiltered = !filters.from && !filters.to && !filters.agentId.length && !filters.sourceType;
    fetchPage(1).then(function(){ if(cancelled) return; if(p.onBootRows) p.onBootRows(); setRows(all); setTotal(grand); setLoading(false);
        // Derive the quarter-filter year list from the archive's actual span (only
        // on an unfiltered load, so a filtered subset never shrinks the list).
        if(wasUnfiltered){ var seen={}; all.forEach(function(m){ if(m.meetingDate) seen[new Date(m.meetingDate).getFullYear()]=1; });
          var ys=Object.keys(seen).map(Number); var lo=ys.length?Math.min.apply(null,ys):nowY; var hi=Math.max(nowY, ys.length?Math.max.apply(null,ys):nowY);
          var list=[]; for(var y=hi;y>=lo;y--) list.push(y); setYears(list); }
      })
      .catch(function(ex){ if(cancelled) return; if(p.onBootRows) p.onBootRows(); setErr((ex&&ex.message)||"Failed to load meetings"); setRows([]); setTotal(0); setLoading(false); });
    return function(){ cancelled=true; };
  // selKey so re-picking the same agents doesn't refetch every page again.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.from, filters.to, selKey(filters.agentId), liveMeetingRev, filters.sourceType, p.token, maySee]);

  var th={ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, borderBottom:"2px solid "+C.border, whiteSpace:"nowrap", position:"sticky", top:0, background:"#fff", zIndex:1 };
  var td={ padding:"10px 12px", fontSize:13, color:C.text, borderBottom:"1px solid #F1F5F9", verticalAlign:"top" };
  // Column pins: width = minWidth = maxWidth, the PHONE_COL_PIN shape, so the table
  // layout cannot depend on which rows are rendered or on PhoneCell's hover reveal.
  // Phone / Alt. Phone use PHONE_COL_PIN itself. Text cells add an ellipsis so a pinned
  // cell truncates instead of spilling into its neighbour.
  var meetingCol=function(w){ return { width:w, minWidth:w, maxWidth:w, boxSizing:"border-box", whiteSpace:"nowrap" }; };
  var MTG_ELLIPSIS={ overflow:"hidden", textOverflow:"ellipsis" };
  var MTG_W={ date:130, client:140, agent:130, by:130, project:130, source:110, feedback:280 };
  var MTG_TABLE_W=MTG_W.date+MTG_W.client+PHONE_COL_W*2+MTG_W.agent+MTG_W.by+MTG_W.project+MTG_W.source+MTG_W.feedback;
  // Empty value → muted em-dash (never a blank-looking cell). Used for client,
  // phone, project, and feedback alike.
  var cell=function(v){ return (v!=null && String(v).trim()!=="") ? v : <span style={{color:C.textLight}}>—</span>; };

  // Defence in depth — hooks run unconditionally above (rules-of-hooks); bail
  // here, before rendering any meeting data, if someone the envelope refuses
  // somehow reaches this. renderPage already returns DashboardPage for exactly
  // this answer, so it is unreachable by navigation; it stays because a second
  // route into this component is one edit away.
  if (!maySee) {
    return <div style={{ padding:"40px 16px", textAlign:"center", color:C.textLight, fontSize:13 }}>Not available.</div>;
  }

  return <div style={{ padding:"18px 16px 40px" }}>
    {/* No in-body heading — the top-bar page title (MeetingDone-accented) is enough. */}
    <div style={{ display:"flex", alignItems:"center", justifyContent:p.isMobile?"space-between":"flex-end", gap:12, marginBottom:14, flexWrap:"wrap" }}>
      <div style={{ fontSize:12, color:C.textLight, fontWeight:600 }}>{loading ? "Loading…" : ("Showing "+rows.length+" of "+total)}</div>
      {/* Export is OWNER-ONLY (requireOwner on the route — a team leader gets 403), so
          the button renders for the owner alone. The envelope flag, same accessor the
          other owner-only controls read; the PAGE gate stays canSeePage above. */}
      {isOwnerFE(p.access) && <Btn outline onClick={exportExcel} disabled={exporting||loading||rows.length===0} style={{ padding:"7px 11px", fontSize:12, color:mdColor, borderColor:mdColor }}><FileSpreadsheet size={13}/> {exporting?"Exporting…":"Export Excel"}</Btn>}
    </div>

    <Card style={{ marginBottom:14, padding:p.isMobile?"10px 12px":"12px 14px" }}>
      <div style={{ display:"flex", flexWrap:"wrap", gap:p.isMobile?8:10, alignItems:"center" }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:6, alignItems:"center" }}>
          <button onClick={function(){ setFilters({ preset:"", from:null, to:null, agentId:[], sourceType:"" }); }} style={chipStyle(!filters.preset&&!filters.from&&!filters.agentId.length&&!filters.sourceType)}>All</button>
          {presets.map(function(pr){ return <button key={pr.id} onClick={function(){ setPreset(pr.id); }} style={chipStyle(filters.preset===pr.id)}>{pr.label}</button>; })}
        </div>
        {!p.isMobile && <div style={{ width:1, height:22, background:C.border }}/>}
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          <Select value={(filters.preset && /^q[1-4]$/.test(filters.preset)) ? filters.preset.charAt(1) : ""} onChange={function(e){ var v=e.target.value; if(v){ setQuarter(parseInt(v,10)); } else { setFilters(function(f){ return Object.assign({},f,{preset:"", from:null, to:null}); }); } }} width={86} maxWidth={86} fontSize={11}
            options={[{ value:"", label:"Quarter" }].concat([1,2,3,4].map(function(q){ return { value:String(q), label:"Q"+q }; }))}/>
          <Select value={String(yearSel)} onChange={function(e){ changeYear(e.target.value); }} width={80} maxWidth={80} fontSize={11}
            options={years.map(function(y){ return { value:String(y), label:String(y) }; })}/>
        </div>
        {!p.isMobile && <div style={{ width:1, height:22, background:C.border }}/>}
        <DashRangePicker isMobile={p.isMobile}
          value={filters.preset==="custom" ? { from:fmtDateInput(filters.from), to:fmtDateInput(filters.to) } : { from:"", to:"" }}
          active={filters.preset==="custom" && !!filters.from}
          min="" todayYmd={dfYmd(new Date())}
          onPick={function(a, b){
            // Same conversions the two raw date inputs used: local start of the first day, local 23:59:59 of the last.
            if(!a){ setFilters(function(f){ return Object.assign({},f,{preset:"", from:null, to:null}); }); return; }
            setFilters(function(f){ return Object.assign({},f,{preset:"custom", from:new Date(a+"T00:00:00").getTime(), to:new Date((b||a)+"T23:59:59").getTime()}); });
          }}/>
        {!p.isMobile && <div style={{ width:1, height:22, background:C.border }}/>}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:8, flex:p.isMobile?"1 1 100%":"0 1 380px", minWidth:0 }}>
          <SearchableSelect multi value={filters.agentId} onChange={function(v){ setFilters(function(f){ return Object.assign({},f,{agentId:v}); }); }} options={agentFilterOpts} emptyLabel="👤 All agents" placeholder="Filter agent…" width="100%"/>
          {/* Source filter — the record's origin (sourceType), applied server-side with the others and carried into Export Excel. */}
          <Select value={filters.sourceType} onChange={function(e){ var v=e.target.value; setFilters(function(f){ return Object.assign({},f,{sourceType:v}); }); }} width="100%" maxWidth="100%" fontSize={11}
            options={[{ value:"", label:"All sources" },{ value:"dailyRequest", label:"Daily Request" },{ value:"lead", label:"Lead" }]}/>
        </div>
      </div>
    </Card>

    {p.isMobile ? (
      /* Mobile: stacked cards — same pattern as Leads/Daily Request (flex column,
         gap:12, maxWidth centered). Every table field is present on the card:
         client + phone + agent in the header, meeting-date accent pill, a
         Recorded-by / Project tile grid, and the feedback block. Empty values
         keep the muted "—" via cell(). Feedback keeps single-line truncation
         with tap-to-expand and dir="auto" for Arabic/RTL. */
      <div style={{ display:"flex", flexDirection:"column", gap:12, padding:"4px 0 0", maxWidth:500, margin:"0 auto", width:"100%", boxSizing:"border-box" }}>
        {loading ? <div style={{ textAlign:"center", padding:40, color:C.textLight }}>Loading…</div>
         : err ? <div style={{ textAlign:"center", padding:40, color:C.danger }}>{err}</div>
         : rows.length===0 ? <div style={{ textAlign:"center", padding:40, color:C.textLight }}>No meetings match these filters.</div>
         : rows.map(function(m){
             var id=String(m._id);
             var fbHas=(m.feedback!=null && String(m.feedback).trim()!=="");
             return <div key={id} style={{ background:"#fff", borderRadius:16, padding:"16px", border:"2px solid "+mdColor, boxShadow:"0 3px 12px "+mdColor+"35", boxSizing:"border-box" }}>
               {/* Header — client + phone + agent, with the meeting-date accent pill */}
               <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, marginBottom:10 }}>
                 <div style={{ flex:1, minWidth:0 }}>
                   <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:3, wordBreak:"break-word" }}>{cell(m.clientName)}</div>
                   {/* Masked like the Leads page — PhoneCell (maskPhone + hover reveal), for owner and TL alike. */}
                   <div style={{ fontSize:12, fontWeight:700, color:C.text, direction:"ltr" }}><PhoneCell phone={m.phone}/></div>
                   {m.phone2 && <div style={{ fontSize:12, fontWeight:700, color:C.text, direction:"ltr", marginTop:2 }}><PhoneCell phone={m.phone2}/></div>}
                   <div style={{ fontSize:11, color:C.accent, fontWeight:600, marginTop:3 }}>👤 {m.agentName||"Unassigned"}</div>
                   {/* Source = the meeting record's own origin (sourceType), never the lead's status. */}
                   <div style={{ fontSize:11, color:C.textLight, fontWeight:600, marginTop:3 }}>Source: {meetingSourceLabel(m)}</div>
                 </div>
                 <span style={{ background:mdColor+"14", color:mdColor, padding:"5px 10px", borderRadius:10, fontSize:11, fontWeight:700, whiteSpace:"nowrap", marginLeft:8, flexShrink:0 }}>{fmtMeetingDate(m.meetingDate)}</span>
               </div>
               {/* Recorded by + Project tiles */}
               <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                 <div style={{ background:"#F8FAFC", borderRadius:12, padding:"10px 12px", border:"1px solid #E8ECF1", minWidth:0 }}>
                   <div style={{ fontSize:10, color:C.textLight, marginBottom:4, fontWeight:600 }}>✍️ Recorded by</div>
                   <div style={{ fontSize:13, fontWeight:700, color:C.text, wordBreak:"break-word" }}>{m.recordedByName || <span style={{color:C.textLight}}>—</span>}</div>
                 </div>
                 <div style={{ background:"#F8FAFC", borderRadius:12, padding:"10px 12px", border:"1px solid #E8ECF1", minWidth:0 }}>
                   <div style={{ fontSize:10, color:C.textLight, marginBottom:4, fontWeight:600 }}>📍 Project</div>
                   <div style={{ fontSize:13, fontWeight:700, color:C.text, wordBreak:"break-word" }}>{cell(m.project)}</div>
                 </div>
               </div>
               {/* Feedback — single-line truncate + tap-to-expand, RTL-aware (dir="auto") */}
               <div style={{ padding:"8px 10px", background:"#F8FAFC", borderRadius:8, borderLeft:"3px solid "+mdColor }}>
                 <div style={{ fontSize:10, color:C.textLight, fontWeight:600, marginBottom:4 }}>💬 Feedback</div>
                 {fbHas
                   ? <div dir="auto" onClick={function(){ toggleFb(id); }} title={fbOpen[id]?"Tap to collapse":"Tap to show full feedback"}
                       style={fbOpen[id]
                         ? { fontSize:13, color:C.text, whiteSpace:"pre-wrap", wordBreak:"break-word", cursor:"pointer" }
                         : { fontSize:13, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", cursor:"pointer" }}>{m.feedback}</div>
                   : <span style={{color:C.textLight}}>—</span>}
               </div>
             </div>;
           })}
      </div>
    ) : (
    <Card p={0} style={{ overflow:"hidden" }}>
      <div style={{ overflowX:"auto" }}>
        <table data-table-align="sheet" style={{ width:"100%", minWidth:MTG_TABLE_W, borderCollapse:"collapse" }}>
          <thead><tr>
            <th style={Object.assign({},th,meetingCol(MTG_W.date))}>Meeting date</th><th style={Object.assign({},th,meetingCol(MTG_W.client))}>Client</th><th style={Object.assign({},th,PHONE_COL_PIN)}>Phone</th><th style={Object.assign({},th,PHONE_COL_PIN)}>Alt. Phone</th>
            <th style={Object.assign({},th,meetingCol(MTG_W.agent))}>Agent</th><th style={Object.assign({},th,meetingCol(MTG_W.by))}>Recorded by</th><th style={Object.assign({},th,meetingCol(MTG_W.project))}>Project</th><th style={Object.assign({},th,meetingCol(MTG_W.source))}>Source</th><th style={Object.assign({},th,meetingCol(MTG_W.feedback))}>Feedback</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td style={td} colSpan={9}>Loading…</td></tr>
             : err ? <tr><td style={Object.assign({},td,{color:C.danger})} colSpan={9}>{err}</td></tr>
             : rows.length===0 ? <tr><td style={Object.assign({},td,{color:C.textLight})} colSpan={9}>No meetings match these filters.</td></tr>
             : rows.map(function(m){ return <tr key={String(m._id)}>
                 <td style={Object.assign({},td,meetingCol(MTG_W.date),MTG_ELLIPSIS)} title={fmtMeetingDate(m.meetingDate)||undefined}>{fmtMeetingDateShort(m.meetingDate)}</td>
                 <td style={Object.assign({},td,meetingCol(MTG_W.client),MTG_ELLIPSIS)} title={m.clientName||undefined}>{cell(m.clientName)}</td>
                 {/* Masked like the Leads page — PhoneCell (maskPhone + hover reveal). Alt. Phone is the
                     source record's current phone2, resolved server-side (meetingAltPhoneMap). */}
                 <td style={Object.assign({},td,PHONE_COL_PIN)}><PhoneCell phone={m.phone}/></td>
                 <td style={Object.assign({},td,PHONE_COL_PIN)}><PhoneCell phone={m.phone2}/></td>
                 <td style={Object.assign({},td,meetingCol(MTG_W.agent),MTG_ELLIPSIS)} title={m.agentName||"Unassigned"}>{m.agentName||"Unassigned"}</td>
                 <td style={Object.assign({},td,meetingCol(MTG_W.by),MTG_ELLIPSIS,{color:C.textLight})} title={m.recordedByName||undefined}>{m.recordedByName||"—"}</td>
                 <td style={Object.assign({},td,meetingCol(MTG_W.project),MTG_ELLIPSIS)} title={m.project||undefined}>{cell(m.project)}</td>
                 {/* Source = the meeting record's own origin (sourceType), never the lead's status. */}
                 <td style={Object.assign({},td,meetingCol(MTG_W.source),MTG_ELLIPSIS)}>{meetingSourceLabel(m)}</td>
                 <td style={Object.assign({},td,meetingCol(MTG_W.feedback))}>{(m.feedback!=null && String(m.feedback).trim()!=="")
                   ? <div dir="auto" onClick={function(){ toggleFb(String(m._id)); }} title={fbOpen[String(m._id)]?"Click to collapse":"Click to show full feedback"}
                       style={fbOpen[String(m._id)]
                         ? { maxWidth:360, whiteSpace:"pre-wrap", wordBreak:"break-word", cursor:"pointer" }
                         : { maxWidth:360, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", cursor:"pointer" }}>{m.feedback}</div>
                   : <span style={{color:C.textLight}}>—</span>}</td>
               </tr>; })}
          </tbody>
        </table>
      </div>
    </Card>
    )}
  </div>;
};

// ===== EOI PAGE =====
var EOIPage = function(p) {
  useLeadSourcesVersion();   // re-render when the lead source registry lands (src/lib/leadSources.js)
  var t=p.t; var isAdmin=p.cu.role==="admin"||p.cu.role==="operation"||positionOf(p.cu)==="director"||positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader"; var isOnlyAdmin=p.cu.role==="admin"||p.cu.role==="operation";
  // Remembered per page per user (src/lib/tabMemory); the strip below still
  // decides what is SHOWN through the envelope.
  var [eoiTabState,setEoiTab]=useRememberedTab("eoi", gid(p.cu), EOI_TABS, null, "active");
  var eoiStrip = resolveTabStrip(p.access, "eoi", EOI_TABS, eoiTabState);
  var eoiTab = eoiStrip.active;
  // MULTI-SELECT (2026-08-06): an ARRAY; [] = all projects.
  var [eoiProjectFilter,setEoiProjectFilter]=useState([]);
  var [eoiPendingOnly,setEoiPendingOnly]=useState(false);   // the ⏳ pill as a filter (owner 2026-10-29): active rows awaiting approval only
  var [eoiSourceFilter,setEoiSourceFilter]=useState([]);   // Source filter (owner 2026-10-30): the Deals page's, client-side over the loaded rows; admin-cluster only (the card's Source line gate)
  // The TARGET and the RING, split for the same reason as the Deals page: the
  // target is cleared by LANDING, the ring by a timer that only starts once the
  // row is on screen. Measured on production, GET /api/eois takes a median
  // 2,120ms — over the 2s the single-state version gave itself, so arriving
  // from another page never scrolled.
  var [eoiLandingId,setEoiLandingId]=useState(null);
  var [eoiFlashId,setEoiFlashId]=useState(null);
  // The target already scrolled to. A ref, not state: readable and writable
  // inside the same tick the scroll happens in, and changing it must not render.
  var eoiLandedForRef=useRef("");
  // STEP 4-4 — paginated EOI fetch from /api/eois?status=all. This is the ONLY
  // source for the EOI list: it is scoped to the EOIs this agent AUTHORED
  // (eoiAgentId). It deliberately does NOT fall back to the in-memory
  // p.leads.filter scan while in flight — that bootstrap is scoped by current
  // lead OWNERSHIP, not EOI authorship, and rendering it first leaked another
  // agent's EOI for a frame (the cross-agent data flash). Null until resolved;
  // the loading / error banner owns the UI until then.
  var [eoiAllData, setEoiAllData] = useState(null);
  var [eoiLoadErr, setEoiLoadErr] = useState(null);
  var [eoiReloadKey, setEoiReloadKey] = useState(0);
  // P2 (2026-11-02): LIVE BY LEAD, NOT BY BOOK. The 30s cbBust throttle that
  // re-walked every page of /api/eois on any lead burst is gone. The server
  // sends entity_changed { family:"eoi", ids:[leadId…] } for every record
  // write and every lead / DR write that moves a transaction; this re-reads
  // those leads' rows (same scope, same tab filter) and swaps them in. A null
  // id list (reconnect resync, a bulk delete) re-reads the book once.
  // Deferred while a modal holds a row (editLead / split / convert).
  var eoiLiveRefetch = useCallback(function(leadIds){
    apiFetch(txnLiveUrl("eoi", leadIds), "GET", null, p.token).then(function(r){
      var fresh = (r && Array.isArray(r.data)) ? r.data : null;
      if (!fresh) return;
      setEoiAllData(function(prev){ return prev ? mergeTxnRowsByLead(prev, fresh, leadIds, txnRowOrder("eoi"), gid) : prev; });
    }).catch(function(){});
  }, [p.token]);
  useLiveFamily("eoi", {
    onChange: function(f, ids){ if (ids) eoiLiveRefetch(ids); else setEoiReloadKey(function(k){ return k + 1; }); },
    isEditing: function(){ return !!(editLead || eoiSplitModal || convertModal); }
  });
  useEffect(function(){
    if (!p.token) return;
    var cancelled = false;
    setEoiLoadErr(null);
    // NO CEILING — was ?limit=1000, a silent stop once EOIs outgrew it.
    // fetchAllPages walks every page; termination comes from the server's
    // totalPages, not a constant here.
    fetchAllPages("/api/eois?status=all", p.token)
      .then(function(rows){
        if (cancelled) return;
        // THE PHASE-2 RELEASE — see the twin on DealsPage. eoisLoaded flips on
        // the next render; this fires in the same tick that makes it true.
        if (p.onBootRows) p.onBootRows();
        var r = { data: rows };
        // Restore the server's display order over the whole book — the keyset
        // walk delivers _id order. See byDatesDesc.
        // The book is ordered by the RECORD's own date, never updatedAt — a
        // restore or an approval must not lift a row (txnRowOrder).
        if (r && Array.isArray(r.data)) { setEoiAllData(r.data.slice().sort(txnRowOrder("eoi"))); }
        // 200 but wrong shape (backend/FE version skew) must NOT pass silently.
        // It would leave eoiAllData null and — pre-fix — fall back to the broad
        // p.leads bootstrap, which scopes sales users by CURRENT lead ownership
        // (a rotated lead's live holder), not by EOI AUTHOR. That surfaced EOIs
        // authored by OTHER agents for a frame (the cross-agent data flash).
        else { setEoiLoadErr("Unexpected response from /api/eois (missing data[] array)."); }
      })
      .catch(function(err){
        if (cancelled) return;
        if (p.onBootRows) p.onBootRows();
        // No silent swallow: surface the failure instead of rendering another
        // agent's rows from the broader bootstrap.
        setEoiLoadErr((err && err.message) ? err.message : "Failed to load EOIs.");
      });
    return function(){ cancelled = true; };
  // NOT p.cbBust and no stale key — other users' changes arrive per lead
  // through useLiveFamily("eoi") above; own mutations through patchEoiRow /
  // patchEoiRowsByLead.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, eoiReloadKey]);
  // ── THE p.leads OVERLAY IS GONE (2026-08-24) ───────────────────────────────
  // This used to be:
  //     var pLeadsMap = useMemo(... m[gid(l)] = l ...);
  //     eoiAllData.map(function(l){ return pLeadsMap[gid(l)] || l; })
  // an overlay that existed so inline mutations (archiveLead / toggleApproved /
  // cancelEOI) appeared in the list before the WS-bumped refetch landed.
  //
  // It did that by SUBSTITUTION, and gid() on a record-backed row returns the
  // LEAD's _id (rowFromTxnRecord does Object.assign({}, lead)), so whenever the
  // lead happened to sit in the 100-row bootstrap the whole row was swapped for
  // a plain lead copy — throwing away _rowKey, _txnStatus, _recordNo and every
  // frozen record field, and un-freezing the transaction's history in the
  // process. That is the mechanism behind two separate incidents: the #50039
  // converted-EOI ghost that appeared on some loads and not others, and the
  // intermittency of the frozen-name revert.
  //
  // It also barely worked. An EOI-stage lead is rarely in the newest-100 window
  // (deal #50043's lead ranks 938 of 4,819), so for most rows the overlay was
  // simply absent and the "instant" update never happened at all.
  //
  // The mutation handlers now patch eoiAllData directly, by rowKey, with only
  // the fields they changed — see patchEoiRow / patchEoiRowsByLead above. That
  // is strictly better on both counts: it reaches every row, not just bootstrap
  // ones, and it cannot destroy a record's projection.
  //
  // ORDER MATTERED. Deleting this before the handlers were taught would have
  // made the frozen-name revert universal instead of fixing it; the identity
  // projection had to leave rowFromTxnRecord first. verify-identity-home.js
  // fails if the substitution comes back, and also fails if the handlers stop
  // patching the list — the two halves cannot drift apart silently.
  //
  // Server-fetched ONLY — scoped to the EOIs THIS agent AUTHORED (/api/eois
  // sales scope keys on eoiAgentId, the maker of the EOI). The previous
  // `: p.leads.filter(...)` fallback rendered the broader /api/leads bootstrap
  // while this fetch was in flight; but /api/leads scopes sales users by the
  // lead's CURRENT owner (a rotated lead's live holder), not by EOI author, so
  // it surfaced another agent's EOI — customer name / phone / budget — for a
  // frame before the scoped response replaced it (the data-isolation flash).
  // Render nothing until the author-scoped response lands; the loading / error
  // banner below owns the UI meanwhile. Same shape as DealsPage's dealsSource.
  var eoisLoaded = Array.isArray(eoiAllData);
  var eoiScope = eoisLoaded
    ? eoiAllData.filter(function(l){return l && !l.archived;})
    : [];
  // The four row-lifecycle predicates this page used to define inline now live at
  // module scope (search isEoiCancelledRow) so verify-converted-eoi.js can RUN
  // them. Nothing about their behaviour changed in the move; what changed is that
  // a gate can now execute them instead of pattern-matching this closure's text.
  // Mirror of the backend author gate (server.js isEoiAuthor) — keep the two in step.
  // Author == the acting creator frozen into eoiAgentId (or the eoiSplitAgent2Id
  // co-author), NOT the lead's live holder. Shape differs by source: /api/eois
  // populates the ref into an object while a raw ObjectId can still arrive on a
  // branch-B row, so unwrap both. (This used to have a third reason — eoiScope
  // substituting p.leads copies — which is gone with the swap; the unwrap is
  // still required for the two remaining shapes.)
  // Legacy rows (eoiAgentId null, pre-backfill) fall back to live ownership.
  var isEoiAuthor = function(l){
    var uid = String((p.cu && p.cu.id) || "");
    if (!uid || !l) return false;
    var idOf = function(v){ return v ? String(v._id || v) : ""; };
    var author = idOf(l.eoiAgentId), coAuthor = idOf(l.eoiSplitAgent2Id);
    if (author || coAuthor) return author === uid || coAuthor === uid;
    return idOf(l.agentId) === uid || idOf(l.splitAgent2Id) === uid;
  };
  // Convert-to-Deal audience — AUTHORSHIP, not role. Team leaders / managers /
  // directors work leads personally and make their own EOIs, so any role converts
  // what it authored; a subordinate's EOI inside their team scope is not theirs.
  // Matters here because /api/eois scopes sales by author but every other role by
  // SUBTREE, so a TL's list mixes their own EOIs with their team's — role alone
  // can't tell them apart. Admin/operation keep blanket access; viewer/hr are
  // read-only (their scope is see-all, so they must be excluded explicitly).
  // Approve / Cancel stay isOnlyAdmin — no one may self-approve then self-convert.
  var canConvertEoi = function(l){
    if (!eoiAllows(l, "convert")) return false;
    if (p.cu && (p.cu.role==="viewer" || p.cu.role==="hr")) return false;
    return isOnlyAdmin || isEoiAuthor(l);
  };
  // Every row is filed by eoiRowBucket and by nothing else, so the chips are
  // mutually exclusive by construction rather than by predicates agreeing. A
  // converted EOI buckets to "none" and therefore renders in neither — it is a
  // deal now. Pending/Approved only SORT within Active; they are not buckets.
  // TAB MERGE — "Active" = everything not cancelled (Pending + Approved together).
  // ORDER (owner ruling 2026-09-12): the record's own date descending, then the
  // record's createdAt — both tabs, every role, one comparator (txnRowOrder).
  // The Pending-first grouping is GONE: it kept an older Pending row above a
  // newer Approved one, which is the reordering the ruling forbids. The badge
  // still says which is which.
  var eoiActiveRows = eoiScope.filter(function(l){ return eoiRowBucket(l)==="active"; });
  var eoiCancelled = eoiScope.filter(function(l){ return eoiRowBucket(l)==="cancelled"; }).sort(txnRowOrder("eoi"));
  var eoiActive = eoiActiveRows.slice().sort(txnRowOrder("eoi"));
  // Source filter (owner 2026-10-30): selMatchName folds case/whitespace; an empty selection matches every row.
  // The tab counts, the Cancelled dot, the ⏳ pill, the rows and the Total all read the source-scoped sets.
  var eoiSourceOk = function(l){ return selMatchName(eoiSourceFilter, l.source); };
  var eoiActiveInSource = eoiActive.filter(eoiSourceOk);
  var eoiCancelledInSource = eoiCancelled.filter(eoiSourceOk);
  var eoiLeads = eoiStrip.empty ? [] : (eoiTab==="cancelled" ? eoiCancelledInSource : eoiActiveInSource);
  // Project filter (parity with DealsPage) — `project` is a free-text string on
  // the lead, so options are distinct values derived client-side from all EOI
  // rows (both tabs), then the active tab bucket is filtered by the selection.
  // 2026-10-29 (owner task): ONE canonical entry per project, folded through the
  // same normProjectFE key selMatchName matches on (case + whitespace), most
  // frequent spelling as the label, A→Z — shared with the Deals dropdown.
  var eoiProjectOpts=canonicalProjectOptions(eoiScope, function(l){ return l.project; }, normProjectFE);
  var eoiSourceOpts=dealSourceOptions(leadSourceNames());   // the Deals page's list: the registry's sources + "Daily Request", labels in the registry colour
  // selMatchName folds case/whitespace. eoiProjectOpts already de-dupes options
  // case-insensitively but emitted the FIRST raw spelling as the value, so the
  // previous exact compare left the other spelling's EOIs unreachable.
  if(eoiProjectFilter.length){ eoiLeads = eoiLeads.filter(function(l){ return selMatchName(eoiProjectFilter, l.project); }); }
  // ⏳ pill focus (owner 2026-10-29): Active tab narrowed to rows awaiting approval. The EOI page has no period, so there is nothing to jump or restore.
  var eoiPendingN = eoiActiveInSource.filter(function(d){ return d.eoiApproved !== true; }).length;   // all-time — the page holds its whole set — within the Source filter
  if(eoiPendingOnly && eoiTab!=="cancelled"){ eoiLeads = eoiLeads.filter(function(l){ return l.eoiApproved !== true; }); }

  // OWNER RULING 2026-10-18 (supersedes R-T7 for THIS BADGE ONLY): the EOI page
  // header total is the sum of budgets of the rows currently SHOWN — whichever
  // tab is open (Active or Cancelled) with the project filter applied. So it
  // reads eoiLeads, after the filter above. Scope is this pill alone: the
  // dashboard, KPIs, Reports and the campaign table keep excluding Cancelled
  // (server EOI_ACTIVE_STATUSES). The pill shows a "Cancelled" qualifier on
  // the Cancelled tab so that sum is never read as live pipeline.
  var parseBudget=function(b){return parseFloat((b||"0").toString().replace(/,/g,""))||0;};
  var total=eoiLeads.reduce(function(s,d){return s+parseBudget(d.budget);},0);

  // ===== ONE LANDING, for every door that means a specific EOI ===============
  //
  // The Deals page's twin, and the same law: a caller that selects a row must
  // also LAND on it, and the scroll must wait for the row to be RENDERED rather
  // than guess with a timer. `eoiLeads` is what both renderers map over — it is
  // the tab bucket with the project filter already applied — so membership in it
  // is exactly "this row's <tr> / card exists".
  //
  //   (1) THE PROJECT FILTER MUST GO. It is the one narrow this page has, and a
  //       row it excludes cannot be selected, scrolled to or highlighted.
  //   (2) THE TAB MUST MATCH. A cancelled EOI lives on the Cancelled tab; a
  //       CONVERTED one lives on neither (it is a deal now — see
  //       eoiRowBucket), so landing is skipped rather than pointed at a row
  //       this page will never render.
  //   (3) THE SCROLL IS NOT DONE HERE — the resets have not rendered yet.
  var landOnEoi = function(target){
    if (!target) return;
    setEoiProjectFilter([]); setEoiSourceFilter([]);                     // (1)
    var t1 = target.eoiStatus || "";
    var cancelled = (t1==="EOI Cancelled" || t1==="Deal Cancelled" || target.status==="Deal Cancelled");
    setEoiTab(cancelled ? "cancelled" : "active");                       // (2)
    setSelectedEOI(target);
    setEoiLandingId(rowKey(target));                                     // (3)
    setEoiFlashId(null);                          // the previous ring is stale
    eoiLandedForRef.current = "";                 // a new target — land again
  };

  // Keyed on a boolean rather than on eoiLeads itself: that array is rebuilt
  // every render, so depending on it would re-run this constantly.
  // eoiLandedForRef makes the scroll once-per-target.
  var eoiLandingPresent = !!(eoiLandingId && eoisLoaded && eoiLeads.some(function(l){
    return rowKey(l) === eoiLandingId;
  }));
  useEffect(function(){
    if (!eoiLandingId) return;
    if (eoiLandedForRef.current === eoiLandingId) return;
    if (!eoiLandingPresent) {
      // HELD while the page's own fetch is still out — eoiAllData is null on a
      // fresh mount, so eoiLeads is [] and the row cannot be present yet.
      if (!eoisLoaded || !eoiLeads.length) return;
      // Loaded, non-empty, still absent: a genuine miss (a CONVERTED EOI is the
      // real case — it buckets to "none" and this page never renders it).
      eoiLandedForRef.current = eoiLandingId;
      setEoiLandingId(null);
      return;
    }
    eoiLandedForRef.current = eoiLandingId;
    var go = function(){
      var el = document.getElementById("eoi-row-" + eoiLandingId);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior:"smooth", block:"center" });
    };
    // rAF, not a timer: the row is in the data, its node lands on the next paint.
    if (typeof window !== "undefined" && window.requestAnimationFrame) window.requestAnimationFrame(go);
    else setTimeout(go, 0);
    setEoiFlashId(eoiLandingId);                  // the ring starts on ARRIVAL
    setEoiLandingId(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[eoiLandingId, eoiLandingPresent, eoisLoaded, eoiLeads.length]);

  // The ring's lifetime — keyed on the RING, which only exists after landing.
  // See the Deals page for why a timer on the TARGET was the bug and a timer
  // here is not.
  useEffect(function(){
    if (!eoiFlashId) return;
    var h = setTimeout(function(){ setEoiFlashId(null); }, 2000);
    return function(){ clearTimeout(h); };
  },[eoiFlashId]);
  // Live-agent resolver (used directly for active rows, and as the fallback).
  var liveAg=function(l){ return userNameOf(l && l.agentId, p.users) || "-"; };
  // Anchored EOI-author resolver — reads the populated eoiAgentId (the agent who
  // MADE the EOI, stamped at creation and never moved by rotation). Server sends
  // it as a populated {name} on the EOI list; fall back to a users[] lookup.
  var authorAg=function(l){ return userNameOf(l && l.eoiAgentId, p.users) || null; };
  // ALL tabs show WHO MADE the EOI (per-action attribution): prefer the anchored
  // author (eoiAgentId) regardless of tab, since the live agentId can differ from
  // the actual author whenever a co-assignee makes the EOI or the lead is
  // reassigned. Fall back to agentAtTime → live for legacy rows with no anchor.
  var getAg=function(l){var live=liveAg(l);return authorAg(l)||agentAtTime(l,live);};
  var [editLead,setEditLead]=useState(null);
  // Split EOI (owner ruling 2026-09-18, mirroring DealsPage's splitModal —
  // same modal shape, same 50/50-only mechanic, same write path).
  var [eoiSplitModal,setEoiSplitModal]=useState(null); // the EOI row being split
  var [eoiSplitAgent2,setEoiSplitAgent2]=useState("");
  var [showAdd,setShowAdd]=useState(false);
  var [selectedEOI,setSelectedEOI]=useState(null);
  // Defensive guard mirroring LeadsPage hasRenderableSelected. Same risk
  // surface: a degenerate selectedEOI (no name/phone) could paint an
  // empty panel post-bootstrap-shrink. Auto-clear on detection.
  var hasRenderableEOI = !!(selectedEOI && gid(selectedEOI) && (selectedEOI.name || selectedEOI.phone));
  useEffect(function(){
    if (selectedEOI && !hasRenderableEOI) {
      // eslint-disable-next-line no-console
      console.warn("[EOIPage] Clearing degenerate `selectedEOI`:", selectedEOI);
      setSelectedEOI(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selectedEOI, hasRenderableEOI]);
  // Click-outside closes the EOI side panel (docked drawer).
  var eoiPanelRef = useOutsideClose(hasRenderableEOI, function(){ setSelectedEOI(null); }, "[data-eoi-row]");
  var [imgUploading,setImgUploading]=useState(false);
  var [docUploading,setDocUploading]=useState(false);
  // In-app document viewer (2026-10-01): the thumbnail tap sets this, DocViewer renders it.
  var [eoiDocViewerSrc,setEoiDocViewerSrc]=useState("");
  var [cancelling,setCancelling]=useState(false);
  var [convertingDeal,setConvertingDeal]=useState(false);
  // PATH B — Convert-to-Deal modal. The EOI→Deal conversion used to be a one-click
  // confirm; it now opens a FULL sale form (all fields required, prefilled from the
  // EOI lead + editable) before hitting /eoi-to-deal. convertModal holds the EOI
  // lead being converted; convertForm holds the editable sale-form values.
  var [convertModal,setConvertModal]=useState(null);
  var mkConvParty=function(){ return { withUs:false, name:"", phone:"", agentId:"", commissionAmount:"", commissionRate:"" }; };
  var [convertForm,setConvertForm]=useState({ name:"", phone:"", phone2:"", project:"", unitType:"", unitCode:"", budget:"", saleType:"Primary", developerId:"", developerPending:"", downPayment:"", downPaymentDate:"", reservationDate:"", contractionDate:"", installmentYears:"", dealDate:"", resaleParties:{ seller:{ withUs:true, name:"", phone:"", agentId:"", commissionAmount:"", commissionRate:"" }, buyer:mkConvParty() } });
  var setConvF=function(k,v){ setConvertForm(function(f){ var n=Object.assign({},f); n[k]=v; return n; }); };
  // Two-party Resale nested updater for the convert form.
  var setConvRP=function(side,field,v){ setConvertForm(function(f){ var rp=Object.assign({ seller:{}, buyer:{} }, f.resaleParties); rp[side]=Object.assign({}, rp[side], {[field]:v}); return Object.assign({},f,{ resaleParties:rp }); }); };
  // Optional deal documents attached in the convert modal ([{fileData,fileName}]);
  // uploaded to /eoi-documents AFTER the conversion succeeds (same endpoint the
  // Add-Deal form uses for deal docs). Not required — the sale form is complete
  // without them.
  var [convertDocs,setConvertDocs]=useState([]);
  // R-T5 — the Convert-to-Deal form asks for the developer. `convertDevPendOpen`
  // is the same two-mode control the EOI/Deal status door uses: pick from the
  // managed list, or type a name nobody has added yet. Mutually exclusive, and
  // reset every time the modal opens.
  var [convertDevPendOpen,setConvertDevPendOpen]=useState(false);
  // Convert-to-Deal accent — Yosra's pick: soft mint background with dark-green
  // text (~8:1 contrast), unified across the row buttons, the card button, and
  // the modal confirm so all three "Convert to Deal" affordances read as one.
  var MINT_BG="#5DCAA5", MINT_FG="#04342C";
  // Side-panel hydration tracker. The /api/leads bootstrap strips heavy fields
  // (eoiImage, eoiDocuments) to keep the payload small; we refetch the full
  // doc when the panel opens. panelHydratedEoiId === selectedEOI._id once the
  // refetch has populated the panel, so mutation responses (already full docs)
  // don't trigger a redundant fetch.
  var [panelHydratedEoiId,setPanelHydratedEoiId]=useState(null);
  var [eoiHydrateErr,setEoiHydrateErr]=useState("");
  // Keyed on the ROW, not the lead: two transactions on one client are two rows
  // sharing a lead _id, and each has to hydrate its own artifacts.
  var isEoiHydrated = !!(selectedEOI && panelHydratedEoiId === rowKey(selectedEOI));
  var salesUsers=p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;});
  // Owner ruling 2026-09-18 — same test DealsPage uses before its own 🤝
  // badge. Row-independent, hoisted once rather than recomputed per row in
  // each map callback.
  var isSalesRole=positionOf(p.cu)==="sales"||positionOf(p.cu)==="team_leader";
  // Developers — EOI panel parity with Deals. R2 added the picker to the Deals
  // panel only; the EOI panel needs the same list for its own developer
  // dropdown. GET /api/developers is open to any auth; the dropdown stays
  // admin/SA-gated in the JSX.
  //
  // A DEVELOPER MINTED BY A WRITE IS NOT IN THIS LIST until the next load, and
  // that is why both pickers inject their current value as an option: the row
  // arrives with developerId POPULATED, so the name renders from the row itself
  // rather than from a lookup that would miss.
  var [eoiDevelopers,setEoiDevelopers]=useState([]);
  var liveDevRev = useLiveRev("developer");
  useEffect(function(){
    if(!p.token) return;
    var cancelled=false;
    apiFetch("/api/developers","GET",null,p.token)
      .then(function(data){ if(cancelled) return; var rows=(data&&Array.isArray(data.data))?data.data:(Array.isArray(data)?data:[]); setEoiDevelopers(rows); })
      .catch(function(){ /* non-fatal */ });
    return function(){ cancelled=true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, liveDevRev]);
  var eoiDevIdOf=function(d){ var dv=d&&d.developerId; return dv?String(dv._id||dv):""; };
  var eoiDevNameOf=function(d){ return developerNameOf(d && d.developerId, eoiDevelopers); };

  // Deep-link: open the side panel when a caller (e.g. the Deals & EOI notifications bell) navigated here with a lead.
  useEffect(function(){
    if (!p.initSelected) return;
    // A caller that navigated here with a RECORD row (the global id search
    // picking one of a client's transactions) already knows which row it means.
    // Re-resolving it against p.leads would swap in the plain lead document and
    // discard _rowKey/_recordNo/_fromRecord — opening whichever of the client's
    // transactions came first, which is the whole bug this search fixes.
    var target = p.initSelected._fromRecord
      ? p.initSelected
      : ((p.leads||[]).find(function(l){return gid(l)===gid(p.initSelected);}) || p.initSelected);
    // A NOTIFICATION HANDS OVER A LEAD, NOT A ROW — see resolveNotifRow at module
    // scope for the full mechanism. Anything that is not already a record row is
    // resolved against THIS page's own book, so the bell and the push tap open
    // the identical row a click in the table opens, artifacts and all.
    //
    // HOLD the cue while the book is still out. eoiAllData is null on a fresh
    // mount — which is how a bell click ALWAYS arrives — and GET /api/eois has a
    // production median of 2,120ms, so resolving now would resolve against an
    // empty array and silently fall back to the shell every time. Same rule the
    // Commissions→Deals consumer follows with `if (!dealsLoaded) return;`, and
    // the same reason the lander holds its target: an empty list before the
    // fetch lands is "not answered yet", never "row absent".
    //
    // p.initSelected is deliberately NOT cleared on this branch — clearing it
    // would throw the cue away during the very wait it is waiting for.
    if (!target._fromRecord) {
      if (!eoisLoaded) return;
      var eoiBook = eoiActive.concat(eoiCancelled);
      var eoiRow = resolveNotifRow(eoiBook, target, isEoiCancelledRow);
      if (eoiRow) target = eoiRow;   // fail soft: no row in the book -> open the shell
    }
    // Through landOnEoi: it picks the tab (the "pending"/"approved" values that
    // used to be written here were never chips in the strip, so both rendered
    // the ACTIVE list with nothing highlighted), clears the project filter, and
    // hands the row to the lander so the page actually scrolls to it.
    landOnEoi(target);
    if (p.setInitSelected) p.setInitSelected(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.initSelected, eoisLoaded]);

  // Hydrate the panel's heavy fields (image, documents) when it opens.
  //
  // TWO THINGS WERE WRONG HERE.
  //
  // 1. It called setSelectedEOI(full), REPLACING the row with the plain lead
  //    document. That threw away every record-owned field the row carried —
  //    _recordNo, _rowKey, and the record's own project/budget/dates/notes — so
  //    opening a transaction's panel silently reverted it to the lead's live
  //    values and its label fell back to the lead id. It now MERGES: the lead
  //    supplies only what it still owns, and the row's own fields win.
  // 2. The promise ended in a bare .catch(function(){}), so any failure left the
  //    panel on "⌛ Loading…" forever with the image block hidden (both are
  //    gated on isEoiHydrated) and NOTHING on screen or in the console said why.
  //    That silence is the actual defect behind "EOI Documents never resolves":
  //    whatever the underlying cause, the UI could not report it. Failures now
  //    surface with a retry.
  //
  // A record-backed row takes its artifacts from ITS OWN record, so the right
  // transaction shows the right files instead of borrowing the lead's.
  useEffect(function(){
    if (!selectedEOI || !selectedEOI._id) return;
    var key = rowKey(selectedEOI);
    if (panelHydratedEoiId === key) return;
    var sid = String(selectedEOI._id);
    var recId = selectedEOI._fromRecord ? String(selectedEOI._rowKey) : null;
    var cancelled = false;
    setEoiHydrateErr("");
    // TWO INDEPENDENT ASKS — the twin of the Deals panel's, same ruling
    // (2026-09-10). Only a failure of the ARTIFACTS call is a files error; a
    // refusal on the lead fetch leaves the panel on the row it already has.
    Promise.all([
      apiFetch("/api/leads/"+sid,"GET",null,p.token).catch(function(){ return null; }),
      // PHASE 3 — ONE reader, for every row kind. A record-backed row asks by its
      // record; a plain lead row asks by its lead. Both answers come out of the
      // `artifacts` collection, so this panel no longer has two sources that can
      // disagree — which is exactly what let a converted EOI's files go missing.
      apiFetch("/api/artifacts?owner=" + (recId ? "eoi:"+recId : "lead:"+sid), "GET", null, p.token)
    ]).then(function(res){
      if (cancelled) return;
      var full = res[0], art = res[1];
      if (!full || !full._id || String(full._id) !== sid) full = {};
      setSelectedEOI(function(prev){
        if (!prev || rowKey(prev) !== key) return prev;   // panel moved on
        // ONE MERGE RULE, at module scope (hydrateTxnRow): lead first, row
        // second, and on a record-backed row the lead's copy of every
        // lifecycle key and of the developer pair is DROPPED, not preferred —
        // the lead's globalStatus "donedeal" is how a Pending EOI on a closed
        // client lost its Approve / Cancel buttons (#50058, three times).
        var merged = hydrateTxnRow(full, prev);
        // Heavy fields only the lead carries, unless this row owns a record.
        // No `full.` fallback: `art` is authoritative for BOTH row kinds now.
        // A fallback here would be a second source, and a second source is the bug.
        merged.eoiImage     = art ? (art.image || "") : "";
        merged.eoiDocuments = art ? (art.documents || []) : [];
        return merged;
      });
      setPanelHydratedEoiId(key);
    }).catch(function(err){
      if (cancelled) return;
      setEoiHydrateErr((err && err.message) || "Could not load this EOI's files.");
    });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selectedEOI && rowKey(selectedEOI)]);

  // Reset hydration tracker when the panel closes so the next open re-hydrates.
  useEffect(function(){ if (!selectedEOI) { setPanelHydratedEoiId(null); setEoiHydrateErr(""); } },[selectedEOI]);

  // ── OPTIMISTIC LIST PATCHES — what replaced the p.leads swap ───────────────
  // eoiScope used to fold p.leads over the fetched rows, so any mutation that
  // called p.setLeads showed up in the list "for free". It bought that by
  // SUBSTITUTING the whole lead copy for the row — discarding _rowKey,
  // _txnStatus, _recordNo and every record-owned value — and it only fired at
  // all when the lead happened to sit in the 100-row bootstrap, which an
  // EOI-stage lead usually does not (deal #50043's lead ranks 938 of 4,819). So
  // it was destructive when it worked and absent when it didn't: it is what made
  // the frozen-name revert look intermittent, and removing it FIRST would have
  // made that revert universal instead of fixing it.
  //
  // These patch this page's OWN row source with only the fields the action
  // changed — the shape the inline Developer picker and DealsPage.saveStages
  // already use.
  //
  // TWO KEYS, DELIBERATELY:
  //   patchEoiRow        keys on rowKey — ONE transaction. Approve / cancel /
  //                      convert act on a row, and a client holding two EOIs
  //                      must not have both move because one was approved.
  //   patchEoiRowsByLead keys on the lead — a fact about the CLIENT (archived is
  //                      the only one), true of every row that lead owns.
  var patchEoiRow = function(row, patch){
    var k = rowKey(row);
    setEoiAllData(function(prev){ return Array.isArray(prev)?prev.map(function(l){ return rowKey(l)===k?Object.assign({},l,patch):l; }):prev; });
  };
  // THE ROW IS GONE, not changed. Its record has been deleted server-side, so
  // there is no status this list could hold it under that the next fetch would
  // agree with. Keyed on rowKey like patchEoiRow — one transaction, not the
  // client — because a client's OTHER live EOI must stay exactly where it is.
  var dropEoiRow = function(row){
    var k = rowKey(row);
    setEoiAllData(function(prev){ return Array.isArray(prev)?prev.filter(function(l){ return rowKey(l)!==k; }):prev; });
  };
  var patchEoiRowsByLead = function(lid, patch){
    var id = String(lid);
    setEoiAllData(function(prev){ return Array.isArray(prev)?prev.map(function(l){ return gid(l)===id?Object.assign({},l,patch):l; }):prev; });
  };

  var archiveLead=async function(lid){
    if(!window.confirm(t.archiveConfirm))return;
    try{
      await apiFetch("/api/leads/"+lid+"/archive","PUT",null,p.token);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===lid?Object.assign({},l,{archived:true}):l;});});
      // Archiving is lead-level, so every row this lead owns goes with it.
      // eoiScope still filters on `archived`, so the rows leave immediately.
      patchEoiRowsByLead(lid, { archived:true });
      if(selectedEOI&&gid(selectedEOI)===lid)setSelectedEOI(null);
    }catch(e){alert(e.message);}
  };

  var handleImageUpload=async function(e,lead,imageType){
    var file=e.target.files[0]; if(!file)return;
    setImgUploading(true);
    try{
      var resized=await new Promise(function(resolve){
        var reader=new FileReader();
        reader.onload=function(ev){
          var img=new Image();
          img.onload=function(){
            var canvas=document.createElement("canvas");
            var maxW=1200,maxH=1200;
            var w=img.width,h=img.height;
            if(w>maxW){h=h*(maxW/w);w=maxW;}
            if(h>maxH){w=w*(maxH/h);h=maxH;}
            canvas.width=w;canvas.height=h;
            canvas.getContext("2d").drawImage(img,0,0,w,h);
            resolve(canvas.toDataURL("image/jpeg",0.7));
          };
          img.src=ev.target.result;
        };
        reader.readAsDataURL(file);
      });
      var kIE=rowKey(lead);
      var updated=await apiFetch("/api/leads/"+gid(lead)+"/upload-image","POST",{imageData:resized,imageType:imageType,txnId:kIE},p.token);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(lead)?updated:l;});});
      // PATCH — replacing with the lead doc dropped the row's record identity.
      setSelectedEOI(function(prev){ return prev&&rowKey(prev)===kIE?Object.assign({},prev,{eoiImage:updated.eoiImage}):prev; });
    }catch(ex){alert("Upload failed");}
    setImgUploading(false);
  };

  var toggleApproved=async function(lead,field){
    try{
      var newVal=!lead[field];
      var update={}; update[field]=newVal;
      // Keep eoiStatus in lock-step with the approval flag. The Pending/Approved
      // tab filters key off eoiStatus (falling back to eoiApproved only when it's
      // blank), so flipping eoiApproved alone leaves eoiStatus="Approved" and the
      // row stays in the Approved tab. Persisting both keeps FE + BE consistent
      // and lets the filter re-file the row into the right tab.
      if(field==="eoiApproved") update.eoiStatus = newVal ? "Approved" : "Pending";
      // NAME THE ROW. Without txnId the server infers the target from the lead's
      // status — a guess that is only ever right by luck on a client holding
      // more than one transaction. Sent for a record-backed row only; a lead-only
      // row has no record to name.
      var kTA = rowKey(lead);
      var bodyTA = lead._fromRecord ? Object.assign({}, update, { txnId: kTA }) : update;
      var updated=await apiFetch("/api/leads/"+gid(lead),"PUT",bodyTA,p.token);
      var mwTA = mirrorWarning(updated); if (mwTA) alert(mwTA);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(lead)?updated:l;});});
      // PATCH the row, never replace it with the lead document: `updated` carries
      // none of _rowKey/_recordNo/_txnCount/_txnIndex/_fromRecord, so assigning it
      // wholesale drops the row back to the lead's values and reverts its label
      // from the record's id to the lead's. Only the fields this action changed
      // are applied, keyed on rowKey so a lead's two rows cannot patch each other.
      var patchTA = {}; patchTA[field] = newVal;
      if(field==="eoiApproved"){ patchTA.eoiStatus = update.eoiStatus; patchTA._txnStatus = newVal ? "Approved" : "Pending"; }
      setSelectedEOI(function(prev){ return prev&&rowKey(prev)===kTA?Object.assign({},prev,patchTA):prev; });
      // The LIST row needs the same patch. It used to arrive via the p.leads
      // swap, which is gone — and which never reached a lead outside the
      // bootstrap anyway, so the badge and the tab filing simply did not move.
      patchEoiRow(lead, patchTA);
      // Follow the row to its destination tab so the open card isn't orphaned on a
      // list it no longer belongs to (fix 4) — and the admin sees where it landed.
      if(field==="eoiApproved") setEoiTab("active");   // both former values showed the Active list
    }catch(e){alert(e.message);}
  };

  // ===== RESTORE A CANCELLED EOI (R-T7 revised) ============================
  // Owner and admin only. Puts the record back on the Active tab as Pending on
  // its ORIGINAL number, so it needs approval again. The server refuses on a
  // newer live EOI or an approved deal and says which — surfacing `message`
  // verbatim is the whole reason those refusals name the blocking record.
  var canRestoreEoi = isOwnerFE(p.access) || p.cu.role==="admin";
  // Owner-only permanent delete (2026-11-02). The row to delete, or null.
  var [deleteEoiRow, setDeleteEoiRow] = useState(null);
  var hardDeleteEoi = async function(row){
    var out = await apiFetch("/api/records/eoi/"+rowKey(row),"DELETE",null,p.token,p.csrfToken);
    dropEoiRow(row);
    setSelectedEOI(function(prev){ return prev && rowKey(prev)===rowKey(row) ? null : prev; });
    setDeleteEoiRow(null);
    try { alert((out && out.message) || ("EOI #"+row._recordNo+" permanently deleted")); } catch(e){}
  };
  var [restoringEoi,setRestoringEoi]=useState("");
  var restoreEoi=async function(row){
    var recId = row && row._fromRecord ? String(row._rowKey) : null;
    if (!recId) { alert("This EOI predates the record layer and cannot be restored automatically."); return; }
    if (!window.confirm("Restore EOI "+txnRowLabel(row)+"?\n\nIt goes back to the Active tab as PENDING and needs approval again. It keeps its number.")) return;
    setRestoringEoi(rowKey(row));
    try {
      await apiFetch("/api/records/"+recId+"/restore-eoi","POST",{},p.token);
      patchEoiRow(row, { eoiStatus:"Pending", eoiApproved:false, _txnStatus:"Pending" });
      setSelectedEOI(null);
      setEoiTab("active");
    } catch(e) {
      alert(e && e.message ? e.message : "Restore failed");
    }
    setRestoringEoi("");
  };

  var cancelEOI=async function(lead){
    if(p.cu.role!=="admin"&&p.cu.role!=="operation") { alert("Only admin can cancel an EOI"); return; }
    if(!window.confirm("Cancel this EOI? The lead will return to Hot Case status and be rotated to another agent.")) return;
    setCancelling(true);
    try{
      // 1) Dedicated EOI-cancel endpoint restores status, sets eoiStatus="Deal Cancelled", syncs the DR mirror.
      // Name the record being cancelled (owner ruling 2026-09-12) — never "the open one".
      var updated = await apiFetch("/api/leads/"+gid(lead)+"/eoi-cancel","POST",{ txnId: lead._fromRecord ? rowKey(lead) : undefined },p.token);
      // 2) Auto-rotate via the ordered rotation list (backend skips previous agents).
      try {
        var rot = await apiFetch("/api/leads/"+gid(lead)+"/auto-rotate","POST",{reason:"manual"},p.token);
        if (rot && rot.lead) updated = rot.lead;
      } catch(rotErr){ /* status + eoiStatus already updated; rotation may be exhausted */ }
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(lead)?updated:l;});});
      // R-T7 REVISED — the row STAYS and moves to the Cancelled tab. PATCH it
      // rather than replacing it with the lead document, so the cancelled row
      // keeps its record identity and its own id label.
      var kCE=rowKey(lead);
      setSelectedEOI(function(prev){ return prev&&rowKey(prev)===kCE?Object.assign({},prev,{eoiStatus:"EOI Cancelled",_txnStatus:"Cancelled"}):prev; });
      // Same patch on the list row, so eoiRowBucket re-files it into Cancelled
      // now rather than on the next refetch.
      patchEoiRow(lead, { eoiStatus:"EOI Cancelled", _txnStatus:"Cancelled" });
      // Switch to the Cancelled tab so the admin sees where it went
      setEoiTab("cancelled");
    }catch(e){alert(e.message||"Cancel failed");}
    setCancelling(false);
  };

  var convertToDeal=async function(lead, form){
    // Author gate, mirroring POST /eoi-to-deal. Role alone is NOT the test — a TL /
    // manager / director may convert an EOI they authored, but not a subordinate's.
    if(!isOnlyAdmin && !isEoiAuthor(lead)) { alert("You can only convert an EOI you created."); return; }
    // Name the actual state. "must be Approved" on an already-converted EOI reads
    // as "go approve it" and sends the user looking for a button that should not
    // exist — the same misdiagnosis the server's 400 used to give.
    if(isConvertedRow(lead)) { alert("This EOI is already Deal "+txnRowLabel(lead)+". Record a second purchase as its own new deal."); return; }
    if(lead.eoiStatus!=="Approved") { alert("EOI must be Approved before converting to a Done Deal"); return; }
    // Full sale form validation (mirrors the backend /eoi-to-deal guards) — ALL
    // fields required (Yosra). Unit Price + Down Payment are money amounts.
    var f = form || {};
    var num = function(v){ var s=String(v||"").replace(/,/g,"").replace(/[^0-9.]/g,""); var n=parseFloat(s); return isFinite(n)?n:0; };
    if(!String(f.name||"").trim()) { alert("Client Name is required"); return; }
    if(!String(f.phone||"").trim()) { alert("Client Phone is required"); return; }
    if(!String(f.project||"").trim()) { alert("Project is required"); return; }
    if(!String(f.unitType||"").trim()) { alert("Please select a Unit Type"); return; }
    var code = String(f.unitCode||"").trim();
    if(!code) { alert("Please enter the Unit Code (e.g. B1-204)"); return; }
    var priceNum = num(f.budget);
    if(!(priceNum>0)) { alert("Unit Price (EGP) must be present and greater than 0 before converting to a Done Deal."); return; }
    if(f.saleType!=="Primary"&&f.saleType!=="Resale") { alert("Please select a Sale Type"); return; }
    // R-T5 — required, exactly as on the EOI/Deal status door.
    if(!String(f.developerId||"").trim() && !String(f.developerPending||"").trim()) { alert("Please pick a developer or enter a new name"); return; }
    if(f.saleType==="Resale") {
      // Resale conversion is BLOCKED when the EOI carries a deposit — a deposit is
      // a developer down payment, incompatible with a resale (direct full-payment).
      if(num(lead.eoiDeposit)>0) { alert("This EOI has a deposit — it can't be converted to a Resale deal (resales have no down payment). Convert as Primary instead."); return; }
      if(!String(f.dealDate||"").trim()) { alert("Please enter the Deal Date"); return; }
      // Two-party — >=1 with us; each with-us party needs name/phone/agent/commission ≤ price.
      var rpC = f.resaleParties || {};
      var wcC = 0;
      var chkC = function(side, lbl){
        var pty=rpC[side]||{};
        if(!pty.withUs) return true;
        wcC++;
        if(!String(pty.name||"").trim()){alert(lbl+" name is required (it's with us)");return false;}
        if(!String(pty.phone||"").trim()){alert(lbl+" phone is required (it's with us)");return false;}
        if(!String(pty.agentId||"").trim()){alert(lbl+" sales agent is required (it's with us)");return false;}
        var ca=num(pty.commissionAmount);
        if(!(ca>0)){alert(lbl+" commission amount is required (it's with us)");return false;}
        if(ca>priceNum){alert(lbl+" commission cannot exceed the Unit Price");return false;}
        return true;
      };
      if(!chkC("seller","Seller")) return;
      if(!chkC("buyer","Buyer")) return;
      if(wcC<1){alert("At least one party (seller or buyer) must be with us");return;}
    } else {
      if(!(num(f.downPayment)>0)) { alert("Please enter the Down Payment (EGP)"); return; }
      if(!String(f.downPaymentDate||"").trim()) { alert("Please enter the Down Payment Date"); return; }
      if(!String(f.reservationDate||"").trim()) { alert("Please enter the Reservation Date"); return; }
      if(!String(f.contractionDate||"").trim()) { alert("Please enter the Contract Date"); return; }
      if(!String(f.installmentYears||"").trim()) { alert("Please enter the Installments (years)"); return; }
    }
    // Phase R-1: warn when the deal's dealDate falls outside the current year.
    // Commission attribution uses dealDate's quarter, so a cross-year close can
    // retroactively change last year's commission shares. The date that becomes
    // dealDate is the Contraction Date (Primary) or the Deal Date (Resale).
    var effConvDate = f.saleType==="Resale" ? f.dealDate : f.contractionDate;
    var dealYr = null;
    try { if (effConvDate) dealYr = new Date(effConvDate).getUTCFullYear(); } catch(e){}
    var curYr = new Date().getUTCFullYear();
    if (dealYr && isFinite(dealYr) && dealYr !== curYr) {
      if (!window.confirm("⚠ This deal's date is in " + dealYr + " (not the current year " + curYr + "). Commission shares will be attributed to " + dealYr + " — confirm continue?")) return;
    }
    setConvertingDeal(true);
    var leadId = gid(lead);
    console.log("[convertToDeal] POST /api/leads/"+leadId+"/eoi-to-deal", { role: p.cu.role, eoiStatus: lead.eoiStatus, source: lead.source });
    try{
      var convBody = {
        // WHICH EOI (owner ruling 2026-09-12: every recordNo is independent). The
        // row's own record id names the EOI being converted, so a client holding
        // two EOIs converts the one that was clicked, not "the open one".
        txnId: lead._fromRecord ? rowKey(lead) : undefined,
        name: String(f.name||"").trim(), phone: String(f.phone||"").trim(),
        phone2: String(f.phone2||"").trim(),
        project: String(f.project||"").trim(), unitType: f.unitType||"", unitCode: code,
        budget: f.budget||"", saleType: f.saleType||"",
        // R-T5 — mutually exclusive, and only ONE of them is ever sent non-empty.
        // The server reads a PRESENT blank as an explicit clear (DISPLAY_CONTRACT),
        // which is exactly right here: choosing a developer must clear any pending
        // name, and typing a new name must clear the old link.
        developerId: String(f.developerId||"").trim(),
        developerPending: String(f.developerId||"").trim() ? "" : String(f.developerPending||"").trim(),
        downPayment: f.downPayment||"", downPaymentDate: f.downPaymentDate||"",
        reservationDate: f.reservationDate||"", contractionDate: f.contractionDate||"",
        installmentYears: f.installmentYears||"",
        // Resale-only: the single Deal Date (backend writes it into dealDate). The
        // backend clears the down-payment/installment fields for a resale anyway.
        dealDate: f.dealDate||""
      };
      if (f.saleType==="Resale") {
        var rpCv = f.resaleParties || {};
        var normPc = function(pty){ pty=pty||{}; var w=!!pty.withUs; return { withUs:w, name:String(pty.name||"").trim(), phone:String(pty.phone||"").trim(), agentId:w?(pty.agentId||""):"", commissionAmount:w?num(pty.commissionAmount):0, commissionRate:w?num(pty.commissionRate):0 }; };
        var selCv=normPc(rpCv.seller), buyCv=normPc(rpCv.buyer);
        convBody.resaleParties = { seller:selCv, buyer:buyCv };
        // Lead name/phone = first with-us party (backend requires them).
        var l1c = selCv.withUs?selCv:(buyCv.withUs?buyCv:selCv);
        convBody.name = l1c.name || convBody.name || "Resale";
        convBody.phone = l1c.phone || convBody.phone || "-";
      }
      var updated = await apiFetch("/api/leads/"+leadId+"/eoi-to-deal","POST",convBody,p.token);
      if (!updated || !updated._id) {
        console.error("[convertToDeal] empty response", updated);
        alert("Convert failed — empty response from server. Please refresh and try again.");
        setConvertingDeal(false);
        return;
      }
      console.log("[convertToDeal] ok", { status: updated.status, globalStatus: updated.globalStatus, eoiStatus: updated.eoiStatus });
      // Upload any deal documents attached in the modal (optional) — after the
      // conversion so they land on the now-DoneDeal lead. These are DEAL
      // documents (the picker says so) and are now stored as such: /deal-documents
      // files them bucket:"deal", which is the panel that will ask for them.
      // They used to go to /eoi-documents and be filed bucket:"eoi", surviving
      // only because reownStrandedArtifacts adopts the other bucket at record
      // creation — a rescue this no longer needs. Failures are non-fatal.
      if (Array.isArray(convertDocs) && convertDocs.length>0) {
        for (var ci=0; ci<convertDocs.length; ci++) {
          var cf=convertDocs[ci];
          if (!cf || !cf.fileData) continue;
          try {
            var withDocs = await apiFetch("/api/leads/"+leadId+"/deal-documents","POST",{fileData:cf.fileData, fileName:cf.fileName||""},p.token);
            if (withDocs && withDocs._id) updated = withDocs;
          } catch(docErr) { console.error("[convertToDeal] deal document upload failed:", docErr && docErr.message); }
        }
      }
      // Close the convert modal + the EOI side panel BEFORE touching p.leads. If
      // bubbling from the row click had already opened the detail panel, this
      // ensures the next render doesn't keep it open on a row that's about to
      // disappear from eoiScope.
      setConvertModal(null); setConvertDocs([]);
      setSelectedEOI(null);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===leadId?updated:l;});});
      // TAKE THE ROW OFF THIS PAGE, NOW. A converted EOI belongs to no chip here
      // — it is a deal and lives on the Deals page (owner ruling 2026-08-24).
      //
      // REMOVED, not restatused (2026-08-28). This used to patch the row to
      // _txnStatus:"Converted" so eoiRowBucket would file it as "none" — a
      // client-side echo of a server state that no longer exists. The server
      // DELETES the EOI record on conversion, so the row this list is holding
      // refers to a document that is gone; keeping it under any status would make
      // the page disagree with the next fetch. Dropping it is what the server
      // will report anyway, one refresh later.
      //
      // The history is worth keeping: before the restatus, this happened by
      // ACCIDENT — the p.leads swap replaced the row with the lead copy, which
      // carried status:"DoneDeal", and isConvertedDeal filed it as "none". That
      // only worked when the lead sat in the bootstrap, which is exactly why the
      // #50039 ghost appeared on some loads and not others.
      dropEoiRow(lead);
      // Belt-and-suspenders refresh, TARGETED (2026-08-22). This used to be a bare
      // GET /api/leads — no ?limit= — which returns the caller's ENTIRE book:
      // measured on production at 4,536 leads / ~77 MB / ~115-130 s for the largest
      // sales agent. Converting one deal downloaded the whole book to re-read one row.
      //
      // Only ONE lead changed, and its id is right here, so fetch that one. The
      // rewrite above already put `updated` into p.leads; this re-reads the
      // server's own copy in case the response and the stored doc differ.
      //
      // Concat when the row is absent rather than dropping it: a converted lead
      // can legitimately not be in p.leads yet (the summary slice is paged, so a
      // lead past page 1 was never loaded). Replacing a missing row with nothing
      // would silently lose it.
      try {
        var fresh = await apiFetch("/api/leads/" + String(leadId), "GET", null, p.token);
        if (fresh && fresh._id) p.setLeads(function(prev){
          var arr = prev || [], hit = false;
          var next = arr.map(function(l){ if (gid(l) === leadId) { hit = true; return fresh; } return l; });
          return hit ? next : next.concat([fresh]);
        });
      } catch(refreshErr) { console.error("[convertToDeal] lead refresh failed (non-fatal)", refreshErr); }
      // Navigate to the Deals page so the freshly-converted deal is visible.
      if (p.nav) p.nav("deals");
    }catch(e){
      console.error("[convertToDeal] failed", e);
      alert("Convert failed: "+(e && e.message ? e.message : "Unknown error"));
    }
    setConvertingDeal(false);
  };
  // Open the Convert-to-Deal modal for an EOI lead — prefill the full sale form
  // from the lead (Client + Property + Sales info); every field stays editable.
  // EXCEPTION: Project starts EMPTY (not inherited from lead.project) so the
  // agent must type the actual development the customer paid for — matching the
  // Add EOI / EOI-transition forms. Still required (guard in convertToDeal), so
  // it can't be skipped.
  // PREFILL EVERYTHING ALREADY KNOWN — EXCEPT PROJECT.
  //
  // `lead` here is the EOI ROW, and the row is already the resolution this form
  // needs: rowFromTxnRecord starts from the LEAD and overlays the EOI record on
  // top (server.js), and a DR-sourced lead IS the DR's mirror — so record → lead
  // → daily request is settled before the row reaches the browser. Reading the
  // row is therefore reading the whole chain, not just the lead.
  //
  // ── PROJECT OPENS BLANK. OWNER RULING 2026-07-20, RE-CONFIRMED 2026-08-26. ──
  //
  // DO NOT "FIX" THIS. `project: ""` is not an oversight and not a missing
  // prefill — it is the rule, and it has now been ruled on twice.
  //
  // WHY: `lead.project` is the development the customer ENQUIRED about. What a
  // Convert-to-Deal form records is the development they PAID for, and those are
  // not reliably the same. Agents were leaving the inherited inquiry project on
  // paid EOIs and deals, which put the wrong development on closed business —
  // and a wrong project on a deal is wrong revenue attribution, wrong developer,
  // wrong reporting. An empty required field forces one deliberate answer.
  //
  // The counter-argument is real and was tried: on 2026-08-26 this line briefly
  // read `lead.project||""` on the reasoning that retyping a value the system
  // already holds is how the wrong one gets typed. The owner reverted it the
  // same day. Retyping is the POINT here — the cost of a typo is a correctable
  // field, the cost of an inherited wrong project is money attributed to the
  // wrong development. If you are about to change this, that is the trade you
  // are overturning, and it needs the owner, not a code review.
  //
  // The field stays REQUIRED (convertToDeal blocks an empty project), and the
  // hint under it — t.projectPurchasedHint — says why it is empty, so a blank
  // box does not read as a bug to the agent looking at it.
  //
  // Everything ELSE on this form still prefills: the ruling is about one field.
  // Down Payment falls back to the EOI DEPOSIT: on all 7 live EOIs `downPayment`
  // is empty while `eoiDeposit` carries the money that was actually paid, so
  // without the fallback the field opens blank on every conversion there is.
  var openConvert=function(lead){ if(!lead) return; setConvertForm({
    name: lead.name||"", phone: lead.phone||"", phone2: lead.phone2||"",
    project: "", unitType: lead.unitType||"", unitCode: lead.unitCode||"",
    budget: lead.budget||"", saleType: lead.saleType||"Primary",
    // R-T5 — PREFILLED FROM THE EOI, and editable. Unlike Project (which opens
    // blank by owner ruling, because the development enquired about is not
    // reliably the development paid for), the developer IS the same fact at both
    // stages: the EOI already had to name one to be recorded at all. Carrying it
    // is what makes this field a confirmation rather than a re-entry.
    developerId: eoiDevIdOf(lead)||"", developerPending: (!eoiDevIdOf(lead)&&lead.developerPending)?lead.developerPending:"",
    downPayment: lead.downPayment||lead.eoiDeposit||"", downPaymentDate: lead.downPaymentDate||"",
    reservationDate: lead.reservationDate||"", contractionDate: lead.contractionDate||"",
    installmentYears: lead.installmentYears||"", dealDate: lead.dealDate||"",
    // Two-party Resale — default seller with-us, prefill its name/phone from the
    // EOI contact (the admin re-assigns/edits). Only used if Sale Type = Resale.
    resaleParties:{ seller:{ withUs:true, name:lead.name||"", phone:lead.phone||"", agentId:"", commissionAmount:"", commissionRate:"" }, buyer:{ withUs:false, name:"", phone:"", agentId:"", commissionAmount:"", commissionRate:"" } }
  }); setConvertDocs([]); setConvertDevPendOpen(!eoiDevIdOf(lead)&&!!lead.developerPending); setConvertModal(lead); };

  var handleDocUpload=async function(e,lead){
    var file=e.target.files[0]; if(!file) return;
    if (file.size>6*1024*1024) { alert("File too large (max 6MB)"); return; }
    setDocUploading(true);
    try{
      var dataUrl = await new Promise(function(resolve,reject){
        var reader=new FileReader();
        reader.onload=function(ev){resolve(ev.target.result);};
        reader.onerror=function(){reject(new Error("Read failed"));};
        reader.readAsDataURL(file);
      });
      // A historical row's files must never touch the lead; a live row still
      // writes the lead and carries txnId so the record is updated too.
      if(isHistoricalRow(lead)){
        var artU=await apiFetch("/api/records/"+rowKey(lead)+"/documents","POST",{fileData:dataUrl},p.token);
        setSelectedEOI(function(prev){ return prev&&rowKey(prev)===rowKey(lead)?Object.assign({},prev,{eoiDocuments:artU.documents||[]}):prev; });
        setDocUploading(false); return;
      }
      var kDU=rowKey(lead);
      var updated=await apiFetch("/api/leads/"+gid(lead)+"/eoi-documents","POST",{fileData:dataUrl,txnId:kDU},p.token);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(lead)?updated:l;});});
      // Match the historical branch above: patch by rowKey, never replace.
      setSelectedEOI(function(prev){ return prev&&rowKey(prev)===kDU?Object.assign({},prev,{eoiDocuments:updated.eoiDocuments||[]}):prev; });
    }catch(ex){alert("Upload failed: "+(ex.message||ex));}
    setDocUploading(false);
    try{ e.target.value=""; }catch(er){}
  };

  var deleteDoc=async function(lead,index){
    if(!window.confirm("Remove this document?")) return;
    try{
      if(isHistoricalRow(lead)){
        var artD=await apiFetch("/api/records/"+rowKey(lead)+"/delete-document","POST",{index:index},p.token);
        setSelectedEOI(function(prev){ return prev&&rowKey(prev)===rowKey(lead)?Object.assign({},prev,{eoiDocuments:artD.documents||[]}):prev; });
        return;
      }
      var kDD=rowKey(lead);
      var updated=await apiFetch("/api/leads/"+gid(lead)+"/delete-eoi-document","POST",{index:index,txnId:kDD},p.token);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(lead)?updated:l;});});
      // Match the historical branch above: patch by rowKey, never replace.
      setSelectedEOI(function(prev){ return prev&&rowKey(prev)===kDD?Object.assign({},prev,{eoiDocuments:updated.eoiDocuments||[]}):prev; });
    }catch(e){alert(e.message||"Delete failed");}
  };

  // EOI table header columns. Single source of truth for both the <th> render
  // and the inline-panel row's colSpan (eoiColCount): 11 columns for every role
  // that can open the page, 10 without the Agent column. Documentation only —
  // the colSpan is derived from this array's length and always tracks it.
  // CLIENT PHONE IS NOT ROLE-GATED HERE (owner ruling 2026-08-26). Anyone who can
  // OPEN this page may see phone / phone2. The two pages used to disagree — EOI
  // said role!=="operation", Deals said role==="admin" — and the split was three
  // successive tightenings inside eleven minutes on 2026-03-27 (cb14124 19:31 ->
  // b1d9e23 19:42 -> 0ee2f21 19:53, header restored by ed64d7e the next morning)
  // that were never reconciled. Both gates were also COSMETIC: the server has
  // always shipped phone/phone2 in EOI_LIST_FIELDS to everyone who could see the
  // row, so the number was one devtools panel away regardless. The real rule now
  // lives in listFieldsFor() on the server — see the policy note above
  // EOI_LIST_FIELDS. Do not put a client-side role test back here.
  // 2026-10-29 (owner task): Alt. Phone lives on line 2 of the Phone cell and
  // Unit Type on line 2 of the Project cell — the two columns are gone from the
  // desktop table (admin-side roles=9, sales=8).
  var eoiHeaderCols=[t.name,t.phone,t.project,t.budget,"Deposit",isAdmin?t.agent:null,"EOI Date","Approved",""].filter(function(h){return h!==null;});
  var eoiColCount=eoiHeaderCols.length;

  // EOI detail panel. Shared renderer for the desktop inline-accordion row
  // (full-width, directly under the selected EOI row) and the mobile
  // full-screen overlay — only the outer `style` differs (passed in by the
  // caller). At most one mounts per render (desktop XOR mobile, gated on
  // p.isMobile), so ref={eoiPanelRef} attaches to a single node and the
  // click-outside close keeps working. Inner content + all logic
  // (toggleApproved/cancelEOI/convertToDeal, image+doc upload/delete,
  // isEoiHydrated gates) is unchanged from the prior beside-the-table panel —
  // only WHERE it mounts on desktop changed.
  var renderEoiPanel=function(styleObj){
    return <div ref={eoiPanelRef} style={styleObj}>
      <div style={{ background:"#F5F3FF", border:"1px solid #DDD6FE", borderRadius:10, margin:"4px 8px", overflow:"hidden", contain:"content" }}>
      <div style={{ background:"#F5F3FF", borderBottom:"1px solid #DDD6FE", padding:"calc(14px + env(safe-area-inset-top, 0px)) 16px" }}>
        {/* Journey strip: Pending → Approved → Deal. Current stage = colored pill
            (amber Pending / green Approved / green Deal); passed = green check;
            future = muted. Hidden for cancelled EOIs (they keep the standalone
            "EOI Cancelled" badge on the right — see the action block below). */}
        {(function(){
          var isCancelled = selectedEOI.eoiStatus==="EOI Cancelled" || selectedEOI.eoiStatus==="Deal Cancelled" || selectedEOI.status==="Deal Cancelled";
          if (isCancelled) return null;
          var stageIdx = selectedEOI.status==="DoneDeal" ? 2 : (selectedEOI.eoiApproved ? 1 : 0);
          var stages = [
            { label:"Pending",  icon:"⏳", pill:{ bg:"#FEF3C7", fg:"#B45309" } },
            { label:"Approved", icon:"✅", pill:{ bg:"#DCFCE7", fg:"#15803D" } },
            { label:"Deal",     icon:"🎉", pill:{ bg:"#DCFCE7", fg:"#15803D" } },
          ];
          return <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:12 }}>
            {stages.map(function(s,i){
              return <Fragment key={s.label}>
                {i>0 && <span style={{ fontSize:11, color:C.textLight, opacity:0.5 }}>→</span>}
                {i<stageIdx
                  ? <span style={{ fontSize:11, fontWeight:700, color:"#15803D", whiteSpace:"nowrap" }}>✓ {s.label}</span>
                  : i===stageIdx
                  ? <span style={{ fontSize:11, fontWeight:700, color:s.pill.fg, background:s.pill.bg, padding:"3px 10px", borderRadius:20, whiteSpace:"nowrap" }}>{s.icon} {s.label}</span>
                  : <span style={{ fontSize:11, fontWeight:600, color:C.textLight, opacity:0.55, whiteSpace:"nowrap" }}>{s.label}</span>}
              </Fragment>;
            })}
            {/* Walk an approval back to Pending — admin cluster only, and only
                when currently Approved (not yet a deal). Reuses the same
                toggleApproved handler as the Approve action. */}
            {isOnlyAdmin && selectedEOI.eoiApproved && selectedEOI.status!=="DoneDeal" && <button onClick={function(){toggleApproved(selectedEOI,"eoiApproved");}} title="Move this EOI back to Pending" style={{ marginLeft:"auto", background:"none", border:"none", padding:0, color:C.textLight, fontSize:11, fontWeight:600, cursor:"pointer", textDecoration:"underline", whiteSpace:"nowrap" }}>↩ Revert to pending</button>}
          </div>;
        })()}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:"8px 10px", flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0, flex:"1 1 auto" }}>
            <button onClick={function(){setSelectedEOI(null);}} style={{ background:"#fff", border:"1px solid #DDD6FE", borderRadius:6, width:24, height:24, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#6D28D9", flexShrink:0 }}><X size={11}/></button>
            <div style={{ width:36, height:36, borderRadius:"50%", background:"#EDE9FE", color:"#6D28D9", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:700, flexShrink:0 }}>{(selectedEOI.name||"?").charAt(0).toUpperCase()}</div>
            <div style={{ minWidth:0 }}>
              <div style={{ color:"#4C1D95", fontSize:14, fontWeight:700, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{selectedEOI.name}</div>
              {canSeeLeadIds(p.cu) && <div style={{ color:C.textLight, fontSize:11, marginTop:2 }}>{txnRowLabel(selectedEOI)}</div>}
              <div style={{ color:"#6D28D9", fontSize:11, marginTop:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}><PhoneCell phone={selectedEOI.phone}/></div>
              {selectedEOI.phone2&&selectedEOI.phone2!==selectedEOI.phone&&<div style={{ color:"#6D28D9", fontSize:11, marginTop:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}><PhoneCell phone={selectedEOI.phone2}/></div>}
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0, marginLeft:"auto" }}>
            {(function(){
              var isCancelled = selectedEOI.eoiStatus==="EOI Cancelled" || selectedEOI.eoiStatus==="Deal Cancelled" || selectedEOI.status==="Deal Cancelled";
              if (isCancelled) return <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap", justifyContent:"flex-end" }}>
                <span style={{ background:C.danger+"18", borderRadius:8, padding:"4px 10px", color:C.danger, fontSize:11, fontWeight:700 }}>❌ EOI Cancelled</span>
                {/* RESTORE — R-T7 revised. Owner and admin only; the server refuses
                    everyone else regardless, because a hidden button is not a gate.
                    Lifecycle says the action is meaningful in this state
                    (eoiLifecycleActions), the role test is applied HERE — the two
                    axes this block has always kept separate. */}
                {/* ALWAYS RENDERED, DISABLED WHEN REFUSED (owner ruling
                    2026-09-11). It used to be enabled on every cancelled row and
                    discover its own refusal by being pressed. The reason arrives
                    with the row now (`_restoreRefusal`, decided server-side by
                    restoreEoiRefusal) and shows as the title and a line below —
                    so the answer is on screen before the click, not after it. */}
                {canRestoreEoi && eoiAllows(selectedEOI, "restore") && (function(){
                  var reason = eoiRestoreReason(selectedEOI);
                  var busy = restoringEoi===rowKey(selectedEOI);
                  var off = !!reason || busy;
                  return <button disabled={off} onClick={off?undefined:function(){restoreEoi(selectedEOI);}}
                    title={reason || "Put this EOI back on the Active tab as Pending — it keeps its number and needs approval again"}
                    style={{ background:"transparent", border:"1px solid "+(reason?C.border:C.warning), borderRadius:8, padding:"6px 12px", cursor:busy?"wait":(reason?"not-allowed":"pointer"), color:reason?C.textLight:C.warning, fontSize:11, fontWeight:700, opacity:off?0.55:1 }}>
                    {busy?"Restoring…":"↻ Restore EOI"}
                  </button>;
                })()}
                {/* The sentence, not just a tooltip — a title is invisible on a
                    phone, which is where this page is most used. */}
                {canRestoreEoi && eoiRestoreReason(selectedEOI) &&
                  <div style={{ flexBasis:"100%", textAlign:"right", fontSize:11, color:C.textLight, lineHeight:1.4, marginTop:2 }}>
                    {eoiRestoreReason(selectedEOI)}
                  </div>}
              </div>;
              // A converted EOI is a deal and does not belong to this page at all,
              // so it is not listed and this panel cannot normally be opened for
              // one. Kept as the last line of defence: if a converted row ever does
              // leak here, it carries NO actions rather than the wrong ones —
              // Convert would 409 already_converted, and Cancel would 409
              // eoi_already_converted because it would otherwise un-deal a closed
              // sale and leave its commission live.
              //
              // isConvertedRow, not selectedEOI.status. The version of this branch
              // that read `status === "DoneDeal"` was dead for exactly the case it
              // was written for, because rowFromTxnRecord pins row.status to "EOI".
              // _txnStatus is the record's own word and cannot be overridden.
              if (isConvertedRow(selectedEOI)) return null;
              // Convert is offered to admin/SA and to the EOI's AUTHOR whatever their
              // role (canConvertEoi); Approve and Cancel remain isOnlyAdmin. A non-admin
              // author therefore sees exactly ONE button here, and only on an Approved
              // EOI they made — never Approve, never Cancel, and nothing at all on a
              // subordinate's EOI that merely sits inside their team scope.
              // NOTE: the two early returns above already handled cancelled + DoneDeal, so
              // for an admin canConvertEoi() reduces to eoiApproved — admin behaviour is
              // byte-for-byte what it was.
              var rowStyle = { display:"flex", gap:6, alignItems:"center", flexWrap:"wrap", justifyContent:"flex-end" };
              // Lifecycle axis from eoiLifecycleActions, role axis here. The two
              // early returns above render the BADGES; these decide the buttons,
              // and they agree with the early returns by construction rather than
              // by two conditions being kept in step.
              var mayApprove = eoiAllows(selectedEOI, "approve");
              var mayCancel  = eoiAllows(selectedEOI, "cancel");
              var convertBtn = canConvertEoi(selectedEOI)
                ? <button disabled={convertingDeal} onClick={function(){openConvert(selectedEOI);}} style={{ background:MINT_BG, border:"none", borderRadius:8, padding:"6px 14px", cursor:convertingDeal?"wait":"pointer", color:MINT_FG, fontSize:11, fontWeight:700, opacity:convertingDeal?0.6:1, boxShadow:"0 1px 3px rgba(4,52,44,0.18)" }}>
                    {convertingDeal?"Converting…":"Convert to Deal"}
                  </button>
                : null;
              if (!isOnlyAdmin) {
                return convertBtn ? <div style={rowStyle}>{convertBtn}</div> : null;
              }
              // State-aware primary action (same handlers as before):
              //   Pending  → Approve (green). No convert yet.
              //   Approved → Convert to Deal (mint). "Approved" now lives as the
              //              journey-strip badge, not a toggle button.
              return <div style={rowStyle}>
                {convertBtn
                  ? convertBtn
                  : mayApprove && <button onClick={function(){toggleApproved(selectedEOI,"eoiApproved");}} style={{ background:"#15803D", border:"none", borderRadius:8, padding:"6px 14px", cursor:"pointer", color:"#fff", fontSize:11, fontWeight:700, boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }}>
                      ✅ Approve
                    </button>}
                {mayCancel && <button disabled={cancelling} onClick={function(){cancelEOI(selectedEOI);}} style={{ background:"transparent", border:"1px solid "+C.danger, borderRadius:8, padding:"6px 12px", cursor:cancelling?"wait":"pointer", color:C.danger, fontSize:11, fontWeight:700, opacity:cancelling?0.6:1 }}>
                  {cancelling?"Cancelling…":"Cancel"}
                </button>}
                {canHardDeleteRow(p.access, selectedEOI) && eoiAllows(selectedEOI,"delete") && <button onClick={function(){setDeleteEoiRow(selectedEOI);}} title="Permanently delete this EOI (owner only)"
                  style={{ background:"transparent", border:"1px solid "+C.danger, borderRadius:8, padding:"6px 12px", cursor:"pointer", color:C.danger, fontSize:12, fontWeight:700 }}>
                  🗑 Delete
                </button>}
              </div>;
            })()}
          </div>
        </div>
      </div>
      {/* ORIGIN (owner brief 2026-10-26): one collapsed line under the header —
          where this EOI came from — expanding into the full chain. Pure model +
          component in src/lib/txnOrigin.js; keyed by the row so the lazy DR
          fetch and the open state never carry over between records. Names go
          through the shared resolvers (userNameOf / txnAgentNameOf); the links
          open the lead / request through the same nav landers the bells use.
          ADMIN CLUSTER ONLY (owner ruling 2026-10-27): mounted behind this
          page's own isOnlyAdmin — admin + operation — so every other role's
          card is exactly what it was before the feature. Pinned per position by
          verify-mobile-filter-chrome.js. */}
      {deleteEoiRow && <TxnDeleteModal kind="eoi" row={deleteEoiRow} colors={C} onClose={function(){setDeleteEoiRow(null);}} onConfirm={function(){ return hardDeleteEoi(deleteEoiRow); }}/>}
      {isOnlyAdmin && <OriginBlock key={"origin:"+rowKey(selectedEOI)} row={selectedEOI} kind="eoi" cu={p.cu} token={p.token} apiFetch={apiFetch} colors={C}
        nameOf={function(ref){ return userNameOf(ref, p.users); }} creditedName={txnAgentNameOf(selectedEOI, p.users)}
        brokerName={brokerNameOf(selectedEOI.externalBrokerId, [])} externalAgentName={userNameOf(selectedEOI.externalSalesAgentId, p.users) || (selectedEOI.externalSalesAgentName || "")}
        onOpenLead={function(id){ apiFetch("/api/leads/"+id,"GET",null,p.token).then(function(full){ if (full && full._id) p.nav("leads", full); else alert("Could not open this lead."); }).catch(function(){ alert("Could not open this lead."); }); }}
        onOpenDr={function(d){ if (d && d._id) { p.nav("dailyReq", d); return; } apiFetch("/api/daily-requests/"+d,"GET",null,p.token).then(function(full){ if (full && full._id) p.nav("dailyReq", full); else alert("Could not open this request."); }).catch(function(){ alert("Could not open this request."); }); }}/>}
      <div style={{ padding:"12px 14px" }}>
        {(function(){
        // Same tile chrome as the Deal panel (owner brief 2026-10-27): desktop is
        // one FIVE-column row — Project · Budget · Deposit · Agent · Developer —
        // with Notes across the full width; every tile one line at a common
        // height. Mobile keeps its auto-fill grid and its own Developer block.
        var eoiTile=function(f, extra){ return <div key={f.l} style={Object.assign({ background:"#fff", borderRadius:8, padding:"8px 10px", minWidth:0, boxSizing:"border-box" }, p.isMobile?{ alignSelf:"start" }:{ minHeight:64 }, extra||{})}>
          <div style={{ fontSize:11, color:C.textLight, marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{f.icon} {f.l}</div>
          <div title={typeof f.v==="string"?f.v:undefined} style={(p.isMobile||f.wrap)?{ fontSize:13, color:C.text, wordBreak:"break-word" }:{ fontSize:13, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{f.v}</div>
        </div>; };
        var devTileStyle=p.isMobile?{ marginTop:12, padding:12, background:"#fff", borderRadius:8 }:{ background:"#fff", borderRadius:8, padding:"8px 10px", minHeight:64, minWidth:0, boxSizing:"border-box" };
        var devLabelStyle=p.isMobile?{ fontSize:11, fontWeight:700, color:C.textLight, marginBottom:6 }:{ fontSize:11, color:C.textLight, marginBottom:2 };
        /* Developer — EOI panel parity with the Deals panel: the picker (writes
            Lead.developerId via PUT, subject to the approval lock for non-admin
            roles), or the value when this viewer or this row cannot edit it. */
        var devTile = canSeeDeveloper(p.cu)&&<div key="dev" style={devTileStyle}>
          <div style={devLabelStyle}>🏗️ Developer</div>
          {/* A converted EOI is read-only, so it renders the value and no editor.
              The write behind this control routes through saveRowEdit, which now
              sends a converted row to PATCH /api/records/:id — a guaranteed 409
              eoi_converted. Offering a picker that can only fail is worse than
              showing the frozen fact. The deal owns this field now. */}
          {/* TWO BRANCHES, and there is no third. The pending-developer banner
              used to sit between them; it is gone (see "THERE IS NO PENDING
              DEVELOPER" at module scope). `developerPending` is no longer read
              here at all — a row's developer is its developerId or nothing, and
              falling back to a typed string is what let this panel show one
              source's answer and then the other's. */}
          {(!canEditDeveloper(p.cu) || isConvertedRow(selectedEOI))
          ? <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{eoiDevNameOf(selectedEOI)||"— None —"}</div>
          : <SearchableSelect
              value={eoiDevIdOf(selectedEOI)}
              emptyLabel="— None —"
              placeholder="Search developers…"
              width="100%" maxWidth={9999} fontSize={12}
              options={(function(){
                var base=(eoiDevelopers||[]).map(function(dv){ return { value:String(dv._id), label:dv.name }; });
                var cur=eoiDevIdOf(selectedEOI);
                if(cur && !base.some(function(o){ return o.value===cur; })) base.unshift({ value:cur, label:eoiDevNameOf(selectedEOI)||"Unknown developer" });
                return base;
              })()}
              onChange={async function(val){
                try{
                  var row=selectedEOI, k=rowKey(row);
                  await saveRowEdit(row,{developerId:val||null},p.token);
                  // Patch, never replace — and key on the ROW. gid() would patch
                  // BOTH rows of a lead holding two transactions.
                  var devObj=(eoiDevelopers||[]).find(function(x){ return String(x._id)===String(val); });
                  var patch={ developerId: devObj || (val||null) };
                  setSelectedEOI(function(prev){ return prev&&rowKey(prev)===k?Object.assign({}, prev, patch):prev; });
                  setEoiAllData(function(prev){ return Array.isArray(prev)?prev.map(function(l){ return rowKey(l)===k?Object.assign({},l,patch):l; }):prev; });
                  // The lead list only mirrors a LIVE row; a historical edit never touched the lead.
                  if(p.setLeads && !isHistoricalRow(row)) p.setLeads(function(prev){ return prev.map(function(l){ return gid(l)===gid(row)?Object.assign({},l,patch):l; }); });
                }catch(ex){ alert((ex&&ex.message)||"Update failed"); }
              }}/>}
        </div>;
        return <>
        <div style={{ display:"grid", gridTemplateColumns:p.isMobile?"repeat(auto-fill, minmax(150px, 1fr))":"repeat(5, minmax(180px, 1fr))", gap:10, alignItems:"stretch" }}>
        {[
          {l:"Project",v:selectedEOI.project||"-",icon:"🏠"},
          {l:"Budget",v:selectedEOI.budget?selectedEOI.budget+" EGP":"-",icon:"💰"},
          {l:"Deposit",v:selectedEOI.eoiDeposit||"-",icon:"💵"},
          {l:"Agent",v:getAg(selectedEOI),icon:"👤"},
        ].map(function(f){ return eoiTile(f); })}
        {!p.isMobile && devTile}
        {eoiTile({l:"Notes",v:selectedEOI.eoiNotes||"-",icon:"📝",wrap:true}, p.isMobile?null:{ gridColumn:"1 / -1" })}
        </div>
        {p.isMobile && devTile}
        </>;
        })()}

        {/* EOI Image — mirrors the Deal card's Contract Images: the whole section
            (incl. any upload affordance) is shown ONLY when an image already
            exists. No image → hidden entirely; EOI Documents below (which accepts
            image or PDF) is the sole upload area. Existing images keep Replace. */}
        {isEoiHydrated && selectedEOI.eoiImage &&
        <div style={{ marginTop:12 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, marginBottom:6 }}>📎 EOI Image</div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"flex-start" }}>
            <TxnDocThumb src={selectedEOI.eoiImage} alt="EOI" title="Tap to view full size" bordered={false}
              onOpen={function(){ setEoiDocViewerSrc(selectedEOI.eoiImage); }}/>
            <label style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", width:80, height:80, flex:"0 0 auto", borderRadius:8, border:"1px dashed "+C.accent, background:C.accent+"08", color:C.accent, fontSize:10, fontWeight:600, cursor:"pointer", textAlign:"center", boxSizing:"border-box" }}>
              {imgUploading?"Uploading…":"🔄 Replace Image"}
              <input type="file" accept="image/*" style={{ display:"none" }} onChange={function(e){handleImageUpload(e,selectedEOI,"eoi");}}/>
            </label>
          </div>
        </div>}

        <DocViewer src={eoiDocViewerSrc} alt="EOI document" onClose={function(){ setEoiDocViewerSrc(""); }}/>
        {/* EOI Documents (images + PDFs) */}
        <div style={{ marginTop:12 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, marginBottom:6 }}>📄 EOI Documents {isEoiHydrated ? "("+(selectedEOI.eoiDocuments||[]).length+")" : ""}</div>
          {/* A failure used to be swallowed, leaving this block on "Loading…"
              forever with no explanation anywhere. Say so, and offer a retry. */}
          {eoiHydrateErr
            ?<div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap", padding:"10px 12px", borderRadius:8, border:"1px solid #FCA5A5", background:"#FEE2E2", color:"#B91C1C", fontSize:11, fontWeight:600 }}>
                <span>⚠️ Couldn’t load this EOI’s files: {eoiHydrateErr}</span>
                <button onClick={function(){ setPanelHydratedEoiId(null); setEoiHydrateErr(""); setSelectedEOI(function(prev){ return prev?Object.assign({},prev):prev; }); }} style={{ padding:"4px 12px", borderRadius:7, border:"1px solid #B91C1C", background:"#fff", color:"#B91C1C", fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>↻ Retry</button>
              </div>
            :!isEoiHydrated
            ?<div style={{ padding:"16px", borderRadius:8, border:"1px dashed #E2E8F0", color:C.textLight, fontSize:11, textAlign:"center" }}>⌛ Loading…</div>
            :<>
              {/* EMPTY IS NOT AN ERROR — the twin of the Deals panel's, same ruling. */}
              {(selectedEOI.eoiDocuments||[]).length===0&&
                <div style={{ padding:"14px", borderRadius:8, border:"1px dashed #E2E8F0", color:C.textLight, fontSize:11, textAlign:"center", marginBottom:8 }}>No documents yet</div>}
              <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"flex-start", marginBottom:8 }}>
              {(selectedEOI.eoiDocuments||[]).length>0&&<div style={{ display:"contents" }}>
                {(selectedEOI.eoiDocuments||[]).map(function(doc,idx){
                  var url = typeof doc==="string" ? doc : (doc && doc.url) || "";
                  var name = typeof doc==="object" && doc && doc.name ? doc.name : ("Document "+(idx+1));
                  var isPdf = (typeof doc==="object" && doc && String(doc.contentType||"").toLowerCase()==="application/pdf") || /\.pdf$/i.test(String(name||"").split("?")[0]) || /\.pdf$/i.test(String(url||"").split("?")[0]) || (typeof url==="string" && url.indexOf("application/pdf")>=0);
                  // IMAGE: the whole thumbnail is the tap target and opens the in-app
                  // viewer; the ✕ is its own target and never opens it (TxnDocThumb,
                  // 2026-10-01). PDF: a target=_blank link with NO download attribute,
                  // so the native viewer opens it (2026-10-02) — never the image viewer.
                  if (!isPdf) return <TxnDocThumb key={idx} src={url} alt={name} title={name}
                    onOpen={function(){ setEoiDocViewerSrc(url); }}
                    onDelete={isOnlyAdmin ? function(){ deleteDoc(selectedEOI,idx); } : null} deleteTitle="Remove"/>;
                  return <div key={idx} style={{ position:"relative", width:80, height:80, flex:"0 0 auto", border:"1px solid #E2E8F0", borderRadius:8, overflow:"hidden", background:"#fff" }} title={name}>
                    <a href={url} target="_blank" rel="noreferrer" style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", textDecoration:"none", color:"#DC2626", fontSize:10, fontWeight:700, padding:4, textAlign:"center" }}><span style={{ fontSize:22 }}>📕</span><span style={{ maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</span></a>
                    {isOnlyAdmin&&<button onClick={function(){deleteDoc(selectedEOI,idx);}} title="Remove" style={{ position:"absolute", top:2, right:2, width:18, height:18, borderRadius:"50%", border:"none", background:"rgba(220,38,38,0.9)", color:"#fff", fontSize:10, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>×</button>}
                  </div>;
                })}
              </div>}
              <label style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", width:80, height:80, flex:"0 0 auto", borderRadius:8, border:"1px dashed "+C.accent, background:C.accent+"08", color:C.accent, fontSize:10, fontWeight:600, cursor:"pointer", textAlign:"center", boxSizing:"border-box", padding:4 }}>
                {docUploading?"Uploading…":"📎 Upload EOI Document (image or PDF)"}
                <input type="file" accept="image/*,application/pdf" style={{ display:"none" }} onChange={function(e){handleDocUpload(e,selectedEOI);}}/>
              </label>
              </div>
            </>}
        </div>
      </div>
      </div>
    </div>;
  };

  return <div style={{ padding:"18px 16px 40px" }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        {/* 2026-10-29 (owner task): the "N EOIs" count is gone from the header on
            both layouts — the selected tab carries the count. */}
        {total>0&&<div data-total-pill={eoiTab} style={txnTotalPillStyle("eoi", eoiTab, C.success)}>Total: {total.toLocaleString()} EGP</div>}
      </div>
      {/* 2026-10-30 (owner task): on the phone the header button is gone — the
          orange floating ＋ (the Leads MobileFab, same component, position and
          size) opens the same Add EOI form; on desktop the button keeps its
          orange and takes the Deals header button box (TXN_ADD_BTN_STYLE). */}
      {isOnlyAdmin&&!p.isMobile&&<Btn onClick={function(){setShowAdd(true);}} style={TXN_ADD_BTN_STYLE}><Plus size={14}/> Add EOI</Btn>}
    </div>
    {isOnlyAdmin&&p.isMobile&&<MobileFab open={false} hidden={hasRenderableEOI} onToggle={function(){setShowAdd(true);}} onClose={function(){}}><Btn onClick={function(){setShowAdd(true);}}><Plus size={14}/> Add EOI</Btn></MobileFab>}

    {/* EOI load state \u2014 never render another agent's rows from the broad
        bootstrap while the author-scoped /api/eois fetch is in flight.
        Error \u2192 loud banner + Retry; loading \u2192 spinner text. */}
    {eoiLoadErr
      ? <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap", background:"#FEE2E2", border:"1px solid #FCA5A5", color:"#B91C1C", borderRadius:10, padding:"10px 14px", marginBottom:12, fontSize:13, fontWeight:600 }}>
          <span>⚠️ Couldn’t load EOIs: {eoiLoadErr} Please retry.</span>
          <button onClick={function(){ setEoiReloadKey(function(k){ return k+1; }); }} style={{ padding:"6px 14px", borderRadius:8, border:"1px solid #B91C1C", background:"#fff", color:"#B91C1C", fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>↻ Retry</button>
        </div>
      : (!eoisLoaded && <div style={{ background:"#F1F5F9", border:"1px solid #E2E8F0", color:C.textLight, borderRadius:10, padding:"10px 14px", marginBottom:12, fontSize:13, fontWeight:600 }}>⏳ Loading EOIs…</div>)
    }

    <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
      {eoiStrip.tabs.map(function(tb){
        var active = eoiTab===tb.id;
        // The count shows ONLY on the selected tab (owner 2026-10-29, eoiTabText); Cancelled shows a red dot when ≥1 cancelled EOI exists (EOI has no period — whole set) while Active is selected.
        return <button key={tb.id} onClick={function(){setSelectedEOI(null);setEoiTab(tb.id);}} style={txnTabStyle(tb, active, p.isMobile, C.textLight)}>{eoiTabText(tb, active, tb.id==="cancelled"?eoiCancelledInSource.length:eoiActiveInSource.length)}{tb.id==="cancelled" && <CancelledDot show={eoiCancelledInSource.length >= 1 && eoiTab!=="cancelled"} title="Cancelled EOIs exist"/>}</button>;
      })}
      {eoiStrip.empty && <span style={{ fontSize:13, color:C.textLight }}>{NO_TABS_MSG}</span>}
      {/* 2026-10-29 (owner task): "⏳ N pending approval" pill beside the chips
          on desktop (Active tab), hidden at 0 — the Deals page's twin. An EOI
          row awaits approval while its record's eoiApproved is not true (R6). */}
      {!eoiStrip.empty && eoiTab!=="cancelled" && <PendingApprovalPill n={eoiPendingN} active={eoiPendingOnly} short={p.isMobile} style={p.isMobile?{ marginLeft:"auto" }:undefined} onClick={function(){ setEoiPendingOnly(!eoiPendingOnly); }} onClear={function(){ setEoiPendingOnly(false); }} title={eoiPendingOnly ? "Showing EOIs awaiting approval — click to show all" : "EOIs awaiting approval — click to show only these"}/>}
    </div>

    {/* Project filter — added for parity with the Deals page (the EOI list had
        no filter bar before). Client-side over the free-text `project` string;
        AND-combines with the active/cancelled tab. Hidden when no projects. */}
    {/* Source filter (owner 2026-10-30): the Deals page's control beside
        "All projects" — on the phone the two sit side by side in a 2-column
        row, the same cell the Deals filter grid uses. Admin-cluster only
        (the card's Source line gate). */}
    {eoiProjectOpts.length>0&&<div style={p.isMobile?{ display:"grid", gridTemplateColumns:isAdmin?"1fr 1fr":"1fr", gap:8, marginBottom:14 }:{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
      <div style={{ minWidth:0 }}><SearchableSelect multi value={eoiProjectFilter} onChange={setEoiProjectFilter} options={eoiProjectOpts} emptyLabel="🏠 All projects" placeholder="Search projects…" width={p.isMobile?"100%":220} maxWidth={p.isMobile?9999:220} fontSize={12}/></div>
      {isAdmin&&<div style={{ minWidth:0 }}><SearchableSelect multi value={eoiSourceFilter} onChange={setEoiSourceFilter} options={eoiSourceOpts} emptyLabel="All sources" placeholder="Search source…" width={p.isMobile?"100%":170} maxWidth={p.isMobile?9999:170} fontSize={12} triggerStyle={eoiSourceFilter.length===1?sourceNameStyle(eoiSourceFilter[0]):undefined}/></div>}
      {!!(eoiProjectFilter.length||eoiSourceFilter.length)&&<button onClick={function(){setEoiProjectFilter([]);setEoiSourceFilter([]);}} style={{ gridColumn:"1 / -1", padding:"7px 12px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", fontSize:12, color:C.danger }}>✕ Clear</button>}
    </div>}

    {showAdd&&<Modal show={true} onClose={function(){setShowAdd(false);}} title={"➕ Add EOI"}>
      <LeadForm t={t} cu={p.cu} users={p.users} token={p.token} isMobile={p.isMobile} isReq={false}
        initialStatus="EOI"
        initial={{status:"EOI", source:"", name:"", phone:"", phone2:"", budget:"", project:"", notes:"", eoiDeposit:""}}
        onClose={function(){setShowAdd(false);}}
        onSave={function(added){p.setLeads(function(prev){var nid=String(added&&added._id||"");if(!nid)return[added].concat(prev);if(prev.some(function(l){return gid(l)===nid;}))return prev.map(function(l){return gid(l)===nid?added:l;});return [added].concat(prev);});setShowAdd(false);}}/>
    </Modal>}

    {editLead&&<Modal show={true} onClose={function(){setEditLead(null);}} title={t.edit}>
      <LeadForm t={t} cu={p.cu} users={p.users} token={p.token} isMobile={p.isMobile} isReq={false}
        editId={gid(editLead)} initial={editLead} txnRow={editLead}
        onClose={function(){setEditLead(null);}}
        onSave={function(updated){
          // A record patch returns the ROW, not a lead — never push it into the
          // lead list, and patch the panel rather than replacing it.
          // EVERY merge onto a row goes through hydrateTxnRow — the patched
          // row over the list/panel row here, the returned LEAD over the panel
          // row below (leadWins). This was the third door: the lead response
          // used to be laid over the selected row with only the record markers
          // kept, so a live record row edited on a DoneDeal client took the
          // lead's globalStatus "donedeal" from its own save (#50058).
          if(updated&&updated._recordPatched){
            var kEF=rowKey(updated);
            setEoiAllData(function(prev){return Array.isArray(prev)?prev.map(function(l){return rowKey(l)===kEF?hydrateTxnRow(l,updated):l;}):prev;});
            setSelectedEOI(function(prev){ return prev&&rowKey(prev)===kEF?hydrateTxnRow(prev,updated):prev; });
            setEditLead(null); return;
          }
          p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(updated)?updated:l;});});setEditLead(null);
          if(selectedEOI&&gid(selectedEOI)===gid(updated))setSelectedEOI(function(prev){return prev?hydrateTxnRow(updated,prev,{leadWins:true}):prev;});
        }}/>
    </Modal>}

    {/* Split EOI Modal — owner ruling 2026-09-18, same shape as DealsPage's
        Split Deal modal: Agent 1 read-only (the EOI's own author, via
        eoiAgentPairOf — the EOI twin of dealAgentPairOf/R-T1), Agent 2 picked
        from salesUsers excluding Agent 1, fixed 50/50 (no percentage field
        exists on either kind of record). Writes PUT /api/leads/:id with
        txnId so mirrorEditToRecord carries it onto the EOI record itself
        (TXN_EDITABLE_EOI/TXN_LEAD_MAP_EOI); the server rejects Agent 2 ===
        Agent 1 (splitAgentSameError) regardless of what this picker already
        filters out. */}
    {eoiSplitModal&&<Modal show={true} onClose={function(){setEoiSplitModal(null);setEoiSplitAgent2("");}} title={"🤝 Split EOI — "+eoiSplitModal.name}>
      <div style={{ fontSize:12, color:C.textLight, marginBottom:12 }}>EOI will be split 50/50 between two agents</div>
      <div style={{ marginBottom:10 }}>
        <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>Agent 1</div>
        <div style={{ padding:"8px 12px", borderRadius:8, background:"#F8FAFC", fontSize:13 }}>{userNameOf(eoiAgentPairOf(eoiSplitModal).primary, p.users)||"—"}</div>
      </div>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>Agent 2</div>
        <SearchableSelect value={eoiSplitAgent2} onChange={setEoiSplitAgent2}
          options={withCurrentOption(salesUsers.filter(function(u){var uid=gid(u);var a1=eoiAgentPairOf(eoiSplitModal).primary;return uid!==a1;}).map(function(u){return { value:gid(u), label:u.name+" — "+u.title };}), eoiSplitAgent2, userNameOf(eoiSplitAgent2, p.users) || "Unknown")}
          emptyLabel="— Select Agent —" placeholder="Search agent…" width="100%" maxWidth={9999} fontSize={14}/>
      </div>
      {getEoiSplitFromObj(eoiSplitModal)&&<div style={{ padding:"8px 12px", background:"#FEF3C7", borderRadius:8, fontSize:12, marginBottom:10 }}>
        Current split: {getEoiSplitFromObj(eoiSplitModal).agent2Name} — <button onClick={async function(){
          try{
            var kSC=rowKey(eoiSplitModal);
            // PUT /api/leads/:id always returns the LEAD (never _recordPatched —
            // that shape is LeadForm/PATCH-records-only). mirrorEditToRecord has
            // already carried the clear onto the EOI record itself server-side;
            // patch this page's OWN row source with the same fields so the list
            // and panel agree with it before the next refetch.
            var upd=await apiFetch("/api/leads/"+gid(eoiSplitModal),"PUT",{splitAgent2Id:null,splitAgent2Name:"",txnId:eoiSplitModal._fromRecord?kSC:undefined},p.token);
            if(p.setLeads) p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(eoiSplitModal)?upd:l;});});
            patchEoiRow(eoiSplitModal, { splitAgent2Id:null, splitAgent2Name:"", eoiSplitAgent2Id:null });
            setSelectedEOI(function(prev){ return prev&&rowKey(prev)===kSC?Object.assign({},prev,{splitAgent2Id:null,splitAgent2Name:"",eoiSplitAgent2Id:null}):prev; });
          }catch(e){alert(e.message);}
          setEoiSplitModal(null);
        }} style={{ background:"none", border:"none", color:C.danger, cursor:"pointer", fontSize:12 }}>Remove Split</button>
      </div>}
      <div style={{ display:"flex", gap:10 }}>
        <Btn outline onClick={function(){setEoiSplitModal(null);setEoiSplitAgent2("");}} style={{ flex:1 }}>Cancel</Btn>
        <Btn onClick={async function(){
          if(!eoiSplitAgent2) return;
          var ag2=salesUsers.find(function(u){return gid(u)===eoiSplitAgent2;});
          try{
            var kSS=rowKey(eoiSplitModal);
            var upd=await apiFetch("/api/leads/"+gid(eoiSplitModal),"PUT",{splitAgent2Id:eoiSplitAgent2,splitAgent2Name:ag2?ag2.name:"?",txnId:eoiSplitModal._fromRecord?kSS:undefined},p.token);
            if(p.setLeads) p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(eoiSplitModal)?upd:l;});});
            patchEoiRow(eoiSplitModal, { splitAgent2Id:eoiSplitAgent2, splitAgent2Name:ag2?ag2.name:"?", eoiSplitAgent2Id:eoiSplitModal._fromRecord?eoiSplitAgent2:null });
            setSelectedEOI(function(prev){ return prev&&rowKey(prev)===kSS?Object.assign({},prev,{splitAgent2Id:eoiSplitAgent2,splitAgent2Name:ag2?ag2.name:"?",eoiSplitAgent2Id:eoiSplitModal._fromRecord?eoiSplitAgent2:null}):prev; });
          }catch(e){alert(e.message);}
          setEoiSplitModal(null); setEoiSplitAgent2("");
        }} style={{ flex:1 }}>✅ Save</Btn>
      </div>
    </Modal>}

    {/* PATH 1 — Convert-to-Deal modal is a FULL sale form (all fields required,
        prefilled from the EOI + editable) posted to /eoi-to-deal (which creates
        the commission). The Modal wrapper scrolls internally (maxHeight 90vh), so
        the tall form stays usable on ~380px phones. */}
    {convertModal&&<Modal show={true} onClose={function(){ if(!convertingDeal){ setConvertModal(null); setConvertDocs([]); } }} title="🎉 Convert to Done Deal">
      {(function(){
        var cStyle={ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" };
        var cLbl={ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 };
        var convAgentOpts=(p.users||[]).filter(function(u){ return u.active!==false && (positionOf(u)==="sales"||u.username==="amgad"); }).map(function(u){ return { value:gid(u), label:u.name+" - "+(u.title||u.role||"") }; });
        var sectionHdr=function(txt){ return <div style={{ fontSize:12, fontWeight:800, color:C.textLight, textTransform:"uppercase", letterSpacing:".4px", margin:"14px 0 8px" }}>{txt}</div>; };
        var cField=function(label, key, o){ o=o||{};
          var onCh=function(e){ var v=e.target.value; if(o.money){ var raw=v.replace(/,/g,"").replace(/[^0-9]/g,""); v=raw?Number(raw).toLocaleString():""; } else if(o.digits){ v=v.replace(/[^0-9]/g,""); } setConvF(key,v); };
          return <div style={{ marginBottom:12 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>{label}{o.req!==false&&<span style={{color:C.danger}}> *</span>}</label>
            {o.type==="select"
              /* NOTE: the source options may carry `disabled`; SearchableSelect has
                 no per-option disabled state, so such a row becomes selectable.
                 Values/labels are otherwise passed through untouched. */
              ? <Select value={convertForm[key]} onChange={onCh} fontSize={13} options={o.options.map(function(op){return { value:op.value, label:op.label };})}/>
              : <input type={o.type||"text"} value={convertForm[key]} placeholder={o.placeholder||""} max={o.max} onChange={onCh} style={cStyle}/>}
          </div>;
        };
        var num=function(v){ var s=String(v||"").replace(/,/g,"").replace(/[^0-9.]/g,""); var n=parseFloat(s); return isFinite(n)?n:0; };
        var priceNum=num(convertForm.budget), dpNum=num(convertForm.downPayment);
        var pct=(priceNum>0&&dpNum>0)?(Math.round((dpNum/priceNum)*10000)/100):null;
        var g2={ display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)", gap:"0 10px" };
        var g4={ display:"grid", gridTemplateColumns:"repeat(2, minmax(0,1fr))", gap:"0 12px", alignItems:"end" };
        // Deal Date ≡ Contraction Date — the deal's close date can't be in the
        // future (backbone dealDate feeds quarter attribution), so cap the
        // Deal / Contraction Date picker at today (Cairo).
        var todayIso=cairoTodayIso();
        // Resale conversion is blocked when the EOI has a deposit (a deposit is a
        // developer down payment). Only Primary is selectable in that case.
        var hasDeposit = num(convertModal.eoiDeposit) > 0;
        var isResaleConv = !hasDeposit && convertForm.saleType === "Resale";
        return <div>
          <div style={{ fontSize:13, color:C.text, marginBottom:6 }}>Complete the sale form to convert <b>{convertModal.name||"this EOI"}</b> to a Done Deal. It will move to the Deals page.</div>

          {!isResaleConv && <>
            {sectionHdr("👤 Client")}
            <div style={{ fontSize:11, color:C.textLight, marginBottom:8, lineHeight:1.5 }}>Prefilled from the EOI. Edit if the final buyer differs (spouse / child / relative) — the original EOI contact is kept and shown on the deal card.</div>
            {cField("Client Name","name")}
            {cField("Client Phone","phone")}
            {/* The second number is identity too. Without it the convert form
                wrote only `phone`, so a buyer who differs from the EOI contact
                got the NEW primary beside the OLD secondary — one row reading
                as one person and describing two.
                OPTIONAL, and labelled from t.phone2 — the SAME key the table
                header and every LeadForm use, so one word changes all of them.
                It was rendering a red required marker it never enforced:
                convertToDeal has no phone2 check, so the asterisk was promising
                a validation that does not exist, and most leads have no second
                number at all (519 of the 4,795 page-visible leads carry one). */}
            {cField(t.phone2,"phone2",{req:false})}
          </>}

          {sectionHdr("🏠 Property")}
          {cField("🏢 " + t.project,"project")}
          {/* The Project box is deliberately empty — see the ruling recorded at
              openConvert. Without this line a blank required field on an
              otherwise-prefilled form reads as something that failed to load. */}
          <div style={{ marginTop:-8, marginBottom:12, fontSize:11, color:C.textLight, lineHeight:1.4 }}>{t.projectPurchasedHint}</div>
          {/* R-T5 (owner ruling 2026-09-10) — DEVELOPER. A NEW field, not a
              restored one: this modal never had one, so a conversion inherited
              whatever the card happened to carry and an agent converting a lead
              with no developer produced a deal with none — silently, because the
              🏗️ pending bell only fires on a non-empty pending NAME, and an
              empty developer is not a pending one.
              Prefilled from the EOI and required, matching the EOI/Deal status
              door. Same two-mode control and the SAME fold as every other
              developer search (devSearchMatch): find it before you add it, so
              "Salam properties" against a stored "Salam Properties" resolves
              here instead of minting a duplicate. */}
          <div style={{ marginBottom:12 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>🏗️ Developer <span style={{color:C.danger}}>*</span></label>
            {!convertDevPendOpen && <SearchableSelect
              value={convertForm.developerId}
              emptyLabel="— Select developer —"
              placeholder="Search developers…"
              width="100%" maxWidth={9999} fontSize={13}
              options={(eoiDevelopers||[]).map(function(dv){ return { value:String(dv._id), label:dv.name }; })}
              matchFn={devSearchMatch} showCount countNoun="developers"
              onChange={function(val){ setConvF("developerId",val); if(val) setConvF("developerPending",""); }}/>}
            {convertDevPendOpen && <input type="text" value={convertForm.developerPending} placeholder="New developer name"
              onChange={function(e){ setConvF("developerPending",e.target.value); setConvF("developerId",""); }}
              style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>}
            <button type="button" onClick={function(){ if(convertDevPendOpen){ setConvertDevPendOpen(false); setConvF("developerPending",""); } else { setConvertDevPendOpen(true); setConvF("developerId",""); } }}
              style={{ marginTop:6, background:"none", border:"none", padding:0, color:C.info, fontSize:12, cursor:"pointer", textDecoration:"underline" }}>
              {convertDevPendOpen ? "← Pick from the list instead" : "Can't find the developer? Enter a new name"}
            </button>
          </div>
          <div style={g2}>
            {cField("🏷️ Unit Type","unitType",{type:"select",options:UNIT_TYPES.map(function(x){return {value:x,label:x||"- Select -"};})})}
            {cField("🔖 Unit Code","unitCode",{placeholder:"e.g. B1-204"})}
          </div>
          {isResaleConv
            ? cField("💰 Unit Price (EGP)","budget",{money:true,placeholder:"e.g. 1,500,000"})
            : <>
                <div style={g2}>
                  {cField("💰 Unit Price (EGP)","budget",{money:true,placeholder:"e.g. 1,500,000"})}
                  {cField("💵 Down Payment (EGP)","downPayment",{money:true,placeholder:"e.g. 500,000"})}
                </div>
                <div style={{ marginTop:-4, marginBottom:12, fontSize:12, fontWeight:600, color:pct!=null?C.success:C.textLight }}>{pct!=null?("= "+pct+"% of unit price"):"= —% (enter Unit Price + Down Payment)"}</div>
              </>}

          {sectionHdr("💰 Sales")}
          {cField("🏷️ Sale Type","saleType",{type:"select",options: hasDeposit ? [{value:"Primary",label:"Primary"},{value:"Resale",label:"Resale — unavailable (EOI has a deposit)",disabled:true}] : [{value:"Primary",label:"Primary"},{value:"Resale",label:"Resale"}]})}
          {isResaleConv
            ? cField("📅 Deal Date","dealDate",{type:"date",max:todayIso})
            : <div style={g4}>
                {cField("📅 Reservation Date","reservationDate",{type:"date"})}
                {cField("📅 Down Payment Date","downPaymentDate",{type:"date"})}
                {cField("📅 Contract Date","contractionDate",{type:"date",max:todayIso})}
                {cField("📆 Installments (years)","installmentYears",{digits:true,placeholder:"e.g. 8"})}
              </div>}

          {isResaleConv && <>
            {sectionHdr("🔁 Resale Parties")}
            <div style={{ fontSize:11, color:C.textLight, marginBottom:8 }}>Set who's with us; each with-us party gets its own agent + commission (tax-free, collected in full). At least one must be with us.</div>
            {[["seller","🔴 Seller","#B91C1C"],["buyer","🔵 Buyer","#1D4ED8"]].map(function(pr){
              var side=pr[0], party=(convertForm.resaleParties&&convertForm.resaleParties[side])||{};
              var withUs=!!party.withUs; var bC=num(convertForm.budget);
              var reqStar=withUs?<span style={{color:C.danger}}> *</span>:null;
              return <div key={side} style={{ border:"1px solid "+pr[2]+"33", borderRadius:12, padding:"12px 14px", marginBottom:12, background:pr[2]+"08" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:13, fontWeight:800, color:pr[2] }}>{pr[1]}</span>
                  <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:600, cursor:"pointer" }}><input type="checkbox" checked={withUs} onChange={function(e){setConvRP(side,"withUs",e.target.checked);}}/> With us?</label>
                </div>
                <div style={g2}>
                  <div style={{ marginBottom:12 }}><label style={cLbl}>Name{reqStar}</label><input value={party.name||""} onChange={function(e){setConvRP(side,"name",e.target.value);}} style={cStyle}/></div>
                  <div style={{ marginBottom:12 }}><label style={cLbl}>Phone{reqStar}</label><input value={party.phone||""} onChange={function(e){setConvRP(side,"phone",e.target.value);}} style={cStyle}/></div>
                </div>
                {withUs && <>
                  <div style={{ marginBottom:12 }}><label style={cLbl}>👤 Agent <span style={{color:C.danger}}>*</span></label><SearchableSelect value={party.agentId||""} onChange={function(v){setConvRP(side,"agentId",v);}} options={withCurrentOption(convAgentOpts, party.agentId, userNameOf(party.agentId, p.users) || "Unknown")} emptyLabel="- Select -" placeholder="Search agent…" width="100%" maxWidth={9999} fontSize={13}/></div>
                  <div style={g2}>
                    <div style={{ marginBottom:12 }}><label style={cLbl}>💰 Commission (EGP) <span style={{color:C.danger}}>*</span></label><input value={party.commissionAmount?Number(String(party.commissionAmount).replace(/,/g,"")).toLocaleString():""} onChange={function(e){var raw=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");setConvRP(side,"commissionAmount",raw?Number(raw).toLocaleString():"");var a=num(raw);setConvRP(side,"commissionRate",(a>0&&bC>0)?String(Math.round(a/bC*10000)/100):"");}} placeholder="e.g. 60,000" style={cStyle}/></div>
                    <div style={{ marginBottom:12 }}><label style={cLbl}>Commission %</label><input value={party.commissionRate||""} onChange={function(e){var raw=e.target.value.replace(/[^0-9.]/g,"");setConvRP(side,"commissionRate",raw);var r=num(raw);setConvRP(side,"commissionAmount",(r>0&&bC>0)?Number(Math.round(bC*r)/100).toLocaleString():"");}} placeholder="e.g. 2" style={cStyle}/></div>
                  </div>
                </>}
              </div>;
            })}
          </>}

          <DocumentsUpload files={convertDocs} onChange={setConvertDocs} label="📎 Upload Deal Documents"/>

          <div style={{ display:"flex", gap:10, marginTop:8 }}>
            <button onClick={function(){ setConvertModal(null); setConvertDocs([]); }} disabled={convertingDeal}
              style={{ flex:1, padding:"10px 0", borderRadius:10, border:"1px solid "+C.border, background:"#fff", color:C.text, fontSize:13, fontWeight:700, cursor:convertingDeal?"not-allowed":"pointer" }}>Cancel</button>
            <button onClick={function(){ convertToDeal(convertModal, convertForm); }} disabled={convertingDeal}
              style={{ flex:1, padding:"10px 0", borderRadius:10, border:"none", background:convertingDeal?"#9CA3AF":MINT_BG, color:convertingDeal?"#fff":MINT_FG, fontSize:13, fontWeight:700, cursor:convertingDeal?"not-allowed":"pointer" }}>{convertingDeal?"Converting…":"Convert to Deal"}</button>
          </div>
        </div>;
      })()}
    </Modal>}

    {eoisLoaded&&eoiLeads.length===0&&!eoiLoadErr&&<div style={{ textAlign:"center", padding:"60px 20px", color:C.textLight }}>
      <div style={{ fontSize:48, marginBottom:12 }}>🎯</div>
      <div style={{ fontSize:16, fontWeight:700 }}>No EOI clients yet</div>
      <div style={{ fontSize:13, marginTop:8 }}>Clients with EOI status will appear here automatically</div>
    </div>}

    <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
    {/* The card shell is the Deals page's, byte for byte (owner 2026-10-29): on
        the phone the Card is transparent and borderless and the list has no
        inner padding, so the cards share the Deals page's insets and never run
        past the viewport. */}
    {eoiLeads.length>0&&<Card p={0} style={p.isMobile?{ flex:1, overflow:"hidden", border:"none", boxShadow:"none", background:"transparent", borderRadius:0 }:{ flex:1, overflow:"hidden" }}>
    {p.isMobile?<div style={{ display:"flex", flexDirection:"column", gap:12, padding:0 }}>
      {eoiLeads.map(function(d){
        var bv=parseBudget(d.budget);
        var isSel=selectedEOI&&rowKey(selectedEOI)===rowKey(d);
        var isLanding=eoiFlashId===rowKey(d);
        return <div key={rowKey(d)} id={"eoi-row-"+rowKey(d)} data-eoi-row="1" onClick={function(){setSelectedEOI(isSel?null:d);}} style={{ background:isSel?"#EFF6FF":"#fff", border:"2px solid "+(isLanding?C.accent:(isSel?"#3B82F6":"#E8ECF1")), boxShadow:isLanding?"0 0 0 4px "+C.accent+"33":"none", borderRadius:14, padding:14, cursor:"pointer", transition:"box-shadow .3s, border-color .3s" }}>
          {/* Status pill (owner brief 2026-10-27): the desktop table's compact
              "✅ Approved" pill, top-right, centred on the NAME line — the
              header row no longer stretches it into a tall pale square. */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6, gap:8 }}>
            <div style={{ fontSize:15, fontWeight:700, minWidth:0 }}>{d.name}{canSeeLeadIds(p.cu) && <div style={{ fontSize:10, fontWeight:400, color:C.textLight, marginTop:1 }}>{txnRowLabel(d)}</div>}</div>
            {isEoiCancelledRow(d)
              ?<span style={{ background:"#FEE2E2", color:"#B91C1C", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, flexShrink:0, whiteSpace:"nowrap", marginTop:-1 }}>❌ Cancelled</span>
              :d.eoiApproved
              ?<span style={{ background:"#DCFCE7", color:"#15803D", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, flexShrink:0, whiteSpace:"nowrap", marginTop:-1 }}>✅ Approved</span>
              :<span style={{ background:"#FEF9C3", color:"#B45309", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, flexShrink:0, whiteSpace:"nowrap", marginTop:-1 }}>⏳ Pending</span>}
          </div>
          {/* Phone + alt phone on ONE line (owner 2026-10-29): "122**40**** · —" */}
          <div style={{ fontSize:12, color:C.textLight, marginBottom:6, display:"flex", alignItems:"center", gap:6, whiteSpace:"nowrap" }}><PhoneCell phone={d.phone}/><span>·</span>{d.phone2&&d.phone2!==d.phone?<PhoneCell phone={d.phone2}/>:<span>—</span>}</div>
          {/* Chips (owner brief 2026-10-27): equal-height single-line pills in one
              wrapping row, centred — never stretched. The agent and the split
              partner (owner ruling 2026-09-18: always the STORED Agent 2 name,
              visible to sales/team_leader as before) have their own line below.
              2026-10-29: shapes, spacing and typography are the Deals card's. */}
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:6 }}>
            {/* No eoiNotes fallback here: notes live in the expanded panel only. */}
            {d.project&&<span title={d.project+(d.unitType?" · "+d.unitType:"")} style={{ display:"inline-flex", alignItems:"center", height:24, padding:"0 8px", borderRadius:6, fontSize:11, whiteSpace:"nowrap", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", color:"#6D28D9", background:"#EDE9FE" }}>🏠 {d.project}{d.unitType?" · "+d.unitType:""}</span>}
            {bv>0&&<span style={{ display:"inline-flex", alignItems:"center", height:24, padding:"0 8px", borderRadius:6, fontSize:11, whiteSpace:"nowrap", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", color:C.success, background:"#F0FDF4", fontSize:13, fontWeight:700 }}>💰 {bv.toLocaleString()}</span>}
            {d.eoiDeposit&&<span style={{ display:"inline-flex", alignItems:"center", height:24, padding:"0 8px", borderRadius:6, fontSize:11, whiteSpace:"nowrap", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", color:C.textLight, background:"#F8FAFC" }}>Deposit: {d.eoiDeposit}</span>}
          </div>
          {(isAdmin||(isSalesRole&&getEoiSplitFromObj(d)))&&<div style={{ display:"flex", justifyContent:"space-between", gap:8, marginBottom:4, fontSize:12 }}>
            <span style={{ color:C.accent, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{isAdmin&&<span>👤 {getAg(d)}</span>}{getEoiSplitFromObj(d)&&<span>{isAdmin?" · ":""}🤝 {getEoiSplitFromObj(d).agent2Name}</span>}</span>
            {/* The lead's Source at the row's end, exactly as the Deals card (owner 2026-10-29): same admin gate, name coloured through SOURCE_COLOR. */}
            {isAdmin&&<span style={{ color:C.textLight, fontSize:11, flexShrink:0 }}>📢 <span style={sourceNameStyle(d.source)}>{d.source||"-"}</span></span>}
          </div>}
          {/* Convert to Deal — per-row action, admin/operation AND sales (the
              author-scoped /api/eois list means a sales user only ever sees their
              own EOIs here; /eoi-to-deal re-checks the slice server-side). Shown
              only on an Approved, non-cancelled row — isConvertibleEoi mirrors the
              handler's own guards so no role gets a guaranteed-400 click.
              The button MUST call both stopPropagation and preventDefault so
              the outer card's onClick (which opens the details panel) doesn't
              fire on the same click. */}
          {/* Footer = the Deals card's footer row (owner 2026-10-29): same top
              rule, spacing and 34px button box; the one action stays the mint
              Convert button, full width of the row. */}
          {canConvertEoi(d)&&<div style={{ display:"flex", gap:8, marginTop:10, paddingTop:10, borderTop:"1px solid #E8ECF1" }}>
          {canConvertEoi(d)&&<button onClick={function(e){e.stopPropagation();e.preventDefault();openConvert(d);}} onMouseDown={function(e){e.stopPropagation();}} onTouchStart={function(e){e.stopPropagation();}} disabled={convertingDeal} style={{ flex:1, height:34, borderRadius:8, border:"none", background:MINT_BG, color:MINT_FG, fontSize:12, fontWeight:700, cursor:convertingDeal?"wait":"pointer", opacity:convertingDeal?0.6:1, display:"flex", alignItems:"center", justifyContent:"center" }}>
            {convertingDeal?"Converting…":"✅ Convert to Deal"}
          </button>}
          </div>}
        </div>;
      })}
    </div>:<div style={{ overflowX:"auto" }}><table data-table-align="sheet" style={{ width:"100%", borderCollapse:"collapse", minWidth:700 }}>
      <thead><tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
        {eoiHeaderCols.map(function(h,i){return <th key={i} style={Object.assign({ textAlign:"left", padding:"11px 12px", fontSize:11, fontWeight:600, color:C.textLight, whiteSpace:"nowrap" }, (h===t.phone||h===t.phone2)?PHONE_COL_PIN:(h==="Approved"?APPROVED_COL_PIN:null))}>{h}</th>;})}
      </tr></thead>
      <tbody>
        {eoiLeads.map(function(d){
          var bv=parseBudget(d.budget);
          var eoiDateStr=d.eoiDate?new Date(d.eoiDate).toLocaleDateString("en-GB"):d.updatedAt?new Date(d.updatedAt).toLocaleDateString("en-GB"):"-";
          var isSel=selectedEOI&&rowKey(selectedEOI)===rowKey(d);
          var isLanding=eoiFlashId===rowKey(d);
          // Inset box-shadow, not a ring border: a border on a <tr> fights the
          // table's own borders and shifts layout. Same choice as the Deals row.
          return <Fragment key={rowKey(d)}><tr id={"eoi-row-"+rowKey(d)} data-eoi-row="1" onClick={function(){setSelectedEOI(isSel?null:d);}} style={{ borderBottom:"1px solid #F1F5F9", cursor:"pointer", background:isLanding?C.accent+"1A":(isSel?"#F0FDF4":"transparent"), boxShadow:isLanding?"inset 0 0 0 2px "+C.accent:"none", transition:"background .3s, box-shadow .3s" }}>
            <td style={{ padding:"11px 12px", fontSize:13, fontWeight:600, textAlign:"left", whiteSpace:"nowrap" }}>{d.name}{canSeeLeadIds(p.cu) && <div style={{ fontSize:10, fontWeight:400, color:C.textLight }}>{txnRowLabel(d)}</div>}</td>
            {/* 2026-10-29 (owner task): Phone + Alt. Phone are ONE pinned cell —
                phone line 1, alt phone muted line 2 ("—" when empty).
                The phone2!==phone guard STAYS: 827 leads store a phone2 equal to
                the primary, and those must keep showing a single number. It is a
                duplicate test, not a permission test. */}
            <td style={Object.assign({ padding:"11px 12px", fontSize:12, direction:"ltr", textAlign:"left" }, PHONE_COL_PIN)}>
              <StackedCell top={<PhoneCell phone={d.phone}/>} bottom={d.phone2&&d.phone2!==d.phone?<PhoneCell phone={d.phone2}/>:null}/>
            </td>
            {/* Project line 1, unit type muted line 2 (the old Unit Type cell's
                own expression, unitType || eoiNotes). */}
            <td style={{ padding:"11px 12px", fontSize:12, color:C.textLight, textAlign:"left" }}>
              {/* Bottom line = unit type ONLY (owner ruling 2026-11-02): the eoiNotes
                  fallback put a free-text note where a unit type belongs. */}
              <StackedCell top={d.project||"-"} bottom={d.unitType||null} bottomStyle={STACKED_ELLIPSIS_STYLE} bottomTitle={d.unitType||""}/>
            </td>
            <td style={{ padding:"11px 12px", fontSize:13, fontWeight:700, color:C.success, textAlign:"left" }}>
              {bv>0?bv.toLocaleString():d.budget||"-"}
              {isSalesRole&&getEoiSplitFromObj(d)&&<div style={{ fontSize:10, color:"#8B5CF6", fontWeight:600, marginTop:1 }}>🤝 +{getEoiSplitFromObj(d).agent2Name}</div>}
            </td>
            <td style={{ padding:"11px 12px", fontSize:12, color:C.textLight, textAlign:"left" }}>{d.eoiDeposit||"-"}</td>
            {/* Split partner name for admin — same owner ruling as the card
                view above: name only, no percentage, own line, SAME visual
                style as the sales-facing 🤝 badge in the budget cell above
                (fontSize 10, #8B5CF6, fontWeight 600, marginTop 1) — not
                appended inline onto the agent name. */}
            {isAdmin&&<td style={{ padding:"11px 12px", fontSize:12, textAlign:"left", whiteSpace:"nowrap" }}>
              {getAg(d)}
              {getEoiSplitFromObj(d)&&<div style={{ fontSize:10, color:"#8B5CF6", fontWeight:600, marginTop:1 }}>🤝 +{getEoiSplitFromObj(d).agent2Name}</div>}
            </td>}
            <td style={{ padding:"11px 12px", fontSize:11, color:C.textLight, textAlign:"left" }}>{eoiDateStr}</td>
            <td style={Object.assign({ padding:"11px 12px", textAlign:"left" }, APPROVED_COL_PIN)}>
              {/* Approved mark = the Deals table's bare "✅" pill, byte for byte
                  (owner brief 2026-10-27); pending and cancelled keep their words.
                  2026-10-29: the column is pinned (APPROVED_COL_PIN) and the pills
                  are nowrap, so "❌ Cancelled" never wraps on a narrow window. */}
              {isEoiCancelledRow(d)
                ?<span style={{ background:"#FEE2E2", color:"#B91C1C", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>❌ Cancelled</span>
                :d.eoiApproved
                ?<span style={{ background:"#DCFCE7", color:"#15803D", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>✅</span>
                :<span style={{ background:"#FEF9C3", color:"#B45309", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>⏳ Pending</span>}
            </td>
            <td style={{ padding:"8px 12px" }} onClick={function(e){e.stopPropagation();}}>
              <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                {/* Convert to Deal — per-row action, admin/operation AND sales
                    (see the mobile-card note above for why that's safe). Shown
                    only on an Approved, non-cancelled row.
                    stopPropagation on the button itself is belt-and-suspenders
                    alongside the td's stopPropagation — keeps the row's
                    detail-panel onClick from firing on the same click. */}
                {canConvertEoi(d)&&<button onClick={function(e){e.stopPropagation();e.preventDefault();openConvert(d);}} onMouseDown={function(e){e.stopPropagation();}} disabled={convertingDeal} title="Convert to Deal" style={{ padding:"6px 10px", borderRadius:6, border:"none", background:MINT_BG, color:MINT_FG, fontSize:11, fontWeight:700, cursor:convertingDeal?"wait":"pointer", opacity:convertingDeal?0.6:1, whiteSpace:"nowrap" }}>
                  {convertingDeal?"…":"Convert to Deal"}
                </button>}
                {/* ✏️ and 🗄️ act on the LEAD, not on this row. On a converted EOI
                    the lead is a live closed deal, so Edit would offer to write
                    status:"EOI" back onto it and Archive would take the deal off
                    every deal-facing surface — neither is an EOI operation at
                    all. Both routes refuse a converted record server-side now
                    (409 txn_historical / eoi_converted); this stops offering the
                    click. Edit the deal from the Deals page instead. */}
                {/* 🤝 Split EOI — owner ruling 2026-09-18, same gate as Edit
                    (isOnlyAdmin + eoiAllows(d,"edit")): a cancelled/converted
                    row can't be split any more than it can be edited — the
                    write goes through the same mirrorEditToRecord door, which
                    already refuses a cancelled record. */}
                {isOnlyAdmin&&eoiAllows(d,"edit")&&<button onClick={function(e){e.stopPropagation();setEoiSplitModal(d);var sp=getEoiSplitFromObj(d);setEoiSplitAgent2(sp?sp.agent2Id:"");}} title="Split EOI"
                  style={{ width:28, height:28, borderRadius:6, border:"1px solid "+(getEoiSplitFromObj(d)?"#8B5CF6":"#E2E8F0"), background:getEoiSplitFromObj(d)?"#F5F3FF":"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>🤝</button>}
                {isOnlyAdmin&&eoiAllows(d,"edit")&&<button onClick={function(){setEditLead(d);}} style={{ width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><Edit size={13} color={C.info}/></button>}
                {isOnlyAdmin&&eoiAllows(d,"archive")&&<button onClick={function(){archiveLead(gid(d));}} style={{ width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><Archive size={13} color={C.warning}/></button>}
              </div>
            </td>
          </tr>
          {isSel && !p.isMobile && hasRenderableEOI && <tr><td colSpan={eoiColCount} style={{ padding:"8px 12px", background:C.bg }}>{renderEoiPanel({ width:"100%", background:"#fff", borderRadius:0 })}</td></tr>}
          </Fragment>;
        })}
      </tbody>
    </table></div>}
    </Card>}

    {/* EOI Side Panel */}
    {/* Mobile: full-screen overlay panel (unchanged). Desktop renders the
        panel inline as a full-width accordion row under the selected EOI row
        — see renderEoiPanel() call inside the table body above. Exactly one
        of the two mounts per render (p.isMobile gate), so eoiPanelRef binds a
        single node and click-outside close keeps working. */}
    {hasRenderableEOI&&p.isMobile&&renderEoiPanel({ position:"fixed", inset:0, zIndex:300, background:"#fff", overflowY:"auto" })}
    </div>
  </div>;
};

// ===== DEALS =====

// ===== COMMISSION SYSTEM =====
// Project weight settings stored in localStorage: crm_proj_weight_{projectName} = 0.5 or 1
var getEffectiveQTarget = function(user, allUsers, forQ) {
  var uid = String(typeof user === "string" ? user : gid(user));
  var userObj = typeof user === "object" ? user : (allUsers||[]).find(function(u){return String(gid(u))===uid;}) || {};
  var curQ = forQ || (function(){var m=new Date().getMonth();return m<3?"Q1":m<6?"Q2":m<9?"Q3":"Q4";})();
  // Year-aware: forQ stays "Q1".."Q4". Year defaults to current calendar year
  // (matches the backend readQTargetServer convention).
  var qNum = parseInt(String(curQ).replace("Q","")) || 1;
  var curYearNum = new Date().getFullYear();

  if((positionOf(userObj) === "manager"||positionOf(userObj) === "team_leader") && allUsers) {
    // Find team members by reportsTo (primary) or teamId (fallback)
    var teamMembers = allUsers.filter(function(u){
      if(positionOf(u) !== "sales") return false;
      // Check reportsTo
      var rt = u.reportsTo && u.reportsTo._id ? String(u.reportsTo._id) :
               u.reportsTo ? String(u.reportsTo) : "";
      if(rt === uid) return true;
      // Check teamId match (if both have same non-empty teamId)
      if(userObj.teamId && u.teamId && userObj.teamId === u.teamId) return true;
      return false;
    });

    if(teamMembers.length > 0) {
      var total = teamMembers.reduce(function(sum, u){
        var qt = (u.qTargets&&Object.keys(u.qTargets).length>0) ? u.qTargets :
          (function(){try{return JSON.parse(localStorage.getItem("crm_qt_"+gid(u))||"{}");}catch(e){return {};}})();
        return sum + readQTargetClient(qt, curYearNum, qNum);
      }, 0);
      if(total > 0) return total;
    }
  }

  // Own qTargets
  var qt = (userObj.qTargets&&Object.keys(userObj.qTargets).length>0) ? userObj.qTargets :
    (function(){try{return JSON.parse(localStorage.getItem("crm_qt_"+uid)||"{}");}catch(e){return {};}})();
  return readQTargetClient(qt, curYearNum, qNum);
};

// ===== qTargets year-aware helpers (Phase R-0) =====
// User.qTargets keys are now "<YYYY>-Q<n>" (e.g. "2026-Q2"). Old data may
// still hold bare "Q1".."Q4" keys; the legacy fallback honors them only for
// the CURRENT calendar year, matching the backend readQTargetServer rule.
var qKeyForClient = function(date) {
  var d = (date instanceof Date) ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  return d.getFullYear() + "-Q" + (Math.floor(d.getMonth() / 3) + 1);
};
var readQTargetClient = function(qtOrUser, year, qNum) {
  // Accepts either a User object (uses .qTargets) or the qTargets object directly.
  var qt = (qtOrUser && qtOrUser.qTargets) ? qtOrUser.qTargets : qtOrUser;
  if (!qt || typeof qt !== "object") return 0;
  var newKey = year + "-Q" + qNum;
  if (qt[newKey] !== undefined) return Number(qt[newKey]) || 0;
  var currentYear = new Date().getFullYear();
  if (year === currentYear) {
    var legacyKey = "Q" + qNum;
    if (qt[legacyKey] !== undefined) return Number(qt[legacyKey]) || 0;
  }
  return 0;
};

// ===== Mandatory quarterly targets (owner decision, 2026-08-14) =====
// The 40M floor is enforced HARD at user creation and SOFT on later edits: the
// Owner may have a legitimate exception for an existing agent, but should have
// to confirm it rather than set it by accident.
var MIN_QUARTERLY_TARGET = 40000000;
// POSITIONS, not roles — hasTargets is banded. Name kept: every consumer already
// compares it against a position (see targetPositionOfDraft).
var TARGET_REQUIRED_ROLES = positionsWhereFE(function(d){ return d.hasTargets; });

// commitQTargets — shared save path for both Quarterly Targets modals (Users
// page and Team page). Two guards:
//   1. below-minimum values ask for confirmation before saving;
//   2. a rewrite of an already-ended quarter is REFUSED by the server (409) and
//      retried only once the Owner explicitly confirms restating history.
// Returns true when the targets were saved.
var commitQTargets = async function(opts) {
  var merged = opts.merged || {};
  var low = Object.keys(merged).filter(function(k){
    var v = Number(merged[k]) || 0;
    return v > 0 && v < MIN_QUARTERLY_TARGET;
  }).sort();
  if (low.length) {
    var warn = "Below the " + MIN_QUARTERLY_TARGET.toLocaleString("en-US") + " EGP minimum:\n\n" +
      low.map(function(k){ return "   " + k + " — " + Number(merged[k]).toLocaleString("en-US") + " EGP"; }).join("\n") +
      "\n\nSave anyway?";
    if (!window.confirm(warn)) return false;
  }
  try {
    await apiFetch("/api/users/" + opts.uid, "PUT", { qTargets: merged }, opts.token, opts.csrfToken);
    return true;
  } catch (e) {
    var msg = (e && e.message) || "";
    // Matches the server's past_quarter_target_edit 409 (see server.js
    // diffPastQuarterEdits). Anything else is a real failure — surface it
    // rather than letting the modal close as if the save had worked.
    if (msg.indexOf("already-ended quarter") === -1) {
      window.alert("Could not save targets:\n\n" + (msg || "Unknown error"));
      return false;
    }
    if (!window.confirm(msg + "\n\nOverride and rewrite them anyway?")) return false;
    try {
      await apiFetch("/api/users/" + opts.uid, "PUT",
        { qTargets: merged, allowPastTargetEdit: true }, opts.token, opts.csrfToken);
      return true;
    } catch (e2) {
      window.alert("Could not save targets:\n\n" + ((e2 && e2.message) || "Unknown error"));
      return false;
    }
  }
};

// Phase 2 Slice 3 — projectWeights are now sourced from the AppSetting doc
// (server-side, single source of truth). Module cache is hydrated on App
// mount via fetchProjectWeights() and refreshed after saveProjectWeight().
// localStorage stays as a fallback for the brief window between mount and
// first fetch resolve — same values post-migration, so no behavioral drift.
var _projWeightsMap = null;
var getProjectWeight = function(project, lead){
  // 1) Lead-level cache (set by server cascade in Slice 1's PUT handler)
  if (lead && lead.projectWeight && lead.projectWeight !== 1) return lead.projectWeight;
  // 2) App-level module cache (hydrated from the AppSetting doc)
  if (_projWeightsMap && project && (project in _projWeightsMap)) return _projWeightsMap[project];
  // 3) Legacy localStorage fallback — only relevant pre-fetch-resolve
  try{ var w=localStorage.getItem("crm_proj_weight_"+(project||"").replace(/\s/g,"_")); return w?parseFloat(w):1; }catch(e){return 1;}
};
// Server PUT is full-replace, so merge the change into the current cache
// before sending. Server cascades projectWeight onto every matching Lead
// doc — the caller is responsible for mirroring that into local p.leads
// and for bumping the App-level rev counter so renders pick it up.
var saveProjectWeight = async function(project, weight, token){
  if (!project) return _projWeightsMap || {};
  var newMap = Object.assign({}, _projWeightsMap || {});
  if (weight === 1) delete newMap[project]; // 1 is the default — server drops it from storage
  else newMap[project] = weight;
  var res = await apiFetch("/api/settings/project-weights", "PUT", newMap, token);
  _projWeightsMap = (res && typeof res === "object") ? res : newMap;
  return _projWeightsMap;
};
// Hydrate the module cache from the AppSetting doc. Called on App mount
// (and again on token change). On error leaves the cache as-is so the
// localStorage fallback in getProjectWeight keeps working.
var fetchProjectWeights = async function(token){
  try {
    var d = await apiFetch("/api/settings/project-weights", "GET", null, token);
    _projWeightsMap = (d && typeof d === "object") ? d : {};
  } catch (e) { /* leave cache as-is */ }
  return _projWeightsMap;
};
// Deal split stored in localStorage: crm_deal_split_{leadId} = {agent2Id, agent2Name}
var getDealSplit = function(lid, leads){
  // Try from leads array first (server data)
  if(leads){
    var l=leads.find(function(x){return String(x._id||x)===String(lid);});
    if(l&&l.splitAgent2Id) return {agent2Id:String(l.splitAgent2Id._id||l.splitAgent2Id),agent2Name:l.splitAgent2Name||"Shared"};
  }
  try{return JSON.parse(localStorage.getItem("crm_deal_split_"+lid)||"null");}catch(e){return null;}
};
// THE DEAL'S OWN AGENTS — OWNER RULING R-T1 (2026-09-10), frontend half.
//
// A deal belongs to the pair recorded ON the deal (dealAgentId /
// dealSplitAgent2Id), never to the lead's live holder. This mirrors
// dealAgentIdsOf() in server.js and must keep mirroring it: the server decides
// which rows arrive and this decides what they are worth, and if the two read
// different fields the list and its own total disagree.
//
// The legacy fallback swaps in the LEAD pair as a pair, and only when the deal
// carries no anchor at all (8 of 52 live deals) — a deal that has an anchor and
// no split anchor is a deal with no split, not one whose partner lives
// elsewhere.
var dealAgentPairOf = function(d){
  var idOf=function(v){ return v?String(v._id||v):""; };
  // EACH SLOT FALLS BACK ON ITS OWN — the twin of dealAgentIdsOf in server.js,
  // and it must stay the twin. See that function for why the all-or-nothing
  // form dropped #50059's and #50043's partners.
  var primary=idOf(d&&d.dealAgentId)||idOf(d&&d.agentId);
  var secAnchor=idOf(d&&d.dealSplitAgent2Id);
  var second=secAnchor||idOf(d&&d.splitAgent2Id);
  // THE NAME MAY NEVER BE BORROWED FROM A DIFFERENT ID, and which field owns
  // the name depends on which slot supplied the id:
  //   * from the ANCHOR — the populated {name} if the list endpoint attached
  //     one, else the card's splitAgent2Name ONLY when it describes this very
  //     id (a record row carries the anchor raw, and the record's own frozen
  //     name is pinned un-projectable by two gates).
  //   * from the CARD — splitAgent2Name is that field's own partner, so it is
  //     the right name by construction.
  // Anything else renders "Shared", which is honest, rather than the name of
  // whoever happens to hold the card today.
  var nm="";
  if(secAnchor){
    nm=(d&&d.dealSplitAgent2Id&&d.dealSplitAgent2Id.name)||"";
    if(!nm&&secAnchor===idOf(d&&d.splitAgent2Id)) nm=(d&&d.splitAgent2Name)||"";
  } else if(second){
    nm=(d&&d.splitAgent2Id&&d.splitAgent2Id.name)||(d&&d.splitAgent2Name)||"";
  }
  return { primary:primary, second:second, name:nm };
};
// Helper to get split directly from deal object — the DEAL's split (R-T1).
// Falls back to the legacy localStorage record for pre-server-split deals.
var getDealSplitFromObj = function(d){
  var pair=dealAgentPairOf(d);
  if(pair.second) return {agent2Id:pair.second,agent2Name:pair.name||"Shared"};
  try{return JSON.parse(localStorage.getItem("crm_deal_split_"+(d._id||d))||"null");}catch(e){return null;}
};
// THE EOI'S OWN AGENTS (owner ruling 2026-09-18) — the EOI twin of
// dealAgentPairOf/R-T1. An EOI belongs to the pair recorded ON the EOI
// record (eoiAgentId / eoiSplitAgent2Id), never to the lead's live holder —
// same reasoning, same each-slot-falls-back-on-its-own shape. No localStorage
// fallback: unlike Deal's split, this field has always been server-shipped,
// so there is no pre-server-split population to fall back for.
var eoiAgentPairOf = function(e){
  var idOf=function(v){ return v?String(v._id||v):""; };
  var primary=idOf(e&&e.eoiAgentId)||idOf(e&&e.agentId);
  var secAnchor=idOf(e&&e.eoiSplitAgent2Id);
  var second=secAnchor||idOf(e&&e.splitAgent2Id);
  var nm="";
  if(secAnchor){
    nm=(e&&e.eoiSplitAgent2Id&&e.eoiSplitAgent2Id.name)||"";
    if(!nm&&secAnchor===idOf(e&&e.splitAgent2Id)) nm=(e&&e.splitAgent2Name)||"";
  } else if(second){
    nm=(e&&e.splitAgent2Id&&e.splitAgent2Id.name)||(e&&e.splitAgent2Name)||"";
  }
  return { primary:primary, second:second, name:nm };
};
// Helper to get split directly from an EOI row — the EOI's split (mirrors
// getDealSplitFromObj).
var getEoiSplitFromObj = function(e){
  var pair=eoiAgentPairOf(e);
  return pair.second ? {agent2Id:pair.second,agent2Name:pair.name||"Shared"} : null;
};
// Scope-based split-deal credit multiplier. Returns 1 when the deal isn't a
// split, OR when both agents are in visibleUserIds (i.e. both sit in the
// caller's reportsTo subtree per the server-scoped p.users); 0.5 otherwise.
// Used by DealsPage Total + filteredTotal and the KPIsPage purple card to
// answer "what total revenue is visible to this caller". Per-agent cards
// (MemberCard, KPIsPage Team Members grid) keep the legacy share-based rule
// (always 0.5 if split) inline so each card shows that agent's slice and the
// sum across cards stays equal to the deal's full value.
var splitMultiplier = function(d, visibleUserIds){
  if (!d || !getDealSplitFromObj(d)) return 1;
  // R-T1 — the DEAL's pair. Reading the lead's pair here is what halved deal
  // #50059 on the closer's own page: she is on the deal, the lead's two agents
  // are other people, so neither side was "visible" and the row paid 50%.
  var pair = dealAgentPairOf(d);
  return (visibleUserIds && visibleUserIds.has(pair.primary) && visibleUserIds.has(pair.second)) ? 1 : 0.5;
};
// THE NAME ON THE 🤝 CHIP — OWNER RULING R-T2 (2026-09-10).
//
// One expression, at MODULE SCOPE so verify-deal-attribution.js can RUN it
// rather than regex the render body it used to live in (twice, in two
// indentations). It takes the resolved split object and returns the STORED
// partner's name — it does not take the viewer, and that is the whole point:
// there is no argument here that could reintroduce "whoever is not me".
//
// Returns "" for a deal with no split, so the caller's `split && otherName`
// guard keeps its existing shape.
var splitPartnerName = function(split){ return split ? (split.agent2Name||"") : ""; };
var saveDealSplit = function(lid,split){ try{localStorage.setItem("crm_deal_split_"+lid,JSON.stringify(split));}catch(e){}};
// getDealExtra survives as a READ-ONLY fallback for deals created before the
// sale fields were persisted server-side. The writer (saveDealExtra) was removed
// once downPayment/downPaymentPct/installmentYears/dealDate began persisting on
// the Lead — see LeadForm.submit / renderDealPanel.
var getDealExtra = function(lid){ try{return JSON.parse(localStorage.getItem("crm_deal_extra_"+lid)||"null");}catch(e){return null;}};
// Get the effective date for a deal — checks custom dealDate first
var getDealDate = function(d){
  try{
    // Read from lead object first (server data)
    if(d&&d.dealDate) return new Date(d.dealDate);
    // Fallback to localStorage
    var extra=getDealExtra(String(d._id||""));
    if(extra&&extra.dealDate) return new Date(extra.dealDate);
  }catch(e){}
  if(d.eoiDate) return new Date(d.eoiDate);
  if(d.updatedAt) return new Date(d.updatedAt);
  return new Date(d.createdAt||0);
};

// fmtDayOnly — render a value that is DATE-ONLY BY CONSTRUCTION ("YYYY-MM-DD")
// as DD/MM/YYYY, pinned to UTC.
//
// `new Date("2026-08-24")` is midnight UTC by spec (the date-only form is parsed
// as UTC, unlike the datetime form). Formatting that in the viewer's own zone
// therefore shows the PREVIOUS calendar day for anyone west of UTC — New York
// renders "23/08/2026" for a deal stored as 2026-08-24. Cairo is UTC+2/+3, east
// of the line, which is the only reason this has never been visible here. It is
// a latent bug, not an absent one, and the fix is to stop letting the viewer's
// zone participate in a value that has no time in it.
//
// ONLY for date-only values. A genuine datetime (createdAt, callbackTime,
// lastSeen, receivedDate) must keep rendering in the viewer's own zone — pinning
// those to UTC would move a 01:00 Cairo event onto the previous day. The shared
// per-page `fmtDate` helpers stay local for exactly that reason; they serve both
// kinds and must not be hardened wholesale.
var fmtDayOnly = function(v){
  if(!v) return "";
  try{ return new Date(v).toLocaleDateString("en-GB",{timeZone:"UTC"}); }catch(e){ return String(v); }
};


var DealsPage = function(p) {
  useLeadSourcesVersion();   // re-render when the lead source registry lands (src/lib/leadSources.js)
  var t=p.t; var isAdmin=p.cu.role==="admin"||p.cu.role==="operation"||positionOf(p.cu)==="director"||positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader"; var isOnlyAdmin=p.cu.role==="admin"||p.cu.role==="operation";
  var [dealTabState,setDealTab]=useRememberedTab("deals", gid(p.cu), DEALS_TABS, null, "active"); // "active" | "cancelled" — remembered per page per user
  var dealsStrip = resolveTabStrip(p.access, "deals", DEALS_TABS, dealTabState);
  var dealTab = dealsStrip.active;
  var [dealCancelling,setDealCancelling]=useState(false);
  var [dealDocUploading,setDealDocUploading]=useState(false);
  // In-app document viewer (2026-10-01): the thumbnail tap sets this, DocViewer renders it.
  var [dealDocViewerSrc,setDealDocViewerSrc]=useState("");
  // STEP 4-4 — paginated Deals fetch from /api/deals?status=all. Replaces the
  // in-memory p.leads.filter scan that would silently miss deals once STEP 4-5
  // shrinks the bootstrap. Falls back to the in-memory filter while the fetch
  // is in flight so first paint shows the legacy data unchanged. Same overlay
  // pattern as EOIPage — p.leads mutations show through before the WS refetch.
  var [dealAllData, setDealAllData] = useState(null);
  var dealsPhase2 = useBootPhase2();       // the two dropdown lists below are phase 2
  var [dealLoadErr, setDealLoadErr] = useState(null);
  var [dealReloadKey, setDealReloadKey] = useState(0);
  // P2 (2026-11-02): LIVE BY LEAD, NOT BY BOOK — the twin of the EOI page's
  // hook. entity_changed { family:"deal", ids:[leadId…] } → re-read those
  // leads' deal rows and swap them in; null ids → the book once. Deferred
  // while a modal holds a row (editDeal / stages / split).
  var dealLiveRefetch = useCallback(function(leadIds){
    apiFetch(txnLiveUrl("deal", leadIds), "GET", null, p.token).then(function(r){
      var fresh = (r && Array.isArray(r.data)) ? r.data : null;
      if (!fresh) return;
      setDealAllData(function(prev){ return prev ? mergeTxnRowsByLead(prev, fresh, leadIds, null, gid) : prev; });
    }).catch(function(){});
  }, [p.token]);
  useLiveFamily("deal", {
    onChange: function(f, ids){ if (ids) dealLiveRefetch(ids); else setDealReloadKey(function(k){ return k + 1; }); },
    isEditing: function(){ return !!(editDeal || stagesModal || splitModal); }
  });
  // Closing Companies (Feature B) — list for the side-panel dropdown + name
  // resolution on cards/table. Admin/SA only; non-fatal if it fails to load.
  var [dealClosingCompanies,setDealClosingCompanies] = useState([]);
  // Developers (D4) — list for the side-panel dropdown + name resolution after a
  // PUT (which returns a raw developerId, not populated). Admin/SA only (edit
  // gate); display elsewhere uses the populated developerId.name from the API.
  var [dealDevelopers,setDealDevelopers] = useState([]);
  useEffect(function(){
    if (!p.token) return;
    var cancelled = false;
    setDealLoadErr(null);
    // NO CEILING — was ?limit=1000. See fetchAllPages.
    fetchAllPages("/api/deals?status=all", p.token)
      .then(function(rows){
        if (cancelled) return;
        // THE PHASE-2 RELEASE. dealsLoaded flips on the next render, so this is
        // the page's own loaded flag and not a proxy for it. Reported on the
        // error path too (below): "answered" is the gate, not "answered well" —
        // a page stuck on its error banner must not hold the rest of the boot.
        if (p.onBootRows) p.onBootRows();
        var r = { data: rows };
        if (r && Array.isArray(r.data)) { setDealAllData(r.data); }
        // 200 but wrong shape (e.g. backend/FE version skew) must NOT pass
        // silently — it would leave dealAllData null and fall back to the
        // shrunk p.leads bootstrap (the "Active (3)" undercount incident).
        else { setDealLoadErr("Unexpected response from /api/deals (missing data[] array)."); }
      })
      .catch(function(err){
        if (cancelled) return;
        if (p.onBootRows) p.onBootRows();
        // No silent swallow: surface the failure instead of rendering a
        // partial count from the first-100-leads bootstrap.
        setDealLoadErr((err && err.message) ? err.message : "Failed to load deals.");
      });
    return function(){ cancelled = true; };
  // NOT p.cbBust and no stale key — other users' changes arrive per lead
  // through useLiveFamily("deal") above; own mutations through patchDealRow /
  // patchDealRowsByLead.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, dealReloadKey]);
  // Closing Companies — fetch once for the dropdown + name map (admin/SA only).
  //
  // PHASE 2. It fills a DROPDOWN and a fallback name lookup: every row that has
  // a closing company already carries it populated from /api/deals (ccNameOf
  // reads d.closingCompanyId.name first and only falls back to this list for a
  // raw ObjectId), so the table does not wait on it.
  var liveCcRev = useLiveRev("closingCompany");
  useEffect(function(){
    if (!p.token || !dealsPhase2 || !canSeeClosingCompany(p.cu)) return;
    var cancelled = false;
    apiFetch("/api/closing-companies","GET",null,p.token)
      .then(function(data){
        if (cancelled) return;
        var rows = (data && Array.isArray(data.data)) ? data.data : (Array.isArray(data) ? data : []);
        setDealClosingCompanies(rows);
      })
      .catch(function(){ /* non-fatal: cards fall back to populated name or — */ });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, dealsPhase2, liveCcRev]);
  // Developers (D4/D5) — fetch once for the side-panel dropdown (admin/SA) AND
  // the developer filter (all roles). GET /api/developers is open to any auth,
  // so load it for everyone; the dropdown itself stays admin/SA-gated below.
  // PHASE 2, for the same reason as Closing Companies above — devNameOf reads
  // the populated d.developerId.name first.
  var liveDevRev = useLiveRev("developer");
  useEffect(function(){
    if (!p.token || !dealsPhase2) return;
    var cancelled = false;
    apiFetch("/api/developers","GET",null,p.token)
      .then(function(data){
        if (cancelled) return;
        var rows = (data && Array.isArray(data.data)) ? data.data : (Array.isArray(data) ? data : []);
        setDealDevelopers(rows);
      })
      .catch(function(){ /* non-fatal: cards fall back to populated name or — */ });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, dealsPhase2, liveDevRev]);
  // The p.leads overlay (pLeadsMapD) is gone — see the long note on EOIPage's
  // eoiScope for the full reasoning. Same substitution, same two failure modes,
  // same replacement: patchDealRow / patchDealRowsByLead above.
  //
  // Server-fetched ONLY. The previous `: p.leads` fallback silently undercounted
  // once STEP 4-5 shrank the bootstrap to the 100 newest-created leads — older
  // deals dropped out and the page showed e.g. "Active (3)" instead of 29. When
  // the fetch hasn't resolved or has errored, render nothing and let the
  // loading / error banner below own the UI.
  var dealsLoaded = Array.isArray(dealAllData);
  var dealsSource = dealsLoaded
    ? dealAllData.filter(function(l){return l && !l.archived;})
    : [];
  // Include every record that the rest of the CRM already treats as a deal:
  // newly-converted EOIs have status="DoneDeal", but older records may carry
  // only globalStatus="donedeal" (or vice-versa) depending on which path
  // stamped them. Mirroring the admin dashboard's rule here ensures existing
  // EOI records with Done Deal status show up alongside new ones.
  // ORDER (owner ruling 2026-09-12): dealDate desc, then the record's createdAt —
  // txnRowOrder, the same comparator the EOI page uses. Not getDealDate: its
  // fallback chain ends in updatedAt, and a restore or approval must not reorder.
  var activeDeals=dealsSource.filter(function(l){return (l.status==="DoneDeal"||l.globalStatus==="donedeal")&&!l.archived&&!(l.dealStatus==="Deal Cancelled"||l.status==="Deal Cancelled");}).slice().sort(txnRowOrder("deal"));
  var cancelledDeals=dealsSource.filter(function(l){return (l.dealStatus==="Deal Cancelled" || l.status==="Deal Cancelled") && !l.archived && !(l.eoiStatus==="EOI Cancelled");}).slice().sort(txnRowOrder("deal"));
  var deals = dealsStrip.empty ? [] : (dealTab==="cancelled" ? cancelledDeals : activeDeals);
  // Single source of truth for "this deal row is cancelled" — mirrors the EOI
  // page's isEoiCancelledRow. The Approved-column badge reads this FIRST so a
  // cancelled deal shows a red ❌ Cancelled instead of leaking its pre-cancel
  // approval state through as the amber ⏳ pending badge.
  var isDealCancelledRow = function(l){ return l.dealStatus==="Deal Cancelled" || l.status==="Deal Cancelled"; };
  // Live-agent resolver (used directly for active rows, and as the fallback).
  var liveAg=function(l){ return userNameOf(l && l.agentId, p.users) || "-"; };
  // Anchored deal-author resolver — reads the populated dealAgentId (the agent
  // who CLOSED the deal, stamped at close and never moved by rotation). Server
  // sends it as a populated {name}; fall back to a users[] lookup.
  var dealAuthorAg=function(l){ return userNameOf(l && l.dealAgentId, p.users) || null; };
  // ALL tabs show WHO CLOSED the deal (per-action attribution): a legacy/former-
  // agent deal shows its typed name (no user credited); otherwise prefer the
  // anchored author, falling back to agentAtTime → live for rows with no anchor.
  // EXTERNAL DEALS HAVE NO INTERNAL AGENT, AND THAT IS LEGAL.
  //
  // POST /api/leads exempts dealType:"external" from primary_sales_recipient_required
  // — a broker-only external deal is closed by the broker, so agentId, dealAgentId
  // and externalSalesAgentEnabled are all null/false by design, and the Commission
  // schema defaults salesAgent to null for exactly this shape. Both external deals
  // in production (#1012 Hashem Hassan / Mina Samy, #1039 Sara Mohamed / Sherif)
  // are that shape, with empty assignments[] and empty agentHistory.
  //
  // So the four internal resolvers below CORRECTLY find nobody, and the column
  // rendered "-" over a deal that has a perfectly well-known counterparty. This
  // adds the two external answers, in the order the money is attributed: the
  // external sales agent when one is configured (externalSalesAgentName is stored
  // denormalized at write time), otherwise the broker.
  //
  // DEFENSIVE ON THE BROKER SHAPE. externalBrokerId only became a populated
  // {_id,name} in the commit before this one; a cached response or a rollback
  // hands back a bare ObjectId, and `b.name` on a string is undefined, not a
  // throw. Reading it through a guard means this degrades to today's "-" instead
  // of rendering "[object Object]" or an id.
  var getAg=function(l){
    if(l&&l.legacyAgentName) return l.legacyAgentName;
    var live=liveAg(l); var internal=dealAuthorAg(l)||agentAtTime(l,live);
    if(internal && internal!=="-") return internal;
    if(l&&l.dealType==="external"){
      if(l.externalSalesAgentName) return l.externalSalesAgentName;
      var b=l.externalBrokerId; var bn=(b&&b.name)?b.name:"";
      if(bn) return "\ud83e\udd1d "+bn+" (broker)";
    }
    return internal;
  };
  // Subtle "(legacy)" tag next to a former-agent name.
  var legacyTag=function(l){ return (l&&l.legacyAgentName) ? <span style={{ fontSize:10, fontStyle:"italic", color:C.textLight, marginLeft:4 }}>(legacy)</span> : null; };
  var parseBudget=function(b){return parseFloat((b||"0").toString().replace(/,/g,""))||0;};
  // Closing Company (Feature B) — closingCompanyId may arrive populated ({_id,name})
  // from the list/deals endpoints or as a raw id from a PUT response; resolve the
  // display name via the fetched companies map, falling back to the populated name.
  var ccIdOf=function(d){ var cc=d&&d.closingCompanyId; return cc?String(cc._id||cc):""; };
  var ccNameOf=function(d){ return closingCompanyNameOf(d && d.closingCompanyId, dealClosingCompanies); };
  // Developer (D4) — developerId may arrive populated ({_id,name}) from the
  // list/deals endpoints or as a raw id from a PUT response; resolve the display
  // name via the populated object first, then the fetched developers map.
  var devIdOf=function(d){ var dv=d&&d.developerId; return dv?String(dv._id||dv):""; };
  var devNameOf=function(d){ return developerNameOf(d && d.developerId, dealDevelopers); };
  // Developer (D5) — options for the filter dropdown (managed developers list).
  var dealDeveloperOpts=(dealDevelopers||[]).map(function(dv){ return { value:String(dv._id), label:dv.name }; });
  // Split-deal credit rule mirrors the backend my-stats reducer: for any
  // non-admin caller, a split deal counts as FULL (1.0) when both agents are
  // visible in p.users (server-scoped subtree), else 0.5. Sales p.users is
  // just self, so split deals degenerate to 0.5 — same as legacy. The shared
  // splitMultiplier helper near getDealSplitFromObj is used here and on the
  // KPIsPage purple card.
  var visibleUserIds = new Set((p.users||[]).map(function(u){return String(u._id||gid(u));}));
  // Admin / Operations: full top-line budget per deal — no project weight
  // and no split halving. This is the gross deal volume the company booked.
  // All other roles keep the share-based view (their slice of revenue).
  var total=deals.reduce(function(s,d){
    if(isOnlyAdmin) return s+parseBudget(d.budget);
    return s+parseBudget(d.budget)*getProjectWeight(d.project,d)*splitMultiplier(d, visibleUserIds);
  },0);
  var salesUsers=p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;});
  // Filter dropdown only — includes inactive agents so admins can filter historical deals.
  var salesUsersForFilter=(function(){var act=p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;});var ina=p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&!u.active;});return act.concat(ina);})();
  var agentFilterOpts=salesUsersForFilter.map(function(u){ return { value:gid(u), label:u.name+(u.active?"":" (inactive)") }; });
  var [showAdd,setShowAdd]=useState(false);
  var [editDeal,setEditDeal]=useState(null);
  var [selectedDeal,setSelectedDeal]=useState(null);
  // Defensive guard mirroring LeadsPage hasRenderableSelected.
  var hasRenderableDeal = !!(selectedDeal && gid(selectedDeal) && (selectedDeal.name || selectedDeal.phone));
  useEffect(function(){
    if (selectedDeal && !hasRenderableDeal) {
      // eslint-disable-next-line no-console
      console.warn("[DealsPage] Clearing degenerate `selectedDeal`:", selectedDeal);
      setSelectedDeal(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selectedDeal, hasRenderableDeal]);
  // Click-outside closes the Deal side panel (docked drawer).
  var dealPanelRef = useOutsideClose(hasRenderableDeal, function(){ setSelectedDeal(null); }, "[data-deal-row]");
  // Side-panel hydration tracker. The /api/leads bootstrap strips dealImages
  // to keep the payload small; we refetch the full doc when the panel opens.
  // panelHydratedDealId === selectedDeal._id once the refetch has populated
  // the panel, so mutation responses (already full docs) don't trigger a
  // redundant fetch. Same pattern as EOIPage.
  var [panelHydratedDealId,setPanelHydratedDealId]=useState(null);
  var [dealHydrateErr,setDealHydrateErr]=useState("");
  // Keyed on the ROW — see the matching note on the EOI panel.
  var isDealHydrated = !!(selectedDeal && panelHydratedDealId === rowKey(selectedDeal));
  // Deep-link: the header global search and the Deals & EOI notification bell
  // both arrive here through nav() -> initSelected. Goes through landOnDeal
  // (declared below, hoisted-safe: this effect body runs after render) so a
  // searched deal lands on its row exactly like the Commissions "View Deal"
  // button — it used to select the row and leave the viewport at the top, which
  // on a 52-row book means the user scrolls looking for a row already open.
  useEffect(function(){
    if (!p.initSelected) return;
    // A record row must not be re-resolved to its plain lead, or the wrong
    // transaction opens (see the EOI page's deep-link for the same rule).
    var target = p.initSelected._fromRecord
      ? p.initSelected
      : ((p.leads||[]).find(function(l){return gid(l)===gid(p.initSelected);}) || p.initSelected);
    // A NOTIFICATION HANDS OVER A LEAD, NOT A ROW — the twin of the EOI page's
    // branch, and the same law: resolve a shell against THIS page's own book so
    // the ⏳ bell, the 🏗️ bell and a push tap all open the row the table opens.
    // HOLD while dealAllData is still null (GET /api/deals medians 2,288ms on
    // production), and do not clear the cue while holding it.
    if (!target._fromRecord) {
      if (!dealsLoaded) return;
      var dealBook = activeDeals.concat(cancelledDeals);
      var dealRow = resolveNotifRow(dealBook, target, isDealCancelledRow);
      if (dealRow) target = dealRow;  // fail soft: no row in the book -> open the shell
    }
    landOnDeal(target);
    if (p.setInitSelected) p.setInitSelected(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.initSelected, dealsLoaded]);

  // Refetch full lead doc on panel open so dealImages hydrate from
  // /api/leads/:id even when the bootstrap stripped them. Gated by
  // panelHydratedDealId so mutation handlers (which already setSelectedDeal
  // to the full response) don't loop. Failures are silent — the panel stays
  // on the stripped row and the user can retry by reopening.
  //
  // MERGES rather than replaces, and reports failure instead of swallowing it —
  // see the long note on the EOI panel's effect for both reasons. A record-backed
  // row takes its contract images from ITS OWN record, which is what stops
  // #01024's June deal from showing the August EOI's files.
  useEffect(function(){
    if (!selectedDeal || !selectedDeal._id) return;
    var key = rowKey(selectedDeal);
    if (panelHydratedDealId === key) return;
    var sid = String(selectedDeal._id);
    var recId = selectedDeal._fromRecord ? String(selectedDeal._rowKey) : null;
    var cancelled = false;
    setDealHydrateErr("");
    // TWO INDEPENDENT ASKS (owner ruling 2026-09-10). Promise.all rejects on the
    // FIRST failure, so a refusal on the lead fetch was reported to the user as
    // "Couldn't load this deal's files" — naming the files, which were fine.
    // Deal #50059: the owner saw all 19; Jana, who closed it, saw that sentence.
    //
    // The files half now stands or falls on its own. Only a failure of the
    // ARTIFACTS call is a files error; a lead-fetch failure leaves the panel on
    // the row it already has, which is what it did before hydration anyway.
    Promise.all([
      apiFetch("/api/leads/"+sid,"GET",null,p.token).catch(function(){ return null; }),
      // PHASE 3 — ONE reader, for every row kind. A record-backed row asks by its
      // record; a plain lead row asks by its lead. Both answers come out of the
      // `artifacts` collection, so this panel no longer has two sources that can
      // disagree — which is exactly what let a converted EOI's files go missing.
      apiFetch("/api/artifacts?owner=" + (recId ? "deal:"+recId : "lead:"+sid), "GET", null, p.token)
    ]).then(function(res){
      if (cancelled) return;
      var full = res[0], art = res[1];
      // A missing lead body is not fatal: merge what we have. The artifacts are
      // the half this panel exists to show, and they arrived.
      if (!full || !full._id || String(full._id) !== sid) full = {};
      setSelectedDeal(function(prev){
        if (!prev || rowKey(prev) !== key) return prev;
        // ONE MERGE RULE, at module scope (hydrateTxnRow) — see the EOI panel.
        var merged = hydrateTxnRow(full, prev);
        merged.dealImages    = art ? (art.images || []) : [];
        merged.dealDocuments = art ? (art.documents || []) : [];
        // The legacy singular is dead here: its key, where one existed, migrated
        // into the deal/image bucket and now arrives inside dealImages.
        merged.dealImage     = "";
        return merged;
      });
      setPanelHydratedDealId(key);
    }).catch(function(err){
      if (cancelled) return;
      setDealHydrateErr((err && err.message) || "Could not load this deal's files.");
    });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selectedDeal && rowKey(selectedDeal)]);
  // Reset hydration tracker when the panel closes so the next open re-hydrates.
  useEffect(function(){ if (!selectedDeal) { setPanelHydratedDealId(null); setDealHydrateErr(""); } },[selectedDeal]);

  var [stagesModal,setStagesModal]=useState(null);
  var [splitModal,setSplitModal]=useState(null); // lead for split
  var [splitAgent2,setSplitAgent2]=useState("");
  var [projWeightModal,setProjWeightModal]=useState(false);
  var [projWeights,setProjWeights]=useState(function(){
    var w={};deals.forEach(function(d){if(d.project)w[d.project]=getProjectWeight(d.project);});return w;
  });
  // THE DASHBOARD DEALS TILE HANDS ITS PERIOD IN (p.dealsInit) so the click opens
  // the rows the tile counted, not the page's default quarter. Seeded from the
  // prop rather than applied only in the effect below: the nav happens before
  // this page mounts, so a mount-time default would render the quarter's list
  // for one frame before the range replaced it. Same one-shot contract as
  // meetingsInit — consumed and cleared, and the page owns its filters after.
  var [dateFrom,setDateFrom]=useState(function(){ return (p.dealsInit && p.dealsInit.from) || ""; });
  var [dateTo,setDateTo]=useState(function(){ return (p.dealsInit && p.dealsInit.to) || ""; });
  // ⏳ pill as a filter (owner 2026-10-29): focusing jumps the period to All (quarter + year, range cleared) and
  // narrows the Active tab to deals awaiting approval; clearing restores the period that was selected before.
  var [dealPendingOnly,setDealPendingOnly]=useState(false);
  var [pendingPrevPeriod,setPendingPrevPeriod]=useState(null);
  var [dealSourceFilter,setDealSourceFilter]=useState([]);   // Source dropdown (owner 2026-10-29): client-side like agent/developer/project; every count and the Total respect it
  // The tile counts APPROVED deals not dated in the future; the Active tab also
  // lists pending ones (7 against 8 for September 2026). The click says so, and
  // the page applies the tile's own row test (dealCountsForTile) until the user
  // clears it — visibly, with the banner below.
  var [dealTileScope,setDealTileScope]=useState(function(){ return !!(p.dealsInit && p.dealsInit.approvedOnly); });
  var [dealSearch,setDealSearch]=useState("");
  useEffect(function(){
    if (p.dealsInit && p.dealsInit.from) {
      setDateFrom(p.dealsInit.from);
      setDateTo(p.dealsInit.to || "");
      setDealTileScope(!!p.dealsInit.approvedOnly);
      if (p.setDealsInit) p.setDealsInit(null);   // consumed
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.dealsInit]);
  // MULTI-SELECT (2026-08-06): an ARRAY of user ids; [] = all agents.
  var [dealAgent,setDealAgent]=useState([]);
  // MULTI-SELECT (2026-08-05): both hold ARRAYS; [] = all. Client-side filters —
  // this page fetches its whole list once (/api/deals?status=all, all pages).
  var [dealDeveloperFilter,setDealDeveloperFilter]=useState([]); // Developer (D5) — [] = all
  var [dealProjectFilter,setDealProjectFilter]=useState([]); // Project (free-text) — [] = all
  // Project filter options — DISTINCT free-text project strings from the loaded
  // deals (case-insensitive de-dupe, alpha sort). CASCADE (P0): when a developer
  // is selected, the option list is scoped to that developer's deals only, so it
  // no longer offers projects that belong to other developers. `project` is a
  // plain string with no entity link yet, so the developer→project relationship
  // is derived here from the deals' own developerId (deals carry it ~always).
  // Declared after the filter states so it can read dealDeveloperFilter.
  // 2026-10-29 (owner task): same canonical builder as the EOI dropdown (one
  // entry per normProjectFE key, most frequent spelling, A→Z); the developer
  // cascade (union of the picked developers) is applied to the rows first.
  // Source options (owner 2026-10-29): the registry's sources plus "Daily Request", each label in the registry colour (sourceNameStyle).
  var dealSourceOpts=dealSourceOptions(leadSourceNames());
  var dealProjectOpts=canonicalProjectOptions(
    (deals||[]).filter(function(d){ return selHasId(dealDeveloperFilter, devIdOf(d)); }),
    function(d){ return d.project; }, normProjectFE);
  // Phase R-12 Part 4 — All / Internal / External filter pill state. Scoped
  // to the current Active|Cancelled tab (counts and filter both). Default
  // "all". Pre-Part-2 leads default to dealType "internal" so the External
  // bucket is empty until admins start tagging deals via Part 3 modal.
  var [dealTypeFilter,setDealTypeFilter]=useState("all");
  // TWO STATES, NOT ONE — and the split is the whole fix (2026-08-27).
  //
  // `dealLandingId` is the TARGET: the row this page has been asked to land on.
  // It is cleared by LANDING, never by a clock.
  // `dealFlashId` is the RING, and it is the only thing a timer is allowed to
  // clear — it starts when the row is actually reached.
  //
  // They used to be one state, and a 2s timer cleared it. That works when the
  // list is already on screen (presence is true on the next render, the scroll
  // happens in ~30ms) and CANNOT work on a fresh mount: measured against
  // production, GET /api/deals takes a median 2,288ms, so the target was
  // discarded 288ms before filteredDeals ever contained the row. Reproduced in
  // headless Chrome on the shipped bundle — same path, latency 900ms lands at
  // t+975ms, latency 2,288ms never calls scrollIntoView at all.
  var [dealLandingId,setDealLandingId]=useState(null);
  var [dealFlashId,setDealFlashId]=useState(null);
  // The target already scrolled to. A ref, not state: it must be readable and
  // writable inside the same tick the scroll happens in, and changing it must
  // not re-render.
  var dealLandedForRef=useRef("");
  var curYear=new Date().getFullYear(); var curQ=(function(){var m=new Date().getMonth();return m<3?"Q1":m<6?"Q2":m<9?"Q3":"Q4";})();
  // "all" FIRST — the year gate below is unconditional, so before this entry
  // existed there was no way to see the whole book from the pickers at all:
  // Q="all" still clamped to one year, and only a manual From/To range escaped.
  // dealYear is therefore Number | "all" from here on; every read guards on it.
  var dealYears=["all",curYear,curYear-1,curYear-2,curYear-3];
  var [dealQ,setDealQ]=useState(curQ); var [dealYear,setDealYear]=useState(curYear);
  // toYMD — local calendar date as "YYYY-MM-DD" from a Date. Uses LOCAL getters
  // to stay consistent with the Deal Date column, the sort, and the period/range
  // gates (all read getDealDate locally). Returns "" for an Invalid/empty date so
  // such a deal is NOT range-excluded (included rather than silently dropped).
  // Defined here (above dealsInPeriod) so both date-scoped filters can call it.
  var toYMD=function(dt){var x=(dt instanceof Date)?dt:new Date(dt);if(isNaN(x.getTime()))return"";var y=x.getFullYear(),mo=x.getMonth()+1,da=x.getDate();return y+"-"+(mo<10?"0"+mo:mo)+"-"+(da<10?"0"+da:da);};
  // ONE period scope for every number on the page (owner task 2026-10-29):
  // the Active / Cancelled chips, the ⏳ pending pill, the All / Internal /
  // External / Ambassador pills, the rows and the Total pill all read the SAME
  // predicate — src/lib/txnPeriodScope.js dealDateScopeOk — over the selected
  // Quarter + Year, or the 📅 range when one is set (it takes precedence).
  // "All" + "all years" + no range = today's numbers. Deliberately excludes the
  // deal-type / search / agent / developer filters so each count shows its own
  // bucket total within the active date scope (not a self-filtered subset).
  var inDealPeriod = function(d){ return dealDateScopeOk(d, { q: dealQ, year: dealYear, from: dateFrom, to: dateTo }, getDealDate, toYMD); };
  // Source filter (owner 2026-10-29): selMatchName folds case/whitespace like the project filter; an empty selection matches every row.
  var dealSourceOk = function(d){ return selMatchName(dealSourceFilter, d.source); };
  var inDealScope = function(d){ return inDealPeriod(d) && dealSourceOk(d); };   // period AND source — every count and the Total read this
  var dealsInPeriod = deals.filter(inDealScope);
  var activeDealsInPeriodN = activeDeals.filter(inDealScope).length;
  var cancelledDealsInPeriodN = cancelledDeals.filter(inDealScope).length;   // ≥1 → red dot on the Cancelled tab (the chips carry no counts)
  // The ⏳ pill is ALL-TIME (owner 2026-10-29, third pass): an approval queue is not a period figure — but it does respect the Source filter.
  var activeDealsInSource = activeDeals.filter(dealSourceOk);
  var approvedAllTimeN = activeDealsInSource.filter(function(d){ return d.dealApproved === true; }).length;
  var pendingAllTimeN = activeDealsInSource.length - approvedAllTimeN;
  var focusPending = function(){ setPendingPrevPeriod({ q: dealQ, year: dealYear, from: dateFrom, to: dateTo }); setDealQ("all"); setDealYear("all"); setDateFrom(""); setDateTo(""); setDealPendingOnly(true); };
  var clearPendingFocus = function(){ var prev = pendingPrevPeriod; setDealPendingOnly(false); setPendingPrevPeriod(null); if (prev) { setDealQ(prev.q); setDealYear(prev.year); setDateFrom(prev.from); setDateTo(prev.to); } };
  var dealsInternalCount = dealsInPeriod.filter(function(d){ return (d.dealType||"internal")==="internal"; }).length;
  var dealsExternalCount = dealsInPeriod.filter(function(d){ return d.dealType==="external"; }).length;
  var dealsAmbassadorCount = dealsInPeriod.filter(function(d){ return d.dealType==="ambassador"; }).length; // Phase R-14
  var filteredDeals=deals.filter(function(d){
    if(dealTileScope && !dealCountsForTile(d, Date.now())) return false;   // the dashboard tile's own row test
    // The page's ONE date gate (manual range precedence, inclusive calendar
    // days, Quarter + Year otherwise) — the same predicate every count reads.
    if(!inDealScope(d)) return false;
    if(dealPendingOnly && dealTab!=="cancelled" && d.dealApproved === true) return false;   // ⏳ pill focus: awaiting approval only
    if(dealSearch){var q2=dealSearch.toLowerCase();var nm=d.name?d.name.toLowerCase():"";var pr=d.project?d.project.toLowerCase():"";var ph=d.phone||"";if(!nm.includes(q2)&&!pr.includes(q2)&&!ph.includes(q2))return false;}
    // Agent — holder OR split partner, against the whole selection.
    // THE DEAL'S OWN PAIR (owner ruling 2026-09-12, R11) — dealAgentPairOf, not
    // the row's raw agentId/splitAgent2Id, which are the LEAD's current holder
    // and its unrelated legacy split field.
    if(dealAgent.length){var _dap=dealAgentPairOf(d);if(!selHasId(dealAgent,_dap.primary)&&!selHasId(dealAgent,_dap.second))return false;}
    if(dealTypeFilter==="internal" && (d.dealType||"internal")!=="internal") return false;
    if(dealTypeFilter==="external" && d.dealType!=="external") return false;
    if(dealTypeFilter==="ambassador" && d.dealType!=="ambassador") return false; // Phase R-14
    if(!selHasId(dealDeveloperFilter, devIdOf(d))) return false; // Developer (D5)
    // Project — selMatchName folds case/whitespace. This is a DELIBERATE change
    // from the previous exact `String(d.project)!==filter` compare: dealProjectOpts
    // dedupes its options case-insensitively (seen[pr.toLowerCase()]) but emitted
    // the FIRST raw spelling as the value, so with both "Sahel" and "sahel" in the
    // data one option existed and the other spelling's deals were unreachable.
    // Folding here makes the option match every spelling it already represents.
    if(!selMatchName(dealProjectFilter, d.project)) return false; // Project
    return true;
  });
  var filteredTotal=filteredDeals.reduce(function(s,d){
    if(isOnlyAdmin) return s+parseBudget(d.budget);
    return s+parseBudget(d.budget)*getProjectWeight(d.project,d)*splitMultiplier(d, visibleUserIds);
  },0);

  // ===== EMPTY STATE — SAY WHICH SCOPE IS HIDING THEM ========================
  //
  // The tab chip used to count `activeDeals`/`cancelledDeals` (unfiltered) while
  // the body counted `filteredDeals`, and the date scope defaults to the CURRENT
  // quarter + year. So a caller whose deals all sit outside that window read
  // "Active (6)" directly above a bare "No deals yet" and reasonably concluded
  // the data was gone. (Since 2026-10-29 the chips follow the period too, so
  // that chip now reads "(0)" — this message is what tells them where the 6 went.)
  //
  // Measured on production 2026-08-26: 52 active deal rows, 12 of them inside
  // the default Q3-2026 window; 7 of the 15 agents holding a deal had ZERO
  // there — Hussien Ahmed (6 deals) and Soliman Shosha (4) among them.
  //
  // Reads `deals`, not `activeDeals`, so the Cancelled tab counts ITS OWN rows;
  // on the Active tab the two are the same object (see the `deals` assignment).
  var dealsDateScopeIsManual = !!(dateFrom || dateTo);
  // Name the scope that is actually applied. When a manual range is set the
  // quarter/year gates are skipped entirely, so quoting them would be a lie.
  // The range text is the shared builder's (dateRangeTagText), never a local copy.
  var dealsPeriodLabel = dealsDateScopeIsManual
    ? dateRangeTagText(dateFrom, dateTo)
    : ((dealQ==="all" ? "" : dealQ + " ") + (dealYear==="all" ? "all years" : dealYear));
  // The button has to clear the manual range too, or on that branch it would
  // set two gates that are not being consulted and visibly do nothing.
  var showAllDealDates = function(){ setDealQ("all"); setDealYear("all"); setDateFrom(""); setDateTo(""); setDealTileScope(false); };
  // dealsInPeriod applies the date gates and NOTHING else, so it separates
  // "the period hid them" from "the search/agent/developer/type filters did".
  // Offering "Show all" for the second case would be a button that cannot help.
  var dealsHiddenByPeriod  = deals.length > 0 && dealsInPeriod.length === 0;
  var dealsHiddenByFilters = !dealsHiddenByPeriod && dealsInPeriod.length > 0 && filteredDeals.length === 0;
  var renderDealsEmpty = function(){
    if (dealsHiddenByPeriod) return <div style={{ padding:40, textAlign:"center", color:C.textLight }}>
      <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:8 }}>
        No deals in {dealsPeriodLabel} — {deals.length} {deals.length===1?"deal":"deals"} in other periods
      </div>
      <button onClick={showAllDealDates} style={{ padding:"6px 14px", borderRadius:8, border:"1px solid "+C.accent, background:"#fff", color:C.accent, fontSize:12, fontWeight:700, cursor:"pointer" }}>Show all</button>
    </div>;
    if (dealsHiddenByFilters) return <div style={{ padding:40, textAlign:"center", color:C.textLight }}>No deals match the current filters.</div>;
    return <div style={{ padding:40, textAlign:"center", color:C.textLight }}>No deals yet</div>;
  };

  // ===== DEEP LINK FROM COMMISSIONS: open THIS deal ==========================
  //
  // The twin of CommissionsPage's selectedCommissionLeadId consumer: read once,
  // act, clear. Three things have to be true before the row is actually on
  // screen, and the old p.nav path did none of them:
  //
  //   1. THE BOOK MUST BE LOADED. Resolution is against dealAllData, not
  //      p.leads — the whole reason this carries an id instead of an object.
  //      Gated on dealsLoaded so a cue that arrives mid-fetch is not dropped.
  //   2. THE PERIOD FILTER MUST BE CLEARED. The date scope defaults to the
  //      current quarter + year, which hides 40 of 52 rows. Selecting a deal
  //      the filter excludes sets state nobody can see: filteredDeals has no
  //      such row, so no <tr> renders and the expanded row has nothing to
  //      attach to. Same reset the "Show all" button performs.
  //   3. THE TAB MUST MATCH. A cancelled deal lives on the Cancelled tab.
  //
  // Prefers an ACTIVE row when a lead has both — a client who bought, cancelled
  // and bought again should land on the live purchase, not the dead one.
  // ONE LANDING, TWO DOORS. Every caller that arrives here meaning a specific
  // deal — the Commissions "View Deal" button (selectedDealLeadId) and the header
  // global search / notification bell (initSelected) — funnels through this. It
  // is deliberately not a second consumer: two copies of "clear the filters,
  // pick the tab, select, scroll" is how one door quietly stops doing the fourth
  // thing, which is exactly the state the search result was in before today.
  //
  //   (1) THE PERIOD FILTER MUST GO. It defaults to the current quarter + year
  //       and hides 40 of the 52 active deals on production. A row filteredDeals
  //       excludes cannot be selected, scrolled to or highlighted.
  //   (2) SO MUST EVERY OTHER NARROW — the exact set "Clear All" resets.
  //   (3) THE TAB MUST MATCH. A cancelled deal lives on the Cancelled tab.
  //   (4) THE SCROLL IS NOT DONE HERE. At this moment the resets have not
  //       rendered, so the row may not be in the DOM and the list is about to
  //       REORDER around it (clearing the period admits ~40 rows, moving the
  //       target's index). The lander below waits for presence instead.
  var landOnDeal = function(target){
    if (!target) return;
    showAllDealDates();                                                  // (1)
    setDealSearch(""); setDealAgent([]); setDealTypeFilter("all");       // (2)
    setDealDeveloperFilter([]); setDealProjectFilter([]); setDealSourceFilter([]);
    setDealTab(isDealCancelledRow(target) ? "cancelled" : "active");     // (3)
    setSelectedDeal(target);
    setDealLandingId(rowKey(target));                                    // (4)
    setDealFlashId(null);                         // the previous ring is stale
    dealLandedForRef.current = "";                // a new target — land again
  };

  useEffect(function(){
    if (!p.selectedDealLeadId) return;
    if (!dealsLoaded) return;                       // wait for the book, do not drop the cue
    var want = String(p.selectedDealLeadId);
    var byLead = function(l){ return String(l._id || "") === want; };
    var target = activeDeals.find(byLead) || cancelledDeals.find(byLead) || null;
    if (target) landOnDeal(target);
    if (p.setSelectedDealLeadId) { try { p.setSelectedDealLeadId(null); } catch(e){} }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.selectedDealLeadId, dealsLoaded]);

  // ===== THE LANDING — scroll only once the row is actually rendered =========
  //
  // The mirror of CommissionsPage's second effect, and the same law: never guess
  // when a render finished. `filteredDeals` IS what the table and the mobile
  // cards map over, so membership in it means the <tr> / card exists — and it is
  // recomputed AFTER the filter resets above land, which is what makes this
  // "scroll after the reorder" rather than before it.
  //
  // Keyed on a boolean, not on filteredDeals itself: that array is a fresh
  // identity every render, so depending on it would re-run this on every
  // keystroke elsewhere on the page. dealLandedForRef then makes the scroll
  // itself once-per-target.
  var dealLandingPresent = !!(dealLandingId && filteredDeals.some(function(d){
    return rowKey(d) === dealLandingId;
  }));
  useEffect(function(){
    if (!dealLandingId) return;
    if (dealLandedForRef.current === dealLandingId) return;
    if (!dealLandingPresent) {
      // NOT A MISS YET. The target is HELD while the book is still loading —
      // on a fresh mount dealAllData is null, filteredDeals is [], and the row
      // cannot be present no matter how correct everything else is. Clearing
      // here is what the old single-state version effectively did via its timer.
      if (!dealsLoaded || !filteredDeals.length) return;
      // Loaded, non-empty, and the row is still not in it: a genuine miss (an
      // EOI that has since converted, a deal archived in another tab). Drop the
      // target so it cannot fire a surprise scroll ten minutes later when a
      // filter change happens to admit it.
      dealLandedForRef.current = dealLandingId;
      setDealLandingId(null);
      return;
    }
    dealLandedForRef.current = dealLandingId;
    var go = function(){
      var el = document.getElementById("deal-row-" + dealLandingId);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior:"smooth", block:"center" });
    };
    // rAF, not a timer: the row is in the data, its node lands on the next paint.
    if (typeof window !== "undefined" && window.requestAnimationFrame) window.requestAnimationFrame(go);
    else setTimeout(go, 0);
    // LANDED — the target's job is done, and the ring's starts here rather than
    // when the cue arrived. This is what makes the flash 2s of VISIBLE highlight
    // instead of 2s of waiting for a list.
    setDealFlashId(dealLandingId);
    setDealLandingId(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[dealLandingId, dealLandingPresent, dealsLoaded, filteredDeals.length]);

  // THE FLASH'S LIFETIME — keyed on the RING, which only exists after landing.
  //
  // A timer here is safe and a timer on the TARGET never was: this one starts
  // when the row is on screen, so the two seconds are two seconds of highlight.
  // Keyed on dealFlashId alone so presence flapping inside the window cannot run
  // the cleanup and leave the ring lit forever.
  useEffect(function(){
    if (!dealFlashId) return;
    var h = setTimeout(function(){ setDealFlashId(null); }, 2000);
    return function(){ clearTimeout(h); };
  },[dealFlashId]);

  // Get stages from the lead document (server-side, shared across admins). Falls back to legacy localStorage if the lead doesn't have one yet.
  var getStages=function(lid){
    var lead = (p.leads||[]).find(function(l){return gid(l)===lid;});
    if (lead && lead.stages && typeof lead.stages==="object" && Object.keys(lead.stages).length>0) return lead.stages;
    try{return JSON.parse(localStorage.getItem("crm_stages_"+lid)||"{}");}catch(e){return {};}
  };
  // resolveStages — read the deal's OWN backend stages (present on both merge
  // branches: /api/deals carries stages via DEAL_LIST_FIELDS, and the bootstrap
  // lead carries it via the summary projection which keeps stages). Falls back
  // to per-browser localStorage ONLY when the deal has no backend stages. This
  // decouples the render from the oscillating p.leads cache (100-newest vs
  // all-leads) that made deals outside the newest-100 flip 3/3 -> 0/3.
  var resolveStages=function(d){
    if (d && d.stages && typeof d.stages==="object" && Object.keys(d.stages).length>0) return d.stages;
    try{return JSON.parse(localStorage.getItem("crm_stages_"+gid(d))||"{}")||{};}catch(e){return {};}
  };
  // Count completed stages straight from a stages object (no p.leads lookup).
  var stagesProgressFrom=function(s){ s=s||{}; return [s.contract,s.payment1,s.payment2].filter(Boolean).length; };
  var saveStages=async function(lid,stages){
    try{localStorage.setItem("crm_stages_"+lid,JSON.stringify(stages));}catch(e){}
    try{
      var updated=await apiFetch("/api/leads/"+lid,"PUT",{stages:stages},p.token);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===lid?updated:l;});});
      // Patch the Deals-page row source too — most deals aren't in p.leads (only
      // the newest-100 bootstrap), so without this the row keeps rendering the
      // stale d.stages until a refetch. Mirrors the row-patch at 12537/12562/12585.
      setDealAllData(function(prev){return Array.isArray(prev)?prev.map(function(l){return gid(l)===lid?Object.assign({},l,{stages:(updated&&updated.stages)||stages}):l;}):prev;});
    }catch(e){ /* keep local copy on failure */ }
  };
  var [stagesForm,setStagesForm]=useState({contract:false,contractDate:"",payment1:false,payment1Date:"",payment1Amount:"",payment2:false,payment2Date:"",payment2Amount:""});

  // Optimistic list patches — the DealsPage half of the pair added on EOIPage.
  // See the long note there for why the p.leads swap these replace was both
  // destructive (it substituted the lead copy for the record row) and mostly
  // inert (it only fired for leads inside the 100-row bootstrap).
  var patchDealRow = function(row, patch){
    var k = rowKey(row);
    setDealAllData(function(prev){ return Array.isArray(prev)?prev.map(function(l){ return rowKey(l)===k?Object.assign({},l,patch):l; }):prev; });
  };
  var patchDealRowsByLead = function(lid, patch){
    var id = String(lid);
    setDealAllData(function(prev){ return Array.isArray(prev)?prev.map(function(l){ return gid(l)===id?Object.assign({},l,patch):l; }):prev; });
  };
  // THE ROW IS GONE — the DealsPage twin of dropEoiRow (owner-only permanent
  // delete, 2026-11-02). Keyed on rowKey: one transaction, never the client.
  var dropDealRow = function(row){
    var k = rowKey(row);
    setDealAllData(function(prev){ return Array.isArray(prev)?prev.filter(function(l){ return rowKey(l)!==k; }):prev; });
  };
  var [deleteDealRow, setDeleteDealRow] = useState(null);
  var hardDeleteDeal = async function(row){
    var out = await apiFetch("/api/records/deal/"+rowKey(row),"DELETE",null,p.token,p.csrfToken);
    dropDealRow(row);
    setSelectedDeal(function(prev){ return prev && rowKey(prev)===rowKey(row) ? null : prev; });
    setDeleteDealRow(null);
    try { alert((out && out.message) || ("Deal #"+row._recordNo+" permanently deleted")); } catch(e){}
  };

  var archiveDeal=async function(lid){
    if(!window.confirm("Archive this deal?"))return;
    try{
      await apiFetch("/api/leads/"+lid+"/archive","PUT",null,p.token);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===lid?Object.assign({},l,{archived:true}):l;});});
      // Lead-level: every row this lead owns leaves with it. dealsSource still
      // filters on `archived`, so they disappear immediately.
      patchDealRowsByLead(lid, { archived:true });
      if(selectedDeal&&gid(selectedDeal)===lid)setSelectedDeal(null);
    }catch(e){alert(e.message);}
  };

  // DEAD CODE (dormant, intentional): the whole deal-stages editor subsystem —
  // openStages, stagesProgress, saveStages, getStages, resolveStages,
  // stagesProgressFrom, the stagesModal/stagesForm state, and the stagesModal
  // <Modal> (~L14109) — has NO entry point anymore. The owner removed deal
  // stages from the Deals UI entirely (list columns + detail-panel control).
  // Kept intact this pass per request; safe to delete in a future cleanup since
  // Lead.stages is purely visual (no commission/report/payout logic reads it).
  var openStages=function(d){
    var s=resolveStages(d)||{};
    setStagesForm({
      contract:s.contract||false, contractDate:s.contractDate||"",
      payment1:s.payment1||false, payment1Date:s.payment1Date||"", payment1Amount:s.payment1Amount||"",
      payment2:s.payment2||false, payment2Date:s.payment2Date||"", payment2Amount:s.payment2Amount||""
    });
    setStagesModal(d);
  };

  var stagesProgress=function(lid){
    var s=getStages(lid);
    var done=[s.contract,s.payment1,s.payment2].filter(Boolean).length;
    return done;
  };

  // Desktop table header columns, role-filtered. Single source of truth for
  // both the <th> render and the inline-panel row's colSpan (dealColCount), so
  // the accordion row always spans the full table width per role and never
  // drifts. After Feature B's "Closing Company" column (admin/operation only)
  // and D4's "Developer" column (all roles): admin=12, operation=12,
  // director/manager/team_leader=11, sales=9. (dealColCount is derived, so these
  // are documentation only — the colSpan always tracks dealHeaderCols.length.)
  // CLIENT PHONE IS NOT ROLE-GATED HERE (owner ruling 2026-08-26). Anyone who can
  // OPEN this page may see phone / phone2. The two pages used to disagree — EOI
  // said role!=="operation", Deals said role==="admin" — and the split was three
  // successive tightenings inside eleven minutes on 2026-03-27 (cb14124 19:31 ->
  // b1d9e23 19:42 -> 0ee2f21 19:53, header restored by ed64d7e the next morning)
  // that were never reconciled. Both gates were also COSMETIC: the server has
  // always shipped phone/phone2 in EOI_LIST_FIELDS to everyone who could see the
  // row, so the number was one devtools panel away regardless. The real rule now
  // lives in listFieldsFor() on the server — see the policy note above
  // EOI_LIST_FIELDS. Do not put a client-side role test back here.
  // 2026-10-29 (owner task): Alt. Phone lives on line 2 of the Phone cell and
  // Developer on line 2 of the Project cell — the two columns are gone from the
  // desktop table (admin/operation=10, director/manager/team_leader=9, sales=8).
  // The developer line keeps the canSeeDeveloper(p.cu) predicate its column had.
  var dealHeaderCols=[t.name,t.phone,t.project,t.budget,"Deal Date",canSeeClosingCompany(p.cu)?"Closing Company":null,isAdmin?t.agent:null,isAdmin?t.source:null,"Approved",""].filter(function(h){return h!==null;});
  var dealColCount=dealHeaderCols.length;

  // Deal Documents (images + PDFs) — mirrors EOIPage handleDocUpload/deleteDoc,
  // pointing at the deal-documents routes. Defined before renderDealPanel so it
  // closes over them.
  var handleDealDocUpload=async function(e,lead){
    var file=e.target.files[0]; if(!file) return;
    if (file.size>6*1024*1024) { alert("File too large (max 6MB)"); return; }
    setDealDocUploading(true);
    try{
      var dataUrl = await new Promise(function(resolve,reject){
        var reader=new FileReader();
        reader.onload=function(ev){resolve(ev.target.result);};
        reader.onerror=function(){reject(new Error("Read failed"));};
        reader.readAsDataURL(file);
      });
      if(isHistoricalRow(lead)){
        var artDU=await apiFetch("/api/records/"+rowKey(lead)+"/documents","POST",{fileData:dataUrl},p.token);
        setSelectedDeal(function(prev){ return prev&&rowKey(prev)===rowKey(lead)?Object.assign({},prev,{dealDocuments:artDU.documents||[]}):prev; });
        setDealDocUploading(false); try{ e.target.value=""; }catch(er0){} return;
      }
      var updated=await apiFetch("/api/leads/"+gid(lead)+"/deal-documents","POST",{fileData:dataUrl,txnId:rowKey(lead)},p.token);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(lead)?updated:l;});});
      var kDoc=rowKey(lead);
      setSelectedDeal(function(prev){ return prev&&rowKey(prev)===kDoc?Object.assign({},prev,{dealDocuments:updated.dealDocuments||[]}):prev; });
    }catch(ex){alert("Upload failed: "+(ex.message||ex));}
    setDealDocUploading(false);
    try{ e.target.value=""; }catch(er){}
  };

  var deleteDealDoc=async function(lead,index){
    if(!window.confirm("Remove this document?")) return;
    try{
      if(isHistoricalRow(lead)){
        var artDD=await apiFetch("/api/records/"+rowKey(lead)+"/delete-document","POST",{index:index},p.token);
        setSelectedDeal(function(prev){ return prev&&rowKey(prev)===rowKey(lead)?Object.assign({},prev,{dealDocuments:artDD.documents||[]}):prev; });
        return;
      }
      var kDDD=rowKey(lead);
      var updated=await apiFetch("/api/leads/"+gid(lead)+"/delete-deal-document","POST",{index:index,txnId:kDDD},p.token);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(lead)?updated:l;});});
      // Match the historical branch above: patch by rowKey, never replace.
      setSelectedDeal(function(prev){ return prev&&rowKey(prev)===kDDD?Object.assign({},prev,{dealDocuments:updated.dealDocuments||[]}):prev; });
    }catch(e){alert(e.message||"Delete failed");}
  };

  // Deal-detail panel. Shared renderer for the desktop inline-accordion row
  // (full-width, directly under the selected deal row) and the mobile
  // full-screen overlay — only the outer `style` differs (passed in by the
  // caller). At most one mounts per render (desktop XOR mobile, gated on
  // p.isMobile), so ref={dealPanelRef} attaches to a single node and the
  // click-outside close keeps working. Inner content + all logic
  // (approve/cancel→deal-cancel→auto-rotate, commission claim, image
  // upload/delete, getDealExtra) is unchanged from the prior beside-the-table
  // panel — only WHERE it mounts on desktop changed.
  var renderDealPanel=function(styleObj){
    // Sale fields now persist on the Lead (server); the legacy localStorage
    // "extra" is only a fallback for deals created before server persistence.
    var extra=getDealExtra(String(selectedDeal._id||gid(selectedDeal)))||{};
    var downPct=selectedDeal.downPaymentPct||extra.downPaymentPct||"";
    var instYears=selectedDeal.installmentYears||extra.installmentYears||"";
    var saleType=selectedDeal.saleType||"";
    var downPayAmt=selectedDeal.downPayment||"";
    // downPaymentDate / reservationDate / contractionDate are 100% date-only
    // strings in prod (11/11 each, measured 2026-08-25), so this delegates.
    var fmtSaleDate=function(v){ return fmtDayOnly(v); };
    var dpDateStr=fmtSaleDate(selectedDeal.downPaymentDate);
    var resDateStr=fmtSaleDate(selectedDeal.reservationDate);
    // Deal Date ≡ Contraction Date (unified). Show ONE tile; fall back to the
    // backbone dealDate for legacy deals whose contractionDate was never set.
    var dealDateStr=(function(){var dd=getDealDate(selectedDeal);return dd?fmtDayOnly(dd):"";})();
    var contrDateStr=fmtSaleDate(selectedDeal.contractionDate)||dealDateStr;
    // Client preservation — EOI-time original contact. Shown as an admin-cluster
    // note on the Origin line when the buyer typed at convert time differed.
    var eoiOrigName=selectedDeal.eoiOriginalName||"";
    var eoiOrigPhone=selectedDeal.eoiOriginalPhone||"";
    var eoiOrigPhone2=selectedDeal.eoiOriginalPhone2||"";
    return <div ref={dealPanelRef} style={styleObj}>
      <div style={{ background:"#F0FDF4", border:"1px solid #BBF7D0", borderRadius:10, margin:"4px 8px", overflow:"hidden", contain:"content" }}>
      <div style={{ background:"#F0FDF4", borderBottom:"1px solid #BBF7D0", padding:"calc(14px + env(safe-area-inset-top, 0px)) 16px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
            <button onClick={function(e){ e.stopPropagation(); setSelectedDeal(null); }} style={{ background:"transparent", border:"1px solid "+C.border, borderRadius:6, width:24, height:24, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:C.textLight, flexShrink:0 }}><X size={11}/></button>
            <div style={{ width:36, height:36, borderRadius:"50%", background:"#DCFCE7", color:"#15803D", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:700, flexShrink:0 }}>{(selectedDeal.name||"?").charAt(0).toUpperCase()}</div>
            <div style={{ minWidth:0 }}>
              <div style={{ color:"#15803D", fontSize:14, fontWeight:700, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{selectedDeal.name}</div>
              {canSeeLeadIds(p.cu) && <div style={{ color:C.textLight, fontSize:11, marginTop:2 }}>{txnRowLabel(selectedDeal)}</div>}
              <div style={{ color:"#16A34A", fontSize:11, marginTop:2 }}><PhoneCell phone={selectedDeal.phone}/></div>
              {selectedDeal.phone2&&selectedDeal.phone2!==selectedDeal.phone&&<div style={{ color:"#16A34A", fontSize:11, marginTop:2 }}><PhoneCell phone={selectedDeal.phone2}/></div>}
              {/* The EOI-time contact (client preservation) no longer renders
                  here: the header is name · ID · phone only (owner ruling
                  2026-10-27). It is an admin-cluster note on the Origin line. */}
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
          {(p.cu.role==="admin"||p.cu.role==="operation")&&<div style={{ display:"flex", gap:6 }}>
            {(function(){
              var isCancelled = selectedDeal.dealStatus==="Deal Cancelled" || selectedDeal.status==="Deal Cancelled";
              if (isCancelled) return <span style={{ background:C.danger+"18", borderRadius:8, padding:"4px 10px", color:C.danger, fontSize:11, fontWeight:700 }}>❌ Deal Cancelled</span>;
              return <>
                <button onClick={async function(){
                  try{
                    var newValDA=!selectedDeal.dealApproved;
                    var upd={dealApproved:newValDA};
                    // Name the row — see the EOI Approve handler for why inference
                    // is never safe on a multi-transaction client.
                    var kDA=rowKey(selectedDeal);
                    var bodyDA=selectedDeal._fromRecord?Object.assign({},upd,{txnId:kDA}):upd;
                    var updated=await apiFetch("/api/leads/"+gid(selectedDeal),"PUT",bodyDA,p.token);
                    var mwDA=mirrorWarning(updated); if(mwDA) alert(mwDA);
                    p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(selectedDeal)?updated:l;});});
                    // PATCH, never replace with the lead doc — see the EOI handler.
                    var patchDA={dealApproved:newValDA,_txnStatus:newValDA?"Approved":"Pending"};
                    setSelectedDeal(function(prev){ return prev&&rowKey(prev)===kDA?Object.assign({},prev,patchDA):prev; });
                    patchDealRow(selectedDeal, patchDA);   // the list row, formerly via the p.leads swap
                  }catch(e){ alert((e&&e.message)||"Update failed"); }  // N4 — surface the pending-developer approval gate (was a silent no-op)
                }} style={{ background:selectedDeal.dealApproved?C.success+"22":"transparent", border:"1px solid "+(selectedDeal.dealApproved?C.success:C.border), borderRadius:8, padding:"4px 10px", cursor:"pointer", color:selectedDeal.dealApproved?C.success:C.textLight, fontSize:11, fontWeight:700 }}>
                  {selectedDeal.dealApproved?"✅ Approved":"⏳ Approve"}
                </button>
                <button disabled={dealCancelling} onClick={async function(){
                  if(!window.confirm("Cancel this deal? The lead will return to Hot Case status and be rotated to another agent.")) return;
                  setDealCancelling(true);
                  try{
                    // Name the deal record being cancelled (owner ruling 2026-09-12): its commission and no other.
                    var updated = await apiFetch("/api/leads/"+gid(selectedDeal)+"/deal-cancel","POST",{ txnId: selectedDeal._fromRecord ? rowKey(selectedDeal) : undefined },p.token);
                    // Auto-rotate via the ordered rotation list (backend skips previous agents).
                    try {
                      var rot = await apiFetch("/api/leads/"+gid(selectedDeal)+"/auto-rotate","POST",{reason:"manual"},p.token);
                      if (rot && rot.lead) updated = rot.lead;
                    } catch(rotErr){ /* deal cancelled; rotation may be exhausted */ }
                    p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(selectedDeal)?updated:l;});});
                    // PATCH — keep the row's record identity through the cancel.
                    var kDC=rowKey(selectedDeal);
                    setSelectedDeal(function(prev){ return prev&&rowKey(prev)===kDC?Object.assign({},prev,{dealStatus:"Deal Cancelled",_txnStatus:"Cancelled"}):prev; });
                    // Same patch on the list row so it re-files into Cancelled now.
                    patchDealRow(selectedDeal, {dealStatus:"Deal Cancelled",_txnStatus:"Cancelled"});
                    setDealTab("cancelled");
                  }catch(e){alert(e.message||"Cancel failed");}
                  setDealCancelling(false);
                }} style={{ background:"transparent", border:"1px solid "+C.danger, borderRadius:8, padding:"4px 10px", cursor:dealCancelling?"wait":"pointer", color:C.danger, fontSize:11, fontWeight:700, opacity:dealCancelling?0.6:1 }}>
                  {dealCancelling?"Cancelling…":"❌ Cancel"}
                </button>
                {canHardDeleteRow(p.access, selectedDeal) && dealLifecycleActions(selectedDeal).indexOf("delete")!==-1 && <button onClick={function(){setDeleteDealRow(selectedDeal);}} title="Permanently delete this deal (owner only)"
                  style={{ background:"transparent", border:"1px solid "+C.danger, borderRadius:8, padding:"4px 10px", cursor:"pointer", color:C.danger, fontSize:11, fontWeight:700 }}>
                  🗑 Delete
                </button>}
                {deleteDealRow && <TxnDeleteModal kind="deal" row={deleteDealRow} colors={C} onClose={function(){setDeleteDealRow(null);}} onConfirm={function(){ return hardDeleteDeal(deleteDealRow); }}/>}
              </>;
            })()}
          </div>}
          </div>
        </div>
      </div>
      {/* ORIGIN — the EOI panel's twin, see the note there (admin cluster only,
          same isOnlyAdmin). On a deal row the chain also carries the absorbed
          EOI stage (row._eoiId / _eoiRecordedAt), and the EOI-time contact when
          the final buyer differed — it used to sit under the phone in the header. */}
      {isOnlyAdmin && <OriginBlock key={"origin:"+rowKey(selectedDeal)} row={selectedDeal} kind="deal" cu={p.cu} token={p.token} apiFetch={apiFetch} colors={C}
        nameOf={function(ref){ return userNameOf(ref, p.users); }} creditedName={txnAgentNameOf(selectedDeal, p.users)}
        brokerName={brokerNameOf(selectedDeal.externalBrokerId, [])} externalAgentName={userNameOf(selectedDeal.externalSalesAgentId, p.users) || (selectedDeal.externalSalesAgentName || "")}
        eoiContact={(eoiOrigName && String(eoiOrigName).trim().toLowerCase()!==String(selectedDeal.name||"").trim().toLowerCase()) ? eoiOrigName : ""}
        eoiContactTitle={[eoiOrigPhone, eoiOrigPhone2].filter(function(x){ return !!x; }).join(" / ")}
        onOpenLead={function(id){ apiFetch("/api/leads/"+id,"GET",null,p.token).then(function(full){ if (full && full._id) p.nav("leads", full); else alert("Could not open this lead."); }).catch(function(){ alert("Could not open this lead."); }); }}
        onOpenDr={function(d){ if (d && d._id) { p.nav("dailyReq", d); return; } apiFetch("/api/daily-requests/"+d,"GET",null,p.token).then(function(full){ if (full && full._id) p.nav("dailyReq", full); else alert("Could not open this request."); }).catch(function(){ alert("Could not open this request."); }); }}/>}
      <div style={{ padding:"14px 16px" }}>
        {(function(){
        // Tile chrome shared by the field tiles and the two select tiles. Desktop
        // tiles are one line (ellipsis, full value in the title) at a common
        // height; mobile keeps the wrapping tiles it always had.
        var dealTile=function(f, extra){ return <div key={f.l} style={Object.assign({ background:"#fff", borderRadius:8, padding:"8px 10px", minWidth:0, boxSizing:"border-box" }, p.isMobile?{ alignSelf:"start" }:{ minHeight:64 }, extra||{})}>
          <div style={{ fontSize:11, color:C.textLight, marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{f.icon} {f.l}</div>
          <div title={typeof f.v==="string"?f.v:undefined} style={(p.isMobile||f.wrap)?{ fontSize:13, color:C.text, wordBreak:"break-word" }:{ fontSize:13, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{f.v}</div>
        </div>; };
        var selTileStyle=p.isMobile?{ padding:12, background:"#fff", borderRadius:8, minWidth:0 }:{ background:"#fff", borderRadius:8, padding:"8px 10px", minHeight:64, minWidth:0, boxSizing:"border-box" };
        var selLabelStyle=p.isMobile?{ fontSize:11, fontWeight:700, color:C.textLight, marginBottom:6 }:{ fontSize:11, color:C.textLight, marginBottom:2 };
        /* Closing Company (Feature B) — admin/SA can set/change which company
            the deal was closed under. The PUT response isn't populated, so the
            <select> value + label resolve via ccIdOf / the companies map.

            WHAT YOU SEE IS WHAT IS STORED. This select used to render no
            <option> matching an empty value, so a deal with closingCompanyId
            null fell back to selectedIndex 0 — and since GET /api/closing-companies
            sorts { isDefault:-1, name:1 }, index 0 is always ARO. The card
            therefore displayed "Closed via: ARO" over a stored null while the
            Deals table (which reads the same field honestly) showed "—". Worse,
            re-picking ARO fired no change event, so the only way to persist it
            was to switch to another company and back. The explicit
            "— Not set —" option below keeps the control truthful; it is
            rendered ONLY while the value is actually empty, so it can't be
            chosen as a way to clear a company that is already set. */
        var ccTile = canSeeClosingCompany(p.cu)&&<div key="cc" style={selTileStyle}>
          <div style={selLabelStyle}>🏢 Closed via</div>
          <Select value={ccIdOf(selectedDeal)} onChange={async function(e){
            var val=e.target.value;
            if(!val) return;   // the "— Not set —" placeholder is not a selectable action
            try{
              var rowCC=selectedDeal, kCC=rowKey(rowCC);
              var resCC=await saveRowEdit(rowCC,{closingCompanyId:val},p.token);
              // PATCH, never replace with the lead doc: setSelectedDeal(updated)
              // discarded _recordNo/_rowKey and every record-owned field, which
              // is the second half of "the edit reverts".
              var ccObj=(dealClosingCompanies||[]).find(function(x){ return String(x._id)===String(val); });
              var patchCC={ closingCompanyId: ccObj || val };
              setSelectedDeal(function(prev){ return prev&&rowKey(prev)===kCC?Object.assign({},prev,patchCC):prev; });
              setDealAllData(function(prev){return Array.isArray(prev)?prev.map(function(l){return rowKey(l)===kCC?Object.assign({},l,patchCC):l;}):prev;});
              if(resCC.updated && p.setLeads) p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(rowCC)?resCC.updated:l;});});
            }catch(ex){ alert((ex&&ex.message)||"Update failed"); }
          }} maxWidth={p.isMobile?260:9999}
            options={(!ccIdOf(selectedDeal) ? [{ value:"", label:"— Not set —" }] : [])
              .concat(((dealClosingCompanies||[]).length===0 && !!ccIdOf(selectedDeal)) ? [{ value:ccIdOf(selectedDeal), label:(ccNameOf(selectedDeal)||"(unknown company)") }] : [])
              .concat((dealClosingCompanies||[]).map(function(co){ return { value:String(co._id), label:co.name }; }))}/>
        </div>;
        {/* Developer (D4 -> R2/Option 1) — searchable picker for ANY deal-editing
            role (the 505 developers are pre-vetted). Writes Lead.developerId via
            PUT; the response isn't populated, so value + label resolve via devIdOf
            / the developers map. "— None —" clears it. */}
        var devTile = canSeeDeveloper(p.cu)&&<div key="dev" style={selTileStyle}>
          <div style={selLabelStyle}>🏗️ Developer</div>
          {/* TWO BRANCHES, and there is no third — the twin of the EOI panel's,
              same ruling. The pending-developer banner is gone and
              `developerPending` is not read here: a deal's developer is its
              developerId or nothing. */}
          {!canEditDeveloper(p.cu)
          ? <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{devNameOf(selectedDeal)||"— None —"}</div>
          : <SearchableSelect
            value={devIdOf(selectedDeal)}
            emptyLabel="— None —"
            placeholder="Search developers…"
            maxWidth={p.isMobile?260:9999}
            options={(function(){
              var base=(dealDevelopers||[]).map(function(dv){ return { value:String(dv._id), label:dv.name }; });
              // Inject the current value if the list hasn't loaded it yet, so the
              // trigger shows the name (devNameOf resolves it) instead of an id.
              var cur=devIdOf(selectedDeal);
              if (cur && !base.some(function(o){ return o.value===cur; })) base.unshift({ value:cur, label:devNameOf(selectedDeal)||"Unknown developer" });
              return base;
            })()}
            onChange={async function(val){
              try{
                var rowDV=selectedDeal, kDV=rowKey(rowDV);
                var resDV=await saveRowEdit(rowDV,{developerId:val||null},p.token);
                var dvObj=(dealDevelopers||[]).find(function(x){ return String(x._id)===String(val); });
                var patchDV={ developerId: dvObj || (val||null) };
                setSelectedDeal(function(prev){ return prev&&rowKey(prev)===kDV?Object.assign({},prev,patchDV):prev; });
                setDealAllData(function(prev){return Array.isArray(prev)?prev.map(function(l){return rowKey(l)===kDV?Object.assign({},l,patchDV):l;}):prev;});
                if(resDV.updated && p.setLeads) p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(rowDV)?resDV.updated:l;});});
              }catch(ex){ alert((ex&&ex.message)||"Update failed"); }
            }}/>}
        </div>;
        return <>
        {/* DESKTOP: a fixed FIVE-column grid (owner brief 2026-10-27) — row 1
            Project · Unit Type · Unit Code · Budget · Sale Type, row 2 the five
            payment dates/amounts, row 3 Installments · Agent · Source · Closed
            via · Developer, then Notes across the full width. Every tile is the
            same height (minHeight 64) and one line with an ellipsis; the old
            auto-fill grid put 8 loose tiles on a 1920px row and wrapped Budget
            to three lines. Mobile keeps its auto-fill grid untouched. */}
        <div style={{ display:"grid", gridTemplateColumns:p.isMobile?"repeat(auto-fill, minmax(150px, 1fr))":"repeat(5, minmax(180px, 1fr))", gap:10, alignItems:"stretch" }}>
        {[
          {l:"Project", v:selectedDeal.project||"-", icon:"🏠"},
          {l:"Unit Type", v:selectedDeal.unitType||"-", icon:"🏷️"},
          // Unit Code — read-only; em-dash (not "-") so it survives the v!=="-" filter
          // below and existing deals with no code still render a "—".
          {l:"Unit Code", v:selectedDeal.unitCode||"—", icon:"🔖"},
          {l:"Budget", v:(function(){
            var raw=parseBudget(selectedDeal.budget);
            var weight=getProjectWeight(selectedDeal.project,selectedDeal);
            var split=getDealSplitFromObj(selectedDeal);
            var splitFactor=split?0.5:1;
            var eff=raw*weight*splitFactor;
            var isSalesRole=positionOf(p.cu)==="sales"||positionOf(p.cu)==="team_leader";
            if(isSalesRole&&eff!==raw&&raw>0) return eff.toLocaleString()+" EGP";
            if(!isSalesRole&&eff!==raw&&raw>0){
              // ONE line for the price, a small sub-line for the effective share
              // (owner brief 2026-10-27) — the "A → B effective" string wrapped to
              // three lines inside a tile.
              var why=[]; if(split) why.push("50% — "+(splitPartnerName(split)||"split")); if(weight!==1) why.push("weight ×"+weight);
              var effTxt="effective "+eff.toLocaleString()+(why.length?" ("+why.join(", ")+")":"");
              return <span><span style={{ display:"block" }}>{selectedDeal.budget+" EGP"}</span><span style={{ display:"block", fontSize:11, color:C.textLight, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }} title={effTxt}>{effTxt}</span></span>;
            }
            return selectedDeal.budget?selectedDeal.budget+" EGP":"-";
          })(), icon:"💰"},
          {l:"Sale Type", v:saleType||"—", icon:"🏷️"},
          // Resale = direct full-payment: no down payment / installments. Blank
          // these (the tile filter drops empty values) and show a single Deal Date
          // instead of Contraction Date.
          {l:"Down Payment", v:saleType==="Resale" ? "" : (function(){ var a=parseBudget(downPayAmt); return a>0?a.toLocaleString()+" EGP":"—"; })(), icon:"💵"},
          {l:"Down Payment %", v:saleType==="Resale" ? "" : (downPct?downPct+"%":"—"), icon:"📊"},
          {l:"Reservation Date", v:saleType==="Resale" ? "" : (resDateStr||"—"), icon:"📅"},
          {l:"Down Payment Date", v:saleType==="Resale" ? "" : (dpDateStr||"—"), icon:"📅"},
          {l:(saleType==="Resale" ? "Deal Date" : "Contract Date"), v:(saleType==="Resale" ? (dealDateStr||"—") : (contrDateStr||"—")), icon:"📅"},
          {l:"Installments", v:saleType==="Resale" ? "" : (instYears?instYears+" yrs":"—"), icon:"📆"},
          {l:"Agent", v:saleType==="Resale" ? "" : <span>{getAg(selectedDeal)}{legacyTag(selectedDeal)}</span>, icon:"👤"},
          {l:"Source", v:selectedDeal.source||"-", icon:"📢"},
        ].filter(function(f){ return f.v&&f.v!=="-"; }).map(function(f){ return dealTile(f); })}
        {/* Closed via + Developer are tiles of the same width on desktop (row 3);
            on mobile they keep their own block below the resale parties. */}
        {!p.isMobile && ccTile}
        {!p.isMobile && devTile}
        {selectedDeal.dealNotes && dealTile({l:"Notes", v:selectedDeal.dealNotes, icon:"📝", wrap:true}, p.isMobile?null:{ gridColumn:"1 / -1" })}
        </div>

        {/* Two-party Resale — seller + buyer blocks (name, phone, agent, commission,
            with-us badge). Each with-us party is its own tax-free, direct-collected
            commission (see the Commissions page for the two cards). */}
        {selectedDeal.saleType==="Resale" && selectedDeal.resaleParties && <div style={{ marginTop:12 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, marginBottom:6 }}>🔁 Resale parties — commission collected in full (direct), tax-free</div>
          <div style={{ display:"grid", gridTemplateColumns:p.isMobile?"1fr":"1fr 1fr", gap:12 }}>
            {[["seller","🔴 Seller","#B91C1C"],["buyer","🔵 Buyer","#1D4ED8"]].map(function(pr){
              var side=pr[0], party=(selectedDeal.resaleParties&&selectedDeal.resaleParties[side])||{};
              var agName=userNameOf(party.agentId, p.users) || party.agentName || "—";
              var ca=Number(party.commissionAmount)||0;
              return <div key={side} style={{ border:"1px solid "+pr[2]+"33", borderRadius:10, padding:"10px 12px", background:pr[2]+"08" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                  <span style={{ fontSize:12, fontWeight:800, color:pr[2] }}>{pr[1]}</span>
                  <span style={{ fontSize:9, fontWeight:700, padding:"1px 7px", borderRadius:9, background:party.withUs?"#DCFCE7":"#F1F5F9", color:party.withUs?"#15803D":C.textLight }}>{party.withUs?"With us":"Not ours"}</span>
                </div>
                <div style={{ fontSize:13, fontWeight:600 }}>{party.name||"—"}</div>
                {party.phone && <div style={{ fontSize:11, color:C.textLight }}>{party.phone}</div>}
                {party.withUs && <div style={{ marginTop:6, fontSize:11, color:C.text }}>👤 {agName}{isAdmin && ca>0 && <span style={{ marginLeft:8, color:"#059669", fontWeight:700 }}>💰 {ca.toLocaleString()} EGP</span>}</div>}
              </div>;
            })}
          </div>
        </div>}

        {/* MOBILE ONLY — Closed via + Developer keep their own block below the
            resale parties (desktop renders them as row-3 tiles in the grid). */}
        {p.isMobile&&(canSeeClosingCompany(p.cu)||canSeeDeveloper(p.cu))&&<div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))", gap:12, marginTop:12 }}>
        {ccTile}
        {devTile}
        </div>}

        </>;
        })()}

        {/* Deal Images — only shown when the deal already has contract images */}
        {isDealHydrated && ((selectedDeal.dealImages && selectedDeal.dealImages.length) || selectedDeal.dealImage) &&
        <div style={{ marginTop:12 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, marginBottom:6 }}>📎 Contract Images</div>
          {(function(){
            var imgs=selectedDeal.dealImages&&selectedDeal.dealImages.length?selectedDeal.dealImages:selectedDeal.dealImage?[selectedDeal.dealImage]:[];
            var uploadHandler=function(e){
              var file=e.target.files[0]; if(!file)return; e.target.value="";
              var reader=new FileReader();
              reader.onload=function(ev){
                var img=new Image();img.onload=function(){
                  var canvas=document.createElement("canvas");var maxW=1200,maxH=1200;var w=img.width,h=img.height;
                  if(w>maxW){h=h*(maxW/w);w=maxW;}if(h>maxH){w=w*(maxH/h);h=maxH;}
                  canvas.width=w;canvas.height=h;canvas.getContext("2d").drawImage(img,0,0,w,h);
                  var resized=canvas.toDataURL("image/jpeg",0.7);
                  (function(){
                    var rowIU=selectedDeal, kIU=rowKey(rowIU);
                    apiFetch("/api/leads/"+gid(rowIU)+"/upload-image","POST",{imageData:resized,imageType:"deal",txnId:kIU},p.token)
                      .then(function(updated){
                        p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(rowIU)?updated:l;});});
                        // Patch the row; replacing it with the lead doc would drop
                        // _recordNo/_rowKey and revert every record-owned field.
                        setSelectedDeal(function(prev){ return prev&&rowKey(prev)===kIU?Object.assign({},prev,{dealImages:updated.dealImages||[]}):prev; });
                      }).catch(function(){alert("Upload failed");});
                  })();
                };img.src=ev.target.result;
              };reader.readAsDataURL(file);
            };
            var deleteHandler=function(idx){
              if(!window.confirm("Delete this image?"))return;
              (function(){
                var rowID=selectedDeal, kID=rowKey(rowID);
                if(isHistoricalRow(rowID)){
                  apiFetch("/api/records/"+kID+"/delete-image","POST",{index:idx},p.token)
                    .then(function(art){ setSelectedDeal(function(prev){ return prev&&rowKey(prev)===kID?Object.assign({},prev,{dealImages:art.images||[]}):prev; }); })
                    .catch(function(){alert("Delete failed");});
                  return;
                }
                apiFetch("/api/leads/"+gid(rowID)+"/delete-deal-image","POST",{index:idx,txnId:kID},p.token)
                  .then(function(updated){
                    p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(rowID)?updated:l;});});
                    setSelectedDeal(function(prev){ return prev&&rowKey(prev)===kID?Object.assign({},prev,{dealImages:updated.dealImages||[]}):prev; });
                  }).catch(function(){alert("Delete failed");});
              })();
            };
            return <div style={{ display:"flex", gap:10, flexWrap:"wrap", overflowX:"auto", alignItems:"flex-start" }}>
              {imgs.length>0&&<div style={{ display:"contents" }}>
                {imgs.map(function(src,i){return <TxnDocThumb key={i} src={src} alt={"Contract "+(i+1)} title="Tap to view full size" bordered={false}
                  onOpen={function(){ setDealDocViewerSrc(src); }}
                  onDelete={function(){ deleteHandler(i); }} deleteTitle="Delete image"/>;})}
              </div>}
              <label style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", width:80, height:80, flex:"0 0 auto", padding:imgs.length>0?"6px":"10px", borderRadius:8, border:"1px dashed "+C.accent, background:C.accent+"08", color:C.accent, fontSize:imgs.length>0?11:12, fontWeight:600, cursor:"pointer", textAlign:"center", boxSizing:"border-box" }}>
                {imgs.length>0?"➕ Add More":"📤 Upload Contract Image"}
                <input type="file" accept="image/*" style={{ display:"none" }} onChange={uploadHandler}/>
              </label>
            </div>;
          })()}
        </div>}

        <DocViewer src={dealDocViewerSrc} alt="Deal document" onClose={function(){ setDealDocViewerSrc(""); }}/>
        {/* Deal Documents (images + PDFs) */}
        <div style={{ marginTop:12 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, marginBottom:6 }}>📄 Deal Documents {isDealHydrated ? "("+(selectedDeal.dealDocuments||[]).length+")" : ""}</div>
          {dealHydrateErr
            ?<div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap", padding:"10px 12px", borderRadius:8, border:"1px solid #FCA5A5", background:"#FEE2E2", color:"#B91C1C", fontSize:11, fontWeight:600 }}>
                <span>⚠️ Couldn’t load this deal’s files: {dealHydrateErr}</span>
                <button onClick={function(){ setPanelHydratedDealId(null); setDealHydrateErr(""); setSelectedDeal(function(prev){ return prev?Object.assign({},prev):prev; }); }} style={{ padding:"4px 12px", borderRadius:7, border:"1px solid #B91C1C", background:"#fff", color:"#B91C1C", fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>↻ Retry</button>
              </div>
            :!isDealHydrated
            ?<div style={{ padding:"16px", borderRadius:8, border:"1px dashed #E2E8F0", color:C.textLight, fontSize:11, textAlign:"center" }}>⌛ Loading…</div>
            :<>
              {/* EMPTY IS NOT AN ERROR (owner ruling 2026-09-10). A record with no
                  files yet says so and offers the upload; it never borrows the
                  error styling, which is what made "no documents" and "we could
                  not read your documents" look identical to the person reading. */}
              {(selectedDeal.dealDocuments||[]).length===0&&
                <div style={{ padding:"14px", borderRadius:8, border:"1px dashed #E2E8F0", color:C.textLight, fontSize:11, textAlign:"center", marginBottom:8 }}>No documents yet</div>}
              <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"flex-start", marginBottom:8 }}>
              {(selectedDeal.dealDocuments||[]).length>0&&<div style={{ display:"contents" }}>
                {(selectedDeal.dealDocuments||[]).map(function(doc,idx){
                  var url = typeof doc==="string" ? doc : (doc && doc.url) || "";
                  var name = typeof doc==="object" && doc && doc.name ? doc.name : ("Document "+(idx+1));
                  var isPdf = (typeof doc==="object" && doc && String(doc.contentType||"").toLowerCase()==="application/pdf") || /\.pdf$/i.test(String(name||"").split("?")[0]) || /\.pdf$/i.test(String(url||"").split("?")[0]) || (typeof url==="string" && url.indexOf("application/pdf")>=0);
                  // IMAGE: whole-thumbnail tap target + in-app viewer, ✕ its own target
                  // (TxnDocThumb, 2026-10-01). PDF: target=_blank, no download attribute —
                  // the native viewer opens it (2026-10-02).
                  if (!isPdf) return <TxnDocThumb key={idx} src={url} alt={name} title={name}
                    onOpen={function(){ setDealDocViewerSrc(url); }}
                    onDelete={isOnlyAdmin ? function(){ deleteDealDoc(selectedDeal,idx); } : null} deleteTitle="Remove"/>;
                  return <div key={idx} style={{ position:"relative", width:80, height:80, flex:"0 0 auto", border:"1px solid #E2E8F0", borderRadius:8, overflow:"hidden", background:"#fff" }} title={name}>
                    <a href={url} target="_blank" rel="noreferrer" style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", textDecoration:"none", color:"#DC2626", fontSize:10, fontWeight:700, padding:4, textAlign:"center" }}><span style={{ fontSize:22 }}>📕</span><span style={{ maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</span></a>
                    {isOnlyAdmin&&<button onClick={function(){deleteDealDoc(selectedDeal,idx);}} title="Remove" style={{ position:"absolute", top:2, right:2, width:18, height:18, borderRadius:"50%", border:"none", background:"rgba(220,38,38,0.9)", color:"#fff", fontSize:10, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>×</button>}
                  </div>;
                })}
              </div>}
              <label style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", width:80, height:80, flex:"0 0 auto", borderRadius:8, border:"1px dashed "+C.accent, background:C.accent+"08", color:C.accent, fontSize:10, fontWeight:600, cursor:"pointer", textAlign:"center", boxSizing:"border-box", padding:4 }}>
                {dealDocUploading?"Uploading…":"📎 Upload Deal Document (image or PDF)"}
                <input type="file" accept="image/*,application/pdf" style={{ display:"none" }} onChange={function(e){handleDealDocUpload(e,selectedDeal);}}/>
              </label>
              </div>
            </>}
        </div>
      </div>
      </div>
    </div>;
  };

  return <div style={{ padding:"18px 16px 40px" }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        {/* 2026-10-29 (owner task): the "N Deals" count is gone from the header on
            both layouts — the period row and the pills carry the numbers. */}
        {filteredTotal>0&&<div data-total-pill={dealTab} style={txnTotalPillStyle("deals", dealTab, C.success)}>Total: {filteredTotal.toLocaleString()} EGP</div>}
      </div>
      {/* 2026-10-29 (owner task): on desktop the Project Weights button (was
          "⚙️ Commission Projects" on its own row) sits beside + Add Deal. On the
          phone it is the 36px ⚖️ icon square (same modal, same gate), and the
          wide "⚙️ Commission Projects" row below is gone. */}
      {/* 2026-10-30 (owner task): on the phone the header keeps only the 🎯
          Project Weights square; "+ Add Deal" moved to the orange floating ＋
          (the Leads MobileFab — same component, position and size) which opens
          the same Add form. On desktop both buttons share ONE box
          (TXN_HEADER_BTN_STYLE / TXN_ADD_BTN_STYLE): same padding, radius and
          font-size, so "+ Add Deal" is exactly the Project Weights height. */}
      {isOnlyAdmin&&(p.isMobile
        ?<ProjectWeightsIconBtn onClick={function(){setProjWeightModal(true);}}/>
        :<div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <Btn outline onClick={function(){setProjWeightModal(true);}} title="Set each project's commission weight — full or half deal" style={Object.assign({ color:C.accent, borderColor:C.accent }, TXN_HEADER_BTN_STYLE)}>Project Weights</Btn>
          <Btn onClick={function(){setShowAdd(true);}} style={TXN_ADD_BTN_STYLE}><Plus size={14}/> Add Deal</Btn>
        </div>)}
    </div>
    {isOnlyAdmin&&p.isMobile&&<MobileFab open={false} hidden={hasRenderableDeal} onToggle={function(){setShowAdd(true);}} onClose={function(){}}><Btn onClick={function(){setShowAdd(true);}}><Plus size={14}/> Add Deal</Btn></MobileFab>}

    {/* Deal load state — never silently render a partial count from the
        shrunk bootstrap. Error → loud banner + Retry; loading → spinner text. */}
    {dealLoadErr
      ? <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap", background:"#FEE2E2", border:"1px solid #FCA5A5", color:"#B91C1C", borderRadius:10, padding:"10px 14px", marginBottom:12, fontSize:13, fontWeight:600 }}>
          <span>⚠️ Couldn’t load deals: {dealLoadErr} The list below is NOT complete — please retry.</span>
          <button onClick={function(){ setDealReloadKey(function(k){ return k+1; }); }} style={{ padding:"6px 14px", borderRadius:8, border:"1px solid #B91C1C", background:"#fff", color:"#B91C1C", fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>↻ Retry</button>
        </div>
      : (!dealsLoaded && <div style={{ background:"#F1F5F9", border:"1px solid #E2E8F0", color:C.textLight, borderRadius:10, padding:"10px 14px", marginBottom:12, fontSize:13, fontWeight:600 }}>⏳ Loading deals…</div>)
    }

    {dealTileScope&&<div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, padding:"8px 14px", background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:10, marginBottom:10 }}>
      <div style={{ fontSize:13, color:"#1D4ED8", fontWeight:600 }}>Dashboard tile: approved deals closed {dealsPeriodLabel}</div>
      <button onClick={function(){setDealTileScope(false);}} style={{ background:"#fff", border:"1px solid #BFDBFE", color:"#1D4ED8", fontSize:12, fontWeight:600, padding:"4px 10px", borderRadius:8, cursor:"pointer" }}>Show pending too</button>
    </div>}
    {/* Deals tab bar: Active vs Deal Cancelled */}
    <div style={p.isMobile?{ display:"flex", gap:8, marginBottom:8 }:{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap" }}>
      {dealsStrip.tabs.map(function(tb){
        var active=dealTab===tb.id;
        // Tabs carry no counts (owner 2026-10-29); Cancelled shows a red dot when ≥1 cancelled deal sits in the CURRENT period (same predicate as every other count).
        return <button key={tb.id} onClick={function(){setSelectedDeal(null);setDealTab(tb.id);}} style={txnTabStyle(tb, active, p.isMobile, C.textLight)}>{txnChipLabel(tb, p.isMobile)}{tb.id==="cancelled" && <CancelledDot show={cancelledDealsInPeriodN >= 1 && dealTab!=="cancelled"} title="Cancelled deals in this period"/>}</button>;
      })}
      {dealsStrip.empty && <span style={{ fontSize:13, color:C.textLight }}>{NO_TABS_MSG}</span>}
      {/* D4: the page and the tile agree by saying which rows the tile counts.
          The Deals tile is APPROVED deals; this list also shows the pending
          ones with their ⏳ badge. 2026-10-29 (owner task): on desktop only the
          pending count renders, as an amber pill, hidden at 0 — "N approved"
          is gone. Mobile keeps the old "N approved · M pending approval" text.
          Third pass the same day: the number is ALL-TIME and the pill is a
          toggle — focus = period All + awaiting-approval rows only; clear
          (second click or ✕) restores the previous period. */}
      {!dealsStrip.empty && dealTab!=="cancelled" && <PendingApprovalPill n={pendingAllTimeN} active={dealPendingOnly} compact={p.isMobile} style={p.isMobile?{ marginLeft:"auto" }:undefined} onClick={dealPendingOnly ? clearPendingFocus : focusPending} onClear={clearPendingFocus} title={dealPendingOnly ? "Showing deals awaiting approval (all time) — click to restore the period" : "Deals awaiting approval, all time — click to show only these"}/>}
    </div>

    {/* Phase R-12 Part 4 \u2014 All / Internal / External filter pill row.
        Phase R-14 \u2014 + Ambassador (amber). Admin/operation only \u2014 hidden
        for sales/team_leader/manager/director. Each pill carries its own active
        theme color so the type accents stay consistent with the deal-type toggle. */}
    {/* 2026-10-30 (owner task): ONE list for both layouts — [id, name, count,
        theme]. Desktop keeps its pills ("🏢 Internal (3)"); the phone draws the
        same four as a full-width SegmentedControl: four equal segments, the full
        name on line one and the count on line two, the selected one white. Same
        counts (dealsInPeriod and its splits), same setter. */}
    {isOnlyAdmin&&(function(){
      var dealTypePills=[["all","All",dealsInPeriod.length,C.accent,""],["internal","Internal",dealsInternalCount,C.accent,"\ud83c\udfe2 "],["external","External",dealsExternalCount,"#7C3AED","\ud83e\udd1d "],["ambassador","Ambassador",dealsAmbassadorCount,"#D97706","\ud83c\udfaf "]];
      if(p.isMobile) return <SegmentedControl label="Deal type" style={{ marginBottom:8 }} items={dealTypePills.map(function(pill){
        return { key:pill[0], label:pill[1], sub:pill[2], active:dealTypeFilter===pill[0], color:pill[3], onClick:function(){setDealTypeFilter(pill[0]);} };
      })}/>;
      return <div style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap" }}>
      {dealTypePills.map(function(pill){
        var active=dealTypeFilter===pill[0];
        var theme=pill[3];
        return <button key={pill[0]} onClick={function(){setDealTypeFilter(pill[0]);}} style={{
          padding:"5px 12px", borderRadius:8, border:"1px solid",
          borderColor: active ? theme : "#E2E8F0",
          background: active ? theme+"12" : "#fff",
          color: active ? theme : C.textLight,
          fontSize:12, fontWeight:600, cursor:"pointer"
        }}>{pill[4]}{pill[1]} ({pill[2]})</button>;
      })}
    </div>;
    })()}

    {/* Deals Search + Filter bar */}
    {/* 2026-10-30 (owner task): the phone period row is the same SegmentedControl
        as the type row — All · Q1 · Q2 · Q3 · Q4 · "2026 ▾" · 📅 — the selected
        quarter white; the year is a native select drawn as a segment and the 📅
        is the icon-only DashRangePicker in its `segment` dress. The three
        handlers (setDealQ, the year change, the range pick) are defined ONCE
        here and read by both layouts. Desktop keeps its row. */}
    {(function(){
      var DEAL_QUARTERS=["all","Q1","Q2","Q3","Q4"];
      var onDealYearChange=function(e){var v=e.target.value;setDealYear(v==="all"?"all":Number(v));};
      var dealYearOpts=dealYears.map(function(y){return { value:String(y), label:y==="all"?"All":String(y) };});
      var onDealRangePick=function(a, b){ if(!a){ setDateFrom(""); setDateTo(""); return; } setDateFrom(a); setDateTo(b||a); };
      var isCurrentQOf=function(q){ return q===curQ && dealYear===curYear && q!=="all"; };
      if(p.isMobile) return <SegmentedControl label="Period" style={{ marginBottom:8 }} items={DEAL_QUARTERS.map(function(q){
          return { key:q, label:q==="all"?"All":q, active:dealQ===q, color:C.accent, onClick:function(){setDealQ(q);}, dot:isCurrentQOf(q), dotTitle:"Current quarter" };
        }).concat([
          { key:"year", node:<SegmentedSelect title="Year" value={String(dealYear)} onChange={onDealYearChange} options={dealYearOpts}/> },
          { key:"date", node:<DashRangePicker isMobile={p.isMobile} iconOnly={p.isMobile} segment
              value={{ from:dateFrom, to:dateTo }} active={!!dateFrom}
              min="" todayYmd={dfYmd(new Date())}
              onPick={onDealRangePick}/> }
        ])}/>;
      var dealAccent=periodAccentOf(C.accent);
      return <div data-period-row="deals" style={{ display:"flex", gap:8, alignItems:"center", marginBottom:10, flexWrap:"wrap" }}>
      <div style={{ display:"flex", gap:5 }}>
        {/* Already the target shape — four fixed quarter buttons beside a year
            select — so only the current-quarter marker needed unifying: it was
            a 🔵 glyph appended to the label, which shifted the button width and
            did not match the small green dot the dashboard picker uses. Same
            condition as before (current quarter AND the selected year is the
            current one); now an absolutely-positioned dot, so button widths
            stay uniform. */}
        {DEAL_QUARTERS.map(function(q){
          var isCurrentQ = isCurrentQOf(q);
          return <button key={q} onClick={function(){setDealQ(q);}}
          style={Object.assign(periodChipStyle(dealQ===q, dealAccent, false), { position:"relative" })}>{q==="all"?"All":q}
          {isCurrentQ&&<span title="Current quarter" style={{ position:"absolute", top:3, right:3, width:6, height:6, borderRadius:"50%", background:"#22C55E" }}/>}
          </button>;})}
      </div>
      {/* Number(e.target.value) alone would turn the "all" option into NaN, and
          `getFullYear() !== NaN` is true for every row — the picker would empty
          the page instead of filling it. */}
      {/* The year is a native <select> dressed as the period chip (owner 2026-10-31): same box, ▾ inside. */}
      <select title="Year" data-period-select="year" value={String(dealYear)} onChange={onDealYearChange} style={periodSelectStyle(false, dealAccent, false)}>
        {dealYearOpts.map(function(o){ return <option key={o.value} value={o.value}>{o.label}</option>; })}
      </select>
      {/* 2026-10-29 (owner task): the Dashboard/Meetings "📅 Date" chip +
          calendar replaces the two raw From/To inputs on desktop, and sits in
          the PERIOD row after the year (All · Q1 · Q2 · Q3 · Q4 · year · 📅 Date),
          on the phone too since the mobile-parity pass (the From/To inputs are
          gone). It writes the SAME dateFrom/dateTo the inputs wrote (one click =
          that day, from==to; second click = the range), so the filter, "Show
          all", ✕ Clear and landOnDeal are untouched. The popover's own Clear
          hands ("","") in. */}
      <DashRangePicker isMobile={p.isMobile} iconOnly={p.isMobile}
        value={{ from:dateFrom, to:dateTo }} active={!!dateFrom}
        min="" todayYmd={dfYmd(new Date())} triggerStyle={periodChipStyle(!!dateFrom, dealAccent, false)}
        onPick={onDealRangePick}/>
    </div>;
    })()}
    {p.isMobile?<div style={{ marginBottom:14 }}>
      {/* Phone filter grid (owner 2026-10-29): two columns, the search spans
          both, then Agents · Developers · Projects · Sources — the same
          controls, the same role gates, as the desktop attribute row. */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
        <input placeholder="🔍 Search by name, project or phone..." value={dealSearch} onChange={function(e){setDealSearch(e.target.value);}} style={{ gridColumn:"1 / -1", padding:"6px 12px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:12, width:"100%", minWidth:0, boxSizing:"border-box" }}/>
        {isAdmin&&<div style={{ minWidth:0 }}><SearchableSelect multi value={dealAgent} onChange={setDealAgent}
          options={agentFilterOpts} emptyLabel="👤 All Agents" placeholder="Search agent…" width="100%" maxWidth={9999} fontSize={12}/></div>}
        {canSeeDeveloper(p.cu)&&<div style={{ minWidth:0 }}><SearchableSelect multi value={dealDeveloperFilter} onChange={function(v){setDealDeveloperFilter(v);setDealProjectFilter([]);}} options={dealDeveloperOpts} emptyLabel="🏗️ All developers" placeholder="Search developers…" width="100%" maxWidth={9999} fontSize={12}/></div>}
        <div style={{ minWidth:0 }}><SearchableSelect multi value={dealProjectFilter} onChange={setDealProjectFilter} options={dealProjectOpts} emptyLabel="🏠 All projects" placeholder="Search projects…" width="100%" maxWidth={9999} fontSize={12}/></div>
        <div style={{ minWidth:0 }}><SearchableSelect multi value={dealSourceFilter} onChange={setDealSourceFilter} options={dealSourceOpts} emptyLabel="All sources" placeholder="Search source…" width="100%" maxWidth={9999} fontSize={12} triggerStyle={dealSourceFilter.length===1?sourceNameStyle(dealSourceFilter[0]):undefined}/></div>
      </div>
      {!!(dateFrom||dateTo||dealSearch||dealAgent.length||dealTypeFilter!=="all"||dealDeveloperFilter.length||dealProjectFilter.length||dealSourceFilter.length)&&<button onClick={function(){setDateFrom("");setDateTo("");setDealSearch("");setDealAgent([]);setDealTypeFilter("all");setDealDeveloperFilter([]);setDealProjectFilter([]);setDealSourceFilter([]);setDealQ(curQ);setDealYear(curYear);}} style={{ width:"100%", padding:"7px 12px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", fontSize:12, cursor:"pointer", color:C.danger }}>✕ Clear All</button>}
    </div>:<div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:14, flexWrap:"wrap" }}>
      <input placeholder="🔍 Search by name, project or phone..." value={dealSearch} onChange={function(e){setDealSearch(e.target.value);}} style={{ padding:"6px 12px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:12, minWidth:220 }}/>
      {isAdmin&&<SearchableSelect multi value={dealAgent} onChange={setDealAgent}
        options={agentFilterOpts} emptyLabel="👤 All Agents" placeholder="Search agent…" width={200} maxWidth={200} fontSize={12}/>}
      {canSeeDeveloper(p.cu)&&<SearchableSelect multi value={dealDeveloperFilter} onChange={function(v){setDealDeveloperFilter(v);setDealProjectFilter([]);}} options={dealDeveloperOpts} emptyLabel="🏗️ All developers" placeholder="Search developers…" width={200} maxWidth={200} fontSize={12}/>}
      <SearchableSelect multi value={dealProjectFilter} onChange={setDealProjectFilter} options={dealProjectOpts} emptyLabel="🏠 All projects" placeholder="Search projects…" width={200} maxWidth={200} fontSize={12}/>
      <SearchableSelect multi value={dealSourceFilter} onChange={setDealSourceFilter} options={dealSourceOpts} emptyLabel="All sources" placeholder="Search source…" width={170} maxWidth={170} fontSize={12} triggerStyle={dealSourceFilter.length===1?sourceNameStyle(dealSourceFilter[0]):undefined}/>
      {!!(dateFrom||dateTo||dealSearch||dealAgent.length||dealTypeFilter!=="all"||dealDeveloperFilter.length||dealProjectFilter.length||dealSourceFilter.length)&&<button onClick={function(){setDateFrom("");setDateTo("");setDealSearch("");setDealAgent([]);setDealTypeFilter("all");setDealDeveloperFilter([]);setDealProjectFilter([]);setDealSourceFilter([]);setDealQ(curQ);setDealYear(curYear);}} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", fontSize:12, cursor:"pointer", color:C.danger }}>✕ Clear All</button>}
    </div>}
    <Modal show={showAdd} onClose={function(){setShowAdd(false);}} title={t.addLead+" (Done Deal)"}>
      <LeadForm t={t} cu={p.cu} users={p.users} token={p.token} isMobile={p.isMobile} isReq={false} initialStatus="DoneDeal"
        initial={{name:"",phone:"",phone2:"",email:"",budget:"",project:"",source:"",agentId:"",callbackTime:"",notes:"",status:"DoneDeal"}}
        onClose={function(){setShowAdd(false);}}
        onSave={function(lead){p.setLeads(function(prev){var nid=String(lead&&lead._id||"");if(!nid)return[lead].concat(prev);if(prev.some(function(l){return gid(l)===nid;}))return prev.map(function(l){return gid(l)===nid?lead:l;});return [lead].concat(prev);});setShowAdd(false);}}/>
    </Modal>

    {editDeal&&<Modal show={true} onClose={function(){setEditDeal(null);}} title={t.edit}>
      <LeadForm t={t} cu={p.cu} users={p.users} token={p.token} isMobile={p.isMobile} isReq={false}
        editId={gid(editDeal)} initial={editDeal} txnRow={editDeal}
        onClose={function(){setEditDeal(null);}}
        onSave={function(updated){
          // Same rule as the EOI modal: every merge onto a row goes through
          // hydrateTxnRow, and the returned LEAD reaches the panel row only
          // through the leadWins orientation (lifecycle stays the record's).
          if(updated&&updated._recordPatched){
            var kDF=rowKey(updated);
            setDealAllData(function(prev){return Array.isArray(prev)?prev.map(function(l){return rowKey(l)===kDF?hydrateTxnRow(l,updated):l;}):prev;});
            setSelectedDeal(function(prev){ return prev&&rowKey(prev)===kDF?hydrateTxnRow(prev,updated):prev; });
            setEditDeal(null); return;
          }
          p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(updated)?updated:l;});});setEditDeal(null);
          if(selectedDeal&&gid(selectedDeal)===gid(updated))setSelectedDeal(function(prev){return prev?hydrateTxnRow(updated,prev,{leadWins:true}):prev;});
        }}/>
    </Modal>}

    {/* Stages Modal */}
    {stagesModal&&<Modal show={true} onClose={function(){setStagesModal(null);}} title={"📋 Deal Stages — "+stagesModal.name}>
      <div style={{ marginBottom:14, padding:"10px 14px", background:"#DCFCE7", borderRadius:10, fontSize:12, color:"#15803D", fontWeight:600 }}>
        {stagesProgressFrom(resolveStages(stagesModal))}/3 stages completed
        <div style={{ height:6, background:"#BBF7D0", borderRadius:3, marginTop:6 }}>
          <div style={{ height:"100%", width:(stagesProgressFrom(resolveStages(stagesModal))/3*100)+"%", background:C.success, borderRadius:3, transition:"width 0.4s" }}/>
        </div>
      </div>

      {/* Contract */}
      <div style={{ marginBottom:14, padding:"12px 14px", background:"#F8FAFC", borderRadius:10, border:"1px solid "+(!stagesForm.contract?"#E2E8F0":C.success) }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:stagesForm.contract?8:0 }}>
          <div onClick={function(){setStagesForm(function(f){return Object.assign({},f,{contract:!f.contract});});}}
            style={{ width:22, height:22, borderRadius:6, border:"2px solid", borderColor:stagesForm.contract?C.success:"#CBD5E1", background:stagesForm.contract?C.success:"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0 }}>
            {stagesForm.contract&&<span style={{ color:"#fff", fontSize:13, fontWeight:700 }}>✓</span>}
          </div>
          <span style={{ fontSize:13, fontWeight:600 }}>📝 Contract Signed</span>
        </div>
        {stagesForm.contract&&<div style={{ marginTop:8 }}>
          <label style={{ fontSize:11, color:C.textLight, display:"block", marginBottom:4 }}>Signing Date</label>
          <input type="date" value={stagesForm.contractDate} onChange={function(e){setStagesForm(function(f){return Object.assign({},f,{contractDate:e.target.value});});}}
            style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13, boxSizing:"border-box" }}/>
        </div>}
      </div>

      {/* Payment 1 */}
      <div style={{ marginBottom:14, padding:"12px 14px", background:"#F8FAFC", borderRadius:10, border:"1px solid "+(!stagesForm.payment1?"#E2E8F0":C.success) }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:stagesForm.payment1?8:0 }}>
          <div onClick={function(){setStagesForm(function(f){return Object.assign({},f,{payment1:!f.payment1});});}}
            style={{ width:22, height:22, borderRadius:6, border:"2px solid", borderColor:stagesForm.payment1?C.success:"#CBD5E1", background:stagesForm.payment1?C.success:"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0 }}>
            {stagesForm.payment1&&<span style={{ color:"#fff", fontSize:13, fontWeight:700 }}>✓</span>}
          </div>
          <span style={{ fontSize:13, fontWeight:600 }}>💰 1st Payment</span>
        </div>
        {stagesForm.payment1&&<div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 10px", marginTop:8 }}>
          <div>
            <label style={{ fontSize:11, color:C.textLight, display:"block", marginBottom:4 }}>Date</label>
            <input type="date" value={stagesForm.payment1Date} onChange={function(e){setStagesForm(function(f){return Object.assign({},f,{payment1Date:e.target.value});});}}
              style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13, boxSizing:"border-box" }}/>
          </div>
          <div>
            <label style={{ fontSize:11, color:C.textLight, display:"block", marginBottom:4 }}>Amount (EGP)</label>
            <input type="text" placeholder="" value={stagesForm.payment1Amount}
              onChange={function(e){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");setStagesForm(function(f){return Object.assign({},f,{payment1Amount:r?Number(r).toLocaleString():""});});}}
              style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13, boxSizing:"border-box", direction:"ltr" }}/>
          </div>
        </div>}
      </div>

      {/* Payment 2 */}
      <div style={{ marginBottom:16, padding:"12px 14px", background:"#F8FAFC", borderRadius:10, border:"1px solid "+(!stagesForm.payment2?"#E2E8F0":C.success) }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:stagesForm.payment2?8:0 }}>
          <div onClick={function(){setStagesForm(function(f){return Object.assign({},f,{payment2:!f.payment2});});}}
            style={{ width:22, height:22, borderRadius:6, border:"2px solid", borderColor:stagesForm.payment2?C.success:"#CBD5E1", background:stagesForm.payment2?C.success:"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0 }}>
            {stagesForm.payment2&&<span style={{ color:"#fff", fontSize:13, fontWeight:700 }}>✓</span>}
          </div>
          <span style={{ fontSize:13, fontWeight:600 }}>💰 2nd Payment</span>
        </div>
        {stagesForm.payment2&&<div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 10px", marginTop:8 }}>
          <div>
            <label style={{ fontSize:11, color:C.textLight, display:"block", marginBottom:4 }}>Date</label>
            <input type="date" value={stagesForm.payment2Date} onChange={function(e){setStagesForm(function(f){return Object.assign({},f,{payment2Date:e.target.value});});}}
              style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13, boxSizing:"border-box" }}/>
          </div>
          <div>
            <label style={{ fontSize:11, color:C.textLight, display:"block", marginBottom:4 }}>Amount (EGP)</label>
            <input type="text" placeholder="" value={stagesForm.payment2Amount}
              onChange={function(e){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");setStagesForm(function(f){return Object.assign({},f,{payment2Amount:r?Number(r).toLocaleString():""});});}}
              style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13, boxSizing:"border-box", direction:"ltr" }}/>
          </div>
        </div>}
      </div>

      <div style={{ display:"flex", gap:10 }}>
        <Btn outline onClick={function(){setStagesModal(null);}} style={{ flex:1 }}>Cancel</Btn>
        <Btn onClick={function(){saveStages(gid(stagesModal),stagesForm);setStagesModal(null);}} style={{ flex:1 }}>✅ Save</Btn>
      </div>
    </Modal>}


    {/* Project Weight Modal */}
    {isAdmin&&projWeightModal&&<Modal show={true} onClose={function(){setProjWeightModal(false);}} title={"Project Weights"}>
      <div style={{ fontSize:12, color:C.textLight, marginBottom:12, padding:"8px 12px", background:"#F8FAFC", borderRadius:8 }}>
        100% = fully counted in Target & Commission<br/>50% = counted as half in Target & Commission
      </div>
      {(function(){var projects=[];deals.forEach(function(d){if(d.project&&!projects.includes(d.project))projects.push(d.project);});return projects.map(function(proj){
        var w=getProjectWeight(proj);
        // Phase 2 Slice 3 — server is now authoritative. saveProjectWeight
        // PUTs to /api/settings/project-weights (which cascades projectWeight
        // onto every matching Lead doc server-side); we mirror that locally
        // so the deals array reflects new weights without a refetch, then
        // bump pwRev so reads from the module cache pick up the change.
        var saveWeight=async function(newW){
          try {
            await saveProjectWeight(proj, newW, p.token);
            if (p.setLeads) p.setLeads(function(prev){
              return prev.map(function(l){
                return (l.project === proj) ? Object.assign({}, l, { projectWeight: newW }) : l;
              });
            });
            setProjWeights(function(prev){return Object.assign({},prev,{[proj]:newW});});
            if (p.bumpProjectWeightsRev) p.bumpProjectWeightsRev();
          } catch(e) {
            window.alert("Failed to save: " + (e && e.message ? e.message : "unknown error"));
          }
        };
        return <div key={proj} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 0", borderBottom:"1px solid #F1F5F9" }}>
          <span style={{ fontSize:13, fontWeight:600 }}>{proj}</span>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={function(){saveWeight(1);}}
              style={{ padding:"5px 12px", borderRadius:7, border:"2px solid", borderColor:w===1?C.success:"#E2E8F0", background:w===1?"#DCFCE7":"#fff", color:w===1?C.success:C.textLight, fontSize:12, fontWeight:w===1?700:400, cursor:"pointer" }}>100%</button>
            <button onClick={function(){saveWeight(0.5);}}
              style={{ padding:"5px 12px", borderRadius:7, border:"2px solid", borderColor:w===0.5?"#F59E0B":"#E2E8F0", background:w===0.5?"#FEF3C7":"#fff", color:w===0.5?"#B45309":C.textLight, fontSize:12, fontWeight:w===0.5?700:400, cursor:"pointer" }}>50%</button>
          </div>
        </div>;
      });})()}
      <Btn onClick={function(){setProjWeightModal(false);}} style={{ marginTop:14, width:"100%" }}>Close</Btn>
    </Modal>}

    {/* Split Modal */}
    {splitModal&&<Modal show={true} onClose={function(){setSplitModal(null);setSplitAgent2("");}} title={"🤝 Split Deal — "+splitModal.name}>
      <div style={{ fontSize:12, color:C.textLight, marginBottom:12 }}>Deal will be split 50/50 between two agents</div>
      <div style={{ marginBottom:10 }}>
        <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>Agent 1</div>
        {/* THE DEAL'S OWN AGENT (owner ruling 2026-09-12, R11): dealAgentPairOf
            reads dealAgentId (the record's own anchor) first, falling back to
            the lead's agentId ONLY when the deal carries no anchor at all — the
            same chokepoint the row's 🤝 chip and DealsPage totals already go
            through. Reading splitModal.agentId directly was reading the LEAD's
            CURRENT HOLDER: #50058's lead had rotated to Rokaya Swidan while the
            deal itself belongs to Jana Mohamed, so the modal showed "Agent 1:
            Rokaya Swidan" on a deal she has never touched. */}
        <div style={{ padding:"8px 12px", borderRadius:8, background:"#F8FAFC", fontSize:13 }}>{userNameOf(dealAgentPairOf(splitModal).primary, p.users)||"—"}</div>
      </div>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>Agent 2</div>
        <SearchableSelect value={splitAgent2} onChange={setSplitAgent2}
          options={withCurrentOption(salesUsers.filter(function(u){var uid=gid(u);var a1=dealAgentPairOf(splitModal).primary;return uid!==a1;}).map(function(u){return { value:gid(u), label:u.name+" — "+u.title };}), splitAgent2, userNameOf(splitAgent2, p.users) || "Unknown")}
          emptyLabel="— Select Agent —" placeholder="Search agent…" width="100%" maxWidth={9999} fontSize={14}/>
      </div>
      {getDealSplit(gid(splitModal))&&<div style={{ padding:"8px 12px", background:"#FEF3C7", borderRadius:8, fontSize:12, marginBottom:10 }}>
        Current split: {getDealSplitFromObj(splitModal).agent2Name} — <button onClick={async function(){
          try{await apiFetch("/api/leads/"+gid(splitModal),"PUT",{splitAgent2Id:null,splitAgent2Name:"",txnId:splitModal._fromRecord?rowKey(splitModal):undefined},p.token);p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(splitModal)?Object.assign({},l,{splitAgent2Id:null,splitAgent2Name:""}):l;});});}catch(e){}
          saveDealSplit(gid(splitModal),null);setSplitModal(null);
        }} style={{ background:"none", border:"none", color:C.danger, cursor:"pointer", fontSize:12 }}>Remove Split</button>
      </div>}
      <div style={{ display:"flex", gap:10 }}>
        <Btn outline onClick={function(){setSplitModal(null);setSplitAgent2("");}} style={{ flex:1 }}>Cancel</Btn>
        <Btn onClick={async function(){
          if(!splitAgent2) return;
          var ag2=salesUsers.find(function(u){return gid(u)===splitAgent2;});
          try{
            // The split is the DEAL's (owner ruling 2026-09-12): name the record so the
            // partner lands on it and its commission follows.
            var updated=await apiFetch("/api/leads/"+gid(splitModal),"PUT",{splitAgent2Id:splitAgent2,splitAgent2Name:ag2?ag2.name:"?",txnId:splitModal._fromRecord?rowKey(splitModal):undefined},p.token);
            p.setLeads(function(prev){return prev.map(function(l){return gid(l)===gid(splitModal)?updated:l;});});
            saveDealSplit(gid(splitModal),{agent2Id:splitAgent2,agent2Name:ag2?ag2.name:"?"});
          }catch(e){}
          setSplitModal(null); setSplitAgent2("");
        }} style={{ flex:1 }}>✅ Save</Btn>
      </div>
    </Modal>}

    {/* The "⚙️ Commission Projects" row is gone on the phone too (owner
        2026-10-29): the ⚖️ icon in the header opens the same modal. */}

    <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
    <Card p={0} style={p.isMobile?{ flex:1, overflow:"hidden", border:"none", boxShadow:"none", background:"transparent", borderRadius:0 }:{ flex:1, overflow:"hidden" }}>{p.isMobile?<div style={{ display:"flex", flexDirection:"column", gap:12, padding:0 }}>
      {/* dealsLoaded gate: while the fetch is in flight dealAllData is null and
          filteredDeals is [], so this used to paint "No deals yet" underneath
          the "⏳ Loading deals…" banner on every single page open. */}
      {dealsLoaded&&filteredDeals.length===0&&renderDealsEmpty()}
      {filteredDeals.map(function(d){
        var bv=parseBudget(d.budget);
        var isSel=selectedDeal&&rowKey(selectedDeal)===rowKey(d);
        var isLanding=dealFlashId===rowKey(d);
        return <div key={rowKey(d)} id={"deal-row-"+rowKey(d)} data-deal-row="1" onClick={function(){setSelectedDeal(isSel?null:d);}} style={{ background:isSel?"#EFF6FF":"#fff", border:"2px solid "+(isLanding?C.accent:(isSel?"#3B82F6":"#E8ECF1")), boxShadow:isLanding?"0 0 0 4px "+C.accent+"33":"none", borderRadius:14, padding:14, cursor:"pointer", transition:"box-shadow .3s, border-color .3s" }}>
          {/* Status pill — the EOI card's twin (owner brief 2026-10-27). */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6, gap:8 }}>
            <div style={{ fontSize:15, fontWeight:700, minWidth:0 }}>
              {resalePartyLabel(d) || d.name}{d.saleType==="Resale" && <span style={{ marginLeft:6, fontSize:9, padding:"1px 6px", borderRadius:9, background:"#FEE2E2", color:"#B91C1C", fontWeight:700, verticalAlign:"middle" }}>🔁 Resale</span>}
              {isOnlyAdmin && d.dealType==="ambassador" && <span style={{ marginLeft:6, fontSize:9, padding:"1px 6px", borderRadius:9, background:"#FEF3C7", color:"#B45309", fontWeight:700, verticalAlign:"middle" }}>🎯 Ambassador</span>}
              {canSeeLeadIds(p.cu) && <div style={{ fontSize:10, fontWeight:400, color:C.textLight, marginTop:1 }}>{txnRowLabel(d)}</div>}
            </div>
            {isDealCancelledRow(d)
              ?<span style={{ background:"#FEE2E2", color:"#B91C1C", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, flexShrink:0, whiteSpace:"nowrap", marginTop:-1 }}>❌ Cancelled</span>
              :d.dealApproved
              ?<span style={{ background:"#DCFCE7", color:"#15803D", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, flexShrink:0, whiteSpace:"nowrap", marginTop:-1 }}>✅ Approved</span>
              :<span style={{ background:"#FEF9C3", color:"#B45309", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, flexShrink:0, whiteSpace:"nowrap", marginTop:-1 }}>⏳ Pending</span>}
          </div>
          {/* Phone + alt phone on ONE line (owner 2026-10-29): "122**40**** · —" */}
          <div style={{ fontSize:12, color:C.textLight, marginBottom:6, display:"flex", alignItems:"center", gap:6, whiteSpace:"nowrap" }}><PhoneCell phone={d.phone}/><span>·</span>{d.phone2&&d.phone2!==d.phone?<PhoneCell phone={d.phone2}/>:<span>—</span>}</div>
          {/* Chips (owner brief 2026-10-27): equal-height single-line pills in one
              wrapping row, centred. The effective-share detail keeps its own
              wording on a small line below; the agent and split partner have
              their own line further down (admin) — a sales viewer's partner
              moves there too. OWNER RULING R-T2 — THE PARTNER SHOWN IS THE STORED
              PARTNER (see the twin block in the table body below). */}
          {(function(){
            var split=getDealSplitFromObj(d);
            var weight=getProjectWeight(d.project,d);
            var splitFactor=split?0.5:1;
            var effectiveBv=bv*weight*splitFactor;
            var showEffective=effectiveBv!==bv&&bv>0;
            var isSalesRole=positionOf(p.cu)==="sales"||positionOf(p.cu)==="team_leader";
            var mainFig=(isSalesRole&&showEffective)?effectiveBv.toLocaleString():(bv>0?bv.toLocaleString():(d.budget||"-"));
            return <>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:6 }}>
              {d.project&&<span title={d.project+(canSeeDeveloper(p.cu)&&devNameOf(d)?" · "+devNameOf(d):"")} style={{ display:"inline-flex", alignItems:"center", height:24, padding:"0 8px", borderRadius:6, fontSize:11, whiteSpace:"nowrap", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", color:"#6D28D9", background:"#EDE9FE" }}>🏠 {d.project}{canSeeDeveloper(p.cu)&&devNameOf(d)?" · "+devNameOf(d):""}</span>}
              <span style={{ display:"inline-flex", alignItems:"center", height:24, padding:"0 8px", borderRadius:6, fontSize:11, whiteSpace:"nowrap", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", color:C.success, background:"#F0FDF4", fontSize:13, fontWeight:700 }}>💰 {mainFig}</span>
            </div>
            {isSalesRole&&showEffective&&<div style={{ fontSize:10, color:C.textLight, marginTop:-2, marginBottom:6 }}>من {bv.toLocaleString()}</div>}
            {!isSalesRole&&showEffective&&<div style={{ fontSize:10, color:"#8B5CF6", fontWeight:600, marginTop:-2, marginBottom:6 }}>
              {split?"🤝":"📊"} {effectiveBv.toLocaleString()} {split?"50% — "+(split.agent2Name||"Shared"):weight*100+"% project"}
            </div>}
            </>;
          })()}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, marginBottom:6 }}>
            {/* Date only — no time. `dealDate` is a date-only String ("YYYY-MM-DD"); `new Date()` parses that as midnight UTC, so a time formatter here printed a fabricated 03:00 (02:00 in winter) — the Cairo offset, not data. The event moment lives on createdAt / Eoi.convertedAt if it is ever wanted. */}
            <span style={{ fontSize:11, color:C.textLight }}>🗓 {(function(){var dd=getDealDate(d);return dd?fmtDayOnly(dd):"-";})()}</span>
          </div>
          {/* Agent + split partner on their own line (owner brief 2026-10-27):
              "👤 Youssef Salama · 🤝 Mariam Yasser", 12px, amber as before. A
              sales viewer sees the partner alone, as the EOI card does. */}
          {isAdmin
            ?<div style={{ display:"flex", justifyContent:"space-between", gap:8, marginBottom:4, fontSize:12 }}>
              <span style={{ color:C.accent, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>👤 {getAg(d)}{legacyTag(d)}{(function(){var sp=getDealSplitFromObj(d);return sp?" · 🤝 "+sp.agent2Name:"";})()}</span>
              <span style={{ color:C.textLight, fontSize:11, flexShrink:0 }}>📢 <span style={sourceNameStyle(d.source)}>{d.source||"-"}</span></span>
            </div>
            :(function(){var sp=getDealSplitFromObj(d);return (sp&&splitPartnerName(sp))?<div style={{ marginBottom:4, fontSize:12, color:C.accent }}>🤝 {splitPartnerName(sp)}</div>:null;})()}
          {canSeeClosingCompany(p.cu)&&<div style={{ fontSize:11, color:C.textLight, marginBottom:4 }}>🏢 Closed via: <span style={{ fontWeight:600, color:C.text }}>{ccNameOf(d)||"—"}</span></div>}
          {canSeeDeveloper(p.cu)&&devNameOf(d)&&<div style={{ fontSize:11, color:C.textLight, marginBottom:4 }}>🏗️ Developer: <span style={{ fontWeight:600, color:C.text }}>{devNameOf(d)}</span></div>}
          {isOnlyAdmin&&<div style={{ display:"flex", gap:8, marginTop:10, paddingTop:10, borderTop:"1px solid #E8ECF1" }}>
            {p.navigateToCommission&&<button onClick={function(e){e.stopPropagation();p.navigateToCommission(gid(d));}} title="View Commission"
              style={{ flex:1, height:34, borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>💰</button>}
            <button onClick={function(e){e.stopPropagation();setSplitModal(d);var sp=getDealSplitFromObj(d);setSplitAgent2(sp?sp.agent2Id:"");}} title="Split Deal"
              style={{ flex:1, height:34, borderRadius:8, border:"1px solid "+(getDealSplitFromObj(d)?"#8B5CF6":"#E2E8F0"), background:getDealSplitFromObj(d)?"#F5F3FF":"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>🤝</button>
            <button onClick={function(e){e.stopPropagation();setEditDeal(d);}} title={t.edit}
              style={{ flex:1, height:34, borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><Edit size={14} color={C.info}/></button>
            <button onClick={function(e){e.stopPropagation();archiveDeal(gid(d));}} title={t.archive}
              style={{ flex:1, height:34, borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><Archive size={14} color={C.warning}/></button>
          </div>}
        </div>;
      })}
    </div>:<div style={{ overflowX:"auto" }}><table data-table-align="sheet" style={{ width:"100%", borderCollapse:"collapse", minWidth:700 }}>
      <thead><tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
        {dealHeaderCols.map(function(h,i){return <th key={i} style={Object.assign({ textAlign:"left", padding:"11px 12px", fontSize:11, fontWeight:600, color:C.textLight, whiteSpace:"nowrap" }, (h===t.phone||h===t.phone2)?PHONE_COL_PIN:(h==="Approved"?APPROVED_COL_PIN:null))}>{h}</th>;})}      </tr></thead>
      <tbody>
        {dealsLoaded&&filteredDeals.length===0&&<tr><td colSpan={dealColCount}>{renderDealsEmpty()}</td></tr>}
        {filteredDeals.map(function(d){
          var bv=parseBudget(d.budget);
          var isSel=selectedDeal&&rowKey(selectedDeal)===rowKey(d);
          var isLanding=dealFlashId===rowKey(d);
          // A <tr> cannot carry a ring border without breaking the table's own
          // borders, so the flash is an inset box-shadow — same accent, no
          // layout shift, and it does not fight the selected-row tint under it.
          return <Fragment key={rowKey(d)}><tr id={"deal-row-"+rowKey(d)} data-deal-row="1" onClick={function(){setSelectedDeal(isSel?null:d);}} style={{ borderBottom:"1px solid #F1F5F9", cursor:"pointer", background:isLanding?C.accent+"1A":(isSel?"#EFF6FF":"transparent"), boxShadow:isLanding?"inset 0 0 0 2px "+C.accent:"none", transition:"background .3s, box-shadow .3s" }}>
            <td style={{ padding:"11px 12px", fontSize:13, fontWeight:600, textAlign:"left", whiteSpace:"nowrap" }}>
              {resalePartyLabel(d) || d.name}{d.saleType==="Resale" && <span style={{ marginLeft:6, fontSize:9, padding:"1px 6px", borderRadius:9, background:"#FEE2E2", color:"#B91C1C", fontWeight:700, verticalAlign:"middle" }}>🔁 Resale</span>}
              {/* Phase R-14 — Ambassador badge. The classification is internal to
                  admin/operation: gated by isOnlyAdmin so sales/TL/manager/
                  director see a plain deal row with no Ambassador indicator. */}
              {isOnlyAdmin && d.dealType==="ambassador" && <span style={{ marginLeft:6, fontSize:9, padding:"1px 6px", borderRadius:9, background:"#FEF3C7", color:"#B45309", fontWeight:700, verticalAlign:"middle" }}>🎯 Ambassador</span>}
              {canSeeLeadIds(p.cu) && <div style={{ fontSize:10, fontWeight:400, color:C.textLight, marginTop:1 }}>{txnRowLabel(d)}</div>}
            </td>
            {/* 2026-10-29 (owner task): Phone + Alt. Phone are ONE pinned cell —
                phone line 1, alt phone muted line 2 ("—" when empty). The
                phone2!==phone guard stays (827 leads store phone2 == phone). */}
            <td style={Object.assign({ padding:"11px 12px", fontSize:12, direction:"ltr", textAlign:"left" }, PHONE_COL_PIN)}>
              <StackedCell top={<PhoneCell phone={d.phone}/>} bottom={d.phone2&&d.phone2!==d.phone?<PhoneCell phone={d.phone2}/>:null}/>
            </td>
            {/* Project line 1, developer muted line 2 (same canSeeDeveloper gate
                the Developer column had). */}
            <td style={{ padding:"11px 12px", fontSize:12, color:C.textLight, textAlign:"left", whiteSpace:"nowrap" }}>
              {canSeeDeveloper(p.cu)?<StackedCell top={d.project||"-"} bottom={devNameOf(d)||null} bottomStyle={{ whiteSpace:"normal" }}/>:(d.project||"-")}
            </td>
            <td style={{ padding:"11px 12px", fontSize:13, fontWeight:700, color:C.success, textAlign:"left" }}>
              {(function(){
                var split=getDealSplitFromObj(d);
                var weight=getProjectWeight(d.project,d);
                var splitFactor=split?0.5:1;
                var effectiveBv=bv*weight*splitFactor;
                var showEffective=effectiveBv!==bv&&bv>0;
                var isSalesRole=positionOf(p.cu)==="sales"||positionOf(p.cu)==="team_leader";
                // Sales sees effective amount only, admin sees both
                if(isSalesRole&&showEffective){
                  // OWNER RULING R-T2 (2026-09-10) — THE PARTNER SHOWN IS THE STORED
                  // PARTNER, NEVER DERIVED FROM WHO IS LOOKING.
                  //
                  // This used to read "whoever is not me": the primary saw the split
                  // partner, the split partner saw the primary. That is only correct
                  // while the viewer IS one of the two, and on this page that
                  // assumption is false. The Deals list is scoped by who CLOSED the
                  // deal (dealAgentId), which is a THIRD person whenever the lead
                  // kept rotating after the sale — the else-branch then fell through
                  // to `primaryName`, the lead's current holder, who is not on the
                  // deal at all.
                  //
                  // Deal #50059 is the measured case (2026-09-09): closed by Jana
                  // Mohamed, stored partner Ahmed Abdelkader, lead held today by
                  // Rokaya Swidan — a SUSPENDED account. Jana's own row rendered
                  // "🤝 +Rokaya Swidan". The name was not stale, it was invented.
                  //
                  // getDealSplitFromObj has already resolved the stored pair. Name
                  // it. A display that has to ask "who am I?" to answer "who is on
                  // this deal?" is answering a different question.
                  var otherName=splitPartnerName(split);
                  return <div>
                    <div style={{ color:C.success }}>{effectiveBv.toLocaleString()}</div>
                    <div style={{ fontSize:10, color:C.textLight, marginTop:1 }}>من {bv.toLocaleString()}</div>
                    {split&&otherName&&<div style={{ fontSize:10, color:"#8B5CF6", fontWeight:600, marginTop:1 }}>🤝 +{otherName}</div>}
                  </div>;
                }
                return <div>
                  <div>{bv>0?bv.toLocaleString():d.budget||"-"}</div>
                  {!isSalesRole&&showEffective&&<div style={{ fontSize:10, color:"#8B5CF6", fontWeight:600, marginTop:1 }}>
                    {split?"🤝":"📊"} {effectiveBv.toLocaleString()} {split?"50% — "+(split.agent2Name||"Shared"):weight*100+"% project"}
                  </div>}
                </div>;
              })()}
            </td>
            {/* Date only — no time. `dealDate` is a date-only String ("YYYY-MM-DD"); `new Date()` parses that as midnight UTC, so a time formatter here printed a fabricated 03:00 (02:00 in winter) — the Cairo offset, not data. The event moment lives on createdAt / Eoi.convertedAt if it is ever wanted. */}
            <td style={{ padding:"11px 12px", fontSize:11, color:C.textLight, whiteSpace:"nowrap", textAlign:"left" }}>{(function(){var dd=getDealDate(d);return dd?fmtDayOnly(dd):"-";})()}</td>
            {canSeeClosingCompany(p.cu)&&<td style={{ padding:"11px 12px", fontSize:12, color:C.textLight, textAlign:"left" }}>{ccNameOf(d)||"—"}</td>}
            {isAdmin&&<td style={{ padding:"11px 12px", fontSize:12, textAlign:"left", whiteSpace:"nowrap" }}>
              <div>{getAg(d)}{legacyTag(d)}</div>
              {(function(){var sp=getDealSplitFromObj(d);return sp?<div style={{ fontSize:10, color:"#8B5CF6", marginTop:2 }}>🤝 +{sp.agent2Name}</div>:null;})()}
            </td>}
            {/* Source NAME coloured through the shared SOURCE_COLOR map (2026-10-29);
                an unmapped value keeps the cell's default colour and weight. */}
            {isAdmin&&<td style={Object.assign({ padding:"11px 12px", fontSize:12, color:C.textLight, textAlign:"left" }, sourceNameStyle(d.source))}>{d.source}</td>}
            <td style={Object.assign({ padding:"11px 12px", textAlign:"left" }, APPROVED_COL_PIN)}>
              {isDealCancelledRow(d)
                ?<span style={{ background:"#FEE2E2", color:"#B91C1C", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>❌ Cancelled</span>
                :d.dealApproved
                ?<span style={{ background:"#DCFCE7", color:"#15803D", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>✅</span>
                :<span style={{ background:"#FEF9C3", color:"#B45309", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>⏳</span>}
              {isOnlyAdmin&&d.commissionClaimDate&&<div style={{ fontSize:9, color:C.textLight, marginTop:2 }}>📋 {new Date(d.commissionClaimDate).toLocaleDateString("en-GB")}</div>}
              {isOnlyAdmin&&d.commissionClaimed&&<div style={{ fontSize:9, color:C.success, marginTop:1 }}>✅ Claimed</div>}
            </td>
            <td style={{ padding:"8px 12px" }}>
              <div style={{ display:"flex", gap:5 }}>
                {isOnlyAdmin&&p.navigateToCommission&&<button onClick={function(e){e.stopPropagation();p.navigateToCommission(gid(d));}} title="View Commission"
                  style={{ width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>💰</button>}
                {isOnlyAdmin&&<button onClick={function(){setSplitModal(d);var sp=getDealSplitFromObj(d);setSplitAgent2(sp?sp.agent2Id:"");}} title="Split Deal"
                  style={{ width:28, height:28, borderRadius:6, border:"1px solid "+(getDealSplitFromObj(d)?"#8B5CF6":"#E2E8F0"), background:getDealSplitFromObj(d)?"#F5F3FF":"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12 }}>🤝</button>}
                {isOnlyAdmin&&<button onClick={function(){setEditDeal(d);}} title={t.edit}
                  style={{ width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <Edit size={13} color={C.info}/>
                </button>}
                {isOnlyAdmin&&<button onClick={function(){archiveDeal(gid(d));}} title={t.archive}
                  style={{ width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <Archive size={13} color={C.warning}/>
                </button>}
              </div>
            </td>
          </tr>
          {isSel && !p.isMobile && hasRenderableDeal && <tr><td colSpan={dealColCount} style={{ padding:"8px 12px", background:C.bg }}>{renderDealPanel({ width:"100%", background:"#fff", borderRadius:0 })}</td></tr>}
          </Fragment>;
        })}
      </tbody>
    </table></div>}</Card>

    {/* Mobile: full-screen overlay panel (unchanged). Desktop renders the
        panel inline as a full-width accordion row under the selected deal row
        — see renderDealPanel() call inside the table body above. Exactly one
        of the two mounts per render (p.isMobile gate), so dealPanelRef binds a
        single node and click-outside close keeps working. */}
    {hasRenderableDeal&&p.isMobile&&renderDealPanel({ position:"fixed", inset:0, zIndex:300, background:"#fff", overflowY:"auto" })}
    </div>
  </div>;
};

var ArchivePage = function(p) {
  var t=p.t; var isAdmin=p.cu.role==="admin"||p.cu.role==="operation"||positionOf(p.cu)==="director"||positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader";
  var isOnlyAdmin=p.cu.role==="admin";
  var [archivedDR,setArchivedDR]=useState([]);
  // Mirror-pair de-dupe: when both sides of a DR↔Lead pair are archived
  // (which is now always the case after the cascade endpoints), only show
  // the DR row in the DRs table and hide the mirror Lead from the leads
  // table — the DR is the original record, the Lead is its mirror. The
  // user's restore/delete on the DR cascades back to the Lead.
  var archivedDRPhones = useMemo(function(){
    var s = new Set();
    archivedDR.forEach(function(r){ if (r && r.phone) s.add(String(r.phone)); });
    return s;
  }, [archivedDR]);
  // Memoized so an unrelated parent re-render (sidebar nav, modal toggle,
  // socket-driven leads refresh that doesn't change reference identity) does
  // NOT re-walk the full leads list. The old plain filter ran on every
  // render and was the main reason the Archive page felt slow on every
  // action — most renders weren't changing p.leads at all.
  // STEP 4-5 X2.5 — server-fetched archived leads. Replaces the in-memory
  // p.leads.filter(archived) which would return ~0 rows after the X3
  // bootstrap shrink (bootstrap sorts by createdAt desc, so the 100 most
  // recent are almost always non-archived). Endpoint is /api/leads with
  // ?archived=true; same role-scoping as the list. Keyed on cbBust so
  // archive/unarchive mutations refresh the list in real time.
  var [archivedLeadsData, setArchivedLeadsData] = useState(null);
  useEffect(function(){
    if (!p.token) return;
    var cancelled = false;
    // NO CEILING — was ?limit=5000. Archived leads only ever grow, so a constant
    // here is a stop waiting to happen; fetchAllPages walks every page instead.
    fetchAllPages("/api/leads?archived=true&fields=summary&codes=1", p.token)
      .then(function(data){
        if (cancelled) return;
        // createdAt desc = the server's default list sort, restored over the
        // whole book (the keyset walk delivers _id order). See byDatesDesc.
        setArchivedLeadsData(Array.isArray(data) ? data.slice().sort(byDatesDesc(["createdAt"])) : []);
      })
      .catch(function(){});
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, p.cbBust]);
  var archived = useMemo(function(){
    // Prefer the server-fetched archived list once resolved; fall back to
    // the in-memory p.leads scan during the first paint.
    var source = archivedLeadsData || p.leads;
    return source.filter(function(l){
      if (!l.archived) return false;
      if (l.source === "Daily Request" && l.phone && archivedDRPhones.has(String(l.phone))) return false;
      return true;
    });
  }, [archivedLeadsData, p.leads, archivedDRPhones]);
  useEffect(function(){
    // Load archived DRs from the server (the API returns all DRs regardless of archived flag).
    apiFetch("/api/daily-requests","GET",null,p.token)
      .then(function(data){
        var all=Array.isArray(data)?data:[];
        var legacyIds=[]; try{legacyIds=JSON.parse(localStorage.getItem("crm_dr_archived")||"[]");}catch(e){}
        setArchivedDR(all.filter(function(r){return r.archived || legacyIds.includes(gid(r));}));
      }).catch(function(){setArchivedDR([]);});
  },[]);
  var restoreDR=async function(rid){
    try{ await apiFetch("/api/daily-requests/"+rid+"/unarchive","PUT",null,p.token); }catch(e){}
    // Also clean the legacy localStorage list so restored items don't re-disappear
    try{
      var ids=JSON.parse(localStorage.getItem("crm_dr_archived")||"[]");
      ids=ids.filter(function(x){return x!==rid;});
      localStorage.setItem("crm_dr_archived",JSON.stringify(ids));
    }catch(e){}
    setArchivedDR(function(prev){return prev.filter(function(r){return gid(r)!==rid;});});
  };
  var restore=async function(lid){
    try{
      // Dedicated /unarchive endpoint (server.js) cascades archived:false to
      // the linked DR mirror. Generic PUT {archived:false} would skip the
      // cascade, leaving the DR archived even after the lead came back.
      await apiFetch("/api/leads/"+lid+"/unarchive","PUT",null,p.token);
      p.setLeads(function(prev){return prev.map(function(l){return gid(l)===lid?Object.assign({},l,{archived:false}):l;});});
      // Mirror cascade also un-archives the DR; refresh local DR state if any
      // is held by the parent so the Daily Requests page reflects it on next
      // navigation. Server emits dr_updated which the WS handler picks up;
      // this is just a belt-and-braces local nudge.
    }catch(e){alert(e.message);}
  };
  var canDelete = p.cu.role==="admin" || p.cu.role==="operation";
  // Restoring a Daily Request is the Owner's alone as of 2026-09-01 — the same
  // ruling that made archiving owner-only, and for the same reason: unarchiving
  // cascades archived:false onto the mirror Lead. PUT /api/daily-requests/:id/
  // unarchive now wears requireOwner, so an operation user pressing Restore
  // would get a 403 with no explanation. This hides the button instead.
  // Deliberately NOT applied to the DR Delete controls: DELETE
  // /api/daily-requests/:id still wears adminOnly and was not part of that ruling.
  var canRestoreDR = isOwnerFE(p.access);
  var deleteLead=async function(lid){
    if(!window.confirm("Delete this record permanently? This cannot be undone.")) return;
    try{
      await apiFetch("/api/leads/"+lid,"DELETE",null,p.token);
      p.setLeads(function(prev){return prev.filter(function(l){return gid(l)!==lid;});});
    }catch(e){alert(e.message);}
  };
  var deleteDR=async function(rid){
    if(!window.confirm("Delete this record permanently? This cannot be undone.")) return;
    try{
      await apiFetch("/api/daily-requests/"+rid,"DELETE",null,p.token);
      try{
        var ids=JSON.parse(localStorage.getItem("crm_dr_archived")||"[]");
        ids=ids.filter(function(x){return x!==rid;});
        localStorage.setItem("crm_dr_archived",JSON.stringify(ids));
      }catch(e){}
      setArchivedDR(function(prev){return prev.filter(function(r){return gid(r)!==rid;});});
    }catch(e){alert(e.message);}
  };
  // ===== HEADER BULK ACTIONS =====
  // Both act on `archived` — the exact array the leads table below renders.
  // That array comes from /api/leads?archived=true, which runs through
  // buildLeadScopeQuery server-side, and has already had DR mirrors de-duped
  // out. Neither button can reach a row the caller cannot already see, and
  // neither touches the Archived Daily Requests table.
  // Gated on isOnlyAdmin, same as Clear All: PUT /api/leads/:id/unarchive is
  // admin/operation only server-side, and the Leads page gates its own Export
  // Excel on role==="admin".
  var [busy,setBusy]=useState("");
  // ===== SELECTION — two tables, two independent sets =====
  // Kept separate because the two tables' actions hit different endpoints
  // (/api/leads/:id/unarchive + /api/leads/bulk-delete vs
  // /api/daily-requests/:id/unarchive + DELETE /api/daily-requests/:id), so one
  // merged set would have to be re-split before every action anyway. Plain
  // useState: navigating away unmounts ArchivePage, so a selection never
  // survives a page change and nothing persists it.
  var [selLeads,setSelLeads]=useState([]);
  var [selDRs,setSelDRs]=useState([]);
  var clearSelection=function(){ setSelLeads([]); setSelDRs([]); };
  // The EFFECTIVE selection is always intersected with what each table is
  // actually rendering. The per-row Restore/Delete buttons are not bulk actions
  // and so don't clear the selection — without this intersection the "(N)"
  // counter would keep counting rows that are already gone, and the action
  // would send ids the user can no longer see.
  var selLeadIds=useMemo(function(){
    var present={}; archived.forEach(function(l){present[gid(l)]=true;});
    return selLeads.filter(function(id){return present[id];});
  },[selLeads,archived]);
  var selDRIds=useMemo(function(){
    var present={}; archivedDR.forEach(function(r){present[gid(r)]=true;});
    return selDRs.filter(function(id){return present[id];});
  },[selDRs,archivedDR]);
  var selLeadSet=useMemo(function(){return new Set(selLeadIds);},[selLeadIds]);
  var selDRSet=useMemo(function(){return new Set(selDRIds);},[selDRIds]);
  // Derived from the selection (not held as its own state) so the header box can
  // never desync from the rows — the bug the Leads page hit at ~L10300 when its
  // select-all was uncontrolled and a second click re-selected instead of clearing.
  var allLeadsSel=archived.length>0&&archived.every(function(l){return selLeadSet.has(gid(l));});
  var allDRsSel=archivedDR.length>0&&archivedDR.every(function(r){return selDRSet.has(gid(r));});
  var toggleId=function(setter){
    return function(id){
      setter(function(prev){return prev.indexOf(id)>=0?prev.filter(function(x){return x!==id;}):prev.concat([id]);});
    };
  };
  var toggleLead=toggleId(setSelLeads);
  var toggleDR=toggleId(setSelDRs);
  // Export the two tables exactly as rendered. Sheet "Archive" = the leads
  // table's five columns; sheet "Archived DRs" = the DR table's five, which are
  // Name / Phone / Location / Budget / Status — that table has no Project and no
  // Agent column, so neither is invented here. Reuses loadXLSX(), the same
  // SheetJS 0.18.5 build the Leads page export loads, so this adds no
  // dependency. Built inline rather than through exportLeadsToExcel() because
  // that helper emits its own fixed 14-column lead sheet; the Daily Requests
  // export at ~L19063 is the same pattern.
  var exportArchived=async function(){
    if(busy) return;
    setBusy("export");
    try{
      var XLSX=await loadXLSX();
      var stats=STATUSES(t);
      var rows=archived.map(function(l){
        var so=stats.find(function(s){return s.value===l.status;})||stats[0];
        return {
          "Name": l.name||"",
          "Phone": l.phone||"",
          // Alt. Phone (2026-08-26) — same column, same name, same position as
          // the Leads sheet. 24 of the 29 archived leads carry one, and an
          // archived record is exactly the case where the second number is the
          // one still worth calling. Blank when absent; the payload is
          // ?fields=summary, which keeps both phone fields.
          "Alt. Phone": altPhoneCell(l.phone, l.phone2),
          "Project": l.project||"",
          // The ACTIONS this archived client holds (ID RULE 2). Same column and
          // same shape as the Leads sheet, so the two files agree; blank for a
          // client who never transacted, which is most of an archive.
          "ID": (l._actionCodes||[]).map(formatLeadId).join(", "),
          "Status": so.label,
          "Agent": userNameOf(l.agentId),
        };
      });
      var ws=XLSX.utils.json_to_sheet(rows);
      var wb=XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb,ws,"Archive");
      // Second sheet — archived DRs. Explicit `header` so the sheet still
      // carries its column row when archivedDR is empty (the export button is
      // gated on archived.length, not on the DR list, so an empty DR table is a
      // normal case) and so column order can't drift from the table.
      var drCols=["Name","Phone","Alt. Phone","Location","Budget","Status"];
      var drRows=archivedDR.map(function(r){
        var so=stats.find(function(s){return s.value===r.status;})||stats[0];
        return {
          "Name": r.name||"",
          "Phone": r.phone||"",
          "Alt. Phone": altPhoneCell(r.phone, r.phone2),
          "Location": r.area||"",
          "Budget": r.budget||"",
          "Status": so.label,
        };
      });
      XLSX.utils.book_append_sheet(wb,XLSX.utils.json_to_sheet(drRows,{header:drCols}),"Archived DRs");
      XLSX.writeFile(wb,"archive_"+new Date().toISOString().slice(0,10)+".xlsx");
    }catch(e){alert(e.message);}
    finally{setBusy("");}
  };
  // Restore every listed archived lead. There is NO bulk-unarchive endpoint —
  // the only /api/leads bulk routes are bulk-delete, bulk-reassign and
  // bulk-redistribute-backlog — so this drives the same single-lead
  // PUT /api/leads/:id/unarchive the per-row Restore uses. That endpoint is the
  // one place the DR-mirror cascade, commission revive and re-seal live, so
  // going through it is what keeps a bulk restore identical to N single ones.
  // Chunked instead of one Promise.all over the whole book: the archived list
  // only ever grows, and firing every request at once would both hammer the
  // backend and race those cascades.
  var RESTORE_CHUNK=8;
  // Shared chunk runner for every per-id bulk path on this page. Returns the ids
  // that succeeded; a rejected call is counted as a failure rather than thrown,
  // so one bad row can never abort the rest of the batch.
  var runChunked=async function(ids,fn){
    var done=[];
    for(var i=0;i<ids.length;i+=RESTORE_CHUNK){
      var res=await Promise.all(ids.slice(i,i+RESTORE_CHUNK).map(function(id){
        return fn(id).then(function(){return id;}).catch(function(){return null;});
      }));
      res.forEach(function(id){if(id) done.push(id);});
    }
    return done;
  };
  var idSet=function(ids){ var m={}; ids.forEach(function(x){m[x]=true;}); return m; };
  var reportFailures=function(done,ids,noun,verb){
    if(done.length!==ids.length) alert((ids.length-done.length)+" of "+ids.length+" "+noun+" could not be "+verb+".");
  };
  var unarchiveLead=function(lid){ return apiFetch("/api/leads/"+lid+"/unarchive","PUT",null,p.token); };
  var unarchiveDR=function(rid){ return apiFetch("/api/daily-requests/"+rid+"/unarchive","PUT",null,p.token); };
  var deleteDRReq=function(rid){ return apiFetch("/api/daily-requests/"+rid,"DELETE",null,p.token); };
  // Restored leads leave the archive list; deleted ones leave both lists. Both
  // also update archivedLeadsData, because `archived` reads that first — a
  // p.setLeads-only update would leave the rows on screen until the next cbBust
  // refetch.
  var applyLeadsRestored=function(done){
    var m=idSet(done);
    p.setLeads(function(prev){return prev.map(function(l){return m[gid(l)]?Object.assign({},l,{archived:false}):l;});});
    setArchivedLeadsData(function(prev){
      if(!Array.isArray(prev)) return prev;
      return prev.filter(function(l){return !m[gid(l)];});
    });
  };
  var applyLeadsDeleted=function(ids){
    var m=idSet(ids);
    p.setLeads(function(prev){return prev.filter(function(l){return !m[gid(l)];});});
    setArchivedLeadsData(function(prev){
      if(!Array.isArray(prev)) return prev;
      return prev.filter(function(l){return !m[gid(l)];});
    });
  };
  // Mirrors restoreDR/deleteDR: drop the ids from the legacy localStorage
  // archive list too, or a restored DR re-disappears on the next load.
  var applyDRsRemoved=function(ids){
    var m=idSet(ids);
    try{
      var cur=JSON.parse(localStorage.getItem("crm_dr_archived")||"[]");
      localStorage.setItem("crm_dr_archived",JSON.stringify(cur.filter(function(x){return !m[x];})));
    }catch(e){}
    setArchivedDR(function(prev){return prev.filter(function(r){return !m[gid(r)];});});
  };
  var restoreAll=async function(){
    if(busy) return;
    var ids=archived.map(function(l){return gid(l);});
    if(!ids.length) return;
    if(!window.confirm("Restore all "+ids.length+" leads to active?")) return;
    setBusy("restore");
    var done=await runChunked(ids,unarchiveLead);
    applyLeadsRestored(done);
    clearSelection();
    setBusy("");
    reportFailures(done,ids,"leads","restored");
  };
  // ===== SELECTION ACTIONS =====
  // Same endpoints and the same chunking as the act-on-everything buttons above,
  // scoped to the selected ids instead of the whole array. Every one of them
  // clears the selection when it finishes, success or partial failure.
  var restoreSelectedLeads=async function(){
    if(busy) return;
    var ids=selLeadIds.slice();
    if(!ids.length) return;
    if(!window.confirm("Restore "+ids.length+" selected lead"+(ids.length===1?"":"s")+" to active?")) return;
    setBusy("restoreSelLeads");
    var done=await runChunked(ids,unarchiveLead);
    applyLeadsRestored(done);
    clearSelection();
    setBusy("");
    reportFailures(done,ids,"leads","restored");
  };
  // Reuses the SAME bulk endpoint Clear All uses: it already takes an explicit
  // id list, and server-side it matches `{_id:{$in:ids}, archived:true}`, so a
  // non-archived lead can't be deleted through it even if an id leaked in.
  var deleteSelectedLeads=async function(){
    if(busy) return;
    var ids=selLeadIds.slice();
    if(!ids.length) return;
    if(!window.confirm("Delete "+ids.length+" selected lead"+(ids.length===1?"":"s")+" permanently? This cannot be undone.")) return;
    setBusy("deleteSelLeads");
    try{
      await apiFetch("/api/leads/bulk-delete","POST",{ids:ids},p.token);
      applyLeadsDeleted(ids);
    }catch(e){alert(e.message);}
    clearSelection();
    setBusy("");
  };
  var restoreSelectedDRs=async function(){
    if(busy) return;
    var ids=selDRIds.slice();
    if(!ids.length) return;
    if(!window.confirm("Restore "+ids.length+" selected daily request"+(ids.length===1?"":"s")+" to active?")) return;
    setBusy("restoreSelDRs");
    var done=await runChunked(ids,unarchiveDR);
    applyDRsRemoved(done);
    clearSelection();
    setBusy("");
    reportFailures(done,ids,"daily requests","restored");
  };
  // No bulk DR delete route exists — bulk-reassign is the only
  // /api/daily-requests bulk path — so this chunks the same single DELETE the
  // per-row button uses. Same shape Clear All uses for DRs, but bounded rather
  // than one unbounded Promise.all over the whole list.
  var deleteSelectedDRs=async function(){
    if(busy) return;
    var ids=selDRIds.slice();
    if(!ids.length) return;
    if(!window.confirm("Delete "+ids.length+" selected daily request"+(ids.length===1?"":"s")+" permanently? This cannot be undone.")) return;
    setBusy("deleteSelDRs");
    var done=await runChunked(ids,deleteDRReq);
    applyDRsRemoved(done);
    clearSelection();
    setBusy("");
    reportFailures(done,ids,"daily requests","deleted");
  };
  return <div style={{ padding:"18px 16px 40px" }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
      <h2 style={{ margin:0, fontSize:18, fontWeight:700 }}>{t.archive} ({archived.length})</h2>
      <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", justifyContent:"flex-end" }}>
      {isOnlyAdmin&&archived.length>0&&<Btn outline onClick={exportArchived} disabled={!!busy} style={{ padding:"7px 11px", fontSize:12, color:C.success, borderColor:C.success }}><FileSpreadsheet size={13}/> {busy==="export"?"Exporting…":t.exportExcel}</Btn>}
      {isOnlyAdmin&&archived.length>0&&<Btn outline onClick={restoreAll} disabled={!!busy} style={{ padding:"7px 11px", fontSize:12, color:C.warning, borderColor:C.warning }}><RotateCcw size={13}/> {busy==="restore"?"Restoring…":"Restore All"}</Btn>}
      {isOnlyAdmin&&(archived.length>0||archivedDR.length>0)&&<Btn outline disabled={!!busy} onClick={async function(){
        if(!window.confirm("Clear all archived items? This cannot be undone.")) return;
        // Bulk delete archived leads
        try{
          var leadIds=archived.map(function(l){return gid(l);});
          if(leadIds.length>0) await apiFetch("/api/leads/bulk-delete","POST",{ids:leadIds},p.token);
          p.setLeads(function(prev){return prev.filter(function(l){return !l.archived;});});
        }catch(e){}
        // Bulk delete DR
        try{
          await Promise.all(archivedDR.map(function(r){return apiFetch("/api/daily-requests/"+gid(r),"DELETE",null,p.token).catch(function(){});}));
        }catch(e){}
        try{localStorage.removeItem("crm_dr_archived");}catch(e){}
        setArchivedDR([]);
        clearSelection();
      }} style={{ padding:"7px 13px", fontSize:12, color:C.danger, borderColor:C.danger }}>🗑 Clear All</Btn>}
      </div>
    </div>
    {archived.length===0&&<div style={{ textAlign:"center", padding:30, color:C.textLight }}>No archived leads</div>}
    {isOnlyAdmin&&selLeadIds.length>0&&<div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:10 }}>
      <Btn outline disabled={!!busy} onClick={restoreSelectedLeads} style={{ padding:"7px 11px", fontSize:12, color:C.warning, borderColor:C.warning }}><RotateCcw size={13}/> {busy==="restoreSelLeads"?"Restoring…":"Restore Selected ("+selLeadIds.length+")"}</Btn>
      <Btn outline disabled={!!busy} onClick={deleteSelectedLeads} style={{ padding:"7px 11px", fontSize:12, color:C.danger, borderColor:C.danger }}><Trash2 size={13}/> {busy==="deleteSelLeads"?"Deleting…":"Delete Selected ("+selLeadIds.length+")"}</Btn>
    </div>}
    {archived.length>0&&<Card p={0} style={{ marginBottom:24 }}><div style={{ overflowX:"auto" }}><table data-table-align="sheet" style={{ width:"100%", borderCollapse:"collapse", minWidth:480 }}>
      <thead><tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
        {isOnlyAdmin&&<th style={{ padding:"11px 8px", width:32 }}><input type="checkbox" checked={allLeadsSel} onChange={function(){setSelLeads(allLeadsSel?[]:archived.map(function(l){return gid(l);}));}}/></th>}
        {/* "Actions" (owner 2026-10-30): an empty label here was dropped by filter(Boolean), so the Restore / Delete column had no header cell */}
        {[t.name,t.phone,t.project,t.status,isAdmin&&t.agent,"Actions"].filter(Boolean).map(function(h){return <th key={h||"x"} style={{ textAlign:"left", padding:"11px 12px", fontSize:11, fontWeight:600, color:C.textLight }}>{h}</th>;})}
      </tr></thead>
      <tbody>{archived.map(function(l){var lid=gid(l);var so=STATUSES(t).find(function(s){return s.value===l.status;})||STATUSES(t)[0];var ag=userNameOf(l.agentId);
        return <tr key={lid} style={{ borderBottom:"1px solid #F1F5F9", opacity:0.7 }}>
          {isOnlyAdmin&&<td style={{ padding:"11px 8px" }} onClick={function(e){e.stopPropagation();toggleLead(lid);}}><input type="checkbox" checked={selLeadSet.has(lid)} readOnly/></td>}
          <td style={{ padding:"11px 12px", fontSize:13, fontWeight:600 }}>{l.name}</td>
          <td style={{ padding:"11px 12px", fontSize:12, direction:"ltr" }}><PhoneCell phone={l.phone}/></td>
          <td style={{ padding:"11px 12px", fontSize:12, color:C.textLight }}>{l.project}</td>
          <td style={{ padding:"11px 12px" }}><Badge bg={so.bg} color={so.color}>{so.label}</Badge></td>
          {isAdmin&&<td style={{ padding:"11px 12px", fontSize:12 }}>{ag}</td>}
          <td style={{ padding:"11px 12px" }}><div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <Btn onClick={function(){restore(lid);}} style={{ padding:"5px 12px", fontSize:11 }}><RotateCcw size={12}/> {t.restore}</Btn>
            {canDelete&&<button onClick={function(){deleteLead(lid);}} title="Delete permanently" style={{ width:28, height:28, borderRadius:6, border:"1px solid "+C.danger, background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><Trash2 size={13} color={C.danger}/></button>}
          </div></td>
        </tr>;
      })}</tbody>
    </table></div></Card>}

    {archivedDR.length>0&&<div>
      <h3 style={{ margin:"0 0 12px", fontSize:15, fontWeight:700, color:C.textLight }}>📋 Archived Daily Requests ({archivedDR.length})</h3>
      {isOnlyAdmin&&selDRIds.length>0&&<div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:10 }}>
        {canRestoreDR&&<Btn outline disabled={!!busy} onClick={restoreSelectedDRs} style={{ padding:"7px 11px", fontSize:12, color:C.warning, borderColor:C.warning }}><RotateCcw size={13}/> {busy==="restoreSelDRs"?"Restoring…":"Restore Selected ("+selDRIds.length+")"}</Btn>}
        <Btn outline disabled={!!busy} onClick={deleteSelectedDRs} style={{ padding:"7px 11px", fontSize:12, color:C.danger, borderColor:C.danger }}><Trash2 size={13}/> {busy==="deleteSelDRs"?"Deleting…":"Delete Selected ("+selDRIds.length+")"}</Btn>
      </div>}
      <Card p={0}><div style={{ overflowX:"auto" }}><table data-table-align="sheet" style={{ width:"100%", borderCollapse:"collapse", minWidth:400 }}>
        <thead><tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
          {isOnlyAdmin&&<th style={{ padding:"11px 8px", width:32 }}><input type="checkbox" checked={allDRsSel} onChange={function(){setSelDRs(allDRsSel?[]:archivedDR.map(function(r){return gid(r);}));}}/></th>}
          {["Name","Phone","Location","Budget","Status",""].map(function(h){return <th key={h||"x"} style={{ textAlign:"left", padding:"11px 12px", fontSize:11, fontWeight:600, color:C.textLight }}>{h}</th>;})}
        </tr></thead>
        <tbody>{archivedDR.map(function(r){var rid=gid(r);var so=STATUSES(t).find(function(s){return s.value===r.status;})||STATUSES(t)[0];
          return <tr key={rid} style={{ borderBottom:"1px solid #F1F5F9", opacity:0.7 }}>
            {isOnlyAdmin&&<td style={{ padding:"11px 8px" }} onClick={function(e){e.stopPropagation();toggleDR(rid);}}><input type="checkbox" checked={selDRSet.has(rid)} readOnly/></td>}
            <td style={{ padding:"11px 12px", fontSize:13, fontWeight:600 }}>{r.name}</td>
            <td style={{ padding:"11px 12px", fontSize:12, direction:"ltr" }}><PhoneCell phone={r.phone}/></td>
            <td style={{ padding:"11px 12px", fontSize:12, color:C.textLight }}>{r.area||"-"}</td>
            <td style={{ padding:"11px 12px", fontSize:12, color:C.success, fontWeight:600 }}>{r.budget||"-"}</td>
            <td style={{ padding:"11px 12px" }}><Badge bg={so.bg} color={so.color}>{so.label}</Badge></td>
            <td style={{ padding:"11px 12px" }}><div style={{ display:"flex", gap:6, alignItems:"center" }}>
              {canRestoreDR&&<Btn onClick={function(){restoreDR(rid);}} style={{ padding:"5px 12px", fontSize:11 }}><RotateCcw size={12}/> Restore</Btn>}
              {canDelete&&<button onClick={function(){deleteDR(rid);}} title="Delete permanently" style={{ width:28, height:28, borderRadius:6, border:"1px solid "+C.danger, background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><Trash2 size={13} color={C.danger}/></button>}
            </div></td>
          </tr>;
        })}</tbody>
      </table></div></Card>
    </div>}
  </div>;
};

// ===== DAILY REQUESTS =====
// One page of Daily Requests. Matches the App-level bootstrap + loadMoreDRs
// limit so both paths request the same window size.
var DR_PAGE_SIZE = 100;

// ===== THE DAILY REQUESTS PAGE IS LIVE OVER WEBSOCKET (2026-10-27) ==========
//
// Until today this page's table read its OWN `requests` list, fetched by an
// effect keyed on the filters alone (cbBust excluded on purpose), and nothing
// merged a `dr_updated` doc into it — the socket handler writes p.dailyReqs,
// which this page never reads. Measured in headless Chrome against production
// (owner on the DR page, agent writes twice): both dr_updated docs arrived and
// the table did not change. Another user's status/feedback change reached an
// open admin page only on a filter change or a reload. Leads has been live
// since the socket shipped because its table reads p.leads, the array the
// handler patches.
//
// ONE PREDICATE, TWO CALLERS. buildFilteredDRs (inside the page) was already a
// client twin of the server's chip / agent / project / property-type /
// location / search narrows — a second pass over the fetched page. It is
// lifted here so (a) a live row is admitted by exactly the predicate the page
// renders with, and (b) verify-leads-window-refresh.js can RUN it. Two things
// the fetched rows carry from the server and a live row does not: the Date
// Added window (sent as from/to on createdAt) — applied here only for live rows
// (`f.win`), identical field and bounds; and a dashboard tile's set
// (?cbOverdue…), which the client cannot evaluate — under a tile a live row is
// never INSERTED (`f.tileActive`), only patched when already on the page.
var drRowMatchesFilters = function(r, f) {
  if (!r || r.archived) return false;
  f = f || {};
  var aid = r.agentId && r.agentId._id ? String(r.agentId._id) : (r.agentId ? String(r.agentId) : "");
  if (f.filterStatus && f.filterStatus !== "all") {
    // "Had Meeting" is the shared OR definition (hadMeeting OR a MeetingDone-
    // family status) — it must mirror the server's drChipQS counterpart exactly.
    if (f.filterStatus === "__hadMeeting") {
      if (!(r.hadMeeting === true || r.status === "Meeting" || r.status === "MeetingDone" || r.status === "Meeting Done")) return false;
    } else if (f.filterStatus === "__noAgent") {
      if (aid) return false;
    } else if (f.filterStatus === "__inactiveAgent") {
      if (!(aid && f.inactiveAgentIds && f.inactiveAgentIds[aid])) return false;
    } else if (r.status !== f.filterStatus) return false;
  }
  // Multi-select filters — [] means "no filter" (selHasId / selMatchName /
  // selMatchLocation all answer true on []). selMatchLocation understands both
  // the managed "loc:<entityKey>" values and legacy free text, folded the way
  // resolveDrLocationVariants does server-side.
  if (f.agentFilter && f.agentFilter.length && !selHasId(f.agentFilter, aid)) return false;
  if (!selMatchName(f.projectFilter || [], r.project)) return false;
  if (!selMatchName(f.propertyTypeFilter || [], r.propertyType)) return false;
  if (!selMatchLocation(f.locationFilter || [], r.area)) return false;
  if (!matchSearch({ name: String(r.name || ""), phone: String(r.phone || ""), phone2: r.phone2 }, f.search)) return false;
  if (f.win) {
    var ct = r.createdAt ? new Date(r.createdAt).getTime() : 0;
    if (ct < f.win[0] || ct > f.win[1]) return false;
  }
  if (f.tileActive) return false;
  return true;
};
// What a batch of live docs does to the held rows. Pure: returns the next
// array and the docs it DEFERRED. A doc for a row the viewer is editing
// (Change Status modal / edit form open on it) is not applied — the caller
// holds it and re-applies it when the editor closes, so a save in progress is
// never built on a row that changed underneath it. An archived doc removes
// its row. A present row is patched in place (the page's memo re-sorts and
// re-narrows, so a row whose status left the active chip leaves the table —
// the same outcome LeadsPage's currentStatus narrow produces). A row not on
// the page is INSERTED only when `matches` admits it, at the front; the memo's
// sort puts it where the current order dictates.
// ONE LIST REQUEST PER BURST (2026-10-27). While the DR page is mounted, the
// App-level window refresh (fetchDRs) is SKIPPED: the page's own page-1 refresh
// + ?ids= verification already answers the burst, and the dashboard tiles that
// read p.dailyReqs are not on screen. Measured on the deployed build before
// this: every empty burst / reconnect on the DR page cost two list requests
// (`page=1&limit=N` from the App and `archived=false&…&sort=` from the page).
// `state` = { mounted, skipped }. A burst skipped while mounted is caught up
// with ONE fetchDRs when the page unmounts; nothing else changes.
var applyDrPageMount = function(on, state, runFetch) {
  state.mounted = !!on;
  if (!on && state.skipped) { state.skipped = false; if (typeof runFetch === "function") runFetch(); return true; }
  return false;
};
var applyDrLiveDocs = function(prev, docs, matches, editingIds) {
  var rows = Array.isArray(prev) ? prev.slice() : [];
  var deferred = [];
  var editing = {};
  (editingIds || []).forEach(function(id){ if (id) editing[String(id)] = true; });
  (docs || []).forEach(function(d){
    var id = d && d._id ? String(d._id) : "";
    if (!id) return;
    if (editing[id]) { deferred.push(d); return; }
    var idx = -1;
    for (var i = 0; i < rows.length; i++) { if (gid(rows[i]) === id) { idx = i; break; } }
    if (d.archived === true) { if (idx >= 0) rows.splice(idx, 1); return; }
    if (idx >= 0) { rows[idx] = d; return; }
    if (matches(d)) rows.unshift(d);
  });
  return { rows: rows, deferred: deferred };
};
// Per-type notification page size. One request per type (never pooled), so this
// is a page size, not a budget shared between types — a high-volume type cannot
// consume another's slots.
var NOTIF_PAGE = 50;
var DailyRequestsPage = function(p) {
  var t=p.t;
  // Today's per-agent receipts — feeds the load column in the agent picker.
  var assignedToday = useAssignedToday(p.token);
  // Dropdown and tabs both drop "Deal Cancelled" from DRs entirely.
  // NewLead is NOT a valid Daily Request status — a DR records an interaction
  // that already happened. It is absent here on purpose, which also keeps it out
  // of the Add form picker and the row status dropdown, both of which read `sc`.
  // (A NewLead chip briefly lived here to make the strip sum to All while 500
  // polluted rows carried the status; the rows are the bug, not the missing chip.)
  var sc=visibleStatuses(DR_STATUSES(t), p.cu&&p.cu.role).filter(function(s){ return s.value!=="Deal Cancelled"; });
  // ===== DR FORM GRID — two per row, one on a phone (2026-09-07) =============
  // The pairing pattern the Deals edit modal uses (LeadForm's `g2`), plus the
  // collapse that form does NOT have: `minmax(0,1fr)` rather than `1fr` so a
  // long option label cannot push a column past its share and overflow the
  // modal, and gap "0 12px" because every field already carries its own 13px
  // bottom margin — a row gap on top of that would double the vertical rhythm.
  // p.isMobile is innerWidth < 768, recomputed on resize (App.js ~L43315), so
  // rotating a phone or narrowing a window re-lays the form out live.
  var drFormGrid = { display:"grid", gridTemplateColumns: p.isMobile ? "minmax(0,1fr)" : "minmax(0,1fr) minmax(0,1fr)", gap:"0 12px" };
  var drFormFull = { gridColumn:"1/-1" };
  var isAdmin=p.cu.role==="admin"||p.cu.role==="operation"||positionOf(p.cu)==="director"||positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader"; var isOnlyAdmin=p.cu.role==="admin"||p.cu.role==="operation";
  // No Agent chip — admin / operation / team_leader / manager. Hidden for
  // sales (their scope is their own assigned book, so it is meaningless) and
  // for director, which is not in the server-side DR_NO_AGENT_ROLES list either;
  // the two must agree or the chip renders against a count the API withholds.
  var canSeeNoAgentChip=p.cu.role==="admin"||p.cu.role==="operation"||positionOf(p.cu)==="team_leader"||positionOf(p.cu)==="manager";
  var salesUsers=p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;});
  // Filter dropdown only — includes inactive agents so admins can filter historical DRs.
  var salesUsersForFilter=(function(){var act=p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;});var ina=p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&!u.active;});return act.concat(ina);})();
  var agentFilterOpts=salesUsersForFilter.map(function(u){ return { value:gid(u), label:u.name+(u.active?"":" (inactive)") }; });
  var [requests,setRequests]=useState([]);
  var [loading,setLoading]=useState(true);
  // Page's own pagination over `requests`. Separate from the App-level
  // drsPage/drsTotalPages, which page p.dailyReqs — a different array that this
  // page does not render (see loadMoreRequests).
  var [drPage,setDrPage]=useState(1);
  // Bumped by the status door when its PUT answers 404 (the request is gone for
  // this caller): the page-1 effect below depends on it and refetches the list.
  var [drRefreshRev,setDrRefreshRev]=useState(0);
  var [drTotalPages,setDrTotalPages]=useState(1);
  var [drLoadingMore,setDrLoadingMore]=useState(false);
  // Server-aggregated chip/card counts over the WHOLE scope. Null until the
  // first response lands; every consumer falls back to the loaded rows until
  // then. Same monotonic-sequence discipline as the Leads badge fetch — see the
  // effect below for why a `cancelled` guard is the wrong tool here.
  var [drCounts,setDrCounts]=useState(null);
  var drCountsReqRef=useRef(0);
  var drCountsAppliedRef=useRef(0);
  // Same discipline for the list fetch itself — see the effect for the warning
  // about adding WS-driven values to its deps.
  var drListReqRef=useRef(0);
  var drListAppliedRef=useRef(0);
  var [showAdd,setShowAdd]=useState(false);
  var [saving,setSaving]=useState(false);
  var [selected,setSelected]=useState(null);
  // Defensive guard mirroring LeadsPage (see hasRenderableSelected at
  // App.js:3586). DRPage has TWO panel paths (mobile inline at the
  // "no requests" branch AND the desktop side-panel Card at the end of
  // the page) — both now gate on hasRenderableSelected so a degenerate
  // `selected` value can't paint a full-screen white overlay (mobile
  // path) or a 320px right-side blank panel (desktop path).
  var hasRenderableSelected = !!(selected && gid(selected) && (selected.name || selected.phone));
  useEffect(function(){
    if (selected && !hasRenderableSelected) {
      // eslint-disable-next-line no-console
      console.warn("[DRPage] Clearing degenerate `selected`:", selected);
      setSelected(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selected, hasRenderableSelected]);
  // Close the DR side panel when the user clicks anywhere outside of it.
  // The same ref is attached to both the mobile and desktop panel renders;
  // only one is mounted at a time so a single ref is enough.
  var drPanelRef = useOutsideClose(hasRenderableSelected, function(){ setSelected(null); });
  var [statusDrop,setStatusDrop]=useState(null);
  var [showStatusComment,setShowStatusComment]=useState(false);
  var [pendingStatus,setPendingStatus]=useState(null);
  // Callback strip clock + reschedule sheet (src/lib/callbackStrip.js, 2026-10-26) — same shape as LeadsPage.
  var cbNow=useMinuteTick();
  var [cbResched,setCbResched]=useState(null);
  var [cbReschedBusy,setCbReschedBusy]=useState(false);
  var [actNote,setActNote]=useState(""); var [actType,setActType]=useState("call"); var [showActForm,setShowActForm]=useState(false);
  var [actSaving,setActSaving]=useState(false);
  var [showDrHistory,setShowDrHistory]=useState(false);
  var [drHistoryReq,setDrHistoryReq]=useState(null);
  var [drHistoryList,setDrHistoryList]=useState([]);
  var [drHistoryLoading,setDrHistoryLoading]=useState(false);
  // Honour a one-shot initial filter from the Sales Dashboard (e.g. CallBack).
  var [filterStatus,setFilterStatus]=useState(p.drInitFilter||"all");
  var [sortBy,setSortBy]=useState("lastActivity");
  // MULTI-SELECT (2026-08-05): an ARRAY of user ids; [] = all agents.
  var [agentFilter,setAgentFilter]=useState([]);
  // "Date Added" filter — client-side over the already-loaded `requests` array
  // (this page loads the full list; createdAt is on every DR). Presets compute
  // browser-local day boundaries, matching the LeadsPage dateRangeWindow
  // convention (App.js:5977) — browser-local == Africa/Cairo on the owner's
  // machine. "custom" reads customFrom/customTo (YYYY-MM-DD, local calendar days).
  // The dashboard Daily Requests tile hands its period in (p.drInit) as a custom
  // Date Added range — seeded here, not applied in an effect, because the nav
  // happens before this page mounts and an "All" first render would fetch the
  // whole book (5,509 rows) before the window replaced it. Consumed below.
  // The Callbacks tile's Daily Request half hands in {cbOverdue:{type,from,to,label}}
  // instead (p.drInit.cbOverdue): the overdue-in-period set, resolved on the server
  // by the tile's own predicate. It narrows like any other filter and says so in
  // the banner; clearing it refetches the plain list.
  var [drTile,setDrTile]=useState(function(){ return (p.drInit && p.drInit.cbOverdue) || null; });
  // The server's total for the list as currently filtered — the number the tile
  // spec's banner prints, so the banner and the rows come from one response.
  var [drListTotal,setDrListTotal]=useState(null);
  var [addedRange,setAddedRange]=useState(function(){ return (p.drInit && p.drInit.from) ? "custom" : "all"; });
  // Phone-only chrome (MobileFilterChrome): whether the floating ＋ speed-dial is open. Presentation state only.
  var [mActionsOpen,setMActionsOpen]=useState(false);
  var [addedQuarter,setAddedQuarter]=useState({ year:new Date().getFullYear(), q:Math.floor(new Date().getMonth()/3)+1 });
  var [customFrom,setCustomFrom]=useState(function(){ return (p.drInit && p.drInit.from) || ""; });
  var [customTo,setCustomTo]=useState(function(){ return (p.drInit && p.drInit.to) || ""; });
  // ===== Project filter (2026-08-04) =====
  // DailyRequest gained a real `project` field in the same change; before that
  // a DR had no development at all (propertyType is the unit type, `area` the
  // location), which is why this page had nothing to filter on.
  //
  // Filtering is CLIENT-SIDE here — unlike the Leads page. This page fetches its
  // whole role-scoped list in one unpaginated request (see the loader below), so
  // the full set is already in memory and a server round-trip would add latency
  // without adding rows. That also makes the option list inherently role-scoped:
  // it is derived from the rows this user already received.
  // "__none" = requests with no project (every DR created before this change).
  // MULTI-SELECT (2026-08-05): an ARRAY of normalized bases; [] = no filter.
  var [projectFilterDR,setProjectFilterDR]=useState([]);
  // Declared here, ABOVE drProjectGroups, on purpose: that memo lists it as a
  // dependency, and dep arrays are evaluated during render. Left further down
  // it would still be the hoisted `undefined` at that point, freezing the dep
  // so the memo never picked up the server options — the same trap documented
  // on the LeadsPage badge-counts effect.
  var [drFilterOpts,setDrFilterOpts]=useState(null); // { drProjects, drLocations }
  // Server-sourced, NOT derived from the loaded rows. It used to walk
  // `requests`, which was fine only while that array held the entire scope; now
  // that the list pages at 100 it would offer whichever projects happened to
  // appear in the newest page and silently hide the rest — and the counts beside
  // each name would be page counts. /api/filter-options?scope=dr already returns
  // drProjects role-scoped with whole-book counts (it is the same fetch the
  // Location dropdown beside it already uses), so read that and fall back to the
  // loaded rows only until it lands.
  var drProjectGroups = useMemo(function(){
    var fromServer = drFilterOpts && Array.isArray(drFilterOpts.drProjects) ? drFilterOpts.drProjects : null;
    if (fromServer) return fromServer;
    var byKey={};
    (requests||[]).forEach(function(r){
      if(!r||r.archived) return;
      var raw=String(r.project||"").trim();
      if(raw===""){ byKey.__none=byKey.__none||{value:"__none",label:"(No project)",count:0}; byKey.__none.count++; return; }
      var base=normProjectFE(raw), key=base.toLowerCase();
      if(!byKey[key]) byKey[key]={value:base,label:base,count:0};
      byKey[key].count++;
    });
    var none=byKey.__none; delete byKey.__none;
    var out=Object.keys(byKey).map(function(k){return byKey[k];})
      .sort(function(a,b){return String(a.label).localeCompare(String(b.label));});
    if(none) out.push(none);
    return out;
  },[requests, drFilterOpts]);
  // ===== Location filter (2026-08-05) =====
  // `area` IS the location: every DR surface (table column, side panel, Excel
  // export, both forms) has always labelled it "Location" — the field just kept
  // its original name. Free text typed by the agent, so "New Cairo" / "new
  // cairo" / "New  Cairo" all exist in the data and must collapse to one option.
  //
  // SERVER-SIDE, unlike the project filter beside it. The selected base goes to
  // GET /api/daily-requests?location=, which expands it back to every raw
  // spelling and ANDs it into the same query object the ?page/?limit slicing and
  // countDocuments() see — so the filter narrows the whole book and stays
  // correct if this page ever paginates, instead of trimming an already-cut
  // page. Everything else on the page (status, agent, date, search, project)
  // then composes on top client-side, so the filters AND together and the status
  // tab counts describe the selected location.
  //
  // Options come from /api/filter-options?scope=dr, NOT from the loaded rows:
  // once the server has filtered the list it contains only the selected
  // location, so deriving the options from it would leave the dropdown with a
  // single choice and no way back. That endpoint is role-scoped by the same
  // rules as this list (?scope=dr skips its expensive lead half entirely).
  // The project dropdown DOES keep deriving from the loaded rows, so picking a
  // location narrows the project options to what that location actually has.
  // "__none" = requests with no location recorded.
  //
  // MULTI-SELECT (2026-08-05): an ARRAY of normalized bases; [] = no filter.
  // Several locations go over the wire as repeated ?location= params (see selQS)
  // and come back as one $in — so "Sahel + Tagamo3" is a single query, not two
  // round-trips merged on the client.
  var [locationFilterDR,setLocationFilterDR]=useState([]);
  // ===== Property Type filter (2026-09-17) =====
  // DailyRequest.propertyType is the "Property Type" table column — a REQUIRED,
  // select-only field, so options are server-driven with zero legacy-fallback
  // scaffolding (unlike the Project filter above, whose drProjectGroups memo
  // falls back to deriving options from the loaded rows for historical reasons
  // that don't apply here). Reads drFilterOpts.drPropertyTypes directly, same
  // simple pattern as Location beside it. "__none" = the 436 sealed DRs (mostly
  // pre-2026-08-08) with no property type recorded.
  var [propertyTypeFilterDR,setPropertyTypeFilterDR]=useState([]);
  useEffect(function(){
    if(!p.token) return;
    var cancelled=false;
    apiFetch("/api/filter-options?scope=dr","GET",null,p.token)
      .then(function(r){ if(!cancelled&&r) setDrFilterOpts(r); })
      .catch(function(){});
    return function(){ cancelled=true; };
  },[p.token]);
  // (drProjectChoices lived here — the name list that fed the two DR form
  // project pickers. Removed 2026-08-08 when both reverted to free text; it had
  // no other consumer. drProjectGroups stays: the Project FILTER still uses it.)
  // Locations (2026-08-07) — the managed vocabulary both DR form pickers bind
  // to. Active-only by design: a deactivated location must not be offerable on
  // a NEW request, while existing rows that already point at it keep resolving
  // (the picker re-adds the current value below so an edit can't silently drop
  // it). locRev forces a refetch when an admin resolves a pending name, which
  // may have minted a brand-new Location that should appear without a reload.
  var [drLocations,setDrLocations]=useState([]);
  var liveLocRev = useLiveRev("location");
  useEffect(function(){
    if(!p.token) return;
    var cancelled=false;
    apiFetch("/api/locations","GET",null,p.token)
      .then(function(d){ if(cancelled) return; var rows=(d&&Array.isArray(d.data))?d.data:(Array.isArray(d)?d:[]); setDrLocations(rows); })
      .catch(function(){});
    return function(){ cancelled=true; };
  },[p.token, p.locationRev, liveLocRev]);
  // Option list for a picker, with `current` (the row's saved value) grafted on
  // when it isn't in the active list — otherwise editing a request whose
  // location was since deactivated, or is still a legacy free-text value, would
  // reset the field to blank the moment the form opens.
  var drLocationOpts = function(current){
    var opts = (drLocations||[]).map(function(l){ return { value:l.name, label:l.name }; });
    var cur = String(current||"").trim();
    if (cur && !opts.some(function(o){ return o.value===cur; })) {
      opts = [{ value:cur, label:cur+" (not in list)" }].concat(opts);
    }
    return [{value:"",label:"- Select -"}].concat(opts);
  };
  useEffect(function(){
    if(p.drInitFilter){ setFilterStatus(p.drInitFilter); if(p.setDrInitFilter) p.setDrInitFilter(null); }
    if(p.drInit){ if(p.setDrInit) p.setDrInit(null); }   // consumed: the page owns its filters from here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
  var [form,setForm]=useState({name:"",phone:"",phone2:"",project:"",propertyType:"",area:"",budget:"",notes:"",agentId:"",callbackTime:"",status:"",dealUnitType:"",dealBudget:"",eoiDeposit:"",eoiDateInput:"",eoiDocFiles:[]});
  // ===== DEVELOPER ON THE NEW-REQUEST FORM (the second door, 2026-09-07) =====
  //
  // THE FIRST DOOR IS THE STATUS MODAL, and it got its developer picker on
  // 2026-08-24 (f3c19a2) together with the server gate that now rejects a
  // developer-less EOI/DoneDeal transition (server.js, "DEVELOPER — ONE GATE,
  // BOTH DOORS"). THIS IS THE OTHER DOOR AND IT WAS MISSED: the New Request
  // form can be submitted straight at EOI / DoneDeal, and `addReq` below turns
  // that into a create-then-PUT two-step whose PUT is exactly the transition the
  // gate guards. It sent no developer, so from 2026-08-24 every EOI or deal
  // registered from the New Request form came back
  // "Developer is required when converting to EOI/Deal" — with no field on the
  // form to satisfy it. Not a degraded path: the DR is created at Potential and
  // the PUT that would advance it is refused, so the agent is stuck.
  //
  // Same shape as the other two pickers (StatusModal ~L4272, LeadForm ~L7185):
  // drDevSelId (a chosen developer) XOR drDevPendName (a typed new name), one of
  // them always "". Distinct names because verify-fe-state.js reads a duplicate
  // useState binding in one function scope as the white-screen defect it is.
  var [drDevSelId,setDrDevSelId]=useState("");
  var [drDevPendName,setDrDevPendName]=useState("");
  var [drDevPendOpen,setDrDevPendOpen]=useState(false);
  var [drDevList,setDrDevList]=useState([]);
  // MUST STAY BELOW `form` — the dep array reads form.status during RENDER, and
  // `var [form,setForm]` is hoisted-but-undefined above its own line. Placed
  // higher (where this was first written) the page throws on mount instead of
  // rendering: a white screen, not a missing dropdown.
  // Developer list — loaded when the New Request form is open AND aimed at
  // EOI/DoneDeal, which is the only status pair that reaches the server gate.
  // Same endpoint and same tolerant unwrapping as the other two pickers; a
  // failed fetch leaves the dropdown empty and the "enter a new name" escape
  // still satisfies the requirement.
  var liveDevRev = useLiveRev("developer");
  useEffect(function(){
    var dealAim = form.status==="EOI"||form.status==="DoneDeal";
    if(!showAdd||!dealAim||!p.token) return;
    var cancelled=false;
    apiFetch("/api/developers","GET",null,p.token).then(function(data){
      if(cancelled) return;
      var rows=(data&&Array.isArray(data.data))?data.data:(Array.isArray(data)?data:[]);
      setDrDevList(rows);
    }).catch(function(){ /* non-fatal: dropdown shows no options */ });
    return function(){ cancelled=true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[showAdd, form.status, p.token, liveDevRev]);
  var [selected2,setSelected2]=useState([]);
  var [showBulk,setShowBulk]=useState(false);
  // Select mode (Bulk Reassign on a phone, where cards have no checkboxes): reuses the desktop selection (selected2) and the
  // existing Bulk Reassign modal (showBulk). The mode ends when that modal closes with nothing left selected (a successful reassign).
  var [mSelectMode, setMSelectMode] = useState(false);
  var mBulkWasOpen = useRef(false);
  useEffect(function(){ if (mSelectMode && !showBulk && mBulkWasOpen.current && selected2.length === 0) setMSelectMode(false); mBulkWasOpen.current = showBulk; }, [showBulk, selected2.length, mSelectMode]);
  var [bulkRows,setBulkRows]=useState([{ agentId:"", count:"" }]); var [bulkBusy,setBulkBusy]=useState(false); var [bulkErr,setBulkErr]=useState("");
  // "Move to Leads as Fresh" (2026-07-08) — bulk-convert selected No-Agent /
  // Inactive-Agent daily requests into fresh New Leads that enter rotation.
  var [showMove,setShowMove]=useState(false);
  var [moving,setMoving]=useState(false);
  var [moveResult,setMoveResult]=useState(null);
  var [moveErr,setMoveErr]=useState("");
  // Admin-only DR edit modal (mirrors LeadsPage side-panel edit). Owner role
  // only — gated on p.cu.role === "admin" at the side-panel button below.
  var [editReq,setEditReq]=useState(null);
  var [editForm,setEditForm]=useState({name:"",phone:"",phone2:"",project:"",propertyType:"",area:"",budget:"",agentId:"",callbackTime:"",status:"",notes:""});
  var [editSaving,setEditSaving]=useState(false);

  // The active chip as list-endpoint query params. Each chip is a targeted
  // server-side fetch, like LeadsPage does — NOT a client filter over whatever
  // rows happen to be loaded. Filtering in memory was correct only while the
  // page held the whole scope; after it started paging at 100 the badges
  // described the book while the rows described page 1, so EOI, Done Deal,
  // Meeting Done, Inactive Agent and Had Meeting all showed zero rows under a
  // non-zero badge. These params compose with ?location and ?from/?to on the
  // server, so a chip click with a month and a location active narrows by all
  // three rather than by whichever one happened to be applied last.
  // NOTE on ?archived=false in both fetches below: the list endpoint only
  // applies an archived filter when the param is present, so without it the
  // server returns archived rows, which the page then drops client-side. That
  // was invisible while the fetch was unbounded, but with paging it inflates
  // `total` (and therefore totalPages) and lets archived rows consume slots in
  // a 100-row page. Measured after the chip filters went live: inactiveAgent
  // returned 726 against a badge of 218, hadMeeting 140 against 135. With the
  // param every chip matches its badge exactly, because /counts has always
  // scoped to archived != true.
  var drChipQS = function(f){
    if (!f || f === "all") return "";
    if (f === "__hadMeeting")    return "&hadMeeting=true";
    if (f === "__noAgent")       return "&noAgent=true";
    if (f === "__inactiveAgent") return "&inactiveAgent=true";
    return "&status=" + encodeURIComponent(f);
  };

  // ===== ONE QUERY STRING, THREE CALLERS (2026-08-26) ======================
  // The page's first fetch, its infinite scroll AND the Excel export all narrow
  // by exactly these params. They were two hand-maintained copies (which already
  // disagreed on the ORDER of the selQS calls — harmless, but the tell); the
  // export made it three, and a third copy is how an export ends up answering a
  // different question than the list it was launched from.
  //
  // Everything here moved server-side on 2026-08-14, and the measurements are
  // the reason: over the 100-row page, 14 of 30 agents and 104 of 152 projects
  // rendered ZERO rows, and search covered 100 of 2,892 requests (3.5%).
  var drFilterQS = function(){
    var win = dateFilterWindow(addedRange, { quarter:addedQuarter, from:customFrom, to:customTo });
    return drChipQS(filterStatus)
      + selQS("location", locationFilterDR)
      + selQS("agentId", agentFilter)
      + selQS("project", projectFilterDR)
      + selQS("propertyType", propertyTypeFilterDR)
      + (String(p.search||"").trim() ? "&search="+encodeURIComponent(String(p.search).trim()) : "")
      + (sortBy ? "&sort="+encodeURIComponent(sortBy) : "")
      + (win && win[0]!==-Infinity ? "&from="+encodeURIComponent(new Date(win[0]).toISOString()) : "")
      + (win && win[1]!==Infinity  ? "&to="+encodeURIComponent(new Date(win[1]).toISOString())   : "")
      + drTileQS(drTile);
  };
  useEffect(function(){
    // STEP 3 — summary projection strips eoiDocuments[] from the wire. DR
    // side panel doesn't render eoiDocuments today, so no detail refetch is
    // wired (GET /api/daily-requests/:id exists for future use). Legacy
    // localStorage fallback preserved unchanged for browsers with stale
    // pre-archived-field IDs.
    //
    // PAGED. This used to request the whole scope with no ?page/?limit, which
    // the backend answers literally: 2.97 MB / 4.1s for an admin, against
    // 92.5 KB / 1.3s for one page (measured on prod 2026-08-11). The page now
    // owns its pagination — see loadMoreRequests below and the note there about
    // why `endReached` could not simply keep calling p.loadMoreDRs.
    //
    // The Date Added window is sent as from/to and applied by the server on
    // createdAt, the same field and params the counts endpoint reads. It used to
    // be filtered client-side, which only worked while the fetch was unbounded:
    // over a paged list a client-side window filters the loaded page instead of
    // the book, so page 1 of "Today" would show whatever slice of today happened
    // to be in the newest 100 rows.
    // Applied by MONOTONIC SEQUENCE, not a `cancelled` flag. The old guard set
    // cancelled=true in cleanup, i.e. on every dep change, which throws away a
    // COMPLETED and correct response. That is exactly what broke the Leads
    // badges: once the request took longer than the gap between dep changes,
    // the state never got written at all. It was harmless here only because
    // these deps change on deliberate user action.
    //
    // WARNING: do NOT add p.cbBust (or any WS-driven value) to the dep array
    // below. It ticks on every dr_updated, and with a `cancelled`-style guard
    // that would reproduce the Leads race verbatim. The sequence ref makes that
    // safe, so if you do add it, keep this pattern.
    var seqReq = ++drListReqRef.current;
    var applyIfCurrent = function(fn){
      if (seqReq <= drListAppliedRef.current) return;
      drListAppliedRef.current = seqReq;
      fn();
    };
    setLoading(true);
    var drUrl="/api/daily-requests?fields=summary&archived=false&page=1&limit="+DR_PAGE_SIZE + drFilterQS();
    apiFetch(drUrl,"GET",null,p.token)
      .then(function(data){
        applyIfCurrent(function(){
          // Sending ?page/?limit switches the response to the object shape
          // ({data, total, totalPages}); the bare-array branch is kept so a
          // cached/older backend can still render.
          var rows = (data && Array.isArray(data.data)) ? data.data : (Array.isArray(data) ? data : []);
          // Legacy localStorage archive list kept as a safety net for browsers that still have it set.
          var archivedIds=[];
          try{archivedIds=JSON.parse(localStorage.getItem("crm_dr_archived")||"[]");}catch(e){}
          var filtered2=rows.filter(function(r){return !r.archived && !archivedIds.includes(gid(r));});
          setRequests(filtered2);
          setDrPage(1);
          setDrTotalPages((data && typeof data.totalPages==="number") ? data.totalPages : 1);
          setDrListTotal((data && typeof data.total==="number") ? data.total : null);
          setLoading(false);
        });
      })
      .catch(function(){ applyIfCurrent(function(){ setRequests([]);setDrPage(1);setDrTotalPages(1);setLoading(false); }); });
  // selKey, not the array itself: a filter array is a fresh identity every time
  // it is re-set, so depending on the array would refetch the whole list even
  // when the user ticks back to the same selection.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selKey(locationFilterDR), selKey(agentFilter), selKey(projectFilterDR), selKey(propertyTypeFilterDR), addedRange, addedQuarter, customFrom, customTo, filterStatus, p.search, sortBy, drTile && (drTile.from + "|" + drTile.to), drRefreshRev]);

  // Next page of the page's OWN list. This exists because the Virtuoso
  // endReached handlers used to call p.loadMoreDRs(), which pages the APP-level
  // p.dailyReqs — an array DailyRequestsPage never reads. Infinite scroll was
  // therefore already a no-op for the visible table; it went unnoticed only
  // because the fetch above returned the whole scope, so there was never a
  // second page to reach. Bounding that fetch without this would have truncated
  // the list to one page with dead scroll.
  // Chip / summary-card counts, server-aggregated over the whole scope rather
  // than the loaded page. Debounced 1000ms so a burst of WS dr_updated events
  // can't flood the endpoint, and keyed on the same location + date filters the
  // list fetch sends so rows and badges always describe the same set.
  //
  // The response is applied by MONOTONIC SEQUENCE, not gated on a `cancelled`
  // flag. That distinction is the whole reason the Leads badges were broken for
  // a day: a cleanup-scoped `cancelled` discards a COMPLETED, correct response
  // on any dep change, and if deps change more often than the request takes,
  // the badges never leave their fallback. Applying by sequence means a
  // finished response is always used and only an out-of-order one is dropped.
  useEffect(function(){
    if (!p.token) return;
    var timer = setTimeout(function(){
      var win = dateFilterWindow(addedRange, { quarter:addedQuarter, from:customFrom, to:customTo });
      var url = "/api/daily-requests/counts?_=1"
        + selQS("location", locationFilterDR)
        + selQS("agentId", agentFilter)
        + selQS("project", projectFilterDR)
        + selQS("propertyType", propertyTypeFilterDR)
        + (String(p.search||"").trim() ? "&search="+encodeURIComponent(String(p.search).trim()) : "")
        + (win && win[0]!==-Infinity ? "&from="+encodeURIComponent(new Date(win[0]).toISOString()) : "")
        + (win && win[1]!==Infinity  ? "&to="+encodeURIComponent(new Date(win[1]).toISOString())   : "");
      var seq = ++drCountsReqRef.current;
      apiFetch(url, "GET", null, p.token)
        .then(function(r){
          if (!r || typeof r !== "object") return;
          if (seq <= drCountsAppliedRef.current) return;
          drCountsAppliedRef.current = seq;
          setDrCounts(r);
        })
        .catch(function(){});
    }, 1000);
    return function(){ clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, p.cbBust, selKey(locationFilterDR), selKey(agentFilter), selKey(projectFilterDR), selKey(propertyTypeFilterDR), addedRange, addedQuarter, customFrom, customTo, p.search]);

  var loadMoreRequests = useCallback(function(){
    if (drLoadingMore || drPage >= drTotalPages || !p.token) return;
    var next = drPage + 1;
    setDrLoadingMore(true);
    var url = "/api/daily-requests?fields=summary&archived=false&page="+next+"&limit="+DR_PAGE_SIZE + drFilterQS();
    apiFetch(url,"GET",null,p.token)
      .then(function(data){
        var rows = (data && Array.isArray(data.data)) ? data.data : (Array.isArray(data) ? data : []);
        if (rows.length) {
          var archivedIds2=[];
          try{archivedIds2=JSON.parse(localStorage.getItem("crm_dr_archived")||"[]");}catch(e){}
          setRequests(function(prev){
            // Dedup by _id: a DR created since page 1 loaded shifts the whole
            // createdAt-desc list by one, which would otherwise duplicate a row
            // across the page boundary.
            var seen={}; prev.forEach(function(r){ if(r&&r._id) seen[String(r._id)]=true; });
            return prev.concat(rows.filter(function(r){
              return r && r._id && !seen[String(r._id)] && !r.archived && !archivedIds2.includes(gid(r));
            }));
          });
        }
        if (data && typeof data.totalPages==="number") setDrTotalPages(data.totalPages);
        setDrPage(next);
      })
      .catch(function(e){ console.error("[loadMoreRequests]", e && e.message); })
      .finally(function(){ setDrLoadingMore(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[drLoadingMore, drPage, drTotalPages, p.token, selKey(locationFilterDR), selKey(agentFilter), selKey(projectFilterDR), selKey(propertyTypeFilterDR), addedRange, addedQuarter, customFrom, customTo, filterStatus, p.search, sortBy, drTile && (drTile.from + "|" + drTile.to)]);

  useEffect(function(){ if(p.initSelected){setSelected(p.initSelected);p.setInitSelected(null);} },[p.initSelected]);
  // Re-hydrate `selected` from the fetched `requests` list when we arrived with
  // only a shell ({_id, name, phone}) — e.g. from the Admin dashboard activity
  // click. createdAt is on every persisted DR but never on the shells we hand
  // in via initSelected, so it's a reliable "is this a real doc" probe.
  useEffect(function(){
    if (!selected || !requests || !requests.length) return;
    var sid = gid(selected);
    var full = requests.find(function(r){ return String(gid(r)) === String(sid); });
    if (full && !selected.createdAt) setSelected(full);
  }, [requests]);

  // addedWindow() was removed with the client-side date filter — the window now
  // goes to the server as from/to in the fetch above, and dateFilterWindow is
  // called there directly. Nothing else read it.

  // Date-only base set. The window is now applied SERVER-SIDE (the fetch sends
  // from/to on createdAt), so `requests` already contains only in-window rows
  // and this is a pass-through. It stays as a named binding because `filtered`
  // and the summary cards read it, and because re-filtering here over a paged
  // list would be actively wrong: it would narrow the loaded page rather than
  // the book, hiding rows the server already decided are in range.
  var dateFiltered = requests;
  // Human-readable label for the active-filter chip.
  var addedLabel = dateFilterLabel(addedRange, { quarter:addedQuarter, from:customFrom, to:customTo });

  // Set of user ids whose account is deactivated (active === false). Built from
  // p.users, which for the owner (admin) includes every user + their active
  // flag. Drives the "Inactive Agent" tab, its live count, and the row badge —
  // purely derived, so reactivating a user drops their numbers automatically.
  //
  // Keyed on a VALUE, not on p.users' identity. getTeamScopeIds returns non-null
  // for role team_leader ONLY, and for that role App builds
  // `scopedUsers = users.filter(...)` fresh on every render (App.js ~L35449) — so
  // p.users arrived as a new array on every WS tick, this memo missed, and because
  // `filtered` below lists it as a dep the whole loaded page got re-filtered AND
  // re-sorted on every tick. Every other role gets the raw `users` array, whose
  // identity is stable, and never saw it. Measured on a 20s idle window, TL
  // session: 16 renders -> 14 filter+sort passes over the loaded page before, 0
  // after; admin measured 0 both before and after, i.e. unchanged. Note this is
  // NOT what the owner is hitting — he is admin. Same class as the work the Leads
  // memoized. The scan below is ~30 users and runs per render either way; what
  // the key buys is that the two memos downstream of it stop recomputing.
  var inactiveAgentKey = (p.users||[]).reduce(function(acc,u){
    return (u && u.active===false) ? acc+String(gid(u))+"," : acc;
  }, "");
  var inactiveAgentIds = useMemo(function(){
    var s = {};
    (p.users||[]).forEach(function(u){ if(u && u.active===false) s[String(gid(u))]=true; });
    return s;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inactiveAgentKey]);
  // A row is in the Inactive Agent bucket when its agent is present (i.e. NOT
  // No Agent) AND that agent is deactivated. Mutually exclusive with No Agent.
  var isInactiveAgentRow = function(r){
    var a = r.agentId && r.agentId._id ? String(r.agentId._id) : (r.agentId ? String(r.agentId) : "");
    return !!(a && inactiveAgentIds[a]);
  };

  // Memoized so unrelated state changes (side panel open/close, status
  // dropdown, modals) don't re-filter and re-sort the full list on every
  // render. Recomputes only when the underlying data or any filter input
  // actually changes.
  // ONE FILTER PIPELINE, TWO CONSUMERS — same shape as LeadsPage. `base` is null
  // for the on-screen memo (reads the loaded page, unchanged to the byte) and the
  // fully-walked book for Export Excel.
  // The filter context the shared predicate reads. `forLiveRow` adds the two
  // things a fetched row already carries from the server (see drRowMatchesFilters).
  var drLiveCtx = function(forLiveRow){
    return {
      filterStatus: filterStatus, agentFilter: agentFilter, projectFilter: projectFilterDR,
      propertyTypeFilter: propertyTypeFilterDR, locationFilter: locationFilterDR,
      search: p.search, inactiveAgentIds: inactiveAgentIds,
      win: forLiveRow ? dateFilterWindow(addedRange, { quarter:addedQuarter, from:customFrom, to:customTo }) : null,
      tileActive: forLiveRow ? !!drTile : false
    };
  };
  var buildFilteredDRs = function(base){
    // The second pass over the fetched page — the SAME predicate a live row is
    // admitted by (drRowMatchesFilters, module scope). No date window here: the
    // server already windowed these rows and re-windowing a paged list would
    // narrow the page, not the book. No tile guard either: a fetched row IS the
    // tile's set.
    var ctx = drLiveCtx(false);
    return (base || dateFiltered).filter(function(r){ return drRowMatchesFilters(r, ctx); }).sort(function(a,b){
      if(sortBy==="lastActivity")return new Date(b.lastActivityTime||0)-new Date(a.lastActivityTime||0);
      if(sortBy==="newest")return new Date(b.createdAt||0)-new Date(a.createdAt||0);
      return 0;
    });
  };
  var filtered = useMemo(function(){
    return buildFilteredDRs(null);
  // Filter arrays enter the dep list through selKey (value, not identity) — see
  // the loader effect above for why.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFiltered, filterStatus, selKey(agentFilter), sortBy, p.search, inactiveAgentIds, selKey(projectFilterDR), selKey(locationFilterDR), selKey(propertyTypeFilterDR)]);

  // ===== LIVE OVER WEBSOCKET — see drRowMatchesFilters / applyDrLiveDocs =====
  // Refs, not state reads, for everything the socket-driven effects below
  // consult: they fire on p.drLiveRev / p.drBurstRev and must see the CURRENT
  // rows and filters, not the ones their closure was built with.
  var requestsRef = useRef([]);
  useEffect(function(){ requestsRef.current = Array.isArray(requests) ? requests : []; }, [requests]);
  var drFilterQSRef = useRef("");
  drFilterQSRef.current = drFilterQS();
  var drLiveCtxRef = useRef(null);
  drLiveCtxRef.current = drLiveCtx(true);
  // Docs held back because their row was being edited; id -> latest doc.
  var drDeferredRef = useRef({});
  var drEditingIds = function(){
    var ids = [];
    if (showStatusComment && pendingStatus && pendingStatus.leadId) ids.push(String(pendingStatus.leadId));
    if (editReq) ids.push(gid(editReq));
    return ids;
  };
  var drEditingKey = drEditingIds().join(",");
  var applyDrDocs = function(docs){
    if (!docs || !docs.length) return;
    // Latest doc per id wins; order of first appearance kept.
    var byId = {}, order = [];
    docs.forEach(function(d){ var id = d && d._id ? String(d._id) : ""; if (!id) return; if (!byId[id]) order.push(id); byId[id] = d; });
    var batch = order.map(function(id){ return byId[id]; });
    var ctx = drLiveCtxRef.current;
    var matches = function(d){ return drRowMatchesFilters(d, ctx); };
    var editing = drEditingIds();
    setRequests(function(prev){
      var out = applyDrLiveDocs(prev, batch, matches, editing);
      out.deferred.forEach(function(d){ drDeferredRef.current[String(d._id)] = d; });
      return out.rows;
    });
  };
  // While mounted, the App-level fetchDRs window refresh stands down — this
  // page answers every burst itself (below). Unmount resumes it, with one
  // catch-up if a burst was skipped meanwhile. See applyDrPageMount.
  useEffect(function(){
    if (p.setDrPageMounted) p.setDrPageMounted(true);
    return function(){ if (p.setDrPageMounted) p.setDrPageMounted(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 1. dr_updated WITH a doc → patch in place / upsert when it matches / drop
  //    when archived / defer while its row is being edited.
  var drLiveSeenRef = useRef(p.drLiveRev || 0);
  useEffect(function(){
    if (!p.drLiveDocsRef || !p.drLiveDocsRef.current) return;
    var docs = p.drLiveDocsRef.current.splice(0);
    // Docs queued while this page was not mounted are already reflected in the
    // fetch the page just issued — drain them, apply nothing.
    if ((p.drLiveRev || 0) === drLiveSeenRef.current) return;
    drLiveSeenRef.current = p.drLiveRev || 0;
    applyDrDocs(docs);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.drLiveRev]);
  // 5. A deferred doc is applied the moment its editor closes.
  useEffect(function(){
    var held = drDeferredRef.current;
    var ids = Object.keys(held);
    if (!ids.length) return;
    var editing = {}; drEditingIds().forEach(function(id){ editing[id] = true; });
    var ready = ids.filter(function(id){ return !editing[id]; });
    if (!ready.length) return;
    var docs = ready.map(function(id){ var d = held[id]; delete held[id]; return d; });
    applyDrDocs(docs);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drEditingKey]);
  // 2./3. An EMPTY dr_updated or a socket reconnect → refresh THIS page's page 1
  //    under the current filters and re-verify, by id, every row held outside
  //    that window (?ids= composes with the same filters) — the merge rules of
  //    the Leads refresh (mergeLeadsWindowRefresh), never a wholesale replace.
  //    drPage / drTotalPages are left alone: the held window did not shrink.
  var drBurstSeenRef = useRef(p.drBurstRev || 0);
  useEffect(function(){
    if (!p.token) return;
    if ((p.drBurstRev || 0) === drBurstSeenRef.current) return;   // mount, or no new burst
    drBurstSeenRef.current = p.drBurstRev || 0;
    var qs = drFilterQSRef.current;
    var held = requestsRef.current;
    var lim = Math.min(Math.max(held.length, DR_PAGE_SIZE), 250);
    apiFetch("/api/daily-requests?fields=summary&archived=false&page=1&limit=" + lim + qs, "GET", null, p.token)
      .then(function(data){
        if (drFilterQSRef.current !== qs) return;                 // the filters moved on; the list effect owns the rows now
        var rows = (data && Array.isArray(data.data)) ? data.data : (Array.isArray(data) ? data : null);
        if (!rows) return;
        var archivedIds = [];
        try { archivedIds = JSON.parse(localStorage.getItem("crm_dr_archived") || "[]"); } catch(e) {}
        var fresh = rows.filter(function(r){ return !r.archived && !archivedIds.includes(gid(r)); });
        if (data && typeof data.total === "number") setDrListTotal(data.total);
        var asked = leadsHeldOutsideWindow(requestsRef.current, fresh).slice(0, LEADS_REFRESH_VERIFY_MAX);
        var finish = function(verified){
          if (drFilterQSRef.current !== qs) return;
          setRequests(function(prev){ return mergeLeadsWindowRefresh(prev, fresh, verified, asked); });
        };
        if (!asked.length) { finish({}); return; }
        var verifyUrl = "/api/daily-requests?fields=summary&archived=false&page=1&limit=" + asked.length + qs +
                        asked.map(function(id){ return "&ids=" + encodeURIComponent(id); }).join("");
        apiFetch(verifyUrl, "GET", null, p.token)
          .then(function(v){ return leadsVerifiedById(asked, (v && Array.isArray(v.data)) ? v.data : (Array.isArray(v) ? v : [])); },
                function(){ return undefined; })
          .then(finish);
      })
      .catch(function(){});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.drBurstRev, p.token]);

  // ===== EXPORT EXCEL — THE WHOLE FILTERED SET, NOT THE LOADED PAGE ========
  //
  // It used to hand `filtered` to SheetJS, and this page has paged at 100 rows
  // since 2026-08-11: measured on production 2026-08-26, that is 100 of 3,608
  // non-archived requests (2.8%). Walks the same query string the list and its
  // infinite scroll send (drFilterQS), then runs the result through the page's
  // own predicates and comparator — so the file equals the table, not the query.
  //
  // The walk asks for ?paging=cursor. This list's default sort is
  // lastActivityTime, so a skip/limit walk loses and repeats rows whenever any
  // agent saves a request while it runs (see the keyset block in server.js).
  var [drExporting,setDrExporting]=useState(false);
  var [drExportDone,setDrExportDone]=useState(0);
  var [drExportTotal,setDrExportTotal]=useState(0);
  var runDrExport=async function(){
    if(drExporting) return;
    setDrExporting(true); setDrExportDone(0); setDrExportTotal(0);
    try{
      var book=await fetchAllPages("/api/daily-requests?fields=summary&archived=false"+drFilterQS(), p.token, 500,
        function(done,total){ setDrExportDone(done); if(typeof total==="number") setDrExportTotal(total); });
      // The legacy pre-archived-field localStorage list, applied by both fetch
      // paths above. Browser-local, so the server cannot apply it for us — and
      // leaving it out would put rows in the file that are not in the table.
      var legacyArchived=[];
      try{legacyArchived=JSON.parse(localStorage.getItem("crm_dr_archived")||"[]");}catch(e){}
      var rows=buildFilteredDRs(book.filter(function(r){ return !legacyArchived.includes(gid(r)); })).map(function(r){return{
        "Name":r.name,
        "Phone":r.phone,
        "Phone2":altPhoneCell(r.phone, r.phone2),
        "Project":r.project||"",
        "Property Type":r.propertyType||"",
        "Location":r.area||"",
        "Budget":r.budget||"",
        "Status":r.status||"",
        "Agent":userNameOf(r.agentId, p.users),
        "Callback":excelWallDate(r.callbackTime),   // a real Excel datetime cell (sorts); 12h number format set below
        "Last Activity":r.lastActivityTime?new Date(r.lastActivityTime).toLocaleDateString("en-GB"):"",
        "Notes":r.notes||""
      };});
      var XLSX=await loadXLSX();
      var ws=XLSX.utils.json_to_sheet(rows);
      var wb=XLSX.utils.book_new();
      excelFormatColumn(XLSX, ws, "Callback", EXCEL_DATETIME_FMT);
      XLSX.utils.book_append_sheet(wb,ws,"Daily Requests");
      XLSX.writeFile(wb,"daily_requests_"+new Date().toISOString().slice(0,10)+".xlsx");
    }catch(e){ alert((e&&e.message)||"Export failed"); }
    finally{ setDrExporting(false); setDrExportDone(0); setDrExportTotal(0); }
  };

  var reqStatus=function(rid,st){
    setPendingStatus({leadId:rid,newStatus:st});setShowStatusComment(true);
  };
  // Reschedule sheet: psOverride = { leadId, newStatus: the request's CURRENT status } —
  // the same handler, PUT, activity row and audit as the modal (see LeadsPage.confirmStatus).
  //
  // THE PARAMETER LIST IS StatusModal's CONTRACT (2026-10-26). The modal calls
  // onConfirm(comment, cbTime, extra, fb) — fb is the feedback object and it is
  // ALWAYS an object, on every page, since 2026-05-02. For one evening this door
  // took psOverride as the FOURTH parameter: fb landed in it, shadowed
  // pendingStatus, had no leadId, and every Daily Request status change (every
  // role, every device) alerted "request ID not found" before a request was sent.
  // fb is unused on this page by design (DR feedback is the notes field); it is
  // named here so psOverride sits FIFTH, exactly as on LeadsPage.confirmStatus.
  // verify-status-door-arity.js RUNS this door the way the modal calls it.
  var pendingStatusOfRender=pendingStatus;
  var confirmStatus=async function(comment,cbTime,extra,fb,psOverride){
    var pendingStatus=psOverride||pendingStatusOfRender;
    if(!pendingStatus)return;
    if(!pendingStatus.leadId){alert("Error: request ID not found. Please refresh.");return;}
    try{
      var updateData={status:pendingStatus.newStatus};
      var fbParts=[];
      if(comment) fbParts.push(comment);
      if(cbTime) updateData.callbackTime=cbTime;
      if(comment) updateData.notes=comment;
      // Pass deal fields from StatusModal.
      //
      // THIS LIST IS THE MODAL'S CONTRACT, NOT A SELECTION (wave 2a). StatusModal
      // is ONE component mounted on two pages. Every field it renders it packs
      // into `extra`; the Leads consumer reads them all and this one read four.
      // The rest were collected from the user, validated by the modal, and thrown
      // away in the browser — a picker with no consumer. unitCode is the sharpest
      // case: the modal REFUSES to submit a DoneDeal without one (see the
      // dealUnitCode guard) and this door then discarded it.
      // Anything added to `extra` must be added here too, or it is dropped.
      if(extra){
        if(extra.budget)    updateData.budget=extra.budget;
        if(extra.project)   updateData.project=extra.project;
        if(extra.unitType)  updateData.unitType=extra.unitType;
        if(extra.unitCode)  updateData.unitCode=extra.unitCode;
        if(extra.eoiDeposit) updateData.eoiDeposit=extra.eoiDeposit;
        if(extra.eoiDate)   updateData.eoiDate=extra.eoiDate;
        // Developer — required on this door as of wave 2a, exactly as on the
        // Leads door. Truthiness-guarded so an empty string is never sent: the
        // backend treats a present-but-empty developerId as an explicit clear,
        // which is the manufactured-null defect this wave exists to close.
        if(extra.developerId)      updateData.developerId=extra.developerId;
        if(extra.developerPending) updateData.developerPending=extra.developerPending;
      }
      if(fbParts.length) updateData.lastFeedback=fbParts.join(" | ");
      var upd=await apiFetch("/api/daily-requests/"+pendingStatus.leadId,"PUT",updateData,p.token);
      // Also log separate activity with comment if exists
      if(comment||cbTime){
        var actNote2="Status: "+pendingStatus.newStatus;
        if(comment) actNote2+=" | "+comment;
        await apiFetch("/api/activities","POST",{leadId:pendingStatus.leadId,type:"status_change",note:actNote2},p.token);
      }
      setRequests(function(prev){return prev.map(function(r){return gid(r)===pendingStatus.leadId?upd:r;});});
      if(selected&&gid(selected)===pendingStatus.leadId)setSelected(upd);
      // Notify admin on DoneDeal or EOI
      if((pendingStatus.newStatus==="DoneDeal"||pendingStatus.newStatus==="EOI")&&p.addDealNotif){
        var req=requests.find(function(r){return gid(r)===pendingStatus.leadId;})||{};
        // Prefer the mirror Lead._id (returned by the DR PUT at server.js:8108)
        // so the notification's leadId resolves in getVisibleNotifications, which
        // only checks the Lead collection. Falling back to the DR id keeps the
        // POST whitelist happy but produces an unresolvable orphan row.
        var dealNotifLeadId = (upd && upd.mirrorLeadId) ? String(upd.mirrorLeadId) : pendingStatus.leadId;
        p.addDealNotif({
          leadId:dealNotifLeadId,
          leadName:req.name||upd.name||"",
          agentName:userNameOf(req.agentId, p.users)||p.cu.name,
          status:pendingStatus.newStatus,
          budget:updateData.budget||req.budget||"",
          time:new Date().toISOString()
        });
      }
      // The backend creates/updates a Lead mirror for DR→EOI/DoneDeal, but the
      // mirror isn't in p.leads yet. Refetch so the EOI/Deals page sees it.
      // TARGETED (2026-08-22). This was a bare GET /api/leads — the caller's whole
      // book, ~77 MB for the largest agent — to pick up ONE new mirror row.
      //
      // The DR PUT already returns the mirror's id (server.js ~28716), and that
      // response field exists for exactly this reason: its own comment says it
      // spares callers "having to refetch the full /api/leads list to look it up".
      // mirrorLeadId is set precisely when the EOI/DoneDeal branch ran, which is
      // the same condition guarding this block — so it is present whenever this
      // fires, and the `if` below is belt-and-braces rather than a real fallback.
      //
      // No full-refetch fallback on the else: the Deals and EOI pages render ONLY
      // their own scoped fetch and never read p.leads (they must not — /api/leads
      // scopes by current owner while /api/deals and /api/eois scope by author, and
      // mixing them flashes another agent's rows). So a missed mirror here costs
      // nothing on the pages this navigates to.
      if(pendingStatus.newStatus==="DoneDeal"||pendingStatus.newStatus==="EOI"){
        var mirrorId = (upd && upd.mirrorLeadId) ? String(upd.mirrorLeadId) : "";
        // ── THE FILES THIS DOOR USED TO THROW AWAY (wave 2a) ──────────────────
        //
        // StatusModal has rendered a DocumentsUpload for every EOI/DoneDeal
        // transition since 2026-04-22, on BOTH pages, and packs the result into
        // extra.eoiDocumentFiles. The Leads consumer has uploaded them since
        // 2026-04-12. This consumer never did — for 124 days the picker on this
        // page collected files, showed them attached, returned a green save, and
        // discarded them in the browser. Nothing reached B2, nothing reached the
        // artifacts collection, and no log line anywhere recorded a loss, which
        // is why it survived a whole file-storage migration undetected.
        //
        // MEASURED: lead #50041 (Sameh, 2026-08-24) has zero artifacts, empty
        // legacy arrays and an EOI record with documents:[]. Lead #50015 came
        // through the same door on 2026-05-10 and is equally empty.
        //
        // The upload targets the MIRROR LEAD, not the DR — a DailyRequest has no
        // artifact home (its eoiDocuments[] is vestigial and always empty), and
        // /api/leads/:id/eoi-documents is the same endpoint the DR EDIT form has
        // always used for exactly this. Awaited before the refresh below so the
        // re-fetched mirror already carries them.
        //
        // A FAILED UPLOAD IS NOT SILENT. The old code had nothing to fail; the
        // new code must not trade a silent discard for a silent 500. Anything
        // that does not land is named to the user, because the file is still in
        // their hands at that moment and re-attaching it is cheap.
        if (mirrorId && extra && Array.isArray(extra.eoiDocumentFiles) && extra.eoiDocumentFiles.length > 0) {
          var drDocFails = [];
          for (var dfi = 0; dfi < extra.eoiDocumentFiles.length; dfi++) {
            var dff = extra.eoiDocumentFiles[dfi];
            if (!dff || !dff.fileData) continue;
            try {
              await apiFetch("/api/leads/" + mirrorId + "/" +
                (pendingStatus.newStatus === "DoneDeal" ? "deal-documents" : "eoi-documents"), "POST",
                { fileData: dff.fileData, fileName: dff.fileName || "" }, p.token);
            } catch (docErr) {
              console.error("[dr-status] document upload failed:", docErr && docErr.message);
              drDocFails.push((dff.fileName || ("file " + (dfi + 1))) + " — " + ((docErr && docErr.message) || "upload failed"));
            }
          }
          if (drDocFails.length) {
            alert("The status was saved, but " + drDocFails.length + " of " +
              extra.eoiDocumentFiles.length + " file(s) did NOT upload:\n\n" + drDocFails.join("\n") +
              "\n\nOpen the record and attach them again.");
          }
        } else if (extra && Array.isArray(extra.eoiDocumentFiles) && extra.eoiDocumentFiles.length > 0) {
          // Files were picked but the DR PUT returned no mirror id, so there is
          // nowhere to put them. Say so rather than dropping them the old way.
          alert("The status was saved, but " + extra.eoiDocumentFiles.length +
            " attached file(s) could not be stored — this request has no linked record yet. " +
            "Open it from the EOI/Deals page and attach them there.");
        }
        if (mirrorId) {
          try {
            var mirror = await apiFetch("/api/leads/" + mirrorId, "GET", null, p.token);
            if (mirror && mirror._id) p.setLeads(function(prev){
              var arr = prev || [], hit = false;
              var next = arr.map(function(l){ if (gid(l) === mirrorId) { hit = true; return mirror; } return l; });
              return hit ? next : next.concat([mirror]);
            });
          } catch(e) { console.error("[dr-status] mirror lead refresh failed (non-fatal)", e); }
        } else {
          console.warn("[dr-status] no mirrorLeadId on the DR PUT response — mirror not merged into p.leads");
        }
      }
      if(pendingStatus.newStatus==="DoneDeal") p.nav("deals");
      else if(pendingStatus.newStatus==="EOI") p.nav("eoi");
    }catch(e){
      // 404 here means the request is gone for THIS caller — deleted, archived or
      // reassigned by someone else since the list loaded (the id sent is always
      // the row's own _id; verify-status-door-arity.js). The stale row is dropped,
      // the panel closed, page 1 refetched (drRefreshRev), and the user told why —
      // instead of a bare "Not found" that the next tap would hit again.
      if(e&&e.status===404){
        setRequests(function(prev){return prev.filter(function(r){return gid(r)!==pendingStatus.leadId;});});
        if(selected&&gid(selected)===pendingStatus.leadId)setSelected(null);
        setDrRefreshRev(function(n){return n+1;});
        alert("This request was updated by someone else and is no longer in your list. The list has been refreshed.");
      } else alert(e.message);
    }
    setShowStatusComment(false);setPendingStatus(null);setStatusDrop(null);
  };
  var saveReschedule=async function(v){
    if(!cbResched||!v)return;
    setCbReschedBusy(true);
    try{ await confirmStatus("",v,null,null,{leadId:cbResched.leadId,newStatus:cbResched.status}); }
    finally{ setCbReschedBusy(false); setCbResched(null); }
  };

  var [drHistory,setDrHistory]=useState({});

  var openDrHistory=async function(r){
    setDrHistoryReq(r); setShowDrHistory(true); setDrHistoryList([]); setDrHistoryLoading(true);
    try{
      var acts=await apiFetch("/api/daily-requests/"+gid(r)+"/history","GET",null,p.token);
      setDrHistoryList(acts||[]);
      var rid=gid(r);
      setDrHistory(function(prev){var upd={};upd[rid]=acts||[];return Object.assign({},prev,upd);});
    }catch(e){setDrHistoryList([]);}
    setDrHistoryLoading(false);
  };

  // useCallback, NOT a plain function — this is a dependency of the
  // drTableComponents memo below, and a fresh identity every render defeated it
  // completely (measured: 18 renders -> 18 rebuilds -> 18 full <table> remounts
  // in a 20s idle window). It closes over p.token and setDrHistory only, both
  // stable, so an empty-but-for-token dep list is exact.
  var loadDrHistory=useCallback(async function(rid){
    try{
      var acts=await apiFetch("/api/daily-requests/"+rid+"/history","GET",null,p.token);
      setDrHistory(function(prev){var upd={};upd[rid]=acts||[];return Object.assign({},prev,upd);});
    }catch(e){}
  },[p.token]);

  // ===== ARCHIVE ONE REQUEST (Owner) =====
  // The page has offered exactly one archive action since 2026-03-28: the bulk
  // toolbar button, which needs a checkbox column that only admin/operation see
  // and a selection before it appears. There was no way to archive the request in
  // front of you.
  //
  // THE PANEL IS THE ONLY PLACE THIS LIVES (owner correction, 2026-09-01). It
  // shipped in 84f53dc on the row and the card as well; both are gone. Archiving
  // is a decision about a request you have opened and looked at, not something to
  // put a click away from every row of a 3,872-row list — and the row control
  // cost every other role a column of horizontal space to render nothing.
  // verify-dr-archive-owner.js asserts their ABSENCE, so they cannot drift back.
  //
  // Gated on isOwnerFE(p.access) — the ENVELOPE's flag, re-resolved on every
  // boot — and not on p.cu.role, which is a login blob that can be up to 30 days
  // stale. Hiding the control is the courtesy; PUT /api/daily-requests/:id/archive
  // wears requireOwner and refuses independently of anything decided here.
  //
  // Confirms first, and names the request, because the server-side cascade
  // reaches further than this page shows: an archived DR takes its mirror Lead
  // with it and deletes that lead's notifications.
  var canArchiveDR = isOwnerFE(p.access);
  var archiveOneReq=async function(rid, rname){
    if(!rid) return;
    if(!window.confirm("Archive \""+(rname||"this request")+"\"?\n\nIt leaves the Daily Requests list and moves to the Archive page, where it can be restored.")) return;
    try{
      await apiFetch("/api/daily-requests/"+rid+"/archive","PUT",null,p.token);
      // Drop it locally rather than refetching the page — the list is paged and
      // a refetch would reset the scroll position and the loaded window.
      setRequests(function(prev){return prev.filter(function(x){return gid(x)!==rid;});});
      setSelected2(function(prev){return prev.filter(function(x){return x!==rid;});});
      setSelected(function(prev){return (prev&&gid(prev)===rid)?null:prev;});
    }catch(e){ alert(e&&e.message?e.message:"Could not archive this request"); }
  };

  // Admin-only edit flow. Mirrors LeadsPage side-panel edit (App.js ~L4170 + L4449).
  // Backend: PUT /api/daily-requests/:id — handles agentId normalization,
  // lastActivityTime stamping, and EOI/DoneDeal transition side-effects
 // (preEoiStatus / preDealStatus stamping at server.js:8657-8664).
  // `selected` is NOT guaranteed to be a full document. It can arrive from any
  // slim surface: the pending-location bell (/with-pending-location projects 10
  // fields — no propertyType/budget/callbackTime/notes/phone2), quick phone
  // search (DR_SEARCH_FIELDS — no project), or a dashboard shell. saveEditReq
  // below sends EVERY field with ||"", and PUT partial-merges, so opening this
  // modal on a partial row and pressing Save silently WIPES whatever the source
  // projection happened to omit.
  //
  // The re-hydration effect above can't be relied on either: its probe is
  // `!selected.createdAt`, and the pending-location feed DOES ship createdAt, so
  // a bell row is mistaken for a complete document and never re-hydrated.
  //
  // So re-read the record here — the one place every entry path funnels through.
  // GET /api/daily-requests/:id returns the unprojected doc. If it fails we do
  // NOT open a form we know is lossy.
  var openEditReq=async function(r){
    if(!r) return;
    var full = r;
    try {
      var fresh = await apiFetch("/api/daily-requests/"+gid(r),"GET",null,p.token);
      if (fresh && gid(fresh)) full = fresh;
      else { alert("Couldn't load this request. Please try again."); return; }
    } catch(e) {
      alert("Couldn't load this request"+(e&&e.message?(" — "+e.message):"")+". Please try again.");
      return;
    }
    var aid = full.agentId && full.agentId._id ? String(full.agentId._id) : (full.agentId ? String(full.agentId) : "");
    setEditForm({
      name: full.name||"",
      phone: full.phone||"",
      phone2: full.phone2||"",
      project: full.project||"",
      propertyType: full.propertyType||"",
      area: full.area||"",
      budget: full.budget||"",
      agentId: aid,
      callbackTime: full.callbackTime||"",
      status: full.status||"",
      notes: full.notes||""
    });
    setEditReq(full);
    // Keep the side panel and the row in sync with what we just read, so the
    // panel behind the modal stops rendering the partial version too.
    setSelected(function(prev){ return (prev && gid(prev)===gid(full)) ? full : prev; });
    setRequests(function(prev){ return prev.map(function(x){ return gid(x)===gid(full) ? Object.assign({}, x, full) : x; }); });
  };
  var saveEditReq=async function(){
    if(!editReq) return;
    if(!editForm.name||!editForm.name.trim()){alert("Name is required");return;}
    if(!editForm.phone||!editForm.phone.trim()){alert("Phone is required");return;}
    // Property Type is REQUIRED here too (owner, 2026-08-08). Unlike the New
    // Request form this field is always visible, so the guard is unconditional.
    // KNOWN CONSEQUENCE: 433 existing requests (14.2%) carry an empty
    // propertyType — mostly EOI/DoneDeal rows, whose add path never collected
    // one — and each must now be given a type before it can be saved from this
    // modal. That is the intended clean-up, not an oversight.
    if(!String(editForm.propertyType||"").trim()){alert("Property Type is required");return;}
    // The server rejects an invalid status on PUT, so catch it here with a
    // readable message instead of a 400. This matters most for the handful of
    // legacy rows still carrying "NewLead": opening one for edit now hydrates an
    // empty status, and the agent must choose a real one before saving.
    if(!String(editForm.status||"").trim()){alert("Status is required");return;}
    setEditSaving(true);
    try{
      var payload={
        name: editForm.name||"",
        phone: editForm.phone||"",
        phone2: editForm.phone2||"",
        project: editForm.project||"",
        propertyType: editForm.propertyType||"",
        area: editForm.area||"",
        budget: editForm.budget||"",
        agentId: editForm.agentId||"",
        callbackTime: editForm.callbackTime||"",
        status: editForm.status||"",
        notes: editForm.notes||""
      };
      var updated=await apiFetch("/api/daily-requests/"+gid(editReq),"PUT",payload,p.token);
      setRequests(function(prev){return prev.map(function(x){return gid(x)===gid(updated)?updated:x;});});
      if(selected && gid(selected)===gid(updated)) setSelected(updated);
      setEditReq(null);
    }catch(e){alert(e.message||"Failed to save");}
    setEditSaving(false);
  };

  var logActivity=async function(){
    if(!actNote.trim()||!selected)return;
    setActSaving(true);
    try{
      var act=await apiFetch("/api/activities","POST",{leadId:gid(selected),type:actType,note:actNote},p.token);
      await apiFetch("/api/daily-requests/"+gid(selected),"PUT",{lastActivityTime:new Date()},p.token);
      setRequests(function(prev){return prev.map(function(r){return gid(r)===gid(selected)?Object.assign({},r,{lastActivityTime:new Date().toISOString()}):r;});});
      var rid=gid(selected);
      setDrHistory(function(prev){var upd={};upd[rid]=[act].concat(prev[rid]||[]);return Object.assign({},prev,upd);});
      setActNote(""); setShowActForm(false);
    }catch(e){}
    setActSaving(false);
  };

  var addReq=async function(){
    if(!form.name||!form.phone)return;
    // Project is REQUIRED on every new request (owner decision 2026-08-04) —
    // unconditional, so a request can never enter the system without the
    // development it is for. Server re-validates (POST returns project_required).
    if(!(form.project||"").trim()){alert("Project is required");return;}
    if(!form.area.trim()){alert("Area is required");return;}
    var isEOI      = form.status==="EOI";
    var isDoneDeal = form.status==="DoneDeal";
    var dealLike   = isEOI||isDoneDeal;
    // Property Type is REQUIRED (owner, 2026-08-08) but only exists on the
    // normal pipeline — the EOI/DoneDeal add captures "🏷️ Unit Type" instead —
    // so guard it inside the same condition that renders it.
    if(!dealLike && !String(form.propertyType||"").trim()){alert("Property Type is required");return;}
    // Status is required and no longer pre-filled. It used to default to
    // "NewLead" — a value the picker did not even offer — so an agent who never
    // opened the dropdown silently submitted it. 500 rows arrived that way.
    if(!dealLike && !String(form.status||"").trim()){alert("Status is required");return;}
    if(!dealLike){
      if(!form.callbackTime){alert("Callback is required");return;}
      if(!form.notes.trim()){alert("Feedback is required");return;}
      if(!form.budget.trim()){alert("Budget is required");return;}
    } else {
      if(!(form.dealBudget||"").trim()){alert("Amount is required");return;}
      // Developer — the server refuses this transition without one (server.js
      // "DEVELOPER — ONE GATE, BOTH DOORS"). Refuse here too, in the same words
      // the other two doors use, so the agent is told by the form rather than by
      // a 400 after the request row has already been created at Potential.
      if(!drDevSelId&&!drDevPendName.trim()){alert("Please pick a developer or enter a new name");return;}
    }
    setSaving(true);
    try{
      var drAgentId=form.agentId||"";
      // For EOI / DoneDeal, the server's POST handler doesn't build the Lead
      // mirror — only the PUT status-transition path does. Create the DR as
      // NewLead first, then PUT the target status so the existing mirror
      // logic fires and the row shows up on the EOI / Deals pages.
      // dealLike creates first, then PUTs the real status so the lead-mirror
      // logic fires. The placeholder must itself be valid — it used to be
      // NewLead, which the server now rejects (and which stranded the DR at an
      // impossible status whenever the follow-up PUT failed).
      var initialStatus = dealLike ? "Potential" : form.status;
      var initialBudget = dealLike ? (form.dealBudget||"") : (form.budget||"");
      var submitData={
        name:form.name||"",
        phone:form.phone||"",
        phone2:form.phone2||"",
        propertyType: dealLike ? "" : (form.propertyType||""),
        project:form.project||"",
        area:form.area||"",
        budget:initialBudget,
        notes:form.notes||"",
        callbackTime:form.callbackTime||"",
        agentId:drAgentId,
        source:"Daily Request",
        status:initialStatus,
        // An EOI/DoneDeal add is created as NewLead here and immediately PUT to
        // its real status. Property Type is not collected on that path (the form
        // asks for Unit Type, which the PUT writes to unitType), so tell the
        // server to skip its propertyType requirement for THIS create only.
        dealFlowCreate: dealLike
      };
      var r=await apiFetch("/api/daily-requests","POST",submitData,p.token);
      if(dealLike){
        var upData={
          status: form.status,
          budget: form.dealBudget||"",
          project: form.project||""
        };
        // Developer — truthiness-guarded and mutually exclusive, matching
        // confirmStatus above and PUT /api/daily-requests/:id, which reads a
        // PRESENT-but-empty developerId as an explicit clear.
        if(drDevSelId) upData.developerId=drDevSelId;
        else if(drDevPendName.trim()) upData.developerPending=drDevPendName.trim();
        if(form.dealUnitType) upData.unitType=form.dealUnitType;
        if(isEOI){
          if(form.eoiDeposit) upData.eoiDeposit=form.eoiDeposit;
          // Only stamp eoiDate on first transition. If the DR already carries one
          // (re-save on an existing record), preserve it so notifications don't reset.
          // DATE ONLY (owner ruling 2026-09-11), like every other eoiDate/dealDate
          // writer — see the StatusModal writer for why the datetime form is gone.
          if(!r.eoiDate) {
            upData.eoiDate = form.eoiDateInput ? String(form.eoiDateInput).slice(0,10) : cairoTodayIso();
          }
        }
        // Date-only Cairo-local (YYYY-MM-DD), unified with every dealDate
        // write path.
        if(isDoneDeal && !r.dealDate) upData.dealDate = cairoTodayIso();
        r = await apiFetch("/api/daily-requests/"+gid(r),"PUT",upData,p.token);
        // Backend PUT now surfaces mirrorLeadId on the response (server.js
        // line ~8108) so we can target the mirror without scanning the full
        // /api/leads list. Saves a 1000-row fetch per save and lets the doc
        // uploads run in parallel.
        var mirrorLeadId = r && r.mirrorLeadId ? String(r.mirrorLeadId) : "";
        if((isEOI||isDoneDeal) && Array.isArray(form.eoiDocFiles) && form.eoiDocFiles.length>0 && mirrorLeadId){
          await Promise.all(form.eoiDocFiles.map(function(ff){
            if(!ff||!ff.fileData) return Promise.resolve();
            return apiFetch("/api/leads/"+mirrorLeadId+"/"+(isDoneDeal?"deal-documents":"eoi-documents"),"POST",{fileData:ff.fileData,fileName:ff.fileName||""},p.token)
              .catch(function(docErr){ console.error("Document upload failed:", docErr.message); });
          }));
        }
        // Refresh the mirror in p.leads so the EOI / Deals page picks it up.
        // Single-lead GET instead of the previous 1000-row /api/leads pull;
        // the server already broadcast lead_updated for this mirror via
        // emitLead() so most of the time the row is already there — this
        // fetch is a belt-and-braces refresh that doesn't block navigation.
        if(mirrorLeadId){
          apiFetch("/api/leads/"+mirrorLeadId,"GET",null,p.token).then(function(fresh){
            if(fresh && fresh._id && p.setLeads){
              p.setLeads(function(prev){
                var found = prev.some(function(l){return gid(l)===String(fresh._id);});
                return found ? prev.map(function(l){return gid(l)===String(fresh._id)?fresh:l;}) : [fresh].concat(prev);
              });
            }
          }).catch(function(){});
        }
        if(p.addDealNotif){
          // Prefer the mirror Lead._id (already resolved into mirrorLeadId above)
          // so getVisibleNotifications can resolve the row — it only checks the
          // Lead collection. Falling back to the DR id keeps the row creatable
          // but produces an unresolvable orphan.
          p.addDealNotif({
            leadId:mirrorLeadId||gid(r),
            leadName:r.name||"",
            agentName:userNameOf(r.agentId, p.users)||p.cu.name,
            status:form.status,
            budget:form.dealBudget||"",
            time:new Date().toISOString()
          });
        }
      }
      // Dedupe by _id — the backend also broadcasts a dr_updated WS event for
      // the same insert, and if it races this prepend we'd end up with two
      // rows in local state until the next refresh.
      setRequests(function(prev){
        var nid=String(r&&r._id||"");
        if(!nid) return [r].concat(prev);
        if(prev.some(function(x){return gid(x)===nid;})) return prev.map(function(x){return gid(x)===nid?r:x;});
        return [r].concat(prev);
      });
      setShowAdd(false);
      setForm({name:"",phone:"",phone2:"",project:"",propertyType:"",area:"",budget:"",notes:"",agentId:"",callbackTime:"",status:"",dealUnitType:"",dealBudget:"",eoiDeposit:"",eoiDateInput:"",eoiDocFiles:[]});
      setDrDevSelId(""); setDrDevPendName(""); setDrDevPendOpen(false);
      if(isDoneDeal) p.nav("deals");
      else if(isEOI) p.nav("eoi");
    }catch(e){alert(e.message);}setSaving(false);
  };

  var getAgentName=function(r){ return userNameOf(r && r.agentId, p.users) || "-"; };

  // ===== STEP 2a — TableVirtuoso wiring for the DR desktop table (DR2). =====
  // Same pattern as LeadsPage: memoized components dict gives Virtuoso a stable
  // identity (avoids re-mount-on-every-render scroll-jump). TableRow closes over
  // selected / selected2 / setSelected / loadDrHistory so each row's highlight
  // and click behavior match the pre-virtualization version exactly.
  //
  // EVERY DEP HERE MUST BE A STABLE IDENTITY. Virtuoso treats components.Table /
  // components.TableRow as element TYPES, so a new object literal makes React
  // unmount and rebuild the entire <table> subtree — it is not a cheap re-render.
  // This memo was dead until 2026-08-15 because loadDrHistory was a plain
  // function redefined on every render, which is the DR page's half of the jitter
  // the Leads page never had (its two equivalents at ~L8603/L8629 depend only on
  // state and setState identities). Measured on the same 20s idle window under
  // identical WS traffic: DR 18 renders -> 18 remounts -> 126 <tr> destroyed and
  // rebuilt, and 15 compositor repaints; Leads 18 renders -> 0 remounts -> 1
  // repaint. Before adding a dep, check it is state, a setState, a ref, a
  // primitive prop, or a useCallback — never a function or object built inline.
  var drTableComponents = useMemo(function(){
    return {
      Table: function TableEl(props){
        return <table {...props} data-table-align="sheet" style={{ width:"100%", borderCollapse:"collapse", minWidth:640 }}/>;
      },
      TableRow: function TableRowEl(props){
        var r = props.item;
        if (!r) return <tr {...props}/>;
        var rid = gid(r);
        var isSel = selected && gid(selected) === rid;
        var isChk = selected2.includes(rid);
        var trProps = {};
        Object.keys(props).forEach(function(k){ if (k !== "item" && k !== "context") trProps[k] = props[k]; });
        return <tr {...trProps} onClick={function(){setSelected(r);loadDrHistory(rid);}} style={{ borderBottom:"1px solid #F1F5F9", cursor:"pointer", background:isChk?"#EFF6FF":isSel?"#EFF6FF":"transparent", borderRight:"3px solid "+(isSel?C.accent:"transparent") }}/>;
      }
    };
  }, [selected, selected2, setSelected, loadDrHistory]);

  return <div style={{ padding:"18px 16px 40px" }}>
    {/* requireDeveloper + token are NOT optional extras on this mount (wave 2a).
        A DR reaching EOI/DoneDeal becomes an ordinary EOI or deal the moment it
        does — same page, same reports, same commission — so it needs the same
        developer the Leads door has required since N1 shipped on 2026-06-29.
        This mount omitted both props, so the modal skipped its own developer
        block AND skipped loading the developer list, and the transition sailed
        through. The backend now refuses it (developer_required on
        PUT /api/daily-requests/:id); these props are what let the user satisfy
        that refusal instead of hitting it. */}
    <StatusModal show={showStatusComment} t={t} token={p.token} isMobile={p.isMobile} requireDeveloper={true} newStatus={pendingStatus?pendingStatus.newStatus:null} lead={selected} cu={p.cu} users={p.users} onClose={function(){setShowStatusComment(false);}} onConfirm={confirmStatus}/>
    <RescheduleSheet open={!!cbResched} t={t} nowMs={cbNow} current={cbResched?cbResched.callbackTime:""} busy={cbReschedBusy} onClose={function(){if(!cbReschedBusy)setCbResched(null);}} onSave={saveReschedule}/>
    {statusDrop&&<div data-overlay-above="true" style={{ position:"fixed", inset:0, zIndex:499 }} onClick={function(){setStatusDrop(null);}}/>}

    {drTile&&<div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, padding:"10px 14px", background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:10, marginBottom:12 }}>
      <div style={{ fontSize:13, color:"#1D4ED8", fontWeight:600, minWidth:0, overflow:"hidden", textOverflow:"ellipsis" }}>Showing: Overdue callbacks{drTile.label ? " \u2014 "+drTile.label : ""} <span style={{ color:"#64748B", fontWeight:500 }}>({drListTotal!==null ? drListTotal : filtered.length})</span></div>
      <button onClick={function(){setDrTile(null);}} style={{ background:"#fff", border:"1px solid #BFDBFE", color:"#1D4ED8", fontSize:12, fontWeight:600, padding:"4px 10px", borderRadius:8, cursor:"pointer", flexShrink:0 }}>Clear</button>
    </div>}

    {/* Stats */}
    <div className={p.isMobile?"crm-m-stats":undefined} style={{ display:"flex", gap:10, marginBottom:12, flexWrap:"wrap" }}>
      <StatCard icon={ClipboardList} label={"Total Numbers"} value={(drCounts?(drCounts.total||0):dateFiltered.length)+""} c={C.info}/>
      <StatCard icon={Target} label={"Potential"} value={((drCounts&&drCounts.byStatus)?(drCounts.byStatus.Potential||0):dateFiltered.filter(function(r){return r.status==="Potential";}).length)+""} c={"#1D4ED8"}/>
      <StatCard icon={DollarSign} label={t.doneDeals} value={((drCounts&&drCounts.byStatus)?(drCounts.byStatus.DoneDeal||0):dateFiltered.filter(function(r){return r.status==="DoneDeal";}).length)+""} c={C.success}/>
    </div>
    {(function(){
    // Same arrangement as LeadsPage: build the four pieces once; desktop returns the
    // original sibling order, a phone gets MobileFilterChrome.
    var drClearAll = function(){
      setProjectFilterDR([]); setLocationFilterDR([]); setPropertyTypeFilterDR([]); setAgentFilter([]); setFilterStatus("all");
      setAddedRange("all"); setCustomFrom(""); setCustomTo("");
      if (p.setSearch) p.setSearch("");
    };
    var actionsNode = <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
      <Btn onClick={function(){setShowAdd(true);}} style={{ padding:"7px 13px", fontSize:12 }}><Plus size={13}/> {t.addRequest}</Btn>
      {isOnlyAdmin&&<Btn outline onClick={function(){ if (p.isMobile) { setMActionsOpen(false); setMSelectMode(true); } else setShowBulk(true); }} style={{ padding:"7px 11px", fontSize:12, color:C.info, borderColor:C.info }}><RotateCcw size={13}/> Bulk Reassign {selected2.length>0?"("+selected2.length+")":""}</Btn>}
      {isOnlyAdmin&&(filterStatus==="__noAgent"||filterStatus==="__inactiveAgent")&&selected2.length>0&&<Btn outline onClick={function(){setMoveErr("");setShowMove(true);}} style={{ padding:"7px 11px", fontSize:12, color:C.success, borderColor:C.success }}>➡️ Move to Leads as Fresh ({selected2.length})</Btn>}
      {p.cu.role==="admin"&&selected2.length>0&&<Btn outline onClick={async function(){
        if(!window.confirm("Archive "+selected2.length+" requests?")) return;
        var ids=[...selected2];
        // Archive each selected DR via the dedicated endpoint.
        for(var i=0;i<ids.length;i++){
          try{ await apiFetch("/api/daily-requests/"+ids[i]+"/archive","PUT",null,p.token); }
          catch(e){ /* swallow — continue archiving the rest */ }
        }
        // Drop archived DRs from the list immediately
        setRequests(function(prev){return prev.filter(function(r){return !ids.includes(gid(r));});});
        setSelected2([]);
        if(selected&&ids.includes(gid(selected)))setSelected(null);
      }} style={{ padding:"7px 11px", fontSize:12, color:C.warning, borderColor:C.warning }}><Archive size={13}/> Archive ({selected2.length})</Btn>}
      {p.cu.role==="admin"&&<Btn outline onClick={runDrExport} disabled={drExporting} style={{ padding:"7px 11px", fontSize:12, color:C.success, borderColor:C.success }}><FileSpreadsheet size={13}/> {drExporting ? ("Exporting… " + drExportDone.toLocaleString() + (drExportTotal ? " / " + drExportTotal.toLocaleString() : "")) : "Export Excel"}</Btn>}
    </div>;

    var chipsNode = <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:p.isMobile?0:10 }}>
      {[{v:"all",l:t.all}].concat(sc.map(function(s){return{v:s.value,l:s.label};})).map(function(s){
        // Server-aggregated over the whole scope when loaded; the in-memory
        // fallback only runs during the 1s debounce before the first response,
        // and now that the list is paged it would otherwise count one page.
        //
        // Every chip here is a STATUS chip reading byStatus, MeetingDone
        // included — so these nine partition the scope and sum to All exactly
        // (2673 + 71 = 2744, measured 2026-08-11). The "ever had a meeting"
        // view lives in the 🤝 Had Meeting overlay chip below.
        var bs = drCounts && drCounts.byStatus;
        var cnt;
        if (drCounts) {
          cnt = s.v==="all" ? (drCounts.total || 0) : ((bs && bs[s.v]) || 0);
        } else {
          cnt = s.v==="all"
            ? dateFiltered.length
            : dateFiltered.filter(function(r){return r.status===s.v;}).length;
        }
        return <button key={s.v} onClick={function(){setFilterStatus(s.v);}} style={{ padding:"5px 10px", borderRadius:7, border:"1px solid", borderColor:filterStatus===s.v?C.accent:"#E8ECF1", background:filterStatus===s.v?C.accent+"12":"#fff", color:filterStatus===s.v?C.accent:C.textLight, fontSize:11, fontWeight:500, cursor:"pointer" }}>{p.isMobile?mobShort(s.l)+" "+cnt:s.l+" ("+cnt+")"}</button>;
      })}
      {/* No Agent is rendered a few lines below, gated on canSeeNoAgentChip. */}
      {/* Had Meeting — OVERLAY, not a status. Sits here with the other
          pseudo-filters, emoji-marked, rather than inline with the status
          partition, following the LeadsPage convention (⭐ Important, 🛑 Rotation
          Stopped, 🌱 Not Rotated, 🕓 Untouched all read as overlays by their
          icon while sharing the status chips' button styling).
          Deliberately overlaps the status chips: 64 of its rows carry a
          different current status (NotInterested 36, CallBack 10, NoAnswer 7,
          Potential 5, HotCase 3, EOI 2, DoneDeal 1 — measured 2026-08-11),
          because a DR that met and then went cold is still a DR that met. That
          overlap is exactly why it cannot live in the partition: with it inline
          the strip summed to 2808 against an All of 2744. Its badge still
          matches its own rows, which is the property that matters. */}
      {(function(){
        var hadMeetingCount = (drCounts && typeof drCounts.meetingDoneCount==="number")
          ? drCounts.meetingDoneCount
          : dateFiltered.filter(function(r){return r.hadMeeting===true;}).length;
        var on = filterStatus==="__hadMeeting";
        return <button key="__hadMeeting" onClick={function(){setFilterStatus(on?"all":"__hadMeeting");}} style={{ padding:"5px 10px", borderRadius:7, border:"1px solid", borderColor:on?"#7C3AED":"#E8ECF1", background:on?"#F3E8FF":"#fff", color:on?"#5B21B6":C.textLight, fontSize:11, fontWeight:600, cursor:"pointer" }} title="Ever reached a meeting — includes requests whose status has since moved on, so this overlaps the status chips">{p.isMobile?"Had Meeting "+hadMeetingCount:"🤝 Had Meeting ("+hadMeetingCount+")"}</button>;
      })()}
      {/* No Agent — pseudo-filter; toggles off when re-clicked or when any other
          filter is chosen. Catches BOTH an empty agentId and one pointing at a
          user document that has been deleted: .populate() returns null for a
          deleted user, which is how this chip has always surfaced their work. */}
      {canSeeNoAgentChip&&(function(){
        var noAgentCount = (drCounts && typeof drCounts.noAgentCount==="number")
          ? drCounts.noAgentCount
          : dateFiltered.filter(function(r){var a=r.agentId&&r.agentId._id?r.agentId._id:r.agentId; return !a;}).length;
        var on = filterStatus==="__noAgent";
        return <button key="__noAgent" onClick={function(){setFilterStatus(on?"all":"__noAgent");}} style={{ padding:"5px 10px", borderRadius:7, border:"1px solid", borderColor:on?"#DC2626":"#E8ECF1", background:on?"#FEE2E2":"#fff", color:on?"#991B1B":C.textLight, fontSize:11, fontWeight:600, cursor:"pointer" }} title="Show only requests with no agent assigned">{p.isMobile?"No Agent "+noAgentCount:"👤 No Agent ("+noAgentCount+")"}</button>;
      })()}
      {/* Inactive Agent — pseudo-filter; agent still assigned but their account is
          deactivated. Admin/leadership only (a sales user's list is just themselves,
          so it would always read 0). Composes with the Date Added filter. */}
      {isAdmin&&(function(){
        var inactiveCount = (drCounts && typeof drCounts.inactiveAgentCount==="number")
          ? drCounts.inactiveAgentCount
          : dateFiltered.filter(isInactiveAgentRow).length;
        var on = filterStatus==="__inactiveAgent";
        return <button key="__inactiveAgent" onClick={function(){setFilterStatus(on?"all":"__inactiveAgent");}} style={{ padding:"5px 10px", borderRadius:7, border:"1px solid", borderColor:on?"#D97706":"#E8ECF1", background:on?"#FEF3C7":"#fff", color:on?"#92400E":C.textLight, fontSize:11, fontWeight:600, cursor:"pointer" }} title="Show only numbers whose assigned agent is deactivated">{p.isMobile?"Inactive Agent "+inactiveCount:"🚫 Inactive Agent ("+inactiveCount+")"}</button>;
      })()}
    </div>;
    var agentNode = isAdmin&&<SearchableSelect multi value={agentFilter} onChange={function(v){setAgentFilter(v);}}
        options={agentFilterOpts} emptyLabel={p.isMobile?"👤 Agent":"👤 All Agents"} placeholder="Search agent…" width={200} maxWidth={200} fontSize={12}/>;
    /* Project filter — ALL ROLES (no isAdmin gate). Options are derived from
              this user's own already-fetched requests, so they are role-scoped by
              construction. */
    var projectNode = <SearchableSelect multi value={projectFilterDR} onChange={function(v){setProjectFilterDR(v);}}
        options={drProjectGroups.map(function(o){ return { value:o.value, label:o.label+" ("+o.count+")" }; })}
        emptyLabel={p.isMobile?"🏢 Project":"🏢 All projects"} placeholder="Search project…" width={200} maxWidth={200} fontSize={12}/>;
    /* Location filter — ALL ROLES, like the project one beside it: the table
              already shows the Location column to every role, so the dropdown
              exposes nothing new. Options come from /api/filter-options?scope=dr
              (role-scoped server-side) rather than the loaded rows, because the
              fetch itself is location-filtered — see the state block above. */
    var locationNode = <SearchableSelect multi value={locationFilterDR} onChange={function(v){setLocationFilterDR(v);}}
        options={((drFilterOpts&&drFilterOpts.drLocations)||[]).map(function(o){ return { value:o.value, label:o.label+" ("+o.count+")" }; })}
        emptyLabel={p.isMobile?"📍 Location":"📍 All locations"} placeholder="Search location…" width={200} maxWidth={200} fontSize={12}/>;
    /* Property Type filter (2026-09-17) — ALL ROLES, like Project/Location
              beside it: the table already shows this column to every role. Options
              come straight from /api/filter-options?scope=dr's drPropertyTypes,
              same simple direct-consumption pattern as Location (no client-derived
              fallback — propertyType is a required field with no legacy gap to
              paper over). */
    var ptypeNode = <SearchableSelect multi value={propertyTypeFilterDR} onChange={function(v){setPropertyTypeFilterDR(v);}}
        options={((drFilterOpts&&drFilterOpts.drPropertyTypes)||[]).map(function(o){ return { value:o.value, label:o.label+" ("+o.count+")" }; })}
        emptyLabel={p.isMobile?"🏠 Type":"🏠 All property types"} placeholder="Search property type…" width={200} maxWidth={200} fontSize={12}/>;
    /* Native <select> -> app dropdown; see the Leads sort picker for why. */
    var sortNode = <SearchableSelect noSearch hideEmpty value={sortBy} onChange={function(v){setSortBy(v);}}
        options={DR_SORT_OPTIONS} width={150} maxWidth={150} fontSize={12}/>;
    var clearBtn = !p.isMobile&&(projectFilterDR.length||locationFilterDR.length||propertyTypeFilterDR.length||agentFilter.length||filterStatus!=="all"||addedRange!=="all"||p.search)&&
        <button onClick={drClearAll} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", fontSize:12, cursor:"pointer", color:C.danger }}>✕ Clear All</button>;

    // Date Added filter — the SHARED bar. Was admin-gated with its own,
    // wider chip set (Day Before Yesterday / Last 7 / Last 30); both the
    // gate and the divergent chips are gone — same component, same chips,
    // all roles, as on Leads.
    // No "Date Added:" label — Leads has none, and the chips read on their
    // own. Keeping the two bars byte-identical is the point of sharing them.
    var dateNode = <div style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap", alignItems:"center" }}>
      <DateFilterBar isMobile={p.isMobile} value={addedRange} onChange={setAddedRange}
        quarter={addedQuarter} onQuarter={setAddedQuarter}
        from={customFrom} to={customTo} onFrom={setCustomFrom} onTo={setCustomTo}/>
    </div>;
    if (!p.isMobile) {
      var selectsNode = <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
        {agentNode}{projectNode}{locationNode}{ptypeNode}{sortNode}{clearBtn}
      </div>;
      return <>
        {actionsNode}
        {chipsNode}
        {selectsNode}
        {dateNode}
      </>;
    }
    // ----- phone: ONE compact card — chips, 3x2 filter grid, active summary; ＋ lives in the header row -----
    var asArr = function(v){ return Array.isArray(v) ? v : (v ? [v] : []); };
    var many = function(v, icon, more){ var a = asArr(v); return a.length === 1 ? (icon+" "+a[0]) : (icon+" "+a.length+" "+more); };
    var pills = [];
    var dl = dateFilterPillLabel(addedRange, customFrom, customTo, addedQuarter);
    if (dl) pills.push({ key:"date", label:dl, onRemove:function(){ setAddedRange("all"); setCustomFrom(""); setCustomTo(""); } });
    if (asArr(agentFilter).length) {
      var agNames = asArr(agentFilter).map(function(id){ var o = agentFilterOpts.filter(function(x){ return String(x.value) === String(id); })[0]; return o ? o.label : ""; }).filter(Boolean);
      pills.push({ key:"agent", label: agNames.length === 1 ? ("👤 "+agNames[0]) : ("👤 "+asArr(agentFilter).length+" agents"), onRemove:function(){ setAgentFilter([]); } });
    }
    if (asArr(projectFilterDR).length) pills.push({ key:"project", label:many(projectFilterDR,"🏢","projects"), onRemove:function(){ setProjectFilterDR([]); } });
    if (asArr(locationFilterDR).length) pills.push({ key:"location", label:many(locationFilterDR,"📍","locations"), onRemove:function(){ setLocationFilterDR([]); } });
    if (asArr(propertyTypeFilterDR).length) pills.push({ key:"ptype", label:many(propertyTypeFilterDR,"🏠","property types"), onRemove:function(){ setPropertyTypeFilterDR([]); } });
    // Date as a compact dropdown over the SAME setter and option list the chip bar uses; Quarter / 📅 Date open
    // the existing quarter picker / the calendar chip inline below the grid (DateFilterBar, compact).
    var dateSel = <SearchableSelect noSearch hideEmpty value={addedRange} onChange={setAddedRange}
      options={DATE_FILTER_CHIPS.map(function(c){ return { value:c.v, label:"📅 "+(c.v === "all" ? "Date added" : c.v === "custom" ? "Date" : c.l) }; })}
      width="100%" maxWidth={9999} fontSize={12}/>;
    var dateInline = <DateFilterBar compact isMobile={p.isMobile} value={addedRange} onChange={setAddedRange}
        quarter={addedQuarter} onQuarter={setAddedQuarter}
        from={customFrom} to={customTo} onFrom={setCustomFrom} onTo={setCustomTo}/>;
    var isSalesPos = positionOf(p.cu) === "sales";
    var dateBarInline = <DateFilterBar isMobile={p.isMobile} value={addedRange} onChange={setAddedRange}
        quarter={addedQuarter} onQuarter={setAddedQuarter}
        from={customFrom} to={customTo} onFrom={setCustomFrom} onTo={setCustomTo}/>;
    var selSet = {}; selected2.forEach(function(id){ selSet[id] = 1; });
    var allSelectedHere = filtered.length > 0 && filtered.every(function(r){ return selSet[gid(r)]; });
    return <>
      <MobileFab open={mActionsOpen} hidden={hasRenderableSelected || mSelectMode} onToggle={function(){ setMActionsOpen(!mActionsOpen); }} onClose={function(){ setMActionsOpen(false); }}>{actionsNode.props.children}</MobileFab>
      {mSelectMode && <MobileSelectBar count={selected2.length} allSelected={allSelectedHere}
        onToggleAll={function(){ setSelected2(allSelectedHere ? [] : filtered.map(gid)); }}
        onReassign={function(){ setShowBulk(true); }}
        onCancel={function(){ setSelected2([]); setMSelectMode(false); }}/>}
      <div style={{ position:"relative", zIndex:90, background:"#F8FAFC", margin:"0 -16px 8px", padding:"8px 12px 8px", borderBottom:"1px solid #E8ECF1", display:"flex", flexDirection:"column", gap:8 }}>
        <ChipRows>{chipsNode.props.children}</ChipRows>
        {isSalesPos && dateBarInline}
        {isSalesPos
          ? <FilterGrid>{projectNode}{locationNode}{ptypeNode}</FilterGrid>
          : <FilterGrid>{dateSel}{agentNode}{projectNode}{locationNode}{ptypeNode}{sortNode}</FilterGrid>}
        {isSalesPos ? <ToggleRow items={[]}>{sortNode}</ToggleRow> : dateInline}
        <ActiveFilterStrip pills={pills} onClearAll={drClearAll}/>
      </div>
    </>;
    })()}
    {/* Active "Date Added" chip — shows the window + how many numbers entered
        the system in it, with an X to clear back to All Time. */}
    {isAdmin&&addedRange!=="all"&&<div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, padding:"10px 14px", background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:10, marginBottom:12 }}>
      <div style={{ fontSize:13, color:"#1D4ED8", fontWeight:600, minWidth:0, overflow:"hidden", textOverflow:"ellipsis" }}>Added: {addedLabel} <span style={{ color:"#64748B", fontWeight:500 }}>({drCounts?(drCounts.total||0):dateFiltered.length})</span></div>
      <button onClick={function(){setAddedRange("all");setCustomFrom("");setCustomTo("");}} style={{ background:"#fff", border:"1px solid #BFDBFE", color:"#1D4ED8", fontSize:12, fontWeight:600, padding:"4px 10px", borderRadius:8, cursor:"pointer", display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>Clear filter <X size={13}/></button>
    </div>}

    <div style={{ display:"flex", gap:14, paddingRight:!p.isMobile&&hasRenderableSelected?330:0, transition:"padding-right 0.25s" }}>
      <Card style={{ flex:1, padding:0, overflow:"hidden", minWidth:0 }}>
        {loading?<Loader/>:p.isMobile?<div style={{ display:"flex", flexDirection:"column", gap:12, padding:"12px 12px 170px", maxWidth:500, margin:"0 auto" }}>
          {filtered.length===0&&<div style={{ textAlign:"center", padding:40, color:C.textLight }}>No requests</div>}
          {hasRenderableSelected&&<div ref={drPanelRef} style={{ position:"fixed", inset:0, zIndex:300, background:"#fff", overflowY:"auto" }}>
            {/* Mobile detail panel - same as leads */}
            <div style={{ background:"linear-gradient(135deg,"+C.primary+","+C.primaryLight+")", padding:"calc(16px + env(safe-area-inset-top, 0px)) 16px 20px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <button onClick={function(){setSelected(null);}} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:8, width:32, height:32, cursor:"pointer", color:"#fff", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}>←</button>
                <div style={{ textAlign:"center", flex:1 }}>
                  <div style={{ color:"#fff", fontSize:16, fontWeight:700 }}>{selected.name}</div>
                  <div style={{ color:"rgba(255,255,255,0.7)", fontSize:12, direction:"ltr" }}><PhoneCell phone={selected.phone}/></div>
                </div>
                <div style={{ width:32 }}/>
              </div>
              <div style={{ display:"flex", gap:8, marginTop:14 }}>
                <a href={"tel:"+cleanPhone(selected.phone)} style={{ flex:1, padding:"10px", borderRadius:10, background:"#EFF6FF", color:"#1D4ED8", fontSize:13, fontWeight:700, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}><Phone size={14} color="#1D4ED8"/> Call</a>
                <a href={"https://wa.me/"+waPhone(selected.phone)} target="_blank" rel="noreferrer" style={{ flex:1, padding:"10px", borderRadius:10, background:"#DCFCE7", color:"#15803D", fontSize:13, fontWeight:700, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}><svg viewBox="0 0 24 24" width="13" height="13" fill="#15803D"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> WhatsApp</a>
              </div>
            </div>
            <div style={{ padding:"16px" }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                {[{l:"Project",v:selected.project,icon:"🏢"},{l:"Property Type",v:selected.propertyType,icon:"🏠"},{l:"Location",v:selected.area,icon:"📍"},{l:"Budget",v:selected.budget,icon:"💰"},{l:"Agent",v:getAgentName(selected),icon:"👤"},{l:"Callback",v:(callbackDisplayMode(selected)!=="hide"&&selected.callbackTime)?fmtWallDateTime12(selected.callbackTime, t):null,icon:"📞"},{l:"Last Activity",v:timeAgo(selected.lastActivityTime,t),icon:"🕐"}].map(function(f){return f.v?<div key={f.l} style={{ background:"#F8FAFC", borderRadius:12, padding:"12px 14px", border:"1px solid #E8ECF1" }}>
                  <div style={{ fontSize:10, color:C.textLight, marginBottom:4, fontWeight:600 }}>{f.icon} {f.l}</div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{f.v}</div>
                </div>:null;})}
              </div>
              {/* Pending location (2026-08-07) — this agent typed a location
                  that isn't in the managed collection. Admin/SA resolve it
                  here; everyone else just sees the free-text value in the
                  Location card above, exactly as before. */}
              {isOnlyAdmin && selected.locationPending && !selected.locationId && <div style={{ marginBottom:16 }}>
                <PendingLocationResolver request={selected} locations={drLocations} token={p.token}
                  onResolved={function(updatedReq, locObj){
                    if(updatedReq){
                      setRequests(function(prev){return prev.map(function(x){return gid(x)===gid(updatedReq)?Object.assign({},x,updatedReq):x;});});
                      setSelected(function(prev){ return (prev&&gid(prev)===gid(updatedReq))?Object.assign({},prev,updatedReq):prev; });
                    }
                    // "Add as new" may have minted a Location — graft it in so
                    // the form pickers offer it without waiting for a refetch.
                    if(locObj && locObj._id){
                      setDrLocations(function(prev){ var has=(prev||[]).some(function(x){return String(x._id)===String(locObj._id);}); return has?(prev||[]):(prev||[]).concat([locObj]); });
                    }
                    if(p.bumpPendingLocRev) p.bumpPendingLocRev();   // header bell refetches now, not in ≤60s
                  }}/>
              </div>}
              {selected.notes&&<div style={{ background:"#FFFBEB", borderRadius:12, padding:"12px 14px", border:"1px solid #FDE68A", marginBottom:16 }}>
                <div style={{ fontSize:10, color:"#92400E", fontWeight:600, marginBottom:4 }}>📝 Notes</div>
                <div style={{ fontSize:13, color:C.text }}>{selected.notes}</div>
              </div>}
              <div style={{ marginTop:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.textLight, marginBottom:8 }}>Change Status</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {sc.map(function(s){return <button key={s.value} onClick={function(){reqStatus(gid(selected),s.value);}} style={{ padding:"6px 12px", borderRadius:8, border:"1px solid", borderColor:selected.status===s.value?s.color:"#E2E8F0", background:selected.status===s.value?s.bg:"#fff", color:selected.status===s.value?s.color:C.textLight, fontSize:11, fontWeight:600, cursor:"pointer" }}>{s.label}</button>;})}
                </div>
              </div>
            </div>
          </div>}
          {/* STEP 2b — DR mobile cards virtualized via Virtuoso. The cards
              include an inline expandable "Mobile History" section when the
              row is the currently-selected one — Virtuoso auto-measures item
              heights via ResizeObserver, so the expand/collapse Just Works
              without any manual remeasure call. `marginBottom:12` on each
              card replaces the wrapper's flex `gap:12`. */}
          {filtered.length > 0 && <Virtuoso
            useWindowScroll
            increaseViewportBy={LIST_VIEWPORT_OVERSCAN}
            defaultItemHeight={LIST_ROW_H_CARD}
            data={filtered}
            endReached={loadMoreRequests}
            itemContent={function(idx, r){
              var rid=gid(r); var so=sc.find(function(s){return s.value===r.status;})||sc[0];
              var lastAct=r.lastActivityTime?timeAgo(r.lastActivityTime,t):"—";
              var actColor=(Date.now()-new Date(r.lastActivityTime||0).getTime())>3*24*60*60*1000?C.danger:C.accent;
              var cbModel=(callbackDisplayMode(r)!=="hide"&&r.callbackTime)?callbackStripModel(r.callbackTime,t,cbNow,callbackDisplayMode(r)==="neutral"):null;
              var borderCol=(cbModel&&cbModel.style.border)||so.color||"#E8ECF1";
              var selOn = selected2.indexOf(rid) >= 0;
              return <div onClick={function(){ if (mSelectMode) { setSelected2(function(prev){ return prev.indexOf(rid) >= 0 ? prev.filter(function(x){ return x !== rid; }) : prev.concat(rid); }); } else { setSelected(r); loadDrHistory(rid); } }}
                style={{ position:"relative", background:"#fff", borderRadius:16, padding:mSelectMode?"16px 16px 16px 50px":"16px", border:"2px solid "+borderCol, cursor:"pointer", boxShadow:"0 3px 12px "+borderCol+"35", marginBottom:12 }}>
              {mSelectMode&&<MobileSelectBox on={selOn}/>}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:2 }}>{r.name}</div>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text, direction:"ltr" }}><PhoneCell phone={r.phone}/></div>
                  {/* Same altPhoneCell dedupe as the table's Alt. Phone column, so
                      the two DR surfaces cannot disagree about what an alt phone is.
                      25 of the 60 requests carrying a phone2 repeat `phone` byte for
                      byte; the card used to print those twice, one under the other.
                      No pin here and none needed — a card's width comes from its
                      container, never from sibling content, so the width->height
                      feedback path the table has does not exist. */}
                  {(function(){var alt=altPhoneCell(r.phone, r.phone2);return alt?<div style={{ fontSize:11, fontWeight:700, color:C.textLight, direction:"ltr" }}><PhoneCell phone={alt}/></div>:null;})()}
                  {(function(){var agName=userNameOf(r.agentId, p.users);return agName?<div style={{ fontSize:11, color:C.accent, fontWeight:600, marginTop:2 }}>👤 {agName}{isInactiveAgentRow(r)&&<span style={{ marginLeft:6, background:"#FEF3C7", color:"#92400E", border:"1px solid #FDE68A", borderRadius:10, padding:"1px 6px", fontSize:9, fontWeight:700 }}>Inactive</span>}</div>:null;})()}
                </div>
                <span style={{ background:so.bg, color:so.color, padding:"5px 12px", borderRadius:20, fontSize:12, fontWeight:700, whiteSpace:"nowrap", marginLeft:8 }}>{so.label}</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {r.area&&<span style={{ fontSize:11, color:"#6D28D9", fontWeight:700, background:"#EDE9FE", padding:"2px 8px", borderRadius:6 }}>📍 {r.area}</span>}
                  {r.budget&&<span style={{ fontSize:11, color:C.success, fontWeight:700 }}>💰 {r.budget}</span>}
                </div>
                <span style={{ fontSize:11, color:actColor, fontWeight:600 }}>🕐 {lastAct}</span>
              </div>
              {/* Last Feedback */}
              {(r.lastFeedback||r.notes)&&<div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:8, padding:"6px 10px", background:"#F8FAFC", borderRadius:8, borderLeft:"3px solid "+C.accent }}>💬 {r.lastFeedback||r.notes}</div>}
              {/* Callback strip — CLOSED RECORD = NO CALLBACK (owner rule, 2026-09-08).
                  A Done Deal / EOI request renders no strip at all (cbModel is null);
                  Not Interested keeps the date in neutral grey, never an overdue level.
                  Wording, levels and the 24 h+ Reschedule pill live in
                  src/lib/callbackStrip.js; the card border follows the level. The
                  sheet writes through confirmStatus — r.callbackTime is not touched here. */}
              <CallbackStrip model={cbModel} variant="card" t={t} onReschedule={function(){setCbResched({leadId:rid,status:r.status,callbackTime:r.callbackTime});}}/>
              <div style={{ display:"flex", gap:8 }}>
                <a href={"tel:"+cleanPhone(r.phone)} onClick={function(e){e.stopPropagation();}} style={{ flex:1, padding:"10px", borderRadius:10, background:"#EFF6FF", color:"#1D4ED8", fontSize:13, fontWeight:700, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:5, border:"1px solid #BFDBFE" }}><Phone size={13} color="#1D4ED8"/> Call</a>
                <a href={"https://wa.me/"+waPhone(r.phone)} target="_blank" rel="noreferrer" onClick={function(e){e.stopPropagation();}} style={{ flex:1, padding:"10px", borderRadius:10, background:"#DCFCE7", color:"#15803D", fontSize:13, fontWeight:700, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:5, border:"1px solid #22C55E60" }}><svg viewBox="0 0 24 24" width="13" height="13" fill="#15803D"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> WhatsApp</a>
                <button onClick={function(e){e.stopPropagation();openDrHistory(r);}} style={{ padding:"10px 14px", borderRadius:10, background:"#F3E8FF", color:"#7C3AED", fontSize:13, fontWeight:700, border:"1px solid #DDD6FE", cursor:"pointer" }}>📋</button>
              </div>
              {/* Mobile History */}
              {selected&&gid(selected)===rid&&<div onClick={function(e){e.stopPropagation();}} style={{ marginTop:10 }}>
                {(function(){
                  var history=drHistory[rid]||[];
                  if(!history.length) return null;
                  return <div style={{ marginTop:10 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:C.textLight, marginBottom:6 }}>📋 History ({history.length})</div>
                    {history.map(function(a){
                      var who=userNameOf(a.userId, p.users);
                      var icon=a.type==="call"?"📞":a.type==="meeting"?"🤝":a.type==="status_change"?"🔄":"📝";
                      return <div key={String(a._id||Math.random())} style={{ padding:"8px 10px", background:"#F8FAFC", borderRadius:9, marginBottom:6, borderLeft:"3px solid "+C.accent }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                          <span style={{ fontSize:10, fontWeight:700 }}>{icon} {who}</span>
                          <span style={{ fontSize:9, color:C.textLight }}>{timeAgo(a.createdAt,t)}</span>
                        </div>
                        {a.note&&<div style={{ fontSize:11, color:C.textLight }}>{maskPhonesInText(a.note)}</div>}
                      </div>;
                    })}
                  </div>;
                })()}
              </div>}
            </div>;
            }}
          />}
        </div>:<div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
          {/* STEP 2a — empty state stays as a plain table for the "No requests"
              placeholder; non-empty state hands off to TableVirtuoso. Wrapper
              <div style="overflowX:auto"> preserved so narrow viewports still
              scroll horizontally without changing page width. useWindowScroll
              keeps the page-level scroll bar as the only vertical scroll. */}
          {filtered.length === 0 ? (
            <table data-table-align="sheet" style={{ width:"100%", borderCollapse:"collapse", minWidth:640 }}>
              <thead><tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
                {isOnlyAdmin&&<th style={{ padding:"10px 8px", width:32 }}><input type="checkbox" disabled/></th>}
                {["Name","Phone",t.phone2,"Project","Property Type","Location","Budget","Status","Last Feedback",isAdmin&&"Agent","Last Activity","Callback"].filter(Boolean).map(function(h){return <th key={h} style={Object.assign({ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:700, color:C.textLight, whiteSpace:"nowrap" }, h==="Callback"?CB_COL_PIN:(h==="Phone"||h===t.phone2)?PHONE_COL_PIN:null)}>{h}</th>;})}
              </tr></thead>
              <tbody>
                <tr><td colSpan={11} style={{ padding:40, textAlign:"center", color:C.textLight }}>No requests</td></tr>
              </tbody>
            </table>
          ) : (
            <TableVirtuoso
              useWindowScroll
              increaseViewportBy={LIST_VIEWPORT_OVERSCAN}
              defaultItemHeight={LIST_ROW_H_TABLE}
              data={filtered}
              components={drTableComponents}
              endReached={loadMoreRequests}
              fixedHeaderContent={function(){
                // Header "select all" must reflect the real selection and toggle OFF
                // when everything is already selected — see the matching fix on the
                // Leads page header. Uncontrolled (no `checked`) desynced after the
                // virtualized header re-rendered, so a 2nd click re-selected.
                var selSet2 = new Set(selected2);
                var allSel = filtered.length>0 && filtered.every(function(r){return selSet2.has(gid(r));});
                return <tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
                  {isOnlyAdmin&&<th style={{ padding:"10px 8px", width:32, background:"#F8FAFC" }}><input type="checkbox" checked={allSel} onChange={function(){setSelected2(allSel?[]:filtered.map(function(r){return gid(r);}));}}/></th>}
                  {/* The Status <th> carries the SAME pin as its <td>. Under
                      table-layout:auto a column is the max over every cell in it,
                      header included, so pinning only the body would leave the
                      header free to widen the column back out. These DR headers
                      carry no minWidth of their own, so nothing competes with it. */}
                  {["Name","Phone",t.phone2,"Project","Property Type","Location","Budget","Status","Last Feedback",isAdmin&&"Agent","Last Activity","Callback"].filter(Boolean).map(function(h){return <th key={h} style={Object.assign({ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:700, color:C.textLight, whiteSpace:"nowrap", background:"#F8FAFC" }, h==="Status"?STATUS_COL_PIN:h==="Callback"?CB_COL_PIN:(h==="Phone"||h===t.phone2)?PHONE_COL_PIN:null)}>{h}</th>;})}
                </tr>;
              }}
              itemContent={function(idx, r){
                var rid=gid(r); var so=sc.find(function(s){return s.value===r.status;})||sc[0];
                var cbMode=callbackDisplayMode(r); var isChk=selected2.includes(rid);
                return <Fragment>
                  {isOnlyAdmin&&<td style={{ padding:"10px 8px" }} onClick={function(e){e.stopPropagation();}}><input type="checkbox" checked={isChk} onChange={function(e){setSelected2(function(prev){return e.target.checked?prev.concat(rid):prev.filter(function(x){return x!==rid;});});}}/></td>}
                  <td style={{ padding:"10px 12px" }}><div style={{ fontSize:13, fontWeight:600 }}>{r.name}</div><div style={{ fontSize:10, color:C.textLight }}>{r.email}</div></td>
                  {/* Two columns, ONE renderer — see DrContactCell at module scope.
                      The second number used to be a small grey sub-line inside this
                      cell with no header of its own, which is why it read as "no Alt
                      Phone column". altPhoneCell is the same dedupe the DR Excel
                      export uses, so the column and the file cannot disagree. */}
                  <DrContactCell phone={r.phone} isMobile={p.isMobile}/>
                  <DrContactCell phone={altPhoneCell(r.phone, r.phone2)} isMobile={p.isMobile}/>
                  <td style={{ padding:"10px 12px", fontSize:12, color:C.textLight }}>{r.project||<span style={{color:"#CBD5E1"}}>-</span>}</td>
                  <td style={{ padding:"10px 12px", fontSize:12, color:C.textLight }}>{r.propertyType||"-"}</td>
                  <td style={{ padding:"10px 12px", fontSize:12, color:C.textLight }}>{r.area||"-"}</td>
                  <td style={{ padding:"10px 12px", fontSize:12, fontWeight:600, color:C.success }}>{r.budget||"-"}</td>
                  <td style={Object.assign({ padding:"10px 12px", position:"relative" }, STATUS_COL_PIN)} onClick={function(e){e.stopPropagation();}}>
                    {/* nowrap on the PILL as well as the cell. `{so.label} ▼` is two
                        text nodes, so in this inline-flex they are two flex items —
                        the flex line itself never wraps, but each item's own text
                        still could, which is how "Not Interested" split after "Not".
                        The cell pin sets the width; this stops the break inside it. */}
                    <span style={{ background:so.bg, color:so.color, padding:"4px 10px", borderRadius:20, fontSize:11, fontWeight:600, border:"1px dashed "+so.color, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:4, whiteSpace:"nowrap" }}
                      onClick={function(e){e.stopPropagation();setStatusDrop(statusDrop===rid?null:rid);}}>
                      {so.label} ▼
                    </span>
                    {statusDrop===rid&&<div data-overlay-above="true" style={Object.assign({ position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)", zIndex:500 }, SS_MENU_PANEL)} onClick={function(e){e.stopPropagation();}}>
                      <div style={SS_MENU_HEADER}>{t.changeStatus}</div>
                      <div style={{ overflowY:"auto" }}>
                      {sc.map(function(s){return <div key={s.value} onClick={function(e){e.stopPropagation();setSelected(r);reqStatus(rid,s.value);setStatusDrop(null);}} style={Object.assign({}, SS_MENU_ITEM, { background:r.status===s.value?s.bg:"transparent", fontWeight:r.status===s.value?600:400 })}
                        onMouseEnter={function(e){if(r.status!==s.value)e.currentTarget.style.background=SS_MENU_HOVER;}}
                        onMouseLeave={function(e){if(r.status!==s.value)e.currentTarget.style.background=r.status===s.value?s.bg:"transparent";}}>
                        <span style={Object.assign({}, SS_MENU_DOT, { background:s.color })}/><span style={{ color:s.color }}>{s.label}</span>
                      </div>;})}
                      </div>
                      <div style={SS_MENU_FOOTER}><button onClick={function(){setStatusDrop(null);}} style={SS_MENU_CANCEL}>{t.cancel}</button></div>
                    </div>}
                  </td>
                  {/* WIDTH IS PINNED, NOT CAPPED — this is the row-height
                      oscillation fix (owner capture 2026-08-15).

                      This cell was `maxWidth:160` with `whiteSpace:normal`. Under
                      `table-layout:auto` a cell's max-width only CAPS the column;
                      it does not floor it, so the column is sized from the content
                      of whichever rows are currently rendered — and Virtuoso keeps
                      changing that set. Measured locally at a 1024px viewport, this
                      same column came out 62px wide on one load and 105px on
                      another; it was never the 160 the style implies.

                      Wrap width decides line count, line count decides row height,
                      and row height is what Virtuoso measures. So a column that
                      moves with its neighbours gives a row whose measured height
                      moves too — measure -> layout -> re-measure, forever.

                      The owner's capture is exactly that: rows flipping between two
                      heights, strictly alternating (203,167,203,167,... and 73,75,
                      73,75,...), 1,800 layout shifts in 30s — one per animation
                      frame — with React idle (3 renders, 0 componentsBuilds). 203
                      and 167 are the same text wrapped at two different widths.

                      Pinning min = width = max makes the wrap width constant, so
                      row height becomes a pure function of the text and the loop
                      has nothing to feed on. TRADE-OFF: this column can no longer
                      be squeezed below 160px, so on a very narrow desktop window
                      the table reaches horizontal scroll slightly sooner. Mobile is
                      unaffected — it renders the card list, not this table. */}
                  <td style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:C.text, width:160, minWidth:160, maxWidth:160, wordBreak:"break-word", whiteSpace:"normal", lineHeight:1.4 }}>{r.lastFeedback||r.notes||<span style={{color:"#CBD5E1", fontWeight:400}}>-</span>}</td>
                  {isAdmin&&<td style={{ padding:"10px 12px", fontSize:11, color:C.textLight }} onClick={function(e){e.stopPropagation();}}>
                    {isInactiveAgentRow(r)&&<div style={{ marginBottom:3 }}><span style={{ background:"#FEF3C7", color:"#92400E", border:"1px solid #FDE68A", borderRadius:10, padding:"1px 6px", fontSize:9, fontWeight:700 }}>Inactive</span></div>}
                    <Select value={r.agentId&&r.agentId._id?r.agentId._id:(r.agentId||"")} onChange={async function(e){
                      var newAgent=e.target.value;
                      try{var upd=await apiFetch("/api/daily-requests/"+rid,"PUT",{agentId:newAgent},p.token);setRequests(function(prev){return prev.map(function(x){return gid(x)===rid?upd:x;});});if(selected&&gid(selected)===rid)setSelected(upd);}catch(ex){}
                    }} searchable hideEmpty renderRow={agentOptionRow} width={170} maxWidth={170} fontSize={13}
                      options={(function(){
                        var pool = isOnlyAdmin?salesUsers:(p.myTeamUsers||salesUsers).filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="team_leader")&&u.active;});
                        var ownerId = r.agentId&&r.agentId._id ? String(r.agentId._id) : (r.agentId ? String(r.agentId) : "");
                        var ownerName = userNameOf(r.agentId, p.users) || userNameOf(ownerId, p.users) || "Unknown";
                        return buildAgentOptions(pool, ownerId, assignedToday, isOnlyAdmin, ownerName);
                      })()}/>
                  </td>}
                  <td style={{ padding:"10px 12px", fontSize:11, color:C.accent }}>{timeAgo(r.lastActivityTime,t)}</td>
                  {/* CLOSED RECORD = NO CALLBACK. "hide" (Done Deal / EOI) renders an
                      EMPTY cell — deliberately different from the "-" below, which means
                      "open, no callback scheduled". A closed record has no callback to
                      have scheduled. The <td> stays for alignment. */}
                  <td style={Object.assign({ padding:"10px 12px", fontSize:11, textAlign:"center" }, CB_COL_PIN)}>
                    {cbMode==="hide"?null:r.callbackTime?<CallbackStrip model={callbackStripModel(r.callbackTime,t,cbNow,cbMode==="neutral",true)} variant="cell" t={t} onReschedule={function(){setCbResched({leadId:rid,status:r.status,callbackTime:r.callbackTime});}}/>:<span style={{ color:C.textLight }}>—</span>}
                  </td>
                </Fragment>;
              }}
            />
          )}
        </div>}
      </Card>

      {/* Side Panel */}
      {hasRenderableSelected&&<Card innerRef={drPanelRef} style={p.isMobile?{ position:"fixed", inset:0, zIndex:300, borderRadius:0, overflowY:"auto", padding:0, margin:0 }:{ position:"fixed", top:0, right:0, bottom:0, width:320, zIndex:300, borderRadius:0, overflowY:"auto", padding:0, boxShadow:"-4px 0 24px rgba(0,0,0,0.12)" }}>
        <div style={{ background:"linear-gradient(135deg,"+C.primary+","+C.primaryLight+")", padding:"calc(14px + env(safe-area-inset-top, 0px)) 16px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <button onClick={function(){setSelected(null);}} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:6, width:24, height:24, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff" }}><X size={11}/></button>
            <div style={{ display:"flex", gap:5 }}>
              {p.cu&&p.cu.role==="admin"&&<button onClick={function(){openEditReq(selected);}} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:6, width:24, height:24, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff" }} title={t.edit}><Edit size={11}/></button>}
              <button onClick={function(){openDrHistory(selected);}} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:6, width:24, height:24, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff" }} title="History">📋</button>
              {/* Owner-only. archiveOneReq clears `selected` on success, which is
                  what closes this panel — the record it describes is gone from
                  the list, so leaving the panel open would show a phantom. */}
              {canArchiveDR&&<button onClick={function(){archiveOneReq(gid(selected),selected.name);}} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:6, width:24, height:24, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff" }} title="Archive this request"><Archive size={11}/></button>}
            </div>
          </div>
          <div style={{ color:"#fff", fontSize:14, fontWeight:700 }}>{selected.name}</div>
          <div style={{ color:"rgba(255,255,255,0.65)", fontSize:11, marginTop:2 }}><PhoneCell phone={selected.phone}/>{selected.phone2?<>{" / "}<PhoneCell phone={selected.phone2}/></>:""}</div>
          <div style={{ display:"flex", gap:6, marginTop:10 }}>
            <a href={"tel:"+cleanPhone(selected.phone)} style={{ flex:1, padding:"6px", borderRadius:8, background:"rgba(34,197,94,0.2)", color:"#fff", fontSize:11, fontWeight:600, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:4 }}><Phone size={12}/> Call</a>
            <a href={"https://wa.me/"+waPhone(selected.phone)} target="_blank" rel="noreferrer" style={{ flex:1, padding:"6px", borderRadius:8, background:"rgba(37,211,102,0.2)", color:"#fff", fontSize:11, fontWeight:600, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:4 }}><svg viewBox="0 0 24 24" width="14" height="14" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> WhatsApp</a>
          </div>
        </div>
        <div style={{ padding:"12px 14px" }}>
          <div style={{ marginBottom:12, padding:10, background:"#F8FAFC", borderRadius:10 }}>
            <div style={{ fontSize:11, color:C.textLight, marginBottom:7, fontWeight:600 }}>{t.changeStatus}</div>
            <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
              {sc.map(function(s){return <button key={s.value} onClick={function(){reqStatus(gid(selected),s.value);}} style={{ padding:"3px 8px", borderRadius:6, border:"1px solid", borderColor:selected.status===s.value?s.color:"#E2E8F0", background:selected.status===s.value?s.bg:"#fff", color:selected.status===s.value?s.color:C.textLight, fontSize:10, fontWeight:600, cursor:"pointer" }}>{s.label}</button>;})}
            </div>
          </div>
          {[{l:"Project",v:selected.project},{l:"Property Type",v:selected.propertyType},{l:"Location",v:selected.area},{l:"Budget",v:selected.budget},{l:t.agent,v:getAgentName(selected)},{l:t.callbackTime,v:callbackDisplayMode(selected)==="hide"?null:selected.callbackTime?fmtWallDateTime12(selected.callbackTime, t):"-"},{l:t.lastActivity,v:timeAgo(selected.lastActivityTime,t)},{l:"Date Added",v:isOnlyAdmin?selected.createdAt?new Date(selected.createdAt).toLocaleDateString("en-GB"):"-":null},{l:t.notes,v:selected.notes}].map(function(f){
            return f.v?<div key={f.l} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid #F1F5F9", gap:8 }}><span style={{ fontSize:11, color:C.textLight, flexShrink:0 }}>{f.l}</span><span style={{ fontSize:11, fontWeight:500, textAlign:"right" }}>{f.v}</span></div>:null;
          })}


          {/* Feedback History */}
          {(function(){
            var sid = gid(selected);
            var history = drHistory[sid]||[];
            if(!history.length) return <div style={{ marginTop:14, fontSize:11, color:C.textLight, textAlign:"center" }}>No history yet</div>;
            return <div style={{ marginTop:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.textLight, marginBottom:8, textTransform:"uppercase", letterSpacing:0.5 }}>📋 History ({history.length})</div>
              {history.map(function(a){
                var who=userNameOf(a.userId, p.users);
                var icon=a.type==="call"?"📞":a.type==="meeting"?"🤝":a.type==="status_change"?"🔄":"📝";
                return <div key={String(a._id||a.id||Math.random())} style={{ padding:"8px 10px", background:"#F8FAFC", borderRadius:9, marginBottom:6, borderLeft:"3px solid "+C.accent }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:2 }}>
                    <span style={{ fontSize:10, fontWeight:700, color:C.text }}>{icon} {who}</span>
                    <span style={{ fontSize:9, color:C.textLight }}>{timeAgo(a.createdAt,t)}</span>
                  </div>
                  {a.note&&<div style={{ fontSize:11, color:C.textLight }}>{maskPhonesInText(a.note)}</div>}
                </div>;
              })}
            </div>;
          })()}
        </div>
      </Card>}
    </div>

    {/* DR History Modal */}
    {showDrHistory&&drHistoryReq&&<Modal show={true} onClose={function(){setShowDrHistory(false);setDrHistoryReq(null);}} title={"📋 History — "+drHistoryReq.name} w={520}>
      {drHistoryLoading&&<div style={{ textAlign:"center", padding:30, color:C.textLight }}>Loading...</div>}
      {!drHistoryLoading&&drHistoryList.length===0&&<div style={{ textAlign:"center", padding:30, color:C.textLight }}>No history yet</div>}
      {!drHistoryLoading&&drHistoryList.length>0&&<div style={{ maxHeight:400, overflowY:"auto" }}>
        {drHistoryList.map(function(a,i){
          var uname=userNameOf(a.userId, p.users);
          return <div key={a._id||i} style={{ padding:"10px 0", borderBottom:"1px solid #F1F5F9" }}>
            <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
              <span style={{ fontSize:16, flexShrink:0 }}>{a.type==="call"?"📞":a.type==="meeting"?"🤝":a.type==="status_change"?"🔄":a.type==="note"?"📝":"🔔"}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:500, color:C.text }}>{maskPhonesInText(a.note)}</div>
                <div style={{ fontSize:10, color:C.textLight, marginTop:3, display:"flex", gap:8 }}>
                  {uname&&<span style={{ fontWeight:600, color:C.accent }}>{uname}</span>}
                  <span>{a.createdAt?fmtDateTime12(a.createdAt, t, { numeric:true, sep:" — " }):""}</span>
                </div>
              </div>
            </div>
          </div>;
        })}
      </div>}
    </Modal>}

    <Modal show={showAdd} onClose={function(){setShowAdd(false);}} title={"➕ "+t.addRequest}>
      {/* ===== TWO PER ROW, ONE ON A PHONE (layout only, 2026-09-07) =====
          The whole form is ONE grid now — it used to be a 2-col grid for the
          first six fields and a stack of full-width rows for everything after
          Status, so the half of the form that matters at conversion time (unit,
          amount, developer, EOI date, deposit) was the long messy half.

          drFormGrid collapses to a single column under p.isMobile (innerWidth
          < 768, re-evaluated on resize), because two 218px columns inside a
          500px modal is exactly the squeezed layout this is fixing. Pairs are
          semantic, not just adjacent: phone+alt, project+location, type+budget,
          agent+status, unit+amount, EOI date+deposit, callback+feedback.

          NOTHING FUNCTIONAL MOVES. Same fields, same conditions, same handlers,
          same validation, same submit — only wrappers and order. */}
      <div style={drFormGrid}>
        <div style={{ gridColumn:"1/-1" }}><Inp label={"Name"} req value={form.name} onChange={function(e){setForm(function(f){return Object.assign({},f,{name:e.target.value});})}}/></div>
        <Inp label={"Phone"} req value={form.phone} onChange={function(e){setForm(function(f){return Object.assign({},f,{phone:e.target.value});})}} placeholder=""/>
        <Inp label={t.phone2} value={form.phone2} onChange={function(e){setForm(function(f){return Object.assign({},f,{phone2:e.target.value});})}} placeholder=""/>
        {/* Project — REQUIRED, and positioned before Property Type per the
            owner's spec. Searchable over the projects already present in the
            data (role-scoped via /api/filter-options) with allowCustom so a
            genuinely new development can still be typed in. Shown for every
            status, including EOI/DoneDeal, where it doubles as the mirror
            Lead's project (it replaced the old separate "🏠 Project" input). */}
        {/* Project — FREE TEXT (owner, 2026-08-08). Reverted from the searchable
            dropdown: DR-stage projects are many and change fast, so a curated
            list was always stale and slowed the agent down. The FORMAL project
            is set properly at deal time; this is the agent's own note of what
            the client asked for. Still required. */}
        <Inp label={"🏢 Project"} req
          value={form.project} onChange={function(e){setForm(function(f){return Object.assign({},f,{project:e.target.value});})}}
          placeholder="e.g. Sarai, Naia Bay, Mivida…"/>
        {/* Location — managed vocabulary (2026-08-07). Searchable over the
            active Location collection with allowCustom, mirroring the Project
            picker above. A typed name that isn't in the list is sent as
            locationName: the backend mints it immediately for admin/SA, and
            queues it as locationPending for everyone else (same approval flow
            as a new developer). The value is the NAME, not the id — the server
            resolves it, so the form never has to know the collection's ids. */}
        <Inp label={"📍 Location"} req searchable allowCustom type="select"
          value={form.area} onChange={function(e){setForm(function(f){return Object.assign({},f,{area:e.target.value});})}}
          options={drLocationOpts(form.area)}
          searchPlaceholder="Search or type a location…"/>
        {/* Property Type — REQUIRED (owner, 2026-08-08). Only rendered for the
            normal pipeline: an EOI/DoneDeal add captures the type as "🏷️ Unit
            Type" instead, so "required" means "required when shown". The submit
            guard and the server rule below are both scoped the same way. */}
        {form.status!=="EOI"&&form.status!=="DoneDeal"&&<Inp label={"Property Type"} req type="select" value={form.propertyType} onChange={function(e){setForm(function(f){return Object.assign({},f,{propertyType:e.target.value});})}} options={[""].concat(PROP_TYPES).map(function(x){return{value:x,label:x||"- Select -"};})}/>}
        {form.status!=="EOI"&&form.status!=="DoneDeal"&&<Inp label={"Budget"} req value={form.budget} onChange={function(e){setForm(function(f){return Object.assign({},f,{budget:(function(){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");return r?Number(r).toLocaleString():"";})()});})}}/>}
      {isAdmin&&<Inp label={t.agent} searchable type="select" value={form.agentId} onChange={function(e){setForm(function(f){return Object.assign({},f,{agentId:e.target.value});})}} options={[{value:"",label:"- Select -"}].concat(withCurrentOption(salesUsers.map(function(u){return{value:gid(u),label:u.name};}), form.agentId, userNameOf(form.agentId, p.users) || "Unknown"))}/>}
      {/* Status pairs with Agent for an admin; with no Agent beside it (sales
          adding their own request) it spans the row rather than sitting half-width. */}
      <div style={isAdmin?null:drFormFull}>
        <Inp label={t.status} req type="select" value={form.status||""} onChange={function(e){setForm(function(f){return Object.assign({},f,{status:e.target.value});})}} options={sc.map(function(s){return{value:s.value,label:s.label};})}/>
      </div>
      {(form.status==="EOI"||form.status==="DoneDeal")&&<div style={Object.assign({}, drFormGrid, drFormFull)}>
        <Inp label={"🏷️ Unit Type"} type="select" value={form.dealUnitType} onChange={function(e){setForm(function(f){return Object.assign({},f,{dealUnitType:e.target.value});})}} options={["","Apartment","Duplex","Townhouse","Twinhouse","Standalone","Commercial","Admin","Clinic","Service Apartment","Chalet"].map(function(x){return{value:x,label:x||"- Select -"};})}/>
        <Inp label={"💰 Amount (EGP)"} req value={form.dealBudget} onChange={function(e){setForm(function(f){return Object.assign({},f,{dealBudget:(function(){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");return r?Number(r).toLocaleString():"";})()});})}}/>
        {/* Developer — REQUIRED, same picker and same wording as the Leads EOI /
            Deal form and the status-change modal: pick one of the managed
            developers, or type a new name for operation to link later. One
            component, so this renders identically on mobile and desktop. */}
        <div style={Object.assign({}, drFormFull, { marginBottom:13 })}>
          <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>🏗️ Developer <span style={{color:C.danger}}>*</span></label>
          {!drDevPendOpen && <SearchableSelect
            value={drDevSelId}
            emptyLabel="— Select developer —"
            placeholder="Search developers…"
            width="100%" maxWidth={9999} fontSize={13}
            options={(drDevList||[]).map(function(dv){ return { value:String(dv._id), label:dv.name }; })}
            onChange={function(val){ setDrDevSelId(val); if(val) setDrDevPendName(""); }}/>}
          {drDevPendOpen && <input type="text" value={drDevPendName} placeholder="New developer name"
            onChange={function(e){ setDrDevPendName(e.target.value); setDrDevSelId(""); }}
            style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>}
          <button type="button" onClick={function(){ if(drDevPendOpen){ setDrDevPendOpen(false); setDrDevPendName(""); } else { setDrDevPendOpen(true); setDrDevSelId(""); } }}
            style={{ marginTop:6, background:"none", border:"none", padding:0, color:C.info, fontSize:12, cursor:"pointer", textDecoration:"underline", maxWidth:"100%", textAlign:"left", whiteSpace:"normal" }}>
            {drDevPendOpen ? "← Pick from the list instead" : "Can't find the developer? Enter a new name"}
          </button>
        </div>
        {form.status==="EOI"&&<Inp label={"📅 EOI Date"} type="date" value={form.eoiDateInput} onChange={function(e){setForm(function(f){return Object.assign({},f,{eoiDateInput:e.target.value});})}}/>}
        {form.status==="EOI"&&<Inp label={"💵 Deposit (EGP)"} value={form.eoiDeposit} onChange={function(e){setForm(function(f){return Object.assign({},f,{eoiDeposit:(function(){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");return r?Number(r).toLocaleString():"";})()});})}}/>}
        <DocumentsUpload
          files={form.eoiDocFiles||[]}
          onChange={function(next){setForm(function(f){return Object.assign({},f,{eoiDocFiles:next});});}}
          label={form.status==="EOI"?"📎 Upload EOI Documents":"📎 Upload Deal Documents"}
          wrapperStyle={Object.assign({}, drFormFull, { marginBottom:13 })}
        />
      </div>}
      {form.status!=="EOI"&&form.status!=="DoneDeal"&&<Inp label={t.callbackTime} req type="datetime-local" value={form.callbackTime} onChange={function(e){setForm(function(f){return Object.assign({},f,{callbackTime:e.target.value});})}}/>}
      {form.status==="EOI"||form.status==="DoneDeal"
        ? <div style={drFormFull}><Inp label={"Feedback"} type="textarea" value={form.notes} onChange={function(e){setForm(function(f){return Object.assign({},f,{notes:e.target.value});})}}/></div>
        : <Inp label={"Feedback"} req type="textarea" value={form.notes} onChange={function(e){setForm(function(f){return Object.assign({},f,{notes:e.target.value});})}}/>}
      <div style={Object.assign({}, drFormFull, { display:"flex", gap:10 })}><Btn outline onClick={function(){setShowAdd(false);}} style={{ flex:1 }}>{t.cancel}</Btn><Btn onClick={addReq} loading={saving} disabled={(form.status==="EOI"||form.status==="DoneDeal") && !drDevSelId && !drDevPendName.trim()} style={{ flex:1 }}>{t.add}</Btn></div>
      </div>
    </Modal>

    {/* Admin-only edit modal (Owner role only — gated at the side-panel button) */}
    {editReq&&<Modal show={true} onClose={function(){if(!editSaving)setEditReq(null);}} title={"✏️ Edit Request"}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
        <div style={{ gridColumn:"1/-1" }}><Inp label={"Name"} req value={editForm.name} onChange={function(e){setEditForm(function(f){return Object.assign({},f,{name:e.target.value});})}}/></div>
        <Inp label={"Phone"} req value={editForm.phone} onChange={function(e){setEditForm(function(f){return Object.assign({},f,{phone:e.target.value});})}}/>
        <Inp label={t.phone2} value={editForm.phone2} onChange={function(e){setEditForm(function(f){return Object.assign({},f,{phone2:e.target.value});})}}/>
        {/* Free text, matching the New Request form. Deliberately NOT marked
            required here: 96.4% of existing requests (2945 of 3054) predate the
            project field and carry "", so requiring it would make almost every
            historical request impossible to edit at all. */}
        <div style={{ gridColumn:"1/-1" }}><Inp label={"🏢 Project"}
          value={editForm.project} onChange={function(e){setEditForm(function(f){return Object.assign({},f,{project:e.target.value});})}}
          placeholder="e.g. Sarai, Naia Bay, Mivida…"/></div>
        <Inp label={"Property Type"} req type="select" value={editForm.propertyType} onChange={function(e){setEditForm(function(f){return Object.assign({},f,{propertyType:e.target.value});})}} options={[""].concat(PROP_TYPES).map(function(x){return{value:x,label:x||"- Select -"};})}/>
        <Inp label={"📍 Location"} searchable allowCustom type="select"
          value={editForm.area} onChange={function(e){setEditForm(function(f){return Object.assign({},f,{area:e.target.value});})}}
          options={drLocationOpts(editForm.area)}
          searchPlaceholder="Search or type a location…"/>
        <div style={{ gridColumn:"1/-1" }}><Inp label={"Budget"} value={editForm.budget} onChange={function(e){setEditForm(function(f){return Object.assign({},f,{budget:(function(){var rb=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");return rb?Number(rb).toLocaleString():"";})()});})}}/></div>
      </div>
      <Inp label={t.agent} searchable type="select" value={editForm.agentId} onChange={function(e){setEditForm(function(f){return Object.assign({},f,{agentId:e.target.value});})}} options={[{value:"",label:"- Select -"}].concat(withCurrentOption(salesUsers.map(function(u){return{value:gid(u),label:u.name};}), editForm.agentId, (userNameOf(editForm.agentId, p.users) ? userNameOf(editForm.agentId, p.users) + " (inactive)" : "Unknown")))}/>
      <Inp label={t.callbackTime} type="datetime-local" value={editForm.callbackTime?String(editForm.callbackTime).slice(0,16):""} onChange={function(e){setEditForm(function(f){return Object.assign({},f,{callbackTime:e.target.value});})}}/>
      <Inp label={t.status} type="select" value={editForm.status||""} onChange={function(e){setEditForm(function(f){return Object.assign({},f,{status:e.target.value});})}} options={sc.map(function(s){return{value:s.value,label:s.label};})}/>
      <Inp label={"Feedback"} type="textarea" value={editForm.notes} onChange={function(e){setEditForm(function(f){return Object.assign({},f,{notes:e.target.value});})}}/>
      <div style={{ display:"flex", gap:10 }}>
        <Btn outline onClick={function(){setEditReq(null);}} disabled={editSaving} style={{ flex:1 }}>{t.cancel}</Btn>
        <Btn onClick={saveEditReq} loading={editSaving} style={{ flex:1 }}>💾 Save Changes</Btn>
      </div>
    </Modal>}

    <Modal show={showBulk} onClose={function(){ if(!bulkBusy){ setShowBulk(false); setBulkErr(""); } }} title={"Bulk Reassign"}>
      {selected2.length===0
        ?<div style={{ padding:"16px", textAlign:"center", color:C.danger, fontSize:13 }}>⚠️ Please select leads first using the checkboxes</div>
        :<div>
          <div style={{ marginBottom:10, fontSize:13, color:C.textLight }}>{selected2.length} leads selected</div>
          <BulkDistributionEditor rows={bulkRows} setRows={setBulkRows} selectedCount={selected2.length} disabled={bulkBusy} noun="requests"
            roster={salesUsers.map(function(u){return{value:gid(u),label:u.name};})}/>
          <div style={{ display:"flex", gap:10, marginTop:10 }}>
            <Btn loading={bulkBusy} disabled={bulkBusy||!bulkDistributionTotals(bulkRows, selected2.length).canSubmit} onClick={async function(){
              if(!bulkDistributionTotals(bulkRows, selected2.length).canSubmit) return;
              setBulkBusy(true); setBulkErr("");
              try{
                var orderedIds = orderSelectedByDisplay(selected2, filtered);
                var distribution = bulkRows.map(function(r){ return { agentId:r.agentId, count:parseInt(r.count,10) }; });
                var res = await apiFetch("/api/daily-requests/bulk-reassign","PUT",{ leadIds:orderedIds, distribution:distribution },p.token,p.csrfToken);
                // Patch agentId from the server's re-read drs[] (populated
                // {_id,name,title}) — the same payload the WS broadcast carries.
                var agentByDr = {};
                (Array.isArray(res && res.drs) ? res.drs : []).forEach(function(d){ if (d && d._id) agentByDr[String(d._id)] = d.agentId || null; });
                setRequests(function(prev){ return prev.map(function(r){ var k = gid(r); return Object.prototype.hasOwnProperty.call(agentByDr, k) ? Object.assign({},r,{agentId:agentByDr[k]}) : r; }); });
                setSelected2([]); setShowBulk(false); setBulkRows([{ agentId:"", count:"" }]);
                var summary = bulkReassignSummary(res, "requests");
                if (summary) alert(summary);
              }catch(e){ setBulkErr((e&&e.message)||"Something went wrong"); }
              setBulkBusy(false);
            }} style={{ flex:1 }}>Bulk Reassign</Btn>
            <Btn outline disabled={bulkBusy} onClick={function(){setShowBulk(false);setBulkErr("");}} style={{ flex:1 }}>{t.cancel}</Btn>
          </div>
          {bulkErr&&<div style={{ marginTop:10, padding:"8px 10px", background:"#FEF3F2", color:C.danger, borderRadius:8, fontSize:12, lineHeight:1.5 }}>{bulkErr}</div>}
        </div>
      }
    </Modal>

    {/* Move to Leads as Fresh — confirmation */}
    <Modal show={showMove} onClose={function(){ if(!moving){ setShowMove(false); setMoveErr(""); } }} title={"Move to Leads as Fresh"}>
      {selected2.length===0
        ?<div style={{ padding:"16px", textAlign:"center", color:C.danger, fontSize:13 }}>⚠️ Please select requests first using the checkboxes</div>
        :<div>
          <div style={{ fontSize:13, color:C.text, lineHeight:1.6, marginBottom:12 }}>
            <b>{selected2.length}</b> daily request{selected2.length>1?"s":""} will be converted into <b>fresh New Leads</b> with <b>no agent</b> and enter the rotation engine (auto-distributed to active agents).
            <ul style={{ margin:"10px 0 0", paddingInlineStart:18, color:C.textLight, fontSize:12 }}>
              <li>The original daily request is archived (removed from this page).</li>
              <li>Numbers that already exist as a lead are skipped and reported.</li>
              <li>Closed requests (deal / EOI) are skipped.</li>
            </ul>
          </div>
          {selected2.length>200&&<div style={{ padding:"8px 10px", background:"#FEF3F2", color:C.danger, borderRadius:8, fontSize:12, marginBottom:10 }}>⚠️ Max 200 per batch — please select 200 or fewer.</div>}
          <div style={{ display:"flex", gap:10, marginTop:4 }}>
            <Btn loading={moving} disabled={selected2.length>200||moving} onClick={async function(){
              setMoving(true); setMoveErr("");
              try{
                var resp=await apiFetch("/api/daily-requests/move-to-leads","POST",{ids:selected2},p.token,p.csrfToken);
                // Drop moved (created) + already-archived DRs from the list immediately.
                var movedIds={};
                (resp.created||[]).forEach(function(x){ movedIds[String(x.drId)]=true; });
                (resp.skippedClosed||[]).forEach(function(x){ if(x.reason==="archived"||x.reason==="already moved") movedIds[String(x.drId)]=true; });
                setRequests(function(prev){return prev.filter(function(r){return !movedIds[String(gid(r))];});});
                if(selected&&movedIds[String(gid(selected))])setSelected(null);
                setSelected2([]);
                setShowMove(false);
                setMoveResult(resp);
              }catch(e){
                // Show the error IN the modal (no raw browser alert). The conversion
                // is idempotent — already-moved DRs are skipped — so a timeout/drop is
                // safe: the backend may have finished; refreshing + re-running never
                // duplicates. Say that instead of a scary raw message.
                var msg=(e&&e.message)||"Something went wrong";
                if(/tim(e|ed)\s*out|timeout/i.test(msg)){
                  setMoveErr("This is taking longer than expected. Some or all of the selected numbers may already have been moved — close this, refresh the page, and re-run on any rows that remain. Nothing gets duplicated.");
                }else{
                  setMoveErr(msg);
                }
              }
              setMoving(false);
            }} style={{ flex:1 }}>➡️ Move {selected2.length} to Leads</Btn>
            <Btn outline disabled={moving} onClick={function(){setShowMove(false);setMoveErr("");}} style={{ flex:1 }}>{t.cancel}</Btn>
          </div>
          {moving&&<div style={{ marginTop:10, fontSize:12, color:C.textLight, display:"flex", alignItems:"center", gap:6 }}>⏳ Processing {selected2.length} request{selected2.length>1?"s":""}… this can take a few seconds for large batches. Please keep this window open.</div>}
          {moveErr&&<div style={{ marginTop:10, padding:"8px 10px", background:"#FEF3F2", color:C.danger, borderRadius:8, fontSize:12, lineHeight:1.5 }}>{moveErr}</div>}
        </div>
      }
    </Modal>

    {/* Move to Leads as Fresh — result report */}
    <Modal show={!!moveResult} onClose={function(){setMoveResult(null);}} title={"Move to Leads — Result"}>
      {moveResult&&(function(){
        var c=moveResult.counts||{};
        var row=function(label,val,color){ return <div style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid #F0F2F5", fontSize:13 }}><span style={{ color:C.textLight }}>{label}</span><b style={{ color:color||C.text }}>{val}</b></div>; };
        return <div>
          {row("✅ Created as fresh leads", c.created||0, C.success)}
          {row("⏭️ Skipped — duplicate phone", c.skippedDuplicates||0, C.warning)}
          {row("⏭️ Skipped — closed (deal / EOI)", c.skippedClosed||0, C.textLight)}
          {row("❌ Failed", c.failed||0, (c.failed>0?C.danger:C.textLight))}
          {moveResult.skippedDuplicates&&moveResult.skippedDuplicates.length>0&&<div style={{ marginTop:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.textLight, marginBottom:6 }}>Skipped duplicate numbers (already a lead)</div>
            <div style={{ maxHeight:180, overflowY:"auto", background:"#F8FAFC", borderRadius:8, padding:"6px 10px" }}>
              {moveResult.skippedDuplicates.map(function(x,i){ return <div key={i} style={{ fontSize:12, direction:"ltr", textAlign:"left", padding:"3px 0", color:C.text }}>{x.phone}</div>; })}
            </div>
          </div>}
          <div style={{ marginTop:14 }}>
            <Btn onClick={function(){setMoveResult(null);}} style={{ width:"100%" }}>Close</Btn>
          </div>
        </div>;
      })()}
    </Modal>
  </div>;
};

// ===== USERS =====
// Job titles are now DERIVED from the ladders (DEPARTMENT_LADDERS_FE, mirrored
// from the server) instead of being a hand-kept list. The flat {label, role}
// shape is preserved exactly, so ROLE_FOR_TITLE and jobTitlesByRole() keep
// working unchanged — only the source moved. `rank` and `ladder` ride along for
// the picker's ordering; nothing else reads them yet.
//
// The sales rows come out in the same order as the old literal array, because
// the ladder is declared rank-ascending and the four sales roles occupy
// contiguous rank blocks. Three ladders that had no titles before — operation,
// hr, office — now contribute their own groups.
var JOB_TITLES = LADDER_KEYS_FE.reduce(function(acc, k){
  DEPARTMENT_LADDERS_FE[k].titles.forEach(function(e){
    acc.push({ label:e.title, role:e.role, rank:e.rank, ladder:k });
  });
  return acc;
}, []);
// THE ROLE THAT OWNS A LADDER — not the band the title sits in.
//
// A ladder entry's `role` column names a POSITION. Before the sales collapse
// every position was also a declared role, so mapping a title straight to it
// worked. It does not now: six of the ten sales titles name team_leader /
// manager / director, which are bands, and setting a draft's role to one of
// those produces a user the server's enum refuses. Picking "Team Leader" in the
// Add User form did exactly that.
var roleOwningLadderFE = function(ladderKey){
  var owners = ROLE_KEYS_FE.filter(function(r){ return ROLE_DEFINITIONS_FE[r].ladder === ladderKey; });
  return owners.length ? owners[0] : null;
};
// Titles for ONE role — the ladder it owns, in rank order. This is what the
// Add/Edit User Job Title picker offers once a Role is chosen.
var titlesForRoleFE = function(role){
  var k = ladderOfFE(role);
  if (!k) return [];
  return (DEPARTMENT_LADDERS_FE[k].titles || []).slice()
    .sort(function(a,b){ return (a.rank||0) - (b.rank||0); })
    .map(function(e){ return { label:e.title, role:role, rank:e.rank, ladder:k }; });
};
var ROLE_FOR_TITLE = JOB_TITLES.reduce(function(a,j){ a[j.label]=roleOwningLadderFE(j.ladder); return a; }, {});

// ===== Job Titles grouped by role — the registry contract (2026-08-16) =====
// WHICH groups exist comes from the role registry (hasJobTitles), not from a
// hand-kept list, so a future role appears here automatically the day it is
// declared — and the build gate refuses a role that claims job titles but has
// none, or has titles but claims not to.
//
// `titles` is a parameter rather than a closed-over constant so this keeps
// working unchanged when job titles move from the JOB_TITLES array to
// owner-managed data behind the Settings screen: that screen passes its fetched
// rows in and gets the same grouping.
//
// Groups come back in REGISTRY order (seniority-descending: Sales Director →
// Sales Agent), which is the same order the Role picker directly above these
// fields uses. Empty groups are returned, not dropped — the Settings screen
// needs a group to add the first title into.
var jobTitlesByRole = function(titles){
  var list = titles || JOB_TITLES;
  return rolesWithJobTitles().map(function(role){
    return {
      role:   role,
      label:  roleLabel(role, null),
      titles: list.filter(function(j){ return j.role === role; })
    };
  });
};

// Flat option list for the Job Title <select>, carrying `group` so
// SearchableSelect renders a heading per role.
//
// Still grouped by jobTitlesByRole() — one grouping definition, shared with the
// Job Titles Settings screen — but ORDERED here, which is the picker's own
// presentation choice: ladder by ladder in declaration order (sales, operation,
// hr, office), and by ascending rank within each.
//
// That reproduces the previous ordering for sales exactly. The old code reversed
// the registry order, which for the sales ladder happens to be rank 1 -> 10; now
// the rank says so directly instead of it being a coincidence of two orderings.
// Sales still leads the list — most hires are agents — whereas a plain reversal
// would now put Office Boy first, since three more ladders contribute groups.
//
// EVERY group keeps its header, including single-title ones. Collapsing the
// header on a redundant group ("SALES DIRECTOR" above a lone "Sales Director")
// was tried and reverted: a headerless entry trailing a headed group is absorbed
// into it visually, so Sales Director read as a Sales Manager option. A repeated
// label is cheaper than a miscategorised one.
// ROLE-FILTERED PICKER (2026-08-21). Passing a role returns ONLY that role's
// ladder, in rank order, with NO group headers — one ladder needs no headings,
// and an Operations hire has no business scrolling past ten sales titles.
//
// This was never filtered: measured at a2003f8, 72ac3bb, 921bc56 and ba0924a,
// the picker always returned every ladder. The sales collapse only changed how
// it READS (four sales groups merged into one "Sales"), which is what made a
// long-standing wrong list newly obvious.
//
// The no-role form is kept for the Job Titles Settings screen, which lists
// everything on purpose and needs the group headers.
var jobTitleOptionsForRole = function(role){
  return titlesForRoleFE(role).map(function(j){ return { value:j.label, label:j.label }; });
};
var jobTitleOptions = function(titles){
  var minRank = function(g){
    return g.titles.reduce(function(m, j){ return Math.min(m, j.rank || 0); }, Infinity);
  };
  var out = [];
  jobTitlesByRole(titles).slice().sort(function(a, b){
    var la = LADDER_KEYS_FE.indexOf(ladderOfFE(a.role));
    var lb = LADDER_KEYS_FE.indexOf(ladderOfFE(b.role));
    return (la - lb) || (minRank(a) - minRank(b));
  }).forEach(function(g){
    g.titles.slice().sort(function(a, b){ return (a.rank || 0) - (b.rank || 0); })
      .forEach(function(j){
        out.push({ value:j.label, label:j.label, group:g.label });
      });
  });
  return out;
};
// Shared role <select> options for the Add/Edit User modals (so they can't drift).
// Takes `t` because labels are translated. `admin` is intentionally excluded (owner-only, locked).
// Derived from the role registry: every creatable role, in registry order, with
// its translated label. `admin` is excluded by creatable:false (owner-only, and
// the backend refuses to create one) rather than by being left off a hand list.
var USER_ROLE_OPTIONS = function(t){
  return rolesWhereFE(function(d){ return d.creatable; })
    .map(function(r){ return { value:r, label:roleLabel(r, t) }; });
};

// ===== Org hierarchy pickers — shared helpers (2026-08-15) =====
// These replaced the free-text "Team Name" / "Team Code" inputs. The creator
// picks ONE leader by name and everything above is walked from the tree.
//
// The role map below decides only what to OFFER in the dropdown. It is NOT a
// validation mirror: legality is decided by the server (validateReportsTo), and
// nothing here blocks a save. If the two ever disagree, the server wins and its
// message is what the user sees.
//
// Position-based, not title-based: `manager` appears as a legal parent of BOTH
// `sales` and `team_leader`, because a manager can hold agents directly while
// also sitting above a team leader who holds more. That is what lets the same
// person be offered as the Team Leader on one row and shown as the Manager on
// another.
// Derived from ROLE_DEFINITIONS_FE.parents — the null-vs-[] distinction lives in
// the registry now (null = outside the hierarchy, [] = in it at the top).
var HIERARCHY_PARENT_ROLES_FE = rolesWhereFE(function(d){ return d.parents !== null; })
  .reduce(function(a, r){ a[r] = ROLE_DEFINITIONS_FE[r].parents; return a; }, {});
// True when this role has a leader to pick at all. Everything else (admin,
// operation, hr, office_boy, viewer, and director) hides the whole block.
// The BAND a draft user would land in, for the flags that are band-bound rather
// than role-bound (today: hasTargets, ranks 1-6). A draft is not a loaded user —
// it carries the role picked and the Job Title picked, so positionOf resolves it
// through the title's rank. Falls back to the raw role when the title yields no
// rank, deliberately: the create is refused for a missing title anyway, and the
// fallback keeps the message identical for a draft that is wrong in both ways.
var targetPositionOfDraft = function(draft){
  if (!draft) return "";
  var rank = rankOfTitleFE(draft.role, draft.title);
  return (typeof rank === "number") ? positionOf({ role: draft.role, titleRank: rank }) : draft.role;
};
var roleNeedsHierarchy = function(role){
  var a = HIERARCHY_PARENT_ROLES_FE[role];
  return !!a && a.length > 0;
};

// reportsTo arrives either populated ({_id,name}) or raw — normalize to a string.
var rtIdOf = function(u){
  if (!u || !u.reportsTo) return "";
  return u.reportsTo._id ? String(u.reportsTo._id) : String(u.reportsTo);
};

// hierarchyCandidates — users offerable as the DIRECT leader of `childRole`.
// Active only (a deactivated leader is refused by the server anyway, and the
// old Edit-Team picker offering them was a standing papercut). `excludeIds`
// carries the edit case: a user may never be their own leader, nor pick anyone
// from their own subtree. Ordered by tier first (team leaders before managers
// for a sales hire), then by name, so the grouped render falls out naturally.
var minLeaderRankFE = function(role){
  var k = ROLE_DEFINITIONS_FE[role] && ROLE_DEFINITIONS_FE[role].ladder;
  var l = k ? DEPARTMENT_LADDERS_FE[k] : null;
  return (l && typeof l.minLeaderRank === "number") ? l.minLeaderRank : null;
};
var hierarchyCandidates = function(users, childRole, excludeIds, childRank){
  var allowed = HIERARCHY_PARENT_ROLES_FE[childRole] || [];
  if (!allowed.length) return [];
  var childDef = ROLE_DEFINITIONS_FE[childRole];
  if (!childDef) return [];
  var skip = {}; (excludeIds || []).forEach(function(id){ skip[String(id)] = true; });
  // RANK RULE (2026-08-19), mirroring validateReportsTo: same department, rank
  // strictly higher, and at or above that ladder's minLeaderRank. Offering
  // someone the server will refuse is worse than not offering them, so the two
  // rules have to agree. `allowed` is still consulted first so a role outside the
  // hierarchy offers nobody at all.
  var rank = (typeof childRank === "number") ? childRank : null;
  return (users || []).filter(function(u){
    if (!u || u.active === false) return false;
    if (skip[String(gid(u))]) return false;
    // DEPARTMENT IS A ROLE PROPERTY, NOT A BAND ONE — look it up by u.role.
    // This read positionOf(u) and broke the moment the sales roles collapsed:
    // ROLE_DEFINITIONS_FE["team_leader"] became undefined, every candidate was
    // filtered out, and the Add/Edit User leader picker went EMPTY. Caught by
    // verify-hierarchy-rules before it shipped. The rank rules below are the
    // band-level half and correctly stay on titleRank.
    var d = ROLE_DEFINITIONS_FE[u.role];
    if (!d || d.department !== childDef.department) return false;
    if (typeof u.titleRank !== "number") return false;
    if (rank !== null && u.titleRank <= rank) return false;
    var mlr = minLeaderRankFE(u.role);
    if (mlr === null || u.titleRank < mlr) return false;
    return true;
  }).sort(function(a,b){
    // Nearest leader first: the lowest rank that can still lead you is the most
    // likely pick, and it keeps the old team-leaders-before-managers ordering.
    var d = (a.titleRank || 0) - (b.titleRank || 0);
    if (d !== 0) return d;
    return String(a.name||"").localeCompare(String(b.name||""));
  });
};

// chainOf — ancestors of `startUserId`, nearest first, EXCLUDING the start user.
// Hop-capped and cycle-guarded: a bad link in the data must never hang a modal.
var chainOf = function(startUserId, users){
  var byId = {}; (users || []).forEach(function(u){ byId[String(gid(u))] = u; });
  var out = [], seen = {}, cursor = byId[String(startUserId || "")], hops = 0;
  while (cursor && hops < 12) {
    hops++;
    var pid = rtIdOf(cursor);
    if (!pid || seen[pid]) break;
    seen[pid] = true;
    var parent = byId[pid];
    if (!parent) break;
    out.push(parent);
    cursor = parent;
  }
  return out;
};

// resolveChainSlots — given the PICKED direct leader, fill the three display
// rows. The leader occupies the row matching their OWN role; every row above is
// walked from the tree. This is what makes the dual hat legible: pick Ahmed
// (team_leader) and Yosra fills the Manager row; pick Yosra directly and she
// fills the Manager row herself while the Team Leader row reads "reports
// directly to the manager". Same record, two positions — one stored link.
var resolveChainSlots = function(leaderId, users){
  var out = { teamLeader:null, manager:null, director:null };
  var byId = {}; (users || []).forEach(function(u){ byId[String(gid(u))] = u; });
  var leader = byId[String(leaderId || "")];
  if (!leader) return out;
  var place = function(u){
    if (positionOf(u) === "team_leader" && !out.teamLeader) out.teamLeader = u;
    else if (positionOf(u) === "manager" && !out.manager) out.manager = u;
    else if (positionOf(u) === "director" && !out.director) out.director = u;
  };
  place(leader);
  chainOf(gid(leader), users).forEach(place);
  return out;
};

// descendantsOf — everyone under `rootId`, BFS down reportsTo, excluding the
// root. Used twice by the edit flow: to keep a user's own subtree out of their
// leader picker (that would be a cycle), and to say how many people move with
// them when they are re-parented.
var descendantsOf = function(rootId, users){
  var root = String(rootId || ""); if (!root) return [];
  var kids = {};
  (users || []).forEach(function(u){
    var rt = rtIdOf(u); if (!rt) return;
    (kids[rt] = kids[rt] || []).push(u);
  });
  // Plain loops, not forEach: a closure declared inside the while would capture
  // `next` across iterations (no-loop-func), and there is nothing to gain here.
  var out = [], seen = {}, frontier = [root], hops = 0;
  while (frontier.length && hops < 12) {
    hops++;
    var next = [];
    for (var fi = 0; fi < frontier.length; fi++) {
      var children = kids[frontier[fi]] || [];
      for (var ci = 0; ci < children.length; ci++) {
        var child = children[ci];
        var uid = String(gid(child));
        if (seen[uid] || uid === root) continue;
        seen[uid] = true; out.push(child); next.push(uid);
      }
    }
    frontier = next;
  }
  return out;
};

// anyActiveDirector — the Director row renders only when one actually exists.
// A greyed-out empty select just invites "why is this disabled"; today there is
// no director, so the chain preview simply terminates at Manager. The row
// appears on its own the day one is created.
var anyActiveDirector = function(users){
  return (users || []).some(function(u){ return u && positionOf(u) === "director" && u.active !== false; });
};

// applyRoleToDraft — role changes can invalidate the chosen leader (promoting a
// sales agent under a team_leader to team_leader: a TL cannot report to a TL).
// Drop a leader that the new role can no longer legally have, so the form never
// posts a chain the server will reject. Shared by the Add and Edit modals.
var applyRoleToDraft = function(draft, newRole, users){
  var next = Object.assign({}, draft, { role: newRole });
  if (!roleNeedsHierarchy(newRole)) { next.reportsTo = ""; return next; }
  // Legality is a RANK question now, so the check needs the rank this draft will
  // have AFTER the role change — without it the strictly-higher test is skipped
  // and an illegal leader is kept (promoting an agent under Ahmed to Team Leader
  // would leave Ahmed in place, and the server would then refuse the save).
  var newRank = rankOfTitleFE(newRole, next.title);
  // No rank means the draft's title is not on the new role's ladder. The server
  // refuses that link outright, so drop the leader rather than carry one the rule
  // was never able to judge — fail closed, and the user re-picks after choosing a
  // valid title.
  if (typeof newRank !== "number") { next.reportsTo = ""; return next; }
  var keep = hierarchyCandidates(users, newRole, null, newRank).some(function(u){
    return String(gid(u)) === String(draft.reportsTo || "");
  });
  if (!keep) next.reportsTo = "";
  return next;
};

var HIER_PICKER_LABEL = { sales:"Team Leader", team_leader:"Manager", manager:"Director" };
var HIER_ROLE_LABEL   = { team_leader:"Team Leader", manager:"Manager", director:"Sales Director", sales:"Sales" };

// ===== HierarchyBlock — the Team Leader / Manager / Director rows =====
// Replaces the free-text Team Name / Team Code inputs. ONE editable picker plus
// read-only echoes of the chain above it, then a preview line. Shared by Add
// User and Edit User so the two can never drift.
//
// Which row is editable depends on the role:
//   sales       → pick a Team Leader (team leaders AND dual-hat managers)
//   team_leader → pick a Manager
//   manager     → pick a Director (absent when none exists — a manager is then
//                 simply top of tree)
// Everything above the picked leader is walked from the existing tree and shown
// read-only, so the chain is stated exactly once and cannot be contradicted.
var HierarchyBlock = function(p){
  var users = p.users || [];
  var role  = p.role;
  if (!roleNeedsHierarchy(role)) return null;

  var hasDirector = anyActiveDirector(users);
  var box = { border:"1px solid "+C.border, borderRadius:10, padding:11, background:"#F8FAFC", marginBottom:13 };
  var lbl = { display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 };
  var roBox = { padding:"9px 12px", borderRadius:9, border:"1px dashed "+C.border, background:"#fff",
                fontSize:13, color:C.text, boxSizing:"border-box" };
  var hint = { fontSize:11, color:C.textLight, marginTop:4 };

  // A manager's only legal leader is a director. With none in the tree there is
  // nothing to pick — state the fact rather than render an empty control.
  if (role === "manager" && !hasDirector) {
    return <div style={box}>
      <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:3 }}>Position in the org</div>
      <div style={{ fontSize:12, color:C.textLight }}>
        No Sales Director exists yet, so a Manager sits at the <b>top of the sales tree</b> and reports to no one.
        Create a Director first if this manager should report upward.
      </div>
    </div>;
  }

  var candidates = hierarchyCandidates(users, role, p.excludeIds, p.childRank);
  var slots = resolveChainSlots(p.value, users);
  var picked = (users || []).filter(function(u){ return String(gid(u)) === String(p.value || ""); })[0] || null;

  // Option label carries the tier and the leader's own chain, so the dual hat
  // reads correctly in a flat list: Ahmed shows "under Yosra Sharaf", Yosra
  // shows "takes direct reports".
  var optLabel = function(u){
    var above = chainOf(gid(u), users)[0];
    var tail;
    if (role === "sales" && positionOf(u) === "manager") tail = "takes direct reports";
    else if (above) tail = "under " + above.name;
    else tail = "top of tree";
    // Job title, not role label — "Ahmed Kassem — Team Leader" says more than
    // "Ahmed Kassem — Team Leader (role)" once ranks are what actually decide.
    return u.name + " — " + (u.title || HIER_ROLE_LABEL[positionOf(u)] || u.role) + " · " + tail;
  };

  var roRow = function(key, label, user, note){
    return <div key={key} style={{ marginBottom:10 }}>
      <label style={lbl}>{label}</label>
      <div style={Object.assign({}, roBox, user ? {} : { color:C.textLight })}>
        {user ? user.name : "— none —"}
        {user && note ? <span style={{ color:C.textLight, fontWeight:500 }}> · {note}</span> : null}
      </div>
    </div>;
  };

  // Read-only rows below the picker: whatever the chain above the pick contains.
  var echoes = [];
  if (role === "sales") {
    echoes.push(roRow("mgr", "Manager", slots.manager,
      (picked && slots.manager && String(gid(picked)) === String(gid(slots.manager)))
        ? "same person — reports to them directly" : "from the org tree"));
    if (hasDirector) echoes.push(roRow("dir", "Director", slots.director, "from the org tree"));
  } else if (role === "team_leader") {
    if (hasDirector) echoes.push(roRow("dir", "Director", slots.director, "from the org tree"));
  }

  var chainParts = [];
  if (slots.teamLeader) chainParts.push(slots.teamLeader.name + " (Team Leader)");
  if (slots.manager)    chainParts.push(slots.manager.name + " (Manager)");
  if (slots.director)   chainParts.push(slots.director.name + " (Director)");

  return <div style={box}>
    <div style={{ marginBottom:10 }}>
      <label style={lbl}>
        {HIER_PICKER_LABEL[role] || "Reports To"}
        {p.required ? <span style={{ color:C.danger, marginInlineStart:3 }}>*</span> : null}
      </label>
      <Select value={p.value || ""} onChange={function(e){ p.onChange(e.target.value || ""); }}
        searchable fontSize={13}
        emptyLabel={p.required ? "- Select " + (HIER_PICKER_LABEL[role] || "leader") + " -" : "— No leader (top of tree) —"}
        options={withCurrentOption(candidates.map(function(u){ return { value:gid(u), label:optLabel(u) }; }), p.value, userNameOf(p.value, users) || "Unknown")}/>
      {role === "sales" && <div style={hint}>
        Pick the person this agent reports to <b>directly</b> — a Team Leader, or a Manager who runs agents of their own.
      </div>}
      {!candidates.length && <div style={{ fontSize:11, color:C.danger, marginTop:4 }}>
        No active {String(HIER_PICKER_LABEL[role] || "leader").toLowerCase()} exists to report to.
      </div>}
    </div>

    {echoes}

    <div style={{ borderTop:"1px solid "+C.border, paddingTop:9, marginTop:2 }}>
      <div style={{ fontSize:11, color:C.textLight, marginBottom:3 }}>Reporting chain</div>
      <div style={{ fontSize:12, color:chainParts.length?C.text:C.textLight, fontWeight:chainParts.length?600:400, direction:"ltr" }}>
        {(p.subjectName || "New user") + (chainParts.length ? " → " + chainParts.join(" → ") : " → not linked to any team yet")}
      </div>
      {p.subtreeCount > 0 && <div style={{ fontSize:11, color:"#B45309", marginTop:5, fontWeight:600 }}>
        ⚠️ {p.subtreeCount} {p.subtreeCount === 1 ? "person reports" : "people report"} to {p.subjectName || "this user"} — moving them moves the whole team.
      </div>}
    </div>
  </div>;
};

// Phase R-12 Part 1 — External brokers registry. Standalone page, admin
// + operation only (gated at sidebar, renderPage, AND backend operationOnly).
// Self-fetches its own data on mount instead of joining the App-level loadData
// — brokers aren't needed elsewhere yet (Parts 2-7 will integrate with deals
// + commissions). Soft-delete vs hard-delete decision lives on the backend
// (DELETE /api/brokers/:id) based on whether any Commission references the
// broker; the FE just shows the appropriate toast based on response.action.
// The Brokers table's columns, hoisted so the expand-below row's colSpan is
// DERIVED from the header rather than a hand-kept number that can drift.
var BROKER_COLS = ["Name","Phone","Email","Notes",""];
var BrokersPage = function(p) {
  var t = p.t;
  var [rows, setRows]            = useState([]);
  var [loading, setLoading]      = useState(true);
  var [error, setError]          = useState(null);
  var [includeArchived, setIncludeArchived] = useState(false);
  var [search, setSearch]        = useState("");
  var [showAdd, setShowAdd]      = useState(false);
  var [adding, setAdding]        = useState(false);
  var [newBroker, setNewBroker]  = useState({ name:"", phone:"", email:"", notes:"" });
  var [editModal, setEditModal]  = useState(null); // { _id, name, phone, email, notes, archived }
  var [editSaving, setEditSaving] = useState(false);
  // ===== EXPAND-BELOW: one broker's deals ====================================
  // Same mechanism the EOI and Deals tables use for a record's details: the row
  // toggles, and a second <tr> spanning every column renders underneath it.
  // Deliberately NOT a side panel — those two pages set the convention.
  //
  // Unlike EOI/Deals the expand row carries no `!p.isMobile` guard, because this
  // page has no separate mobile card list to fall through to: the table is the
  // only rendering, at every viewport.
  var [selectedBroker, setSelectedBroker] = useState(null);   // broker _id | null
  var [brokerDeals, setBrokerDeals] = useState({ loading:false, err:null, rows:[] });
  useEffect(function(){
    if (!selectedBroker) { setBrokerDeals({ loading:false, err:null, rows:[] }); return; }
    var cancelled = false;
    setBrokerDeals({ loading:true, err:null, rows:[] });
    // Same walker the Deals page uses. The server applies the caller's scope,
    // the archived gate and the tab filter to this query too, so a broker's
    // panel can only ever show deals this caller could already see on Deals.
    fetchAllPages("/api/deals?status=all&brokerId=" + encodeURIComponent(selectedBroker), p.token)
      .then(function(rows){ if (cancelled) return;
        setBrokerDeals({ loading:false, err:null, rows: Array.isArray(rows) ? rows : [] }); })
      .catch(function(err){ if (cancelled) return;
        setBrokerDeals({ loading:false, err:(err && err.message) || "Failed to load deals.", rows:[] }); });
    return function(){ cancelled = true; };
  }, [selectedBroker, p.token]);
  var brokerDealTotal = brokerDeals.rows.reduce(function(sum, d){
    return sum + (parseFloat(String(d.budget || "0").replace(/,/g, "")) || 0);
  }, 0);

  var liveBrokerRev = useLiveRev("broker");
  var load = useCallback(function(){
    setLoading(true); setError(null);
    var qs = includeArchived ? "?includeArchived=1" : "";
    apiFetch("/api/brokers" + qs, "GET", null, p.token).then(function(resp){
      setRows((resp && resp.data) || []);
    }).catch(function(e){
      setError(e.message || "Failed to load brokers");
    }).then(function(){ setLoading(false); });
  }, [p.token, includeArchived, liveBrokerRev]);

  useEffect(function(){ load(); }, [load]);

  var add = async function(){
    var name = String(newBroker.name||"").trim();
    if (!name) { alert("Name is required"); return; }
    setAdding(true);
    try {
      var doc = await apiFetch("/api/brokers", "POST", {
        name:  name,
        phone: newBroker.phone,
        email: newBroker.email,
        notes: newBroker.notes
      }, p.token);
      setRows(function(prev){ return [doc].concat(prev).sort(function(a,b){
        if ((a.archived?1:0) !== (b.archived?1:0)) return (a.archived?1:0) - (b.archived?1:0);
        return String(a.name||"").localeCompare(String(b.name||""));
      }); });
      setShowAdd(false);
      setNewBroker({ name:"", phone:"", email:"", notes:"" });
    } catch(e) { alert(e.message || "Failed to create broker"); }
    setAdding(false);
  };

  var saveEdit = async function(){
    if (!editModal) return;
    var name = String(editModal.name||"").trim();
    if (!name) { alert("Name cannot be empty"); return; }
    setEditSaving(true);
    try {
      var upd = await apiFetch("/api/brokers/"+editModal._id, "PATCH", {
        name:  name,
        phone: editModal.phone,
        email: editModal.email,
        notes: editModal.notes
      }, p.token);
      setRows(function(prev){ return prev.map(function(r){ return gid(r)===gid(upd) ? upd : r; }); });
      setEditModal(null);
    } catch(e) { alert(e.message || "Failed to update broker"); }
    setEditSaving(false);
  };

  var toggleArchive = async function(b){
    try {
      var upd = await apiFetch("/api/brokers/"+gid(b), "PATCH", { archived: !b.archived }, p.token);
      setRows(function(prev){ return prev.map(function(r){ return gid(r)===gid(upd) ? upd : r; }); });
    } catch(e) { alert(e.message || "Failed to update broker"); }
  };

  // Hard-delete attempt — backend returns { action: "deleted" | "archived",
  // refCount? } depending on whether the broker is referenced. Soft-delete
  // (archived) keeps the row in the list with the Archived badge.
  var del = async function(b){
    if (!window.confirm("Delete broker \""+b.name+"\"? If this broker is linked to any commission, it will be archived instead of deleted.")) return;
    try {
      var resp = await apiFetch("/api/brokers/"+gid(b), "DELETE", null, p.token);
      if (resp && resp.action === "archived") {
        setRows(function(prev){ return prev.map(function(r){ return gid(r)===gid(b) ? Object.assign({}, r, { archived: true }) : r; }); });
        alert("Broker is referenced by "+(resp.refCount||0)+" commission(s) — archived instead of deleted.");
      } else {
        setRows(function(prev){ return prev.filter(function(r){ return gid(r) !== gid(b); }); });
      }
    } catch(e) { alert(e.message || "Failed to delete broker"); }
  };

  var visible = rows.filter(function(r){
    if (!search) return true;
    return String(r.name||"").toLowerCase().indexOf(search.toLowerCase()) !== -1;
  });
  var activeCount   = rows.filter(function(r){ return !r.archived; }).length;
  var archivedCount = rows.filter(function(r){ return  r.archived; }).length;

  return <div style={{ padding:"18px 16px 40px" }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18, flexWrap:"wrap", gap:10 }}>
      <h2 style={{ margin:0, fontSize:12, fontWeight:600, color:C.textLight }}>{activeCount}{includeArchived&&archivedCount>0 ? (" active, "+archivedCount+" archived") : " active"}</h2>
      <Btn onClick={function(){ setShowAdd(true); }} style={{ padding:"7px 13px", fontSize:13 }}><Plus size={14}/> Add Broker</Btn>
    </div>

    <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:14, flexWrap:"wrap" }}>
      <div style={{ position:"relative", flex:"1 1 240px", minWidth:200 }}>
        <Search size={14} style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:C.textLight }}/>
        <input type="text" placeholder="Search by name..." value={search} onChange={function(e){ setSearch(e.target.value); }}
          style={{ width:"100%", padding:"8px 12px 8px 32px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:13, outline:"none", boxSizing:"border-box" }}/>
      </div>
      <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:C.textLight, cursor:"pointer" }}>
        <input type="checkbox" checked={includeArchived} onChange={function(e){ setIncludeArchived(e.target.checked); }}/>
        Include archived
      </label>
    </div>

    {loading
      ? <Loader/>
      : error
      ? <Card><div style={{ color:C.danger, fontSize:13 }}>⚠️ {error}</div></Card>
      : visible.length === 0
      ? <Card><div style={{ color:C.textLight, fontSize:13, textAlign:"center", padding:"30px 20px" }}>{rows.length===0 ? "No brokers yet. Click \"Add Broker\" to create one." : "No brokers match your search."}</div></Card>
      : <Card p={0}><div style={{ overflowX:"auto" }}><table style={{ width:"100%", borderCollapse:"collapse", minWidth:580 }}>
          <thead><tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
            {BROKER_COLS.map(function(h){
              return <th key={h||"x"} style={{ textAlign:t.dir==="rtl"?"right":"left", padding:"11px 12px", fontSize:11, fontWeight:600, color:C.textLight, whiteSpace:"nowrap" }}>{h}</th>;
            })}
          </tr></thead>
          <tbody>{visible.map(function(b){
            var bid = gid(b);
            var isSel = selectedBroker === bid;
            return <Fragment key={bid}>
            <tr onClick={function(){ setSelectedBroker(isSel ? null : bid); }}
              style={{ borderBottom:"1px solid #F1F5F9", opacity:b.archived?0.55:1, cursor:"pointer", background:isSel?"#EFF6FF":"transparent" }}>
              <td style={{ padding:"11px 12px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:C.accent+"15", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:12, color:C.accent, flexShrink:0 }}>{(b.name||"?")[0].toUpperCase()}</div>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, display:"flex", alignItems:"center", gap:6 }}>
                      {b.name}
                      {b.archived&&<Badge bg="#FEE2E2" color="#B91C1C">Archived</Badge>}
                    </div>
                  </div>
                </div>
              </td>
              <td style={{ padding:"11px 12px", fontSize:12, fontFamily:"monospace", direction:"ltr" }}>{b.phone||"—"}</td>
              <td style={{ padding:"11px 12px", fontSize:12 }}>{b.email||"—"}</td>
              <td style={{ padding:"11px 12px", fontSize:12, color:C.textLight, maxWidth:240, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={b.notes||""}>{b.notes||"—"}</td>
              <td style={{ padding:"11px 12px" }}>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <button onClick={function(e){ e.stopPropagation(); setEditModal({ _id:bid, name:b.name||"", phone:b.phone||"", email:b.email||"", notes:b.notes||"", archived:!!b.archived }); }}
                    style={{ width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }} title="Edit">
                    <Edit size={12} color={C.accent}/>
                  </button>
                  <button onClick={function(e){ e.stopPropagation(); toggleArchive(b); }}
                    style={{ padding:"3px 10px", borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", fontSize:11, cursor:"pointer", color:C.textLight }}>
                    {b.archived ? "Restore" : "Archive"}
                  </button>
                  <button onClick={function(e){ e.stopPropagation(); del(b); }}
                    style={{ width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }} title="Delete">
                    <Trash2 size={12} color={C.danger}/>
                  </button>
                </div>
              </td>
            </tr>
            {isSel && <tr><td colSpan={BROKER_COLS.length} style={{ padding:"8px 12px", background:C.bg }}>
              <div style={{ background:"#fff", borderRadius:0, padding:"12px 14px" }}>
                <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:10, flexWrap:"wrap", marginBottom:10 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text }}>
                    🤝 {b.name} — {brokerDeals.rows.length} {brokerDeals.rows.length===1?"deal":"deals"}
                  </div>
                  {brokerDealTotal>0&&<div style={{ fontSize:12, fontWeight:700, color:C.success, background:"#DCFCE7", padding:"3px 10px", borderRadius:20 }}>
                    {brokerDealTotal.toLocaleString()} EGP
                  </div>}
                </div>
                {brokerDeals.loading
                  ? <div style={{ padding:"18px 0", textAlign:"center", color:C.textLight, fontSize:12 }}>Loading deals…</div>
                  : brokerDeals.err
                  ? <div style={{ padding:"12px 14px", background:"#FEE2E2", border:"1px solid #FCA5A5", color:"#B91C1C", borderRadius:8, fontSize:12, fontWeight:600 }}>⚠️ {brokerDeals.err}</div>
                  : brokerDeals.rows.length===0
                  ? <div style={{ padding:"18px 0", textAlign:"center", color:C.textLight, fontSize:12 }}>No deals for this broker</div>
                  : <div style={{ overflowX:"auto" }}><table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead><tr style={{ background:"#F8FAFC" }}>
                        {["Client","Budget","Project","Developer","Deal Date"].map(function(h){
                          return <th key={h} style={{ textAlign:t.dir==="rtl"?"right":"left", padding:"8px 10px", fontSize:10, fontWeight:600, color:C.textLight, whiteSpace:"nowrap" }}>{h}</th>;
                        })}
                      </tr></thead>
                      <tbody>{brokerDeals.rows.map(function(d){
                        var bv = parseFloat(String(d.budget || "0").replace(/,/g, "")) || 0;
                        var dv = d.developerId;
                        return <tr key={d._rowKey || d._id} style={{ borderTop:"1px solid #F1F5F9" }}>
                          <td style={{ padding:"8px 10px", fontSize:12, fontWeight:600, color:C.text }}>{d.name||"—"}</td>
                          <td style={{ padding:"8px 10px", fontSize:12, fontWeight:700, color:C.success, whiteSpace:"nowrap" }}>{bv>0?bv.toLocaleString()+" EGP":"—"}</td>
                          <td style={{ padding:"8px 10px", fontSize:12, color:C.textLight }}>{d.project||"—"}</td>
                          <td style={{ padding:"8px 10px", fontSize:12, color:C.textLight }}>{(dv&&dv.name)?dv.name:"—"}</td>
                          <td style={{ padding:"8px 10px", fontSize:12, color:C.textLight, whiteSpace:"nowrap" }}>{(function(){var dd=getDealDate(d);return dd?fmtDayOnly(dd):"—";})()}</td>
                        </tr>;
                      })}</tbody>
                    </table></div>}
              </div>
            </td></tr>}
            </Fragment>;
          })}</tbody>
        </table></div></Card>}

    {showAdd&&<Modal show={true} onClose={function(){ setShowAdd(false); }} title="➕ Add Broker">
      <Inp label="Name" req value={newBroker.name} onChange={function(e){ setNewBroker(Object.assign({}, newBroker, { name:e.target.value })); }}/>
      <Inp label={t.phone} value={newBroker.phone} onChange={function(e){ setNewBroker(Object.assign({}, newBroker, { phone:e.target.value })); }}/>
      <Inp label={t.email} value={newBroker.email} onChange={function(e){ setNewBroker(Object.assign({}, newBroker, { email:e.target.value })); }}/>
      <Inp label={t.notes} type="textarea" value={newBroker.notes} onChange={function(e){ setNewBroker(Object.assign({}, newBroker, { notes:e.target.value })); }}/>
      <div style={{ display:"flex", gap:10, marginTop:6 }}>
        <Btn outline onClick={function(){ setShowAdd(false); }} style={{ flex:1 }}>{t.cancel}</Btn>
        <Btn onClick={add} loading={adding} style={{ flex:1 }}>{adding ? "Saving..." : t.save}</Btn>
      </div>
    </Modal>}

    {editModal&&<Modal show={true} onClose={function(){ setEditModal(null); }} title={"✏️ Edit Broker — "+(editModal.name||"")}>
      <Inp label="Name" req value={editModal.name} onChange={function(e){ setEditModal(Object.assign({}, editModal, { name:e.target.value })); }}/>
      <Inp label={t.phone} value={editModal.phone} onChange={function(e){ setEditModal(Object.assign({}, editModal, { phone:e.target.value })); }}/>
      <Inp label={t.email} value={editModal.email} onChange={function(e){ setEditModal(Object.assign({}, editModal, { email:e.target.value })); }}/>
      <Inp label={t.notes} type="textarea" value={editModal.notes} onChange={function(e){ setEditModal(Object.assign({}, editModal, { notes:e.target.value })); }}/>
      <div style={{ display:"flex", gap:10, marginTop:6 }}>
        <Btn outline onClick={function(){ setEditModal(null); }} style={{ flex:1 }}>{t.cancel}</Btn>
        <Btn onClick={saveEdit} loading={editSaving} style={{ flex:1 }}>{editSaving ? "Saving..." : t.save}</Btn>
      </div>
    </Modal>}
  </div>;
};

var UsersPage = function(p) {
  var t=p.t; var [showAdd,setShowAdd]=useState(false); var [saving,setSaving]=useState(false);
  var [nU,setNU]=useState({name:"",username:"",password:"",email:"",phone:"",role:"sales",title:"",quarterlyTarget:"",reportsTo:"",startingDate:""});
  var todayStr=(function(){var d=new Date();return d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0");})();
  var [pwModal,setPwModal]=useState(null); // {userId, userName}
  var [pwForm,setPwForm]=useState({newPass:"",confirmPass:""});
  var [pwMsg,setPwMsg]=useState(""); var [pwSaving,setPwSaving]=useState(false);
  // The separate "Edit Team" modal was retired 2026-08-15 — its free-text Team
  // Code / Team Name inputs are gone (both fields are now derived server-side),
  // and its reportsTo picker lives in Edit User as a HierarchyBlock. The 👥
  // button is kept as an alias that opens the same Edit User modal.
  var [editModal,setEditModal]=useState(null); // {userId, userName, title, role, reportsTo}
  var [editSaving,setEditSaving]=useState(false);
  var [devicesModal,setDevicesModal]=useState(null); // {userId, userName}
  // Sessions/devices is OWNER-ONLY on every route (requireOwner — the flag, not
  // the role). Not a page gate, so it reads the envelope's owner flag rather
  // than access.pages; off p.access so a flag that moves reaches the button on
  // the next boot instead of the next login. See isOwnerFE.
  var canSeeDevices=isOwnerFE(p.access);
  var saveUserEdit=async function(){
    if(!editModal)return;
    // Saturday schedule validation: alternating requires a pattern start date.
    if (editModal.saturdaySchedule === "alternating" && !editModal.saturdayPatternStartDate) {
      alert("Pattern start date is required when Saturday schedule is Alternating. Pick a Saturday that is a working day to anchor the pattern.");
      return;
    }
    // EDIT: starting date stays optional, but may not be in the future.
    if (editModal.startingDate && editModal.startingDate > todayStr) {
      alert("Starting Date cannot be in the future");
      return;
    }
    // Job Title must be valid for the role being saved (owner decision D2). This
    // form always SENDS the title, so the server's grandfather clause — which
    // spares edits that don't mention it, like the active toggle — does not
    // apply here: an explicit save must carry an explicit, valid title. The
    // Owner is exempt; `admin` has no ladder and "CEO" is free text.
    if (!editModal.isOwner && isLadderRoleFE(editModal.role) &&
        rankOfTitleFE(editModal.role, editModal.title) === null) {
      alert("Job Title is required for this role — pick one from the list before saving.");
      return;
    }
    setEditSaving(true);
    try{
      var sd=editModal.startingDate||null;
      var sat=editModal.saturdaySchedule||"always_work";
      var satStart=sat==="alternating"?(editModal.saturdayPatternStartDate||null):null;
      // Owner self-edit: omit `role` from the payload so the backend's
      // role-lockdown guards don't 403 a legitimate self-edit. Owner role
      // is permanent and already correct in the DB.
      var payload={title:editModal.title,startingDate:sd,saturdaySchedule:sat,saturdayPatternStartDate:satStart};
      if(!editModal.isOwner) payload.role=editModal.role;
      // Hierarchy — always send reportsTo for a non-owner edit, including the
      // empty case. Omitting it would leave a stale leader behind when the role
      // moves out of the sales tree; sending "" is what clears it. teamId /
      // teamName are never sent: the server derives both from this leader.
      if(!editModal.isOwner) payload.reportsTo=editModal.reportsTo||null;
      var upd=await apiFetch("/api/users/"+editModal.userId,"PUT",payload,p.token);
      // Trust the server's response for the derived fields rather than guessing
      // them client-side — teamId/teamName are computed there.
      p.setUsers(function(prev){return prev.map(function(x){return gid(x)===editModal.userId?Object.assign({},x,upd||{}):x;});});
      setEditModal(null);
    }catch(e){alert(e.message);} setEditSaving(false);
  };
  // Single opener for the Edit User modal, shared by the ✏️ button and the 👥
  // alias that used to open the retired Edit Team modal.
  var openEditUser=function(u,uid,displayName,isOwnerRow){
    setEditModal({
      userId:uid, userName:displayName, isOwner:isOwnerRow,
      title:u.title||"", role:u.role||"sales",
      reportsTo:rtIdOf(u),
      startingDate:u.startingDate?String(u.startingDate).slice(0,10):"",
      saturdaySchedule:u.saturdaySchedule||"always_work",
      saturdayPatternStartDate:u.saturdayPatternStartDate?String(u.saturdayPatternStartDate).slice(0,10):""
    });
  };
  // Derived from the role registry. Both maps previously omitted `hr` entirely,
  // so an HR user's badge rendered with an undefined colour and label — the exact
  // half-defined-role failure this registry exists to make impossible.
  // Roles first, then the sales BANDS layered on top — so rc[positionOf(u)]
  // resolves for "manager"/"team_leader"/"director" whether those are still
  // roles or have become bands. See salesBandDescriptorsFE.
  var rc=ROLE_KEYS_FE.reduce(function(a,r){ a[r]=ROLE_DEFINITIONS_FE[r].colour; return a; },{});
  salesBandDescriptorsFE().forEach(function(b){ if (b && b.colour) rc[b.position]=b.colour; });
  var getManagerName=function(uid){var u=p.users.find(function(x){return gid(x)===String(uid||"");});return u?u.name:"";};
  var getRoleLabel=function(u){if(positionOf(u)==="manager"&&u.reportsTo)return "Team Leader";if(positionOf(u)==="manager")return "Manager";return u.role==="admin"?"Admin":"Sales";};
  var rl=ROLE_KEYS_FE.reduce(function(a,r){ a[r]=roleLabel(r,t); return a; },{});
  salesBandDescriptorsFE().forEach(function(b){
    if (!b) return;
    rl[b.position] = (b.i18nKey && t && t[b.i18nKey]) || b.label || rl[b.position];
  });
  var changePassword=async function(){if(!pwForm.newPass||!pwForm.confirmPass)return;if(pwForm.newPass!==pwForm.confirmPass){setPwMsg(t.passwordMismatch);return;}setPwSaving(true);try{await apiFetch("/api/users/"+pwModal.userId,"PUT",{password:pwForm.newPass},p.token);setPwMsg(t.passwordSuccess);setTimeout(function(){setPwModal(null);setPwMsg("");setPwForm({newPass:"",confirmPass:""});},1500);}catch(e){setPwMsg(t.passwordError);}setPwSaving(false);};
  var add=async function(){if(!nU.name||!nU.username)return;if(!nU.password||!nU.password.trim()){alert("Please enter a password");return;}if(!nU.phone||!String(nU.phone).trim()){alert("Phone number is required");return;}if(!nU.startingDate){alert("Please select a Starting Date");return;}if(nU.startingDate>todayStr){alert("Starting Date cannot be in the future");return;}
    // A hierarchy role must be linked into the real tree at creation — this is
    // the whole point of replacing the free-text team fields. Not mirrored on
    // the server (the API can still create a leaderless user for edge cases);
    // this is a create-form requirement only.
    if(roleNeedsHierarchy(nU.role)&&!(positionOf(nU)==="manager"&&!anyActiveDirector(p.users))&&!nU.reportsTo){
      alert("Please select a "+(HIER_PICKER_LABEL[positionOf(nU)]||"leader")+" — the new user must be linked to the org tree.");return;
    }
    // Job Title is mandatory for every role that has a ladder (owner decision
    // D2, 2026-08-16). The server enforces the same rule (400) — this is the
    // friendly mirror, not the gate. Without a title there is no rank, and a
    // rankless user drops silently out of every rank-scoped query later.
    if(isLadderRoleFE(nU.role)&&rankOfTitleFE(nU.role,nU.title)===null){
      alert("Job Title is required for this role — pick one from the list.");return;
    }
    // Quarterly target is mandatory for the target-carrying BANDS (ranks 1-6),
    // not for the sales ROLE. The server resolves it the same way; this is the
    // friendly mirror, not the gate.
    //
    // Reading it off nU.role was correct only while each band had its own role.
    // Once the sales roles collapse, every sales-side create arrives as "sales"
    // and this would demand a quarterly target from a new Sales Manager, who has
    // never carried one. nU holds the chosen Job Title, so positionOf resolves
    // the band through its rank.
    if(TARGET_REQUIRED_ROLES.indexOf(targetPositionOfDraft(nU))!==-1){
      var qtv=Number(String(nU.quarterlyTarget).replace(/,/g,""));
      if(!nU.quarterlyTarget||!isFinite(qtv)||qtv<=0){alert("Quarterly Target is required for this role");return;}
      if(qtv<MIN_QUARTERLY_TARGET){alert("Quarterly Target must be at least "+MIN_QUARTERLY_TARGET.toLocaleString("en-US")+" EGP");return;}
    }
    setSaving(true);try{var payload=Object.assign({},nU,{quarterlyTarget:nU.quarterlyTarget?Number(String(nU.quarterlyTarget).replace(/,/g,"")):undefined});var user=await apiFetch("/api/users","POST",payload,p.token);p.setUsers(function(prev){return prev.concat([user]);});setShowAdd(false);setNU({name:"",username:"",password:"",email:"",phone:"",role:"sales",title:"",quarterlyTarget:"",reportsTo:"",startingDate:""});}catch(e){alert(e.message);}setSaving(false);};
  var toggleActive=async function(u){var uid=gid(u);try{var upd=await apiFetch("/api/users/"+uid,"PUT",{active:!u.active},p.token);p.setUsers(function(prev){return prev.map(function(x){return gid(x)===uid?upd:x;});});}catch(e){}};
  var del=async function(uid){if(!window.confirm(t.deleteConfirm))return;try{await apiFetch("/api/users/"+uid,"DELETE",null,p.token);p.setUsers(function(prev){return prev.filter(function(x){return gid(x)!==uid;});});}catch(e){alert(e.message);}};
  var getQTargets=function(uid){
    var u=p.users.find(function(x){return gid(x)===uid;});
    if(u&&u.qTargets&&Object.keys(u.qTargets).length>0) return u.qTargets;
    try{return JSON.parse(localStorage.getItem("crm_qt_"+uid)||"{}");} catch(e){return {};}
  };
  // Mirrors local state ONLY after the server accepts. The previous version
  // wrote localStorage and setUsers unconditionally, so a rejected save (now
  // possible — past-quarter guard) would have left the UI showing targets the
  // database never stored.
  var saveQTargets=async function(uid,qt){
    var ok=await commitQTargets({uid:uid,merged:qt,token:p.token,csrfToken:p.csrfToken});
    if(!ok) return false;
    try{localStorage.setItem("crm_qt_"+uid,JSON.stringify(qt));}catch(e){}
    p.setUsers(function(prev){return prev.map(function(u){return gid(u)===uid?Object.assign({},u,{qTargets:qt}):u;});});
    return true;
  };
  var [qtModal,setQtModal]=useState(null);
  // Active/Inactive filter. Predicate mirrors the Status column badge below
  // (green when u.active is truthy) so pill counts match the rendered chips.
  // Opens on Active. 21 of 47 accounts are deactivated leavers, so "All" put
  // the working roster in a minority of the rows and every scan of this page
  // started with a scroll past people who left. All and Inactive are one click
  // away and their counts are still on the pills, so nothing is hidden — only
  // the starting view changed.
  var [statusFilter,setStatusFilter]=useState("active");
  var activeCount=p.users.filter(function(u){return !!u.active;}).length;
  var inactiveCount=p.users.length-activeCount;
  var visibleUsers=statusFilter==="all"
    ? p.users
    : p.users.filter(function(u){return statusFilter==="active"?!!u.active:!u.active;});

  return <div style={{ padding:"18px 16px 40px" }}>
    {/* No heading: the pill row already names the page's one number three
        ways (All / Active / Inactive, each with its count), so a "Users (47)"
        line above it restated the All pill and nothing else.
        ONE header row: status pills on the left, Add User on the far right.
        These used to be two stacked rows (button row, then pill row) — a full
        row of empty space above the table for a single button. The button
        carries marginLeft:auto rather than relying on space-between, so when
        the row wraps on a narrow screen it drops to its own line and stays
        right-aligned instead of jumping to the left edge under the pills.
        Status filter pills mirror the Deals type-filter pill row. Counts
        reflect the full list; default Active. Client-side (full list is loaded). */}
    <div style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:8, marginBottom:12 }}>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
        {[["all",t.all,p.users.length,C.accent],["active",t.active,activeCount,C.success],["inactive",t.inactive,inactiveCount,C.danger]].map(function(pill){
          var active=statusFilter===pill[0];
          var theme=pill[3];
          return <button key={pill[0]} onClick={function(){setStatusFilter(pill[0]);}} style={{
            padding:"5px 12px", borderRadius:8, border:"1px solid",
            borderColor: active ? theme : "#E2E8F0",
            background: active ? theme+"12" : "#fff",
            color: active ? theme : C.textLight,
            fontSize:12, fontWeight:600, cursor:"pointer"
          }}>{pill[1]} ({pill[2]})</button>;
        })}
      </div>
      <Btn onClick={function(){setShowAdd(true);}} style={{ marginLeft:"auto", padding:"7px 14px", fontSize:13, fontWeight:700, whiteSpace:"nowrap", boxShadow:"0 1px 3px rgba(232,168,56,0.35)" }}><UserPlus size={15}/> {t.addUser}</Btn>
    </div>
    <Card p={0}><div style={{ overflowX:"auto" }}><table style={{ width:"100%", borderCollapse:"collapse", minWidth:580 }}>
      <thead><tr style={{ background:"#F8FAFC", borderBottom:"2px solid #E8ECF1" }}>
        {[t.name,t.username,t.title,t.role,t.phone,"Quarterly Target","Last Seen","Starting Date",t.status,""].map(function(h){return <th key={h||"x"} style={{ textAlign:h==="Quarterly Target"?"center":(t.dir==="rtl"?"right":"left"), padding:"11px 12px", fontSize:11, fontWeight:600, color:C.textLight, whiteSpace:"nowrap" }}>{h}</th>;})}
      </tr></thead>
      <tbody>{visibleUsers.map(function(u){
        var uid=gid(u);
        var displayName=u.username==="amgad"?"Amgad Mohamed":u.name;
        // Owner lockdown — mirrors the backend guards in PUT/DELETE /api/users/:id.
        // ownerLocked rows hide Edit/Delete/active-toggle/password-reset for non-Owner viewers.
        var isOwnerRow=!!u.isOwner;
        var isSelf=String((p.cu&&p.cu.id)||"")===String(uid);
        var ownerLocked=isOwnerRow&&!isSelf;
        return <tr key={uid} style={{ borderBottom:"1px solid #F1F5F9" }}>
        <td style={{ padding:"11px 12px" }}><div style={{ display:"flex", alignItems:"center", gap:8 }}><div style={{ width:32, height:32, borderRadius:8, background:C.primary+"15", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:12, color:C.primary, flexShrink:0 }}>{displayName[0]}</div><div><div style={{ fontSize:12, fontWeight:600, display:"flex", alignItems:"center", gap:5 }}>{displayName}{isOwnerRow&&<span title="Owner" style={{ fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:4, background:"#FEF3C7", color:"#92400E", letterSpacing:0.3 }}>👑 OWNER</span>}</div><div style={{ fontSize:10, color:C.textLight }}>{u.email}</div></div></div></td>
        <td style={{ padding:"11px 12px", fontSize:12, fontFamily:"monospace" }}>{u.username}</td>
        <td style={{ padding:"11px 12px", fontSize:12 }}>{u.title}</td>
        <td style={{ padding:"11px 12px" }}><Badge bg={(rc[positionOf(u)]||"#94A3B8")+"15"} color={rc[positionOf(u)]||"#94A3B8"}>{rl[positionOf(u)]||u.role}</Badge></td>
        <td style={{ padding:"11px 12px", fontSize:12, direction:"ltr" }}>{u.phone}</td>
        <td style={{ padding:"8px 12px", textAlign:"center", verticalAlign:"middle" }}>
          {/* One render for every viewer: the Q button on rows whose position carries a
              target (registry hasTargets, via TARGET_REQUIRED_ROLES), "—" on every other;
              both centred in the cell, header centred to match (owner 2026-11-02). */}
          {TARGET_REQUIRED_ROLES.indexOf(positionOf(u))>=0
            ?<button onClick={function(){
                var qt=getQTargets(uid);
                var yr=new Date().getFullYear();
                setQtModal({
                  user:u,
                  year: yr,
                  allTargets: Object.assign({}, qt),
                  targets:{
                    Q1: readQTargetClient(qt, yr, 1),
                    Q2: readQTargetClient(qt, yr, 2),
                    Q3: readQTargetClient(qt, yr, 3),
                    Q4: readQTargetClient(qt, yr, 4)
                  }
                });
              }}
                title="Quarterly Targets"
                style={{ padding:"3px 10px", borderRadius:6, border:"1px solid "+C.accent, background:C.accent+"10", color:C.accent, fontSize:11, fontWeight:700, cursor:"pointer" }}>Q</button>
            :<span style={{ fontSize:11, color:C.textLight }}>—</span>}
        </td>
        <td style={{ padding:"11px 12px" }}>
          {(function(){
            var isOn=u.lastSeen&&(Date.now()-new Date(u.lastSeen).getTime())<3*60*1000;
            var ls=u.lastSeen?timeAgo(u.lastSeen,p.t):"—";
            return <div style={{ display:"flex", alignItems:"center", gap:5 }}>
              <span style={{ width:8, height:8, borderRadius:"50%", background:isOn?"#22C55E":"#CBD5E1", flexShrink:0 }}/>
              <span style={{ fontSize:11, color:isOn?C.success:C.textLight, fontWeight:isOn?700:400 }}>{isOn?"Online":ls}</span>
            </div>;
          })()}
        </td>
        <td style={{ padding:"11px 12px", fontSize:12, color:C.textLight, whiteSpace:"nowrap" }}>
          {(function(){
            if(!u.startingDate) return "—";
            var d=new Date(u.startingDate);
            if(isNaN(d.getTime())) return "—";
            return d.toLocaleDateString("en-GB");
          })()}
        </td>
        <td style={{ padding:"11px 12px" }}>{isOwnerRow
          ? <span style={{ fontSize:11, color:C.textLight }}>—</span>
          : <Badge bg={u.active?"#DCFCE7":"#FEE2E2"} color={u.active?"#15803D":"#B91C1C"} onClick={function(){toggleActive(u);}}>{u.active?t.active:t.inactive}</Badge>}</td>
        <td style={{ padding:"11px 12px" }}><div style={{display:"flex",gap:6,alignItems:"center"}}><button onClick={function(){if(!ownerLocked){setPwModal({userId:uid,userName:displayName});setPwForm({newPass:"",confirmPass:""});setPwMsg("");}}} disabled={ownerLocked} style={{ width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", cursor:ownerLocked?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", opacity:ownerLocked?0.3:1 }} title={ownerLocked?"Owner password resets via Forgot Password only":t.changePassword}><KeyRound size={12} color={C.info}/></button>
              {canSeePage(p.access,"users")&&!ownerLocked&&<button onClick={function(){openEditUser(u,uid,displayName,isOwnerRow);}} style={{ width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }} title={t.edit||"Edit"}><Edit size={12} color={C.accent}/></button>}
              {canSeeDevices&&<button onClick={function(){setDevicesModal({userId:uid,userName:displayName});}} style={{ width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }} title="Devices & sessions"><Monitor size={12} color={C.info}/></button>}
              <button onClick={function(){if(!isOwnerRow&&u.role!=="admin")del(uid);}} disabled={isOwnerRow||u.role==="admin"} style={{ width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", cursor:(isOwnerRow||u.role==="admin")?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", opacity:(isOwnerRow||u.role==="admin")?0.3:1 }} title={isOwnerRow?"Owner cannot be deleted":(u.role==="admin"?"Admin users cannot be deleted":"")}><Trash2 size={12} color={C.danger}/></button></div></td>
      </tr>;})}
      </tbody>
    </table></div></Card>
    {qtModal&&<Modal show={true} onClose={function(){setQtModal(null);}} title={"🎯 Quarterly Targets — "+qtModal.user.name}>
      <div style={{ fontSize:12, color:C.textLight, marginBottom:14, padding:"8px 12px", background:"#F8FAFC", borderRadius:8 }}>
        تارجت كل Quarter بالمليون — بيتحسب من Deals Done Deal
      </div>
      {/* Year selector — switch between years; other years stay persisted in allTargets. */}
      <div style={{ marginBottom:14 }}>
        <label style={{ display:"block", fontSize:12, fontWeight:600, color:C.textLight, marginBottom:4 }}>Year</label>
        <Select value={String(qtModal.year)}
          onChange={function(e){
            var newYear = Number(e.target.value);
            setQtModal(function(prev){
              return Object.assign({}, prev, {
                year: newYear,
                targets: {
                  Q1: readQTargetClient(prev.allTargets, newYear, 1),
                  Q2: readQTargetClient(prev.allTargets, newYear, 2),
                  Q3: readQTargetClient(prev.allTargets, newYear, 3),
                  Q4: readQTargetClient(prev.allTargets, newYear, 4)
                }
              });
            });
          }}
          width={140} maxWidth={140} fontSize={13}
          options={(function(){
            var nowYear = new Date().getFullYear();
            return [nowYear - 1, nowYear, nowYear + 1].map(function(y){
              return { value:String(y), label:String(y) + (y === nowYear ? " (current)" : "") };
            });
          })()}/>
      </div>
      {["Q1","Q2","Q3","Q4"].map(function(q,i){
        var labels=["Jan — Mar","Apr — Jun","Jul — Sep","Oct — Dec"];
        // Below-minimum is a WARNING, not a block — the Owner may have a
        // legitimate exception, but should see it before saving. Already-ended
        // quarters are flagged here and refused by the server unless overridden.
        var _v=Number(qtModal.targets[q])||0;
        var _low=_v>0&&_v<MIN_QUARTERLY_TARGET;
        var _now=new Date(), _nY=_now.getFullYear(), _nQ=Math.floor(_now.getMonth()/3)+1, _qn=i+1;
        var _past=(qtModal.year<_nY)||(qtModal.year===_nY&&_qn<_nQ);
        return <div key={q} style={{ marginBottom:11 }}>
          <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:4 }}>{q} {qtModal.year} <span style={{ fontSize:11, color:C.textLight, fontWeight:400 }}>({labels[i]})</span>{_past&&<span style={{ fontSize:10, color:C.textLight, fontWeight:600, background:"#F1F5F9", borderRadius:6, padding:"1px 6px", marginInlineStart:6 }}>ended — frozen</span>}</label>
          <input type="text" placeholder="e.g. 40,000,000"
            value={qtModal.targets[q]?Number(qtModal.targets[q]).toLocaleString():""}
            onChange={function(e){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");setQtModal(function(prev){return Object.assign({},prev,{targets:Object.assign({},prev.targets,{[q]:r?Number(r):0})});});}}
            style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid "+(_low?C.warning:"#E2E8F0"), fontSize:14, boxSizing:"border-box", direction:"ltr" }}/>
          {_low&&<div style={{ fontSize:11, color:"#B45309", marginTop:3 }}>Below the {MIN_QUARTERLY_TARGET.toLocaleString("en-US")} minimum — you'll be asked to confirm.</div>}
        </div>;
      })}
      <div style={{ display:"flex", gap:10, marginTop:4 }}>
        <Btn outline onClick={function(){setQtModal(null);}} style={{ flex:1 }}>Cancel</Btn>
        <Btn onClick={function(){
          // Merge year's targets into allTargets using year-aware keys.
          // If saving the CURRENT year, also drop legacy bare keys so the
          // server's backward-compat fallback doesn't return stale legacy data.
          var nowYear = new Date().getFullYear();
          var merged = Object.assign({}, qtModal.allTargets);
          ["Q1","Q2","Q3","Q4"].forEach(function(q){
            var k = qtModal.year + "-" + q;
            var v = Number(qtModal.targets[q]) || 0;
            if (v > 0) merged[k] = v; else delete merged[k];
            if (qtModal.year === nowYear) delete merged[q];
          });
          saveQTargets(gid(qtModal.user), merged).then(function(ok){ if(ok) setQtModal(null); });
        }} style={{ flex:1 }}>✅ Save</Btn>
      </div>
    </Modal>}
    {devicesModal&&<Modal show={true} onClose={function(){setDevicesModal(null);}} title={"🖥️ Devices & Sessions — "+devicesModal.userName} w={640}><UserDevicesPanel userId={devicesModal.userId} userName={devicesModal.userName} token={p.token} csrfToken={p.csrfToken} t={t}/></Modal>}
    {editModal&&<Modal show={true} onClose={function(){setEditModal(null);}} title={"✏️ Edit User — "+editModal.userName}>
      {/* Role FIRST, matching the Add form — it decides which hierarchy row is
          editable below, so it is chosen before the fields it governs. */}
      {editModal.isOwner
        ? <div style={{ marginBottom:13 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>{t.role}</label>
            <Select value="admin" disabled fontSize={14} options={[{ value:"admin", label:t.admin }]}/>
            <div style={{fontSize:11,color:"#92400E",marginTop:4,fontWeight:600}}>👑 Owner role cannot be changed</div>
          </div>
        : <Inp label={t.role} type="select" value={editModal.role}
            onChange={function(e){setEditModal(function(prev){return applyRoleToDraft(prev,e.target.value,p.users);});}}
            options={USER_ROLE_OPTIONS(t)}/>}
      <Inp label={t.title} type="select" value={editModal.title}
        onChange={function(e){
          var newTitle=e.target.value;
          var mapped=ROLE_FOR_TITLE[newTitle];
          // Title maps to Role, and a Role change can invalidate the current
          // leader (a team_leader cannot report to a team_leader) — same guard
          // the Role select and the Add form use.
          setEditModal(function(prev){
            var withTitle=Object.assign({},prev,{title:newTitle});
            return mapped?applyRoleToDraft(withTitle,mapped,p.users):withTitle;
          });
        }}
        req={isLadderRoleFE(editModal.role)}
        options={[{value:"",label:"- Select Job Title -"}].concat(jobTitleOptionsForRole(editModal.role))}/>
      {!editModal.isOwner&&<div style={{fontSize:11,color:C.textLight,marginTop:-6,marginBottom:12}}>Job Titles are the ones for the selected Role. Change the Role to see a different ladder.</div>}
      {/* Hierarchy — the retired Edit Team modal's reportsTo picker, now sharing
          one component with Add User. Excludes the user and their whole subtree
          (picking either would be a cycle) and warns how many people move with
          them. The Owner sits outside the sales tree entirely. */}
      {!editModal.isOwner&&(function(){
        var subtree=descendantsOf(editModal.userId,p.users);
        return <HierarchyBlock users={p.users} role={editModal.role} value={editModal.reportsTo||""}
                  childRank={rankOfTitleFE(editModal.role, editModal.title)}
          subjectName={editModal.userName}
          subtreeCount={subtree.length}
          excludeIds={[editModal.userId].concat(subtree.map(function(u){return gid(u);}))}
          onChange={function(id){setEditModal(function(prev){return Object.assign({},prev,{reportsTo:id});});}}/>;
      })()}
      <div style={{ marginBottom:12 }}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, marginBottom:5 }}>Starting Date</label>
        <input type="date" max={todayStr} value={editModal.startingDate||""} onChange={function(e){setEditModal(function(prev){return Object.assign({},prev,{startingDate:e.target.value});});}}
          style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>
      </div>
      <div style={{ marginBottom:12 }}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, marginBottom:5 }}>Saturday Schedule</label>
        <Select value={editModal.saturdaySchedule||"always_work"} onChange={function(e){setEditModal(function(prev){return Object.assign({},prev,{saturdaySchedule:e.target.value, saturdayPatternStartDate: e.target.value==="alternating" ? prev.saturdayPatternStartDate : ""});});}}
          fontSize={14} options={[{ value:"always_work", label:"Always work" },{ value:"always_off", label:"Always off" },{ value:"alternating", label:"Alternating (every other Saturday)" }]}/>
        <div style={{fontSize:11, color:C.textLight, marginTop:4}}>Friday is off for everyone. Saturday is per-user — Owner can override single Saturdays from the salary sheet.</div>
      </div>
      {editModal.saturdaySchedule==="alternating" && <div style={{ marginBottom:12 }}>
        <label style={{ display:"block", fontSize:13, fontWeight:600, marginBottom:5 }}>Pattern Start Date <span style={{color:C.danger, fontWeight:400}}>*</span></label>
        <input type="date" value={editModal.saturdayPatternStartDate||""} onChange={function(e){setEditModal(function(prev){return Object.assign({},prev,{saturdayPatternStartDate:e.target.value});});}}
          style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>
        <div style={{fontSize:11, color:C.textLight, marginTop:4}}>Pick a Saturday that <b>is</b> a working day. Subsequent alternating Saturdays anchor on this date (even weeks-since-anchor are work, odd are off).</div>
      </div>}
      <div style={{display:"flex",gap:10}}><Btn outline onClick={function(){setEditModal(null);}} style={{flex:1}}>{t.cancel}</Btn><Btn onClick={saveUserEdit} loading={editSaving} style={{flex:1}}>{t.save}</Btn></div>
    </Modal>}
    {pwModal&&<Modal show={true} onClose={function(){setPwModal(null);setPwMsg("");}} title={t.changePassword+" — "+pwModal.userName}>
      <Inp label={t.newPassword} type="password" value={pwForm.newPass} onChange={function(e){setPwForm(Object.assign({},pwForm,{newPass:e.target.value}));setPwMsg("");}}/>
      <Inp label={t.confirmPassword} type="password" value={pwForm.confirmPass} onChange={function(e){setPwForm(Object.assign({},pwForm,{confirmPass:e.target.value}));setPwMsg("");}}/>
      {pwMsg&&<div style={{padding:"9px 14px",borderRadius:9,fontSize:13,marginBottom:12,background:pwMsg.startsWith("✅")?"#DCFCE7":"#FEE2E2",color:pwMsg.startsWith("✅")?"#15803D":"#B91C1C"}}>{pwMsg}</div>}
      <div style={{display:"flex",gap:10}}><Btn outline onClick={function(){setPwModal(null);setPwMsg("");}} style={{flex:1}}>{t.cancel}</Btn><Btn onClick={changePassword} loading={pwSaving} style={{flex:1}}>{t.save}</Btn></div>
    </Modal>}
    <Modal show={showAdd} onClose={function(){setShowAdd(false);}} title={t.addUser}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
        {/* Role FIRST — it decides the shape of everything below it (which
            hierarchy row is editable, whether Quarterly Target applies), so it
            is chosen before the fields it governs rather than after them. */}
        <div style={{ gridColumn:"1/-1" }}><Inp label={t.role} type="select" value={nU.role} onChange={function(e){setNU(applyRoleToDraft(nU,e.target.value,p.users));}} options={USER_ROLE_OPTIONS(t)}/></div>
        <div style={{ gridColumn:"1/-1" }}><Inp label={t.name} req value={nU.name} onChange={function(e){setNU(Object.assign({},nU,{name:e.target.value}));}}/></div>
        <Inp label={t.username} req value={nU.username} onChange={function(e){setNU(Object.assign({},nU,{username:e.target.value}));}}/>
        <Inp label={t.password} req value={nU.password} onChange={function(e){setNU(Object.assign({},nU,{password:e.target.value}));}}/>
        <Inp label={t.title} type="select" value={nU.title}
          onChange={function(e){
            var newTitle=e.target.value;
            var mapped=ROLE_FOR_TITLE[newTitle];
            // Title maps to Role, and a Role change can invalidate the chosen
            // leader — run it through the same guard the Role select uses.
            var withTitle=Object.assign({},nU,{title:newTitle});
            setNU(mapped?applyRoleToDraft(withTitle,mapped,p.users):withTitle);
          }}
          req={isLadderRoleFE(nU.role)}
          options={[{value:"",label:"- Select Job Title -"}].concat(jobTitleOptionsForRole(nU.role))}/>
        <Inp label={t.email} value={nU.email} onChange={function(e){setNU(Object.assign({},nU,{email:e.target.value}));}}/>
        {/* Phone + Starting Date share one two-column row, matching the
            Username/Password and Job Title/Email pairs above. marginBottom
            tracks Inp's own 13 so both cells sit on the same baseline. */}
        <Inp label={t.phone} req value={nU.phone} onChange={function(e){setNU(Object.assign({},nU,{phone:e.target.value}));}}/>
        <div style={{ marginBottom:13 }}>
          <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>Starting Date<span style={{ color:C.danger, marginRight:3 }}>*</span></label>
          <input type="date" max={todayStr} value={nU.startingDate||""} onChange={function(e){setNU(Object.assign({},nU,{startingDate:e.target.value}));}}
            style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, boxSizing:"border-box" }}/>
        </div>
        {TARGET_REQUIRED_ROLES.indexOf(targetPositionOfDraft(nU))!==-1 && (function(){
          // Quarterly Target — mandatory at creation (owner decision 2026-08-14),
          // replacing the old free-text "Monthly Target" which nothing ever read.
          // Seeds the current quarter and every remaining quarter of this year;
          // past quarters are deliberately left empty, because an agent who did
          // not exist then must not carry a target that drags historical
          // achievement reports down.
          var _n=new Date(), _y=_n.getFullYear(), _q=Math.floor(_n.getMonth()/3)+1;
          var _seeded=[]; for(var _i=_q;_i<=4;_i++) _seeded.push("Q"+_i);
          var _raw=String(nU.quarterlyTarget||"").replace(/,/g,"");
          var _num=Number(_raw)||0;
          var _low=_raw!==""&&_num>0&&_num<MIN_QUARTERLY_TARGET;
          return <div style={{ gridColumn:"1/-1", marginBottom:13 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5 }}>
              Quarterly Target (EGP)<span style={{ color:C.danger, marginRight:3 }}>*</span>
            </label>
            <input type="text" inputMode="numeric" placeholder="e.g. 40,000,000"
              value={_raw?Number(_raw).toLocaleString("en-US"):""}
              onChange={function(e){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");setNU(Object.assign({},nU,{quarterlyTarget:r}));}}
              style={{ width:"100%", padding:"9px 12px", borderRadius:10, fontSize:14, boxSizing:"border-box", direction:"ltr",
                       border:"1px solid "+(_low?C.danger:"#E2E8F0") }}/>
            <div style={{ fontSize:11, color:_low?C.danger:C.textLight, marginTop:4 }}>
              {_low
                ? "Minimum is "+MIN_QUARTERLY_TARGET.toLocaleString("en-US")+" EGP."
                : "Minimum "+MIN_QUARTERLY_TARGET.toLocaleString("en-US")+" EGP. Applies to "+_seeded.join(", ")+" "+_y+" — earlier quarters stay empty."}
            </div>
          </div>;
        })()}
        {/* Hierarchy — replaces the old free-text Team Name / Team Code pair.
            Last in the form: Role (top) decides which row is editable here. */}
        <div style={{ gridColumn:"1/-1" }}>
          <HierarchyBlock users={p.users} role={nU.role} value={nU.reportsTo||""} required
            childRank={rankOfTitleFE(nU.role, nU.title)}
            subjectName={nU.name ? nU.name.trim() : ""}
            onChange={function(id){ setNU(Object.assign({},nU,{reportsTo:id})); }}/>
        </div>
      </div>
      <div style={{ display:"flex", gap:10 }}><Btn outline onClick={function(){setShowAdd(false);}} style={{ flex:1 }}>{t.cancel}</Btn><Btn onClick={add} loading={saving} style={{ flex:1 }}>{t.add}</Btn></div>
    </Modal>
  </div>;
};

// ===== TEAM =====
// ===== Phase 4 (Target Periods) — admin "Manage Target Periods" modal. Owner-
// only entry from the Sales Team page. Per leader: create draft → edit member
// presence (joinedAt/leftAt) → Close (freeze) → Reopen. Closing a period is what
// makes the Sales Team cards show that quarter's frozen, roster-locked numbers;
// individual agents' own qTargets are never touched.
var TargetPeriodsManager = function(pp) {
  var year = pp.year, quarter = pp.quarter;
  var qKey = year + "-Q" + quarter;
  var [periods, setPeriods] = useState(null);        // array | null=loading
  var [busy, setBusy] = useState(false);
  var [expanded, setExpanded] = useState(null);      // leaderId (string) expanded
  var [edits, setEdits] = useState({});              // periodId -> { key -> {joinedAt, leftAt} } (YYYY-MM-DD)
  var [orphans, setOrphans] = useState([]);          // hard-deleted producers this quarter (manual-link dropdown)
  var [pickUser, setPickUser] = useState({});        // periodId -> selected userId to add
  var [manualForm, setManualForm] = useState({});    // periodId -> { open, userName, role, fullQuarterTarget, agentId, joinedAt, leftAt }

  var leaders = (pp.users || []).filter(function(u){ return (positionOf(u) === "manager" || positionOf(u) === "team_leader") && u.active; });
  var userById = {}; (pp.users || []).forEach(function(u){ userById[String(gid(u))] = u; });
  var isInactiveUser = function(uid){ var u = userById[String(uid)]; return !!(u && u.active === false); };
  // Addable = any sales / team_leader (incl. deactivated) not already in the period.
  var addableUsers = function(period){
    var inPeriod = {}; (period.members || []).forEach(function(m){ if (m.userId) inPeriod[String(m.userId)] = true; });
    return (pp.users || []).filter(function(u){ return (positionOf(u) === "sales" || positionOf(u) === "team_leader") && !inPeriod[String(gid(u))]; });
  };
  var inpS = { fontSize: 12, padding: "3px 5px", border: "1px solid " + C.border, borderRadius: 5, width: 120 };

  var load = function(){
    apiFetch("/api/target-periods?year=" + year + "&quarter=" + quarter, "GET", null, pp.token)
      .then(function(rows){ setPeriods(Array.isArray(rows) ? rows : []); })
      .catch(function(){ setPeriods([]); });
    apiFetch("/api/target-periods/orphan-producers?year=" + year + "&quarter=" + quarter, "GET", null, pp.token)
      .then(function(rows){ setOrphans(Array.isArray(rows) ? rows : []); })
      .catch(function(){ setOrphans([]); });
  };
  var liveTpRev = useLiveRev("targetPeriod");
  useEffect(function(){ load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year, quarter, liveTpRev]);

  var periodByLeader = {};
  (periods || []).forEach(function(pr){ periodByLeader[String(pr.leaderId)] = pr; });

  var fmtEGP = function(n){ return (Number(n) || 0).toLocaleString(); };
  var fmtDate = function(d){ return d ? new Date(d).toISOString().slice(0, 10) : ""; };
  var pct = function(f){ return Math.round((Number(f) || 0) * 100); };
  var afterChange = function(){ load(); if (pp.onChanged) pp.onChanged(); };

  var createDraft = async function(leaderId, refresh){
    if (busy) return; setBusy(true);
    try {
      var d = await apiFetch("/api/target-periods/draft", "POST", { leaderId: leaderId, year: year, quarter: quarter, refresh: !!refresh }, pp.token, pp.csrfToken);
      // Year-rollover report. Carried-forward targets are defaults the Owner is
      // expected to review; anyone with no prior target at all cannot be seeded
      // automatically and is named here so the gap doesn't stay invisible.
      var cf = d && d.targetCarryForward;
      if (cf && ((cf.seeded||[]).length || (cf.noHistory||[]).length)) {
        var lines = [];
        if ((cf.seeded||[]).length) {
          lines.push("Carried " + cf.seeded.length + " target(s) forward into " + year + ":");
          lines.push(cf.seeded.map(function(s){ return "   " + s.name + " — " + Number(s.value).toLocaleString("en-US"); }).join("\n"));
          lines.push("Review and adjust per person as needed.");
        }
        if ((cf.noHistory||[]).length) {
          lines.push((lines.length?"\n":"") + "No target could be carried for " + cf.noHistory.length + " user(s) — set these manually:");
          lines.push(cf.noHistory.map(function(s){ return "   " + s.name; }).join("\n"));
        }
        alert(lines.join("\n"));
      }
      afterChange();
    }
    catch(e){ alert(e.message || "Failed to create draft"); } finally { setBusy(false); }
  };
  // Target-aggregation mode per leader (direct | subtree). Flipping it changes how
  // the leader's card/row rolls up on the Sales Team page for OPEN quarters (live
  // recompute); CLOSED periods stay frozen (the mode used was snapshotted at close).
  // onChanged() bumps member-stats so the page reflects the flip immediately.
  var saveScope = async function(leaderId, mode){
    if (busy) return; setBusy(true);
    try {
      await apiFetch("/api/users/" + leaderId, "PUT", { targetScope: mode }, pp.token, pp.csrfToken);
      if (pp.setUsers) pp.setUsers(function(prev){ return prev.map(function(u){ return String(gid(u)) === String(leaderId) ? Object.assign({}, u, { targetScope: mode }) : u; }); });
      afterChange();
    } catch(e){ alert(e.message || "Failed to update roll-up mode"); } finally { setBusy(false); }
  };
  var openEditor = function(period){
    var lid = String(period.leaderId);
    if (expanded === lid) { setExpanded(null); return; }
    setExpanded(lid);
    var seed = {};
    (period.members || []).forEach(function(m){ seed[String(m.userId || m.manualId)] = { joinedAt: fmtDate(m.joinedAt), leftAt: fmtDate(m.leftAt) }; });
    setEdits(function(prev){ var n = Object.assign({}, prev); n[String(period._id)] = seed; return n; });
  };
  var setMemberEdit = function(periodId, uid, field, val){
    setEdits(function(prev){
      var pe = Object.assign({}, prev[periodId] || {});
      pe[uid] = Object.assign({ joinedAt: "", leftAt: "" }, pe[uid] || {});
      pe[uid][field] = val;
      var n = Object.assign({}, prev); n[periodId] = pe; return n;
    });
  };
  var savePresence = async function(period){
    if (busy) return; setBusy(true);
    try {
      var e = edits[String(period._id)] || {};
      var members = Object.keys(e).map(function(k){ return { key: k, joinedAt: e[k].joinedAt || null, leftAt: e[k].leftAt || null }; });
      await apiFetch("/api/target-periods/" + period._id, "PATCH", { members: members }, pp.token, pp.csrfToken); afterChange();
    } catch(e){ alert(e.message || "Failed to save presence"); } finally { setBusy(false); }
  };
  var doClose = async function(period){
    if (busy) return;
    if (!window.confirm("Freeze " + qKey + " for " + (period.teamName || "this team") + "?\n\nClosed periods are immutable — the Sales Team page shows these frozen numbers for this quarter. Reopen to edit.")) return;
    setBusy(true);
    try { await apiFetch("/api/target-periods/" + period._id + "/close", "POST", null, pp.token, pp.csrfToken); afterChange(); }
    catch(e){ alert(e.message || "Failed to close"); } finally { setBusy(false); }
  };
  var doReopen = async function(period){
    if (busy) return;
    if (!window.confirm("Reopen " + qKey + "?\n\nThis unfreezes it back to a draft; the Sales Team page reverts to live numbers until you close it again.")) return;
    setBusy(true);
    try { await apiFetch("/api/target-periods/" + period._id + "/reopen", "POST", { reason: "admin reopen" }, pp.token, pp.csrfToken); afterChange(); }
    catch(e){ alert(e.message || "Failed to reopen"); } finally { setBusy(false); }
  };
  var doDelete = async function(period){
    if (busy) return;
    if (!window.confirm("Delete this draft period for " + qKey + "?")) return;
    setBusy(true);
    try { await apiFetch("/api/target-periods/" + period._id, "DELETE", null, pp.token, pp.csrfToken); afterChange(); }
    catch(e){ alert(e.message || "Failed to delete"); } finally { setBusy(false); }
  };
  var addExisting = async function(period){
    if (busy) return;
    var uid = pickUser[String(period._id)];
    if (!uid) return;
    setBusy(true);
    try {
      await apiFetch("/api/target-periods/" + period._id + "/members", "POST", { userId: uid }, pp.token, pp.csrfToken);
      setPickUser(function(prev){ var n = Object.assign({}, prev); delete n[String(period._id)]; return n; });
      afterChange();
    } catch(e){ alert(e.message || "Failed to add member"); } finally { setBusy(false); }
  };
  var setManual = function(pid, field, val){ setManualForm(function(prev){ var f = Object.assign({ open: true }, prev[pid] || {}); f[field] = val; var n = Object.assign({}, prev); n[pid] = f; return n; }); };
  var addManual = async function(period){
    if (busy) return;
    var f = manualForm[String(period._id)] || {};
    if (!f.userName || !f.userName.trim()) { alert("Enter a name"); return; }
    setBusy(true);
    try {
      await apiFetch("/api/target-periods/" + period._id + "/members", "POST", { manual: true, userName: f.userName, role: f.role || "sales", fullQuarterTarget: Number(f.fullQuarterTarget) || 0, agentId: f.agentId || null, joinedAt: f.joinedAt || null, leftAt: f.leftAt || null }, pp.token, pp.csrfToken);
      setManualForm(function(prev){ var n = Object.assign({}, prev); delete n[String(period._id)]; return n; });
      afterChange();
    } catch(e){ alert(e.message || "Failed to add member"); } finally { setBusy(false); }
  };
  var removeMember = async function(period, key){
    if (busy) return;
    if (!window.confirm("Remove this member from the period?")) return;
    setBusy(true);
    try { await apiFetch("/api/target-periods/" + period._id + "/members?key=" + encodeURIComponent(key), "DELETE", null, pp.token, pp.csrfToken); afterChange(); }
    catch(e){ alert(e.message || "Failed to remove"); } finally { setBusy(false); }
  };
  var autoCloseAll = async function(){
    if (busy) return;
    if (!window.confirm("Auto-close ALL leaders for " + qKey + " from the live roster + membership log?\n\nSkips any leader that already has a period. Members who left during the quarter may need a manual add (reopen → Add member).")) return;
    setBusy(true);
    try { var r = await apiFetch("/api/target-periods/auto-close", "POST", { year: year, quarter: quarter }, pp.token, pp.csrfToken); afterChange(); alert("Auto-closed " + ((r && r.closed) || 0) + " period(s); skipped " + ((r && r.skipped) || 0) + " (already had a period)."); }
    catch(e){ alert(e.message || "Failed to auto-close"); } finally { setBusy(false); }
  };
  var badge = function(status){
    var m = { closed: { bg:"#dcfce7", fg:"#166534", label:"Closed ✓ frozen" }, draft: { bg:"#fef9c3", fg:"#854d0e", label:"Draft" }, open: { bg:"#dbeafe", fg:"#1e40af", label:"Open" } }[status] || { bg:"#f1f5f9", fg:"#475569", label:"No period" };
    return <span style={{ background:m.bg, color:m.fg, padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:700 }}>{m.label}</span>;
  };

  return <Modal show={true} onClose={pp.onClose} title={"🎯 Manage Target Periods — " + qKey}>
    <div style={{ fontSize:12.5, color:C.textLight, marginBottom:14, lineHeight:1.5 }}>
      Lock this quarter's team targets to the roster that was actually present. Create a draft, set each member's join / leave dates (blank = present all quarter), then <b>Close</b> to freeze. Closed quarters show frozen numbers on the Sales Team page; individual agents' own targets are never changed.
    </div>
    <div style={{ marginBottom:12, display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
      <Btn outline disabled={busy} onClick={autoCloseAll} style={{ padding:"5px 11px", fontSize:12 }}>⚡ Auto-close all leaders for {qKey}</Btn>
      <span style={{ fontSize:11, color:C.textLight }}>freezes every leader from the live roster + membership log (skips ones you've already started; a daily job does this automatically after a quarter ends)</span>
    </div>
    {periods === null
      ? <div style={{ padding:20, textAlign:"center", color:C.textLight }}>Loading…</div>
      : leaders.length === 0
        ? <div style={{ padding:20, textAlign:"center", color:C.textLight }}>No managers or team leaders found.</div>
        : <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {leaders.map(function(lead){
              var lid = String(gid(lead));
              var period = periodByLeader[lid] || null;
              var status = period ? period.status : "none";
              var isOpen = expanded === lid && period;
              var leadMode = lead.targetScope === "subtree" ? "subtree" : "direct";
              return <div key={lid} style={{ border:"1px solid "+C.border, borderRadius:10, padding:12, background:"#fff" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:180, flexWrap:"wrap" }}>
                    <span style={{ fontWeight:700, color:C.text }}>{lead.name}</span>
                    <span style={{ fontSize:11, color:C.textLight }}>{lead.role}</span>
                    {badge(status)}
                    {/* Frozen roll-up mode used at close (transparency) — closed periods only. */}
                    {status==="closed" && period && <span
                      title={"Roll-up mode frozen when this period was closed: " + (period.targetScopeUsed==="subtree" ? "full subtree (team leaders' teams included)" : "direct reports only")}
                      style={{ fontSize:10, fontWeight:700, color: period.targetScopeUsed==="subtree" ? "#3b5cb8" : "#64748b", background: period.targetScopeUsed==="subtree" ? "#eef2ff" : "#f1f5f9", padding:"2px 8px", borderRadius:20 }}>
                      {period.targetScopeUsed==="subtree" ? "subtree" : "direct"} roll-up
                    </span>}
                  </div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
                    {/* Target roll-up mode — direct reports vs full subtree. Applies to
                        OPEN quarters live; closed periods stay frozen at the mode used. */}
                    <span style={{ display:"flex", alignItems:"center", gap:5, marginRight:2 }}
                      title={"How " + (lead.name||"this leader") + "'s Target/Achieved roll up on the Sales Team page.\nDirect reports = direct reports only (default).\nFull subtree = the entire reporting tree beneath them (team leaders' teams included).\nClosed periods stay frozen at the mode used when closed."}>
                      <span style={{ fontSize:11, color:C.textLight }}>Roll-up:</span>
                      <Select value={leadMode} disabled={busy} onChange={function(ev){ saveScope(lid, ev.target.value); }}
                        width={130} maxWidth={130} fontSize={11}
                        options={[{ value:"direct", label:"Direct reports" },{ value:"subtree", label:"Full subtree" }]}/>
                    </span>
                    {period && <span style={{ fontSize:12, color:C.textLight }}>Target <b style={{ color:C.text }}>{fmtEGP(period.teamTarget)}</b>{status==="closed" ? <span> · Achieved <b style={{ color:C.text }}>{fmtEGP(period.teamAchievedSnapshot)}</b></span> : null}</span>}
                    {!period && <Btn outline disabled={busy} onClick={function(){createDraft(lid,false);}} style={{ padding:"5px 10px", fontSize:12 }}>Create draft</Btn>}
                    {period && status!=="closed" && <Btn outline disabled={busy} onClick={function(){openEditor(period);}} style={{ padding:"5px 10px", fontSize:12 }}>{isOpen?"Hide":"Edit roster"}</Btn>}
                    {period && status!=="closed" && <Btn success disabled={busy} onClick={function(){doClose(period);}} style={{ padding:"5px 10px", fontSize:12 }}>Close</Btn>}
                    {period && status!=="closed" && <Btn danger disabled={busy} onClick={function(){doDelete(period);}} style={{ padding:"5px 10px", fontSize:12 }}>Delete</Btn>}
                    {period && status==="closed" && <Btn outline disabled={busy} onClick={function(){doReopen(period);}} style={{ padding:"5px 10px", fontSize:12 }}>Reopen</Btn>}
                  </div>
                </div>
                {isOpen && <div style={{ marginTop:12, borderTop:"1px solid "+C.border, paddingTop:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, gap:8, flexWrap:"wrap" }}>
                    <span style={{ fontSize:12, fontWeight:700, color:C.text }}>Roster — join / leave within {qKey}</span>
                    <div style={{ display:"flex", gap:6 }}>
                      <Btn outline disabled={busy} onClick={function(){createDraft(lid,true);}} style={{ padding:"4px 9px", fontSize:11 }}>↻ Reseed from roster</Btn>
                      <Btn disabled={busy} onClick={function(){savePresence(period);}} style={{ padding:"4px 9px", fontSize:11 }}>Save presence</Btn>
                    </div>
                  </div>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                      <thead><tr style={{ color:C.textLight, textAlign:"left" }}>
                        <th style={{ padding:"4px 6px" }}>Member</th>
                        <th style={{ padding:"4px 6px" }}>Joined</th>
                        <th style={{ padding:"4px 6px" }}>Left</th>
                        <th style={{ padding:"4px 6px", textAlign:"right" }}>Present</th>
                        <th style={{ padding:"4px 6px", textAlign:"right" }}>Full target</th>
                        <th style={{ padding:"4px 6px", textAlign:"right" }}>Pro-rated</th>
                      </tr></thead>
                      <tbody>
                        {(period.members||[]).map(function(m){
                          var uid = String(m.userId || m.manualId);
                          var e = (edits[String(period._id)]||{})[uid] || { joinedAt: fmtDate(m.joinedAt), leftAt: fmtDate(m.leftAt) };
                          return <tr key={uid} style={{ borderTop:"1px solid #f1f5f9" }}>
                            <td style={{ padding:"5px 6px" }}>
                              {!m.isLeader && <button onClick={function(){removeMember(period,uid);}} title="Remove from period" style={{ border:"none", background:"none", color:C.danger, cursor:"pointer", marginRight:5, fontSize:14, lineHeight:1, padding:0 }}>×</button>}
                              {m.userName}
                              {m.isLeader?<span style={{ fontSize:10, color:C.textLight }}> (leader)</span>:null}
                              {m.manual?<span style={{ fontSize:10, color:"#854d0e", background:"#fef9c3", padding:"1px 5px", borderRadius:10, marginLeft:4 }}>manual{m.agentId?" · linked":""}</span>:null}
                              {(!m.isLeader && !m.manual && isInactiveUser(m.userId))?<span style={{ fontSize:10, color:C.textLight, marginLeft:4 }}>(inactive)</span>:null}
                            </td>
                            <td style={{ padding:"5px 6px" }}>{m.isLeader ? <span style={{ color:C.textLight }}>—</span> : <input type="date" value={e.joinedAt} onChange={function(ev){setMemberEdit(String(period._id),uid,"joinedAt",ev.target.value);}} style={{ fontSize:12, padding:"2px 4px", border:"1px solid "+C.border, borderRadius:5 }}/>}</td>
                            <td style={{ padding:"5px 6px" }}>{m.isLeader ? <span style={{ color:C.textLight }}>—</span> : <input type="date" value={e.leftAt} onChange={function(ev){setMemberEdit(String(period._id),uid,"leftAt",ev.target.value);}} style={{ fontSize:12, padding:"2px 4px", border:"1px solid "+C.border, borderRadius:5 }}/>}</td>
                            <td style={{ padding:"5px 6px", textAlign:"right" }}>{pct(m.presenceFraction)}%</td>
                            <td style={{ padding:"5px 6px", textAlign:"right" }}>{fmtEGP(m.fullQuarterTarget)}</td>
                            <td style={{ padding:"5px 6px", textAlign:"right", fontWeight:600 }}>{m.isLeader?<span style={{ color:C.textLight }}>excl.</span>:fmtEGP(m.proratedTarget)}</td>
                          </tr>;
                        })}
                      </tbody>
                      <tfoot><tr style={{ borderTop:"2px solid "+C.border, fontWeight:700 }}>
                        <td style={{ padding:"6px" }} colSpan={5}>Team target (Σ pro-rated, leader excluded)</td>
                        <td style={{ padding:"6px", textAlign:"right" }}>{fmtEGP(period.teamTarget)}</td>
                      </tr></tfoot>
                    </table>
                  </div>
                  <div style={{ fontSize:11, color:C.textLight, marginTop:6 }}>Edit dates then <b>Save presence</b> to recompute pro-rated targets. The leader's own target is excluded from the team total (matches the Sales Team card).</div>
                  {/* Add a departed member — policy: anyone on the team during the period counts. */}
                  <div style={{ marginTop:12, paddingTop:10, borderTop:"1px dashed "+C.border }}>
                    <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:6 }}>Add a departed / off-roster member</div>
                    <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap", marginBottom:6 }}>
                      <SearchableSelect value={pickUser[String(period._id)]||""} onChange={function(v){setPickUser(function(prev){var n=Object.assign({},prev);n[String(period._id)]=v;return n;});}}
                        options={addableUsers(period).map(function(u){ return { value:gid(u), label:u.name+" ("+u.role+")"+(u.active===false?" — inactive":"") }; })}
                        emptyLabel="— pick a user (includes deactivated) —" placeholder="Search user…" width={280} maxWidth={280} fontSize={12}/>
                      <Btn outline disabled={busy||!pickUser[String(period._id)]} onClick={function(){addExisting(period);}} style={{ padding:"4px 9px", fontSize:11 }}>Add user</Btn>
                    </div>
                    {!(manualForm[String(period._id)]&&manualForm[String(period._id)].open)
                      ? <button onClick={function(){setManual(String(period._id),"open",true);}} style={{ border:"none", background:"none", color:C.accent, cursor:"pointer", fontSize:11, padding:0 }}>+ manual entry (hard-deleted member — no account)</button>
                      : (function(){ var f=manualForm[String(period._id)]||{}; return <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center", background:"#F8FAFC", padding:8, borderRadius:8 }}>
                          <input placeholder="Name" value={f.userName||""} onChange={function(ev){setManual(String(period._id),"userName",ev.target.value);}} style={inpS}/>
                          <input placeholder="Role" value={f.role||"sales"} onChange={function(ev){setManual(String(period._id),"role",ev.target.value);}} style={Object.assign({},inpS,{width:90})}/>
                          <input placeholder="Full target EGP" value={f.fullQuarterTarget||""} onChange={function(ev){setManual(String(period._id),"fullQuarterTarget",ev.target.value);}} style={Object.assign({},inpS,{width:120})}/>
                          <Select value={f.agentId||""} onChange={function(ev){var v=ev.target.value;var o=orphans.filter(function(x){return x.agentId===v;})[0];setManual(String(period._id),"agentId",v);if(o&&!f.userName)setManual(String(period._id),"userName",o.userName);}} searchable width={210} maxWidth={210} fontSize={11}
                            options={[{ value:"", label:"link deals: none" }].concat(orphans.map(function(o){ return { value:o.agentId, label:o.userName + " — " + o.deals + " deal(s)" }; }))}/>
                          <input type="date" title="joined" value={f.joinedAt||""} onChange={function(ev){setManual(String(period._id),"joinedAt",ev.target.value);}} style={inpS}/>
                          <input type="date" title="left" value={f.leftAt||""} onChange={function(ev){setManual(String(period._id),"leftAt",ev.target.value);}} style={inpS}/>
                          <Btn disabled={busy} onClick={function(){addManual(period);}} style={{ padding:"4px 9px", fontSize:11 }}>Add</Btn>
                        </div>; })()}
                    {orphans.length>0 && <div style={{ fontSize:10.5, color:C.textLight, marginTop:5 }}>{orphans.length} hard-deleted producer(s) with deals this quarter available to link.</div>}
                  </div>
                </div>}
              </div>;
            })}
          </div>}
  </Modal>;
};

var TeamPage = function(p) {
  var t=p.t;
  var isAdmin=p.cu.role==="admin"||p.cu.role==="operation"||positionOf(p.cu)==="director"||positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader";
  // The company TOTAL row is admin/operation only — `isAdmin` above spans every
  // managerial role, which is the right gate for the Teams Comparison TABLE but not
  // for a company-scoped footer. See the tfoot below.
  var isOnlyAdmin=p.cu.role==="admin"||p.cu.role==="operation";
  var allDeals=p.leads.filter(function(l){return l.status==="DoneDeal"&&!l.archived;});
  var getQ=function(date){var m=new Date(date).getMonth();return m<3?"Q1":m<6?"Q2":m<9?"Q3":"Q4";};
  var curQ=(function(){var m=new Date().getMonth();return m<3?"Q1":m<6?"Q2":m<9?"Q3":"Q4";})();
  var parseBudget=function(b){return parseFloat((b||"0").toString().replace(/,/g,""))||0;};
  var getQTargets=function(uid){
    var u=p.users.find(function(x){return gid(x)===uid;});
    if(u&&u.qTargets&&Object.keys(u.qTargets).length>0) return u.qTargets;
    try{return JSON.parse(localStorage.getItem("crm_qt_"+uid)||"{}");} catch(e){return {};}
  };
  // Same contract as the Users-page copy: mirrors local state only after the
  // server accepts, so a rejected past-quarter edit can't leave a phantom value.
  var saveQTargets=async function(uid,qt){
    var ok=await commitQTargets({uid:uid,merged:qt,token:p.token,csrfToken:p.csrfToken});
    if(!ok) return false;
    try{localStorage.setItem("crm_qt_"+uid,JSON.stringify(qt));}catch(e){}
    p.setUsers(function(prev){return prev.map(function(u){return gid(u)===uid?Object.assign({},u,{qTargets:qt}):u;});});
    return true;
  };
  var [viewQ,setViewQ]=useState(curQ);
  var curYear=new Date().getFullYear();
  var years=[curYear,curYear-1,curYear-2,curYear-3];
  var [viewYear,setViewYear]=useState(curYear);
  // STEP 4-5 X2.5 — server-driven MemberCard quarterly stats. Replaces the
  // per-card p.leads / p.activities scans that would silently undercount
  // after the X3 bootstrap shrink. One fetch covers every visible card;
  // we keep the in-memory derivation as a bootstrap-time fallback.
  var [memberStatsMap, setMemberStatsMap] = useState(null);
  // Company TOTAL for the Teams Comparison table — a member-level de-duplicated,
  // period-aware roll-up computed server-side (see /api/team/member-stats).
  var [memberStatsTotal, setMemberStatsTotal] = useState(null);
  // Phase 4 — admin "Manage Target Periods" modal + a bump that re-fetches
  // member-stats after a period is closed/reopened so cards reflect the change.
  var [showPeriods, setShowPeriods] = useState(false);
  var [periodBust, setPeriodBust] = useState(0);
  useEffect(function(){
    if (!p.token) return;
    var cancelled = false;
    var qNum = parseInt(String(viewQ).replace("Q","")) || 1;
    apiFetch("/api/team/member-stats?year="+viewYear+"&quarter="+qNum,"GET",null,p.token)
      .then(function(r){
        if (cancelled || !r || !Array.isArray(r.members)) return;
        var map = {};
        r.members.forEach(function(m){ if (m && m.uid) map[String(m.uid)] = m; });
        setMemberStatsMap(map);
        setMemberStatsTotal(r.total || null);
      })
      .catch(function(){});
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, viewYear, viewQ, p.cbBust, periodBust]);
  var [editQModal,setEditQModal]=useState(null);
  var [expandedManager,setExpandedManager]=useState(null); // uid of expanded manager
  // Hide-all confirm flow — admin / operation only. Triggers the same
  // visibility-hide effect as the Phase P 7-day staleness rule, immediately,
  // for every lead the agent sees. Backend sets assignments[].hiddenManually
  // on each non-EOI/DoneDeal slice; admin's view here is unaffected.
  var [unassignModal,setUnassignModal]=useState(null);
  var [unassigning,setUnassigning]=useState(false);
  var canUnassignAll = p.cu.role==="admin" || p.cu.role==="operation";
  var doUnassignAll = async function(){
    if (!unassignModal || unassigning) return;
    setUnassigning(true);
    try {
      var u = unassignModal.user;
      var uid = String(gid(u));
      var res = await apiFetch("/api/agents/"+uid+"/unassign-all-leads","POST",null,p.token,p.csrfToken);
      var n = (res && typeof res.hidden==="number") ? res.hidden : 0;
      setUnassignModal(null);
      alert("Hidden "+n+" lead"+(n===1?"":"s")+" from "+(u.name||"")+".");
    } catch(e) {
      alert(e.message||"Failed to hide leads");
    } finally {
      setUnassigning(false);
    }
  };

  // Build hierarchy: managers + team leaders + their teams
  var managers = p.users.filter(function(u){return (positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;});
  var getSalesUnder = function(muid){
    return p.users.filter(function(u){
      var rt=u.reportsTo&&u.reportsTo._id?String(u.reportsTo._id):String(u.reportsTo||"");
      return u.active && (positionOf(u)==="sales"||positionOf(u)==="team_leader") && rt===muid;
    });
  };

  // For team_leader: show only themselves (their card shows their team)
  // For manager: show themselves + their team_leaders
  var visibleManagers = isAdmin ? managers : managers.filter(function(m){return gid(m)===String(p.cu.id||"");});
  // Also show sales not under any manager (top-level sales)
  var topLevelSales = isAdmin ? p.users.filter(function(u){return positionOf(u)==="sales"&&u.active&&!u.reportsTo;}) : [];

  // Stable gradient assignment — same user always gets the same tile color
  // regardless of where they appear in the list. 8 gradients per the spec.
  var gradientForUid = function(uid){
    var h = 0; var s = String(uid||"");
    for (var i=0;i<s.length;i++) { h = (h*31 + s.charCodeAt(i)) & 0x7fffffff; }
    return "tp-grad-" + ((h % 8) + 1);
  };

  // Card for one member — gradient top + white bottom per the new design.
  var MemberCard = function(mp){
    var a=mp.user; var uid=String(gid(a));
    var isManagerCard = positionOf(a)==="manager"||positionOf(a)==="team_leader";
    // For manager card: get all team member IDs. Filter mirrors getSalesUnder
    // (line 7194) — active sales/team_leader only — so MemberCard counts
    // match the manager-group header (mRev) and don't inflate when an
    // inactive or wrong-role user happens to point reportsTo at this manager.
    var teamUids = isManagerCard ? new Set(p.users.filter(function(u){
      var rt=u.reportsTo&&u.reportsTo._id?String(u.reportsTo._id):String(u.reportsTo||"");
      return u.active && (positionOf(u)==="sales"||positionOf(u)==="team_leader") && rt===uid;
    }).map(function(u){return String(u._id);})) : null;
    // THE DEAL'S OWN PAIR (owner ruling 2026-09-12, R11) — dealAgentPairOf.
    var matchesAgent = function(d){
      var _dapMA=dealAgentPairOf(d);
      var aid=_dapMA.primary;
      var splitId=_dapMA.second;
      if(isManagerCard && teamUids){
        // teamUids is a snapshot of p.users built once at the top of this
        // MemberCard call. Belt-and-suspenders: re-verify at match time that
        // the candidate's CURRENT reportsTo still resolves to this team
        // leader's uid. The rule is universal — a team leader must only see
        // deals from agents who currently report to them — and this guard
        // makes a misattribution impossible even if teamUids ever drifts.
        var stillUnder=function(id){
          var u=p.users.find(function(x){return String(x._id)===id;});
          if(!u||!u.active) return false;
          if(positionOf(u)!=="sales"&&positionOf(u)!=="team_leader") return false;
          var rt=u.reportsTo&&u.reportsTo._id?String(u.reportsTo._id):String(u.reportsTo||"");
          return rt===uid;
        };
        return aid===uid
          ||(teamUids.has(aid)&&stillUnder(aid))
          ||(!!splitId&&(splitId===uid||(teamUids.has(splitId)&&stillUnder(splitId))));
      }
      return aid===uid||splitId===uid;
    };
    // For manager cards, revenue is the per-member share sum (matches the
    // header): a within-team split contributes both halves because each
    // member's iteration adds their 50%. Distinct DEALS counts (qDeals.length)
    // keep the team-wide matchesAgent set so a single split deal between two
    // team members is still counted as 1 deal.
    var perMemberShare = isManagerCard ? function(filterFn){
      var memberIds=[uid].concat(Array.from(teamUids||[]));
      return memberIds.reduce(function(total,mid){
        return total+allDeals.reduce(function(s,d){
          // THE DEAL'S OWN PAIR (owner ruling 2026-09-12, R11) — dealAgentPairOf.
          var _dapR1=dealAgentPairOf(d);
          var aid=_dapR1.primary;
          var splitId=_dapR1.second;
          if(aid!==mid&&splitId!==mid) return s;
          if(!filterFn(d)) return s;
          var w=getProjectWeight(d.project,d);
          var sp=getDealSplitFromObj(d);
          return s+parseBudget(d.budget)*w*(sp?0.5:1);
        },0);
      },0);
    } : null;
    var qTarget=getEffectiveQTarget(a,p.users,viewQ);
    var qDeals=allDeals.filter(function(d){if(!matchesAgent(d))return false;var dd=getDealDate(d);return dd&&getQ(dd)===viewQ&&new Date(dd).getFullYear()===viewYear;});
    var qRevenue=isManagerCard
      ? perMemberShare(function(d){var dd=getDealDate(d);return dd&&getQ(dd)===viewQ&&new Date(dd).getFullYear()===viewYear;})
      : qDeals.reduce(function(s,d){var w=getProjectWeight(d.project,d);var sp=getDealSplitFromObj(d);return s+parseBudget(d.budget)*w*(sp?0.5:1);},0);
    var qProg=qTarget>0?Math.min(100,Math.round((qRevenue/qTarget)*100)):0;
    // Q-filtered Leads / Calls for the stat cells. Mirrors the agent's own
    // primary-only filter (matches the legacy `al` / `calls` definition):
    // the manager-card variant intentionally counts the manager's own
    // leads/calls, not the team's, preserving prior behavior.
    var qLeads=p.leads.filter(function(l){var aid=l.agentId&&l.agentId._id?l.agentId._id:l.agentId;if(String(aid)!==uid||l.archived||!l.createdAt)return false;return getQ(l.createdAt)===viewQ&&new Date(l.createdAt).getFullYear()===viewYear;});
    var qCalls=p.activities.filter(function(ac){var auid=ac.userId&&ac.userId._id?ac.userId._id:ac.userId;if(String(auid)!==uid||ac.type!=="call"||!ac.createdAt)return false;return getQ(ac.createdAt)===viewQ&&new Date(ac.createdAt).getFullYear()===viewYear;}).length;
    // STEP 4-5 X2.5 — once /api/team/member-stats resolves, prefer the
    // server-aggregated numbers over the in-memory scans (which become
    // wrong post-X3 bootstrap shrink). For consistency every per-card
    // metric switches together: qTarget, qDeals length, qLeads length,
    // qCalls, qRevenue, qProg.
    var serverMember = memberStatsMap ? memberStatsMap[uid] : null;
    if (serverMember) {
      qTarget = serverMember.qTarget || 0;
      qRevenue = typeof serverMember.qRev === "number" ? serverMember.qRev : qRevenue;
      qProg = qTarget > 0 ? Math.min(100, Math.round((qRevenue / qTarget) * 100)) : 0;
      // Shadow .length on the qDeals/qLeads arrays so the existing render
      // sites (stats[].v at L12276-12279 read .length) get the server number
      // without rewriting their access pattern. qDeals.length / qLeads.length
      // are used at the render site (see stats[] below); reassigning to a
      // plain array of the right length keeps that code unchanged.
      qDeals = new Array(serverMember.qDeals || 0);
      qLeads = new Array(serverMember.qLeads || 0);
      qCalls = serverMember.qCalls || 0;
    }
    var isOnlineNow=a.lastSeen&&(Date.now()-new Date(a.lastSeen).getTime())<2*60*1000;
    var lastSeenStr=a.lastSeen?("Last seen: "+fmtTime12(a.lastSeen, p.t)+" — "+timeAgo(a.lastSeen,p.t)):"Never logged in";
    var initials = (a.name||"?").split(" ").slice(0,2).map(function(x){return x[0];}).join("").toUpperCase();
    var grad = gradientForUid(uid);
    var roleLabel = a.title || ({admin:"Admin",operation:"Operations",manager:"Manager",team_leader:"Team Leader",sales:"Sales",viewer:"Viewer",office_boy:"Office Boy"}[a.role]||"");
    // Loading placeholder. While /api/team/member-stats is in flight AND the
    // in-memory fallback also resolves to 0 (p.leads / p.activities not loaded
    // yet, or no matching rows in the bootstrap subset), show "…" instead of
    // a literal 0. Mirrors the KPIsPage fix — member-stats does N×(3-5) queries
    // serially and can take a few seconds on a full sales floor.
    var pending = memberStatsMap === null;
    var phLeads = (pending && qLeads.length === 0) ? "…" : qLeads.length;
    var phDeals = (pending && qDeals.length === 0) ? "…" : qDeals.length;
    var phCalls = (pending && qCalls === 0) ? "…" : qCalls;
    var phRev1  = (pending && qRevenue === 0) ? "…" : (qRevenue/1000000).toFixed(1) + "M";
    var phRev2  = (pending && qRevenue === 0) ? "…" : (qRevenue/1000000).toFixed(2) + "M";
    var stats = [
      { v: phLeads, l: "Leads", isDeals:false },
      { v: phDeals, l: "Deals", isDeals:true },
      { v: phRev1,  l: "Total", isDeals:false },
      { v: phCalls, l: "Calls", isDeals:false }
    ];
    return <div key={uid} style={{ borderRadius:16, overflow:"hidden", background:"#fff", boxShadow:"0 2px 10px rgba(0,0,0,0.08)" }}>
      {/* Top — gradient hero */}
      <div className={grad} style={{ padding:"18px 14px 16px", position:"relative", display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
        {isOnlineNow && <span title="Online" style={{ position:"absolute", top:10, right:12, width:9, height:9, borderRadius:"50%", background:"#22c55e", boxShadow:"0 0 0 2px rgba(255,255,255,0.45)" }}/>}
        <div style={{ width:44, height:44, borderRadius:12, background:"rgba(255,255,255,0.22)", color:"#fff", border:"2px solid rgba(255,255,255,0.35)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:800 }}>{initials}</div>
        <div style={{ fontSize:13, fontWeight:700, color:"#fff", textAlign:"center", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.name}</div>
        <div style={{ fontSize:10, color:"rgba(255,255,255,0.85)", textTransform:"uppercase", letterSpacing:"0.04em", textAlign:"center" }}>{roleLabel}</div>
        <div style={{ fontSize:9, color:"rgba(255,255,255,0.55)", textAlign:"center", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{isOnlineNow?"Online now":lastSeenStr}</div>
      </div>
      {/* Bottom — white panel */}
      <div style={{ background:"#fff", padding:"14px 14px 16px" }}>
        {isAdmin && <button onClick={function(){
          var qt=getQTargets(uid);
          var yr=new Date().getFullYear();
          setEditQModal({
            user:a,
            year:yr,
            allTargets:Object.assign({},qt),
            targets:{
              Q1: readQTargetClient(qt, yr, 1),
              Q2: readQTargetClient(qt, yr, 2),
              Q3: readQTargetClient(qt, yr, 3),
              Q4: readQTargetClient(qt, yr, 4)
            }
          });
        }}
          style={{ width:"100%", padding:"8px 0", borderRadius:8, border:"none", background:"#f1f5f9", color:"#1e3a5f", fontSize:10, fontWeight:700, cursor:"pointer", marginBottom:8 }}>🎯 Edit Targets</button>}
        {/* Hide-all — admin / operation only, sales agent cards only.
            Backend computes the actual count after applying the EOI/DoneDeal
            exclusion, so we always allow the click and report the result. */}
        {canUnassignAll && !isManagerCard && <button onClick={function(){setUnassignModal({user:a});}}
          style={{ width:"100%", padding:"8px 0", borderRadius:8, border:"1px solid #FECACA", background:"#FEF2F2", color:"#B91C1C", fontSize:10, fontWeight:700, cursor:"pointer", marginBottom:12 }}>↩ Hide all leads</button>}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
          <span style={{ fontSize:10, fontWeight:600, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.04em" }}>{viewQ} Target</span>
          <span style={{ fontSize:10, fontWeight:700, color:"#334155" }}>{qTarget>0?qTarget.toLocaleString()+" EGP":"Not set"}</span>
        </div>
        <div style={{ height:4, background:"#e2e8f0", borderRadius:2, marginBottom:10, overflow:"hidden" }}>
          <div className={grad} style={{ height:"100%", width:qProg+"%", borderRadius:2, transition:"width 0.6s" }}/>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:12 }}>
          <span style={{ fontSize:18, fontWeight:800, color:qRevenue>0?"#0f172a":"#94a3b8" }}>{phRev2}</span>
          <span style={{ fontSize:12, fontWeight:700, color:"#64748b" }}>{qProg}%</span>
        </div>
        <div style={{ height:1, background:"#e2e8f0", marginBottom:10, transform:"scaleY(0.5)" }}/>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:6 }}>
          {stats.map(function(s,i){
            // Zero handling: dim numbers when they're 0; deals get green when > 0.
            var isZero = (s.v === 0) || (s.v === "0.0M") || (s.v === "0M") || (s.v === "0");
            var color;
            if (s.isDeals) color = (s.v > 0 ? "#15803d" : "#cbd5e1");
            else color = isZero ? "#cbd5e1" : "#0f172a";
            return <div key={i} style={{ textAlign:"center" }}>
              <div style={{ fontSize:16, fontWeight:800, color:color, lineHeight:1.1 }}>{s.v}</div>
              <div style={{ fontSize:8, fontWeight:600, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.06em", marginTop:4 }}>{s.l}</div>
            </div>;
          })}
        </div>
      </div>
    </div>;
  };

  // Teams Comparison table — per-leader row + a de-duplicated company TOTAL.
  // Colors the % with the agent-leaderboard thresholds (≥70 green / ≥30 amber / red).
  var pctColor = function(pct){ return pct>=70 ? "#16A34A" : pct>=30 ? "#F59E0B" : "#DC2626"; };
  var fmtM = function(n){ return ((Number(n)||0)/1000000).toFixed(2)+"M"; };
  var fmtEGP = function(n){ return (Number(n)||0).toLocaleString()+" EGP"; };
  // Dedup note under the TOTAL — spells out WHY Σ rows ≠ TOTAL instead of leaving
  // the reader to rediscover it. Built entirely from the server's sharedMembers /
  // dedupedRev (member-stats), so it never hardcodes a name and follows the org
  // chart. Empty string when no member sits in two rows, or (amount only) when a
  // frozen period makes the exact figure unavailable — the names still render.
  var totalDedupNote = (function(){
    var sm = (memberStatsTotal && memberStatsTotal.sharedMembers) || [];
    if (!sm.length) return "";
    var amt = (memberStatsTotal && typeof memberStatsTotal.dedupedRev === "number" && memberStatsTotal.dedupedRev > 0)
      ? memberStatsTotal.dedupedRev : null;
    var timesWord = sm.every(function(s){ return (s.rows||[]).length === 2; }) ? "twice" : "more than once";
    var who;
    if (sm.length === 1) {
      var s0 = sm[0];
      // Own row first — "their own team and Yosra's" reads better than the reverse,
      // and the server's row order is just the member-query order.
      var ordered = (s0.rows||[]).slice().sort(function(a,b){ return (a===s0.name?0:1)-(b===s0.name?0:1); });
      var where = ordered.map(function(r){ return r === s0.name ? "their own team" : r + "'s"; });
      who = s0.name + " is counted in " + where.length + " rows — "
        + where.slice(0,-1).join(", ") + " and " + where[where.length-1];
    } else {
      who = sm.length + " members appear in more than one row ("
        + sm.map(function(s){ return s.name; }).join(", ") + ")";
    }
    return who + (amt ? ", so the TOTAL removes the " + fmtM(amt) + " counted " + timesWord : "") + ".";
  })();

  return <div className="team-page-v2" style={{ padding:"20px", background:"#f1f5f9", minHeight:"100%", fontFamily:"'Inter','Segoe UI',sans-serif" }}>
    {/* Inter font + the 8 gradient classes used by MemberCard. Scoped under
        .team-page-v2 so they don't leak to other pages. */}
    <style>{""
      + "@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');"
      + ".team-page-v2 .tp-grad-1 { background: linear-gradient(135deg, #43c6db, #3b5cb8); }"
      + ".team-page-v2 .tp-grad-2 { background: linear-gradient(135deg, #f953c6, #b91d73); }"
      + ".team-page-v2 .tp-grad-3 { background: linear-gradient(135deg, #56ab2f, #a8e063); }"
      + ".team-page-v2 .tp-grad-4 { background: linear-gradient(135deg, #f7797d, #c6426e); }"
      + ".team-page-v2 .tp-grad-5 { background: linear-gradient(135deg, #e52d27, #b31217); }"
      + ".team-page-v2 .tp-grad-6 { background: linear-gradient(135deg, #f46b45, #eea849); }"
      + ".team-page-v2 .tp-grad-7 { background: linear-gradient(135deg, #b8d435, #56ab2f); }"
      + ".team-page-v2 .tp-grad-8 { background: linear-gradient(135deg, #a18cd1, #e8a4c8); }"
    }</style>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18, flexWrap:"wrap", gap:10 }}>
      <h2 style={{ margin:0, fontSize:18, fontWeight:700, color:"#0f172a" }}>{t.team}</h2>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
        {["Q1","Q2","Q3","Q4"].map(function(q){var isCurrentQ=q===curQ&&viewYear===curYear;return <button key={q} onClick={function(){setViewQ(q);}}
          style={{ padding:"6px 14px", borderRadius:8, border:"1px solid", borderColor:viewQ===q?C.accent:"#E2E8F0",
            background:viewQ===q?C.accent+"12":"#fff", color:viewQ===q?C.accent:C.textLight, position:"relative",
            fontSize:12, fontWeight:600, cursor:"pointer" }}>{q}
          {isCurrentQ&&<span title="Current quarter" style={{ position:"absolute", top:3, right:3, width:6, height:6, borderRadius:"50%", background:"#22C55E" }}/>}</button>;})}
        <Select value={String(viewYear)} onChange={function(e){setViewYear(Number(e.target.value));}} width={90} maxWidth={90}
          options={years.map(function(y){return { value:String(y), label:String(y) };})}/>
        {p.cu.role==="admin" && <Btn outline onClick={function(){setShowPeriods(true);}} style={{ padding:"6px 12px", fontSize:12 }}>🎯 Manage Periods</Btn>}
      </div>
    </div>

    {/* Manager groups */}
    {visibleManagers.map(function(mgr){
      var muid=gid(mgr); var team=getSalesUnder(muid); var isExpanded=expandedManager===muid;
      // Header total = sum across each direct member's share-based total
      // (manager + their direct reports). Both halves of a within-team split
      // count — each member's iteration adds their own 50%, so the deal
      // contributes its full value when both sides are inside the team. A
      // split with an outsider only contributes the inside member's 50%.
      // Equivalent to summing every member-card's TOTAL value.
      var memberIds=[muid].concat(team.map(function(u){return gid(u);}));
      var mRev=memberIds.reduce(function(total,mid){
        return total+allDeals.reduce(function(s,d){
          // THE DEAL'S OWN PAIR (owner ruling 2026-09-12, R11) — dealAgentPairOf.
          var _dapR2=dealAgentPairOf(d);
          var aid=_dapR2.primary;
          var splitId=_dapR2.second;
          if(aid!==mid&&splitId!==mid) return s;
          var dd=getDealDate(d);
          if(!dd||getQ(dd)!==viewQ||new Date(dd).getFullYear()!==viewYear) return s;
          var w=getProjectWeight(d.project,d);
          var sp=getDealSplitFromObj(d);
          return s+parseBudget(d.budget)*w*(sp?0.5:1);
        },0);
      },0);
      var mTarget=getEffectiveQTarget(mgr,p.users,viewQ);
      var mProg=mTarget>0?Math.min(100,Math.round(mRev/mTarget*100)):0;
      // Server override — mirrors the MemberCard pattern at line 12540. The
      // manager-bar header was left reading from `allDeals` (= p.leads slice)
      // which post-bootstrap-shrink no longer contains the team's deals, so
      // mRev would render 0.0M while the cards below show the correct number.
      // memberStatsMap[muid] already exists because /api/team/member-stats
      // returns one row per visible user including managers.
      var serverMgr = memberStatsMap ? memberStatsMap[muid] : null;
      if (serverMgr) {
        mRev    = typeof serverMgr.qRev === "number" ? serverMgr.qRev : mRev;
        mTarget = serverMgr.qTarget || mTarget;
        mProg   = mTarget > 0 ? Math.min(100, Math.round(mRev / mTarget * 100)) : 0;
      }
      var isOnline=mgr.lastSeen&&(Date.now()-new Date(mgr.lastSeen).getTime())<3*60*1000;
      var mgrInitials=(mgr.name||"?").split(" ").slice(0,2).map(function(x){return x[0];}).join("").toUpperCase();
      // Loading placeholder — same rule as the MemberCard. While the fetch
      // is in flight AND mRev is still 0 from the shrunken fallback, show
      // "…" instead of "0.0M". Target half always renders from p.users.qTargets
      // (never shrunken) so its placeholder isn't needed.
      var mgrBarPending = memberStatsMap === null;
      var mgrRevPart = (mgrBarPending && mRev === 0) ? "…" : (mRev/1000000).toFixed(1)+"M";
      var mgrTotalLabel = mgrRevPart + " / " + (mTarget>0?(mTarget/1000000).toFixed(1)+"M":"—");
      return <div key={muid} style={{ marginBottom:16 }}>
        {/* Manager group header — navy, redesigned */}
        <div onClick={function(){setExpandedManager(isExpanded?null:muid);}}
          style={Object.assign({ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, padding:"16px 24px", background:"#1e3a5f", borderRadius:12, cursor:"pointer", marginBottom:isExpanded?12:0 }, p.isMobile ? { flexWrap:"wrap" } : null)}>
          {/* Phone: the total/progress block never shrinks, so beside it the
              name + "title — N members" column was squeezed to ~60px and the
              count was cut off. On a phone the name column takes the first row
              and the total/progress block wraps under it. */}
          <div style={Object.assign({ display:"flex", alignItems:"center", gap:14, minWidth:0, flex:1 }, p.isMobile ? { flexBasis:"100%" } : null)}>
            <div style={{ position:"relative", flexShrink:0 }}>
              <div style={{ width:44, height:44, borderRadius:11, background:"#162d4a", color:"#93c5fd", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:700 }}>{mgrInitials}</div>
              {isOnline && <span title="Online" style={{ position:"absolute", bottom:-1, right:-1, width:10, height:10, borderRadius:"50%", background:"#22c55e", border:"2px solid #1e3a5f" }}/>}
            </div>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:700, color:"#e0f0ff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{mgr.name}</div>
              {/* The count is its own unshrinkable span: on a phone the progress
                  block beside it squeezes this line, and the ellipsis used to cut
                  the count off. Only the title truncates now; the text is unchanged. */}
              <div style={{ display:"flex", fontSize:11, color:"#4a7aa0", whiteSpace:"nowrap", minWidth:0 }}>
                <span style={{ overflow:"hidden", textOverflow:"ellipsis", minWidth:0 }}>{mgr.title||""}</span>
                <span style={{ flexShrink:0 }}>&nbsp;— {team.length} member{team.length===1?"":"s"}</span>
              </div>
            </div>
          </div>
          <div style={Object.assign({ display:"flex", alignItems:"center", gap:14, flexShrink:0 }, p.isMobile ? { marginInlineStart:"auto" } : null)}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
              <div style={{ fontSize:14, fontWeight:700, color:"#93c5fd" }}>{mgrTotalLabel}</div>
              <div style={{ height:5, background:"#162d4a", width:200, maxWidth:"40vw", borderRadius:3, overflow:"hidden" }}>
                <div style={{ height:"100%", width:mProg+"%", background:"#60a5fa", borderRadius:3 }}/>
              </div>
              <div style={{ fontSize:11, color:"#4a7aa0" }}>{mProg}%</div>
            </div>
            <span style={{ color:"#93c5fd", fontSize:14 }}>{isExpanded?"▲":"▼"}</span>
          </div>
        </div>
        {/* Team members expanded — responsive grid */}
        {isExpanded&&<div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:16 }}>
          <MemberCard user={mgr}/>
          {team.map(function(u){return <MemberCard key={gid(u)} user={u}/>;})}</div>}
      </div>;
    })}

    {/* Top-level sales (no manager) */}
    {topLevelSales.length>0&&<div style={{ marginBottom:16 }}>
      <div style={{ fontSize:12, fontWeight:700, color:"#64748b", marginBottom:10, textTransform:"uppercase", letterSpacing:"0.04em" }}>Agents without a manager</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:16 }}>
        {topLevelSales.map(function(a){return <MemberCard key={gid(a)} user={a}/>;})}
      </div>
    </div>}

    {/* Old fallback if no managers defined */}
    {visibleManagers.length===0&&<div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:16 }}>
      {p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="manager"||positionOf(u)==="team_leader")&&u.active;}).map(function(a){
        return <MemberCard key={gid(a)} user={a}/>;
      })}
    </div>}

    {/* Teams Comparison — one row per leader + a de-duplicated company TOTAL.
        Same server data source as the cards above (member-stats / period-aware):
        frozen for closed periods, live for the current one, honoring selected Q/year.
        (Replaced the old Live Activity Feed, which duplicated the Dashboard's.) */}
    {isAdmin && visibleManagers.length>0 && <div style={{ marginTop:20 }}>
      <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:12, flexWrap:"wrap" }}>
        <h3 style={{ fontSize:14, fontWeight:700, margin:0 }}>📊 Teams Comparison</h3>
        <span style={{ fontSize:11, color:C.textLight }}>{viewQ} {viewYear} · same numbers as the cards above</span>
      </div>
      <div style={{ background:"#fff", borderRadius:12, border:"1px solid #E8ECF1", overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
        <table style={{ width:"100%", minWidth:560, borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"#F8FAFC", color:"#64748b", textAlign:"left" }}>
              <th style={{ padding:"10px 14px", fontWeight:700 }}>Team</th>
              <th style={{ padding:"10px 12px", fontWeight:700, textAlign:"right" }}>Target</th>
              <th style={{ padding:"10px 12px", fontWeight:700, textAlign:"right" }}>Achieved</th>
              <th style={{ padding:"10px 12px", fontWeight:700, textAlign:"right" }}>%</th>
              <th style={{ padding:"10px 12px", fontWeight:700, textAlign:"right" }}>Deals</th>
              <th style={{ padding:"10px 14px", fontWeight:700, textAlign:"right" }}>EOIs</th>
            </tr>
          </thead>
          <tbody>
            {memberStatsMap===null && <tr><td colSpan={6} style={{ padding:20, textAlign:"center", color:C.textLight }}>Loading…</td></tr>}
            {memberStatsMap!==null && visibleManagers.map(function(mgr){
              var muid=String(gid(mgr));
              var s = memberStatsMap[muid] || null;
              var tgt = s ? (s.qTarget||0) : 0;
              var ach = s ? (s.qRev||0) : 0;
              var prog = s ? (s.qProg||0) : 0;
              var deals = s ? (s.qDeals||0) : 0;
              var eois = s ? (s.qEois||0) : 0;
              var frozen = !!(s && s.fromPeriod);
              var subtree = !!(s && s.targetScope==="subtree");
              return <tr key={muid} style={{ borderTop:"1px solid #F1F5F9" }}>
                <td style={{ padding:"10px 14px", whiteSpace:"nowrap" }}>
                  <span style={{ fontWeight:600, color:"#0f172a" }}>{mgr.name}</span>
                  {subtree && <span title="Subtree mode — rolls up the full reporting tree beneath this leader" style={{ marginLeft:6, fontSize:9, fontWeight:700, color:"#3b5cb8", background:"#eef2ff", padding:"1px 6px", borderRadius:10 }}>SUBTREE</span>}
                  {frozen && <span title="Frozen — this quarter's Target Period is closed" style={{ marginLeft:6, fontSize:9, fontWeight:700, color:"#166534", background:"#dcfce7", padding:"1px 6px", borderRadius:10 }}>FROZEN</span>}
                </td>
                <td style={{ padding:"10px 12px", textAlign:"right", color:"#334155" }} title={fmtEGP(tgt)}>{tgt>0?fmtM(tgt):"—"}</td>
                <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:600, color:"#0f172a" }} title={fmtEGP(ach)}>{fmtM(ach)}</td>
                <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:700, color:pctColor(prog) }}>{prog}%</td>
                <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:600, color:deals>0?"#15803d":"#94a3b8" }}>{deals}</td>
                <td style={{ padding:"10px 14px", textAlign:"right", color:eois>0?"#334155":"#94a3b8" }}>{eois}</td>
              </tr>;
            })}
          </tbody>
          {/* Company TOTAL — admin / operation ONLY. A leader's scope holds exactly
              one rendered row (their own team), so a "company" footer under it can only
              restate that row against a different population: it sums their own sub-tree
              while the label says company, and drops every sibling team. Measured on prod
              2026-08-13 (Q3): a TL's row read 470M target while this footer read 790M, and
              a manager's read 370M against 1,335M. Their single row IS their total. The
              backend returns total:null for those roles too — this gate is the second half
              of the same fix, not a substitute for it. */}
          {isOnlyAdmin && memberStatsTotal && <tfoot>
            <tr style={{ borderTop:"2px solid #E2E8F0", background:"#F8FAFC", fontWeight:800 }}>
              <td style={{ padding:"11px 14px", color:"#0f172a", whiteSpace:"nowrap" }}>TOTAL <span style={{ fontSize:10, fontWeight:600, color:C.textLight }}>(company · each member once)</span></td>
              <td style={{ padding:"11px 12px", textAlign:"right", color:"#0f172a" }} title={fmtEGP(memberStatsTotal.target)}>{fmtM(memberStatsTotal.target)}</td>
              <td style={{ padding:"11px 12px", textAlign:"right", color:"#0f172a" }} title={fmtEGP(memberStatsTotal.achieved)}>{fmtM(memberStatsTotal.achieved)}</td>
              <td style={{ padding:"11px 12px", textAlign:"right", color:pctColor(memberStatsTotal.prog||0) }}>{memberStatsTotal.prog||0}%</td>
              <td style={{ padding:"11px 12px", textAlign:"right", color:"#0f172a" }}>{memberStatsTotal.deals||0}</td>
              <td style={{ padding:"11px 14px", textAlign:"right", color:"#0f172a" }}>{memberStatsTotal.eois||0}</td>
            </tr>
          </tfoot>}
        </table>
      </div>
      <div style={{ fontSize:11, color:C.textLight, marginTop:8, lineHeight:1.5 }}>
        Rows mirror the cards above.
        {isOnlyAdmin && <span> The <b>TOTAL</b> counts each member once, so a team leader who also appears inside their manager's row — or a whole sub-team rolled up by a manager in <b>subtree</b> mode — is never double-counted.</span>}
        {isOnlyAdmin && totalDedupNote && <span style={{ display:"block", marginTop:5, color:"#475569" }}>↳ {totalDedupNote}</span>}
      </div>
    </div>}

    {showPeriods && <TargetPeriodsManager token={p.token} csrfToken={p.csrfToken} users={p.users} setUsers={p.setUsers} year={viewYear} quarter={parseInt(String(viewQ).replace("Q",""))||1} t={t} onClose={function(){setShowPeriods(false);}} onChanged={function(){setPeriodBust(function(x){return x+1;});}} />}
    {editQModal&&<Modal show={true} onClose={function(){setEditQModal(null);}} title={"🎯 Quarterly Targets — "+editQModal.user.name}>
      <div style={{ fontSize:12, color:C.textLight, marginBottom:14, padding:"8px 12px", background:"#F8FAFC", borderRadius:8 }}>Quarterly target in EGP</div>
      <div style={{ marginBottom:14 }}>
        <label style={{ display:"block", fontSize:12, fontWeight:600, color:C.textLight, marginBottom:4 }}>Year</label>
        <Select value={String(editQModal.year)}
          onChange={function(e){
            var newYear = Number(e.target.value);
            setEditQModal(function(prev){
              return Object.assign({}, prev, {
                year: newYear,
                targets: {
                  Q1: readQTargetClient(prev.allTargets, newYear, 1),
                  Q2: readQTargetClient(prev.allTargets, newYear, 2),
                  Q3: readQTargetClient(prev.allTargets, newYear, 3),
                  Q4: readQTargetClient(prev.allTargets, newYear, 4)
                }
              });
            });
          }}
          width={140} maxWidth={140} fontSize={13}
          options={(function(){
            var nowYear = new Date().getFullYear();
            return [nowYear - 1, nowYear, nowYear + 1].map(function(y){
              return { value:String(y), label:String(y) + (y === nowYear ? " (current)" : "") };
            });
          })()}/>
      </div>
      {["Q1","Q2","Q3","Q4"].map(function(q,i){
        var labels=["Jan — Mar","Apr — Jun","Jul — Sep","Oct — Dec"];
        // Same two signals as the Users-page modal: below-minimum warns, an
        // already-ended quarter is marked frozen (server refuses it by default).
        var _v=Number(editQModal.targets[q])||0;
        var _low=_v>0&&_v<MIN_QUARTERLY_TARGET;
        var _now=new Date(), _nY=_now.getFullYear(), _nQ=Math.floor(_now.getMonth()/3)+1, _qn=i+1;
        var _past=(editQModal.year<_nY)||(editQModal.year===_nY&&_qn<_nQ);
        return <div key={q} style={{ marginBottom:11 }}>
          <label style={{ display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:4 }}>{q} {editQModal.year} <span style={{ fontSize:11, color:C.textLight }}>({labels[i]})</span>{_past&&<span style={{ fontSize:10, color:C.textLight, fontWeight:600, background:"#F1F5F9", borderRadius:6, padding:"1px 6px", marginInlineStart:6 }}>ended — frozen</span>}</label>
          <input type="text" placeholder="e.g. 40,000,000"
            value={editQModal.targets[q]?Number(editQModal.targets[q]).toLocaleString():""}
            onChange={function(e){var r=e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");setEditQModal(function(prev){return Object.assign({},prev,{targets:Object.assign({},prev.targets,{[q]:r?Number(r):0})});});}}
            style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid "+(_low?C.warning:"#E2E8F0"), fontSize:14, boxSizing:"border-box", direction:"ltr" }}/>
          {_low&&<div style={{ fontSize:11, color:"#B45309", marginTop:3 }}>Below the {MIN_QUARTERLY_TARGET.toLocaleString("en-US")} minimum — you'll be asked to confirm.</div>}
        </div>;
      })}
      <div style={{ display:"flex", gap:10 }}>
        <Btn outline onClick={function(){setEditQModal(null);}} style={{ flex:1 }}>Cancel</Btn>
        <Btn onClick={function(){
          var nowYear = new Date().getFullYear();
          var merged = Object.assign({}, editQModal.allTargets);
          ["Q1","Q2","Q3","Q4"].forEach(function(q){
            var k = editQModal.year + "-" + q;
            var v = Number(editQModal.targets[q]) || 0;
            if (v > 0) merged[k] = v; else delete merged[k];
            if (editQModal.year === nowYear) delete merged[q];
          });
          saveQTargets(gid(editQModal.user), merged).then(function(ok){ if(ok) setEditQModal(null); });
        }} style={{ flex:1 }}>✅ Save</Btn>
      </div>
    </Modal>}

    {unassignModal&&<Modal show={true} onClose={function(){if(!unassigning)setUnassignModal(null);}} title={"↩ Hide all leads"}>
      <div style={{ fontSize:13, color:C.text, lineHeight:1.55, marginBottom:18 }}>
        Hide all leads from <b>{unassignModal.user.name}</b>? This applies the same effect as 7 days of inactivity to all leads currently visible to this agent. Notes, Done Deals, and active EOIs are preserved.
      </div>
      <div style={{ display:"flex", gap:10 }}>
        <Btn outline onClick={function(){setUnassignModal(null);}} disabled={unassigning} style={{ flex:1 }}>Cancel</Btn>
        <Btn danger onClick={doUnassignAll} loading={unassigning} style={{ flex:1 }}>{unassigning?"Hiding…":"Hide all"}</Btn>
      </div>
    </Modal>}
  </div>;
};

// ===== REPORTS HELPERS =====
var fmtEGP = function(n) {
  n = Number(n) || 0;
  var sign = n < 0 ? "-" : "";
  var abs = Math.abs(n);
  if (abs >= 1000000) {
    var m = abs / 1000000;
    return sign + (m >= 10 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, "")) + "M EGP";
  }
  if (abs >= 1000) return sign + Math.round(abs / 1000) + "K EGP";
  return sign + Math.round(abs) + " EGP";
};

var Sparkline = function(p) {
  var values = p.values || [];
  var w = p.width || 80, h = p.height || 26;
  if (values.length < 2) {
    return <svg width={w} height={h} style={{ display:"block" }}>
      <line x1={0} y1={h/2} x2={w} y2={h/2} stroke="#CBD5E1" strokeWidth={1} strokeDasharray="2 2"/>
    </svg>;
  }
  var minV = Math.min.apply(null, values);
  var maxV = Math.max.apply(null, values);
  var range = maxV - minV; if (range === 0) range = 1;
  var stepX = (values.length - 1) > 0 ? w / (values.length - 1) : 0;
  var pts = values.map(function(v, i){
    var x = i * stepX;
    var y = h - 2 - ((v - minV) / range) * (h - 4);
    return x.toFixed(1) + "," + y.toFixed(1);
  }).join(" ");
  return <svg width={w} height={h} style={{ display:"block" }}>
    <polyline fill="none" stroke={p.color || "#1F2937"} strokeWidth={1.5} points={pts} strokeLinecap="round" strokeLinejoin="round"/>
  </svg>;
};

var KpiCard = function(p) {
  var c = p.card;
  var d = c.delta;
  var positive = d != null && d > 0;
  var negative = d != null && d < 0;
  var deltaColor = d == null ? C.textLight : (positive ? "#16A34A" : negative ? "#DC2626" : C.textLight);
  var arrow = positive ? "▲" : negative ? "▼" : "—";
  return <Card style={{ padding:"14px 16px", minHeight:118 }}>
    <div style={{ fontSize:11, color:C.textLight, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em" }}>{c.label}</div>
    {p.skeleton
      ? <div style={{ height:24, marginTop:10, borderRadius:4, background:"#F1F5F9", width:"60%" }}/>
      : <div style={{ fontSize:20, fontWeight:800, color:c.color, marginTop:6 }} title={c.tooltip||""}>{c.value}</div>}
    {!p.skeleton && c.hint && <div style={{ fontSize:9, color:C.textLight, marginTop:2 }} title={c.tooltip||""}>{c.hint}</div>}
    <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:10 }}>
      {!c.snapshot && !p.skeleton && <span style={{ fontSize:11, fontWeight:700, color:deltaColor }}>
        {d == null ? "—" : arrow + " " + Math.abs(d).toFixed(1) + (c.deltaUnit || "%")}
      </span>}
      {c.snapshot && !p.skeleton && <span style={{ fontSize:10, color:C.textLight, fontWeight:600, letterSpacing:"0.04em" }}>SNAPSHOT</span>}
      {p.compare && c.prev != null && !p.skeleton && <span style={{ fontSize:10, color:C.textLight }}>
        prev {c.prevFmt(c.prev)}
      </span>}
      <div style={{ flex:1 }}/>
      <Sparkline values={p.skeleton ? [] : (c.spark || [])} color={c.color} width={70} height={24}/>
    </div>
  </Card>;
};

var KpiCardsRow = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var f = p.filters;
  useEffect(function(){
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/overview/kpis" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.from, f.to, f.team, f.source]);

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load KPIs: {state.error}</div>
    </Card>;
  }

  var skel = state.loading || !state.data;
  var k = state.data && state.data.kpis;
  var fmtPct = function(v){ return (Number(v) || 0).toFixed(1) + "%"; };

  var cards = [
    { id:"revenue",  label:"Revenue",        value: skel ? "" : fmtEGP(k.revenue.value),       prev: skel ? null : k.revenue.prev,       prevFmt: fmtEGP, delta: skel ? null : k.revenue.deltaPct,    deltaUnit:"%",  spark: skel ? [] : k.revenue.sparkline,       color: C.success },
    { id:"pipeline", label:"Pipeline value", value: skel ? "" : fmtEGP(k.pipelineValue.value), prev: null,                                prevFmt: fmtEGP, delta: null,                                 deltaUnit:null, spark: skel ? [] : k.pipelineValue.sparkline, color: C.info, snapshot: true },
    { id:"avg",      label:"Avg deal size",  value: skel ? "" : fmtEGP(k.avgDealSize.value),   prev: skel ? null : k.avgDealSize.prev,    prevFmt: fmtEGP, delta: skel ? null : k.avgDealSize.deltaPct, deltaUnit:"%",  spark: skel ? [] : k.avgDealSize.sparkline,   color: C.accent },
    { id:"conv",     label:"Lead → deal %",  value: skel ? "" : fmtPct(k.convRatePct.value),   prev: skel ? null : k.convRatePct.prev,    prevFmt: fmtPct, delta: skel ? null : k.convRatePct.deltaPp,  deltaUnit:"pp", spark: skel ? [] : k.convRatePct.sparkline,   color: "#8B5CF6" }
  ];

  return <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(210px, 1fr))", gap:10, marginBottom:14 }}>
    {cards.map(function(c){ return <KpiCard key={c.id} card={c} compare={f.compare} skeleton={skel}/>; })}
  </div>;
};

var TrendsChart = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [hoverIdx, setHoverIdx] = useState(null);
  var f = p.filters;

  useEffect(function() {
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/overview/trends" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.from, f.to, f.team, f.source]);

  // SVG geometry — viewBox-based so the chart scales fluidly with container width.
  var W = 800, H = 220;
  var padL = 44, padR = 16, padT = 12, padB = 28;
  var plotW = W - padL - padR;
  var plotH = H - padT - padB;

  // Calls aren't source-filtered server-side (Activity has no source attribution).
  // Surface this in the legend whenever a specific source filter is active so users
  // don't expect the calls line to drop alongside leads/deals.
  var sourceFiltered = f.source && f.source !== "all";
  var headerRow = <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, flexWrap:"wrap", gap:8 }}>
    <div style={{ fontSize:13, fontWeight:700 }}>📈 Trends</div>
    <div style={{ display:"flex", gap:14, fontSize:11, color:C.textLight }}>
      <span style={{ display:"flex", alignItems:"center", gap:5 }}><span style={{ width:14, height:2, background:"#3B82F6", borderRadius:1 }}/>Leads</span>
      <span style={{ display:"flex", alignItems:"center", gap:5 }}><span style={{ width:14, height:2, background:"#16A34A", borderRadius:1 }}/>Deals</span>
      <span title={sourceFiltered ? "Calls aren't filtered by source — Activity logs don't carry source attribution" : ""} style={{ display:"flex", alignItems:"center", gap:5, cursor: sourceFiltered ? "help" : "default" }}><span style={{ width:14, height:2, background:"#F59E0B", borderRadius:1 }}/>Calls{sourceFiltered ? " (all sources)" : ""}</span>
    </div>
  </div>;

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      {headerRow}
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load: {state.error}</div>
    </Card>;
  }
  if (state.loading || !state.data) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:240 }}>
      {headerRow}
      <div style={{ height:200, background:"#F1F5F9", borderRadius:6 }}/>
    </Card>;
  }

  var series = (state.data && state.data.series) || [];
  var n = series.length;

  if (n === 0) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      {headerRow}
      <div style={{ position:"relative" }}>
        <svg viewBox={"0 0 " + W + " " + H} style={{ width:"100%", height:H, display:"block" }}>
          {[0, 0.5, 1].map(function(t){
            var y = padT + plotH - t * plotH;
            return <line key={t} x1={padL} y1={y} x2={W - padR} y2={y} stroke="#E2E8F0" strokeWidth={0.5}/>;
          })}
        </svg>
        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", color:C.textLight, fontSize:13, pointerEvents:"none" }}>No activity in this period</div>
      </div>
    </Card>;
  }

  var maxLeads = Math.max.apply(null, series.map(function(s){ return s.leads; }));
  var maxDeals = Math.max.apply(null, series.map(function(s){ return s.deals; }));
  var maxCalls = Math.max.apply(null, series.map(function(s){ return s.calls; }));
  var maxAll = Math.max(1, maxLeads, maxDeals, maxCalls);

  var stepX = n > 1 ? plotW / (n - 1) : plotW;

  var pts = function(key) {
    return series.map(function(s, i){
      var x = padL + i * stepX;
      var y = padT + plotH - (s[key] / maxAll) * plotH;
      return x.toFixed(1) + "," + y.toFixed(1);
    }).join(" ");
  };

  var nLabels = Math.min(7, n);
  var labelStride = n > 1 ? Math.max(1, Math.floor((n - 1) / Math.max(1, nLabels - 1))) : 1;
  var fmtAxisDate = function(iso) { return new Date(iso).toLocaleDateString("en-GB", { month: "short", day: "numeric" }); };

  var setIdxFromX = function(clientX, rect) {
    var svgX = ((clientX - rect.left) / rect.width) * W;
    if (svgX < padL || svgX > padL + plotW) { setHoverIdx(null); return; }
    var idx = Math.round((svgX - padL) / stepX);
    idx = Math.max(0, Math.min(n - 1, idx));
    setHoverIdx(idx);
  };
  var onMouseMove = function(e) { setIdxFromX(e.clientX, e.currentTarget.getBoundingClientRect()); };
  var onTouchMove = function(e) {
    if (!e.touches || !e.touches[0]) return;
    setIdxFromX(e.touches[0].clientX, e.currentTarget.getBoundingClientRect());
  };

  return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
    {headerRow}
    <div style={{ position:"relative" }}>
      <svg viewBox={"0 0 " + W + " " + H} style={{ width:"100%", height:H, display:"block" }}
        onMouseMove={onMouseMove} onMouseLeave={function(){ setHoverIdx(null); }}
        onTouchStart={onTouchMove} onTouchMove={onTouchMove}>
        {[0, 0.5, 1].map(function(t){
          var y = padT + plotH - t * plotH;
          return <g key={t}>
            <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#E2E8F0" strokeWidth={0.5}/>
            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize={10} fill="#94A3B8">{Math.round(maxAll * t)}</text>
          </g>;
        })}
        {(function(){
          var labels = [];
          var seen = {};
          for (var i = 0; i < n; i += labelStride) {
            seen[i] = true;
            var x = padL + i * stepX;
            labels.push(<text key={i} x={x} y={H - 8} textAnchor="middle" fontSize={10} fill="#94A3B8">{fmtAxisDate(series[i].date)}</text>);
          }
          if (!seen[n - 1] && n > 0) {
            var lx = padL + (n - 1) * stepX;
            labels.push(<text key={"last-"+(n-1)} x={lx} y={H - 8} textAnchor="middle" fontSize={10} fill="#94A3B8">{fmtAxisDate(series[n - 1].date)}</text>);
          }
          return labels;
        })()}
        {hoverIdx != null && <line x1={padL + hoverIdx * stepX} y1={padT} x2={padL + hoverIdx * stepX} y2={padT + plotH} stroke="#94A3B8" strokeWidth={0.5} strokeDasharray="3 3"/>}
        <polyline fill="none" stroke="#3B82F6" strokeWidth={1.8} points={pts("leads")} strokeLinecap="round" strokeLinejoin="round"/>
        <polyline fill="none" stroke="#16A34A" strokeWidth={1.8} points={pts("deals")} strokeLinecap="round" strokeLinejoin="round"/>
        <polyline fill="none" stroke="#F59E0B" strokeWidth={1.8} points={pts("calls")} strokeLinecap="round" strokeLinejoin="round"/>
        {hoverIdx != null && (function(){
          var s = series[hoverIdx];
          var x = padL + hoverIdx * stepX;
          var y = function(key){ return padT + plotH - (s[key] / maxAll) * plotH; };
          return <g>
            <circle cx={x} cy={y("leads")} r={3.5} fill="#fff" stroke="#3B82F6" strokeWidth={2}/>
            <circle cx={x} cy={y("deals")} r={3.5} fill="#fff" stroke="#16A34A" strokeWidth={2}/>
            <circle cx={x} cy={y("calls")} r={3.5} fill="#fff" stroke="#F59E0B" strokeWidth={2}/>
          </g>;
        })()}
      </svg>
      {hoverIdx != null && (function(){
        var s = series[hoverIdx];
        return <div style={{ position:"absolute", top:8, right:8, background:"#fff", border:"1px solid #E2E8F0", borderRadius:8, padding:"8px 10px", fontSize:11, boxShadow:"0 4px 12px rgba(0,0,0,0.08)", minWidth:140, pointerEvents:"none" }}>
          <div style={{ fontWeight:700, color:C.text, marginBottom:6 }}>{new Date(s.date).toLocaleDateString("en-GB", { weekday:"short", month:"short", day:"numeric" })}</div>
          <div style={{ display:"flex", justifyContent:"space-between", color:C.textLight, gap:12 }}><span><span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", background:"#3B82F6", marginRight:6 }}/>Leads</span><b style={{ color:"#3B82F6" }}>{s.leads}</b></div>
          <div style={{ display:"flex", justifyContent:"space-between", color:C.textLight, gap:12 }}><span><span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", background:"#16A34A", marginRight:6 }}/>Deals</span><b style={{ color:"#16A34A" }}>{s.deals}</b></div>
          <div style={{ display:"flex", justifyContent:"space-between", color:C.textLight, gap:12 }}><span><span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", background:"#F59E0B", marginRight:6 }}/>Calls</span><b style={{ color:"#F59E0B" }}>{s.calls}</b></div>
        </div>;
      })()}
    </div>
  </Card>;
};

var SalesFunnel = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var f = p.filters;

  useEffect(function() {
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/overview/funnel" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.from, f.to, f.team, f.source]);

  var headerRow = <>
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
      <div style={{ fontSize:13, fontWeight:700 }}>🌪 Sales Funnel</div>
      <div style={{ fontSize:10, color:C.textLight, fontStyle:"italic", textAlign:"right" }}>Conversion of leads created in this period (deals from older leads appear in Revenue KPI)</div>
    </div>
    <div style={{ fontSize:10, color:C.textLight, marginTop:4, marginBottom:10, lineHeight:1.4 }}>
      Contacted = any agent action recorded on the lead (status change, feedback, callback, or logged activity). The meaningful conversion funnel begins at Meeting.
    </div>
  </>;

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      {headerRow}
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load: {state.error}</div>
    </Card>;
  }
  if (state.loading || !state.data) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:300 }}>
      {headerRow}
      {[0,1,2,3,4].map(function(i){
        return <div key={i} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
          <div style={{ width:90, height:14, background:"#F1F5F9", borderRadius:4 }}/>
          <div style={{ flex:1, height:24, background:"#F1F5F9", borderRadius:4, width:(100 - i*18) + "%" }}/>
          <div style={{ width:60, height:14, background:"#F1F5F9", borderRadius:4 }}/>
        </div>;
      })}
    </Card>;
  }

  var stages = (state.data && state.data.stages) || [];
  var bottleneckKey = state.data && state.data.bottleneckKey;
  var total = stages[0] ? stages[0].count : 0;

  if (total === 0) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      {headerRow}
      <div style={{ height:160, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", color:C.textLight, fontSize:13, padding:"0 24px", textAlign:"center", gap:6 }}>
        <div style={{ fontWeight:600, color:C.text }}>No new leads created in this period.</div>
        <div style={{ fontSize:12 }}>Pick a different range, or check Revenue KPI for deals from older leads.</div>
      </div>
    </Card>;
  }

  // Purple → green gradient across the 5 stages.
  var stageColors = ["#8B5CF6", "#6366F1", "#3B82F6", "#10B981", "#16A34A"];

  var fmtDays = function(d){
    if (d == null) return null;
    if (d < 0.1) return "<0.1d";
    return d.toFixed(1) + "d";
  };

  // EOI/Deal counts are scoped to leads created in the period — they won't
  // equal the EOI/Deals page totals (those pages have no date filter).
  // Surface this caveat as a hover hint on the count for those two stages.
  var stageHint = {
    eoi: "Counts EOIs from leads created in this period only. EOI page shows all active EOIs regardless of creation date.",
    deal: "Counts deals from leads created in this period only. Deals page shows all active deals regardless of creation date."
  };

  return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
    {headerRow}
    <div>
      {stages.map(function(s, i){
        var prop = total > 0 ? s.count / total : 0;
        var isBottle = s.key === bottleneckKey;
        var color = stageColors[i] || C.accent;
        var dayLabel = fmtDays(s.avgDays);
        return <div key={s.key} style={{ marginBottom:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:90, fontSize:12, fontWeight:600, color:C.text }}>{s.label}</div>
            <div style={{ flex:1, position:"relative", height:24 }}>
              <svg viewBox="0 0 100 24" preserveAspectRatio="none" style={{ width:"100%", height:"100%", display:"block" }}>
                <rect x={0} y={4} width={Math.max(0.5, prop * 100)} height={16} fill={color} rx={3}/>
              </svg>
            </div>
            <div style={{ width:80, textAlign:"right", fontSize:13, fontWeight:700, color:C.text }}>
              {s.count.toLocaleString()}
              {stageHint[s.key] && <span title={stageHint[s.key]} style={{ marginLeft:3, color:C.textLight, cursor:"help", fontSize:10, fontWeight:400 }}>ⓘ</span>}
            </div>
          </div>
          {i > 0 && <div style={{ fontSize:11, color:C.textLight, paddingLeft:102, marginTop:3, display:"flex", flexWrap:"wrap", gap:14, alignItems:"center" }}>
            <span><b style={{ color:C.text }}>{s.conversionPct == null ? "—" : s.conversionPct.toFixed(1) + "%"}</b> conversion</span>
            <span style={{ color: isBottle ? "#DC2626" : C.textLight, fontWeight: isBottle ? 700 : 500 }}>
              {s.dropOffPct == null ? "—" : "−" + s.dropOffPct.toFixed(1) + "% drop-off"}{isBottle ? " ⚠" : ""}
            </span>
            {dayLabel && <span style={{ color:C.textLight }}>~{dayLabel} avg</span>}
          </div>}
        </div>;
      })}
    </div>
    {bottleneckKey && (function(){
      var bIdx = -1;
      for (var i = 0; i < stages.length; i++) { if (stages[i].key === bottleneckKey) { bIdx = i; break; } }
      if (bIdx < 1) return null;
      var b = stages[bIdx], prev = stages[bIdx - 1];
      return <div style={{ marginTop:14, padding:"10px 12px", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, fontSize:12, color:"#991B1B" }}>
        <b>⚠ Bottleneck: {prev.label} → {b.label}</b>
        {b.dropOffPct != null && <span> · {b.dropOffPct.toFixed(1)}% drop off here</span>}
        {b.avgDays != null && <span> · avg {b.avgDays.toFixed(1)} days in stage</span>}
      </div>;
    })()}
  </Card>;
};

var SourceRoiList = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var f = p.filters;

  useEffect(function() {
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/overview/source-roi" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.from, f.to, f.team, f.source]);

  var headerRow = <div style={{ fontSize:13, fontWeight:700, marginBottom:10 }}>📊 Source ROI</div>;

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      {headerRow}
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load: {state.error}</div>
    </Card>;
  }
  if (state.loading || !state.data) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:220 }}>
      {headerRow}
      {[0,1,2,3,4].map(function(i){
        return <div key={i} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
          <div style={{ width:120, height:14, background:"#F1F5F9", borderRadius:4 }}/>
          <div style={{ flex:1, height:14, background:"#F1F5F9", borderRadius:4 }}/>
          <div style={{ width:60, height:14, background:"#F1F5F9", borderRadius:4 }}/>
          <div style={{ width:90, height:14, background:"#F1F5F9", borderRadius:4 }}/>
        </div>;
      })}
    </Card>;
  }

  var sources = (state.data && state.data.sources) || [];

  if (sources.length === 0) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      {headerRow}
      <div style={{ height:160, display:"flex", alignItems:"center", justifyContent:"center", color:C.textLight, fontSize:13 }}>No leads from any source in this period</div>
    </Card>;
  }

  // Heat-map color thresholds per spec: ≥10% green, 3-10% amber, <3% red.
  var convColor = function(pct){
    if (pct >= 10) return "#16A34A";
    if (pct >= 3)  return "#F59E0B";
    return "#DC2626";
  };

  return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
    {headerRow}
    {sources.map(function(s, i){
      var color = convColor(s.conversionPct);
      // Bar width = absolute conversion % capped at 100. A 10% conv reads as
      // a 10%-wide bar — same scale across all sources for direct comparison.
      var barWidth = Math.max(0.5, Math.min(100, s.conversionPct));
      return <div key={s.source} style={{ marginBottom: i < sources.length - 1 ? 12 : 0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ width:120, fontSize:12, fontWeight:600, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={s.source}>{s.source}</div>
          <div style={{ flex:1, position:"relative", height:18 }}>
            <svg viewBox="0 0 100 18" preserveAspectRatio="none" style={{ width:"100%", height:"100%", display:"block" }}>
              <rect x={0} y={4} width={barWidth} height={10} fill={color} rx={2}/>
            </svg>
          </div>
          <div style={{ width:54, textAlign:"right", fontSize:12, fontWeight:700, color:color }}>{s.conversionPct.toFixed(1)}%</div>
          <div style={{ width:90, textAlign:"right", fontSize:12, fontWeight:700, color:C.success }}>{fmtEGP(s.revenue)}</div>
        </div>
        <div style={{ fontSize:11, color:C.textLight, paddingLeft:132, marginTop:3 }}>
          {s.leadCount.toLocaleString()} lead{s.leadCount === 1 ? "" : "s"} · {s.dealCount.toLocaleString()} deal{s.dealCount === 1 ? "" : "s"}
        </div>
      </div>;
    })}
  </Card>;
};

var AgentLeaderboard = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  // expand/collapse state for the "View all N agents" toggle. Fetched list
  // contains all eligible agents (backend default = 100 cap); we slice
  // client-side so the toggle is instant with no extra round-trip.
  var [expanded, setExpanded] = useState(false);
  var [hover, setHover] = useState(false);
  var f = p.filters;

  useEffect(function() {
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/overview/agents" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.from, f.to, f.team, f.source]);

  var headerRow = <div style={{ fontSize:13, fontWeight:700, marginBottom:4 }}>🏆 Agent Leaderboard</div>;

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      {headerRow}
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load: {state.error}</div>
    </Card>;
  }
  if (state.loading || !state.data) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:340 }}>
      {headerRow}
      <div style={{ fontSize:11, color:C.textLight, marginBottom:10 }}>Loading…</div>
      {[0,1,2,3,4,5,6,7,8,9].map(function(i){
        return <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
          <div style={{ width:24, height:14, background:"#F1F5F9", borderRadius:4 }}/>
          <div style={{ flex:1, height:14, background:"#F1F5F9", borderRadius:4 }}/>
        </div>;
      })}
    </Card>;
  }

  var agents = (state.data && state.data.agents) || [];
  var totalAgents = (state.data && state.data.totalAgents) || 0;
  var quarter = (state.data && state.data.quarter) || "";

  var subtitle = (function(){
    var bits = [];
    bits.push(totalAgents.toLocaleString() + " agent" + (totalAgents === 1 ? "" : "s") + " active");
    if (quarter) bits.push("Target vs " + quarter);
    if (f.source && f.source !== "all") bits.push("Calls shown for all sources");
    bits.push("Split deals halved per agent for revenue");
    return <div style={{ fontSize:11, color:C.textLight, marginBottom:10 }}>{bits.join(" · ")}</div>;
  })();

  if (agents.length === 0) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      {headerRow}
      {subtitle}
      <div style={{ height:200, display:"flex", alignItems:"center", justifyContent:"center", color:C.textLight, fontSize:13 }}>No agent activity in this period</div>
    </Card>;
  }

  var medal = function(rank){
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return null;
  };
  var targetColor = function(pct){
    if (pct >= 70) return "#16A34A";
    if (pct >= 30) return "#F59E0B";
    return "#DC2626";
  };

  // Column flex spec — proportional widths so the table fills the card on
  // desktop. minWidth:0 on every flex cell so long agent names ellipsize
  // instead of pushing the row wider than the parent.
  var rankFlex = "0 0 32px";
  var H = function(label, flex, align){
    return <div style={{ flex: flex, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:0.4, color:C.textLight, textAlign: align || "left", minWidth: 0 }}>{label}</div>;
  };
  var V = function(content, flex, align, color, weight){
    return <div style={{ flex: flex, fontSize:12, color: color || C.text, textAlign: align || "left", fontWeight: weight || 600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", minWidth: 0 }}>{content}</div>;
  };

  // Slice client-side for instant toggle. canToggle gates the link visibility
  // so it never appears when the response already fits on one screen.
  var displayAgents = expanded ? agents : agents.slice(0, 10);
  var canToggle = agents.length > 10;

  // Outer Card has overflowX:auto for narrow viewports. Inner wrapper has
  // minWidth:720 so on screens narrower than that, the table keeps its
  // legible size and the Card scrolls horizontally; on wider screens the
  // wrapper grows to 100% and flex distributes the extra space.
  return <Card style={{ marginBottom:14, padding:"14px 16px", overflowX:"auto" }}>
    {headerRow}
    {subtitle}
    <div style={{ minWidth: 720 }}>
      <div style={{ display:"flex", gap:8, alignItems:"center", padding:"6px 0", borderBottom:"1px solid #E2E8F0" }}>
        {H("#", rankFlex)}
        {H("Agent", 1.5)}
        {H("Leads", 0.7, "right")}
        {H("Calls", 0.7, "right")}
        {H("Mtgs", 0.7, "right")}
        {H("EOIs", 0.7, "right")}
        {H("Deals", 0.7, "right")}
        {H("Revenue", 1, "right")}
        {H("Conv %", 0.7, "right")}
        {H("Target", 1.4, "left")}
      </div>
      {displayAgents.map(function(a, i){
        var rank = i + 1;
        var mEmoji = medal(rank);
        var tColor = targetColor(a.targetProgressPct || 0);
        var barW = Math.max(0, Math.min(100, a.targetProgressPct || 0));
        var convStr = (a.conversionPct == null) ? "—" : (a.conversionPct.toFixed(1) + "%");
        return <div key={a.agentId} style={{ display:"flex", gap:8, alignItems:"center", padding:"8px 0", borderBottom: i < displayAgents.length - 1 ? "1px dashed #F1F5F9" : "none" }}>
          <div style={{ flex: rankFlex, fontSize: mEmoji ? 16 : 13, fontWeight:700, color: mEmoji ? C.text : C.textLight, textAlign: "left" }}>{mEmoji || rank}</div>
          {V(a.name || "(unknown)", 1.5)}
          {V((a.leads || 0).toLocaleString(), 0.7, "right")}
          {V((a.calls || 0).toLocaleString(), 0.7, "right")}
          {V((a.meetings || 0).toLocaleString(), 0.7, "right")}
          {V((a.eois || 0).toLocaleString(), 0.7, "right")}
          {V((a.deals || 0).toLocaleString(), 0.7, "right")}
          {V(fmtEGP(a.revenue || 0), 1, "right", C.success, 700)}
          {V(convStr, 0.7, "right")}
          <div style={{ flex: 1.4, minWidth: 0 }}>
            {a.qTarget > 0 ? (
              <div>
                <div style={{ height:6, background:"#F1F5F9", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ width: barW + "%", height:"100%", background: tColor, borderRadius:3 }}/>
                </div>
                <div style={{ fontSize:10, color:tColor, fontWeight:700, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {(a.targetProgressPct || 0).toFixed(0)}% of {fmtEGP(a.qTarget)}
                </div>
              </div>
            ) : (
              <div style={{ fontSize:10, color:C.textLight }}>No {quarter || "Q"} target</div>
            )}
          </div>
        </div>;
      })}
    </div>
    {canToggle && <div
      onClick={function(){ setExpanded(!expanded); }}
      onMouseEnter={function(){ setHover(true); }}
      onMouseLeave={function(){ setHover(false); }}
      style={{
        marginTop:10, textAlign:"right", fontSize:11, fontWeight:600,
        color: C.accent, cursor:"pointer", userSelect:"none",
        textDecoration: hover ? "underline" : "none"
      }}>
      {expanded ? "Show top 10 ←" : "View all " + agents.length + " agents →"}
    </div>}
  </Card>;
};

var LeadAgingBuckets = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [hoverKey, setHoverKey] = useState(null);
  var f = p.filters;

  useEffect(function() {
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var bits = [];
    // Aging is a snapshot — no from/to. Team + source still apply.
    if (f.team) bits.push("team=" + encodeURIComponent(f.team));
    if (f.source && f.source !== "all") bits.push("source=" + encodeURIComponent(f.source));
    var qs = bits.length ? "?" + bits.join("&") : "";
    apiFetch("/api/reports/overview/aging" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.team, f.source]);

  var headerRow = <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
    <div style={{ fontSize:13, fontWeight:700 }}>⏱️ Lead Aging</div>
    <div title="Last contact = the agent's most recent meaningful action — a status change, a feedback note, or a logged activity (call / meeting / followup / email / note). Rescheduling a callback by itself is NOT counted, since rescheduling without reaching the customer isn't real progress. If none of these have happened since the agent took the lead, we show how long they've held it untouched."
         style={{ fontSize:11, color:C.textLight, cursor:"help" }}>ⓘ</div>
  </div>;

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      {headerRow}
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load: {state.error}</div>
    </Card>;
  }
  if (state.loading || !state.data) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:140 }}>
      {headerRow}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:10, marginTop:10 }}>
        {[0,1,2,3].map(function(i){
          return <div key={i} style={{ height:88, background:"#F1F5F9", borderRadius:10 }}/>;
        })}
      </div>
    </Card>;
  }

  var b = (state.data && state.data.buckets) || {};
  var filtersSpec = (state.data && state.data.filters) || {};
  var total = (state.data && state.data.total) || 0;

  if (total === 0) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      {headerRow}
      <div style={{ fontSize:11, color:C.textLight, marginBottom:10 }}>Active leads grouped by days since last contact</div>
      <div style={{ height:80, display:"flex", alignItems:"center", justifyContent:"center", color:C.textLight, fontSize:13 }}>No active leads</div>
    </Card>;
  }

  // Tile spec: high-priority on the left so the eye lands on red first.
  var tiles = [
    { key:"over14",    label:"14+ days",     sub:"High priority",   count: b.over14    || 0, bg:"#FEF2F2", border:"#FECACA", color:"#B91C1C" },
    { key:"days7to14", label:"7–14 days", sub:"Needs attention", count: b.days7to14 || 0, bg:"#FFFBEB", border:"#FDE68A", color:"#B45309" },
    { key:"days3to7",  label:"3–7 days",  sub:"Monitor",         count: b.days3to7  || 0, bg:"#F8FAFC", border:"#E2E8F0", color:"#475569" },
    { key:"under3",    label:"Under 3 days", sub:"Active",          count: b.under3    || 0, bg:"#ECFDF5", border:"#A7F3D0", color:"#047857" }
  ];

  var goToFilter = function(key){
    var spec = filtersSpec[key];
    if (!spec || !p.nav) return;
    if (p.setSpecialFilter) p.setSpecialFilter(spec);
    if (p.setFilter) p.setFilter("all");
    p.nav("leads");
  };

  return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
    {headerRow}
    <div style={{ fontSize:11, color:C.textLight, marginBottom:10 }}>
      Active leads grouped by days since last contact · {total.toLocaleString()} total
    </div>
    <div style={{ display:"grid", gridTemplateColumns:"repeat(4, minmax(0, 1fr))", gap:10 }}>
      {tiles.map(function(tile){
        var hovered = hoverKey === tile.key;
        return <div key={tile.key}
          onClick={function(){ goToFilter(tile.key); }}
          onMouseEnter={function(){ setHoverKey(tile.key); }}
          onMouseLeave={function(){ setHoverKey(null); }}
          style={{
            background: tile.bg,
            border: "1px solid " + tile.border,
            borderRadius: 10,
            padding: "12px 14px",
            cursor: "pointer",
            display: "flex", flexDirection: "column", justifyContent: "space-between",
            minHeight: 88,
            transform: hovered ? "translateY(-1px)" : "translateY(0)",
            boxShadow: hovered ? "0 2px 6px rgba(0,0,0,0.06)" : "none",
            transition: "transform 0.12s ease, box-shadow 0.12s ease"
          }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:700, color: tile.color, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{tile.label}</div>
              <div style={{ fontSize:10, color: tile.color, opacity:0.75, marginTop:2 }}>{tile.sub}</div>
            </div>
            <div style={{ fontSize:22, fontWeight:800, color: tile.color, lineHeight:1 }}>{tile.count.toLocaleString()}</div>
          </div>
          <div style={{ fontSize:11, color: tile.color, fontWeight:700, textAlign:"right", opacity: hovered ? 1 : 0.7 }}>→</div>
        </div>;
      })}
    </div>
  </Card>;
};

var AlertsBanner = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [hoverIdx, setHoverIdx] = useState(-1);
  var f = p.filters;

  useEffect(function() {
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    // Alerts ignore date range / source — each alert defines its own window.
    // Only the team filter is honored so admins can scope to a manager / TL.
    var qs = f.team ? "?team=" + encodeURIComponent(f.team) : "";
    apiFetch("/api/reports/overview/alerts" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.team]);

  // Hide silently while loading and on error — don't break the page
  // and don't flash an empty banner before data arrives.
  if (state.loading || state.error || !state.data) return null;
  var alerts = (state.data && state.data.alerts) || [];
  if (alerts.length === 0) return null;

  var dotColor = function(prio){ return prio === "high" ? "#DC2626" : "#F59E0B"; };

  var handleClick = function(a){
    var act = a && a.action;
    if (!act) return;
    if (act.type === "drill_aging") {
      if (p.setSpecialFilter) p.setSpecialFilter({ type: "aging", ageMin: act.ageMin });
      if (p.setFilter) p.setFilter("all");
      if (p.nav) p.nav("leads");
    } else if (act.type === "drill_meeting_aging") {
      if (p.setSpecialFilter) p.setSpecialFilter({ type: "aging", ageMin: act.ageMin });
      if (p.setFilter) p.setFilter(act.status || "MeetingDone");
      if (p.nav) p.nav("leads");
    } else if (act.type === "scroll_to" && act.target === "agent_leaderboard") {
      var el = (typeof document !== "undefined") ? document.getElementById("reports-agent-leaderboard") : null;
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (act.type === "set_source") {
      if (p.setReportsSource) p.setReportsSource(act.source);
    }
  };

  return <Card style={{ marginBottom:14, padding:"12px 14px", background:"#FFFBEB", border:"1px solid #FDE68A" }}>
    <div style={{ fontSize:12, fontWeight:700, color:"#B45309", marginBottom:6 }}>⚠️ Alerts</div>
    {alerts.map(function(a, i){
      var hovered = hoverIdx === i;
      return <div key={a.key + "_" + i}
        onClick={function(){ handleClick(a); }}
        onMouseEnter={function(){ setHoverIdx(i); }}
        onMouseLeave={function(){ setHoverIdx(-1); }}
        style={{
          display:"flex", alignItems:"center", gap:10, padding:"7px 0",
          borderTop: i > 0 ? "1px dashed #FCD34D" : "none",
          cursor:"pointer", userSelect:"none"
        }}>
        <div style={{ width:8, height:8, borderRadius:"50%", flexShrink:0, background: dotColor(a.priority) }}/>
        <div style={{ flex:1, minWidth:0, fontSize:12, color:"#92400E", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.message}</div>
        <div style={{ fontSize:11, color:"#B45309", fontWeight:700, flexShrink:0, textDecoration: hovered ? "underline" : "none" }}>→ view</div>
      </div>;
    })}
  </Card>;
};

var ForecastCard = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var f = p.filters;

  useEffect(function() {
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    // Forecast uses its own windows (90d for win rate, current quarter for
    // target). Date range / source ignored. Team filter honored.
    var qs = f.team ? "?team=" + encodeURIComponent(f.team) : "";
    apiFetch("/api/reports/overview/forecast" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.team]);

  if (state.error) return null; // hide silently on error
  if (state.loading || !state.data) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:160 }}>
      <div style={{ height:14, width:140, background:"#F1F5F9", borderRadius:4, marginBottom:10 }}/>
      <div style={{ height:32, width:120, background:"#F1F5F9", borderRadius:6, marginBottom:8 }}/>
      <div style={{ height:10, width:200, background:"#F1F5F9", borderRadius:4, marginBottom:18 }}/>
      <div style={{ height:6, background:"#F1F5F9", borderRadius:3, marginBottom:6 }}/>
      <div style={{ height:10, width:240, background:"#F1F5F9", borderRadius:4 }}/>
    </Card>;
  }

  var fc = state.data.forecast || {};
  var q  = state.data.quarter  || {};
  var hasForecast = (fc.pipelineCount || 0) > 0;
  var hasQTarget  = (q.target || 0) > 0;
  // Hide entirely when there's nothing useful to show.
  if (!hasForecast && !hasQTarget) return null;

  var winRate = Number(fc.winRatePct) || 0;
  var pr = fc.projectedRevenue || { low:0, mid:0, high:0 };
  var pd = fc.projectedDeals   || { low:0, mid:0, high:0 };
  var sample = fc.sample || { dealsLast90:0, leadsLast90:0 };

  var qProgress = Number(q.progressPct) || 0;
  var barWidth  = Math.max(0, Math.min(100, qProgress));
  // Purple progress bar — distinct from the leaderboard's red/amber/green
  // semantic palette so it reads as "neutral progress" rather than a grade.
  var qBarColor = "#8B5CF6";

  var forecastTooltip = "Pipeline value × historical win rate (last 90 days). Range reflects ±20% uncertainty on revenue, ±30% on deal count.";
  var winRateNote = sample.leadsLast90 > 0
    ? winRate.toFixed(1) + "% win rate · " + sample.dealsLast90 + " deals from " + sample.leadsLast90 + " leads (90d)"
    : "";

  return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
    {hasForecast && <div style={{ marginBottom: hasQTarget ? 16 : 0 }}>
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
        <div style={{ fontSize:13, fontWeight:700 }}>🔮 Forecast — next 30 days</div>
        <div title={forecastTooltip} style={{ fontSize:11, color:C.textLight, cursor:"help" }}>ⓘ</div>
      </div>
      {winRate > 0 ? <div>
        <div style={{ fontSize:30, fontWeight:800, color:C.success, lineHeight:1.1 }}>{fmtEGP(pr.mid)}</div>
        <div style={{ fontSize:12, color:C.textLight, marginTop:4 }}>
          range {fmtEGP(pr.low)} – {fmtEGP(pr.high)} · {pd.low.toLocaleString()}–{pd.high.toLocaleString()} deal{pd.high === 1 ? "" : "s"}
        </div>
        {winRateNote && <div style={{ fontSize:10, color:"#94A3B8", marginTop:3 }}>{winRateNote}</div>}
      </div> : <div>
        <div style={{ fontSize:13, fontWeight:600, color:C.textLight, padding:"6px 0" }}>Building forecast baseline…</div>
        <div style={{ fontSize:11, color:"#94A3B8" }}>{sample.dealsLast90} deals from {sample.leadsLast90.toLocaleString()} leads in the last 90 days — need at least one deal to project.</div>
      </div>}
    </div>}

    {hasForecast && hasQTarget && <div style={{ height:1, background:"#E2E8F0", margin:"0 0 14px" }}/>}

    {hasQTarget && <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
        <div style={{ fontSize:13, fontWeight:700 }}>🎯 {q.key} target progress</div>
        <div style={{ fontSize:13, fontWeight:700, color:qBarColor }}>{qProgress.toFixed(qProgress < 10 ? 1 : 0)}%</div>
      </div>
      <div style={{ height:8, background:"#F1F5F9", borderRadius:4, overflow:"hidden", marginBottom:6 }}>
        <div style={{ width: barWidth + "%", height:"100%", background: qBarColor, borderRadius:4, transition:"width 0.3s ease" }}/>
      </div>
      <div style={{ fontSize:11, color:C.textLight }}>
        {fmtEGP(q.achieved || 0)} of {fmtEGP(q.target || 0)} · {(q.daysRemaining || 0).toLocaleString()} day{q.daysRemaining === 1 ? "" : "s"} remaining in quarter
      </div>
    </div>}
  </Card>;
};

// ===== REPORTS =====
var ReportsPage = function(p) {
  useLeadSourcesVersion();   // re-render when the lead source registry lands (src/lib/leadSources.js)
  var t = p.t;
  var cu = p.cu;

  // The selected tab is remembered per page per user through the ONE
  // mechanism every strip uses (src/lib/tabMemory, 2026-10-28; this page used
  // to keep its own `crm_reports_tab`). The "← Back to report" drill-down
  // return leaves a one-shot request the hook consumes ahead of the stored tab.
  var [tabState, selectTab] = useRememberedTab("reports", gid(cu), REPORTS_TABS, null, "overview");
  // A STORED TAB IS RESTORED, SO IT MUST ALSO BE CHECKED. The remembered tab
  // survives across sessions and across a take-over; validating it against the
  // hard-coded id list above only proved it was spelled right, never that this
  // caller may still see it. resolveTabStrip answers both, and `tab` is derived
  // from it so every `tab === "x"` read below is envelope-answered unchanged.
  var reportsStrip = resolveTabStrip(p.access, "reports", REPORTS_TABS, tabState);
  var tab = reportsStrip.active;
  // Campaigns-tab sub-view. "performance" (VIEW 1, cohort table) is the default;
  // "intake" (VIEW 2, monthly-by-source matrix) and "roi" (the original tracked-
  // campaign spend/ROI table) round it out. Persisted like the main tab.
  var [campaignsView, setCampaignsView] = useState(function(){
    var valid = { performance:1, intake:1, roi:1 };
    try { var saved = localStorage.getItem("crm_reports_campaigns_view"); return (saved && valid[saved]) ? saved : "performance"; }
    catch (e) { return "performance"; }
  });
  var selectCampaignsView = function(id){ setCampaignsView(id); try { localStorage.setItem("crm_reports_campaigns_view", id); } catch (e) {} };
  // Whole-book sources for the Source picker (REPORTS_ROSTER.sources below).
  // Declared with the other hooks, above any early return (rules-of-hooks).
  var [bookSources, setBookSources] = useState([]);
  useEffect(function(){
    var cancelled = false;
    apiFetch("/api/filter-options?scope=leads", "GET", null, p.token)
      .then(function(r){ if (!cancelled && r && Array.isArray(r.leadSources)) setBookSources(r.leadSources); })
      .catch(function(){ /* catalog-only until it answers */ });
    return function(){ cancelled = true; };
  }, [p.token]);
  var [filters, setFilters] = useState(function(){
    // Back from a Leads drill-down ("\u2190 Back to report" on the ROI / Spend view): the
    // toolbar the report was left in. Read once; null on any ordinary visit.
    var rr = peekReportReturn("roi");
    if (rr && rr.filters && typeof rr.filters.from === "number" && typeof rr.filters.to === "number") return rr.filters;
    var now = new Date();
    var monthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    return { from: monthStart.getTime(), to: now.getTime(), preset: "thisMonth", team: "", source: "all", compare: false };
  });
  useEffect(function(){ if (peekReportReturn("roi")) clearReportReturn(); }, []);

  // EXPORT EXCEL, PER TAB. This was a stub alert for every tab. A tab body that
  // can build its own workbook registers one here through onExportReady; the
  // button then runs THAT, and the tabs that still cannot say so plainly rather
  // than pretending. Held in a ref-backed state so registering does not loop:
  // the body re-registers whenever its filters change, and the toolbar only
  // needs to re-render when `busy`/`ready` actually flip.
  //
  // ABOVE THE ROLE GATE, not beside the handler that uses it — the early return
  // below is a CONDITIONAL, and three hooks after it would change hook ORDER the
  // moment cu.role changes under an identity refresh. React's rule, and this
  // component already has four useStates on this side of the line.
  var [tabExportState, setTabExportState] = useState(null);
  var tabExportRef = useRef(null);
  var setTabExport = useCallback(function(api){
    tabExportRef.current = api;
    setTabExportState(api ? { busy: !!api.busy, ready: !!api.ready } : null);
  }, []);
  // Export-PDF state for the app path. UP HERE, ABOVE THE ROLE GUARD, because
  // that guard is an early return: hooks declared below it run on some renders
  // and not others, which is a conditional hook. eslint's rules-of-hooks caught
  // it on the first build — the handler that uses them stays below, where the
  // rest of the page's helpers are.
  var reportsRootRef = useRef(null);
  var [pdfBusy, setPdfBusy] = useState(false);
  var [pdfErr, setPdfErr] = useState("");

  // A matrix-only role (the accountant) reaches this page only through an owner
  // grant on page:reports — renderPage already asked canSeePage — and the tabs it
  // sees come from resolveTabStrip over the same envelope. The literal check for
  // every other role is unchanged.
  if (cu.role !== "admin" && cu.role !== "operation" && !roleHasFinanceReadsFE(cu)) {
    return <div style={{ padding:"40px 16px", textAlign:"center", color:C.textLight, fontSize:13 }}>
      Reports are not available for your role.
    </div>;
  }

  var presetRange = function(preset) {
    var now = new Date();
    var y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    var todayStart = new Date(y, m, d, 0, 0, 0, 0).getTime();
    if (preset === "today") return { from: todayStart, to: now.getTime() };
    if (preset === "yesterday") {
      return { from: new Date(y, m, d-1, 0, 0, 0, 0).getTime(), to: todayStart };
    }
    if (preset === "thisWeek") {
      var dow = now.getDay();
      var daysSinceSat = (dow - 6 + 7) % 7;
      return { from: new Date(y, m, d - daysSinceSat, 0, 0, 0, 0).getTime(), to: now.getTime() };
    }
    if (preset === "thisMonth") return { from: new Date(y, m, 1, 0, 0, 0, 0).getTime(), to: now.getTime() };
    if (preset === "thisQuarter") {
      var qStart = m < 3 ? 0 : m < 6 ? 3 : m < 9 ? 6 : 9;
      return { from: new Date(y, qStart, 1, 0, 0, 0, 0).getTime(), to: now.getTime() };
    }
    if (preset === "lastQuarter") {
      var curQStart = m < 3 ? 0 : m < 6 ? 3 : m < 9 ? 6 : 9;
      var lastQStart = curQStart - 3;
      var lastQYear = y;
      if (lastQStart < 0) { lastQStart = 9; lastQYear = y - 1; }
      return {
        from: new Date(lastQYear, lastQStart, 1, 0, 0, 0, 0).getTime(),
        to: new Date(lastQYear, lastQStart + 3, 1, 0, 0, 0, 0).getTime()
      };
    }
    if (preset === "thisYear") return { from: new Date(y, 0, 1, 0, 0, 0, 0).getTime(), to: now.getTime() };
    return null;
  };

  var setPreset = function(preset) {
    var range = presetRange(preset);
    if (!range) { setFilters(function(f){ return Object.assign({}, f, { preset: "custom" }); }); return; }
    setFilters(function(f){ return Object.assign({}, f, { preset: preset, from: range.from, to: range.to }); });
  };
  var setCustomFrom = function(ms) { setFilters(function(f){ return Object.assign({}, f, { preset: "custom", from: ms }); }); };
  var setCustomTo = function(ms) { setFilters(function(f){ return Object.assign({}, f, { preset: "custom", to: ms }); }); };

  // Roster pickers (owner rule 2026-10-01, REPORTS_ROSTER): teams = active
  // managers / team leaders from the users roster; sources = the source
  // registry ∪ the whole book's distinct sources (/api/filter-options) — the old
  // fold over p.leads read only the 100-row bootstrap slice.
  var teamOptions = REPORTS_ROSTER.people(p.users, ["manager", "team_leader"]);
  var sourceOptions = REPORTS_ROSTER.sources(leadSourceNames(), bookSources);

  var fmtRange = function(){
    var fd = new Date(filters.from), td = new Date(filters.to);
    if (fd.toDateString() === td.toDateString()) return fd.toLocaleDateString();
    return fd.toLocaleDateString() + " — " + td.toLocaleDateString();
  };

  var tabs = reportsStrip.tabs;

  var presets = [
    { id: "today",       label: "Today" },
    { id: "yesterday",   label: "Yesterday" },
    { id: "thisWeek",    label: "This Week" },
    { id: "thisMonth",   label: "This Month" },
    { id: "thisQuarter", label: "This Quarter" },
    { id: "lastQuarter", label: "Last Quarter" },
    { id: "thisYear",    label: "This Year" }
  ];

  // EXPORT PDF ON THE APPS (2026-09-11). `window.print()` is undefined in both
  // WebViews, so this button did nothing there — no dialog, no error, no log
  // line. The app path rasterises the report and hands the PDF to the native
  // share sheet; `captureAndShare` owns every step of that, shared with the
  // other three print surfaces.
  //
  // THE WEB PRINT AND THE APP PDF NOW CONTAIN THE SAME THING (2026-09-11).
  //
  // This surface used to have NO print stylesheet, so a bare `window.print()`
  // printed the whole application — sidebar, header, nav, the lot — while the
  // app captured only the report. That gap was reported rather than closed at
  // the time, because closing it changes the working web path. The owner asked
  // for it closed: the @media print block on the root below scopes the printed
  // page to #reports-print-area, exactly as the Commission Payout Report has
  // always done for the same shape.
  //
  // THE TWO PATHS HIDE THE SAME THING BY CONSTRUCTION, not by review: the print
  // rule and the cloneSwaps below use the IDENTICAL selector string,
  // "#reports-print-area .no-print". A second spelling is a second answer, and
  // it would drift silently — nobody prints from a phone or shares from a
  // desktop, so neither half is checked by using the other.
  //
  // PAGINATED, never fitted: a report is metres long and shrinking it onto one
  // page produces a file that opens and cannot be read.
  var handleExportPdf = function(){
    var mode = pdfShareSupport();
    if (mode === "print") { window.print(); return; }
    if (mode !== "share") { setPdfErr(PRINT_UNSUPPORTED_MSG[mode] || PRINT_UNSUPPORTED_MSG.none); return; }
    var node = reportsRootRef.current;
    if (!node) { setPdfErr("The report is not ready yet."); return; }
    setPdfBusy(true); setPdfErr("");
    captureAndShare(node, {
      layout: "paginate",
      // THE SAME SELECTOR THE @media print RULE USES. Pinned as a pair by
      // verify-print-surfaces.js, like the payout report's.
      cloneSwaps: [{ selector: "#reports-print-area .no-print", display: "none" }],
      filename: pdfFilename("report", (tab || "report") + "-" + fmtRange().replace(/\s+/g, ""), Date.now()),
      title: "ARO Report — " + (tab || "")
    })
      .then(function(){ setPdfBusy(false); })
      .catch(function(ex){ setPdfBusy(false); setPdfErr(String((ex && ex.message) || "Could not create the PDF.")); });
  };
  var excelBusy  = !!(tabExportState && tabExportState.busy);
  var excelReady = !!(tabExportState && tabExportState.ready);
  var handleExportExcel = function(){
    var api = tabExportRef.current;
    if (!api) { window.alert("Excel export is not available for this report section yet."); return; }
    if (excelBusy) return;
    api.run();
  };

  var btnOutline = { padding:"6px 12px", fontSize:12, borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", color:C.text, cursor:"pointer", fontWeight:600 };

  return <div ref={reportsRootRef} id="reports-print-area" style={{ padding:"18px 16px 40px" }}>
    {/* PRINT ONLY THE REPORT (2026-09-11). Mirrors the Commission Payout
        Report's block, because it is the same shape of problem: an element
        NESTED inside the app shell, printing to many pages.
        Deliberately NOT the broker modal's `body > * { display:none }`
        pattern — that one works because its print root is injected as a SIBLING
        of #root, and this report is not. Hiding body's children here would take
        the report down with the shell.
        Known cost, the same one the payout block carries: `visibility:hidden`
        leaves the shell occupying its 100vh, so a report SHORTER than one page
        can print a trailing blank page. A real report is many pages and
        subsumes it. Accepted rather than solved by reaching into the shell's
        own layout from a page's stylesheet. */}
    <style>{`
      @media print {
        @page { size: A4 portrait; margin: 14mm; }
        html, body { background: #fff !important; }
        body * { visibility: hidden !important; }
        #reports-print-area, #reports-print-area * { visibility: visible !important; }
        /* Badges, chips and chart fills carry meaning here — the browser strips
           backgrounds by default and the report reads as plain text without this. */
        #reports-print-area * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        /* Lifted out of the shell's flow to the page's top-left. The sidebar is
           position:fixed and the content column carries a 240px margin, so
           without this the report prints indented and below a band of nothing. */
        #reports-print-area {
          position: absolute !important;
          left: 0 !important; top: 0 !important;
          width: 100% !important; padding: 0 !important;
        }
        #reports-print-area .no-print { display: none !important; }
        /* Tables run past a page here (leaderboard, agent performance), so rows
           stay whole and headers repeat. Same rules as the payout report. */
        #reports-print-area table { page-break-inside: auto; }
        #reports-print-area tr { page-break-inside: avoid; page-break-after: auto; }
        #reports-print-area thead { display: table-header-group; }
        #reports-print-area tfoot { display: table-footer-group; }
        /* A horizontally scrolling strip prints clipped at its scroll width. */
        #reports-print-area [style*="overflow-x"], #reports-print-area [style*="overflowX"] {
          overflow: visible !important;
        }
      }
    `}</style>
    {/* Title removed — the shared header renders it. The export actions keep
        this row and now sit flush right on their own. */}
    {/* `no-print` — the export controls themselves. They are the one thing that
        must not appear in a report the owner sends to somebody: a picture of an
        "Export PDF" button. ONE class, both paths: the @media print rule above
        hides it on the web, and the cloneSwaps hide it on the app CLONE only,
        so nothing on screen moves. */}
    <div className="no-print" style={{ display:"flex", justifyContent:"flex-end", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
        <button onClick={pdfBusy ? undefined : handleExportPdf}
          style={Object.assign({}, btnOutline, { opacity: pdfBusy ? 0.55 : 1, cursor: pdfBusy ? "wait" : "pointer" })}>
          {pdfBusy ? "Preparing PDF…" : (pdfShareSupport() === "print" ? "Export PDF" : "Share PDF")}
        </button>
        <button onClick={handleExportExcel} disabled={excelBusy || (!!tabExportState && !excelReady)}
          title={tabExportState ? "" : "Not available for this report section yet"}
          style={Object.assign({}, btnOutline, { color: tabExportState ? C.text : C.textLight,
            opacity: (excelBusy || (!!tabExportState && !excelReady)) ? 0.55 : 1,
            cursor: (excelBusy || (!!tabExportState && !excelReady)) ? "not-allowed" : "pointer" })}>
          {excelBusy ? "Exporting…" : "Export Excel"}
        </button>
      </div>
    </div>
    {/* NEVER SILENT. The failure this replaces was a button that did nothing. */}
    {pdfErr && <div className="no-print" style={{ marginBottom:14, padding:"9px 12px", borderRadius:8, background:"#FEE2E2", border:"1px solid #FCA5A5", color:"#B91C1C", fontSize:12, lineHeight:1.5 }}>{pdfErr}</div>}

    <div style={{ display:"flex", gap:4, marginBottom:14, borderBottom:"1px solid #E2E8F0", overflowX:"auto", overflowY:"hidden", WebkitOverflowScrolling:"touch", flexWrap:"nowrap", scrollbarWidth:"thin" }}>
      {tabs.map(function(tb){
        var active = tab === tb.id;
        var disabled = !tb.enabled;
        return <button key={tb.id}
          onClick={function(){ if(!disabled) selectTab(tb.id); }}
          title={disabled ? "Coming soon" : ""}
          style={{
            padding:"9px 16px", border:"none",
            borderBottom: active ? "2px solid "+C.accent : "2px solid transparent",
            background:"transparent",
            color: disabled ? "#CBD5E1" : (active ? C.accent : C.textLight),
            fontSize:13, fontWeight:600, flexShrink:0, whiteSpace:"nowrap",
            cursor: disabled ? "not-allowed" : "pointer"
          }}>
          {tb.label}
          {disabled && <span style={{ marginInlineStart:6, fontSize:9, color:"#94A3B8", fontWeight:500 }}>Soon</span>}
        </button>;
      })}
    </div>

    {reportsStrip.empty && <div style={{ fontSize:13, color:C.textLight, padding:"18px 2px" }}>{NO_TABS_MSG}</div>}

    {tab === "campaigns" && <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
      {[{ id:"performance", label:"Performance" }, { id:"intake", label:"Monthly intake" }, { id:"roi", label:"ROI / Spend" }].map(function(sv){
        var active = campaignsView === sv.id;
        return <button key={sv.id} onClick={function(){ selectCampaignsView(sv.id); }} style={{
          padding:"6px 14px", borderRadius:8, border:"1px solid", borderColor: active ? C.accent : "#E2E8F0",
          background: active ? C.accent + "12" : "#fff", color: active ? C.accent : C.textLight,
          fontSize:12, fontWeight:600, cursor:"pointer"
        }}>{sv.label}</button>;
      })}
    </div>}

    {(tab === "overview" || tab === "pipeline" || tab === "agents" || (tab === "campaigns" && campaignsView === "roi")) && <Card style={{ marginBottom:16, padding:"12px 14px" }}>
      <div style={{ display:"flex", flexWrap:"wrap", gap:10, alignItems:"center" }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
          {presets.map(function(pr){
            var active = filters.preset === pr.id;
            return <button key={pr.id} onClick={function(){ setPreset(pr.id); }} style={{
              padding:"5px 10px", borderRadius:7,
              border:"1px solid", borderColor: active ? C.accent : "#E2E8F0",
              background: active ? C.accent + "12" : "#fff",
              color: active ? C.accent : C.textLight,
              fontSize:11, fontWeight:600, cursor:"pointer"
            }}>{pr.label}</button>;
          })}
        </div>

        <div style={{ width:1, height:22, background:"#E2E8F0" }}/>

        {/* 📅 Date (owner 2026-10-31): the shared calendar chip, LAST after the
            presets, in place of the From → To inputs. reportsDateChip writes the
            same filters.from / filters.to (epoch ms) + preset "custom" through
            setCustomFrom / setCustomTo; `to` stays the server's exclusive end. */}
        <DashRangePicker isMobile={p.isMobile} iconOnly={p.isMobile} todayYmd={cairoTodayIso()} min=""
          value={reportsDateChip.value(filters)} active={reportsDateChip.active(filters)}
          triggerStyle={periodChipStyle(reportsDateChip.active(filters), PERIOD_ACCENT_BLUE, p.isMobile)}
          onPick={function(a, b){ reportsDateChip.pick(a, b, { setPreset:setPreset, setCustomFrom:setCustomFrom, setCustomTo:setCustomTo }); }}/>

        {tab !== "campaigns" && <div style={{ width:1, height:22, background:"#E2E8F0" }}/>}

        {tab !== "campaigns" && <Select value={filters.team}
          onChange={function(e){ setFilters(function(f){ return Object.assign({},f,{team:e.target.value}); }); }}
          searchable width={150} maxWidth={150} fontSize={11}
          options={[{ value:"", label:"All teams" }].concat(teamOptions.map(function(u){ return { value:gid(u), label:u.name + " (" + (positionOf(u)==="manager"?"Manager":"Team Leader") + ")" }; }))}/>}

        <Select value={filters.source}
          onChange={function(e){ setFilters(function(f){ return Object.assign({},f,{source:e.target.value}); }); }}
          searchable width={150} maxWidth={150} fontSize={11}
          options={[{ value:"all", label:"All sources" }].concat(sourceOptions.map(function(s){ return { value:s, label:s }; }))}/>

        <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:C.textLight, cursor:"pointer" }}>
          <input type="checkbox" checked={filters.compare}
            onChange={function(e){ setFilters(function(f){ return Object.assign({},f,{compare:e.target.checked}); }); }}/>
          Compare to previous period
        </label>

        <div style={{ flex:1, minWidth:8 }}/>
        <span style={{ fontSize:11, color:C.textLight }}>{fmtRange()}</span>
      </div>
    </Card>}

    {tab === "overview" && <ReportsOverviewBody filters={filters} cu={cu} t={t} token={p.token}
      nav={p.nav} setFilter={p.setFilter} setSpecialFilter={p.setSpecialFilter}
      setReportsSource={function(src){ setFilters(function(prev){ return Object.assign({}, prev, { source: src }); }); }}/>}

    {tab === "pipeline" && <ReportsPipelineBody filters={filters} cu={cu} t={t} token={p.token}
      nav={p.nav} setFilter={p.setFilter} setSpecialFilter={p.setSpecialFilter}/>}

    {tab === "agents" && <ReportsAgentsBody filters={filters} cu={cu} t={t} token={p.token} users={p.users}
      nav={p.nav} setFilter={p.setFilter} setSpecialFilter={p.setSpecialFilter}/>}

    {tab === "meetings" && <ReportsMeetingsBody cu={cu} t={t} token={p.token} csrfToken={p.csrfToken}
      users={p.users} isMobile={p.isMobile} onExportReady={setTabExport}/>}

    {tab === "leaderboard" && <ReportsLeaderboardBody cu={cu} t={t} token={p.token} onExportReady={setTabExport}/>}

    {tab === "campaigns" && campaignsView === "roi" && <ReportsCampaignsBody filters={filters} cu={cu} t={t} token={p.token}
      nav={p.nav} setFilter={p.setFilter} setSpecialFilter={p.setSpecialFilter}/>}

    {tab === "campaigns" && campaignsView === "performance" && <ReportsCampaignPerformanceSwitch cu={cu} token={p.token} csrfToken={p.csrfToken}
      users={p.users} isMobile={p.isMobile} nav={p.nav} setSpecialFilter={p.setSpecialFilter} setFilter={p.setFilter} sourceOptions={sourceOptions} ui={{ C:C, Card:Card, Select:Select, apiFetch:apiFetch, loadXLSX:loadXLSX }}
      legacy={<ReportsCampaignPerformance cu={cu} t={t} token={p.token}
        nav={p.nav} setFilter={p.setFilter} setSpecialFilter={p.setSpecialFilter}/>}/>}

    {tab === "campaigns" && campaignsView === "intake" && <ReportsCampaignIntake cu={cu} t={t} token={p.token}/>}
  </div>;
};


// ===== Reports › Meetings — meetings per person ==============================
// One row per agent over a Cairo calendar-day period, off the permanent Meeting
// archive (GET /api/reports/meetings-by-agent). Total, and the same total split
// by where the meeting came from: a Lead, or a Daily Request.
//
// The period control is the Leaderboard's, deliberately — Year select, Q1-Q4
// chips, Month select, all mutually exclusive — plus a From/To pair for a range
// that is not a calendar period. From/To WINS over Q/Month, which is what the
// server does with the same params; the strip greys the chips while a custom
// range is live so the precedence is visible instead of surprising.
//
// Clicking a name opens an expand-below <tr>, the pattern EOI / Deals / Brokers
// set. The detail fetch is the SAME endpoint with detail=1, so a row's list can
// only ever contain meetings the number beside it counted.
//
// EXPORT. The Reports toolbar's Export Excel button was a stub alert until now;
// on this tab it writes the workbook. Both sheets come from the server under the
// ACTIVE FILTERS — sheet 1 re-fetches the aggregate, sheet 2 fetches detail=1
// with NO agentId (every meeting in range, one call, never one per agent) — so
// the file is the report, not the rows that happen to be expanded. See
// verify-export-completeness.js, which is what holds that true.
var REPORT_MEETING_COLS = ["Agent", "Total", "From Leads", "From DR"];
// The workbook's columns, at module scope so verify-export-completeness can read
// them as data. Sheet 1 carries a Status column the table renders as a badge —
// a spreadsheet has no badges, and "inactive"/"former" is exactly the thing a
// reader sorting this file needs to know about a name they do not recognise.
var RM_SHEET1_COLS = ["Agent", "Total", "Leads", "DR", "Status"];
var RM_SHEET2_COLS = ["Agent", "Date", "Client", "Project", "Source"];
var RM_MONTHS = ["January","February","March","April","May","June",
                 "July","August","September","October","November","December"];
// The active filters -> the query string BOTH the table and the export send.
// One builder, for the reason the meetings archive has one: a file built from a
// different query than the table is a file that answers a different question.
function reportMeetingsQuery(f) {
  var qs = [];
  if (f.from && f.to) { qs.push("from=" + f.from); qs.push("to=" + f.to); }
  else {
    qs.push("year=" + f.year);
    if (f.month !== "all") qs.push("month=" + (Number(f.month) + 1));
    else if (f.quarter !== "all") qs.push("quarter=" + String(f.quarter).slice(1));
  }
  if (f.team) qs.push("team=" + encodeURIComponent(f.team));
  return qs.join("&");
}
// A meeting's day as the reader sees it. The server already sends dayBucket —
// the CAIRO calendar day this meeting belongs to — so it is used verbatim and
// `date` is only the fallback for a row that somehow carries no bucket. Never
// new Date(m.date).toLocaleDateString(): that re-derives the day in the BROWSER's
// zone, and for a meeting recorded after 22:00 Cairo it prints yesterday.
function fmtRmDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("en-CA", { timeZone:"Africa/Cairo" }); }
  catch (e) { return String(v).slice(0, 10); }
}
// WHAT KIND OF ROW THIS IS, from the two flags the server sends and nothing
// else. `active` is THREE-VALUED and that is the whole mechanism: true/false is
// a user record that says so, and null means there is no user record to ask.
// Collapsing null to false would print "inactive" over a hard-deleted account —
// a person who cannot be reactivated, described as though they could be.
//
//   roster                       on today's roster: no badge, nothing to explain
//   !roster, agentId === null    the archive's agentId:null rows (a non-sales
//                                actor on an agent-less source). Not a person.
//   !roster, active === false    deactivated, and holds meetings in this range
//   !roster, active === null     the User record is GONE — the name printed is
//                                the archive's frozen snapshot
//   !roster, recorderCredited>0  credited because they RECORDED the meeting and
//                                no holder could be attributed (owner ruling
//                                2026-08-27). This is the row the ruling
//                                created: an operation or managerial user is not
//                                in a meeting-holding position, so they are
//                                off-roster BY DESIGN and would otherwise be
//                                explained as "off-roster" — which says what
//                                they are not, when what the reader needs is why
//                                they are in the table. Ranked above off-roster
//                                for that reason, and below inactive/former,
//                                which are facts about the ACCOUNT and outrank a
//                                fact about the row.
//   !roster, active === true     active, but not in a meeting-holding position
//                                (an off-ladder or rank-less holder) and holding
//                                no recorder credit either.
function rmRowStatus(r) {
  if (r.roster) return null;
  if (r.agentId === null)  return { key:"unassigned", label:"unassigned", fg:"#475569", bg:"#F1F5F9" };
  if (r.active === false)  return { key:"inactive",   label:"inactive",   fg:"#B45309", bg:"#FEF3C7" };
  if (r.active === null)   return { key:"former",     label:"former",     fg:"#B91C1C", bg:"#FEE2E2" };
  if (r.recorderCredited > 0) return { key:"recorded", label:"recorded", fg:"#1D4ED8", bg:"#EFF6FF" };
  return { key:"offRoster", label:"off-roster", fg:"#475569", bg:"#F1F5F9" };
}
// The badge's tooltip. A one-word badge in a table of names has to be able to
// say WHY, or the reader's only options are to guess or to go and look.
var RM_STATUS_TITLE = {
  unassigned: "No agent was attributed and no recorder was stored — a historical row from the archive backfill.",
  recorded:   "Credited to the person who RECORDED the meeting, because no holder could be attributed (owner ruling, 2026-08-27). They are not in a meeting-holding position, so they are not expected to hold meetings — they are listed so the totals stay complete.",
  inactive:   "Deactivated account. Kept here because these meetings happened inside the selected period.",
  former:     "The user record no longer exists. The name shown is the one frozen on the meeting itself.",
  offRoster:  "Active, but not in a meeting-holding position — so not counted as somebody a meeting is expected from."
};
// The same fact as one word, for the export's Status column. "active" for a
// roster row — the sheet has no badge to leave blank, and a blank cell in a
// status column reads as missing data rather than as "nothing to say".
function rmStatusWord(r) {
  var st = rmRowStatus(r);
  return st ? st.key : "active";
}
// The position label beside a name. positionOf(), never `.role` — after the
// sales collapse every ladder user STORES "sales", so reading role here would
// print "sales" for a team leader (verify-fe-position.js). The row carries
// titleRank for exactly this call. "—" for the archive's unassigned row, which
// has no user behind it.
function rmRoleLabel(r) {
  var pos = positionOf(r);
  if (!pos) return "—";
  return ({ sales:"Sales", team_leader:"Team Leader", manager:"Manager",
            director:"Director", admin:"Admin", operation:"Operation" })[pos] || pos;
}
var ReportsMeetingsBody = function(p) {
  var now = new Date();
  var curYear = now.getFullYear();
  var years = [curYear, curYear - 1, curYear - 2, curYear - 3];

  var [year, setYear]       = useState(curYear);
  var [quarter, setQuarter] = useState("all");
  var [month, setMonth]     = useState(now.getMonth());
  var [rangeFrom, setRangeFrom] = useState("");     // "YYYY-MM-DD" | ""
  var [rangeTo, setRangeTo]     = useState("");
  var [team, setTeam]       = useState("");
  var [state, setState]     = useState({ loading:true, data:null, error:null });
  var [openAgent, setOpenAgent] = useState(null);   // agentId | "unassigned" | null
  var [detail, setDetail]   = useState({ loading:false, error:null, rows:[] });
  var [exporting, setExporting] = useState(false);

  // A custom range is only ACTIVE when both ends are set — a half-filled pair is
  // somebody mid-typing, and silently switching the whole report on the first
  // keystroke would make the second one look like it changed nothing.
  var customOn = !!(rangeFrom && rangeTo);
  var pickMonth   = function(m){ setMonth(m); if (m !== "all") setQuarter("all"); };
  var pickQuarter = function(q){ setQuarter(q); if (q !== "all") setMonth("all"); };

  var filters = { year:year, quarter:quarter, month:month, team:team,
                  from: customOn ? rangeFrom : "", to: customOn ? rangeTo : "" };
  var qs = reportMeetingsQuery(filters);

  useEffect(function(){
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading:true, error:null }); });
    setOpenAgent(null);                    // a new period invalidates the open row
    apiFetch("/api/reports/meetings-by-agent?" + qs, "GET", null, p.token, p.csrfToken)
      .then(function(d){ if (!aborted) setState({ loading:false, data:d, error:null }); })
      .catch(function(e){ if (!aborted) setState({ loading:false, data:null, error:(e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [qs, p.token, p.csrfToken]);

  // The expand row's own fetch — same endpoint, same filters, detail=1.
  useEffect(function(){
    if (!openAgent) { setDetail({ loading:false, error:null, rows:[] }); return; }
    var aborted = false;
    setDetail({ loading:true, error:null, rows:[] });
    apiFetch("/api/reports/meetings-by-agent?" + qs + "&detail=1&agentId=" + encodeURIComponent(openAgent),
             "GET", null, p.token, p.csrfToken)
      .then(function(d){ if (!aborted) setDetail({ loading:false, error:null, rows:(d && d.rows) || [] }); })
      .catch(function(e){ if (!aborted) setDetail({ loading:false, error:(e && e.message) || "Failed to load meetings", rows:[] }); });
    return function(){ aborted = true; };
  }, [openAgent, qs, p.token, p.csrfToken]);

  var rows   = (state.data && state.data.rows) || [];
  var totals = (state.data && state.data.totals) || { total:0, leadMeetings:0, drMeetings:0, agents:0, withMeetings:0 };
  var label  = (state.data && state.data.range && state.data.range.label) || "";
  var rowKeyOf = function(r){ return r.agentId || "unassigned"; };

  // ---- EXPORT ---------------------------------------------------------------
  // Declared in verify-export-completeness.js as `server-aggregate`: both sheets
  // are re-fetched from the endpoint under the active filters, never mapped off
  // `rows`/`detail`, so the file is the whole result set even though the table
  // happens to be showing all of it today.
  var runMeetingsReportExport = async function(){
    if (exporting) return;
    setExporting(true);
    try {
      var XLSX = await loadXLSX();
      var agg = await apiFetch("/api/reports/meetings-by-agent?" + qs, "GET", null, p.token, p.csrfToken);
      var det = await apiFetch("/api/reports/meetings-by-agent?" + qs + "&detail=1", "GET", null, p.token, p.csrfToken);
      var aggRows = (agg && agg.rows) || [];
      // EVERY row, zeros included — the sheet is the table, and an export that
      // quietly drops the people who held nothing answers a different question
      // than the screen it came from. No .filter() here on purpose.
      var perAgent = aggRows.map(function(r){
        return { "Agent": r.name || "Unassigned", "Total": r.total || 0,
                 "Leads": r.leadMeetings || 0, "DR": r.drMeetings || 0,
                 "Status": rmStatusWord(r) };
      });
      // The totals line the table renders, written as a row so the sheet totals
      // without the reader re-adding the column.
      var at = (agg && agg.totals) || { total:0, leadMeetings:0, drMeetings:0 };
      perAgent.push({ "Agent":"TOTAL", "Total":at.total || 0, "Leads":at.leadMeetings || 0,
                      "DR":at.drMeetings || 0, "Status":"" });

      var detCols = RM_SHEET2_COLS;
      var detRows = ((det && det.rows) || []).map(function(m){
        return { "Agent": m.agentName || "Unassigned",
                 "Date": m.dayBucket || fmtRmDate(m.date),
                 "Client": m.clientName || "",
                 "Project": m.project || "",
                 "Source": m.sourceType === "dailyRequest" ? "Daily Request" : "Lead" };
      });

      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(perAgent, { header:RM_SHEET1_COLS }), "Meetings per person");
      // Explicit `header` so sheet 2 still carries its column row when the range
      // holds no meetings — an empty period is a normal answer, not an error.
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detRows, { header:detCols }), "Meeting detail");
      var stamp = (agg && agg.range) ? (agg.range.from + "_" + agg.range.to) : new Date().toISOString().slice(0,10);
      XLSX.writeFile(wb, "meetings_per_person_" + stamp + ".xlsx");
    } catch (e) { alert((e && e.message) || "Export failed"); }
    setExporting(false);
  };
  // Hand the button up to ReportsPage's toolbar, which owns the Export Excel
  // chrome for every tab. Sent through a ref-setter rather than lifted state:
  // the parent only ever CALLS it, so re-rendering the toolbar on every keystroke
  // in this body would be work for nothing.
  var onExportRef = p.onExportReady;
  useEffect(function(){
    if (!onExportRef) return;
    onExportRef({ run: runMeetingsReportExport, busy: exporting, ready: !state.loading && !state.error });
    return function(){ onExportRef(null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exporting, state.loading, state.error, qs]);

  // Roster picker (owner rule 2026-10-01): the users roster through REPORTS_ROSTER.
  var teamOptions = REPORTS_ROSTER.people(p.users, ["manager", "team_leader"]);

  var thStyle = { fontSize:11, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em", textAlign:"left", padding:"8px 12px", borderBottom:"1px solid #E2E8F0", fontWeight:600, whiteSpace:"nowrap" };
  var tdStyle = { fontSize:13, padding:"9px 12px", borderBottom:"1px solid #F1F5F9", color:C.text };
  var tdNum   = Object.assign({}, tdStyle, { textAlign:"right", fontVariantNumeric:"tabular-nums" });
  var qBtn = function(q){
    // A quarter chip lights only when nothing narrower is active — a month, or a
    // custom range, both of which outrank it on the server too.
    var active = !customOn && (month === "all") && (quarter === q);
    return <button key={q} type="button" disabled={customOn} onClick={function(){ pickQuarter(q); }}
      style={{ padding:"5px 12px", borderRadius:8, border:"1px solid",
        borderColor: active ? C.accent : "#E2E8F0",
        background: active ? C.accent + "12" : "#fff",
        color: active ? C.accent : (customOn ? "#CBD5E1" : C.textLight),
        fontSize:12, fontWeight:600, cursor: customOn ? "not-allowed" : "pointer" }}>{q === "all" ? "All" : q}</button>;
  };

  // The small badge beside an off-roster name. Never rendered for a roster row:
  // a badge on everybody is decoration, and the point of this one is that it is
  // rare enough to mean something.
  var statusBadge = function(r){
    var st = rmRowStatus(r);
    if (!st) return null;
    return <span title={RM_STATUS_TITLE[st.key]} style={{ marginInlineStart:6, fontSize:9.5, fontWeight:700,
      padding:"1px 6px", borderRadius:20, background:st.bg, color:st.fg,
      textTransform:"uppercase", letterSpacing:"0.04em", verticalAlign:"middle" }}>{st.label}</span>;
  };
  // The source, spelled out. "dailyRequest" is a wire value, never a label.
  var srcLabel = function(st){
    var lead = st === "lead";
    return <span style={{ fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:20,
      background: lead ? "#EFF6FF" : "#F0FDF4", color: lead ? "#1D4ED8" : "#15803D" }}>{lead ? "Lead" : "Daily Request"}</span>;
  };
  var sourceChip = function(kind, n){
    var on = kind === "lead";
    return <span style={{ fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:20,
      background: on ? "#EFF6FF" : "#F0FDF4", color: on ? "#1D4ED8" : "#15803D" }}>{n} {on ? "Lead" : "DR"}</span>;
  };

  var renderDetail = function(r){
    if (detail.loading) return <div style={{ padding:"16px 0", textAlign:"center", color:C.textLight, fontSize:12 }}>Loading meetings…</div>;
    if (detail.error)   return <div style={{ padding:"10px 12px", background:"#FEE2E2", border:"1px solid #FCA5A5", color:"#B91C1C", borderRadius:8, fontSize:12, fontWeight:600 }}>⚠️ {detail.error}</div>;
    if (!detail.rows.length) return <div style={{ padding:"16px 0", textAlign:"center", color:C.textLight, fontSize:12 }}>No meetings for this agent in {label}.</div>;
    return <div style={{ overflowX:"auto" }}><table style={{ width:"100%", borderCollapse:"collapse", minWidth:420 }}>
      <thead><tr style={{ background:"#F8FAFC" }}>
        {["Date","Client","Project","Source"].map(function(h){
          return <th key={h} style={{ textAlign:"left", padding:"8px 10px", fontSize:10, fontWeight:600, color:C.textLight, whiteSpace:"nowrap" }}>{h}</th>;
        })}
      </tr></thead>
      <tbody>{detail.rows.map(function(m){
        return <tr key={m._id} style={{ borderTop:"1px solid #F1F5F9" }}>
          <td style={{ padding:"8px 10px", fontSize:12, color:C.textLight, whiteSpace:"nowrap" }}>{m.dayBucket || fmtRmDate(m.date)}</td>
          <td style={{ padding:"8px 10px", fontSize:12, fontWeight:600, color:C.text }}>{m.clientName || "—"}</td>
          <td style={{ padding:"8px 10px", fontSize:12, color:C.textLight }}>{m.project || "—"}</td>
          <td style={{ padding:"8px 10px", fontSize:12 }}>{srcLabel(m.sourceType)}</td>
        </tr>;
      })}</tbody>
    </table></div>;
  };

  return <div>
    <Card style={{ marginBottom:16, padding:"12px 14px" }}>
      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
        <span style={{ fontSize:11, fontWeight:600, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em" }}>Period</span>
        <Select value={String(year)} onChange={function(e){ setYear(Number(e.target.value)); }} width={90} maxWidth={90}
          options={years.map(function(y){ return { value:String(y), label:String(y) }; })}/>
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>{["all","Q1","Q2","Q3","Q4"].map(qBtn)}</div>
        <Select value={String(month)} onChange={function(e){ var v = e.target.value; pickMonth(v === "all" ? "all" : Number(v)); }}
          searchable width={130} maxWidth={130} disabled={customOn}
          options={[{ value:"all", label:"All months" }].concat(RM_MONTHS.map(function(mn, i){ return { value:String(i), label:mn }; }))}/>

        <div style={{ width:1, height:22, background:"#E2E8F0" }}/>

        {/* 📅 Date (owner 2026-10-31): the shared calendar chip, LAST after the
            year / quarter / month controls, in place of the From → To inputs and
            their Clear. meetingsReportDateChip writes the same rangeFrom / rangeTo
            (inclusive; one day = both ends); the picker's Clear blanks both. */}
        <DashRangePicker isMobile={p.isMobile} iconOnly={p.isMobile} todayYmd={cairoTodayIso()} min=""
          value={meetingsReportDateChip.value({ rangeFrom:rangeFrom, rangeTo:rangeTo })}
          active={meetingsReportDateChip.active({ rangeFrom:rangeFrom, rangeTo:rangeTo })}
          triggerStyle={periodChipStyle(customOn, PERIOD_ACCENT_BLUE, p.isMobile)}
          onPick={function(a, b){ meetingsReportDateChip.pick(a, b, { setRangeFrom:setRangeFrom, setRangeTo:setRangeTo }); }}/>

        <div style={{ width:1, height:22, background:"#E2E8F0" }}/>

        <Select value={team} onChange={function(e){ setTeam(e.target.value); }}
          searchable width={150} maxWidth={150} fontSize={11}
          options={[{ value:"", label:"All teams" }].concat(teamOptions.map(function(u){
            return { value:gid(u), label:u.name + " (" + (positionOf(u)==="manager"?"Manager":"Team Leader") + ")" }; }))}/>

        <div style={{ flex:1, minWidth:8 }}/>
        <span style={{ fontSize:11, color:C.textLight }}>{label}{customOn ? " · custom range" : ""}</span>
      </div>
    </Card>

    {state.error && <Card style={{ marginBottom:16, padding:"12px 14px", background:"#FEE2E2", border:"1px solid #FCA5A5" }}>
      <span style={{ color:"#B91C1C", fontSize:12, fontWeight:600 }}>⚠️ {state.error}</span>
    </Card>}

    <Card style={{ padding:0, overflow:"hidden" }}>
      <div style={{ padding:"14px 16px 10px", display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:C.text }}>🤝 Meetings per person</div>
          <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>
            {label}{team ? " · one team" : ""} · click a name for that agent's meetings
          </div>
        </div>
        {!state.loading && !state.error && <div style={{ fontSize:12, fontWeight:700, color:C.accent, background:C.accent+"14", padding:"3px 10px", borderRadius:20 }}>
          {/* TWO numbers, not one. "28 agents" beside a meeting count reads as
              28 people having met somebody; the table now lists everyone who was
              EXPECTED to. */}
          {totals.total} {totals.total === 1 ? "meeting" : "meetings"} · {(totals.withMeetings != null ? totals.withMeetings : totals.agents)} of {totals.agents} {totals.agents === 1 ? "agent" : "agents"}
        </div>}
      </div>

      {state.loading
        ? <div style={{ padding:"28px 12px", textAlign:"center", color:C.textLight, fontSize:13 }}>Loading…</div>
        : p.isMobile
        /* MOBILE — a card list, because four numeric columns plus an expand row
           do not survive a 360px viewport as a table. Same data, same toggle. */
        ? <div style={{ padding:"0 12px 14px" }}>
            {rows.length === 0
              ? <div style={{ padding:"24px 0", textAlign:"center", color:C.textLight, fontSize:13, fontStyle:"italic" }}>No agents to report on for this period</div>
              : rows.map(function(r){
                  var k = rowKeyOf(r); var open = openAgent === k;
                  return <div key={k} style={{ border:"1px solid #E8ECF1", borderRadius:10, padding:"10px 12px", marginBottom:8,
                      background: open ? "#F8FAFC" : "#fff", opacity: r.total > 0 ? 1 : 0.72,
                      cursor: r.total > 0 ? "pointer" : "default" }}
                    onClick={function(){ if (r.total > 0) setOpenAgent(open ? null : k); }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.name}{statusBadge(r)}</div>
                        <div style={{ fontSize:10, color:C.textLight, marginTop:2 }}>{rmRoleLabel(r)}</div>
                      </div>
                      {/* A zero is grey, not gold. It still PRINTS — the whole
                          point is that it is on the page — but it must not read
                          as an achievement at a glance. */}
                      <div style={{ fontSize:20, fontWeight:800, color: r.total > 0 ? C.accent : "#CBD5E1", fontVariantNumeric:"tabular-nums" }}>{r.total}</div>
                    </div>
                    {r.total > 0
                      ? <div style={{ display:"flex", gap:6, marginTop:8 }}>
                          {sourceChip("lead", r.leadMeetings)}
                          {sourceChip("dr", r.drMeetings)}
                        </div>
                      : <div style={{ fontSize:11, color:C.textLight, marginTop:8, fontStyle:"italic" }}>No meetings in {label}</div>}
                    {open && <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid #E8ECF1" }} onClick={function(e){ e.stopPropagation(); }}>{renderDetail(r)}</div>}
                  </div>;
                })}
            {rows.length > 0 && <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 12px", borderTop:"2px solid #E2E8F0", fontSize:13, fontWeight:700, color:C.text }}>
              <span>Total</span>
              <span style={{ fontVariantNumeric:"tabular-nums" }}>{totals.total} ({totals.leadMeetings} L · {totals.drMeetings} DR)</span>
            </div>}
          </div>
        : <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:420 }}>
              <thead><tr>
                <th style={thStyle}>{REPORT_MEETING_COLS[0]}</th>
                <th style={Object.assign({}, thStyle, { textAlign:"right" })}>{REPORT_MEETING_COLS[1]}</th>
                <th style={Object.assign({}, thStyle, { textAlign:"right" })}>{REPORT_MEETING_COLS[2]}</th>
                <th style={Object.assign({}, thStyle, { textAlign:"right" })}>{REPORT_MEETING_COLS[3]}</th>
              </tr></thead>
              <tbody>
                {rows.length === 0
                  ? <tr><td colSpan={REPORT_MEETING_COLS.length} style={Object.assign({}, tdStyle, { textAlign:"center", color:C.textLight, fontStyle:"italic", padding:"28px 12px" })}>No agents to report on for this period</td></tr>
                  : rows.map(function(r){
                      var k = rowKeyOf(r); var open = openAgent === k;
                      var hasAny = r.total > 0;
                      return <Fragment key={k}>
                        {/* A ZERO ROW IS NOT CLICKABLE. Its expand row could only
                            ever say "no meetings", which the 0 already says — and a
                            caret that opens an empty panel teaches the reader that
                            the carets are not worth pressing. */}
                        <tr onClick={function(){ if (hasAny) setOpenAgent(open ? null : k); }}
                          style={{ cursor: hasAny ? "pointer" : "default",
                                   background: open ? "#EFF6FF" : "transparent",
                                   color: hasAny ? C.text : C.textLight }}>
                          <td style={Object.assign({}, tdStyle, { fontWeight:600, whiteSpace:"nowrap" })}>
                            <span style={{ display:"inline-block", width:14, color:C.textLight }}>{hasAny ? (open ? "▾" : "▸") : ""}</span>
                            {r.name}
                            {statusBadge(r)}
                            <span style={{ color:C.textLight, fontSize:11, fontWeight:500 }}> · {rmRoleLabel(r)}</span>
                          </td>
                          <td style={Object.assign({}, tdNum, { fontWeight:700, color: hasAny ? C.text : "#CBD5E1" })}>{r.total}</td>
                          <td style={Object.assign({}, tdNum, hasAny ? null : { color:"#CBD5E1" })}>{r.leadMeetings}</td>
                          <td style={Object.assign({}, tdNum, hasAny ? null : { color:"#CBD5E1" })}>{r.drMeetings}</td>
                        </tr>
                        {/* The expand-below row, spanning every column — colSpan is
                            DERIVED from the header array, as on Brokers/EOI/Deals,
                            so adding a column cannot leave it one short. */}
                        {open && <tr><td colSpan={REPORT_MEETING_COLS.length} style={{ padding:"8px 12px", background:C.bg }}>
                          <div style={{ background:"#fff", padding:"12px 14px" }}>
                            <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:10 }}>
                              🤝 {r.name} — {r.total} {r.total === 1 ? "meeting" : "meetings"} in {label}
                            </div>
                            {renderDetail(r)}
                          </div>
                        </td></tr>}
                      </Fragment>;
                    })}
              </tbody>
              {rows.length > 0 && <tfoot><tr style={{ background:"#F8FAFC" }}>
                <td style={Object.assign({}, tdStyle, { fontWeight:800, borderTop:"2px solid #E2E8F0", borderBottom:"none" })}>Total</td>
                <td style={Object.assign({}, tdNum, { fontWeight:800, borderTop:"2px solid #E2E8F0", borderBottom:"none" })}>{totals.total}</td>
                <td style={Object.assign({}, tdNum, { fontWeight:800, borderTop:"2px solid #E2E8F0", borderBottom:"none" })}>{totals.leadMeetings}</td>
                <td style={Object.assign({}, tdNum, { fontWeight:800, borderTop:"2px solid #E2E8F0", borderBottom:"none" })}>{totals.drMeetings}</td>
              </tr></tfoot>}
            </table>
          </div>}
    </Card>
  </div>;
};

var LB_MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// Leaderboard "Include managers" filter (FIX 3) — sales-role agents are ALWAYS
// shown and are never listed/toggleable. Managers / team_leaders are OFF by
// default and opted-in here. Persisted as an array of INCLUDED manager/TL
// agentIds (default = none => sales-only view); a manager not in the saved set
// defaults to EXCLUDED. Display-only; never affects calculations.
var LB_MGR_KEY = "crm_lb_included_mgrs";
function lbLoadMgrSet(){
  try {
    var raw = localStorage.getItem(LB_MGR_KEY);
    if (!raw) return {};
    var parsed = JSON.parse(raw);
    var m = {};
    if (Array.isArray(parsed)) parsed.forEach(function(id){ m[String(id)] = true; });
    else if (parsed && typeof parsed === "object") Object.keys(parsed).forEach(function(id){ if (parsed[id]) m[String(id)] = true; });
    return m;
  } catch (e) { return {}; }
}
function lbSaveMgrSet(m){
  try { localStorage.setItem(LB_MGR_KEY, JSON.stringify(Object.keys(m || {}))); } catch (e) {}
}
// THE PICKER LISTS THE ROSTER, THE TABLE LISTS THE PERIOD (owner ruling
// 2026-10-01). The "Include managers" list came from the period's result rows,
// which drop zero-activity people and hide managers until selected — so a
// quarter with no manager activity said "No managers or team leaders in range"
// while "All" listed them. Circular. Now:
//   pickerAgents — every active TL / manager from the endpoint's `mgrRoster`
//                  (the users roster, period-independent), alphabetical;
//   included     — every sales row for the period, plus each opted-in manager's
//                  row for the period, or a ZERO row built from the roster when
//                  the period has none for them (0 is a valid result, shown as 0).
// Module scope and pure so the table, the export ("the sheet is the table") and
// verify-leaderboard-roster-picker.js all run the ONE composition. `mgrRoster`
// absent (a response from a server built before this) falls back to the
// managers present in `agents`, which is exactly the old behaviour.
// ===== ONE ROSTER HELPER FOR EVERY REPORTS PICKER (owner rule 2026-10-01) =====
// A picker under Reports that lists people, teams or sources is populated from
// the ROSTER — the users list (active ones), a server roster the endpoint built
// from users, or the sources catalog plus the whole book's distinct sources —
// never from the current period's result rows. An option with 0 rows in the
// period is still listed and shows 0 when selected. Every Reports picker builds
// its options HERE, so the rule has one implementation and one gate
// (verify-leaderboard-roster-picker.js §5 RUNS it and counts the call sites).
//   people(items, positions, keepOrder) — drops inactive (active === false),
//     keeps only the given positions (null = any), sorts by name unless the
//     caller's server order is meaningful (keepOrder, e.g. revenue-sorted).
//   sources(catalog, extra) — the SOURCES catalog ∪ the whole-book distinct
//     sources (/api/filter-options leadSources), sorted, deduped.
var REPORTS_ROSTER = {
  people: function(items, positions, keepOrder){
    var out = (items || []).filter(function(u){
      if (!u || u.active === false) return false;
      return !positions || positions.indexOf(positionOf(u)) !== -1;
    });
    if (!keepOrder) out = out.slice().sort(function(a, b){ return String(a.name || "").localeCompare(String(b.name || "")); });
    return out;
  },
  sources: function(catalog, extra){
    var set = {};
    (catalog || []).forEach(function(v){ if (v) set[v] = true; });
    (extra || []).forEach(function(v){ if (typeof v === "string" && v.trim()) set[v] = true; });
    return Object.keys(set).sort();
  }
};
var LB_ROLES = { sales: true, team_leader: true, manager: true };
var lbIsMgr = function(a){ var pos = positionOf(a); return pos === "manager" || pos === "team_leader"; };
function lbComposeRows(agents, mgrRoster, includedMgrs) {
  var rows = (agents || []).filter(function(a){ return a && LB_ROLES[positionOf(a)]; });
  var byId = {}; rows.forEach(function(a){ byId[String(a.agentId)] = a; });
  var pickerAgents = REPORTS_ROSTER.people(Array.isArray(mgrRoster) ? mgrRoster : rows, ["manager", "team_leader"]);
  var included = rows.filter(function(a){ return positionOf(a) === "sales"; });
  var includedMgrCount = 0;
  pickerAgents.forEach(function(m){
    var id = String(m.agentId);
    if (!includedMgrs || !includedMgrs[id]) return;
    includedMgrCount++;
    included.push(byId[id] || { agentId: id, name: m.name || "(unknown)", role: m.role || "", titleRank: m.titleRank,
      active: true, leads: 0, calls: 0, meetings: 0, eois: 0, deals: 0, revenue: 0, conversionPct: null, qTarget: 0, targetProgressPct: 0, _zeroRow: true });
  });
  return { pickerAgents: pickerAgents, included: included, includedMgrCount: includedMgrCount };
}

// Export sheet columns, DECLARED at module scope (not a header literal at the
// call site) so verify-export-completeness.js can read them as data — same
// convention as RM_SHEET1_COLS/RM_SHEET2_COLS above.
var LB_SHEET1_COLS = ["Rank", "Agent", "Total (EGP)", "Deals", "Meetings", "Target %"];
var LB_SHEET2_COLS = ["Rank", "Agent", "EOIs"];

// 🏆 Sales Leaderboard tab (admin/operation only — inherits the ReportsPage
// role gate; no extra guard needed here). Two rankings from a SINGLE
// GET /api/reports/overview/agents call:
//   • Deals Leaderboard — agent-share revenue (budget × projectWeight, split
//     deals halved), DoneDeal status, sorted by revenue desc.
//   • Top EOI — Pending+Approved EOIs (cancelled excluded, deal-superset
//     included per the endpoint), sorted by eois desc.
// Roster is filtered client-side to sales + team_leader using the payload's
// `role` field (Decision C — adjust the LB_ROLES map if the desired roster
// changes). Period control mirrors the Deals page: Quarter buttons + Year
// select, plus a Month select. Month and Quarter are mutually exclusive.
var ReportsLeaderboardBody = function(p) {
  var now = new Date();
  var curYear = now.getFullYear();
  var years = [curYear, curYear - 1, curYear - 2, curYear - 3];

  var [year, setYear] = useState(curYear);
  var [quarter, setQuarter] = useState("all");
  var [month, setMonth] = useState(now.getMonth());
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [includedMgrs, setIncludedMgrs] = useState(lbLoadMgrSet);
  var [agentsPanelOpen, setAgentsPanelOpen] = useState(false);
  var [exporting, setExporting] = useState(false);
  // FIX A — smart default. autoRef stays true only while the initial auto-widen
  // chain (current month -> current quarter -> whole year) is allowed to run;
  // any manual period change turns it off. `resolving` keeps the loading state
  // visible across the widen hops so an empty month never flashes its zeros.
  var autoRef = useRef(true);
  var [resolving, setResolving] = useState(true);
  var curQuarter = (function(){ var m = now.getMonth(); return m < 3 ? "Q1" : m < 6 ? "Q2" : m < 9 ? "Q3" : "Q4"; })();

  // Roster gate + "is this agent shown" predicate (sales always; managers/TLs
  // only when opted in). Defined up here so the fetch's empty-check and the
  // render use the exact same membership rule.
  var isIncludedAgent = function(a){
    if (!a || !LB_ROLES[positionOf(a)]) return false;
    if (positionOf(a) === "sales") return true;
    return !!includedMgrs[String(a.agentId)];
  };
  // A period is "empty" when every SHOWN agent has 0 deals AND 0 EOIs.
  var agentsAllZero = function(agents){
    var arr = (agents || []).filter(isIncludedAgent);
    var tot = 0;
    for (var i = 0; i < arr.length; i++) { tot += (Number(arr[i].deals) || 0) + (Number(arr[i].eois) || 0); }
    return tot === 0;
  };

  // Month and Quarter are mutually exclusive — picking one clears the other.
  // Any manual pick also ends the smart-default auto-widen.
  var endAuto = function(){ autoRef.current = false; setResolving(false); };
  var pickMonth = function(m) { endAuto(); setMonth(m); if (m !== "all") setQuarter("all"); };
  var pickQuarter = function(q) { endAuto(); setQuarter(q); if (q !== "all") setMonth("all"); };

  // Active [from, to) window in ms for the endpoint (reportsParseRange needs from < to).
  var range = (function(){
    if (month !== "all") {
      return { from: new Date(year, month, 1).getTime(), to: new Date(year, month + 1, 1).getTime() };
    }
    if (quarter !== "all") {
      var qStart = { Q1: 0, Q2: 3, Q3: 6, Q4: 9 }[quarter];
      return { from: new Date(year, qStart, 1).getTime(), to: new Date(year, qStart + 3, 1).getTime() };
    }
    return { from: new Date(year, 0, 1).getTime(), to: new Date(year + 1, 0, 1).getTime() };
  })();

  // Hoisted out of the fetch effect (was local to it) so the export handler
  // below can reuse the EXACT same query string instead of re-deriving it —
  // one predicate, not two copies that could drift.
  var qs = "?from=" + range.from + "&to=" + range.to + "&limit=200";
  // A specific quarter (not "All", not a month) is exactly the case the
  // Starting-Date/overlap rule applies to — see the matching comment on
  // GET /api/reports/overview/agents in server.js.
  if (month === "all" && quarter !== "all") qs += "&quarterOverlap=1";

  useEffect(function(){
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    apiFetch("/api/reports/overview/agents" + qs, "GET", null, p.token)
      .then(function(d){
        if (aborted) return;
        setState({ loading: false, data: d, error: null });
        if (!autoRef.current) return;                       // manual selection — never auto-widen
        if (!agentsAllZero(d && d.agents)) { autoRef.current = false; setResolving(false); return; }
        // Empty period: widen once (current month -> current quarter -> whole
        // year), at most 2 extra fetches. Keep `resolving` true so the grid
        // never flashes an all-zero table between hops.
        if (month !== "all") { setMonth("all"); setQuarter(curQuarter); return; }
        if (quarter !== "all") { setQuarter("all"); return; }
        autoRef.current = false; setResolving(false);       // already whole-year and still empty
      })
      .catch(function(e){
        if (aborted) return;
        autoRef.current = false; setResolving(false);
        setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" });
      });
    return function(){ aborted = true; };
  }, [range.from, range.to]);

  // Roster = pooled agents (sales + TL + manager). Picker lists ONLY managers/
  // TLs (alphabetical). Rankings include every sales agent plus any opted-in
  // manager/TL (isIncludedAgent, defined above).
  // ONE composition for picker + rows (lbComposeRows, module scope): the picker
  // is the roster, the rows are the period, a selected manager with no rows is 0.
  var composed = lbComposeRows(state.data && state.data.agents, state.data && state.data.mgrRoster, includedMgrs);
  var pickerAgents = composed.pickerAgents, included = composed.included, includedMgrCount = composed.includedMgrCount;
  var mgrRoster = pickerAgents;

  var setMgrs = function(next){ lbSaveMgrSet(next); setIncludedMgrs(next); };
  var toggleMgr = function(id){
    var key = String(id);
    var next = Object.assign({}, includedMgrs);
    if (next[key]) delete next[key]; else next[key] = true;
    setMgrs(next);
  };
  var selectAllMgrs = function(){
    var next = {};
    mgrRoster.forEach(function(a){ next[String(a.agentId)] = true; });
    setMgrs(next);
  };
  var clearAllMgrs = function(){ setMgrs({}); };

  var dealRows = included.slice().sort(function(a, b){
    return (b.revenue - a.revenue) || (b.deals - a.deals) || String(a.name || "").localeCompare(String(b.name || ""));
  });
  var eoiRows = included.slice().sort(function(a, b){
    return (b.eois - a.eois) || (b.revenue - a.revenue) || String(a.name || "").localeCompare(String(b.name || ""));
  });

  // ---- EXPORT ---------------------------------------------------------------
  // Declared in verify-export-completeness.js as `singleFetch` server-aggregate
  // (a new shape beside Meetings' detail=1 one): the handler RE-FETCHES
  // /api/reports/overview/agents under `qs` — the exact same query string the
  // on-screen table just fetched with, never `state.data`/`included`/
  // `dealRows`/`eoiRows`, the already-rendered arrays. Both sheets come from
  // that ONE fresh response, just sorted two different ways — there is no
  // per-row detail endpoint to walk here, unlike Meetings' per-agent detail.
  // The response is then composed by lbComposeRows — sales always, opted-in
  // managers/TLs with a zero row when the period has none for them — the SAME
  // composition the table renders with, so the file matches what the owner is
  // looking at, not the raw roster the endpoint returns. Every row it keeps is
  // written, zeros included: "the sheet is the table," same law as Meetings'.
  var runLeaderboardExport = async function(){
    if (exporting) return;
    setExporting(true);
    try {
      var XLSX = await loadXLSX();
      var resp = await apiFetch("/api/reports/overview/agents" + qs, "GET", null, p.token);
      var freshAgents = lbComposeRows(resp && resp.agents, resp && resp.mgrRoster, includedMgrs).included;

      var freshDealRows = freshAgents.slice().sort(function(a, b){
        return (b.revenue - a.revenue) || (b.deals - a.deals) || String(a.name || "").localeCompare(String(b.name || ""));
      });
      var freshEoiRows = freshAgents.slice().sort(function(a, b){
        return (b.eois - a.eois) || (b.revenue - a.revenue) || String(a.name || "").localeCompare(String(b.name || ""));
      });

      var dealSheetRows = freshDealRows.map(function(a, idx){
        return { "Rank": idx + 1, "Agent": a.name || "(unknown)",
                 "Total (EGP)": Number(a.revenue) || 0, "Deals": Number(a.deals) || 0,
                 "Meetings": Number(a.meetings) || 0,
                 "Target %": (a.qTarget > 0) ? ((a.targetProgressPct || 0) + "%") : "—" };
      });
      var eoiSheetRows = freshEoiRows.map(function(a, idx){
        return { "Rank": idx + 1, "Agent": a.name || "(unknown)", "EOIs": Number(a.eois) || 0 };
      });

      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dealSheetRows, { header: LB_SHEET1_COLS }), "Deals Leaderboard");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(eoiSheetRows, { header: LB_SHEET2_COLS }), "Top EOI");
      var stamp = (resp && resp.range) ? (resp.range.from + "_" + resp.range.to) : new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, "leaderboard_" + stamp + ".xlsx");
    } catch (e) { alert((e && e.message) || "Export failed"); }
    setExporting(false);
  };
  // Hand the button up to ReportsPage's toolbar, which owns the Export Excel
  // chrome for every tab — same ref-setter convention as Meetings.
  var onExportRef = p.onExportReady;
  useEffect(function(){
    if (!onExportRef) return;
    onExportRef({ run: runLeaderboardExport, busy: exporting, ready: !state.loading && !state.error });
    return function(){ onExportRef(null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exporting, state.loading, state.error, qs, includedMgrs]);

  var medal = function(rank, value){
    if (value > 0 && rank === 1) return "🥇";
    if (value > 0 && rank === 2) return "🥈";
    if (value > 0 && rank === 3) return "🥉";
    return String(rank);
  };

  var periodLabel = month !== "all" ? (LB_MONTHS[month] + " " + year)
    : quarter !== "all" ? (quarter + " " + year)
    : ("Full year " + year);

  var thStyle = { fontSize:11, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em", textAlign:"left", padding:"8px 12px", borderBottom:"1px solid #E2E8F0", fontWeight:600, whiteSpace:"nowrap" };
  var tdStyle = { fontSize:13, padding:"9px 12px", borderBottom:"1px solid #F1F5F9", color:C.text };
  var tdNum = Object.assign({}, tdStyle, { textAlign:"right", fontVariantNumeric:"tabular-nums" });

  var qBtn = function(q){
    // FIX C — a quarter chip (incl. "All") is highlighted only when no month is
    // active, so a selected month never leaves the quarter row falsely lit.
    var active = (month === "all") && (quarter === q);
    return <button key={q} type="button" onClick={function(){ pickQuarter(q); }}
      style={{ padding:"5px 12px", borderRadius:8, border:"1px solid", borderColor: active ? C.accent : "#E2E8F0",
        background: active ? C.accent + "12" : "#fff", color: active ? C.accent : C.textLight,
        fontSize:12, fontWeight:600, cursor:"pointer" }}>{q === "all" ? "All" : q}</button>;
  };

  var renderRankTable = function(opts){
    var rows = opts.rows;
    return <Card style={{ padding:0, overflow:"hidden" }}>
      <div style={{ padding:"14px 16px 10px" }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{opts.title}</div>
        <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>{opts.subtitle}</div>
      </div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", minWidth:300 }}>
          <thead><tr>
            <th style={Object.assign({}, thStyle, { width:44, textAlign:"center" })}>#</th>
            <th style={thStyle}>Agent</th>
            {opts.cols.map(function(c, i){ return <th key={i} style={Object.assign({}, thStyle, { textAlign: c.align || "right" })}>{c.header}</th>; })}
          </tr></thead>
          <tbody>
            {rows.length === 0
              ? <tr><td colSpan={2 + opts.cols.length} style={Object.assign({}, tdStyle, { textAlign:"center", color:C.textLight, fontStyle:"italic", padding:"28px 12px" })}>No agents for this period</td></tr>
              : rows.map(function(a, idx){
                  var rank = idx + 1;
                  var mv = a[opts.metricKey] || 0;
                  return <tr key={a.agentId || idx}>
                    <td style={Object.assign({}, tdStyle, { textAlign:"center", fontSize:15 })}>{medal(rank, mv)}</td>
                    <td style={Object.assign({}, tdStyle, { fontWeight:600, whiteSpace:"nowrap" })}>{a.name || "(unknown)"}</td>
                    {opts.cols.map(function(c, i){ return <td key={i} style={Object.assign({}, tdNum, { textAlign: c.align || "right" })}>{c.render(a)}</td>; })}
                  </tr>;
                })}
          </tbody>
        </table>
      </div>
    </Card>;
  };

  var dealsCard = renderRankTable({
    title: "💰 Deals Leaderboard",
    subtitle: "Total deal value (EGP) · " + periodLabel,
    rows: dealRows, metricKey: "revenue",
    cols: [
      { header: "Total (EGP)", align:"right", render: function(a){ return fmtEGPRaw(a.revenue); } },
      { header: "Deals",       align:"right", render: function(a){ return a.deals || 0; } },
      { header: "Meetings",    align:"right", render: function(a){ return a.meetings || 0; } },
      { header: "Target",      align:"right", render: function(a){ return (a.qTarget > 0) ? ((a.targetProgressPct || 0) + "%") : "—"; } }
    ]
  });
  var eoiCard = renderRankTable({
    title: "📝 Top EOI",
    subtitle: "EOI count · " + periodLabel,
    rows: eoiRows, metricKey: "eois",
    cols: [ { header: "EOIs", align:"right", render: function(a){ return a.eois || 0; } } ]
  });

  return <div>
    <style>{".lb-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px;}@media (max-width:720px){.lb-grid{grid-template-columns:1fr;}}"}</style>

    <Card style={{ marginBottom:16, padding:"12px 14px" }}>
      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
        <span style={{ fontSize:11, fontWeight:600, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em" }}>Period</span>
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>{["all","Q1","Q2","Q3","Q4"].map(qBtn)}</div>
        <Select value={String(month)} onChange={function(e){ var v = e.target.value; pickMonth(v === "all" ? "all" : Number(v)); }} searchable width={130} maxWidth={130}
          options={[{ value:"all", label:"All months" }].concat(LB_MONTHS.map(function(mn, i){ return { value:String(i), label:mn }; }))}/>
        <Select value={String(year)} onChange={function(e){ endAuto(); setYear(Number(e.target.value)); }} width={90} maxWidth={90}
          options={years.map(function(y){ return { value:String(y), label:String(y) }; })}/>

        <div style={{ position:"relative", display:"inline-flex", minWidth:0 }}>
          <button type="button" onClick={function(){ setAgentsPanelOpen(function(o){ return !o; }); }}
            style={{ padding:"5px 12px", borderRadius:8, border:"1px solid",
              borderColor: agentsPanelOpen ? C.accent : "#E2E8F0",
              background: agentsPanelOpen ? C.accent + "12" : "#fff",
              color: agentsPanelOpen ? C.accent : C.textLight,
              fontSize:12, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap" }}>
            ⚙️ Include managers{includedMgrCount > 0 ? " (" + includedMgrCount + ")" : ""}
          </button>
          {agentsPanelOpen && <>
            <div onClick={function(){ setAgentsPanelOpen(false); }} style={{ position:"fixed", inset:0, zIndex:40 }}/>
            <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, zIndex:50,
              width:"min(300px, 88vw)", maxWidth:"88vw", maxHeight:296, display:"flex", flexDirection:"column",
              background:"#fff", border:"1px solid #E2E8F0", borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,0.14)", overflow:"hidden" }}>
              <div style={{ flexShrink:0, display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, padding:"9px 12px", borderBottom:"1px solid #F1F5F9", background:"#FAFBFC" }}>
                <span style={{ fontSize:11, fontWeight:700, color:C.text, textTransform:"uppercase", letterSpacing:"0.04em" }}>Managers &amp; TLs</span>
                <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                  <button type="button" onClick={selectAllMgrs} style={{ padding:"3px 8px", borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", color:C.accent, fontSize:11, fontWeight:600, cursor:"pointer" }}>Select all</button>
                  <button type="button" onClick={clearAllMgrs} style={{ padding:"3px 8px", borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", color:C.textLight, fontSize:11, fontWeight:600, cursor:"pointer" }}>Clear</button>
                </div>
              </div>
              <div style={{ flexShrink:0, padding:"6px 12px", fontSize:10.5, color:C.textLight, background:"#FAFBFC", borderBottom:"1px solid #F1F5F9" }}>Sales agents are always shown.</div>
              <div style={{ flex:1, minHeight:0, overflowY:"auto", padding:"4px 0", WebkitOverflowScrolling:"touch" }}>
                {pickerAgents.length === 0
                  ? <div style={{ fontSize:12, color:C.textLight, padding:"16px 12px", textAlign:"center", fontStyle:"italic" }}>No active managers or team leaders</div>
                  : pickerAgents.map(function(a){
                      var checked = !!includedMgrs[String(a.agentId)];
                      return <label key={a.agentId} style={{ display:"flex", alignItems:"center", gap:9, padding:"7px 12px", cursor:"pointer", fontSize:13, color:C.text }}>
                        <input type="checkbox" checked={checked} onChange={function(){ toggleMgr(a.agentId); }} style={{ cursor:"pointer", flexShrink:0 }}/>
                        <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", minWidth:0 }}>{a.name || "(unknown)"}<span style={{ color:C.textLight, fontSize:11 }}>{positionOf(a) === "manager" ? " · Manager" : " · TL"}</span></span>
                      </label>;
                    })}
              </div>
            </div>
          </>}
        </div>

        <div style={{ flex:1, minWidth:8 }}/>
        <span style={{ fontSize:11, color:C.textLight }}>{periodLabel}</span>
      </div>
    </Card>

    {state.error && <Card style={{ padding:"14px 16px", marginBottom:16, color:"#DC2626", fontSize:13 }}>Couldn't load leaderboard: {state.error}</Card>}

    {state.error
      ? null
      : (resolving || (state.loading && !state.data))
        ? <div style={{ fontSize:13, color:C.textLight, padding:"32px 12px", textAlign:"center" }}>Loading leaderboard…</div>
        : agentsAllZero(state.data && state.data.agents)
          ? <Card style={{ padding:"40px 24px", textAlign:"center" }}>
              <div style={{ fontSize:28, marginBottom:8 }}>📭</div>
              <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:4 }}>No deals or EOIs in {periodLabel}</div>
              <div style={{ fontSize:12, color:C.textLight }}>Try a different month, quarter, or year using the filters above.</div>
            </Card>
          : <div className="lb-grid">{dealsCard}{eoiCard}</div>}
  </div>;
};

var ReportsOverviewBody = function(p) {
  var sections = [
    { key:"alerts",   title:"Alerts",            height:60 },
    { key:"kpis",     title:"KPI Cards",         height:120 },
    { key:"trends",   title:"Trends",            height:240 },
    { key:"funnel",   title:"Sales Funnel",      height:300 },
    { key:"sources",  title:"Source ROI",        height:220 },
    { key:"agents",   title:"Agent Leaderboard", height:340 },
    { key:"aging",    title:"Lead Aging",        height:120 },
    { key:"forecast", title:"Forecast",          height:160 }
  ];
  return <div>
    {sections.map(function(s){
      if (s.key === "kpis") return <KpiCardsRow key="kpis" filters={p.filters} token={p.token}/>;
      if (s.key === "trends") return <TrendsChart key="trends" filters={p.filters} token={p.token}/>;
      if (s.key === "funnel") return <SalesFunnel key="funnel" filters={p.filters} token={p.token}/>;
      if (s.key === "sources") return <SourceRoiList key="sources" filters={p.filters} token={p.token}/>;
      if (s.key === "agents") return <div key="agents" id="reports-agent-leaderboard">
        <AgentLeaderboard filters={p.filters} token={p.token}/>
      </div>;
      if (s.key === "aging") return <LeadAgingBuckets key="aging" filters={p.filters} token={p.token}
        nav={p.nav} setFilter={p.setFilter} setSpecialFilter={p.setSpecialFilter}/>;
      if (s.key === "alerts") return <AlertsBanner key="alerts" filters={p.filters} token={p.token}
        nav={p.nav} setFilter={p.setFilter} setSpecialFilter={p.setSpecialFilter}
        setReportsSource={p.setReportsSource}/>;
      if (s.key === "forecast") return <ForecastCard key="forecast" filters={p.filters} token={p.token}/>;
      return <Card key={s.key} style={{ marginBottom:14, padding:"14px 16px", minHeight:s.height, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", background:"#FAFBFC", border:"1px dashed #E2E8F0" }}>
        <div style={{ fontSize:13, fontWeight:600, color:C.textLight }}>{s.title}</div>
        <div style={{ fontSize:11, color:"#94A3B8", marginTop:4 }}>Section in development</div>
      </Card>;
    })}
  </div>;
};

var PipelineKpiCard = function(p) {
  var c = p.card;
  return <Card style={{ padding:"14px 16px", minHeight:96 }}>
    <div style={{ fontSize:11, color:C.textLight, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", display:"flex", alignItems:"center", gap:6 }}>
      <span>{c.label}</span>
      {c.tooltip && <span title={c.tooltip} style={{ cursor:"help", color:"#94A3B8", fontSize:10, fontWeight:700 }}>ⓘ</span>}
    </div>
    {p.skeleton
      ? <div style={{ height:24, marginTop:10, borderRadius:4, background:"#F1F5F9", width:"60%" }}/>
      : <div style={{ fontSize:22, fontWeight:800, color:c.color, marginTop:6 }}>{c.value}</div>}
    {!p.skeleton && c.hint && <div style={{ fontSize:10, color:C.textLight, marginTop:4 }}>{c.hint}</div>}
  </Card>;
};

var PipelineKpiRow = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var f = p.filters;

  useEffect(function(){
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/pipeline/kpis" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.from, f.to, f.team, f.source]);

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load Pipeline KPIs: {state.error}</div>
    </Card>;
  }

  var skel = state.loading || !state.data;
  var k = state.data || {};
  var pv = k.pipelineValue || {};
  var wf = k.weightedForecast || {};
  var wr = k.winRatePct || {};
  var vel = k.avgVelocityDays || {};
  var wrSample = wr.sample || {};

  var fmtPct = function(v){ return (Number(v) || 0).toFixed(1) + "%"; };
  var fmtDays = function(v){ var n = Number(v) || 0; return n < 1 ? n.toFixed(1) + " d" : Math.round(n) + " d"; };

  var methodology = wf.methodology || "fixed";
  var wfTooltip = methodology === "historical"
    ? "Σ (stage value × historical win rate). Per-stage probability comes from leads that touched the stage in the last 90 days. Sample threshold (≥ 20 closed deals) met."
    : "Σ (stage value × fixed coefficient). Sample below the 20-deal threshold needed for historical rates. Coefficients: NewLead 5%, Potential 15%, HotCase 35%, MeetingDone 60%, CallBack 25%.";

  var cards = [
    { id:"pipeline", label:"Open pipeline value", color: C.info,
      value: skel ? "" : fmtEGP(pv.value),
      hint:  skel ? "" : "snapshot · all open stages",
      tooltip: "Σ raw budget of leads in NewLead/Potential/HotCase/MeetingDone/CallBack. Lead-only, mirrors excluded. Ignores date range." },
    { id:"forecast", label:"Weighted forecast", color: C.accent,
      value: skel ? "" : fmtEGP(wf.value),
      hint:  skel ? "" : (methodology === "historical" ? "historical · last 90 days" : "fixed coefficients · low sample"),
      tooltip: wfTooltip },
    { id:"winrate", label:"Win rate", color: C.success,
      value: skel ? "" : fmtPct(wr.value),
      hint:  skel ? "" : ((wrSample.won || 0) + " won · " + (wrSample.lost || 0) + " lost"),
      tooltip: "DoneDeal / (DoneDeal + NotInterested + NoAnswer) for leads resolved in the selected range. Lead-only, mirrors excluded." },
    { id:"velocity", label:"Sales velocity (median)", color:"#8B5CF6",
      value: skel ? "" : (vel.sampleSize ? fmtDays(vel.value) : "—"),
      hint:  skel ? "" : (vel.sampleSize ? ("median of " + vel.sampleSize + " deals") : "no deals closed in range"),
      tooltip: "Median days from lead created to deal closed, for DoneDeals in range. Lead-only, mirrors excluded." }
  ];

  return <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(210px, 1fr))", gap:10, marginBottom:14 }}>
    {cards.map(function(c){ return <PipelineKpiCard key={c.id} card={c} skeleton={skel}/>; })}
  </div>;
};

var PipelineByStageRow = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var f = p.filters;

  useEffect(function() {
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "";
    if (f.team) qs += (qs ? "&" : "?") + "team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += (qs ? "&" : "?") + "source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/pipeline/by-stage" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.team, f.source]);

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load pipeline by stage: {state.error}</div>
    </Card>;
  }

  var stageDef = {
    NewLead:     { label: "New Lead",     color: "#8B5CF6", tint: "rgba(139, 92, 246, 0.08)" },
    Potential:   { label: "Potential",    color: "#3B82F6", tint: "rgba(59, 130, 246, 0.08)" },
    HotCase:     { label: "Hot Case",     color: "#F59E0B", tint: "rgba(245, 158, 11, 0.08)" },
    CallBack:    { label: "Callback",     color: "#06B6D4", tint: "rgba(6, 182, 212, 0.08)" },
    MeetingDone: { label: "Meeting Done", color: "#10B981", tint: "rgba(16, 185, 129, 0.08)" }
  };
  var stageOrder = ["NewLead", "Potential", "HotCase", "CallBack", "MeetingDone"];

  var skel = state.loading || !state.data;
  var stages = (state.data && state.data.stages) || [];
  var byKey = {};
  stages.forEach(function(s){ byKey[s.key] = s; });

  var goToStage = function(stageKey) {
    if (p.setFilter) p.setFilter(stageKey);
    if (p.nav) p.nav("leads");
  };

  return <Card style={{ marginBottom:14, padding:"12px 14px" }}>
    <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:8 }}>Pipeline by stage</div>
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:8 }}>
      {stageOrder.map(function(key){
        var def = stageDef[key];
        var s = byKey[key] || { count: 0, value: 0, avgDaysInStage: 0 };
        var hasLeads = !skel && (s.count || 0) > 0;
        return <div key={key}
          onClick={hasLeads ? function(){ goToStage(key); } : null}
          title={hasLeads ? "View leads in " + def.label : ""}
          style={{
            background: def.tint, border: "1px solid " + def.color + "33", borderRadius: 10,
            padding: "10px 12px", cursor: hasLeads ? "pointer" : "default", minHeight: 110,
            display:"flex", flexDirection:"column", gap:4
          }}>
          <div style={{ fontSize:11, fontWeight:700, color:def.color, textTransform:"uppercase", letterSpacing:"0.04em" }}>{def.label}</div>
          {skel
            ? <div style={{ height:24, marginTop:4, borderRadius:4, background:"#E2E8F0", width:"50%" }}/>
            : <div style={{ fontSize:22, fontWeight:800, color:C.text }}>{s.count}</div>}
          {!skel && <div style={{ fontSize:12, fontWeight:600, color:C.textLight }}>{s.value > 0 ? fmtEGP(s.value) : "—"}</div>}
          {!skel && <div style={{ fontSize:10, color:"#94A3B8", marginTop:"auto" }}>
            {s.count > 0 ? ("avg " + s.avgDaysInStage + " d in stage") : "no leads in stage"}
          </div>}
        </div>;
      })}
    </div>
  </Card>;
};

var DealsAtRiskTable = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [expanded, setExpanded] = useState(false);
  var f = p.filters;

  useEffect(function() {
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "";
    if (f.team) qs += (qs ? "&" : "?") + "team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += (qs ? "&" : "?") + "source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/pipeline/at-risk" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.team, f.source]);

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load deals at risk: {state.error}</div>
    </Card>;
  }

  var skel = state.loading || !state.data;
  var deals = (state.data && state.data.deals) || [];
  var total = (state.data && state.data.total) || 0;
  var visible = expanded ? deals.slice(0, 50) : deals.slice(0, 20);

  var stageBadge = function(stage) {
    var color = stage === "HotCase" ? "#F59E0B" : "#10B981";
    var label = stage === "HotCase" ? "Hot Case" : "Meeting Done";
    var bg    = stage === "HotCase" ? "rgba(245, 158, 11, 0.12)" : "rgba(16, 185, 129, 0.12)";
    return <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:6, background:bg, color:color, fontSize:10, fontWeight:700 }}>{label}</span>;
  };

  var ageCell = function(days) {
    var color = days >= 14 ? "#DC2626" : "#F59E0B";
    var fmt = days >= 1 ? Math.round(days) : days.toFixed(1);
    return <span style={{ color:color, fontWeight:700 }}>{fmt} d</span>;
  };

  // Fetch-first, like openUntouchedLead and openActivity. LeadsPage consumes
  // p.initSelected VERBATIM — no re-hydration — so a literal handed over here IS
  // what the side panel renders: it came up with no phone, no budget, no agent
  // and no notes, only the three fields typed below.
  //
  // The fail-soft matters MORE here than on the Untouched card, not less: that
  // endpoint at least sends a phone, so its degraded path could still dial. This
  // one does not project phone at all (GET /api/reports/... returns leadId, name
  // and the stage), so the fetch is the ONLY source of a number. A shell open is
  // a genuinely degraded panel, and it happens only when the request fails.
  var openLead = function(deal) {
    if (!deal || !deal.leadId) return;
    var shell = { _id: deal.leadId, name: deal.name, status: deal.stage };
    apiFetch("/api/leads/" + deal.leadId, "GET", null, p.token).then(function(fresh){
      if (p.nav) p.nav("leads", (fresh && fresh._id) ? fresh : shell);
    }).catch(function(){ if (p.nav) p.nav("leads", shell); });
  };

  var thStyle = { textAlign:"left", padding:"6px 8px", fontWeight:600, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em", fontSize:10 };
  var thRight = Object.assign({}, thStyle, { textAlign:"right" });

  return <Card style={{ marginBottom:14, padding:"12px 14px" }}>
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4, flexWrap:"wrap", gap:8 }}>
      <div style={{ fontSize:12, fontWeight:700, color:C.text }}>Deals at risk</div>
      {!skel && <div style={{ fontSize:11, color:C.textLight }}>{total} {total === 1 ? "lead" : "leads"} stuck 7+ days at stage</div>}
    </div>
    <div style={{ fontSize:10, color:C.textLight, marginBottom:8, lineHeight:1.4 }}>
      Stuck = no stage progression. Days counted from when the lead first entered its current stage. Reschedules and status flips don't reset the clock.
    </div>

    {skel && [0,1,2,3].map(function(i){ return <div key={i} style={{ height:32, marginBottom:6, background:"#F1F5F9", borderRadius:6 }}/>; })}

    {!skel && deals.length === 0 && <div style={{ fontSize:12, color:C.textLight, padding:"24px 8px", textAlign:"center" }}>
      ✓ No at-risk deals — every advanced-stage lead has progressed within 7 days.
    </div>}

    {!skel && deals.length > 0 && <div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", fontSize:11, borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ borderBottom:"1px solid #E2E8F0" }}>
              <th style={thStyle}>Lead</th>
              <th style={thStyle}>Stage</th>
              <th style={thRight}>Value</th>
              <th style={thStyle}>Agent</th>
              <th style={thRight}>Days in stage</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(function(d, i){
              return <tr key={d.leadId + "-" + i}
                onClick={function(){ openLead(d); }}
                onMouseEnter={function(e){ e.currentTarget.style.background = "#F8FAFC"; }}
                onMouseLeave={function(e){ e.currentTarget.style.background = "transparent"; }}
                style={{ borderBottom:"1px solid #F1F5F9", cursor:"pointer" }}>
                <td style={{ padding:"8px", color:C.text, fontWeight:600 }}>{d.name || "—"}</td>
                <td style={{ padding:"8px" }}>{stageBadge(d.stage)}</td>
                <td style={{ padding:"8px", textAlign:"right", color:C.text }}>{d.value > 0 ? fmtEGP(d.value) : "—"}</td>
                <td style={{ padding:"8px", color:C.textLight }}>{d.agentName}</td>
                <td style={{ padding:"8px", textAlign:"right" }}>{ageCell(d.daysInStage)}</td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      {!expanded && total > 20 && <div style={{ marginTop:8, textAlign:"center" }}>
        <button onClick={function(){ setExpanded(true); }} style={{
          background:"none", border:"none", color:C.accent, fontSize:11, fontWeight:600, cursor:"pointer", padding:"6px 12px"
        }}>View all {total > 50 ? "(top 50 of " + total + ")" : total} at-risk →</button>
      </div>}
    </div>}
  </Card>;
};

var PipelineByProjectTable = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [expanded, setExpanded] = useState(false);
  var f = p.filters;

  useEffect(function() {
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/pipeline/by-project" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.from, f.to, f.team, f.source]);

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load pipeline by project: {state.error}</div>
    </Card>;
  }

  var skel = state.loading || !state.data;
  var projects = (state.data && state.data.projects) || [];
  var total = (state.data && state.data.total) || 0;
  var visible = expanded ? projects.slice(0, 50) : projects.slice(0, 12);

  var openProject = function(projectName) {
    if (p.setSpecialFilter) p.setSpecialFilter({ type: "project", value: projectName || "" });
    if (p.setFilter) p.setFilter("all");
    if (p.nav) p.nav("leads");
  };

  var thStyle = { textAlign:"left", padding:"6px 8px", fontWeight:600, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em", fontSize:10 };
  var thRight = Object.assign({}, thStyle, { textAlign:"right" });

  return <Card style={{ marginBottom:14, padding:"12px 14px" }}>
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4, flexWrap:"wrap", gap:8 }}>
      <div style={{ fontSize:12, fontWeight:700, color:C.text }}>Pipeline by project</div>
      {!skel && <div style={{ fontSize:11, color:C.textLight }}>{total} {total === 1 ? "project" : "projects"}</div>}
    </div>
    <div style={{ fontSize:10, color:"#94A3B8", marginBottom:8 }}>
      Open columns reflect current snapshot · Closed columns reflect selected date range
    </div>

    {skel && [0,1,2,3,4].map(function(i){ return <div key={i} style={{ height:32, marginBottom:6, background:"#F1F5F9", borderRadius:6 }}/>; })}

    {!skel && projects.length === 0 && <div style={{ fontSize:12, color:C.textLight, padding:"24px 8px", textAlign:"center" }}>
      No projects with pipeline activity.
    </div>}

    {!skel && projects.length > 0 && <div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", fontSize:11, borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ borderBottom:"1px solid #E2E8F0" }}>
              <th style={thStyle}>Project</th>
              <th style={thRight}>Open</th>
              <th style={thRight}>Open value</th>
              <th style={thRight}>Closed</th>
              <th style={thRight}>Revenue</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(function(pr, i){
              var displayName = pr.project || "(no project)";
              var isEmpty = !pr.project;
              return <tr key={(pr.project || "_") + "-" + i}
                onClick={function(){ openProject(pr.project); }}
                onMouseEnter={function(e){ e.currentTarget.style.background = "#F8FAFC"; }}
                onMouseLeave={function(e){ e.currentTarget.style.background = "transparent"; }}
                style={{ borderBottom:"1px solid #F1F5F9", cursor:"pointer" }}>
                <td style={{ padding:"8px", color: isEmpty ? C.textLight : C.text, fontWeight:600, fontStyle: isEmpty ? "italic" : "normal" }}>{displayName}</td>
                <td style={{ padding:"8px", textAlign:"right", color:C.text }}>{pr.openCount}</td>
                <td style={{ padding:"8px", textAlign:"right", color:C.text }}>{pr.openValue > 0 ? fmtEGP(pr.openValue) : "—"}</td>
                <td style={{ padding:"8px", textAlign:"right", color:C.textLight }}>{pr.dealsCount}</td>
                <td style={{ padding:"8px", textAlign:"right", color:C.textLight }}>{pr.dealsRevenue > 0 ? fmtEGP(pr.dealsRevenue) : "—"}</td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      {!expanded && total > 12 && <div style={{ marginTop:8, textAlign:"center" }}>
        <button onClick={function(){ setExpanded(true); }} style={{
          background:"none", border:"none", color:C.accent, fontSize:11, fontWeight:600, cursor:"pointer", padding:"6px 12px"
        }}>View all {total > 50 ? "(top 50 of " + total + ")" : total} projects →</button>
      </div>}
    </div>}
  </Card>;
};

var OutcomeBreakdown = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var f = p.filters;

  useEffect(function() {
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/pipeline/outcomes" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.from, f.to, f.team, f.source]);

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load outcome breakdown: {state.error}</div>
    </Card>;
  }

  var skel = state.loading || !state.data;
  var d = state.data || {};
  var won = d.won || { count:0, value:0, avgDaysToResolve:0 };
  var ni  = d.lostNotInterested || { count:0, value:0, avgDaysToResolve:0 };
  var na  = d.lostNoAnswer || { count:0, value:0, avgDaysToResolve:0 };
  var total = d.totalResolved || 0;
  var winRate = total > 0 ? (won.count / total) * 100 : 0;

  var buckets = [
    { key:"won", label:"Won (Done Deal)",       color:"#10B981", tint:"rgba(16, 185, 129, 0.10)", data:won },
    { key:"ni",  label:"Lost — Not Interested", color:"#DC2626", tint:"rgba(220, 38, 38, 0.10)",  data:ni  },
    { key:"na",  label:"Lost — No Answer",      color:"#64748B", tint:"rgba(100, 116, 139, 0.10)",data:na  }
  ];

  var fmtDays = function(v){ var n = Number(v) || 0; return n < 1 ? n.toFixed(1) + " d" : Math.round(n) + " d"; };
  var methodologyTooltip = "Buckets count leads whose current status is the resolved state and whose resolution date falls in the selected range. Resolution: dealDate (parsed) for Won; updatedAt for Lost (proxy — no explicit lostAt field, so leads edited after being lost can register on the edit date). Lead-only, mirrors excluded.";

  return <Card style={{ marginBottom:14, padding:"12px 14px" }}>
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10, flexWrap:"wrap", gap:8 }}>
      <div style={{ fontSize:12, fontWeight:700, color:C.text, display:"flex", alignItems:"center", gap:6 }}>
        <span>Outcome breakdown</span>
        <span title={methodologyTooltip} style={{ cursor:"help", color:"#94A3B8", fontSize:10, fontWeight:700 }}>ⓘ</span>
      </div>
      {!skel && <div style={{ fontSize:11, color:C.textLight }}>{total} {total === 1 ? "lead" : "leads"} resolved in range</div>}
    </div>

    {!skel && total === 0 && <div style={{ fontSize:12, color:C.textLight, padding:"24px 8px", textAlign:"center" }}>
      No leads resolved in this period.
    </div>}

    {(skel || total > 0) && <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))", gap:10 }}>
      {buckets.map(function(b){
        return <div key={b.key} style={{
          background:b.tint, border:"1px solid " + b.color + "33", borderRadius:10,
          padding:"12px 14px", minHeight:130, display:"flex", flexDirection:"column", gap:6
        }}>
          <div style={{ fontSize:11, fontWeight:700, color:b.color, textTransform:"uppercase", letterSpacing:"0.04em" }}>{b.label}</div>
          {skel
            ? <div style={{ height:24, marginTop:4, borderRadius:4, background:"#E2E8F0", width:"50%" }}/>
            : <div style={{ fontSize:24, fontWeight:800, color:C.text }}>{b.data.count}</div>}
          {!skel && <div style={{ fontSize:13, fontWeight:600, color:C.textLight }}>{b.data.value > 0 ? fmtEGP(b.data.value) : "—"}</div>}
          {!skel && <div style={{ fontSize:10, color:"#94A3B8", marginTop:"auto" }}>
            {b.data.count > 0 ? ("avg " + fmtDays(b.data.avgDaysToResolve) + " to resolve") : "no leads in this bucket"}
          </div>}
        </div>;
      })}
    </div>}

    {!skel && total > 0 && <div style={{ fontSize:11, color:C.textLight, marginTop:10, textAlign:"right" }}>
      Win rate: <span style={{ fontWeight:700, color:"#10B981" }}>{winRate.toFixed(1)}%</span> <span style={{ color:"#94A3B8" }}>({won.count} / {total})</span>
    </div>}
  </Card>;
};

var ReportsPipelineBody = function(p) {
  var sections = [
    { key:"kpis",      title:"Pipeline KPIs",       height:120 },
    { key:"byStage",   title:"Pipeline by Stage",   height:200 },
    { key:"atRisk",    title:"Deals at Risk",       height:280 },
    { key:"byProject", title:"Pipeline by Project", height:280 },
    { key:"outcomes",  title:"Outcome Breakdown",   height:180 }
  ];
  return <div>
    {sections.map(function(s){
      if (s.key === "kpis") return <PipelineKpiRow key="kpis" filters={p.filters} token={p.token}/>;
      if (s.key === "byStage") return <PipelineByStageRow key="byStage" filters={p.filters} token={p.token} nav={p.nav} setFilter={p.setFilter}/>;
      if (s.key === "atRisk") return <DealsAtRiskTable key="atRisk" filters={p.filters} token={p.token} nav={p.nav}/>;
      if (s.key === "byProject") return <PipelineByProjectTable key="byProject" filters={p.filters} token={p.token} nav={p.nav} setFilter={p.setFilter} setSpecialFilter={p.setSpecialFilter}/>;
      if (s.key === "outcomes") return <OutcomeBreakdown key="outcomes" filters={p.filters} token={p.token}/>;
      return <Card key={s.key} style={{ marginBottom:14, padding:"14px 16px", minHeight:s.height, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", background:"#FAFBFC", border:"1px dashed #E2E8F0" }}>
        <div style={{ fontSize:13, fontWeight:600, color:C.textLight }}>{s.title}</div>
        <div style={{ fontSize:11, color:"#94A3B8", marginTop:4 }}>Section in development</div>
      </Card>;
    })}
  </div>;
};

var ReportsAgentsBody = function(p) {
  var [selectedAgentId, setSelectedAgentId] = useState(null);
  var [agentList, setAgentList] = useState({ loading: true, data: null, error: null });
  var f = p.filters;

  // Fetch the eligible-roster + revenue list for the picker.
  // Reloads whenever range / team / source changes.
  useEffect(function(){
    var aborted = false;
    setAgentList(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/agents/list" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setAgentList({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setAgentList({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.from, f.to, f.team, f.source]);

  // Default selection: top-revenue agent. Re-defaults if the current
  // selection drops out of the list (e.g. team filter narrows the roster).
  useEffect(function(){
    var agents = (agentList.data && agentList.data.agents) || [];
    if (agents.length === 0) return;
    var stillExists = selectedAgentId && agents.some(function(a){ return String(a.id) === String(selectedAgentId); });
    if (!stillExists) setSelectedAgentId(agents[0].id);
  }, [agentList.data]);

  var roleLabel = function(role){
    return role === "manager" ? "Manager" : role === "team_leader" ? "Team Leader" : "Sales";
  };

  var agents = (agentList.data && agentList.data.agents) || [];
  var selectedAgent = agents.find(function(a){ return String(a.id) === String(selectedAgentId); });

  // All 6 sections shipped — placeholder list now empty. Kept as an
  // empty array (rather than removed) so the .map() below still runs
  // harmlessly if a future placeholder needs to be added.
  var sections = [];

  return <div>
    <Card style={{ marginBottom:14, padding:"12px 14px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
        <span style={{ fontSize:11, fontWeight:600, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em" }}>Agent</span>
        <SearchableSelect value={selectedAgentId || ""} onChange={setSelectedAgentId}
          disabled={agentList.loading || agents.length === 0}
          hideEmpty={!agentList.loading && agents.length > 0}
          emptyLabel={agentList.loading ? "Loading…" : "No eligible agents"}
          /* /api/reports/agents/list is the users roster (active, no period term) decorated with the
             period's revenue; REPORTS_ROSTER keeps the server's revenue order (keepOrder). 0 shows as 0. */
          options={REPORTS_ROSTER.people(agents, null, true).map(function(a){ return { value:a.id, label:a.name+" ("+roleLabel(a.role)+") — "+fmtEGP(a.revenue) }; })}
          placeholder="Search agent…" width={260} maxWidth={260} fontSize={12}/>
        {selectedAgent && (selectedAgent.title || selectedAgent.teamName) && <span style={{ fontSize:11, color:C.textLight }}>
          {[selectedAgent.title, selectedAgent.teamName].filter(Boolean).join(" · ")}
        </span>}
        {agentList.error && <span style={{ fontSize:11, color:"#DC2626" }}>· couldn't load roster: {agentList.error}</span>}
      </div>
    </Card>

    <AgentKpiRow filters={f} token={p.token} agentId={selectedAgentId}/>

    <AgentRadarChart filters={f} token={p.token} agentId={selectedAgentId}/>

    <AgentActivityHeatmap filters={f} token={p.token} agentId={selectedAgentId}/>

    <AgentStageProgression filters={f} token={p.token} agentId={selectedAgentId}/>

    <RecentDealsTable filters={f} token={p.token} agentId={selectedAgentId} nav={p.nav}/>

    <StuckLeadsTable filters={f} token={p.token} agentId={selectedAgentId} nav={p.nav}/>

    {sections.map(function(s){
      return <Card key={s.key} style={{ marginBottom:14, padding:"14px 16px", minHeight:s.height, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", background:"#FAFBFC", border:"1px dashed #E2E8F0" }}>
        <div style={{ fontSize:13, fontWeight:600, color:C.textLight }}>{s.title}</div>
        <div style={{ fontSize:11, color:"#94A3B8", marginTop:4 }}>Section in development</div>
      </Card>;
    })}
  </div>;
};

// Reports > Campaigns > "ROI / Spend". Backed by /api/reports/campaigns, which
// since 2026-08-23 joins spend to leads by CAMPAIGN GROUP and CALENDAR MONTH.
//
// COST PER LEAD LEADS THIS TABLE, not ROI%. Across the campaign-tagged book
// there are 5 closed deals — tagging began in June and deals lag intake by
// months — so an ROI column sorted descending was a column of dashes pretending
// to rank something. CPL works from the first month a campaign is priced. ROI is
// still here, still real, and fills in as deals close.
//
// Rows arrive sorted (CPL ascending, priced first, unpriced by volume) and this
// component does NOT re-sort them: the ordering rule lives with the numbers that
// justify it. The month breakdown, the group membership and the derived metrics
// are all computed server-side for the same reason the Settings screen renders
// server rows — a second opinion about which spellings are one campaign is the
// one thing this feature cannot afford.
var ReportsCampaignsBody = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [openRow, setOpenRow] = useState(null);
  var f = p.filters;

  useEffect(function(){
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    // Team is still not sent — see spendComparable below. The backend honours it
    // if some future caller does pass one, and says so in the response.
    apiFetch("/api/reports/campaigns" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [f.from, f.to, f.source]);

  var skel = state.loading || !state.data;
  var data = state.data || {};
  var campaigns = data.campaigns || [];
  var summary = data.summary || {};
  var months = data.months || [];
  var comparable = summary.spendComparable !== false;

  var fmtPct = function(v){
    if (v == null) return "—";
    var n = Number(v);
    return (n > 0 ? "+" : "") + n.toFixed(1) + "%";
  };
  var monthLabel = function(m){
    if (!m) return "";
    var parts = String(m).split("-");
    try { return new Date(Number(parts[0]), Number(parts[1])-1, 1)
      .toLocaleDateString("en-GB", { month:"short", year:"2-digit" }); } catch(_){ return m; }
  };
  var roiColor = function(v){
    if (v == null) return "#94A3B8";
    if (v < 0)     return C.danger;
    if (v <= 100)  return C.warning;
    return C.success;
  };
  var roiBg = function(v){
    if (v == null) return "#F1F5F9";
    if (v < 0)     return "#FEE2E2";
    if (v <= 100)  return "#FEF3C7";
    return "#DCFCE7";
  };
  // Cheapest lead is best, so the scale runs the other way from ROI. Coloured
  // against the run's own median rather than a fixed threshold — what counts as
  // a good CPL differs by market and nobody has given us a target.
  var cplMedian = (function(){
    var vals = campaigns.map(function(c){ return c.costPerLead; })
                        .filter(function(v){ return v != null; })
                        .sort(function(a, b){ return a - b; });
    return vals.length ? vals[Math.floor(vals.length / 2)] : null;
  })();
  var cplColor = function(v){
    if (v == null || cplMedian == null) return C.textLight;
    if (v <= cplMedian * 0.75) return C.success;
    if (v >= cplMedian * 1.5)  return C.danger;
    return C.text;
  };

  // DRILL-DOWN: this row's leads on the Leads page. The row's identity is its campaign
  // group (g:<id>) or ungrouped spelling (k:<key>) - the same ids this report buckets
  // by - and the server resolves it (?campaignGroup=) with the report's own fold, over
  // THIS report's effective window (the response's range: floored, `to` exclusive here,
  // inclusive there, hence the 1 ms). It replaces a client filter over the ~100 rows the
  // browser had loaded, which could not honestly count and came back empty for deep rows.
  var openCampaign = function(c){
    var rg = data.range || {};
    var toIncl = rg.to ? new Date(new Date(rg.to).getTime() - 1).toISOString() : undefined;
    var fmtD = function(iso){ try { return new Date(iso).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" }); } catch (e) { return ""; } };
    if (p.setSpecialFilter) p.setSpecialFilter({
      type: "reportDrill", cohort: "roi", campaignGroup: c.id,
      source: (f.source && f.source !== "all") ? f.source : undefined,
      periodFrom: rg.from, periodTo: toIncl,
      periodLabel: rg.from ? (fmtD(rg.from) + " \u2192 " + fmtD(toIncl)) : "",
      label: (c.isGroup ? "Campaign group: " : "Campaign (ungrouped): ") + c.label,
      back: { kind: "roi", tab: "campaigns", view: "roi", state: { filters: p.filters } }
    });
    if (p.setFilter) p.setFilter("all");
    if (p.nav) p.nav("leads");
  };

  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load Campaigns: {state.error}</div>
    </Card>;
  }

  var nothingAtAll = !skel && campaigns.length === 0;

  var cards = [
    { id:"spend",  label:"Total spend", color:C.text,
      value: skel ? "" : fmtEGP(summary.totalSpend),
      hint:  skel ? "" : (months.length === 1 ? monthLabel(months[0])
                          : months.length + " months") },
    { id:"cpl",    label:"Cost per lead", color: comparable ? C.accent : C.textLight,
      value: skel ? "" : (summary.overallCostPerLead == null ? "—" : fmtEGP(summary.overallCostPerLead)),
      hint:  skel ? "" : (comparable ? (summary.totalLeads || 0) + " leads in window" : "hidden for this filter") },
    { id:"best",   label:"Cheapest per lead", color:C.success,
      value: skel ? "" : (summary.cheapestPerLead ? summary.cheapestPerLead.name : "—"),
      hint:  skel ? "" : (summary.cheapestPerLead ? fmtEGP(summary.cheapestPerLead.costPerLead) + " / lead" : "no campaign priced yet") },
    { id:"roi",    label:"Overall ROI", color: roiColor(summary.overallRoi),
      value: skel ? "" : (summary.overallRoi == null ? "—" : fmtPct(summary.overallRoi)),
      hint:  skel ? "" : ((summary.totalDeals || 0) > 0 ? (summary.totalDeals + " closed deal" + (summary.totalDeals === 1 ? "" : "s"))
                                                        : "needs closed deals") }
  ];

  var thStyle = { textAlign:"left", padding:"6px 8px", fontWeight:600, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em", fontSize:10 };
  var thRight = Object.assign({}, thStyle, { textAlign:"right" });

  return <div>
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(210px, 1fr))", gap:10, marginBottom:14 }}>
      {cards.map(function(c){
        return <Card key={c.id} style={{ padding:"14px 16px", minHeight:96 }}>
          <div style={{ fontSize:11, color:C.textLight, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em" }}>{c.label}</div>
          {skel
            ? <div style={{ height:24, marginTop:10, borderRadius:4, background:"#F1F5F9", width:"60%" }}/>
            : <div title={c.value} style={{ fontSize: c.id === "best" ? 16 : 22, fontWeight:800, color:c.color, marginTop:6, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.value}</div>}
          {!skel && c.hint && <div style={{ fontSize:10, color:C.textLight, marginTop:4 }}>{c.hint}</div>}
        </Card>;
      })}
    </div>

    {/* The window this tab actually used. The server floors it at 2026-07-01
        because campaign names were not captured before then; printing it is what
        keeps a floor from reading as a silently different answer. */}
    {!skel && data.range && data.range.floored && <Card style={{ marginBottom:14, padding:"10px 14px", background:"#EFF6FF", border:"1px solid #BFDBFE" }}>
      <div style={{ fontSize:12, color:"#1D4ED8", fontWeight:600 }}>{data.range.floorNote}</div>
    </Card>}

    {/* A filter that narrows leads but cannot narrow spend — say so where the
        missing numbers are, not in a tooltip nobody opens. */}
    {!skel && !comparable && summary.spendNote && <Card style={{ marginBottom:14, padding:"10px 14px", background:"#FFFBEB", border:"1px solid #FCD34D" }}>
      <div style={{ fontSize:12, color:"#92400E", fontWeight:600 }}>{summary.spendNote}</div>
    </Card>}

    {/* Replaces the old "N campaign names found in leads but not tracked here"
        banner. Every name in the data is a row now; the only thing that can be
        missing is a spend figure. */}
    {!skel && comparable && (summary.unpricedCampaigns || 0) > 0 && <Card style={{ marginBottom:14, padding:"10px 14px", background:"#FFFBEB", border:"1px solid #FCD34D" }}>
      <div style={{ fontSize:12, color:"#92400E", fontWeight:600 }}>
        {summary.unpricedCampaigns} campaign{summary.unpricedCampaigns === 1 ? " has" : "s have"} no spend recorded for
        {months.length === 1 ? " " + monthLabel(months[0]) : " this period"} — their cost per lead is blank until you
        enter it in <b>Settings → Campaigns</b>.
      </div>
    </Card>}

    {nothingAtAll && <Card style={{ marginBottom:14, padding:"32px 16px", background:"#FAFBFC", border:"1px dashed #E2E8F0" }}>
      <div style={{ fontSize:13, fontWeight:600, color:C.textLight, textAlign:"center" }}>No campaign names in this period</div>
      <div style={{ fontSize:11, color:"#94A3B8", textAlign:"center", marginTop:6 }}>Leads only carry a campaign name from June 2026 onward — try a later date range.</div>
    </Card>}

    {!nothingAtAll && <Card style={{ marginBottom:14, padding:"12px 14px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4, flexWrap:"wrap", gap:8 }}>
        <div style={{ fontSize:12, fontWeight:700, color:C.text }}>Campaign cost</div>
        {!skel && <div style={{ fontSize:11, color:C.textLight }}>{campaigns.length} campaign{campaigns.length === 1 ? "" : "s"}{months.length ? " · " + months.map(monthLabel).join(", ") : ""}</div>}
      </div>
      <div style={{ fontSize:10, color:"#94A3B8", marginBottom:8 }}>
        Cheapest lead first · click a row to see its leads · ▸ opens the month breakdown
      </div>

      {skel && [0,1,2,3].map(function(i){ return <div key={i} style={{ height:32, marginBottom:6, background:"#F1F5F9", borderRadius:6 }}/>; })}

      {!skel && campaigns.length > 0 && <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", fontSize:11, borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ borderBottom:"1px solid #E2E8F0" }}>
              <th style={Object.assign({}, thStyle, { width:16 })}/>
              <th style={thStyle}>Campaign</th>
              <th style={thStyle}>Top source</th>
              <th style={thRight}>Spend</th>
              <th style={thRight}>Leads</th>
              <th style={thRight} title="Contacted / Meeting / EOI / Deal">Funnel</th>
              <th style={thRight}>Cost / lead</th>
              <th style={thRight}>Revenue</th>
              <th style={thRight}>Cost / deal</th>
              <th style={thRight}>ROI</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map(function(c, i){
              var isOpen = openRow === (c.id || i);
              var fnl = c.contacted + " / " + c.meeting + " / " + c.eoi + " / " + c.deal;
              var rows = [
                <tr key={(c.id || i) + "-main"}
                  onClick={function(){ openCampaign(c); }}
                  onMouseEnter={function(e){ e.currentTarget.style.background = "#F8FAFC"; }}
                  onMouseLeave={function(e){ e.currentTarget.style.background = "transparent"; }}
                  style={{ borderBottom: isOpen ? "none" : "1px solid #F1F5F9", cursor:"pointer" }}
                  title={"View the leads behind " + (c.label)}>
                  <td style={{ padding:"8px 4px" }}>
                    {(c.months && c.months.length > 1) && <span role="button" title="Month breakdown"
                      onClick={function(e){ e.stopPropagation(); setOpenRow(isOpen ? null : (c.id || i)); }}
                      style={{ cursor:"pointer", color:C.textLight, userSelect:"none" }}>{isOpen ? "▾" : "▸"}</span>}
                  </td>
                  <td style={{ padding:"8px", color:C.text, fontWeight:600 }}>
                    {c.label}
                    {c.isGroup && <span title={(c.members || []).join(", ")}
                      style={{ marginLeft:6, fontSize:9.5, fontWeight:600, padding:"1px 5px", borderRadius:4, background:"#E6F1FB", color:"#185FA5", whiteSpace:"nowrap" }}>
                      {(c.members || []).length} name{(c.members || []).length === 1 ? "" : "s"}
                    </span>}
                    {c.spend == null && c.leads > 0 && <span title="No spend recorded for this period"
                      style={{ marginLeft:6, fontSize:9.5, fontWeight:600, padding:"1px 5px", borderRadius:4, background:"#FFF7ED", color:"#B45309", whiteSpace:"nowrap" }}>no spend</span>}
                    {c.leads === 0 && c.spend != null && <span title="Money spent, no leads arrived in this period"
                      style={{ marginLeft:6, fontSize:9.5, fontWeight:600, padding:"1px 5px", borderRadius:4, background:"#FEE2E2", color:"#B91C1C", whiteSpace:"nowrap" }}>no leads</span>}
                  </td>
                  <td style={{ padding:"8px", color:C.textLight }}>{c.topSource || "—"}</td>
                  <td style={{ padding:"8px", textAlign:"right", color:C.text }}>{c.spend == null ? "—" : fmtEGP(c.spend)}</td>
                  <td style={{ padding:"8px", textAlign:"right", color:C.text }}>{c.leads}</td>
                  <td style={{ padding:"8px", textAlign:"right", color:C.textLight, fontVariantNumeric:"tabular-nums" }}>{fnl}</td>
                  <td style={{ padding:"8px", textAlign:"right", fontWeight:700, color: cplColor(c.costPerLead), fontVariantNumeric:"tabular-nums" }}>
                    {c.costPerLead == null ? "—" : fmtEGP(c.costPerLead)}
                  </td>
                  <td style={{ padding:"8px", textAlign:"right", color:C.text }}>{c.revenue > 0 ? fmtEGP(c.revenue) : "—"}</td>
                  <td style={{ padding:"8px", textAlign:"right", color:C.textLight }}>{c.costPerDeal == null ? "—" : fmtEGP(c.costPerDeal)}</td>
                  <td style={{ padding:"8px", textAlign:"right" }}>
                    <span title={c.roi == null && c.deal === 0 ? "Needs closed deals" : ""}
                      style={{ display:"inline-block", padding:"2px 8px", borderRadius:6, background:roiBg(c.roi), color:roiColor(c.roi), fontWeight:700, fontVariantNumeric:"tabular-nums" }}>
                      {c.roi == null ? "—" : fmtPct(c.roi)}
                    </span>
                  </td>
                </tr>
              ];
              // Month breakdown — the whole point of month-keyed spend is being
              // able to see that a campaign got dearer, so it is one click away
              // rather than a separate report.
              if (isOpen) {
                (c.months || []).forEach(function(m){
                  rows.push(<tr key={(c.id || i) + "-" + m.month} style={{ background:"#FBFCFD", borderBottom:"1px solid #F8FAFC" }}>
                    <td/>
                    <td style={{ padding:"6px 8px 6px 20px", color:C.textLight, fontSize:10.5 }}>{monthLabel(m.month)}</td>
                    <td/>
                    <td style={{ padding:"6px 8px", textAlign:"right", color:C.textLight, fontSize:10.5 }}>{m.spend == null ? "—" : fmtEGP(m.spend)}</td>
                    <td style={{ padding:"6px 8px", textAlign:"right", color:C.textLight, fontSize:10.5 }}>{m.leads}</td>
                    <td/>
                    <td style={{ padding:"6px 8px", textAlign:"right", color:C.textLight, fontSize:10.5, fontVariantNumeric:"tabular-nums" }}>{m.costPerLead == null ? "—" : fmtEGP(m.costPerLead)}</td>
                    <td style={{ padding:"6px 8px", textAlign:"right", color:C.textLight, fontSize:10.5 }}>{m.revenue > 0 ? fmtEGP(m.revenue) : "—"}</td>
                    <td/><td/>
                  </tr>);
                });
              }
              return <Fragment key={(c.id || i) + "-frag"}>{rows}</Fragment>;
            })}
          </tbody>
        </table>
      </div>}

      {!skel && (summary.ignoredLeads || 0) > 0 && <div style={{ fontSize:10, color:"#94A3B8", marginTop:8 }}>
        {summary.ignoredLeads} lead{summary.ignoredLeads === 1 ? "" : "s"} carry a name marked &ldquo;not a campaign&rdquo; in Settings and are excluded here.
      </div>}
    </Card>}
  </div>;
};
// Reports > Campaigns > "Performance" (VIEW 1). Cohort per-campaign table keyed
// off the raw lead.campaign field, attributed by LEAD CREATION DATE. Month/
// Quarter/Year selector mirrors the Leaderboard tab. Backed by
// /api/reports/campaigns/performance (one aggregation; variant-merge + top unit
// types resolved server-side). Distinct from the ROI sub-view (tracked
// campaigns / spend). In this data campaign≈project-name, project≈unit-type.
var ReportsCampaignPerformance = function(p) {
  var now = new Date();
  var curYear = now.getFullYear();
  var years = [curYear, curYear - 1, curYear - 2, curYear - 3];
  // Coming back from a drill-down (the Leads page's "← Back to report" chip): the period
  // and sort the report was left in. Read once; null on any ordinary visit.
  var rr0 = peekReportReturn("performance-development") || {};
  useEffect(function(){ clearReportReturn(); }, []);
  var [year, setYear] = useState(rr0.year != null ? rr0.year : curYear);
  var [quarter, setQuarter] = useState(rr0.quarter != null ? rr0.quarter : "all");
  var [month, setMonth] = useState(rr0.month != null ? rr0.month : now.getMonth());
  var [sortBy, setSortBy] = useState(rr0.sortBy || "leads"); // leads | eois | deals
  var [state, setState] = useState({ loading: true, data: null, error: null });

  // Month and Quarter are mutually exclusive — picking one clears the other
  // (same rule as the Leaderboard period selector).
  var pickMonth = function(m){ setMonth(m); if (m !== "all") setQuarter("all"); };
  var pickQuarter = function(q){ setQuarter(q); if (q !== "all") setMonth("all"); };

  var range = (function(){
    if (month !== "all") return { from: new Date(year, month, 1).getTime(), to: new Date(year, month + 1, 1).getTime() };
    if (quarter !== "all") { var qs = { Q1:0, Q2:3, Q3:6, Q4:9 }[quarter]; return { from: new Date(year, qs, 1).getTime(), to: new Date(year, qs + 3, 1).getTime() }; }
    return { from: new Date(year, 0, 1).getTime(), to: new Date(year + 1, 0, 1).getTime() };
  })();

  useEffect(function(){
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    apiFetch("/api/reports/campaigns/performance?from=" + range.from + "&to=" + range.to, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [range.from, range.to]);

  var periodLabel = month !== "all" ? (LB_MONTHS[month] + " " + year) : quarter !== "all" ? (quarter + " " + year) : ("Full year " + year);
  var qBtn = function(q){
    var active = (month === "all") && (quarter === q);
    return <button key={q} type="button" onClick={function(){ pickQuarter(q); }}
      style={{ padding:"5px 12px", borderRadius:8, border:"1px solid", borderColor: active ? C.accent : "#E2E8F0", background: active ? C.accent + "26" : "#fff", color: active ? "#92400E" : C.textLight, fontSize:12, fontWeight: active ? 700 : 600, cursor:"pointer" }}>{q === "all" ? "All" : q}</button>;
  };

  var skel = state.loading || !state.data;
  var rows = (state.data && state.data.campaigns) || [];
  // Manual bucket always sinks last; real rows sort by the active metric desc,
  // tie-broken by leads.
  var sorted = rows.slice().sort(function(a, b){
    if (a.manual !== b.manual) return a.manual ? 1 : -1;
    return ((b[sortBy] || 0) - (a[sortBy] || 0)) || (b.leads - a.leads);
  });
  var totalLeads = rows.reduce(function(s, r){ return s + (r.leads || 0); }, 0);
  var realCount = rows.filter(function(r){ return !r.manual; }).length;

  // DRILL-DOWN: the leads this row counted. The spec carries the row's identity (the
  // development, resolved on the server with this report's own grouping - an empty one
  // is the Manual row) and THIS report's period, so the Leads page opens the same set
  // and the same count. `back` is what the Back chip restores.
  var openCampaign = function(c){
    if (p.setSpecialFilter) p.setSpecialFilter({
      type:"reportDrill", devGroup: c.manual ? "" : c.campaign,
      periodFrom: new Date(range.from).toISOString(), periodTo: new Date(range.to).toISOString(), periodLabel: periodLabel,
      label: "Development: " + (c.manual ? "No campaign / Manual" : c.campaign),
      back: { kind:"performance-development", tab:"campaigns", view:"performance", mode:"development",
              state:{ year:year, quarter:quarter, month:month, sortBy:sortBy } }
    });
    if (p.setFilter) p.setFilter("all");
    if (p.nav) p.nav("leads");
  };

  var thBase = { fontSize:11, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em", padding:"8px 12px", borderBottom:"1px solid #E2E8F0", fontWeight:600, whiteSpace:"nowrap" };
  var thNum = function(key, label){
    var active = sortBy === key;
    return <th onClick={function(){ setSortBy(key); }} style={Object.assign({}, thBase, { textAlign:"right", cursor:"pointer", color: active ? C.accent : C.textLight })}>{label}{active ? " ↓" : ""}</th>;
  };
  var tdStyle = { fontSize:13, padding:"9px 12px", borderBottom:"1px solid #F1F5F9", color:C.text };
  var tdNum = Object.assign({}, tdStyle, { textAlign:"right", fontVariantNumeric:"tabular-nums" });

  return <div>
    <Card style={{ marginBottom:16, padding:"12px 14px" }}>
      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
        <span style={{ fontSize:11, fontWeight:600, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em" }}>Period</span>
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>{["all","Q1","Q2","Q3","Q4"].map(qBtn)}</div>
        <Select value={String(month)} onChange={function(e){ var v = e.target.value; pickMonth(v === "all" ? "all" : Number(v)); }} searchable width={130} maxWidth={130}
          options={[{ value:"all", label:"All months" }].concat(LB_MONTHS.map(function(mn, i){ return { value:String(i), label:mn }; }))}/>
        <Select value={String(year)} onChange={function(e){ setYear(Number(e.target.value)); }} width={90} maxWidth={90}
          options={years.map(function(y){ return { value:String(y), label:String(y) }; })}/>
        <div style={{ flex:1, minWidth:8 }}/>
        <span style={{ fontSize:11, color:C.textLight }}>{periodLabel}</span>
      </div>
    </Card>

    <Card style={{ padding:"12px 14px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4, flexWrap:"wrap", gap:8 }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text }}>Campaign performance</div>
        {!skel && <div style={{ fontSize:11, color:C.textLight }}>{realCount} {realCount === 1 ? "campaign" : "campaigns"} · {totalLeads} leads</div>}
      </div>
      <div style={{ fontSize:10.5, color:"#94A3B8", marginBottom:10 }}>
        By lead creation date (cohort) · variants merged · EOI = Pending/Approved · Deal = Done Deal · click a row to view its leads
      </div>

      {state.error && <div style={{ fontSize:12, color:"#DC2626", padding:"8px 0" }}>Couldn't load: {state.error}</div>}

      {skel && !state.error && [0,1,2,3,4].map(function(i){ return <div key={i} style={{ height:34, marginBottom:6, background:"#F1F5F9", borderRadius:6 }}/>; })}

      {!skel && !state.error && rows.length === 0 && <div style={{ fontSize:12, color:C.textLight, padding:"28px 8px", textAlign:"center" }}>No leads in {periodLabel}.</div>}

      {!skel && !state.error && rows.length > 0 && <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", minWidth:520 }}>
          <thead><tr>
            <th style={Object.assign({}, thBase, { textAlign:"left" })}>Campaign / Project</th>
            <th style={Object.assign({}, thBase, { textAlign:"left" })}>Top unit types</th>
            {thNum("leads","Leads")}
            {thNum("eois","EOIs")}
            {thNum("deals","Deals")}
          </tr></thead>
          <tbody>
            {sorted.map(function(c, i){
              return <tr key={i}
                onClick={function(){ openCampaign(c); }}
                onMouseEnter={function(e){ e.currentTarget.style.background = "#F8FAFC"; }}
                onMouseLeave={function(e){ e.currentTarget.style.background = "transparent"; }}
                style={{ cursor:"pointer" }}
                title={c.manual ? "View leads with no campaign" : ("View leads matching " + c.campaign)}>
                <td style={Object.assign({}, tdStyle, { fontWeight:600, whiteSpace:"nowrap", color: c.manual ? C.textLight : C.text, fontStyle: c.manual ? "italic" : "normal" })}>{c.campaign}</td>
                <td style={Object.assign({}, tdStyle, { color:C.textLight, maxWidth:220, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" })}>{(c.unitTypes && c.unitTypes.length) ? c.unitTypes.join(", ") : "—"}</td>
                <td style={tdNum}>{c.leads}</td>
                <td style={Object.assign({}, tdNum, { color: c.eois > 0 ? C.text : "#CBD5E1" })}>{c.eois}</td>
                <td style={Object.assign({}, tdNum, { color: c.deals > 0 ? C.success : "#CBD5E1", fontWeight: c.deals > 0 ? 700 : 400 })}>{c.deals}</td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>}
    </Card>
  </div>;
};

// Reports > Campaigns > "Monthly intake" (VIEW 2). Month × source matrix for
// the selected year (rows = months with intake, columns = curated sources +
// Total). Backed by /api/reports/campaigns/intake (two aggregations: Lead by
// month/source excluding DR mirrors + DailyRequest by month → the "Daily
// Request" column, so intake never double-counts). Sticky first column keeps
// the month labels visible while the source columns scroll on mobile.
var ReportsCampaignIntake = function(p) {
  var now = new Date();
  var curYear = now.getFullYear();
  var years = [curYear, curYear - 1, curYear - 2, curYear - 3];
  var [year, setYear] = useState(curYear);
  var [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(function(){
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    apiFetch("/api/reports/campaigns/intake?year=" + year, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [year]);

  var skel = state.loading || !state.data;
  var data = state.data || {};
  var columns = data.columns || [];
  var months = data.months || [];
  var colTotals = data.colTotals || {};
  var grandTotal = data.grandTotal || 0;
  // Only months with intake, so a partial year doesn't show 8 empty rows.
  var activeMonths = months.filter(function(m){ return (m.total || 0) > 0; });

  var thBase = { fontSize:11, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em", padding:"8px 10px", borderBottom:"1px solid #E2E8F0", fontWeight:600, whiteSpace:"nowrap" };
  var stickyL = { position:"sticky", left:0, background:"#fff", zIndex:1 };
  var tdNum = { fontSize:13, padding:"8px 10px", borderBottom:"1px solid #F1F5F9", color:C.text, fontVariantNumeric:"tabular-nums", textAlign:"right" };
  var totCell = { fontSize:13, padding:"9px 10px", borderTop:"2px solid #E2E8F0", textAlign:"right", fontVariantNumeric:"tabular-nums", fontWeight:700, color:C.text };

  return <div>
    <Card style={{ marginBottom:16, padding:"12px 14px" }}>
      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
        <span style={{ fontSize:11, fontWeight:600, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em" }}>Year</span>
        <Select value={String(year)} onChange={function(e){ setYear(Number(e.target.value)); }} width={90} maxWidth={90}
          options={years.map(function(y){ return { value:String(y), label:String(y) }; })}/>
        <div style={{ flex:1, minWidth:8 }}/>
        {!skel && <span style={{ fontSize:11, color:C.textLight }}>{grandTotal} leads in {year}</span>}
      </div>
    </Card>

    <Card style={{ padding:"12px 14px" }}>
      <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:4 }}>Monthly intake by source</div>
      <div style={{ fontSize:10.5, color:"#94A3B8", marginBottom:10 }}>
        Leads per month by source (lead creation date, Cairo time) · Daily Request counted from daily-request intake
      </div>

      {state.error && <div style={{ fontSize:12, color:"#DC2626", padding:"8px 0" }}>Couldn't load: {state.error}</div>}

      {skel && !state.error && [0,1,2,3].map(function(i){ return <div key={i} style={{ height:34, marginBottom:6, background:"#F1F5F9", borderRadius:6 }}/>; })}

      {!skel && !state.error && (columns.length === 0 || activeMonths.length === 0) && <div style={{ fontSize:12, color:C.textLight, padding:"28px 8px", textAlign:"center" }}>No intake recorded in {year}.</div>}

      {!skel && !state.error && columns.length > 0 && activeMonths.length > 0 && <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", minWidth: 110 + columns.length * 74 }}>
          <thead><tr>
            <th style={Object.assign({}, thBase, { textAlign:"left" }, stickyL)}>Month</th>
            {columns.map(function(c){ return <th key={c} style={Object.assign({}, thBase, { textAlign:"right" })}>{c}</th>; })}
            <th style={Object.assign({}, thBase, { textAlign:"right", color:C.text })}>Total</th>
          </tr></thead>
          <tbody>
            {activeMonths.map(function(m){
              return <tr key={m.month}>
                <td style={Object.assign({}, tdNum, { textAlign:"left", color:C.text, fontWeight:600, whiteSpace:"nowrap" }, stickyL)}>{LB_MONTHS[m.month - 1]}</td>
                {columns.map(function(c){ var v = (m.counts && m.counts[c]) || 0; return <td key={c} style={Object.assign({}, tdNum, { color: v > 0 ? C.text : "#CBD5E1" })}>{v}</td>; })}
                <td style={Object.assign({}, tdNum, { fontWeight:700 })}>{m.total}</td>
              </tr>;
            })}
            <tr>
              <td style={Object.assign({}, totCell, { textAlign:"left", color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em" }, stickyL)}>Total</td>
              {columns.map(function(c){ return <td key={c} style={totCell}>{colTotals[c] || 0}</td>; })}
              <td style={Object.assign({}, totCell, { fontWeight:800, color:C.accent })}>{grandTotal}</td>
            </tr>
          </tbody>
        </table>
      </div>}
    </Card>
  </div>;
};

var AgentKpiCard = function(p) {
  var c = p.card;
  var skel = p.skeleton;
  var hasPeers = p.peerCount > 0;
  var agentVal = c.agent;
  var peerVal  = c.peer;

  // Delta math — null peer or empty peer set → no delta row.
  var deltaPct = null;
  var deltaDir = null; // "up" | "down" | "flat"
  if (!skel && hasPeers && peerVal != null && agentVal != null) {
    if (peerVal === 0 && agentVal === 0) {
      deltaDir = "flat";
    } else if (peerVal === 0) {
      deltaDir = agentVal > 0 ? "up" : "down";
    } else {
      deltaPct = ((agentVal - peerVal) / Math.abs(peerVal)) * 100;
      if (Math.abs(deltaPct) < 1) deltaDir = "flat";
      else deltaDir = deltaPct > 0 ? "up" : "down";
    }
  }

  // Color: ▲ green / ▼ red for higherIsBetter; inverted for responseHours.
  var goodColor = "#10B981", badColor = "#DC2626", neutralColor = "#94A3B8";
  var deltaColor = neutralColor;
  if (deltaDir === "up")   deltaColor = c.lowerIsBetter ? badColor  : goodColor;
  if (deltaDir === "down") deltaColor = c.lowerIsBetter ? goodColor : badColor;
  var arrow = deltaDir === "up" ? "▲" : deltaDir === "down" ? "▼" : deltaDir === "flat" ? "—" : "";

  return <Card style={{ padding:"12px 14px", minHeight:108 }}>
    <div style={{ fontSize:11, color:C.textLight, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em" }}>
      {c.label}
    </div>
    {skel ? <div>
      <div style={{ height:24, marginTop:8, borderRadius:4, background:"#F1F5F9", width:"60%" }}/>
      <div style={{ height:11, marginTop:8, borderRadius:4, background:"#F1F5F9", width:"40%" }}/>
    </div> : <div>
      <div style={{ fontSize:22, fontWeight:800, color:C.text, marginTop:6 }}>
        {agentVal == null ? "—" : c.fmt(agentVal)}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:4, flexWrap:"wrap" }}>
        <span style={{ fontSize:10, color:C.textLight }}>
          median: {peerVal == null ? "—" : c.fmt(peerVal)}
        </span>
        {deltaDir && <span style={{ fontSize:11, fontWeight:700, color:deltaColor }}>
          {arrow}{deltaPct != null ? " " + Math.abs(Math.round(deltaPct)) + "%" : ""}
        </span>}
      </div>
    </div>}
  </Card>;
};

var AgentKpiRow = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var f = p.filters;
  var agentId = p.agentId;

  useEffect(function(){
    if (!agentId) { setState({ loading: false, data: null, error: null }); return; }
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/agents/" + agentId + "/kpis" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [agentId, f.from, f.to, f.team, f.source]);

  if (!agentId) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:108, background:"#FAFBFC", border:"1px dashed #E2E8F0", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ fontSize:12, color:C.textLight }}>Select an agent to see KPIs</div>
    </Card>;
  }
  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load agent KPIs: {state.error}</div>
    </Card>;
  }

  var skel = state.loading || !state.data;
  var data = state.data || {};
  var ag = data.agent || {};
  var pm = data.peerMedian || {};
  var pc = data.peerCount || 0;

  var fmtPct   = function(v){ return v == null ? "—" : (Number(v) || 0).toFixed(1) + "%"; };
  var fmtHours = function(v){ if (v == null) return "—"; var n = Number(v) || 0; return n < 1 ? n.toFixed(1) + " h" : (Math.round(n * 10) / 10) + " h"; };
  var fmtNum   = function(v){ return v == null ? "—" : (Number(v) || 0).toLocaleString(); };

  var cards = [
    { id:"revenue",    label:"Revenue",       agent: ag.revenue,       peer: pm.revenue,       fmt: fmtEGP,   lowerIsBetter:false },
    { id:"deals",      label:"Deals",         agent: ag.deals,         peer: pm.deals,         fmt: fmtNum,   lowerIsBetter:false },
    { id:"conv",       label:"Conversion",    agent: ag.conversionPct, peer: pm.conversionPct, fmt: fmtPct,   lowerIsBetter:false },
    { id:"activities", label:"Activities",    agent: ag.activities,    peer: pm.activities,    fmt: fmtNum,   lowerIsBetter:false },
    { id:"response",   label:"Response time", agent: ag.responseHours, peer: pm.responseHours, fmt: fmtHours, lowerIsBetter:true  }
  ];

  return <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:10, marginBottom:14 }}>
    {cards.map(function(c){ return <AgentKpiCard key={c.id} card={c} skeleton={skel} peerCount={pc}/>; })}
  </div>;
};

// Geometry helpers shared across the radar component. 5-vertex regular
// pentagon, vertex 0 at the top (12 o'clock), going clockwise. SVG y-axis
// is inverted (positive = down), so sin(-π/2) = -1 puts vertex 0 above
// the centre as desired.
var RADAR_SIZE = 280;
var RADAR_CX = 140;
var RADAR_CY = 140;
var RADAR_R = 90;       // 100% guide-ring radius
var RADAR_LABEL_R = 112; // label centre — outside the 100% ring
var radarVertexAngle = function(i) { return -Math.PI / 2 + (i * 2 * Math.PI / 5); };
var radarVertexAt = function(i, scale) {
  var ang = radarVertexAngle(i);
  return { x: RADAR_CX + RADAR_R * scale * Math.cos(ang), y: RADAR_CY + RADAR_R * scale * Math.sin(ang) };
};
var radarPentagonPoints = function(scale) {
  return [0,1,2,3,4].map(function(i){ var v = radarVertexAt(i, scale); return v.x.toFixed(1) + "," + v.y.toFixed(1); }).join(" ");
};
var radarLabelPos = function(i) {
  var ang = radarVertexAngle(i);
  return { x: RADAR_CX + RADAR_LABEL_R * Math.cos(ang), y: RADAR_CY + RADAR_LABEL_R * Math.sin(ang) };
};
var radarLabelAnchor = function(i) {
  var cx = Math.cos(radarVertexAngle(i));
  return Math.abs(cx) < 0.3 ? "middle" : (cx > 0 ? "start" : "end");
};
var radarLabelBaseline = function(i) {
  var sy = Math.sin(radarVertexAngle(i));
  return Math.abs(sy) < 0.3 ? "central" : (sy > 0 ? "hanging" : "auto");
};

var AgentRadarChart = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [hoverIdx, setHoverIdx] = useState(null);
  var f = p.filters;
  var agentId = p.agentId;

  useEffect(function(){
    if (!agentId) { setState({ loading: false, data: null, error: null }); return; }
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    setHoverIdx(null);
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/agents/" + agentId + "/radar" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [agentId, f.from, f.to, f.team, f.source]);

  if (!agentId) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:300, background:"#FAFBFC", border:"1px dashed #E2E8F0", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ fontSize:12, color:C.textLight }}>Select an agent to see the performance radar</div>
    </Card>;
  }
  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load radar: {state.error}</div>
    </Card>;
  }

  var skel = state.loading || !state.data;
  var data = state.data || {};
  var axes = (data.axes || []);
  var peerCount = data.peerCount || 0;
  var hasPeers = peerCount > 0 && axes.length === 5;

  // Tooltip formatters per axis key — match Slice 1's KPI-row formatting
  // so the user sees the same units.
  var fmtPct   = function(v){ return v == null ? "—" : (Number(v) || 0).toFixed(1) + "%"; };
  var fmtHours = function(v){ if (v == null) return "—"; var n = Number(v) || 0; return n < 1 ? n.toFixed(1) + " h" : (Math.round(n * 10) / 10) + " h"; };
  var fmtNum   = function(v){ return v == null ? "—" : (Number(v) || 0).toLocaleString(); };
  var axisFormatters = { revenue: fmtEGP, deals: fmtNum, conversionPct: fmtPct, activities: fmtNum, responseHours: fmtHours };

  // Polygon points — agent vertex i scaled by its percentile (null → 0,
  // collapses to centre, tooltip explains). Peer median polygon is a
  // pentagon at uniform 50% scale by definition.
  var agentPoints = hasPeers ? axes.map(function(a, i){
    var pct = a.agentPercentile != null ? a.agentPercentile : 0;
    var v = radarVertexAt(i, pct / 100);
    return v.x.toFixed(1) + "," + v.y.toFixed(1);
  }).join(" ") : "";
  var medianPoints = radarPentagonPoints(0.5);

  var anyLowSample = axes.some(function(a){ return a.lowSample; });
  var hover = hoverIdx != null ? axes[hoverIdx] : null;
  var hoverPos = hoverIdx != null ? radarVertexAt(hoverIdx, ((hover && hover.agentPercentile) != null ? hover.agentPercentile : 0) / 100) : null;

  return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
    <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:6 }}>
      Performance radar{data.agentName ? " — " + data.agentName : ""}
    </div>
    {skel ? <div style={{ height: RADAR_SIZE, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <svg width={RADAR_SIZE} height={RADAR_SIZE} viewBox={"0 0 " + RADAR_SIZE + " " + RADAR_SIZE} style={{ opacity:0.5 }}>
        {[0.25, 0.5, 0.75, 1].map(function(s){
          return <polygon key={s} points={radarPentagonPoints(s)} fill="none" stroke="#E2E8F0" strokeWidth={s === 1 ? 1.5 : 0.8} strokeDasharray={s === 1 ? "" : "2 2"}/>;
        })}
      </svg>
    </div> : !hasPeers ? <div style={{ height: RADAR_SIZE, display:"flex", alignItems:"center", justifyContent:"center", color:C.textLight, fontSize:12 }}>
      No peer data for radar — need at least one same-role peer
    </div> : <div style={{ position:"relative", display:"flex", justifyContent:"center" }}>
      <svg width={RADAR_SIZE} height={RADAR_SIZE} viewBox={"0 0 " + RADAR_SIZE + " " + RADAR_SIZE}>
        {/* Concentric guide rings — pentagonal, matching the polygon shape. */}
        {[0.25, 0.5, 0.75, 1].map(function(s){
          return <polygon key={s} points={radarPentagonPoints(s)} fill="none" stroke="#E2E8F0" strokeWidth={s === 1 ? 1.5 : 0.8} strokeDasharray={s === 1 ? "" : "2 2"}/>;
        })}
        {/* Axis spokes from centre to each vertex. */}
        {[0,1,2,3,4].map(function(i){
          var v = radarVertexAt(i, 1);
          return <line key={i} x1={RADAR_CX} y1={RADAR_CY} x2={v.x} y2={v.y} stroke="#E2E8F0" strokeWidth={0.8}/>;
        })}
        {/* Peer median polygon — drawn first so the agent polygon sits on top. */}
        <polygon points={medianPoints} fill="rgba(148, 163, 184, 0.18)" stroke="#94A3B8" strokeWidth={1}/>
        {/* Agent polygon. */}
        <polygon points={agentPoints} fill="rgba(139, 92, 246, 0.32)" stroke="#8B5CF6" strokeWidth={1.5}/>
        {/* Vertex dots — the hover/tap target. r=5 for an easy hit area. */}
        {axes.map(function(a, i){
          var pct = a.agentPercentile != null ? a.agentPercentile : 0;
          var v = radarVertexAt(i, pct / 100);
          return <circle key={a.key} cx={v.x} cy={v.y} r={5} fill="#8B5CF6" stroke="#fff" strokeWidth={1.5} style={{ cursor:"pointer" }}
            onMouseEnter={function(){ setHoverIdx(i); }} onMouseLeave={function(){ setHoverIdx(null); }}
            onClick={function(){ setHoverIdx(hoverIdx === i ? null : i); }}/>;
        })}
        {/* Axis labels — outside the 100% ring; asterisk on low-sample axes. */}
        {axes.map(function(a, i){
          var lp = radarLabelPos(i);
          return <text key={a.key} x={lp.x} y={lp.y} textAnchor={radarLabelAnchor(i)} dominantBaseline={radarLabelBaseline(i)}
            fontSize={11} fontWeight={600} fill={C.text}>
            {a.label}{a.lowSample ? " *" : ""}
          </text>;
        })}
      </svg>
      {hover && hoverPos && <div style={{
        position:"absolute",
        left: Math.min(RADAR_SIZE - 140, Math.max(0, hoverPos.x + 12)),
        top:  Math.max(0, hoverPos.y - 38),
        background:"#1F2937", color:"#fff", padding:"6px 10px", borderRadius:6,
        fontSize:11, lineHeight:1.4, pointerEvents:"none", zIndex:5,
        boxShadow:"0 2px 8px rgba(0,0,0,0.18)", whiteSpace:"nowrap"
      }}>
        <div style={{ fontWeight:700 }}>{hover.label}</div>
        <div>Value: {(axisFormatters[hover.key] || fmtNum)(hover.agentValue)}</div>
        <div>Percentile: {hover.agentPercentile != null ? Math.round(hover.agentPercentile) + "%" : "—"}{hover.lowSample ? " (low sample)" : ""}</div>
      </div>}
    </div>}
    {/* Legend + low-sample footnote */}
    {!skel && hasPeers && <div style={{ display:"flex", gap:14, fontSize:10, color:C.textLight, marginTop:6, justifyContent:"center", flexWrap:"wrap" }}>
      <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
        <span style={{ width:10, height:10, borderRadius:2, background:"rgba(139, 92, 246, 0.45)", border:"1px solid #8B5CF6" }}/>
        Selected agent
      </span>
      <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
        <span style={{ width:10, height:10, borderRadius:2, background:"rgba(148, 163, 184, 0.3)", border:"1px solid #94A3B8" }}/>
        Peer median (50th percentile)
      </span>
      {anyLowSample && <span>* low sample — under 3 peers with data, or no spread</span>}
    </div>}
  </Card>;
};

// Heatmap geometry. 7 rows (Sat..Fri, Egypt business week) × 24 cols.
// Cell 20×20 with a 2px gap → 18×18 visible rect. Left gutter holds
// day labels; top header holds hour ticks at 0/4/8/12/16/20.
var HEAT_CELL = 20;
var HEAT_GUTTER_L = 50;
var HEAT_HEADER_H = 18;
var HEAT_COLS = 24;
var HEAT_ROWS = 7;
var HEAT_GRID_W = HEAT_COLS * HEAT_CELL;
var HEAT_GRID_H = HEAT_ROWS * HEAT_CELL;
var HEAT_TOTAL_W = HEAT_GUTTER_L + HEAT_GRID_W;
var HEAT_TOTAL_H = HEAT_HEADER_H + HEAT_GRID_H;
var HEAT_DAY_NAMES = ["Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri"];
var HEAT_DAY_NAMES_FULL = ["Saturday", "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
var HEAT_HOUR_TICKS = [0, 4, 8, 12, 16, 20];

// Cream → orange → deep red 3-stop linear interpolation. Plain
// 2-stop (cream → red) skips the orange band the spec calls for; this
// returns the proper warm-yellow midpoint. t in [0, 1]; t<=0 returns
// the empty-cell grey (count=0 cells handled separately by caller).
var heatColor = function(t) {
  if (!(t > 0)) return "#F1F5F9"; // empty-cell grey
  var stops = [
    { t: 0,    r: 255, g: 247, b: 237 }, // cream  #FFF7ED
    { t: 0.5,  r: 249, g: 115, b: 22  }, // orange #F97316
    { t: 1.0,  r: 185, g: 28,  b: 28  }  // deep red #B91C1C
  ];
  var s0 = stops[0], s1 = stops[1];
  if (t > stops[1].t) { s0 = stops[1]; s1 = stops[2]; }
  var lt = (t - s0.t) / (s1.t - s0.t);
  var r = Math.round(s0.r + (s1.r - s0.r) * lt);
  var g = Math.round(s0.g + (s1.g - s0.g) * lt);
  var b = Math.round(s0.b + (s1.b - s0.b) * lt);
  return "rgb(" + r + "," + g + "," + b + ")";
};

var AgentActivityHeatmap = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [hoverIdx, setHoverIdx] = useState(null);
  var f = p.filters;
  var agentId = p.agentId;

  useEffect(function(){
    if (!agentId) { setState({ loading: false, data: null, error: null }); return; }
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    setHoverIdx(null);
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/agents/" + agentId + "/heatmap" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [agentId, f.from, f.to, f.team, f.source]);

  if (!agentId) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:240, background:"#FAFBFC", border:"1px dashed #E2E8F0", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ fontSize:12, color:C.textLight }}>Select an agent to see the activity heatmap</div>
    </Card>;
  }
  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load activity heatmap: {state.error}</div>
    </Card>;
  }

  var skel = state.loading || !state.data;
  var data = state.data || {};
  var grid = data.grid || [];
  var maxCount = data.maxCount || 0;
  var total = data.totalActivities || 0;
  var peak = data.peak;
  var quiet = data.quiet;

  var hoverCell = hoverIdx != null ? grid[hoverIdx] : null;
  var hoverPos = hoverIdx != null ? {
    x: HEAT_GUTTER_L + (hoverIdx % HEAT_COLS) * HEAT_CELL + HEAT_CELL / 2,
    y: HEAT_HEADER_H + Math.floor(hoverIdx / HEAT_COLS) * HEAT_CELL + HEAT_CELL / 2
  } : null;

  return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
    <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:8 }}>
      Activity heatmap{data.agentName ? " — " + data.agentName : ""}
      <span style={{ fontSize:10, fontWeight:500, color:C.textLight, marginLeft:8 }}>
        calls + meetings + followups · Africa/Cairo time
      </span>
    </div>
    {skel ? <div style={{ height: HEAT_TOTAL_H, opacity:0.5 }}>
      <svg width={HEAT_TOTAL_W} height={HEAT_TOTAL_H} viewBox={"0 0 " + HEAT_TOTAL_W + " " + HEAT_TOTAL_H}>
        {[0,1,2,3,4,5,6].map(function(d){
          return [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23].map(function(h){
            return <rect key={d + "-" + h} x={HEAT_GUTTER_L + h * HEAT_CELL + 1} y={HEAT_HEADER_H + d * HEAT_CELL + 1}
              width={HEAT_CELL - 2} height={HEAT_CELL - 2} fill="#F1F5F9" rx={2}/>;
          });
        })}
      </svg>
    </div> : total === 0 ? <div style={{ height: HEAT_TOTAL_H, display:"flex", alignItems:"center", justifyContent:"center", color:C.textLight, fontSize:12 }}>
      No activities in this period
    </div> : <div style={{ position:"relative" }}>
      <svg width={HEAT_TOTAL_W} height={HEAT_TOTAL_H} viewBox={"0 0 " + HEAT_TOTAL_W + " " + HEAT_TOTAL_H} style={{ display:"block", maxWidth:"100%" }}>
        {/* Hour ticks across the top — every 4 hours to keep readable. */}
        {HEAT_HOUR_TICKS.map(function(h){
          return <text key={h} x={HEAT_GUTTER_L + h * HEAT_CELL + HEAT_CELL / 2} y={12}
            textAnchor="middle" fontSize={10} fill={C.textLight}>{h}</text>;
        })}
        {/* 7 day rows — label gutter + 24 cells per row. */}
        {HEAT_DAY_NAMES.map(function(name, d){
          return <g key={d}>
            <text x={HEAT_GUTTER_L - 8} y={HEAT_HEADER_H + d * HEAT_CELL + HEAT_CELL / 2 + 3}
              textAnchor="end" fontSize={10} fill={C.textLight}>{name}</text>
            {Array.from({ length: HEAT_COLS }).map(function(_, h){
              var i = d * HEAT_COLS + h;
              var cell = grid[i] || { count: 0 };
              var t = maxCount > 0 ? cell.count / maxCount : 0;
              var fill = cell.count === 0 ? "#F1F5F9" : heatColor(t);
              var isHover = hoverIdx === i;
              return <rect key={h} x={HEAT_GUTTER_L + h * HEAT_CELL + 1} y={HEAT_HEADER_H + d * HEAT_CELL + 1}
                width={HEAT_CELL - 2} height={HEAT_CELL - 2} rx={2}
                fill={fill}
                stroke={isHover ? "#1F2937" : "none"} strokeWidth={isHover ? 1.5 : 0}
                style={{ cursor:"pointer" }}
                onMouseEnter={function(){ setHoverIdx(i); }}
                onMouseLeave={function(){ setHoverIdx(null); }}
                onClick={function(){ setHoverIdx(hoverIdx === i ? null : i); }}/>;
            })}
          </g>;
        })}
      </svg>
      {hoverCell && hoverPos && <div style={{
        position:"absolute",
        left: Math.min(HEAT_TOTAL_W - 160, Math.max(0, hoverPos.x + 10)),
        top:  Math.max(0, hoverPos.y - 44),
        background:"#1F2937", color:"#fff", padding:"6px 10px", borderRadius:6,
        fontSize:11, lineHeight:1.4, pointerEvents:"none", zIndex:5,
        boxShadow:"0 2px 8px rgba(0,0,0,0.18)", whiteSpace:"nowrap"
      }}>
        <div style={{ fontWeight:700 }}>{HEAT_DAY_NAMES_FULL[hoverCell.dow]} at {hoverCell.hour}:00</div>
        <div>{hoverCell.count} {hoverCell.count === 1 ? "activity" : "activities"}</div>
      </div>}
    </div>}
    {/* Gradient legend + peak/quiet insights */}
    {!skel && total > 0 && <div style={{ marginTop:10 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:10, color:C.textLight }}>
        <span>0</span>
        <svg width={140} height={10} style={{ display:"block" }}>
          <defs>
            <linearGradient id="heatmapGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor="#FFF7ED"/>
              <stop offset="50%"  stopColor="#F97316"/>
              <stop offset="100%" stopColor="#B91C1C"/>
            </linearGradient>
          </defs>
          <rect x={0} y={0} width={140} height={10} rx={2} fill="url(#heatmapGradient)"/>
        </svg>
        <span>{maxCount}</span>
        <span style={{ marginLeft:6 }}>· {total.toLocaleString()} total</span>
      </div>
      <div style={{ fontSize:11, color:C.textLight, marginTop:6, lineHeight:1.5 }}>
        {peak && <div>
          <span style={{ fontWeight:600, color:C.text }}>Most active:</span> {peak.dayName} at {peak.timeLabel} ({peak.count} {peak.count === 1 ? "activity" : "activities"})
        </div>}
        {quiet && <div>
          <span style={{ fontWeight:600, color:C.text }}>Quiet zone:</span> {quiet.description} ({quiet.percentage}% of activities)
        </div>}
      </div>
    </div>}
    {/* Permanent caveat — explains the cell-count vs KPI-activities gap users
        will hit (KPI counts 5 types, heatmap counts 3). Always shown except
        during initial skeleton load, including in the empty state where
        someone might wonder why an agent with KPI activity has no cells. */}
    {!skel && <div style={{ fontSize:10, color:C.textLight, marginTop:10, lineHeight:1.4 }}>
      This view shows only active contact actions (calls, meetings, followups). Notes and emails are tracked separately and don't appear on this map.
    </div>}
  </Card>;
};

// Stage-progression delta colour. Spec: green when agent ≥ median + 5pp,
// red when agent ≤ median − 5pp, purple (neutral) otherwise. Null → purple.
var stageDeltaColor = function(agentPct, medianPct) {
  if (agentPct == null || medianPct == null) return "#8B5CF6";
  var gap = agentPct - medianPct;
  if (gap >= 5)  return "#10B981";
  if (gap <= -5) return "#DC2626";
  return "#8B5CF6";
};

var AgentStageProgression = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var f = p.filters;
  var agentId = p.agentId;

  useEffect(function(){
    if (!agentId) { setState({ loading: false, data: null, error: null }); return; }
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/agents/" + agentId + "/stage-progression" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [agentId, f.from, f.to, f.team, f.source]);

  if (!agentId) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:240, background:"#FAFBFC", border:"1px dashed #E2E8F0", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ fontSize:12, color:C.textLight }}>Select an agent to see the stage progression funnel</div>
    </Card>;
  }
  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load stage progression: {state.error}</div>
    </Card>;
  }

  var skel = state.loading || !state.data;
  var data = state.data || {};
  var agentStages = (data.agent && data.agent.stages) || [];
  var medianStages = (data.teamMedian && data.teamMedian.stages) || [];
  var medianByKey = {};
  medianStages.forEach(function(s){ medianByKey[s.key] = s.pctOfReceived; });
  var leak = data.biggestLeak;
  var peerCount = data.peerCount || 0;

  // Empty state — agent received zero leads in range. Distinguished
  // from "no peers" (still render the agent bars at 0%) by checking the
  // received row's count.
  var receivedCount = !skel && agentStages.length ? (agentStages[0].count || 0) : null;
  var isEmpty = !skel && receivedCount === 0;

  // Skeleton: 5 grey bar pairs.
  var skeletonRows = [0,1,2,3,4];

  return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
    <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:4 }}>
      Stage progression{data.agentName ? " — " + data.agentName : ""}
      <span style={{ fontSize:10, fontWeight:500, color:C.textLight, marginLeft:8 }}>
        forward-superset funnel · agent vs same-role peer median
      </span>
    </div>
    <div style={{ fontSize:10, color:C.textLight, marginBottom:10, lineHeight:1.4 }}>
      Contacted = any agent action recorded on the lead (status change, feedback, callback, or logged activity). The meaningful conversion funnel begins at Meeting.
    </div>
    {skel ? <div>
      {skeletonRows.map(function(i){
        return <div key={i} style={{ display:"grid", gridTemplateColumns:"110px 1fr 110px", gap:10, alignItems:"center", marginBottom:14 }}>
          <div style={{ height:10, background:"#F1F5F9", borderRadius:3, width:"60%" }}/>
          <div>
            <div style={{ height:10, background:"#F1F5F9", borderRadius:5, width: (90 - i * 15) + "%" }}/>
            <div style={{ height:6 }}/>
            <div style={{ height:10, background:"#F1F5F9", borderRadius:5, width: (80 - i * 12) + "%" }}/>
          </div>
          <div style={{ height:10, background:"#F1F5F9", borderRadius:3 }}/>
        </div>;
      })}
    </div> : isEmpty ? <div style={{ minHeight:140, display:"flex", alignItems:"center", justifyContent:"center", color:C.textLight, fontSize:12 }}>
      No leads received in this period
    </div> : <div>
      {agentStages.map(function(s){
        var medianPct = medianByKey[s.key];
        var agentPct = s.pctOfReceived;
        var color = stageDeltaColor(agentPct, medianPct);
        var agentBarW = agentPct != null ? Math.max(0, Math.min(100, agentPct)) : 0;
        var medianBarW = medianPct != null ? Math.max(0, Math.min(100, medianPct)) : 0;
        return <div key={s.key} style={{ display:"grid", gridTemplateColumns:"110px 1fr 110px", gap:10, alignItems:"center", marginBottom:14 }}>
          <div style={{ fontSize:11, fontWeight:600, color:C.text }}>{s.label}</div>
          <div>
            {/* Agent bar (top) — colour reflects gap vs median. */}
            <div style={{ height:10, background:"#F1F5F9", borderRadius:5, overflow:"hidden" }}>
              <div style={{ width: agentBarW + "%", height:"100%", background: color, transition:"width 0.3s, background 0.3s" }}/>
            </div>
            <div style={{ height:6 }}/>
            {/* Peer median bar (bottom) — always grey. */}
            <div style={{ height:10, background:"#F1F5F9", borderRadius:5, overflow:"hidden" }}>
              <div style={{ width: medianBarW + "%", height:"100%", background: "#94A3B8", transition:"width 0.3s" }}/>
            </div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.text }}>
              {(s.count || 0).toLocaleString()} {s.count === 1 ? "lead" : "leads"}
              {agentPct != null && <span style={{ fontWeight:500, color:C.textLight }}> · {agentPct}%</span>}
            </div>
            <div style={{ fontSize:10, color:C.textLight, marginTop:8 }}>
              median: {medianPct != null ? medianPct + "%" : "—"}
            </div>
          </div>
        </div>;
      })}
    </div>}
    {/* Leak callout + peer benchmark count */}
    {!skel && !isEmpty && <div style={{ fontSize:11, color:C.textLight, marginTop:6, lineHeight:1.5 }}>
      {leak ? <div>
        <span style={{ fontWeight:600, color:"#DC2626" }}>Biggest leak:</span> {capitalizeStage(leak.fromStage)} → {capitalizeStage(leak.toStage)} ({leak.agentPct}% vs {leak.medianPct}% median, {leak.gap}pp gap)
      </div> : peerCount > 0 ? <div>
        No significant gaps vs peer median (within ±5pp on every stage)
      </div> : <div>
        No same-role peers to benchmark against
      </div>}
      {peerCount > 0 && <div style={{ marginTop:2 }}>Median computed across {peerCount} same-role {peerCount === 1 ? "peer" : "peers"}.</div>}
    </div>}
  </Card>;
};

// Title-case stage keys for the leak callout. Single-word stages
// stay verbatim; "EOI" gets all-caps.
var capitalizeStage = function(key) {
  if (!key) return "";
  if (key === "eoi") return "EOI";
  return key.charAt(0).toUpperCase() + key.slice(1);
};

// Compact-table styles shared by RecentDealsTable + StuckLeadsTable.
// Mirrors the DealsAtRiskTable styling on the Pipeline tab.
var agentTableTh      = { textAlign:"left",  padding:"6px 8px", fontWeight:600, color:C.textLight, textTransform:"uppercase", letterSpacing:"0.04em", fontSize:10 };
var agentTableThRight = Object.assign({}, agentTableTh, { textAlign:"right" });

var fmtCloseDate = function(d) {
  if (!d) return "—";
  var dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

var RecentDealsTable = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [expanded, setExpanded] = useState(false);
  var f = p.filters;
  var agentId = p.agentId;

  useEffect(function() {
    if (!agentId) { setState({ loading: false, data: null, error: null }); return; }
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    setExpanded(false);
    var qs = "?from=" + f.from + "&to=" + f.to;
    if (f.team) qs += "&team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += "&source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/agents/" + agentId + "/recent-deals" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [agentId, f.from, f.to, f.team, f.source]);

  if (!agentId) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:200, background:"#FAFBFC", border:"1px dashed #E2E8F0", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ fontSize:12, color:C.textLight }}>Select an agent to see recent deals</div>
    </Card>;
  }
  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load recent deals: {state.error}</div>
    </Card>;
  }

  var skel = state.loading || !state.data;
  var deals = (state.data && state.data.deals) || [];
  var total = (state.data && state.data.total) || 0;
  var visible = expanded ? deals.slice(0, 50) : deals.slice(0, 10);

  var fmtDays = function(d) {
    var n = Number(d) || 0;
    return n < 1 ? n.toFixed(1) + " d" : Math.round(n) + " d";
  };
  // Fetch-first — see the note on DealsAtRiskTable's openLead.
  var openLead = function(deal) {
    if (!deal || !deal.leadId) return;
    var shell = { _id: deal.leadId, name: deal.name, status: "DoneDeal" };
    apiFetch("/api/leads/" + deal.leadId, "GET", null, p.token).then(function(fresh){
      if (p.nav) p.nav("leads", (fresh && fresh._id) ? fresh : shell);
    }).catch(function(){ if (p.nav) p.nav("leads", shell); });
  };

  return <Card style={{ marginBottom:14, padding:"12px 14px" }}>
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, flexWrap:"wrap", gap:8 }}>
      <div style={{ fontSize:12, fontWeight:700, color:C.text }}>Recent deals</div>
      {!skel && <div style={{ fontSize:11, color:C.textLight }}>{total} {total === 1 ? "deal" : "deals"} closed in range</div>}
    </div>

    {skel && [0,1,2,3].map(function(i){ return <div key={i} style={{ height:32, marginBottom:6, background:"#F1F5F9", borderRadius:6 }}/>; })}

    {!skel && deals.length === 0 && <div style={{ fontSize:12, color:C.textLight, padding:"24px 8px", textAlign:"center" }}>
      No deals closed in this period
    </div>}

    {!skel && deals.length > 0 && <div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", fontSize:11, borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ borderBottom:"1px solid #E2E8F0" }}>
              <th style={agentTableTh}>Lead</th>
              <th style={agentTableTh}>Project</th>
              <th style={agentTableThRight}>Value</th>
              <th style={agentTableThRight}>Days to close</th>
              <th style={agentTableThRight}>Close date</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(function(d, i){
              return <tr key={d.leadId + "-" + i}
                onClick={function(){ openLead(d); }}
                onMouseEnter={function(e){ e.currentTarget.style.background = "#F8FAFC"; }}
                onMouseLeave={function(e){ e.currentTarget.style.background = "transparent"; }}
                style={{ borderBottom:"1px solid #F1F5F9", cursor:"pointer" }}>
                <td style={{ padding:"8px", color:C.text, fontWeight:600 }}>
                  {d.name || "—"}
                  {d.isSplit && <span style={{ marginLeft:6, fontSize:9, color:C.textLight, fontWeight:500 }}>(split)</span>}
                </td>
                <td style={{ padding:"8px", color:C.textLight }}>{d.project || "—"}</td>
                <td style={{ padding:"8px", textAlign:"right", color:C.text }}>{d.value > 0 ? fmtEGP(d.value) : "—"}</td>
                <td style={{ padding:"8px", textAlign:"right", color:C.textLight }}>{fmtDays(d.daysToClose)}</td>
                <td style={{ padding:"8px", textAlign:"right", color:C.textLight }}>{fmtCloseDate(d.closeDate)}</td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      {!expanded && total > 10 && <div style={{ marginTop:8, textAlign:"center" }}>
        <button onClick={function(){ setExpanded(true); }} style={{
          background:"none", border:"none", color:C.accent, fontSize:11, fontWeight:600, cursor:"pointer", padding:"6px 12px"
        }}>View all {total > 50 ? "(top 50 of " + total + ")" : total} deals →</button>
      </div>}
    </div>}
  </Card>;
};

var StuckLeadsTable = function(p) {
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [expanded, setExpanded] = useState(false);
  var f = p.filters;
  var agentId = p.agentId;

  useEffect(function() {
    if (!agentId) { setState({ loading: false, data: null, error: null }); return; }
    var aborted = false;
    setState(function(s){ return Object.assign({}, s, { loading: true, error: null }); });
    setExpanded(false);
    // Stuck-leads is a snapshot — no from/to. Team + source still wired.
    var qs = "";
    if (f.team) qs += (qs ? "&" : "?") + "team=" + encodeURIComponent(f.team);
    if (f.source && f.source !== "all") qs += (qs ? "&" : "?") + "source=" + encodeURIComponent(f.source);
    apiFetch("/api/reports/agents/" + agentId + "/stuck-leads" + qs, "GET", null, p.token)
      .then(function(d){ if (!aborted) setState({ loading: false, data: d, error: null }); })
      .catch(function(e){ if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function(){ aborted = true; };
  }, [agentId, f.team, f.source]);

  if (!agentId) {
    return <Card style={{ marginBottom:14, padding:"14px 16px", minHeight:200, background:"#FAFBFC", border:"1px dashed #E2E8F0", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ fontSize:12, color:C.textLight }}>Select an agent to see stuck leads</div>
    </Card>;
  }
  if (state.error) {
    return <Card style={{ marginBottom:14, padding:"14px 16px" }}>
      <div style={{ fontSize:12, color:"#DC2626", fontWeight:600 }}>Couldn't load stuck leads: {state.error}</div>
    </Card>;
  }

  var skel = state.loading || !state.data;
  var deals = (state.data && state.data.deals) || [];
  var total = (state.data && state.data.total) || 0;
  var visible = expanded ? deals.slice(0, 50) : deals.slice(0, 10);

  var stageBadge = function(stage) {
    var color = stage === "HotCase" ? "#F59E0B" : "#10B981";
    var label = stage === "HotCase" ? "Hot Case" : "Meeting Done";
    var bg    = stage === "HotCase" ? "rgba(245, 158, 11, 0.12)" : "rgba(16, 185, 129, 0.12)";
    return <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:6, background:bg, color:color, fontSize:10, fontWeight:700 }}>{label}</span>;
  };
  var ageCell = function(days) {
    var color = days >= 14 ? "#DC2626" : "#F59E0B";
    var fmt = days >= 1 ? Math.round(days) : days.toFixed(1);
    return <span style={{ color:color, fontWeight:700 }}>{fmt} d</span>;
  };
  // Fetch-first — see the note on DealsAtRiskTable's openLead.
  var openLead = function(deal) {
    if (!deal || !deal.leadId) return;
    var shell = { _id: deal.leadId, name: deal.name, status: deal.stage };
    apiFetch("/api/leads/" + deal.leadId, "GET", null, p.token).then(function(fresh){
      if (p.nav) p.nav("leads", (fresh && fresh._id) ? fresh : shell);
    }).catch(function(){ if (p.nav) p.nav("leads", shell); });
  };

  return <Card style={{ marginBottom:14, padding:"12px 14px" }}>
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4, flexWrap:"wrap", gap:8 }}>
      <div style={{ fontSize:12, fontWeight:700, color:C.text }}>Stuck leads</div>
      {!skel && <div style={{ fontSize:11, color:C.textLight }}>{total} {total === 1 ? "lead" : "leads"} stuck 7+ days at stage</div>}
    </div>
    <div style={{ fontSize:10, color:C.textLight, marginBottom:8, lineHeight:1.4 }}>
      Stuck = no stage progression. Days counted from when the lead first entered its current stage. Reschedules and status flips don't reset the clock.
    </div>

    {skel && [0,1,2,3].map(function(i){ return <div key={i} style={{ height:32, marginBottom:6, background:"#F1F5F9", borderRadius:6 }}/>; })}

    {!skel && deals.length === 0 && <div style={{ fontSize:12, color:C.textLight, padding:"24px 8px", textAlign:"center" }}>
      ✓ No stuck leads — every advanced-stage lead has progressed within 7 days
    </div>}

    {!skel && deals.length > 0 && <div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", fontSize:11, borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ borderBottom:"1px solid #E2E8F0" }}>
              <th style={agentTableTh}>Lead</th>
              <th style={agentTableTh}>Stage</th>
              <th style={agentTableThRight}>Value</th>
              <th style={agentTableThRight}>Days in stage</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(function(d, i){
              return <tr key={d.leadId + "-" + i}
                onClick={function(){ openLead(d); }}
                onMouseEnter={function(e){ e.currentTarget.style.background = "#F8FAFC"; }}
                onMouseLeave={function(e){ e.currentTarget.style.background = "transparent"; }}
                style={{ borderBottom:"1px solid #F1F5F9", cursor:"pointer" }}>
                <td style={{ padding:"8px", color:C.text, fontWeight:600 }}>{d.name || "—"}</td>
                <td style={{ padding:"8px" }}>{stageBadge(d.stage)}</td>
                <td style={{ padding:"8px", textAlign:"right", color:C.text }}>{d.value > 0 ? fmtEGP(d.value) : "—"}</td>
                <td style={{ padding:"8px", textAlign:"right" }}>{ageCell(d.daysInStage)}</td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      {!expanded && total > 10 && <div style={{ marginTop:8, textAlign:"center" }}>
        <button onClick={function(){ setExpanded(true); }} style={{
          background:"none", border:"none", color:C.accent, fontSize:11, fontWeight:600, cursor:"pointer", padding:"6px 12px"
        }}>View all {total > 50 ? "(top 50 of " + total + ")" : total} stuck →</button>
      </div>}
    </div>}
  </Card>;
};

// ===== PROJECTS =====
var ProjectsPage = function(p) {
  var t=p.t;
  var pj=[{id:1,name:"العاصمة الإدارية",developer:"شركة النيل",units:450,sold:280,loc:"العاصمة الإدارية الجديدة"},{id:2,name:"المستقبل سيتي",developer:"شركة الأهلي",units:320,sold:190,loc:"المستقبل سيتي"},{id:3,name:"التجمع الخامس",developer:"بالم هيلز",units:200,sold:145,loc:"القاهرة الجديدة"},{id:4,name:"الشروق",developer:"المقاولون العرب",units:180,sold:95,loc:"مدينة الشروق"},{id:5,name:"6 أكتوبر",developer:"سوديك",units:260,sold:210,loc:"مدينة 6 أكتوبر"}];
  return <div style={{ padding:"18px 16px 40px" }}>
    <h2 style={{ margin:"0 0 18px", fontSize:18, fontWeight:700 }}>{t.projects}</h2>
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      {pj.map(function(pr){return <Card key={pr.id} style={{ flex:"1 1 260px", maxWidth:340 }}>
        <h3 style={{ margin:"0 0 4px", fontSize:15, fontWeight:700 }}>{pr.name}</h3>
        <div style={{ fontSize:12, color:C.textLight, marginBottom:14 }}>{pr.developer} · {pr.loc}</div>
        <div style={{ display:"flex", justifyContent:"space-around", marginBottom:12 }}>
          {[{v:pr.units,l:t.units,c:C.text},{v:pr.sold,l:t.sold,c:C.success},{v:pr.units-pr.sold,l:t.available,c:C.warning}].map(function(s){return <div key={s.l} style={{ textAlign:"center" }}><div style={{ fontSize:18, fontWeight:700, color:s.c }}>{s.v}</div><div style={{ fontSize:10, color:C.textLight }}>{s.l}</div></div>;})}
        </div>
        <div style={{ height:6, background:"#F1F5F9", borderRadius:3 }}><div style={{ height:"100%", width:Math.round(pr.sold/pr.units*100)+"%", background:"linear-gradient(90deg,"+C.success+","+C.accentLight+")", borderRadius:3 }}/></div>
      </Card>;})}
    </div>
  </div>;
};

// Phase R-13.3 — Rotation Diagnostics tab inside SettingsPage. Pulls one
// payload from GET /api/rotation/diagnostics (admin / operation only; BE
// enforces the gate). Five collapsible sections, all default-expanded.
// Manual refresh only — no auto-poll (would create churn during cron runs).
var RotationDiagnosticsTab = function(props) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState("");
  var [expanded, setExpanded] = useState({
    recent: true, lastRun: true, tiers: true, available: true, locked: true
  });
  // TEMPORARY — slice.status pollution dry-run (read-only). Separate state from
  // the rotation-diagnostics `data` above; never auto-fires (explicit button only).
  var [pollData, setPollData] = useState(null);
  var [pollLoading, setPollLoading] = useState(false);
  var [pollError, setPollError] = useState("");
  var loadPoll = async function() {
    setPollLoading(true); setPollError("");
    try {
      var d = await apiFetch("/api/admin/rotation-pollution-dryrun", "GET", null, props.token);
      setPollData(d);
    } catch(e) {
      setPollError((e && e.message) || "Failed to run dry-run");
    }
    setPollLoading(false);
  };
  // TEMPORARY — Phase 1 single-lead cleanup state (separate from the dry-run).
  var [clLeadId, setClLeadId] = useState("");
  var [clBusy, setClBusy] = useState(false);
  var [clError, setClError] = useState("");
  var [clData, setClData] = useState(null);
  var [clConfirm, setClConfirm] = useState("");
  var runCleanupSingle = async function(apply) {
    setClBusy(true); setClError(""); setClData(null);
    try {
      var body = { leadId: String(clLeadId || "").trim(), dryRun: !apply };
      if (apply) body.confirm = "yes-cleanup-this-lead";
      var d = await apiFetch("/api/admin/rotation-pollution-cleanup-single", "POST", body, props.token);
      setClData(d);
      if (apply) setClConfirm("");   // reset the confirm gate after an apply
    } catch(e) {
      setClError((e && e.message) || "Failed");
    }
    setClBusy(false);
  };
  // TEMPORARY — Phase 2 full-database cleanup state.
  var [allBusy, setAllBusy] = useState(false);
  var [allError, setAllError] = useState("");
  var [allData, setAllData] = useState(null);
  var [allConfirm, setAllConfirm] = useState("");
  var runCleanupAll = async function(apply) {
    setAllBusy(true); setAllError(""); setAllData(null);
    try {
      var body = { dryRun: !apply };
      if (apply) body.confirm = "yes-cleanup-all-polluted-slices";
      var d = await apiFetch("/api/admin/rotation-pollution-cleanup-all", "POST", body, props.token);
      setAllData(d);
      if (apply) setAllConfirm("");
    } catch(e) {
      setAllError((e && e.message) || "Failed");
    }
    setAllBusy(false);
  };
  // TEMPORARY — Phase 3 holder-slice cleanup state.
  var [holdBusy, setHoldBusy] = useState(false);
  var [holdError, setHoldError] = useState("");
  var [holdData, setHoldData] = useState(null);
  var [holdConfirm, setHoldConfirm] = useState("");
  var runCleanupHolders = async function(apply) {
    setHoldBusy(true); setHoldError(""); setHoldData(null);
    try {
      var body = { dryRun: !apply };
      if (apply) body.confirm = "yes-cleanup-holder-pollution";
      var d = await apiFetch("/api/admin/rotation-pollution-cleanup-holders", "POST", body, props.token);
      setHoldData(d);
      if (apply) setHoldConfirm("");
    } catch(e) {
      setHoldError((e && e.message) || "Failed");
    }
    setHoldBusy(false);
  };
  // TEMPORARY — single-slice inspection state.
  var [insLeadId, setInsLeadId] = useState("");
  var [insAgent, setInsAgent] = useState("");
  var [insBusy, setInsBusy] = useState(false);
  var [insError, setInsError] = useState("");
  var [insData, setInsData] = useState(null);
  var runInspect = async function() {
    setInsBusy(true); setInsError(""); setInsData(null);
    try {
      var url = "/api/admin/inspect-slice?leadId=" + encodeURIComponent(String(insLeadId||"").trim()) + "&agentName=" + encodeURIComponent(String(insAgent||"").trim());
      var d = await apiFetch(url, "GET", null, props.token);
      setInsData(d);
    } catch(e) {
      setInsError((e && e.message) || "Failed");
    }
    setInsBusy(false);
  };
  var load = async function() {
    setLoading(true); setError("");
    try {
      var d = await apiFetch("/api/rotation/diagnostics", "GET", null, props.token);
      setData(d);
    } catch(e) {
      setError((e && e.message) || "Failed to load diagnostics");
    }
    setLoading(false);
  };
  useEffect(function(){ load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  var section = function(key, title, content){
    var isOpen = !!expanded[key];
    return <div style={{ marginBottom:16, border:"1px solid #E2E8F0", borderRadius:10, overflow:"hidden", background:"#fff" }}>
      <div onClick={function(){ setExpanded(Object.assign({}, expanded, { [key]: !isOpen })); }}
        style={{ padding:"10px 14px", background:"#F8FAFC", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", userSelect:"none" }}>
        <span style={{ fontSize:13, fontWeight:700, color:C.text }}>{title}</span>
        <span style={{ fontSize:11, color:C.textLight }}>{isOpen ? "▼" : "▶"}</span>
      </div>
      {isOpen && <div style={{ padding:"12px 14px" }}>{content}</div>}
    </div>;
  };

  var fmtTs = function(d){
    if (!d) return "—";
    try {
      var dt = new Date(d);
      if (isNaN(dt.getTime())) return "—";
      return fmtDateTime12(dt, undefined, { numeric:true, sep:", " });
    } catch(e) { return "—"; }
  };
  var fmtDate = function(d){
    if (!d) return "—";
    try {
      var dt = new Date(d);
      if (isNaN(dt.getTime())) return "—";
      return fmtDateTime12(dt, undefined, { numeric:true, sep:", " });
    } catch(e) { return "—"; }
  };

  var th = { padding:"8px 10px", fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, textAlign:"left", borderBottom:"2px solid #E2E8F0", whiteSpace:"nowrap" };
  var td = { padding:"7px 10px", fontSize:12, color:C.text, borderBottom:"1px solid #F1F5F9", verticalAlign:"top" };

  return <div style={{ padding:"16px 4px" }}>
    {/* Phone rules for this tab, `.diag-*`. Four of the six tables here had
        no scroll wrapper (the poll-samples and repair tables already had
        one), so at 299px their cells squeezed and the panel's overflow was
        clipped by `.crm-app{overflow-x:hidden}` -- the right-hand columns
        simply vanished. `display:block` on the <table> itself turns it into
        its own scroll container without a wrapper element, which keeps the
        DOM and the desktop render identical; the anonymous inner table
        shrink-wraps, so this is confined to <=768 where these tables always
        overflow. `border-spacing` is inherited, so zeroing it on the block
        keeps the collapsed look inside. The leadId input was a fixed 300px
        in a 299px column. */}
    <style>{""
+ "@media (max-width: 768px) {"
+ "  .diag-table { display: block; overflow-x: auto; -webkit-overflow-scrolling: touch; max-width: 100%; border-spacing: 0; }"
+ "  .diag-lead-input { width: 100% !important; max-width: 300px; }"
+ "}"
}</style>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
      <div>
        <h2 style={{ margin:0, fontSize:18, fontWeight:700, color:C.text }}>Rotation Diagnostics</h2>
        <div style={{ fontSize:11, color:C.textLight, marginTop:4 }}>
          Why is or isn't auto-rotation running. Updates every 5 min via the cron — refresh below to re-pull.
          {data && data.generatedAt && <span> · Last loaded {fmtTs(data.generatedAt)}</span>}
        </div>
      </div>
      <button onClick={load} disabled={loading}
        style={{ padding:"8px 14px", borderRadius:8, border:"1px solid "+C.accent, background:loading?"#F1F5F9":"#fff", color:C.accent, fontSize:12, fontWeight:600, cursor:loading?"wait":"pointer" }}>
        {loading ? "Loading…" : "↻ Refresh"}
      </button>
    </div>

    {error && <div style={{ padding:"10px 14px", marginBottom:12, background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:8, color:"#B91C1C", fontSize:12 }}>{error}</div>}

    {data && data.rotationStatus && !data.rotationStatus.autoRotationEnabled && <div style={{ padding:"10px 14px", marginBottom:12, background:"#FEF3C7", border:"1px solid #FCD34D", borderRadius:8, color:"#92400E", fontSize:12, fontWeight:600 }}>
      ⚠ Auto-rotation is DISABLED in Rotation settings. The cron is still ticking but bails immediately.
    </div>}
    {data && data.rotationStatus && data.rotationStatus.autoRotationPausedUntil && new Date(data.rotationStatus.autoRotationPausedUntil) > new Date() && <div style={{ padding:"10px 14px", marginBottom:12, background:"#FEF3C7", border:"1px solid #FCD34D", borderRadius:8, color:"#92400E", fontSize:12, fontWeight:600 }}>
      ⏸ Auto-rotation is PAUSED until {fmtTs(data.rotationStatus.autoRotationPausedUntil)}.
    </div>}

    {/* (1) Last Cron Run Summary + recent runs table */}
    {data && section("recent", "1. Last Cron Run Summary", (function(){
      var last = data.lastRun;
      var recent = data.recentRuns || [];
      return <div>
        {last ? <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginBottom:12 }}>
          <div style={{ background:"#F8FAFC", padding:"8px 12px", borderRadius:8, minWidth:140 }}>
            <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3 }}>Started</div>
            <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{fmtTs(last.startedAt)}</div>
          </div>
          <div style={{ background:"#F8FAFC", padding:"8px 12px", borderRadius:8, minWidth:90 }}>
            <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3 }}>Evaluated</div>
            <div style={{ fontSize:18, fontWeight:700, color:C.text }}>{last.evaluated || 0}</div>
          </div>
          <div style={{ background:"#F0FDF4", padding:"8px 12px", borderRadius:8, minWidth:90 }}>
            <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3 }}>Rotated</div>
            <div style={{ fontSize:18, fontWeight:700, color:"#15803D" }}>{last.rotated || 0}</div>
          </div>
          <div style={{ background:"#FEF3C7", padding:"8px 12px", borderRadius:8, minWidth:90 }}>
            <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3 }}>Skipped</div>
            <div style={{ fontSize:18, fontWeight:700, color:"#B45309" }}>{last.skipped || 0}</div>
          </div>
        </div> : <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", marginBottom:12 }}>No cron runs recorded yet. The cron ticks every 5 minutes — the first row will appear after the next tick.</div>}
        <div style={{ fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, marginBottom:6 }}>Last 10 runs</div>
        {recent.length > 0 ? <table className="diag-table" style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead><tr>
            <th style={th}>Started</th>
            <th style={Object.assign({}, th, { textAlign:"right" })}>Evaluated</th>
            <th style={Object.assign({}, th, { textAlign:"right" })}>Rotated</th>
            <th style={Object.assign({}, th, { textAlign:"right" })}>Skipped</th>
          </tr></thead>
          <tbody>{recent.map(function(r, i){
            return <tr key={i}>
              <td style={td}>{fmtTs(r.startedAt)}</td>
              <td style={Object.assign({}, td, { textAlign:"right" })}>{r.evaluated || 0}</td>
              <td style={Object.assign({}, td, { textAlign:"right", color:"#15803D", fontWeight:600 })}>{r.rotated || 0}</td>
              <td style={Object.assign({}, td, { textAlign:"right", color:"#B45309" })}>{r.skipped || 0}</td>
            </tr>;
          })}</tbody>
        </table> : <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic" }}>—</div>}
      </div>;
    })())}

    {/* (2) Skipped Leads Breakdown (most recent run) */}
    {data && section("lastRun", "2. Skipped Leads (most recent run)", (function(){
      var last = data.lastRun;
      if (!last) return <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic" }}>No run data yet.</div>;
      var reasons = last.skippedReasons || {};
      var reasonKeys = Object.keys(reasons).sort(function(a,b){ return reasons[b]-reasons[a]; });
      var skipped = Array.isArray(last.skippedLeads) ? last.skippedLeads : [];
      return <div>
        {reasonKeys.length > 0 && <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, marginBottom:6 }}>Skip reasons (histogram)</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>{reasonKeys.map(function(k){
            return <span key={k} style={{ fontSize:11, padding:"3px 8px", borderRadius:14, background:"#FEF3C7", color:"#92400E", fontWeight:600 }}>{k} · {reasons[k]}</span>;
          })}</div>
        </div>}
        <div style={{ fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, marginBottom:6 }}>
          Skipped leads detail{skipped.length >= 200 ? " (capped at 200)" : ""}
        </div>
        {skipped.length > 0 ? <div style={{ maxHeight:520, overflowY:"auto", border:"1px solid #E2E8F0", borderRadius:8 }}>
          <table className="diag-table" style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
            <thead><tr>
              <th style={th}>Lead</th>
              <th style={th}>Agent</th>
              <th style={th}>Status</th>
              <th style={th}>Last action</th>
              <th style={th}>Callback time</th>
              <th style={th}>Reason</th>
            </tr></thead>
            <tbody>{skipped.map(function(s, i){
              return <tr key={i}>
                <td style={td}>{s.leadName || "(no name)"}</td>
                <td style={td}>{s.currentAgent || "—"}</td>
                <td style={td}>{s.status || "—"}</td>
                <td style={td}>{fmtDate(s.lastActionAt)}</td>
                <td style={td}>{fmtDate(s.callbackTime)}</td>
                <td style={Object.assign({}, td, { color:"#92400E" })}>{s.reason || "—"}</td>
              </tr>;
            })}</tbody>
          </table>
        </div> : <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic" }}>—</div>}
      </div>;
    })())}

    {/* (3) Current Tier Distribution */}
    {data && section("tiers", "3. Current Tier Distribution", (function(){
      var td2 = data.tierDistribution || {};
      var cells = [
        { label: "Tier 1", value: td2.tier1 || 0, bg:"#EFF6FF", fg:"#1E40AF" },
        { label: "Tier 2", value: td2.tier2 || 0, bg:"#F0FDF4", fg:"#15803D" },
        { label: "Tier 3", value: td2.tier3 || 0, bg:"#FEF3C7", fg:"#92400E" },
        { label: "Outside tiers", value: td2.assignedOutsideTiers || 0, bg:"#F8FAFC", fg:C.textLight },
        { label: "Unassigned", value: td2.unassigned || 0, bg:"#FEF2F2", fg:"#B91C1C" }
      ];
      return <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>{cells.map(function(c){
        return <div key={c.label} style={{ background:c.bg, padding:"10px 14px", borderRadius:8, minWidth:120 }}>
          <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3 }}>{c.label}</div>
          <div style={{ fontSize:22, fontWeight:700, color:c.fg }}>{c.value}</div>
          <div style={{ fontSize:10, color:C.textLight }}>leads currently owned</div>
        </div>;
      })}</div>;
    })())}

    {/* (4) Agents Available for Rotation */}
    {data && section("available", "4. Agents Available for Rotation", (function(){
      var avail = data.availableForRotation || [];
      if (!avail.length) return <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic" }}>No agents configured in any rotation tier.</div>;
      return <table className="diag-table" style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead><tr>
          <th style={th}>Agent</th>
          <th style={th}>Tier</th>
          <th style={th}>Role</th>
          <th style={Object.assign({}, th, { textAlign:"right" })}>Lead count</th>
        </tr></thead>
        <tbody>{avail.map(function(a, i){
          var tierBg = a.tier === 1 ? "#EFF6FF" : a.tier === 2 ? "#F0FDF4" : "#FEF3C7";
          var tierFg = a.tier === 1 ? "#1E40AF" : a.tier === 2 ? "#15803D" : "#92400E";
          return <tr key={i}>
            <td style={td}>{a.userName}{a.title ? " — " + a.title : ""}</td>
            <td style={td}><span style={{ fontSize:10, padding:"2px 6px", borderRadius:10, background:tierBg, color:tierFg, fontWeight:700 }}>Tier {a.tier || "?"}</span></td>
            <td style={Object.assign({}, td, { textTransform:"capitalize" })}>{String(a.role || "").replace(/_/g, " ")}</td>
            <td style={Object.assign({}, td, { textAlign:"right", fontWeight:700, color: a.leadCount === 0 ? "#15803D" : C.text })}>{a.leadCount}</td>
          </tr>;
        })}</tbody>
      </table>;
    })())}

    {/* (5) Locked Leads */}
    {data && section("locked", "5. Locked Leads", (function(){
      var locked = data.lockedLeads || [];
      if (!locked.length) return <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic" }}>No leads are currently locked.</div>;
      return <div>
        <div style={{ fontSize:11, color:C.textLight, marginBottom:6, fontStyle:"italic" }}>
          Lock metadata (when / by whom) is captured starting this commit. Locks applied earlier show "—".
        </div>
        <table className="diag-table" style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead><tr>
            <th style={th}>Lead</th>
            <th style={th}>Current agent</th>
            <th style={th}>Status</th>
            <th style={th}>Locked at</th>
            <th style={th}>Locked by</th>
          </tr></thead>
          <tbody>{locked.map(function(l, i){
            return <tr key={i}>
              <td style={td}>{l.name || "(no name)"}</td>
              <td style={td}>{l.currentAgent || "—"}</td>
              <td style={td}>{l.status || "—"}</td>
              <td style={td}>{fmtDate(l.lockedAt)}</td>
              <td style={td}>{l.lockedBy || "—"}</td>
            </tr>;
          })}</tbody>
        </table>
      </div>;
    })())}

    {/* (6) Slice Status Pollution — TEMPORARY read-only dry-run (admin-only endpoint) */}
    <div style={{ marginBottom:16, border:"1px solid #FED7AA", borderRadius:10, overflow:"hidden", background:"#fff" }}>
      <div style={{ padding:"10px 14px", background:"#FFF7ED", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap" }}>
        <div>
          <span style={{ fontSize:13, fontWeight:700, color:"#9A3412" }}>6. Slice Status Pollution (dry-run)</span>
          <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>
            Strictly READ-ONLY — measures pre-existing slice.status pollution. Performs no writes.
            {pollData && pollData.total != null && <span> · <b>{pollData.total}</b> polluted slices across <b>{pollData.leadsAffected}</b> leads</span>}
          </div>
        </div>
        <button onClick={loadPoll} disabled={pollLoading}
          style={{ padding:"8px 14px", borderRadius:8, border:"1px solid #EA580C", background:pollLoading?"#FFEDD5":"#fff", color:"#EA580C", fontSize:12, fontWeight:600, cursor:pollLoading?"wait":"pointer", flexShrink:0 }}>
          {pollLoading ? "Running…" : "▶ Run dry-run"}
        </button>
      </div>
      <div style={{ padding:"12px 14px" }}>
        {pollError && <div style={{ padding:"10px 14px", marginBottom:12, background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:8, color:"#B91C1C", fontSize:12 }}>{pollError}</div>}
        {!pollData && !pollLoading && !pollError && <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic" }}>Click “Run dry-run” to measure. This endpoint only reads (find().lean()) — it changes nothing.</div>}
        {pollData && <div>
          {/* Summary cards */}
          <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:10 }}>
            <div style={{ background:"#FEE2E2", padding:"10px 14px", borderRadius:8, minWidth:160, border:"1.5px solid #B91C1C" }}>
              <div style={{ fontSize:10, color:"#B91C1C", textTransform:"uppercase", letterSpacing:0.3, fontWeight:700 }}>Polluted slices</div>
              <div style={{ fontSize:26, fontWeight:700, color:"#B91C1C" }}>{pollData.total != null ? pollData.total : "—"}</div>
              <div style={{ fontSize:9, color:C.textLight }}>will be cleaned</div>
            </div>
            <div style={{ background:"#FFF7ED", padding:"10px 14px", borderRadius:8, minWidth:130 }}>
              <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3 }}>Leads affected</div>
              <div style={{ fontSize:26, fontWeight:700, color:"#9A3412" }}>{pollData.leadsAffected || 0}</div>
            </div>
          </div>
          <div style={{ fontSize:10, color:C.textLight, marginBottom:14 }}>
            {pollData.mode || "—"}{pollData.readOnly ? " · read-only" : ""} · {pollData.scope || ""}
            {pollData.rule ? <span> · rule: {pollData.rule}</span> : null}
          </div>

          {/* Breakdown by current (polluted) status */}
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, marginBottom:6 }}>Breakdown by current (polluted) status</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:14 }}>
            {Object.keys(pollData.byCurrent || {}).length === 0 && <span style={{ fontSize:12, color:C.textLight, fontStyle:"italic" }}>—</span>}
            {Object.keys(pollData.byCurrent || {}).sort(function(a,b){ return pollData.byCurrent[b]-pollData.byCurrent[a]; }).map(function(k){
              return <span key={k} style={{ fontSize:11, padding:"3px 8px", borderRadius:14, background:"#FEE2E2", color:"#B91C1C", fontWeight:600 }}>{k} · {pollData.byCurrent[k]}</span>;
            })}
          </div>

          {/* Breakdown by proposed repair (agent's proven last status) */}
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, marginBottom:6 }}>Proposed repair target (agent's proven last status)</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:14 }}>
            {Object.keys(pollData.byProposed || {}).length === 0 && <span style={{ fontSize:12, color:C.textLight, fontStyle:"italic" }}>—</span>}
            {Object.keys(pollData.byProposed || {}).sort(function(a,b){ return pollData.byProposed[b]-pollData.byProposed[a]; }).map(function(k){
              return <span key={k} style={{ fontSize:11, padding:"3px 8px", borderRadius:14, background:"#DCFCE7", color:"#15803D", fontWeight:600 }}>{k} · {pollData.byProposed[k]}</span>;
            })}
          </div>

          {/* Sample — distinct leads, status-spread */}
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, marginBottom:6 }}>Sample — distinct leads, status-spread (up to 12)</div>
          {Array.isArray(pollData.samples) && pollData.samples.length > 0 ? <div style={{ overflowX:"auto", border:"1px solid #E2E8F0", borderRadius:8, marginBottom:14 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
              <thead><tr>
                <th style={th}>Lead</th>
                <th style={th}>Agent</th>
                <th style={th}>Current (polluted)</th>
                <th style={th}>Proposed (repair)</th>
                <th style={th}>Self-authored history row?</th>
              </tr></thead>
              <tbody>{pollData.samples.map(function(s, i){
                return <tr key={i}>
                  <td style={td}>{s.leadName || "(no name)"}</td>
                  <td style={td}>{s.agent || "—"}</td>
                  <td style={Object.assign({}, td, { color:"#B91C1C", fontWeight:600 })}>{s.current || "—"}</td>
                  <td style={Object.assign({}, td, { color:"#15803D", fontWeight:600 })}>{s.proposed || "—"}</td>
                  <td style={Object.assign({}, td, { color: s.lastHistoryRow ? "#15803D" : "#B45309" })}>{s.lastHistoryRow ? ("yes → last was " + s.lastHistoryRow.newStatus) : "NONE → NewLead"}</td>
                </tr>;
              })}</tbody>
            </table>
          </div> : <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", marginBottom:14 }}>No samples (no pollution detected).</div>}

          {/* Raw JSON — copy/paste back for analysis */}
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, marginBottom:6 }}>Raw JSON</div>
          <pre style={{ margin:0, padding:"10px 12px", background:"#0F172A", color:"#E2E8F0", borderRadius:8, fontSize:11, lineHeight:1.5, overflowX:"auto", maxHeight:360, whiteSpace:"pre", fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace" }}>{JSON.stringify(pollData, null, 2)}</pre>
        </div>}
      </div>
    </div>

    {/* (7) Single-Lead Cleanup (test) — TEMPORARY. Phase 1: repairs CONFIDENT
        slices on ONE lead. Preview (dry-run) makes no writes; Apply requires
        typing CONFIRM and is gated server-side by a confirm string. */}
    <div style={{ marginBottom:16, border:"1px solid #C7D2FE", borderRadius:10, overflow:"hidden", background:"#fff" }}>
      <div style={{ padding:"10px 14px", background:"#EEF2FF" }}>
        <span style={{ fontSize:13, fontWeight:700, color:"#3730A3" }}>7. Single-Lead Cleanup (test)</span>
        <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>
          Repairs only CONFIDENT polluted slices on ONE lead (skips ambiguous + after-fix). Preview = no writes. Apply writes an auditable status_cleanup entry per change.
        </div>
      </div>
      <div style={{ padding:"12px 14px" }}>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:10 }}>
          <input className="diag-lead-input" value={clLeadId} onChange={function(e){ setClLeadId(e.target.value); }} placeholder="Paste leadId (24-hex)"
            style={{ padding:"7px 10px", border:"1px solid #C7D2FE", borderRadius:8, fontSize:12, width:300, fontFamily:"ui-monospace, monospace" }}/>
          <button onClick={function(){ runCleanupSingle(false); }} disabled={clBusy || !String(clLeadId||"").trim()}
            style={{ padding:"7px 14px", borderRadius:8, border:"1px solid #4F46E5", background:clBusy?"#E0E7FF":"#fff", color:"#4F46E5", fontSize:12, fontWeight:600, cursor:(clBusy||!String(clLeadId||"").trim())?"not-allowed":"pointer" }}>
            {clBusy ? "Running…" : "▶ Preview (dry-run)"}
          </button>
        </div>
        {/* Apply gate */}
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:12, padding:"8px 10px", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8 }}>
          <span style={{ fontSize:11, color:"#B91C1C", fontWeight:600 }}>Apply (writes to DB):</span>
          <input value={clConfirm} onChange={function(e){ setClConfirm(e.target.value); }} placeholder="type CONFIRM"
            style={{ padding:"6px 10px", border:"1px solid #FECACA", borderRadius:8, fontSize:12, width:140 }}/>
          <button onClick={function(){}} disabled={true} title="Disabled — see admin"
            style={{ padding:"7px 14px", borderRadius:8, border:"none", background:(clConfirm==="CONFIRM"&&!clBusy&&String(clLeadId||"").trim())?"#B91C1C":"#FCA5A5", color:"#fff", fontSize:12, fontWeight:700, cursor:(clConfirm==="CONFIRM"&&!clBusy&&String(clLeadId||"").trim())?"pointer":"not-allowed" }}>
            ✓ Apply cleanup to this lead
          </button>
        </div>
        {clError && <div style={{ padding:"10px 14px", marginBottom:12, background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:8, color:"#B91C1C", fontSize:12 }}>{clError}</div>}
        {clData && <div>
          <div style={{ fontSize:12, marginBottom:8 }}>
            <b>{clData.leadName || clData.leadId}</b> · {clData.dryRun ? <span style={{ color:"#4F46E5", fontWeight:700 }}>PREVIEW (no writes)</span> : <span style={{ color:"#B91C1C", fontWeight:700 }}>APPLIED</span>}
            {" · "}polluted slices: <b>{clData.totalPollutedSlices != null ? clData.totalPollutedSlices : 0}</b>
            {!clData.dryRun && <span> · repaired: <b>{clData.repaired}</b></span>}
            {Array.isArray(clData.errors) && clData.errors.length > 0 && <span style={{ color:"#B91C1C" }}> · errors: {clData.errors.length}</span>}
          </div>
          {Array.isArray(clData.repairs) && clData.repairs.length > 0 ? <div style={{ overflowX:"auto", border:"1px solid #E2E8F0", borderRadius:8, marginBottom:10 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
              <thead><tr>
                <th style={th}>Agent</th>
                <th style={th}>Before</th>
                <th style={th}>After</th>
              </tr></thead>
              <tbody>{clData.repairs.map(function(r, i){
                return <tr key={i}>
                  <td style={td}>{r.agent}</td>
                  <td style={Object.assign({}, td, { color:"#B91C1C", fontWeight:600 })}>{r.before}</td>
                  <td style={Object.assign({}, td, { color:"#15803D", fontWeight:600 })}>{r.after}</td>
                </tr>;
              })}</tbody>
            </table>
          </div> : <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", marginBottom:10 }}>No polluted slices to repair on this lead.</div>}
          {Array.isArray(clData.errors) && clData.errors.length > 0 && <pre style={{ margin:0, padding:"8px 10px", background:"#FEF2F2", color:"#B91C1C", borderRadius:8, fontSize:11, overflowX:"auto" }}>{JSON.stringify(clData.errors, null, 2)}</pre>}
        </div>}
      </div>
    </div>

    {/* (8) Full-Database Cleanup — TEMPORARY. Phase 2: repairs ALL polluted
        slices DB-wide. Preview (dry-run) makes no writes; Apply requires typing
        CONFIRM-ALL and is gated server-side by a confirm string. */}
    <div style={{ marginBottom:16, border:"2px solid #B91C1C", borderRadius:10, overflow:"hidden", background:"#fff" }}>
      <div style={{ padding:"10px 14px", background:"#FEF2F2" }}>
        <span style={{ fontSize:13, fontWeight:700, color:"#B91C1C" }}>8. Full-Database Cleanup (ALL polluted slices)</span>
        <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>
          Repairs EVERY polluted non-holder slice across all leads. Run Preview first to confirm the count matches the dry-run. Apply writes an auditable status_cleanup entry per change (reversible via the audit trail).
        </div>
      </div>
      <div style={{ padding:"12px 14px" }}>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:10 }}>
          <button onClick={function(){ runCleanupAll(false); }} disabled={allBusy}
            style={{ padding:"7px 14px", borderRadius:8, border:"1px solid #4F46E5", background:allBusy?"#E0E7FF":"#fff", color:"#4F46E5", fontSize:12, fontWeight:600, cursor:allBusy?"wait":"pointer" }}>
            {allBusy ? "Running…" : "▶ Preview ALL (dry-run)"}
          </button>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:12, padding:"8px 10px", background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:8 }}>
          <span style={{ fontSize:11, color:"#B91C1C", fontWeight:700 }}>DANGER — writes to ALL leads:</span>
          <input value={allConfirm} onChange={function(e){ setAllConfirm(e.target.value); }} placeholder="type CONFIRM-ALL"
            style={{ padding:"6px 10px", border:"1px solid #FECACA", borderRadius:8, fontSize:12, width:160 }}/>
          <button onClick={function(){}} disabled={true} title="Disabled — see admin"
            style={{ padding:"7px 14px", borderRadius:8, border:"none", background:(allConfirm==="CONFIRM-ALL"&&!allBusy)?"#B91C1C":"#FCA5A5", color:"#fff", fontSize:12, fontWeight:700, cursor:(allConfirm==="CONFIRM-ALL"&&!allBusy)?"pointer":"not-allowed" }}>
            ✓ Apply cleanup to ALL polluted slices
          </button>
        </div>
        {allError && <div style={{ padding:"10px 14px", marginBottom:12, background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:8, color:"#B91C1C", fontSize:12 }}>{allError}</div>}
        {allData && <div>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:10 }}>
            <div style={{ background:"#F8FAFC", padding:"8px 12px", borderRadius:8, minWidth:120 }}>
              <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase" }}>Leads processed</div>
              <div style={{ fontSize:18, fontWeight:700, color:C.text }}>{allData.totalLeadsProcessed != null ? allData.totalLeadsProcessed : "—"}</div>
            </div>
            <div style={{ background:"#FEF2F2", padding:"8px 12px", borderRadius:8, minWidth:120 }}>
              <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase" }}>Slices detected</div>
              <div style={{ fontSize:18, fontWeight:700, color:"#B91C1C" }}>{allData.totalSlicesDetected != null ? allData.totalSlicesDetected : "—"}</div>
            </div>
            <div style={{ background:"#ECFDF5", padding:"8px 12px", borderRadius:8, minWidth:120 }}>
              <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase" }}>{allData.dryRun ? "Would repair" : "Repaired"}</div>
              <div style={{ fontSize:18, fontWeight:700, color:"#047857" }}>{allData.dryRun ? (allData.totalSlicesDetected != null ? allData.totalSlicesDetected : "—") : (allData.totalSlicesRepaired != null ? allData.totalSlicesRepaired : "—")}</div>
            </div>
            <div style={{ background:"#FFF7ED", padding:"8px 12px", borderRadius:8, minWidth:100 }}>
              <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase" }}>Errors</div>
              <div style={{ fontSize:18, fontWeight:700, color: allData.totalErrors ? "#B91C1C" : C.text }}>{allData.totalErrors != null ? allData.totalErrors : "—"}</div>
            </div>
            <div style={{ background:"#EEF2FF", padding:"8px 12px", borderRadius:8, minWidth:120 }}>
              <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase" }}>Mode</div>
              <div style={{ fontSize:14, fontWeight:700, color: allData.dryRun ? "#4F46E5" : "#B91C1C" }}>{allData.dryRun ? "PREVIEW" : "APPLIED"}</div>
            </div>
          </div>
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, marginBottom:6 }}>Sample repairs (first 50)</div>
          {Array.isArray(allData.sampleRepairs) && allData.sampleRepairs.length > 0 ? <div style={{ overflowX:"auto", border:"1px solid #E2E8F0", borderRadius:8, marginBottom:10, maxHeight:360, overflowY:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
              <thead><tr>
                <th style={th}>Lead</th>
                <th style={th}>Agent</th>
                <th style={th}>Before</th>
                <th style={th}>After</th>
              </tr></thead>
              <tbody>{allData.sampleRepairs.map(function(r, i){
                return <tr key={i}>
                  <td style={td}>{r.leadName || r.leadId}</td>
                  <td style={td}>{r.agent}</td>
                  <td style={Object.assign({}, td, { color:"#B91C1C", fontWeight:600 })}>{r.before}</td>
                  <td style={Object.assign({}, td, { color:"#15803D", fontWeight:600 })}>{r.after}</td>
                </tr>;
              })}</tbody>
            </table>
          </div> : <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", marginBottom:10 }}>No polluted slices detected.</div>}
          {Array.isArray(allData.errors) && allData.errors.length > 0 && <pre style={{ margin:0, padding:"8px 10px", background:"#FEF2F2", color:"#B91C1C", borderRadius:8, fontSize:11, overflowX:"auto" }}>{JSON.stringify(allData.errors, null, 2)}</pre>}
        </div>}
      </div>
    </div>

    {/* (10) Holder-Slice Cleanup (Phase 3) — TEMPORARY. Cleans ONLY holder
        slices polluted by a pre-703ef2f SALES caller; managerial writes preserved.
        Preview makes no writes; Apply requires typing CONFIRM-HOLDERS. */}
    <div style={{ marginBottom:16, border:"2px solid #7C3AED", borderRadius:10, overflow:"hidden", background:"#fff" }}>
      <div style={{ padding:"10px 14px", background:"#F5F3FF" }}>
        <span style={{ fontSize:13, fontWeight:700, color:"#5B21B6" }}>10. Holder-Slice Cleanup (Phase 3 — sales-caller pollution only)</span>
        <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>
          Repairs holder slices whose status came from a pre-703ef2f SALES-caller mirror (most-recent producing history row authored by a current sales-role user). Managerial holder writes (admin/manager/TL/director) are preserved. Auditable + idempotent.
        </div>
      </div>
      <div style={{ padding:"12px 14px" }}>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:10 }}>
          <button onClick={function(){ runCleanupHolders(false); }} disabled={holdBusy}
            style={{ padding:"7px 14px", borderRadius:8, border:"1px solid #7C3AED", background:holdBusy?"#EDE9FE":"#fff", color:"#7C3AED", fontSize:12, fontWeight:600, cursor:holdBusy?"wait":"pointer" }}>
            {holdBusy ? "Running…" : "▶ Preview holders (dry-run)"}
          </button>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:12, padding:"8px 10px", background:"#F5F3FF", border:"1px solid #DDD6FE", borderRadius:8 }}>
          <span style={{ fontSize:11, color:"#5B21B6", fontWeight:700 }}>Writes to holder slices:</span>
          <input value={holdConfirm} onChange={function(e){ setHoldConfirm(e.target.value); }} placeholder="type CONFIRM-HOLDERS"
            style={{ padding:"6px 10px", border:"1px solid #DDD6FE", borderRadius:8, fontSize:12, width:180 }}/>
          <button onClick={function(){}} disabled={true} title="Disabled — see admin"
            style={{ padding:"7px 14px", borderRadius:8, border:"none", background:(holdConfirm==="CONFIRM-HOLDERS"&&!holdBusy)?"#7C3AED":"#C4B5FD", color:"#fff", fontSize:12, fontWeight:700, cursor:(holdConfirm==="CONFIRM-HOLDERS"&&!holdBusy)?"pointer":"not-allowed" }}>
            ✓ Apply holder cleanup
          </button>
        </div>
        {holdError && <div style={{ padding:"10px 14px", marginBottom:12, background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:8, color:"#B91C1C", fontSize:12 }}>{holdError}</div>}
        {holdData && <div>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:10 }}>
            <div style={{ background:"#F8FAFC", padding:"8px 12px", borderRadius:8, minWidth:120 }}>
              <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase" }}>Leads processed</div>
              <div style={{ fontSize:18, fontWeight:700, color:C.text }}>{holdData.totalLeadsProcessed != null ? holdData.totalLeadsProcessed : "—"}</div>
            </div>
            <div style={{ background:"#F5F3FF", padding:"8px 12px", borderRadius:8, minWidth:140 }}>
              <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase" }}>Holder slices detected</div>
              <div style={{ fontSize:18, fontWeight:700, color:"#5B21B6" }}>{holdData.totalHolderSlicesDetected != null ? holdData.totalHolderSlicesDetected : "—"}</div>
            </div>
            <div style={{ background:"#ECFDF5", padding:"8px 12px", borderRadius:8, minWidth:120 }}>
              <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase" }}>{holdData.dryRun ? "Would repair" : "Repaired"}</div>
              <div style={{ fontSize:18, fontWeight:700, color:"#047857" }}>{holdData.dryRun ? (holdData.totalHolderSlicesDetected != null ? holdData.totalHolderSlicesDetected : "—") : (holdData.totalRepaired != null ? holdData.totalRepaired : "—")}</div>
            </div>
            <div style={{ background:"#FFF7ED", padding:"8px 12px", borderRadius:8, minWidth:100 }}>
              <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase" }}>Errors</div>
              <div style={{ fontSize:18, fontWeight:700, color: holdData.totalErrors ? "#B91C1C" : C.text }}>{holdData.totalErrors != null ? holdData.totalErrors : "—"}</div>
            </div>
            <div style={{ background:"#F5F3FF", padding:"8px 12px", borderRadius:8, minWidth:110 }}>
              <div style={{ fontSize:10, color:C.textLight, textTransform:"uppercase" }}>Mode</div>
              <div style={{ fontSize:14, fontWeight:700, color: holdData.dryRun ? "#7C3AED" : "#B91C1C" }}>{holdData.dryRun ? "PREVIEW" : "APPLIED"}</div>
            </div>
          </div>
          <div style={{ fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, marginBottom:6 }}>Sample holder repairs (first 50)</div>
          {Array.isArray(holdData.sampleRepairs) && holdData.sampleRepairs.length > 0 ? <div style={{ overflowX:"auto", border:"1px solid #E2E8F0", borderRadius:8, marginBottom:10, maxHeight:360, overflowY:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
              <thead><tr>
                <th style={th}>Lead</th>
                <th style={th}>Holder</th>
                <th style={th}>Before</th>
                <th style={th}>After</th>
                <th style={th}>Polluted by (sales caller)</th>
              </tr></thead>
              <tbody>{holdData.sampleRepairs.map(function(r, i){
                return <tr key={i}>
                  <td style={td}>{r.leadName || r.leadId}</td>
                  <td style={td}>{r.agent}</td>
                  <td style={Object.assign({}, td, { color:"#B91C1C", fontWeight:600 })}>{r.before}</td>
                  <td style={Object.assign({}, td, { color:"#15803D", fontWeight:600 })}>{r.after}</td>
                  <td style={Object.assign({}, td, { color:"#7C3AED" })}>{r.pollutedBy || "—"}</td>
                </tr>;
              })}</tbody>
            </table>
          </div> : <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", marginBottom:10 }}>No sales-caller-polluted holder slices detected.</div>}
          {Array.isArray(holdData.errors) && holdData.errors.length > 0 && <pre style={{ margin:0, padding:"8px 10px", background:"#FEF2F2", color:"#B91C1C", borderRadius:8, fontSize:11, overflowX:"auto" }}>{JSON.stringify(holdData.errors, null, 2)}</pre>}
        </div>}
      </div>
    </div>

    {/* (9) Single-Slice Inspection (test) — TEMPORARY, read-only. Explains why
        one agent's slice on one lead was/wasn't flagged by the detector. */}
    <div style={{ marginBottom:16, border:"1px solid #CBD5E1", borderRadius:10, overflow:"hidden", background:"#fff" }}>
      <div style={{ padding:"10px 14px", background:"#F1F5F9" }}>
        <span style={{ fontSize:13, fontWeight:700, color:"#334155" }}>9. Single-Slice Inspection (read-only)</span>
        <div style={{ fontSize:11, color:C.textLight, marginTop:2 }}>
          Dumps one agent's slice + agentHistory + lead.history rows + the exact condition that exempted/flagged it.
        </div>
      </div>
      <div style={{ padding:"12px 14px" }}>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:10 }}>
          <input value={insLeadId} onChange={function(e){ setInsLeadId(e.target.value); }} placeholder="leadId"
            style={{ padding:"7px 10px", border:"1px solid #CBD5E1", borderRadius:8, fontSize:12, width:300, fontFamily:"ui-monospace, monospace" }}/>
          <input value={insAgent} onChange={function(e){ setInsAgent(e.target.value); }} placeholder="agent name"
            style={{ padding:"7px 10px", border:"1px solid #CBD5E1", borderRadius:8, fontSize:12, width:180 }}/>
          <button onClick={runInspect} disabled={insBusy || !String(insLeadId||"").trim()}
            style={{ padding:"7px 14px", borderRadius:8, border:"1px solid #334155", background:insBusy?"#E2E8F0":"#fff", color:"#334155", fontSize:12, fontWeight:600, cursor:(insBusy||!String(insLeadId||"").trim())?"not-allowed":"pointer" }}>
            {insBusy ? "Inspecting…" : "🔍 Inspect slice"}
          </button>
        </div>
        {insError && <div style={{ padding:"10px 14px", marginBottom:12, background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:8, color:"#B91C1C", fontSize:12 }}>{insError}</div>}
        {insData && <div>
          <div style={{ fontSize:12, marginBottom:8 }}>
            <b>{insData.agent}</b> on <b>{insData.leadName}</b> · slice status: <span style={{ color:"#B91C1C", fontWeight:700 }}>{insData.slice && insData.slice.status}</span>
            {" · "}holder: <b>{insData.holderName || "—"}</b>{insData.isCurrentHolder ? <span style={{ color:"#B45309", fontWeight:700 }}> (THIS AGENT IS HOLDER — skipped)</span> : null}
            {" · "}detector flags: <b style={{ color: insData.detectorFlagsThisSlice ? "#B91C1C" : "#15803D" }}>{insData.detectorFlagsThisSlice ? "YES" : "no"}</b>
          </div>
          {insData.condition2_feedbackBearingStatusChange && <div style={{ padding:"8px 10px", marginBottom:8, background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:8, fontSize:11, color:"#92400E" }}>
            <b>Exempted by condition 2 (feedback-bearing status_change):</b> status “{insData.condition2_feedbackBearingStatusChange.parsedStatus}” · authorRole={String(insData.condition2_feedbackBearingStatusChange.authorRole)} · authorName={String(insData.condition2_feedbackBearingStatusChange.authorName)} · feedback: “{insData.condition2_feedbackBearingStatusChange.feedback}”
          </div>}
          {insData.condition1_selfAuthoredHistoryRow && <div style={{ padding:"8px 10px", marginBottom:8, background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:8, fontSize:11, color:"#1E40AF" }}>
            <b>Exempted by condition 1 (self-authored lead.history row):</b> {insData.condition1_selfAuthoredHistoryRow.description}
          </div>}
          <pre style={{ margin:0, padding:"10px 12px", background:"#0F172A", color:"#E2E8F0", borderRadius:8, fontSize:11, lineHeight:1.5, overflowX:"auto", maxHeight:420, whiteSpace:"pre", fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace" }}>{JSON.stringify(insData, null, 2)}</pre>
        </div>}
      </div>
    </div>
  </div>;
};

// ===== SETTINGS TAB ERROR BOUNDARY =====
// Until 2026-08-20 a throw inside ONE settings tab unmounted the WHOLE app and
// left a blank white page — there was no error boundary anywhere in the tree, so
// React's default (unmount everything) was the behaviour. That is the worst
// possible failure mode for a diagnosis, because a white page carries no
// information: the owner cannot tell a crashed tab from a dead server, and the
// stack is gone the moment they navigate away.
//
// Scoped to the tab CONTENT, so the header, the tab bar and every other tab keep
// working and the user can navigate out of a broken tab instead of reloading.
//
// KEYED ON THE ACTIVE TAB by the caller. A boundary latches into its error state
// and keeps rendering the fallback until it is remounted; without the key, one
// bad tab would poison every tab the user switched to afterwards.
//
// It also PRINTS the error and the component stack. For an intermittent crash
// that is the whole point: the card holds the exact message on screen until it is
// read, rather than requiring the console to have been open when it fired.
class SettingsTabBoundary extends Component {
  constructor(props){ super(props); this.state = { error: null, stack: "" }; }
  static getDerivedStateFromError(error){ return { error: error, stack: "" }; }
  componentDidCatch(error, info){
    var stack = (info && info.componentStack) ? String(info.componentStack) : "";
    this.setState({ stack: stack });
    console.error("[settings-tab:" + (this.props.tab || "?") + "] crashed:", error);
    if (stack) console.error("[settings-tab:" + (this.props.tab || "?") + "] component stack:", stack);
  }
  render(){
    if (!this.state.error) return this.props.children;
    var e = this.state.error;
    var msg = (e && e.message) ? e.message : String(e);
    var box = { padding:16, border:"1px solid #E5B4B4", background:"#FBEAEA", borderRadius:10,
                fontSize:13, color:"#7A1F1F", lineHeight:1.55, maxWidth:900 };
    var pre = { margin:"8px 0 0", padding:10, background:"#fff", border:"1px solid #E5D2D2",
                borderRadius:8, fontSize:11, lineHeight:1.5, color:"#5A2B2B",
                whiteSpace:"pre-wrap", wordBreak:"break-word", maxHeight:260, overflow:"auto",
                fontFamily:"ui-monospace,SFMono-Regular,Menlo,Consolas,monospace" };
    return <div style={box}>
      <div style={{fontWeight:600, marginBottom:6}}>This tab failed to render.</div>
      <div>The rest of Settings still works — switch tabs to carry on. Please send this text:</div>
      <pre style={pre}>{"tab: " + (this.props.tab || "?") + "\n" + msg + (this.state.stack ? "\n" + this.state.stack : "")}</pre>
      <button type="button" onClick={function(){ try { window.location.reload(); } catch(_){} }}
        style={{marginTop:10, padding:"6px 14px", fontSize:12, borderRadius:8, cursor:"pointer",
                border:"1px solid #C98A8A", background:"#fff", color:"#7A1F1F", fontFamily:"inherit"}}>
        Reload the app
      </button>
    </div>;
  }
}

var SettingsPage = function(p) {
  var t=p.t;
  // Rotation eligibility (spec v3): only sales + team_leader receive leads.
  var salesAgentsForSetting = p.users ? p.users.filter(function(u){return (positionOf(u)==="sales"||positionOf(u)==="team_leader")&&u.active;}) : [];
  // General-tab company/email/phone/timezone/currency were removed (2026-07-04):
  // localStorage-only decorations nothing in the app consumed (timezone is
  // hardcoded Africa/Cairo, currency hardcoded EGP). The tab now shows a static
  // truth line + the Language selector; the old `crm_set_*` keys are no longer written.
  // Rotation settings live in MongoDB — single source of truth for every user.
  // Start with empty/defaults; useEffect below hydrates from /api/settings/rotation.
  var [tier1,setTier1]=useState([]);
  var [tier2,setTier2]=useState([]);
  var [tier3,setTier3]=useState([]);
  var [tier1LastIdx,setTier1LastIdx]=useState(-1);
  var [rotNoAnswerCount,setRotNoAnswerCount]=useState(2);
  var [rotNoAnswerHours,setRotNoAnswerHours]=useState(1);
  var [rotNotIntDays,setRotNotIntDays]=useState(1);
  var [rotNewLeadHours,setRotNewLeadHours]=useState(2);
  var [rotCbHours,setRotCbHours]=useState("");
  var [rotHotDays,setRotHotDays]=useState(2);
  var [rotStopDays,setRotStopDays]=useState(45);
  // Master switch + pause
  var [autoRotEnabled,setAutoRotEnabled]=useState(true);
  var [pausedUntil,setPausedUntil]=useState(null);
  // Server-side per-agent rotation stats (id -> {active, todayReceived}) from
  // /api/rotation/diagnostics. Source of truth for the tier "N active" + "N/cap
  // today" badges so they don't depend on the paginated p.leads bootstrap slice.
  // null = not loaded / fetch failed -> the badges fall back to the p.leads estimate.
  var [rotDiagStats,setRotDiagStats]=useState(null);
  // Eligible-for-rotation backlog from /api/rotation/capacity (admin only).
  // Only the Y term of the capacity strip: X and Z are derived client-side from
  // the caps currently in the form so they move while the owner types.
  // null = not loaded / not permitted -> the strip renders nothing.
  var [rotCapacity,setRotCapacity]=useState(null);
  // In-flight daily-cap edits, keyed by user id. The cap inputs are uncontrolled
  // (defaultValue + save on blur, because they persist per-agent via
  // PUT /api/users/:id rather than with the rotation settings form), so without
  // this the capacity total could only move after a blur+save round trip. This
  // holds the typed value from the first keystroke; p.users stays the source of
  // truth once saveDailyCap lands.
  var [capDraft,setCapDraft]=useState({});
  // Working hours (company default)
  var [whDays,setWhDays]=useState(["Sun","Mon","Tue","Wed","Thu"]);
  var [whFrom,setWhFrom]=useState("10:00");
  var [whTo,setWhTo]=useState("19:00");
  var [whAfter,setWhAfter]=useState("queue");
  // Smart skip rules
  var [srVac,setSrVac]=useState(true);
  var [srOffH,setSrOffH]=useState(4);
  var [srHours,setSrHours]=useState(true);
  var [srHandled,setSrHandled]=useState(true);
  var [srHaltNI,setSrHaltNI]=useState(3);
  var [srHaltAll,setSrHaltAll]=useState(true);
  var [manualWindowMin,setManualWindowMin]=useState(15);
  // Phase Q — days of inactivity by holding sales after which a lead disappears
  // from sales / manager / team_leader views. Admin / operation always see all.
  var [staleLeadDays,setStaleLeadDays]=useState(7);
  // Separate (typically longer) threshold for managerial roles (team_leader,
  // manager, director). Sales role keeps using staleLeadDays above.
  var [staleLeadDaysManagers,setStaleLeadDaysManagers]=useState(30);
  var [simulateOpen,setSimulateOpen]=useState(false);
  var [redistBusy,setRedistBusy]=useState(false);
  var [redistResult,setRedistResult]=useState(null); // {total,distributed,skipped,perAgent}
  // Company Announcement (admin-only broadcast)
  var [annOpen,setAnnOpen]=useState(false);
  var [annTitle,setAnnTitle]=useState("");
  var [annBody,setAnnBody]=useState("");
  var [annBusy,setAnnBusy]=useState(false);
  var [annResult,setAnnResult]=useState(null); // {recipients} on success
  var sendAnnouncement=function(){
    var msg=(annBody||"").trim();
    if(!msg){ return; }
    if(!window.confirm("Send this announcement as a push notification to ALL employees?")) return;
    setAnnBusy(true); setAnnResult(null);
    apiFetch("/api/notifications/broadcast","POST",{title:(annTitle||"").trim(),body:msg},p.token,p.csrfToken)
      .then(function(r){
        setAnnResult({recipients:(r&&r.recipients)||0});
        setAnnTitle(""); setAnnBody("");
        setTimeout(function(){ setAnnOpen(false); setAnnResult(null); },1500);
      })
      .catch(function(e){ window.alert("Failed to send: "+(e&&e.message||"error")); })
      .finally(function(){ setAnnBusy(false); });
  };
  // Scheduled broadcasts
  var [annMode,setAnnMode]=useState("now");          // "now" | "later"
  var [annWhen,setAnnWhen]=useState("");             // datetime-local value
  var [annScheduled,setAnnScheduled]=useState([]);   // pending list
  var loadScheduled=function(){
    if(!(p.cu && p.cu.role==="admin")) return;
    apiFetch("/api/notifications/scheduled","GET",null,p.token)
      .then(function(rows){ setAnnScheduled(Array.isArray(rows)?rows:[]); })
      .catch(function(){});
  };
  var scheduleAnnouncement=function(){
    var msg=(annBody||"").trim();
    if(!msg) return;
    if(!annWhen){ window.alert("Pick a date and time"); return; }
    var iso=new Date(annWhen).toISOString();          // local picker -> real UTC instant
    if(new Date(iso).getTime() <= Date.now()){ window.alert("Pick a future time"); return; }
    if(!window.confirm("Schedule this announcement for "+fmtDateTime12(iso)+"?")) return;
    setAnnBusy(true);
    apiFetch("/api/notifications/schedule","POST",{title:(annTitle||"").trim(),body:msg,scheduledFor:iso},p.token,p.csrfToken)
      .then(function(){
        setAnnTitle(""); setAnnBody(""); setAnnWhen(""); setAnnMode("now");
        setAnnOpen(false); loadScheduled();
      })
      .catch(function(e){ window.alert("Failed to schedule: "+(e&&e.message||"error")); })
      .finally(function(){ setAnnBusy(false); });
  };
  var deleteScheduled=function(id){
    if(!window.confirm("Cancel this scheduled announcement?")) return;
    apiFetch("/api/notifications/scheduled/"+id,"DELETE",null,p.token,p.csrfToken)
      .then(function(){ loadScheduled(); })
      .catch(function(e){ window.alert("Failed: "+(e&&e.message||"error")); });
  };
  useEffect(function(){ loadScheduled(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  // Audit Log tab
  var [auditEntries,setAuditEntries]=useState([]);   // populated from /api/settings/audit when endpoint ships
  var [auditLoaded,setAuditLoaded]=useState(false);
  var [auditDate,setAuditDate]=useState("all");     // "today" | "week" | "month" | "all"
  var [auditAdmin,setAuditAdmin]=useState("all");
  var [auditField,setAuditField]=useState("all");
  // System Audit tab — the AuditLog collection (GET /api/audit), a DIFFERENT
  // store from the "Audit Log" tab above (which reads SettingsAudit: one row per
  // changed settings FIELD, with rollback). This one holds whole-action records
  // with before/after blobs: salary changes, off-site approvals, permission and
  // shift changes, VIP flips, rotation-stop clears.
  //
  // Why this tab exists: /api/audit's only caller was the per-employee modal on
  // the Salaries screen, which always sends ?targetUserId=<uid>. Rows with no
  // targetUserId — every company-level and lead-level event — could therefore
  // never be displayed anywhere. 29 of 137 rows were in that state on
  // 2026-08-11, some dating back to the first permission change.
  var [sysAuditAll,setSysAuditAll]=useState(null);     // EVERY row, unfiltered — drives the dropdown
  // DB total, straight from the server's countDocuments. "Showing X of Y" used
  // to read Y off the fetched array, so it always claimed to be showing the lot
  // — the denominator could not report a truncation by construction.
  var [sysAuditTotal,setSysAuditTotal]=useState(null);
  var [sysAuditTyped,setSysAuditTyped]=useState(null); // EVERY row of one type, fetched on demand
  var [sysAuditType,setSysAuditType]=useState("all");
  var [sysAuditErr,setSysAuditErr]=useState("");
  var [sysAuditBusy,setSysAuditBusy]=useState(false);
  useEffect(function(){
    var cancelled=false;
    apiFetch("/api/settings/audit","GET",null,p.token)
      .then(function(data){ if(!cancelled && Array.isArray(data)) setAuditEntries(data); })
      .catch(function(){}) // endpoint may not exist yet — silently leave empty
      .finally(function(){ if(!cancelled) setAuditLoaded(true); });
    return function(){ cancelled=true; };
  },[p.token]);
  // Commissions tab (Phase R-1) — per-1000 commission rates, separate AppSetting.
  // Not part of doSave's rotation bundle; uses its own PUT to /api/settings/commission-rates.
  var [commRates,setCommRates] = useState({ sales:{base:5,target_met:6,double_target:7}, team_leader:2, manager:1, director:0.5 });
  var [commRatesLoaded,setCommRatesLoaded] = useState(false);
  var [commRatesSaving,setCommRatesSaving] = useState(false);
  var [commRatesSavedFlag,setCommRatesSavedFlag] = useState(false);
  useEffect(function(){
    var cancelled = false;
    apiFetch("/api/settings/commission-rates","GET",null,p.token)
      .then(function(d){ if (!cancelled && d && typeof d === "object") setCommRates(d); })
      .catch(function(){}) // endpoint may not exist yet — keep defaults
      .finally(function(){ if (!cancelled) setCommRatesLoaded(true); });
    return function(){ cancelled = true; };
  },[p.token]);
  var saveCommRates = async function(){
    setCommRatesSaving(true);
    try {
      var d = await apiFetch("/api/settings/commission-rates","PUT", commRates, p.token);
      if (d && typeof d === "object") setCommRates(d);
      setCommRatesSavedFlag(true);
      setTimeout(function(){ setCommRatesSavedFlag(false); }, 2500);
    } catch(e){
      alert("Save failed: " + (e && e.message ? e.message : "Unknown"));
    }
    setCommRatesSaving(false);
  };
  // Hard cap on total rotation events per lead. 0 = no cap. Persisted on
  // the rotation AppSetting alongside rotationStopAfterDays / srHaltNI etc;
  // hydrated below and saved via the shared doSave PUT.
  var [maxRotationsPerLead,setMaxRotationsPerLead]=useState(0);
  var [saved,setSaved]=useState(false);
  var [saveError,setSaveError]=useState("");
  var [loading,setLoading]=useState(true);
  var [activeTabState,setActiveTab]=useRememberedTab("settings", gid(p.cu), SETTINGS_TABS, null, "general"); // remembered per page per user
  // THE STRIP IS RESOLVED ONCE, HERE, AND `activeTab` IS DERIVED FROM IT.
  //
  // Shadowing the state variable is what keeps this a two-line change instead of
  // a forty-site one: every `activeTab === "x"` read below is unchanged, and
  // every one of them is now answered by the envelope. A useEffect that
  // corrected the state instead would leave one painted frame in which a hidden
  // tab's content is on screen, which is the thing being fixed.
  var settingsStrip = resolveTabStrip(p.access, "settings", SETTINGS_TABS, activeTabState);
  var activeTab = settingsStrip.active;
  // Keep the selected chip in view. Eighteen chips overflow every viewport
  // this app runs on, and the strip used to open scrolled to "General" no
  // matter which tab was active — land on Rotation Diagnostics (18th) and
  // the selected chip sat ~1,500px off-screen. Runs on `activeTab`, the
  // RESOLVED id, so it also fires when the envelope moves the selection.
  // `inline:"center"` is direction-aware in the spec, so RTL needs nothing
  // extra here; `block:"nearest"` keeps the page from jumping vertically.
  var tabsRef = useRef(null);
  useEffect(function(){
    var el = tabsRef.current; if(!el) return;
    var chip = el.querySelector('[role="tab"][aria-selected="true"]');
    if(chip && typeof chip.scrollIntoView==="function") chip.scrollIntoView({inline:"center",block:"nearest"});
  },[activeTab]);

  // Attendance settings — Office Location + Work Shifts tabs. Owner-only, loaded
  // lazily when either is opened. Multi-branch: an array of locations; existing
  // singletons land at index 0, and the backend already stores the array shape.
  //
  // The Permissions tab and its attPerms state were REMOVED 2026-08-22. Access is
  // the rank matrix's job now, and the stored permissions blob is kept only as a
  // rollback path — nothing reads it, so nothing renders it either. Leaving a
  // read-only copy of a dead grid would have been worse than deleting it: it
  // would still look like the place you change permissions.
  var [attLocs,setAttLocs] = useState([]);
  var [attLoaded,setAttLoaded] = useState(false);
  var [attSaving,setAttSaving] = useState(false);
  var [attMsg,setAttMsg] = useState("");
  var [attError,setAttError] = useState("");

  // Work Shifts tab (Phase B) — per-role attendance shift editor. Owner-only.
  // Hydrated from the SAME GET /api/settings/attendance as Office Location /
  // Permissions (the backend returns roleShifts merged over defaults, so a
  // never-configured doc renders today's values). The roles below are the only
  // ones the backend accepts; admin/viewer are never shown.
  //
  // THIS LIST MUST EQUAL ATTENDANCE_TRACKED_ROLES in server.js — a gate asserts
  // it. A tracked role missing here has no editor and silently keeps its code
  // default; an untracked role listed here renders an editor whose saves the
  // backend drops. Both fail quietly, which is why it is gated rather than
  // trusted to whoever adds the next role.
  var [roleShifts,setRoleShifts] = useState(null);
  var SHIFT_ROLES = [
    { role:"sales",       label:"Sales" },
    { role:"team_leader", label:"Team Leader" },
    { role:"manager",     label:"Manager" },
    { role:"director",    label:"Sales Director" },
    { role:"operation",   label:"Operations" },
    { role:"hr",          label:"HR" },
    { role:"marketing",   label:"Marketing" },
    { role:"office_boy",  label:"Office Boy" },
    { role:"accountant",  label:"Accountant" }
  ];
  // Map the server's roleShifts (numbers) into an editable string-keyed form so
  // partial typing never produces NaN. Tier 3's maxLateMinutes is always null
  // ("and above"). Always returns all 7 roles populated.
  var shiftsToEditable = function(rs){
    var src = (rs && typeof rs === "object") ? rs : {};
    var out = {};
    SHIFT_ROLES.forEach(function(sr){
      var r = (src[sr.role] && typeof src[sr.role] === "object") ? src[sr.role] : {};
      var t = (Array.isArray(r.tiers) && r.tiers.length === 3) ? r.tiers : [{},{},{}];
      var mkTier = function(tier, fixedNull){
        return {
          maxLateMinutes: fixedNull ? null : (tier && tier.maxLateMinutes != null ? String(tier.maxLateMinutes) : ""),
          deductionFraction: (tier && tier.deductionFraction != null) ? String(tier.deductionFraction) : ""
        };
      };
      out[sr.role] = {
        startTime:    r.startTime || "",
        endTime:      r.endTime   || "",
        graceMinutes: (r.graceMinutes != null) ? String(r.graceMinutes) : "",
        tiers: [ mkTier(t[0], false), mkTier(t[1], false), mkTier(t[2], true) ]
      };
    });
    return out;
  };

  useEffect(function(){
    if (activeTab !== "officeLocation" && activeTab !== "workShifts") return;
    if (attLoaded) return;
    var cancelled = false;
    apiFetch("/api/settings/attendance","GET",null,p.token).then(function(s){
      if (cancelled || !s) return;
      var locs = Array.isArray(s.companyLocations) ? s.companyLocations : [];
      setAttLocs(locs.map(function(l){
        return {
          name: l.name || "",
          latitude:  l.latitude  != null ? String(l.latitude)  : "",
          longitude: l.longitude != null ? String(l.longitude) : "",
          radiusMeters: l.radiusMeters || 100,
          isActive: l.isActive !== false
        };
      }));
      setRoleShifts(shiftsToEditable(s.roleShifts));
      setAttLoaded(true);
    }).catch(function(err){
      // Endpoint is Owner-only; non-Owner users won't see the tabs anyway.
      setAttError((err && err.message) || "Failed to load attendance settings");
      setAttLoaded(true);
    });
    return function(){ cancelled = true; };
  },[activeTab, attLoaded, p.token]);

  // ── Capabilities tab — the rank-capability matrix ─────────────────────────
  // Loaded from ONE endpoint (matrix + vocabulary + ladders + current holders) so
  // the screen never assembles the picture from three calls and risks showing a
  // half-stale view of who holds what.
  var [capData,setCapData] = useState(null);
  var [capDept,setCapDept] = useState("operation");
  var [capMatrixDraft,setCapMatrixDraft] = useState(null);      // { grants, managed } being edited
  var [capSaving,setCapSaving] = useState(false);
  var [capMsg,setCapMsg] = useState("");
  var [capError,setCapError] = useState("");
  var [capTakeover,setCapTakeover] = useState(null); // capability key pending confirmation

  // ===== PAGE VISIBILITY MATRIX (Phase 6) =====================================
  // Same contract as Capabilities: ticking rank boxes is INERT until a surface is
  // explicitly taken over. The draft is local until Save; the take-over is a
  // separate, confirmed action that shows the holder diff first.
  var [visData,setVisData]       = useState(null);
  var [visDraft,setVisDraft]     = useState(null);
  var [visDept,setVisDept]       = useState("sales");
  var [visSaving,setVisSaving]   = useState(false);
  var [visMsg,setVisMsg]         = useState("");
  var [visError,setVisError]     = useState("");
  var [visWiden,setVisWiden]       = useState(null);   // transient auto-widen note
  // P5 — the replica IS the editor now, so the table's per-row maps
  // (visExpanded / visDetail / visAdvanced / visHoverRow) are gone with it. One
  // surface is selected at a time and the pane beside the menu belongs to it.
  var [visSelected,setVisSelected] = useState(null);   // surface key shown in the detail pane
  var [visAdvOpen,setVisAdvOpen]   = useState(false);  // per-rank grid open in that pane
  var [visBlast,setVisBlast]       = useState(null);   // "what that click just moved"
  // The server's CONFIRMABLE refusal, held so the save can be retried with the
  // flag. Only `surface_stranded` is confirmable — the other three guards are
  // final, so this is set from the error CODE and never from the prose.
  var [visStrand,setVisStrand]     = useState(null);   // { message } | null
  var [visHintOff,setVisHintOff]   = useState({});     // dismissed consistency hints
  // P4 — the read-only sidebar replica. Its own rank + expansion state, kept
  // Its own expansion state, separate from anything the pane does.
  var [visRank,setVisRank]         = useState(null);   // selected rung, per department
  var [visRepExpanded,setVisRepExpanded] = useState({});
  // The Capabilities screen became the same live editor on 2026-08-23; the
  // table's per-row maps went with it, exactly as they did for Page Visibility.
  var [capRank,setCapRank]         = useState(null);   // rung being edited
  var [capSelected,setCapSelected] = useState(null);   // card whose detail is open
  var [capBlast,setCapBlast]       = useState(null);   // "what that switch just moved"
  var [capAdvOpen,setCapAdvOpen]   = useState({});     // capability key -> per-rank grid open
  // THE PROPOSAL SNAPSHOT (capabilities only since 2026-11-02 — the Page
  // Visibility screen has no take-over, so no proposal; Save is its one action).
  // Holds { key, grants } captured when the proposal opened; the threshold writer
  // is pure and never mutates its input, so the captured object stays valid.
  var [capRevert,setCapRevert]     = useState(null);

  var loadVisibility = useCallback(function(){
    return apiFetch("/api/settings/visibility-matrix","GET",null,p.token).then(function(d){
      if (!d) return;
      setVisData(d);
      setVisDraft({
        grants: JSON.parse(JSON.stringify(d.matrix.grants || {})),
        managed: Object.assign({}, d.matrix.managed || {})
      });
    }).catch(function(err){
      setVisError((err && err.message) || "Failed to load page visibility");
    });
  },[p.token]);

  useEffect(function(){
    if (activeTab !== "visibility" || visData) return;
    loadVisibility();
  },[activeTab, visData, loadVisibility]);

  // Real-time: another Owner session took a surface over. Refetch rather than
  // patching — holders are server-computed, and a partial update would show
  // stale names beside fresh toggles.
  useEffect(function(){
    var onVis = function(){ if (activeTab === "visibility") loadVisibility(); };
    window.addEventListener("crm:visibility_matrix_updated", onVis);
    return function(){ window.removeEventListener("crm:visibility_matrix_updated", onVis); };
  },[activeTab, loadVisibility]);

  var loadCapabilities = useCallback(function(){
    return apiFetch("/api/settings/capability-matrix","GET",null,p.token).then(function(d){
      if (!d) return;
      setCapData(d);
      setCapMatrixDraft({
        grants: JSON.parse(JSON.stringify(d.matrix.grants || {})),
        managed: Object.assign({}, d.matrix.managed || {})
      });
    }).catch(function(err){
      setCapError((err && err.message) || "Failed to load capabilities");
    });
  },[p.token]);

  useEffect(function(){
    if (activeTab !== "capabilities" || capData) return;
    loadCapabilities();
  },[activeTab, capData, loadCapabilities]);

  // Real-time: another Owner session took a capability over. Refetch rather than
  // patching locally — holders are server-computed and a partial update would
  // show stale names next to fresh toggles.
  useEffect(function(){
    var onCap = function(){ if (activeTab === "capabilities") loadCapabilities(); };
    window.addEventListener("crm:capability_matrix_updated", onCap);
    return function(){ window.removeEventListener("crm:capability_matrix_updated", onCap); };
  },[activeTab, loadCapabilities]);

  // Campaigns tab — the screen is the DATA. Every campaign name that appears in
  // leads is already a row; the only thing the owner adds is a month's spend.
  //
  // NO CLIENT MIRROR OF ANY SERVER RULE. The fold that decides which spellings
  // are one campaign, which months may be picked, the floor, the suggestions and
  // the cost-per-lead all arrive computed in `screen`. This component renders
  // rows and posts intentions. That is deliberate: a client-side copy of the
  // fold would agree with the server on today's data and disagree the first time
  // someone pastes a double-spaced name, and the screen would then price a
  // campaign the reports attribute somewhere else.
  //
  // Every write returns the recomputed screen, so a save REPLACES state rather
  // than patching it — the row you look at after saving is the row the next
  // reader will get.
  var [campScreen,setCampScreen]=useState(null);
  var [campMonth,setCampMonth]=useState("");
  // Which platform's spend column the inputs edit. A spend cell is keyed
  // (campaign, platform, month) since 2026-08-29, and this screen edits ONE
  // platform at a time because that is how the work happens: open Meta Ads
  // Manager, read the numbers, type them; then TikTok. The server owns both the
  // vocabulary and the default — never hardcode a platform list here, or the
  // picker can offer a value the write route refuses.
  var [campPlatform,setCampPlatform]=useState("");
  var [campaignsLoaded,setCampaignsLoaded]=useState(false);
  var [campBusy,setCampBusy]=useState(false);
  var [campError,setCampError]=useState("");
  var [campSel,setCampSel]=useState({});          // row id -> true
  var [campEdits,setCampEdits]=useState({});      // row id -> typed spend string (unsaved)
  var [campShowIgnored,setCampShowIgnored]=useState(false);
  var [campMenuFor,setCampMenuFor]=useState(null);
  var [mergeOpen,setMergeOpen]=useState(false);
  var [mergeName,setMergeName]=useState("");
  var [mergeExtra,setMergeExtra]=useState({});    // suggested row id -> include?
  var [addOpen,setAddOpen]=useState(false);
  var [addName,setAddName]=useState("");

  var loadCampaigns = useCallback(function(month, platform){
    setCampError("");
    var qs = [];
    if (month) qs.push("month=" + encodeURIComponent(month));
    if (platform) qs.push("platform=" + encodeURIComponent(platform));
    return apiFetch("/api/settings/campaigns" + (qs.length ? "?" + qs.join("&") : ""),
                    "GET", null, p.token)
      .then(function(data){
        setCampScreen(data);
        setCampMonth((data && data.month) || "");
        setCampPlatform((data && data.platform) || "");
        setCampaignsLoaded(true);
        return data;
      })
      .catch(function(err){
        setCampError((err && err.message) || "Couldn't load campaigns");
        setCampaignsLoaded(true);
      });
  },[p.token]);

  useEffect(function(){
    if (activeTab !== "campaigns" || campaignsLoaded) return;
    loadCampaigns("", "");
  },[activeTab, campaignsLoaded, loadCampaigns]);

  // Switching month discards unsaved spend edits — they belong to the month they
  // were typed in, and carrying them across would silently book August's numbers
  // against July. The button warns before it happens.
  var campSwitchMonth = function(next){
    if (Object.keys(campEdits).length &&
        !window.confirm("Discard unsaved spend for " + campMonth + " and switch to " + next + "?")) return;
    setCampEdits({}); setCampSel({}); setCampMenuFor(null);
    loadCampaigns(next, campPlatform);
  };

  // Switching platform discards unsaved spend for the same reason switching month
  // does: the numbers were typed against the platform that was selected, and
  // carrying them across would book Meta's spend against TikTok.
  var campSwitchPlatform = function(next){
    if (next === campPlatform) return;
    if (Object.keys(campEdits).length &&
        !window.confirm("Discard unsaved spend for this platform and switch?")) return;
    setCampEdits({}); setCampMenuFor(null);
    loadCampaigns(campMonth, next);
  };

  // Branches tab (AssetTracker S1). The local `canManageBranches` flag that used
  // to gate this tab — `role === "admin" || isOwner === true` — is gone: it is
  // transcribed on the server as LEGACY_TAB_RULES["settings.branches"] and the
  // tab now resolves through the envelope like every other. It was one of four
  // flags (with ccCanManage / devCanManage / locCanManage) that each answered
  // "may you see this tab" a second time, in a bundle with no matrix in it.
  var [branches,setBranches] = useState([]);
  var [branchesLoaded,setBranchesLoaded] = useState(false);
  var [branchForm,setBranchForm] = useState({ id:null, name:"", code:"", address:"", isActive:true });
  var [branchSaving,setBranchSaving] = useState(false);
  var [branchError,setBranchError] = useState("");
  var [branchMsg,setBranchMsg] = useState("");
  // Closing Companies (Feature B) — admin-only lookup CRUD. Mirrors the branch
  // state shape; add-new uses ccName, inline rename uses ccEditId/ccEditName.
  var [closingCompanies,setClosingCompanies] = useState([]);
  var [ccLoaded,setCcLoaded] = useState(false);
  var [ccName,setCcName] = useState("");
  var [ccEditId,setCcEditId] = useState(null);
  var [ccEditName,setCcEditName] = useState("");
  var [ccSaving,setCcSaving] = useState(false);
  var [ccError,setCcError] = useState("");
  var [ccMsg,setCcMsg] = useState("");
  // Developers (D2) — admin-only lookup CRUD. Mirrors the closing-companies
  // state shape; add-new uses devName, inline rename uses devEditId/devEditName.
  var [developers,setDevelopers] = useState([]);
  var [devLoaded,setDevLoaded] = useState(false);
  var [devName,setDevName] = useState("");
  var [devEditId,setDevEditId] = useState(null);
  var [devEditName,setDevEditName] = useState("");
  var [devSaving,setDevSaving] = useState(false);
  var [devError,setDevError] = useState("");
  var [devMsg,setDevMsg] = useState("");
  // The list is 505 rows long and the reason to open this tab is usually to
  // check whether a developer already exists before adding it. devSearch holds
  // the filter box's text; the rows and the count both come from
  // filterDevelopersFE, which folds the same way the add-dedupe does.
  var [devSearch,setDevSearch] = useState("");
  var liveBranchRev = useLiveRev("branch");
  useEffect(function(){
    if (activeTab !== "branches") return;
    var cancelled = false;
    apiFetch("/api/branches","GET",null,p.token)
      .then(function(data){ if (!cancelled && Array.isArray(data)) setBranches(data); })
      .catch(function(err){ if (!cancelled) setBranchError((err && err.message) || "Failed to load branches"); })
      .finally(function(){ if (!cancelled) setBranchesLoaded(true); });
    return function(){ cancelled = true; };
  },[activeTab,p.token, liveBranchRev]);
  // Load closing companies when the tab opens. The `ccCanManage` flag that used
  // to sit in this condition is gone with the other three: `activeTab` is now
  // envelope-derived, so reaching this line at all means the caller may see the
  // tab. Keeping the flag would have meant a rank granted the tab after a
  // take-over opening it empty, with no error — a fetch refusing itself on a
  // rule the server no longer uses. GET returns { data: rows }; tolerate a bare
  // array too for safety.
  var liveCcRev = useLiveRev("closingCompany");
  useEffect(function(){
    if (activeTab !== "closingCompanies") return;
    var cancelled = false;
    apiFetch("/api/closing-companies","GET",null,p.token)
      .then(function(data){
        if (cancelled) return;
        var rows = (data && Array.isArray(data.data)) ? data.data : (Array.isArray(data) ? data : []);
        setClosingCompanies(rows);
      })
      .catch(function(err){ if (!cancelled) setCcError((err && err.message) || "Failed to load closing companies"); })
      .finally(function(){ if (!cancelled) setCcLoaded(true); });
    return function(){ cancelled = true; };
  },[activeTab,p.token, liveCcRev]);
  // Load developers when the Developers tab opens (admin-only). Same shape as
  // the closing-companies effect: GET returns { data: rows }; tolerate a bare
  // array too for safety.
  var liveDevRev = useLiveRev("developer");
  useEffect(function(){
    if (activeTab !== "developers") return;
    var cancelled = false;
    apiFetch("/api/developers","GET",null,p.token)
      .then(function(data){
        if (cancelled) return;
        var rows = (data && Array.isArray(data.data)) ? data.data : (Array.isArray(data) ? data : []);
        setDevelopers(rows);
      })
      .catch(function(err){ if (!cancelled) setDevError((err && err.message) || "Failed to load developers"); })
      .finally(function(){ if (!cancelled) setDevLoaded(true); });
    return function(){ cancelled = true; };
  },[activeTab,p.token, liveDevRev]);

  // Locations (2026-08-07) — admin-only lookup CRUD. Same state shape as the
  // developers block above, plus locBusyId: (de)activation is a per-row toggle,
  // so a single shared `saving` flag would grey out every row at once.
  var [locations,setLocations] = useState([]);
  var [locLoaded,setLocLoaded] = useState(false);
  var [locName,setLocName] = useState("");
  var [locEditId,setLocEditId] = useState(null);
  var [locEditName,setLocEditName] = useState("");
  var [locSaving,setLocSaving] = useState(false);
  var [locBusyId,setLocBusyId] = useState(null);
  var [locReordering,setLocReordering] = useState(false);
  var [locError,setLocError] = useState("");
  var [locMsg,setLocMsg] = useState("");
  // includeInactive=1 — Settings must list deactivated rows so they can be
  // reactivated; every other consumer (the DR pickers) gets active-only.
  var liveLocRev = useLiveRev("location");
  useEffect(function(){
    if (activeTab !== "locations") return;
    var cancelled = false;
    apiFetch("/api/locations?includeInactive=1","GET",null,p.token)
      .then(function(data){
        if (cancelled) return;
        var rows = (data && Array.isArray(data.data)) ? data.data : (Array.isArray(data) ? data : []);
        setLocations(rows);
      })
      .catch(function(err){ if (!cancelled) setLocError((err && err.message) || "Failed to load locations"); })
      .finally(function(){ if (!cancelled) setLocLoaded(true); });
    return function(){ cancelled = true; };
  },[activeTab,p.token, liveLocRev]);

  // Rotation tab — pull server-side per-agent active + today-received counts so
  // the tier badges reflect ALL of an agent's leads (incl. re-rotated older ones
  // outside the paginated p.leads slice). admin/operation only; on any failure
  // we leave rotDiagStats null and the badges fall back to the p.leads estimate.
  // Re-pulled whenever the rotation live feed moves (a new rotation row means
  // someone's "untouched" count just changed), so the tier badges track the feed
  // (2026-10-01). Keyed on the newest feed row + length, not the array identity.
  var rotFeedKey = (p.rotNotifs && p.rotNotifs.length)
    ? String(p.rotNotifs[0]._id || p.rotNotifs[0].createdAt || "") + ":" + p.rotNotifs.length : "";
  useEffect(function(){
    if (activeTab !== "rotation") return;
    var cancelled = false;
    apiFetch("/api/rotation/diagnostics","GET",null,p.token)
      .then(function(d){ if (!cancelled && d && d.tierAgentStats) setRotDiagStats(d.tierAgentStats); })
      .catch(function(){ /* leave rotDiagStats as-is -> p.leads fallback */ });
    return function(){ cancelled = true; };
  },[activeTab,p.token,rotFeedKey]);

  // Rotation tab — eligible-backlog size for the capacity strip. Separate from
  // the diagnostics fetch above because it is admin-ONLY (operation can read
  // diagnostics but cannot edit rotation settings, so it never sees this) and
  // because a failure here must not cost the tier badges their stats. Fetched
  // once per tab entry: Y is a property of the lead data, not of the form, so
  // it does not need to follow cap edits.
  useEffect(function(){
    if (activeTab !== "rotation") return;
    if (!p.cu || p.cu.role !== "admin") return;
    var cancelled = false;
    apiFetch("/api/rotation/capacity","GET",null,p.token)
      .then(function(d){ if (!cancelled && d && typeof d.eligibleBacklog === "number") setRotCapacity(d); })
      .catch(function(){ /* strip stays hidden */ });
    return function(){ cancelled = true; };
  },[activeTab,p.token,p.cu && p.cu.role]);

  // System Audit tab — every row, no filters. This unfiltered set is what
  // populates the type dropdown, so the options are whatever the collection
  // actually contains rather than a hardcoded list that would go stale the next
  // time someone adds an AuditLog.create (`type` is an open string by design).
  useEffect(function(){
    if (activeTab !== "sysAudit") return;
    var cancelled = false;
    setSysAuditErr(""); setSysAuditBusy(true);
    // NO CEILING — was ?limit=200 against 154 live rows (77% of the way to
    // silently truncating). Also captures the DB total so "Showing X of Y" can
    // state the real total instead of the length of whatever was fetched.
    Promise.all([
      fetchAllPages("/api/audit", p.token),
      apiFetch("/api/audit?page=1&limit=1","GET",null,p.token).catch(function(){ return null; })
    ])
      .then(function(res){
        if (cancelled) return;
        // timestamp desc restored over the whole book — see byDatesDesc.
        setSysAuditAll(Array.isArray(res[0]) ? res[0].slice().sort(byDatesDesc(["timestamp"])) : []);
        setSysAuditTotal(res[1] && typeof res[1].total === "number" ? res[1].total : null);
      })
      .catch(function(e){ if (!cancelled) { setSysAuditErr((e && e.message) || "Failed to load"); setSysAuditAll([]); } })
      .finally(function(){ if (!cancelled) setSysAuditBusy(false); });
    return function(){ cancelled = true; };
  },[activeTab,p.token]);

  // Picking a type REFETCHES with ?type= rather than filtering the 200 rows
  // already loaded. Once the collection outgrows 200 rows a client-side filter
  // would only ever search the newest window — selecting a rare type would show
  // "none" while rows existed just outside it. The endpoint already supports
  // ?type=, so each type gets its own newest-200 window for free.
  useEffect(function(){
    if (activeTab !== "sysAudit") return;
    if (sysAuditType === "all") { setSysAuditTyped(null); return; }
    var cancelled = false;
    setSysAuditErr(""); setSysAuditBusy(true);
    // NO CEILING — same ruling as the unfiltered fetch above. Narrowing by type
    // must not also silently narrow to the newest 200 of that type.
    fetchAllPages("/api/audit?type=" + encodeURIComponent(sysAuditType), p.token)
      .then(function(r){ if (!cancelled) setSysAuditTyped(Array.isArray(r) ? r.slice().sort(byDatesDesc(["timestamp"])) : []); })
      .catch(function(e){ if (!cancelled) { setSysAuditErr((e && e.message) || "Failed to load"); setSysAuditTyped([]); } })
      .finally(function(){ if (!cancelled) setSysAuditBusy(false); });
    return function(){ cancelled = true; };
  },[activeTab,p.token,sysAuditType]);

  useEffect(function(){
    var cancelled=false;
    apiFetch("/api/settings/rotation","GET",null,p.token).then(function(s){
      if(cancelled||!s) return;
      var t=(s&&s.tiers)||{};
      var g=function(k){return (t[k]&&Array.isArray(t[k].agents))?t[k].agents.map(String):[];};
      setTier1(g("tier1"));
      setTier2(g("tier2"));
      setTier3(g("tier3"));
      setTier1LastIdx((t.tier1&&typeof t.tier1.lastIdx==="number")?t.tier1.lastIdx:-1);
      setRotNoAnswerCount(Number(s.naCount)||2);
      setRotNoAnswerHours(Number(s.naHours)||1);
      setRotNotIntDays(Number(s.niDays)||1);
      setRotNewLeadHours(Number(s.newLeadHours)||2);
      setRotCbHours(s.cbHours!=null && s.cbHours!==""?Number(s.cbHours):"");
      setRotHotDays(Number(s.hotDays)||2);
      setRotStopDays(Number(s.rotationStopAfterDays)||45);
      if(s.maxRotationsPerLead!=null) setMaxRotationsPerLead(Number(s.maxRotationsPerLead)||0);
      if(s.manualAssignmentWindowMinutes!=null) setManualWindowMin(Number(s.manualAssignmentWindowMinutes)||0);
      if(s.staleLeadDays!=null) setStaleLeadDays(Number(s.staleLeadDays)||7);
      if(s.staleLeadDaysManagers!=null) setStaleLeadDaysManagers(Number(s.staleLeadDaysManagers)||30);
      setAutoRotEnabled(s.autoRotationEnabled!==false);
      setPausedUntil(s.autoRotationPausedUntil||null);
      var w=s.workingHours||{};
      if(Array.isArray(w.days)) setWhDays(w.days);
      if(w.from) setWhFrom(w.from);
      if(w.to) setWhTo(w.to);
      if(w.afterHoursBehavior) setWhAfter(w.afterHoursBehavior);
      var sr=s.smartSkipRules||{};
      if(typeof sr.skipOnVacation==="boolean") setSrVac(sr.skipOnVacation);
      if(sr.skipIfOfflineHours!=null) setSrOffH(Number(sr.skipIfOfflineHours)||0);
      if(typeof sr.respectWorkingHours==="boolean") setSrHours(sr.respectWorkingHours);
      if(typeof sr.skipIfAlreadyHandled==="boolean") setSrHandled(sr.skipIfAlreadyHandled);
      if(sr.haltAfterNotInterested!=null) setSrHaltNI(Number(sr.haltAfterNotInterested)||0);
      if(typeof sr.haltWhenAllHandled==="boolean") setSrHaltAll(sr.haltWhenAllHandled);
    }).catch(function(){}).finally(function(){ if(!cancelled) setLoading(false); });
    return function(){ cancelled=true; };
  },[p.token]);

  // Vacation state — admin/operation only. Loaded on mount; refreshed after
  // every create/cancel. Active set is derived on render for the agent badge.
  var [vacations,setVacations]=useState([]);
  var [vacModalAgent,setVacModalAgent]=useState(null); // user object when modal open
  var [vacStart,setVacStart]=useState("");
  var [vacEnd,setVacEnd]=useState("");
  var [vacReason,setVacReason]=useState("");
  var [vacSaving,setVacSaving]=useState(false);
  var [vacError,setVacError]=useState("");
  var canManageVacations = p.cu && (p.cu.role==="admin" || p.cu.role==="operation");
  var loadVacations = function(){
    if(!canManageVacations || !p.token) return;
    apiFetch("/api/vacations","GET",null,p.token)
      .then(function(rows){ setVacations(Array.isArray(rows)?rows:[]); })
      .catch(function(){});
  };
  var liveVacRev = useLiveRev("vacation");
  useEffect(function(){ loadVacations(); /* eslint-disable-next-line */ },[p.token, p.cu && p.cu.role, liveVacRev]);
  var activeVacSet = (function(){
    var now = new Date();
    var s = new Set();
    (vacations||[]).forEach(function(v){
      if(!v || !v.agentId) return;
      var start = new Date(v.startDate), end = new Date(v.endDate);
      if(start <= now && now <= end){
        var aid = v.agentId && v.agentId._id ? v.agentId._id : v.agentId;
        s.add(String(aid));
      }
    });
    return s;
  })();
  var openVacModal = function(uid){
    var u = (p.users||[]).find(function(x){return String(gid(x))===String(uid);});
    if(!u) return;
    setVacError("");
    var today = new Date().toISOString().slice(0,10);
    setVacStart(today); setVacEnd(today); setVacReason("");
    setVacModalAgent(u);
  };
  var closeVacModal = function(){ setVacModalAgent(null); setVacError(""); };
  var saveVacation = async function(){
    if(!vacModalAgent) return;
    if(!vacStart || !vacEnd){ setVacError("Start and end date required"); return; }
    if(new Date(vacEnd) < new Date(vacStart)){ setVacError("End must be ≥ start"); return; }
    setVacSaving(true); setVacError("");
    try {
      await apiFetch("/api/vacations","POST",{
        agentId: gid(vacModalAgent),
        startDate: vacStart,
        endDate: vacEnd,
        reason: vacReason
      }, p.token);
      closeVacModal();
      loadVacations();
    } catch(e) {
      setVacError((e && e.message) || "Failed to save");
    } finally {
      setVacSaving(false);
    }
  };
  var cancelVacation = async function(id){
    if(!id) return;
    if(!window.confirm("Cancel this vacation?")) return;
    try {
      await apiFetch("/api/vacations/"+id,"DELETE",null,p.token);
      loadVacations();
    } catch(e) { alert((e&&e.message)||"Failed"); }
  };

  // ---- Campaigns tab — actions ---------------------------------------------
  // Each one posts an intention and swaps in the screen the server returns.
  // campAct centralises the busy flag, the error surface and that swap so a new
  // action cannot forget one of the three.
  var campAct = async function(fn){
    setCampError("");
    setCampBusy(true);
    try {
      var out = await fn();
      if (out && out.screen) {
        setCampScreen(out.screen);
        setCampMonth(out.screen.month || campMonth);
        // The screen carries the platform its rows were built for. Adopting it
        // here is what stops the picker and the rows disagreeing: a caller that
        // forgets to send one gets the server default back, and without this the
        // picker would still read "TikTok" over rows holding Meta's cells — so
        // the input would show the wrong current value and the dirty check would
        // compare against the wrong platform's number.
        if (out.screen.platform) setCampPlatform(out.screen.platform);
      }
      return out;
    } catch (e) {
      setCampError((e && e.message) || "Save failed");
      return null;
    } finally {
      setCampBusy(false);
    }
  };

  var campRows = (campScreen && campScreen.rows) || [];
  var campRowById = function(id){
    for (var i=0;i<campRows.length;i++) if (campRows[i].id === id) return campRows[i];
    return null;
  };
  var campSelectedRows = function(){
    return Object.keys(campSel).filter(function(k){ return campSel[k]; })
      .map(campRowById).filter(Boolean);
  };
  var campToggleSel = function(id){
    setCampSel(function(prev){
      var n = Object.assign({}, prev);
      if (n[id]) delete n[id]; else n[id] = true;
      return n;
    });
  };

  // ---- merge -----------------------------------------------------------------
  var openMerge = function(){
    var sel = campSelectedRows();
    if (!sel.length) return;
    // Prefill with the biggest selected row's label — the spelling with the most
    // leads behind it is the one the owner is most likely to recognise.
    var biggest = sel.slice().sort(function(a,b){ return b.leadsAllTime - a.leadsAllTime; })[0];
    setMergeName((biggest && biggest.label) || "");
    setMergeExtra({});
    setCampError("");
    setMergeOpen(true);
  };
  // Rows the server suggested for the selection but which the owner has not
  // picked — offered as opt-in checkboxes, never pre-ticked.
  var mergeSuggestions = function(){
    var sel = campSelectedRows();
    var selIds = {}; sel.forEach(function(r){ selIds[r.id] = true; });
    var out = [], seen = {};
    sel.forEach(function(r){
      (r.suggestMergeWith || []).forEach(function(otherId){
        if (selIds[otherId] || seen[otherId]) return;
        var row = campRowById(otherId);
        if (!row || row.isGroup) return;
        seen[otherId] = true; out.push(row);
      });
    });
    return out;
  };
  var submitMerge = async function(){
    var name = String(mergeName || "").trim();
    if (!name) { setCampError("Give the group a name"); return; }
    var rows = campSelectedRows().concat(
      mergeSuggestions().filter(function(r){ return mergeExtra[r.id]; }));
    var members = [];
    rows.forEach(function(r){
      (r.members || []).forEach(function(m){ if (members.indexOf(m) === -1) members.push(m); });
    });
    var out = await campAct(function(){
      return apiFetch("/api/settings/campaign-groups","POST",
        { name: name, members: members, month: campMonth, platform: campPlatform }, p.token, p.csrfToken);
    });
    if (out) { setMergeOpen(false); setCampSel({}); setMergeExtra({}); }
  };

  // ---- not-a-campaign --------------------------------------------------------
  // A group flips its flag; an ungrouped spelling has to become a group first,
  // because `ignored` lives on the group table — the lead is never touched.
  var markNotCampaign = async function(rows, ignored){
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      /* eslint-disable no-loop-func */
      await campAct((function(row){ return function(){
        if (row.groupId) {
          return apiFetch("/api/settings/campaign-groups/"+row.groupId,"PATCH",
            { ignored: ignored, month: campMonth, platform: campPlatform }, p.token, p.csrfToken);
        }
        return apiFetch("/api/settings/campaign-groups","POST",
          { name: row.label, members: row.members || [row.label], ignored: ignored, month: campMonth, platform: campPlatform },
          p.token, p.csrfToken);
      }; })(r));
      /* eslint-enable no-loop-func */
    }
    setCampSel({}); setCampMenuFor(null);
  };

  // ---- rename / ungroup ------------------------------------------------------
  var renameGroup = async function(row){
    var next = window.prompt("Rename this campaign group", row.label || "");
    if (next === null) return;
    next = String(next).trim();
    if (!next || next === row.label) return;
    setCampMenuFor(null);
    await campAct(function(){
      return apiFetch("/api/settings/campaign-groups/"+row.groupId,"PATCH",
        { name: next, month: campMonth, platform: campPlatform }, p.token, p.csrfToken);
    });
  };
  var ungroup = async function(row){
    var g = ((campScreen && campScreen.groups) || []).filter(function(x){ return x.id === row.groupId; })[0];
    var months = (g && g.spendMonths) || 0;
    // Name what will be destroyed BEFORE the click, not after.
    var warn = "Ungroup \"" + row.label + "\"?\n\n" +
      (row.members || []).length + " campaign name(s) go back to the list on their own." +
      (months ? "\n\nThis group holds " + months + " month(s) of spend totalling " +
                Math.round((g && g.spendTotal) || 0).toLocaleString("en-US") +
                " EGP. That spend is deleted with the group — it is recorded in the audit log, " +
                "but you will have to re-enter it." : "");
    if (!window.confirm(warn)) return;
    setCampMenuFor(null);
    await campAct(function(){
      return apiFetch("/api/settings/campaign-groups/"+row.groupId+"?month="+encodeURIComponent(campMonth)
                        + "&platform=" + encodeURIComponent(campPlatform),
        "DELETE", null, p.token, p.csrfToken);
    });
  };

  // ---- spend -----------------------------------------------------------------
  var campSetEdit = function(id, val){
    setCampEdits(function(prev){ var n = Object.assign({}, prev); n[id] = val; return n; });
  };
  // THE INPUT EDITS ONE PLATFORM'S CELL. `row.spend` is the month TOTAL across
  // platforms (what cost-per-lead divides and what the ROI tab shows);
  // `row.spendForPlatform` is the selected platform's cell alone. Binding the
  // input to `row.spend` would show the total and then save it as this
  // platform's figure, inflating the month by every other platform's spend on
  // the first keystroke.
  var campCellValue = function(row){
    return row.spendForPlatform == null ? "" : String(row.spendForPlatform);
  };
  var campSpendValue = function(row){
    if (Object.prototype.hasOwnProperty.call(campEdits, row.id)) return campEdits[row.id];
    return campCellValue(row);
  };
  var campDirtyCount = function(){
    return Object.keys(campEdits).filter(function(id){
      var row = campRowById(id);
      if (!row) return false;
      return String(campEdits[id]).trim() !== campCellValue(row);
    }).length;
  };
  var saveMonth = async function(){
    var cells = [];
    Object.keys(campEdits).forEach(function(id){
      var row = campRowById(id);
      if (!row) return;
      var typed = String(campEdits[id]).trim();
      if (typed === campCellValue(row)) return;
      var cell = { spend: typed === "" ? null : Number(typed) };
      if (row.groupId) cell.groupId = row.groupId;
      else { cell.label = row.label; cell.members = row.members || [row.label]; }
      cells.push(cell);
    });
    if (!cells.length) return;
    var out = await campAct(function(){
      return apiFetch("/api/settings/campaign-spend","PUT",
        { month: campMonth, platform: campPlatform, cells: cells }, p.token, p.csrfToken);
    });
    if (out) setCampEdits({});
  };
  // Copy the previous month's figures into the editable cells — a starting point
  // for a month whose spend mostly repeats. Nothing is saved until Save is
  // pressed, so it is a draft, not a write.
  var copyPrevMonth = async function(){
    var months = (campScreen && campScreen.months) || [];
    var idx = months.indexOf(campMonth);
    if (idx <= 0) return;
    var prevMonth = months[idx-1];
    setCampBusy(true);
    try {
      // Same PLATFORM as well as the previous month — copying the month total
      // into one platform's column is how a two-platform campaign doubles.
      var prev = await apiFetch("/api/settings/campaigns?month="+encodeURIComponent(prevMonth)
                                + "&platform=" + encodeURIComponent(campPlatform),
                                "GET", null, p.token);
      var byGroup = {}, byLabel = {};
      ((prev && prev.rows) || []).forEach(function(r){
        if (r.spendForPlatform == null) return;
        if (r.groupId) byGroup[r.groupId] = r.spendForPlatform; else byLabel[r.label] = r.spendForPlatform;
      });
      var next = {};
      campRows.forEach(function(r){
        if (r.ignored) return;
        var v = r.groupId ? byGroup[r.groupId] : byLabel[r.label];
        if (v != null) next[r.id] = String(v);
      });
      setCampEdits(next);
      if (!Object.keys(next).length) setCampError("No spend recorded for " + prevMonth + " to copy.");
    } catch (e) {
      setCampError((e && e.message) || "Couldn't read " + prevMonth);
    }
    setCampBusy(false);
  };

  // ---- manual add (pre-launch only) ------------------------------------------
  // The fallback, not the front door: a campaign with no leads yet, so there is
  // no name in the data to pick. It creates a group with ZERO members, which
  // matches nothing until a real spelling is merged into it.
  var submitAddCampaign = async function(){
    var name = String(addName || "").trim();
    if (!name) { setCampError("Give the campaign a name"); return; }
    var out = await campAct(function(){
      return apiFetch("/api/settings/campaign-groups","POST",
        { name: name, members: [], month: campMonth, platform: campPlatform }, p.token, p.csrfToken);
    });
    if (out) { setAddOpen(false); setAddName(""); }
  };

  // Tier-aware drag state: source and destination each tracked with {tier,idx}.
  var [dragFrom,setDragFrom]=useState(null);
  var [dropOn,setDropOn]=useState(null);
  var tierArrays=function(){return {tier1:tier1.slice(),tier2:tier2.slice(),tier3:tier3.slice()};};
  var writeTiers=function(a){setTier1(a.tier1);setTier2(a.tier2);setTier3(a.tier3);};
  // Atomic move across tiers: splice from source, insert at destination index.
  var moveAgent=function(from,to){
    if(!from||!to) return;
    if(from.tier===to.tier&&from.idx===to.idx) return;
    var arrs=tierArrays();
    var src=arrs[from.tier], dst=arrs[to.tier];
    var moved=src.splice(from.idx,1)[0];
    if(!moved) return;
    var effIdx=(from.tier===to.tier&&from.idx<to.idx)?to.idx-1:to.idx;
    dst.splice(Math.max(0,Math.min(effIdx,dst.length)),0,moved);
    writeTiers(arrs);
  };
  var appendToTier=function(uid,tierKey){
    var arrs=tierArrays();
    ["tier1","tier2","tier3"].forEach(function(k){arrs[k]=arrs[k].filter(function(x){return x!==uid;});});
    arrs[tierKey].push(uid);
    writeTiers(arrs);
  };
  var removeFromAllTiers=function(uid){
    var arrs=tierArrays();
    ["tier1","tier2","tier3"].forEach(function(k){arrs[k]=arrs[k].filter(function(x){return x!==uid;});});
    writeTiers(arrs);
  };
  // Persists the Rotation + Business Rules bundle (both tabs edit the same state
  // and share this one save). No longer writes any General-tab localStorage.
  var doSave=async function(){
    setSaveError("");
    try{
      await apiFetch("/api/settings/rotation","PUT",{
        tiers: {
          tier1: { agents: tier1 },
          tier2: { agents: tier2 },
          tier3: { agents: tier3 }
        },
        naCount: Number(rotNoAnswerCount),
        naHours: Number(rotNoAnswerHours),
        niDays:  Number(rotNotIntDays),
        newLeadHours: Number(rotNewLeadHours),
        cbHours: rotCbHours===""||rotCbHours==null?undefined:Number(rotCbHours),
        hotDays: Number(rotHotDays),
        rotationStopAfterDays: Number(rotStopDays),
        maxRotationsPerLead: Number(maxRotationsPerLead)||0,
        manualAssignmentWindowMinutes: Number(manualWindowMin)||0,
        staleLeadDays: Number(staleLeadDays)||7,
        staleLeadDaysManagers: Number(staleLeadDaysManagers)||30,
        autoRotationEnabled: autoRotEnabled,
        autoRotationPausedUntil: pausedUntil,
        workingHours: { days: whDays, from: whFrom, to: whTo, afterHoursBehavior: whAfter },
        smartSkipRules: {
          skipOnVacation:         srVac,
          skipIfOfflineHours:     Number(srOffH),
          respectWorkingHours:    srHours,
          skipIfAlreadyHandled:   srHandled,
          haltAfterNotInterested: Number(srHaltNI),
          haltWhenAllHandled:     srHaltAll
        }
      },p.token,p.csrfToken);
      setSaved(true); setTimeout(function(){setSaved(false);},2500);
    }catch(e){
      setSaveError(e&&e.message?e.message:"Save failed");
    }
  };

  // Still read by the Audit Log tab's rollback button, which is a WRITE
  // permission inside a tab everyone with the tab can see — a different question
  // from "may you see this tab", and deliberately left alone.
  var isOwner = p.cu && p.cu.role === "admin";
  // The strip is SETTINGS_TABS filtered by the envelope. The eleven role tests
  // that used to live in this array are transcribed in LEGACY_TAB_RULES and
  // gated equal to them for all 154 synthetic subjects.
  var tabs = settingsStrip.tabs;
  // Tab chip: white-on-gray, active = white bg with 0.5px border. Matches mockup .tab.
  var tabBtn=function(tab){
    var act=activeTab===tab.id;
    // Stays a <div> on purpose: a <button> would inherit UA form styles and
    // the global 40px floor on every width. Keyboard + AT get role/tabIndex/
    // aria-selected and Enter/Space instead; the touch floor is the `.set-tab`
    // rule, mobile only. `aria-selected` doubles as the hook the scroll-into-
    // view effect queries for, so it is load-bearing, not decoration.
    return <div key={tab.id} className="set-tab" role="tab" tabIndex={0} aria-selected={act}
      onClick={function(){setActiveTab(tab.id);}}
      onKeyDown={function(e){ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); setActiveTab(tab.id); } }}
      style={{padding:"8px 14px",fontSize:13,cursor:"pointer",borderRadius:8,whiteSpace:"nowrap",userSelect:"none",flexShrink:0,scrollSnapAlign:"start",
        color:act?"#1a1a1a":"#666",fontWeight:act?500:400,
        background:act?"#fff":"transparent",
        border:"0.5px solid "+(act?"rgba(0,0,0,0.1)":"transparent")}}>
      {tab.label}
    </div>;
  };
  var placeholder=function(icon,title,msg){return <div style={{padding:"40px 20px",textAlign:"center",color:C.textLight}}>
    <div style={{fontSize:40,marginBottom:10}}>{icon}</div>
    <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:4}}>{title}</div>
    <div style={{fontSize:12}}>{msg}</div>
  </div>;};
  return <div style={{padding:"24px 16px 40px",fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
    <div style={{maxWidth:1200,margin:"0 auto"}}>
      <div style={{background:"#fff",borderRadius:12,border:"0.5px solid rgba(0,0,0,0.1)",overflow:"hidden"}}>
        {/* Header */}
        <div style={{padding:"18px 22px",borderBottom:"0.5px solid rgba(0,0,0,0.1)",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:17,fontWeight:500,color:"#1a1a1a"}}>{t.settings}</div>
            <div style={{fontSize:13,color:"#666",marginTop:2}}>ARO Investment · Admin</div>
          </div>
          {/* Save persists the Rotation + Business Rules bundle (the only settings
              wired to doSave); shown only on those two tabs so it isn't an empty
              gesture elsewhere. Other tabs carry their own save controls. */}
          {(activeTab==="rotation"||activeTab==="rules")&&<div style={{display:"flex",gap:8,alignItems:"center"}}>
            {saved&&<span style={{fontSize:12,color:"#0F6E56",background:"#EAF6F0",padding:"4px 10px",borderRadius:8,fontWeight:500}}>✓ Saved</span>}
            {saveError&&<span title={saveError} style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"4px 10px",borderRadius:8,fontWeight:500}}>Save failed</span>}
            <button type="button" onClick={doSave} disabled={loading} style={{fontSize:12,padding:"6px 14px",border:"0.5px solid rgba(24,95,165,0.3)",background:"#E6F1FB",color:"#185FA5",borderRadius:8,cursor:loading?"not-allowed":"pointer",fontWeight:500,fontFamily:"inherit"}}>Save</button>
          </div>}
        </div>

        {/* Tabs. Shared by all 18 tabs, so the `.set-*` rules below are the
            only place this strip's mobile behaviour lives — never per-tab.
            Everything mobile-only is inside the 768 query so desktop keeps
            its exact box: the chip's 40px tap floor (the chips are <div>s,
            not <button>s, so the global `button{min-height:40px}` rule never
            reached them and a 13px label on 8px padding gave ~34px), and the
            trailing fade that says "there is more to the right". Eighteen
            chips are ~1,600px wide against a 343px phone; with a 4px auto-
            hiding scrollbar there was no other signal that 15 of them exist.
            The fade is static — it does not lift at scroll-end — and it sits
            on the wrapper, not the scroller, or it would scroll away with
            the content. `inset-inline-end` + the [dir] variant put it on the
            overflow side in Arabic too. */}
        <style>{""
+ "@media (max-width: 768px) {"
+ "  .set-tab { min-height: 40px; display: flex; align-items: center; }"
+ "  .set-tabs-wrap::after { content: \"\"; position: absolute; top: 0; bottom: 0.5px; inset-inline-end: 0; width: 36px; pointer-events: none; background: linear-gradient(to right, rgba(247,247,245,0), #F7F7F5 70%); }"
+ "  .set-tabs-wrap[dir=\"rtl\"]::after { background: linear-gradient(to left, rgba(247,247,245,0), #F7F7F5 70%); }"
+ "}"
}</style>
        <div className="set-tabs-wrap" dir={t.dir==="rtl"?"rtl":"ltr"} style={{position:"relative"}}>
          <div ref={tabsRef} role="tablist"
            style={{display:"flex",gap:4,padding:"10px 14px",borderBottom:"0.5px solid rgba(0,0,0,0.1)",background:"#F7F7F5",overflowX:"auto",
              WebkitOverflowScrolling:"touch",scrollSnapType:"x proximity"}}>
            {tabs.map(tabBtn)}
          </div>
        </div>

        {/* Panel */}
        <div style={{padding:22,background:"#fff"}}>
        {/* Every tab body renders inside the boundary, keyed on the active tab so a
            crash in one tab cannot latch and poison the tabs the user switches to. */}
        <SettingsTabBoundary key={activeTab} tab={activeTab}>
        {settingsStrip.empty && <div style={{fontSize:13,color:"#666",padding:"18px 2px"}}>{NO_TABS_MSG}</div>}
      {activeTab==="general"&&(function(){
        var fieldLabel = {fontSize:12,color:"#666",display:"block",marginBottom:6};
        var inputStyle = {padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",fontFamily:"inherit",width:"100%",boxSizing:"border-box"};
        return <div>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Company</div>
          <div style={{fontSize:12,color:"#666",marginBottom:14}}>Organization details are fixed for this deployment.</div>
          {/* Static truth line — the app is hardcoded to Africa/Cairo + EGP, so these
              are shown as facts, not editable fields that silently do nothing. */}
          <div style={{fontSize:13,color:"#1a1a1a",fontWeight:500,marginBottom:20,padding:"10px 14px",background:"#F7F7F5",border:"0.5px solid rgba(0,0,0,0.08)",borderRadius:8,maxWidth:700}}>ARO Investment · Cairo (GMT+2) · EGP</div>
          <div style={{maxWidth:340}}>
            <label style={fieldLabel}>Language</label>
            {/* Persisted: writes crm_lang so the choice survives a refresh (read back
                at app init). setLang updates the live translation object. */}
            <Select value={p.lang} onChange={function(e){var v=e.target.value;try{localStorage.setItem("crm_lang",v);}catch(_){}p.setLang(v);}} fontSize={13}
              options={[{ value:"en", label:"English" },{ value:"ar", label:"العربية" }]}/>
          </div>
          {p.cu && p.cu.role === "admin" && <div style={{marginTop:24, paddingTop:20, borderTop:"0.5px solid rgba(0,0,0,0.1)"}}>
            <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Company Announcement</div>
            <div style={{fontSize:12,color:"#666",marginBottom:12}}>
              Send a push notification to every active employee. They'll receive it as a notification on their device.
            </div>
            <Btn onClick={function(){ setAnnOpen(true); }}>📢 Send announcement</Btn>
            {annScheduled.length > 0 && <div style={{marginTop:16}}>
              <div style={{fontSize:12,fontWeight:600,color:"#444",marginBottom:8}}>Scheduled ({annScheduled.length})</div>
              {annScheduled.map(function(s){ return <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"8px 12px",background:"#F7F7F5",border:"0.5px solid rgba(0,0,0,0.08)",borderRadius:8,marginBottom:6}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:"#1a1a1a",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.title || s.body}</div>
                  <div style={{fontSize:11,color:"#666"}}>🕑 {fmtDateTime12(s.scheduledFor)}{s.createdByName?(" · "+s.createdByName):""}</div>
                </div>
                <button onClick={function(){ deleteScheduled(s.id); }} style={{flexShrink:0,padding:"5px 10px",borderRadius:8,border:"1px solid #FCA5A5",background:"#FEF2F2",color:"#B91C1C",fontSize:12,fontWeight:600,cursor:"pointer"}}>Cancel</button>
              </div>; })}
            </div>}
          </div>}
        </div>;
      })()}

      {activeTab==="rotation"&&(function(){
        // ───── Derived values ─────
        var pausedActive = pausedUntil && new Date(pausedUntil) > new Date();
        var masterOn = autoRotEnabled && !pausedActive;
        var allLeads = p.leads || [];
        var nowMs = Date.now();
        var WEEK_MS = 7*24*60*60*1000;

        var rotationsThisWeek = 0, autoAssignedWeek = 0, manualWeek = 0;
        allLeads.forEach(function(l){
          (l.agentHistory||[]).forEach(function(h){
            if(!h || h.action!=="Rotation" || !h.date) return;
            if((nowMs - new Date(h.date).getTime()) >= WEEK_MS) return;
            rotationsThisWeek++;
            var r = String(h.reason||"").toLowerCase();
            if(r==="manual" || r.indexOf("manual")===0) manualWeek++; else autoAssignedWeek++;
          });
        });
        var haltedLeads = allLeads.filter(function(l){return l.rotationStopped===true;}).length;
        var exhaustedLeads = allLeads.filter(function(l){return l.rotationExhausted===true;}).length;
        var stoppedByAge = allLeads.filter(function(l){
          if(!l.createdAt||l.archived) return false;
          if(l.status==="DoneDeal"||l.globalStatus==="donedeal"||l.globalStatus==="eoi") return false;
          return (nowMs - new Date(l.createdAt).getTime()) > rotStopDays*24*60*60*1000;
        }).length;

        var nextInLineIdx = tier1.length>0 ? (((tier1LastIdx+1)%tier1.length+tier1.length)%tier1.length) : -1;
        var lastAssignedUser = (tier1LastIdx>=0 && tier1LastIdx<tier1.length)
          ? (p.users||[]).find(function(u){return String(gid(u))===String(tier1[tier1LastIdx]);})
          : null;

        var roleBadgeOf = function(role){
          if(role==="team_leader") return {label:"TEAM LEADER", bg:"#EEEDFE", fg:"#3C3489"};
          if(role==="sales")       return {label:"SALES",       bg:"#E6F1FB", fg:"#185FA5"};
          if(role==="manager")     return {label:"MANAGER",     bg:"#FAEEDA", fg:"#854F0B"};
          return {label:String(role||"").toUpperCase(), bg:"#EEEEEA", fg:"#666"};
        };
        var initialsOf = function(n){return String(n||"?").split(" ").slice(0,2).map(function(s){return (s[0]||"").toUpperCase();}).join("");};
        // Fallback "currently held" count from the paginated p.leads slice —
        // undercounts agents whose leads aren't in the bootstrap window.
        var activeForFallback = function(uid){return allLeads.filter(function(l){var a=l.agentId&&l.agentId._id?l.agentId._id:l.agentId;return String(a)===String(uid)&&!l.archived;}).length;};
        // The row's stats line is tierRowStats (src/lib/rotationRowStats — pure,
        // gate-run): "N active" is the agent's active SLICES, then "R received ·
        // U uncalled today" with the week muted beside it, all on the agent's own
        // slice (owner decisions 2026-10-28). Server truth when loaded; the
        // active count falls back to the p.leads estimate so the tab never
        // blanks out, and a missing stat renders nothing — never a fake 0.
        var isOnlineNow = function(u){return u && u.lastSeen && (nowMs-new Date(u.lastSeen).getTime()) < 3*60*1000;};

        // Per-agent rotations received today — derived client-side from
        // p.leads.agentHistory[] entries (already in the bootstrap). Day
        // boundary is local-browser 00:00, which on the deployed setup
        // matches the server's Africa/Cairo midnight. Cap-edit gated to
        // admin only per spec (settings page is already admin-routed; this
        // is a defensive belt-and-suspenders).
        var canEditCap = p.cu && p.cu.role === "admin";
        var startOfTodayMs = (function(){ var d=new Date(); d.setHours(0,0,0,0); return d.getTime(); })();
        var todayRotByName = {};
        var todayRotById = {};
        allLeads.forEach(function(l){
          (l.agentHistory||[]).forEach(function(h){
            if (!h || h.action !== "Rotation") return;
            var t = h.date ? new Date(h.date).getTime() : 0;
            if (!t || t < startOfTodayMs) return;
            if (h.agentId) {
              var k = String(h.agentId);
              todayRotById[k] = (todayRotById[k]||0) + 1;
            } else if (h.toAgent) {
              todayRotByName[h.toAgent] = (todayRotByName[h.toAgent]||0) + 1;
            }
          });
        });
        var todayRotFor = function(u){
          if (!u) return 0;
          // Prefer server-side truth (same aggregation cap enforcement uses), so
          // "N/cap today" matches what the cap actually counts and isn't limited
          // to the paginated p.leads slice.
          var s = rotDiagStats && rotDiagStats[String(u._id || u.id || "")];
          if (s && typeof s.todayReceived === "number") return s.todayReceived;
          // Fallback (diagnostics not loaded / failed): client-side estimate from
          // p.leads. Prefer id-keyed count (post-deploy entries). Name-keyed count
          // covers legacy entries from earlier today that pre-date the schema
          // change — used only when there's no id-keyed match.
          var byId = todayRotById[String(u._id || u.id || "")] || 0;
          var byName = todayRotByName[u.name || ""] || 0;
          return byId > 0 ? byId : byName;
        };
        // Save handler — admin-only, fires on blur/Enter. "" → null (clear
        // cap). Optimistic local update via p.setUsers; on failure we re-
        // throw via alert so the row reverts on next render.
        var saveDailyCap = async function(u, rawVal){
          if (!canEditCap || !u) return;
          var uid = String(u._id || u.id || "");
          if (!uid) return;
          var payload;
          var newVal;
          var s = String(rawVal == null ? "" : rawVal).trim();
          if (s === "") { payload = null; newVal = null; }
          else {
            var n = Number(s);
            if (!isFinite(n) || n < 0) { alert("Daily cap must be 0 or a positive integer."); return; }
            payload = Math.floor(n);
            newVal = payload;
          }
          // Drop the shadow draft either way — p.users is authoritative from
          // here, whether the value changed, was rejected, or was a no-op.
          var clearDraft = function(){ setCapDraft(function(prev){ var n=Object.assign({},prev); delete n[uid]; return n; }); };
          // No-op if unchanged.
          var prevVal = (u.dailyLeadCap == null) ? null : Number(u.dailyLeadCap);
          if (prevVal === newVal) { clearDraft(); return; }
          try {
            await apiFetch("/api/users/"+uid, "PUT", { dailyLeadCap: payload }, p.token);
            p.setUsers(function(prev){ return prev.map(function(x){ return String(x._id||x.id)===uid ? Object.assign({}, x, { dailyLeadCap: newVal }) : x; }); });
            clearDraft();
          } catch(e) {
            clearDraft();
            alert("Failed to save daily cap: " + ((e && e.message) || "Unknown error"));
          }
        };

        var tierMeta = {
          tier1:{label:"Top — first priority",  sub:"First 2 rotations go here",                      bg:"#EAF6F0", border:"rgba(15,110,86,0.3)", num:"#0F6E56", text:"#0F6E56"},
          tier2:{label:"Regular",                sub:"Joins Tier 3 after Tier 1 is exhausted",         bg:"#E6F1FB", border:"rgba(24,95,165,0.3)", num:"#185FA5", text:"#185FA5"},
          tier3:{label:"New / Training",         sub:"Joins Tier 2 after Tier 1 is exhausted",         bg:"#F7F7F5", border:"rgba(0,0,0,0.1)",     num:"#666",    text:"#1a1a1a"}
        };
        var tierArrs = {tier1:tier1, tier2:tier2, tier3:tier3};

        var simulatedList = (function(){
          if(!simulateOpen || tier1.length===0) return [];
          var out=[], idx=tier1LastIdx;
          for(var i=0;i<10;i++){
            idx = (idx+1) % tier1.length;
            var uid = tier1[idx];
            var u = (p.users||[]).find(function(x){return String(gid(x))===String(uid);});
            if(u) out.push(u);
          }
          return out;
        })();

        var feedItems = (p.rotNotifs||[]).slice(0,6);

        // ───── agent row ─────
        var renderAgentRow = function(tierKey, uid, idx){
          var u=(p.users||[]).find(function(x){return String(gid(x))===String(uid);});
          if(!u) return null;
          var rb = roleBadgeOf(u.role);
          var online = isOnlineNow(u);
          // ACCOUNT STATE, which is NOT what `online` above means. `online` is
          // presence (lastSeen within 3 minutes); `deactivated` is the Users
          // page's Status badge, u.active === false. The row used to render
          // only presence, so a deactivated agent sitting in a tier was
          // indistinguishable from a working one — the Users page said
          // Inactive and this page said nothing at all. Explicit === false so
          // a user doc that predates the field (undefined) still reads active,
          // which is what the server's own guards assume.
          var deactivated = u.active === false;
          var team = u.teamName || "";
          var dragging = dragFrom && dragFrom.tier===tierKey && dragFrom.idx===idx;
          var hover    = dropOn   && dropOn.tier===tierKey && dropOn.idx===idx && !dragging;
          var meta = tierMeta[tierKey];
          var infoParts = [];
          if(team) infoParts.push(team);
          var rowStats = tierRowStats(rotDiagStats && rotDiagStats[String(uid)], activeForFallback(uid));
          infoParts.push(rowStats.activeLabel);
          var groupLeftStyle = p.isMobile
            ? {display:"flex",alignItems:"center",gap:10,flex:"1 1 100%",minWidth:0}
            : {display:"contents"};
          var groupRightStyle = p.isMobile
            ? {display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",flex:"1 1 100%",justifyContent:"flex-start"}
            : {display:"contents"};
          return <div key={uid}
            draggable={true}
            onDragStart={function(e){ setDragFrom({tier:tierKey,idx:idx}); try{e.dataTransfer.effectAllowed="move";}catch(_){} }}
            onDragOver={function(e){ e.preventDefault(); if(!dropOn||dropOn.tier!==tierKey||dropOn.idx!==idx) setDropOn({tier:tierKey,idx:idx}); try{e.dataTransfer.dropEffect="move";}catch(_){} }}
            onDragLeave={function(){ if(dropOn&&dropOn.tier===tierKey&&dropOn.idx===idx) setDropOn(null); }}
            onDrop={function(e){ e.preventDefault(); e.stopPropagation(); moveAgent(dragFrom,{tier:tierKey,idx:idx}); setDragFrom(null); setDropOn(null); }}
            onDragEnd={function(){ setDragFrom(null); setDropOn(null); }}
            style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"#fff",borderRadius:8,marginBottom:4,cursor:"grab",userSelect:"none",flexWrap:p.isMobile?"wrap":"nowrap",
              border: hover ? "1.5px solid "+meta.num : "0.5px solid rgba(0,0,0,0.05)",
              opacity: dragging?0.5:1}}>
            <div style={groupLeftStyle}>
            <span style={{color:"#999",cursor:"grab",fontSize:13,flexShrink:0}}>⋮⋮</span>
            <div style={{width:30,height:30,borderRadius:"50%",background:meta.num,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:500,flexShrink:0}}>{initialsOf(u.name)}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:500,display:"flex",alignItems:"center",gap:0,flexWrap:"wrap"}}>
                <span style={p.isMobile?{whiteSpace:"normal",wordBreak:"break-word"}:{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.name}</span>
                <span style={{fontSize:9,padding:"2px 6px",borderRadius:10,fontWeight:500,letterSpacing:"0.3px",background:rb.bg,color:rb.fg,marginLeft:6}}>{rb.label}</span>
                {activeVacSet.has(String(uid)) && <span title="Currently on vacation — skipped by auto-rotation"
                  style={{fontSize:9,padding:"2px 6px",borderRadius:10,fontWeight:500,letterSpacing:"0.3px",background:"#FAEEDA",color:"#854F0B",marginLeft:6}}>On Vacation</span>}
                {deactivated && <span title="Account deactivated on the Users page — cannot log in and is skipped by auto-rotation"
                  style={{fontSize:9,padding:"2px 6px",borderRadius:10,fontWeight:500,letterSpacing:"0.3px",background:"#FCEBEB",color:"#A32D2D",marginLeft:6}}>Inactive</span>}
              </div>
              {/* Wraps on mobile like the name one line up. Nowrap+ellipsis cut
                  "Team Alpha · 12 active" to "Team Alp…" once the row wrapped. */}
              <div title={rowStats.activeTitle} style={Object.assign({fontSize:11,color:"#666",fontWeight:400},
                p.isMobile?{whiteSpace:"normal",wordBreak:"break-word",lineHeight:1.4}
                          :{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"})}>
                {infoParts.join(" · ")}
              </div>
              {rowStats.todayLine && <div title={rowStats.title} style={Object.assign({fontSize:11,color:"#444",fontWeight:500},
                p.isMobile?{whiteSpace:"normal",wordBreak:"break-word",lineHeight:1.4}
                          :{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"})}>
                {rowStats.todayLine}{rowStats.weekLine && <span style={{color:"#999",fontWeight:400}}>{" · "+rowStats.weekLine}</span>}
              </div>}
            </div>
            </div>
            <div style={groupRightStyle}>
            {/* Daily lead cap — admin-only; empty = unlimited. The under-
                line shows today's received-via-rotation count, either as
                "N/cap today" when a cap is set or "N today" when unlimited. */}
            {(function(){
              var capVal = (u.dailyLeadCap == null || u.dailyLeadCap === "") ? "" : String(u.dailyLeadCap);
              var received = todayRotFor(u);
              var hint = (capVal === "") ? (received + " today") : (received + "/" + capVal + " today");
              var atCap = (capVal !== "" && received >= Number(capVal));
              return <div title={canEditCap ? "Daily lead cap (auto-rotation). Blank = unlimited." : "Daily lead cap — admin only"}
                style={{display:"flex",flexDirection:"column",alignItems:"flex-end",flexShrink:0,gap:2}}
                onClick={function(e){e.stopPropagation();}}
                onMouseDown={function(e){e.stopPropagation();}}>
                <input type="number" min="0" step="1"
                  disabled={!canEditCap}
                  defaultValue={capVal}
                  placeholder={"∞"}
                  draggable={false}
                  onDragStart={function(e){e.preventDefault(); e.stopPropagation();}}
                  onClick={function(e){e.stopPropagation();}}
                  onKeyDown={function(e){ if(e.key==="Enter"){ e.preventDefault(); e.target.blur(); } }}
                  /* Track the typed value so the capacity strip's X and Z move
                     on every keystroke, not only after the blur+save round
                     trip. The input stays uncontrolled — this is a shadow
                     value, never fed back as `value`. */
                  onChange={function(e){ var v=e.target.value; setCapDraft(function(prev){ var n=Object.assign({},prev); n[String(u._id||u.id||"")]=v; return n; }); }}
                  onBlur={function(e){ saveDailyCap(u, e.target.value); }}
                  style={{width:60,flexShrink:0,fontSize:11,padding:"3px 6px",borderRadius:6,border:"0.5px solid rgba(0,0,0,0.15)",background:canEditCap?"#fff":"#F7F7F5",color:"#1a1a1a",fontFamily:"inherit",textAlign:"center"}}/>
                <span style={{fontSize:9,color:atCap?"#A32D2D":"#999",lineHeight:1,whiteSpace:"nowrap"}}>{hint}</span>
              </div>;
            })()}
            {/* Touch reorder. HTML5 drag-and-drop never fires on touch, so on
                a phone the tiers were read-only. These call the SAME
                `moveAgent` the drop handlers call -- there is one reorder path.
                Its `to.idx` means "insert BEFORE row to.idx" and a same-tier
                move subtracts one when from < to, so a drop onto the row
                immediately below is a no-op (0 -> 1 lands back at 0). Down is
                therefore idx+2, which resolves to idx+1; up is the plain
                idx-1. Both are clamped inside moveAgent, and disabled at the
                ends so nothing ever reaches the clamp. Not `.crm-btn-xs`: the
                global 40px floor is the tap target here. Desktop renders
                nothing -- drag is untouched. */}
            {p.isMobile && (function(){
              var len = (tierArrs[tierKey]||[]).length;
              var atTop = idx<=0, atBottom = idx>=len-1;
              var arrowStyle = function(dis){ return {minWidth:40,fontSize:14,lineHeight:1,padding:"0 8px",borderRadius:8,background:dis?"#F7F7F5":"#fff",border:"0.5px solid rgba(0,0,0,0.15)",color:dis?"#BBB":"#1a1a1a",cursor:dis?"default":"pointer",flexShrink:0,fontFamily:"inherit"}; };
              return <div style={{display:"flex",gap:4,flexShrink:0}} onClick={function(e){e.stopPropagation();}}>
                <button type="button" aria-label="Move up" title="Move up" disabled={atTop} style={arrowStyle(atTop)}
                  onClick={function(e){ e.stopPropagation(); if(!atTop) moveAgent({tier:tierKey,idx:idx},{tier:tierKey,idx:idx-1}); }}>↑</button>
                <button type="button" aria-label="Move down" title="Move down" disabled={atBottom} style={arrowStyle(atBottom)}
                  onClick={function(e){ e.stopPropagation(); if(!atBottom) moveAgent({tier:tierKey,idx:idx},{tier:tierKey,idx:idx+2}); }}>↓</button>
              </div>;
            })()}
            {canManageVacations
              ? <button type="button"
                  title="Schedule vacation"
                  style={{fontSize:11,padding:"3px 8px",borderRadius:8,background:"transparent",border:"0.5px solid rgba(133,79,11,0.3)",color:"#854F0B",cursor:"pointer",flexShrink:0,fontFamily:"inherit"}}
                  onClick={function(e){e.stopPropagation(); openVacModal(uid);}}>Vacation</button>
              : null}
            <button type="button"
              title="Remove from rotation"
              style={{fontSize:11,padding:"3px 8px",borderRadius:8,background:"transparent",border:"0.5px solid rgba(163,45,45,0.3)",color:"#A32D2D",cursor:"pointer",flexShrink:0,fontFamily:"inherit"}}
              onClick={function(e){e.stopPropagation(); removeFromAllTiers(uid);}}>Remove</button>
            {/* Three states, and deactivated OUTRANKS presence. A deactivated
                account cannot produce a fresh lastSeen — login requires
                active:true and every request re-checks it — so green+inactive
                is unreachable, but reading the flag first means the dot can
                never be the last thing still claiming the agent is fine. */}
            <div title={deactivated?"Account deactivated — skipped by auto-rotation":(online?"Online":"Offline")}
              style={{width:8,height:8,borderRadius:"50%",background:deactivated?"#A32D2D":(online?"#0F6E56":"#999"),flexShrink:0}}/>
            </div>
          </div>;
        };

        // ───── tier box ─────
        var renderTier = function(k){
          var m = tierMeta[k]; var agents = tierArrs[k];
          var isEmptyHover = dropOn && dropOn.tier===k && agents.length===0;
          return <div key={k}
            onDragOver={function(e){ if(agents.length===0){ e.preventDefault(); setDropOn({tier:k,idx:0}); } }}
            onDrop={function(e){ if(agents.length===0){ e.preventDefault(); moveAgent(dragFrom,{tier:k,idx:0}); setDragFrom(null); setDropOn(null); } }}
            style={{background:m.bg,border:"0.5px solid "+m.border,borderRadius:12,marginBottom:10,overflow:"hidden"}}>
            <div style={{padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:26,height:26,borderRadius:"50%",background:m.num,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:500}}>{k.slice(-1)}</div>
                <div>
                  <div style={{fontSize:13,fontWeight:500,color:m.text}}>{m.label}</div>
                  <div style={{fontSize:11,color:m.text,opacity:0.8}}>{m.sub}</div>
                </div>
              </div>
              <div style={{fontSize:11,color:m.text,fontWeight:500}}>{agents.length} agent{agents.length===1?"":"s"}</div>
            </div>
            <div style={{padding:"0 8px 8px 8px"}}>
              {agents.length===0
                ? <div style={{fontSize:11,color:m.text,opacity:0.7,padding:"10px 6px",textAlign:"center",fontStyle:"italic",border:"1px dashed "+m.border,borderRadius:8,background:isEmptyHover?"rgba(255,255,255,0.6)":"rgba(255,255,255,0.4)"}}>Drop agents here{isEmptyHover?" ⤵":""}</div>
                : agents.map(function(uid,idx){return renderAgentRow(k,uid,idx);})
              }
            </div>
          </div>;
        };

        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          {/* Mobile/tablet rules for the Rotation tab. Own `.rot-*` namespace —
              `.at-*` belongs to Branches + AssetTrackerPage and is not reused.
              Every declaration carries `!important` because the base values are
              inline styles on the same elements, and an inline style outranks a
              plain stylesheet rule. Desktop (>=1025px) matches none of the three
              queries, so it renders exactly as it did before this block existed.
              Three breakpoints, each for a measured reason:
                1024 — the two-column split needs a range `isMobile` cannot see.
                       `isMobile` is `innerWidth < 768`, so at 768-1024 the grid
                       ran 2fr/1fr while a 240px sidebar held the content to
                       ~452px, leaving the right column at ~144px.
                1100 — the 3-up "This week" tiles. Above 1024 the right column
                       is back to 1/3 of the row, which at 1025 is ~229px and
                       gives each tile ~52px of content — "Auto-assigned" at
                       10px needs ~68 and wrapped to two lines. Widened from
                       900 on 2026-09-02 once that range had been measured.
                 900 — where the 2-up Rotation-durations grid stops fitting.
                 768 — the phone-only stacking of the manual-window row. */}
          <style>{""
+ "@media (max-width: 1100px) {"
+ "  .rot-week-grid { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }"
+ "}"
+ "@media (max-width: 1024px) {"
+ "  .rot-cols { grid-template-columns: minmax(0,1fr) !important; gap: 14px !important; }"
+ "}"
+ "@media (max-width: 900px) {"
+ "  .rot-dur-grid { grid-template-columns: minmax(0,1fr) !important; }"
+ "  /* The 45-day row is `grid-column: span 2` inline. Left alone in a one-column grid it would create an implicit SECOND column and reintroduce the exact overflow the rule above removes. `1 / -1` is full width in both. */"
+ "  .rot-dur-wide { grid-column: 1 / -1 !important; }"
+ "}"
+ "@media (max-width: 768px) {"
+ "  .rot-manual-row { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }"
+ "  .rot-manual-input { width: 72px !important; }"
+ "}"
}</style>
          {/* ══ Master Auto-Rotation card ══ */}
          <div style={{
            background:masterOn?"#EAF6F0":"#FCEBEB",
            border:"0.5px solid "+(masterOn?"rgba(15,110,86,0.3)":"rgba(163,45,45,0.3)"),
            borderRadius:12,padding:"14px 16px",marginBottom:14,
            display:"flex",flexDirection:p.isMobile?"column":"row",justifyContent:"space-between",alignItems:p.isMobile?"stretch":"center",gap:12
          }}>
            <div style={{display:"flex",alignItems:"center",gap:12,flex:1,minWidth:0}}>
              <div onClick={function(){setAutoRotEnabled(!autoRotEnabled); if(!autoRotEnabled) setPausedUntil(null);}}
                title="Toggle auto-rotation"
                style={{width:40,height:22,borderRadius:11,cursor:"pointer",position:"relative",flexShrink:0,
                  background:autoRotEnabled?"#0F6E56":"#94A3B8",transition:"background 0.15s"}}>
                <span style={{display:"block",width:16,height:16,background:"#fff",borderRadius:"50%",position:"absolute",top:3,left:autoRotEnabled?21:3,transition:"left 0.15s"}}/>
              </div>
              <div style={{minWidth:0}}>
                <div style={{fontSize:14,fontWeight:500,color:masterOn?"#0F6E56":"#A32D2D"}}>
                  {!autoRotEnabled ? "Auto-Rotation is OFF" : (pausedActive ? "Auto-Rotation paused" : "Auto-Rotation is active")}
                </div>
                {/* Same branch the agent name above already uses. Nowrap+ellipsis
                    truncated this at "Round-robin pointer at Ahmed (…" on a phone,
                    cutting the rotations-this-week count off the end. */}
                <div style={Object.assign({fontSize:12,color:masterOn?"#0F6E56":"#A32D2D",opacity:0.85},
                  p.isMobile?{whiteSpace:"normal",wordBreak:"break-word",lineHeight:1.4}
                            :{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"})}>
                  {!autoRotEnabled
                    ? "Leads will not auto-rotate until turned back on."
                    : pausedActive
                      ? ("Paused until "+fmtDateTime12(pausedUntil, undefined, { year:false }))
                      : (lastAssignedUser && tier1.length>0
                          ? ("Round-robin pointer at "+String(lastAssignedUser.name||"").split(" ")[0]+" ("+(tier1LastIdx+1)+"/"+tier1.length+") · "+rotationsThisWeek+" rotations this week")
                          : (rotationsThisWeek+" rotations this week · "+(tier1.length+tier2.length+tier3.length)+" agents across 3 tiers"))}
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:6,flexShrink:0,flexWrap:"wrap"}}>
              <button type="button" onClick={function(){setAutoRotEnabled(true); setPausedUntil(new Date(Date.now()+2*60*60*1000).toISOString());}}
                style={{fontSize:12,padding:"6px 12px",border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",borderRadius:8,cursor:"pointer",color:"#1a1a1a",fontFamily:"inherit"}}>Pause 2h</button>
              <button type="button" onClick={function(){setAutoRotEnabled(false); setPausedUntil(null);}}
                style={{fontSize:12,padding:"6px 12px",border:"0.5px solid rgba(163,45,45,0.3)",background:"#FCEBEB",borderRadius:8,cursor:"pointer",color:"#A32D2D",fontFamily:"inherit"}}>Stop</button>
              {p.cu && p.cu.role==="admin" && (
                <button type="button" disabled={redistBusy}
                  onClick={async function(){
                    if(redistBusy) return;
                    if(!window.confirm("This will evenly distribute all pending rotation-eligible leads across all agents. Continue?")) return;
                    setRedistBusy(true);
                    try {
                      var r = await apiFetch("/api/leads/bulk-redistribute-backlog","POST",{},p.token);
                      setRedistResult(r || { total:0, distributed:0, skipped:0, perAgent:{} });
                    } catch(e) {
                      window.alert("Redistribution failed: "+(e && e.message ? e.message : "unknown error"));
                    } finally {
                      setRedistBusy(false);
                    }
                  }}
                  style={{fontSize:12,padding:"6px 12px",border:"0.5px solid rgba(15,110,86,0.35)",background:redistBusy?"#D6E8E0":"#EAF6F0",borderRadius:8,cursor:redistBusy?"wait":"pointer",color:"#0F6E56",fontFamily:"inherit",fontWeight:500}}>
                  {redistBusy ? "Redistributing…" : "Redistribute Backlog"}
                </button>
              )}
            </div>
          </div>

          {/* ══ Redistribute result modal ══ */}
          {redistResult && (function(){
            var rows = Object.keys(redistResult.perAgent||{}).map(function(uid){
              var u = (p.users||[]).find(function(x){return gid(x)===uid;});
              return { uid: uid, name: u ? u.name : uid, count: Number(redistResult.perAgent[uid])||0 };
            }).sort(function(a,b){ return b.count - a.count; });
            var diag = redistResult.diagnostic || null;
            var sectionTitle = {fontSize:12,fontWeight:600,color:"#444",marginTop:14,marginBottom:6,letterSpacing:"0.3px",textTransform:"uppercase"};
            var kvBox = {border:"0.5px solid rgba(0,0,0,0.08)",borderRadius:8,overflow:"hidden"};
            var kvRow = function(k, v, i, highlight){
              return <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"6px 12px",borderTop:i===0?"none":"0.5px solid rgba(0,0,0,0.06)",fontSize:12,background:highlight?"#FFF7E6":"transparent"}}>
                <span style={{color:"#555"}}>{k}</span>
                <span style={{fontWeight:500,color:Number(v)>0?"#1a1a1a":"#999"}}>{v}</span>
              </div>;
            };
            return <div data-overlay-above="true" onClick={function(){setRedistResult(null);}}
              style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999}}>
              <div onClick={function(e){e.stopPropagation();}}
                style={{background:"#fff",borderRadius:12,padding:20,width:"min(560px, 94vw)",maxHeight:"90vh",overflow:"auto",boxShadow:"0 20px 48px rgba(0,0,0,0.25)",fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
                <div style={{fontSize:16,fontWeight:600,marginBottom:10,color:"#0F6E56"}}>Backlog redistribution — diagnostic</div>
                <div style={{fontSize:13,color:"#1a1a1a",marginBottom:6,lineHeight:1.6}}>
                  Distributed <b>{redistResult.distributed}</b> of <b>{redistResult.total}</b> eligible leads. Skipped <b>{redistResult.skipped}</b>.
                </div>

                {diag && (<>
                  <div style={sectionTitle}>Funnel</div>
                  <div style={kvBox}>
                    {kvRow("Total leads scanned", diag.totalLeadsScanned, 0)}
                    {kvRow("  − archived", diag.excluded.archived, 1)}
                    {kvRow("  − noAgent (no agentId)", diag.excluded.noAgent, 1)}
                    {kvRow("  − source = Daily Request", diag.excluded.dailyRequestSource, 1)}
                    {kvRow("  − globalStatus eoi/donedeal", diag.excluded.eoiOrDoneDeal, 1)}
                    {kvRow("  − rotationStopped = true", diag.excluded.rotationStopped, 1)}
                    {kvRow("  − locked = true (🔒 on lead)", diag.excluded.locked || 0, 1)}
                    {kvRow("  − older than " + (diag.thresholds?diag.thresholds.rotationStopAfterDays:"45") + " days", diag.excluded.tooYoung_lessThan45days, 1)}
                    {kvRow("  − lastRotationAt within 1h", diag.excluded.lastRotationWithin1h, 1)}
                    {kvRow("  − no current slice & no fallback", diag.excluded.noCurrentSliceAndNoFallback, 1)}
                    {kvRow("  − noRotation flag on slice", diag.excluded.noRotationFlag, 1)}
                    {kvRow("Passed all DB filters", diag.passed, 1, true)}
                  </div>

                  <div style={sectionTitle}>Status of passing pool</div>
                  <div style={kvBox}>
                    {Object.keys(diag.byStatus).map(function(k, i){ return kvRow(k, diag.byStatus[k], i); })}
                  </div>

                  <div style={sectionTitle}>Eligible by rule</div>
                  <div style={kvBox}>
                    {kvRow("NewLead (newLeadHours)", diag.eligible.byRule.newLead, 0)}
                    {kvRow("NotInterested (niDays)", diag.eligible.byRule.notInt, 1)}
                    {kvRow("CallBack (cbHours)", diag.eligible.byRule.callBack, 1)}
                    {kvRow("Hot/Potential/MeetingDone (hotDays)", diag.eligible.byRule.hot, 1)}
                    {kvRow("NoAnswer (naCount/naHours)", diag.eligible.byRule.noAns, 1)}
                    {kvRow("TOTAL eligible", diag.eligible.total, 1, true)}
                  </div>

                  <div style={sectionTitle}>Not-eligible reasons</div>
                  <div style={kvBox}>
                    {Object.keys(diag.notEligibleReasons).length===0
                      ? <div style={{padding:"8px 12px",fontSize:12,color:"#999"}}>None (every passing lead matched a rule).</div>
                      : Object.keys(diag.notEligibleReasons)
                          .sort(function(a,b){ return diag.notEligibleReasons[b] - diag.notEligibleReasons[a]; })
                          .map(function(k, i){ return kvRow(k, diag.notEligibleReasons[k], i); })}
                  </div>

                  <div style={sectionTitle}>Thresholds in use</div>
                  <div style={kvBox}>
                    {Object.keys(diag.thresholds||{}).map(function(k, i){ return kvRow(k, diag.thresholds[k], i); })}
                  </div>
                </>)}

                <div style={sectionTitle}>Per agent (distributed)</div>
                <div style={kvBox}>
                  {rows.length===0 ? <div style={{padding:"8px 12px",fontSize:12,color:"#999"}}>No agents received leads.</div>
                    : rows.map(function(r, i){
                        return <div key={r.uid} style={{display:"flex",justifyContent:"space-between",padding:"6px 12px",borderTop:i===0?"none":"0.5px solid rgba(0,0,0,0.06)",fontSize:12}}>
                          <span style={{color:"#555"}}>{r.name}</span>
                          <span style={{fontWeight:500,color:r.count>0?"#0F6E56":"#999"}}>{r.count}</span>
                        </div>;
                      })}
                </div>

                <div style={{display:"flex",justifyContent:"flex-end",marginTop:14,gap:8}}>
                  <button type="button"
                    onClick={function(){
                      try { navigator.clipboard.writeText(JSON.stringify(redistResult, null, 2)); } catch(e) {}
                    }}
                    style={{fontSize:12,padding:"8px 14px",border:"0.5px solid rgba(0,0,0,0.15)",background:"#fff",color:"#1a1a1a",borderRadius:8,cursor:"pointer",fontFamily:"inherit"}}>Copy JSON</button>
                  <button type="button" onClick={function(){setRedistResult(null);}}
                    style={{fontSize:13,padding:"8px 16px",border:"0.5px solid rgba(0,0,0,0.1)",background:"#1a1a1a",color:"#fff",borderRadius:8,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
                </div>
              </div>
            </div>;
          })()}

          {/* ══ Manual assignment window (blue) ══ */}
          <div style={{background:"#E6F1FB",border:"0.5px solid rgba(24,95,165,0.3)",borderRadius:12,padding:"14px 16px",marginBottom:18}}>
            <div className="rot-manual-row" style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{fontSize:13,fontWeight:500,color:"#185FA5"}}>Manual assignment window</div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                <span style={{fontSize:12,color:"#185FA5",whiteSpace:"nowrap"}}>Auto-assign after</span>
                {/* flexShrink 0 stops the flex row squeezing this below its
                    content box; the width bump to 72 lives in the <=768 rule,
                    NOT here, because the global mobile stylesheet forces
                    `font-size:16px` on every input (iOS zoom guard) and 60px
                    then clips the max legal value, 1440, while it is typed.
                    Desktop keeps 60 and renders unchanged. */}
                <input className="rot-manual-input" type="number" min={0} max={1440} value={manualWindowMin}
                  onChange={function(e){setManualWindowMin(Number(e.target.value)||0);}}
                  style={{width:60,flexShrink:0,padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",fontFamily:"inherit"}}/>
                <span style={{fontSize:12,color:"#185FA5",whiteSpace:"nowrap"}}>minutes</span>
              </div>
            </div>
            <div style={{fontSize:11,color:"#185FA5",opacity:0.85,lineHeight:1.5}}>Applies to all sources. Admin and Operations assign manually within this window. After timeout, system auto-assigns to Tier 1 round-robin.</div>
          </div>

          {/* ══ Two-column: priority tiers (2fr) + live feed/metrics (1fr) ══
              The collapse is CSS, not `p.isMobile`, on purpose. `isMobile` is
              `innerWidth < 768`, so the desktop branch ran at 768–1024 with a
              240px sidebar eating the width — the right column landed at ~144px
              and the "This week" tiles inside it overflowed. A media query is
              the only thing that can see that range. */}
          <div className="rot-cols" style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:20,marginBottom:24}}>
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:10}}>
                <div style={{fontSize:14,fontWeight:500}}>Priority tiers</div>
                <div style={{fontSize:11,color:"#666"}}>Only Team Leaders &amp; Sales · Directors/Managers excluded</div>
              </div>
              {renderTier("tier1")}
              {renderTier("tier2")}
              {renderTier("tier3")}
              {/* ─── Capacity strip ───────────────────────────────────────────
                  "Total daily caps: X/day · Eligible backlog: Y · Full cycle: ~Z days"
                  The owner tunes caps by hand and needs the cycle-time
                  consequence while editing, so X and Z are derived here from
                  the caps currently on screen (capDraft first, then the saved
                  p.users value) over the tier membership currently in the form
                  — both move before anything is saved. Y comes from the server
                  and stays as fetched: it is a property of the lead data, not
                  of the form. Admin-only, matching who may edit these settings. */}
              {(function(){
                if (!p.cu || p.cu.role !== "admin") return null;
                var inTiers = [];
                var seenCap = {};
                [].concat(tier1,tier2,tier3).forEach(function(id){
                  var s=String(id); if(s && !seenCap[s]){ seenCap[s]=true; inTiers.push(s); }
                });
                var uncapped = 0;
                var X = 0;
                inTiers.forEach(function(uid){
                  var u=(p.users||[]).find(function(x){return String(gid(x))===uid;});
                  if(!u) return;
                  var draft = capDraft[uid];
                  var raw = (draft !== undefined) ? draft : (u.dailyLeadCap == null ? "" : String(u.dailyLeadCap));
                  var s=String(raw).trim();
                  if(s===""){ uncapped++; return; }          // blank = unlimited
                  var n=Number(s);
                  if(isFinite(n) && n>0) X += Math.floor(n);
                });
                var Y = rotCapacity ? rotCapacity.eligibleBacklog : null;
                var Z = (X>0 && Y!=null) ? Math.ceil(Y/X) : null;
                var dash = "—";
                var sep = <span style={{color:"#CBD5E1",margin:"0 8px"}}>·</span>;
                // Direction follows the app language so the "·" separators and
                // the uncapped note sit correctly in Arabic. Strings stay
                // English literals like the rest of this tab (tier labels,
                // "Priority tiers", the manual-window copy) — the layout is what
                // has to be RTL-aware here, and marginInlineStart flips itself.
                return <div dir={t.dir==="rtl"?"rtl":"ltr"} style={{
                  display:"flex", flexWrap:"wrap", alignItems:"center",
                  gap:"2px 0", padding:"8px 12px", marginBottom:10,
                  background:"#F8FAFC", border:"0.5px solid rgba(0,0,0,0.07)",
                  borderRadius:8, fontSize:11.5, color:"#475569", lineHeight:1.6
                }}>
                  <span>Total daily caps: <b style={{color:"#1a1a1a"}}>{X>0?(X+"/day"):dash}</b></span>
                  {sep}
                  <span>Eligible backlog: <b style={{color:"#1a1a1a"}}>{Y!=null?Y.toLocaleString():dash}</b></span>
                  {sep}
                  <span>Full cycle: <b style={{color:"#1a1a1a"}}>{Z!=null?("~"+Z+" day"+(Z===1?"":"s")):dash}</b></span>
                  {uncapped>0 && <span style={{marginInlineStart:8,color:"#854F0B"}}>
                    ({uncapped} uncapped — X excludes {uncapped===1?"it":"them"})
                  </span>}
                </div>;
              })()}
              {(function(){
                var inAny = new Set([].concat(tier1,tier2,tier3).map(String));
                var remaining = salesAgentsForSetting.filter(function(u){return !inAny.has(String(gid(u)));});
                if(!remaining.length) return null;
                // Per-tier "add" button — styled with the tier's accent so it's visually obvious
                // which tier the agent is going to land in.
                var tierAddBtn = function(uid, key, label, bg, brd, fg){
                  return <button type="button" key={key}
                    onClick={function(e){e.stopPropagation(); appendToTier(uid,key);}}
                    title={"Add to "+label}
                    style={{fontSize:11,padding:"4px 8px",border:"0.5px solid "+brd,background:bg,color:fg,borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontWeight:500,flexShrink:0}}>
                    → {label}
                  </button>;
                };
                return <div style={{marginTop:8,border:"1px dashed rgba(0,0,0,0.1)",borderRadius:8,background:"#F7F7F5",maxHeight:200,overflowY:"auto"}}>
                  <div style={{padding:"8px 10px",fontSize:11,fontWeight:500,color:"#666",borderBottom:"0.5px solid rgba(0,0,0,0.05)"}}>Add agent to rotation ({remaining.length} available — pick a target tier)</div>
                  {remaining.map(function(u){
                    var uid=String(gid(u)); var rb=roleBadgeOf(u.role);
                    return <div key={uid}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderBottom:"0.5px solid rgba(0,0,0,0.05)"}}>
                      <div style={{flex:1,minWidth:0,fontSize:12,display:"flex",alignItems:"center",gap:6}}>
                        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.name}</span>
                        <span style={{fontSize:9,padding:"2px 6px",borderRadius:10,fontWeight:500,background:rb.bg,color:rb.fg,flexShrink:0}}>{rb.label}</span>
                      </div>
                      <div style={{display:"flex",gap:4,flexShrink:0}}>
                        {tierAddBtn(uid,"tier1","Tier 1","#EAF6F0","rgba(15,110,86,0.3)","#0F6E56")}
                        {tierAddBtn(uid,"tier2","Tier 2","#E6F1FB","rgba(24,95,165,0.3)","#185FA5")}
                        {tierAddBtn(uid,"tier3","Tier 3","#F7F7F5","rgba(0,0,0,0.15)",       "#666")}
                      </div>
                    </div>;
                  })}
                </div>;
              })()}
            </div>

            <div>
              {/* Live feed */}
              <div style={{fontSize:14,fontWeight:500,marginBottom:8}}>Live feed</div>
              <div style={{background:"#F7F7F5",borderRadius:8,padding:10,lineHeight:1.7,minHeight:60}}>
                {feedItems.length===0
                  ? <div style={{fontSize:11,color:"#999",fontStyle:"italic",padding:"6px 0"}}>No recent rotations.</div>
                  : feedItems.map(function(n,i){
                      var ts = n.createdAt||n.timestamp||n.date;
                      var time = ts ? fmtTime12(ts) : "";
                      var reason = String(n.reason||"").toLowerCase();
                      var dotColor = "#185FA5"; var tagTxt = "T1"; var tagBg="#EAF6F0"; var tagFg="#0F6E56";
                      var shortId = n.leadId ? String(n.leadId).slice(-4) : "—";
                      var txt = "#"+shortId+" → "+(n.toName||"—");
                      // A manual reassign and an auto rotation used to render
                      // identically here ("#d410 → Waad Ahmed"), so an admin
                      // assigning a lead to an agent outside every tier looked
                      // like the rotation engine had done it (2026-08-09). The
                      // reason field already carried the distinction — surface it.
                      var isManual = reason.indexOf("manual")>=0;
                      if(reason.indexOf("cancel")>=0){ dotColor="#854F0B"; txt=(n.leadName||"Lead")+" Cancel → re-rotate"; tagTxt=""; }
                      else if(reason.indexOf("exhausted")>=0){ dotColor="#A32D2D"; txt=(n.leadName||"Lead")+" rotation EXHAUSTED"; tagTxt=""; }
                      else if(isManual){
                        dotColor="#0F6E56"; tagTxt="";
                        txt += " · Manual reassign" + (n.byName ? " — by "+n.byName : "");
                      }
                      else { txt += " · Auto rotation"; }
                      return <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 0",color:"#666",fontSize:11}}>
                        <span style={{width:5,height:5,borderRadius:"50%",background:dotColor,flexShrink:0}}/>
                        <span style={{color:"#1a1a1a",fontWeight:500,minWidth:32}}>{time}</span>
                        <span style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{txt}</span>
                        {tagTxt && <span style={{background:tagBg,color:tagFg,padding:"1px 5px",borderRadius:3,fontSize:10,fontWeight:500,flexShrink:0}}>{tagTxt}</span>}
                      </div>;
                    })}
              </div>

              {/* This week metrics */}
              <div style={{fontSize:14,fontWeight:500,marginTop:14,marginBottom:8}}>This week</div>
              <div className="rot-week-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
                {[
                  {l:"Auto-assigned", v:autoAssignedWeek},
                  {l:"Manual",        v:manualWeek},
                  {l:"Skipped",       v:"—"},
                  {l:"Halted",        v:haltedLeads},
                  {l:"Exhausted",     v:exhaustedLeads},
                  {l:"45d stopped",   v:stoppedByAge}
                ].map(function(m,i){return <div key={i} style={{background:"#F7F7F5",padding:"8px 10px",borderRadius:8}}>
                  <div style={{fontSize:10,color:"#666"}}>{m.l}</div>
                  <div style={{fontSize:18,fontWeight:500,marginTop:2}}>{m.v}</div>
                </div>;})}
              </div>

              <button type="button" onClick={function(){setSimulateOpen(!simulateOpen);}}
                style={{marginTop:10,width:"100%",fontSize:12,padding:"8px 12px",border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",borderRadius:8,cursor:"pointer",color:"#1a1a1a",fontFamily:"inherit"}}>
                {simulateOpen?"Hide simulation":"Simulate next 10"}
              </button>
              {simulateOpen && <div style={{marginTop:8,background:"#F7F7F5",borderRadius:8,padding:10}}>
                {simulatedList.length===0
                  ? <div style={{fontSize:11,color:"#999"}}>Add agents to Tier 1 to simulate.</div>
                  : simulatedList.map(function(u,i){return <div key={i} style={{fontSize:11,color:"#1a1a1a",padding:"3px 0",display:"flex",gap:6}}>
                      <span style={{color:"#666",minWidth:24}}>{(i+1)+"."}</span>
                      <span>{u.name}</span>
                    </div>;})
                }
              </div>}
            </div>
          </div>

          {/* ══ Agent vacations ══ admin/operation only */}
          {canManageVacations && <div style={{marginBottom:20}}>
            <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Agent vacations</div>
            <div style={{fontSize:12,color:"#666",marginBottom:10}}>
              Schedule time off for an agent. When "Skip if agent is on vacation" is enabled, active vacations are skipped by auto-rotation. Click "Vacation" on any agent row above to add one.
            </div>
            <div style={{background:"#F7F7F5",borderRadius:8,padding:vacations.length?6:14}}>
              {vacations.length===0
                ? <div style={{fontSize:12,color:"#999",fontStyle:"italic",textAlign:"center"}}>No upcoming or active vacations.</div>
                : vacations.map(function(v){
                    var ag = v.agentId || {};
                    var aid = ag && ag._id ? String(ag._id) : String(ag);
                    var start = new Date(v.startDate), end = new Date(v.endDate);
                    var now = new Date();
                    var isActive = start <= now && now <= end;
                    var fmt = function(d){ return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}); };
                    return <div key={String(v._id)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"#fff",borderRadius:8,marginBottom:4,border:"0.5px solid rgba(0,0,0,0.05)"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:500,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                          <span>{userNameOf(ag, p.users) || "(unknown)"}</span>
                          {isActive && <span style={{fontSize:9,padding:"2px 6px",borderRadius:10,fontWeight:500,letterSpacing:"0.3px",background:"#FAEEDA",color:"#854F0B"}}>On Vacation</span>}
                        </div>
                        <div style={{fontSize:11,color:"#666",marginTop:2}}>
                          {fmt(start)} → {fmt(end)}{v.reason?(" · "+v.reason):""}
                        </div>
                      </div>
                      <button type="button"
                        style={{fontSize:11,padding:"3px 8px",borderRadius:8,background:"transparent",border:"0.5px solid rgba(163,45,45,0.3)",color:"#A32D2D",cursor:"pointer",flexShrink:0,fontFamily:"inherit"}}
                        onClick={function(){cancelVacation(String(v._id));}}>Cancel</button>
                    </div>;
                  })
              }
            </div>
          </div>}

          {/* ══ Rotation durations ══ */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Rotation durations</div>
            <div style={{fontSize:12,color:"#666",marginBottom:10}}>Applies to Hot Case, Potential, Meeting, CallBack, No Answer, New Lead, and Not Interested</div>
            <div className="rot-dur-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {[
                {label:"No Answer — times before rotation",val:rotNoAnswerCount,set:setRotNoAnswerCount,max:100},
                {label:"No Answer — wait after last (hrs)",val:rotNoAnswerHours,set:setRotNoAnswerHours,max:720},
                {label:"Not Interested — return after (days)",val:rotNotIntDays,set:setRotNotIntDays,max:365},
                {label:"NewLead — rotate after (hrs)",val:rotNewLeadHours,set:setRotNewLeadHours,max:8760},
                {label:"CallBack overdue — rotate after (hrs)",val:rotCbHours,set:setRotCbHours,max:8760,emptyOk:true},
                {label:"Hot / Potential / Meeting no action (days)",val:rotHotDays,set:setRotHotDays,max:365,bold:true}
              ].map(function(row){return <div key={row.label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"#F7F7F5",padding:"8px 10px",borderRadius:8,color:"#666",gap:8,fontSize:12}}>
                <span style={{fontWeight:row.bold?500:400,color:row.bold?"#1a1a1a":"#666"}}>{row.label}</span>
                <input type="number" min={1} max={row.max} value={row.val} onChange={function(e){var raw=e.target.value; if(row.emptyOk && raw===""){row.set(""); return;} row.set(Number(raw));}}
                  style={{width:56,padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",textAlign:"center",fontFamily:"inherit"}}/>
              </div>;})}
              {/* 45-day row spans 2 cols, highlighted yellow */}
              <div className="rot-dur-wide" style={{gridColumn:"span 2",display:"flex",justifyContent:"space-between",alignItems:"center",background:"#FAEEDA",padding:"8px 10px",borderRadius:8,color:"#854F0B",gap:8,fontSize:12}}>
                <span style={{fontWeight:500}}>Stop rotation after (days in CRM) — lead stays with current agent</span>
                <input type="number" min={1} max={3650} value={rotStopDays} onChange={function(e){setRotStopDays(Number(e.target.value));}}
                  style={{width:56,padding:"6px 10px",border:"0.5px solid rgba(133,79,11,0.3)",borderRadius:8,fontSize:13,background:"#fff",textAlign:"center",fontFamily:"inherit"}}/>
              </div>
            </div>
          </div>

          {/* ══ Stale lead visibility (Phase Q) ══ */}
          <div style={{background:"#F1ECF7",border:"0.5px solid rgba(95,55,160,0.3)",borderRadius:12,padding:"14px 16px",marginBottom:18}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{fontSize:13,fontWeight:500,color:"#5F37A0"}}>Stale Lead Threshold — Sales (days)</div>
              <input type="number" min={1} max={365} step={1} value={staleLeadDays}
                onChange={function(e){var v=Math.floor(Number(e.target.value)); if(!isFinite(v)||v<1)v=1; if(v>365)v=365; setStaleLeadDays(v);}}
                style={{width:60,padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",textAlign:"center",fontFamily:"inherit"}}/>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:10,marginBottom:6}}>
              <div style={{fontSize:13,fontWeight:500,color:"#5F37A0"}}>Stale Lead Threshold — Managers (days)</div>
              <input type="number" min={1} max={365} step={1} value={staleLeadDaysManagers}
                onChange={function(e){var v=Math.floor(Number(e.target.value)); if(!isFinite(v)||v<1)v=1; if(v>365)v=365; setStaleLeadDaysManagers(v);}}
                style={{width:60,padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",textAlign:"center",fontFamily:"inherit"}}/>
            </div>
            <div style={{fontSize:11,color:"#5F37A0",opacity:0.85,lineHeight:1.5}}>Sales threshold hides leads from the holding sales agent's own view. Managers threshold (team_leader / manager / director) is independent so supervisors can keep visibility for longer. Admin and operation always see all leads. The rotation system is not affected.</div>
          </div>

          {/* ══ Smart skip rules ══ */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Smart skip rules</div>
            <div style={{fontSize:12,color:"#666",marginBottom:10}}>Applied to each candidate before assignment. Unchecked rules are ignored by the backend.</div>
            <div style={{background:"#F7F7F5",borderRadius:8,padding:14,display:"grid",gap:12,fontSize:13}}>
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={srVac} onChange={function(e){setSrVac(e.target.checked);}}/>
                <span>Skip if agent is on vacation</span>
              </label>
              <label style={{display:"flex",alignItems:"center",gap:10}}>
                <input type="checkbox" checked={srOffH>0} onChange={function(e){setSrOffH(e.target.checked?4:0);}}/>
                <span>Skip if agent offline more than</span>
                <input type="number" min={0} max={168} value={srOffH} onChange={function(e){setSrOffH(Number(e.target.value)||0);}}
                  style={{width:50,padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",textAlign:"center",fontFamily:"inherit"}}/>
                <span>hours</span>
              </label>
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={srHours} onChange={function(e){setSrHours(e.target.checked);}}/>
                <span>Respect working hours</span>
              </label>
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={srHandled} onChange={function(e){setSrHandled(e.target.checked);}}/>
                <span>Skip agents who already handled this lead</span>
              </label>
              <label style={{display:"flex",alignItems:"center",gap:10}}>
                <input type="checkbox" checked={srHaltNI>0} onChange={function(e){setSrHaltNI(e.target.checked?3:0);}}/>
                <span>Halt rotation after</span>
                <input type="number" min={0} max={20} value={srHaltNI} onChange={function(e){setSrHaltNI(Number(e.target.value)||0);}}
                  style={{width:50,padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",textAlign:"center",fontFamily:"inherit"}}/>
                <span>× Not Interested</span>
              </label>
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={srHaltAll} onChange={function(e){setSrHaltAll(e.target.checked);}}/>
                <span>Halt rotation when all agents have handled the lead</span>
              </label>
            </div>
          </div>

          {/* ══ Working hours ══ */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Working hours</div>
            <div style={{fontSize:12,color:"#666",marginBottom:10}}>Company default. Per-agent overrides live in Team &amp; Roles (coming soon).</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(function(d){
                var on = whDays.indexOf(d)>=0;
                return <div key={d}
                  onClick={function(){setWhDays(function(prev){return prev.indexOf(d)>=0 ? prev.filter(function(x){return x!==d;}) : prev.concat([d]);});}}
                  style={{padding:"6px 12px",background:on?"#E6F1FB":"#fff",border:"0.5px solid "+(on?"rgba(24,95,165,0.3)":"rgba(0,0,0,0.1)"),borderRadius:8,fontSize:12,cursor:"pointer",color:on?"#185FA5":"#666",fontWeight:on?500:400,userSelect:"none"}}>{d}</div>;
              })}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:12,color:"#666"}}>From</span>
                <input type="time" value={whFrom} onChange={function(e){setWhFrom(e.target.value);}}
                  style={{padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",fontFamily:"inherit"}}/>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:12,color:"#666"}}>to</span>
                <input type="time" value={whTo} onChange={function(e){setWhTo(e.target.value);}}
                  style={{padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",fontFamily:"inherit"}}/>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:12,color:"#666"}}>After-hours</span>
                <Select value={whAfter} onChange={function(e){setWhAfter(e.target.value);}} width={210} maxWidth={210} fontSize={13}
                  options={[{ value:"queue", label:"Queue until next shift" },{ value:"oncall", label:"Route to on-call agent" }]}/>
              </div>
            </div>
          </div>

          {/* ══ Vacation modal — opened from any agent row ══ */}
          {vacModalAgent && <div data-overlay-above="true" onClick={closeVacModal}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
            <div onClick={function(e){e.stopPropagation();}}
              style={{background:"#fff",borderRadius:12,padding:20,width:400,maxWidth:"92vw",boxShadow:"0 10px 40px rgba(0,0,0,0.2)"}}>
              <div style={{fontSize:15,fontWeight:500,marginBottom:4}}>Schedule vacation</div>
              <div style={{fontSize:12,color:"#666",marginBottom:14}}>
                Agent: <span style={{color:"#1a1a1a",fontWeight:500}}>{vacModalAgent.name}</span>
              </div>
              <div style={{display:"grid",gap:10,marginBottom:14}}>
                <label style={{fontSize:12,color:"#666",display:"flex",flexDirection:"column",gap:4}}>
                  Start date
                  <input type="date" value={vacStart} onChange={function(e){setVacStart(e.target.value);}}
                    style={{padding:"7px 10px",border:"0.5px solid rgba(0,0,0,0.15)",borderRadius:8,fontSize:13,fontFamily:"inherit"}}/>
                </label>
                <label style={{fontSize:12,color:"#666",display:"flex",flexDirection:"column",gap:4}}>
                  End date <span style={{fontSize:11,color:"#999"}}>(inclusive — covers through 23:59)</span>
                  <input type="date" value={vacEnd} onChange={function(e){setVacEnd(e.target.value);}}
                    style={{padding:"7px 10px",border:"0.5px solid rgba(0,0,0,0.15)",borderRadius:8,fontSize:13,fontFamily:"inherit"}}/>
                </label>
                <label style={{fontSize:12,color:"#666",display:"flex",flexDirection:"column",gap:4}}>
                  Reason <span style={{fontSize:11,color:"#999"}}>(optional)</span>
                  <textarea value={vacReason} onChange={function(e){setVacReason(e.target.value);}} rows={2} maxLength={500}
                    style={{padding:"7px 10px",border:"0.5px solid rgba(0,0,0,0.15)",borderRadius:8,fontSize:13,fontFamily:"inherit",resize:"vertical"}}/>
                </label>
              </div>
              {vacError && <div style={{fontSize:12,color:"#A32D2D",marginBottom:10}}>{vacError}</div>}
              <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                <button type="button" onClick={closeVacModal} disabled={vacSaving}
                  style={{fontSize:13,padding:"7px 14px",borderRadius:8,background:"transparent",border:"0.5px solid rgba(0,0,0,0.15)",color:"#1a1a1a",cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                <button type="button" onClick={saveVacation} disabled={vacSaving}
                  style={{fontSize:13,padding:"7px 14px",borderRadius:8,background:"#0F6E56",border:"0.5px solid #0F6E56",color:"#fff",cursor:vacSaving?"wait":"pointer",fontFamily:"inherit",fontWeight:500}}>
                  {vacSaving?"Saving…":"Save"}
                </button>
              </div>
            </div>
          </div>}
        </div>;
      })()}

      {activeTab==="rotationDiag" && <RotationDiagnosticsTab token={p.token} cu={p.cu}/>}

      {activeTab==="team"&&(function(){
        // ═══ THE ROSTER IS AN EXPRESSION OVER THE ROLE REGISTRY ═══════════════
        //
        // Until 2026-08-27 this tab carried three hand-written literals: a
        // `byRole` bucket map naming six positions, a `roleCols` header naming
        // the same six, and a 14-row permissions table whose `vals` arrays were
        // six wide and positionally coupled to that header. The bucket loop was
        // `if(byRole[pos]) byRole[pos].push(u)` with NO else, so anyone the six
        // did not name was dropped — not shown as "other", not counted, absent.
        //
        // Measured on the live roster the day this was rewritten: 34 active
        // users, 30 drawn. The four missing were the HR specialist, the office
        // assistant, the Play Store reviewer, and — because `operation` had a
        // bucket that no JSX row ever consumed — the Operations manager. Three
        // of those were invisible to the literal; the fourth was invisible to
        // the render, which is why counting buckets is not the same check as
        // counting what reaches the screen.
        //
        // So: buckets come from the registry, everyone the registry cannot place
        // lands in a visible Unplaced group, and the banner reconciles the users
        // the JSX will actually draw against the roster it was handed.
        //
        // THIS TAB IS READ-ONLY. Nothing below writes, and every action button
        // stays disabled exactly as it was.
        var users = (p.users||[]).filter(function(u){return u.active;});

        // ───── GATE-LIFTED REGION — START ────────────────────────────────────
        // verify-team-roles-registry.js slices this file between the anchor line
        // below (`var trPositions =`) and the END anchor, prepends the
        // module-scope registry, and RUNS it four ways: against the real
        // registry; with a fake role spliced in (which must appear here with no
        // code change); with a real role removed (its holders must land in
        // Unplaced, never vanish); and with one render group dropped (the banner
        // predicate must fire). A comment claiming this is registry-driven would
        // be a claim; the gate executes it.
        //
        // Everything between the anchors must therefore stay PURE over
        // (the registry, `t`, `users`) — no `p.`, no hooks, no JSX.
        var trPositions = positionsWhereFE(function(){ return true; });

        // Label / colour / banded flags for a POSITION. A role with no bands
        // answers for itself; a banded role answers per band, because
        // hasTargets, inRotation and leadScope on `sales` carry the BANDED_FE
        // sentinel and there IS no role-level answer to read.
        var trMeta = (function(){
          var m = {};
          Object.keys(ROLE_DEFINITIONS_FE).forEach(function(r){
            var d = ROLE_DEFINITIONS_FE[r];
            var bands = (d && Array.isArray(d.bands)) ? d.bands : [];
            if(!bands.length){
              m[r] = { position:r, role:r, label:roleLabel(r,t), colour:d.colour,
                       department:d.department, ladder:d.ladder, readOnly:!!d.readOnly,
                       leadScope:d.leadScope, inRotation:d.inRotation, hasTargets:d.hasTargets };
              return;
            }
            bands.forEach(function(b){
              m[b.position] = { position:b.position, role:r,
                                label:(b.i18nKey&&t&&t[b.i18nKey])||b.label||roleLabel(r,t),
                                colour:b.colour||d.colour, department:d.department, ladder:d.ladder,
                                readOnly:!!d.readOnly, leadScope:b.leadScope,
                                inRotation:b.inRotation, hasTargets:b.hasTargets };
            });
          });
          return m;
        })();

        // Departments in the registry's declared order, then any department a
        // role names that ROLE_DEPARTMENTS_FE forgot — the two lists drifting
        // must not be a way for a department to disappear.
        //
        // TR_NO_DEPT is the same idea one level down: `department` is a required
        // registry field, but a position that reaches here without one would
        // belong to no department card, be drawn nowhere, and trip the banner
        // with no card to point at. It gets a card labelled for what it is.
        var TR_NO_DEPT = "(undeclared)";
        var trDeptOf = function(pos){ return (trMeta[pos]||{}).department || TR_NO_DEPT; };
        var trDepts = (function(){
          var out = [], seen = {};
          ROLE_DEPARTMENTS_FE.forEach(function(dep){ if(!seen[dep]){seen[dep]=1; out.push(dep);} });
          trPositions.forEach(function(pos){
            var dep = trDeptOf(pos);
            if(!seen[dep]){ seen[dep]=1; out.push(dep); }
          });
          return out;
        })();
        var trPositionsIn = function(dep){
          return trPositions.filter(function(pos){ return trDeptOf(pos) === dep; });
        };
        // deptLabel answers from the registry's ladder labels. A department with
        // no ladder (executive holds the Owner alone; technical holds the demo
        // viewer) has no label to offer and it hands the key straight back.
        // Capitalising the key is a RENDER decision; inventing a prettier name
        // here would be a second naming authority, which is the exact failure
        // LADDER_LABEL_BY_DEPT_FE was built to end.
        var trDeptTitle = function(dep){
          var lab = deptLabel(null, dep);
          return lab === dep ? (String(dep).charAt(0).toUpperCase()+String(dep).slice(1)) : lab;
        };
        var trDeptColour = function(dep){
          var first = trPositionsIn(dep)[0];
          return (trMeta[first]||{}).colour || "#94A3B8";
        };

        // THE BUCKETS. Every user lands somewhere: a position the registry
        // knows, or UNPLACED. positionOf() returns null for a sales-ladder user
        // with neither titleRank nor a resolvable title, and returns the raw
        // role for a role the registry has since dropped — both land here.
        var TR_UNPLACED = "__unplaced";
        var trBuckets = (function(){
          var b = {}; trPositions.forEach(function(pos){ b[pos] = []; });
          b[TR_UNPLACED] = [];
          users.forEach(function(u){
            var pos = positionOf(u);
            if(pos && b[pos]) b[pos].push(u); else b[TR_UNPLACED].push(u);
          });
          return b;
        })();
        var trMembersOf = function(pos){ return trBuckets[pos] || []; };

        var trRtId = function(u){ if(!u.reportsTo) return ""; return u.reportsTo._id ? String(u.reportsTo._id) : String(u.reportsTo); };
        var trChildren = function(parentId, pos){
          return trMembersOf(pos).filter(function(u){ return trRtId(u) === String(parentId); });
        };

        // ───── The sales ladder, from the registry's own band order ──────────
        // The bands are declared bottom-up (agent → TL → manager → director).
        // A band that leads other people is one whose leadScope is not "own";
        // those become the stacked rows, drawn top-down. The "own" bands are the
        // people IN the teams and are drawn as chips inside the team cards —
        // which is exactly the shape this tab has always had, now derived rather
        // than transcribed.
        var trSalesPositions   = trPositionsIn("sales");
        var trSalesLeadPos     = trSalesPositions.filter(function(pos){ return (trMeta[pos]||{}).leadScope !== "own"; }).slice().reverse();
        var trSalesMemberPos   = trSalesPositions.filter(function(pos){ return (trMeta[pos]||{}).leadScope === "own"; });
        // The executive department sits above every department rather than
        // inside one, so it is the chart's top row. Derived, not spelled
        // "admin": a second executive-department role must appear here too.
        var trTopPositions     = trPositionsIn("executive");
        var trLadderRows = trTopPositions.concat(trSalesLeadPos).map(function(pos){
          return { position:pos, members:trMembersOf(pos) };
        });

        // Team cards: one per leader who has direct members under them, plus one
        // card for members no leader claims. Today every agent reports to a drawn
        // leader, so the unattached card renders empty-handed and is skipped —
        // but a leader going inactive is a one-click event on the Users page, and
        // without this card their whole team would silently leave the screen.
        var trTeamCards = (function(){
          var cards = [], claimed = {};
          trSalesLeadPos.slice().reverse().forEach(function(lpos){
            trMembersOf(lpos).forEach(function(leader){
              var direct = [];
              trSalesMemberPos.forEach(function(mpos){
                trChildren(gid(leader), mpos).forEach(function(u){ direct.push(u); claimed[gid(u)] = 1; });
              });
              if(!direct.length) return;
              cards.push({ key:lpos+"-"+gid(leader),
                           title:(lpos==="team_leader" ? (leader.teamName || (leader.name+" Team"))
                                                       : ("Direct Reports · "+leader.name)),
                           leadPosition:lpos, members:direct });
            });
          });
          var loose = [];
          trSalesMemberPos.forEach(function(mpos){
            trMembersOf(mpos).forEach(function(u){ if(!claimed[gid(u)]) loose.push(u); });
          });
          if(loose.length) cards.push({ key:"unattached", title:"Unattached — no active leader",
                                        leadPosition:null, members:loose, warn:true });
          return cards;
        })();

        // Flat cards for every department the chart above does not draw. Derived
        // by subtraction, so a department added to the registry gets a card the
        // same day rather than the day someone remembers this list exists.
        var TR_CHART_DEPTS = ["executive","sales"];
        var trDeptCards = trDepts.filter(function(dep){ return TR_CHART_DEPTS.indexOf(dep) === -1; })
          .map(function(dep){
            return { dept:dep, title:trDeptTitle(dep), colour:trDeptColour(dep),
                     rows:trPositionsIn(dep).map(function(pos){
                       return { position:pos, meta:trMeta[pos], members:trMembersOf(pos) };
                     }) };
          });

        // THE RECONCILIATION. trDrawn is assembled from the four arrays the JSX
        // below maps over — not from the bucket partition, which is total by
        // construction and would make this check unable to fail. Narrow, filter
        // or drop any one of those arrays and the count moves.
        var trDrawn = (function(){
          var out = [];
          trLadderRows.forEach(function(r){ r.members.forEach(function(u){ out.push(u); }); });
          trTeamCards.forEach(function(c){ c.members.forEach(function(u){ out.push(u); }); });
          trDeptCards.forEach(function(c){ c.rows.forEach(function(r){ r.members.forEach(function(u){ out.push(u); }); }); });
          trMembersOf(TR_UNPLACED).forEach(function(u){ out.push(u); });
          return out;
        })();
        // Two distinct failures, and a single count would hide the second: a
        // person drawn NOWHERE, and a person drawn TWICE (two containers both
        // claiming them, which reads as a bigger roster than there is).
        var trAuditOf = function(drawn){
          var seen = {}, dupes = [];
          drawn.forEach(function(u){ var k = gid(u); if(seen[k]) dupes.push(u); seen[k] = 1; });
          var missing = users.filter(function(u){ return !seen[gid(u)]; });
          return { chips:drawn.length, distinct:Object.keys(seen).length, expected:users.length,
                   missing:missing, dupes:dupes,
                   ok:(Object.keys(seen).length === users.length && drawn.length === Object.keys(seen).length) };
        };
        var trAudit = trAuditOf(trDrawn);

        // ───── The permissions matrix, per POSITION ─────────────────────────
        // Scope words come from the registry's leadScope, so director and
        // manager now read the same ("SUBTREE") because the registry says they
        // ARE the same scope. The old table drew them as "ALL UNDER" vs "TEAMS
        // UNDER", a distinction no code enforces.
        var TR_SCOPE_WORD = { unrestricted:"ALL", subtree:"SUBTREE", team:"OWN TEAM", own:"OWN", none:"—" };
        var trScope   = function(m){ return TR_SCOPE_WORD[m.leadScope] || "—"; };
        var trAll     = function(m){ return m.leadScope === "unrestricted" && !m.readOnly; };
        // The registry carries no owner flag — isOwner lives on the User document
        // and is deliberately not mirrored here. These three rows therefore name
        // the admin ROLE, and are marked reference-only for exactly that reason.
        var trAdminOnly = function(m){ return m.role === "admin"; };
        var trYes = "✓", trNo = "—";
        var trRefRows = [
          {label:"View own data",       cell:function(){ return trYes; }},
          {label:"View team data",      cell:function(m){ return trScope(m); }, src:"leadScope"},
          {label:"Edit lead data",      cell:function(m){ return m.readOnly ? trNo : (m.leadScope==="unrestricted" ? trYes : (m.leadScope==="own" ? "OWN" : trNo)); }},
          {label:"Reassign lead",       cell:function(m){ return m.readOnly ? trNo : (m.leadScope==="unrestricted" ? trYes : (m.leadScope==="own" ? trNo : trScope(m))); }},
          {label:"Change lead status",  cell:function(m){ return m.readOnly ? trNo : (m.leadScope==="unrestricted" ? trYes : trScope(m)); }},
          {label:"Convert Hot → EOI",   cell:function(m){ return m.readOnly ? trNo : (m.leadScope==="unrestricted" ? trYes : trScope(m)); }, highlight:"green", bold:true},
          {label:"Convert EOI → Deal",  cell:function(m){ return m.readOnly ? trNo : (m.leadScope==="unrestricted" ? trYes : trScope(m)); }, highlight:"green", bold:true},
          {label:"Approve EOI",         cell:function(m){ return trAll(m) ? trYes : trNo; }},
          {label:"Approve Deal",        cell:function(m){ return trAll(m) ? trYes : trNo; }},
          {label:"Cancel EOI / Deal",   cell:function(m){ return trAll(m) ? trYes : trNo; }},
          {label:"Delete lead",         cell:function(m){ return trAdminOnly(m) ? trYes : trNo; }},
          {label:"Delete user",         cell:function(m){ return trAdminOnly(m) ? trYes : trNo; }},
          {label:"Edit commissions",    cell:function(m){ return trAdminOnly(m) ? trYes : trNo; }},
          {label:"Receive rotation",    cell:function(m){ return m.inRotation ? trYes : trNo; }, src:"inRotation", highlight:"blue", bold:true},
          {label:"Carries a quarterly target", cell:function(m){ return m.hasTargets ? trYes : trNo; }, src:"hasTargets"}
        ];
        // ───── GATE-LIFTED REGION — END ─────────────────────────────────────

        // Node palette. The four the chart has always drawn keep their exact
        // colours; anything the registry adds falls back to its own colour so a
        // new position renders rather than throwing on an undefined lookup.
        function trNodeFg(pos){
          var legacy = {admin:"#A32D2D", director:"#0F6E56", manager:"#854F0B", team_leader:"#3C3489"}[pos];
          return legacy || (trMeta[pos]||{}).colour || "#666";
        }
        var trNodeStyle = function(pos){
          var legacy = {
            admin:       {bg:"#FCEBEB",fg:"#A32D2D",fontSize:12,padding:"10px 14px",minWidth:160},
            director:    {bg:"#E1F5EE",fg:"#0F6E56",fontSize:12,padding:"10px 14px",minWidth:160},
            manager:     {bg:"#FAEEDA",fg:"#854F0B",fontSize:12,padding:"10px 14px",minWidth:160},
            team_leader: {bg:"#EEEDFE",fg:"#3C3489",fontSize:11,padding:"8px 10px",  minWidth:140}
          }[pos];
          var map = legacy || {bg:"#F7F7F5",fg:trNodeFg(pos),fontSize:12,padding:"10px 14px",minWidth:160};
          return {padding:map.padding,borderRadius:8,background:map.bg,color:map.fg,fontSize:map.fontSize,fontWeight:500,display:"flex",alignItems:"center",gap:6,minWidth:map.minWidth,justifyContent:"center"};
        };
        var hRow  = function(nodes){return <div style={{display:"flex",gap:12,justifyContent:"center",marginBottom:6,flexWrap:"wrap"}}>{nodes}</div>;};
        var hDown = <div style={{textAlign:"center",color:"#999",fontSize:18,lineHeight:1,padding:"4px 0"}}>↓</div>;
        var trChip = function(u, colour){
          return <span key={gid(u)} style={{fontSize:10,padding:"2px 6px",background:"#E6F1FB",color:colour||"#185FA5",borderRadius:8}}>{u.name}</span>;
        };

        // Deletion preview: first holder of the lowest leadership band that has
        // one, so the card keeps working when the roster has no manager.
        var previewMgr  = (function(){
          var found = null;
          trSalesLeadPos.slice().reverse().forEach(function(pos){ if(!found && trMembersOf(pos).length) found = trMembersOf(pos)[0]; });
          return found;
        })();
        var previewPos  = previewMgr ? positionOf(previewMgr) : null;
        var previewTLs  = previewMgr ? trSalesLeadPos.reduce(function(acc,pos){ return pos===previewPos ? acc : acc.concat(trChildren(gid(previewMgr),pos)); },[]) : [];
        var previewSales = previewTLs.reduce(function(acc,tl){
          return acc.concat(trSalesMemberPos.reduce(function(a2,mpos){ return a2.concat(trChildren(gid(tl),mpos)); },[]));
        },[]);

        // ───── Enforced capabilities ────────────────────────────────────────
        // The key list is the SERVER'S registry, delivered in the access
        // envelope — not a copy of CAPABILITIES kept here. A fifth capability
        // appears on this screen the deploy it appears in the envelope.
        //
        // canDo() answers for the SIGNED-IN USER and nobody else. The per-rank
        // grants live in the capability matrix, which this screen does not
        // fetch, so only the viewer's own column is resolved and every other
        // cell says "resolved per rank" rather than inventing a ✓ or a —.
        var trCapKeys = Object.keys((p.access&&p.access.capabilities)||{});
        var trCapLabel = function(k){ return String(k).replace(/([A-Z])/g," $1").replace(/^./,function(c){return c.toUpperCase();}); };
        var trMyPos = positionOf(p.cu);

        var cellFor = function(v){
          if(v===trYes) return <span style={{color:"#0F6E56",fontWeight:500,fontSize:13}}>✓</span>;
          if(v===trNo)  return <span style={{color:"#999"}}>—</span>;
          return <span style={{color:"#0F6E56",fontWeight:500,fontSize:9,letterSpacing:"0.3px"}}>{v}</span>;
        };
        var rowBg = function(h){ if(h==="green") return "#EAF6F0"; if(h==="blue") return "#E6F1FB"; return "transparent"; };
        var colHead = function(pos){
          var m = trMeta[pos] || {};
          return <th key={pos} title={m.department||""} style={{textAlign:"center",padding:"10px 8px",fontWeight:500,fontSize:11,color:m.colour||"#666",whiteSpace:"nowrap"}}>{m.label||pos}</th>;
        };

        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          {/* ══ Roster reconciliation banner ══ */}
          {!trAudit.ok && <div style={{background:"#FCEBEB",border:"0.5px solid rgba(163,45,45,0.35)",borderLeft:"3px solid #A32D2D",padding:"12px 14px",borderRadius:8,fontSize:12,color:"#A32D2D",marginBottom:16,lineHeight:1.6}}>
            <div style={{fontWeight:600,fontSize:13,marginBottom:4}}>⚠ This screen is not showing everyone.</div>
            <div>{trAudit.expected} active user{trAudit.expected===1?"":"s"} in the roster · {trAudit.distinct} drawn below{trAudit.chips!==trAudit.distinct?(" · "+(trAudit.chips-trAudit.distinct)+" drawn twice"):""}.</div>
            {trAudit.missing.length>0 && <div style={{marginTop:4}}>Never drawn: {trAudit.missing.map(function(u){return u.name+" ("+(positionOf(u)||u.role||"?")+")";}).join(", ")}</div>}
            {trAudit.dupes.length>0 && <div style={{marginTop:4}}>Drawn more than once: {trAudit.dupes.map(function(u){return u.name;}).join(", ")}</div>}
            <div style={{marginTop:6,opacity:0.85}}>Every position on this screen comes from the role registry, so this means a render group was narrowed or dropped — not that a role is missing from the registry.</div>
          </div>}

          {/* ══ Team hierarchy ══ */}
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Team hierarchy</div>
          <div style={{fontSize:12,color:"#666",marginBottom:10}}>
            {trAudit.expected} active user{trAudit.expected===1?"":"s"} across {trPositions.length} positions in {trDepts.length} departments — every position declared in the role registry, drawn whether or not anyone holds it.
          </div>

          <div style={{padding:24,background:"#F7F7F5",borderRadius:8,marginBottom:16}}>
            {trLadderRows.map(function(row,ri){
              var m = trMeta[row.position] || {};
              return <Fragment key={row.position}>
                {ri>0 && hDown}
                {row.members.length===0
                  ? hRow([<div key="empty" style={Object.assign({},trNodeStyle(row.position),{opacity:0.5,fontStyle:"italic"})}>No {m.label||row.position}</div>])
                  : hRow(row.members.map(function(u){return <div key={gid(u)} style={trNodeStyle(row.position)}>{u.name} · {m.label||row.position}</div>;}))
                }
              </Fragment>;
            })}

            {/* Team cards — one per leader with direct members, plus the
                unattached card when a member's leader is gone. */}
            {trTeamCards.length>0 && <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))",gap:10,marginTop:14}}>
              {trTeamCards.map(function(tc){
                return <div key={tc.key} style={{background:"#fff",border:"0.5px solid "+(tc.warn?"rgba(163,45,45,0.35)":"rgba(0,0,0,0.1)"),borderRadius:8,padding:"10px 12px"}}>
                  <div style={{fontSize:11,fontWeight:500,color:tc.warn?"#A32D2D":trNodeFg(tc.leadPosition),marginBottom:6}}>{tc.title}</div>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    {tc.members.map(function(mm){ return trChip(mm); })}
                  </div>
                </div>;
              })}
            </div>}

            {/* Flat department cards — every department the chart above does not
                draw, in the registry's declared order. */}
            {trDeptCards.length>0 && <div style={{marginTop:18}}>
              <div style={{fontSize:11,color:"#999",textAlign:"center",marginBottom:8}}>Other departments</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))",gap:10}}>
                {trDeptCards.map(function(dc){
                  var total = dc.rows.reduce(function(n,r){ return n + r.members.length; },0);
                  return <div key={dc.dept} style={{background:"#fff",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,padding:"10px 12px"}}>
                    <div style={{fontSize:11,fontWeight:500,color:dc.colour,marginBottom:6}}>{dc.title}</div>
                    {dc.rows.map(function(r){
                      return <div key={r.position} style={{marginBottom:6}}>
                        <div style={{fontSize:10,color:"#999",marginBottom:3}}>{(r.meta&&r.meta.label)||r.position}</div>
                        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                          {r.members.length===0
                            ? <span style={{fontSize:10,color:"#CBD5E1",fontStyle:"italic"}}>nobody</span>
                            : r.members.map(function(mm){ return trChip(mm, (r.meta&&r.meta.colour)||"#185FA5"); })}
                        </div>
                      </div>;
                    })}
                    {total===0 && <div style={{fontSize:10,color:"#CBD5E1",fontStyle:"italic"}}>No members yet</div>}
                  </div>;
                })}
              </div>
            </div>}

            {/* Unplaced — positionOf() could not place these people. Usually a
                sales-ladder user with no job title, so no rank, so no band. */}
            {trMembersOf(TR_UNPLACED).length>0 && <div style={{marginTop:18,background:"#FCEBEB",border:"0.5px solid rgba(163,45,45,0.3)",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:11,fontWeight:500,color:"#A32D2D",marginBottom:6}}>Unplaced — no position resolves</div>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {trMembersOf(TR_UNPLACED).map(function(u){
                  return <span key={gid(u)} title={"role: "+(u.role||"—")+" · title: "+(u.title||"—")} style={{fontSize:10,padding:"2px 6px",background:"#fff",color:"#A32D2D",border:"0.5px solid rgba(163,45,45,0.3)",borderRadius:8}}>{u.name}</span>;
                })}
              </div>
              <div style={{fontSize:10,color:"#A32D2D",opacity:0.85,marginTop:6,lineHeight:1.5}}>A sales-ladder user with no Job Title has no rank, and without a rank there is no band to place them in. Set the Job Title on the Users page.</div>
            </div>}

            <div style={{marginTop:14,display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
              <button type="button" disabled title="Coming in later tab" style={{fontSize:12,padding:"6px 12px",border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",borderRadius:8,cursor:"not-allowed",color:"#1a1a1a",fontFamily:"inherit",opacity:0.6}}>+ Add Team</button>
              <button type="button" disabled title="Coming in later tab" style={{fontSize:12,padding:"6px 12px",border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",borderRadius:8,cursor:"not-allowed",color:"#1a1a1a",fontFamily:"inherit",opacity:0.6}}>+ Add Manager</button>
              <button type="button" disabled title="Coming in later tab" style={{fontSize:12,padding:"6px 12px",border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",borderRadius:8,cursor:"not-allowed",color:"#1a1a1a",fontFamily:"inherit",opacity:0.6}}>Edit hierarchy</button>
            </div>
          </div>

          {/* Info box */}
          <div style={{background:"#E6F1FB",borderLeft:"3px solid #185FA5",padding:"10px 14px",borderRadius:8,fontSize:12,color:"#185FA5",marginBottom:16,lineHeight:1.5}}>
            <b>Multiple Sales Managers:</b> A Sales Director can oversee multiple Sales Managers. Each Manager can have multiple Team Leaders under them. A Team Leader leads one team of Sales agents. Every other department is flat — it has a job-title ladder for rank, not a reporting chart.
          </div>

          {/* ══ Enforced capabilities ══ */}
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Enforced capabilities</div>
          <div style={{fontSize:12,color:"#666",marginBottom:10}}>
            Real gates. The server resolves each one per rank and sends the answer for whoever is signed in; this table renders that answer and owns no copy of the rule.
            {trMyPos ? <Fragment> Your column is <b style={{color:(trMeta[trMyPos]||{}).colour||"#1a1a1a"}}>{(trMeta[trMyPos]||{}).label||trMyPos}</b> — every other cell shows <span style={{color:"#999"}}>·</span>, meaning “resolved per rank”, because this screen does not fetch the grant matrix. Edit them on the Capabilities tab.</Fragment> : null}
          </div>

          <div style={{overflowX:"auto",marginBottom:24}}>
            {trCapKeys.length===0
              ? <div style={{background:"#F7F7F5",border:"0.5px dashed rgba(0,0,0,0.1)",borderRadius:8,padding:"14px 16px",fontSize:12,color:"#666"}}>The access envelope has not answered yet — no capability is being reported for this session.</div>
              : <table style={{width:"100%",fontSize:11,borderCollapse:"collapse"}}>
                <thead style={{background:"#EAF6F0"}}>
                  <tr>
                    <th style={{textAlign:"left",padding:"10px 8px",fontWeight:500,fontSize:11,color:"#0F6E56",minWidth:180}}>Capability · enforced</th>
                    {trPositions.map(colHead)}
                  </tr>
                </thead>
                <tbody>
                  {trCapKeys.map(function(k){
                    var mine = canDo(p.access, k);
                    return <tr key={k}>
                      <td style={{padding:"8px",borderTop:"0.5px solid rgba(0,0,0,0.1)",fontSize:11,fontWeight:500,color:"#0F6E56"}}>{trCapLabel(k)}</td>
                      {trPositions.map(function(pos){
                        var isMine = pos === trMyPos;
                        return <td key={pos} style={{padding:"8px",borderTop:"0.5px solid rgba(0,0,0,0.1)",fontSize:11,textAlign:"center",background:isMine?"#EAF6F0":"transparent"}}>
                          {isMine
                            ? cellFor(mine ? trYes : trNo)
                            : <span title="Resolved per rank by the server — see the Capabilities tab" style={{color:"#CBD5E1"}}>·</span>}
                        </td>;
                      })}
                    </tr>;
                  })}
                </tbody>
              </table>}
          </div>

          {/* ══ Reference matrix ══ */}
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Permissions — reference, not enforced</div>
          <div style={{fontSize:12,color:"#666",marginBottom:10,lineHeight:1.6}}>
            <b style={{color:"#854F0B"}}>Nothing in this table is a gate.</b> It describes rules the server enforces elsewhere; changing a cell here would change nothing. Three rows are read straight from the role registry and are marked <span style={{fontSize:10,padding:"1px 5px",background:"#E6F1FB",color:"#185FA5",borderRadius:6}}>registry</span>; the rest are written here and can drift from the server. Scope: ALL = everyone · SUBTREE = everything under them · OWN TEAM = their own team · OWN = their own records.
          </div>

          <div style={{overflowX:"auto",marginBottom:24}}>
            <table style={{width:"100%",fontSize:11,borderCollapse:"collapse"}}>
              <thead style={{background:"#F7F7F5"}}>
                <tr>
                  <th style={{textAlign:"left",padding:"10px 8px",fontWeight:500,fontSize:11,color:"#666",minWidth:180}}>Action</th>
                  {trPositions.map(colHead)}
                </tr>
              </thead>
              <tbody>
                {trRefRows.map(function(row,i){
                  var hBg = rowBg(row.highlight);
                  var actionColor = row.highlight==="blue" ? "#185FA5" : "#1a1a1a";
                  return <tr key={i} style={{background:hBg}}>
                    <td style={{padding:"8px",borderTop:"0.5px solid rgba(0,0,0,0.1)",fontSize:11,fontWeight:row.bold?500:400,color:actionColor}}>
                      {row.bold ? <b>{row.label}</b> : row.label}
                      {row.src && <span title={"read from ROLE_DEFINITIONS_FE."+row.src} style={{marginLeft:6,fontSize:9,padding:"1px 5px",background:"#E6F1FB",color:"#185FA5",borderRadius:6}}>registry</span>}
                    </td>
                    {trPositions.map(function(pos){
                      return <td key={pos} style={{padding:"8px",borderTop:"0.5px solid rgba(0,0,0,0.1)",fontSize:11,textAlign:"center"}}>{cellFor(row.cell(trMeta[pos]||{}, pos))}</td>;
                    })}
                  </tr>;
                })}
              </tbody>
            </table>
          </div>

          {/* ══ User deletion confirmation preview ══ */}
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>User deletion confirmation preview</div>
          <div style={{fontSize:12,color:"#666",marginBottom:10}}>Cascade effects vary by role</div>

          {previewMgr
            ? <div style={{background:"#FCEBEB",border:"0.5px solid rgba(163,45,45,0.3)",borderRadius:12,padding:"14px 16px",maxWidth:540}}>
                <div style={{fontSize:14,fontWeight:500,color:"#A32D2D",marginBottom:10}}>Delete {previewMgr.name}?</div>
                <div style={{fontSize:13,color:"#A32D2D",lineHeight:1.7,marginBottom:12}}>
                  This user is a <b>{(trMeta[previewPos]||{}).label||previewPos}</b>. Affected items:<br/>
                  • {previewTLs.length} leader{previewTLs.length===1?"":"s"} under them → flagged for new assignment<br/>
                  • {previewSales.length} sales in those teams → hierarchy chain broken<br/>
                  • 0 active leads ({(trMeta[previewPos]||{}).inRotation ? "rotation stops on deletion" : "this position does not receive rotation"})<br/>
                  • Historical Deals approvals → preserved as "Ex-{previewMgr.name}"
                </div>
                <div style={{fontSize:12,color:"#A32D2D",opacity:0.85,marginBottom:14,lineHeight:1.5}}>You must assign the orphaned leaders to another manager before the hierarchy is complete again.</div>
                <div style={{display:"flex",gap:8}}>
                  <button type="button" disabled style={{fontSize:12,padding:"6px 12px",border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",borderRadius:8,cursor:"not-allowed",color:"#1a1a1a",fontFamily:"inherit",opacity:0.7}}>Cancel</button>
                  <button type="button" disabled style={{fontSize:12,padding:"6px 12px",border:"0.5px solid #A32D2D",background:"#A32D2D",borderRadius:8,cursor:"not-allowed",color:"#fff",fontWeight:500,fontFamily:"inherit",opacity:0.8}} title="Preview only — actual deletion lives in Users page">Delete &amp; Flag Orphans</button>
                </div>
              </div>
            : <div style={{background:"#F7F7F5",border:"0.5px dashed rgba(0,0,0,0.1)",borderRadius:8,padding:"14px 16px",fontSize:12,color:"#666",maxWidth:540}}>No sales leaders in the roster yet — add one in the Users page to preview the cascade.</div>
          }
        </div>;
      })()}
      {activeTab==="integrations"&&(function(){
        var integrations = [
          {id:"gs", name:"Google Sheets",     sub:"Lead intake via Apps Script",        tile:"GS", tBg:"#EAF6F0", tFg:"#0F6E56", status:"connected"},
          {id:"fb", name:"Facebook Lead Ads", sub:"via Make.com scenario",              tile:"FB", tBg:"#E6F1FB", tFg:"#185FA5", status:"connected"},
          {id:"wa", name:"WhatsApp API",      sub:"Outbound callbacks & reminders",     tile:"WA", tBg:"#E1F5EE", tFg:"#0F6E56", status:"disconnected"}
        ];
        var pill = function(status){
          if(status==="connected")    return {label:"Connected",    bg:"#EAF6F0", fg:"#0F6E56"};
          if(status==="disconnected") return {label:"Disconnected", bg:"#EEEEEA", fg:"#666"};
          return {label:"Error",                                    bg:"#FCEBEB", fg:"#A32D2D"};
        };
        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>External integrations</div>
          <div style={{fontSize:12,color:"#666",marginBottom:12}}>Third-party services that feed leads into the CRM or push notifications out.</div>
          <div style={{display:"grid",gap:10}}>
            {integrations.map(function(it){
              var pl = pill(it.status);
              return <div key={it.id} style={{background:"#F7F7F5",borderRadius:8,padding:14,display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:36,height:36,background:it.tBg,color:it.tFg,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:500,fontSize:11,flexShrink:0}}>{it.tile}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500}}>{it.name}</div>
                  <div style={{fontSize:12,color:"#666"}}>{it.sub}</div>
                </div>
                <span style={{fontSize:11,background:pl.bg,color:pl.fg,padding:"3px 8px",borderRadius:10,fontWeight:500,flexShrink:0}}>{pl.label}</span>
                <button type="button" disabled title="Integration settings ship later"
                  style={{fontSize:12,padding:"6px 12px",border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",borderRadius:8,cursor:"not-allowed",color:"#1a1a1a",fontFamily:"inherit",opacity:0.6,flexShrink:0}}>Configure</button>
              </div>;
            })}
          </div>
        </div>;
      })()}
      {activeTab==="rules"&&(function(){
        // Switch pill — green ON, neutral gray OFF. Grayed + locked cursor when disabled.
        var switchPill = function(on, onClick, disabled){
          return <div
            onClick={disabled ? undefined : onClick}
            title={disabled ? "Locked — cannot be disabled" : "Toggle"}
            style={{width:36,height:20,borderRadius:10,position:"relative",
              cursor: disabled ? "not-allowed" : "pointer",
              flexShrink:0,
              background: on ? (disabled ? "#A0A0A0" : "#0F6E56") : "#CBD5E1",
              opacity: disabled ? 0.7 : 1,
              transition: "background 0.15s"}}>
            <span style={{display:"block",width:14,height:14,background:"#fff",borderRadius:"50%",position:"absolute",top:3,left: on ? 19 : 3,transition:"left 0.15s"}}/>
          </div>;
        };

        // Rule row: label + optional description + switch on the right. Optional locked + redTone.
        var ruleRow = function(opts){
          var locked  = !!opts.locked;
          var redTone = !!opts.redTone;
          return <div style={{
            display:"flex", alignItems:"center", gap:12, padding:"12px 14px",
            background: redTone && locked ? "rgba(252,235,235,0.6)" : (locked ? "#F7F7F5" : "#fff"),
            border: locked
              ? "0.5px dashed " + (redTone ? "rgba(163,45,45,0.4)" : "rgba(0,0,0,0.1)")
              : "0.5px solid rgba(0,0,0,0.08)",
            borderRadius:8, marginBottom:6
          }}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:500,color:redTone?"#A32D2D":"#1a1a1a",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span>{opts.label}</span>
                {locked && <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,
                  background: redTone ? "#FCEBEB" : "#EEEEEA",
                  color: redTone ? "#A32D2D" : "#666",fontWeight:500}}>LOCKED</span>}
              </div>
              {opts.desc && <div style={{fontSize:11,color:redTone?"#A32D2D":"#666",marginTop:2,lineHeight:1.5,opacity:redTone?0.9:1}}>{opts.desc}</div>}
              {opts.inline && <div style={{marginTop:6}}>{opts.inline}</div>}
            </div>
            {switchPill(opts.on, opts.onClick, locked)}
          </div>;
        };

        var sectionHdr = function(text, redTone){return <div style={{fontSize:12,color:redTone?"#A32D2D":"#666",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.3px",fontWeight:500}}>{text}</div>;};

        var inlineNum = function(val,setter,min,max,suffix){return <span style={{fontSize:11,color:"#666",display:"inline-flex",alignItems:"center",gap:6}}>
          <input type="number" min={min} max={max} value={val} onChange={function(e){setter(Number(e.target.value)||0);}}
            style={{width:56,padding:"4px 8px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:6,fontSize:12,background:"#fff",textAlign:"center",fontFamily:"inherit"}}/>
          <span>{suffix}</span>
        </span>;};

        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Business rules</div>
          <div style={{fontSize:12,color:"#666",marginBottom:10}}>Core business logic — locked rules cannot be disabled</div>

          <div style={{background:"#E6F1FB",borderLeft:"3px solid #185FA5",padding:"10px 14px",borderRadius:8,fontSize:12,color:"#185FA5",marginBottom:16,lineHeight:1.5}}>
            These rules govern lead flow, permissions, and rotation exclusions. Locked rules are system-critical.
          </div>

          {/* ── Rotation exclusions ── */}
          <div style={{marginBottom:20}}>
            {sectionHdr("Rotation exclusions")}
            {ruleRow({label:"Exclude Done Deals", desc:"Leads marked DoneDeal never enter rotation — they stay locked with the closing agent.", on:true, locked:true})}
            {ruleRow({label:"Exclude EOIs",       desc:"Leads with an active EOI stay with the current agent until approved or cancelled.",      on:true, locked:true})}
            {ruleRow({label:"Stop rotation after N days in CRM", desc:"After this age a lead stops rotating and stays with its current agent. Synced with the Rotation tab.", on:rotStopDays>0, onClick:function(){setRotStopDays(rotStopDays>0?0:45);},
              inline: rotStopDays>0 ? inlineNum(rotStopDays,setRotStopDays,1,3650,"days") : null})}
          </div>

          {/* ── Cancel behavior ── */}
          <div style={{marginBottom:20}}>
            {sectionHdr("Cancel behavior")}
            {ruleRow({label:"Admin + Operations only", desc:"Only these roles can cancel a Deal or an EOI. Others see view-only within their scope.", on:true, locked:true})}
          </div>

          {/* ── Rotation limits ── */}
          <div style={{marginBottom:20}}>
            {sectionHdr("Rotation limits")}
            {ruleRow({label:"Halt after N× consecutive Not Interested",
              desc:"Stops rotation on a lead once this threshold hits. Synced with the Rotation tab.",
              on:srHaltNI>0, onClick:function(){setSrHaltNI(srHaltNI>0?0:3);},
              inline: srHaltNI>0 ? inlineNum(srHaltNI,setSrHaltNI,1,20,"× Not Interested") : null})}
            {ruleRow({label:"Max rotations per lead",
              desc:"Caps the total number of auto-rotation events a single lead can trigger. 0 = no cap. Manual admin reassigns are not gated.",
              on:maxRotationsPerLead>0,
              onClick:function(){setMaxRotationsPerLead(maxRotationsPerLead>0 ? 0 : 10);},
              inline: maxRotationsPerLead>0
                ? inlineNum(maxRotationsPerLead, setMaxRotationsPerLead, 1, 100, "rotations")
                : null})}
            {ruleRow({label:"Skip agents offline longer than N hours",
              desc:"Agents whose last heartbeat is older than this are skipped in rotation. Synced with the Rotation tab.",
              on:srOffH>0, onClick:function(){setSrOffH(srOffH>0?0:4);},
              inline: srOffH>0 ? inlineNum(srOffH,setSrOffH,1,168,"hours") : null})}
            {ruleRow({label:"Halt rotation when all agents have handled the lead",
              desc:"When every agent in every tier has already handled the lead, rotation halts and admin is notified.",
              on:srHaltAll, onClick:function(){setSrHaltAll(!srHaltAll);}})}
          </div>

          {/* ── Workflow rules ── */}
          <div style={{marginBottom:20}}>
            {sectionHdr("Workflow rules")}
            {ruleRow({label:"Admin approves EOI and Deal",
              desc:"Every EOI and every Deal requires Admin or Operations approval before it activates.",
              on:true, locked:true})}
          </div>

          {/* ── User deletion cascade — locked, red-toned card ── */}
          <div style={{marginBottom:20,padding:"12px 14px",background:"#FCEBEB",border:"0.5px solid rgba(163,45,45,0.3)",borderRadius:12}}>
            {sectionHdr("User deletion cascade", true)}
            {ruleRow({label:"Active Leads & Daily Requests become 'No Agent'",
              desc:"Ownership clears on delete — admin must manually reassign.",
              on:true, locked:true, redTone:true})}
            {ruleRow({label:"Historical Deals and EOIs preserve original agent as 'Ex-{name}'",
              desc:"Closed deals and EOIs keep their original agent label for commission and reporting.",
              on:true, locked:true, redTone:true})}
            {ruleRow({label:"Cascade orphan flags for deleted TLs / Managers / Directors",
              desc:"Sales under a deleted TL get teamLeaderId=null; same for managerId / directorId.",
              on:true, locked:true, redTone:true})}
            {ruleRow({label:"Confirmation dialog required",
              desc:"Admin sees a counts-of-affected-items preview before the delete goes through.",
              on:true, locked:true, redTone:true})}
          </div>

          <div style={{background:"#FCEBEB",borderLeft:"3px solid #A32D2D",padding:"10px 14px",borderRadius:8,fontSize:12,color:"#A32D2D",lineHeight:1.5}}>
            Locked rules protect critical data integrity. They cannot be disabled from the UI.
          </div>
        </div>;
      })()}
      {activeTab==="commissions"&&(function(){
        var rateInput = { width:80, padding:"6px 8px", borderRadius:7, border:"1px solid #E2E8F0", fontSize:13, textAlign:"center" };
        var setSales = function(key, val){
          setCommRates(function(prev){
            var nxt = Object.assign({}, prev);
            nxt.sales = Object.assign({}, prev.sales || {}, (function(){ var o = {}; o[key] = Number(val) || 0; return o; })());
            return nxt;
          });
        };
        var setTop = function(key, val){
          setCommRates(function(prev){
            var nxt = Object.assign({}, prev);
            nxt[key] = Number(val) || 0;
            return nxt;
          });
        };
        return <div style={{ padding:"6px 4px", fontSize:13 }}>
          <div style={{ fontWeight:500, fontSize:14, color:"#1a1a1a", marginBottom:4 }}>Per-1000 commission rates</div>
          <div style={{ color:"#666", fontSize:12, marginBottom:14 }}>
            Rates are applied to <code>dealTotal × projectWeight × splitMultiplier</code>. Sales rate depends on the agent's quarter achievement bucket
            (&lt;2× = base, 2-3× = target met, ≥3× = double target). Changes apply to new closes automatically; existing commissions keep their stored
            shares until they next recompute (via a new sibling close, cancel, or admin script).
          </div>

          <div style={{ background:"#F7F7F5", borderRadius:10, padding:"14px 16px", marginBottom:14, border:"1px solid #E8ECF1" }}>
            <div style={{ fontWeight:600, fontSize:12, color:"#1a1a1a", marginBottom:10, textTransform:"uppercase" }}>Sales agent (bucketed)</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:14 }}>
              <label style={{ display:"flex", flexDirection:"column", gap:4, fontSize:12, color:"#444" }}>
                Base (&lt;2× target)
                <input type="number" min={0} step="0.1" value={commRates.sales && commRates.sales.base != null ? commRates.sales.base : 5}
                  onChange={function(e){ setSales("base", e.target.value); }} style={rateInput}/>
              </label>
              <label style={{ display:"flex", flexDirection:"column", gap:4, fontSize:12, color:"#444" }}>
                Target met (2× — 3×)
                <input type="number" min={0} step="0.1" value={commRates.sales && commRates.sales.target_met != null ? commRates.sales.target_met : 6}
                  onChange={function(e){ setSales("target_met", e.target.value); }} style={rateInput}/>
              </label>
              <label style={{ display:"flex", flexDirection:"column", gap:4, fontSize:12, color:"#444" }}>
                Double target (≥3×)
                <input type="number" min={0} step="0.1" value={commRates.sales && commRates.sales.double_target != null ? commRates.sales.double_target : 7}
                  onChange={function(e){ setSales("double_target", e.target.value); }} style={rateInput}/>
              </label>
            </div>
          </div>

          <div style={{ background:"#F7F7F5", borderRadius:10, padding:"14px 16px", marginBottom:14, border:"1px solid #E8ECF1" }}>
            <div style={{ fontWeight:600, fontSize:12, color:"#1a1a1a", marginBottom:10, textTransform:"uppercase" }}>Chain (fixed)</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:14 }}>
              <label style={{ display:"flex", flexDirection:"column", gap:4, fontSize:12, color:"#444" }}>
                Team leader
                <input type="number" min={0} step="0.1" value={commRates.team_leader != null ? commRates.team_leader : 2}
                  onChange={function(e){ setTop("team_leader", e.target.value); }} style={rateInput}/>
              </label>
              <label style={{ display:"flex", flexDirection:"column", gap:4, fontSize:12, color:"#444" }}>
                Manager
                <input type="number" min={0} step="0.1" value={commRates.manager != null ? commRates.manager : 1}
                  onChange={function(e){ setTop("manager", e.target.value); }} style={rateInput}/>
              </label>
              <label style={{ display:"flex", flexDirection:"column", gap:4, fontSize:12, color:"#444" }}>
                Director
                <input type="number" min={0} step="0.1" value={commRates.director != null ? commRates.director : 0.5}
                  onChange={function(e){ setTop("director", e.target.value); }} style={rateInput}/>
              </label>
            </div>
          </div>

          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <button type="button" onClick={saveCommRates} disabled={commRatesSaving || !commRatesLoaded}
              style={{ fontSize:12, padding:"6px 14px", border:"0.5px solid rgba(24,95,165,0.3)", background:"#E6F1FB", color:"#185FA5", borderRadius:8, cursor: commRatesSaving ? "wait" : "pointer", fontWeight:500 }}>
              {commRatesSaving ? "Saving…" : "Save rates"}
            </button>
            {commRatesSavedFlag && <span style={{ fontSize:12, color:"#0F6E56" }}>✓ Saved (applies to new closes; run admin script to backfill existing)</span>}
          </div>
        </div>;
      })()}
      {activeTab==="campaigns"&&(function(){
        var money = function(n){ try { return Math.round(Number(n||0)).toLocaleString("en-US"); } catch(_){ return String(n); } };
        var monthLabel = function(m){
          if (!m) return "—";
          var parts = String(m).split("-");
          var d = new Date(Number(parts[0]), Number(parts[1])-1, 1);
          try { return d.toLocaleDateString("en-GB", { month:"long", year:"numeric" }); } catch(_){ return m; }
        };
        var shortMonth = function(iso){
          if (!iso) return "—";
          try { return new Date(iso).toLocaleDateString("en-GB", { day:"2-digit", month:"short" }); } catch(_){ return "—"; }
        };
        var months = (campScreen && campScreen.months) || [];
        var mIdx = months.indexOf(campMonth);
        // Server-supplied vocabulary — see campPlatform.
        var platforms = (campScreen && campScreen.platforms) || [];
        var platformLabel = function(k){
          for (var i=0;i<platforms.length;i++) if (platforms[i].key===k) return platforms[i].label;
          return k || "—";
        };
        var totals = (campScreen && campScreen.totals) || {};
        var visible = campRows.filter(function(r){ return campShowIgnored ? true : !r.ignored; });
        var selRows = campSelectedRows();
        var dirty = campDirtyCount();

        var thStyle = { textAlign:"left", padding:"9px 12px", fontSize:10.5, fontWeight:700, color:"#94A3B8",
                        textTransform:"uppercase", letterSpacing:"0.05em", borderBottom:"1px solid #E8ECF1",
                        background:"#F8FAFC", whiteSpace:"nowrap" };
        var thNum = Object.assign({}, thStyle, { textAlign:"right" });
        var tdStyle = { padding:"9px 12px", fontSize:12.5, color:"#1a1a1a", verticalAlign:"top" };
        var tdNum = Object.assign({}, tdStyle, { textAlign:"right", fontFamily:"ui-monospace, Menlo, monospace",
                                                 fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" });
        var chip = { display:"inline-block", fontFamily:"ui-monospace, Menlo, monospace", fontSize:10,
                     padding:"1px 6px", borderRadius:4, background:"#F1F5F9", border:"1px solid #E8ECF1",
                     color:"#64748B", marginRight:4, marginTop:3, whiteSpace:"nowrap" };
        var chipGroup = Object.assign({}, chip, { background:"#E6F1FB", borderColor:"transparent", color:"#185FA5" });
        var chipSuggest = Object.assign({}, chip, { background:"#FFF7ED", borderColor:"transparent", color:"#B45309" });
        var btn = { fontSize:12, padding:"6px 12px", borderRadius:8, border:"0.5px solid rgba(0,0,0,0.12)",
                    background:"#fff", color:"#334155", cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" };
        var btnPri = Object.assign({}, btn, { background:"#E6F1FB", border:"0.5px solid rgba(24,95,165,0.3)",
                                              color:"#185FA5", fontWeight:600 });

        return <div>
          {/* header: what this is, and which month is being priced */}
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, marginBottom:14, flexWrap:"wrap"}}>
            <div>
              <div style={{fontSize:14, fontWeight:500, marginBottom:4}}>Campaign cost tracking</div>
              <div style={{fontSize:12, color:"#666", maxWidth:560, lineHeight:1.5}}>
                Every campaign name found in your leads is listed below. Enter what you spent on each for the
                selected month <strong>and platform</strong>, in EGP. One campaign can run on several platforms —
                switch the platform to enter the rest; cost per lead always uses the month&rsquo;s total.
                Names are never typed — merge the spellings that mean the same campaign.
              </div>
            </div>
            <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
              <div style={{display:"inline-flex", alignItems:"center", gap:8, border:"1px solid #E2E8F0",
                           borderRadius:8, padding:"4px 8px", background:"#fff"}}>
                <button type="button" disabled={mIdx<=0||campBusy}
                  onClick={function(){ campSwitchMonth(months[mIdx-1]); }}
                  title={mIdx>0 ? monthLabel(months[mIdx-1]) : "No earlier month has campaign data"}
                  style={{border:"none", background:"none", cursor: mIdx>0?"pointer":"default", color: mIdx>0?"#185FA5":"#CBD5E1", fontSize:15, padding:"0 4px", fontFamily:"inherit"}}>‹</button>
                <span style={{fontFamily:"ui-monospace, Menlo, monospace", fontSize:12.5, fontWeight:600, minWidth:104, textAlign:"center"}}>{monthLabel(campMonth)}</span>
                <button type="button" disabled={mIdx<0||mIdx>=months.length-1||campBusy}
                  onClick={function(){ campSwitchMonth(months[mIdx+1]); }}
                  style={{border:"none", background:"none", cursor: (mIdx>=0&&mIdx<months.length-1)?"pointer":"default", color:(mIdx>=0&&mIdx<months.length-1)?"#185FA5":"#CBD5E1", fontSize:15, padding:"0 4px", fontFamily:"inherit"}}>›</button>
              </div>
              <select value={campPlatform} disabled={campBusy||!platforms.length}
                onChange={function(e){ campSwitchPlatform(e.target.value); }}
                aria-label="Ad platform this spend column belongs to"
                style={{border:"1px solid #E2E8F0", borderRadius:8, padding:"6px 8px", background:"#fff",
                        fontSize:12, fontFamily:"inherit", color:"#1a1a1a", cursor: campBusy?"wait":"pointer"}}>
                {platforms.map(function(pf){ return <option key={pf.key} value={pf.key}>{pf.label}</option>; })}
              </select>
              <button type="button" style={btn} disabled={mIdx<=0||campBusy} onClick={copyPrevMonth}>
                Copy {mIdx>0 ? monthLabel(months[mIdx-1]).split(" ")[0] : "previous"}&rsquo;s spend
              </button>
              <button type="button" style={btn} onClick={function(){ setCampError(""); setAddName(""); setAddOpen(true); }}>
                + Add campaign with no leads yet
              </button>
            </div>
          </div>

          {campError && <div style={{marginBottom:12, padding:"10px 14px", background:"#FCEBEB", color:"#A32D2D", borderRadius:10, fontSize:12.5}}>{campError}</div>}

          {/* selection bar — merging is the reason this screen has checkboxes */}
          {selRows.length>0 && <div style={{display:"flex", gap:10, alignItems:"center", flexWrap:"wrap",
                padding:"9px 14px", background:"#E6F1FB", borderRadius:10, marginBottom:12, fontSize:12.5, color:"#12497E"}}>
            <span><b>{selRows.length} selected</b>{" — "}
              {selRows.slice(0,3).map(function(r){ return r.label; }).join(", ")}{selRows.length>3?", …":""}</span>
            <span style={{flex:1}}/>
            <button type="button" style={btnPri} disabled={campBusy} onClick={openMerge}>Merge into group…</button>
            <button type="button" style={btn} disabled={campBusy}
              onClick={function(){ markNotCampaign(selRows, true); }}>Mark as not a campaign</button>
            <button type="button" style={Object.assign({},btn,{background:"transparent",border:"none",color:"#64748B"})}
              onClick={function(){ setCampSel({}); }}>Clear</button>
          </div>}

          {!campaignsLoaded ? <div style={{padding:30, textAlign:"center", color:"#999", fontSize:12}}>Loading…</div>
            : visible.length===0 ? <div style={{padding:"40px 20px", textAlign:"center", color:"#666", border:"1px dashed #E2E8F0", borderRadius:10}}>
                <div style={{fontSize:36, marginBottom:8}}>📣</div>
                <div style={{fontSize:14, fontWeight:500, color:"#1a1a1a", marginBottom:4}}>No campaign names in your leads yet</div>
                <div style={{fontSize:12}}>Campaign names arrive with the leads. Use &ldquo;Add campaign with no leads yet&rdquo; to budget ahead of a launch.</div>
              </div>
            : <div style={{overflowX:"auto", border:"0.5px solid rgba(0,0,0,0.1)", borderRadius:10}}>
                <table style={{width:"100%", borderCollapse:"collapse"}}>
                  <thead>
                    <tr>
                      <th style={Object.assign({},thStyle,{width:34})}/>
                      <th style={thStyle}>Campaign</th>
                      <th style={thNum}>Leads<br/>{monthLabel(campMonth).split(" ")[0]}</th>
                      <th style={thNum}>Leads<br/>all time</th>
                      <th style={thStyle}>Top source</th>
                      <th style={thNum}>Last lead</th>
                      <th style={thNum}>Spend — {monthLabel(campMonth)}<br/>{platformLabel(campPlatform)}</th>
                      <th style={thNum}>Month total<br/>all platforms</th>
                      <th style={thNum}>Cost / lead</th>
                      <th style={Object.assign({},thStyle,{width:40})}/>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(function(r){
                      var selected = !!campSel[r.id];
                      var typed = campSpendValue(r);
                      var isDirty = Object.prototype.hasOwnProperty.call(campEdits, r.id) &&
                                    String(campEdits[r.id]).trim() !== campCellValue(r);
                      // Spend already booked against this month on the OTHER platforms. The
                      // preview has to add the typed figure to it, not replace the total with
                      // it, or a second platform's entry reads as a collapse in cost per lead.
                      var otherPlatforms = (r.spend || 0) - (r.spendForPlatform || 0);
                      var liveCPL = (function(){
                        var v = String(typed).trim();
                        if (!r.leadsInMonth) return null;
                        var n = v === "" ? 0 : Number(v);
                        if (!isFinite(n)) return null;
                        var tot = otherPlatforms + n;
                        return tot > 0 ? Math.round(tot / r.leadsInMonth) : null;
                      })();
                      return <tr key={r.id} style={{borderTop:"0.5px solid rgba(0,0,0,0.06)",
                                                    background: selected ? "#F2F8FE" : (r.ignored ? "#FCFCFD" : "#fff")}}>
                        <td style={tdStyle}>
                          <input type="checkbox" checked={selected} onChange={function(){ campToggleSel(r.id); }}
                            style={{cursor:"pointer"}} aria-label={"Select "+r.label}/>
                        </td>
                        <td style={tdStyle}>
                          <div style={{fontWeight:500, color: r.ignored ? "#94A3B8" : "#1a1a1a"}}>
                            {r.label}
                            {r.isGroup && <span style={chipGroup}>group · {(r.members||[]).length} name{(r.members||[]).length===1?"":"s"}</span>}
                            {r.ignored && <span style={chip}>not a campaign</span>}
                          </div>
                          {/* the spellings this row actually matches — shown so a merge is verifiable */}
                          {(r.members||[]).length>1 && <div>
                            {(r.members||[]).slice(0,6).map(function(m){ return <span key={m} style={chip}>{m}</span>; })}
                            {(r.members||[]).length>6 && <span style={chip}>+{(r.members||[]).length-6}</span>}
                          </div>}
                          {!r.isGroup && r.suggestMergeWith && r.suggestMergeWith.length>0 &&
                            <div><span style={chipSuggest} title="Suggested only — nothing is merged until you say so">
                              suggest: merge {r.suggestMergeWith.length} more
                            </span></div>}
                        </td>
                        <td style={tdNum}>{r.leadsInMonth}</td>
                        <td style={Object.assign({},tdNum,{color:"#64748B"})}>{r.leadsAllTime}</td>
                        <td style={Object.assign({},tdStyle,{fontSize:12, color:"#64748B", whiteSpace:"nowrap"})}>
                          {r.topSource || "—"}{r.topSourceShare!=null && <span style={chip}>{r.topSourceShare}%</span>}
                        </td>
                        <td style={Object.assign({},tdNum,{color:"#64748B"})}>{shortMonth(r.lastLeadAt)}</td>
                        <td style={tdNum}>
                          {r.ignored ? <span style={{color:"#CBD5E1"}}>hidden</span> :
                            <input value={typed} inputMode="numeric"
                              onChange={function(e){ campSetEdit(r.id, e.target.value.replace(/[^0-9.]/g,"")); }}
                              placeholder="—"
                              style={{width:104, textAlign:"right", padding:"5px 8px", borderRadius:6,
                                      border: isDirty ? "1px solid #185FA5" : "1px solid #E2E8F0",
                                      boxShadow: isDirty ? "0 0 0 3px #E6F1FB" : "none",
                                      fontFamily:"ui-monospace, Menlo, monospace", fontSize:12,
                                      fontVariantNumeric:"tabular-nums", outline:"none", background:"#fff"}}/>}
                        </td>
                        <td style={Object.assign({},tdNum,{color:"#64748B"})}>
                          {r.ignored ? <span style={{color:"#CBD5E1"}}>—</span> :
                            r.spend == null ? "—" : <span title={Object.keys(r.spendByPlatform||{}).map(function(k){
                              return platformLabel(k) + ": " + money(r.spendByPlatform[k]); }).join("\n")}>
                              {money(r.spend)}
                              {Object.keys(r.spendByPlatform||{}).length>1 &&
                                <span style={chip}>{Object.keys(r.spendByPlatform).length} platforms</span>}
                            </span>}
                        </td>
                        <td style={Object.assign({},tdNum,{color: liveCPL==null?"#CBD5E1":"#0F6E56"})}>
                          {liveCPL==null ? "—" : money(liveCPL)}
                        </td>
                        <td style={Object.assign({},tdStyle,{textAlign:"right", position:"relative"})}>
                          <button type="button" onClick={function(){ setCampMenuFor(campMenuFor===r.id?null:r.id); }}
                            style={{border:"none", background:"none", cursor:"pointer", color:"#94A3B8", fontSize:15, padding:"0 4px", fontFamily:"inherit"}}>⋯</button>
                          {campMenuFor===r.id && <div style={{position:"absolute", right:8, top:30, zIndex:20,
                                background:"#fff", border:"1px solid #E2E8F0", borderRadius:10, boxShadow:"0 10px 30px -12px rgba(15,23,42,0.35)",
                                minWidth:190, padding:4, textAlign:"left"}}>
                            {r.isGroup && <button type="button" onClick={function(){ renameGroup(r); }}
                              style={{display:"block", width:"100%", textAlign:"left", border:"none", background:"none", padding:"7px 10px", fontSize:12, cursor:"pointer", color:"#334155", fontFamily:"inherit"}}>Rename group</button>}
                            {r.isGroup && <button type="button" onClick={function(){ ungroup(r); }}
                              style={{display:"block", width:"100%", textAlign:"left", border:"none", background:"none", padding:"7px 10px", fontSize:12, cursor:"pointer", color:"#334155", fontFamily:"inherit"}}>Ungroup</button>}
                            <button type="button" onClick={function(){ markNotCampaign([r], !r.ignored); }}
                              style={{display:"block", width:"100%", textAlign:"left", border:"none", background:"none", padding:"7px 10px", fontSize:12, cursor:"pointer", color:"#334155", fontFamily:"inherit"}}>
                              {r.ignored ? "Treat as a campaign again" : "Mark as not a campaign"}
                            </button>
                          </div>}
                        </td>
                      </tr>;
                    })}
                  </tbody>
                </table>
              </div>
          }

          {/* footer: how much of the month is done, and the only Save button */}
          {campaignsLoaded && <div style={{display:"flex", justifyContent:"space-between", alignItems:"center",
                gap:12, flexWrap:"wrap", marginTop:12, fontSize:12, color:"#64748B"}}>
            <span>
              <b style={{color:"#1a1a1a"}}>{totals.priced||0} of {totals.priceable||0}</b> campaigns priced for {monthLabel(campMonth)}
              {" · total "}<b style={{color:"#1a1a1a"}}>{money(totals.totalSpend)} EGP</b>
              {(totals.ignored||0)>0 && <span>{" · "}{totals.ignored} not a campaign</span>}
            </span>
            <span style={{display:"flex", gap:8, alignItems:"center"}}>
              {(totals.ignored||0)>0 && <button type="button" style={btn}
                onClick={function(){ setCampShowIgnored(!campShowIgnored); }}>
                {campShowIgnored ? "Hide" : "Show"} ignored ({totals.ignored})</button>}
              <button type="button" style={Object.assign({}, btnPri, { opacity: (dirty&&!campBusy)?1:0.55, cursor:(dirty&&!campBusy)?"pointer":"default" })}
                disabled={!dirty||campBusy} onClick={saveMonth}>
                {campBusy ? "Saving…" : dirty ? "Save "+monthLabel(campMonth).split(" ")[0]+" ("+dirty+")" : "Saved"}
              </button>
            </span>
          </div>}

          {/* merge dialog */}
          <Modal show={mergeOpen} onClose={function(){ setMergeOpen(false); }} title={"Merge "+selRows.length+" campaign name"+(selRows.length===1?"":"s")+" into one group"}>
            {campError && <div style={{marginBottom:14, padding:"10px 14px", background:"#FCEBEB", color:"#A32D2D", borderRadius:10, fontSize:13}}>{campError}</div>}
            <div style={{marginBottom:13}}>
              <label style={{display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5}}>Group name<span style={{color:C.danger}}> *</span></label>
              <input type="text" value={mergeName} onChange={function(e){ setMergeName(e.target.value); }}
                placeholder="e.g. درّة سراي"
                style={{width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, outline:"none", boxSizing:"border-box"}}/>
            </div>
            <div style={{marginBottom:13}}>
              <label style={{display:"block", fontSize:11, fontWeight:600, letterSpacing:"0.05em", textTransform:"uppercase", color:"#64748B", marginBottom:6}}>Raw names this group will match</label>
              <div>
                {selRows.map(function(r){
                  return <span key={r.id} style={Object.assign({},chipGroup,{fontSize:11})}>{r.label} · {r.leadsAllTime} leads</span>;
                })}
              </div>
              {mergeSuggestions().length>0 && <div style={{marginTop:10}}>
                <div style={{fontSize:11, color:"#B45309", marginBottom:4}}>Also suggested — tick to include:</div>
                {mergeSuggestions().map(function(r){
                  return <label key={r.id} style={{display:"inline-flex", alignItems:"center", gap:5, marginRight:10, marginTop:4, fontSize:12, cursor:"pointer"}}>
                    <input type="checkbox" checked={!!mergeExtra[r.id]}
                      onChange={function(){ setMergeExtra(function(prev){ var n=Object.assign({},prev); if(n[r.id]) delete n[r.id]; else n[r.id]=true; return n; }); }}/>
                    <span style={Object.assign({},chipSuggest,{margin:0})}>{r.label} · {r.leadsAllTime}</span>
                  </label>;
                })}
              </div>}
            </div>
            <div style={{background:"#F8FAFC", borderRadius:8, padding:"11px 13px", fontSize:12, color:"#475569", borderLeft:"2px solid #185FA5", marginBottom:16, lineHeight:1.6}}>
              Matches <b>{(function(){ var n=0; selRows.concat(mergeSuggestions().filter(function(r){return mergeExtra[r.id];})).forEach(function(r){ n += (r.members||[]).length; }); return n; })()} spelling(s)</b>
              {" · "}<b>{(function(){ var n=0; selRows.concat(mergeSuggestions().filter(function(r){return mergeExtra[r.id];})).forEach(function(r){ n += r.leadsAllTime; }); return n; })()} leads</b> all time.<br/>
              New leads arriving with any of these names count into this group automatically.<br/>
              Spend is entered once per month for the group. <b>Lead records are not modified.</b>
            </div>
            <div style={{display:"flex", gap:10}}>
              <Btn outline onClick={function(){ setMergeOpen(false); }} style={{flex:1}}>{t.cancel}</Btn>
              <Btn loading={campBusy} onClick={submitMerge} style={{flex:1}}>Create group</Btn>
            </div>
          </Modal>

          {/* manual add — the fallback for a campaign with no leads yet */}
          <Modal show={addOpen} onClose={function(){ setAddOpen(false); }} title="Add a campaign with no leads yet">
            {campError && <div style={{marginBottom:14, padding:"10px 14px", background:"#FCEBEB", color:"#A32D2D", borderRadius:10, fontSize:13}}>{campError}</div>}
            <div style={{marginBottom:13, fontSize:12.5, color:"#64748B", lineHeight:1.6}}>
              Only needed to budget <b>before a launch</b>. Once leads arrive carrying a campaign name, that
              name appears in the list on its own — then merge it into this one.
            </div>
            <div style={{marginBottom:16}}>
              <label style={{display:"block", fontSize:13, fontWeight:600, color:C.text, marginBottom:5}}>Campaign name<span style={{color:C.danger}}> *</span></label>
              <input type="text" value={addName} onChange={function(e){ setAddName(e.target.value); }}
                placeholder="e.g. Autumn Launch"
                style={{width:"100%", padding:"9px 12px", borderRadius:10, border:"1px solid #E2E8F0", fontSize:14, outline:"none", boxSizing:"border-box"}}/>
            </div>
            <div style={{display:"flex", gap:10}}>
              <Btn outline onClick={function(){ setAddOpen(false); }} style={{flex:1}}>{t.cancel}</Btn>
              <Btn loading={campBusy} onClick={submitAddCampaign} style={{flex:1}}>Add campaign</Btn>
            </div>
          </Modal>
        </div>;
      })()}
      {activeTab==="audit"&&(function(){
        // Apply UI-level filters to the entries array (when backend ships, these run client-side).
        var nowMs = Date.now();
        var withinDate = function(ts){
          if(!ts || auditDate==="all") return true;
          var diff = nowMs - new Date(ts).getTime();
          if(auditDate==="today") return diff < 24*60*60*1000;
          if(auditDate==="week")  return diff < 7*24*60*60*1000;
          if(auditDate==="month") return diff < 30*24*60*60*1000;
          return true;
        };
        var filtered = auditEntries.filter(function(e){
          if(!withinDate(e.timestamp||e.createdAt)) return false;
          if(auditAdmin!=="all" && String(e.actorId||"") !== auditAdmin) return false;
          if(auditField!=="all" && e.field !== auditField) return false;
          return true;
        });

        // Distinct admin + field lists for the filter dropdowns
        var admins = {};
        auditEntries.forEach(function(e){
          var id   = String(e.actorId||"");
          var name = e.actorName || userNameOf(id, p.users) || "Unknown admin";
          if(id) admins[id] = name;
        });
        var fields = Array.from(new Set(auditEntries.map(function(e){return e.field;}).filter(Boolean))).sort();


        // Pretty-format a value for display (truncate long values, stringify objects).
        var fmt = function(v){
          if(v==null) return "∅";
          if(typeof v==="boolean") return v ? "on" : "off";
          if(typeof v==="object") { try { v = JSON.stringify(v); } catch(_){ v = "[object]"; } }
          v = String(v);
          return v.length>60 ? v.slice(0,60)+"…" : v;
        };

        var formatTs = function(ts){
          if(!ts) return "";
          var d = new Date(ts);
          var today = new Date(); today.setHours(0,0,0,0);
          var dDay  = new Date(d);  dDay.setHours(0,0,0,0);
          var time = fmtTime12(d);
          if(dDay.getTime()===today.getTime()) return "Today "+time;
          var yesterday = new Date(today); yesterday.setDate(today.getDate()-1);
          if(dDay.getTime()===yesterday.getTime()) return "Yesterday "+time;
          return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short"})+" "+time;
        };

        var doRollback = async function(entry){
          if(!entry || entry.rolledBack) return;
          if(!isOwner) return;   // BE gates rollback to admin; operation never sees the button
          if(!window.confirm("Are you sure? This will revert \""+entry.field+"\" to its previous value and create a counter-entry in the log.")) return;
          try {
            await apiFetch("/api/settings/audit/"+entry._id+"/rollback","POST",null,p.token,p.csrfToken);
            var fresh = await apiFetch("/api/settings/audit","GET",null,p.token);
            if(Array.isArray(fresh)) setAuditEntries(fresh);
          } catch(err) {
            alert("Rollback failed: " + ((err && err.message) || "Unknown error"));
          }
        };

        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Recent settings changes</div>
          <div style={{fontSize:12,color:"#666",marginBottom:12}}>
            Changes to rotation settings, business-rule timers, project weights, commission rates, and campaigns — with who made each one and when.
            {isOwner ? " Click Rollback to revert a single change." : ""}
          </div>

          {/* Filter bar */}
          <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
            <span style={{fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px"}}>Filter</span>
            <Select value={auditDate} onChange={function(e){setAuditDate(e.target.value);}} width={140} maxWidth={140}
              options={[{ value:"all", label:"Any date" },{ value:"today", label:"Today" },{ value:"week", label:"Past 7 days" },{ value:"month", label:"Past 30 days" }]}/>
            <Select value={auditAdmin} onChange={function(e){setAuditAdmin(e.target.value);}} searchable width={150} maxWidth={150}
              options={[{ value:"all", label:"Any admin" }].concat(Object.keys(admins).map(function(id){return { value:id, label:admins[id] };}))}/>
            <Select value={auditField} onChange={function(e){setAuditField(e.target.value);}} searchable width={150} maxWidth={150}
              options={[{ value:"all", label:"Any field" }].concat(fields.map(function(f){return { value:f, label:f };}))}/>
            {(auditDate!=="all"||auditAdmin!=="all"||auditField!=="all") &&
              <button type="button" onClick={function(){setAuditDate("all");setAuditAdmin("all");setAuditField("all");}}
                style={{fontSize:12,padding:"6px 12px",border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",borderRadius:8,cursor:"pointer",color:"#1a1a1a",fontFamily:"inherit"}}>Clear</button>}
          </div>

          {/* Entries */}
          {!auditLoaded
            ? <div style={{background:"#F7F7F5",border:"0.5px dashed rgba(0,0,0,0.1)",borderRadius:8,padding:"16px",textAlign:"center",color:"#666",fontSize:12}}>Loading audit log…</div>
            : filtered.length===0
              ? <div style={{background:"#F7F7F5",border:"0.5px dashed rgba(0,0,0,0.1)",borderRadius:8,padding:"24px 16px",textAlign:"center",color:"#666"}}>
                  <div style={{fontSize:14,fontWeight:500,marginBottom:6,color:"#1a1a1a"}}>
                    {auditEntries.length===0 ? "No settings changes yet" : "No entries match these filters"}
                  </div>
                  <div style={{fontSize:12,lineHeight:1.5}}>
                    {auditEntries.length===0
                      ? "Saving Rotation, Business Rules, Commissions, Commission Projects, or Campaigns records one entry per changed field here. Lead edits, status changes, user/role changes, and logins are not tracked on this tab."
                      : "Try clearing the filters or widening the date range."}
                  </div>
                </div>
              : <div>
                  {filtered.map(function(e,i){
                    var actor = e.actorName || "Admin";
                    var field = e.field || "(unknown field)";
                    var rolled = !!e.rolledBack;
                    return <div key={e._id||i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"0.5px solid rgba(0,0,0,0.1)",fontSize:12}}>
                      <div style={{flex:1,minWidth:0,lineHeight:1.5}}>
                        <b>{actor}</b> changed <span style={{color:"#185FA5",fontFamily:"ui-monospace, Menlo, monospace"}}>{field}</span>
                        {" "}from <span style={{color:"#666"}}>"{fmt(e.oldValue)}"</span>
                        {" "}to <span style={{color:"#0F6E56",fontWeight:500}}>"{fmt(e.newValue)}"</span>
                        {rolled && <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:"#EEEEEA",color:"#666",fontWeight:500,marginLeft:8}}>ROLLED BACK</span>}
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                        <span style={{color:"#666",fontSize:11}}>{formatTs(e.timestamp||e.createdAt)}</span>
                        {isOwner && <button type="button" disabled={rolled}
                          onClick={rolled ? undefined : function(){doRollback(e);}}
                          title={rolled ? "Already rolled back" : "Revert this change"}
                          style={{fontSize:12,padding:"4px 10px",border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",borderRadius:6,cursor: rolled ? "not-allowed" : "pointer",color:"#1a1a1a",fontFamily:"inherit",opacity: rolled ? 0.4 : 0.8}}>
                          Rollback
                        </button>}
                      </div>
                    </div>;
                  })}
                </div>
          }

          {auditEntries.length>0 && <div style={{fontSize:11,color:"#999",marginTop:12,textAlign:"right"}}>
            {/* auditEntries is the SETTINGS-change log (/api/settings/audit), a
                different dataset from the system audit (/api/audit) below — that
                endpoint returns its whole set unpaged, so this denominator is
                already the true total. Do not wire sysAuditTotal in here. */}
            Showing {filtered.length} of {auditEntries.length} {auditEntries.length===1?"entry":"entries"}
          </div>}
        </div>;
      })()}

      {activeTab==="sysAudit" && (function(){
        var rows = (sysAuditType==="all" ? sysAuditAll : sysAuditTyped) || [];
        var loaded = (sysAuditType==="all" ? sysAuditAll : sysAuditTyped) !== null;
        // Options come from the UNFILTERED set so picking a type never removes
        // the option you'd need to get back to.
        var types = Array.from(new Set((sysAuditAll||[]).map(function(r){return r.type;}).filter(Boolean))).sort();

        var nameOf = function(u){ return userNameOf(u, p.users) || null; };
        var fmtDate = function(v){
          if (!v) return "";
          var d = new Date(v);
          if (isNaN(d.getTime())) return String(v);
          return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short"});
        };
        var money = function(v){ return (v==null||v==="") ? "—" : Number(v).toLocaleString(); };

        // Per-type renderer. `type` is an open string on the model, so anything
        // not listed falls through to the generic branch rather than rendering
        // blank — a new AuditLog.create elsewhere in the codebase shows up here
        // automatically, just less prettily.
        var describe = function(r){
          var f = r.flags || {}, b = r.before || {}, a = r.after || {};
          var tgt = nameOf(r.targetUserId);
          switch (r.type) {
            case "LEAD_VIP_CHANGED":
              return { subject: f.leadName || "(lead)", kind:"lead", detail: r.reason || (a.isVIP ? "Marked VIP" : "VIP removed") };
            case "ROTATION_STOP_CLEARED":
              return { subject: f.leadName || "(lead)", kind:"lead", detail: r.reason || "Rotation stop cleared" };
            case "BASE_SALARY_CHANGED":
              return { subject: tgt || "(employee)", kind:"user", detail: "Base salary " + money(b.baseSalary) + " → " + money(a.baseSalary) };
            case "ATTENDANCE_AUTO_CLOSED":
              return { subject: tgt || "(employee)", kind:"user", detail: (f.date ? f.date + " · " : "") + (r.reason || "Auto-closed") };
            case "ATTENDANCE_CHECKOUT_UNDONE":
              return { subject: tgt || "(employee)", kind:"user",
                       detail: (f.date ? f.date + " · " : "") + "check-out "
                         + (f.clearedCheckOutLabel || "—") + " cleared, day reopened"
                         + (f.wasAutoClosed ? " (was auto-closed)" : "") };
            case "ATTENDANCE_RESTORED":
              return { subject: tgt || "(employee)", kind:"user",
                       detail: r.reason || ("Day restored" + (f.action ? " · " + f.action : "")) };
            case "OFFSITE_APPROVED":
              return { subject: tgt || "(employee)", kind:"user", detail: "Off-site " + (a.type || "request") + " approved" };
            case "OFFSITE_REJECTED":
              return { subject: tgt || "(employee)", kind:"user", detail: "Off-site " + (a.type || "request") + " rejected" };
            case "TARGET_PERIOD_REOPENED":
              return { subject: tgt || "(leader)", kind:"user", detail: (f.qKey ? f.qKey + " " : "") + "reopened (" + (b.status||"?") + " → " + (a.status||"?") + ")" };
            case "COMPANY_OFFDAY_ADDED":
              return { subject: a.name || "(off-day)", kind:"company",
                       detail: "Added" + (a.days ? " · " + a.days + " day" + (a.days===1?"":"s") : "") + (a.from ? " · " + fmtDate(a.from) + "–" + fmtDate(a.to) : "") };
            case "COMPANY_OFFDAY_REMOVED":
              return { subject: b.name || "(off-day)", kind:"company",
                       detail: "Removed" + (b.days ? " · " + b.days + " day" + (b.days===1?"":"s") : "") + (b.from ? " · " + fmtDate(b.from) + "–" + fmtDate(b.to) : "") };
            case "PERMISSION_CHANGED":
              return { subject: "Company settings", kind:"company", detail: "Attendance / salary permissions updated" };
            case "OFFICE_LOCATION_CHANGED":
              return { subject: "Company settings", kind:"company", detail: "Office location / geofence updated" };
            case "ROLE_SHIFTS_CHANGED":
              return { subject: "Company settings", kind:"company", detail: "Work shift rules updated" };
            default:
              // Unknown//future type: best-effort subject, and the reason if the
              // writer supplied one.
              return { subject: tgt || f.leadName || a.name || b.name || "Company settings",
                       kind: tgt ? "user" : (f.leadName ? "lead" : "company"),
                       detail: r.reason || "—" };
          }
        };
        var kindStyle = {
          lead:    {bg:"#FEF3C7", fg:"#854F0B"},
          user:    {bg:"#E6F1FB", fg:"#185FA5"},
          company: {bg:"#EEEEEA", fg:"#666"}
        };

        return <div dir={t.dir==="rtl"?"rtl":"ltr"} style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>System audit</div>
          <div style={{fontSize:12,color:"#666",marginBottom:12,lineHeight:1.5}}>
            Sensitive actions recorded with who did them and what changed — salary edits, off-site approvals,
            permission and shift changes, company off-days, VIP flips, rotation-stop clears.
            {" "}Separate from <b>Audit Log</b>, which tracks individual settings fields and can roll them back.
          </div>

          <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
            <span style={{fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px"}}>Type</span>
            <Select value={sysAuditType} onChange={function(e){setSysAuditType(e.target.value);}} searchable width={230} maxWidth={230}
              options={[{value:"all",label:"All types"}].concat(types.map(function(x){return {value:x,label:x};}))}/>
            {sysAuditType!=="all" &&
              <button type="button" onClick={function(){setSysAuditType("all");}}
                style={{fontSize:12,padding:"6px 12px",border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",borderRadius:8,cursor:"pointer",color:"#1a1a1a",fontFamily:"inherit"}}>Clear</button>}
            {sysAuditBusy && <span style={{fontSize:11,color:"#666"}}>Loading…</span>}
          </div>

          {sysAuditErr
            ? <div style={{background:"#FCEBEB",border:"0.5px solid rgba(163,45,45,0.3)",borderRadius:8,padding:"14px",color:"#A32D2D",fontSize:12}}>{sysAuditErr}</div>
            : !loaded
              ? <div style={{background:"#F7F7F5",border:"0.5px dashed rgba(0,0,0,0.1)",borderRadius:8,padding:"16px",textAlign:"center",color:"#666",fontSize:12}}>Loading system audit…</div>
              : rows.length===0
                ? <div style={{background:"#F7F7F5",border:"0.5px dashed rgba(0,0,0,0.1)",borderRadius:8,padding:"24px 16px",textAlign:"center",color:"#666",fontSize:12}}>
                    No entries{sysAuditType!=="all" ? " of type " + sysAuditType : ""} yet.
                  </div>
                : <div>
                    {rows.map(function(r,i){
                      var d = describe(r);
                      var ks = kindStyle[d.kind] || kindStyle.company;
                      var actor = nameOf(r.performedBy) || "System";
                      var ts = r.timestamp ? new Date(r.timestamp) : null;
                      return <div key={r._id||i} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,padding:"10px 0",borderBottom:"0.5px solid rgba(0,0,0,0.1)",fontSize:12,flexWrap:"wrap"}}>
                        <div style={{flex:1,minWidth:220,lineHeight:1.6}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                            <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:ks.bg,color:ks.fg,fontWeight:500,fontFamily:"ui-monospace, Menlo, monospace"}}>{r.type}</span>
                            <b style={{color:"#1a1a1a"}}>{d.subject}</b>
                          </div>
                          <div style={{color:"#666",marginTop:2}}>{d.detail}</div>
                        </div>
                        <div style={{textAlign:t.dir==="rtl"?"left":"right",flexShrink:0,color:"#666",lineHeight:1.6,minWidth:150}}>
                          <div>by <b style={{color:"#1a1a1a",fontWeight:500}}>{actor}</b></div>
                          <div style={{fontSize:11}}>
                            {ts ? fmtDateTime12(ts, undefined, { sep:" " }) : ""}
                            {r.ipAddress ? <span style={{marginInlineStart:6,color:"#999"}}>{r.ipAddress}</span> : null}
                          </div>
                        </div>
                      </div>;
                    })}
                  </div>
          }

          {loaded && rows.length>0 && <div style={{fontSize:11,color:"#999",marginTop:12,textAlign:t.dir==="rtl"?"left":"right"}}>
            {/* Y is the DB total from the server's countDocuments, not the length
                of what was fetched. The old line stated only rows.length and then
                appended "newest 200" when it happened to equal the cap — a
                denominator that could not reveal a truncation, because it agreed
                with itself at any size. The cap is gone (fetchAllPages walks every
                page), so X and Y are equal unless a type filter is narrowing. */}
            {(function(){
              var denom = (sysAuditType === "all" && typeof sysAuditTotal === "number")
                ? sysAuditTotal : rows.length;
              return "Showing " + rows.length + " of " + denom + " " + (denom===1 ? "entry" : "entries");
            })()}
          </div>}
        </div>;
      })()}

      {activeTab==="officeLocation" && (function(){
        var fieldLabel = {fontSize:11,color:"#666",display:"block",marginBottom:4};
        var inputStyle = {padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",fontFamily:"inherit",width:"100%",boxSizing:"border-box"};
        var btnPrimary = {fontSize:12,padding:"8px 16px",border:"0.5px solid rgba(24,95,165,0.3)",background:"#185FA5",color:"#fff",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var btnGhost   = {fontSize:12,padding:"8px 14px",border:"0.5px solid rgba(0,0,0,0.15)",background:"transparent",borderRadius:8,cursor:"pointer",fontFamily:"inherit",color:"#1a1a1a"};

        var updateRow = function(idx, patch){
          setAttLocs(function(prev){
            return prev.map(function(row, i){ return i === idx ? Object.assign({}, row, patch) : row; });
          });
        };
        var addRow = function(){
          setAttLocs(function(prev){
            return prev.concat([{ name: "", latitude: "", longitude: "", radiusMeters: 100, isActive: true }]);
          });
        };
        var deleteRow = function(idx){
          if (!window.confirm("Delete this location? Past check-ins keep their recorded location name; future ones can no longer match here.")) return;
          setAttLocs(function(prev){ return prev.filter(function(_, i){ return i !== idx; }); });
        };
        var captureGpsFor = function(idx){
          setAttError(""); setAttMsg("Getting your location…");
          geoGetCurrentPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 })
            .then(function(c){
              updateRow(idx, {
                latitude:  String(c.latitude.toFixed(6)),
                longitude: String(c.longitude.toFixed(6))
              });
              setAttMsg("Captured for row " + (idx + 1) + " (" + c.accuracy.toFixed(0) + "m accuracy)");
            })
            .catch(function(err){
              setAttMsg("");
              setAttError("Could not get location: " + ((err && err.message) || ""));
            });
        };

        var doSaveLocs = async function(){
          setAttError(""); setAttMsg(""); setAttSaving(true);
          try {
            // Client-side validation pass — surfaces row-specific errors
            // before the round-trip. Server re-validates anyway.
            for (var i = 0; i < attLocs.length; i++) {
              var r = attLocs[i];
              if (!r.name || !r.name.trim()) throw new Error("Row " + (i + 1) + ": name is required");
              if (!isFinite(Number(r.latitude))  || Number(r.latitude)  < -90  || Number(r.latitude)  > 90)  throw new Error("Row " + (i + 1) + ": latitude must be between -90 and 90");
              if (!isFinite(Number(r.longitude)) || Number(r.longitude) < -180 || Number(r.longitude) > 180) throw new Error("Row " + (i + 1) + ": longitude must be between -180 and 180");
              var rad = Number(r.radiusMeters);
              if (!isFinite(rad) || rad < 1 || rad > 10000) throw new Error("Row " + (i + 1) + ": radius must be between 1 and 10000");
            }
            var payload = { companyLocations: attLocs.map(function(r){
              return {
                name: String(r.name).trim(),
                latitude:  Number(r.latitude),
                longitude: Number(r.longitude),
                radiusMeters: Number(r.radiusMeters),
                isActive: !!r.isActive
              };
            })};
            var res = await apiFetch("/api/settings/office-location","PATCH",payload,p.token,p.csrfToken);
            if (res && Array.isArray(res.companyLocations)) {
              setAttLocs(res.companyLocations.map(function(l){
                return {
                  name: l.name || "",
                  latitude:  l.latitude  != null ? String(l.latitude)  : "",
                  longitude: l.longitude != null ? String(l.longitude) : "",
                  radiusMeters: l.radiusMeters || 100,
                  isActive: l.isActive !== false
                };
              }));
            }
            setAttMsg("Saved " + (res && res.companyLocations ? res.companyLocations.length : 0) + " location" + ((res && res.companyLocations && res.companyLocations.length === 1) ? "" : "s"));
          } catch (err) {
            setAttError((err && err.message) || "Save failed");
          } finally {
            setAttSaving(false);
          }
        };

        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          {/* Phone/tablet rules for this tab, `.off-*`. Each location is a
              five-column input grid, `2fr 1fr 1fr 0.8fr 90px`. At 299px that
              is ~40px per input; at 768, with the 16px input font forced on,
              latitude/longitude got ~67px of box for a value like 30.0444
              that needs ~60 of text. Two columns under 900 (1fr ~76px at 13px
              is enough for seven digits), one column under 768. The fifth
              cell -- the active checkbox and its buttons -- is a flex row
              and sits happily in either. */}
          <style>{""
+ "@media (max-width: 900px) {"
+ "  .off-loc-grid { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }"
+ "}"
+ "@media (max-width: 768px) {"
+ "  .off-loc-grid { grid-template-columns: minmax(0,1fr) !important; }"
+ "}"
}</style>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Office Locations</div>
          <div style={{fontSize:12,color:"#666",marginBottom:18}}>Geofences used for attendance check-in/check-out. Add a row per branch — employees can check in from any active location. Distance is computed server-side using the Haversine formula.</div>

          {!attLoaded ? <div style={{fontSize:12,color:"#666"}}>Loading…</div>
            : attLocs.length === 0 ? <div style={{padding:"24px 16px",textAlign:"center",color:"#666",background:"#FAFAF7",border:"0.5px dashed rgba(0,0,0,0.1)",borderRadius:8,fontSize:13}}>
                No office locations yet. Click <b>+ Add location</b> to create the first one.
              </div>
            : <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {attLocs.map(function(loc, idx){
                return <div key={idx} style={{padding:"14px 16px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:10,background: loc.isActive ? "#fff" : "#FAFAF7"}}>
                  <div className="off-loc-grid" style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 0.8fr 90px",gap:10,alignItems:"end"}}>
                    <div>
                      <label style={fieldLabel}>Name</label>
                      <input type="text" value={loc.name} onChange={function(e){updateRow(idx,{name:e.target.value});}} style={inputStyle} placeholder="ARO HQ — Cairo"/>
                    </div>
                    <div>
                      <label style={fieldLabel}>Latitude</label>
                      <input type="text" inputMode="decimal" value={loc.latitude} onChange={function(e){updateRow(idx,{latitude:e.target.value});}} style={inputStyle} placeholder="30.1376"/>
                    </div>
                    <div>
                      <label style={fieldLabel}>Longitude</label>
                      <input type="text" inputMode="decimal" value={loc.longitude} onChange={function(e){updateRow(idx,{longitude:e.target.value});}} style={inputStyle} placeholder="31.6817"/>
                    </div>
                    <div>
                      <label style={fieldLabel}>Radius (m)</label>
                      <input type="number" min="1" max="10000" value={loc.radiusMeters} onChange={function(e){updateRow(idx,{radiusMeters:e.target.value});}} style={inputStyle}/>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6,paddingBottom:6}}>
                      <label style={{fontSize:12,color:"#1a1a1a",display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
                        <input type="checkbox" checked={loc.isActive} onChange={function(e){updateRow(idx,{isActive:e.target.checked});}}/>
                        Active
                      </label>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}}>
                    <button type="button" onClick={function(){captureGpsFor(idx);}} style={Object.assign({},btnGhost,{fontSize:11,padding:"5px 10px"})}>Use my current location</button>
                    <button type="button" onClick={function(){deleteRow(idx);}} style={{fontSize:11,padding:"5px 10px",border:"0.5px solid rgba(0,0,0,0.1)",background:"transparent",borderRadius:6,cursor:"pointer",fontFamily:"inherit",color:"#A32D2D"}}>Delete</button>
                  </div>
                </div>;
              })}
            </div>}

          <div style={{display:"flex",gap:8,marginTop:18,flexWrap:"wrap",alignItems:"center"}}>
            <button type="button" onClick={addRow} style={btnGhost}>+ Add location</button>
            <button type="button" onClick={doSaveLocs} disabled={attSaving||!attLoaded} style={Object.assign({},btnPrimary,{opacity:attSaving?0.6:1,cursor:attSaving?"not-allowed":"pointer"})}>
              {attSaving?"Saving…":"Save all locations"}
            </button>
            {attMsg   && <span style={{fontSize:12,color:"#0F6E56",background:"#EAF6F0",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{attMsg}</span>}
            {attError && <span style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{attError}</span>}
          </div>
        </div>;
      })()}

      {activeTab==="visibility" && (function(){
        if (visError && !visData) return <div style={{fontSize:12,color:"#c00"}}>{visError}</div>;
        if (!visData || !visDraft) return <div style={{fontSize:12,color:"#666"}}>Loading…</div>;

        var btnPrimary = {fontSize:12,padding:"8px 16px",border:"0.5px solid rgba(24,95,165,0.3)",background:"#185FA5",color:"#fff",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var surfaces = visData.surfaces || {};
        var depts = Object.keys(visData.ladders || {});
        if (depts.indexOf(visDept) === -1 && depts.length) visDept = depts[0];
        var ranks = (visData.ladders || {})[visDept] || [];
        var rankNums = ranks.map(function(r){ return r.rank; });
        var maxRank = rankNums.length ? rankNums[rankNums.length-1] : 0;
        var pageKeys = Object.keys(surfaces).filter(function(k){ return surfaces[k].kind === "page"; });
        var tabsOf = function(pageId){
          return Object.keys(surfaces).filter(function(k){
            return surfaces[k].kind === "tab" && surfaces[k].pageId === pageId;
          });
        };

        // ---- P4: the read-only replica -------------------------------------
        // The rung being previewed. Derived rather than corrected in an effect,
        // same as the tab strips: switching department must land on a rank that
        // exists on the NEW ladder, and rank 7 of Operations is not rank 7 of
        // Sales.
        var repRank = (function(){
          if (visRank !== null && rankNums.indexOf(visRank) !== -1) return visRank;
          return rankNums.length ? rankNums[0] : null;
        })();
        // How many PEOPLE stand on each rung, so the dropdown says which rungs
        // are real. Most ladder rungs are empty — the same fact `blank_rank`
        // leans on server-side — and previewing an empty one is legitimate but
        // worth knowing.
        var headcount = (function(){
          var m = {};
          (visData.roster || []).forEach(function(u){
            if (u.department !== visDept || u.titleRank === null || u.titleRank === undefined) return;
            m[u.titleRank] = (m[u.titleRank] || 0) + 1;
          });
          return m;
        })();
        var repCtx = previewContext(visData, visDraft, visDept, repRank);
        var repTitle = (ranks.filter(function(r){ return r.rank === repRank; })[0] || {}).title || "";
        var repCounts = (function(){
          var pg = 0, tb = 0;
          Object.keys(surfaces).forEach(function(k){
            if (!previewSees(repCtx, k)) return;
            if (surfaces[k].kind === "page") pg++; else tb++;
          });
          return { pages: pg, tabs: tb };
        })();

        // NOBODY is a real column, not the absence of one — an empty rank array is
        // a grant the model can express, so it round-trips and is never `custom`.
        var PRESET_COLS = presetsForDept(visData, visDept).concat([{ key:"none", label:"Nobody", minRank:VIS_NOBODY }]);

        var isChecked = function(rank, key){
          var d = visDraft.grants[visDept] || {};
          return (d[String(rank)] || []).indexOf(key) !== -1;
        };
        // NO PROPOSAL FLOW (2026-11-02). Every ladder role resolves from grants, so an
        // edit on this screen is a change, not a proposal to take a surface over;
        // Save writes it and the server's four guards answer. The capability screen
        // keeps its proposal snapshot (capRevert) because take-over still exists there.
        var toggle = function(rank, key, on){
          setVisDraft(function(prev){
            var next = { grants: JSON.parse(JSON.stringify(prev.grants)), managed: Object.assign({}, prev.managed) };
            var d = next.grants[visDept] = next.grants[visDept] || {};
            var list = d[String(rank)] = (d[String(rank)] || []).slice();
            var i = list.indexOf(key);
            if (on && i === -1) list.push(key);
            if (!on && i !== -1) list.splice(i,1);
            // A tab cannot be granted where its page is not — the server refuses
            // that save (orphan_tab), so the grid must not offer it. Ticking a tab
            // ticks its page; unticking a page unticks its tabs.
            var def = surfaces[key];
            if (on && def && def.kind === "tab" && list.indexOf(def.parent) === -1) list.push(def.parent);
            if (!on && def && def.kind === "page") {
              tabsOf(def.pageId).forEach(function(tk){
                var ti = list.indexOf(tk); if (ti !== -1) list.splice(ti,1);
              });
            }
            return next;
          });
          setVisMsg(""); setVisError("");
        };

        // THE TABLE WRITER. Delegates to the module-scope pure function so the
        // build gate can simulate exactly this write without a browser.
        var setThreshold = function(key, minRank){
          var def = surfaces[key] || {};
          var beforePage = (def.kind === "tab" && def.parent)
            ? thresholdOfKey(visDraft.grants, visDept, rankNums, def.parent) : null;
          var nextGrants = applyThresholdToGrants(visDraft.grants, visDept, rankNums, key, minRank, surfaces);
          if (def.kind === "tab" && def.parent) {
            var afterPage = thresholdOfKey(nextGrants, visDept, rankNums, def.parent);
            // AUTO-WIDEN, said out loud. The page moved because this tab moved, and
            // a silent change to a row the owner is not looking at is exactly the
            // kind of edit this redesign exists to prevent.
            setVisWiden(afterPage !== beforePage
              ? { tabKey:key, pageLabel:(surfaces[def.parent]||{}).label || def.parent, to:afterPage }
              : null);
          } else setVisWiden(null);
          setVisDraft({ grants: nextGrants, managed: visDraft.managed });
          setVisMsg(""); setVisError("");
        };

        // ===== ONE POSTER, ONE PLACE THE 409 IS HANDLED ====================
        //
        // Every write to the matrix goes through here, and the body it was given
        // is the body it keeps: on a confirmable refusal the EXACT payload is
        // stashed so the retry can resend it rather than reconstruct it. Two
        // posters, each rebuilding its own body, is precisely how the confirm
        // path shipped dropping the take-over flag — see confirmedRetryBody().
        var postMatrix = async function(body, okMsg, onOk){
          setVisSaving(true); setVisMsg(""); setVisError("");
          try {
            var res = await apiFetch("/api/settings/visibility-matrix","PATCH",
              body, p.token, p.csrfToken);
            if (res && res.matrix) {
              setVisData(function(prev){ return Object.assign({}, prev, { matrix: res.matrix, holders: res.holders }); });
              setVisDraft({ grants: JSON.parse(JSON.stringify(res.matrix.grants||{})), managed: Object.assign({}, res.matrix.managed||{}) });
            }
            setVisMsg(okMsg);
            setVisStrand(null);
            if (onOk) onOk();
            loadVisibility();
          } catch (err) {
            // The endpoint refuses a save that would strand a surface, blank a
            // rank, orphan a tab, or hide the surface from the person saving.
            // Its message names which and why — show it verbatim. NO CLIENT MIRROR
            // of those rules: a second copy is a second answer.
            //
            // ONE of the four is confirmable, and it is identified by err.code —
            // apiFetch carries the machine slug precisely so a caller can branch
            // without regex-matching prose that is free to be reworded or
            // translated. Everything else still lands in visError and is final.
            // apiFetch forwards `message`, `code`, `status` and `fields` — NOT
            // the endpoint's `surfaces` array. The server's message already
            // names the labels, so it is shown verbatim rather than rebuilt from
            // a field this client cannot see.
            //
            // THE REFUSED BODY IS KEPT VERBATIM, and the okMsg with it, so the
            // confirmed retry is the same request twice — same payload, same
            // success wording — differing only by the flag.
            if (err && err.code === "surface_stranded") {
              setVisStrand({ message: err.message || "", body: body, okMsg: okMsg });
            } else {
              setVisError((err && err.message) || "Save failed");
            }
          } finally { setVisSaving(false); }
        };

        // The first press. Builds from the draft — which is correct HERE, because
        // this is the request the owner is actually making. `managed` travels
        // exactly as loaded: the resolver no longer reads it (2026-11-02), and
        // the document keeps it as inert data the seed's rollback restores around.
        var save = function(){
          setVisStrand(null);
          var managed = Object.assign({}, visDraft.managed);
          return postMatrix({ grants: visDraft.grants, managed: managed }, "Saved", function(){
            setVisBlast(null);
          });
        };

        // The second press. Resends what was refused — it must NOT consult the
        // draft, which is the whole point of confirmedRetryBody().
        var confirmStrand = function(){
          if (!visStrand || !visStrand.body) return;
          return postMatrix(confirmedRetryBody(visStrand.body), visStrand.okMsg || "Saved", function(){
            setVisBlast(null);
          });
        };

        // WHO WOULD SEE IT AFTER SAVE — resolved per roster user through previewSees
        // on the DRAFT, so the pane answers for the edit being considered, not the
        // one already saved. Rank-less accounts (the Owner, the sealed reviewer) are
        // not on any rung and are left to "Sees it now", which the server computes.
        var wouldSee = function(key){
          var out = [];
          (visData.roster || []).forEach(function(u){
            if (!u.department || u.titleRank === null || u.titleRank === undefined) return;
            var ctx = previewContext(visData, visDraft, u.department, u.titleRank);
            if (previewSees(ctx, key)) out.push(u.name);
          });
          return out.sort();
        };

        var TIER = {
          strict:   { label:"Fully enforced", fg:"#15803D",
                      tip:"Hiding this refuses the API routes behind it too." },
          partial:  { label:"Menu + partial",  fg:"#B45309",
                      tip:"Some routes are shared with pages this person may still see, so hiding refuses less than it appears to." },
          cosmetic: { label:"Menu only",       fg:"#B91C1C",
                      tip:"Every route this page reads is also read by another page they can see. Hiding removes the menu entry and refuses nothing." }
        };

        // The per-rank grid — the ONLY honest editor for a grant no threshold
        // describes, and the "only this rank" escape the band toggle deliberately
        // does not offer on the row itself. It survived the table's deletion
        // because a custom grant still has to be readable and repairable.
        var rankGrid = function(key){
          return <div style={{display:"flex",flexWrap:"wrap",gap:"6px 14px",padding:"8px 0 4px"}}>
            {ranks.map(function(r){
              var on = isChecked(r.rank, key);
              return <label key={r.rank} style={{display:"flex",alignItems:"center",gap:5,fontSize:11.5,color:"#444",cursor:"pointer",minWidth:180}}>
                <input type="checkbox" checked={on} onChange={function(e){toggle(r.rank,key,e.target.checked);}}/>
                <span style={{fontVariantNumeric:"tabular-nums",color:"#888",minWidth:16}}>{r.rank}</span>
                <span>{r.title}</span>
                {r.rank === repRank && <span style={{fontSize:10,color:"#185FA5"}}>· previewing</span>}
              </label>;
            })}
          </div>;
        };

        // ===== THE BAND WRITE ===============================================
        //
        // ON at rank N  -> threshold min(current, N): N and everyone ABOVE gain it.
        // OFF at rank N -> threshold N+1: N and everyone BELOW lose it, seniors keep it.
        //
        // OFF RAISES THE BAR rather than clearing the column, and that is the
        // owner's ruling (2026-08-23). It keeps every write from this screen
        // TOP-ANCHORED, which is what lets the existing pure writer stay the only
        // writer and what stops the replica from manufacturing `custom` grants
        // nobody asked for. Removing a page from everyone is what the Advanced
        // drawer is for.
        //
        // A per-rung write was the alternative and it is the documented trap:
        // "grant the RANK BAND, not the one rank — or a promotion silently
        // revokes". Making that the default gesture would have built the trap in.
        var repToggle = function(key, on){
          var cur = thresholdOfKey(visDraft.grants, visDept, rankNums, key);
          var next = bandThresholdFor(cur, repRank, maxRank, on);
          // WHAT THIS CLICK ACTUALLY MOVES, computed BEFORE the write and shown
          // under the row. A rank-scoped editor that silently rewrites the other
          // nine rungs is the thing this whole redesign exists to prevent.
          //
          // COMPARED AS RESOLVED VISIBILITY, NOT AS GRANT MEMBERSHIP. The first
          // version diffed the grant arrays and was backwards on exactly the
          // rows that matter: on a surface still ruled by the sidebar the grants
          // are empty, so switching KPIs off at rank 4 reported "gained ranks
          // 5-10" and lost NOTHING — while the real effect is that ranks 1-4
          // lose a page they can see today. A staged grant is not what anybody
          // sees; the resolved answer is.
          //
          // `after` resolves the simulated grants through the same resolver, so the
          // sentence describes what Save will do and nothing else.
          var simulated = applyThresholdToGrants(visDraft.grants, visDept, rankNums, key, next, surfaces);
          var before = {}, after = {};
          rankNums.forEach(function(r){
            before[r] = previewSees(previewContext(visData, visDraft, visDept, r), key);
            after[r]  = previewSees(previewContext(visData, { grants: simulated }, visDept, r), key);
          });
          var gained = rankNums.filter(function(r){ return !before[r] && after[r]; });
          var lost   = rankNums.filter(function(r){ return before[r] && !after[r]; });
          var peopleOn = function(list){ return list.reduce(function(n,r){ return n + (headcount[r] || 0); }, 0); };
          setVisBlast({
            key: key, wasCustom: cur === null,
            gained: gained, lost: lost,
            gainedPeople: peopleOn(gained), lostPeople: peopleOn(lost)
          });
          setVisSelected(key);
          setThreshold(key, next);
        };

        // ===== THE CONSISTENCY HINT =========================================
        //
        // A lower rung seeing something a higher rung does not. INFORM, NEVER
        // BLOCK — because the inversions that exist are the product's own: `kpis`
        // is sales-position-only, so a Property Advisor sees KPIs and the Sales
        // Director above them does not, by decision.
        //
        // Every inversion now comes from the grants on this screen (there is no
        // sidebar half left for a rung), so the hint says so plainly and the KPIs
        // one reads as a setting the owner can change, not a rule they cannot.
        var inversions = (function(){
          if (!rankNums.length) return [];
          var ctxByRank = {};
          rankNums.forEach(function(r){ ctxByRank[r] = previewContext(visData, visDraft, visDept, r); });
          var out = [];
          Object.keys(surfaces).forEach(function(k){
            var seen = rankNums.map(function(r){ return previewSees(ctxByRank[r], k); });
            for (var i = 0; i < rankNums.length; i++) {
              if (!seen[i]) continue;
              for (var j = i + 1; j < rankNums.length; j++) {
                if (seen[j]) continue;
                out.push({ key: k, lower: rankNums[i], higher: rankNums[j] });
                return;                       // one report per surface is enough
              }
            }
          });
          return out;
        })();

        var titleOfRank = function(r){
          return (ranks.filter(function(x){ return x.rank === r; })[0] || {}).title || ("rank " + r);
        };

        var selDef  = visSelected ? surfaces[visSelected] : null;
        var selTier = selDef ? (TIER[selDef.enforcement] || TIER.cosmetic) : null;
        var selCur  = visSelected ? thresholdOfKey(visDraft.grants, visDept, rankNums, visSelected) : null;

        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          {/* Phone rule for this tab, `.vis-*`. The department pane is
              `flex:"1 1 320px", minWidth:300` in a flex-wrap card. The wrap
              is right -- the pane drops below the list on a phone -- but 300
              is a floor a 299px panel cannot meet, and inside the card's own
              padding the pane had ~270, so it overflowed ~30px and
              `.crm-app{overflow-x:hidden}` clipped its right edge. Under 768
              the floor goes to 0; the 320 basis still governs wrapping, so
              the two-pane layout is untouched wherever it fits. */}
          <style>{""
+ "@media (max-width: 768px) {"
+ "  .vis-pane { min-width: 0 !important; }"
+ "}"
}</style>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Page Visibility</div>
          <div style={{fontSize:12,color:"#666",marginBottom:14,lineHeight:1.5}}>
            Pick a department and a rank, then switch entries on and off in that rank's own menu.
            What you see here is exactly what that rank resolves to — <b>Save applies it to everyone in the ranks you changed</b>,
            immediately, in every department you edited.
          </div>

          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
            {depts.map(function(dp){
              var act = dp === visDept;
              return <button key={dp} type="button" onClick={function(){
                        setVisDept(dp);setVisSelected(null);setVisBlast(null);setVisStrand(null);
                        setVisMsg("");setVisError("");setVisWiden(null);}}
                style={{padding:"6px 12px",fontSize:12,borderRadius:6,cursor:"pointer",
                        border:act?"0.5px solid rgba(0,0,0,0.15)":"0.5px solid transparent",
                        background:act?"#fff":"rgba(0,0,0,0.04)",color:act?"#1a1a1a":"#666",
                        textTransform:"capitalize",fontWeight:act?500:400,fontFamily:"inherit"}}>{deptLabel(visData, dp)}</button>;
            })}
          </div>

          <div style={{display:"flex",gap:22,flexWrap:"wrap",alignItems:"flex-start"}}>
            {/* ---- the menu ------------------------------------------------ */}
            <div style={{flex:"0 0 auto"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                <span style={{fontSize:11,color:"#666"}}>Editing</span>
                <select value={repRank === null ? "" : String(repRank)}
                        onChange={function(e){
                          setVisBlast(null);
                          setVisStrand(null);
                          setVisRank(e.target.value === "" ? null : Number(e.target.value));
                        }}
                        style={{fontSize:12,padding:"5px 8px",border:"0.5px solid rgba(0,0,0,0.15)",borderRadius:6,
                                background:"#fff",fontFamily:"inherit",color:"#1a1a1a",fontWeight:500}}>
                  {ranks.map(function(r){
                    var n = headcount[r.rank] || 0;
                    return <option key={r.rank} value={String(r.rank)}>
                      {r.title} · rank {r.rank} · {n ? (n + (n===1?" person":" people")) : "nobody"}
                    </option>;
                  })}
                </select>
              </div>
              {repRank === null
                ? <div style={{fontSize:12,color:"#666"}}>This department has no ladder to edit.</div>
                : <SidebarReplica t={t} ctx={repCtx} expanded={visRepExpanded}
                    selected={visSelected}
                    onToggleExpand={function(pageId){
                      setVisRepExpanded(function(x){ var n=Object.assign({},x); n[pageId]=!n[pageId]; return n; });
                    }}
                    onToggle={repToggle}
                    onSelect={function(k){ setVisSelected(k); setVisBlast(null); }}/>}
              {/* THE LEGEND IS RENDERED ON THE PANEL'S OWN GROUND, so the two
                  switches read exactly as they do six pixels above it. A legend
                  drawn in light-UI colours would be describing a different
                  control from the one it sits under. */}
              <div style={{marginTop:10,maxWidth:262,background:"#1C1E28",borderRadius:8,padding:"8px 10px",
                           border:"1px solid rgba(255,255,255,0.08)",display:"flex",gap:14,flexWrap:"wrap"}}>
                <span style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:11,color:NAV_LIT_INK}}>
                  {previewSwitch(true, true, null, null)} sees it
                </span>
                <span style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:11,color:NAV_DIM_INK,
                              textDecoration:"line-through",textDecorationColor:NAV_STRIKE,textDecorationThickness:"1px"}}>
                  {previewSwitch(false, true, null, null)} hidden
                </span>
              </div>
              <div style={{fontSize:11,color:"#888",marginTop:8,lineHeight:1.5,maxWidth:262}}>
                Click a row to switch it, or the <b>▸2</b> to open its tabs.
                This previews a <b>rank</b>, not a person — your own account always sees everything.
              </div>
            </div>

            {/* ---- the pane ------------------------------------------------ */}
            <div className="vis-pane" style={{flex:"1 1 320px",minWidth:300}}>
              {visBlast && (function(){
                var b = visBlast;
                if (!b.gained.length && !b.lost.length) {
                  return <div style={{fontSize:11.5,color:"#666",background:"#F7F8FA",border:"0.5px solid rgba(0,0,0,0.08)",
                                      borderRadius:8,padding:"9px 11px",marginBottom:12,lineHeight:1.5}}>
                    No rank in {deptLabel(visData, visDept)} changes — {surfaces[b.key] ? surfaces[b.key].label : b.key} already
                    resolves that way for every rung here. Other departments can still be affected by a take-over.
                  </div>;
                }
                // THE BAND TOTAL HID THE DISTRIBUTION (owner ruling 2026-08-29).
                // "ranks 6–7 · 1 person" is true and useless: it does not say
                // that rank 7 is EMPTY, so a toggle set to 6–7 reads as covering
                // two rungs when it covers one. Operations has exactly one
                // occupant on a seven-rung ladder, which is the ordinary case
                // here rather than the odd one — most rungs are empty, which is
                // the same fact `blank_rank` leans on server-side.
                //
                // So a multi-rank band also prints its per-rung split. Display
                // only: `headcount` is already computed for this department and
                // nothing about the grant changes.
                var perRank = function(list){
                  if (list.length < 2) return null;
                  return list.map(function(r){ return "r" + r + " · " + (headcount[r] || 0); }).join(", ");
                };
                var phrase = function(list, people, verb){
                  if (!list.length) return null;
                  var lo = list[0], hi = list[list.length-1];
                  var split = perRank(list);
                  return verb + " " + (list.length === 1
                      ? titleOfRank(lo) + " (rank " + lo + ")"
                      : "ranks " + lo + "–" + hi + " (" + titleOfRank(lo) + " → " + titleOfRank(hi) + ")")
                    + " · " + (people ? people + (people===1?" person":" people") : "nobody today")
                    + (split ? " — " + split : "");
                };
                return <div style={{fontSize:11.5,color:"#5a4a20",background:"#FFF8E6",border:"0.5px solid rgba(184,122,20,0.3)",
                                    borderRadius:8,padding:"9px 11px",marginBottom:12,lineHeight:1.55}}>
                  <b>{surfaces[b.key] ? surfaces[b.key].label : b.key}</b>
                  {b.lost.length   ? <div>{phrase(b.lost,   b.lostPeople,   "will be hidden from")}</div> : null}
                  {b.gained.length ? <div>{phrase(b.gained, b.gainedPeople, "will be shown to")}</div> : null}
                  <div style={{marginTop:4,color:"#7a6a40"}}>…once you save. Nothing has changed yet.</div>
                  {b.wasCustom && <div style={{marginTop:4,color:"#7a6a40"}}>
                    This replaced a per-rank setting that no band described. Open <b>Set per rank</b> below to see the result.
                  </div>}
                </div>;
              })()}

              {visWiden && <div style={{fontSize:11.5,color:"#5a4a20",background:"#FFF8E6",border:"0.5px solid rgba(184,122,20,0.3)",
                                        borderRadius:8,padding:"9px 11px",marginBottom:12,lineHeight:1.5}}>
                “{visWiden.pageLabel}” was widened to {visWiden.to ? "rank "+visWiden.to+"+" : "nobody"} as well — a tab cannot be visible where its page is not.
              </div>}

              {inversions.filter(function(iv){ return !visHintOff[iv.key]; }).slice(0,2).map(function(iv){
                return <div key={iv.key} style={{fontSize:11.5,color:"#5a4a20",background:"#FFFBEB",
                              border:"0.5px solid rgba(184,122,20,0.25)",borderRadius:8,padding:"9px 11px",
                              marginBottom:12,lineHeight:1.55,display:"flex",gap:10,alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <b>{titleOfRank(iv.lower)} (rank {iv.lower})</b> sees {surfaces[iv.key].label};{" "}
                    <b>{titleOfRank(iv.higher)} (rank {iv.higher})</b> does not.{" "}
                    <span>That is what the settings here currently say.</span>
                  </div>
                  <span onClick={function(){ setVisHintOff(function(x){ var n=Object.assign({},x); n[iv.key]=true; return n; }); }}
                        style={{cursor:"pointer",color:"#8a7a50",fontSize:11,whiteSpace:"nowrap",userSelect:"none"}}>Dismiss</span>
                </div>;
              })}

              {!selDef
                ? <div style={{fontSize:12,color:"#666",border:"0.5px dashed rgba(0,0,0,0.15)",borderRadius:8,
                               padding:"18px 14px",lineHeight:1.55}}>
                    Pick an entry in the menu to see who holds it and what hiding it actually does.
                  </div>
                : <div style={{border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,overflow:"hidden"}}>
                    <div style={{padding:"12px 14px",borderBottom:"0.5px solid rgba(0,0,0,0.08)",background:"#FAFBFC"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span style={{width:8,height:8,borderRadius:"50%",background:selTier.fg,flexShrink:0}} title={selTier.tip}/>
                        <span style={{fontSize:13,fontWeight:500,color:"#1a1a1a"}}>{selDef.label}</span>
                      </div>
                      <div style={{fontSize:11,color:selTier.fg,marginTop:6,lineHeight:1.5}}>
                        <b>{selTier.label}</b> — {selTier.tip}
                      </div>
                    </div>

                    <div style={{padding:"12px 14px",fontSize:11.5,color:"#666",lineHeight:1.6}}>
                      <div><b style={{color:"#444"}}>Sees it now</b> — {(function(){
                        var h = (visData.holders || {})[visSelected] || [];
                        return h.length ? h.length + " · " + h.slice(0,6).map(function(x){return x.name;}).join(", ") + (h.length>6?", +"+(h.length-6):"") : "nobody";
                      })()}</div>
                      <div><b style={{color:"#444"}}>Would see</b> — {(function(){
                        var w = wouldSee(visSelected);
                        return w.length ? w.length + " · " + w.slice(0,6).join(", ") + (w.length>6?", +"+(w.length-6):"") : "nobody";
                      })()}  (after Save)</div>
                    </div>

                    <div style={{borderTop:"0.5px solid rgba(0,0,0,0.08)",padding:"10px 14px",background:"#FAFBFC"}}>
                      <span onClick={function(){ setVisAdvOpen(!visAdvOpen); }}
                            style={{fontSize:11.5,color:"#666",cursor:"pointer",userSelect:"none"}}>
                        {visAdvOpen?"▾":"▸"} Set per rank
                        {selCur === null && <span style={{color:"#6D28D9",marginInlineStart:6}}>· custom</span>}
                      </span>
                      {visAdvOpen && <div>
                        {selCur === null && <div style={{fontSize:11,color:"#6D28D9",lineHeight:1.45,paddingTop:6}}>
                          No band describes this grant — it does not run from one rank up to the top. The switch in
                          the menu will replace it with a band; set it here to keep it per rank.
                        </div>}
                        {rankGrid(visSelected)}
                      </div>}
                    </div>
                  </div>}

              <div style={{display:"flex",gap:8,marginTop:16,flexWrap:"wrap",alignItems:"center"}}>
                <button type="button" onClick={function(){save();}} disabled={visSaving}
                  style={Object.assign({},btnPrimary,{opacity:visSaving?0.6:1})}>
                  {visSaving?"Saving…":"Save"}
                </button>
                <span style={{fontSize:11.5,color:"#888"}}>Save applies these settings to everyone in the ranks you changed, immediately.</span>
                {visMsg   && <span style={{fontSize:12,color:"#0F6E56",background:"#EAF6F0",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{visMsg}</span>}
                {visError && <span style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"4px 10px",borderRadius:8,fontWeight:500,maxWidth:520,lineHeight:1.4}}>{visError}</span>}
              </div>

              {/* ===== THE ONE REFUSAL WITH A SECOND PRESS BEHIND IT ==========
                  Owner-only is a legal end state, so this is a confirmation and
                  not an error: it is drawn in the same amber as the blast note
                  rather than the red of visError, because the three
                  other guards are final and this one is a question. The server's
                  own sentence is shown verbatim — no client copy of the rule
                  that produced it. */}
              {visStrand && <div style={{marginTop:12,maxWidth:560,fontSize:12,color:"#5a4a20",background:"#FFF8E6",
                                         border:"0.5px solid rgba(184,122,20,0.3)",borderRadius:8,padding:12,lineHeight:1.55}}>
                <div style={{fontWeight:500,marginBottom:6}}>This will be visible to the Owner only — confirm?</div>
                <div style={{marginBottom:9}}>{visStrand.message}</div>
                <div style={{display:"flex",gap:8}}>
                  <button type="button" disabled={visSaving}
                          onClick={confirmStrand}
                          style={Object.assign({},btnPrimary,{opacity:visSaving?0.6:1})}>
                    {visSaving ? "Saving…" : "Yes — Owner only"}
                  </button>
                  <button type="button" onClick={function(){ setVisStrand(null); }}
                          style={{fontSize:12,padding:"8px 14px",border:"0.5px solid rgba(0,0,0,0.15)",background:"#fff",
                                  borderRadius:8,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                </div>
                <div style={{marginTop:7,fontSize:11,color:"#7a6a40"}}>
                  Nothing has been saved yet. Cancel leaves the settings exactly as they are on screen.
                </div>
              </div>}
            </div>
          </div>
        </div>;
      })()}

      {activeTab==="capabilities" && (function(){
        if (capError && !capData) return <div style={{fontSize:12,color:"#c00"}}>{capError}</div>;
        if (!capData || !capMatrixDraft) return <div style={{fontSize:12,color:"#666"}}>Loading…</div>;

        var btnPrimary = {fontSize:12,padding:"8px 16px",border:"0.5px solid rgba(24,95,165,0.3)",background:"#185FA5",color:"#fff",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var caps = capData.capabilities || {};
        // ROWS ARE SCOPED TO THE DEPARTMENT THAT ADMINISTERS THEM (owner ruling,
        // 2026-08-22). Capabilities belong to Operations and HR; a tick under
        // sales/marketing/office is the dormant-grant trap — it changes no holder
        // today and detonates on a future hire or promotion into that rank. See
        // the relevantDepartments note in the server registry.
        //
        // A MISSING declaration renders the row, i.e. today's behaviour. That is
        // the safe direction for the only window where it can happen — a frontend
        // live before the server that feeds it — because the alternative prints
        // "no capabilities" on every tab and makes the screen look broken. It is
        // safe to fail open here ONLY because the build gate refuses a capability
        // that omits the field; absence cannot survive a deploy, so this branch
        // exists for deploy skew alone, never as a resting state.
        var administers = function(key, dept){
          var rel = caps[key] && caps[key].relevantDepartments;
          if (!rel || !rel.length) return true;
          return rel.indexOf(dept) !== -1;
        };
        var capKeys = Object.keys(caps);
        var deptsWithLadders = Object.keys(capData.ladders || {});
        if (deptsWithLadders.indexOf(capDept) === -1 && deptsWithLadders.length) capDept = deptsWithLadders[0];
        // Filtered AFTER capDept is normalised — on first render capDept can still
        // be a stale key from a previous session, and scoping to that would show
        // the wrong department's rows for one frame.
        var visibleCapKeys = capKeys.filter(function(k){ return administers(k, capDept); });
        var ranks = (capData.ladders || {})[capDept] || [];
        var rankNums = ranks.map(function(r){ return r.rank; });
        var maxRank = rankNums.length ? rankNums[rankNums.length-1] : 0;

        // Same threshold columns as Page Visibility, off the same ladder registry.
        // A capability has no parent/child relationship, so applyThresholdToGrants
        // is handed an empty surface table and its tab/page branches never run —
        // the writer is shared, the implication logic simply does not apply here.
        var PRESET_COLS = presetsForDept(capData, capDept).concat([{ key:"none", label:"Nobody", minRank:VIS_NOBODY }]);

        var isChecked = function(rank, key){
          var d = capMatrixDraft.grants[capDept] || {};
          return (d[String(rank)] || []).indexOf(key) !== -1;
        };
        // Same proposal model as Page Visibility — see the long note there. Dormant
        // today (every capability is source:"matrix", so `live` is always true and
        // nothing is ever a proposal), and kept correct for the future capability
        // that wraps a pre-existing rule.
        var beginProposal = function(k){
          setCapRevert(function(prev){
            if (prev && prev.key === k) return prev;
            return { key: k, grants: capMatrixDraft.grants };
          });
          setCapSelected(k);
          setCapTakeover(k);
        };
        var cancelProposal = function(k){
          if (capRevert && capRevert.key === k) {
            setCapMatrixDraft({ grants: capRevert.grants, managed: capMatrixDraft.managed });
          }
          setCapRevert(null);
          setCapTakeover(null);
        };

        var toggle = function(rank, key, on){
          if (caps[key] && !caps[key].matrixMode) beginProposal(key);
          setCapMatrixDraft(function(prev){
            var next = { grants: JSON.parse(JSON.stringify(prev.grants)), managed: Object.assign({}, prev.managed) };
            var d = next.grants[capDept] = next.grants[capDept] || {};
            var list = d[String(rank)] = (d[String(rank)] || []).slice();
            var i = list.indexOf(key);
            if (on && i === -1) list.push(key);
            if (!on && i !== -1) list.splice(i,1);
            return next;
          });
          setCapMsg(""); setCapError("");
        };

        var setThreshold = function(key, minRank){
          setCapMatrixDraft({
            grants: applyThresholdToGrants(capMatrixDraft.grants, capDept, rankNums, key, minRank, {}),
            managed: capMatrixDraft.managed
          });
          setCapMsg(""); setCapError("");
        };

        var save = async function(extraManaged){
          setCapSaving(true); setCapMsg(""); setCapError("");
          try {
            var managed = Object.assign({}, capMatrixDraft.managed, extraManaged || {});
            var res = await apiFetch("/api/settings/capability-matrix","PATCH",
              { grants: capMatrixDraft.grants, managed: managed }, p.token, p.csrfToken);
            if (res && res.matrix) {
              setCapData(function(prev){ return Object.assign({}, prev, { matrix: res.matrix, holders: res.holders }); });
              setCapMatrixDraft({ grants: JSON.parse(JSON.stringify(res.matrix.grants||{})), managed: Object.assign({}, res.matrix.managed||{}) });
            }
            setCapMsg("Saved");
            setCapTakeover(null);
            setCapRevert(null);          // the proposal landed; nothing to withdraw
            loadCapabilities();
          } catch (err) {
            setCapError((err && err.message) || "Save failed");
          } finally { setCapSaving(false); }
        };

        // Computed from the draft grants against the ladders + holders the server
        // sent, so the confirmation shows real names before anything is written.
        var wouldHold = function(key){
          var out = [];
          (capData.roster || []).forEach(function(u){
            var dept = u.department, rank = u.titleRank;
            if (!dept || rank === null || rank === undefined) return;
            var g = (capMatrixDraft.grants[dept] || {})[String(rank)] || [];
            if (g.indexOf(key) !== -1) out.push(u.name);
          });
          return out.sort();
        };

        // ---- THE LIVE EDITOR, APPLIED HERE TOO (owner, 2026-08-23) ---------
        //
        // Same shape as Page Visibility: department chip -> rank dropdown -> the
        // things that rank holds, as switches. What is NOT copied is the sidebar
        // replica — a capability is a VERB and has no menu entry to draw, so the
        // cards are the surface. Borrowing the dark panel here would be drawing a
        // menu for something that never appears in one.
        //
        // ALL FOUR ARE source:"matrix", so there is no take-over step: a switch
        // stages and Save commits, which is what this screen has always done. The
        // take-over branch below stays for the future capability that wraps a
        // pre-existing rule.
        var capRepRank = (function(){
          if (capRank !== null && rankNums.indexOf(capRank) !== -1) return capRank;
          return rankNums.length ? rankNums[0] : null;
        })();
        var capHeadcount = (function(){
          var m = {};
          (capData.roster || []).forEach(function(u){
            if (u.department !== capDept || u.titleRank === null || u.titleRank === undefined) return;
            m[u.titleRank] = (m[u.titleRank] || 0) + 1;
          });
          return m;
        })();
        var capTitleOfRank = function(r){
          return (ranks.filter(function(x){ return x.rank === r; })[0] || {}).title || ("rank " + r);
        };
        // A matrix-native capability resolves to grant membership for the rung —
        // capabilityFor's own rule, minus the admin short-circuit, because this
        // previews a RUNG and not the Owner.
        var capHeldAt = function(key, rank, grants){
          if (!caps[key] || !caps[key].matrixMode) return null;   // resolved elsewhere; no client answer
          var list = ((grants || capMatrixDraft.grants)[capDept] || {})[String(rank)] || [];
          return list.indexOf(key) !== -1;
        };

        // The same band write, from the same module-scope function the Page
        // Visibility toggles use. One ruling, one implementation.
        var capToggle = function(key, on){
          if (capRepRank === null) return;
          var cur = thresholdOfKey(capMatrixDraft.grants, capDept, rankNums, key);
          var next = bandThresholdFor(cur, capRepRank, maxRank, on);
          var simulated = applyThresholdToGrants(capMatrixDraft.grants, capDept, rankNums, key, next, {});
          var gained = [], lost = [];
          rankNums.forEach(function(r){
            var b4 = capHeldAt(key, r, capMatrixDraft.grants);
            var af = capHeldAt(key, r, simulated);
            if (!b4 && af) gained.push(r);
            if (b4 && !af) lost.push(r);
          });
          var peopleOn = function(list){ return list.reduce(function(n,r){ return n + (capHeadcount[r] || 0); }, 0); };
          setCapBlast({ key:key, gained:gained, lost:lost,
                        gainedPeople:peopleOn(gained), lostPeople:peopleOn(lost),
                        wasCustom: cur === null });
          if (!caps[key].matrixMode) beginProposal(key);
          setThreshold(key, next);
          setCapSelected(key);
        };

        // A light-UI switch, same FILLED = on / HOLLOW = off language as the
        // replica's. The colours differ because the ground does; the convention
        // does not, because a reader should not have to learn it twice.
        var capSwitch = function(on, onClick, title){
          return <span onClick={onClick} title={title}
            style={{ width:30, height:17, borderRadius:9, position:"relative", flexShrink:0, boxSizing:"border-box",
                     cursor: onClick ? "pointer" : "default",
                     background: on ? "#185FA5" : "transparent",
                     border: on ? "1px solid #185FA5" : "1px solid rgba(0,0,0,0.28)" }}>
            <span style={{ position:"absolute", top:2, width:11, height:11, borderRadius:"50%",
                           left: on ? "auto" : 2, right: on ? 2 : "auto",
                           background: on ? "#fff" : "rgba(0,0,0,0.32)" }}/>
          </span>;
        };

        var capCard = function(key){
          var c = caps[key];
          var live = c.matrixMode;
          var cur = thresholdOfKey(capMatrixDraft.grants, capDept, rankNums, key);
          var custom = cur === null;
          var held = capHeldAt(key, capRepRank, null);
          var open = capSelected === key;
          var advOpen = !!capAdvOpen[key];
          var holders = (capData.holders || {})[key] || [];
          var draftHolders = wouldHold(key);

          return <div key={key} style={{border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,marginBottom:10,
                                        background: held ? "#F6FBF8" : "#fff", overflow:"hidden"}}>
            <div onClick={function(){ setCapSelected(open ? null : key); }}
                 style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",cursor:"pointer"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500,
                             color: held ? "#1a1a1a" : "#7A828C",
                             textDecoration: held ? "none" : "line-through",
                             textDecorationColor: held ? undefined : "rgba(0,0,0,0.22)",
                             textDecorationThickness: held ? undefined : "1px"}}>{c.label}</div>
                <div style={{fontSize:11,color:"#8A929C",marginTop:2}}>
                  {custom
                    ? "set per rank"
                    : (cur === VIS_NOBODY ? "nobody at any rank" : "rank " + cur + " and above")}
                  {" · "}{holders.length} {holders.length===1?"holder":"holders"}
                </div>
              </div>
              {live
                ? capSwitch(!!held, function(e){ e.stopPropagation(); capToggle(key, !held); },
                            held ? "Take it away from this rank down" : "Give it to this rank and up")
                : <span style={{fontSize:10.5,color:"#9AA3AE"}} title="Resolved outside the matrix — take it over to switch it here.">not yet governed here</span>}
            </div>

            {open && <div style={{borderTop:"0.5px solid rgba(0,0,0,0.08)",padding:"11px 14px",
                                  fontSize:11.5,color:"#666",lineHeight:1.6,background:"#FAFBFC"}}>
              <div><b style={{color:"#444"}}>Holds it now</b> — {holders.length ? holders.map(function(h){return h.name;}).join(", ") : "nobody"}</div>
              <div><b style={{color:"#444"}}>Would hold it</b> — {draftHolders.length ? draftHolders.join(", ") : "nobody"} (plus the Owner, always)</div>
              <div style={{marginTop:9}}>
                <span onClick={function(){ setCapAdvOpen(function(x){ var n=Object.assign({},x); n[key]=!advOpen; return n; }); }}
                      style={{fontSize:11.5,color:"#666",cursor:"pointer",userSelect:"none"}}>
                  {advOpen?"▾":"▸"} Set per rank{custom && <span style={{color:"#6D28D9",marginInlineStart:6}}>· custom</span>}
                </span>
                {advOpen && <div>
                  {custom && <div style={{fontSize:11,color:"#6D28D9",lineHeight:1.45,paddingTop:6}}>
                    No band describes this grant — it does not run from one rank up to the top. The switch above
                    will replace it with a band; set it here to keep it per rank.
                  </div>}
                  <div style={{display:"flex",flexWrap:"wrap",gap:"6px 14px",padding:"8px 0 4px"}}>
                    {ranks.map(function(r){
                      return <label key={r.rank} style={{display:"flex",alignItems:"center",gap:5,fontSize:11.5,color:"#444",cursor:"pointer",minWidth:180}}>
                        <input type="checkbox" checked={isChecked(r.rank,key)} onChange={function(e){toggle(r.rank,key,e.target.checked);}}/>
                        <span style={{fontVariantNumeric:"tabular-nums",color:"#888",minWidth:16}}>{r.rank}</span>
                        <span>{r.title}</span>
                        {r.rank === capRepRank && <span style={{fontSize:10,color:"#185FA5"}}>· editing</span>}
                      </label>;
                    })}
                  </div>
                </div>}
              </div>

              {/* DORMANT BY DESIGN. Every capability shipping today is
                  source:"matrix", so `live` is always true and this never
                  renders. Kept correct for the future capability that wraps a
                  pre-existing rule — the same reason the machinery survived the
                  table's deletion on the Page Visibility side. */}
              {!live && <div style={{marginTop:9}}>
                {capTakeover === key
                  ? <div style={{background:"#FFF8E6",border:"0.5px solid rgba(184,122,20,0.3)",borderRadius:8,padding:12,fontSize:12,color:"#5a4a20"}}>
                      <div style={{fontWeight:500,marginBottom:6}}>Take “{c.label}” over?</div>
                      <div style={{marginBottom:8}}>Would hold: <b>{draftHolders.length ? draftHolders.join(", ") : "nobody"}</b> (plus the Owner, always)</div>
                      <div style={{display:"flex",gap:8}}>
                        <button type="button" disabled={capSaving} onClick={function(){ var m={}; m[key]=true; save(m); }}
                          style={Object.assign({},btnPrimary,{opacity:capSaving?0.6:1})}>Take over</button>
                        <button type="button" onClick={function(){cancelProposal(key);setCapBlast(null);}}
                          style={{padding:"7px 14px",fontSize:12,borderRadius:6,border:"0.5px solid rgba(0,0,0,0.15)",background:"#fff",cursor:"pointer"}}>Cancel</button>
                      </div>
                    </div>
                  : <button type="button" onClick={function(){beginProposal(key);setCapMsg("");setCapError("");}}
                      style={{padding:"6px 12px",fontSize:12,borderRadius:6,border:"0.5px solid rgba(0,0,0,0.15)",background:"#fff",cursor:"pointer"}}>
                      Take over from legacy…
                    </button>}
              </div>}
            </div>}
          </div>;
        };

        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Capabilities</div>
          <div style={{fontSize:12,color:"#666",marginBottom:16,lineHeight:1.5}}>
            Ranks are distinct jobs, so access is set per department rank rather than per role.
            A capability keeps resolving from the <b>Permissions</b> tab until you take it over here —
            setting a threshold alone changes nothing until then. Changes apply in real-time.
          </div>

          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
            {deptsWithLadders.map(function(d){
              var act = d === capDept;
              return <button key={d} type="button" onClick={function(){if(capRevert)cancelProposal(capRevert.key);setCapDept(d);setCapMsg("");setCapError("");}}
                style={{padding:"6px 12px",fontSize:12,borderRadius:6,cursor:"pointer",
                        border:act?"0.5px solid rgba(0,0,0,0.15)":"0.5px solid transparent",
                        background:act?"#fff":"rgba(0,0,0,0.04)",color:act?"#1a1a1a":"#666",
                        textTransform:"capitalize",fontWeight:act?500:400}}>{deptLabel(capData, d)}</button>;
            })}
          </div>

          {visibleCapKeys.length === 0
            ? <div style={{fontSize:12,color:"#666",padding:"14px 0",lineHeight:1.5}}>
                No capabilities are administered by this department.
              </div>
            : <div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,flexWrap:"wrap"}}>
                  <span style={{fontSize:11,color:"#666"}}>Editing</span>
                  <select value={capRepRank === null ? "" : String(capRepRank)}
                          onChange={function(e){
                            if(capRevert)cancelProposal(capRevert.key);
                            setCapBlast(null); setCapSelected(null);
                            setCapRank(e.target.value === "" ? null : Number(e.target.value));
                          }}
                          style={{fontSize:12,padding:"5px 8px",border:"0.5px solid rgba(0,0,0,0.15)",borderRadius:6,
                                  background:"#fff",fontFamily:"inherit",color:"#1a1a1a",fontWeight:500}}>
                    {ranks.map(function(r){
                      var n = capHeadcount[r.rank] || 0;
                      return <option key={r.rank} value={String(r.rank)}>
                        {r.title} · rank {r.rank} · {n ? (n + (n===1?" person":" people")) : "nobody"}
                      </option>;
                    })}
                  </select>
                  <span style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:11,color:"#888",marginInlineStart:6}}>
                    {capSwitch(true, null, null)} holds it
                    <span style={{display:"inline-flex",alignItems:"center",gap:6,marginInlineStart:8}}>
                      {capSwitch(false, null, null)} does not
                    </span>
                  </span>
                </div>

                {capBlast && (function(){
                  var b = capBlast;
                  var phrase = function(list, people, verb){
                    if (!list.length) return null;
                    var lo = list[0], hi = list[list.length-1];
                    return verb + " " + (list.length === 1
                        ? capTitleOfRank(lo) + " (rank " + lo + ")"
                        : "ranks " + lo + "–" + hi + " (" + capTitleOfRank(lo) + " → " + capTitleOfRank(hi) + ")")
                      + " · " + (people ? people + (people===1?" person":" people") : "nobody today");
                  };
                  if (!b.lost.length && !b.gained.length) {
                    return <div style={{fontSize:11.5,color:"#666",background:"#F7F8FA",border:"0.5px solid rgba(0,0,0,0.08)",
                                        borderRadius:8,padding:"9px 11px",marginBottom:12,lineHeight:1.5}}>
                      No rank in {deptLabel(capData, capDept)} changes — {caps[b.key] ? caps[b.key].label : b.key} already resolves that way for every rung here.
                    </div>;
                  }
                  return <div style={{fontSize:11.5,color:"#5a4a20",background:"#FFF8E6",border:"0.5px solid rgba(184,122,20,0.3)",
                                      borderRadius:8,padding:"9px 11px",marginBottom:12,lineHeight:1.55}}>
                    <b>{caps[b.key] ? caps[b.key].label : b.key}</b>
                    {b.lost.length   ? <div>{phrase(b.lost,   b.lostPeople,   "will be taken from")}</div> : null}
                    {b.gained.length ? <div>{phrase(b.gained, b.gainedPeople, "will be given to")}</div> : null}
                    {b.wasCustom && <div style={{marginTop:4,color:"#7a6a40"}}>
                      This replaced a per-rank setting that no band described.
                    </div>}
                    <div style={{marginTop:4,color:"#7a6a40"}}>…once you press Save. Nothing has changed yet.</div>
                  </div>;
                })()}

                {visibleCapKeys.map(function(key){ return capCard(key); })}
              </div>}

          <div style={{display:"flex",gap:8,marginTop:16,flexWrap:"wrap",alignItems:"center"}}>
            <button type="button" onClick={function(){save();}} disabled={capSaving}
              style={Object.assign({},btnPrimary,{opacity:capSaving?0.6:1,cursor:capSaving?"not-allowed":"pointer"})}>
              {capSaving ? "Saving…" : "Save grants"}
            </button>
            {capMsg && <span style={{fontSize:12,color:"#0F6E56"}}>{capMsg}</span>}
            {capError && <span style={{fontSize:12,color:"#c00"}}>{capError}</span>}
          </div>
        </div>;
      })()}

      {activeTab==="workShifts" && (function(){
        var fieldLabel = {fontSize:11,color:"#666",display:"block",marginBottom:4};
        var inputStyle = {padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",fontFamily:"inherit",width:"100%",boxSizing:"border-box"};
        var btnPrimary = {fontSize:12,padding:"8px 16px",border:"0.5px solid rgba(24,95,165,0.3)",background:"#185FA5",color:"#fff",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};

        var updateShift = function(role, patch){
          setRoleShifts(function(prev){
            var next = Object.assign({}, prev);
            next[role] = Object.assign({}, prev[role], patch);
            return next;
          });
        };
        var updateTier = function(role, idx, patch){
          setRoleShifts(function(prev){
            var next = Object.assign({}, prev);
            var r = Object.assign({}, prev[role]);
            r.tiers = prev[role].tiers.map(function(t,i){ return i===idx ? Object.assign({},t,patch) : t; });
            next[role] = r;
            return next;
          });
        };

        // Client-side validation mirroring the backend (server stays the source
        // of truth). Returns an error string or null for one role's editable shift.
        var isTime = function(v){ return /^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(String(v||"")); };
        var isPosInt = function(v){ var n=Number(v); return v!=="" && isFinite(n) && n>0 && Math.floor(n)===n; };
        var isFrac = function(v){ var n=Number(v); return v!=="" && isFinite(n) && n>=0 && n<=1; };
        var validateRole = function(e){
          if (!e) return "missing";
          if (!isTime(e.startTime)) return "Start time must be HH:MM";
          if (!isTime(e.endTime))   return "End time must be HH:MM";
          var g = Number(e.graceMinutes);
          if (e.graceMinutes==="" || !isFinite(g) || g<0 || g>1440 || Math.floor(g)!==g) return "Grace must be an integer 0–1440";
          if (!isPosInt(e.tiers[0].maxLateMinutes)) return "Tier 1 minutes must be a positive integer";
          if (!isPosInt(e.tiers[1].maxLateMinutes)) return "Tier 2 minutes must be a positive integer";
          if (!(Number(e.tiers[0].maxLateMinutes) < Number(e.tiers[1].maxLateMinutes))) return "Tier 1 minutes must be less than Tier 2";
          for (var i=0;i<3;i++){ if (!isFrac(e.tiers[i].deductionFraction)) return "Tier "+(i+1)+" deduction must be 0–1"; }
          return null;
        };
        var fracHint = function(v){
          var n = Number(v);
          if (v==="" || !isFinite(n)) return "";
          if (n===0)    return "no deduction";
          if (n===0.25) return "¼ day";
          if (n===0.5)  return "½ day";
          if (n===0.75) return "¾ day";
          if (n===1)    return "full day";
          if (n>0 && n<1) return Math.round(n*100)+"% of a day";
          return "";
        };

        var errors = {};
        var hasErrors = false;
        SHIFT_ROLES.forEach(function(sr){
          var er = validateRole(roleShifts && roleShifts[sr.role]);
          if (er){ errors[sr.role] = er; hasErrors = true; }
        });

        var doSaveShifts = async function(){
          setAttError(""); setAttMsg("");
          var bad = null;
          SHIFT_ROLES.forEach(function(sr){ if (!bad){ var er = validateRole(roleShifts[sr.role]); if (er) bad = sr.label + ": " + er; } });
          if (bad){ setAttError(bad); return; }
          setAttSaving(true);
          try {
            var payloadShifts = {};
            SHIFT_ROLES.forEach(function(sr){
              var e = roleShifts[sr.role];
              payloadShifts[sr.role] = {
                startTime: e.startTime,
                endTime: e.endTime,
                graceMinutes: Number(e.graceMinutes),
                tiers: [
                  { maxLateMinutes: Number(e.tiers[0].maxLateMinutes), deductionFraction: Number(e.tiers[0].deductionFraction) },
                  { maxLateMinutes: Number(e.tiers[1].maxLateMinutes), deductionFraction: Number(e.tiers[1].deductionFraction) },
                  { maxLateMinutes: null,                              deductionFraction: Number(e.tiers[2].deductionFraction) }
                ]
              };
            });
            await apiFetch("/api/settings/role-shifts","PATCH",{ roleShifts: payloadShifts },p.token,p.csrfToken);
            // Refetch the GET to reflect stored state — saveAttendanceSettings'
            // broadcast doesn't carry roleShifts, so we don't rely on the socket.
            var fresh = await apiFetch("/api/settings/attendance","GET",null,p.token);
            if (fresh && fresh.roleShifts) setRoleShifts(shiftsToEditable(fresh.roleShifts));
            setAttMsg("Work shifts saved");
          } catch (err) {
            setAttError((err && err.message) || "Save failed");
          } finally {
            setAttSaving(false);
          }
        };

        var renderTier = function(role, idx, tier){
          var fixed = (idx === 2); // 3rd tier = "and above" (maxLateMinutes null, not editable)
          return <div key={idx} className="shift-tier-row" style={{display:"grid",gridTemplateColumns:"70px 1fr 90px 1.4fr",gap:10,alignItems:"center",padding:"6px 0"}}>
            <div style={{fontSize:12,color:"#1a1a1a",fontWeight:500}}>Tier {idx+1}</div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              {fixed
                ? <span style={{fontSize:12,color:"#666"}}>and above</span>
                : <span style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:11,color:"#666"}}>up to</span>
                    <input type="number" min="1" step="1" value={tier.maxLateMinutes} onChange={function(ev){updateTier(role,idx,{maxLateMinutes:ev.target.value});}} style={Object.assign({},inputStyle,{width:72})}/>
                    <span style={{fontSize:11,color:"#666"}}>min late</span>
                  </span>}
            </div>
            <div style={{fontSize:11,color:"#666",textAlign:"right"}}>→ deduct</div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <input type="number" min="0" max="1" step="0.05" value={tier.deductionFraction} onChange={function(ev){updateTier(role,idx,{deductionFraction:ev.target.value});}} style={Object.assign({},inputStyle,{width:80})}/>
              <span style={{fontSize:11,color:"#0F6E56"}}>{fracHint(tier.deductionFraction)}</span>
            </div>
          </div>;
        };

        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          {/* Phone/tablet rules for this tab, `.shift-*`. The late-tier row is
              a four-column grid, `70px 1fr 90px 1.4fr`; at 299px the two fr
              columns got ~45 and ~63px, so "up to [n] min late" and the
              deduction input were unreadable. It goes 2x2 under 900 -- tier
              label beside the late range, "deduct" beside its fraction --
              and the "deduct" label drops its right alignment so it does not
              butt against the input it now precedes. The three <input
              type=time> fields sat in a 1fr x3 grid, ~86px each at 299 when a
              time control needs ~110, so they stack under 768. */}
          <style>{""
+ "@media (max-width: 900px) {"
+ "  .shift-tier-row { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }"
+ "  .shift-tier-row > :nth-child(3) { text-align: left !important; }"
+ "}"
+ "@media (max-width: 768px) {"
+ "  .shift-times { grid-template-columns: minmax(0,1fr) !important; }"
+ "}"
}</style>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Work Shifts</div>
          <div style={{fontSize:12,color:"#666",marginBottom:18}}>Per-role attendance shift: start/end time, grace period, and 3 late tiers. <b>Grace</b> = minutes after shift start with no deduction. Each tier's deduction is a <b>fraction of one day's salary</b> (e.g. 0.25 = ¼ day). Affects future check-ins only — already-recorded days keep their saved deduction. Separate from rotation working hours.</div>

          {!attLoaded ? <div style={{fontSize:12,color:"#666"}}>Loading…</div>
            : !roleShifts ? <div style={{fontSize:12,color:"#A32D2D"}}>Could not load work shifts.</div>
            : <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {SHIFT_ROLES.map(function(sr){
                var e = roleShifts[sr.role];
                if (!e) return null;
                var err = errors[sr.role];
                return <div key={sr.role} style={{padding:"14px 16px",border:"0.5px solid "+(err?"rgba(163,45,45,0.4)":"rgba(0,0,0,0.1)"),borderRadius:10,background:"#fff"}}>
                  <div style={{fontSize:13,fontWeight:600,color:"#1a1a1a",marginBottom:12}}>{sr.label}</div>
                  <div className="shift-times" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
                    <div>
                      <label style={fieldLabel}>Shift start</label>
                      <input type="time" value={e.startTime} onChange={function(ev){updateShift(sr.role,{startTime:ev.target.value});}} style={inputStyle}/>
                    </div>
                    <div>
                      <label style={fieldLabel}>Shift end</label>
                      <input type="time" value={e.endTime} onChange={function(ev){updateShift(sr.role,{endTime:ev.target.value});}} style={inputStyle}/>
                    </div>
                    <div>
                      <label style={fieldLabel}>Grace (min)</label>
                      <input type="number" min="0" max="1440" step="1" value={e.graceMinutes} onChange={function(ev){updateShift(sr.role,{graceMinutes:ev.target.value});}} style={inputStyle}/>
                    </div>
                  </div>
                  <div style={{borderTop:"0.5px solid rgba(0,0,0,0.06)",paddingTop:8}}>
                    <div style={{fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px",marginBottom:2}}>Late tiers</div>
                    {e.tiers.map(function(tier, idx){ return renderTier(sr.role, idx, tier); })}
                  </div>
                  {err && <div style={{fontSize:11,color:"#A32D2D",marginTop:8}}>{err}</div>}
                </div>;
              })}
            </div>}

          <div style={{display:"flex",gap:8,marginTop:18,flexWrap:"wrap",alignItems:"center"}}>
            <button type="button" onClick={doSaveShifts} disabled={attSaving||!attLoaded||!roleShifts||hasErrors} style={Object.assign({},btnPrimary,{opacity:(attSaving||hasErrors)?0.6:1,cursor:(attSaving||hasErrors)?"not-allowed":"pointer"})}>
              {attSaving?"Saving…":"Save work shifts"}
            </button>
            {hasErrors && !!roleShifts && <span style={{fontSize:12,color:"#A32D2D"}}>Fix the highlighted role(s) before saving.</span>}
            {attMsg   && <span style={{fontSize:12,color:"#0F6E56",background:"#EAF6F0",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{attMsg}</span>}
            {attError && <span style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{attError}</span>}
          </div>
        </div>;
      })()}

      {activeTab==="branches" && (function(){
        var inputStyle = {padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",fontFamily:"inherit",width:"100%",boxSizing:"border-box"};
        var fieldLabel = {fontSize:12,color:"#666",display:"block",marginBottom:6};
        var btnPrimary = {fontSize:12,padding:"8px 16px",border:"0.5px solid rgba(24,95,165,0.3)",background:"#185FA5",color:"#fff",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var btnGhost = {fontSize:12,padding:"8px 14px",border:"0.5px solid rgba(0,0,0,0.1)",background:"#fff",color:"#1a1a1a",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var resetForm = function(){ setBranchForm({id:null,name:"",code:"",address:"",isActive:true}); setBranchError(""); };
        var startEdit = function(b){
          setBranchForm({ id:b._id, name:b.name||"", code:b.code||"", address:b.address||"", isActive:b.isActive!==false });
          setBranchError(""); setBranchMsg("");
        };
        var save = async function(){
          setBranchError(""); setBranchMsg("");
          var nm = (branchForm.name||"").trim();
          var cd = (branchForm.code||"").trim().toUpperCase();
          if (!nm) { setBranchError("Name is required"); return; }
          if (!cd) { setBranchError("Code is required"); return; }
          setBranchSaving(true);
          try {
            var payload = { name:nm, code:cd, address:(branchForm.address||"").trim(), isActive:branchForm.isActive };
            var saved;
            if (branchForm.id) {
              saved = await apiFetch("/api/branches/"+branchForm.id, "PATCH", payload, p.token, p.csrfToken);
              setBranches(branches.map(function(b){ return String(b._id)===String(saved._id) ? saved : b; }));
              setBranchMsg("Branch updated");
            } else {
              saved = await apiFetch("/api/branches", "POST", payload, p.token, p.csrfToken);
              setBranches([saved].concat(branches));
              setBranchMsg("Branch created");
            }
            resetForm();
            setTimeout(function(){ setBranchMsg(""); }, 2500);
          } catch (err) {
            setBranchError((err && err.message) || "Save failed");
          } finally {
            setBranchSaving(false);
          }
        };
        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          {/* Mobile rules for the Branches tab — same .at-* convention as
              AssetTrackerPage so the namespace stays consistent. */}
          <style>{""
+ "@media (max-width: 768px) {"
+ "  .at-branch-form-grid { grid-template-columns: 1fr !important; gap: 10px !important; }"
+ "  .at-branch-list { overflow-x: auto; -webkit-overflow-scrolling: touch; }"
+ "  .at-branch-list > div { min-width: 520px; }"
+ "}"
}</style>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Branches</div>
          <div style={{fontSize:12,color:"#666",marginBottom:18}}>Office branches used by AssetTracker. Code is used inside asset codes — short, uppercase, three or four letters works best.</div>

          {/* Add / edit form */}
          <div style={{background:"#F7F7F5",border:"0.5px solid rgba(0,0,0,0.08)",borderRadius:10,padding:14,marginBottom:18}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:10}}>{branchForm.id ? "Edit branch" : "Add branch"}</div>
            <div className="at-branch-form-grid" style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12,marginBottom:10}}>
              <div>
                <label style={fieldLabel}>Name</label>
                <input style={inputStyle} value={branchForm.name} onChange={function(e){setBranchForm(Object.assign({},branchForm,{name:e.target.value}));}} placeholder="New Cairo"/>
              </div>
              <div>
                <label style={fieldLabel}>Code (uppercase)</label>
                <input style={inputStyle} value={branchForm.code} maxLength={6} onChange={function(e){setBranchForm(Object.assign({},branchForm,{code:e.target.value.toUpperCase()}));}} placeholder="TGM"/>
              </div>
            </div>
            <div style={{marginBottom:10}}>
              <label style={fieldLabel}>Address (optional)</label>
              <input style={inputStyle} value={branchForm.address} onChange={function(e){setBranchForm(Object.assign({},branchForm,{address:e.target.value}));}} placeholder=""/>
            </div>
            <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"#1a1a1a",marginBottom:14,cursor:"pointer"}}>
              <input type="checkbox" checked={branchForm.isActive} onChange={function(e){setBranchForm(Object.assign({},branchForm,{isActive:e.target.checked}));}} style={{width:14,height:14,cursor:"pointer"}}/>
              Active
            </label>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <button type="button" onClick={save} disabled={branchSaving} style={Object.assign({},btnPrimary,{opacity:branchSaving?0.6:1,cursor:branchSaving?"not-allowed":"pointer"})}>
                {branchSaving ? "Saving…" : (branchForm.id ? "Save changes" : "Add branch")}
              </button>
              {branchForm.id && <button type="button" onClick={resetForm} style={btnGhost}>Cancel</button>}
              {branchMsg   && <span style={{fontSize:12,color:"#0F6E56",background:"#EAF6F0",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{branchMsg}</span>}
              {branchError && <span style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{branchError}</span>}
            </div>
          </div>

          {/* List */}
          {!branchesLoaded ? <div style={{fontSize:12,color:"#666"}}>Loading…</div> : (
            branches.length === 0
              ? <div style={{fontSize:12,color:"#666",padding:"12px 0"}}>No branches yet.</div>
              : <div className="at-branch-list">
                  <div style={{display:"grid",gridTemplateColumns:"1.5fr 90px 1.5fr 80px 80px",gap:10,padding:"10px 0",borderBottom:"0.5px solid rgba(0,0,0,0.1)",fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px"}}>
                    <div>Name</div>
                    <div>Code</div>
                    <div>Address</div>
                    <div style={{textAlign:"center"}}>Status</div>
                    <div style={{textAlign:"right"}}>Action</div>
                  </div>
                  {branches.map(function(b){
                    return <div key={b._id} style={{display:"grid",gridTemplateColumns:"1.5fr 90px 1.5fr 80px 80px",gap:10,padding:"12px 0",borderBottom:"0.5px solid rgba(0,0,0,0.06)",alignItems:"center",fontSize:13}}>
                      <div style={{fontWeight:500,color:"#1a1a1a"}}>{b.name}</div>
                      <div style={{fontFamily:"ui-monospace, SFMono-Regular, monospace",fontSize:12,color:"#444"}}>{b.code}</div>
                      <div style={{color:"#666"}}>{b.address || "—"}</div>
                      <div style={{textAlign:"center"}}>
                        <span style={{fontSize:11,padding:"3px 8px",borderRadius:6,fontWeight:500,background:b.isActive!==false?"#EAF6F0":"#F4F4F4",color:b.isActive!==false?"#0F6E56":"#888"}}>
                          {b.isActive!==false ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <button type="button" onClick={function(){startEdit(b);}} style={Object.assign({},btnGhost,{padding:"4px 10px"})}>Edit</button>
                      </div>
                    </div>;
                  })}
                </div>
          )}
        </div>;
      })()}

      {activeTab==="closingCompanies" && (function(){
        var inputStyle = {padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",fontFamily:"inherit",width:"100%",boxSizing:"border-box"};
        var btnPrimary = {fontSize:12,padding:"8px 16px",border:"0.5px solid rgba(24,95,165,0.3)",background:"#185FA5",color:"#fff",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var btnGhost = {fontSize:12,padding:"8px 14px",border:"0.5px solid rgba(0,0,0,0.1)",background:"#fff",color:"#1a1a1a",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var btnDanger = {fontSize:12,padding:"4px 10px",border:"0.5px solid rgba(163,45,45,0.3)",background:"#fff",color:"#A32D2D",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var sortCC = function(list){ return list.slice().sort(function(a,b){ return (b.isDefault?1:0)-(a.isDefault?1:0) || String(a.name||"").localeCompare(String(b.name||"")); }); };
        var addCompany = async function(){
          setCcError(""); setCcMsg("");
          var nm = (ccName||"").trim();
          if (!nm) { setCcError("Name is required"); return; }
          setCcSaving(true);
          try {
            var saved = await apiFetch("/api/closing-companies","POST",{name:nm},p.token,p.csrfToken);
            setClosingCompanies(sortCC([saved].concat(closingCompanies)));
            setCcName(""); setCcMsg("Company added");
            setTimeout(function(){ setCcMsg(""); }, 2500);
          } catch(err){ setCcError((err&&err.message)||"Add failed"); }
          finally { setCcSaving(false); }
        };
        var startEdit = function(c){ setCcEditId(c._id); setCcEditName(c.name||""); setCcError(""); setCcMsg(""); };
        var cancelEdit = function(){ setCcEditId(null); setCcEditName(""); };
        var saveEdit = async function(c){
          setCcError(""); setCcMsg("");
          var nm = (ccEditName||"").trim();
          if (!nm) { setCcError("Name cannot be empty"); return; }
          setCcSaving(true);
          try {
            var saved = await apiFetch("/api/closing-companies/"+c._id,"PATCH",{name:nm},p.token,p.csrfToken);
            setClosingCompanies(sortCC(closingCompanies.map(function(x){ return String(x._id)===String(saved._id)?saved:x; })));
            cancelEdit(); setCcMsg("Company renamed");
            setTimeout(function(){ setCcMsg(""); }, 2500);
          } catch(err){ setCcError((err&&err.message)||"Rename failed"); }
          finally { setCcSaving(false); }
        };
        var removeCompany = async function(c){
          setCcError(""); setCcMsg("");
          if (!window.confirm("Delete closing company \""+(c.name||"")+"\"? This cannot be undone.")) return;
          setCcSaving(true);
          try {
            await apiFetch("/api/closing-companies/"+c._id,"DELETE",null,p.token,p.csrfToken);
            setClosingCompanies(closingCompanies.filter(function(x){ return String(x._id)!==String(c._id); }));
            setCcMsg("Company deleted");
            setTimeout(function(){ setCcMsg(""); }, 2500);
          } catch(err){
            var m = (err&&err.message)||"Delete failed";
            if (m === "in_use") m = "Cannot delete — this company is used by one or more deals. Reassign them to another company first.";
            setCcError(m);
          }
          finally { setCcSaving(false); }
        };
        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          {/* Phone rules for this tab, `.cc-*`. Rows are `1fr 200px` -- name,
              then a right-aligned actions cell. At 299px the fixed 200 left
              the name ~89px, so a company name wrapped to three or four
              lines beside the buttons. Under 768 the row is one column: name
              above, actions beneath and left-aligned to it. The header's
              "Action" label is hidden there -- it is no longer a column. At
              768 the name has 242px and needs nothing. Developers is the
              same shape and gets the same rule under `.dev-*`. */}
          <style>{""
+ "@media (max-width: 768px) {"
+ "  .cc-row { grid-template-columns: minmax(0,1fr) !important; }"
+ "  .cc-row > :nth-child(2) { justify-content: flex-start !important; text-align: left !important; }"
+ "  .cc-head > :nth-child(2) { display: none; }"
+ "}"
}</style>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Closing Companies</div>
          <div style={{fontSize:12,color:"#666",marginBottom:18}}>Companies a deal can be closed under — ARO directly, or a partner company for developers ARO has no direct contract with. This is purely a label on deals; it has no effect on commission, tax, or payouts. ARO is the default and cannot be renamed or deleted.</div>

          {/* Add form */}
          <div style={{background:"#F7F7F5",border:"0.5px solid rgba(0,0,0,0.08)",borderRadius:10,padding:14,marginBottom:18}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:10}}>Add company</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <div style={{flex:"1 1 240px",minWidth:200}}>
                <input style={inputStyle} value={ccName} onChange={function(e){setCcName(e.target.value);}} placeholder="e.g. Partner Co."
                  onKeyDown={function(e){ if(e.key==="Enter"){ e.preventDefault(); addCompany(); } }}/>
              </div>
              <button type="button" onClick={addCompany} disabled={ccSaving} style={Object.assign({},btnPrimary,{opacity:ccSaving?0.6:1,cursor:ccSaving?"not-allowed":"pointer"})}>
                {ccSaving ? "Saving…" : "Add company"}
              </button>
              {ccMsg   && <span style={{fontSize:12,color:"#0F6E56",background:"#EAF6F0",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{ccMsg}</span>}
              {ccError && <span style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{ccError}</span>}
            </div>
          </div>

          {/* List */}
          {!ccLoaded ? <div style={{fontSize:12,color:"#666"}}>Loading…</div> : (
            closingCompanies.length === 0
              ? <div style={{fontSize:12,color:"#666",padding:"12px 0"}}>No closing companies yet.</div>
              : <div>
                  <div className="cc-row cc-head" style={{display:"grid",gridTemplateColumns:"1fr 200px",gap:10,padding:"10px 0",borderBottom:"0.5px solid rgba(0,0,0,0.1)",fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px"}}>
                    <div>Name</div>
                    <div style={{textAlign:"right"}}>Action</div>
                  </div>
                  {closingCompanies.map(function(c){
                    var editing = String(ccEditId||"")===String(c._id);
                    return <div key={c._id} className="cc-row" style={{display:"grid",gridTemplateColumns:"1fr 200px",gap:10,padding:"12px 0",borderBottom:"0.5px solid rgba(0,0,0,0.06)",alignItems:"center",fontSize:13}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        {editing
                          ? <input style={Object.assign({},inputStyle,{maxWidth:280})} value={ccEditName} onChange={function(e){setCcEditName(e.target.value);}}
                              onKeyDown={function(e){ if(e.key==="Enter"){ e.preventDefault(); saveEdit(c); } if(e.key==="Escape"){ cancelEdit(); } }} autoFocus/>
                          : <span style={{fontWeight:500,color:"#1a1a1a"}}>{c.name}</span>}
                        {c.isDefault && <span style={{fontSize:11,padding:"3px 8px",borderRadius:6,fontWeight:500,background:"#EEF2F7",color:"#185FA5"}}>Default</span>}
                      </div>
                      <div style={{textAlign:"right",display:"flex",gap:6,justifyContent:"flex-end",alignItems:"center"}}>
                        {c.isDefault && <span title="Default — cannot be renamed or deleted" style={{fontSize:12,color:"#999"}}>Locked</span>}
                        {!c.isDefault && editing && <button type="button" onClick={function(){saveEdit(c);}} disabled={ccSaving} style={Object.assign({},btnPrimary,{padding:"4px 12px",opacity:ccSaving?0.6:1})}>Save</button>}
                        {!c.isDefault && editing && <button type="button" onClick={cancelEdit} style={Object.assign({},btnGhost,{padding:"4px 10px"})}>Cancel</button>}
                        {!c.isDefault && !editing && <button type="button" onClick={function(){startEdit(c);}} style={Object.assign({},btnGhost,{padding:"4px 10px"})}>Edit</button>}
                        {!c.isDefault && !editing && <button type="button" onClick={function(){removeCompany(c);}} disabled={ccSaving} style={btnDanger}>Delete</button>}
                      </div>
                    </div>;
                  })}
                </div>
          )}
        </div>;
      })()}

      {activeTab==="developers" && (function(){
        var inputStyle = {padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",fontFamily:"inherit",width:"100%",boxSizing:"border-box"};
        var btnPrimary = {fontSize:12,padding:"8px 16px",border:"0.5px solid rgba(24,95,165,0.3)",background:"#185FA5",color:"#fff",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var btnGhost = {fontSize:12,padding:"8px 14px",border:"0.5px solid rgba(0,0,0,0.1)",background:"#fff",color:"#1a1a1a",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var btnDanger = {fontSize:12,padding:"4px 10px",border:"0.5px solid rgba(163,45,45,0.3)",background:"#fff",color:"#A32D2D",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var sortDev = function(list){ return list.slice().sort(function(a,b){ return String(a.name||"").localeCompare(String(b.name||"")); }); };
        // Search / filter. 505 rows, and the reason to open this tab is usually
        // to find out whether a developer already exists under SOME spelling
        // before adding it — a question scrolling cannot answer. The rows and
        // the count both come from filterDevelopersFE, which folds case and
        // spacing exactly the way POST /api/developers' dedupe does, so a name
        // this box reports as absent is a name the server will accept.
        var devQ = (devSearch||"").trim();
        var shownDevelopers = filterDevelopersFE(developers, devSearch);
        var addDeveloper = async function(){
          setDevError(""); setDevMsg("");
          var nm = (devName||"").trim();
          if (!nm) { setDevError("Name is required"); return; }
          setDevSaving(true);
          try {
            var saved = await apiFetch("/api/developers","POST",{name:nm},p.token,p.csrfToken);
            setDevelopers(sortDev([saved].concat(developers)));
            // Clear the filter too: "Developer added" over a list that does not
            // contain the new row (because the old query does not match it)
            // reads as a failed save.
            setDevName(""); setDevSearch(""); setDevMsg("Developer added");
            setTimeout(function(){ setDevMsg(""); }, 2500);
          } catch(err){ setDevError((err&&err.message)||"Add failed"); }
          finally { setDevSaving(false); }
        };
        var startEdit = function(c){ setDevEditId(c._id); setDevEditName(c.name||""); setDevError(""); setDevMsg(""); };
        var cancelEdit = function(){ setDevEditId(null); setDevEditName(""); };
        var saveEdit = async function(c){
          setDevError(""); setDevMsg("");
          var nm = (devEditName||"").trim();
          if (!nm) { setDevError("Name cannot be empty"); return; }
          setDevSaving(true);
          try {
            var saved = await apiFetch("/api/developers/"+c._id,"PATCH",{name:nm},p.token,p.csrfToken);
            setDevelopers(sortDev(developers.map(function(x){ return String(x._id)===String(saved._id)?saved:x; })));
            cancelEdit(); setDevMsg("Developer renamed");
            setTimeout(function(){ setDevMsg(""); }, 2500);
          } catch(err){ setDevError((err&&err.message)||"Rename failed"); }
          finally { setDevSaving(false); }
        };
        var removeDeveloper = async function(c){
          setDevError(""); setDevMsg("");
          if (!window.confirm("Delete developer \""+(c.name||"")+"\"? This cannot be undone.")) return;
          setDevSaving(true);
          try {
            await apiFetch("/api/developers/"+c._id,"DELETE",null,p.token,p.csrfToken);
            setDevelopers(developers.filter(function(x){ return String(x._id)!==String(c._id); }));
            setDevMsg("Developer deleted");
            setTimeout(function(){ setDevMsg(""); }, 2500);
          } catch(err){
            // On a 409 the backend puts the friendly "X deal(s) are linked…" text
            // in err.message (apiFetch surfaces only `error`); show it verbatim.
            setDevError((err&&err.message)||"Delete failed");
          }
          finally { setDevSaving(false); }
        };
        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          {/* Phone rules for this tab, `.dev-*`. Same shape and same fix as
              Closing Companies, under its own prefix so each tab reverts on
              its own. Rows are `1fr 200px`; at 299px the fixed 200 left the
              name ~89px. Under 768 the row is one column -- name above,
              actions beneath and left-aligned -- and the header's "Action"
              label is hidden because it is no longer a column. */}
          <style>{""
+ "@media (max-width: 768px) {"
+ "  .dev-row { grid-template-columns: minmax(0,1fr) !important; }"
+ "  .dev-row > :nth-child(2) { justify-content: flex-start !important; text-align: left !important; }"
+ "  .dev-head > :nth-child(2) { display: none; }"
+ "}"
}</style>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Developers</div>
          <div style={{fontSize:12,color:"#666",marginBottom:18}}>Real-estate developers a deal can belong to (e.g. El Masria Group, MNHD). This is purely a label on deals; it has no effect on commission, tax, or payouts. Names are matched case- and spacing-insensitively, so the same developer can't be added twice under different spellings.</div>

          {/* Add form */}
          <div style={{background:"#F7F7F5",border:"0.5px solid rgba(0,0,0,0.08)",borderRadius:10,padding:14,marginBottom:18}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:10}}>Add developer</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <div style={{flex:"1 1 240px",minWidth:200}}>
                <input style={inputStyle} value={devName} onChange={function(e){setDevName(e.target.value);}} placeholder="e.g. El Masria Group"
                  onKeyDown={function(e){ if(e.key==="Enter"){ e.preventDefault(); addDeveloper(); } }}/>
              </div>
              <button type="button" onClick={addDeveloper} disabled={devSaving} style={Object.assign({},btnPrimary,{opacity:devSaving?0.6:1,cursor:devSaving?"not-allowed":"pointer"})}>
                {devSaving ? "Saving…" : "Add developer"}
              </button>
              {devMsg   && <span style={{fontSize:12,color:"#0F6E56",background:"#EAF6F0",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{devMsg}</span>}
              {devError && <span style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{devError}</span>}
            </div>
          </div>

          {/* Search — above the LIST, not inside the Add card: it answers a
              question about what is already there. Escape and the ✕ both clear
              it, because a 505-row list left under a stale filter reads as a
              3-row list. Hidden until there is a list to search. */}
          {devLoaded && developers.length > 0 && <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",marginBottom:10}}>
            <div style={{flex:"1 1 260px",minWidth:200,position:"relative"}}>
              <input style={Object.assign({},inputStyle,{paddingRight:28})} value={devSearch}
                onChange={function(e){setDevSearch(e.target.value);}}
                onKeyDown={function(e){ if(e.key==="Escape"){ e.preventDefault(); setDevSearch(""); } }}
                placeholder="🔍 Search developers by name…"/>
              {devQ && <button type="button" onClick={function(){setDevSearch("");}} title="Clear search (Esc)"
                style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",border:"none",background:"transparent",cursor:"pointer",fontSize:13,color:"#666",lineHeight:1,padding:"2px 4px",fontFamily:"inherit"}}>✕</button>}
            </div>
            <span style={{fontSize:12,fontWeight:500,color:(devQ && shownDevelopers.length===0)?"#A32D2D":"#666"}}>
              {devQ ? (shownDevelopers.length + " of " + developers.length + " match") : (developers.length + " developers")}
            </span>
          </div>}

          {/* List */}
          {!devLoaded ? <div style={{fontSize:12,color:"#666"}}>Loading…</div> : (
            developers.length === 0
              ? <div style={{fontSize:12,color:"#666",padding:"12px 0"}}>No developers yet.</div>
              : <div>
                  <div className="dev-row dev-head" style={{display:"grid",gridTemplateColumns:"1fr 200px",gap:10,padding:"10px 0",borderBottom:"0.5px solid rgba(0,0,0,0.1)",fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px"}}>
                    <div>Name</div>
                    <div style={{textAlign:"right"}}>Action</div>
                  </div>
                  {/* "under any spelling" is a claim this box can make ONLY
                      because its fold is the dedupe's fold — see
                      filterDevelopersFE. If the two ever drift this sentence
                      starts lying, which is what verify-developer-search.js
                      runs both functions to prevent. */}
                  {shownDevelopers.length === 0 && <div style={{fontSize:12,color:"#666",padding:"14px 0"}}>
                    No developer matches "{devQ}" — under any spelling. It can safely be added above.
                  </div>}
                  {shownDevelopers.map(function(c){
                    var editing = String(devEditId||"")===String(c._id);
                    return <div key={c._id} className="dev-row" style={{display:"grid",gridTemplateColumns:"1fr 200px",gap:10,padding:"12px 0",borderBottom:"0.5px solid rgba(0,0,0,0.06)",alignItems:"center",fontSize:13}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        {editing
                          ? <input style={Object.assign({},inputStyle,{maxWidth:280})} value={devEditName} onChange={function(e){setDevEditName(e.target.value);}}
                              onKeyDown={function(e){ if(e.key==="Enter"){ e.preventDefault(); saveEdit(c); } if(e.key==="Escape"){ cancelEdit(); } }} autoFocus/>
                          : <span style={{fontWeight:500,color:"#1a1a1a"}}>{c.name}</span>}
                      </div>
                      <div style={{textAlign:"right",display:"flex",gap:6,justifyContent:"flex-end",alignItems:"center"}}>
                        {editing && <button type="button" onClick={function(){saveEdit(c);}} disabled={devSaving} style={Object.assign({},btnPrimary,{padding:"4px 12px",opacity:devSaving?0.6:1})}>Save</button>}
                        {editing && <button type="button" onClick={cancelEdit} style={Object.assign({},btnGhost,{padding:"4px 10px"})}>Cancel</button>}
                        {!editing && <button type="button" onClick={function(){startEdit(c);}} style={Object.assign({},btnGhost,{padding:"4px 10px"})}>Edit</button>}
                        {!editing && <button type="button" onClick={function(){removeDeveloper(c);}} disabled={devSaving} style={btnDanger}>Delete</button>}
                      </div>
                    </div>;
                  })}
                </div>
          )}
        </div>;
      })()}

      {activeTab==="locations" && (function(){
        var inputStyle = {padding:"6px 10px",border:"0.5px solid rgba(0,0,0,0.1)",borderRadius:8,fontSize:13,background:"#fff",fontFamily:"inherit",width:"100%",boxSizing:"border-box"};
        var btnPrimary = {fontSize:12,padding:"8px 16px",border:"0.5px solid rgba(24,95,165,0.3)",background:"#185FA5",color:"#fff",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var btnGhost = {fontSize:12,padding:"8px 14px",border:"0.5px solid rgba(0,0,0,0.1)",background:"#fff",color:"#1a1a1a",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        var btnDanger = {fontSize:12,padding:"4px 10px",border:"0.5px solid rgba(163,45,45,0.3)",background:"#fff",color:"#A32D2D",borderRadius:8,cursor:"pointer",fontWeight:500,fontFamily:"inherit"};
        // The owner's manual order (2026-08-08) — NOT alphabetical, and NOT
        // active-first. The list here must be the exact sequence the pickers and
        // the filter show, otherwise "move up" in Settings wouldn't match what
        // an agent sees. Deactivated rows keep their slot in the order; they are
        // greyed out instead of being shunted to the bottom.
        var sortLoc = function(list){
          return list.slice().sort(function(a,b){
            var sa = typeof a.sortOrder === "number" ? a.sortOrder : Number.MAX_SAFE_INTEGER;
            var sb = typeof b.sortOrder === "number" ? b.sortOrder : Number.MAX_SAFE_INTEGER;
            if (sa !== sb) return sa - sb;
            return String(a.name||"").localeCompare(String(b.name||""));
          });
        };
        // Move a row one slot up/down and persist the whole sequence. Sends the
        // FULL order rather than a swap so the server never has to reconcile two
        // concurrent single-row edits — last writer wins on the whole list.
        var moveLocation = async function(idx, delta){
          var next = idx + delta;
          if (next < 0 || next >= locations.length) return;
          var reordered = locations.slice();
          var moved = reordered.splice(idx, 1)[0];
          reordered.splice(next, 0, moved);
          setLocations(reordered);            // optimistic — the arrows stay responsive
          setLocError(""); setLocMsg("");
          setLocReordering(true);
          try {
            var resp = await apiFetch("/api/locations/reorder","PUT",
              { order: reordered.map(function(x){ return String(x._id); }) }, p.token, p.csrfToken);
            var rows = (resp && Array.isArray(resp.data)) ? resp.data : null;
            if (rows) setLocations(sortLoc(rows));   // adopt the server's truth
            setLocMsg("Order saved");
            setTimeout(function(){ setLocMsg(""); }, 1800);
          } catch(err){
            setLocError((err&&err.message)||"Reorder failed");
            // Put the list back the way the server still has it.
            try {
              var fresh = await apiFetch("/api/locations?includeInactive=1","GET",null,p.token);
              var frows = (fresh && Array.isArray(fresh.data)) ? fresh.data : null;
              if (frows) setLocations(sortLoc(frows));
            } catch(e2){}
          }
          finally { setLocReordering(false); }
        };
        var addLocation = async function(){
          setLocError(""); setLocMsg("");
          var nm = (locName||"").trim();
          if (!nm) { setLocError("Name is required"); return; }
          setLocSaving(true);
          try {
            var saved = await apiFetch("/api/locations","POST",{name:nm},p.token,p.csrfToken);
            setLocations(sortLoc([saved].concat(locations)));
            setLocName(""); setLocMsg("Location added");
            setTimeout(function(){ setLocMsg(""); }, 2500);
          } catch(err){ setLocError((err&&err.message)||"Add failed"); }
          finally { setLocSaving(false); }
        };
        var startEdit = function(c){ setLocEditId(c._id); setLocEditName(c.name||""); setLocError(""); setLocMsg(""); };
        var cancelEdit = function(){ setLocEditId(null); setLocEditName(""); };
        var saveEdit = async function(c){
          setLocError(""); setLocMsg("");
          var nm = (locEditName||"").trim();
          if (!nm) { setLocError("Name cannot be empty"); return; }
          setLocSaving(true);
          try {
            var saved = await apiFetch("/api/locations/"+c._id,"PATCH",{name:nm},p.token,p.csrfToken);
            setLocations(sortLoc(locations.map(function(x){ return String(x._id)===String(saved._id)?saved:x; })));
            cancelEdit();
            // The rename also rewrote DailyRequest.area on every linked request
            // (server-side), so say so — otherwise it looks like a label-only edit.
            setLocMsg("Renamed — linked requests updated");
            setTimeout(function(){ setLocMsg(""); }, 3000);
          } catch(err){ setLocError((err&&err.message)||"Rename failed"); }
          finally { setLocSaving(false); }
        };
        var toggleActive = async function(c){
          setLocError(""); setLocMsg("");
          var next = !(c.active !== false);
          setLocBusyId(String(c._id));
          try {
            var saved = await apiFetch("/api/locations/"+c._id,"PATCH",{active:next},p.token,p.csrfToken);
            setLocations(sortLoc(locations.map(function(x){ return String(x._id)===String(saved._id)?saved:x; })));
            setLocMsg(next ? "Location reactivated" : "Location deactivated — hidden from the pickers, history kept");
            setTimeout(function(){ setLocMsg(""); }, 3000);
          } catch(err){ setLocError((err&&err.message)||"Update failed"); }
          finally { setLocBusyId(null); }
        };
        var removeLocation = async function(c){
          setLocError(""); setLocMsg("");
          if (!window.confirm("Delete location \""+(c.name||"")+"\"? This cannot be undone. If any request uses it, deactivate it instead.")) return;
          setLocBusyId(String(c._id));
          try {
            await apiFetch("/api/locations/"+c._id,"DELETE",null,p.token,p.csrfToken);
            setLocations(locations.filter(function(x){ return String(x._id)!==String(c._id); }));
            setLocMsg("Location deleted");
            setTimeout(function(){ setLocMsg(""); }, 2500);
          } catch(err){
            // On a 409 the backend puts the friendly "X request(s) use this
            // location…" text in err.message (apiFetch surfaces only `error`).
            setLocError((err&&err.message)||"Delete failed");
          }
          finally { setLocBusyId(null); }
        };
        var activeCount = locations.filter(function(x){ return x.active !== false; }).length;
        return <div style={{fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}}>
          {/* Phone/tablet rules for this tab, `.loc-*`. The list grid is
              `74px 1fr 110px 240px` -- 424px fixed before the name column
              gets a pixel. At 299px the name resolved to zero; at 768 the
              panel is 452px and 424 + 30 of gaps is 454, so it was ALSO zero
              on a tablet. Under 900 the actions cell drops to its own
              full-width row beneath, left-aligned, and the grid becomes
              `74px 1fr 110px` -- the name keeps ~130px at 901 and ~230 at
              768. The header's "Action" label is hidden there: it named a
              column that is no longer a column. */}
          <style>{""
+ "@media (max-width: 900px) {"
+ "  .loc-row { grid-template-columns: 74px minmax(0,1fr) 110px !important; }"
+ "  .loc-row > :nth-child(4) { grid-column: 1 / -1; justify-content: flex-start !important; text-align: left !important; }"
+ "  .loc-head > :nth-child(4) { display: none; }"
+ "}"
}</style>
          <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Locations</div>
          <div style={{fontSize:12,color:"#666",marginBottom:18}}>The geographic areas a Daily Request can be for (e.g. New Cairo, Sahel). Agents pick from this list when creating a request; a name they type that isn't here is held as <b>pending</b> for you to approve. Names are matched case-, spacing- and punctuation-insensitively, so the same place can't be added twice under different spellings. <b>Renaming</b> updates every request already using it. <b>Deactivating</b> hides a location from the pickers but keeps all history — use it instead of deleting anything with requests attached. <b>The order below is the order everywhere</b> — both request forms and the Location filter list them exactly like this; use ▲▼ to arrange them. New locations are added at the bottom.</div>

          {/* Add form */}
          <div style={{background:"#F7F7F5",border:"0.5px solid rgba(0,0,0,0.08)",borderRadius:10,padding:14,marginBottom:18}}>
            <div style={{fontSize:13,fontWeight:500,marginBottom:10}}>Add location</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <div style={{flex:"1 1 240px",minWidth:200}}>
                <input style={inputStyle} value={locName} onChange={function(e){setLocName(e.target.value);}} placeholder="e.g. Ain Sokhna"
                  onKeyDown={function(e){ if(e.key==="Enter"){ e.preventDefault(); addLocation(); } }}/>
              </div>
              <button type="button" onClick={addLocation} disabled={locSaving} style={Object.assign({},btnPrimary,{opacity:locSaving?0.6:1,cursor:locSaving?"not-allowed":"pointer"})}>
                {locSaving ? "Saving…" : "Add location"}
              </button>
              {locMsg   && <span style={{fontSize:12,color:"#0F6E56",background:"#EAF6F0",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{locMsg}</span>}
              {locError && <span style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{locError}</span>}
            </div>
          </div>

          {/* List */}
          {!locLoaded ? <div style={{fontSize:12,color:"#666"}}>Loading…</div> : (
            locations.length === 0
              ? <div style={{fontSize:12,color:"#666",padding:"12px 0"}}>No locations yet.</div>
              : <div>
                  <div style={{fontSize:11,color:"#666",marginBottom:6}}>{activeCount} active · {locations.length - activeCount} deactivated</div>
                  <div className="loc-row loc-head" style={{display:"grid",gridTemplateColumns:"74px 1fr 110px 240px",gap:10,padding:"10px 0",borderBottom:"0.5px solid rgba(0,0,0,0.1)",fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px"}}>
                    <div>Order</div>
                    <div>Name</div>
                    <div>Status</div>
                    <div style={{textAlign:"right"}}>Action</div>
                  </div>
                  {locations.map(function(c,idx){
                    var editing = String(locEditId||"")===String(c._id);
                    var isActive = c.active !== false;
                    var busy = String(locBusyId||"")===String(c._id);
                    var arrowBtn = {width:24,height:22,border:"0.5px solid rgba(0,0,0,0.12)",background:"#fff",borderRadius:6,cursor:"pointer",fontSize:11,lineHeight:"1",padding:0,color:"#444"};
                    var arrowOff = Object.assign({},arrowBtn,{opacity:0.3,cursor:"not-allowed"});
                    return <div key={c._id} className="loc-row" style={{display:"grid",gridTemplateColumns:"74px 1fr 110px 240px",gap:10,padding:"12px 0",borderBottom:"0.5px solid rgba(0,0,0,0.06)",alignItems:"center",fontSize:13,opacity:isActive?1:0.6}}>
                      <div style={{display:"flex",gap:4,alignItems:"center"}}>
                        <button type="button" title="Move up" aria-label={"Move "+(c.name||"")+" up"} disabled={idx===0||locReordering}
                          onClick={function(){moveLocation(idx,-1);}} style={idx===0||locReordering?arrowOff:arrowBtn}>▲</button>
                        <button type="button" title="Move down" aria-label={"Move "+(c.name||"")+" down"} disabled={idx===locations.length-1||locReordering}
                          onClick={function(){moveLocation(idx,1);}} style={(idx===locations.length-1||locReordering)?arrowOff:arrowBtn}>▼</button>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        {editing
                          ? <input style={Object.assign({},inputStyle,{maxWidth:280})} value={locEditName} onChange={function(e){setLocEditName(e.target.value);}}
                              onKeyDown={function(e){ if(e.key==="Enter"){ e.preventDefault(); saveEdit(c); } if(e.key==="Escape"){ cancelEdit(); } }} autoFocus/>
                          : <span style={{fontWeight:500,color:"#1a1a1a"}}>{c.name}</span>}
                      </div>
                      <div>
                        <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:8,background:isActive?"#EAF6F0":"#F1F1EF",color:isActive?"#0F6E56":"#666"}}>{isActive?"Active":"Inactive"}</span>
                      </div>
                      <div style={{textAlign:"right",display:"flex",gap:6,justifyContent:"flex-end",alignItems:"center"}}>
                        {editing && <button type="button" onClick={function(){saveEdit(c);}} disabled={locSaving} style={Object.assign({},btnPrimary,{padding:"4px 12px",opacity:locSaving?0.6:1})}>Save</button>}
                        {editing && <button type="button" onClick={cancelEdit} style={Object.assign({},btnGhost,{padding:"4px 10px"})}>Cancel</button>}
                        {!editing && <button type="button" onClick={function(){startEdit(c);}} style={Object.assign({},btnGhost,{padding:"4px 10px"})}>Edit</button>}
                        {!editing && <button type="button" onClick={function(){toggleActive(c);}} disabled={busy} style={Object.assign({},btnGhost,{padding:"4px 10px",opacity:busy?0.6:1})}>{isActive?"Deactivate":"Reactivate"}</button>}
                        {!editing && <button type="button" onClick={function(){removeLocation(c);}} disabled={busy} style={Object.assign({},btnDanger,{opacity:busy?0.6:1})}>Delete</button>}
                      </div>
                    </div>;
                  })}
                </div>
          )}
        </div>;
      })()}
        </SettingsTabBoundary>

      <Modal show={annOpen} onClose={function(){ if(!annBusy){ setAnnOpen(false); setAnnResult(null); } }} title="Company Announcement" w={520}>
        <Inp label="Title (optional)" value={annTitle} placeholder="e.g. Office closed Thursday"
             onChange={function(e){ setAnnTitle(e.target.value); }}/>
        <Inp label="Message" type="textarea" value={annBody} placeholder="Type the announcement everyone will receive…"
             onChange={function(e){ setAnnBody(e.target.value); }}/>
        <div style={{display:"flex",gap:14,margin:"4px 0 12px",fontSize:13}}>
          <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
            <input type="radio" name="annMode" checked={annMode==="now"} onChange={function(){ setAnnMode("now"); }}/> Send now
          </label>
          <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
            <input type="radio" name="annMode" checked={annMode==="later"} onChange={function(){ setAnnMode("later"); }}/> Schedule for later
          </label>
        </div>
        {annMode==="later" && <div style={{marginBottom:12}}>
          <label style={{display:"block",fontSize:13,fontWeight:600,color:C.text,marginBottom:5}}>Send at</label>
          <input type="datetime-local" value={annWhen} onChange={function(e){ setAnnWhen(e.target.value); }}
                 style={{width:"100%",padding:"9px 12px",borderRadius:10,border:"1px solid #E2E8F0",fontSize:14,boxSizing:"border-box"}}/>
        </div>}
        {annResult && <div style={{fontSize:13,color:"#0F6E56",background:"#EAF6F0",padding:"8px 12px",borderRadius:8,marginBottom:12}}>
          ✓ Sent to {annResult.recipients} employee{annResult.recipients===1?"":"s"}.
        </div>}
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:6}}>
          <Btn outline onClick={function(){ if(!annBusy){ setAnnOpen(false); setAnnResult(null); } }}>Cancel</Btn>
          {annMode==="now"
            ? <Btn onClick={sendAnnouncement} loading={annBusy} disabled={!annBody.trim()}>Send to all employees</Btn>
            : <Btn onClick={scheduleAnnouncement} loading={annBusy} disabled={!annBody.trim() || !annWhen}>Schedule</Btn>}
        </div>
      </Modal>

        </div>
      </div>
    </div>
  </div>;
};

// Parses a scanned payload (URL OR bare code) into an assetCode string. Lives
// at module scope so the scan-mount effect can use it without triggering the
// exhaustive-deps rule. Accepts both shapes the QR symbol may carry:
//   https://<host>/assets/ARO-LT-0001  →  "ARO-LT-0001"
//   ARO-LT-0001                        →  "ARO-LT-0001"
// Returns null when the payload doesn't match either shape (so the caller can
// show "That doesn't look like an asset code").
function parseAssetScanPayload(raw) {
  var s = String(raw || "").trim();
  if (!s) return null;
  var m = s.match(/^(?:https?:\/\/[^/]+)?\/?assets\/([A-Za-z0-9-]+)\/?$/i);
  if (m) return m[1];
  return s.match(/^[A-Za-z0-9-]+$/) ? s : null;
}

// Build a download-filename prefix from an asset name. Returns "" when the
// name is empty/unusable so the caller can just concatenate with assetCode
// to get e.g. "dell-latitude-ARO-LT-0001.png" (or "ARO-LT-0001.png" when no
// useful name exists). Strips non-ASCII (so Arabic names don't end up
// percent-encoded across browsers), lowercases, collapses dashes, trims.
function slugifyAssetName(name) {
  var s = String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s ? s + "-" : "";
}

// ===== Asset label printing (Xprinter XP-233B thermal label printer) =====
// The 10 supported label sizes. `id` is the dropdown value; w/h are the exact
// physical millimetres pushed into `@page { size: Wmm Hmm }` so the label
// prints at true size on the thermal head.
var LABEL_SIZES = [
  { id: "25x15", w: 25, h: 15, label: "25 × 15 mm" },
  { id: "30x20", w: 30, h: 20, label: "30 × 20 mm" },
  { id: "40x30", w: 40, h: 30, label: "40 × 30 mm" },
  { id: "40x40", w: 40, h: 40, label: "40 × 40 mm" },
  { id: "50x30", w: 50, h: 30, label: "50 × 30 mm" },
  { id: "50x40", w: 50, h: 40, label: "50 × 40 mm" },
  { id: "55x40", w: 55, h: 40, label: "55 × 40 mm" },
  { id: "60x40", w: 60, h: 40, label: "60 × 40 mm" },
  { id: "70x40", w: 70, h: 40, label: "70 × 40 mm" },
  { id: "80x50", w: 80, h: 50, label: "80 × 50 mm" }
];

// Per-label element toggles. `qr` is locked ON (a QR-less label is pointless).
// `def` seeds the dialog's default checkbox state.
var LABEL_ELEMENTS = [
  { key: "qr",           label: "QR Code",                  def: true,  locked: true },
  { key: "code",         label: "Asset Code",               def: true },
  { key: "name",         label: "Asset Name",               def: true },
  { key: "category",     label: "Category",                 def: true },
  { key: "branch",       label: "Branch",                   def: true },
  { key: "custodian",    label: "Custodian Name",           def: true },
  { key: "purchaseDate", label: "Purchase Date",            def: false },
  { key: "serialNumber", label: "Serial Number",            def: false },
  { key: "footer",       label: "\"ARO INVESTMENT\" footer", def: true }
];

// Which elements physically fit at a given size. Tiny labels can only carry the
// QR + a line or two; everything 40×40 and up gets the full layout. The dialog
// greys out checkboxes outside this set so the user can't toggle on something
// that wouldn't render. QR is always allowed (locked elsewhere).
function labelAllowedElements(sizeId) {
  if (sizeId === "25x15") return { code: true };
  if (sizeId === "30x20") return { code: true, name: true };
  if (sizeId === "40x30") return { code: true, name: true };
  return { code: true, name: true, category: true, branch: true, custodian: true,
           purchaseDate: true, serialNumber: true, footer: true };
}

// HTML-escape for values dropped into the label markup string.
function escapeLabelHtml(s) {
  return String(s == null ? "" : s).replace(/[<>&"]/g, function(c) {
    return ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" })[c];
  });
}

// Traces a rounded-rectangle sub-path onto an existing canvas path (no
// beginPath/fill of its own) so callers can compose ring shapes via evenodd.
function qrRoundedRectPath(ctx, x, y, w, h, r) {
  r = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Renders a styled QR PNG (data URL) for the print label: the three finder
// ("eye") patterns get gently rounded corners while every data module stays a
// crisp square — squares rasterise cleanest on a thermal head. Error
// correction H, quiet-zone margin 2, colour #0F172A. Returns "" on any failure
// so the caller can fall back gracefully. Separate from the plain on-screen QR
// (QRCode.toDataURL) — that one is intentionally left untouched.
function generateStyledQR(text) {
  return new Promise(function(resolve) {
    if (!text) { resolve(""); return; }
    var qr;
    try { qr = QRCode.create(String(text), { errorCorrectionLevel: "H" }); }
    catch (e) { resolve(""); return; }
    try {
      var count = qr.modules.size;
      var get = function(r, c) {
        if (r < 0 || c < 0 || r >= count || c >= count) return false;
        return !!qr.modules.get(r, c);
      };
      var margin = 2;
      var px = 16; // device px per module — high enough to stay crisp at print
      var dim = (count + margin * 2) * px;
      var canvas = document.createElement("canvas");
      canvas.width = dim; canvas.height = dim;
      var ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, dim, dim);
      ctx.fillStyle = "#0F172A";
      // Finder patterns occupy the three 7×7 corners.
      var finders = [[0, 0], [0, count - 7], [count - 7, 0]];
      var inFinder = function(r, c) {
        for (var i = 0; i < finders.length; i++) {
          var fr = finders[i][0], fc = finders[i][1];
          if (r >= fr && r < fr + 7 && c >= fc && c < fc + 7) return true;
        }
        return false;
      };
      // Data modules — crisp squares, finder area skipped.
      for (var r = 0; r < count; r++) {
        for (var c = 0; c < count; c++) {
          if (inFinder(r, c)) continue;
          if (get(r, c)) ctx.fillRect((c + margin) * px, (r + margin) * px, px, px);
        }
      }
      // Finder patterns — rounded outer ring + rounded inner dot.
      finders.forEach(function(f) {
        var x = (f[1] + margin) * px, y = (f[0] + margin) * px, sz = 7 * px;
        ctx.beginPath();
        qrRoundedRectPath(ctx, x, y, sz, sz, px * 1.7);
        qrRoundedRectPath(ctx, x + px, y + px, sz - 2 * px, sz - 2 * px, px * 1.0);
        ctx.fill("evenodd"); // outer minus inner = ring
        ctx.beginPath();
        qrRoundedRectPath(ctx, x + 2 * px, y + 2 * px, sz - 4 * px, sz - 4 * px, px * 0.85);
        ctx.fill();
      });
      resolve(canvas.toDataURL("image/png"));
    } catch (e) { resolve(""); }
  });
}

// Builds the inner HTML string for ONE printed asset label. Pure + module-level
// so the print popup and the in-dialog live preview render from the SAME code
// path — preview fidelity is guaranteed because there is exactly one renderer.
// Every style is inline (no class hooks) so the string drops straight into the
// popup document AND into the React preview via dangerouslySetInnerHTML.
// Authored in mm/pt (absolute units): the label prints at true physical size
// and the on-screen preview stays proportional under a CSS scale().
// Reference layout is 55×40mm; all other sizes scale off it.
function buildAssetLabelHTML(o) {
  var esc = escapeLabelHtml;
  var w = o.size.w, h = o.size.h, show = o.show || {};

  var s = Math.min(w / 55, h / 40);
  s = Math.max(0.6, Math.min(1.3, s));

  var pad = Math.max(0.9, Math.min(2.2, w * 0.042));
  var gap = Math.max(0.8, pad * 0.9);

  var hasFooter = !!show.footer;
  var footerH = hasFooter ? (2.4 + 1.1 * Math.min(s, 1)) : 0; // mm

  var innerW = w - pad * 2;
  var bodyH  = h - pad * 2 - footerH;
  var qrAreaW = innerW * 0.47;
  var qrFrame = Math.max(4, Math.min(qrAreaW, bodyH));

  // QR frame chrome scales with the symbol so proportions hold at every size.
  var qrBorder = Math.max(0.25, Math.min(0.5, qrFrame * 0.035));
  var qrRadius = Math.max(0.7, Math.min(2.4, qrFrame * 0.10));
  var qrInPad  = Math.max(0.35, Math.min(1.3, qrFrame * 0.07));

  var rightColW = Math.max(3, innerW - qrAreaW - gap);

  // The asset code auto-fits its column width so it never overflows, even on
  // the 25×15 label. On big labels the scaled font wins.
  var codeStr = String(o.code || "");
  var codeLen = Math.max(1, codeStr.length);
  var fitCode = (rightColW * 0.94) / (codeLen * 0.62 * 0.3528);
  var fCode   = Math.max(3.6, Math.min(10.5 * s, fitCode));
  var fName   = Math.max(4,   8.6 * s);
  var fMeta   = Math.max(3.6, 6.8 * s);
  var fExtra  = Math.max(3.4, 6.2 * s);
  var fFooter = Math.max(5,   Math.min(7.5, 7 * s));
  var fAro    = Math.max(8,   Math.min(15.5, 14 * s)); // "ARO" wordmark, 14pt @ ref
  var codeSpacing = fCode < 6 ? 0 : 0.3;

  // ----- QR card (left half, always present) -----
  var qrImg = o.qrUrl
    ? '<img src="' + o.qrUrl + '" alt="QR" style="width:100%;height:100%;display:block;image-rendering:pixelated;image-rendering:crisp-edges;"/>'
    : '';
  var qrBlock =
    '<div style="width:' + qrAreaW + 'mm;display:flex;align-items:center;justify-content:center;flex:0 0 auto;">' +
      '<div style="width:' + qrFrame + 'mm;height:' + qrFrame + 'mm;box-sizing:border-box;' +
        'border:' + qrBorder + 'mm solid #0F172A;border-radius:' + qrRadius + 'mm;' +
        'padding:' + qrInPad + 'mm;background:#fff;">' + qrImg + '</div>' +
    '</div>';

  // ----- right column: "ARO" wordmark (top) + text block (vertically centred) -----
  // Plain Georgia serif text — no image, no toggle. Skipped on the two tiniest
  // sizes (25×15 / 30×20), which have no room beside the QR for a brand mark.
  var rightParts = [];
  if (w >= 40 && h >= 30) {
    rightParts.push(
      '<div style="display:flex;justify-content:flex-end;margin-bottom:' + (0.5 * s) + 'mm;flex:0 0 auto;">' +
        '<div style="font-family:Georgia,\'Times New Roman\',serif;font-size:' + fAro + 'pt;' +
          'color:#0F172A;line-height:1;">ARO</div>' +
      '</div>'
    );
  }

  var textParts = [];
  if (show.code && codeStr) {
    textParts.push(
      '<div style="font-family:\'Courier New\',Courier,monospace;font-weight:700;' +
        'font-size:' + fCode + 'pt;letter-spacing:' + codeSpacing + 'px;line-height:1.1;' +
        'color:#0F172A;white-space:nowrap;overflow:hidden;">' + esc(codeStr) + '</div>'
    );
  }
  if (show.name && o.name) {
    textParts.push(
      '<div style="font-weight:600;font-size:' + fName + 'pt;line-height:1.15;color:#0F172A;' +
        'margin-top:' + (0.4 * s) + 'mm;overflow:hidden;display:-webkit-box;' +
        '-webkit-line-clamp:2;-webkit-box-orient:vertical;">' + esc(o.name) + '</div>'
    );
  }

  var secondary = [];
  var metaBits = [];
  if (show.category && o.category) metaBits.push(esc(o.category));
  if (show.branch && o.branch)     metaBits.push(esc(o.branch));
  var metaLine = function(content, mt) {
    return '<div style="font-size:' + fMeta + 'pt;color:#64748B;line-height:1.2;' +
      (mt ? 'margin-top:' + (0.25 * s) + 'mm;' : '') +
      'overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">' + content + '</div>';
  };
  if (metaBits.length) secondary.push(metaLine(metaBits.join(' &middot; '), false));
  if (show.custodian && o.custodian) secondary.push(metaLine(esc(o.custodian), secondary.length > 0));
  if (show.serialNumber && o.serialNumber) {
    secondary.push('<div style="font-size:' + fExtra + 'pt;color:#64748B;line-height:1.2;margin-top:' +
      (0.25 * s) + 'mm;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">S/N ' + esc(o.serialNumber) + '</div>');
  }
  if (show.purchaseDate && o.purchaseDate) {
    secondary.push('<div style="font-size:' + fExtra + 'pt;color:#64748B;line-height:1.2;margin-top:' +
      (0.25 * s) + 'mm;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">Purchased ' + esc(o.purchaseDate) + '</div>');
  }

  if (secondary.length && textParts.length) {
    textParts.push('<div style="border-top:0.18mm solid #CBD5E1;margin:' + (0.7 * s) + 'mm 0;"></div>');
  }
  textParts = textParts.concat(secondary);

  rightParts.push(
    '<div style="flex:1;display:flex;flex-direction:column;justify-content:center;min-width:0;overflow:hidden;">' +
      textParts.join('') +
    '</div>'
  );

  var rightBlock =
    '<div style="flex:1;display:flex;flex-direction:column;min-width:0;overflow:hidden;">' +
      rightParts.join('') +
    '</div>';

  // ----- footer: thin rule + "ARO INVESTMENT" (plain Arial Bold text) -----
  var footerBlock = hasFooter
    ? '<div style="flex:0 0 auto;margin-top:' + (0.6 * s) + 'mm;">' +
        '<div style="border-top:0.25mm solid #0F172A;margin-bottom:' + (0.8 * s) + 'mm;"></div>' +
        '<div style="text-align:center;font-family:Arial,Helvetica,sans-serif;font-weight:700;' +
          'font-size:' + fFooter + 'pt;letter-spacing:1.5px;color:#0F172A;line-height:1;">ARO INVESTMENT</div>' +
      '</div>'
    : '';

  return (
    '<div style="width:' + w + 'mm;height:' + h + 'mm;box-sizing:border-box;padding:' + pad + 'mm;' +
      'background:#fff;display:flex;flex-direction:column;overflow:hidden;' +
      'font-family:Arial,Helvetica,sans-serif;color:#0F172A;">' +
      '<div style="flex:1;display:flex;align-items:stretch;gap:' + gap + 'mm;min-height:0;overflow:hidden;">' +
        qrBlock + rightBlock +
      '</div>' +
      footerBlock +
    '</div>'
  );
}

// ===== ASSET TRACKER PAGE (admin / isOwner only) =====
// Single page id "assets" with three internal sub-views: list, new (also used
// for edit), and detail. Custody actions (transfer/return/mark-status) ship
// in S3 and surface as buttons on the detail view; QR rendering ships in S4.
// Sub-view nav is state-only — no URL routing today; QR deep-links land in S4.
var AssetTrackerPage = function(p) {
  var liveAssetRev = useLiveRev("asset");
  // English-only LTR module — the rest of the CRM follows the global lang
  // direction, but inventory copy is owner-facing and stays in English so
  // we force LTR on the root and skip translation ternaries throughout.

  // ===== Hooks — all declared up-front, never below a return. =====
  // QR deep-link: App captured the code from the URL into its own state
  // before mounting us; we read it once via the prop to seed the detail
  // sub-view.
  //
  // SUBTLE: we deliberately do NOT consume the prop (call onInitialConsumed)
  // from a mount effect. The App-level loading flag flips true while
  // loadData fetches /api/leads etc., which forces App's `if(loading)
  // return spinner` to unmount this component mid-flight. When loading
  // flips back to false, we REMOUNT — useState initializers run again
  // with the props as they are at that moment. If we'd already cleared
  // deepLinkAssetCode in App state during the first short-lived mount,
  // the remount would see initialAssetCode=null and re-init subPage to
  // "list" (the symptom that broke deep-links before this fix). Instead
  // we keep the App-level deep-link intact until the user explicitly
  // navigates away from the detail view (see the [subPage] effect below).
  var [subPage, setSubPage] = useState(p.initialAssetCode ? "detail" : "list");
  var [selectedAssetCode, setSelectedAssetCode] = useState(p.initialAssetCode || null);
  var [assets, setAssets] = useState([]);
  var [categories, setCategories] = useState([]);
  var [branches, setBranches] = useState([]);
  // Custodian roster for the New-asset / Transfer pickers (owner ruling 2026-11-02).
  // GET /api/users answers an accountant with itself alone, so the pickers read
  // GET /api/assets/custodians (name/username/role only). null = not answered /
  // refused → fall back to p.users, which is what every other role always used.
  var [custodians, setCustodians] = useState(null);
  var custodianRoster = Array.isArray(custodians) ? custodians : (p.users || []);
  var [loaded, setLoaded] = useState(false);
  var [loadError, setLoadError] = useState("");

  var [groupFilter, setGroupFilter] = useState("");
  var [statusFilter, setStatusFilter] = useState("");
  var [branchFilter, setBranchFilter] = useState("");
  var [searchQuery, setSearchQuery] = useState("");
  // List grouping: collapse same-name assets (e.g. 24 chairs all named
  // "متحرك اسود") into one row with a count badge. Expand state is per-name,
  // client-side only — no backend change. Auto-flattens during active search
  // so matches surface as individual rows.
  var [expandedGroups, setExpandedGroups] = useState({});

  // Owner-only aggregate of active assets' purchase value. Backend strips this
  // for non-owners; the FE also gates the fetch behind isOwner so non-owners
  // never even ask for it. null = not loaded yet / not applicable.
  var [activeValueStat, setActiveValueStat] = useState(null);

  var emptyForm = { name:"", categoryId:"", branchId:"", purchasePrice:"", purchaseDate:"", supplier:"", serialNumber:"", assignmentType:"personal", currentCustodian:"", notes:"" };
  var [form, setForm] = useState(emptyForm);
  var [formMode, setFormMode] = useState("create"); // "create" | "edit"
  var [formEditId, setFormEditId] = useState(null);
  var [formSaving, setFormSaving] = useState(false);
  var [formError, setFormError] = useState("");

  var [detailAsset, setDetailAsset] = useState(null);
  var [detailLoading, setDetailLoading] = useState(false);
  var [detailError, setDetailError] = useState("");
  var [deleting, setDeleting] = useState(false);

  // Custody (S3): timeline rows + transfer modal + per-action busy flag.
  // History loads in parallel with the asset detail; transfer modal opens on
  // demand from the detail view; statusBusy disables all custody buttons
  // simultaneously to prevent double-clicks across the small button cluster.
  var [historyRows, setHistoryRows] = useState([]);
  var [historyLoading, setHistoryLoading] = useState(false);
  var [historyError, setHistoryError] = useState("");
  var [showTransferModal, setShowTransferModal] = useState(false);
  var [transferToUserId, setTransferToUserId] = useState("");
  var [transferNotes, setTransferNotes] = useState("");
  var [transferSaving, setTransferSaving] = useState(false);
  var [transferError, setTransferError] = useState("");
  var [statusBusy, setStatusBusy] = useState(false);
  var [statusError, setStatusError] = useState("");

  // QR (S4): data URL is generated client-side from the asset's qrCodeData
  // string whenever the detail view loads. Empty until the asset is fetched.
  var [qrDataUrl, setQrDataUrl] = useState("");
  // Scanner (S4): manual code fallback + scanner error surface. The scanner
  // library itself is lazy-imported inside the scan sub-view to keep it out
  // of the main bundle.
  var [scanManualCode, setScanManualCode] = useState("");
  var [scanError, setScanError] = useState("");
  // Reports (S5): a lazy-loaded dict keyed by tab name. Each tab fetches its
  // own data the first time it's opened and caches the result for the
  // remainder of the AssetTrackerPage instance. historyFilters apply only
  // to the history tab and are also forwarded to the Excel export.
  var [reportsTab, setReportsTab] = useState("summary");
  var [reportsData, setReportsData] = useState({});
  var [reportsLoading, setReportsLoading] = useState({});
  var [reportsError, setReportsError] = useState({});
  var [historyFilters, setHistoryFilters] = useState({ from: "", to: "", action: "" });
  var [exporting, setExporting] = useState(false);

  // Print dialog (Xprinter XP-233B label printer). The configuration here
  // drives both the live preview and the print popup — see buildAssetLabelHTML.
  //   styledQrUrl  — the rounded-finder QR PNG for the printed label (the plain
  //                  on-screen QR card keeps using qrDataUrl, untouched).
  //   printElements— per-element visibility, seeded from LABEL_ELEMENTS defaults.
  var [showPrintDialog, setShowPrintDialog] = useState(false);
  var [printSize, setPrintSize] = useState("40x30");
  var [printCopies, setPrintCopies] = useState("1");
  var [printElements, setPrintElements] = useState(function() {
    var init = {};
    LABEL_ELEMENTS.forEach(function(el) { init[el.key] = el.def; });
    return init;
  });
  var [styledQrUrl, setStyledQrUrl] = useState("");
  // Label-PDF state for the app path. The web path's feedback is the popup
  // itself; the app path has none unless it says so, and the failure being
  // replaced was an alert about pop-up settings on a device with no pop-ups.
  var [labelPdfBusy, setLabelPdfBusy] = useState(false);
  var [labelPdfErr, setLabelPdfErr] = useState("");

  useEffect(function() {
    var cancelled = false;
    setLoadError("");
    Promise.all([
      apiFetch("/api/assets",            "GET", null, p.token).catch(function(err){ if (!cancelled) setLoadError((err && err.message) || "Failed to load assets"); return []; }),
      apiFetch("/api/asset-categories",  "GET", null, p.token).catch(function(){ return []; }),
      apiFetch("/api/branches?active=true", "GET", null, p.token).catch(function(){ return []; }),
      apiFetch("/api/assets/custodians", "GET", null, p.token).catch(function(){ return null; })
    ]).then(function(results) {
      if (cancelled) return;
      setAssets(    Array.isArray(results[0]) ? results[0] : []);
      setCategories(Array.isArray(results[1]) ? results[1] : []);
      setBranches(  Array.isArray(results[2]) ? results[2] : []);
      setCustodians(Array.isArray(results[3]) ? results[3] : null);
      setLoaded(true);
    });
    return function() { cancelled = true; };
  }, [p.token, liveAssetRev]);

  // Owner-level: pull the total active-value aggregate. Backend returns 403 for
  // anyone else, so we gate the call here on the envelope's owner flag — OR the
  // financeReads role, which the server serves the owner-level view since
  // 2026-11-02 (assetAccess.ownerLevelForFinanceReads). A CARD, not a page —
  // page:assets is a separate, wider answer, which is why this cannot read
  // access.pages. See isOwnerFE.
  useEffect(function() {
    if (!(isOwnerFE(p.access) || roleHasFinanceReadsFE(p.cu))) { setActiveValueStat(null); return; }
    var cancelled = false;
    apiFetch("/api/assets/stats/active-value", "GET", null, p.token)
      .then(function(d){ if (!cancelled) setActiveValueStat(d && typeof d.activeValue === "number" ? d.activeValue : 0); })
      .catch(function(){ if (!cancelled) setActiveValueStat(null); });
    return function() { cancelled = true; };
  }, [p.token, p.access, assets]);

  // Deep-link consumer — fires when subPage moves AWAY from "detail" (not on
  // mount). Reason: a loading-state flicker in App can unmount this component
  // briefly during the initial data fetch; if we'd consumed the prop on
  // mount, the App state would be cleared by the time we remount, and the
  // remount would init subPage="list" because the prop is now null. By
  // keeping the App-level state intact until the user navigates AWAY,
  // remounts during loading flickers still see the deep-link prop and
  // re-pin to "detail" — and once the user clicks Back / New Asset / Scan,
  // we clear so future fresh navigations land on the list view.
  useEffect(function() {
    if (subPage !== "detail" && p.initialAssetCode && typeof p.onInitialConsumed === "function") {
      p.onInitialConsumed();
    }
    // The print dialog belongs to the detail view — drop it on any nav away so
    // it can't reappear over an unrelated asset next time detail opens.
    if (subPage !== "detail") setShowPrintDialog(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subPage]);

  // Generate the QR image (data URL) whenever the detail asset changes.
  // Styling rationale:
  //   - errorCorrectionLevel "H": ~30% damage recovery, important for thermal
  //     labels that scuff or peel; M-level (~15%) was too fragile in testing.
  //   - color #0F172A (slate-900) instead of pure black — slightly softer on
  //     screen and prints just as crisp on a thermal head.
  //   - margin 2 (was 1): wider quiet zone gives phone cameras a real edge
  //     to lock onto, especially in low light or at angles.
  //   - width 320 px is enough for the on-screen card and for the popup print
  //     preview to scale to 30mm without aliasing.
  // Same data URL is consumed by both the on-screen QR and the print popup,
  // so any tweak here propagates to printed labels automatically.
  useEffect(function() {
    if (!detailAsset || !detailAsset.qrCodeData) { setQrDataUrl(""); return; }
    var cancelled = false;
    QRCode.toDataURL(detailAsset.qrCodeData, {
      errorCorrectionLevel: "H",
      margin: 2,
      width: 320,
      color: { dark: "#0F172A", light: "#FFFFFF" }
    })
      .then(function(url){ if (!cancelled) setQrDataUrl(url); })
      .catch(function(){ if (!cancelled) setQrDataUrl(""); });
    return function() { cancelled = true; };
  }, [detailAsset]);

  // Styled QR for the PRINT label — rounded finder patterns, crisp data
  // modules (see generateStyledQR). Kept separate from qrDataUrl so the
  // on-screen QR card and the PNG download stay exactly as they were.
  useEffect(function() {
    if (!detailAsset || !detailAsset.qrCodeData) { setStyledQrUrl(""); return; }
    var cancelled = false;
    generateStyledQR(detailAsset.qrCodeData).then(function(url) {
      if (!cancelled) setStyledQrUrl(url);
    });
    return function() { cancelled = true; };
  }, [detailAsset]);

  // Mount + tear down the html5-qrcode scanner when subPage flips to "scan".
  // Library is lazy-imported so it never lands in the main bundle. Browser
  // camera perms are requested by the library itself; we surface any error
  // through scanError so the manual-entry fallback stays visible. The
  // first successful decode stops the camera before navigating, so the
  // light + stream don't linger on the user's device.
  useEffect(function() {
    if (subPage !== "scan") return;
    var stopped = false;
    var scanner = null;
    setScanError("");
    import("html5-qrcode").then(function(mod) {
      if (stopped) return;
      var Html5Qrcode = mod.Html5Qrcode;
      if (!document.getElementById("asset-scanner-region")) return;
      scanner = new Html5Qrcode("asset-scanner-region");
      scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        function(decoded) {
          var afterStop = function() {
            var code = parseAssetScanPayload(decoded);
            if (code) { setSelectedAssetCode(code); setSubPage("detail"); }
            else      { setScanError("That QR doesn't look like an asset code — try again or use manual entry below."); }
          };
          try {
            if (scanner && scanner.isScanning) scanner.stop().then(afterStop).catch(afterStop);
            else afterStop();
          } catch (e) { afterStop(); }
        },
        function() { /* per-frame decode misses are constant noise — silent */ }
      ).catch(function(err) {
        setScanError((err && err.message) || "Couldn't access the camera. Use manual entry below.");
      });
    }).catch(function() {
      setScanError("Couldn't load the scanner library.");
    });
    return function() {
      stopped = true;
      try { if (scanner && scanner.isScanning) scanner.stop().catch(function(){}); } catch (e) {}
    };
  }, [subPage]);

  useEffect(function() {
    if (subPage !== "detail" || !selectedAssetCode) return;
    var cancelled = false;
    setDetailLoading(true); setDetailError(""); setDetailAsset(null);
    setHistoryRows([]); setHistoryError(""); setStatusError("");
    var requestedCode = selectedAssetCode; // capture for the error message — see catch below
    apiFetch("/api/assets/" + encodeURIComponent(selectedAssetCode), "GET", null, p.token)
      .then(function(d){
        if (cancelled) return;
        setDetailAsset(d);
        // Now that we have the asset id, fan out for the history. Sequential
        // (not parallel with the asset fetch) because the assetCode→id mapping
        // lives on the asset doc.
        if (d && d._id) {
          setHistoryLoading(true);
          apiFetch("/api/assets/" + d._id + "/history", "GET", null, p.token)
            .then(function(rows){ if (!cancelled) setHistoryRows(Array.isArray(rows) ? rows : []); })
            .catch(function(err){ if (!cancelled) setHistoryError((err && err.message) || "Failed to load custody history"); })
            .finally(function(){ if (!cancelled) setHistoryLoading(false); });
        }
      })
      .catch(function(err){
        if (cancelled) return;
        // Surface the requested code when the asset doesn't exist (e.g. a stale
        // QR was scanned or a deep-link to a typo'd code was followed) — the
        // generic "Failed to load asset" wasn't actionable.
        var raw = (err && err.message) || "";
        if (/not found/i.test(raw)) setDetailError("Asset " + requestedCode + " was not found. The code may be invalid or the asset was deleted.");
        else                        setDetailError(raw || "Failed to load asset");
      })
      .finally(function(){ if (!cancelled) setDetailLoading(false); });
    return function() { cancelled = true; };
  }, [subPage, selectedAssetCode, p.token]);

  // ===== Navigation + handlers =====
  var goList = function() {
    setSubPage("list"); setSelectedAssetCode(null); setDetailAsset(null);
    setForm(emptyForm); setFormMode("create"); setFormEditId(null); setFormError("");
  };
  var openNew = function() {
    setForm(emptyForm); setFormMode("create"); setFormEditId(null); setFormError(""); setSubPage("new");
  };
  var openDetail = function(asset) {
    setSelectedAssetCode(asset.assetCode); setSubPage("detail");
  };
  var openEdit = function(asset) {
    setForm({
      name: asset.name || "",
      categoryId: (asset.categoryId && (asset.categoryId._id || asset.categoryId)) || "",
      branchId:   (asset.branchId   && (asset.branchId._id   || asset.branchId))   || "",
      purchasePrice: asset.purchasePrice != null ? String(asset.purchasePrice) : "",
      purchaseDate:  asset.purchaseDate  ? new Date(asset.purchaseDate).toISOString().slice(0,10) : "",
      supplier:      asset.supplier || "",
      serialNumber:  asset.serialNumber || "",
      assignmentType: asset.assignmentType || "personal",
      currentCustodian: "", // not editable via PATCH; custody flows live in S3
      notes: asset.notes || ""
    });
    setFormMode("edit"); setFormEditId(asset._id); setFormError(""); setSubPage("new");
  };
  var saveForm = async function() {
    setFormError("");
    if (!form.name.trim())  { setFormError("Name is required"); return; }
    if (!form.categoryId)   { setFormError("Pick a category"); return; }
    if (!form.branchId)     { setFormError("Pick a branch"); return; }
    setFormSaving(true);
    try {
      var payload = {
        name: form.name.trim(),
        categoryId: form.categoryId,
        branchId: form.branchId,
        purchasePrice: form.purchasePrice ? Number(form.purchasePrice) : 0,
        purchaseDate: form.purchaseDate || null,
        supplier: form.supplier.trim(),
        serialNumber: form.serialNumber.trim(),
        assignmentType: form.assignmentType,
        notes: form.notes.trim()
      };
      var saved;
      if (formMode === "edit" && formEditId) {
        saved = await apiFetch("/api/assets/" + formEditId, "PATCH", payload, p.token, p.csrfToken);
        setAssets(assets.map(function(a){ return String(a._id) === String(saved._id) ? saved : a; }));
        setSelectedAssetCode(saved.assetCode); setSubPage("detail"); setDetailAsset(saved);
      } else {
        if (form.currentCustodian) payload.currentCustodian = form.currentCustodian;
        saved = await apiFetch("/api/assets", "POST", payload, p.token, p.csrfToken);
        setAssets([saved].concat(assets));
        setSelectedAssetCode(saved.assetCode); setSubPage("detail"); setDetailAsset(saved);
      }
      setForm(emptyForm); setFormMode("create"); setFormEditId(null);
    } catch (err) {
      setFormError((err && err.message) || "Save failed");
    } finally {
      setFormSaving(false);
    }
  };
  var doDelete = async function() {
    if (!detailAsset || !detailAsset._id) return;
    if (!window.confirm("Retire this asset? Status becomes 'retired' and the custodian is cleared. Audit trail is kept.")) return;
    setDeleting(true);
    try {
      await apiFetch("/api/assets/" + detailAsset._id, "DELETE", null, p.token, p.csrfToken);
      // Refresh list so the row reflects the retired status
      var fresh = await apiFetch("/api/assets", "GET", null, p.token).catch(function(){ return assets; });
      if (Array.isArray(fresh)) setAssets(fresh);
      goList();
    } catch (err) {
      alert((err && err.message) || "Delete failed");
    }
    setDeleting(false);
  };

  // After any custody mutation, sync three things in order: the asset shown on
  // the detail view, the row in the cached list, and the history timeline.
  // Centralised here so /transfer, /return, /mark-status all behave the same.
  var refreshAfterMutation = async function(updatedAsset) {
    if (updatedAsset && updatedAsset._id) {
      setDetailAsset(updatedAsset);
      setAssets(assets.map(function(a){ return String(a._id) === String(updatedAsset._id) ? updatedAsset : a; }));
      try {
        var rows = await apiFetch("/api/assets/" + updatedAsset._id + "/history", "GET", null, p.token);
        if (Array.isArray(rows)) setHistoryRows(rows);
      } catch (e) { /* keep stale timeline rather than blow up */ }
    }
  };

  var openTransferModal = function() {
    setTransferToUserId(""); setTransferNotes(""); setTransferError(""); setShowTransferModal(true);
  };
  var closeTransferModal = function() {
    if (transferSaving) return;
    setShowTransferModal(false);
  };
  var doTransfer = async function() {
    if (!detailAsset || !detailAsset._id) return;
    setTransferError("");
    if (!transferToUserId) { setTransferError("Pick a user"); return; }
    setTransferSaving(true);
    try {
      var updated = await apiFetch("/api/assets/" + detailAsset._id + "/transfer", "POST",
        { toUserId: transferToUserId, notes: transferNotes }, p.token, p.csrfToken);
      await refreshAfterMutation(updated);
      setShowTransferModal(false);
    } catch (err) {
      setTransferError((err && err.message) || "Transfer failed");
    }
    setTransferSaving(false);
  };
  var doReturn = async function() {
    if (!detailAsset || !detailAsset._id) return;
    if (!window.confirm("Return this asset to the warehouse? The current custodian will be cleared.")) return;
    setStatusBusy(true); setStatusError("");
    try {
      var updated = await apiFetch("/api/assets/" + detailAsset._id + "/return", "POST", { notes: "" }, p.token, p.csrfToken);
      await refreshAfterMutation(updated);
    } catch (err) {
      setStatusError((err && err.message) || "Return failed");
    }
    setStatusBusy(false);
  };
  var doMarkStatus = async function(newStatus) {
    if (!detailAsset || !detailAsset._id) return;
    setStatusBusy(true); setStatusError("");
    try {
      var updated = await apiFetch("/api/assets/" + detailAsset._id + "/mark-status", "POST",
        { status: newStatus, notes: "" }, p.token, p.csrfToken);
      await refreshAfterMutation(updated);
    } catch (err) {
      setStatusError((err && err.message) || "Status change failed");
    }
    setStatusBusy(false);
  };

  // Print dialog open/close. Opening just flips the flag — size, element
  // toggles and copy count persist across opens so a batch-printing session
  // keeps the user's choices.
  var openPrintDialog  = function() { setShowPrintDialog(true); };
  var closePrintDialog = function() { setShowPrintDialog(false); };

  // Single source of truth for the rendered label markup. Both the live
  // preview and the print popup call this — guaranteeing the preview matches
  // the print. Resolves the user's element toggles against what physically
  // fits the chosen size (labelAllowedElements).
  var currentLabelHTML = function() {
    if (!detailAsset || !styledQrUrl) return "";
    var a = detailAsset;
    var sizeObj = LABEL_SIZES.filter(function(z){ return z.id === printSize; })[0] || LABEL_SIZES[2];
    var allowed = labelAllowedElements(sizeObj.id);
    var effShow = {};
    LABEL_ELEMENTS.forEach(function(el) {
      if (el.key === "qr") return;
      effShow[el.key] = !!printElements[el.key] && !!allowed[el.key];
    });
    return buildAssetLabelHTML({
      code: a.assetCode, name: a.name,
      category:  (a.categoryId && a.categoryId.name) || "",
      branch:    (a.branchId && a.branchId.name) || "",
      custodian: (a.currentCustodian && a.currentCustodian.name) || "",
      serialNumber: a.serialNumber || "",
      purchaseDate: a.purchaseDate ? new Date(a.purchaseDate).toLocaleDateString("en-GB") : "",
      size: sizeObj, show: effShow, qrUrl: styledQrUrl
    });
  };

  // Sends the configured label to the print queue N times. Uses a popup so the
  // CRM's own styles don't fight the @page rule. One label is written into the
  // source; the popup's own script clones it (copies − 1) more times — keeps
  // the document small even when the QR data URL is large and copies is high.
  // Auto-prints once the QR image has settled, so the thermal head never
  // receives a half-decoded label.
  // ON THE APPS THIS DID NOTHING USEFUL (fixed 2026-09-11), and it failed one
  // step earlier than the other three surfaces. This path does not call
  // window.print() on OUR document at all — it opens a popup and writes a label
  // document whose own script prints itself. In a WebView `window.open("", "_blank")`
  // does not give back a writable window, so the branch that fires is the
  // alert: "Couldn't open the print window. Allow pop-ups for this site" —
  // advice about a browser setting that does not exist on a phone, for a popup
  // that was never going to open.
  //
  // THE APP GETS A LABEL PDF INSTEAD, and it is the one surface whose page is
  // not A4: `formatMm: [w, h]`, `marginPt: 0` and `layout: "repeat"` give
  // exactly N pages at the label's real size — the same N copies
  // the popup's script clones, each its own page, which is what a run of
  // thermal labels is. Fitted onto A4 it would be a postage stamp in the middle
  // of a sheet.
  //
  // scale 3, not the default 2: a 40x30mm label is ~151 CSS px wide, so the
  // rasterised QR needs the extra density to stay scannable when the PDF is
  // printed at real size. Measured at 11,778 bytes for three pages — the whole
  // document is smaller than one page of the payout report.
  //
  // THE LABEL IS BUILT FROM currentLabelHTML() — the SAME builder the popup
  // uses. The one document rule holds: there is no second label.
  var doPrintLabels = function() {
    if (!detailAsset || !styledQrUrl) return;
    var sizeObj = LABEL_SIZES.filter(function(z){ return z.id === printSize; })[0] || LABEL_SIZES[2];
    var copies = Math.max(1, Math.min(99, parseInt(printCopies, 10) || 1));
    var labelHtml = currentLabelHTML();
    if (!labelHtml) return;
    var mode = pdfShareSupport();
    if (mode !== "print") {
      if (mode !== "share") { setLabelPdfErr(PRINT_UNSUPPORTED_MSG[mode] || PRINT_UNSUPPORTED_MSG.none); return; }
      // The label lives in a popup on the web, so on the app it has to exist in
      // THIS document to be photographed. Mounted, captured, removed —
      // captureAndShare parks it off-screen at the label's own CSS width, which
      // is what keeps the rasterised layout identical to the printed one.
      var host = document.createElement("div");
      host.setAttribute("data-label-capture", "1");
      host.innerHTML = labelHtml;
      document.body.appendChild(host);
      var drop = function(){ try { if (host.parentNode) host.parentNode.removeChild(host); } catch(_){} };
      var mmToCssPx = function(mm){ return Math.round(mm * 96 / 25.4); };
      setLabelPdfBusy(true); setLabelPdfErr("");
      captureAndShare(host, {
        layout: "repeat",
        repeat: copies,
        formatMm: [sizeObj.w, sizeObj.h],   // MILLIMETRES — see the note in printPdf.js
        marginPt: 0,
        scale: 3,
        widthPx: mmToCssPx(sizeObj.w),
        filename: pdfFilename("label", detailAsset.assetCode || "asset", Date.now()),
        title: (detailAsset.assetCode || "Asset") + " — " + copies + (copies === 1 ? " label" : " labels")
      })
        .then(function(){ setLabelPdfBusy(false); drop(); })
        .catch(function(ex){ setLabelPdfBusy(false); drop(); setLabelPdfErr(String((ex && ex.message) || "Could not create the label PDF.")); });
      return;
    }
    var w = window.open("", "_blank", "width=580,height=640");
    if (!w) { alert("Couldn't open the print window. Allow pop-ups for this site and try again."); return; }
    var copyLabel = copies + (copies === 1 ? " label" : " copies");
    var html = '<!doctype html><html><head><meta charset="utf-8"><title>'
      + escapeLabelHtml(detailAsset.assetCode) + ' — labels</title><style>'
      + '@page { size: ' + sizeObj.w + 'mm ' + sizeObj.h + 'mm; margin: 0; }'
      + 'html,body { margin:0; padding:0; background:#fff; }'
      + '.pl { display:block; }'
      + '.pl + .pl { page-break-before: always; }'
      + '@media screen {'
      + '  body { background:#e5e7eb; padding:16px; text-align:center; }'
      + '  .pl { margin:0 auto 12px; box-shadow:0 1px 6px rgba(0,0,0,0.25); }'
      + '  .toolbar { margin-bottom:14px; }'
      + '  .toolbar button { padding:7px 16px; border:0.5px solid rgba(24,95,165,0.3); background:#185FA5; color:#fff; border-radius:6px; font-size:13px; cursor:pointer; font-family:sans-serif; }'
      + '  .hint { font-size:12px; color:#666; margin-top:10px; font-family:sans-serif; }'
      + '}'
      + '@media print { .toolbar, .hint { display:none !important; } }'
      + '</style></head><body>'
      + '<div class="toolbar"><button type="button" onclick="window.print()">Print ' + copyLabel + '</button></div>'
      + '<div id="pl-wrap"><div class="pl">' + labelHtml + '</div></div>'
      + '<div class="hint">' + copyLabel + ' &middot; ' + sizeObj.label + ' &middot; Xprinter XP-233B</div>'
      + '<script>'
      + '(function(){'
      + 'var COPIES=' + copies + ';'
      + 'var wrap=document.getElementById("pl-wrap");'
      + 'var first=wrap.firstChild;'
      + 'for(var i=1;i<COPIES;i++){ wrap.appendChild(first.cloneNode(true)); }'
      + 'var imgs=document.images, left=imgs.length;'
      + 'function fire(){ try{ window.focus(); window.print(); }catch(e){} }'
      + 'function done(){ left--; if(left<=0) setTimeout(fire,160); }'
      + 'if(left===0){ setTimeout(fire,200); }'
      + 'else { for(var j=0;j<imgs.length;j++){ var im=imgs[j];'
      + '  if(im.complete){ done(); } else { im.addEventListener("load",done); im.addEventListener("error",done); } } }'
      + '})();'
      // eslint-disable-next-line no-useless-concat -- split so a literal </script> can't terminate an enclosing script tag a bundler may parse
      + '</' + 'script></body></html>';
    w.document.open(); w.document.write(html); w.document.close();
  };

  var openScan = function() {
    setScanManualCode(""); setScanError(""); setSubPage("scan");
  };
  var openReports = function() {
    setSubPage("reports");
  };

  // Loads the active reports tab on demand. Cached per-tab in reportsData so
  // re-opening a tab is instant; the History tab re-fetches whenever its
  // filters change so the visible rows always match the filter inputs.
  var loadReport = useCallback(function(tab, filters) {
    setReportsLoading(function(prev){ var n = Object.assign({}, prev); n[tab] = true; return n; });
    setReportsError(function(prev){ var n = Object.assign({}, prev); n[tab] = ""; return n; });
    var url = "/api/assets/reports/" + tab;
    if (tab === "history") {
      var qs = [];
      if (filters && filters.from)   qs.push("from="   + encodeURIComponent(filters.from));
      if (filters && filters.to)     qs.push("to="     + encodeURIComponent(filters.to));
      if (filters && filters.action) qs.push("action=" + encodeURIComponent(filters.action));
      if (qs.length) url += "?" + qs.join("&");
    }
    return apiFetch(url, "GET", null, p.token)
      .then(function(d){
        setReportsData(function(prev){ var n = Object.assign({}, prev); n[tab] = d; return n; });
      })
      .catch(function(err){
        setReportsError(function(prev){ var n = Object.assign({}, prev); n[tab] = (err && err.message) || "Failed to load"; return n; });
      })
      .finally(function(){
        setReportsLoading(function(prev){ var n = Object.assign({}, prev); n[tab] = false; return n; });
      });
  }, [p.token]);

  // Trigger a load when the reports view opens and again when the tab or
  // history filters change. Static tabs cache after the first load; history
  // re-fetches because its filters are part of the request.
  useEffect(function() {
    if (subPage !== "reports") return;
    if (reportsTab === "history") { loadReport("history", historyFilters); return; }
    if (reportsData[reportsTab]) return; // cached
    loadReport(reportsTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subPage, reportsTab, historyFilters, loadReport]);

  // Download an .xlsx for the named report. fetch() with the Authorization
  // header (anchor <a download> can't carry headers), then trigger download
  // via a temporary <a> + object URL. Filename also set on the anchor as a
  // belt-and-braces measure alongside the server's Content-Disposition.
  var exportReport = async function(type) {
    setExporting(true);
    try {
      var qs = "";
      if (type === "history") {
        var parts = [];
        if (historyFilters.from)   parts.push("from="   + encodeURIComponent(historyFilters.from));
        if (historyFilters.to)     parts.push("to="     + encodeURIComponent(historyFilters.to));
        if (historyFilters.action) parts.push("action=" + encodeURIComponent(historyFilters.action));
        if (parts.length) qs = "&" + parts.join("&");
      }
      var res = await fetch(API + "/api/assets/reports/export?type=" + encodeURIComponent(type) + qs, {
        headers: { "Authorization": "Bearer " + p.token }
      });
      if (!res.ok) {
        var msg = "Export failed (" + res.status + ")";
        try { var body = await res.json(); if (body && body.error) msg = body.error; } catch(e){}
        throw new Error(msg);
      }
      var blob = await res.blob();
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = "assets_report_" + type + "_" + new Date().toISOString().slice(0,10) + ".xlsx";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert((err && err.message) || "Export failed");
    }
    setExporting(false);
  };
  // Manual fallback when the camera path is blocked (desktop, denied perms).
  // Accepts the same shapes as a real QR scan via parseAssetScanPayload.
  var goManualScan = function() {
    var code = parseAssetScanPayload(scanManualCode);
    if (!code) { setScanError("Enter a valid asset code like ARO-LT-0001"); return; }
    setSelectedAssetCode(code); setSubPage("detail"); setScanManualCode(""); setScanError("");
  };

  // ===== Style helpers =====
  var btnPrimary = { padding:"8px 16px", border:"0.5px solid rgba(24,95,165,0.3)", background:"#185FA5", color:"#fff",     borderRadius:8, fontSize:13, cursor:"pointer", fontWeight:500, fontFamily:"inherit" };
  var btnGhost   = { padding:"8px 14px", border:"0.5px solid rgba(0,0,0,0.1)",     background:"#fff",   color:"#1a1a1a",  borderRadius:8, fontSize:13, cursor:"pointer", fontWeight:500, fontFamily:"inherit" };
  var btnDanger  = { padding:"8px 14px", border:"0.5px solid rgba(163,45,45,0.3)", background:"#FCEBEB",color:"#A32D2D",  borderRadius:8, fontSize:13, cursor:"pointer", fontWeight:500, fontFamily:"inherit" };
  var inputStyle = { padding:"8px 12px", border:"0.5px solid rgba(0,0,0,0.1)",     borderRadius:8, fontSize:13, background:"#fff", fontFamily:"inherit", width:"100%", boxSizing:"border-box" };
  var fieldLabel = { fontSize:12, color:"#666", display:"block", marginBottom:6 };
  var cardWrap   = { background:"#fff", borderRadius:12, border:"0.5px solid rgba(0,0,0,0.1)" };
  var fmtEGP = function(n) {
    var v = Number(n) || 0;
    return v.toLocaleString("en-EG") + " EGP";
  };
  var statusBadge = function(s) {
    var palette = {
      active:      { bg:"#EAF6F0", color:"#0F6E56", label:"Active" },
      maintenance: { bg:"#FFF4E5", color:"#B45309", label:"Maintenance" },
      lost:        { bg:"#FCEBEB", color:"#A32D2D", label:"Lost" },
      retired:     { bg:"#F4F4F4", color:"#666",    label:"Retired" }
    };
    var pal = palette[s] || palette.retired;
    return <span style={{ fontSize:11, padding:"3px 8px", borderRadius:6, fontWeight:500, background:pal.bg, color:pal.color }}>{pal.label}</span>;
  };
  var rowHighlight = function(s) {
    if (s === "maintenance") return { background:"#FFFBEB" };
    if (s === "lost")        return { background:"#FEF2F2" };
    return null;
  };
  var prefixBadge = function(cat) {
    var code = (cat && cat.codePrefix) || "??";
    return <div style={{ width:30, height:30, background:"#E6F1FB", color:"#185FA5", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:11, flexShrink:0, fontFamily:"ui-monospace, SFMono-Regular, monospace" }}>{code}</div>;
  };

  // ===== Derived data =====
  var groupBuckets = (function() {
    var b = { all:0, it:0, furniture:0, appliance:0, other:0 };
    assets.forEach(function(a) {
      b.all += 1;
      var g = a.categoryId && a.categoryId.group;
      if (g && b[g] != null) b[g] += 1;
    });
    return b;
  })();
  var filteredAssets = (function() {
    var q = (searchQuery || "").trim().toLowerCase();
    return assets.filter(function(a) {
      if (groupFilter  && (!a.categoryId || a.categoryId.group !== groupFilter)) return false;
      if (statusFilter && a.status !== statusFilter) return false;
      if (branchFilter && String(a.branchId && (a.branchId._id || a.branchId)) !== String(branchFilter)) return false;
      if (!q) return true;
      var hayParts = [a.assetCode, a.name, a.serialNumber, a.currentCustodian && a.currentCustodian.name].filter(Boolean);
      return hayParts.join(" ").toLowerCase().indexOf(q) !== -1;
    });
  })();

  // Grouped list view. Buckets are keyed by the trimmed asset name (case-sensitive)
  // so e.g. 24 chairs all named "متحرك اسود" collapse into one parent row with a
  // ×N badge. Singletons render as plain rows. During an active search we flatten
  // grouping so the matching individual assets surface immediately.
  var isSearching = (searchQuery || "").trim().length > 0;
  var groupedRows = (function() {
    if (isSearching) {
      return filteredAssets.map(function(a){ return { kind:"single", asset:a }; });
    }
    var byName = [];
    var idx = {};
    filteredAssets.forEach(function(a) {
      var key = (a.name || "").trim();
      if (idx[key] == null) { idx[key] = byName.length; byName.push({ key:key, assets:[] }); }
      byName[idx[key]].assets.push(a);
    });
    return byName.map(function(g) {
      if (g.assets.length === 1) return { kind:"single", asset: g.assets[0] };
      // Group summary fields. Branch / status surface "Multiple" / "Mixed" when
      // not all members agree — keeps the parent row truthful at a glance.
      var first = g.assets[0];
      var branchIds = g.assets.map(function(a){ return String((a.branchId && (a.branchId._id || a.branchId)) || ""); });
      var sameBranch = branchIds.every(function(id){ return id === branchIds[0]; });
      var statuses = g.assets.map(function(a){ return a.status; });
      var sameStatus = statuses.every(function(s){ return s === statuses[0]; });
      var sumValue = g.assets.reduce(function(s,a){ return s + (Number(a.purchasePrice)||0); }, 0);
      return {
        kind: "group",
        key: g.key,
        name: g.key,
        count: g.assets.length,
        category: first.categoryId,
        sameBranch: sameBranch,
        branchName: sameBranch ? (first.branchId ? first.branchId.name : "—") : null,
        sameStatus: sameStatus,
        status: sameStatus ? statuses[0] : null,
        sumValue: sumValue,
        assets: g.assets
      };
    });
  })();
  var toggleGroup = function(key) {
    setExpandedGroups(function(prev){
      var next = Object.assign({}, prev);
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });
  };
  var mixedBadge = function(label) {
    return <span style={{ fontSize:11, padding:"3px 8px", borderRadius:6, fontWeight:500, background:"#F4F4F4", color:"#666" }}>{label}</span>;
  };
  var countBadge = function(n) {
    return <span style={{ fontSize:11, padding:"2px 8px", borderRadius:999, fontWeight:600, background:"#185FA5", color:"#fff", fontFamily:"ui-monospace, SFMono-Regular, monospace", marginLeft:8, flexShrink:0 }}>×{n}</span>;
  };
  var caret = function(open) {
    return <span style={{ display:"inline-block", width:14, fontSize:11, color:"#888", transform: open ? "rotate(90deg)" : "none", transition:"transform 0.12s", flexShrink:0 }}>▶</span>;
  };
  var stats = (function() {
    var total = assets.length;
    var totalValue = assets.reduce(function(s,a){ return s + (Number(a.purchasePrice)||0); }, 0);
    var activeCustodies = assets.filter(function(a){ return a.status==="active" && a.currentCustodian; }).length;
    var attention = assets.filter(function(a){ return a.status==="lost" || a.status==="maintenance"; }).length;
    return { total:total, totalValue:totalValue, activeCustodies:activeCustodies, attention:attention };
  })();

  // ===== Render =====
  return <div style={{padding:"24px 16px 40px",fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", direction:"ltr"}}>
    {/* Mobile layout rules for AssetTracker.
        CRITICAL: do NOT put JS block comments between the + operators in
        the style string below. The JS expression "+ comment + 'str'"
        parses the second + as unary plus on the next string, yielding NaN
        that poisons the CSS. CSS comments (inside the strings themselves)
        are fine.
        Classes are namespaced .at-* so they can't collide. Inline styles
        win over class styles, so each override uses !important. Desktop
        layout is untouched. */}
    <style>{""
+ "@media (max-width: 768px) {"
+ "  /* List view header stacks vertically. */"
+ "  .at-list-header { flex-direction: column !important; align-items: stretch !important; }"
+ "  /* Action buttons stay inline (3-up) by default mobile; only stack below 380px (see narrow block at the bottom). */"
+ "  .at-list-actions { flex-wrap: nowrap !important; gap: 6px !important; }"
+ "  .at-list-actions > button { flex: 1 1 0 !important; min-width: 0 !important; padding: 9px 6px !important; font-size: 12px !important; white-space: nowrap; }"
+ "  /* Stat cards: 2-up grid with reduced typography. */"
+ "  .at-stat-cards { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 8px !important; }"
+ "  .at-stat-card { min-width: 0 !important; flex: unset !important; padding: 10px 12px !important; }"
+ "  .at-stat-card .at-stat-label { font-size: 10px !important; margin-bottom: 4px !important; }"
+ "  .at-stat-card .at-stat-value { font-size: 16px !important; }"
+ "  /* Search + status + branch dropdowns stack 1-col. */"
+ "  .at-list-controls { grid-template-columns: 1fr !important; gap: 6px !important; }"
+ "  /* Mobile asset cards visible; desktop table hidden. */"
+ "  .at-list-mobile-cards { display: block !important; }"
+ "  .at-list-desktop-table { display: none !important; }"
+ "  /* Detail view: collapse the 2-col grid + flex the actions vertically. */"
+ "  .at-detail-header { flex-direction: column !important; align-items: stretch !important; gap: 10px !important; }"
+ "  .at-detail-header-actions { justify-content: flex-start !important; flex-wrap: wrap !important; }"
+ "  .at-detail-grid { grid-template-columns: 1fr !important; }"
+ "  .at-qr-card { max-width: 320px; margin: 0 auto; }"
+ "  .at-fields-grid { grid-template-columns: 1fr !important; gap: 10px !important; }"
+ "  .at-custody-actions { flex-direction: column !important; align-items: stretch !important; }"
+ "  .at-custody-actions > button { width: 100% !important; }"
+ "  /* Form view: fields stack, save/cancel full-width. */"
+ "  .at-form-grid { grid-template-columns: 1fr !important; gap: 10px !important; }"
+ "  .at-form-card { padding: 16px !important; }"
+ "  .at-form-actions { flex-direction: column !important; align-items: stretch !important; }"
+ "  .at-form-actions > button { width: 100% !important; }"
+ "  /* Scan card unbounded to viewport. */"
+ "  .at-scan-card { max-width: 100% !important; }"
+ "  /* Reports header stacks; tab strip already scrolls horizontally, just pin tab widths. */"
+ "  .at-reports-header { flex-direction: column !important; align-items: stretch !important; gap: 10px !important; }"
+ "  .at-reports-header > button { width: 100% !important; }"
+ "  .at-reports-tabs > button { flex-shrink: 0 !important; }"
+ "  .at-history-filters { grid-template-columns: 1fr !important; gap: 8px !important; }"
+ "  /* Report content tables get horizontal-scroll containers. */"
+ "  .at-report-content { overflow-x: auto; -webkit-overflow-scrolling: touch; }"
+ "  .at-report-content table { min-width: 560px; }"
+ "}"
+ "@media (max-width: 379px) {"
+ "  /* Very narrow phones: action buttons stack instead of fighting for the width. */"
+ "  .at-list-actions { flex-direction: column !important; align-items: stretch !important; flex-wrap: wrap !important; }"
+ "  .at-list-actions > button { flex: unset !important; width: 100% !important; padding: 9px 12px !important; font-size: 13px !important; }"
+ "}"
+ "@media (min-width: 769px) {"
+ "  /* Desktop: hide the mobile cards entirely. */"
+ "  .at-list-mobile-cards { display: none !important; }"
+ "}"
}</style>
    <div style={{maxWidth:1200,margin:"0 auto"}}>

      {/* ---------- LIST VIEW ---------- */}
      {subPage === "list" && (function() {
        var statCard = function(label, value) {
          return <div className="at-stat-card" style={Object.assign({}, cardWrap, { padding:"14px 16px", minWidth:160, flex:"1 1 200px" })}>
            <div className="at-stat-label" style={{fontSize:11, color:"#666", textTransform:"uppercase", letterSpacing:"0.4px", marginBottom:6}}>{label}</div>
            <div className="at-stat-value" style={{fontSize:20, fontWeight:600, color:"#1a1a1a"}}>{value}</div>
          </div>;
        };
        var pill = function(id, label, count) {
          var act = groupFilter === id;
          return <button key={id||"all"} type="button" onClick={function(){ setGroupFilter(id); }}
            style={{
              padding:"6px 14px", fontSize:13, borderRadius:999, cursor:"pointer", whiteSpace:"nowrap",
              border:"0.5px solid "+(act?"#185FA5":"rgba(0,0,0,0.1)"),
              background:act?"#185FA5":"#fff", color:act?"#fff":"#1a1a1a", fontWeight:500, fontFamily:"inherit"
            }}>
            {label} <span style={{opacity:0.6, marginLeft:4}}>({count})</span>
          </button>;
        };
        return <div>
          {/* Header */}
          <div className="at-list-header" style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:16}}>
            <div>
              {/* Title removed — the shared header renders it. The descriptive
                  line stays: it is context, not a duplicate of the page name. */}
              <div style={{fontSize:12,color:"#666"}}>All company-owned equipment, furniture, and appliances</div>
            </div>
            <div className="at-list-actions" style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button type="button" onClick={openReports} style={btnGhost}>Reports</button>
              <button type="button" onClick={openScan} style={btnGhost}>Scan QR</button>
              <button type="button" onClick={openNew} style={btnPrimary}>+ New Asset</button>
            </div>
          </div>

          {/* Stat cards. Total active value is an aggregate — Owner-only per
              the access spec; backend strips it for non-owners. Sales_admin
              sees the remaining three cards. */}
          <div className="at-stat-cards" style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:16}}>
            {(isOwnerFE(p.access) || roleHasFinanceReadsFE(p.cu)) && statCard("Total Active Value", activeValueStat == null ? "—" : fmtEGP(activeValueStat))}
            {statCard("Total assets",         stats.total)}
            {statCard("Active custodies",     stats.activeCustodies)}
            {statCard("Needs attention",      stats.attention)}
          </div>

          {/* Filter pills */}
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
            {pill("",          "All",         groupBuckets.all)}
            {pill("it",        "IT",          groupBuckets.it)}
            {pill("furniture", "Furniture",   groupBuckets.furniture)}
            {pill("appliance", "Appliances",  groupBuckets.appliance)}
            {pill("other",     "Other",       groupBuckets.other)}
          </div>

          {/* Search + dropdowns */}
          <div className="at-list-controls" style={{display:"grid",gridTemplateColumns:"1fr 160px 200px",gap:8,marginBottom:14,maxWidth:900}}>
            <input style={inputStyle} placeholder="Search code, name, or custodian…" value={searchQuery} onChange={function(e){setSearchQuery(e.target.value);}}/>
            <Select value={statusFilter} onChange={function(e){setStatusFilter(e.target.value);}} fontSize={13}
              options={[{ value:"", label:"All statuses" },{ value:"active", label:"Active" },{ value:"maintenance", label:"Maintenance" },{ value:"lost", label:"Lost" },{ value:"retired", label:"Retired" }]}/>
            <Select value={branchFilter} onChange={function(e){setBranchFilter(e.target.value);}} searchable fontSize={13}
              options={[{ value:"", label:"All branches" }].concat(branches.map(function(b){ return { value:b._id, label:b.name + " (" + b.code + ")" }; }))}/>
          </div>

          {/* Errors */}
          {loadError && <div style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"8px 12px",borderRadius:8,marginBottom:12}}>{loadError}</div>}

          {/* Desktop table — hidden via display:none on mobile (see CSS). */}
          <div className="at-list-desktop-table" style={Object.assign({}, cardWrap, { overflow:"hidden" })}>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1fr 120px 130px",gap:10,padding:"12px 16px",borderBottom:"0.5px solid rgba(0,0,0,0.08)",fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px",background:"#FAFAF9"}}>
              <div>Asset</div>
              <div>Custodian</div>
              <div>Branch</div>
              <div style={{textAlign:"center"}}>Status</div>
              <div style={{textAlign:"right"}}>Value</div>
            </div>
            {!loaded
              ? <div style={{padding:24,textAlign:"center",fontSize:13,color:"#666"}}>Loading…</div>
              : groupedRows.length === 0
                ? <div style={{padding:32,textAlign:"center",fontSize:13,color:"#666"}}>No matching assets</div>
                : groupedRows.map(function(row) {
                    var gridCols = "2fr 1.5fr 1fr 120px 130px";
                    var renderAssetRow = function(a, isChild) {
                      var rs = Object.assign({
                        display:"grid", gridTemplateColumns: gridCols, gap:10,
                        padding: isChild ? "10px 16px 10px 32px" : "12px 16px",
                        borderBottom:"0.5px solid rgba(0,0,0,0.05)",
                        fontSize:13, alignItems:"center", cursor:"pointer",
                        background: isChild ? "#FAFAF9" : "#fff"
                      }, rowHighlight(a.status) || {});
                      return <div key={a._id} onClick={function(e){ e.stopPropagation(); openDetail(a); }}
                        onMouseEnter={function(e){ if (!rowHighlight(a.status)) e.currentTarget.style.background = isChild ? "#F2F2EF" : "#F7F7F5"; }}
                        onMouseLeave={function(e){ if (!rowHighlight(a.status)) e.currentTarget.style.background = isChild ? "#FAFAF9" : "#fff"; }}
                        style={rs}>
                        <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                          {!isChild && prefixBadge(a.categoryId)}
                          {isChild && <span style={{width:30, flexShrink:0}}/>}
                          <div style={{minWidth:0}}>
                            <div style={{fontWeight:isChild?400:500,color:"#1a1a1a",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.name}</div>
                            <div style={{fontSize:11,color:"#666",fontFamily:"ui-monospace, SFMono-Regular, monospace"}}>{a.assetCode}</div>
                          </div>
                        </div>
                        <div style={{color:"#1a1a1a"}}>
                          {a.currentCustodian
                            ? a.currentCustodian.name
                            : (a.assignmentType === "shared"
                                ? <span style={{color:"#666",fontStyle:"italic"}}>Shared</span>
                                : <span style={{color:"#A32D2D"}}>Unassigned</span>)
                          }
                        </div>
                        <div style={{color:"#666"}}>{a.branchId ? a.branchId.name : "—"}</div>
                        <div style={{textAlign:"center"}}>{statusBadge(a.status)}</div>
                        <div style={{textAlign:"right",color:"#1a1a1a",fontFamily:"ui-monospace, SFMono-Regular, monospace",fontSize:12}}>{fmtEGP(a.purchasePrice)}</div>
                      </div>;
                    };
                    if (row.kind === "single") return renderAssetRow(row.asset, false);
                    // Group parent row: click toggles expand/collapse, children render below.
                    var open = !!expandedGroups[row.key];
                    var parentStyle = {
                      display:"grid", gridTemplateColumns: gridCols, gap:10,
                      padding:"12px 16px", borderBottom:"0.5px solid rgba(0,0,0,0.05)",
                      fontSize:13, alignItems:"center", cursor:"pointer", background:"#fff"
                    };
                    return <div key={"g:"+row.key}>
                      <div onClick={function(){ toggleGroup(row.key); }}
                        onMouseEnter={function(e){ e.currentTarget.style.background = "#F7F7F5"; }}
                        onMouseLeave={function(e){ e.currentTarget.style.background = "#fff"; }}
                        style={parentStyle}>
                        <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                          {prefixBadge(row.category)}
                          <div style={{minWidth:0, display:"flex", alignItems:"center", gap:6}}>
                            {caret(open)}
                            <div style={{minWidth:0}}>
                              <div style={{fontWeight:500,color:"#1a1a1a",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",display:"flex",alignItems:"center"}}>
                                <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{row.name}</span>
                                {countBadge(row.count)}
                              </div>
                              <div style={{fontSize:11,color:"#666"}}>{open ? "Click to collapse" : "Click to expand"}</div>
                            </div>
                          </div>
                        </div>
                        <div style={{color:"#666",fontStyle:"italic",fontSize:12}}>—</div>
                        <div style={{color:"#666"}}>{row.sameBranch ? row.branchName : <span style={{fontStyle:"italic"}}>Multiple</span>}</div>
                        <div style={{textAlign:"center"}}>{row.sameStatus ? statusBadge(row.status) : mixedBadge("Mixed")}</div>
                        <div style={{textAlign:"right",color:"#1a1a1a",fontFamily:"ui-monospace, SFMono-Regular, monospace",fontSize:12,fontWeight:600}}>{fmtEGP(row.sumValue)}</div>
                      </div>
                      {open && row.assets.map(function(a){ return renderAssetRow(a, true); })}
                    </div>;
                  })
            }
          </div>

          {/* Mobile card list — hidden on desktop via CSS. Same data shaped as
              tap-friendly cards: three rows per card (asset+code, custodian+
              status, branch+value). Spec calls out horizontal-scroll as the
              alternative; the card form was the cleaner choice for phone UX. */}
          <div className="at-list-mobile-cards" style={{ display: "none", flexDirection: "column", gap: 8 }}>
            {!loaded
              ? <div style={Object.assign({}, cardWrap, {padding:20, textAlign:"center", fontSize:13, color:"#666"})}>Loading…</div>
              : groupedRows.length === 0
                ? <div style={Object.assign({}, cardWrap, {padding:24, textAlign:"center", fontSize:13, color:"#666"})}>No matching assets</div>
                : groupedRows.map(function(row) {
                    var rowFlex = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, minWidth: 0 };
                    var renderAssetCard = function(a, isChild) {
                      var cs = Object.assign({}, cardWrap, {
                        padding: "12px 14px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 8,
                        marginLeft: isChild ? 16 : 0,
                        background: isChild ? "#FAFAF9" : "#fff"
                      }, rowHighlight(a.status) || {});
                      var custodianBit = a.currentCustodian
                        ? <span style={{fontSize:12, color:"#1a1a1a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{a.currentCustodian.name}</span>
                        : (a.assignmentType === "shared"
                            ? <span style={{fontSize:12, color:"#666", fontStyle:"italic"}}>Shared</span>
                            : <span style={{fontSize:12, color:"#A32D2D"}}>Unassigned</span>);
                      return <div key={a._id} onClick={function(e){ e.stopPropagation(); openDetail(a); }} style={cs}>
                        <div style={{display:"flex", alignItems:"center", gap:10, minWidth:0}}>
                          {!isChild && prefixBadge(a.categoryId)}
                          <div style={{minWidth:0, flex:1}}>
                            <div style={{fontSize:14, fontWeight:isChild?400:500, color:"#1a1a1a", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{a.name}</div>
                            <div style={{fontSize:11, color:"#666", fontFamily:"ui-monospace, SFMono-Regular, monospace"}}>{a.assetCode}</div>
                          </div>
                        </div>
                        <div style={rowFlex}>
                          {custodianBit}
                          {statusBadge(a.status)}
                        </div>
                        <div style={rowFlex}>
                          <span style={{fontSize:12, color:"#666", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{a.branchId ? a.branchId.name : "—"}</span>
                          <span style={{fontSize:12, color:"#1a1a1a", fontFamily:"ui-monospace, SFMono-Regular, monospace"}}>{fmtEGP(a.purchasePrice)}</span>
                        </div>
                      </div>;
                    };
                    if (row.kind === "single") return renderAssetCard(row.asset, false);
                    var open = !!expandedGroups[row.key];
                    var parentCard = Object.assign({}, cardWrap, {
                      padding: "12px 14px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 8
                    });
                    return <div key={"g:"+row.key} style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      <div onClick={function(){ toggleGroup(row.key); }} style={parentCard}>
                        <div style={{display:"flex", alignItems:"center", gap:10, minWidth:0}}>
                          {prefixBadge(row.category)}
                          <div style={{minWidth:0, flex:1, display:"flex", alignItems:"center", gap:6}}>
                            {caret(open)}
                            <div style={{minWidth:0, flex:1, display:"flex", alignItems:"center"}}>
                              <span style={{fontSize:14, fontWeight:500, color:"#1a1a1a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{row.name}</span>
                              {countBadge(row.count)}
                            </div>
                          </div>
                        </div>
                        <div style={rowFlex}>
                          <span style={{fontSize:12, color:"#666", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{row.sameBranch ? row.branchName : "Multiple branches"}</span>
                          {row.sameStatus ? statusBadge(row.status) : mixedBadge("Mixed")}
                        </div>
                        <div style={rowFlex}>
                          <span style={{fontSize:12, color:"#666"}}>{open ? "Tap to collapse" : "Tap to expand"}</span>
                          <span style={{fontSize:12, color:"#1a1a1a", fontFamily:"ui-monospace, SFMono-Regular, monospace", fontWeight:600}}>{fmtEGP(row.sumValue)}</span>
                        </div>
                      </div>
                      {open && row.assets.map(function(a){ return renderAssetCard(a, true); })}
                    </div>;
                  })
            }
          </div>
        </div>;
      })()}

      {/* ---------- FORM VIEW (create + edit) ---------- */}
      {subPage === "new" && (function() {
        var pickedCat = categories.find(function(c){ return String(c._id) === String(form.categoryId); });
        // Auto-set assignment type when category changes (only in create mode)
        var onCategoryChange = function(e) {
          var newId = e.target.value;
          var cat = categories.find(function(c){ return String(c._id) === String(newId); });
          var next = Object.assign({}, form, { categoryId:newId });
          if (formMode === "create" && cat) next.assignmentType = cat.defaultAssignmentType || "personal";
          setForm(next);
        };
        var custodianCandidates = custodianRoster.filter(function(u){ return u && u.active !== false; });
        return <div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <button type="button" onClick={goList} style={btnGhost}>← Back</button>
            <div style={{fontSize:18,fontWeight:500,color:"#1a1a1a"}}>
              {formMode === "edit" ? "Edit asset" : "New asset"}
            </div>
          </div>

          <div className="at-form-card" style={Object.assign({}, cardWrap, { padding:22, maxWidth:760 })}>
            <div className="at-form-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
              <div>
                <label style={fieldLabel}>Name *</label>
                <input style={inputStyle} value={form.name} onChange={function(e){setForm(Object.assign({},form,{name:e.target.value}));}} placeholder="Dell Latitude 5540"/>
              </div>
              <div>
                <label style={fieldLabel}>Category *</label>
                <SearchableSelect value={form.categoryId} onChange={function(v){ onCategoryChange({ target:{ value:v } }); }}
                  options={categories.map(function(c){ return { value:c._id, label:c.name+" ("+c.codePrefix+")", group:c.group }; })}
                  emptyLabel="Choose…" placeholder="Search category…" width="100%" maxWidth={9999} fontSize={14}/>
              </div>
              <div>
                <label style={fieldLabel}>Branch *</label>
                <Select value={form.branchId} onChange={function(e){setForm(Object.assign({},form,{branchId:e.target.value}));}} searchable fontSize={13}
                  options={[{ value:"", label:"Choose…" }].concat(branches.map(function(b){ return { value:b._id, label:b.name + " (" + b.code + ")" }; }))}/>
              </div>
              <div>
                <label style={fieldLabel}>Assignment type</label>
                <Select value={form.assignmentType} onChange={function(e){setForm(Object.assign({},form,{assignmentType:e.target.value}));}} fontSize={13}
                  options={[{ value:"personal", label:"Personal" },{ value:"shared", label:"Shared" }]}/>
              </div>
              <div>
                <label style={fieldLabel}>Purchase price (EGP)</label>
                <input style={inputStyle} type="number" min="0" step="1" value={form.purchasePrice} onChange={function(e){setForm(Object.assign({},form,{purchasePrice:e.target.value}));}}/>
              </div>
              <div>
                <label style={fieldLabel}>Purchase date</label>
                <input style={inputStyle} type="date" value={form.purchaseDate} onChange={function(e){setForm(Object.assign({},form,{purchaseDate:e.target.value}));}}/>
              </div>
              <div>
                <label style={fieldLabel}>Supplier <span style={{color:"#888"}}>(optional)</span></label>
                <input style={inputStyle} value={form.supplier} onChange={function(e){setForm(Object.assign({},form,{supplier:e.target.value}));}} placeholder="B.TECH"/>
              </div>
              <div>
                <label style={fieldLabel}>Serial number <span style={{color:"#888"}}>(optional)</span></label>
                <input style={inputStyle} value={form.serialNumber} onChange={function(e){setForm(Object.assign({},form,{serialNumber:e.target.value}));}}/>
              </div>
              {/* Initial custodian — only on create + when assignmentType is personal. */}
              {formMode === "create" && form.assignmentType === "personal" && <div style={{gridColumn:"1 / -1"}}>
                <label style={fieldLabel}>Initial custodian *</label>
                <SearchableSelect value={form.currentCustodian} onChange={function(v){setForm(Object.assign({},form,{currentCustodian:v}));}}
                  options={custodianCandidates.map(function(u){ return { value:u._id, label:u.name+" ("+u.username+")" }; })}
                  emptyLabel="Choose a user…" placeholder="Search user…" width="100%" maxWidth={9999} fontSize={14}/>
                <div style={{fontSize:11,color:"#888",marginTop:4}}>Personal active assets require a custodian.</div>
              </div>}
              <div style={{gridColumn:"1 / -1"}}>
                <label style={fieldLabel}>Notes <span style={{color:"#888"}}>(optional)</span></label>
                <textarea style={Object.assign({}, inputStyle, { minHeight:70, resize:"vertical" })} value={form.notes} onChange={function(e){setForm(Object.assign({},form,{notes:e.target.value}));}}/>
              </div>
            </div>

            <div className="at-form-actions" style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <button type="button" onClick={saveForm} disabled={formSaving} style={Object.assign({}, btnPrimary, {opacity:formSaving?0.6:1, cursor:formSaving?"not-allowed":"pointer"})}>
                {formSaving ? "Saving…" : (formMode === "edit" ? "Save changes" : "Create asset")}
              </button>
              <button type="button" onClick={goList} style={btnGhost}>Cancel</button>
              {formError && <span style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{formError}</span>}
              {pickedCat && formMode === "create" && <span style={{fontSize:11,color:"#666"}}>Code will be auto-generated (ARO-{pickedCat.codePrefix}-####)</span>}
            </div>
          </div>
        </div>;
      })()}

      {/* ---------- SCAN VIEW ---------- */}
      {subPage === "scan" && (function() {
        return <div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <button type="button" onClick={goList} style={btnGhost}>← Back</button>
            <div style={{fontSize:18,fontWeight:500,color:"#1a1a1a"}}>Scan asset QR</div>
          </div>
          <div className="at-scan-card" style={Object.assign({}, cardWrap, { padding:18, maxWidth:520 })}>
            <div style={{fontSize:12,color:"#666",marginBottom:12,lineHeight:1.5}}>
              Point your camera at an asset's QR label. The app jumps to that asset on the first read and turns the camera off. On desktop or if camera access is denied, type or paste the code manually below.
            </div>
            {/* html5-qrcode mounts its <video> inside this div by id. The
                element must exist before the dynamic import resolves, hence
                rendering it whenever subPage==="scan" even before the lib
                lands. Square aspect keeps the framing reticle centred. */}
            <div id="asset-scanner-region" style={{ width:"100%", aspectRatio:"1/1", maxWidth:360, margin:"0 auto", background:"#0d0d0d", borderRadius:8, overflow:"hidden" }}/>
            {scanError && <div style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"8px 12px",borderRadius:8,marginTop:12}}>{scanError}</div>}
            <div style={{marginTop:18,paddingTop:14,borderTop:"0.5px dashed rgba(0,0,0,0.1)"}}>
              <div style={{fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px",marginBottom:8}}>Manual entry</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <input style={Object.assign({}, inputStyle, {flex:1, minWidth:200, fontFamily:"ui-monospace, SFMono-Regular, monospace"})} value={scanManualCode} onChange={function(e){setScanManualCode(e.target.value);}} placeholder="ARO-LT-0001" onKeyDown={function(e){ if (e.key === "Enter") goManualScan(); }}/>
                <button type="button" onClick={goManualScan} style={btnPrimary}>Go</button>
              </div>
            </div>
          </div>
        </div>;
      })()}

      {/* ---------- DETAIL VIEW ---------- */}
      {subPage === "detail" && (function() {
        if (detailLoading || (!detailAsset && !detailError)) {
          return <div>
            <button type="button" onClick={goList} style={Object.assign({}, btnGhost, {marginBottom:16})}>← Back</button>
            <div style={{fontSize:13,color:"#666"}}>Loading…</div>
          </div>;
        }
        if (detailError) {
          return <div>
            <button type="button" onClick={goList} style={Object.assign({}, btnGhost, {marginBottom:16})}>← Back</button>
            <div style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"10px 14px",borderRadius:8}}>{detailError}</div>
          </div>;
        }
        var a = detailAsset;
        var cat = a.categoryId || {};
        var br  = a.branchId   || {};
        var cust= a.currentCustodian;
        var dt  = a.purchaseDate ? new Date(a.purchaseDate).toLocaleDateString("en-GB") : "—";
        var label = function(k){ return <div style={{fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px",marginBottom:4}}>{k}</div>; };
        var value = function(v){ return <div style={{fontSize:13,color:"#1a1a1a",marginBottom:14}}>{v || "—"}</div>; };
        var canEdit = a.status !== "retired";
        return <div>
          {/* Header */}
          <div className="at-detail-header" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",gap:12,minWidth:0}}>
              <button type="button" onClick={goList} style={btnGhost}>← Back</button>
              <div style={{minWidth:0}}>
                <div style={{fontSize:18,fontWeight:500,color:"#1a1a1a",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.name}</div>
                <div style={{fontSize:12,color:"#666",fontFamily:"ui-monospace, SFMono-Regular, monospace",marginTop:2}}>{a.assetCode}</div>
                {/* Who registered / last edited it — "by Name — DD/MM/YYYY h:mm AM/PM" (owner ruling 2026-11-02).
                    Stamps are additive: an asset created before them shows "by —" for the creator and no edit line. */}
                <div style={{fontSize:11,color:"#888",marginTop:4,display:"flex",gap:10,flexWrap:"wrap"}}>
                  <span data-asset-audit="created">Registered {auditCaption(a.createdByName || userNameOf(a.createdByUserId, custodianRoster), a.createdAt)}</span>
                  {(a.updatedByUserId || a.updatedByName) ? <span data-asset-audit="edited">Edited {auditCaption(a.updatedByName || userNameOf(a.updatedByUserId, custodianRoster), a.updatedAt)}</span> : null}
                </div>
              </div>
            </div>
            <div className="at-detail-header-actions" style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              {statusBadge(a.status)}
              {canEdit && <button type="button" onClick={function(){ openEdit(a); }} style={btnGhost}>Edit</button>}
              {canEdit && <button type="button" onClick={doDelete} disabled={deleting} style={Object.assign({}, btnDanger, {opacity:deleting?0.6:1, cursor:deleting?"not-allowed":"pointer"})}>{deleting ? "..." : "Retire"}</button>}
            </div>
          </div>

          <div className="at-detail-grid" style={{display:"grid",gridTemplateColumns:"260px 1fr",gap:16,alignItems:"start"}}>
            {/* Left: QR card */}
            <div className="at-qr-card" style={Object.assign({}, cardWrap, { padding:18 })}>
              <div style={{fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px",marginBottom:10}}>QR Code</div>
              <div style={{aspectRatio:"1/1", background:"#fff", border:"0.5px solid rgba(0,0,0,0.08)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", padding:10}}>
                {qrDataUrl
                  ? <img src={qrDataUrl} alt={"QR for " + a.assetCode} style={{width:"100%", height:"100%", display:"block", imageRendering:"pixelated"}}/>
                  : <div style={{fontSize:12,color:"#888"}}>Generating…</div>
                }
              </div>
              {/* Print opens the label dialog (size dropdown, element toggles,
                  copy count, live preview). PNG download is unchanged — its
                  filename uses the slugified asset name as a prefix so a folder
                  of downloaded labels is human-readable, not just code-soup. */}
              <div style={{display:"flex",gap:6,marginTop:10}}>
                <button type="button" onClick={openPrintDialog} disabled={!styledQrUrl} style={Object.assign({}, btnPrimary, {flex:1, padding:"7px 12px", opacity:styledQrUrl?1:0.55, cursor:styledQrUrl?"pointer":"not-allowed"})}>Print QR</button>
                <a href={qrDataUrl || "#"} download={slugifyAssetName(a.name) + a.assetCode + ".png"} onClick={function(e){ if (!qrDataUrl) e.preventDefault(); }} style={Object.assign({}, btnGhost, {padding:"7px 12px", textDecoration:"none", textAlign:"center", display:"inline-flex", alignItems:"center", justifyContent:"center", opacity:qrDataUrl?1:0.55, pointerEvents:qrDataUrl?"auto":"none"})}>PNG</a>
              </div>
              <div style={{fontSize:10,color:"#888",marginTop:10,wordBreak:"break-all",fontFamily:"ui-monospace, SFMono-Regular, monospace",lineHeight:1.4}}>{a.qrCodeData}</div>
            </div>

            {/* Right: Fields + custodian card */}
            <div style={Object.assign({}, cardWrap, { padding:20 })}>
              <div className="at-fields-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
                <div>
                  {label("Category")}
                  {value((cat.name || "—") + (cat.codePrefix ? " ("+cat.codePrefix+")" : ""))}
                  {label("Branch")}
                  {value(br.name ? br.name + " ("+br.code+")" : "—")}
                  {label("Assignment type")}
                  {value(a.assignmentType === "shared" ? "Shared" : "Personal")}
                  {label("Purchase price")}
                  {value(fmtEGP(a.purchasePrice))}
                </div>
                <div>
                  {label("Purchase date")}
                  {value(dt)}
                  {label("Supplier")}
                  {value(a.supplier)}
                  {label("Serial number")}
                  {value(a.serialNumber)}
                  {label("Notes")}
                  {value(a.notes)}
                </div>
              </div>

              {/* Custodian card */}
              <div style={{marginTop:10,padding:14,background:"#FAFAF9",border:"0.5px solid rgba(0,0,0,0.06)",borderRadius:10}}>
                <div style={{fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px",marginBottom:8}}>Current custodian</div>
                {cust ? <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:36,height:36,borderRadius:"50%",background:"#E6F1FB",color:"#185FA5",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:14}}>{(cust.name||"?")[0]}</div>
                  <div>
                    <div style={{fontSize:13,fontWeight:500,color:"#1a1a1a"}}>{cust.name}</div>
                    <div style={{fontSize:11,color:"#666"}}>{cust.username} · {cust.role}</div>
                  </div>
                </div> : <div style={{fontSize:13,color:"#666",fontStyle:"italic"}}>{a.assignmentType === "shared" ? "Shared asset — no personal custodian" : "Unassigned"}</div>}
              </div>

              {/* Custody actions */}
              <div style={{marginTop:14,paddingTop:14,borderTop:"0.5px dashed rgba(0,0,0,0.1)"}}>
                <div style={{fontSize:11,color:"#666",textTransform:"uppercase",letterSpacing:"0.3px",marginBottom:10}}>Custody actions</div>
                <div className="at-custody-actions" style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                  {/* Transfer — only for personal assets that aren't lost/retired */}
                  {a.status !== "retired" && a.status !== "lost" && a.assignmentType === "personal" &&
                    <button type="button" onClick={openTransferModal} disabled={statusBusy} style={Object.assign({}, btnPrimary, {opacity:statusBusy?0.6:1, cursor:statusBusy?"not-allowed":"pointer"})}>
                      Transfer custody
                    </button>
                  }
                  {/* Return — only when someone holds custody */}
                  {cust && a.status !== "retired" &&
                    <button type="button" onClick={doReturn} disabled={statusBusy} style={Object.assign({}, btnGhost, {opacity:statusBusy?0.6:1, cursor:statusBusy?"not-allowed":"pointer"})}>
                      Return to warehouse
                    </button>
                  }
                  {/* Status flips — gated by current status */}
                  {a.status !== "active" && a.status !== "retired" &&
                    <button type="button" onClick={function(){doMarkStatus("active");}} disabled={statusBusy} style={Object.assign({}, btnGhost, {opacity:statusBusy?0.6:1, cursor:statusBusy?"not-allowed":"pointer"})}>
                      Mark active
                    </button>
                  }
                  {a.status !== "maintenance" && a.status !== "retired" &&
                    <button type="button" onClick={function(){doMarkStatus("maintenance");}} disabled={statusBusy} style={Object.assign({}, btnGhost, {opacity:statusBusy?0.6:1, cursor:statusBusy?"not-allowed":"pointer"})}>
                      Mark maintenance
                    </button>
                  }
                  {a.status !== "lost" && a.status !== "retired" &&
                    <button type="button" onClick={function(){if(window.confirm("Mark this asset as lost? The custodian will be cleared.")){doMarkStatus("lost");}}} disabled={statusBusy} style={Object.assign({}, btnDanger, {opacity:statusBusy?0.6:1, cursor:statusBusy?"not-allowed":"pointer"})}>
                      Mark lost
                    </button>
                  }
                  {statusError && <span style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"4px 10px",borderRadius:8,fontWeight:500}}>{statusError}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* ---------- Custody History timeline ---------- */}
          <div style={Object.assign({}, cardWrap, { padding:20, marginTop:16 })}>
            <div style={{fontSize:13,fontWeight:500,color:"#1a1a1a",marginBottom:14}}>Custody history</div>
            {historyLoading
              ? <div style={{fontSize:12,color:"#666"}}>Loading…</div>
              : historyError
                ? <div style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"8px 12px",borderRadius:8}}>{historyError}</div>
                : historyRows.length === 0
                  ? <div style={{fontSize:12,color:"#666",fontStyle:"italic"}}>No events recorded.</div>
                  : <div>{historyRows.map(function(h, idx) {
                      var ACTION_LABELS = {
                        registered:     { label:"Registered",     color:"#185FA5", bg:"#E6F1FB" },
                        assigned:       { label:"Assigned",       color:"#0F6E56", bg:"#EAF6F0" },
                        transferred:    { label:"Transferred",    color:"#185FA5", bg:"#E6F1FB" },
                        returned:       { label:"Returned",       color:"#7A4FB4", bg:"#F2EBFB" },
                        marked_lost:    { label:"Marked lost",    color:"#A32D2D", bg:"#FCEBEB" },
                        retired:        { label:"Retired",        color:"#666",    bg:"#F4F4F4" },
                        status_changed: { label:"Status changed", color:"#B45309", bg:"#FFF4E5" }
                      };
                      var meta = ACTION_LABELS[h.action] || { label:h.action, color:"#666", bg:"#F4F4F4" };
                      var when = new Date(h.performedAt || h.createdAt);
                      var whenStr = fmtDateTime12(when, undefined, { sep:", " });
                      var byName = userNameOf(h.performedBy) || "System";
                      var from = h.fromUserId && h.fromUserId.name;
                      var to   = h.toUserId   && h.toUserId.name;
                      var fromTo;
                      if (from && to)      fromTo = from + " → " + to;
                      else if (to)         fromTo = "Warehouse → " + to;
                      else if (from)       fromTo = from + " → Warehouse";
                      else                 fromTo = null;
                      return <div key={h._id} style={{display:"flex",gap:12,padding:"12px 0",borderBottom: idx === historyRows.length-1 ? "none" : "0.5px solid rgba(0,0,0,0.05)"}}>
                        <div style={{flexShrink:0,width:10,marginTop:6}}>
                          <div style={{width:10,height:10,borderRadius:"50%",background:meta.color}}/>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",gap:8,alignItems:"baseline",flexWrap:"wrap"}}>
                            <span style={{fontSize:12,padding:"3px 8px",borderRadius:6,fontWeight:500,background:meta.bg,color:meta.color}}>{meta.label}</span>
                            {fromTo && <span style={{fontSize:13,color:"#1a1a1a"}}>{fromTo}</span>}
                          </div>
                          {h.notes && <div style={{fontSize:12,color:"#666",marginTop:4,lineHeight:1.4}}>{h.notes}</div>}
                          <div style={{fontSize:11,color:"#888",marginTop:4}}>by {byName} · {whenStr}</div>
                        </div>
                      </div>;
                    })}</div>
            }
          </div>

          {/* ---------- Transfer custody modal ---------- */}
          {showTransferModal && <div className="crm-modal" style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}} onClick={closeTransferModal}>
            <div onClick={function(e){e.stopPropagation();}} style={{background:"#fff",borderRadius:12,padding:22,width:460,maxWidth:"100%",border:"0.5px solid rgba(0,0,0,0.1)"}}>
              <div style={{fontSize:16,fontWeight:500,color:"#1a1a1a",marginBottom:6}}>Transfer custody</div>
              <div style={{fontSize:12,color:"#666",marginBottom:16}}>{a.name} · <span style={{fontFamily:"ui-monospace, SFMono-Regular, monospace"}}>{a.assetCode}</span></div>
              <div style={{marginBottom:12}}>
                <label style={fieldLabel}>Transfer to *</label>
                <Select value={transferToUserId} onChange={function(e){setTransferToUserId(e.target.value);}} searchable fontSize={13}
                  options={[{ value:"", label:"Choose a user…" }].concat(custodianRoster.filter(function(u){
                    if (!u || u.active === false) return false;
                    // Hide the current custodian from the list — transferring to themself isn't a transfer.
                    if (cust && String(u._id) === String(cust._id)) return false;
                    return true;
                  }).map(function(u){
                    return { value:u._id, label:u.name + " (" + u.username + " · " + u.role + ")" };
                  }))}/>
              </div>
              <div style={{marginBottom:18}}>
                <label style={fieldLabel}>Notes <span style={{color:"#888"}}>(optional)</span></label>
                <textarea style={Object.assign({}, inputStyle, { minHeight:60, resize:"vertical" })} value={transferNotes} onChange={function(e){setTransferNotes(e.target.value);}} placeholder="Reason for the transfer, condition of the asset, etc."/>
              </div>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end",flexWrap:"wrap",alignItems:"center"}}>
                {transferError && <span style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"4px 10px",borderRadius:8,fontWeight:500,marginRight:"auto"}}>{transferError}</span>}
                <button type="button" onClick={closeTransferModal} disabled={transferSaving} style={btnGhost}>Cancel</button>
                <button type="button" onClick={doTransfer} disabled={transferSaving} style={Object.assign({}, btnPrimary, {opacity:transferSaving?0.6:1, cursor:transferSaving?"not-allowed":"pointer"})}>
                  {transferSaving ? "Transferring…" : "Transfer"}
                </button>
              </div>
            </div>
          </div>}

          {/* ---------- PRINT LABEL DIALOG (Xprinter XP-233B) ---------- */}
          {showPrintDialog && <div className="crm-modal" style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}} onClick={closePrintDialog}>
            <div onClick={function(e){e.stopPropagation();}} style={{background:"#fff",borderRadius:12,width:840,maxWidth:"100%",maxHeight:"92vh",overflowY:"auto",border:"0.5px solid rgba(0,0,0,0.1)"}}>
              {(function(){
                var sizeObj = LABEL_SIZES.filter(function(z){ return z.id === printSize; })[0] || LABEL_SIZES[2];
                var allowed = labelAllowedElements(sizeObj.id);
                var copies = Math.max(1, Math.min(99, parseInt(printCopies, 10) || 1));
                var previewHtml = currentLabelHTML();
                var PS = 1.3, MMPX = 3.7795;          // CSS px per mm × preview zoom
                var boxW = sizeObj.w * MMPX * PS, boxH = sizeObj.h * MMPX * PS;
                var suppressed = LABEL_ELEMENTS.some(function(el){
                  return el.key !== "qr" && printElements[el.key] && !allowed[el.key];
                });
                var copyLabel = copies === 1 ? "Print label" : "Print " + copies + " copies";
                var pill = { fontSize:10, fontWeight:600, padding:"1px 6px", borderRadius:5, marginLeft:"auto", whiteSpace:"nowrap" };
                return <div style={{padding:22}}>
                  {/* Header */}
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                    <div>
                      <div style={{fontSize:16,fontWeight:500,color:"#1a1a1a"}}>Print label</div>
                      <div style={{fontSize:12,color:"#666",marginTop:3}}>{a.name} · <span style={{fontFamily:"ui-monospace, SFMono-Regular, monospace"}}>{a.assetCode}</span></div>
                    </div>
                    <button type="button" onClick={closePrintDialog} aria-label="Close" style={{border:"none",background:"transparent",fontSize:22,lineHeight:1,color:"#999",cursor:"pointer",padding:"0 4px"}}>×</button>
                  </div>

                  <div style={{display:"flex",gap:20,flexWrap:"wrap",marginTop:16}}>
                    {/* ----- Controls ----- */}
                    <div style={{flex:"1 1 260px",minWidth:240}}>
                      <div style={{marginBottom:14}}>
                        <label style={fieldLabel}>Label size</label>
                        <Select value={printSize} onChange={function(e){ setPrintSize(e.target.value); }} fontSize={13}
                          options={LABEL_SIZES.map(function(z){ return { value:z.id, label:z.label + (z.id === "40x30" ? " (default)" : "") }; })}/>
                      </div>

                      <div style={{marginBottom:16}}>
                        <label style={fieldLabel}>Number of copies</label>
                        <input type="number" min="1" max="99" style={Object.assign({}, inputStyle, {width:120})}
                          value={printCopies}
                          onChange={function(e){ setPrintCopies(e.target.value); }}
                          onBlur={function(e){ setPrintCopies(String(Math.max(1, Math.min(99, parseInt(e.target.value, 10) || 1)))); }}/>
                      </div>

                      <label style={fieldLabel}>Show on label</label>
                      <div style={{display:"flex",flexDirection:"column",gap:1}}>
                        {LABEL_ELEMENTS.map(function(el){
                          var locked = !!el.locked;
                          var fits = locked || !!allowed[el.key];
                          var checked = locked ? true : !!printElements[el.key];
                          var disabled = locked || !fits;
                          return <label key={el.key} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,padding:"5px 6px",borderRadius:6,cursor:disabled?"default":"pointer",color:disabled?"#9aa0a6":"#1a1a1a"}}>
                            <input type="checkbox" checked={checked} disabled={disabled}
                              onChange={function(){ if (disabled) return; setPrintElements(function(prev){ var n = Object.assign({}, prev); n[el.key] = !n[el.key]; return n; }); }}/>
                            <span>{el.label}</span>
                            {locked && <span style={Object.assign({}, pill, {color:"#185FA5",background:"#E6F1FB"})}>Required</span>}
                            {!locked && !fits && <span style={Object.assign({}, pill, {color:"#888",background:"#F1F1F0"})}>Hidden at this size</span>}
                          </label>;
                        })}
                      </div>
                    </div>

                    {/* ----- Live preview ----- */}
                    <div style={{flex:"1 1 330px",minWidth:300}}>
                      <label style={fieldLabel}>Live preview</label>
                      <div style={{background:"#e5e7eb",borderRadius:10,padding:18,display:"flex",alignItems:"center",justifyContent:"center",minHeight:boxH+36}}>
                        {styledQrUrl
                          ? <div style={{width:boxW,height:boxH,position:"relative",flex:"0 0 auto"}}>
                              <div style={{position:"absolute",top:0,left:0,transform:"scale("+PS+")",transformOrigin:"top left",boxShadow:"0 1px 8px rgba(0,0,0,0.28)"}}
                                dangerouslySetInnerHTML={{__html: previewHtml}}/>
                            </div>
                          : <div style={{fontSize:12,color:"#666"}}>Generating QR…</div>
                        }
                      </div>
                      <div style={{fontSize:11,color:"#888",marginTop:8,textAlign:"center"}}>
                        {sizeObj.label}{suppressed ? " · some checked elements don't fit this size" : ""}
                      </div>
                    </div>
                  </div>

                  {/* NEVER SILENT. On the app the popup this dialog used to
                      open cannot exist, and the alert it fell back to gave
                      advice about a browser setting there is no way to change. */}
                  {labelPdfErr && <div style={{marginTop:14,padding:"9px 12px",borderRadius:8,background:"#FEE2E2",border:"1px solid #FCA5A5",color:"#B91C1C",fontSize:12,lineHeight:1.5}}>{labelPdfErr}</div>}
                  {/* Footer */}
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end",alignItems:"center",marginTop:18,paddingTop:16,borderTop:"0.5px solid rgba(0,0,0,0.08)"}}>
                    <button type="button" onClick={closePrintDialog} style={btnGhost}>Cancel</button>
                    {/* The label says which it is: a print queue on the web, a
                        share sheet on the app. */}
                    <button type="button" onClick={labelPdfBusy ? undefined : doPrintLabels} disabled={!styledQrUrl || labelPdfBusy}
                      style={Object.assign({}, btnPrimary, {opacity:(styledQrUrl && !labelPdfBusy)?1:0.55, cursor:(styledQrUrl && !labelPdfBusy)?"pointer":"not-allowed"})}>
                      {labelPdfBusy ? "Preparing PDF…" : (pdfShareSupport() === "print" ? copyLabel : "Share " + copyLabel + " as PDF")}
                    </button>
                  </div>
                </div>;
              })()}
            </div>
          </div>}
        </div>;
      })()}

      {/* ---------- REPORTS VIEW (5 tabs + Excel export) ---------- */}
      {subPage === "reports" && (function() {
        // Aggregated company-wide totals are Owner-only. Backend already
        // strips them from JSON responses for non-owners; the frontend
        // mirrors that by hiding the corresponding columns/cells. Belt-
        // and-braces: even if the backend ever leaks the values, the UI
        // won't render them for non-owners.
        // COLUMNS, not a page — same reasoning as the total-value card above.
        var canSeeAggregates = isOwnerFE(p.access) || roleHasFinanceReadsFE(p.cu);
        var TABS = [
          { id: "summary",     label: "Summary" },
          { id: "by-category", label: "By Category" },
          { id: "by-branch",   label: "By Branch" },
          { id: "by-employee", label: "By Employee" },
          { id: "history",     label: "Custody History" }
        ];
        var ACTION_LABELS = {
          registered:     "Registered",
          assigned:       "Assigned",
          transferred:    "Transferred",
          returned:       "Returned",
          marked_lost:    "Marked lost",
          retired:        "Retired",
          status_changed: "Status changed"
        };
        var tabBtn = function(t) {
          var act = reportsTab === t.id;
          return <button key={t.id} type="button" onClick={function(){ setReportsTab(t.id); }}
            style={{ padding:"8px 14px", fontSize:13, cursor:"pointer", borderRadius:8, whiteSpace:"nowrap", flexShrink:0,
              color: act ? "#1a1a1a" : "#666", fontWeight: act ? 500 : 400,
              background: act ? "#fff" : "transparent",
              border: "0.5px solid " + (act ? "rgba(0,0,0,0.1)" : "transparent"), fontFamily:"inherit"
            }}>{t.label}</button>;
        };
        var current = reportsData[reportsTab];
        var loading = !!reportsLoading[reportsTab];
        var error = reportsError[reportsTab] || "";
        var thStyle = {fontSize:11, color:"#666", textTransform:"uppercase", letterSpacing:"0.3px", textAlign:"left", padding:"10px 12px", borderBottom:"0.5px solid rgba(0,0,0,0.08)", background:"#FAFAF9", fontWeight:500};
        var tdStyle = {fontSize:13, padding:"10px 12px", borderBottom:"0.5px solid rgba(0,0,0,0.05)", color:"#1a1a1a"};
        var tdNum   = Object.assign({}, tdStyle, {textAlign:"right", fontFamily:"ui-monospace, SFMono-Regular, monospace", fontSize:12});

        var emptyState = function(msg) {
          return <div style={{fontSize:13, color:"#666", padding:"32px 24px", textAlign:"center", fontStyle:"italic"}}>{msg}</div>;
        };
        var renderSummary = function() {
          if (!current) return null;
          if (current.totals && current.totals.count === 0) return emptyState("No assets yet — create one to see the summary populate.");
          var s = current;
          var section = function(title, rows) {
            return <div style={{marginBottom:18}}>
              <div style={{fontSize:11, color:"#666", textTransform:"uppercase", letterSpacing:"0.3px", marginBottom:8}}>{title}</div>
              <table style={{width:"100%", borderCollapse:"collapse"}}>
                <tbody>
                  {rows.map(function(r, i){
                    return <tr key={i}>
                      <td style={Object.assign({}, tdStyle, {width: canSeeAggregates ? "55%" : "70%"})}>{r.label}</td>
                      <td style={tdNum}>{(r.count != null) ? r.count.toLocaleString("en-EG") : ""}</td>
                      {canSeeAggregates && <td style={Object.assign({}, tdNum, {width:"30%"})}>{(r.value != null) ? fmtEGP(r.value) : ""}</td>}
                    </tr>;
                  })}
                </tbody>
              </table>
            </div>;
          };
          return <div>
            {section("Totals", [{ label: "All assets", count: s.totals.count, value: s.totals.value }])}
            {section("By status", s.byStatus.map(function(r){ return { label: r.status, count: r.count, value: r.value }; }))}
            {section("By group",  (s.byGroup || []).map(function(r){ return { label: r.group, count: r.count, value: r.value }; }))}
            {section("By assignment type", ["personal","shared"].map(function(k){
              var b = (s.byAssignment && s.byAssignment[k]) || { count: 0, value: 0 };
              return { label: k, count: b.count, value: b.value };
            }))}
          </div>;
        };

        var renderByCategory = function() {
          if (!current) return null;
          if (!current.length) return emptyState("No assets yet — every category will appear once it has at least one asset.");
          return <table style={{width:"100%", borderCollapse:"collapse"}}>
            <thead><tr>
              <th style={thStyle}>Category</th>
              <th style={thStyle}>Group</th>
              <th style={Object.assign({}, thStyle, {textAlign:"right"})}>Count</th>
              {canSeeAggregates && <th style={Object.assign({}, thStyle, {textAlign:"right"})}>Value</th>}
              <th style={Object.assign({}, thStyle, {textAlign:"center"})}>Active</th>
              <th style={Object.assign({}, thStyle, {textAlign:"center"})}>Maint.</th>
              <th style={Object.assign({}, thStyle, {textAlign:"center"})}>Lost</th>
              <th style={Object.assign({}, thStyle, {textAlign:"center"})}>Retired</th>
            </tr></thead>
            <tbody>
              {current.map(function(r){
                return <tr key={r.categoryId}>
                  <td style={tdStyle}>{r.name} <span style={{color:"#888"}}>({r.codePrefix})</span></td>
                  <td style={Object.assign({}, tdStyle, {color:"#666"})}>{r.group}</td>
                  <td style={tdNum}>{r.count}</td>
                  {canSeeAggregates && <td style={tdNum}>{fmtEGP(r.value)}</td>}
                  <td style={Object.assign({}, tdStyle, {textAlign:"center"})}>{r.byStatus.active}</td>
                  <td style={Object.assign({}, tdStyle, {textAlign:"center"})}>{r.byStatus.maintenance}</td>
                  <td style={Object.assign({}, tdStyle, {textAlign:"center"})}>{r.byStatus.lost}</td>
                  <td style={Object.assign({}, tdStyle, {textAlign:"center"})}>{r.byStatus.retired}</td>
                </tr>;
              })}
            </tbody>
          </table>;
        };

        var renderByBranch = function() {
          if (!current) return null;
          if (!current.length) return emptyState("No assets yet — every branch will appear once it has at least one asset.");
          return <table style={{width:"100%", borderCollapse:"collapse"}}>
            <thead><tr>
              <th style={thStyle}>Branch</th>
              <th style={thStyle}>Code</th>
              <th style={Object.assign({}, thStyle, {textAlign:"right"})}>Count</th>
              {canSeeAggregates && <th style={Object.assign({}, thStyle, {textAlign:"right"})}>Value</th>}
              <th style={Object.assign({}, thStyle, {textAlign:"center"})}>Active</th>
              <th style={Object.assign({}, thStyle, {textAlign:"center"})}>Maint.</th>
              <th style={Object.assign({}, thStyle, {textAlign:"center"})}>Lost</th>
              <th style={Object.assign({}, thStyle, {textAlign:"center"})}>Retired</th>
            </tr></thead>
            <tbody>
              {current.map(function(r){
                return <tr key={r.branchId}>
                  <td style={tdStyle}>{r.name}</td>
                  <td style={Object.assign({}, tdStyle, {fontFamily:"ui-monospace, SFMono-Regular, monospace", fontSize:12, color:"#444"})}>{r.code}</td>
                  <td style={tdNum}>{r.count}</td>
                  {canSeeAggregates && <td style={tdNum}>{fmtEGP(r.value)}</td>}
                  <td style={Object.assign({}, tdStyle, {textAlign:"center"})}>{r.byStatus.active}</td>
                  <td style={Object.assign({}, tdStyle, {textAlign:"center"})}>{r.byStatus.maintenance}</td>
                  <td style={Object.assign({}, tdStyle, {textAlign:"center"})}>{r.byStatus.lost}</td>
                  <td style={Object.assign({}, tdStyle, {textAlign:"center"})}>{r.byStatus.retired}</td>
                </tr>;
              })}
            </tbody>
          </table>;
        };

        var renderByEmployee = function() {
          if (!current) return null;
          if (!current.length) return emptyState("No personal + active assets to report — everything is shared, retired, or unassigned.");
          return <div>
            <div style={{fontSize:11, color:"#666", marginBottom:8, fontStyle:"italic"}}>Counts cover personal + active assets only (current accountability).</div>
            <table style={{width:"100%", borderCollapse:"collapse"}}>
              <thead><tr>
                <th style={thStyle}>Employee</th>
                <th style={thStyle}>Role</th>
                <th style={Object.assign({}, thStyle, {textAlign:"right"})}>Count</th>
                {canSeeAggregates && <th style={Object.assign({}, thStyle, {textAlign:"right"})}>Value (EGP)</th>}
              </tr></thead>
              <tbody>
                {current.map(function(r, i){
                  return <tr key={r.userId || "unassigned-" + i}>
                    <td style={tdStyle}>{r.name}{r.username ? <span style={{color:"#888"}}> ({r.username})</span> : null}</td>
                    <td style={Object.assign({}, tdStyle, {color:"#666"})}>{r.role}</td>
                    <td style={tdNum}>{r.count}</td>
                    {canSeeAggregates && <td style={tdNum}>{fmtEGP(r.value)}</td>}
                  </tr>;
                })}
              </tbody>
            </table>
          </div>;
        };

        var renderHistory = function() {
          return <div>
            {/* Filters bar */}
            <div className="at-history-filters" style={{display:"grid", gridTemplateColumns:"auto 180px 1fr", gap:8, marginBottom:14, alignItems:"end"}}>
              {/* 📅 Date (owner 2026-10-31): the shared calendar chip in place of the
                  From / To inputs. assetsHistoryDateChip writes the same
                  historyFilters.from / .to; `to` stays the server's exclusive end. */}
              <div>
                <label style={fieldLabel}>Date</label>
                <DashRangePicker isMobile={p.isMobile} todayYmd={cairoTodayIso()} min=""
                  value={assetsHistoryDateChip.value(historyFilters)} active={assetsHistoryDateChip.active(historyFilters)}
                  triggerStyle={periodChipStyle(assetsHistoryDateChip.active(historyFilters), PERIOD_ACCENT_BLUE, p.isMobile)}
                  onPick={function(a, b){ assetsHistoryDateChip.pick(a, b, historyFilters, setHistoryFilters); }}/>
              </div>
              <div>
                <label style={fieldLabel}>Action</label>
                <Select value={historyFilters.action} onChange={function(e){ setHistoryFilters(Object.assign({}, historyFilters, {action:e.target.value})); }} searchable fontSize={13}
                  options={[{ value:"", label:"All actions" }].concat(Object.keys(ACTION_LABELS).map(function(k){ return { value:k, label:ACTION_LABELS[k] }; }))}/>
              </div>
              <div style={{textAlign:"right"}}>
                {historyFilters.from || historyFilters.to || historyFilters.action
                  ? <button type="button" onClick={function(){ setHistoryFilters({ from:"", to:"", action:"" }); }} style={btnGhost}>Clear filters</button>
                  : null}
              </div>
            </div>
            {!current ? null : current.length === 0
              ? <div style={{fontSize:13, color:"#666", padding:24, textAlign:"center"}}>No events match these filters.</div>
              : <table style={{width:"100%", borderCollapse:"collapse"}}>
                  <thead><tr>
                    <th style={thStyle}>When</th>
                    <th style={thStyle}>Asset</th>
                    <th style={thStyle}>Action</th>
                    <th style={thStyle}>From → To</th>
                    <th style={thStyle}>By</th>
                    <th style={thStyle}>Notes</th>
                  </tr></thead>
                  <tbody>
                    {current.map(function(r){
                      var fromN = r.fromUserId && r.fromUserId.name;
                      var toN   = r.toUserId   && r.toUserId.name;
                      var arrow = fromN && toN ? (fromN + " → " + toN) : (toN ? "Warehouse → " + toN : (fromN ? fromN + " → Warehouse" : "—"));
                      return <tr key={r._id}>
                        <td style={Object.assign({}, tdStyle, {fontSize:12, color:"#666", whiteSpace:"nowrap"})}>{fmtDateTime12(r.performedAt || r.createdAt, undefined, { sep:", " })}</td>
                        <td style={tdStyle}>{r.assetId ? <span><span style={{fontWeight:500}}>{r.assetId.name}</span> <span style={{fontFamily:"ui-monospace, SFMono-Regular, monospace", fontSize:11, color:"#888"}}>{r.assetId.assetCode}</span></span> : "—"}</td>
                        <td style={tdStyle}>{ACTION_LABELS[r.action] || r.action}</td>
                        <td style={Object.assign({}, tdStyle, {color:"#666"})}>{arrow}</td>
                        <td style={Object.assign({}, tdStyle, {color:"#666"})}>{userNameOf(r.performedBy) || "System"}</td>
                        <td style={Object.assign({}, tdStyle, {color:"#666"})}>{r.notes || ""}</td>
                      </tr>;
                    })}
                  </tbody>
                </table>
            }
          </div>;
        };

        var renderActiveTab = function() {
          if (reportsTab === "summary")     return renderSummary();
          if (reportsTab === "by-category") return renderByCategory();
          if (reportsTab === "by-branch")   return renderByBranch();
          if (reportsTab === "by-employee") return renderByEmployee();
          if (reportsTab === "history")     return renderHistory();
          return null;
        };

        return <div>
          <div className="at-reports-header" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
              <button type="button" onClick={goList} style={btnGhost}>← Back</button>
              <div style={{fontSize:18,fontWeight:500,color:"#1a1a1a"}}>Asset reports</div>
            </div>
            <button type="button" onClick={function(){ exportReport(reportsTab); }} disabled={exporting || loading || !current} style={Object.assign({}, btnPrimary, {opacity:(exporting || loading || !current) ? 0.55 : 1, cursor:(exporting || loading || !current) ? "not-allowed" : "pointer"})}>
              {exporting ? "Preparing…" : "Download Excel"}
            </button>
          </div>

          {/* Tabs */}
          <div style={Object.assign({}, cardWrap, { overflow:"hidden" })}>
            <div className="at-reports-tabs" style={{display:"flex", gap:4, padding:"10px 14px", borderBottom:"0.5px solid rgba(0,0,0,0.1)", background:"#F7F7F5", overflowX:"auto"}}>
              {TABS.map(tabBtn)}
            </div>
            <div className="at-report-content" style={{padding:18}}>
              {error && <div style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"8px 12px",borderRadius:8,marginBottom:12}}>{error}</div>}
              {loading && !current
                ? <div style={{fontSize:13, color:"#666", padding:24, textAlign:"center"}}>Loading…</div>
                : renderActiveTab()
              }
            </div>
          </div>
        </div>;
      })()}

    </div>
  </div>;
};

// ===== KPIs PAGE — card text helpers (module scope so a gate can RUN them) =====
// Conversion: the server's qConversion is Deals ÷ New Leads of the SAME quarter
// and is NULL when no leads were received; null renders "—", never "0%" (a 0%
// says leads were received and none closed). `pending` keeps the loading "…".
// Before the response lands (or from an older server) the in-memory fallback
// divides the same two numbers the cards show.
var kpiConvDisplay = function(qStats, deals, leads, pending) {
  var conv = (qStats && qStats.qConversion !== undefined)
    ? qStats.qConversion
    : (leads > 0 ? Math.round(deals / leads * 100) : null);
  if (pending && leads === 0) return "\u2026";
  return conv === null ? "\u2014" : conv + "%";
};
// Calls: logging began on callsSince (the first call Activity company-wide). A
// quarter that starts before it carries a "since <date>" note so a low or zero
// count is not read as inactivity. Returns "" when the quarter is fully covered.
var kpiCallsSinceNote = function(callsSince, year, qNum) {
  if (!callsSince) return "";
  var since = new Date(callsSince);
  if (isNaN(since.getTime())) return "";
  var qStartMs = Date.UTC(year, (qNum - 1) * 3, 1);
  if (since.getTime() <= qStartMs) return "";
  return "since " + since.toLocaleDateString("en-GB", { timeZone: "Africa/Cairo", day: "2-digit", month: "2-digit", year: "numeric" });
};
// ===== KPIs PAGE (Sales only) =====
var KPIsPage = function(p) {
  var uid = String(p.cu.id);
  var parseBudget = function(b){return parseFloat((b||"0").toString().replace(/,/g,""))||0;};
  var isTeamLeader = positionOf(p.cu)==="manager"||positionOf(p.cu)==="team_leader";
  // For manager/team leader: include all team deals in revenue calc
  var teamUids = isTeamLeader ? new Set((p.myTeamUsers||[]).map(function(u){return String(u._id);})) : null;
  var myLeads = p.leads.filter(function(l){
    var aid=String(l.agentId&&l.agentId._id?l.agentId._id:l.agentId||"");
    var splitId=String(l.splitAgent2Id&&l.splitAgent2Id._id?l.splitAgent2Id._id:l.splitAgent2Id||"");
    if(isTeamLeader && teamUids) return (teamUids.has(aid)||(splitId&&teamUids.has(splitId)))&&!l.archived&&l.source!=="Daily Request";
    return (aid===uid||splitId===uid)&&!l.archived&&l.source!=="Daily Request";
  });
  // THE DEAL'S OWN PAIR (owner ruling 2026-09-12, R11) — myLeads' agent match
  // is the LEAD's current holder / its unrelated legacy split field, right for
  // pipeline/EOI counts but wrong for revenue: a deal whose lead has since
  // rotated (#50058: lead holder Rokaya Swidan, deal agent Jana Mohamed) would
  // vanish from Jana's own KPIs, and a split partner (#50058/#50059: Ahmed
  // Abdelkader) would never see it at all, since dealSplitAgent2Id is a
  // different field than the lead's own splitAgent2Id. myDeals is filtered
  // independently of myLeads (which other KPIs on this page still need
  // unchanged) through dealAgentPairOf, the same chokepoint the Deals page uses.
  var myDeals = p.leads.filter(function(l){
    if (l.status!=="DoneDeal" || l.archived || l.source==="Daily Request") return false;
    var pair = dealAgentPairOf(l);
    if (isTeamLeader && teamUids) return teamUids.has(pair.primary) || (pair.second && teamUids.has(pair.second));
    return pair.primary===uid || pair.second===uid;
  });
  var myActs = p.activities.filter(function(a){var auid=a.userId&&a.userId._id?a.userId._id:a.userId;return auid===uid;});
  var myUser = p.users.find(function(u){return String(gid(u))===uid;})||{};

  var getQ = function(date){var m=new Date(date).getMonth();return m<3?"Q1":m<6?"Q2":m<9?"Q3":"Q4";};
  var getYear = function(date){return new Date(date).getFullYear();};
  var curQ = (function(){var m=new Date().getMonth();return m<3?"Q1":m<6?"Q2":m<9?"Q3":"Q4";})();
  var curYear = new Date().getFullYear();

  var [selQ, setSelQ] = useState(curQ);
  var [selYear, setSelYear] = useState(curYear);

  // STEP 4-3 — quarterly stats from /api/agents/:id/quarterly-stats. Replaces
  // the in-memory myDeals.filter / myLeads.filter / myActs.filter scans below
  // that would silently miss data once STEP 4-5 shrinks the bootstrap. Server
  // owns the team-subtree aggregation for TL/manager (see /api/agents/...
  // handler). State stays null while loading — the existing in-memory derivs
  // serve as the bootstrap-time fallback so the page never flashes zeros.
  var [qStats, setQStats] = useState(null);
  // STEP 4-5 X2 — status distribution + lifetime totals for KPIsPage charts.
  // The page-wide myLeads scan becomes wrong post-X3 bootstrap shrink; this
  // endpoint covers the chart data (byStatus + totals.leads/deals/calls).
  var [myStats, setMyStats] = useState(null);
  // STEP 4-5 X2.5 — team_leader Team Members grid. Same /api/team/member-stats
  // endpoint TeamPage uses. Map keyed on uid → server-aggregated stats so each
  // card can override its in-memory aQLeads/aQDeals/aQRev/aQCalls scans (which
  // become wrong post-X3 shrink).
  var [kpiMemberStatsMap, setKpiMemberStatsMap] = useState(null);
  var liveCommRev = useLiveRev("commission");
  useEffect(function(){
    if (!p.token || !uid) return;
    var cancelled = false;
    var qNum = parseInt(String(selQ).replace("Q","")) || 1;
    var fetches = [
      apiFetch("/api/agents/"+uid+"/quarterly-stats?year="+selYear+"&quarter="+qNum,"GET",null,p.token).catch(function(){return null;}),
      apiFetch("/api/agents/"+uid+"/my-stats","GET",null,p.token).catch(function(){return null;})
    ];
    // Only TL/manager need the team grid data — skip the extra call otherwise.
    if (isTeamLeader) {
      fetches.push(apiFetch("/api/team/member-stats?year="+selYear+"&quarter="+qNum,"GET",null,p.token).catch(function(){return null;}));
    }
    Promise.all(fetches).then(function(r){
      if (cancelled) return;
      if (r[0] && typeof r[0] === "object") setQStats(r[0]);
      if (r[1] && typeof r[1] === "object") setMyStats(r[1]);
      if (isTeamLeader && r[2] && Array.isArray(r[2].members)) {
        var map = {};
        r[2].members.forEach(function(m){ if (m && m.uid) map[String(m.uid)] = m; });
        setKpiMemberStatsMap(map);
      }
    });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[p.token, uid, selQ, selYear, p.cbBust, isTeamLeader, liveCommRev]);

  // Team leader target: sum of every team member's quarterly target so the
  // progress bar reflects the whole team's quota, matching aggregated deals.
  // Falls back to in-memory derivation while qStats is loading.
  var qTarget = qStats ? (qStats.qTarget || 0) : (isTeamLeader
    ? (p.myTeamUsers||[]).reduce(function(s,u){ return s + (getEffectiveQTarget(u, p.users, selQ)||0); }, 0)
    : getEffectiveQTarget(myUser, p.users, selQ));

  // Scope-based split-deal credit (rule (B) — purple card only). p.users is
  // server-scoped per role: sales sees just self → splits always 0.5; TL sees
  // self + reports → in-team splits count full; manager/director sees subtree
  // → cross-TL splits within company count full; admin sees everyone.
  var visibleUserIds = new Set((p.users||[]).map(function(u){return String(u._id||gid(u));}));

  // Filter by selected Q and year. Uses getDealDate so KPIs aligns with the
  // DealsPage / Sales Team MemberCard quarter categorisation (dealDate →
  // localStorage extra → eoiDate → updatedAt → createdAt). Pre-fix this used
  // updatedAt only, which mis-bucketed deals whose closing date was set to a
  // different quarter than their last edit.
  var qDeals = myDeals.filter(function(d){
    var dd = getDealDate(d); if(!dd) return false;
    return getQ(dd)===selQ && getYear(dd)===selYear;
  });
  var qCalls = myActs.filter(function(a){
    if(a.type!=="call"||!a.createdAt) return false;
    return getQ(a.createdAt)===selQ && getYear(a.createdAt)===selYear;
  });
  var qLeads = myLeads.filter(function(l){
    if(!l.createdAt) return false;
    return getQ(l.createdAt)===selQ && getYear(l.createdAt)===selYear;
  });
  var qRev = qDeals.reduce(function(s,d){return s+parseBudget(d.budget)*getProjectWeight(d.project,d)*splitMultiplier(d, visibleUserIds);},0);
  // STEP 4-3 — once /api/agents/:id/quarterly-stats has resolved, prefer the
  // server-aggregated counts and revenue over the in-memory derivations.
  // Note: server returns raw budget sum (no project-weight / split-halving
  // applied) because those rules are FE-owned. After STEP 4-5 the in-memory
  // qRev would understate (weights × splits applied to a shrunk lead list);
  // server qRev is a slight overstatement (no weight/split) but stays
  // consistent across the bootstrap window. Acceptable trade-off documented
  // for STEP 4-4 follow-up if exact parity becomes required.
  var qLeadsCount = qStats ? (qStats.qLeads || 0) : qLeads.length;
  var qDealsCount = qStats ? (qStats.qDeals || 0) : qDeals.length;
  var qCallsCount = qStats ? (qStats.qCalls || 0) : qCalls.length;
  if (qStats && typeof qStats.qRev === "number") qRev = qStats.qRev;
  var qProg = qTarget>0?Math.min(100,Math.round(qRev/qTarget*100)):0;

  // Loading placeholder. While qStats is still in flight AND the in-memory
  // fallback also resolves to 0 (p.leads/p.activities not loaded yet), show
  // an ellipsis instead of a literal 0 — the sales-role quarterly-stats fetch
  // can take ~30s and a flash of "0" reads as bad data. Once qStats arrives
  // (even with a real 0) the truthful value renders.
  var pending = qStats === null;
  var phLeads = (pending && qLeadsCount === 0) ? "…" : qLeadsCount;
  var phDeals = (pending && qDealsCount === 0) ? "…" : qDealsCount;
  var phCalls = (pending && qCallsCount === 0) ? "…" : qCallsCount;
  var phRev1  = (pending && qRev === 0) ? "…" : (qRev/1000000).toFixed(1) + "M";
  var phRev2  = (pending && qRev === 0) ? "…" : (qRev/1000000).toFixed(2) + "M";
  var phConv  = kpiConvDisplay(qStats, qDealsCount, qLeadsCount, pending);
  var callsNote = qStats ? kpiCallsSinceNote(qStats.callsSince, selYear, parseInt(String(selQ).replace("Q",""))||1) : "";

  var isOnlineNow = myUser.lastSeen&&(Date.now()-new Date(myUser.lastSeen).getTime())<3*60*1000;

  // Available years — current and past 2
  var years = [curYear, curYear-1, curYear-2, curYear-3];

  // Profile card uses the exact same design as the Admin's Sales Team page
  // individual agent card (MemberCard). Stable per-user gradient + white
  // stats panel beneath a gradient hero. Data and logic below are unchanged.
  var kpiGradFor = function(uid){var h=0;var s=String(uid||"");for(var i=0;i<s.length;i++){h=(h*31+s.charCodeAt(i))&0x7fffffff;}return "kpi-grad-"+((h%8)+1);};
  var kpiGradClass = kpiGradFor(uid);
  var kpiDisplayName = p.cu.username==="amgad" ? "Amgad Mohamed" : p.cu.name;
  var kpiInitials = (kpiDisplayName||"?").split(" ").slice(0,2).map(function(x){return x[0]||"";}).join("").toUpperCase();
  var kpiRoleLabel = p.cu.title || ({admin:"Admin",operation:"Operations",manager:"Manager",team_leader:"Team Leader",sales:"Sales",viewer:"Viewer"}[p.cu.role]||"");
  var kpiLastSeenStr = myUser.lastSeen ? ("Last seen: "+fmtTime12(myUser.lastSeen, p.t)+" \u2014 "+timeAgo(myUser.lastSeen,p.t)) : "Never logged in";
  // Stat cells reflect the active Quarter+Year filter (was: all-time, which
  // diverged from the DealsPage Q-filtered total — see KPIs Q-filter bugfix).
  var kpiMemberStats = [
    { v: phLeads, l: "Leads", isDeals:false },
    { v: phDeals, l: "Deals", isDeals:true  },
    { v: phRev1,  l: "Total", isDeals:false },
    { v: phCalls, l: "Calls", isDeals:false }
  ];
  return <div className="kpi-page-v2" style={{ padding:"18px 16px 40px" }}>
    {/* Inter font + the 8 gradient classes — mirrors Admin's Sales Team page. */}
    <style>{""
      + "@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');"
      + ".kpi-page-v2 .kpi-grad-1 { background: linear-gradient(135deg, #43c6db, #3b5cb8); }"
      + ".kpi-page-v2 .kpi-grad-2 { background: linear-gradient(135deg, #f953c6, #b91d73); }"
      + ".kpi-page-v2 .kpi-grad-3 { background: linear-gradient(135deg, #56ab2f, #a8e063); }"
      + ".kpi-page-v2 .kpi-grad-4 { background: linear-gradient(135deg, #f7797d, #c6426e); }"
      + ".kpi-page-v2 .kpi-grad-5 { background: linear-gradient(135deg, #e52d27, #b31217); }"
      + ".kpi-page-v2 .kpi-grad-6 { background: linear-gradient(135deg, #f46b45, #eea849); }"
      + ".kpi-page-v2 .kpi-grad-7 { background: linear-gradient(135deg, #b8d435, #56ab2f); }"
      + ".kpi-page-v2 .kpi-grad-8 { background: linear-gradient(135deg, #a18cd1, #e8a4c8); }"
    }</style>
    {/* Title removed — the shared header renders it. */}

    {/* Profile Card — MemberCard design (mirrors Admin's Sales Team page) */}
    <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}>
      <div style={{ width:"100%", maxWidth:320, borderRadius:16, overflow:"hidden", background:"#fff", boxShadow:"0 2px 10px rgba(0,0,0,0.08)", fontFamily:"'Inter','Segoe UI',sans-serif" }}>
        {/* Gradient hero */}
        <div className={kpiGradClass} style={{ padding:"18px 14px 16px", position:"relative", display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
          {isOnlineNow && <span title="Online" style={{ position:"absolute", top:10, right:12, width:9, height:9, borderRadius:"50%", background:"#22c55e", boxShadow:"0 0 0 2px rgba(255,255,255,0.45)" }}/>}
          <div style={{ width:44, height:44, borderRadius:12, background:"rgba(255,255,255,0.22)", color:"#fff", border:"2px solid rgba(255,255,255,0.35)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:800 }}>{kpiInitials}</div>
          <div style={{ fontSize:13, fontWeight:700, color:"#fff", textAlign:"center", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{kpiDisplayName}</div>
          <div style={{ fontSize:10, color:"rgba(255,255,255,0.85)", textTransform:"uppercase", letterSpacing:"0.04em", textAlign:"center" }}>{kpiRoleLabel}</div>
          <div style={{ fontSize:9, color:"rgba(255,255,255,0.55)", textAlign:"center", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{isOnlineNow?"Online now":kpiLastSeenStr}</div>
        </div>
        {/* White panel */}
        <div style={{ background:"#fff", padding:"14px 14px 16px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
            <span style={{ fontSize:10, fontWeight:600, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.04em" }}>{selQ} Target</span>
            <span style={{ fontSize:10, fontWeight:700, color:"#334155" }}>{qTarget>0?qTarget.toLocaleString()+" EGP":"Not set"}</span>
          </div>
          <div style={{ height:4, background:"#e2e8f0", borderRadius:2, marginBottom:10, overflow:"hidden" }}>
            <div className={kpiGradClass} style={{ height:"100%", width:qProg+"%", borderRadius:2, transition:"width 0.6s" }}/>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:12 }}>
            <span style={{ fontSize:18, fontWeight:800, color:qRev>0?"#0f172a":"#94a3b8" }}>{phRev2}</span>
            <span style={{ fontSize:12, fontWeight:700, color:"#64748b" }}>{qProg}%</span>
          </div>
          <div style={{ height:1, background:"#e2e8f0", marginBottom:10, transform:"scaleY(0.5)" }}/>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:6 }}>
            {kpiMemberStats.map(function(s,i){
              var isZero = (s.v === 0) || (s.v === "0.0M") || (s.v === "0M") || (s.v === "0");
              var color;
              if (s.isDeals) color = (s.v > 0 ? "#15803d" : "#cbd5e1");
              else color = isZero ? "#cbd5e1" : "#0f172a";
              return <div key={i} style={{ textAlign:"center" }}>
                <div style={{ fontSize:16, fontWeight:800, color:color, lineHeight:1.1 }}>{s.v}</div>
                <div style={{ fontSize:8, fontWeight:600, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.06em", marginTop:4 }}>{s.l}</div>
              </div>;
            })}
          </div>
        </div>
      </div>
    </div>

    {/* Q selector + year */}
    <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center", flexWrap:"wrap" }}>
      <div style={{ display:"flex", gap:6 }}>
        {["Q1","Q2","Q3","Q4"].map(function(q){var isCurrentQ=q===curQ&&selYear===curYear;return <button key={q} onClick={function(){setSelQ(q);}}
          style={{ padding:"7px 16px", borderRadius:8, border:"1px solid", borderColor:selQ===q?C.accent:"#E2E8F0",
            background:selQ===q?C.accent+"12":"#fff", color:selQ===q?C.accent:C.textLight, position:"relative",
            fontSize:13, fontWeight:600, cursor:"pointer" }}>{q}
          {isCurrentQ&&<span title="Current quarter" style={{ position:"absolute", top:3, right:3, width:6, height:6, borderRadius:"50%", background:"#22C55E" }}/>}</button>;})}
      </div>
      <Select value={String(selYear)} onChange={function(e){setSelYear(Number(e.target.value));}} width={100} maxWidth={100} fontSize={13}
        options={years.map(function(y){return { value:String(y), label:String(y) };})}/>
    </div>

    {/* Q Target progress */}
    <Card style={{ marginBottom:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
        <span style={{ fontSize:13, fontWeight:700 }}>{selQ} Target — {selYear}</span>
        <span style={{ fontSize:12, color:C.textLight }}>{qTarget>0?qTarget.toLocaleString()+" EGP":"Not set"}</span>
      </div>
      <div style={{ height:10, background:"#E2E8F0", borderRadius:5, marginBottom:8 }}>
        <div style={{ height:"100%", width:qProg+"%", borderRadius:5, background:qProg>=100?C.success:qProg>=50?C.accent:C.warning, transition:"width 0.6s" }}/>
      </div>
      <div style={{ display:"flex", justifyContent:"space-between" }}>
        <span style={{ fontSize:13, color:C.success, fontWeight:700 }}>{phRev2} EGP</span>
        <span style={{ fontSize:13, fontWeight:700, color:qProg>=100?C.success:C.accent }}>{qProg}%</span>
      </div>
    </Card>

    {/* Q stats */}
    <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
      {[
        {v:phDeals, l:"Deals", c:C.success, icon:"🏆"},
        {v:phCalls, l:"Calls", c:C.info, icon:"📞", sub:callsNote},
        {v:phLeads, l:"New Leads", c:C.accent, icon:"👤"},
        {v:phConv,  l:"Conversion Rate", c:C.warning, icon:"📊", sub:"of leads received this period"},
      ].map(function(s){return <Card key={s.l} style={{ flex:"1 1 120px", textAlign:"center", padding:"16px 12px" }}>
        <div style={{ fontSize:22, marginBottom:4 }}>{s.icon}</div>
        <div style={{ fontSize:22, fontWeight:800, color:s.c }}>{s.v}</div>
        <div style={{ fontSize:11, color:C.textLight, marginTop:4 }}>{s.l}</div>
        {s.sub ? <div style={{ fontSize:10, color:C.textLight, marginTop:2 }}>{s.sub}</div> : null}
      </Card>;})}
    </div>

    {/* Team members grid — team_leader only. Mirrors the admin Sales Team
        MemberCard design (gradient hero + white stats panel) using the 8
        .kpi-page-v2 .kpi-grad-N classes already defined above. */}
    {positionOf(p.cu) === "team_leader" && (p.myTeamUsers||[]).length > 1 && <div style={{ marginTop:20 }}>
      <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:10, textTransform:"uppercase", letterSpacing:"0.04em" }}>Team Members</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:12 }}>
        {(p.myTeamUsers||[]).filter(function(u){ return String(gid(u)) !== uid; }).map(function(a){
          var auid = String(gid(a));
          var aLeads = p.leads.filter(function(l){ var aid=String(l.agentId&&l.agentId._id?l.agentId._id:l.agentId||""); return aid===auid && !l.archived && l.source!=="Daily Request"; });
          var aDeals = aLeads.filter(function(l){ return l.status==="DoneDeal"; });
          // Q-filtered slices for the stat cells. Per-agent cards keep the
          // legacy share-based split rule (0.5 always if split) so the sum
          // across cards equals the deal's full value.
          var aQLeads = aLeads.filter(function(l){ if(!l.createdAt) return false; return getQ(l.createdAt)===selQ && getYear(l.createdAt)===selYear; });
          var aQDeals = aDeals.filter(function(d){ var dd=getDealDate(d); return dd && getQ(dd)===selQ && getYear(dd)===selYear; });
          var aQRev = aQDeals.reduce(function(s,d){ var w=getProjectWeight(d.project,d); var sp=getDealSplitFromObj(d); return s+parseBudget(d.budget)*w*(sp?0.5:1); }, 0);
          var aQCalls = p.activities.filter(function(ac){ var aauid=ac.userId&&ac.userId._id?ac.userId._id:ac.userId; if(String(aauid)!==auid||ac.type!=="call"||!ac.createdAt) return false; return getQ(ac.createdAt)===selQ && getYear(ac.createdAt)===selYear; }).length;
          var aTarget = getEffectiveQTarget(a, p.users, selQ);
          var aProg = aTarget>0 ? Math.min(100, Math.round(aQRev/aTarget*100)) : 0;
          // STEP 4-5 X2.5 — once /api/team/member-stats has resolved, prefer
          // the server numbers. Same pattern as the TeamPage MemberCard
          // migration. .length-based render sites are preserved by replacing
          // the source arrays with empty arrays of the right length.
          var kpiSrv = kpiMemberStatsMap ? kpiMemberStatsMap[auid] : null;
          if (kpiSrv) {
            aTarget = kpiSrv.qTarget || 0;
            aQRev   = typeof kpiSrv.qRev === "number" ? kpiSrv.qRev : aQRev;
            aProg   = aTarget > 0 ? Math.min(100, Math.round(aQRev / aTarget * 100)) : 0;
            aQDeals = new Array(kpiSrv.qDeals || 0);
            aQLeads = new Array(kpiSrv.qLeads || 0);
            aQCalls = kpiSrv.qCalls || 0;
          }
          var aGrad = kpiGradFor(auid);
          var aInitials = (a.name||"?").split(" ").slice(0,2).map(function(x){return x[0]||"";}).join("").toUpperCase();
          var aOnline = a.lastSeen && (Date.now()-new Date(a.lastSeen).getTime()) < 3*60*1000;
          var aRoleLabel = a.title || ({operation:"Operations",manager:"Manager",team_leader:"Team Leader",sales:"Sales",viewer:"Viewer",admin:"Admin"}[a.role]||"");
          return <div key={auid} style={{ borderRadius:16, overflow:"hidden", background:"#fff", boxShadow:"0 2px 10px rgba(0,0,0,0.08)" }}>
            <div className={aGrad} style={{ padding:"18px 14px 16px", position:"relative", display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
              {aOnline && <span title="Online" style={{ position:"absolute", top:10, right:12, width:9, height:9, borderRadius:"50%", background:"#22c55e", boxShadow:"0 0 0 2px rgba(255,255,255,0.45)" }}/>}
              <div style={{ width:44, height:44, borderRadius:12, background:"rgba(255,255,255,0.22)", color:"#fff", border:"2px solid rgba(255,255,255,0.35)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:800 }}>{aInitials}</div>
              <div style={{ fontSize:13, fontWeight:700, color:"#fff", textAlign:"center", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.name}</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.85)", textTransform:"uppercase", letterSpacing:"0.04em" }}>{aRoleLabel}</div>
            </div>
            <div style={{ background:"#fff", padding:"14px 14px 16px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
                <span style={{ fontSize:10, fontWeight:600, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.04em" }}>{selQ} Target</span>
                <span style={{ fontSize:10, fontWeight:700, color:"#334155" }}>{aTarget>0?aTarget.toLocaleString()+" EGP":"Not set"}</span>
              </div>
              <div style={{ height:4, background:"#e2e8f0", borderRadius:2, marginBottom:10, overflow:"hidden" }}>
                <div className={aGrad} style={{ height:"100%", width:aProg+"%", borderRadius:2, transition:"width 0.6s" }}/>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:12 }}>
                <span style={{ fontSize:18, fontWeight:800, color:aQRev>0?"#0f172a":"#94a3b8" }}>{(aQRev/1000000).toFixed(2)}M</span>
                <span style={{ fontSize:12, fontWeight:700, color:"#64748b" }}>{aProg}%</span>
              </div>
              <div style={{ height:1, background:"#e2e8f0", marginBottom:10, transform:"scaleY(0.5)" }}/>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:6 }}>
                {[
                  { v:aQLeads.length, l:"Leads", isDeals:false },
                  { v:aQDeals.length, l:"Deals", isDeals:true },
                  { v:(aQRev/1000000).toFixed(1)+"M", l:"Total", isDeals:false },
                  { v:aQCalls, l:"Calls", isDeals:false }
                ].map(function(s,i){
                  var isZero = (s.v === 0) || (s.v === "0.0M") || (s.v === "0M") || (s.v === "0");
                  var color = s.isDeals ? (s.v > 0 ? "#15803d" : "#cbd5e1") : (isZero ? "#cbd5e1" : "#0f172a");
                  return <div key={i} style={{ textAlign:"center" }}>
                    <div style={{ fontSize:16, fontWeight:800, color:color, lineHeight:1.1 }}>{s.v}</div>
                    <div style={{ fontSize:8, fontWeight:600, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.06em", marginTop:4 }}>{s.l}</div>
                  </div>;
                })}
              </div>
            </div>
          </div>;
        })}
      </div>
    </div>}

    {/* Q Deals list */}
    {qDeals.length>0&&<Card style={{ marginTop:16 }}>
      <div style={{ fontSize:13, fontWeight:700, marginBottom:12 }}>🏆 Deals {selQ} — {selYear}</div>
      {qDeals.map(function(d){return <div key={gid(d)} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:"1px solid #F1F5F9" }}>
        <div>
          <div style={{ fontSize:12, fontWeight:600 }}>{d.name}</div>
          <div style={{ fontSize:10, color:C.textLight }}>{d.project||"-"}</div>
        </div>
        {(function(){
          // Change 1 — credit the viewer their SPLIT SHARE (50%) of a split deal,
          // mirroring the quarter-revenue Total above and the Deals-page row.
          // splitMultiplier returns 0.5 when the partner is outside the viewer's
          // scope; the full deal value shows as a "من" subline.
          var full=parseBudget(d.budget);
          var credited=full*getProjectWeight(d.project,d)*splitMultiplier(d, visibleUserIds);
          var showFull=credited>0&&Math.abs(credited-full)>=1;
          return <div>
            <div style={{ fontSize:13, fontWeight:700, color:C.success }}>{credited>0?(credited/1000000).toFixed(2)+"M":d.budget||"-"}</div>
            {showFull&&<div style={{ fontSize:9, color:C.textLight, fontWeight:500 }}>من {(full/1000000).toFixed(2)}M</div>}
          </div>;
        })()}
      </div>;})}
    </Card>}
  </div>;
};

// ===== COMMISSIONS PAGE modals (Phase D) =====
// Each modal is a self-contained form. They use the shared Modal wrapper for
// chrome + the parent CommissionsPage's writeCommission() callback for the API
// call. Defined here (not inside CommissionsPage) so they own their own form
// state via useState without re-mounting on every parent render.

// Compact label+control row for forms.
var FormRow = function(p) {
  return <div style={{ marginBottom:12 }}>
    <label style={{ display:"block", fontSize:12, fontWeight:700, color:"#475569", marginBottom:4 }}>{p.label}</label>
    {p.children}
  </div>;
};

// Money input — accepts the codebase-standard pattern (state stores the
// formatted string like "1,500,000"; caller does Number(value.replace(/,/g,""))
// on submit). Strips non-digits on each keystroke and re-formats.
// Phase R-5 follow-up — MoneyInput now accepts an optional decimal point with
// up to 2 trailing digits (piasters). Integer entry is unchanged: typing
// "100000" still yields "100,000" with no ".00" tail. Rule for the decimal:
// only the LAST "." in the raw value counts (earlier dots are treated as
// thousand-separator typos, matching European number style — see test 5);
// digits after the dot are truncated to the first 2.
//
// inputMode is "decimal" so mobile keyboards surface a "." key.
var MoneyInput = function(p) {
  return <input type="text" inputMode="decimal"
    value={p.value || ""}
    autoFocus={!!p.autoFocus}
    placeholder={p.placeholder || ""}
    onChange={function(e){
      var raw = String(e.target.value || "").replace(/,/g, "");
      var lastDot = raw.lastIndexOf(".");
      if (lastDot === -1) {
        var digits = raw.replace(/[^0-9]/g, "");
        p.onChange(digits === "" ? "" : Number(digits).toLocaleString("en-US"));
        return;
      }
      var intRaw = raw.slice(0, lastDot).replace(/[^0-9]/g, "");
      var decRaw = raw.slice(lastDot + 1).replace(/[^0-9]/g, "").slice(0, 2);
      var intFmt = intRaw === "" ? "0" : Number(intRaw).toLocaleString("en-US");
      p.onChange(intFmt + "." + decRaw);
    }}
    style={p.style || { width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13 }}
  />;
};
// Helper for parsing on submit. Returns Number or NaN if blank.
function parseMoney(formatted) {
  var s = String(formatted || "").replace(/,/g, "").trim();
  if (!s) return NaN;
  return Number(s);
}

// Phase R-13 — applyRecipientOverridesFE(commission): canonical post-override
// recipient list (FE mirror of the BE applyRecipientOverrides helper at
// crm-aro-backend/server.js right after computeAllRecipientShares).
//
// Returns the same row shape as the BE helper so any FE site that aggregates
// recipients stays consistent with the BE Payout Report / cash-flow / payout
// creation membership checks. Rows:
//   { slotKey, userName, userId, role, computedShare, overrideAmount,
//     effectiveOwed, isExtra, overrideSource, overrideId, addedBy, addedAt,
//     isSplit }
//
// Currently consumed by: the "Add Payout" recipient dropdown inside the
// Commission detail. The Commission detail's recipient-list rendering retains
// its own inline override layer (added when overrides shipped); it produces
// the same output by construction. If a third FE site needs to aggregate
// recipients, call this helper rather than duplicating logic.
function applyRecipientOverridesFE(commission) {
  var rows = [];
  if (!commission || !commission.snapshot) return rows;
  var snap = commission.snapshot;
  var overrides = Array.isArray(commission.recipientOverrides) ? commission.recipientOverrides : [];
  var bySlot = {};
  var manualAdds = [];
  overrides.forEach(function(o){
    if (!o || !o.source) return;
    if (o.source === "manual_add") { manualAdds.push(o); return; }
    if (o.targetSlot) bySlot[o.targetSlot] = o;
  });
  var chain = [
    ["salesAgent", snap.salesAgent, false],
    ["teamLeader", snap.teamLeader, false],
    ["manager",    snap.manager,    false],
    ["director",   snap.director,   false]
  ];
  if (snap.isSplitDeal && snap.splitChain) {
    chain.push(["salesAgent2", snap.splitChain.salesAgent2, true]);
    chain.push(["teamLeader2", snap.splitChain.teamLeader2, true]);
    chain.push(["manager2",    snap.splitChain.manager2,    true]);
    chain.push(["director2",   snap.splitChain.director2,   true]);
  }
  chain.forEach(function(t){
    var slotKey = t[0], r = t[1], isSplit = t[2];
    if (!r || !r.userName) return;
    var ov = bySlot[slotKey];
    if (ov && ov.source === "manual_remove") return;
    var computed = Number(r.computedShare || 0);
    var override = (r.overrideAmount != null && Number(r.overrideAmount) > 0) ? Number(r.overrideAmount) : null;
    var effective;
    var overrideSource = null;
    if (ov && ov.source === "manual_zero") {
      effective = 0;
      overrideSource = "manual_zero";
    } else {
      effective = override != null ? override : computed;
    }
    rows.push({
      slotKey:        slotKey,
      userName:       r.userName,
      userId:         r.userId || null,
      role:           r.role || "sales",
      computedShare:  computed,
      overrideAmount: override,
      effectiveOwed:  effective,
      isExtra:        false,
      overrideSource: overrideSource,
      overrideId:     ov && ov._id ? String(ov._id) : null,
      addedBy:        ov && ov.addedBy ? ov.addedBy : "",
      addedAt:        ov && ov.addedAt ? ov.addedAt : null,
      isSplit:        isSplit
    });
  });
  manualAdds.forEach(function(ma){
    var maAmt = Number(ma.computedShare || 0);
    rows.push({
      slotKey:        "extra",
      userName:       ma.name || "",
      userId:         ma.userId || null,
      role:           ma.role || "sales",
      computedShare:  maAmt,
      overrideAmount: null,
      effectiveOwed:  maAmt,
      isExtra:        true,
      overrideSource: "manual_add",
      overrideId:     ma._id ? String(ma._id) : null,
      addedBy:        ma.addedBy || "",
      addedAt:        ma.addedAt || null,
      isSplit:        false
    });
  });
  return rows;
}

// Phase R-5 — piaster-level precision helpers. Every derived tax value
// (netOfVat, vat, withholding, netRevenue) must round to 2 decimals so
// totals reconcile against Excel/government tax filings. claimAmount itself
// stays as a clean product of admin-typed inputs; only the derivatives round.
function round2(n) { return Math.round(Number(n || 0) * 100) / 100; }
function fmtMoney2(n) {
  return round2(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " EGP";
}

// Snapshot edit (Phase R-1) — partial PATCH of {developer, unitDetails, dealTotal,
// expectedTotal, expectedCollectionDate} plus per-recipient overrideAmount/
// overrideReason. Computed shares are read-only here (server recomputes on save).
// Phase R-8 — Unified "Edit Collection" modal. Replaces the three separate
// editors (Snapshot, Payout-Add, Incentive) with a single 720px modal that
// manages the developer-side collection flow: claims, payouts, and incentives.
// Four stacked sections — snapshot (Deal Total + read-only Project Name),
// recipients/overrides, payouts (list + inline add), incentive (list + inline
// add). One "Save All Changes" button runs every dirty mutation in sequence,
// then calls onSaved() with the final commission.
//
// Project Name is read-only — the PATCH /snapshot endpoint only whitelists
// developer/unitDetails/dealTotal (and the top-level expectedTotal /
// expectedCollectionDate which we intentionally hide here per user spec:
// those stats are gone from the card and the modal too). Making projectName
// editable would require a backend whitelist expansion — flagged for the user.
var CommissionEditCollectionModal = function(p) {
  var c = p.target;
  var snap = (c && c.snapshot) || {};
  var token = p.token;
  var users = p.users || [];
  // Phase R-14 — role + ambassador flag. The modal is only reachable from the
  // admin/operation-gated Commissions page, but we still derive isOnlyAdmin
  // (defense-in-depth) to gate the AMBASSADOR DETAILS section explicitly.
  var cu = p.cu || {};
  var isOnlyAdmin = cu.role === "admin" || cu.role === "operation";
  var isAmbassador = !!(c && c.ambassadorSplit && c.ambassadorSplit.isAmbassador);

  // ---------- SECTION 1 — snapshot ----------
  var [dealTotal, setDealTotal] = useState(Number(snap.dealTotal || 0) > 0 ? Number(snap.dealTotal).toLocaleString() : "");
  // Read-only display value for projectName (server doesn't accept it on the
  // snapshot PATCH today — see component comment). Closed Date used to render
  // here too but was dropped to slim Section 1; the card itself still shows
  // "closed <date>" in its header.
  var projectNameDisplay = snap.projectName || "";

  // ---------- SECTION 2 — recipients/overrides ----------
  var initOverrides = function(){
    var out = {};
    var roles = [
      ["salesAgent", snap.salesAgent],
      ["teamLeader", snap.teamLeader],
      ["manager",    snap.manager],
      ["director",   snap.director]
    ];
    if (snap.isSplitDeal && snap.splitChain) {
      roles.push(["salesAgent2", snap.splitChain.salesAgent2]);
      roles.push(["teamLeader2", snap.splitChain.teamLeader2]);
      roles.push(["manager2",    snap.splitChain.manager2]);
      roles.push(["director2",   snap.splitChain.director2]);
    }
    roles.forEach(function(pair){
      if (!pair[1]) return;
      var ov = pair[1].overrideAmount;
      out[pair[0]] = {
        userName:       pair[1].userName,
        role:           pair[1].role,
        computedShare:  Number(pair[1].computedShare || 0),
        rate:           Number(pair[1].rate || 0),
        rateBucket:     pair[1].rateBucket || "",
        isHalfWeight:   !!pair[1].isHalfWeight,
        overrideAmount: ov != null && Number(ov) > 0 ? Number(ov).toLocaleString() : "",
        overrideReason: pair[1].overrideReason || ""
      };
    });
    return out;
  };
  var [overrides, setOverrides] = useState(initOverrides);

  // ---------- SECTION 3 — payouts ----------
  // existingPayouts: stable copies of payouts already on the commission.
  // payoutsToDelete: Set of _ids to DELETE on save.
  // newPayouts: draft rows the admin added via "+ Add Payout".
  // Stable snapshot of payouts already on the commission. We never mutate the
  // list itself — additions go into newPayouts; removals are queued in
  // payoutsToDelete and flushed on Save. Hence no setter is needed.
  var [existingPayouts] = useState(function(){
    return (c.payouts || []).slice();
  });
  var [payoutsToDelete, setPayoutsToDelete] = useState({});  // {payoutId: true}
  var [newPayouts, setNewPayouts] = useState([]);            // each: {recipientUserName, recipientRole, amount, date, notes}

  // ---------- SECTION 3.5 — broker payouts (Phase R-12 Part 6) ----------
  // Mirrors the internal-payout pattern above. Only surfaced in the modal
  // when externalSplit.isExternal — but we still init state so saveAll can
  // be a single sequential flush. brokerPayouts have no userId, no debt
  // machinery, no role enum (Part 5 judgment #1).
  var [existingBrokerPayouts] = useState(function(){
    return ((c.externalSplit && c.externalSplit.brokerPayouts) || []).slice();
  });
  var [brokerPayoutsToDelete, setBrokerPayoutsToDelete] = useState({});
  var [newBrokerPayouts, setNewBrokerPayouts] = useState([]);
  var addBrokerPayoutDraft = function(){
    setNewBrokerPayouts(function(prev){
      return prev.concat([{ amount: "", date: new Date().toISOString().slice(0,10), notes: "" }]);
    });
  };
  var setNewBrokerPayoutField = function(idx, patch){
    setNewBrokerPayouts(function(prev){ return prev.map(function(r, i){ return i === idx ? Object.assign({}, r, patch) : r; }); });
  };
  var removeNewBrokerPayout = function(idx){
    setNewBrokerPayouts(function(prev){ return prev.filter(function(_, i){ return i !== idx; }); });
  };
  var toggleBrokerPayoutDelete = function(id){
    setBrokerPayoutsToDelete(function(prev){
      var next = Object.assign({}, prev);
      if (next[id]) delete next[id]; else next[id] = true;
      return next;
    });
  };

  // Build the recipient dropdown via the canonical override layer so the
  // dropdown matches what the Payout Report / BE membership check will accept.
  // Phase R-13 — manual_remove drops the slot; manual_zero shows the row with
  // owed=0; manual_add extras are valid payout targets.
  var recipientOptions = applyRecipientOverridesFE(c).map(function(r){
    return { userName: r.userName, role: r.role, owed: Number(r.effectiveOwed || 0) };
  });
  var todayIso = new Date().toISOString().slice(0, 10);
  var addPayoutDraft = function(){
    if (recipientOptions.length === 0) { alert("No recipients on this commission's snapshot."); return; }
    var first = recipientOptions[0];
    setNewPayouts(function(prev){
      return prev.concat([{
        recipientUserName: first.userName,
        recipientRole:     first.role,
        amount:            "",
        date:              todayIso,
        notes:             ""
      }]);
    });
  };
  var setNewPayoutField = function(idx, patch){
    setNewPayouts(function(prev){ return prev.map(function(r, i){ return i === idx ? Object.assign({}, r, patch) : r; }); });
  };
  var removeNewPayout = function(idx){
    setNewPayouts(function(prev){ return prev.filter(function(_, i){ return i !== idx; }); });
  };
  var togglePayoutDelete = function(id){
    setPayoutsToDelete(function(prev){
      var next = Object.assign({}, prev);
      if (next[id]) delete next[id]; else next[id] = true;
      return next;
    });
  };

  // ---------- SECTION 4 — incentive (full replacement) ----------
  var [incentiveRows, setIncentiveRows] = useState(function(){
    return ((c.incentive && Array.isArray(c.incentive.recipients)) ? c.incentive.recipients : []).map(function(r){
      return {
        userName:     r.userName || "",
        userId:       r.userId || "",
        role:         r.role || "sales",
        amount:       Number(r.amount || 0) > 0 ? Number(r.amount).toLocaleString() : "",
        status:       r.status || "pending",
        receivedDate: r.receivedDate || ""
      };
    });
  });
  var initialIncentiveSnapshot = JSON.stringify(((c.incentive && c.incentive.recipients) || []).map(function(r){
    return { userName: r.userName||"", role: r.role||"", amount: Number(r.amount||0), status: r.status||"pending", receivedDate: r.receivedDate||"" };
  }));
  var setIncRow = function(idx, patch){
    setIncentiveRows(function(prev){ return prev.map(function(r, i){ return i === idx ? Object.assign({}, r, patch) : r; }); });
  };
  var addIncRow = function(){
    setIncentiveRows(function(prev){ return prev.concat([{ userName:"", userId:"", role:"sales", amount:"0", status:"pending", receivedDate:"" }]); });
  };
  var removeIncRow = function(idx){
    setIncentiveRows(function(prev){ return prev.filter(function(_, i){ return i !== idx; }); });
  };

  // ---------- Phase R-14 — ambassador edit state (immediate-apply) ----------
  // Unlike the internal/external sections (batched into Save All), ambassador
  // recipient/payout/config actions hit their endpoints immediately and refresh
  // liveC in place — this sidesteps the new-recipient → payout ordering problem
  // (a payout needs a persisted recipient _id) and keeps the nested CRUD robust.
  var [liveC, setLiveC] = useState(c);
  var ambSplit = (liveC && liveC.ambassadorSplit) || {};
  var [ambBusy, setAmbBusy] = useState(false);
  // Developer-tax draft (the only editable AMBASSADOR DETAILS field; rate is RO).
  var [taxEnabledDraft, setTaxEnabledDraft] = useState(!!ambSplit.developerTaxEnabled);
  var [taxRateDraft, setTaxRateDraft] = useState(ambSplit.developerTaxRate != null ? String(ambSplit.developerTaxRate) : "");
  // Add-recipient draft.
  var [showAddRecip, setShowAddRecip] = useState(false);
  var [addRecipUserId, setAddRecipUserId] = useState("");
  var [addRecipAmount, setAddRecipAmount] = useState("");
  // Manual-recipient fields (free-text name + role when no User is picked).
  var [addRecipName, setAddRecipName] = useState("");
  var [addRecipRole, setAddRecipRole] = useState("sales");
  // Inline recipient amount edit (recipientId being edited + draft amount).
  var [editRecipId, setEditRecipId] = useState("");
  var [editRecipAmount, setEditRecipAmount] = useState("");
  // Add-payout draft, scoped to one recipient at a time.
  var [payoutForRecip, setPayoutForRecip] = useState("");
  var [payoutAmount, setPayoutAmount] = useState("");
  var [payoutDate, setPayoutDate] = useState(new Date().toISOString().slice(0,10));
  var [payoutNote, setPayoutNote] = useState("");

  // ---------- save-all flow ----------
  var [saving, setSaving] = useState(false);

  var buildSnapshotBody = function(){
    var body = {};
    var parsedDeal = parseMoney(dealTotal);
    if (isFinite(parsedDeal) && parsedDeal !== Number(snap.dealTotal || 0)) body.dealTotal = parsedDeal;
    Object.keys(overrides).forEach(function(k){
      var o = overrides[k];
      var origSlot = (k.indexOf("2") > -1 && snap.splitChain) ? snap.splitChain[k] : snap[k];
      var origAmt = origSlot && origSlot.overrideAmount != null ? Number(origSlot.overrideAmount) : null;
      var origReason = (origSlot && origSlot.overrideReason) || "";
      var parsedOv = o.overrideAmount === "" ? null : parseMoney(o.overrideAmount);
      var newAmt = (parsedOv != null && isFinite(parsedOv) && parsedOv > 0) ? parsedOv : null;
      var amtChanged = (origAmt || 0) !== (newAmt || 0);
      var reasonChanged = origReason !== (o.overrideReason || "");
      if (amtChanged || reasonChanged) {
        body[k] = { overrideAmount: newAmt == null ? "" : newAmt, overrideReason: o.overrideReason || "" };
      }
    });
    return body;
  };
  var buildIncentiveBody = function(){
    var rec = incentiveRows.map(function(r){
      var parsedAmt = parseMoney(r.amount);
      var out = {
        userName: String(r.userName || "").trim(),
        role:     r.role,
        amount:   isFinite(parsedAmt) ? parsedAmt : 0,
        status:   r.status,
        receivedDate: r.receivedDate || ""
      };
      if (r.userId) out.userId = r.userId;
      return out;
    });
    return { recipients: rec };
  };
  var incentiveChanged = (function(){
    var current = JSON.stringify(incentiveRows.map(function(r){
      var parsedAmt = parseMoney(r.amount);
      return { userName: String(r.userName||"").trim(), role: r.role||"", amount: isFinite(parsedAmt)?parsedAmt:0, status: r.status||"pending", receivedDate: r.receivedDate||"" };
    }));
    return current !== initialIncentiveSnapshot;
  })();

  var saveAll = async function(){
    // ---- pre-flight validation ----
    var parsedDeal = parseMoney(dealTotal);
    if (dealTotal && (!isFinite(parsedDeal) || parsedDeal < 0)) {
      alert("Deal total must be a non-negative number."); return;
    }
    for (var i = 0; i < newPayouts.length; i++) {
      var np = newPayouts[i];
      var an = parseMoney(np.amount);
      if (!isFinite(an) || an <= 0) { alert("New payout row " + (i+1) + ": amount must be > 0"); return; }
      if (!np.date) { alert("New payout row " + (i+1) + ": date required"); return; }
      if (!np.recipientUserName) { alert("New payout row " + (i+1) + ": recipient required"); return; }
    }
    // Phase R-12 Part 6 — broker-payout draft validation. Mirrors internal
    // payout validation; date is required, amount must be > 0.
    for (var bi = 0; bi < newBrokerPayouts.length; bi++) {
      var bnp = newBrokerPayouts[bi];
      var ban = parseMoney(bnp.amount);
      if (!isFinite(ban) || ban <= 0) { alert("New broker payout row " + (bi+1) + ": amount must be > 0"); return; }
      if (!bnp.date) { alert("New broker payout row " + (bi+1) + ": date required"); return; }
    }
    for (var ii = 0; ii < incentiveRows.length; ii++) {
      var ir = incentiveRows[ii];
      if (!String(ir.userName || "").trim()) { alert("Incentive row " + (ii+1) + ": name required"); return; }
    }

    setSaving(true);
    var snapshotBody = buildSnapshotBody();
    var deletions = Object.keys(payoutsToDelete);
    var brokerDeletions = Object.keys(brokerPayoutsToDelete);
    var hadSnapshotEdit  = Object.keys(snapshotBody).length > 0;
    var hadPayoutDel     = deletions.length > 0;
    var hadNewPayouts    = newPayouts.length > 0;
    var hadBrokerDel     = brokerDeletions.length > 0;
    var hadNewBrokerPayouts = newBrokerPayouts.length > 0;
    var hadIncentiveEdit = incentiveChanged;
    var didAnything      = hadSnapshotEdit || hadPayoutDel || hadNewPayouts || hadBrokerDel || hadNewBrokerPayouts || hadIncentiveEdit;

    try {
      // 1. Snapshot + overrides (single PATCH)
      if (hadSnapshotEdit) {
        await apiFetch("/api/commissions/" + c._id + "/snapshot", "PATCH", snapshotBody, token);
      }
      // 2. Delete payouts (parallel — independent debt math)
      if (hadPayoutDel) {
        await Promise.all(deletions.map(function(pid){
          return apiFetch("/api/commissions/" + c._id + "/payouts/" + pid, "DELETE", null, token);
        }));
      }
      // 3. Create new payouts (sequential — debt accumulates across them)
      for (var n = 0; n < newPayouts.length; n++) {
        var npRow = newPayouts[n];
        await apiFetch("/api/commissions/" + c._id + "/payouts", "POST", {
          recipientUserName: npRow.recipientUserName,
          recipientRole:     npRow.recipientRole,
          amount:            parseMoney(npRow.amount),
          date:              npRow.date,
          notes:             npRow.notes || ""
        }, token);
      }
      // 3.5. Broker payout deletes (parallel) + creates (sequential — owed
      //       cap is checked server-side per request, so order matters).
      if (hadBrokerDel) {
        await Promise.all(brokerDeletions.map(function(bid){
          return apiFetch("/api/commissions/" + c._id + "/broker-payouts/" + bid, "DELETE", null, token);
        }));
      }
      for (var bn = 0; bn < newBrokerPayouts.length; bn++) {
        var bnpRow = newBrokerPayouts[bn];
        await apiFetch("/api/commissions/" + c._id + "/broker-payouts", "POST", {
          amount: parseMoney(bnpRow.amount),
          date:   bnpRow.date,
          notes:  bnpRow.notes || ""
        }, token);
      }
      // 4. Incentive PUT (full replacement, only if changed)
      if (hadIncentiveEdit) {
        await apiFetch("/api/commissions/" + c._id + "/incentive", "PUT", buildIncentiveBody(), token);
      }
      // 5. Pull fresh commission so the caller can swap rows[] in place.
      var fresh = await apiFetch("/api/commissions/" + c._id, "GET", null, token);
      setSaving(false);
      if (p.onSaved) p.onSaved(fresh, didAnything);
      p.onClose();
    } catch(e) {
      setSaving(false);
      // Some sub-calls may have committed before the failure. Trigger a parent
      // reload so the user sees the actual current state, then surface the
      // error.
      if (p.onPartialFailure) p.onPartialFailure();
      alert("Save partially failed: " + ((e && e.message) || "unknown error") + ". The view has been refreshed — please re-check and retry any unsaved changes.");
    }
  };

  // ---------- render helpers ----------
  var sectionHeaderStyle = { fontSize:11, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.4, marginBottom:8 };
  var dividerStyle = { height:1, background:"#F1F5F9", margin:"14px 0" };
  var roSubLabel = { fontSize:10, color:C.textLight, marginTop:3, fontStyle:"italic" };
  var roFieldStyle = { width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13, background:"#F8FAFC", color:C.textLight };

  // Section 2 row — same visual shape as the existing recipient card row,
  // plus override + reason inputs at the right edge.
  var renderOverrideRow = function(key){
    var o = overrides[key];
    if (!o) return null;
    var bucketLabel = o.rateBucket === "double_target" ? "double target"
                    : o.rateBucket === "target_met"    ? "target met"
                    : o.rateBucket === "base"          ? "base"
                    : o.rateBucket === "fixed"         ? "fixed" : "";
    return <div key={key} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:"1px solid #E2E8F0" }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{key}</div>
        <div style={{ fontSize:10, color:C.textLight }}>{o.userName} ({o.role}) · {Math.round(o.computedShare).toLocaleString()} EGP computed{o.rate > 0 ? " · " + o.rate + "/1000" : ""}{bucketLabel ? " · " + bucketLabel : ""}{o.isHalfWeight ? " · ½×" : ""}</div>
      </div>
      <input type="text" inputMode="numeric" placeholder="override EGP" value={o.overrideAmount || ""}
        onChange={function(e){
          var raw = e.target.value.replace(/,/g, "").replace(/[^0-9]/g, "");
          var fmt = raw === "" ? "" : Number(raw).toLocaleString();
          setOverrides(function(prev){ var n = Object.assign({}, prev); n[key] = Object.assign({}, prev[key], { overrideAmount: fmt }); return n; });
        }}
        style={{ width:110, padding:"5px 8px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:12 }}/>
      <input type="text" placeholder="reason" value={o.overrideReason || ""}
        onChange={function(e){
          var v = e.target.value;
          setOverrides(function(prev){ var n = Object.assign({}, prev); n[key] = Object.assign({}, prev[key], { overrideReason: v }); return n; });
        }}
        style={{ width:140, padding:"5px 8px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:12 }}/>
    </div>;
  };

  // Section 3 — existing payouts (with strike-through if queued for delete)
  // + draft new-payout rows + "+ Add Payout" trigger.
  var visibleExisting = existingPayouts;
  var totalPayoutsCount = visibleExisting.length + newPayouts.length;

  // Section 4 incentive — reuse the role/name/amount/status row pattern.
  var renderIncentiveRow = function(r, idx){
    var matching = users.filter(function(u){ return u && positionOf(u) === r.role; });
    return <div key={idx} style={{ display:"flex", gap:6, alignItems:"center", marginBottom:6, padding:"6px 8px", background:"#F8FAFC", borderRadius:6 }}>
      <Select value={r.role} onChange={function(e){ setIncRow(idx, { role: e.target.value, userId: "" }); }} width={100} maxWidth={100}
        options={[{ value:"sales", label:"Sales" },{ value:"team_leader", label:"Team Lead" },{ value:"manager", label:"Manager" },{ value:"director", label:"Director" }]}/>
      <Select value={r.userId} onChange={function(e){
        var uid = e.target.value;
        var u = matching.find(function(x){ return String(x._id) === uid; });
        setIncRow(idx, { userId: uid, userName: u ? u.name : r.userName });
      }} searchable width={140} maxWidth={140}
        options={[{ value:"", label:"— pick / free text —" }].concat(withCurrentOption(matching.map(function(u){ return { value:String(u._id), label:u.name }; }), r.userId, r.userName || userNameOf(r.userId, users) || "Unknown"))}/>
      <input type="text" placeholder="Or type name" value={r.userName}
        onChange={function(e){ setIncRow(idx, { userName: e.target.value, userId: "" }); }}
        style={{ flex:1, padding:"5px 8px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:12 }}/>
      <input type="text" inputMode="numeric" placeholder="Amount" value={r.amount}
        onChange={function(e){
          var raw = e.target.value.replace(/,/g, "").replace(/[^0-9]/g, "");
          var fmt = raw === "" ? "" : Number(raw).toLocaleString();
          setIncRow(idx, { amount: fmt });
        }}
        style={{ width:100, padding:"5px 8px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:12 }}/>
      <Select value={r.status} onChange={function(e){
        setIncRow(idx, { status: e.target.value, receivedDate: e.target.value === "received" ? (r.receivedDate || todayIso) : "" });
      }} width={100} maxWidth={100}
        options={[{ value:"pending", label:"Pending" },{ value:"received", label:"Received" }]}/>
      {r.status === "received" && <input type="date" value={r.receivedDate}
        onChange={function(e){ setIncRow(idx, { receivedDate: e.target.value }); }}
        style={{ width:120, padding:"5px 8px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:12 }}/>}
      <button onClick={function(){ removeIncRow(idx); }} title="Remove"
        style={{ background:"#fff", border:"1px solid #FCA5A5", borderRadius:6, color:"#B91C1C", padding:"4px 8px", cursor:"pointer", fontSize:11 }}>×</button>
    </div>;
  };

  // ===== Phase R-14 — Ambassador edit layout (immediate-apply) =====
  var ambParse = function(s){ var n = Number(String(s||"").replace(/,/g,"").replace(/[^0-9.]/g,"")); return isFinite(n) ? n : NaN; };
  var ambCall = async function(method, path, body){
    setAmbBusy(true);
    try {
      var fresh = await apiFetch("/api/commissions/" + c._id + path, method, body || null, token);
      if (fresh && fresh._id) { setLiveC(fresh); if (p.onSaved) p.onSaved(fresh, true); }
      return true;
    } catch(e){ alert((e && e.message) || "Action failed"); return false; }
    finally { setAmbBusy(false); }
  };
  var saveTaxConfig = async function(){
    var body = { developerTaxEnabled: !!taxEnabledDraft, developerTaxRate: null };
    if (taxEnabledDraft) {
      var r = ambParse(taxRateDraft);
      if (!isFinite(r) || r <= 0 || r > 100) { alert("Developer Tax Rate must be between 0 and 100"); return; }
      body.developerTaxRate = r;
    }
    await ambCall("PUT", "/ambassador-config", body);
  };
  var resetAddRecip = function(){ setShowAddRecip(false); setAddRecipUserId(""); setAddRecipAmount(""); setAddRecipName(""); setAddRecipRole("sales"); };
  var addRecipient = async function(){
    var nm = String(addRecipName || "").trim();
    // One of (User, typed name) is required. Picking a User takes precedence.
    if (!addRecipUserId && !nm) { alert("Pick a User or type a name"); return; }
    var a = ambParse(addRecipAmount);
    if (addRecipAmount !== "" && (!isFinite(a) || a < 0)) { alert("Amount must be ≥ 0"); return; }
    var amt = isFinite(a) ? a : 0;
    var body = addRecipUserId
      ? { userId: addRecipUserId, amount: amt }
      : { name: nm, role: addRecipRole, amount: amt, isManual: true };
    var ok = await ambCall("POST", "/ambassador-recipients", body);
    if (ok) resetAddRecip();
  };
  var startEditRecip = function(r){ setEditRecipId(String(r._id)); setEditRecipAmount(Number(r.amount||0) > 0 ? Number(r.amount).toLocaleString() : ""); };
  var saveEditRecip = async function(rid){
    var a = ambParse(editRecipAmount);
    if (!isFinite(a) || a < 0) { alert("Amount must be ≥ 0"); return; }
    var ok = await ambCall("PUT", "/ambassador-recipients/" + rid, { amount: a });
    if (ok) { setEditRecipId(""); setEditRecipAmount(""); }
  };
  var removeRecip = async function(r){
    if (!window.confirm("Remove " + (r.userName || "this recipient") + " from this deal?")) return;
    await ambCall("DELETE", "/ambassador-recipients/" + r._id);
  };
  var addPayout = async function(rid){
    var a = ambParse(payoutAmount);
    if (!isFinite(a) || a <= 0) { alert("Payout amount must be > 0"); return; }
    if (!payoutDate) { alert("Date required"); return; }
    var ok = await ambCall("POST", "/ambassador-payouts", { recipientId: rid, amount: a, date: payoutDate, note: payoutNote });
    if (ok) { setPayoutForRecip(""); setPayoutAmount(""); setPayoutNote(""); }
  };
  var deletePayout = async function(payoutId){
    if (!window.confirm("Delete this payout?")) return;
    await ambCall("DELETE", "/ambassador-payouts/" + payoutId);
  };

  if (isAmbassador) {
    var amb = ambSplit;
    var recips = (amb.recipients || []);
    var fmtA = function(n){ return Math.round(Number(n||0)).toLocaleString(); };
    var totalTeam = recips.reduce(function(s, r){ return s + Number(r.amount || 0); }, 0);
    var aroNetA = Number(amb.aroNetTotal || 0);
    var aroKeeps = aroNetA - totalTeam;
    var ambStaff = users.filter(function(u){ return u && u.active && ["sales","team_leader","manager","director"].indexOf(u.role) >= 0; });
    var amberHeader = Object.assign({}, sectionHeaderStyle, { color:"#B45309" });
    var roleLabel = function(role){ return role === "team_leader" ? "Team Lead" : role === "manager" ? "Manager" : role === "director" ? "Director" : "Sales"; };
    return <><Modal show={true} onClose={p.onClose} title={"Edit Collection — " + (snap.customerName || "(unknown)")} w={720}>
      {/* SECTION 1 — Snapshot (read-only for ambassador: gross derives from the
          cycle's claimAmount, so editing dealTotal here would be inert). */}
      <div style={sectionHeaderStyle}>Deal Snapshot</div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:4 }}>
        <FormRow label="Deal Total (EGP)"><input type="text" value={Number(snap.dealTotal || 0).toLocaleString()} readOnly disabled style={roFieldStyle}/></FormRow>
        <FormRow label="Project Name"><input type="text" value={snap.projectName || ""} readOnly disabled style={roFieldStyle}/></FormRow>
      </div>

      {/* AMBASSADOR DETAILS — admin/operation only (defense-in-depth on top of
          the page-level admin gate). The classification is internal to admins. */}
      {isOnlyAdmin && <>
        <div style={dividerStyle}/>
        <div style={amberHeader}>🎯 Ambassador Details</div>
        <div style={{ background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:8, padding:"12px 14px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
            <FormRow label="Commission Rate (%)"><input type="text" value={String(amb.commissionRate != null ? amb.commissionRate : "")} readOnly disabled style={roFieldStyle}/></FormRow>
            <FormRow label="Gross Commission (EGP)"><input type="text" value={fmtA(amb.grossCommission)} readOnly disabled style={roFieldStyle}/></FormRow>
          </div>
          <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, fontWeight:600, color:C.text, cursor: ambBusy ? "wait" : "pointer", marginBottom: taxEnabledDraft ? 8 : 0 }}>
            <input type="checkbox" checked={taxEnabledDraft} disabled={ambBusy}
              onChange={function(e){ setTaxEnabledDraft(e.target.checked); }}/>
            Developer takes tax from commission
          </label>
          {taxEnabledDraft && <div style={{ maxWidth:200, marginBottom:10 }}>
            <FormRow label="Developer Tax Rate (%)">
              <input type="text" inputMode="decimal" value={taxRateDraft} disabled={ambBusy}
                onChange={function(e){
                  var raw = e.target.value.replace(/[^0-9.]/g, "");
                  var fd = raw.indexOf("."); if (fd >= 0) raw = raw.slice(0, fd+1) + raw.slice(fd+1).replace(/\./g, "");
                  setTaxRateDraft(raw);
                }}
                style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13 }} placeholder="e.g. 10"/>
            </FormRow>
          </div>}
          <div style={{ background:"#FEF3C7", border:"1px solid #FCD34D", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#92400E", lineHeight:1.7, marginBottom:10 }}>
            <div>Developer Tax: <b>{fmtA(amb.developerTaxAmount)} EGP</b>{amb.developerTaxEnabled ? " (" + (amb.developerTaxRate || 0) + "%)" : " (disabled)"}</div>
            <div style={{ color:"#B45309", fontWeight:700 }}>ARO Net Total: {fmtA(amb.aroNetTotal)} EGP</div>
          </div>
          <button onClick={saveTaxConfig} disabled={ambBusy}
            style={{ padding:"6px 14px", borderRadius:8, border:"none", background:"#D97706", color:"#fff", fontSize:12, fontWeight:600, cursor: ambBusy ? "wait" : "pointer" }}>
            {ambBusy ? "Saving…" : "Update Developer Tax"}
          </button>
        </div>
      </>}

      {/* RECIPIENTS — manual EGP amounts, replaces the standard payouts table. */}
      <div style={dividerStyle}/>
      <div style={Object.assign({}, amberHeader, { display:"flex", justifyContent:"space-between", alignItems:"center" })}>
        <span>Recipients ({recips.length})</span>
        {isOnlyAdmin && <button onClick={function(){ resetAddRecip(); setShowAddRecip(true); }} disabled={ambBusy}
          style={{ padding:"3px 10px", borderRadius:6, border:"1px solid #D97706", background:"#FEF3C7", color:"#B45309", fontSize:11, fontWeight:600, cursor: ambBusy ? "wait" : "pointer", textTransform:"none", letterSpacing:0 }}>+ Add Manual Recipient</button>}
      </div>
      {recips.length === 0 && <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", padding:"6px 10px" }}>No recipients yet.</div>}
      {recips.length > 0 && <div style={{ border:"1px solid #F1F5F9", borderRadius:8, overflow:"hidden" }}>
        <div style={{ display:"flex", padding:"6px 10px", background:"#F8FAFC", fontSize:10, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3 }}>
          <div style={{ flex:2 }}>Name</div><div style={{ flex:1 }}>Role</div><div style={{ flex:1, textAlign:"right" }}>Amount</div><div style={{ flex:1, textAlign:"right" }}>Paid</div><div style={{ flex:1, textAlign:"right" }}>Remaining</div>{isOnlyAdmin && <div style={{ width:90, textAlign:"right" }}>Actions</div>}
        </div>
        {recips.map(function(r){
          var rid = String(r._id);
          var paid = Number(r.amountPaid || 0);
          var remaining = Number(r.amount || 0) - paid;
          var editing = editRecipId === rid;
          return <div key={rid} style={{ borderTop:"1px solid #F1F5F9", padding:"7px 10px", fontSize:12 }}>
            <div style={{ display:"flex", alignItems:"center" }}>
              <div style={{ flex:2, fontWeight:600, color:C.text, display:"flex", alignItems:"center", gap:6 }}>
                <span>{r.userName || "(unknown)"}</span>
                {r.isManual && <span title="Added by name with no linked User account" style={{ fontSize:9, padding:"1px 5px", borderRadius:6, background:"#EDE9FE", color:"#5B21B6", fontWeight:700, whiteSpace:"nowrap" }}>manually added</span>}
              </div>
              <div style={{ flex:1, color:C.textLight, textTransform:"capitalize" }}>{roleLabel(r.userRole)}</div>
              <div style={{ flex:1, textAlign:"right", fontWeight:600 }}>
                {editing
                  ? <input type="text" inputMode="numeric" value={editRecipAmount} autoFocus
                      onChange={function(e){ var raw = e.target.value.replace(/,/g,"").replace(/[^0-9]/g,""); setEditRecipAmount(raw === "" ? "" : Number(raw).toLocaleString()); }}
                      style={{ width:90, padding:"3px 6px", borderRadius:5, border:"1px solid #E2E8F0", fontSize:12, textAlign:"right" }}/>
                  : fmtA(r.amount)}
              </div>
              <div style={{ flex:1, textAlign:"right", color:C.success }}>{fmtA(paid)}</div>
              <div style={{ flex:1, textAlign:"right", color: remaining > 0 ? "#B45309" : C.textLight }}>{fmtA(remaining)}</div>
              {isOnlyAdmin && <div style={{ width:90, textAlign:"right", display:"flex", gap:4, justifyContent:"flex-end" }}>
                {editing
                  ? <><button onClick={function(){ saveEditRecip(rid); }} disabled={ambBusy} style={{ background:"#D97706", border:"none", borderRadius:4, color:"#fff", padding:"3px 7px", fontSize:10, cursor:"pointer" }}>✓</button>
                       <button onClick={function(){ setEditRecipId(""); }} disabled={ambBusy} style={{ background:"#fff", border:"1px solid #E2E8F0", borderRadius:4, color:C.textLight, padding:"3px 7px", fontSize:10, cursor:"pointer" }}>×</button></>
                  : <><button onClick={function(){ startEditRecip(r); }} disabled={ambBusy} title="Edit amount" style={{ background:"#fff", border:"1px solid #E2E8F0", borderRadius:4, color:C.text, padding:"3px 7px", fontSize:10, cursor:"pointer" }}>✎</button>
                       <button onClick={function(){ removeRecip(r); }} disabled={ambBusy} title="Remove" style={{ background:"#fff", border:"1px solid #FCA5A5", borderRadius:4, color:"#B91C1C", padding:"3px 7px", fontSize:10, cursor:"pointer" }}>🗑</button></>}
              </div>}
            </div>
            {/* Per-recipient payouts */}
            <div style={{ marginTop:6, marginLeft:8, paddingLeft:10, borderLeft:"2px solid #FDE68A" }}>
              {(r.payouts || []).map(function(po){
                return <div key={String(po._id)} style={{ display:"flex", alignItems:"center", gap:8, fontSize:11, color:C.textLight, padding:"2px 0" }}>
                  <span style={{ minWidth:78 }}>{po.date ? new Date(po.date).toLocaleDateString("en-GB") : "—"}</span>
                  <span style={{ fontWeight:600, color:C.success }}>{fmtA(po.amount)} EGP</span>
                  {po.note && <span style={{ fontStyle:"italic" }}>· {po.note}</span>}
                  {isOnlyAdmin && <button onClick={function(){ deletePayout(String(po._id)); }} disabled={ambBusy} title="Delete payout" style={{ marginLeft:"auto", background:"#fff", border:"1px solid #FCA5A5", borderRadius:4, color:"#B91C1C", padding:"1px 6px", fontSize:10, cursor:"pointer" }}>✕</button>}
                </div>;
              })}
              {isOnlyAdmin && (payoutForRecip === rid
                ? <div style={{ display:"flex", gap:5, alignItems:"center", marginTop:4 }}>
                    <input type="date" value={payoutDate} onChange={function(e){ setPayoutDate(e.target.value); }} style={{ padding:"3px 6px", borderRadius:5, border:"1px solid #E2E8F0", fontSize:11 }}/>
                    <input type="text" inputMode="numeric" placeholder="Amount" value={payoutAmount} onChange={function(e){ var raw = e.target.value.replace(/,/g,"").replace(/[^0-9]/g,""); setPayoutAmount(raw === "" ? "" : Number(raw).toLocaleString()); }} style={{ width:90, padding:"3px 6px", borderRadius:5, border:"1px solid #E2E8F0", fontSize:11 }}/>
                    <input type="text" placeholder="Note" value={payoutNote} onChange={function(e){ setPayoutNote(e.target.value); }} style={{ flex:1, padding:"3px 6px", borderRadius:5, border:"1px solid #E2E8F0", fontSize:11 }}/>
                    <button onClick={function(){ addPayout(rid); }} disabled={ambBusy} style={{ background:"#16A34A", border:"none", borderRadius:5, color:"#fff", padding:"3px 9px", fontSize:11, cursor:"pointer" }}>Save</button>
                    <button onClick={function(){ setPayoutForRecip(""); }} disabled={ambBusy} style={{ background:"#fff", border:"1px solid #E2E8F0", borderRadius:5, color:C.textLight, padding:"3px 7px", fontSize:11, cursor:"pointer" }}>×</button>
                  </div>
                : <button onClick={function(){ setPayoutForRecip(rid); setPayoutAmount(""); setPayoutDate(new Date().toISOString().slice(0,10)); setPayoutNote(""); }} disabled={ambBusy}
                    style={{ marginTop:4, background:"none", border:"none", color:"#B45309", fontSize:11, fontWeight:600, cursor:"pointer", padding:0 }}>+ Add payout</button>)}
            </div>
          </div>;
        })}
      </div>}
      {/* Footer totals + ARO keeps (admin/SA only — money breakdown is internal) */}
      {isOnlyAdmin && <div style={{ marginTop:10, padding:"8px 12px", background:"#F8FAFC", borderRadius:8, fontSize:12, fontWeight:600, color:C.text, display:"flex", justifyContent:"space-between" }}>
        <span>Total to team: <b>{fmtA(totalTeam)} EGP</b></span>
        <span style={{ color: aroKeeps < 0 ? "#B91C1C" : "#B45309" }}>ARO keeps: <b>{fmtA(aroKeeps)} EGP</b></span>
      </div>}

      {/* FOOTER — actions apply immediately, so this just closes. */}
      <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:16, paddingTop:14, borderTop:"1px solid "+C.border }}>
        <button onClick={p.onClose} disabled={ambBusy}
          style={{ padding:"7px 16px", borderRadius:8, border:"none", background: C.accent, color:"#fff", cursor: ambBusy ? "wait" : "pointer", fontSize:13, fontWeight:600 }}>Done</button>
      </div>
    </Modal>

    {/* ADD MANUAL RECIPIENT — mirrors the internal CommissionsPage modal: User
        dropdown (recommended) → "or" → free-text Name + Role + Amount. Picking a
        User links the payout (userId path); a typed name posts isManual:true. */}
    {showAddRecip && isOnlyAdmin && <Modal show={true} onClose={resetAddRecip} title={"➕ Add Manual Recipient — " + (snap.customerName || "Commission")} w={460}>
      <div style={{ fontSize:11, color:C.textLight, marginBottom:12, lineHeight:1.5 }}>
        Add a recipient to this ambassador deal with a manual EGP amount.
        <br/><b>Tip:</b> picking an existing User links the payout to their profile (so it shows on the Payout Report). Use the manual-name field below only for off-system recipients.
      </div>
      <Inp label="User (recommended)" searchable type="select" value={addRecipUserId} onChange={function(e){
        setAddRecipUserId(e.target.value);
        if (e.target.value) {
          var u = ambStaff.find(function(x){ return String(gid(x)) === String(e.target.value); });
          if (u && u.name) setAddRecipName(u.name);
        }
      }} options={[{ value:"", label:"— Pick a User (recommended) —" }].concat(
        ambStaff.map(function(u){ return { value: String(gid(u)), label: (u.name || "(unnamed)") + " · " + roleLabel(u.role) }; })
      )}/>
      <div style={{ fontSize:10, color:C.textLight, margin:"-6px 0 8px 4px" }}>— or enter a name manually below (no User link; appears on Payout Report under the typed name) —</div>
      <Inp label="Name" req value={addRecipName} onChange={function(e){ setAddRecipName(e.target.value); }} placeholder="Full name"/>
      <Inp label="Role" type="select" value={addRecipRole} onChange={function(e){ setAddRecipRole(e.target.value); }} options={[
        { value:"sales", label:"Sales" },
        { value:"team_leader", label:"Team Leader" },
        { value:"manager", label:"Manager" },
        { value:"director", label:"Director" }
      ]}/>
      <Inp label="Amount (EGP)" value={addRecipAmount} onChange={function(e){
        var raw = e.target.value.replace(/,/g,"").replace(/[^0-9]/g,"");
        setAddRecipAmount(raw === "" ? "" : Number(raw).toLocaleString());
      }} placeholder="e.g. 25,000 (0 allowed)"/>
      <div style={{ display:"flex", gap:10, marginTop:6 }}>
        <Btn outline onClick={resetAddRecip} style={{ flex:1 }}>Cancel</Btn>
        <Btn onClick={addRecipient} style={{ flex:1 }} loading={ambBusy}>Add</Btn>
      </div>
    </Modal>}
    </>;
  }

  return <Modal show={true} onClose={p.onClose} title={"Edit Collection — " + (snap.customerName || "(unknown)")} w={720}>
    {/* SECTION 1 — Snapshot */}
    <div style={sectionHeaderStyle}>Deal Snapshot</div>
    <FormRow label="Deal Total (EGP)"><MoneyInput value={dealTotal} onChange={setDealTotal} autoFocus={true}/></FormRow>
    <FormRow label="Project Name">
      <input type="text" value={projectNameDisplay} readOnly disabled style={roFieldStyle}/>
      <div style={roSubLabel}>Snapshotted at deal close. Edit on the lead page to change.</div>
    </FormRow>

    {/* Phase R-12 Part 6 — SECTION 1.5: External Split (read-only). Surfaces
        between Snapshot and Recipients only when the commission is external.
        All three input fields (broker, both pcts) are read-only here — the
        Part 5 lock prevents mutation while the commission is active. To
        change them, admin cancels the commission then edits via the deal. */}
    {c.externalSplit && c.externalSplit.isExternal && (function(){
      var es = c.externalSplit;
      var taxAmt    = Number(es.commissionTaxAmount || 0);
      var aroNet    = Number(es.aroNetAmount || 0);
      // Phase R-13.2 — show brokerNetOwed when present (paidBy-adjusted).
      var brokerGrossOwd = Number(es.brokerOwed || 0);
      var brokerOwd = (es.brokerNetOwed != null) ? Number(es.brokerNetOwed) : brokerGrossOwd;
      var paidByBrokerHere = String(es.salesAgentPaidBy || "company") === "broker";
      var aroOwd    = Number(es.aroOwed || 0);
      var fmt = function(n){ return Math.round(Number(n||0)).toLocaleString(); };
      return <>
        <div style={dividerStyle}/>
        <div style={Object.assign({}, sectionHeaderStyle, { color:"#5B21B6" })}>External Split</div>
        <div style={{ fontSize:10, color:C.textLight, marginBottom:8, lineHeight:1.5 }}>
          🔒 Locked — change via the Edit Deal modal on the lead (requires cancelling this commission first).
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:10 }}>
          <FormRow label="Broker"><input type="text" value={es.brokerName || "(unnamed)"} readOnly disabled style={roFieldStyle}/></FormRow>
          <FormRow label="Commission Tax %"><input type="text" value={String(es.commissionTaxPct || 0)} readOnly disabled style={roFieldStyle}/></FormRow>
          <FormRow label="Broker Share %"><input type="text" value={String(es.brokerSharePct || 0)} readOnly disabled style={roFieldStyle}/></FormRow>
        </div>
        <div style={{ background:"#F5F3FF", border:"1px solid #DDD6FE", borderRadius:8, padding:"10px 12px", fontSize:12, color:"#3730A3", lineHeight:1.7 }}>
          <div><b>Computed (from received cycles)</b></div>
          <div>Tax: <b>{fmt(taxAmt)} EGP</b> &nbsp;·&nbsp; ARO net: <b>{fmt(aroNet)} EGP</b></div>
          <div>Broker owed: <b>{fmt(brokerOwd)} EGP</b>{paidByBrokerHere && brokerGrossOwd !== brokerOwd && <span style={{ color:C.textLight }}> (gross {fmt(brokerGrossOwd)} − sales {fmt(brokerGrossOwd - brokerOwd)})</span>} &nbsp;·&nbsp; ARO owed: <b>{fmt(aroOwd)} EGP</b></div>
        </div>
      </>;
    })()}

    <div style={dividerStyle}/>

    {/* SECTION 2 — Recipients */}
    <div style={sectionHeaderStyle}>Recipients & Overrides</div>
    <div style={{ fontSize:10, color:C.textLight, marginBottom:8, lineHeight:1.5 }}>
      {c.externalSplit && c.externalSplit.isExternal
        ? <>Internal team share (out of ARO's <b>{Math.round(Number(c.externalSplit.aroOwed || 0)).toLocaleString()} EGP</b>). Computed share + rate are derived from the agent's quarterly bucket against this ARO-owed base — they recompute on save. Type an override amount (EGP) to pin a custom value; clear it to revert.</>
        : <>Computed share + rate are derived from the agent's quarterly bucket — they recompute on save. Type an override amount (EGP) to pin a custom value; clear it to revert.</>}
    </div>
    <div style={{ background:"#F8FAFC", borderRadius:8, padding:"6px 12px" }}>
      {Object.keys(overrides).length === 0 && <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic" }}>No recipients on snapshot.</div>}
      {Object.keys(overrides).map(function(k){ return renderOverrideRow(k); })}
    </div>

    <div style={dividerStyle}/>

    {/* SECTION 3 — Payouts */}
    <div style={Object.assign({}, sectionHeaderStyle, { display:"flex", justifyContent:"space-between", alignItems:"center" })}>
      <span>Payouts ({totalPayoutsCount})</span>
      {recipientOptions.length > 0 && <button onClick={addPayoutDraft} disabled={saving}
        style={{ padding:"3px 10px", borderRadius:6, border:"1px solid "+C.accent, background:C.accent+"12", color:C.accent, fontSize:11, fontWeight:600, cursor: saving ? "wait" : "pointer", textTransform:"none", letterSpacing:0 }}>+ Add Payout</button>}
    </div>
    {totalPayoutsCount === 0 && <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", padding:"6px 10px" }}>No payouts recorded yet.</div>}
    {visibleExisting.map(function(po){
      var marked = !!payoutsToDelete[String(po._id)];
      return <div key={String(po._id)} style={{
        display:"flex", justifyContent:"space-between", alignItems:"center",
        padding:"6px 10px", background: marked ? "#FEF2F2" : "#F8FAFC",
        borderRadius:6, marginBottom:4,
        textDecoration: marked ? "line-through" : "none",
        opacity: marked ? 0.7 : 1
      }}>
        <div style={{ minWidth:0, flex:1 }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{po.recipientUserName} <span style={{ fontSize:10, color:C.textLight, textTransform:"capitalize" }}>({po.recipientRole})</span></div>
          <div style={{ fontSize:10, color:C.textLight }}>{po.date ? new Date(po.date).toLocaleDateString("en-GB") : "—"}{po.notes ? " · " + po.notes : ""}{Number(po.appliedToDebt || 0) > 0 ? " · " + Math.round(po.appliedToDebt).toLocaleString() + " applied to debt" : ""}</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:12, fontWeight:600, color:C.success }}>{Math.round(Number(po.netPaid || 0)).toLocaleString() + " EGP"}</div>
            {Number(po.amount) !== Number(po.netPaid) && <div style={{ fontSize:9, color:C.textLight }}>gross {Math.round(Number(po.amount || 0)).toLocaleString() + " EGP"}</div>}
          </div>
          <button onClick={function(){ togglePayoutDelete(String(po._id)); }} disabled={saving}
            title={marked ? "Undo delete" : "Mark for deletion"}
            style={{ background:"#fff", border:"1px solid "+(marked ? C.textLight : "#FCA5A5"), borderRadius:4, padding:"2px 6px", fontSize:10, color: marked ? C.textLight : "#B91C1C", cursor: saving ? "wait" : "pointer" }}>
            {marked ? "undo" : "✕"}
          </button>
        </div>
      </div>;
    })}
    {newPayouts.map(function(np, idx){
      return <div key={"np-" + idx} style={{ display:"flex", gap:6, alignItems:"center", marginBottom:4, padding:"6px 10px", background:"#F0FDF4", borderRadius:6, border:"1px dashed #86EFAC" }}>
        <Select value={np.recipientUserName + "::" + np.recipientRole}
          onChange={function(e){
            var parts = String(e.target.value).split("::");
            setNewPayoutField(idx, { recipientUserName: parts[0], recipientRole: parts[1] });
          }}
          searchable width={170} maxWidth={170} fontSize={11}
          options={recipientOptions.map(function(o){
            return { value:o.userName + "::" + o.role, label:o.userName + " (" + o.role + ")" };
          })}/>
        <input type="date" value={np.date}
          onChange={function(e){ setNewPayoutField(idx, { date: e.target.value }); }}
          style={{ width:130, padding:"4px 6px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:11 }}/>
        <input type="text" inputMode="numeric" placeholder="Amount" value={np.amount}
          onChange={function(e){
            var raw = e.target.value.replace(/,/g, "").replace(/[^0-9]/g, "");
            var fmt = raw === "" ? "" : Number(raw).toLocaleString();
            setNewPayoutField(idx, { amount: fmt });
          }}
          style={{ width:100, padding:"4px 6px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:11 }}/>
        <input type="text" placeholder="Notes" value={np.notes}
          onChange={function(e){ setNewPayoutField(idx, { notes: e.target.value }); }}
          style={{ flex:1, padding:"4px 6px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:11 }}/>
        <button onClick={function(){ removeNewPayout(idx); }} disabled={saving} title="Discard draft"
          style={{ background:"#fff", border:"1px solid #FCA5A5", borderRadius:4, padding:"3px 7px", fontSize:10, color:"#B91C1C", cursor: saving ? "wait" : "pointer" }}>×</button>
      </div>;
    })}

    {/* Phase R-12 Part 6 — SECTION 3.5: Broker Payouts. Surfaces only when
        external. Same structure as internal Payouts above (existing rows w/
        toggle-delete + draft rows w/ inline form), but with the broker-only
        schema (no recipient picker, no role, no debt). */}
    {c.externalSplit && c.externalSplit.isExternal && (function(){
      var totalBpCount = existingBrokerPayouts.length + newBrokerPayouts.length;
      return <>
        <div style={dividerStyle}/>
        <div style={Object.assign({}, sectionHeaderStyle, { display:"flex", justifyContent:"space-between", alignItems:"center", color:"#5B21B6" })}>
          <span>Broker Payouts ({totalBpCount}) — to <b>{c.externalSplit.brokerName || "(unnamed)"}</b></span>
          <button onClick={addBrokerPayoutDraft} disabled={saving}
            style={{ padding:"3px 10px", borderRadius:6, border:"1px solid #5B21B6", background:"#EDE9FE", color:"#5B21B6", fontSize:11, fontWeight:600, cursor: saving ? "wait" : "pointer", textTransform:"none", letterSpacing:0 }}>+ Add Broker Payout</button>
        </div>
        {totalBpCount === 0 && <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", padding:"6px 10px" }}>No broker payouts recorded yet.</div>}
        {existingBrokerPayouts.map(function(bp){
          var marked = !!brokerPayoutsToDelete[String(bp._id)];
          return <div key={String(bp._id)} style={{
            display:"flex", justifyContent:"space-between", alignItems:"center",
            padding:"6px 10px", background: marked ? "#FEF2F2" : "#F8FAFC",
            borderRadius:6, marginBottom:4,
            textDecoration: marked ? "line-through" : "none",
            opacity: marked ? 0.7 : 1
          }}>
            <div style={{ minWidth:0, flex:1 }}>
              <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{bp.date ? new Date(bp.date).toLocaleDateString("en-GB") : "—"}{bp.byUser ? " · by " + bp.byUser : ""}</div>
              {bp.notes && <div style={{ fontSize:10, color:C.textLight }}>{bp.notes}</div>}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ fontSize:12, fontWeight:600, color:"#5B21B6" }}>{Math.round(Number(bp.amount || 0)).toLocaleString() + " EGP"}</div>
              <button onClick={function(){ toggleBrokerPayoutDelete(String(bp._id)); }} disabled={saving}
                title={marked ? "Undo delete" : "Mark for deletion"}
                style={{ background:"#fff", border:"1px solid "+(marked ? C.textLight : "#FCA5A5"), borderRadius:4, padding:"2px 6px", fontSize:10, color: marked ? C.textLight : "#B91C1C", cursor: saving ? "wait" : "pointer" }}>
                {marked ? "undo" : "✕"}
              </button>
            </div>
          </div>;
        })}
        {newBrokerPayouts.map(function(bnp, idx){
          return <div key={"bnp-" + idx} style={{ display:"flex", gap:6, alignItems:"center", marginBottom:4, padding:"6px 10px", background:"#F5F3FF", borderRadius:6, border:"1px dashed #DDD6FE" }}>
            <input type="date" value={bnp.date}
              onChange={function(e){ setNewBrokerPayoutField(idx, { date: e.target.value }); }}
              style={{ width:130, padding:"4px 6px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:11 }}/>
            <input type="text" inputMode="numeric" placeholder="Amount" value={bnp.amount}
              onChange={function(e){
                var raw = e.target.value.replace(/,/g, "").replace(/[^0-9]/g, "");
                var fmt = raw === "" ? "" : Number(raw).toLocaleString();
                setNewBrokerPayoutField(idx, { amount: fmt });
              }}
              style={{ width:120, padding:"4px 6px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:11 }}/>
            <input type="text" placeholder="Notes" value={bnp.notes}
              onChange={function(e){ setNewBrokerPayoutField(idx, { notes: e.target.value }); }}
              style={{ flex:1, padding:"4px 6px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:11 }}/>
            <button onClick={function(){ removeNewBrokerPayout(idx); }} disabled={saving} title="Discard draft"
              style={{ background:"#fff", border:"1px solid #FCA5A5", borderRadius:4, padding:"3px 7px", fontSize:10, color:"#B91C1C", cursor: saving ? "wait" : "pointer" }}>×</button>
          </div>;
        })}
      </>;
    })()}

    {/* Phase R-12 Part 6 — orphan brokerPayouts warning. From Part 5 judgment
        #4: a deal that flipped External→Internal while cancelled, then was
        revived, may carry historical brokerPayouts on a now-internal commission.
        Show a small audit notice; the backend POST endpoint refuses new
        broker payouts in this state, so this is read-only. */}
    {c.externalSplit && !c.externalSplit.isExternal && Array.isArray(c.externalSplit.brokerPayouts) && c.externalSplit.brokerPayouts.length > 0 && <div style={{ marginTop:10, padding:"10px 12px", background:"#FEF3C7", border:"1px solid #FDE68A", borderRadius:8, fontSize:11, color:"#92400E", lineHeight:1.6 }}>
      ⚠️ <b>Historical broker payouts preserved.</b> This commission carries {c.externalSplit.brokerPayouts.length} broker payout entr{c.externalSplit.brokerPayouts.length === 1 ? "y" : "ies"} from a previous external lifecycle. They are kept for audit. Contact admin to reconcile.
    </div>}

    <div style={dividerStyle}/>

    {/* SECTION 4 — Incentive */}
    <div style={Object.assign({}, sectionHeaderStyle, { display:"flex", justifyContent:"space-between", alignItems:"center" })}>
      <span>Incentive (Developer-Direct) — {incentiveRows.length}</span>
      <button onClick={addIncRow} disabled={saving}
        style={{ padding:"3px 10px", borderRadius:6, border:"1px solid "+C.accent, background:C.accent+"12", color:C.accent, fontSize:11, fontWeight:600, cursor: saving ? "wait" : "pointer", textTransform:"none", letterSpacing:0 }}>+ Add Incentive Recipient</button>
    </div>
    <div style={{ fontSize:10, color:C.textLight, marginBottom:6, lineHeight:1.5 }}>
      Incentives are paid directly by the developer to the recipient. This page only tracks who is owed what.
    </div>
    {incentiveRows.length === 0 && <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", padding:"6px 10px" }}>No incentive recipients recorded.</div>}
    {incentiveRows.map(renderIncentiveRow)}

    {/* FOOTER */}
    <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:16, paddingTop:14, borderTop:"1px solid "+C.border }}>
      <button onClick={p.onClose} disabled={saving} style={{ padding:"7px 16px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor: saving ? "wait" : "pointer", fontSize:13 }}>Cancel</button>
      <button onClick={saveAll} disabled={saving}
        style={{ padding:"7px 16px", borderRadius:8, border:"none", background: C.accent, color:"#fff", cursor: saving ? "wait" : "pointer", fontSize:13, fontWeight:600 }}>
        {saving ? "Saving…" : "Save All Changes"}
      </button>
    </div>
  </Modal>;
};

// Cycle stage advance — server enforces the state machine, this modal just
// gathers date + notes (+ amount when advancing to received, + claim values
// when advancing to claim_submitted in Phase R-5).
var CommissionCycleStageModal = function(p) {
  var c = p.target && p.target.commission;
  var cyc = p.target && p.target.cycle;
  // mode: "advance" (default) advances to nextStage; "edit" updates an already-
  // completed stageName without changing cycle.state.
  var mode = (p.target && p.target.mode) || "advance";
  var nextStage = p.target && p.target.nextStage;
  var stageKey = mode === "edit" ? (p.target && p.target.stageName) : nextStage;
  var initial  = (p.target && p.target.initialValues) || {};
  var [date, setDate]     = useState(initial.date || new Date().toISOString().slice(0,10));
  var [notes, setNotes]   = useState(initial.notes || "");
  // The invoice file (owner decision 2026-11-02): REQUIRED when advancing to
  // invoice_submitted, optional (a replacement) when editing that stage. Read as
  // a data URL and sent as fileData/fileName — the same contract as a record
  // document; the server validates type and size (PDF/JPEG/PNG/WEBP, 6MB).
  var [invoiceFile, setInvoiceFile] = useState(null);
  var existingInvoiceDoc = (mode === "edit" && stageKey === "invoice_submitted" && cyc && cyc.invoice_submitted && cyc.invoice_submitted.document) || null;
  var pickInvoiceFile = function(e){
    var f = e.target.files && e.target.files[0];
    if (!f) { setInvoiceFile(null); return; }
    if (f.size > 6*1024*1024) { alert("The invoice file must be under 6MB"); e.target.value = ""; setInvoiceFile(null); return; }
    var r = new FileReader();
    r.onload = function(ev){ setInvoiceFile({ fileData: ev.target.result, fileName: f.name }); };
    r.onerror = function(){ alert("Could not read " + f.name); setInvoiceFile(null); };
    r.readAsDataURL(f);
  };
  // Phase R-5: received-stage advance pre-fills with claimAmount × 0.95 (net
  // after 5% withholding) when claim data exists. Admin can override before save.
  var [amount, setAmount] = useState(function(){
    if (initial.amount != null && initial.amount !== "" && Number(initial.amount) > 0) {
      return Number(initial.amount).toLocaleString();
    }
    if (mode === "advance" && stageKey === "received" && cyc && Number(cyc.claimAmount || 0) > 0) {
      var ca = Number(cyc.claimAmount);
      // Phase R-14 — ambassador deals pay NO VAT and NO withholding. The only
      // deduction is the optional developer tax (a % of gross commission). So
      // the received-stage pre-fill is gross − developer tax (or gross when the
      // developer tax is disabled), NOT the 5%-withholding net used for
      // internal/external deals.
      var ambS = c && c.ambassadorSplit;
      if (ambS && ambS.isAmbassador) {
        var ambNr = (ambS.developerTaxEnabled && Number(ambS.developerTaxRate || 0) > 0)
          ? ca - (ca * Number(ambS.developerTaxRate) / 100)
          : ca;
        return round2(ambNr).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      if (c && c.snapshot && c.snapshot.saleType === "Resale") {
        // Resale — no VAT / withholding: net received == gross claim.
        return round2(ca).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      // Phase R-5 — exact formula: netReceived = grossClaim − (grossClaim / 1.14) × 0.05.
      // Pre-fill at piaster precision; MoneyInput preserves the decimal until the
      // admin edits the field (its onChange strip is integer-only — pre-R-5 behavior).
      // Withholding-exempt cycle: no 5% withheld, so the net received == full claim.
      var nr = cyc.withholdingExempt ? ca : ca - (ca / 1.14) * 0.05;
      return round2(nr).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return "";
  });
  // Phase R-5: claim_submitted stage collects claimUnitValue (EGP) + commissionRate
  // (admin types percentage like "3", stored backend as 0.03). Edit mode pre-fills
  // from the cycle's existing values; advance mode starts empty.
  var [claimUnitValue, setClaimUnitValue] = useState(function(){
    if (mode === "edit" && cyc && Number(cyc.claimUnitValue || 0) > 0) {
      return Number(cyc.claimUnitValue).toLocaleString();
    }
    return "";
  });
  var [commissionRatePct, setCommissionRatePct] = useState(function(){
    if (mode === "edit" && cyc && Number(cyc.commissionRate || 0) > 0) {
      return String(Number((Number(cyc.commissionRate) * 100).toFixed(4)));
    }
    return "";
  });

  var snap = (c && c.snapshot) || {};
  var STAGE_LABELS_LOCAL = {
    claim_submitted: "Submit Claim",
    invoice_submitted: "Submit Invoice",
    received: "Mark Received",
    paid_to_team: "Mark Paid to Team"
  };
  var STAGE_LABELS_EDIT = {
    claim_submitted: "Edit Claim",
    invoice_submitted: "Edit Invoice",
    received: "Edit Received",
    paid_to_team: "Edit Paid to Team"
  };
  var titleLabel = mode === "edit" ? STAGE_LABELS_EDIT[stageKey] : STAGE_LABELS_LOCAL[stageKey];

  // Live derived breakdown for the claim_submitted modal (Phase R-5).
  // Compute with full float precision; round only at display via fmtMoney2.
  var cuvNum   = parseMoney(claimUnitValue) || 0;
  var rateNum  = parseFloat(commissionRatePct);
  var rateDec  = isFinite(rateNum) ? (rateNum / 100) : 0;
  var gross    = cuvNum > 0 && rateDec > 0 ? cuvNum * rateDec : 0;
  var netOfVat = gross > 0 ? gross / 1.14 : 0;
  var vat      = gross - netOfVat;
  var with5    = (cyc && cyc.withholdingExempt) ? 0 : netOfVat * 0.05;
  var netRcv   = gross > 0 ? gross - with5 : 0;
  // Phase R-14 — ambassador deals pay NO VAT and NO withholding; the only
  // deduction is the optional developer tax. The claim preview must reflect
  // that (claim total → developer tax → ARO net), not the VAT/withholding chain.
  var stgIsAmb     = !!(c && c.ambassadorSplit && c.ambassadorSplit.isAmbassador);
  var stgAmbTaxOn  = stgIsAmb && !!c.ambassadorSplit.developerTaxEnabled && Number(c.ambassadorSplit.developerTaxRate || 0) > 0;
  var stgAmbTaxPct = stgAmbTaxOn ? Number(c.ambassadorSplit.developerTaxRate) : 0;
  var stgAmbTaxAmt = stgAmbTaxOn ? gross * stgAmbTaxPct / 100 : 0;
  var stgAmbNet    = gross - stgAmbTaxAmt;
  var stgIsResale  = !!(c && c.snapshot && c.snapshot.saleType === "Resale");

  // Phase R-1: per-recipient payouts moved off the cycle. The received-stage
  // modal now collects amount only (cash flow from the developer); recipient
  // payouts are recorded separately via the commission-level Payouts UI.
  var save = async function(){
    var body = { date: date, notes: notes };
    if (mode === "advance") body.stage = nextStage;
    if (stageKey === "received") {
      var amt = parseMoney(amount);
      if (!isFinite(amt) || amt <= 0) { alert("Amount is required (>0) for the received stage"); return; }
      body.amount = amt;
    }
    if (stageKey === "claim_submitted") {
      var cuv = parseMoney(claimUnitValue);
      var rp  = parseFloat(commissionRatePct);
      if (!isFinite(cuv) || cuv <= 0) { alert("Unit Value is required (> 0)"); return; }
      if (!isFinite(rp)  || rp  <= 0) { alert("Commission Rate is required (> 0)"); return; }
      body.claimUnitValue = cuv;
      body.commissionRate = rp / 100;
    }
    if (stageKey === "invoice_submitted") {
      if (mode === "advance" && !invoiceFile) { alert("The invoice file (PDF/JPEG/PNG/WEBP) is required to submit the invoice"); return; }
      if (invoiceFile) { body.fileData = invoiceFile.fileData; body.fileName = invoiceFile.fileName; }
    }
    var path = mode === "edit"
      ? "/api/commissions/" + c._id + "/cycles/" + cyc._id + "/stage/" + stageKey
      : "/api/commissions/" + c._id + "/cycles/" + cyc._id + "/stage";
    var msg = mode === "edit" ? "Stage updated" : "Stage advanced";
    try {
      await p.writeCommission("PATCH", path, body, msg);
      p.onClose();
    } catch(e){}
  };

  return <Modal show={true} onClose={p.onClose} title={"Cycle " + (cyc && cyc.cycleNumber) + (mode === "edit" ? " — " : " → ") + (titleLabel || stageKey)} w={520}>
    <FormRow label="Date"><input type="date" value={date} onChange={function(e){ setDate(e.target.value); }} style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13 }}/></FormRow>
    <FormRow label="Notes (optional)"><textarea rows={2} value={notes} onChange={function(e){ setNotes(e.target.value); }} style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13, resize:"vertical", fontFamily:"inherit" }}/></FormRow>
    {stageKey === "claim_submitted" && <>
      <FormRow label="Unit Value (EGP)">
        <MoneyInput value={claimUnitValue} onChange={setClaimUnitValue} autoFocus={mode !== "edit"}/>
      </FormRow>
      <FormRow label="Commission Rate (%)">
        <input type="number" step="0.01" min="0" value={commissionRatePct}
          onChange={function(e){ setCommissionRatePct(e.target.value); }}
          placeholder="e.g. 3"
          style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13 }}/>
      </FormRow>
      {gross > 0 && stgIsAmb && <div style={{ background:"#FFFBEB", border:"1px dashed #FDE68A", borderRadius:8, padding:"10px 12px", marginBottom:10, fontSize:12, color:"#92400E" }}>
        {/* Phase R-14 — ambassador claim preview: no VAT, no withholding. */}
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Claim Total</span><b>{fmtMoney2(gross)}</b></div>
        {stgAmbTaxOn && <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Developer Tax ({stgAmbTaxPct}%)</span><span>−{fmtMoney2(stgAmbTaxAmt)}</span></div>}
        <div style={{ display:"flex", justifyContent:"space-between", paddingTop:6, marginTop:4, borderTop:"1px solid #FDE68A", color:"#B45309", fontWeight:700 }}><span>ARO Net</span><span>{fmtMoney2(stgAmbNet)}</span></div>
      </div>}
      {gross > 0 && stgIsResale && <div style={{ background:"#ECFDF5", border:"1px dashed #A7F3D0", borderRadius:8, padding:"10px 12px", marginBottom:10, fontSize:12, color:"#065F46" }}>
        {/* Resale claim preview — no VAT, no withholding: net == gross claim. */}
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Claim Total (Resale — no VAT)</span><b>{fmtMoney2(gross)}</b></div>
        <div style={{ display:"flex", justifyContent:"space-between", paddingTop:6, marginTop:4, borderTop:"1px solid #A7F3D0", fontWeight:700 }}><span>Expected Net Received</span><span>{fmtMoney2(gross)}</span></div>
      </div>}
      {gross > 0 && !stgIsAmb && !stgIsResale && <div style={{ background:"#F8FAFC", border:"1px dashed #CBD5E1", borderRadius:8, padding:"10px 12px", marginBottom:10, fontSize:12, color:C.text }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Claim Total (incl. VAT)</span><b>{fmtMoney2(gross)}</b></div>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Net of VAT</span><span>{fmtMoney2(netOfVat)}</span></div>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>VAT 14%</span><span>{fmtMoney2(vat)}</span></div>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Withholding 5%</span><span>{fmtMoney2(with5)}</span></div>
        <div style={{ display:"flex", justifyContent:"space-between", paddingTop:6, marginTop:4, borderTop:"1px solid #CBD5E1", color:C.success, fontWeight:700 }}><span>Expected Net Received</span><span>{fmtMoney2(netRcv)}</span></div>
      </div>}
    </>}
    {stageKey === "received" && <FormRow label="Amount Received (EGP)">
      <MoneyInput value={amount} onChange={setAmount} autoFocus={mode !== "edit"}/>
    </FormRow>}
    {stageKey === "invoice_submitted" && <FormRow label={mode === "edit" ? "Invoice file (replace, optional)" : "Invoice file (required)"}>
      {existingInvoiceDoc && <div style={{ fontSize:11, color:C.textLight, marginBottom:4 }}>📄 {existingInvoiceDoc.name || "invoice"} — uploaded {auditCaption(existingInvoiceDoc.uploadedByName, existingInvoiceDoc.uploadedAt)}</div>}
      <input type="file" data-invoice-file="1" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={pickInvoiceFile}
        style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid "+(mode !== "edit" && !invoiceFile ? "#F59E0B" : "#E2E8F0"), fontSize:12 }}/>
      <div style={{ fontSize:10, color:C.textLight, marginTop:3 }}>PDF, JPEG, PNG or WEBP, up to 6MB.{invoiceFile ? " Selected: " + invoiceFile.fileName : ""}</div>
    </FormRow>}
    <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
      <button onClick={p.onClose} disabled={p.savingFlag} style={{ padding:"7px 16px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor: p.savingFlag ? "wait" : "pointer", fontSize:13 }}>Cancel</button>
      <button onClick={save} disabled={p.savingFlag} style={{ padding:"7px 16px", borderRadius:8, border:"none", background: C.accent, color:"#fff", cursor: p.savingFlag ? "wait" : "pointer", fontSize:13, fontWeight:600 }}>{p.savingFlag ? "Saving…" : (mode === "edit" ? "Save" : "Advance")}</button>
    </div>
  </Modal>;
};

// Phase R-6 polish — CommissionClaimBackfillModal. Focused modal for filling
// in claimUnitValue + commissionRate on cycles that passed claim_submitted
// before R-5 shipped (so claimAmount was never captured). Distinct from the
// stage-advance/edit modal: doesn't touch the stage subdoc (date/notes stay
// as recorded historically). Reuses the live tax breakdown layout from the
// R-5 modal for visual consistency. Idempotent on the backend — same endpoint
// handles both first-time backfill and overwrite of existing values.
var CommissionClaimBackfillModal = function(p) {
  var c   = p.commission;
  var cyc = p.cycle;
  var [unit, setUnit] = useState(function(){
    if (cyc && Number(cyc.claimUnitValue || 0) > 0) {
      return round2(cyc.claimUnitValue).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return "";
  });
  var [ratePct, setRatePct] = useState(function(){
    if (cyc && Number(cyc.commissionRate || 0) > 0) {
      return String(Number((Number(cyc.commissionRate) * 100).toFixed(4)));
    }
    return "";
  });
  var [saving, setSaving] = useState(false);

  var unitNum  = parseMoney(unit) || 0;
  var rateNum  = parseFloat(ratePct);
  var rateDec  = isFinite(rateNum) ? rateNum / 100 : 0;
  var gross    = unitNum > 0 && rateDec > 0 ? unitNum * rateDec : 0;
  var netOfVat = gross > 0 ? gross / 1.14 : 0;
  var vat      = gross - netOfVat;
  var with5    = (cyc && cyc.withholdingExempt) ? 0 : netOfVat * 0.05;
  var netRcv   = gross > 0 ? gross - with5 : 0;
  // Phase R-14 — ambassador deals: no VAT / no withholding; only the optional
  // developer tax. Mirror the stage-modal claim preview branch.
  var bfIsAmb     = !!(c && c.ambassadorSplit && c.ambassadorSplit.isAmbassador);
  var bfAmbTaxOn  = bfIsAmb && !!c.ambassadorSplit.developerTaxEnabled && Number(c.ambassadorSplit.developerTaxRate || 0) > 0;
  var bfAmbTaxPct = bfAmbTaxOn ? Number(c.ambassadorSplit.developerTaxRate) : 0;
  var bfAmbTaxAmt = bfAmbTaxOn ? gross * bfAmbTaxPct / 100 : 0;
  var bfAmbNet    = gross - bfAmbTaxAmt;
  var bfIsResale  = !!(c && c.snapshot && c.snapshot.saleType === "Resale");

  var claimDateStr = "—";
  try {
    if (cyc && cyc.claim_submitted && cyc.claim_submitted.date) {
      claimDateStr = new Date(cyc.claim_submitted.date).toLocaleDateString("en-GB");
    }
  } catch(_){}

  var save = async function(){
    var u = parseMoney(unit);
    var r = parseFloat(ratePct);
    if (!isFinite(u) || u <= 0) { alert("Unit Value is required (> 0)"); return; }
    if (!isFinite(r) || r <= 0) { alert("Commission Rate is required (> 0)"); return; }
    setSaving(true);
    try {
      // Reuse writeCommission so the parent's rows array gets the updated
      // commission spliced in place (no full refetch flicker).
      await p.writeCommission("PATCH",
        "/api/commissions/" + c._id + "/cycles/" + cyc._id + "/claim-data",
        { claimUnitValue: u, commissionRate: r / 100 },
        "Claim data saved"
      );
      setSaving(false);
      p.onClose();
    } catch(_e) {
      setSaving(false);
    }
  };

  return <Modal show={true} onClose={p.onClose} title={"Add Claim Data — Cycle #" + (cyc && cyc.cycleNumber)} w={520}>
    <div style={{ fontSize:11, color:C.textLight, marginBottom:10, padding:"6px 10px", background:"#F8FAFC", borderRadius:6 }}>
      Claim date <b style={{ color:C.text }}>{claimDateStr}</b> (preserved from the original claim_submitted stage; not editable here).
    </div>
    <FormRow label="Unit Value (EGP)">
      <MoneyInput value={unit} onChange={setUnit} autoFocus={true}/>
    </FormRow>
    <FormRow label="Commission Rate (%)">
      <input type="number" step="0.01" min="0" value={ratePct} placeholder="e.g. 3"
        onChange={function(e){ setRatePct(e.target.value); }}
        style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13 }}/>
    </FormRow>
    {gross > 0 && bfIsAmb && <div style={{ background:"#FFFBEB", border:"1px dashed #FDE68A", borderRadius:8, padding:"10px 12px", marginBottom:10, fontSize:12, color:"#92400E" }}>
      {/* Phase R-14 — ambassador claim preview: no VAT, no withholding. */}
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Claim Total</span><b>{fmtMoney2(gross)}</b></div>
      {bfAmbTaxOn && <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Developer Tax ({bfAmbTaxPct}%)</span><span>−{fmtMoney2(bfAmbTaxAmt)}</span></div>}
      <div style={{ display:"flex", justifyContent:"space-between", paddingTop:6, marginTop:4, borderTop:"1px solid #FDE68A", color:"#B45309", fontWeight:700 }}><span>ARO Net</span><span>{fmtMoney2(bfAmbNet)}</span></div>
    </div>}
    {gross > 0 && bfIsResale && <div style={{ background:"#ECFDF5", border:"1px dashed #A7F3D0", borderRadius:8, padding:"10px 12px", marginBottom:10, fontSize:12, color:"#065F46" }}>
      {/* Resale claim preview — no VAT, no withholding: net == gross claim. */}
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Claim Total (Resale — no VAT)</span><b>{fmtMoney2(gross)}</b></div>
      <div style={{ display:"flex", justifyContent:"space-between", paddingTop:6, marginTop:4, borderTop:"1px solid #A7F3D0", fontWeight:700 }}><span>Expected Net Received</span><span>{fmtMoney2(gross)}</span></div>
    </div>}
    {gross > 0 && !bfIsAmb && !bfIsResale && <div style={{ background:"#F8FAFC", border:"1px dashed #CBD5E1", borderRadius:8, padding:"10px 12px", marginBottom:10, fontSize:12, color:C.text }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Claim Total (incl. VAT)</span><b>{fmtMoney2(gross)}</b></div>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Net of VAT</span><span>{fmtMoney2(netOfVat)}</span></div>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>VAT 14%</span><span>{fmtMoney2(vat)}</span></div>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}><span>Withholding 5%</span><span>{fmtMoney2(with5)}</span></div>
      <div style={{ display:"flex", justifyContent:"space-between", paddingTop:6, marginTop:4, borderTop:"1px solid #CBD5E1", color:C.success, fontWeight:700 }}><span>Expected Net Received</span><span>{fmtMoney2(netRcv)}</span></div>
    </div>}
    <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
      <button onClick={p.onClose} disabled={saving} style={{ padding:"7px 16px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor: saving ? "wait" : "pointer", fontSize:13 }}>Cancel</button>
      <button onClick={save} disabled={saving} style={{ padding:"7px 16px", borderRadius:8, border:"none", background: C.accent, color:"#fff", cursor: saving ? "wait" : "pointer", fontSize:13, fontWeight:600 }}>{saving ? "Saving…" : "Save"}</button>
    </div>
  </Modal>;
};

// Phase R-6 — AddExpenseModal. Dual-purpose: create when target is falsy,
// edit when target carries an existing expense row. Passes the result back
// via onSaved (caller refetches the P&L). MoneyInput is decimal-aware
// (commit e9fbe23) so piaster precision survives admin overrides.
//
// Phase R-7 polish (2026-05-14) — add mode is an inline horizontal grid
// (date / category / amount / description / remove). Edit mode keeps the
// vertical single-row form. Empty rows are silently skipped on save;
// "Save N expenses" reflects the count of valid rows.
var AddExpenseModal = function(p) {
  var [descFocus, setDescFocus] = useState(null);   // phone: { idx, w } of the focused Description cell
  var editing = p.target || null;
  var isEdit  = !!editing;
  var todayIso = new Date().toISOString().slice(0, 10);
  var initialRow = function(){
    if (editing) {
      var dateStr = todayIso;
      try { if (editing.date) dateStr = new Date(editing.date).toISOString().slice(0, 10); } catch(_){}
      var n = Number(editing.amount || 0);
      var amtStr = (isFinite(n) && n > 0)
        ? round2(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "";
      return {
        date: dateStr,
        categoryId: editing.categoryId ? String(editing.categoryId) : "",
        amount: amtStr,
        description: editing.description || ""
      };
    }
    return { date: todayIso, categoryId: "", amount: "", description: "" };
  };
  var [rows, setRows] = useState([initialRow()]);
  var [saving, setSaving] = useState(false);

  // Active categories only in the dropdown — archived stays selectable when
  // editing an existing expense that points to it (so we don't silently lose
  // the row's tag), but doesn't appear for new entries.
  var activeCats = (p.categories || []).filter(function(c){ return !c.archived; });
  var buildDropdown = function(currentCategoryId) {
    var out = activeCats.slice();
    if (isEdit && currentCategoryId) {
      var alreadyIn = activeCats.some(function(c){ return String(c._id) === String(currentCategoryId); });
      if (!alreadyIn) {
        var arch = (p.categories || []).find(function(c){ return String(c._id) === String(currentCategoryId); });
        if (arch) out.push(arch);
      }
    }
    return out;
  };

  var setRowField = function(idx, field, value) {
    setRows(function(prev){
      var next = prev.slice();
      next[idx] = Object.assign({}, next[idx], (function(){ var o = {}; o[field] = value; return o; })());
      return next;
    });
  };
  var addRow = function(){ setRows(function(prev){ return prev.concat([{ date: todayIso, categoryId: "", amount: "", description: "" }]); }); };
  var removeRow = function(idx){ setRows(function(prev){ return prev.filter(function(_, i){ return i !== idx; }); }); };

  // Row classification for the inline grid:
  //   "empty"    — date defaulted, no category, no amount, no description
  //   "complete" — date + category + amount > 0
  //   "partial"  — has SOME of date/category/amount but not all 3 (or amount <= 0)
  // Empty rows are skipped on save; partial rows block save; complete rows commit.
  function classifyRow(r){
    var hasCat  = !!r.categoryId;
    var amtN    = parseMoney(r.amount);
    var hasAmt  = isFinite(amtN) && amtN > 0;
    var hasDesc = !!(r.description && r.description.trim());
    var hasAnyInput = hasCat || hasAmt || hasDesc;
    if (!hasAnyInput) return "empty";
    if (r.date && hasCat && hasAmt) return "complete";
    return "partial";
  }
  var rowClasses = rows.map(classifyRow);
  var completeCount = rowClasses.filter(function(c){ return c === "complete"; }).length;
  var partialCount  = rowClasses.filter(function(c){ return c === "partial";  }).length;
  var saveBlocked   = saving || (isEdit ? rowClasses[0] !== "complete" : (completeCount === 0 || partialCount > 0));

  var save = async function(){
    if (isEdit) {
      if (rowClasses[0] !== "complete") {
        alert("Date, category and a positive amount are required.");
        return;
      }
    } else {
      if (partialCount > 0) {
        alert("Fix the incomplete row" + (partialCount === 1 ? "" : "s") + " (highlighted in red) — every saved row needs date + category + amount.");
        return;
      }
      if (completeCount === 0) {
        alert("Add at least one expense.");
        return;
      }
    }
    setSaving(true);
    try {
      if (isEdit) {
        var r0 = rows[0];
        var body = {
          date: r0.date, categoryId: r0.categoryId, amount: parseMoney(r0.amount),
          description: r0.description || ""
        };
        await apiFetch("/api/expenses/" + editing._id, "PATCH", body, p.token);
      } else {
        // Only "complete" rows ship; "empty" rows are silently dropped.
        var payload = {
          rows: rows.filter(function(r, i){ return rowClasses[i] === "complete"; }).map(function(r){
            return {
              date: r.date, categoryId: r.categoryId, amount: parseMoney(r.amount),
              description: r.description || ""
            };
          })
        };
        await apiFetch("/api/expenses/bulk", "POST", payload, p.token);
      }
      setSaving(false);
      if (p.onSaved) p.onSaved();
      p.onClose();
    } catch(e) {
      setSaving(false);
      alert((e && e.message) || "Save failed");
    }
  };

  // ---------- Edit mode: classic vertical single-row form ----------
  if (isEdit) {
    var r0 = rows[0];
    return <Modal show={true} onClose={p.onClose} title="Edit Expense" w={460}>
      <FormRow label="Date">
        <input type="date" value={r0.date} onChange={function(e){ setRowField(0, "date", e.target.value); }}
          style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13 }}/>
      </FormRow>
      <FormRow label="Category">
        <Select value={r0.categoryId} onChange={function(e){ setRowField(0, "categoryId", e.target.value); }} searchable fontSize={13}
          options={[{ value:"", label:"— select —" }].concat(buildDropdown(r0.categoryId).map(function(c){
            return { value:c._id, label:c.name + (c.archived ? " (archived)" : "") };
          }))}/>
      </FormRow>
      <FormRow label="Amount (EGP)">
        <MoneyInput value={r0.amount} onChange={function(v){ setRowField(0, "amount", v); }}/>
      </FormRow>
      <FormRow label="Description (optional)">
        <textarea rows={2} value={r0.description} onChange={function(e){ setRowField(0, "description", e.target.value); }}
          style={{ width:"100%", padding:"7px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13, resize:"vertical", fontFamily:"inherit" }}/>
      </FormRow>
      <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
        <button onClick={p.onClose} disabled={saving} style={{ padding:"7px 16px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor: saving ? "wait" : "pointer", fontSize:13 }}>Cancel</button>
        <button onClick={save} disabled={saveBlocked} style={{
          padding:"7px 16px", borderRadius:8, border:"none",
          background: saveBlocked ? "#E2E8F0" : C.accent,
          color: saveBlocked ? C.textLight : "#fff",
          cursor: saveBlocked ? "not-allowed" : "pointer", fontSize:13, fontWeight:600
        }}>{saving ? "Saving…" : "Save"}</button>
      </div>
    </Modal>;
  }

  // ---------- Add mode: inline horizontal grid ----------
  // Column widths kept fixed (date/category/amount/remove) with description
  // flex-growing into the remaining space.
  var colDateW   = 130;
  var colCatW    = 150;
  var colAmtW    = 130;
  var colRemoveW = 36;
  var rowGap     = 8;
  var cellPad    = "6px 8px";
  var fieldBase  = { padding:"6px 8px", borderRadius:6, fontSize:12, border:"1px solid "+C.border, background:"#fff", boxSizing:"border-box", width:"100%" };
  var errBorder  = "1px solid "+C.danger;
  // Phone: the row keeps its original widths, so Description stays compact.
  // While it is focused the INPUT is laid full width on its own line under the
  // row (absolute, inside its own cell — same element, so focus and caret
  // survive; the cell is pinned to its compact width so no other field moves) and the row
  // makes room below; blur puts it back. Desktop never takes this path.
  var mob        = !!p.isMobile;
  var descLineH  = 40;
  return <Modal show={true} onClose={p.onClose} title={"Add Expense" + (completeCount > 0 ? " (" + completeCount + " ready)" : "")} w={720}>
    <div style={{ marginBottom:10 }}>
      {/* Header row — column labels */}
      <div style={{ display:"flex", gap:rowGap, alignItems:"center", paddingBottom:6, borderBottom:"1px solid "+C.border, marginBottom:6, fontSize:10, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3 }}>
        <div style={{ width:colDateW,   padding:cellPad }}>Date</div>
        <div style={{ width:colCatW,    padding:cellPad }}>Category</div>
        <div style={{ width:colAmtW,    padding:cellPad }}>Amount (EGP)</div>
        <div style={{ flex:1,           padding:cellPad }}>Description</div>
        <div style={{ width:colRemoveW, padding:cellPad }}/>
      </div>
      {/* Data rows */}
      {rows.map(function(r, idx){
        var cls = rowClasses[idx];
        var isPartial = cls === "partial";
        // Highlight specific missing fields when the row is partial.
        var dateBad = isPartial && !r.date;
        var catBad  = isPartial && !r.categoryId;
        var amtN    = parseMoney(r.amount);
        var amtBad  = isPartial && !(isFinite(amtN) && amtN > 0);
        var descOpen = mob && !!descFocus && descFocus.idx === idx;
        return <div key={"erow-" + idx} style={descOpen
          ? { display:"flex", gap:rowGap, alignItems:"center", marginBottom:rowGap + descLineH, position:"relative" }
          : { display:"flex", gap:rowGap, alignItems:"center", marginBottom:rowGap }}>
          <div style={{ width:colDateW }}>
            <input type="date" value={r.date}
              onChange={function(e){ setRowField(idx, "date", e.target.value); }}
              style={Object.assign({}, fieldBase, dateBad ? { border:errBorder } : {})}/>
          </div>
          {/* The category control carried an inline red border when the row was
              partial. SearchableSelect owns its own trigger styling, so the
              error ring moves to this wrapper — same signal, same condition. */}
          <div style={Object.assign({ width:colCatW }, catBad ? { border:errBorder, borderRadius:9 } : {})}>
            <Select value={r.categoryId}
              onChange={function(e){ setRowField(idx, "categoryId", e.target.value); }}
              searchable fontSize={12}
              options={[{ value:"", label:"— select —" }].concat(buildDropdown(r.categoryId).map(function(c){
                return { value:c._id, label:c.name + (c.archived ? " (archived)" : "") };
              }))}/>
          </div>
          <div style={{ width:colAmtW }}>
            <MoneyInput value={r.amount} onChange={function(v){ setRowField(idx, "amount", v); }}
              autoFocus={idx === 0}
              style={Object.assign({}, fieldBase, amtBad ? { border:errBorder } : {})}/>
          </div>
          <div data-expense-desc={mob ? idx : undefined} style={descOpen ? { flex:"0 0 " + descFocus.w + "px" } : { flex:1 }}>
            <input type="text" value={r.description}
              placeholder="optional"
              onFocus={mob ? function(e){ var w = e.currentTarget.parentElement.getBoundingClientRect().width; setDescFocus({ idx: idx, w: w }); } : undefined}
              onBlur={mob ? function(){ setDescFocus(function(cur){ return cur && cur.idx === idx ? null : cur; }); } : undefined}
              onChange={function(e){ setRowField(idx, "description", e.target.value); }}
              style={descOpen ? Object.assign({}, fieldBase, { position:"absolute", left:0, right:0, top:"calc(100% + " + rowGap + "px)", width:"auto" }) : fieldBase}/>
          </div>
          <div style={{ width:colRemoveW, textAlign:"center" }}>
            <button onClick={function(){ removeRow(idx); }}
              disabled={saving || rows.length === 1}
              title={rows.length === 1 ? "At least one row is required" : "Remove this row"}
              style={{
                width:28, height:28, borderRadius:6, fontSize:13, fontWeight:700,
                border:"1px solid "+C.border, background:"#fff",
                color: rows.length === 1 ? C.textLight : C.danger,
                cursor: (saving || rows.length === 1) ? "not-allowed" : "pointer",
                opacity: rows.length === 1 ? 0.4 : 1
              }}>✕</button>
          </div>
        </div>;
      })}
      {/* Add-row button — sits below the rows, left-aligned. */}
      <div style={{ marginTop:4 }}>
        <button onClick={addRow} disabled={saving} style={{
          padding:"5px 12px", borderRadius:6, fontSize:11, fontWeight:600,
          border:"1px dashed "+C.accent, background:"#fff", color:C.accent,
          cursor: saving ? "wait" : "pointer"
        }}>+ Add row</button>
        {partialCount > 0 && <span style={{ marginInlineStart:10, fontSize:11, color:C.danger }}>
          {partialCount} row{partialCount === 1 ? "" : "s"} incomplete — date, category, and amount are required.
        </span>}
      </div>
    </div>
    <div style={{ display:"flex", justifyContent:"flex-end", gap:8, paddingTop:10, borderTop:"1px solid "+C.border }}>
      <button onClick={p.onClose} disabled={saving} style={{ padding:"7px 16px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor: saving ? "wait" : "pointer", fontSize:13 }}>Cancel</button>
      <button onClick={save} disabled={saveBlocked} style={{
        padding:"7px 16px", borderRadius:8, border:"none",
        background: saveBlocked ? "#E2E8F0" : C.accent,
        color: saveBlocked ? C.textLight : "#fff",
        cursor: saveBlocked ? "not-allowed" : "pointer",
        fontSize:13, fontWeight:600
      }}>{saving ? "Saving…" : ("Save " + completeCount + " expense" + (completeCount === 1 ? "" : "s"))}</button>
    </div>
  </Modal>;
};

// Phase R-6 — ManageCategoriesModal. List with inline rename, archive/restore,
// and delete (server soft-deletes when referenced, hard-deletes otherwise).
// New-category row at the bottom. Caller refetches categories + P&L on close.
var ManageCategoriesModal = function(p) {
  var [localCats, setLocalCats] = useState((p.categories || []).slice());
  var [newName, setNewName] = useState("");
  var [saving, setSaving] = useState(false);

  // Pull fresh categories on mount so the modal isn't stale if the user
  // opened it after some other admin made changes.
  var liveCatRev = useLiveRev("expenseCategory");
  useEffect(function(){
    var cancelled = false;
    apiFetch("/api/expense-categories", "GET", null, p.token)
      .then(function(d){ if (!cancelled) setLocalCats((d && d.data) || []); })
      .catch(function(){});
    return function(){ cancelled = true; };
  }, [p.token, liveCatRev]);

  var renameCat = async function(id, name) {
    name = String(name || "").trim();
    if (!name) return;
    setSaving(true);
    try {
      await apiFetch("/api/expense-categories/" + id, "PATCH", { name: name }, p.token);
      setLocalCats(function(prev){ return prev.map(function(c){ return String(c._id) === String(id) ? Object.assign({}, c, { name: name }) : c; }); });
    } catch(e) { alert((e && e.message) || "Rename failed"); }
    setSaving(false);
  };
  var toggleArchive = async function(c) {
    setSaving(true);
    try {
      await apiFetch("/api/expense-categories/" + c._id, "PATCH", { archived: !c.archived }, p.token);
      setLocalCats(function(prev){ return prev.map(function(x){ return String(x._id) === String(c._id) ? Object.assign({}, x, { archived: !c.archived }) : x; }); });
    } catch(e) { alert((e && e.message) || "Archive toggle failed"); }
    setSaving(false);
  };
  var deleteCat = async function(c) {
    if (!window.confirm("Delete category \"" + c.name + "\"? If any expense uses it, it will be archived instead.")) return;
    setSaving(true);
    try {
      var res = await apiFetch("/api/expense-categories/" + c._id, "DELETE", null, p.token);
      if (res && res.action === "archived") {
        setLocalCats(function(prev){ return prev.map(function(x){ return String(x._id) === String(c._id) ? Object.assign({}, x, { archived: true }) : x; }); });
        alert("Category is in use by " + (res.expenseCount || 0) + " expense(s) — archived instead of deleted.");
      } else {
        setLocalCats(function(prev){ return prev.filter(function(x){ return String(x._id) !== String(c._id); }); });
      }
    } catch(e) { alert((e && e.message) || "Delete failed"); }
    setSaving(false);
  };
  var addNew = async function() {
    var nm = newName.trim();
    if (!nm) return;
    setSaving(true);
    try {
      var d = await apiFetch("/api/expense-categories", "POST", { name: nm }, p.token);
      setLocalCats(function(prev){ return prev.concat([d]); });
      setNewName("");
    } catch(e) { alert((e && e.message) || "Create failed"); }
    setSaving(false);
  };

  var sorted = localCats.slice().sort(function(a, b){
    if (a.archived !== b.archived) return a.archived ? 1 : -1;
    return String(a.name).localeCompare(String(b.name));
  });

  return <Modal show={true} onClose={function(){ if (p.onClose) p.onClose(localCats); }} title="Manage Categories" w={520}>
    <div style={{ marginBottom:12 }}>
      {sorted.length === 0 && <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", padding:"8px 0" }}>No categories yet.</div>}
      {sorted.map(function(c){
        return <div key={c._id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:"1px solid #F1F5F9" }}>
          <input type="text" defaultValue={c.name} disabled={saving}
            onBlur={function(e){ if (e.target.value.trim() !== c.name) renameCat(c._id, e.target.value); }}
            style={{ flex:1, padding:"5px 8px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:13, background: c.archived ? "#F8FAFC" : "#fff", color: c.archived ? C.textLight : C.text }}/>
          <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, fontWeight:700,
            background: c.archived ? "#FEF3C7" : "#DCFCE7",
            color: c.archived ? "#B45309" : "#15803D" }}>
            {c.archived ? "Archived" : "Active"}
          </span>
          <button onClick={function(){ toggleArchive(c); }} disabled={saving} style={{ padding:"3px 10px", borderRadius:6, border:"1px solid #E2E8F0", background:"#fff", fontSize:11, cursor: saving ? "wait" : "pointer", color:C.textLight }}>{c.archived ? "Restore" : "Archive"}</button>
          <button onClick={function(){ deleteCat(c); }} disabled={saving} style={{ padding:"3px 10px", borderRadius:6, border:"1px solid #FCA5A5", background:"#fff", fontSize:11, cursor: saving ? "wait" : "pointer", color:"#B91C1C" }}>Delete</button>
        </div>;
      })}
    </div>
    <div style={{ display:"flex", gap:6, borderTop:"1px solid #E2E8F0", paddingTop:10 }}>
      <input type="text" placeholder="Add new category" value={newName} onChange={function(e){ setNewName(e.target.value); }} disabled={saving}
        onKeyDown={function(e){ if (e.key === "Enter") addNew(); }}
        style={{ flex:1, padding:"5px 8px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:13 }}/>
      <button onClick={addNew} disabled={saving || !newName.trim()} style={{ padding:"5px 14px", borderRadius:6, border:"none", background: C.accent, color:"#fff", fontSize:12, fontWeight:600, cursor: (saving || !newName.trim()) ? "not-allowed" : "pointer" }}>Add</button>
    </div>
    <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:12 }}>
      <button onClick={function(){ if (p.onClose) p.onClose(localCats); }} style={{ padding:"7px 16px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", fontSize:13 }}>Done</button>
    </div>
  </Modal>;
};

// Phase R-6 — Lightweight inline SVG bar chart. One bar per month (Jan-Dec).
// Stacked variant: each month is split by category, stacked bottom→top in
// the same fixed color palette so legend → bar segments stay aligned. No
// external chart library — keeps the bundle lean.
var ExpenseBarChart = function(p) {
  var year = p.year || String(new Date().getUTCFullYear());
  var rows = Array.isArray(p.rows) ? p.rows : [];
  var mode = p.mode || "total";

  // Aggregate Cairo-local yyyy-mm bins from the raw expense rows. The backend
  // already returns these in pnl.expenses.byMonth, but recomputing client-side
  // means the chart can switch between Total and Stacked modes without a
  // refetch.
  var monthLabels = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  var totals = monthLabels.map(function(){ return 0; });
  var categoryNames = []; // ordered list, first-seen wins
  var byMonthCat = monthLabels.map(function(){ return {}; });
  rows.forEach(function(r){
    var d = new Date(r.date);
    if (isNaN(d.getTime())) return;
    if (dfCairoYear(d) !== Number(year)) return;
    var m = dfCairoMonthIdx(d);
    var amt = Number(r.amount || 0);
    if (!(amt > 0)) return;
    totals[m] += amt;
    var cat = r.categoryName || "(unknown)";
    if (categoryNames.indexOf(cat) === -1) categoryNames.push(cat);
    byMonthCat[m][cat] = (byMonthCat[m][cat] || 0) + amt;
  });

  var max = totals.reduce(function(s, n){ return n > s ? n : s; }, 0);
  if (max <= 0) {
    return <div style={{ padding:"30px 12px", textAlign:"center", color:C.textLight, fontSize:12 }}>No expenses recorded for {year}.</div>;
  }

  // Fixed color palette — long enough for ~12 categories. Repeats after that.
  var palette = ["#3B82F6","#10B981","#F59E0B","#EF4444","#8B5CF6","#EC4899","#14B8A6","#F97316","#06B6D4","#84CC16","#A855F7","#F43F5E"];
  var colorFor = function(name){
    var idx = categoryNames.indexOf(name);
    if (idx < 0) idx = 0;
    return palette[idx % palette.length];
  };

  var chartW = 720, chartH = 220;
  var padL = 50, padR = 10, padT = 10, padB = 24;
  var plotW = chartW - padL - padR;
  var plotH = chartH - padT - padB;
  var barW = plotW / 12 * 0.65;
  var barGap = plotW / 12;

  // Y-axis ticks: 4 grid lines (0, 25%, 50%, 75%, 100% of max).
  var yTicks = [0, 0.25, 0.5, 0.75, 1].map(function(f){ return Math.round(max * f); });

  return <div>
    <svg viewBox={"0 0 " + chartW + " " + chartH} width="100%" preserveAspectRatio="xMidYMid meet" style={{ display:"block" }}>
      {/* Y grid + labels */}
      {yTicks.map(function(t, i){
        var y = padT + plotH - (t / max) * plotH;
        return <g key={"y" + i}>
          <line x1={padL} y1={y} x2={chartW - padR} y2={y} stroke="#E8ECF1" strokeWidth="1"/>
          <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="9" fill={C.textLight}>{Math.round(t).toLocaleString()}</text>
        </g>;
      })}
      {/* Bars */}
      {monthLabels.map(function(label, mi){
        var cx = padL + barGap * mi + (barGap - barW) / 2;
        var monthTotal = totals[mi];
        if (mode === "byCategory") {
          var stackY = padT + plotH;
          return <g key={"bar" + mi}>
            {categoryNames.map(function(cat, ci){
              var v = byMonthCat[mi][cat] || 0;
              if (v <= 0) return null;
              var h = (v / max) * plotH;
              stackY -= h;
              return <rect key={"seg" + ci} x={cx} y={stackY} width={barW} height={h} fill={colorFor(cat)} stroke="#fff" strokeWidth="0.5">
                <title>{label} · {cat} · {Math.round(v).toLocaleString()} EGP</title>
              </rect>;
            })}
            <text x={cx + barW / 2} y={chartH - padB + 14} textAnchor="middle" fontSize="10" fill={C.textLight}>{label}</text>
          </g>;
        }
        var h = (monthTotal / max) * plotH;
        var y = padT + plotH - h;
        return <g key={"bar" + mi}>
          <rect x={cx} y={y} width={barW} height={h} fill={C.accent} opacity="0.85">
            <title>{label} · {Math.round(monthTotal).toLocaleString()} EGP</title>
          </rect>
          <text x={cx + barW / 2} y={chartH - padB + 14} textAnchor="middle" fontSize="10" fill={C.textLight}>{label}</text>
        </g>;
      })}
    </svg>
    {mode === "byCategory" && categoryNames.length > 0 && <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginTop:8 }}>
      {categoryNames.map(function(cat){
        return <div key={cat} style={{ display:"flex", alignItems:"center", gap:5, fontSize:10, color:C.textLight }}>
          <span style={{ display:"inline-block", width:10, height:10, borderRadius:2, background: colorFor(cat) }}/>
          <span>{cat}</span>
        </div>;
      })}
    </div>}
  </div>;
};

// ===== COMMISSIONS PAGE — Phase C (read-only) + Phase D (edit modals) =====
// admin / operation only. Fetches /api/commissions + /api/commissions/stats.
var CommissionsPage = function(p) {
  var t = p.t;
  // ALL hooks before any role-gate (CRA rules-of-hooks).
  var [statusFilter, setStatusFilter] = useState("all");
  // Phase R-12 Part 4 — All / Internal / External pill filter on Claims tab.
  // Client-side filter against commission.externalSplit.isExternal (added in
  // Part 5). Until Part 5 ships, no commission has externalSplit set so
  // every row reads as Internal — the External bucket counts to 0. Counts
  // are computed against the loaded `rows` (post-server-filters) so picking
  // a different status pill rescopes the dealType counts naturally.
  var [dealTypeFilter, setDealTypeFilter] = useState("all");
  var [search, setSearch] = useState("");
  // MULTI-SELECT (2026-08-06): an ARRAY of user ids; [] = all agents.
  var [agentFilter, setAgentFilter] = useState([]);
  // Phase R-5: each tab owns its own year state so the Annual Summary
  // auto-select doesn't leak into Claims. claimsYearFilter defaults to ""
  // ("All years"); annualYear auto-fills to the most recent available year
  // when the user first lands on the Annual Summary tab.
  var [claimsYearFilter, setClaimsYearFilter] = useState("");
  var [annualYear, setAnnualYear] = useState("");
  // Phase R-5: "deals" (default) shows the existing list/cards UI; "annual"
  // shows the new tax-tracking summary tab.
  var [activeTabState, setActiveTab] = useRememberedTab("commissions", gid(p.cu), COMMISSIONS_TABS, null, "deals"); // remembered per page per user
  // Same derive-don't-correct shape as Settings and Reports. The `role ===
  // "admin"` test that used to sit on the Annual Summary chip is now
  // LEGACY_TAB_RULES["commissions.annual"], resolved server-side.
  // The Calculator tab follows the grant alone since 2026-11-02 (owner ruling):
  // the financeReads role is no longer stripped of it.
  var commissionsStrip = resolveTabStrip(p.access, "commissions", COMMISSIONS_TABS, activeTabState);
  var activeTab = commissionsStrip.active;
  var [annualSummary, setAnnualSummary] = useState(null);
  var [annualLoading, setAnnualLoading] = useState(false);
  // Phase R-13 — per-month expand state for the Monthly VAT table.
  // Keyed by "YYYY-MM"; row clicks toggle.
  var [vatMonthExpanded, setVatMonthExpanded] = useState({});
  // Partial VAT payments (2026-10-25). The year's payment ledger from
  // GET /api/vat-payments (admin, or a financeReads role through the route's
  // matrix branch — owner ruling 2026-11-02; the route refuses everyone else,
  // so the fetch is gated the same way and other roles see an "Unavailable"
  // pill rather than a false Unpaid). Adding AND deleting a payment follow the
  // same pair (the server asks manageFinanceEntries on both writes; the delete
  // joined on the second ruling of 2026-11-02). Status is composed HERE from
  // byMonth.paidTotal against the zone-owned row.vatAmount. The legacy row.paid/row.paidAt that
  // /annual-summary still emits are read ONLY as a fallback for a month with no
  // ledger payments (see legacyPaid); row.overdue is not read. vatReloadTick
  // refetches the ledger alone, never the Claims list (same reason pnlReloadTick exists).
  var vatIsAdmin = !!(p.cu && p.cu.role === "admin");
  var vatCanRead = vatIsAdmin || roleHasFinanceReadsFE(p.cu);   // read the ledger + Add / Delete Payment (server: manageFinanceEntries)
  var [vatPayments, setVatPayments] = useState(null);     // null = loading | { error:true } | { year, payments, byMonth }
  var [vatReloadTick, setVatReloadTick] = useState(0);
  var [vatAddMonth, setVatAddMonth] = useState(null);     // "YYYY-MM" while the Add Payment modal is open
  var [vatAddForm, setVatAddForm] = useState({ date:"", amount:"", note:"" });
  var [vatAddError, setVatAddError] = useState("");
  var [vatAddBusy, setVatAddBusy] = useState(false);
  // Phase R-5 standalone calculator on the Annual Summary tab. State lives
  // here (not inside the IIFE) so it survives tab switches; resets on full
  // page reload. No persistence by design — pure UI scratchpad.
  var [calcUnit, setCalcUnit] = useState("");
  var [calcRate, setCalcRate] = useState("");
  // Withholding exemption for the calculator — the same sense as Claims'
  // per-cycle withholdingExempt ("No 5% W/H"): unchecked (default) = the 5% is
  // applied; checked = exempt, Withholding 0 and Net Due = Gross. Inverted
  // 2026-10-31 from "Withholding 5% (checked = apply)". Calculator-only, no
  // persistence; the default is CALC_EXEMPT_DEFAULT (src/lib/commissionCalculator.js).
  var [calcWithholdingExempt, setCalcWithholdingExempt] = useState(CALC_EXEMPT_DEFAULT);
  // Phase R-6 — Expenses + P&L section on Annual Summary tab (admin only).
  // pnl is the full /api/annual-pnl response (totals + chart bins + folded
  // expense rows). Refetched on year change + after any expense/tax edit.
  var [pnl, setPnl] = useState(null);
  // Phase 4 audit feeds for the Annual tab. The P&L's expense rows come from a
  // frozen projection that carries no actor, so the ledger's own GET (full
  // documents) is read beside it and joined by _id; the profit-tax entry's own
  // GET carries its updater. Both additive reads; nothing else changes.
  var [expenseAudit, setExpenseAudit] = useState({});
  var [profitTaxAudit, setProfitTaxAudit] = useState(null);
  var [pnlLoading, setPnlLoading] = useState(false);
  // Phase R-15 — Net Profit by Deal table (Annual Summary tab, above the P&L
  // statement). Fed by /api/commissions/profit-by-deal; sorted + filtered
  // client-side. Default sort: ARO Net descending.
  var [profitByDeal, setProfitByDeal] = useState(null);
  var [profitByDealLoading, setProfitByDealLoading] = useState(false);
  var [pbdSort, setPbdSort] = useState({ col: "aroNet", dir: "desc" });
  var [pbdFilter, setPbdFilter] = useState("all"); // all | internal | external
  // Feature B — Net Profit by Deal closing-company filter ("" = all) + the
  // company list that feeds its dropdown.
  var [pbdCcFilter, setPbdCcFilter] = useState("");
  var [commClosingCompanies, setCommClosingCompanies] = useState([]);
  // Developer (D5) — claims-list filter ("" = all), Net-Profit-by-Deal filter,
  // and the shared developers list feeding both searchable dropdowns.
  var [developerFilter, setDeveloperFilter] = useState("");
  var [pbdDevFilter, setPbdDevFilter] = useState("");
  var [commDevelopers, setCommDevelopers] = useState([]);
  var [expenseCategories, setExpenseCategories] = useState([]);
  var [addingExpense, setAddingExpense] = useState(false);
  var [editingExpense, setEditingExpense] = useState(null); // expense row or null
  var [managingCategories, setManagingCategories] = useState(false);
  var [expCatFilter, setExpCatFilter] = useState(""); // "" = all
  // Phase R-16 — per-month detail modal for the Monthly Expenses grid.
  // Holds the clicked month index (0-11), or null when closed.
  var [expenseMonthModal, setExpenseMonthModal] = useState(null);
  var [chartMode, setChartMode] = useState("total"); // "total" | "byCategory"
  var [pnlReloadTick, setPnlReloadTick] = useState(0);
  // Silent-reload ref: when an in-tab mutation refetches the P&L, this flag
  // suppresses the "Loading P&L…" spinner so numbers update in place without
  // a visible flicker. Ref (not state) — setting it does NOT trigger another
  // effect run. Cleared by the fetch effect on completion.
  var pnlSilentReloadRef = useRef(false);
  var triggerPnlReload = function(){
    pnlSilentReloadRef.current = true;
    setPnlReloadTick(function(x){ return x + 1; });
  };
  // Inline profit-tax editor (always visible per judgment #6). Drafts sync
  // from pnl.profitTax when the response loads/changes; admin edits + Saves.
  var [taxModeDraft, setTaxModeDraft] = useState("percent");
  var [taxValueDraft, setTaxValueDraft] = useState("");
  // Phase R-6 polish — claim-data backfill modal target. Holds the cycle +
  // commission pair when admin clicks "Add claim data" on a pre-R-5 cycle.
  var [backfillTarget, setBackfillTarget] = useState(null); // { commission, cycle }
  // Phase R-7 — manual revert modal target. Holds the commission to be
  // reverted from fully_paid back to active. Two-step CONFIRM gate.
  var [revertTarget, setRevertTarget] = useState(null);   // commission obj
  var [revertConfirm, setRevertConfirm] = useState("");
  var [revertBusy, setRevertBusy] = useState(false);
  // Phase R-7 — Payout Report tab state. monthKey "YYYY-MM" defaults to
  // current Cairo month; expandedAgents is a Set-as-object for per-agent
  // drill-down.
  var defaultMonthKeyR7 = dfCairoMonthKey();
  var [payoutMonth, setPayoutMonth] = useState(defaultMonthKeyR7);
  var [payoutReport, setPayoutReport] = useState(null);
  var [payoutLoading, setPayoutLoading] = useState(false);
  var [expandedPayoutAgents, setExpandedPayoutAgents] = useState({});
  // Payout-report PDF state for the app path. The failure this replaces was a
  // button that did nothing, so the busy and error states are not polish —
  // rasterising a month of payouts and writing a file takes real time, and
  // silence is the one outcome that must not return.
  var [payoutPdfBusy, setPayoutPdfBusy] = useState(false);
  var [payoutPdfErr, setPayoutPdfErr] = useState("");
  var [rows, setRows] = useState(null);
  // Server-side paging (2026-08-21). `rows` is now a PAGE, not the result set —
  // so nothing may derive a count or a filter from it any more. commMeta carries
  // what used to be computed by scanning the array: the pill counts, the imminent
  // tally, and how many pages are left.
  var [commMeta, setCommMeta] = useState(null);   // {total,page,totalPages,limit,counts}
  var [commLoadingMore, setCommLoadingMore] = useState(false);
  var [stats, setStats] = useState(null);
  var [loadErr, setLoadErr] = useState("");
  // The 💰 deep-link's target, from the Deals page. Was `expandedId`, which was
  // WRITTEN AND NEVER READ — a leftover from when these cards collapsed. They
  // render fully expanded now, so there is nothing to expand; what the click has
  // to do is make the row REACHABLE and then point at it. See the two effects
  // below.
  var [selectedCommissionId, setSelectedCommissionId] = useState(null);
  // The id we have already scrolled to. Not state: it must be readable and
  // writable inside the same tick the scroll happens in, and changing it must
  // not re-render.
  var scrolledForRef = useRef("");
  var [reloadTick, setReloadTick] = useState(0);
  // Phase R-8 — single unified Edit Collection modal replaces the old trio
  // (editingSnapshot / editingIncentive / addingPayoutFor). Triggered by the
  // pencil icon next to the deal total on each card.
  var [editingCollection, setEditingCollection] = useState(null); // commission obj
  var [editingCycleStage, setEditingCycleStage] = useState(null); // {commission, cycle, nextStage}
  // P3 (2026-11-02): finance is live. An id-only entity_changed for a
  // commission the list holds re-reads THAT row in place (GET /api/commissions/
  // :id — the accountant's copy comes back redacted by the route, nothing here
  // can un-redact it) and refreshes the stats strip; a commission the list does
  // not hold (a new one), a null id list (recompute-all, a resync) or any VAT /
  // expense / profit-tax change re-runs the effects through reloadTick /
  // liveFinRev. Deferred while an editor holds a row.
  var [liveFinRev, setLiveFinRev] = useState(0);
  var commLiveRefetch = function(ids){
    var held = {}; (rows || []).forEach(function(r){ held[String(r._id)] = true; });
    if (!ids.every(function(id){ return held[String(id)]; })) { setReloadTick(function(t){ return t + 1; }); return; }
    ids.forEach(function(id){
      apiFetch("/api/commissions/" + encodeURIComponent(id), "GET", null, p.token).then(function(fresh){
        if (!fresh || !fresh._id) return;
        setRows(function(prev){ return (prev || []).map(function(r){ return String(r._id) === String(fresh._id) ? fresh : r; }); });
      }).catch(function(){});
    });
    var liveStatsUrl = "/api/commissions/stats" + (claimsYearFilter ? "?year=" + encodeURIComponent(claimsYearFilter) : "");
    apiFetch(liveStatsUrl, "GET", null, p.token).then(function(d){ if (d) setStats(d); }).catch(function(){});
  };
  useLiveFamily(["commission", "vatPayment", "expense", "profitTax"], {
    onChange: function(f, ids){
      setLiveFinRev(function(v){ return v + 1; });
      if (f === "commission") { if (ids) commLiveRefetch(ids); else setReloadTick(function(t){ return t + 1; }); }
    },
    isEditing: function(){ return !!(editingCollection || editingCycleStage || editingExpense || expenseMonthModal || vatAddMonth); }
  });
  var [openCycleHistory, setOpenCycleHistory] = useState({});     // cycle key → History list open
  // Phase R-8 — per-card collapse state for the Recipients block. Default is
  // collapsed; keyed by commission _id so each card remembers its own toggle.
  var [expandedRecipients, setExpandedRecipients] = useState({}); // {commissionId: true}
  // Phase R-10 — hover state for the Recipients header so the row reads as
  // clearly clickable. Keyed by commission _id; only one row hovers at a time
  // in practice, but a map keeps the logic per-card.
  var [hoveredRecipientHeader, setHoveredRecipientHeader] = useState(null);
  // Phase R-13 — Add Recipient modal state. Holds the commission being edited
  // (or null when modal is closed). The modal collects name/role/amount, then
  // POSTs to /api/commissions/:id/recipients via writeCommission.
  var [addRecipientFor, setAddRecipientFor] = useState(null);
  var [addRecipName, setAddRecipName] = useState("");
  var [addRecipRole, setAddRecipRole] = useState("sales");
  var [addRecipAmount, setAddRecipAmount] = useState("");
  var [addRecipUserId, setAddRecipUserId] = useState("");
  // Phase R-13.1 — Broker Calculation modal state. Holds the External
  // commission being viewed (or null when modal is closed).
  var [brokerCalcFor, setBrokerCalcFor] = useState(null);
  // The Print button's own busy + error state. It lives HERE, beside
  // brokerCalcFor, because the modal below is an IIFE inside JSX and not a
  // component — a useState declared in there would be a conditionally-called
  // hook. On the website the print dialog is its own feedback; on the app the
  // work is a rasterise, a file write and a share sheet, and the failure this
  // whole change replaces was SILENCE. Nothing about it may be silent again.
  var [brokerPrinting, setBrokerPrinting] = useState(false);
  var [brokerPrintErr, setBrokerPrintErr] = useState("");
  // Phase R-14 — Ambassador Calculation modal state (parallel to brokerCalcFor).
  var [ambassadorCalcFor, setAmbassadorCalcFor] = useState(null);
  // Phase R-9 — cashFlow state removed; the new Recipients breakdown computes
  // paid totals straight from c.payouts, so the global cash-flow endpoint is no
  // longer needed for card rendering.
  // Due Payouts tab + day-10-to-15 red banner — admin/operation only.
  // Refetches on reloadTick so any in-tab mutation (payout recorded, cycle
  // advanced) refreshes the list without a full page reload.
  var [duePayouts, setDuePayouts] = useState(null);
  var [dueLoading, setDueLoading] = useState(false);
  var [savingFlag, setSavingFlag] = useState(false);
  var [toast, setToast] = useState(null);                       // {kind, msg}

  useEffect(function(){
    var cancelled = false;
    var qs = [];
    if (statusFilter && statusFilter !== "all") qs.push("status=" + encodeURIComponent(statusFilter));
    if (search.trim()) qs.push("q=" + encodeURIComponent(search.trim()));
    agentFilter.forEach(function(a){ qs.push("agentId=" + encodeURIComponent(a)); });
    if (claimsYearFilter) qs.push("year=" + encodeURIComponent(claimsYearFilter));
    // Deal type and developer are SERVER filters now. Filtering the fetched page
    // for them would show fewer rows than the pill's own count promises the
    // moment the result set outgrows one page.
    if (dealTypeFilter && dealTypeFilter !== "all") qs.push("dealType=" + encodeURIComponent(dealTypeFilter));
    if (developerFilter) qs.push("developerId=" + encodeURIComponent(developerFilter));
    qs.push("page=1");
    var url = "/api/commissions" + (qs.length ? "?" + qs.join("&") : "");
    var statsUrl = "/api/commissions/stats" + (claimsYearFilter ? "?year=" + encodeURIComponent(claimsYearFilter) : "");
    setRows(null); setCommMeta(null); setLoadErr("");
    apiFetch(url, "GET", null, p.token)
      .then(function(d){ if(!cancelled){ setRows((d && d.data) || []); setCommMeta(d || null); } })
      .catch(function(e){ if(!cancelled){ setRows([]); setCommMeta(null); setLoadErr(e && e.message ? e.message : "Failed to load commissions"); } });
    apiFetch(statsUrl, "GET", null, p.token)
      .then(function(d){ if(!cancelled) setStats(d || {}); })
      .catch(function(){ if(!cancelled) setStats({}); });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, selKey(agentFilter), claimsYearFilter, dealTypeFilter, developerFilter, p.token, reloadTick]);

  // Load-more — appends the next page. It rebuilds the SAME query string as the
  // effect above, so a page-2 fetch can never carry different filters than page 1
  // (the classic pager bug: the filters move, the page number does not, and the
  // two pages come from different result sets).
  var commQueryString = function(pageNum){
    var qs2 = [];
    if (statusFilter && statusFilter !== "all") qs2.push("status=" + encodeURIComponent(statusFilter));
    if (search.trim()) qs2.push("q=" + encodeURIComponent(search.trim()));
    agentFilter.forEach(function(a){ qs2.push("agentId=" + encodeURIComponent(a)); });
    if (claimsYearFilter) qs2.push("year=" + encodeURIComponent(claimsYearFilter));
    if (dealTypeFilter && dealTypeFilter !== "all") qs2.push("dealType=" + encodeURIComponent(dealTypeFilter));
    if (developerFilter) qs2.push("developerId=" + encodeURIComponent(developerFilter));
    qs2.push("page=" + pageNum);
    return "/api/commissions?" + qs2.join("&");
  };
  var loadMoreCommissions = function(){
    if (commLoadingMore || !commMeta) return;
    var next = (commMeta.page || 1) + 1;
    if (next > (commMeta.totalPages || 1)) return;
    setCommLoadingMore(true);
    apiFetch(commQueryString(next), "GET", null, p.token)
      .then(function(d){
        setRows(function(prev){
          var add = (d && d.data) || [];
          // De-duplicate on append. skip/limit over a live collection can repeat a
          // row when something is inserted between two page fetches, and a
          // duplicate React key is a silent render bug rather than a visible one.
          var seen = {}; (prev || []).forEach(function(r){ seen[String(r._id)] = 1; });
          return (prev || []).concat(add.filter(function(r){ return !seen[String(r._id)]; }));
        });
        setCommMeta(d || null);
        setCommLoadingMore(false);
      })
      .catch(function(){ setCommLoadingMore(false); });
  };

  // Phase R-5 — annual summary fetcher. Fires when the user is on the Annual
  // Summary tab; the year follows annualYear, defaulting to the current Cairo
  // calendar year when annualYear is empty (no data yet — see auto-select below).
  useEffect(function(){
    if (activeTab !== "annual") return;
    var cancelled = false;
    var effYear = annualYear || String(dfCairoYear());
    setAnnualLoading(true);
    apiFetch("/api/commissions/annual-summary?year=" + encodeURIComponent(effYear), "GET", null, p.token)
      .then(function(d){ if (!cancelled) { setAnnualSummary(d || null); setAnnualLoading(false); } })
      .catch(function(){ if (!cancelled) { setAnnualSummary(null); setAnnualLoading(false); } });
    return function(){ cancelled = true; };
  }, [activeTab, annualYear, p.token, reloadTick, liveFinRev]);

  // Partial VAT payments ledger — same tab + year scope as the summary above.
  useEffect(function(){
    if (activeTab !== "annual") return;
    if (!vatCanRead) { setVatPayments({ error:true }); return; }
    var cancelled = false;
    var effYear = annualYear || String(dfCairoYear());
    setVatPayments(null);
    apiFetch("/api/vat-payments?year=" + encodeURIComponent(effYear), "GET", null, p.token)
      .then(function(d){ if (!cancelled) setVatPayments(d && d.byMonth ? d : { error:true }); })
      .catch(function(){ if (!cancelled) setVatPayments({ error:true }); });
    return function(){ cancelled = true; };
  }, [activeTab, annualYear, p.token, vatReloadTick, vatCanRead, liveFinRev]);

  // Phase R-6 — annual P&L fetcher (admin only). Fires alongside the R-5
  // summary fetch, scoped to the same annualYear. pnlReloadTick lets the
  // Expenses + ProfitTax handlers force a refetch after a mutation without
  // touching reloadTick (which would also rebuild the Claims list).
  // Categories load once when the user enters the tab; refetched only when
  // ManageCategoriesModal closes (handled inline in that modal's save path).
  var liveCatRev = useLiveRev("expenseCategory");
  useEffect(function(){
    if (activeTab !== "annual") return;
    var cancelled = false;
    var effYear = annualYear || String(dfCairoYear());
    var silent = pnlSilentReloadRef.current;
    if (!silent) setPnlLoading(true);
    apiFetch("/api/annual-pnl?year=" + encodeURIComponent(effYear), "GET", null, p.token)
      .then(function(d){ if (!cancelled) { setPnl(d || null); setPnlLoading(false); pnlSilentReloadRef.current = false; } })
      .catch(function(){ if (!cancelled) { setPnl(null); setPnlLoading(false); pnlSilentReloadRef.current = false; } });
    // Phase R-15 — Net Profit by Deal feed (admin-only, same year scope).
    if (!silent) setProfitByDealLoading(true);
    apiFetch("/api/commissions/profit-by-deal?year=" + encodeURIComponent(effYear) + (pbdCcFilter ? "&closingCompanyId=" + encodeURIComponent(pbdCcFilter) : "") + (pbdDevFilter ? "&developerId=" + encodeURIComponent(pbdDevFilter) : ""), "GET", null, p.token)
      .then(function(d){ if (!cancelled) { setProfitByDeal(d || null); setProfitByDealLoading(false); } })
      .catch(function(){ if (!cancelled) { setProfitByDeal(null); setProfitByDealLoading(false); } });
    apiFetch("/api/expense-categories", "GET", null, p.token)
      .then(function(d){ if (!cancelled) setExpenseCategories((d && d.data) || []); })
      .catch(function(){ if (!cancelled) setExpenseCategories([]); });
    // Phase 4: who created / last edited each expense, and who last set the tax.
    apiFetch("/api/expenses?year=" + encodeURIComponent(effYear), "GET", null, p.token)
      .then(function(d){
        if (cancelled) return;
        var m = {};
        ((d && d.data) || []).forEach(function(x){ if (x && x._id) m[String(x._id)] = x; });
        setExpenseAudit(m);
      })
      .catch(function(){ if (!cancelled) setExpenseAudit({}); });
    apiFetch("/api/profit-tax/" + encodeURIComponent(effYear), "GET", null, p.token)
      .then(function(d){ if (!cancelled) setProfitTaxAudit(d || null); })
      .catch(function(){ if (!cancelled) setProfitTaxAudit(null); });
    return function(){ cancelled = true; };
  }, [activeTab, annualYear, p.cu, p.token, pnlReloadTick, pbdCcFilter, pbdDevFilter, liveCatRev, liveFinRev]);
  // Feature B — load closing companies for the Net Profit by Deal filter dropdown.
  var liveCcRev = useLiveRev("closingCompany");
  useEffect(function(){
    if (activeTab !== "annual" || !p.token) return;
    var cancelled = false;
    apiFetch("/api/closing-companies", "GET", null, p.token)
      .then(function(d){ if (!cancelled) { var rows=(d&&Array.isArray(d.data))?d.data:(Array.isArray(d)?d:[]); setCommClosingCompanies(rows); } })
      .catch(function(){ /* non-fatal: dropdown just shows "All companies" */ });
    return function(){ cancelled = true; };
  }, [activeTab, p.cu, p.token, liveCcRev]);
  // Developer (D5) — load the managed developers list for the claims-list filter
  // AND the Net-Profit-by-Deal filter. Page-wide (not tab-scoped) since the
  // claims filter lives on the main tab. Page is admin/SA-only at routing.
  var liveDevRev = useLiveRev("developer");
  useEffect(function(){
    if (!p.token) return;
    var cancelled = false;
    apiFetch("/api/developers", "GET", null, p.token)
      .then(function(d){ if (!cancelled) { var rows=(d&&Array.isArray(d.data))?d.data:(Array.isArray(d)?d:[]); setCommDevelopers(rows); } })
      .catch(function(){ /* non-fatal: dropdown just shows "All developers" */ });
    return function(){ cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.token, liveDevRev]);

  // Phase R-7 — Payout Report fetcher. payoutMonth is "YYYY-MM"; backend
  // accepts year/month numeric params.
  useEffect(function(){
    if (activeTab !== "payout") return;
    var cancelled = false;
    var parts = String(payoutMonth || "").split("-");
    if (parts.length !== 2) return;
    var y = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10);
    if (!isFinite(y) || !isFinite(m)) return;
    setPayoutLoading(true);
    apiFetch("/api/commissions/payout-report?year=" + y + "&month=" + m, "GET", null, p.token)
      .then(function(d){ if (!cancelled) { setPayoutReport(d || null); setPayoutLoading(false); } })
      .catch(function(){ if (!cancelled) { setPayoutReport(null); setPayoutLoading(false); } });
    return function(){ cancelled = true; };
  }, [activeTab, payoutMonth, p.token, reloadTick, liveFinRev]);

  // ===== THE CLOSED-DEAL COMMISSION INVARIANT — WARNING BANNER =============
  // Owner rule: a closed deal ALWAYS has a commission, so a non-zero count here
  // is an invariant violation, not a to-do list. The write path was closed on
  // 2026-08-26 (see closeDealWithCommission in server.js) and production
  // reconciled to 0 the same day — this exists so a REGRESSION, or a row created
  // before that date by a path nobody has walked yet, is visible on the page that
  // owns commissions instead of waiting to be noticed in a payout.
  //
  // Deliberately NOT gated on a tab: the banner belongs to the page. Fetched
  // once per mount plus on reloadTick, so repairing a deal and coming back
  // reflects it. A failed fetch renders nothing rather than a false all-clear.
  var [dealsNoComm, setDealsNoComm] = useState(null);
  useEffect(function(){
    if (!p.token) return;
    var cancelled = false;
    apiFetch("/api/deals/without-commission", "GET", null, p.token)
      .then(function(d){ if (!cancelled) setDealsNoComm(d || null); })
      .catch(function(){ if (!cancelled) setDealsNoComm(null); });
    return function(){ cancelled = true; };
  }, [p.token, reloadTick]);

  // ===== THE 💰 DEEP-LINK FROM A DEAL ROW ==================================
  //
  // One-shot consume of selectedCommissionLeadId from CRMApp state: resolve the
  // deal's commission, put the list on a filter that CONTAINS it, then point at
  // it once it is actually on screen.
  //
  // WHAT IT USED TO DO, AND WHY THE BUTTON DID NOTHING. Three separate defects,
  // measured on production 2026-08-26:
  //
  //   1. IT RACED A REFETCH IT STARTED ITSELF. It called setReloadTick, which
  //      makes the list effect run setRows(null) and re-fetch, and then looked
  //      for the card with document.getElementById on a fixed 250ms timer.
  //      GET /api/commissions?page=1 takes 2,017ms on prod. At t=250ms the list
  //      is guaranteed to be a spinner, so getElementById returned null, every
  //      time, for every deal — and there was no retry. This is why the button
  //      appeared to do nothing at all rather than working intermittently.
  //   2. IT FORCED THE ONE FILTER THAT HIDES A CANCELLED ROW. `status=all`
  //      means "all EXCEPT cancelled" server-side (q.status = {$ne:"cancelled"}
  //      — see GET /api/commissions). 14 of the 61 commissions on prod are
  //      cancelled, and for those the card was not in the result set at all, so
  //      no amount of waiting would have found it.
  //   3. A DEAL WITH NO COMMISSION SAID NOTHING. The 404 hit .catch(){} and the
  //      user got a page change and silence.
  //
  // The rule this now follows: never guess when the DOM is ready — wait for the
  // row to appear in the data that renders it (`rows` IS what renders, there is
  // no client-side narrow) and scroll from there. The second effect does that.
  useEffect(function(){
    if (!p.selectedCommissionLeadId) return;
    var cancelled = false;
    apiFetch("/api/commissions/by-lead/" + p.selectedCommissionLeadId, "GET", null, p.token)
      .then(function(c){
        if (cancelled || !c || !c._id) return;
        var id = String(c._id);
        setSelectedCommissionId(id);
        scrolledForRef.current = "";          // a new target — scroll again
        // The list only exists on the Claims tab.
        setActiveTab("deals");
        // THE FILTER MUST BE ONE THAT CONTAINS THIS ROW. "all" excludes
        // cancelled, so a cancelled commission needs its own pill; everything
        // else (active / fully_paid) lives under "all".
        setStatusFilter(c.status === "cancelled" ? "cancelled" : "all");
        // Clear every other narrow that could hide the row. All of these are at
        // their defaults on a fresh mount, which is the only way this page is
        // reached today — they are cleared anyway so the deep-link stays correct
        // if it is ever fired at an already-mounted page.
        setSearch(""); setAgentFilter([]); setClaimsYearFilter("");
        setDealTypeFilter("all"); setDeveloperFilter("");
      })
      .catch(function(err){
        if (cancelled) return;
        setToast({ kind: "err", msg: (err && err.status === 404)
          ? "No commission has been recorded for that deal yet."
          : ((err && err.message) || "Could not open that deal's commission.") });
      })
      .then(function(){
        if (p.setSelectedCommissionLeadId) { try { p.setSelectedCommissionLeadId(null); } catch(e){} }
      });
    return function(){ cancelled = true; };
  }, [p.selectedCommissionLeadId, p.token]);

  // The second half: scroll to the target WHEN IT EXISTS, not on a timer.
  // `displayRows = rows` with no client-side filtering, so membership in `rows`
  // is exactly "this card is rendered".
  //
  // The paging branch matters even though it cannot fire today (47 commissions,
  // one page of 100): the moment the book passes 100 rows, a deep-link to a row
  // on page 3 would otherwise land on a list that does not contain it and say
  // nothing — the silent-truncation failure this page's own paging comment was
  // written about. So it pages forward until the row appears or the server says
  // there is no more, and only then reports the miss.
  useEffect(function(){
    if (!selectedCommissionId || !Array.isArray(rows)) return;
    if (scrolledForRef.current === selectedCommissionId) return;
    var present = rows.some(function(r){ return String(r && r._id) === selectedCommissionId; });
    if (present) {
      scrolledForRef.current = selectedCommissionId;
      // The row is in `rows`; its DOM node lands on the next paint.
      var go = function(){
        var el = document.getElementById("commission-card-" + selectedCommissionId);
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior:"smooth", block:"center" });
      };
      if (typeof window.requestAnimationFrame === "function") window.requestAnimationFrame(go);
      else setTimeout(go, 0);
      return;
    }
    if (commMeta && (commMeta.page || 1) < (commMeta.totalPages || 1)) { loadMoreCommissions(); return; }
    scrolledForRef.current = selectedCommissionId;   // stop looking
    setToast({ kind:"err", msg:"That deal's commission is not in this list." });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, commMeta, selectedCommissionId]);

  // Toast auto-dismiss
  useEffect(function(){
    if (!toast) return;
    var h = setTimeout(function(){ setToast(null); }, 3500);
    return function(){ clearTimeout(h); };
  }, [toast]);

  // The "bounce a non-admin off the Annual Summary tab" effect used to live here.
  // It was a FIFTH copy of the tab rule, and the most dangerous kind: after a
  // take-over granting commissions.annual to a rank, the chip would render, the
  // click would register, and this would throw them straight back to Claims with
  // no message. The stale-state case it defended against is now structural —
  // activeTab is derived through resolveTabStrip, so a stored "annual" simply
  // does not survive an envelope that denies it.

  // Phase R-6 — sync the profit-tax draft inputs from the loaded pnl response.
  // Drafts default to ("percent", "") when no config exists for the year; the
  // FE renders an inline CTA ("Set profit tax to calculate net profit") in
  // that case (judgment #9). Re-runs when annualYear flips so admin sees the
  // saved values for the year they just selected.
  useEffect(function(){
    if (!pnl || !pnl.profitTax) {
      setTaxModeDraft("percent");
      setTaxValueDraft("");
      return;
    }
    var cfg = pnl.profitTax;
    if (cfg.mode) {
      setTaxModeDraft(cfg.mode);
      setTaxValueDraft(cfg.value != null ? String(cfg.value) : "");
    } else {
      setTaxModeDraft("percent");
      setTaxValueDraft("");
    }
  }, [pnl, annualYear]);

  // Annual Summary tab: auto-select the most recent available year when the
  // user lands on the tab with no year set. Prevents the dropdown from showing
  // a "Default (YYYY)" placeholder that duplicates the real year option below
  // it. Falls back gracefully when stats haven't loaded yet (no-op) or when
  // there's no data at all (leave annualYear empty so the disabled "—" option
  // stays selected). Scoped to annualYear — does NOT touch claimsYearFilter.
  useEffect(function(){
    if (activeTab !== "annual") return;
    if (annualYear) return;
    var avail = stats && Array.isArray(stats.availableYears) ? stats.availableYears : [];
    if (avail.length === 0) return;
    setAnnualYear(avail[0]);
  }, [activeTab, annualYear, stats]);

  // Due Payouts fetch — runs once on mount + on every reloadTick (mutations
  // bump that counter). Gated to admin/operation so the endpoint isn't
  // hit by other roles. Drives both the Due Payouts tab table and the red
  // banner at the top of the page.
  useEffect(function(){
    if (!p.cu || (p.cu.role !== "admin" && p.cu.role !== "operation" && !roleHasFinanceReadsFE(p.cu))) return;
    var cancelled = false;
    setDueLoading(true);
    apiFetch("/api/commissions/due-payouts", "GET", null, p.token)
      .then(function(d){ if (!cancelled) { setDuePayouts(d || null); setDueLoading(false); } })
      .catch(function(){ if (!cancelled) { setDuePayouts(null); setDueLoading(false); } });
    return function(){ cancelled = true; };
  }, [p.cu, p.token, reloadTick, liveFinRev]);

  // Role-gate AFTER hooks. The literal check is unchanged; a matrix-only role
  // (the accountant) is admitted on the owner's page:commissions grant, which
  // renderPage already asked through canSeePage.
  if (!p.cu || (p.cu.role !== "admin" && p.cu.role !== "operation" && !roleHasFinanceReadsFE(p.cu))) {
    return <div style={{ padding:"40px 16px", textAlign:"center", color:C.textLight }}>
      Commissions are not available for your role.
    </div>;
  }

  // Shared write helper — wraps apiFetch with savingFlag + toast on error.
  // After save, splice the updated commission into `rows` IN PLACE so the
  // page doesn't unmount/remount the whole list (preserves scroll position
  // and expanded-card state). Stats + cash-flow refresh in the background
  // because they don't reset rows. Falls back to a full refetch only when
  // the API response doesn't look like a commission doc.
  var writeCommission = async function(method, path, body, successMsg){
    setSavingFlag(true);
    try {
      var d = await apiFetch(path, method, body, p.token);
      setSavingFlag(false);
      if (d && d._id && d.snapshot) {
        // In-place splice — swap the matching commission, leave everything else.
        setRows(function(prev){
          if (!Array.isArray(prev)) return prev;
          var idx = prev.findIndex(function(c){ return String(c._id) === String(d._id); });
          if (idx >= 0) {
            var next = prev.slice();
            next[idx] = d;
            return next;
          }
          // Not in current filter (e.g. status changed and the new state
          // doesn't match the active pill) — leave rows untouched. The
          // freshly-edited card is no longer visible; toast confirms the save.
          return prev;
        });
        // Refresh stats lightly. setStats doesn't reset rows.
        var statsRefreshUrl = "/api/commissions/stats" + (claimsYearFilter ? "?year=" + encodeURIComponent(claimsYearFilter) : "");
        apiFetch(statsRefreshUrl, "GET", null, p.token).then(function(s){ setStats(s || {}); }).catch(function(){});
      } else {
        // Unknown response shape — fall back to full refetch.
        setReloadTick(function(v){ return v + 1; });
      }
      setToast({ kind: "ok", msg: successMsg || "Saved" });
      return d;
    } catch(e) {
      setSavingFlag(false);
      setToast({ kind: "err", msg: (e && e.message) || "Save failed" });
      throw e;
    }
  };

  var fmtMoney = function(n){
    var v = Number(n || 0);
    if (!isFinite(v) || v === 0) return "—";
    return Math.round(v).toLocaleString() + " EGP";
  };
  var fmtDate = function(s){
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("en-GB"); } catch(e) { return s; }
  };
  // The "imminent, missing a stage" rule USED to live here and the banner counted
  // it by filtering the loaded array. Both moved to the server (2026-08-21):
  // `rows` is a page now, so a count derived from it would silently under-report,
  // and keeping a second copy of the predicate here beside the server's would be
  // the fork this codebase has already paid for once. The card reads the flag the
  // server computed; the banner reads the tally computed from that same flag.
  var imminentCount = (commMeta && commMeta.counts && commMeta.counts.imminent) || 0;

  var STATUS_PILLS = [
    { id:"all", label:"All" },
    { id:"pending_claim", label:"Pending Claim" },
    { id:"claim_submitted", label:"Claim Submitted" },
    { id:"invoice_submitted", label:"Invoice Submitted" },
    { id:"received", label:"Received" },
    // Paid pill: filter by Commission.status === "fully_paid" (the terminal
    // state set by zombie cleanup when all cycles are paid_to_team or
    // cancelled). Previously this pill sent ?status=paid_to_team which the
    // BE interprets as { status:"active", "cycles.state":"paid_to_team" } —
    // that excluded every commission already flipped to fully_paid, so the
    // tab read 0 even when fully-paid commissions existed. The cycle-level
    // "paid_to_team" filter has no separate pill (admins inspect cycles via
    // the open card UI instead).
    { id:"fully_paid", label:"Paid" },
    { id:"cancelled", label:"Cancelled" }
  ];

  var STAGE_LABELS = {
    claim_submitted: "Claim",
    invoice_submitted: "Invoice",
    received: "Received",
    paid_to_team: "Paid"
  };
  // Under every recorded stage, for every role: who recorded it and when (the
  // stage's `date` is the calendar day typed; `at` is the recording moment).
  // Pre-Phase-4 stages carry byUser alone → "by Name — —".
  var stageAuditCaption = function(st){
    if (!st || !st.date) return "";
    return auditCaption(st.byUser, st.at);
  };

  // Phase R-9 — renderRecipient helper removed. The new Recipients block in
  // renderCard renders a richer Owed / Paid / Remaining breakdown inline.

  // Tiny pencil SVG — 10px, matches the design tokens of the existing buttons.
  var PencilIcon = function(col){
    return <svg width="10" height="10" viewBox="0 0 14 14" fill="none" style={{display:"block"}}><path d="M2 12l3-1L11 5l-2-2-6 6-1 3zM9 3l2 2" stroke={col||"#64748B"} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
  };

  var renderStageDot = function(c, cycle, stageKey){
    var st = cycle && cycle[stageKey];
    var done = !!(st && st.date);
    var isCancelled = c && c.status === "cancelled";
    var canEdit = done && !isCancelled;
    var openEdit = function(e){
      if (e && e.stopPropagation) e.stopPropagation();
      setEditingCycleStage({
        commission: c, cycle: cycle, mode: "edit", stageName: stageKey,
        initialValues: {
          date: (st && st.date) || "",
          notes: (st && st.notes) || "",
          amount: stageKey === "received" ? (cycle.receivedAmount || "") : ""
        }
      });
    };
    return <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, flex:1, minWidth:0 }}>
      <div style={{ display:"flex", alignItems:"center", gap:4 }}>
        <div style={{ width:14, height:14, borderRadius:"50%", background: done ? C.success : "#E2E8F0", border:"2px solid "+(done?C.success:"#CBD5E1") }}/>
        {canEdit && <button onClick={openEdit} disabled={savingFlag} title={"Edit " + (STAGE_LABELS[stageKey] || stageKey)}
          style={{ background:"#fff", border:"1px solid #E2E8F0", borderRadius:4, width:18, height:18, padding:0, cursor: savingFlag ? "wait" : "pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
          {PencilIcon(C.textLight)}
        </button>}
      </div>
      <div style={{ fontSize:10, fontWeight:600, color:C.textLight, textAlign:"center", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:"100%" }}>{STAGE_LABELS[stageKey]}</div>
      <div style={{ fontSize:9, color: done ? C.text : "#CBD5E1" }}>{done ? fmtDate(st.date) : "—"}</div>
      {done && <div data-stage-audit={stageKey} style={{ fontSize:8, color:C.textLight, textAlign:"center", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:"100%" }} title={stageAuditCaption(st)}>{stageAuditCaption(st)}</div>}
      {/* The invoice file (2026-11-02): one line under the Invoice stage —
          "📄 name · uploaded by Name — date" — opening a short-lived signed URL
          from the cycle's own read route. Cycles recorded before the rule carry
          no document and show nothing here. */}
      {done && stageKey === "invoice_submitted" && st.document && st.document.key && <button data-invoice-doc={String(cycle._id)}
        onClick={function(e){ e.stopPropagation(); apiFetch("/api/commissions/"+c._id+"/cycles/"+cycle._id+"/invoice-document","GET",null,p.token).then(function(r){ if (r && r.url) window.open(r.url, "_blank", "noopener"); else alert("Invoice file not available"); }).catch(function(err){ alert((err && err.message) || "Invoice file not available"); }); }}
        title={"📄 " + (st.document.name || "invoice") + " — uploaded " + auditCaption(st.document.uploadedByName, st.document.uploadedAt)}
        style={{ background:"transparent", border:"none", padding:0, cursor:"pointer", fontSize:8, color:C.accent, textDecoration:"underline", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:"100%" }}>
        📄 uploaded {auditCaption(st.document.uploadedByName, st.document.uploadedAt)}
      </button>}
    </div>;
  };

  // Per-cycle delete. Early states get a lightweight confirm; received /
  // paid_to_team get the full destructive copy + cycle-number entry (backend
  // requires confirmCycleNumber match for those states).
  var handleDeleteCycle = async function(c, cycle){
    if (!c || !cycle) return;
    var early = ["pending_claim","claim_submitted","invoice_submitted"].indexOf(cycle.state) >= 0;
    if (early) {
      if (!window.confirm("Delete cycle " + cycle.cycleNumber + "? This cannot be undone.")) return;
      try { await writeCommission("DELETE", "/api/commissions/" + c._id + "/cycles/" + cycle._id, null, "Cycle deleted"); } catch(_e){}
      return;
    }
    var typed = window.prompt(
      "Delete Cycle #" + cycle.cycleNumber + "? This will remove the cycle and its received amount. Payouts on this commission will remain unchanged — recipients may appear overpaid. The commission status may revert to active. This action is logged. Continue?\n\nTo confirm, type the cycle number (" + cycle.cycleNumber + ") below:"
    );
    if (typed == null) return;
    var n = Number(String(typed).trim());
    if (!isFinite(n) || n !== Number(cycle.cycleNumber)) {
      alert("Cycle number didn't match — delete cancelled.");
      return;
    }
    try { await writeCommission("DELETE", "/api/commissions/" + c._id + "/cycles/" + cycle._id, { confirmCycleNumber: n }, "Cycle deleted"); } catch(_e){}
  };

  // Restore a cancelled cycle back to its last meaningful stage. Backend
  // infers the target stage from captured dates (received → invoice → claim →
  // pending_claim) and recomputes recipient shares.
  var handleRestoreCycle = async function(c, cycle){
    if (!c || !cycle) return;
    if (!window.confirm("Restore this cycle? It will return to its previous stage.")) return;
    try { await writeCommission("POST", "/api/commissions/" + c._id + "/cycles/" + cycle._id + "/restore", null, "Cycle restored"); } catch(_e){}
  };

  // Owner rule (2026-09-19): a control that acts on ONE cycle renders on EVERY
  // cycle it can legally act on, from that cycle's OWN state only. Advance Stage
  // used to render once per card, on the LAST non-terminal cycle ("activeCycle"),
  // so an older cycle still mid-flight — deal #01028's Cycle #2, stuck at
  // invoice_submitted beside a newer Cycle #3 — had no way forward. No control
  // here reads a neighbour's position; verify-per-item-controls.js renders this
  // function over every state, with and without siblings, and requires the
  // markup to be identical.
  var CYCLE_NEXT_STAGE = {
    pending_claim:     "claim_submitted",
    claim_submitted:   "invoice_submitted",
    invoice_submitted: "received",
    received:          "paid_to_team"
  };
  var isTerminalCycleState = function(state){ return state === "paid_to_team" || state === "cancelled"; };
  var renderCycle = function(c, cycle){
    var isOpenCycle = !isTerminalCycleState(cycle.state);
    var pillBg = "#F1F5F9", pillFg = C.textLight;
    if (cycle.state === "paid_to_team") { pillBg = "#DCFCE7"; pillFg = "#15803D"; }
    else if (cycle.state === "cancelled") { pillBg = "#FEE2E2"; pillFg = "#B91C1C"; }
    else if (cycle.state === "received") { pillBg = "#DBEAFE"; pillFg = "#1D4ED8"; }
    var isCancelledCommission = c && c.status === "cancelled";
    // A financeReads role (the accountant) may enter figures, advance a stage,
    // edit a previous one, and — since the second ruling of 2026-11-02 — delete
    // and restore a cycle (the routes ask manageCommissionCycle). Backfilling
    // claim data stays admin/operation, as its route does.
    var cycleFullControls = !roleHasFinanceReadsFE(p.cu);
    var canDelete = !isCancelledCommission && ["pending_claim","claim_submitted","invoice_submitted","received","paid_to_team"].indexOf(cycle.state) >= 0;
    // Advance — any non-terminal cycle on a live commission; the next stage is
    // this cycle's own, never the card's.
    var nextStage = CYCLE_NEXT_STAGE[cycle.state] || null;
    var canAdvance = !isCancelledCommission && !!nextStage;
    // Phase R-6 polish — cycles that passed claim_submitted but never captured
    // unit/rate/claimAmount (pre-R-5 data) qualify for the backfill flow.
    var passedClaim = ["claim_submitted","invoice_submitted","received","paid_to_team"].indexOf(cycle.state) >= 0;
    var missingClaimData = Number(cycle.claimAmount || 0) === 0 || Number(cycle.claimUnitValue || 0) === 0 || Number(cycle.commissionRate || 0) === 0;
    var canBackfill = cycleFullControls && !isCancelledCommission && passedClaim && missingClaimData;
    // Restore — only a cancelled cycle inside a still-active/fully_paid
    // commission. (Page is already admin/operation-gated at the route level.)
    var canRestore = !isCancelledCommission && cycle.state === "cancelled";
    // Withholding-5% exemption (per-cycle). Checkbox is admin/SA, plus the
    // financeReads role (owner ruling 2026-11-02 — the route asks the
    // manageCommissionCycle grant), and hidden on ambassador + resale deals
    // (already tax-free), on paid_to_team/cancelled cycles, and on a cancelled
    // commission. The "No 5% W/H" badge shows for all
    // roles when set — but never on ambassador/resale cycles (withholding is 0
    // there anyway, so the badge would mislead).
    var cycIsAmb    = !!(c && c.ambassadorSplit && c.ambassadorSplit.isAmbassador);
    var cycIsResale = !!(c && c.snapshot && c.snapshot.saleType === "Resale");
    var isAdminSA   = p.cu && (p.cu.role === "admin" || p.cu.role === "operation");
    var canToggleWithholdingExempt = (isAdminSA || roleHasFinanceReadsFE(p.cu)) && !cycIsAmb && !cycIsResale
      && cycle.state !== "paid_to_team" && cycle.state !== "cancelled" && !isCancelledCommission;
    var showNoWhBadge = !!cycle.withholdingExempt && !cycIsAmb && !cycIsResale;
    var histKey = String(cycle._id || cycle.cycleNumber), histLines = cycleHistoryLines(cycle), histOpen = !!openCycleHistory[histKey];
    return <div key={String(cycle._id || cycle.cycleNumber)} style={{ border:"1px solid #E2E8F0", borderRadius:10, padding:"10px 12px", marginBottom:8, background: isOpenCycle ? "#FAFBFF" : "#fff" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:13, fontWeight:700, color:C.text }}>Cycle #{cycle.cycleNumber}</span>
          <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, background: pillBg, color: pillFg }}>
            {String(cycle.state || "").replace(/_/g, " ")}
          </span>
          {histLines.length > 0 && <button type="button" data-cycle-history-toggle={histKey} onClick={function(){ setOpenCycleHistory(function(prev){ var n = Object.assign({}, prev); n[histKey] = !n[histKey]; return n; }); }}
            title="Every recorded state change on this cycle (deletes are in the audit log)"
            style={{ padding:"2px 8px", borderRadius:10, border:"1px solid #E2E8F0", background: histOpen ? "#F1F5F9" : "#fff", color:C.textLight, fontSize:10, fontWeight:600, cursor:"pointer" }}>
            History ({histLines.length})
          </button>}
          {showNoWhBadge && <span title="Under new tax regulation — no 5% withholding on this cycle" style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, background:"#EFF6FF", color:"#1D4ED8" }}>No 5% W/H</span>}
        </div>
        <div style={{ fontSize:11, color:C.textLight, display:"flex", alignItems:"center", gap:8 }}>
          {Number(cycle.receivedAmount || 0) > 0 && <span>Received: <b>{fmtMoney(cycle.receivedAmount)}</b></span>}
          {canBackfill && <button onClick={function(){ setBackfillTarget({ commission: c, cycle: cycle }); }} disabled={savingFlag}
            title="Add unit value & rate (pre-R-5 cycle)"
            style={{ padding:"3px 8px", borderRadius:6, border:"1px solid " + C.accent, background: C.accent + "12", color: C.accent, fontSize:10, fontWeight:600, cursor: savingFlag ? "wait" : "pointer" }}>
            + Add claim data
          </button>}
          {canRestore && <button onClick={function(){ handleRestoreCycle(c, cycle); }} disabled={savingFlag} title={"Restore cycle " + cycle.cycleNumber}
            style={{ padding:"3px 8px", borderRadius:6, border:"1px solid #86EFAC", background:"#fff", color:"#15803D", fontSize:10, fontWeight:600, cursor: savingFlag ? "wait" : "pointer" }}>
            ↻ Restore
          </button>}
          {canDelete && <button onClick={function(){ handleDeleteCycle(c, cycle); }} disabled={savingFlag} title={"Delete cycle " + cycle.cycleNumber}
            style={{ padding:"3px 8px", borderRadius:6, border:"1px solid #FCA5A5", background:"#fff", color:"#B91C1C", fontSize:10, fontWeight:600, cursor: savingFlag ? "wait" : "pointer" }}>
            ✕ Delete
          </button>}
        </div>
      </div>
      {histOpen && histLines.length > 0 && <div data-cycle-history={histKey} style={{ margin:"0 0 8px", padding:"6px 10px", borderRadius:8, background:"#F8FAFC", border:"1px solid #E2E8F0" }}>
        {histLines.map(function(line, i){ return <div key={i} style={{ fontSize:10, color:C.textLight, padding:"2px 0" }}>{line}</div>; })}
      </div>}
      <div style={{ display:"flex", alignItems:"flex-start", gap:8, marginTop:6 }}>
        {renderStageDot(c, cycle, "claim_submitted")}
        {renderStageDot(c, cycle, "invoice_submitted")}
        {renderStageDot(c, cycle, "received")}
        {renderStageDot(c, cycle, "paid_to_team")}
      </div>
      {canToggleWithholdingExempt && <div style={{ marginTop:8 }}>
        <label title="Under new tax regulation — no 5% withholding on this cycle" style={{ display:"inline-flex", alignItems:"center", gap:6, cursor: savingFlag ? "wait" : "pointer", fontSize:11, color:C.textLight, userSelect:"none" }}>
          <input type="checkbox" checked={!!cycle.withholdingExempt} disabled={savingFlag}
            onChange={function(e){ var nv = e.target.checked; writeCommission("PATCH", "/api/commissions/" + c._id + "/cycles/" + cycle._id + "/withholding-exempt", { exempt: nv }, "Cycle updated").catch(function(){}); }}
            style={{ cursor: savingFlag ? "wait" : "pointer", margin:0 }}/>
          <span>Withholding exempt (5%)</span>
        </label>
      </div>}
      {/* Bottom block — Phase R-5 tax breakdown lines on the LEFT (only when claim
          data exists; old cycles with claimAmount === 0 have no lines, all derived
          values at piaster precision / round2 for Excel-reconcile parity) and, on
          the RIGHT of those same rows, vertically centred, ▶ Advance Stage (owner
          layout ruling 2026-09-19 — not a line of its own, not beside the checkbox,
          away from ✕ Delete in the header). A pending_claim cycle has no lines yet
          and shows the button alone, right-aligned. */}
      {(function(){
        var hasBreakdown = Number(cycle.claimAmount || 0) > 0;
        if (!hasBreakdown && !canAdvance) return null;
        var lines = null;
        if (hasBreakdown) {
          var ga    = Number(cycle.claimAmount);
          var rateP = Number(cycle.commissionRate || 0) * 100;
          var fmt   = function(n){ return round2(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
          if (c && c.snapshot && c.snapshot.saleType === "Resale") {
            // Resale — no VAT / withholding: Expected Net == gross claim.
            lines = <div style={{ fontSize:10, color:C.textLight, lineHeight:1.6, minWidth:0 }}>
              <div>Unit Value {fmt(cycle.claimUnitValue)} EGP  •  Rate {rateP.toFixed(2)}%  •  Total {fmt(ga)} EGP</div>
              <div style={{ color:"#059669" }}>Resale — no VAT / withholding  •  Expected Net {fmt(ga)} EGP</div>
            </div>;
          } else {
            var nv    = ga / 1.14;
            var vt    = ga - nv;
            var wh    = cycle.withholdingExempt ? 0 : nv * 0.05;
            var nr    = ga - wh;
            lines = <div style={{ fontSize:10, color:C.textLight, lineHeight:1.6, minWidth:0 }}>
              <div>Unit Value {fmt(cycle.claimUnitValue)} EGP  •  Rate {rateP.toFixed(2)}%  •  Total {fmt(ga)} EGP</div>
              <div>Net of VAT {fmt(nv)}  •  VAT {fmt(vt)}  •  Withholding 5% {fmt(wh)}  •  Expected Net {fmt(nr)} EGP</div>
            </div>;
          }
        }
        return <div style={{ marginTop:8, paddingTop: hasBreakdown ? 6 : 0, borderTop: hasBreakdown ? "1px dashed #E2E8F0" : "none", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
          <div style={{ flex:1, minWidth:0 }}>{lines}</div>
          {canAdvance && <button onClick={function(){ setEditingCycleStage({ commission: c, cycle: cycle, nextStage: nextStage }); }} disabled={savingFlag}
            title={"Advance cycle " + cycle.cycleNumber + " to " + (STAGE_LABELS[nextStage] || nextStage)}
            style={{ flexShrink:0, padding:"5px 12px", borderRadius:8, border:"1px solid " + C.accent, background: C.accent + "12", color: C.accent, fontSize:12, fontWeight:600, cursor: savingFlag ? "wait" : "pointer" }}>
            ▶ Advance Stage
          </button>}
        </div>;
      })()}
    </div>;
  };

  // Every cycle, ascending by cycleNumber, each through renderCycle with its own
  // controls. There is no "active" cycle: the list has no position-dependent
  // branch, only the card-level "+ Add Cycle" that acts on the commission.
  var renderCycleList = function(c){
    var isCancelled = c.status === "cancelled";
    var cycles = (c.cycles || []).slice().sort(function(a, b){
      return (Number(a.cycleNumber) || 0) - (Number(b.cycleNumber) || 0);
    });
    return <div>
      {cycles.map(function(cy){ return renderCycle(c, cy); })}
      {!isCancelled && <div style={{ display:"flex", gap:6, marginTop:6, marginBottom:8, flexWrap:"wrap" }}>
        <button
          onClick={async function(){
            try { await writeCommission("POST", "/api/commissions/" + c._id + "/cycles", {}, "Cycle added"); } catch(_e){}
          }}
          disabled={savingFlag}
          style={{ padding:"5px 12px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", color:C.textLight, fontSize:12, fontWeight:600, cursor: savingFlag ? "wait" : "pointer" }}
        >+ Add Cycle</button>
      </div>}
    </div>;
  };

  // Phase R-8 — renderPayouts / renderIncentive removed. Payouts and incentive
  // recipients are now displayed and edited inside the unified Edit Collection modal.

  // "View Deal" — hand the Deals page a LEAD ID, not a lead object.
  //
  // This used to look the lead up in p.leads and pass the object to p.nav. p.leads
  // is the 100-newest-created bootstrap, so for any deal outside those 100 the
  // lookup returned undefined, nav was called with null, and the Deals page opened
  // with nothing selected — silently, because passing null is a legal call.
  // Measured on production: 54 deal rows against a 100-row bootstrap that is not
  // even sorted by deal date, so the hit rate was incidental.
  //
  // The id form has no such failure: DealsPage already holds the whole book
  // (dealAllData) and resolves against that. Falls back to the old nav only if a
  // host somehow did not supply the navigator.
  var goToDeal = function(c){
    var leadId = c && c.leadId;
    if (!leadId) return;
    if (p.navigateToDeal) { p.navigateToDeal(String(leadId)); return; }
    if (p.setPage) p.setPage("deals");
  };

  var renderCard = function(c){
    var isCancelled = c.status === "cancelled";
    var isFullyPaid = c.status === "fully_paid";
    var snap = c.snapshot || {};
    var sortedCycles = (c.cycles || []).slice().sort(function(a, b){
      return (Number(a.cycleNumber) || 0) - (Number(b.cycleNumber) || 0);
    });
    var totalReceived = sortedCycles.reduce(function(s, cy){ return s + Number(cy.receivedAmount || 0); }, 0);
    var imminent = !!c._imminentMissing;   // server-computed; see the note above imminentCount
    // Server-computed too, and deliberately the SAME VALUE the backend sorted
    // this list by — not a re-derivation. commissionSortStages uses _futureDated
    // as its primary sort key and lets it survive $unset precisely so the badge
    // and the demotion cannot disagree. Recomputing it here from snap.dealDate
    // would be a second definition, and it would be WRONG on every status tab:
    // the flag describes whichever key that tab sorts on (the claim date on the
    // Claim tab, the received date on Received), not the close date.
    var futureDated = !!c._futureDated;

    // Per-recipient cash-out totals on this commission. Keyed by userName
    // (the only recipient identifier on the payout subdoc). Uses netPaid so
    // debt-deducted amounts don't inflate the breakdown.
    var paidByName = (c.payouts || []).reduce(function(acc, po){
      var k = po.recipientUserName || "(unknown)";
      acc[k] = (acc[k] || 0) + Number(po.netPaid || 0);
      return acc;
    }, {});

    // The 💰 deep-link's target. These cards do not collapse, so "selected" can
    // only mean a visible one — without it the page scrolls somewhere and the
    // user has to work out which of the identical cards they were sent to.
    var isDeepLinked = !!selectedCommissionId && String(c._id) === selectedCommissionId;
    return <div id={"commission-card-" + String(c._id)} key={String(c._id)} style={{
      background:"#fff", borderRadius:14,
      border:"1px solid " + (isDeepLinked ? C.accent : (imminent ? "#FCA5A5" : "#E8ECF1")),
      boxShadow: isDeepLinked ? "0 0 0 3px " + C.accent + "33, 0 1px 4px rgba(0,0,0,0.07)"
                              : "0 1px 4px rgba(0,0,0,0.07)",
      padding:"14px 16px", marginBottom:12,
      opacity: isCancelled ? 0.6 : 1
    }}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, marginBottom:10 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:4 }}>
            <span style={{ fontSize:15, fontWeight:700, color:C.text, textDecoration: isCancelled ? "line-through" : "none" }}>
              {snap.customerName || "(unknown)"}
            </span>
            {/* Developer (D4) — prefer the live linked name (c.developer.name, joined
                on the commissions endpoint via the lead's developerId); fall back to
                the frozen snapshot.developer for deals not yet linked (Option A). */}
            {((c.developer && c.developer.name) || snap.developer) && <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#EFF6FF", color:"#1D4ED8", fontWeight:700 }}>{(c.developer && c.developer.name) || snap.developer}</span>}
            {snap.isSplitDeal && <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#F5F3FF", color:"#7C3AED", fontWeight:700 }}>🤝 Split</span>}
            {/* DEAL CHANGED AFTER A CLAIM (owner ruling 2026-09-12): a deal edit after a
    claim was submitted never moves money here — it flags the commission for
    review; the owner adjusts via the claim-data modal, which clears the flag. */}
            {c.dealChangedAfterClaim && <span title={"Deal #" + (c.dealChangedAfterClaim.recordNo || "") + " changed after a claim: " + (c.dealChangedAfterClaim.fields || []).join(", ") + " — review the claim data"} style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#FEF3C7", color:"#B45309", fontWeight:700 }}>⚠️ Deal changed after claim — review</span>}
            {/* Phase R-12 Part 6 — External badge. Distinct violet tint to
                differentiate from the amber/green/gray status badges below.
                Only shows when commission.externalSplit.isExternal === true. */}
            {c.externalSplit && c.externalSplit.isExternal && <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#EDE9FE", color:"#5B21B6", fontWeight:700 }}>🤝 External</span>}
            {/* Phase R-14 — Ambassador badge (amber). The classification is
                internal to admin/operation; the explicit role check is
                defense-in-depth on top of the page-level admin gate. */}
            {c.ambassadorSplit && c.ambassadorSplit.isAmbassador && (p.cu.role==="admin"||p.cu.role==="operation") && <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#FEF3C7", color:"#B45309", fontWeight:700 }}>🎯 Ambassador</span>}
            {/* Two-party Resale — tax-free, direct-collected; partySide distinguishes
                the seller-side vs buyer-side commission on the same lead. */}
            {c.snapshot && c.snapshot.saleType==="Resale" && <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#FEE2E2", color:"#B91C1C", fontWeight:700 }}>🔁 Resale{c.partySide||c.snapshot.partySide?(" · "+(c.partySide||c.snapshot.partySide)):""}</span>}
            {/* Phase R-7 — commission status badges. Active = amber (in-progress
                book), Fully paid = green (closed), Cancelled = gray (dead).
                The Revert button only shows for status=fully_paid AND admin role. */}
            {c.status === "active" && <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#FEF3C7", color:"#B45309", fontWeight:700 }}>Active</span>}
            {isFullyPaid && <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#DCFCE7", color:"#15803D", fontWeight:700 }}>Fully paid</span>}
            {isFullyPaid && p.cu && p.cu.role === "admin" && <button
              onClick={function(){ setRevertTarget(c); setRevertConfirm(""); }}
              disabled={savingFlag}
              title="Manually revert this commission back to Active (admin-only correction tool)"
              style={{
                fontSize:10, padding:"2px 8px", borderRadius:10,
                border:"1px solid #FCA5A5", background:"#FFF", color:"#B91C1C",
                fontWeight:700, cursor: savingFlag ? "wait" : "pointer"
              }}
            >↺ Revert to Active</button>}
            {isCancelled && <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#E5E7EB", color:"#4B5563", fontWeight:700 }}>Cancelled</span>}
            {imminent && <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#FEE2E2", color:"#B91C1C", fontWeight:700 }}>⏰ collection due</span>}
            {/* Future-dated (2026-08-21). This row sorts BELOW everything that
                has already happened, so without a badge its position reads as an
                unexplained reshuffle. Slate rather than red: a forward date is
                usually a typo to correct, not an emergency. The title names the
                tab's own key, because on the Claim tab this is the claim date,
                not the close date. */}
            {futureDated && <span
              title="This row's date is in the future, so it sorts below everything that has already happened. Check the date on the tab you are viewing — the All tab reads the close date, each status tab reads its own stage date."
              style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#F1F5F9", color:"#475569", fontWeight:700, border:"1px solid #CBD5E1" }}
            >🔮 future-dated</span>}
          </div>
          <div style={{ fontSize:12, color:C.textLight }}>
            {/* The COMMISSION's own code, stamped from the action it belongs to
                (Commission.recordNo). It used to render the client's leadId via a
                join that no longer exists: a commission is one action's money, so
                labelling it with the client's number pointed at the wrong thing
                the moment that client bought twice. A pre-record-layer commission
                with no code reads "No code", which is true. */}
            {canSeeLeadIds(p.cu) && <span>{formatLeadId(c.recordNo)} · </span>}
            {snap.projectName || "—"}
            {snap.unitDetails && <span> · {snap.unitDetails}</span>}
            <span> · closed {fmtDayOnly(snap.dealDate || c.effectiveDealDate) || "—"}</span>
            {/* Feature B — closing company (admin/SA only; data attached by the
                /api/commissions leadId→closingCompany join). */}
            {canSeeClosingCompany(p.cu) && c.closingCompany && c.closingCompany.name && <span> · Closed via <b style={{ color:C.text }}>{c.closingCompany.name}</b></span>}
            {/* Phase R-12 Part 6 — broker subtitle. brokerName is a snapshot
                from commission-creation time (Part 5), so a later Broker
                rename never rewrites this line — it preserves who actually
                brought the deal at close. */}
            {c.externalSplit && c.externalSplit.isExternal && c.externalSplit.brokerName && <span> · External via <b style={{ color:"#5B21B6" }}>{c.externalSplit.brokerName}</b></span>}
            {/* Phase R-14 — ambassador subtitle. Classification is internal to
                admin/operation and, since 2026-11-02, the financeReads role. */}
            {c.ambassadorSplit && c.ambassadorSplit.isAmbassador && (p.cu.role==="admin"||p.cu.role==="operation"||roleHasFinanceReadsFE(p.cu)) && <span> · <b style={{ color:"#B45309" }}>Ambassador deal · Commission {Number(c.ambassadorSplit.commissionRate || 0)}%</b></span>}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
          <div style={{ fontSize:18, fontWeight:700, color:C.success }}>{fmtMoney(snap.dealTotal)}</div>
          {!isCancelled && <button onClick={function(){ setEditingCollection(c); }} disabled={savingFlag} title="Edit Collection" style={{
            width:28, height:28, borderRadius:6, border:"1px solid #E2E8F0", background:"#fff",
            cursor: savingFlag ? "wait" : "pointer", display:"flex", alignItems:"center", justifyContent:"center"
          }}><Edit size={13} color={C.info}/></button>}
        </div>
      </div>

      {/* Phase R-8 — compact two-card stats row. Expected collection removed
          per spec; only Received-so-far + Cycle count remain. */}
      <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:10 }}>
        <div style={{ flex:1, minWidth:140, background:"#F8FAFC", padding:"8px 10px", borderRadius:8 }}>
          <div style={{ fontSize:10, color:C.textLight }}>Received so far</div>
          <div style={{ fontSize:13, fontWeight:700, color:C.success }}>{fmtMoney(totalReceived)}</div>
        </div>
        <div style={{ flex:1, minWidth:140, background:"#F8FAFC", padding:"8px 10px", borderRadius:8 }}>
          <div style={{ fontSize:10, color:C.textLight }}>Cycles</div>
          <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{(c.cycles || []).length}</div>
        </div>
      </div>

      {/* Phase R-9 — Recipients block with per-recipient Owed / Paid /
          Remaining breakdown + Incentive sub-section. Defaults to collapsed;
          clicking the header toggles BOTH sub-sections together. Header label
          shows both counts when incentives exist.
          Match key for "Paid" is payouts[].recipientUserName (the only
          recipient identifier on the payout subdoc); sum uses netPaid (gross
          minus appliedToDebt) to match the existing paidByName convention. */}
      {(function(){
        // Phase R-14 — ambassador recipients branch. The snapshot chain is null
        // for ambassador deals, so the standard rows below would render empty
        // ("No recipients on snapshot"). Render the ambassadorSplit.recipients[]
        // list instead, with a Gross/Tax/ARO-Net summary + Team Total / ARO Keep
        // footer. The whole block lives on the admin/operation-only page.
        if (c.ambassadorSplit && c.ambassadorSplit.isAmbassador) {
          var amb = c.ambassadorSplit;
          var aRecips = amb.recipients || [];
          var nAmb = function(v){ var num = Number(v||0); return (!isFinite(num)||num===0) ? "0" : Math.round(num).toLocaleString(); };
          var teamTotal = aRecips.reduce(function(s, r){ return s + Number(r.amount || 0); }, 0);
          var aroNetAmb = Number(amb.aroNetTotal || 0);
          var aroKeepAmb = aroNetAmb - teamTotal;
          var roleLbl = function(role){ return role === "team_leader" ? "Team Lead" : role === "manager" ? "Manager" : role === "director" ? "Director" : "Sales"; };
          var expandedA = !!expandedRecipients[String(c._id)];
          var toggleA = function(){
            setExpandedRecipients(function(prev){
              var next = Object.assign({}, prev);
              if (next[String(c._id)]) delete next[String(c._id)]; else next[String(c._id)] = true;
              return next;
            });
          };
          var headerHoveredA = hoveredRecipientHeader === String(c._id);
          return <div style={{ marginBottom:10 }}>
            <div onClick={toggleA}
              onMouseEnter={function(){ setHoveredRecipientHeader(String(c._id)); }}
              onMouseLeave={function(){ setHoveredRecipientHeader(null); }}
              style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 10px", margin:"0 -10px", borderRadius:8, cursor:"pointer", userSelect:"none", background: headerHoveredA ? "#F1F5F9" : (expandedA ? "#F9FAFB" : "transparent"), transition:"background 0.15s ease" }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.text, textTransform:"uppercase", letterSpacing:0.3 }}>
                Recipients ({aRecips.length})
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:6, color:C.text }}>
                <span style={{ fontSize:11, fontWeight:600 }}>{expandedA ? "Hide" : "Show"}</span>
                <ChevronDown size={18} style={{ transform: expandedA ? "rotate(0deg)" : "rotate(-90deg)", transition:"transform 0.2s ease" }}/>
              </div>
            </div>
            <div style={{ overflow:"hidden", transition:"max-height 0.25s ease", maxHeight: expandedA ? 1800 : 0 }}>
              <div style={{ paddingTop:6 }}>
                {/* AMBASSADOR SPLIT summary (amber) */}
                <div style={{ fontSize:10, fontWeight:700, color:"#B45309", textTransform:"uppercase", letterSpacing:0.3, padding:"2px 0 6px" }}>Ambassador Split</div>
                <div style={{ border:"1px solid #FDE68A", borderRadius:10, background:"#FFFBEB", padding:"8px 12px", marginBottom:12, fontSize:11, color:"#92400E", lineHeight:1.8 }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:C.textLight }}>Gross Commission</span><b style={{ color:C.text }}>{nAmb(amb.grossCommission)} EGP</b></div>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:C.textLight }}>Developer Tax{amb.developerTaxEnabled ? " (" + Number(amb.developerTaxRate || 0) + "%)" : ""}</span><b style={{ color:"#B45309" }}>{amb.developerTaxEnabled ? "−" : ""}{nAmb(amb.developerTaxAmount)} EGP</b></div>
                  <div style={{ display:"flex", justifyContent:"space-between", borderTop:"1px solid #FDE68A", marginTop:4, paddingTop:4 }}><span style={{ fontWeight:700, color:"#B45309" }}>ARO Net Total</span><b style={{ color:"#B45309" }}>{nAmb(amb.aroNetTotal)} EGP</b></div>
                </div>
                {aRecips.length === 0 && <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", padding:"6px 0" }}>No recipients yet — assign team commissions via Edit Collection.</div>}
                {aRecips.length > 0 && <div style={{ border:"1px solid #E2E8F0", borderRadius:10, overflow:"hidden", background:"#fff" }}>
                  {aRecips.map(function(r, idx){
                    var owed = Number(r.amount || 0);
                    var paid = Number(r.amountPaid || 0);
                    var remaining = Math.max(0, owed - paid);
                    var overpaid = paid > owed && owed > 0;
                    var fullyPaid = !overpaid && owed > 0 && remaining === 0;
                    var notStarted = paid === 0 && owed > 0;
                    var rowBg = fullyPaid ? "#F0FDF4" : notStarted ? "#FFFBEB" : "#fff";
                    var statusIcon = overpaid ? "⚠️" : fullyPaid ? "✅" : owed > 0 ? "🔴" : "—";
                    var remColor = overpaid ? "#B91C1C" : fullyPaid ? "#15803D" : C.text;
                    var remValue = overpaid ? ("+" + nAmb(paid - owed)) : nAmb(remaining);
                    return <div key={String(r._id) || idx} style={{ padding:"8px 12px", borderTop: idx > 0 ? "1px solid #F1F5F9" : "none", background: rowBg }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4, gap:8 }}>
                        <div style={{ display:"flex", gap:8, alignItems:"baseline", minWidth:0, flexWrap:"wrap" }}>
                          <span style={{ fontSize:10, fontWeight:700, color:C.textLight, textTransform:"uppercase", minWidth:72 }}>{roleLbl(r.userRole)}</span>
                          <span style={{ fontSize:12, fontWeight:700, color:C.text }}>{r.userName || "(unknown)"}</span>
                        </div>
                        <span style={{ fontSize:12 }}>{statusIcon}</span>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, fontSize:11, marginInlineStart:80 }}>
                        <div style={{ textAlign:"right" }}><span style={{ color:C.textLight }}>Owed:&nbsp;</span><span style={{ color:C.text }}>{nAmb(owed)}</span></div>
                        <div style={{ textAlign:"right" }}><span style={{ color:C.textLight }}>Paid:&nbsp;</span><span style={{ color:C.text }}>{nAmb(paid)}</span></div>
                        <div style={{ textAlign:"right" }}><span style={{ color:C.textLight }}>Rem:&nbsp;</span><span style={{ color:remColor, fontWeight:700 }}>{remValue}</span></div>
                      </div>
                    </div>;
                  })}
                </div>}
                {/* Team Total / ARO Keep footer */}
                <div style={{ marginTop:10, padding:"8px 12px", background:"#F8FAFC", borderRadius:8, fontSize:12, fontWeight:600, color:C.text, display:"flex", justifyContent:"space-between" }}>
                  <span>Total to team: <b>{nAmb(teamTotal)} EGP</b></span>
                  <span style={{ color: aroKeepAmb < 0 ? "#B91C1C" : "#B45309" }}>ARO keeps: <b>{nAmb(aroKeepAmb)} EGP</b></span>
                </div>
              </div>
            </div>
          </div>;
        }
        var primarySlots = [
          ["salesAgent", "Sales",       snap.salesAgent],
          ["teamLeader", "Team Lead",   snap.teamLeader],
          ["manager",    "Manager",     snap.manager],
          ["director",   "Director",    snap.director]
        ];
        if (snap.isSplitDeal && snap.splitChain) {
          primarySlots.push(["salesAgent2", "Sales 2",     snap.splitChain.salesAgent2]);
          primarySlots.push(["teamLeader2", "Team Lead 2", snap.splitChain.teamLeader2]);
          primarySlots.push(["manager2",    "Manager 2",   snap.splitChain.manager2]);
          primarySlots.push(["director2",   "Director 2",  snap.splitChain.director2]);
        }
        // Phase R-13 — Per-deal recipient overrides. Three actions:
        //   manual_remove → skip the chain slot entirely.
        //   manual_zero   → render the slot but with computedShare = 0.
        //   manual_add    → extra rows appended below (off-chain).
        var allOverrides = Array.isArray(c.recipientOverrides) ? c.recipientOverrides : [];
        var overridesBySlot = {};
        allOverrides.forEach(function(o){
          if (o && o.targetSlot && o.targetSlot !== "extra") overridesBySlot[o.targetSlot] = o;
        });
        var manualAddOverrides = allOverrides.filter(function(o){ return o && o.source === "manual_add"; });
        var rows = primarySlots.filter(function(t){ return !!t[2]; }).map(function(t){
          var r = t[2];
          var ov = overridesBySlot[t[0]];
          var removed = ov && ov.source === "manual_remove";
          var zeroed  = ov && ov.source === "manual_zero";
          var hasOverride = r.overrideAmount != null && Number(r.overrideAmount) > 0;
          var owed = removed ? 0 : (zeroed ? 0 : (hasOverride ? Number(r.overrideAmount) : Number(r.computedShare || 0)));
          var paid = Number(paidByName[r.userName] || 0);
          var remaining = Math.max(0, owed - paid);
          var overpaid = paid > owed && owed > 0;
          var fullyPaid = !overpaid && owed > 0 && remaining === 0;
          var notStarted = paid === 0 && owed > 0;
          return {
            key: t[0], label: t[1], r: r,
            owed: owed, paid: paid, remaining: remaining,
            overpaid: overpaid, fullyPaid: fullyPaid, notStarted: notStarted,
            isSplit: t[0].indexOf("2") > -1,
            override: ov || null,
            removed: !!removed, zeroed: !!zeroed
          };
        }).filter(function(x){ return !x.removed; });
        // Append off-chain manual_add overrides as extra rows.
        manualAddOverrides.forEach(function(ov, idx){
          var owed = Number(ov.computedShare || 0);
          var paid = Number(paidByName[ov.name] || 0);
          var remaining = Math.max(0, owed - paid);
          rows.push({
            key: "extra-" + (ov._id ? String(ov._id) : idx),
            label: "Extra · " + (ov.role || "sales"),
            r: { userName: ov.name, role: ov.role || "sales", computedShare: owed, rate: 0, rateBucket: "manual", isHalfWeight: false, overrideAmount: null },
            owed: owed, paid: paid, remaining: remaining,
            overpaid: paid > owed && owed > 0,
            fullyPaid: !(paid > owed && owed > 0) && owed > 0 && remaining === 0,
            notStarted: paid === 0 && owed > 0,
            isSplit: false,
            override: ov, removed: false, zeroed: false, isExtra: true
          });
        });
        var totals = rows.reduce(function(acc, x){
          acc.owed += x.owed; acc.paid += x.paid; acc.remaining += x.remaining;
          return acc;
        }, { owed:0, paid:0, remaining:0 });
        var incentives = (c.incentive && Array.isArray(c.incentive.recipients)) ? c.incentive.recipients : [];

        var recipientCount = rows.length;
        var incentiveCount = incentives.length;
        var expanded = !!expandedRecipients[String(c._id)];
        var toggle = function(){
          setExpandedRecipients(function(prev){
            var next = Object.assign({}, prev);
            if (next[String(c._id)]) delete next[String(c._id)]; else next[String(c._id)] = true;
            return next;
          });
        };

        // Inline number formatter — strips " EGP" suffix from the page-level
        // fmtMoney since we render the breakdown numbers without per-cell units.
        var n2 = function(v){
          var num = Number(v || 0);
          if (!isFinite(num) || num === 0) return "0";
          return Math.round(num).toLocaleString();
        };

        var headerHovered = hoveredRecipientHeader === String(c._id);
        return <div style={{ marginBottom:10 }}>
          <div onClick={toggle}
            onMouseEnter={function(){ setHoveredRecipientHeader(String(c._id)); }}
            onMouseLeave={function(){ setHoveredRecipientHeader(null); }}
            style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              padding:"8px 10px", margin:"0 -10px",
              borderRadius:8,
              cursor:"pointer", userSelect:"none",
              background: headerHovered ? "#F1F5F9" : (expanded ? "#F9FAFB" : "transparent"),
              transition:"background 0.15s ease"
            }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.text, textTransform:"uppercase", letterSpacing:0.3 }}>
              Recipients ({recipientCount}){incentiveCount > 0 && <span> · Incentives ({incentiveCount})</span>}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:6, color:C.text }}>
              <span style={{ fontSize:11, fontWeight:600 }}>{expanded ? "Hide" : "Show"}</span>
              <ChevronDown size={18} style={{
                transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                transition:"transform 0.2s ease"
              }}/>
            </div>
          </div>
          <div style={{ overflow:"hidden", transition:"max-height 0.25s ease", maxHeight: expanded ? 1800 : 0 }}>
            <div style={{ paddingTop:6 }}>
              {/* Phase R-12 Part 6 — External Split sub-section. Renders first
                  (above the internal team table) when commission.externalSplit
                  .isExternal. Two rows: Broker (with Owed/Paid/Remaining) and
                  ARO (informational only — no actions). Tax footnote below. */}
              {c.externalSplit && c.externalSplit.isExternal && (function(){
                var es = c.externalSplit;
                // Phase R-13.2 — display brokerNetOwed when present (paidBy
                // adjusts the take-home). Pre-13.2 docs have no brokerNetOwed;
                // fall back to brokerOwed.
                var brokerGross = Number(es.brokerOwed || 0);
                var brokerOwed = (es.brokerNetOwed != null) ? Number(es.brokerNetOwed) : brokerGross;
                var paidByBroker = String(es.salesAgentPaidBy || "company") === "broker";
                var brokerPaid = (es.brokerPayouts || []).reduce(function(s, bp){ return s + Number(bp.amount || 0); }, 0);
                var brokerRem  = Math.max(0, brokerOwed - brokerPaid);
                var brokerOver = brokerPaid > brokerOwed && brokerOwed > 0;
                var brokerFull = !brokerOver && brokerOwed > 0 && brokerRem === 0;
                var brokerNot  = brokerPaid === 0 && brokerOwed > 0;
                var aroOwed    = Number(es.aroOwed || 0);
                var taxAmt     = Number(es.commissionTaxAmount || 0);
                var taxPct     = Number(es.commissionTaxPct || 0);
                var brokerRowBg = brokerFull ? "#F0FDF4" : brokerNot ? "#FFFBEB" : "#fff";
                var brokerIcon  = brokerOver ? "⚠️" : brokerFull ? "✅" : "🔴";
                var brokerRemColor = brokerOver ? "#B91C1C" : brokerFull ? "#15803D" : C.text;
                var brokerRemValue = brokerOver ? ("+" + n2(brokerPaid - brokerOwed)) : n2(brokerRem);
                var brokerSubLine = paidByBroker
                  ? "Gross " + n2(brokerGross) + " − sales " + n2(Math.max(0, brokerGross - brokerOwed)) + " (paid by broker)"
                  : "";
                return <div style={{ marginBottom: rows.length > 0 ? 12 : 6 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#5B21B6", textTransform:"uppercase", letterSpacing:0.3, padding:"2px 0 6px" }}>
                    External Split
                  </div>
                  <div style={{ border:"1px solid #DDD6FE", borderRadius:10, overflow:"hidden", background:"#fff" }}>
                    {/* Broker row (with payout state) */}
                    <div style={{ padding:"8px 12px", background: brokerRowBg }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4, gap:8 }}>
                        <div style={{ display:"flex", gap:8, alignItems:"baseline", minWidth:0, flexWrap:"wrap" }}>
                          <span style={{ fontSize:10, fontWeight:700, color:C.textLight, textTransform:"uppercase", minWidth:72 }}>Broker</span>
                          <span style={{ fontSize:12, fontWeight:700, color:C.text }}>{es.brokerName || "(unnamed)"}</span>
                          {paidByBroker && <span style={{ fontSize:9, padding:"1px 5px", borderRadius:6, background:"#EDE9FE", color:"#5B21B6", fontWeight:700 }}>pays sales</span>}
                        </div>
                        <span style={{ fontSize:12 }}>{brokerIcon}</span>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, fontSize:11, marginInlineStart:80 }}>
                        <div style={{ textAlign:"right" }}><span style={{ color:C.textLight }}>Owed:&nbsp;</span><span style={{ color:C.text }}>{n2(brokerOwed)}</span></div>
                        <div style={{ textAlign:"right" }}><span style={{ color:C.textLight }}>Paid:&nbsp;</span><span style={{ color:C.text }}>{n2(brokerPaid)}</span></div>
                        <div style={{ textAlign:"right" }}><span style={{ color:C.textLight }}>Rem:&nbsp;</span><span style={{ color:brokerRemColor, fontWeight:700 }}>{brokerRemValue}</span></div>
                      </div>
                      {brokerSubLine && <div style={{ fontSize:10, color:C.textLight, marginTop:3, marginInlineStart:80, fontStyle:"italic" }}>{brokerSubLine}</div>}
                    </div>
                    {/* ARO informational row — no actions, grayed-out */}
                    <div style={{ padding:"8px 12px", borderTop:"1px solid #F1F5F9", background:"#F8FAFC" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4, gap:8 }}>
                        <div style={{ display:"flex", gap:8, alignItems:"baseline", minWidth:0, flexWrap:"wrap" }}>
                          <span style={{ fontSize:10, fontWeight:700, color:C.textLight, textTransform:"uppercase", minWidth:72 }}>ARO</span>
                          <span style={{ fontSize:12, fontWeight:700, color:C.textLight }}>(company keep)</span>
                        </div>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, fontSize:11, marginInlineStart:80 }}>
                        <div style={{ textAlign:"right" }}><span style={{ color:C.textLight }}>Owed:&nbsp;</span><span style={{ color:C.text }}>{n2(aroOwed)}</span></div>
                        <div style={{ textAlign:"right", color:"#CBD5E1" }}>—</div>
                        <div style={{ textAlign:"right", color:"#CBD5E1" }}>—</div>
                      </div>
                    </div>
                  </div>
                  {/* Tax footnote */}
                  <div style={{ fontSize:10, color:C.textLight, padding:"6px 4px 0", fontStyle:"italic" }}>
                    Commission tax: {taxPct}% = {n2(taxAmt)} EGP (accounting adjustment, not paid to anyone).
                  </div>
                </div>;
              })()}
              {/* Primary recipients table — for external deals, shares are
                  computed against aroOwed (Part 5 server-side); the sub-heading
                  reflects that so admins understand the smaller numbers. */}
              {c.externalSplit && c.externalSplit.isExternal && rows.length > 0 && <div style={{ fontSize:10, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, padding:"2px 0 6px" }}>
                Internal team share (out of ARO's {n2(Number(c.externalSplit.aroOwed || 0))} EGP)
              </div>}
              {rows.length === 0 && (!(c.externalSplit && c.externalSplit.isExternal)) && <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", padding:"6px 0" }}>No recipients on snapshot.</div>}
              {rows.length === 0 && (c.externalSplit && c.externalSplit.isExternal) && <div style={{ fontSize:12, color:C.textLight, fontStyle:"italic", padding:"6px 0" }}>No internal recipients — broker-only deal.</div>}
              {rows.length > 0 && <div style={{ border:"1px solid #E2E8F0", borderRadius:10, overflow:"hidden", background:"#fff" }}>
                {rows.map(function(x, idx){
                  var rowBg = x.fullyPaid ? "#F0FDF4" : x.notStarted ? "#FFFBEB" : "#fff";
                  var statusIcon = x.overpaid ? "⚠️" : x.fullyPaid ? "✅" : "🔴";
                  var remColor   = x.overpaid ? "#B91C1C" : x.fullyPaid ? "#15803D" : C.text;
                  var remValue   = x.overpaid ? ("+" + n2(x.paid - x.owed)) : n2(x.remaining);
                  return <div key={x.key} style={{
                    padding:"8px 12px",
                    borderTop: idx > 0 ? "1px solid #F1F5F9" : "none",
                    background: rowBg
                  }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4, gap:8 }}>
                      <div style={{ display:"flex", gap:8, alignItems:"baseline", minWidth:0, flexWrap:"wrap" }}>
                        <span style={{ fontSize:10, fontWeight:700, color:C.textLight, textTransform:"capitalize", minWidth:72 }}>{x.label}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:C.text }}>{x.r.userName || "(unknown)"}</span>
                        {x.r.userActiveAtClose === false && <span title="User deactivated" style={{ fontSize:9, padding:"1px 5px", borderRadius:6, background:"#FEE2E2", color:"#B91C1C", fontWeight:700 }}>⊘ inactive</span>}
                        {x.r.overrideAmount != null && Number(x.r.overrideAmount) > 0 && <span title={"Override: " + (x.r.overrideReason || "no reason")} style={{ fontSize:9, padding:"1px 5px", borderRadius:6, background:"#FDF4FF", color:"#86198F", fontWeight:700 }}>override</span>}
                        {x.isSplit && <span style={{ fontSize:9, padding:"1px 5px", borderRadius:6, background:"#F5F3FF", color:"#7C3AED", fontWeight:700 }}>split</span>}
                        {x.zeroed && <span title={"Manually zeroed by " + (x.override.addedBy || "admin") + " on " + (x.override.addedAt ? new Date(x.override.addedAt).toLocaleDateString("en-GB") : "—")} style={{ fontSize:9, padding:"1px 5px", borderRadius:6, background:"#FEF3C7", color:"#92400E", fontWeight:700 }}>manually zeroed</span>}
                        {x.isExtra && <span title={"Added by " + (x.override.addedBy || "admin") + " on " + (x.override.addedAt ? new Date(x.override.addedAt).toLocaleDateString("en-GB") : "—")} style={{ fontSize:9, padding:"1px 5px", borderRadius:6, background:"#EDE9FE", color:"#5B21B6", fontWeight:700 }}>manually added</span>}
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <span style={{ fontSize:12 }}>{statusIcon}</span>
                        {c.status !== "cancelled" && (x.isExtra
                          ? <button type="button" title="Remove this manual recipient"
                              onClick={async function(){
                                if (!x.override || !x.override._id) return;
                                if (!window.confirm("Remove manual recipient '" + (x.r.userName || "") + "' from this deal?")) return;
                                try { await writeCommission("DELETE", "/api/commissions/" + c._id + "/recipients/" + x.override._id, null, "Manual recipient removed"); } catch(_){}
                              }}
                              style={{ fontSize:10, padding:"2px 8px", borderRadius:6, border:"1px solid #FCA5A5", background:"#FEF2F2", color:"#B91C1C", cursor:"pointer", fontWeight:600 }}>
                              ✕ Remove
                            </button>
                          : <>
                              {x.zeroed
                                ? <button type="button" title="Restore this recipient's computed share"
                                    onClick={async function(){
                                      if (!x.override || !x.override._id) return;
                                      try { await writeCommission("DELETE", "/api/commissions/" + c._id + "/recipients/" + x.override._id, null, "Override removed"); } catch(_){}
                                    }}
                                    style={{ fontSize:10, padding:"2px 8px", borderRadius:6, border:"1px solid #A7F3D0", background:"#ECFDF5", color:"#065F46", cursor:"pointer", fontWeight:600 }}>
                                    ↶ Restore
                                  </button>
                                : <button type="button" title="Set this recipient's share to 0 for this deal"
                                    onClick={async function(){
                                      if (!window.confirm("Set " + x.label + " (" + (x.r.userName || "") + ") share to 0 for this deal?")) return;
                                      try { await writeCommission("PATCH", "/api/commissions/" + c._id + "/recipients/" + x.key + "/zero", {}, "Set to zero"); } catch(_){}
                                    }}
                                    style={{ fontSize:10, padding:"2px 8px", borderRadius:6, border:"1px solid #FDE68A", background:"#FFFBEB", color:"#92400E", cursor:"pointer", fontWeight:600 }}>
                                    🚫 Zero
                                  </button>}
                              <button type="button" title="Exclude this recipient from this deal entirely"
                                onClick={async function(){
                                  if (!window.confirm("Remove " + x.label + " (" + (x.r.userName || "") + ") from this deal? They will not appear on the payout list.")) return;
                                  try { await writeCommission("PATCH", "/api/commissions/" + c._id + "/recipients/" + x.key + "/remove", {}, "Recipient removed from deal"); } catch(_){}
                                }}
                                style={{ fontSize:10, padding:"2px 8px", borderRadius:6, border:"1px solid #FCA5A5", background:"#FEF2F2", color:"#B91C1C", cursor:"pointer", fontWeight:600 }}>
                                ✕ Remove
                              </button>
                            </>
                        )}
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, fontSize:11, marginInlineStart:80 }}>
                      <div style={{ textAlign:"right" }}>
                        <span style={{ color:C.textLight }}>Owed:&nbsp;</span>
                        <span style={{ color:C.text }}>{n2(x.owed)}</span>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <span style={{ color:C.textLight }}>Paid:&nbsp;</span>
                        <span style={{ color:C.text }}>{n2(x.paid)}</span>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <span style={{ color:C.textLight }}>Rem:&nbsp;</span>
                        <span style={{ color:remColor, fontWeight:700 }}>{remValue}</span>
                      </div>
                    </div>
                  </div>;
                })}
                {/* Totals row */}
                <div style={{
                  padding:"8px 12px",
                  borderTop:"2px solid #CBD5E1",
                  background:"#F8FAFC",
                  display:"grid",
                  gridTemplateColumns:"80px 1fr 1fr 1fr",
                  gap:6,
                  fontSize:11,
                  alignItems:"baseline"
                }}>
                  <span style={{ fontSize:10, fontWeight:700, color:C.textLight, textTransform:"uppercase" }}>Total</span>
                  <span style={{ textAlign:"right" }}>
                    <span style={{ color:C.textLight }}>Owed:&nbsp;</span>
                    <span style={{ color:C.text, fontWeight:700 }}>{n2(totals.owed)}</span>
                  </span>
                  <span style={{ textAlign:"right" }}>
                    <span style={{ color:C.textLight }}>Paid:&nbsp;</span>
                    <span style={{ color:C.text, fontWeight:700 }}>{n2(totals.paid)}</span>
                  </span>
                  <span style={{ textAlign:"right" }}>
                    <span style={{ color:C.textLight }}>Rem:&nbsp;</span>
                    <span style={{ color: totals.remaining === 0 ? "#15803D" : C.text, fontWeight:700 }}>{n2(totals.remaining)}</span>
                  </span>
                </div>
              </div>}

              {/* Phase R-13 — Add Recipient button */}
              {c.status !== "cancelled" && <div style={{ marginTop:8 }}>
                <button type="button" onClick={function(){
                  setAddRecipName(""); setAddRecipRole("sales"); setAddRecipAmount(""); setAddRecipUserId("");
                  setAddRecipientFor(c);
                }} style={{ padding:"6px 12px", borderRadius:8, border:"1px dashed " + C.accent, background:C.accent+"08", color:C.accent, fontSize:11, fontWeight:600, cursor:"pointer" }}>
                  + Add manual recipient
                </button>
              </div>}

              {/* Incentive sub-section */}
              {incentiveCount > 0 && <div style={{ marginTop:8 }}>
                <div style={{ fontSize:10, fontWeight:700, color:C.textLight, textTransform:"uppercase", padding:"4px 0", marginBottom:4 }}>
                  Incentive Recipients ({incentiveCount})
                </div>
                <div style={{ border:"1px solid #E2E8F0", borderRadius:10, overflow:"hidden", background:"#fff" }}>
                  {incentives.map(function(ir, i){
                    var isPaid = ir.status === "received";
                    var rowBg  = isPaid ? "#F0FDF4" : "#FFFBEB";
                    var statusBg = isPaid ? "#DCFCE7" : "#FEF9C3";
                    var statusFg = isPaid ? "#15803D" : "#B45309";
                    var statusLabel = isPaid ? ("Paid · " + (ir.receivedDate ? new Date(ir.receivedDate).toLocaleDateString("en-GB") : "—")) : "Pending";
                    var roleLabel = String(ir.role || "").replace(/_/g, " ");
                    return <div key={i} style={{
                      padding:"8px 12px",
                      borderTop: i > 0 ? "1px solid #F1F5F9" : "none",
                      background: rowBg,
                      display:"flex",
                      justifyContent:"space-between",
                      alignItems:"center",
                      gap:8,
                      flexWrap:"wrap"
                    }}>
                      <div style={{ display:"flex", gap:8, alignItems:"baseline", minWidth:0, flexWrap:"wrap" }}>
                        <span style={{ fontSize:10, fontWeight:700, color:C.textLight, textTransform:"capitalize", minWidth:72 }}>{roleLabel || "—"}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:C.text }}>{ir.userName || "(unknown)"}</span>
                        <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:statusBg, color:statusFg, fontWeight:700 }}>{statusLabel}</span>
                      </div>
                      <span style={{ fontSize:12, fontWeight:700, color:C.success }}>{n2(ir.amount)} EGP</span>
                    </div>;
                  })}
                </div>
              </div>}
            </div>
          </div>
        </div>;
      })()}

      {/* Cycles — every cycle with its own controls (owner rule 2026-09-19), plus Add Cycle. */}
      {renderCycleList(c)}

      {/* Phase R-8 — Payouts + Incentive blocks moved into the unified Edit
          Deal modal. They no longer appear on the card. */}

      {/* Footer */}
      <div style={{ marginTop:12, paddingTop:10, borderTop:"1px solid #F1F5F9", display:"flex", justifyContent:"flex-end", gap:8 }}>
        {/* Phase R-13.1 — Broker Calculation modal trigger. External only. */}
        {c.externalSplit && c.externalSplit.isExternal && <button onClick={function(){ setBrokerCalcFor(c); setBrokerPrintErr(""); setBrokerPrinting(false); }} style={{
          padding:"6px 12px", borderRadius:8, border:"1px solid #DDD6FE", background:"#F5F3FF",
          cursor:"pointer", fontSize:12, fontWeight:600, color:"#5B21B6",
          display:"inline-flex", alignItems:"center", gap:4
        }}>
          💼 Broker Calculation
        </button>}
        {/* Phase R-14 — Ambassador Calculation trigger. Ambassador + admin/SA, and the financeReads role (2026-11-02). */}
        {c.ambassadorSplit && c.ambassadorSplit.isAmbassador && (p.cu.role==="admin"||p.cu.role==="operation"||roleHasFinanceReadsFE(p.cu)) && <button onClick={function(){ setAmbassadorCalcFor(c); }} style={{
          padding:"6px 12px", borderRadius:8, border:"1px solid #FDE68A", background:"#FFFBEB",
          cursor:"pointer", fontSize:12, fontWeight:600, color:"#B45309",
          display:"inline-flex", alignItems:"center", gap:4
        }}>
          🎯 Ambassador Calculation
        </button>}
        {/* Cancel Deal — admin path to drop a deal directly from the
            Commissions card. Hits POST /api/commissions/:id/cancel which
            handles BOTH the live-lead path (full /deal-cancel behavior:
            lead → HotCase, dealStatus → Deal Cancelled, DR mirror sync,
            cascadeCommissionCancel) and the orphan path (lead missing,
            archived, or already Deal Cancelled → flips just the commission
            via cascadeCommissionCancel). The c.leadId guard is dropped
            intentionally: orphans whose lead has been hard-deleted still
            need a cancel path, and the BE endpoint tolerates a missing
            Lead doc. Hidden once the commission is already cancelled and
            for non-admin roles. */}
        {!isCancelled && (p.cu.role === "admin" || p.cu.role === "operation") && <button
          onClick={async function(){
            if(!window.confirm("Cancel this deal? The commission will be cancelled too (lockstep).")) return;
            try {
              var res = await apiFetch("/api/commissions/" + c._id + "/cancel", "POST", {}, p.token);
              var okMsg = (res && res.mode === "orphan")
                ? "Orphan commission cancelled — lead was not live"
                : "Deal cancelled — commission moved to Cancelled tab";
              setToast({ kind:"ok", msg: okMsg });
              setReloadTick(function(v){ return v + 1; });
            } catch(e) {
              setToast({ kind:"err", msg: (e && e.message) || "Cancel failed" });
            }
          }}
          style={{
            padding:"6px 12px", borderRadius:8, border:"1px solid #FCA5A5", background:"#FEF2F2",
            cursor:"pointer", fontSize:12, fontWeight:600, color:"#B91C1C",
            display:"inline-flex", alignItems:"center", gap:4
          }}>
          ❌ Cancel Deal
        </button>}
        {!roleHidesCustomerDataFE(p.cu) && <button onClick={function(){ goToDeal(c); }} style={{
          padding:"6px 12px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff",
          cursor:"pointer", fontSize:12, fontWeight:600, color:C.text,
          display:"inline-flex", alignItems:"center", gap:4
        }}>
          <ExternalLink size={12}/> View Deal
        </button>}
      </div>
    </div>;
  };

  var loading = rows === null;

  // Partial VAT payments (2026-10-25) — add one payment / delete one payment.
  // Both refetch the ledger alone via vatReloadTick. No Edit by owner decision:
  // a wrong amount is delete + re-add, and the server audits both. The date
  // rule (today-or-past, Cairo) is the server's; the client mirrors it only to
  // give a message before the round trip, and the server's answer wins.
  var openVatAdd = function(month){
    setVatAddForm({ date: cairoTodayIso(), amount:"", note:"" });
    setVatAddError("");
    setVatAddMonth(month);
  };
  var closeVatAdd = function(){ if (!vatAddBusy) setVatAddMonth(null); };
  var submitVatAdd = async function(){
    var f = vatAddForm;
    var amount = Number(f.amount);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.date || "")) { setVatAddError("Pick a payment date."); return; }
    if (f.date > cairoTodayIso()) { setVatAddError("The payment date cannot be in the future."); return; }
    if (!isFinite(amount) || amount <= 0) { setVatAddError("Enter an amount greater than 0."); return; }
    setVatAddBusy(true); setVatAddError("");
    try {
      await apiFetch("/api/vat-payments", "POST", { claimMonth: vatAddMonth, date: f.date, amount: amount, note: (f.note || "").trim() }, p.token);
      setVatAddBusy(false);
      setVatAddMonth(null);
      setVatReloadTick(function(v){ return v + 1; });
      setToast({ kind:"ok", msg:"Payment recorded for " + vatAddMonth });
    } catch(e) {
      setVatAddBusy(false);
      setVatAddError((e && e.message) || "Failed");
    }
  };
  var deleteVatPayment = async function(pay){
    if (!window.confirm("Delete this " + fmtMoney2(pay.amount) + " payment dated " + fmtDayOnly(pay.date) + "?")) return;
    try {
      await apiFetch("/api/vat-payments/" + encodeURIComponent(pay.id), "DELETE", null, p.token);
      setVatReloadTick(function(v){ return v + 1; });
      setToast({ kind:"ok", msg:"Payment deleted" });
    } catch(e) {
      setToast({ kind:"err", msg: (e && e.message) || "Failed" });
    }
  };

  // Phase R-6 handlers. deleteExpense + saveProfitTax both refetch the P&L
  // via pnlReloadTick (the R-6 fetch effect re-runs); category mutations
  // come back through ManageCategoriesModal's onClose callback below.
  var deleteExpense = async function(row){
    if (!window.confirm("Delete this expense?")) return;
    try {
      await apiFetch("/api/expenses/" + row._id, "DELETE", null, p.token);
      triggerPnlReload();
      setToast({ kind:"ok", msg:"Expense deleted" });
    } catch(e) {
      setToast({ kind:"err", msg: (e && e.message) || "Failed" });
    }
  };
  var saveProfitTax = async function(){
    var v = Number(taxValueDraft);
    if (!isFinite(v) || v < 0) { alert("Value must be a number ≥ 0"); return; }
    var effY = annualYear || String(dfCairoYear());
    try {
      await apiFetch("/api/profit-tax/" + effY, "PUT", { mode: taxModeDraft, value: v }, p.token);
      triggerPnlReload();
      setToast({ kind:"ok", msg:"Profit tax saved" });
    } catch(e) {
      setToast({ kind:"err", msg: (e && e.message) || "Failed" });
    }
  };

  // Phase R-5 — tab strip. Sentence case, thin underline on active. Each tab
  // owns its own year state (claimsYearFilter / annualYear); Claims keeps the
  // search/agent/status filters in its own filter row, Annual Summary uses
  // only the year. Switching tabs leaves the other tab's filter untouched.
  var TabBtn = function(props){
    var active = activeTab === props.id;
    return <button onClick={function(){ setActiveTab(props.id); }} style={{
      padding:"10px 4px", marginInlineEnd: 24, border:"none",
      borderBottom: active ? "2px solid " + C.accent : "2px solid transparent",
      background:"transparent", cursor:"pointer", fontSize:14,
      fontWeight: active ? 700 : 500,
      color: active ? C.accent : C.textLight
    }}>{props.label}</button>;
  };

  return <div style={{ padding:"24px 16px", maxWidth:1100, margin:"0 auto" }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
      {/* Title removed — the shared header renders it (titles.commissions). */}
    </div>

    {/* Due Payouts banner — shows on Cairo days 10-15 when the latest
        /api/commissions/due-payouts response carries dealCount > 0. Admin
        and operation only (matches the endpoint gate). Clicking jumps
        to the Due Payouts tab. */}
    {(function(){
      if (!p.cu) return null;
      if (p.cu.role !== "admin" && p.cu.role !== "operation" && !roleHasFinanceReadsFE(p.cu)) return null;
      var day = dfCairoDay();
      if (day < 10 || day > 15) return null;
      var deals = (duePayouts && duePayouts.dealCount) || 0;
      if (!(deals > 0)) return null;
      return <div onClick={function(){ setActiveTab("duePayouts"); }}
        style={{ background:"#DC2626", color:"#fff", padding:"12px 16px", borderRadius:10, marginBottom:14, cursor:"pointer", fontSize:13, fontWeight:600, display:"flex", alignItems:"center", gap:10 }}>
        <AlertCircle size={18} style={{ flexShrink:0 }}/>
        <span style={{ flex:1 }}>{"في " + deals + " deals مستحقة الدفع — اضغط لتسجيل المدفوعات"}</span>
      </div>;
    })()}

    {/* Invariant violation — an APPROVED deal with no commission. Zero is the
        only correct value, so this renders ONLY when something is wrong and says
        what to do about it. Named rows (up to 4) because "3 deals" sends an admin
        hunting; the ids are what make it actionable.

        A PENDING deal with no commission is NOT a violation and must never
        appear here — that is the normal state of every deal between close and
        approval (owner ruling 2026-09-02). The server narrowed the population to
        approved deals; if this banner ever lights up for a row still showing ⏳,
        the server predicate has regressed, not the data. */}
    {dealsNoComm && dealsNoComm.count > 0 && <div style={{
      background:"#FEF2F2", border:"1px solid #FCA5A5", color:"#991B1B",
      padding:"12px 16px", borderRadius:10, marginBottom:14, fontSize:13
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, fontWeight:700, marginBottom:6 }}>
        <AlertCircle size={18} style={{ flexShrink:0 }}/>
        <span>{dealsNoComm.count === 1
          ? "1 approved deal has no commission"
          : (dealsNoComm.count + " approved deals have no commission")}</span>
      </div>
      <div style={{ fontWeight:400, marginBottom:6 }}>
        Every approved deal must have one. Re-save the deal from the Deals page to create it;
        if that fails, the reason below says what the deal is missing.
      </div>
      {(dealsNoComm.deals || []).slice(0, 4).map(function(d){
        return <div key={d._id} style={{ fontSize:12, marginTop:3 }}>
          <b>{d.recordNo != null ? formatLeadId(d.recordNo) + " · " : ""}{d.name || "(no name)"}</b>
          {d.agentName ? " · " + d.agentName : ""}
          {" — " + d.reason}
        </div>;
      })}
      {dealsNoComm.count > 4 && <div style={{ fontSize:12, marginTop:4, opacity:0.85 }}>
        …and {dealsNoComm.count - 4} more.
      </div>}
    </div>}

    {/* THE OTHER HALF OF THE INVARIANT — a commission on a deal that is NOT
        approved. This is NOT a violation and is deliberately not red: the only
        way to reach it is to un-approve an already-approved deal, which leaves
        the commission alive on purpose (birth once — cancelling it would let a
        later re-approve mint a SECOND commission on the same lead). It is shown
        because the alternative is that the state is invisible. */}
    {dealsNoComm && dealsNoComm.earlyCount > 0 && <div style={{
      background:"#FFFBEB", border:"1px solid #FDE68A", color:"#92400E",
      padding:"10px 14px", borderRadius:10, marginBottom:14, fontSize:12.5
    }}>
      <div style={{ fontWeight:700, marginBottom:4 }}>
        {dealsNoComm.earlyCount === 1
          ? "1 commission sits on a deal that is not approved"
          : (dealsNoComm.earlyCount + " commissions sit on deals that are not approved")}
      </div>
      <div style={{ fontWeight:400 }}>
        Expected after un-approving a deal — the commission is kept rather than cancelled.
        Approving the deal again clears this.
      </div>
      {(dealsNoComm.early || []).slice(0, 4).map(function(d){
        return <div key={"early-"+d._id} style={{ marginTop:3 }}>
          <b>{d.recordNo != null ? formatLeadId(d.recordNo) + " · " : ""}{d.name || "(no name)"}</b>
          {" — " + d.reason}
        </div>;
      })}
    </div>}

    <div style={{ borderBottom:"1px solid #E8ECF1", marginBottom:14, display:"flex" }}>
      {commissionsStrip.tabs.map(function(tb){ return <TabBtn key={tb.id} id={tb.id} label={tb.label}/>; })}
    </div>
    {commissionsStrip.empty && <div style={{ fontSize:13, color:C.textLight, padding:"18px 2px" }}>{NO_TABS_MSG}</div>}

    {activeTab === "deals" && <>
    {/* Stats row — admin + operation see Active Deals + Active Cycles. Phase R-13: Received Commission card removed. */}
    <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
      <StatCard label="Active Deals"          value={(stats && stats.dealsCount) != null ? stats.dealsCount : "—"} icon={Briefcase} c={C.info}/>
      <StatCard label="Active Cycles"         value={(stats && stats.activeCycles) != null ? stats.activeCycles : "—"} icon={Activity} c={C.accent}/>
    </div>

    {/* Imminent alert banner */}
    {imminentCount > 0 && <div style={{ background:"#FEF2F2", border:"1px solid #FCA5A5", color:"#B91C1C", padding:"10px 14px", borderRadius:10, marginBottom:14, display:"flex", alignItems:"center", gap:8, fontSize:13, fontWeight:600 }}>
      <AlertCircle size={16}/>
      {imminentCount} commission{imminentCount === 1 ? "" : "s"} ha{imminentCount === 1 ? "s" : "ve"} an expected collection within 7 days and missing cycle stages.
    </div>}

    {/* Filter pills */}
    <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
      {STATUS_PILLS.map(function(s){
        var active = statusFilter === s.id;
        return <button key={s.id} onClick={function(){ setStatusFilter(s.id); }} style={{
          padding:"5px 12px", borderRadius:8, border:"1px solid",
          borderColor: active ? C.accent : "#E2E8F0",
          background: active ? C.accent + "12" : "#fff",
          color: active ? C.accent : C.textLight,
          fontSize:12, fontWeight:600, cursor:"pointer"
        }}>{s.label}</button>;
      })}
    </div>

    {/* Phase R-12 Part 4 — Deal-type filter pills (All / Internal / External).
        Phase R-14 — + Ambassador (amber), counted via ambassadorSplit.isAmbassador
        (mirror flag, parallel to externalSplit.isExternal). Counts come from
        `rows` after the server-side filters. Internal = neither external nor
        ambassador. */}
    {(function(){
      // Counts come from the SERVER, over the whole filtered set — not from
      // `rows`, which is one page. They are also computed WITHOUT the deal-type
      // narrowing, so each pill keeps showing what it would select while a
      // different pill is active. Null while a fetch is in flight: showing a
      // stale count next to a loading list is worse than showing none.
      var cts = (commMeta && commMeta.counts) || null;
      var extCount = cts ? cts.external : 0;
      var ambCount = cts ? cts.ambassador : 0;
      var intCount = cts ? cts.internal : 0;
      var DEAL_TYPE_PILLS = [
        { id:"all",        label:"All",            count: cts ? cts.all : 0,  theme: C.accent },
        { id:"internal",   label:"🏢 Internal",    count: intCount,   theme: C.accent },
        { id:"external",   label:"🤝 External",    count: extCount,   theme: "#7C3AED" },
        { id:"ambassador", label:"🎯 Ambassador",  count: ambCount,   theme: "#D97706" }
      ];
      return <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
        {DEAL_TYPE_PILLS.map(function(s){
          var active = dealTypeFilter === s.id;
          return <button key={s.id} onClick={function(){ setDealTypeFilter(s.id); }} style={{
            padding:"5px 12px", borderRadius:8, border:"1px solid",
            borderColor: active ? s.theme : "#E2E8F0",
            background: active ? s.theme + "12" : "#fff",
            color: active ? s.theme : C.textLight,
            fontSize:12, fontWeight:600, cursor:"pointer"
          }}>{s.label} ({s.count})</button>;
        })}
      </div>;
    })()}

    {/* Search + agent + year filters */}
    <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
      <input type="text" placeholder="Search customer / project / developer…" value={search} onChange={function(e){ setSearch(e.target.value); }} style={{
        flex:1, minWidth:200, padding:"7px 12px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:13, outline:"none"
      }}/>
      <Select value={claimsYearFilter} onChange={function(e){ setClaimsYearFilter(e.target.value); }}
        width={130} maxWidth={130} fontSize={13} title="Filter by year money was received"
        options={[{ value:"", label:"All years" }].concat((stats && Array.isArray(stats.availableYears) ? stats.availableYears : []).map(function(y){
          return { value:String(y), label:String(y) };
        }))}/>
      <SearchableSelect multi value={agentFilter} onChange={setAgentFilter}
        options={(p.users || []).filter(function(u){ return u && (positionOf(u) === "sales" || positionOf(u) === "team_leader"); }).map(function(u){ return { value:String(u._id), label:u.name }; })}
        emptyLabel="All agents" placeholder="Search agent…" width={200} maxWidth={200} fontSize={13}/>
      {/* Developer (D5) — searchable filter; options from the managed list. */}
      <SearchableSelect value={developerFilter} onChange={setDeveloperFilter}
        options={(commDevelopers||[]).map(function(dv){ return { value:String(dv._id), label:dv.name }; })}
        emptyLabel="🏗️ All developers" placeholder="Search developers…" width={180} maxWidth={180} fontSize={13}/>
      {(search || agentFilter.length || claimsYearFilter || statusFilter !== "all" || dealTypeFilter !== "all" || developerFilter) && <button onClick={function(){ setSearch(""); setAgentFilter([]); setClaimsYearFilter(""); setStatusFilter("all"); setDealTypeFilter("all"); setDeveloperFilter(""); }} style={{
        padding:"7px 12px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", fontSize:12, color:C.textLight
      }}>Clear</button>}
    </div>

    {/* List */}
    {loading && <div style={{ padding:"40px 16px", textAlign:"center", color:C.textLight }}>Loading…</div>}
    {!loading && loadErr && <div style={{ padding:"16px", background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:8, color:"#B91C1C", fontSize:13, marginBottom:14 }}>{loadErr}</div>}
    {!loading && rows && (function(){
      // NO CLIENT-SIDE FILTERING (2026-08-21). Deal type and developer are both
      // server params now, so what arrived IS what matches. Filtering here would
      // reintroduce exactly the bug paging was added to prevent: a page of 50
      // narrowed to 3 on screen while the pill promises 40, and no way to reach
      // the other 37 except by loading pages until they happen to appear.
      var displayRows = rows;
      if (displayRows.length === 0) {
        return <div style={{ padding:"40px 16px", textAlign:"center", color:C.textLight, background:"#fff", borderRadius:14, border:"1px solid #E8ECF1" }}>
          No commissions match the current filters.
        </div>;
      }
      return displayRows.map(renderCard);
    })()}

    {/* Load more — the visible half of the paging that replaced the .limit(100).
        Rendered only when the server says there IS another page, and it reports
        how many of how many are loaded so a truncated view can never look
        complete. That readout is the point: the old cap failed silently, and a
        pager without a total fails the same way. */}
    {!loading && rows && commMeta && (commMeta.page || 1) < (commMeta.totalPages || 1) && (
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, padding:"18px 0 6px" }}>
        <button onClick={loadMoreCommissions} disabled={commLoadingMore} style={{
          padding:"9px 22px", borderRadius:10, border:"1px solid #E2E8F0",
          background: commLoadingMore ? "#F1F5F9" : "#fff", color: C.text,
          fontSize:13, fontWeight:700, cursor: commLoadingMore ? "default" : "pointer"
        }}>{commLoadingMore ? "Loading…" : "Load more"}</button>
        <div style={{ fontSize:11, color:C.textLight }}>
          {rows.length} of {commMeta.total} loaded
        </div>
      </div>
    )}
    </>}

    {activeTab === "calculator" && (
      // Standalone calculator — pure UI scratchpad, visible to whoever holds the
      // tab grant (admin, operation, and the financeReads role since 2026-11-02).
      // State (calcUnit / calcRate / calcWithholdingExempt) lives on CommissionsPage
      // so it survives tab switches; resets on page reload. The card and its math
      // live in their own files (src/components/CommissionCalculatorCard.js,
      // src/lib/commissionCalculator.js); the same round2/fmtMoney2 helpers as
      // the claim_submitted modal keep the two breakdowns in lockstep. Layout is
      // the ORIGINAL stacked card, centred (owner ruling 2026-10-31, second pass).
      <CommissionCalculatorCard
        unit={calcUnit} onUnit={setCalcUnit}
        rate={calcRate} onRate={setCalcRate}
        exempt={calcWithholdingExempt} onExempt={setCalcWithholdingExempt}
        FormRow={FormRow} MoneyInput={MoneyInput} fmtMoney={fmtMoney2} parseMoney={parseMoney} C={C}/>
    )}

    {activeTab === "duePayouts" && (function(){
      // Due Payouts — cycles in `received` state with team payouts not yet
      // fully settled. Data is from /api/commissions/due-payouts; flatten
      // items × recipients into one row per (cycle, recipient) so the table
      // reads as a punch list of individual payments to make.
      var data  = duePayouts || { totalDue:0, recipientCount:0, dealCount:0, items:[] };
      var items = Array.isArray(data.items) ? data.items : [];
      var rowsFlat = [];
      items.forEach(function(it){
        (it.recipients || []).forEach(function(r){
          rowsFlat.push({
            userName:     r.userName,
            role:         r.role,
            leadName:     it.leadName,
            projectName:  it.projectName,
            receivedDate: it.receivedDate,
            amountDue:    r.amountDue,
            commissionId: it.commissionId,
            leadId:       it.leadId,
            cycleNumber:  it.cycleNumber
          });
        });
      });
      var dealCount = data.dealCount || 0;
      return <>
        {dealCount > 0 && <div style={{ marginBottom:14, background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, padding:"12px 14px" }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#DC2626" }}>{dealCount + " deal" + (dealCount === 1 ? "" : "s") + " due for payment"}</div>
        </div>}

        {dueLoading && rowsFlat.length === 0 && <div style={{
          padding:"30px 16px", textAlign:"center", color:C.textLight,
          background:"#fff", border:"1px solid #E8ECF1", borderRadius:10
        }}>Loading…</div>}

        {!dueLoading && rowsFlat.length === 0 && <div style={{
          padding:"30px 16px", textAlign:"center", color:C.textLight,
          background:"#fff", border:"1px solid #E8ECF1", borderRadius:10
        }}>No due payouts.</div>}

        {rowsFlat.length > 0 && <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
          <table style={{ width:"100%", minWidth:620, borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#F8FAFC", borderBottom:"1px solid #E8ECF1" }}>
                <th style={{ textAlign:"start", padding:"10px 12px", fontWeight:700, color:C.textLight }}>Recipient</th>
                <th style={{ textAlign:"start", padding:"10px 12px", fontWeight:700, color:C.textLight }}>Customer</th>
                <th style={{ textAlign:"start", padding:"10px 12px", fontWeight:700, color:C.textLight }}>Project</th>
                <th style={{ textAlign:"start", padding:"10px 12px", fontWeight:700, color:C.textLight }}>Received date</th>
                <th style={{ textAlign:"end",   padding:"10px 12px", fontWeight:700, color:C.textLight }}>Amount due (EGP)</th>
              </tr>
            </thead>
            <tbody>
              {rowsFlat.map(function(r, idx){
                return <tr key={r.commissionId + ":" + r.cycleNumber + ":" + idx} style={{ borderTop:"1px solid #F1F5F9" }}>
                  <td style={{ padding:"10px 12px", color:C.text }}>
                    <div style={{ fontWeight:600 }}>{r.userName || "(unknown)"}</div>
                    <div style={{ fontSize:10, color:C.textLight, marginTop:2 }}>{r.role || "—"}</div>
                  </td>
                  <td style={{ padding:"10px 12px", color:C.text }}>{r.leadName || "(unknown)"}</td>
                  <td style={{ padding:"10px 12px", color:C.text }}>{r.projectName || "—"}</td>
                  <td style={{ padding:"10px 12px", color:C.textLight }}>{fmtDate(r.receivedDate)}</td>
                  <td style={{ padding:"10px 12px", textAlign:"end", fontWeight:700, color:"#DC2626" }}>{fmtMoney(r.amountDue)}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>}
      </>;
    })()}

    {activeTab === "annual" && (function(){
      // Effective year — annualYear when set, else current Cairo year. Mirrors
      // the backend default so the UI always shows what the endpoint returned.
      var effYear = annualYear || String(dfCairoYear());
      var ay = (annualSummary && annualSummary.year) || effYear;
      var totals = (annualSummary && annualSummary.totals) || { grossClaim:0, netOfVat:0, vat:0, withholding5pct:0, netRevenue:0 };
      var byMonth = (annualSummary && annualSummary.vatByMonth) || [];
      var byDeal  = (annualSummary && annualSummary.withholdingByDeal) || [];
      var totalWh = byDeal.reduce(function(s, r){ return s + Number(r.withholdingAmount || 0); }, 0);
      // Phase R-5 — tax displays render at piaster precision (round2) so totals
      // reconcile against Excel/government filings. Module-level fmtMoney2 helper.
      var fmtMoneyAr = fmtMoney2;
      var fmtDateShort = function(iso){ if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("en-GB"); } catch(_){ return iso; } };
      // Partial VAT payments — status composed from the ledger (vatPayments)
      // against the zone-owned row.vatAmount. Both sides round to the piaster
      // first so a 0.004 residue cannot turn a Paid month into Partial.
      //   Auto-paid : vatDue == 0 (no invoices, or all exempt) — nothing to pay
      //   Unpaid    : nothing recorded yet
      //   Partial   : 0 < paid < due
      //   Paid      : paid == due
      //   Overpaid  : paid > due (allowed — penalties / corrections)
      // Overdue (red row) is unpaid-or-partial AND the Cairo calendar is past
      // the payment month: a lexicographic "YYYY-MM" compare, no Date math.
      var vatLedgerReady = !!(vatPayments && vatPayments.byMonth);
      var vatStatusOf = function(vatDue, paidTotal){
        var due = round2(vatDue), paid = round2(paidTotal);
        if (due <= 0) return "auto";
        if (paid <= 0) return "unpaid";
        if (paid < due) return "partial";
        if (paid > due) return "overpaid";
        return "paid";
      };
      var VAT_PILL = {
        unpaid:   { bg:"#FEE2E2", fg:"#B91C1C", label:"Unpaid" },
        partial:  { bg:"#FEF3C7", fg:"#B45309", label:"Partial" },
        paid:     { bg:"#DCFCE7", fg:"#15803D", label:"Paid" },
        overpaid: { bg:"#F3E8FF", fg:"#7E22CE", label:"Overpaid" },
        auto:     { bg:"#DCFCE7", fg:"#15803D", label:"Auto-paid" },
        loading:  { bg:"#F1F5F9", fg:C.textLight, label:"…" },
        unavail:  { bg:"#F1F5F9", fg:C.textLight, label:"Unavailable" }
      };
      var vatPaymentsFor = function(month){
        if (!vatLedgerReady) return [];
        return (vatPayments.payments || []).filter(function(x){ return x.claimMonth === month; });
      };
      var vatTodayMonth = cairoTodayIso().slice(0, 7);

      // Change A — internal-scroll table chrome. Tables on this tab cap at
      // TABLE_MAX_H and scroll internally; the header (and footer, where a
      // table has one) stay pinned via position:sticky. boxShadow draws the
      // divider so it survives borderCollapse:collapse.
      var TABLE_MAX_H = 400;
      var stickyTh = { position:"sticky", top:0, zIndex:2, background:"#F8FAFC", boxShadow:"inset 0 -1px 0 #E8ECF1" };
      var stickyFoot = { position:"sticky", bottom:0, zIndex:2, background:"#F8FAFC", boxShadow:"inset 0 1px 0 #E8ECF1" };

      var summaryCard = function(label, value, color){
        return <div style={{ flex:1, minWidth:140, background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, padding:"12px 14px" }}>
          <div style={{ fontSize:10, color:C.textLight, marginBottom:4 }}>{label}</div>
          <div style={{ fontSize:14, fontWeight:700, color: color || C.text }}>{fmtMoneyAr(value)}</div>
        </div>;
      };

      return <div>
        {/* Year selector — bound to annualYear (independent from claimsYearFilter
            on the Claims tab). Auto-fills to availableYears[0] on first entry. */}
        <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:14, flexWrap:"wrap" }}>
          <span style={{ fontSize:12, color:C.textLight }}>Year</span>
          {(function(){
            var avail = stats && Array.isArray(stats.availableYears) ? stats.availableYears : [];
            return <Select value={annualYear} onChange={function(e){ setAnnualYear(e.target.value); }}
              width={130} maxWidth={130} fontSize={13}
              options={(avail.length === 0 ? [{ value:"", label:"—" }] : []).concat(avail.map(function(y){ return { value:String(y), label:String(y) }; }))}/>;
          })()}
        </div>

        {annualLoading && <div style={{ padding:"40px 16px", textAlign:"center", color:C.textLight }}>Loading…</div>}
        {!annualLoading && <>
          {/* Section 1 — five totals cards */}
          <div style={{ display:"flex", gap:10, marginBottom:18, flexWrap:"wrap" }}>
            {summaryCard("Total Claims", totals.grossClaim, C.text)}
            {summaryCard("Net of VAT", totals.netOfVat, C.text)}
            {summaryCard("VAT 14%", totals.vat, C.warning)}
            {summaryCard("Withholding 5%", totals.withholding5pct, C.warning)}
            {summaryCard("Net Revenue", totals.netRevenue, C.success)}
          </div>

          {/* Section 2 — VAT monthly table */}
          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:8 }}>Monthly VAT</div>
            {byMonth.length === 0 && <div style={{ padding:"20px 14px", background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, color:C.textLight, fontSize:12 }}>No claims for this year</div>}
            {byMonth.length > 0 && <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, overflowY:"auto", maxHeight:TABLE_MAX_H }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr>
                    <th style={Object.assign({}, stickyTh, { width:30, padding:"8px 0 8px 12px" })}></th>
                    <th style={Object.assign({}, stickyTh, { textAlign:"start", padding:"8px 12px", fontWeight:700, color:C.textLight })}>Claim month</th>
                    <th style={Object.assign({}, stickyTh, { textAlign:"end",   padding:"8px 12px", fontWeight:700, color:C.textLight })}>VAT due</th>
                    <th style={Object.assign({}, stickyTh, { textAlign:"start", padding:"8px 12px", fontWeight:700, color:C.textLight })}>Payment month</th>
                    <th style={Object.assign({}, stickyTh, { textAlign:"end",   padding:"8px 12px", fontWeight:700, color:C.textLight })}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {byMonth.map(function(row){
                    var isOpen = !!vatMonthExpanded[row.month];
                    var leadsForRow = Array.isArray(row.leads) ? row.leads : [];
                    var payRows = vatPaymentsFor(row.month);
                    var monthFold = vatLedgerReady ? (vatPayments.byMonth[row.month] || null) : null;
                    var paidTotal = monthFold ? Number(monthFold.paidTotal || 0) : 0;
                    var vatDue = Number(row.vatAmount || 0);
                    // Legacy fallback (owner, 2026-10-25): a month with NO ledger payments
                    // but the old single "Mark paid" still set reads as Paid in full. The
                    // VatPayment row (row.paid / row.paidAt from /annual-summary) stays the
                    // source for that month until a real payment is recorded against it.
                    var legacyPaid = vatLedgerReady && payRows.length === 0 && !!row.paid && vatDue > 0;
                    if (legacyPaid) paidTotal = vatDue;
                    var status = !vatPayments ? "loading" : (vatPayments.error ? "unavail" : vatStatusOf(vatDue, paidTotal));
                    var pill = VAT_PILL[status];
                    var remaining = round2(vatDue - paidTotal);
                    var isOverdue = (status === "unpaid" || status === "partial") && vatTodayMonth > row.paymentMonth;
                    var rowBg = isOverdue ? "#FEF2F2" : "#fff";
                    var canAdd = vatCanRead && vatDue > 0 && status !== "loading" && status !== "unavail";
                    var toggleRow = function(){
                      setVatMonthExpanded(function(prev){
                        var next = Object.assign({}, prev);
                        next[row.month] = !prev[row.month];
                        return next;
                      });
                    };
                    return <Fragment key={row.month}>
                      <tr style={{ borderTop:"1px solid #F1F5F9", background: rowBg, cursor:"pointer" }}
                          onClick={function(e){
                            var tag = e.target && e.target.tagName;
                            if (tag === "BUTTON" || tag === "SPAN" || tag === "svg" || tag === "path") return;
                            toggleRow();
                          }}>
                        <td style={{ padding:"8px 0 8px 12px", color:C.textLight, fontSize:11 }}>
                          {isOpen ? "▾" : "▸"}
                        </td>
                        <td style={{ padding:"8px 12px", fontWeight:600 }}>{row.month}</td>
                        <td style={{ padding:"8px 12px", textAlign:"end" }}>{fmtMoneyAr(row.vatAmount)}</td>
                        <td style={{ padding:"8px 12px" }}>{row.paymentMonth}</td>
                        <td style={{ padding:"8px 12px", textAlign:"end" }}>
                          <span style={{ display:"inline-block", padding:"3px 10px", borderRadius:10, background:pill.bg, color:pill.fg, fontSize:11, fontWeight:700, opacity: status === "auto" ? 0.75 : 1 }}>
                            {pill.label}
                          </span>
                          {status === "auto" && <div style={{ fontSize:10, color:C.textLight, marginTop:3 }}>No VAT due this month</div>}
                          {/* Unpaid shows the pill alone — nothing is paid, and the VAT due
                              column already carries the full amount (owner, 2026-10-25). */}
                          {status === "partial" && <div style={{ fontSize:10, color:C.textLight, marginTop:3 }}>
                            Paid: {fmtMoneyAr(paidTotal)} · Remaining: <span style={{ color: isOverdue ? "#B91C1C" : C.text, fontWeight:600 }}>{fmtMoneyAr(remaining)}</span>
                          </div>}
                          {status === "paid" && !legacyPaid && <div style={{ fontSize:10, color:C.textLight, marginTop:3 }}>Paid: {fmtMoneyAr(paidTotal)}</div>}
                          {status === "paid" && legacyPaid && <div style={{ fontSize:10, color:C.textLight, marginTop:3 }}>Marked paid {fmtDateShort(row.paidAt)}</div>}
                          {status === "overpaid" && <div style={{ fontSize:10, color:"#7E22CE", marginTop:3, fontWeight:600 }}>
                            Paid: {fmtMoneyAr(paidTotal)} · Overpaid by {fmtMoneyAr(round2(paidTotal - vatDue))}
                          </div>}
                        </td>
                      </tr>
                      {isOpen && <tr style={{ background:"#F8FAFC", borderTop:"1px solid #F1F5F9" }}>
                        <td></td>
                        <td colSpan={4} style={{ padding:"6px 12px 10px" }}>
                          {/* Payments ledger for this claim month. Delete renders on EVERY
                              payment from its own existence (per-item controls rule), admin only. */}
                          <div style={{ fontSize:10, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, marginBottom:4 }}>Payments</div>
                          {status === "auto" && <div style={{ fontSize:11, color:C.textLight, padding:"2px 0 6px" }}>No VAT due this month — nothing to pay.</div>}
                          {status === "unavail" && <div style={{ fontSize:11, color:C.textLight, padding:"2px 0 6px" }}>Payment ledger unavailable.</div>}
                          {legacyPaid && <div style={{ fontSize:11, color:C.textLight, padding:"2px 0 6px" }}>Marked paid in full on {fmtDateShort(row.paidAt)} (single mark, before itemised payments).</div>}
                          {status !== "auto" && status !== "unavail" && payRows.length === 0 && !legacyPaid && <div style={{ fontSize:11, color:C.textLight, padding:"2px 0 6px" }}>No payments recorded yet.</div>}
                          {payRows.map(function(pay, pIdx){
                            return <div key={pay.id} style={{ display:"flex", alignItems:"center", gap:10, fontSize:11, color:C.text, padding:"3px 0", borderTop: pIdx>0?"1px dashed #E2E8F0":"none" }}>
                              <span style={{ width:80, flex:"none", color:C.textLight }}>{fmtDayOnly(pay.date)}</span>
                              <span style={{ width:120, flex:"none", fontWeight:600, textAlign:"end" }}>{fmtMoneyAr(pay.amount)}</span>
                              <span style={{ flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", color:C.textLight }} title={pay.note || ""}>{pay.note || ""}</span>
                              <span data-vat-audit={pay.id} style={{ flex:"none", fontSize:9, color:C.textLight }}>{auditCaption(pay.createdByName || userNameOf(pay.createdBy, p.users), pay.createdAt)}</span>
                              {vatCanRead && <button onClick={function(e){ e.stopPropagation(); deleteVatPayment(pay); }} title="Delete payment"
                                style={{ background:"#fff", border:"1px solid #E2E8F0", borderRadius:4, padding:"2px 6px", fontSize:11, color:"#B91C1C", cursor:"pointer", lineHeight:1 }}>✕</button>}
                            </div>;
                          })}
                          {canAdd && <button onClick={function(e){ e.stopPropagation(); openVatAdd(row.month); }} style={{
                            marginTop:6, padding:"4px 10px", borderRadius:6, border:"1px solid " + C.accent, background: C.accent + "12",
                            color: C.accent, fontSize:11, fontWeight:600, cursor:"pointer"
                          }}>+ Add Payment</button>}
                          {leadsForRow.length > 0 && <div style={{ fontSize:10, fontWeight:700, color:C.textLight, textTransform:"uppercase", letterSpacing:0.3, margin:"10px 0 4px" }}>Deals contributing to this month</div>}
                          {leadsForRow.map(function(ld, lIdx){
                            return <div key={lIdx} style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.text, padding:"2px 0", borderTop: lIdx>0?"1px dashed #E2E8F0":"none" }}>
                              <span style={{ flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ld.name}</span>
                              <span style={{ color:C.textLight, marginInlineStart:12 }}>Claim {fmtMoneyAr(ld.claimAmount)}</span>
                              <span style={{ color:"#B45309", fontWeight:600, marginInlineStart:12 }}>VAT {fmtMoneyAr(ld.vatAmount)}</span>
                            </div>;
                          })}
                        </td>
                      </tr>}
                    </Fragment>;
                  })}
                </tbody>
              </table>
            </div>}
            {/* Add Payment modal — one payment for the month that opened it. */}
            <Modal show={!!vatAddMonth} onClose={closeVatAdd} title={"Add VAT payment — " + (vatAddMonth || "")} w={420}>
              <Inp label="Payment date" type="date" req value={vatAddForm.date}
                max={cairoTodayIso()}
                onChange={function(e){ var v = e.target.value; setVatAddForm(function(f){ return Object.assign({}, f, { date: v }); }); }}/>
              <Inp label="Amount (EGP)" type="number" req min="0.01" placeholder="e.g. 200000" value={vatAddForm.amount}
                onChange={function(e){ var v = e.target.value; setVatAddForm(function(f){ return Object.assign({}, f, { amount: v }); }); }}/>
              <Inp label="Note" type="text" placeholder="Bank reference, receipt no. (optional)" value={vatAddForm.note}
                onChange={function(e){ var v = e.target.value; setVatAddForm(function(f){ return Object.assign({}, f, { note: v }); }); }}/>
              {vatAddError && <div style={{ fontSize:12, color:C.danger, marginBottom:10 }}>{vatAddError}</div>}
              <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
                <Btn outline onClick={closeVatAdd} disabled={vatAddBusy}>Cancel</Btn>
                <Btn onClick={submitVatAdd} loading={vatAddBusy}>Add Payment</Btn>
              </div>
            </Modal>
          </div>

          {/* Section 3 — withholding by deal */}
          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:8 }}>Withholding by deal</div>
            {byDeal.length === 0 && <div style={{ padding:"20px 14px", background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, color:C.textLight, fontSize:12 }}>No claims for this year</div>}
            {byDeal.length > 0 && <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, overflowY:"auto", maxHeight:TABLE_MAX_H }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr>
                    <th style={Object.assign({}, stickyTh, { textAlign:"start", padding:"8px 12px", fontWeight:700, color:C.textLight })}>Customer / Project</th>
                    <th style={Object.assign({}, stickyTh, { textAlign:"start", padding:"8px 12px", fontWeight:700, color:C.textLight })}>Cycle</th>
                    <th style={Object.assign({}, stickyTh, { textAlign:"start", padding:"8px 12px", fontWeight:700, color:C.textLight })}>Date</th>
                    <th style={Object.assign({}, stickyTh, { textAlign:"end",   padding:"8px 12px", fontWeight:700, color:C.textLight })}>Claim total</th>
                    <th style={Object.assign({}, stickyTh, { textAlign:"end",   padding:"8px 12px", fontWeight:700, color:C.textLight })}>Withholding 5%</th>
                  </tr>
                </thead>
                <tbody>
                  {byDeal.map(function(r){
                    return <tr key={r.cycleId} style={{ borderTop:"1px solid #F1F5F9" }}>
                      <td style={{ padding:"8px 12px" }}>
                        <div style={{ fontWeight:600 }}>{r.customerName || "—"}</div>
                        {r.projectName && <div style={{ fontSize:10, color:C.textLight }}>{r.projectName}</div>}
                      </td>
                      <td style={{ padding:"8px 12px" }}>#{r.cycleNumber}</td>
                      <td style={{ padding:"8px 12px" }}>{fmtDateShort(r.claimDate)}</td>
                      <td style={{ padding:"8px 12px", textAlign:"end" }}>{fmtMoneyAr(r.claimAmount)}</td>
                      <td style={{ padding:"8px 12px", textAlign:"end", fontWeight:600, color:C.warning }}>{fmtMoneyAr(r.withholdingAmount)}</td>
                    </tr>;
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} style={Object.assign({}, stickyFoot, { padding:"10px 12px", textAlign:"end", fontWeight:700, color:C.text })}>Total withholding for year</td>
                    <td style={Object.assign({}, stickyFoot, { padding:"10px 12px", textAlign:"end", fontWeight:700, color:C.warning })}>{fmtMoneyAr(totalWh)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>}
          </div>

          {/* Phase R-6 — Expenses + P&L section. The Annual Summary tab is
              already gated to admin role (commit 0821637), so anyone reaching
              this point is admin. No redundant per-section check (judgment #5).
              Sections: merged Monthly Expenses (grid + chart), Net Profit by
              Deal, P&L statement. */}
          {(function(){
            if (pnlLoading) return <div style={{ padding:"40px 16px", textAlign:"center", color:C.textLight }}>Loading P&amp;L…</div>;
            var pnlData      = pnl || { revenue:{netRevenue:0}, teamCommissions:{total:0,byMonth:[]}, expenses:{total:0,rows:[],byCategory:[]}, profitBeforeTax:0, profitTax:{mode:null,value:null,amount:0}, netProfit:0 };
            var allExpenses  = (pnlData.expenses && Array.isArray(pnlData.expenses.rows)) ? pnlData.expenses.rows : [];
            var filteredExp  = expCatFilter
              ? allExpenses.filter(function(r){ return String(r.categoryId) === String(expCatFilter); })
              : allExpenses;
            var filteredTotal = filteredExp.reduce(function(s,r){ return s + Number(r.amount || 0); }, 0);
            var hasTaxCfg = !!(pnlData.profitTax && pnlData.profitTax.mode);

            // Sub-Block C inline tax editor — render its bits up-front so the
            // statement layout below stays declarative.
            var taxEditor = <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
              <Select value={taxModeDraft} onChange={function(e){ setTaxModeDraft(e.target.value); }}
                width={130} maxWidth={130} fontSize={11}
                options={[{ value:"percent", label:"Percent (%)" },{ value:"fixed", label:"Fixed (EGP)" }]}/>
              <input type="number" step="0.01" min="0" value={taxValueDraft}
                placeholder={taxModeDraft === "percent" ? "e.g. 22.5" : "e.g. 50000"}
                onChange={function(e){ setTaxValueDraft(e.target.value); }}
                style={{ width:120, padding:"4px 8px", borderRadius:6, border:"1px solid #E2E8F0", fontSize:11 }}/>
              <button onClick={saveProfitTax} style={{
                padding:"4px 12px", borderRadius:6, border:"none", background: C.accent, color:"#fff", fontSize:11, fontWeight:600, cursor:"pointer"
              }}>Save</button>
            </div>;

            return <>
              {/* --- Monthly Expenses (Phase R-16) — merged section ----
                  One "Monthly Expenses" heading over a 4×3 month-card grid,
                  the year total, and the chart. Replaces the old separate
                  "Operating Expenses" 12-row table + "Monthly Expenses" chart
                  sub-blocks. A month card opens a per-month detail modal
                  (edit/delete inside). Empty months are dimmed + not
                  clickable; the live month gets an accent highlight. */}
              <div style={{ marginTop:24, marginBottom:18 }}>
                <style>{
                  ".aro-exp-grid{display:grid;gap:10px;grid-template-columns:repeat(4,1fr);}" +
                  "@media(max-width:1024px){.aro-exp-grid{grid-template-columns:repeat(3,1fr);}}" +
                  "@media(max-width:768px){.aro-exp-grid{grid-template-columns:repeat(2,1fr);}}" +
                  ".aro-exp-card{transition:border-color .15s ease,box-shadow .15s ease;}" +
                  ".aro-exp-card:hover{border-color:" + C.accent + ";box-shadow:0 2px 10px rgba(15,23,42,0.10);}"
                }</style>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10, flexWrap:"wrap" }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, flex:1, minWidth:140 }}>Monthly Expenses</div>
                  <div style={{ display:"inline-flex", border:"1px solid #E2E8F0", borderRadius:8, overflow:"hidden", fontSize:11 }}>
                    {[{ id:"total", label:"Total" },{ id:"byCategory", label:"By category" }].map(function(opt){
                      var active = chartMode === opt.id;
                      return <button key={opt.id} onClick={function(){ setChartMode(opt.id); }} style={{
                        padding:"4px 10px", border:"none", background: active ? C.accent + "12" : "#fff",
                        color: active ? C.accent : C.textLight, fontWeight: active ? 700 : 500, cursor:"pointer"
                      }}>{opt.label}</button>;
                    })}
                  </div>
                  <Select value={expCatFilter} onChange={function(e){ setExpCatFilter(e.target.value); }}
                    searchable width={150} maxWidth={150}
                    options={[{ value:"", label:"All categories" }]
                      .concat(expenseCategories.filter(function(c){ return !c.archived; }).map(function(c){ return { value:c._id, label:c.name }; }))
                      .concat(expenseCategories.filter(function(c){ return c.archived; }).map(function(c){ return { value:c._id, label:c.name + " (archived)" }; }))}/>
                  <button onClick={function(){ setManagingCategories(true); }} style={{
                    padding:"6px 12px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", fontSize:12, fontWeight:600, color:C.text, cursor:"pointer"
                  }}>Manage Categories</button>
                  <button onClick={function(){ setAddingExpense(true); }} style={{
                    padding:"6px 14px", borderRadius:8, border:"none", background: C.accent, color:"#fff", fontSize:12, fontWeight:600, cursor:"pointer"
                  }}>+ Add Expense</button>
                </div>
                {(function(){
                  // Phase R-16 — Cairo-local month grouping of the (category-
                  // filtered) expense rows for the selected year. Shared by the
                  // 4×3 card grid and the per-month detail modal.
                  var monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
                  var yearStr = String(ay);
                  var buckets = monthNames.map(function(){ return []; });
                  filteredExp.forEach(function(r){
                    var d = new Date(r.date);
                    if (isNaN(d.getTime())) return;
                    if (String(dfCairoYear(d)) !== yearStr) return;
                    buckets[dfCairoMonthIdx(d)].push(r);
                  });
                  // Current month accent — only when the displayed year is the
                  // live Cairo year.
                  var curMonthIdx = (String(dfCairoYear()) === yearStr) ? dfCairoMonthIdx() : -1;
                  return <>
                    {/* 4×3 month-card grid (responsive: 4/3/2 cols) */}
                    <div className="aro-exp-grid">
                      {monthNames.map(function(mn, mi){
                        var entries = buckets[mi];
                        var has = entries.length > 0;
                        var monthTotal = entries.reduce(function(s, r){ return s + Number(r.amount || 0); }, 0);
                        var isCur = mi === curMonthIdx;
                        return <div key={mi}
                          className={has ? "aro-exp-card" : undefined}
                          onClick={has ? function(){ setExpenseMonthModal(mi); } : undefined}
                          style={{
                            border: isCur ? ("1.5px solid " + C.accent) : "1px solid #E8ECF1",
                            borderRadius:10, padding:"12px 14px", minHeight:86,
                            background: has ? "#fff" : "#F8FAFC",
                            cursor: has ? "pointer" : "default",
                            display:"flex", flexDirection:"column", gap:3
                          }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <span style={{ fontSize:12, fontWeight:700, color: has ? C.text : C.textLight }}>{mn}</span>
                            {isCur && <span style={{ fontSize:9, fontWeight:700, color:C.accent, background:C.accent + "16", padding:"1px 6px", borderRadius:8 }}>Current</span>}
                          </div>
                          <div style={{ fontSize:10, color:C.textLight }}>
                            {has ? (entries.length + " entr" + (entries.length === 1 ? "y" : "ies")) : "—"}
                          </div>
                          <div style={{ fontSize:15, fontWeight:700, marginTop:"auto", color: has ? C.text : "#CBD5E1" }}>
                            {has ? fmtMoneyAr(monthTotal) : "—"}
                          </div>
                        </div>;
                      })}
                    </div>
                    {/* Year total */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12, padding:"10px 14px", background:"#F8FAFC", border:"1px solid #E8ECF1", borderRadius:10 }}>
                      <span style={{ fontSize:12, fontWeight:700, color:C.text }}>{expCatFilter ? "Filtered total" : "Total expenses for year"}</span>
                      <span style={{ fontSize:14, fontWeight:700, color:C.text }}>{fmtMoneyAr(filteredTotal)}</span>
                    </div>
                    {/* Monthly chart */}
                    <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, padding:"12px 14px", marginTop:18 }}>
                      <ExpenseBarChart rows={allExpenses} year={ay} mode={chartMode}/>
                    </div>
                    {/* Per-month detail modal — entries with edit/delete */}
                    {expenseMonthModal != null && buckets[expenseMonthModal] && (function(){
                      var mi = expenseMonthModal;
                      var entries = buckets[mi];
                      var monthTotal = entries.reduce(function(s, r){ return s + Number(r.amount || 0); }, 0);
                      return <Modal show={true} onClose={function(){ setExpenseMonthModal(null); }}
                        title={monthNames[mi] + " " + yearStr + " — Expenses"} w={640}>
                        {entries.length === 0 && <div style={{ padding:"24px 4px", textAlign:"center", color:C.textLight, fontSize:13 }}>No expense entries for this month.</div>}
                        {entries.length > 0 && <div style={{ border:"1px solid #E8ECF1", borderRadius:10, overflow:"hidden" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                            <thead>
                              <tr style={{ background:"#F8FAFC", borderBottom:"1px solid #E8ECF1" }}>
                                <th style={{ textAlign:"start", padding:"7px 10px", fontWeight:700, color:C.textLight, width:92 }}>Date</th>
                                <th style={{ textAlign:"start", padding:"7px 10px", fontWeight:700, color:C.textLight, width:150 }}>Category</th>
                                <th style={{ textAlign:"start", padding:"7px 10px", fontWeight:700, color:C.textLight }}>Description</th>
                                <th style={{ textAlign:"end",   padding:"7px 10px", fontWeight:700, color:C.textLight, width:118 }}>Amount</th>
                                <th style={{ textAlign:"end",   padding:"7px 10px", fontWeight:700, color:C.textLight, width:78 }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {entries.map(function(r){
                                return <tr key={r._id} style={{ borderTop:"1px solid #F1F5F9" }}>
                                  <td style={{ padding:"7px 10px" }}>{fmtDateShort(r.date)}</td>
                                  <td style={{ padding:"7px 10px" }}>
                                    {r.categoryName}
                                    {r.categoryArchived && <span style={{ marginInlineStart:6, fontSize:9, color:"#B45309", background:"#FEF3C7", padding:"1px 6px", borderRadius:10, fontWeight:700 }}>archived</span>}
                                  </td>
                                  <td style={{ padding:"7px 10px", color:C.textLight }}>
                                    {r.description || "—"}
                                    {(function(){
                                      var a = expenseAudit[String(r._id)];
                                      if (!a) return null;
                                      var created = auditCaption(a.createdByName || userNameOf(a.createdByUserId, p.users), a.createdAt);
                                      var edited = (a.updatedByUserId || a.updatedByName) ? auditCaption(a.updatedByName || userNameOf(a.updatedByUserId, p.users), a.updatedAt) : "";
                                      return <div data-expense-audit={String(r._id)} style={{ fontSize:9, color:C.textLight, marginTop:2 }}>{created}{edited ? " · edited " + edited : ""}</div>;
                                    })()}
                                  </td>
                                  <td style={{ padding:"7px 10px", textAlign:"end", fontWeight:600 }}>{fmtMoneyAr(r.amount)}</td>
                                  <td style={{ padding:"7px 10px", textAlign:"end", whiteSpace:"nowrap" }}>
                                    <button onClick={function(){ setEditingExpense(r); }} title="Edit" style={{ background:"#fff", border:"1px solid #E2E8F0", borderRadius:4, padding:"3px 8px", fontSize:10, color:C.textLight, cursor:"pointer", marginInlineEnd:4 }}>✎</button>
                                    <button onClick={function(){ deleteExpense(r); }} title="Delete" style={{ background:"#fff", border:"1px solid #FCA5A5", borderRadius:4, padding:"3px 8px", fontSize:10, color:"#B91C1C", cursor:"pointer" }}>✕</button>
                                  </td>
                                </tr>;
                              })}
                            </tbody>
                            <tfoot>
                              <tr style={{ background:"#F8FAFC", borderTop:"1px solid #E8ECF1" }}>
                                <td colSpan={3} style={{ padding:"8px 10px", fontWeight:700, color:C.text }}>Month total</td>
                                <td style={{ padding:"8px 10px", textAlign:"end", fontWeight:700, color:C.text }}>{fmtMoneyAr(monthTotal)}</td>
                                <td/>
                              </tr>
                            </tfoot>
                          </table>
                        </div>}
                      </Modal>;
                    })()}
                  </>;
                })()}
              </div>

              {/* --- Sub-Block B.5: Net Profit by Deal (Phase R-15) ----
                  Per-deal breakdown that reconciles to the P&L statement
                  below: Σ Revenue === P&L Revenue, Σ ARO Net === P&L Revenue
                  − Team & Broker Commissions. Sortable + filterable; internal
                  scroll (Change A). Sits directly above the P&L statement. */}
              {(function(){
                var pbdEmpty = { revenue:0, brokerPayouts:0, teamPayouts:0, aroNet:0 };
                var pbd = profitByDeal || { deals: [], totals: pbdEmpty };
                var allDeals = Array.isArray(pbd.deals) ? pbd.deals : [];
                var shown = pbdFilter === "all"
                  ? allDeals
                  : allDeals.filter(function(d){ return d.type === pbdFilter; });
                var sorted = shown.slice().sort(function(a, b){
                  var dir = pbdSort.dir === "asc" ? 1 : -1;
                  return (Number(a[pbdSort.col] || 0) - Number(b[pbdSort.col] || 0)) * dir;
                });
                // Footer: "all" uses the endpoint totals (these reconcile to
                // the P&L exactly); a filtered view recomputes from the
                // visible rows.
                var foot = pbdFilter === "all"
                  ? (pbd.totals || pbdEmpty)
                  : shown.reduce(function(s, d){
                      return {
                        revenue:       s.revenue       + Number(d.revenue || 0),
                        brokerPayouts: s.brokerPayouts + Number(d.brokerPayouts || 0),
                        teamPayouts:   s.teamPayouts   + Number(d.teamPayouts || 0),
                        aroNet:        s.aroNet        + Number(d.aroNet || 0)
                      };
                    }, { revenue:0, brokerPayouts:0, teamPayouts:0, aroNet:0 });
                var setSort = function(col){
                  setPbdSort(function(prev){
                    if (prev.col === col) return { col: col, dir: prev.dir === "desc" ? "asc" : "desc" };
                    return { col: col, dir: "desc" };
                  });
                };
                var sortTh = function(label, col){
                  var active = pbdSort.col === col;
                  return <th onClick={function(){ setSort(col); }}
                    style={Object.assign({}, stickyTh, { textAlign:"end", padding:"8px 12px", fontWeight:700, color: active ? C.accent : C.textLight, cursor:"pointer", whiteSpace:"nowrap", userSelect:"none" })}>
                    {label}{active ? (pbdSort.dir === "desc" ? " ↓" : " ↑") : ""}
                  </th>;
                };
                var typeBadge = function(type){
                  // Phase R-14 — ambassador (amber) alongside external (violet) / internal (gray).
                  if (type === "ambassador") {
                    return <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700, background:"#FEF3C7", color:"#B45309" }}>Ambassador</span>;
                  }
                  var ext = type === "external";
                  return <span style={{
                    display:"inline-block", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700,
                    background: ext ? "#EDE9FE" : "#F1F5F9", color: ext ? "#5B21B6" : C.textLight
                  }}>{ext ? "External" : "Internal"}</span>;
                };
                var filterChip = function(id, label){
                  var active = pbdFilter === id;
                  return <button onClick={function(){ setPbdFilter(id); }} style={{
                    padding:"4px 12px", borderRadius:14, fontSize:11, fontWeight:600, cursor:"pointer",
                    border:"1px solid " + (active ? C.accent : "#E2E8F0"),
                    background: active ? C.accent + "12" : "#fff",
                    color: active ? C.accent : C.textLight
                  }}>{label}</button>;
                };
                return <div style={{ marginBottom:18 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8, flexWrap:"wrap" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text, flex:1, minWidth:140 }}>Net Profit by Deal</div>
                    <div style={{ display:"flex", gap:6 }}>
                      {filterChip("all", "All")}
                      {filterChip("internal", "Internal")}
                      {filterChip("external", "External")}
                      {filterChip("ambassador", "Ambassador")}
                    </div>
                    {/* Feature B — closing company filter (server-side via
                        profit-by-deal?closingCompanyId=...). */}
                    <Select value={pbdCcFilter} onChange={function(e){ setPbdCcFilter(e.target.value); }}
                      title="Filter by closing company" searchable width={160} maxWidth={160} fontSize={11}
                      options={[{ value:"", label:"All companies" }].concat((commClosingCompanies||[]).map(function(co){ return { value:String(co._id), label:co.name }; }))}/>
                    {/* Developer (D5) — searchable filter (server-side via
                        profit-by-deal?developerId=..., ANDed with the company filter). */}
                    <SearchableSelect value={pbdDevFilter} onChange={setPbdDevFilter}
                      options={(commDevelopers||[]).map(function(dv){ return { value:String(dv._id), label:dv.name }; })}
                      emptyLabel="🏗️ All developers" placeholder="Search developers…" width={170} maxWidth={170} fontSize={11}/>
                  </div>
                  {profitByDealLoading && <div style={{ padding:"20px 14px", background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, color:C.textLight, fontSize:12 }}>Loading…</div>}
                  {!profitByDealLoading && allDeals.length === 0 && <div style={{ padding:"20px 14px", background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, color:C.textLight, fontSize:12 }}>No deals for this year</div>}
                  {!profitByDealLoading && allDeals.length > 0 && <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, overflowY:"auto", maxHeight:TABLE_MAX_H }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                      <thead>
                        <tr>
                          <th style={Object.assign({}, stickyTh, { textAlign:"start", padding:"8px 12px", fontWeight:700, color:C.textLight })}>Customer</th>
                          <th style={Object.assign({}, stickyTh, { textAlign:"start", padding:"8px 12px", fontWeight:700, color:C.textLight })}>Type</th>
                          <th style={Object.assign({}, stickyTh, { textAlign:"start", padding:"8px 12px", fontWeight:700, color:C.textLight })}>Closing Company</th>
                          <th style={Object.assign({}, stickyTh, { textAlign:"start", padding:"8px 12px", fontWeight:700, color:C.textLight })}>Developer</th>
                          {sortTh("Revenue", "revenue")}
                          {sortTh("Broker", "brokerPayouts")}
                          {sortTh("Team", "teamPayouts")}
                          {sortTh("ARO Net", "aroNet")}
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map(function(d){
                          var neg = Number(d.aroNet || 0) < 0;
                          return <tr key={d.commissionId} style={{ borderTop:"1px solid #F1F5F9" }}>
                            <td style={{ padding:"8px 12px", fontWeight:600, color:C.text }}>{d.customerName}</td>
                            <td style={{ padding:"8px 12px" }}>{typeBadge(d.type)}</td>
                            <td style={{ padding:"8px 12px", color:C.textLight }}>{d.closingCompany || "—"}</td>
                            <td style={{ padding:"8px 12px", color:C.textLight }}>{d.developer || "—"}</td>
                            <td style={{ padding:"8px 12px", textAlign:"end" }}>{fmtMoneyAr(d.revenue)}</td>
                            <td style={{ padding:"8px 12px", textAlign:"end", color: Number(d.brokerPayouts || 0) > 0 ? "#5B21B6" : C.textLight }}>{fmtMoneyAr(d.brokerPayouts)}</td>
                            <td style={{ padding:"8px 12px", textAlign:"end", color: Number(d.teamPayouts || 0) > 0 ? "#B45309" : C.textLight }}>{fmtMoneyAr(d.teamPayouts)}</td>
                            <td style={{ padding:"8px 12px", textAlign:"end", fontWeight:700, color: neg ? "#B91C1C" : C.success }}>{fmtMoneyAr(d.aroNet)}</td>
                          </tr>;
                        })}
                        {sorted.length === 0 && <tr><td colSpan={8} style={{ padding:"16px 12px", textAlign:"center", color:C.textLight }}>No {pbdFilter} deals for this year</td></tr>}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={4} style={Object.assign({}, stickyFoot, { padding:"10px 12px", fontWeight:700, color:C.text })}>Total</td>
                          <td style={Object.assign({}, stickyFoot, { padding:"10px 12px", textAlign:"end", fontWeight:700, color:C.text })}>{fmtMoneyAr(foot.revenue)}</td>
                          <td style={Object.assign({}, stickyFoot, { padding:"10px 12px", textAlign:"end", fontWeight:700, color:"#5B21B6" })}>{fmtMoneyAr(foot.brokerPayouts)}</td>
                          <td style={Object.assign({}, stickyFoot, { padding:"10px 12px", textAlign:"end", fontWeight:700, color:"#B45309" })}>{fmtMoneyAr(foot.teamPayouts)}</td>
                          <td style={Object.assign({}, stickyFoot, { padding:"10px 12px", textAlign:"end", fontWeight:700, color: Number(foot.aroNet || 0) < 0 ? "#B91C1C" : C.success })}>{fmtMoneyAr(foot.aroNet)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>}
                </div>;
              })()}

              {/* --- Sub-Block C: P&L statement ----------------------- */}
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:8 }}>Annual P&amp;L Statement — {ay}</div>
                {/* Change A — full-width card (was maxWidth:600), matching
                    the other Annual Summary sections. */}
                <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, padding:"16px 18px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                    {/* Phase R-14 — Revenue = Gross − VAT − Withholding, recognised
                        on cash receipt (cycle.received.date). Backend field
                        revenue.netRevenue; equals the Annual Summary "Net
                        Revenue" card. */}
                    <span style={{ fontSize:13, color:C.text }}>Revenue</span>
                    <span style={{ fontSize:13, fontWeight:600, color:C.text }}>{fmtMoneyAr(pnlData.revenue && pnlData.revenue.netRevenue)}</span>
                  </div>
                  <div style={{ borderTop:"1px solid #E2E8F0", marginTop:4, marginBottom:8 }}/>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    {/* Phase R-12 Part 7 — label rename. Backend field name
                        (teamCommissions.total) unchanged for API compat;
                        the line now folds in broker payouts from external
                        deals alongside internal team payouts. */}
                    <span style={{ fontSize:13, color:C.text }}>Less: Team &amp; Broker Commissions</span>
                    <span style={{ fontSize:13, color:"#B45309" }}>({fmtMoneyAr(pnlData.teamCommissions && pnlData.teamCommissions.total)})</span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                    <span style={{ fontSize:13, color:C.text }}>Less: Operating Expenses</span>
                    <span style={{ fontSize:13, color:"#B45309" }}>({fmtMoneyAr(pnlData.expenses && pnlData.expenses.total)})</span>
                  </div>
                  <div style={{ borderTop:"1px solid #E2E8F0", marginTop:4, marginBottom:8 }}/>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:C.text }}>Profit Before Tax</span>
                    <span style={{ fontSize:13, fontWeight:700, color: pnlData.profitBeforeTax >= 0 ? C.text : "#B91C1C" }}>{fmtMoneyAr(pnlData.profitBeforeTax)}</span>
                  </div>

                  <div style={{ background:"#F8FAFC", border:"1px dashed #CBD5E1", borderRadius:8, padding:"10px 12px", marginBottom:8 }}>
                    <div style={{ fontSize:11, color:C.textLight, marginBottom:6 }}>Profit Tax</div>
                    {taxEditor}
                    {!hasTaxCfg && <div style={{ fontSize:11, color:"#B45309", marginTop:6 }}>Set profit tax to calculate net profit</div>}
                  </div>

                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                    <span style={{ fontSize:13, color:C.text }}>
                      Less: Profit Tax
                      {hasTaxCfg && <span style={{ fontSize:10, color:C.textLight, marginInlineStart:6 }}>
                        ({pnlData.profitTax.mode === "percent" ? (pnlData.profitTax.value + "%") : "fixed"})
                      </span>}
                      {hasTaxCfg && profitTaxAudit && <span data-profit-tax-audit="1" style={{ fontSize:9, color:C.textLight, marginInlineStart:6 }}>
                        {auditCaption(profitTaxAudit.updatedByName || userNameOf(profitTaxAudit.updatedByUserId, p.users), profitTaxAudit.updatedAt)}
                      </span>}
                    </span>
                    <span style={{ fontSize:13, color:"#B45309" }}>({fmtMoneyAr(pnlData.profitTax && pnlData.profitTax.amount)})</span>
                  </div>
                  <div style={{ borderTop:"1px solid #E2E8F0", marginTop:4, marginBottom:8 }}/>
                  <div style={{ display:"flex", justifyContent:"space-between", paddingTop:4 }}>
                    <span style={{ fontSize:14, fontWeight:700, color:C.text }}>Net Profit (after tax)</span>
                    <span style={{ fontSize:15, fontWeight:700, color: hasTaxCfg ? (pnlData.netProfit >= 0 ? C.success : "#B91C1C") : C.textLight }}>
                      {hasTaxCfg ? fmtMoneyAr(pnlData.netProfit) : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </>;
          })()}
        </>}
      </div>;
    })()}

    {activeTab === "payout" && (function(){
      // Phase R-7 — Payout Report. Aggregated view of what we owe the team
      // for the selected month. Grouped by agent; each agent row expands to
      // show contributing deals.
      //
      // The month is picked as YEAR + MONTH, two selects, rather than the one
      // rolling "last 24 months" dropdown this used to be. Only the CONTROL
      // changed: both selects write the same "YYYY-MM" payoutMonth key the
      // fetcher above already splits into ?year=&month=, so the request, the
      // endpoint and the printed PDF are byte-for-byte what they were.
      //
      // Years are the Deals page's list — current plus three back
      // (dealYears, App.js) — minus its "all" entry, which cannot compose a
      // month key. ANCHORED ON THE CAIRO YEAR, not the device's: Deals reads
      // new Date().getFullYear(), but payoutMonth defaults to
      // dfCairoMonthKey(), and for the hours each New Year when a device sits
      // in a different calendar year than Cairo does, a device-anchored list
      // would not contain the default key's own year.
      var payoutYearStr = (/^(\d{4})-(\d{2})$/.exec(String(payoutMonth || "")) || [])[1] || String(dfCairoYear());
      var payoutMonStr  = (/^(\d{4})-(\d{2})$/.exec(String(payoutMonth || "")) || [])[2] || String(dfCairoMonthIdx() + 1).padStart(2, "0");
      var payoutYears = (function(){
        var y0 = dfCairoYear(), arr = [];
        for (var i = 0; i < 4; i++) arr.push(y0 - i);
        // A key from outside that window — a report left open across a year
        // boundary, or a year list that shrinks past a key already in state —
        // still has to be SELECTABLE. A Select whose value is absent from its
        // own options renders blank, and the blank reads as "no year chosen"
        // while the fetch below keeps querying the year that is still in state.
        if (arr.indexOf(Number(payoutYearStr)) === -1) {
          arr.push(Number(payoutYearStr));
          arr.sort(function(a, b){ return b - a; });
        }
        return arr;
      })();
      var payoutMonthNames = ["January","February","March","April","May","June",
                              "July","August","September","October","November","December"];
      // "YYYY-MM" -> "August 2026", the exact string the 24-month list used to
      // carry as its label (toLocaleString en-GB month:long year:numeric). It
      // is derived from the KEY now, not looked up in a list, because a
      // Year+Month pair can compose a month the old list never held and the
      // print header must not fall back to showing the raw key.
      var payoutKeyLabel = function(key){
        var mm = /^(\d{4})-(\d{2})$/.exec(String(key || ""));
        if (!mm) return String(key || "");
        return (payoutMonthNames[parseInt(mm[2], 10) - 1] || mm[2]) + " " + mm[1];
      };
      // The one writer of payoutMonth. Both selects go through it so neither
      // can compose a half-key, and the agent drill-downs collapse on any
      // change exactly as the single dropdown made them.
      var setPayoutYearMonth = function(y, m){
        setPayoutMonth(String(y) + "-" + String(m).padStart(2, "0"));
        setExpandedPayoutAgents({});
      };
      var agents  = (payoutReport && payoutReport.agents)  || [];
      // Phase R-12 Part 8 — broker rows from the same /payout-report
      // response. Rendered AS A SEPARATE GROUP at the bottom of the table,
      // not interleaved with agents (judgment call: clean visual split,
      // and admins typically pay one big broker payment vs many small
      // internal ones — easier to scan when grouped).
      var brokers = (payoutReport && payoutReport.brokers) || [];
      var summary = (payoutReport && payoutReport.summary) || { totalPayout:0, agentCount:0, brokerCount:0, recipientCount:0, dealCount:0 };
      // recipientCount falls back to agentCount on pre-Part-8 responses (none
      // exist in prod yet, but keeps this resilient if a stale cache hits).
      var recipientCount = (summary.recipientCount != null) ? summary.recipientCount : (summary.agentCount + brokers.length);
      var currentMonthLabel = payoutKeyLabel(payoutMonth);
      // Phase R-7 — Print to PDF. Expand all agents first so every drill-down
      // row renders into the DOM, then call window.print() on the next tick
      // so React has a chance to commit. The browser's "Save as PDF" sink
      // produces the file — no PDF library dependency. @media print CSS in
      // the inline <style> block below hides everything outside
      // #payout-print-area and forces A4 portrait.
      // Phase R-12 Part 8 — also expand broker rows. expandedPayoutAgents
      // doubles as the broker-expand map; broker keys are namespaced
      // ("broker:" + id) so they can never collide with a 24-char user._id.
      // ON THE APPS THIS DID NOTHING (fixed 2026-09-11). `window.print()` is
      // undefined in both WebViews, so an admin tapped "Print / Save PDF" on a
      // month's payouts and got no dialog, no error and no log line. The app
      // path rasterises #payout-print-area and hands a paginated PDF to the
      // native share sheet, through the shared captureAndShare.
      //
      // THE EXPAND STILL HAPPENS FIRST, on both paths and for the same reason:
      // the drill-down rows are what the report IS, and a collapsed report is a
      // list of totals. The two rAFs that used to exist only to let
      // window.print see the expanded DOM now also let html2canvas see it —
      // captureAndShare waits two more frames of its own after the off-screen
      // layout, so the expand is committed well before the capture.
      //
      // CLONE SWAPS CARRY THE PRINT STYLESHEET'S OWN TWO RULES. `.no-print` and
      // `.print-only` are declared inside `@media print` below, which is NOT in
      // effect on screen — so a naive rasterise would photograph a DIFFERENT
      // document from the one the browser prints: interactive chrome IN, the
      // report header OUT. The swaps are applied to html2canvas's clone, so the
      // live page never flickers. They must stay in step with the two
      // declarations in the @media print block; the gate asserts that pair.
      var printPayoutReport = function(){
        var allExpanded = {};
        agents.forEach(function(a){ allExpanded[a.userId] = true; });
        brokers.forEach(function(b){ allExpanded["broker:" + (b.brokerId || b.brokerName)] = true; });
        setExpandedPayoutAgents(allExpanded);
        var mode = pdfShareSupport();
        var afterExpand = function(fn){
          // Two rAFs to be safe — first commits the expand, second is the
          // post-commit paint. On the web, window.print is synchronous and
          // blocks; on return, leave the expand state as-is (admin can collapse
          // manually).
          if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(function(){ window.requestAnimationFrame(fn); });
          } else {
            setTimeout(fn, 80);
          }
        };
        if (mode === "print") {
          afterExpand(function(){ try { window.print(); } catch(_){} });
          return;
        }
        if (mode !== "share") {
          setPayoutPdfErr(PRINT_UNSUPPORTED_MSG[mode] || PRINT_UNSUPPORTED_MSG.none);
          return;
        }
        setPayoutPdfBusy(true); setPayoutPdfErr("");
        afterExpand(function(){
          var node = document.getElementById("payout-print-area");
          if (!node) { setPayoutPdfBusy(false); setPayoutPdfErr("The report is not ready yet."); return; }
          captureAndShare(node, {
            layout: "paginate",
            cloneSwaps: [
              { selector: "#payout-print-area .no-print", display: "none" },
              { selector: "#payout-print-area .print-only", display: "block" }
            ],
            filename: pdfFilename("payout", payoutMonth || "month", Date.now()),
            title: "ARO — Commission Payout Report " + (currentMonthLabel || "")
          })
            .then(function(){ setPayoutPdfBusy(false); })
            .catch(function(ex){ setPayoutPdfBusy(false); setPayoutPdfErr(String((ex && ex.message) || "Could not create the PDF.")); });
        });
      };
      return <div id="payout-print-area">
        {/* Print-only CSS — hides everything outside the report when printing,
            forces A4 portrait, expands all expansion rows, hides interactive
            chrome. Lives inline so it ships with the report and nothing else
            on the page bleeds into the PDF. */}
        <style>{`
          @media print {
            @page { size: A4 portrait; margin: 14mm; }
            html, body { background: #fff !important; }
            body * { visibility: hidden !important; }
            #payout-print-area, #payout-print-area * { visibility: visible !important; }
            /* Phase R-12 Part 8 — force background colors to print so the
               violet "Broker" badge + sub-header stripe stay visible in
               the PDF. Without these the browser strips backgrounds by
               default and the broker section reads as plain text. */
            #payout-print-area * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            #payout-print-area {
              position: absolute !important;
              left: 0 !important; top: 0 !important;
              width: 100% !important; padding: 0 !important;
            }
            #payout-print-area .no-print { display: none !important; }
            #payout-print-area .print-only { display: block !important; }
            #payout-print-area table { page-break-inside: auto; }
            #payout-print-area tr { page-break-inside: avoid; page-break-after: auto; }
            #payout-print-area thead { display: table-header-group; }
            #payout-print-area tfoot { display: table-footer-group; }
          }
          .print-only { display: none; }
        `}</style>
        {/* Print-only header — appears at the top of the PDF, hidden on screen. */}
        <div className="print-only" style={{ marginBottom:14, paddingBottom:10, borderBottom:"2px solid #1E293B" }}>
          <div style={{ fontSize:20, fontWeight:700, color:"#1E293B" }}>ARO — Commission Payout Report</div>
          <div style={{ fontSize:13, color:"#475569", marginTop:4 }}>{currentMonthLabel}</div>
          <div style={{ fontSize:11, color:"#64748B", marginTop:2 }}>Generated {fmtDateTime12(new Date(), undefined, { numeric:true, sep:", " })}</div>
        </div>

        {/* Top filter row. The pair is wrapped in its own flex box so the
            label stays glued to the selects it names when the row wraps, and
            so mobile can stack the two selects (column) without stacking the
            Print button along with them. */}
        <div className="no-print" style={{ display:"flex", gap:10, alignItems:"center", marginBottom:14, flexWrap:"wrap" }}>
          <div style={{ display:"flex", gap:8, alignItems:p.isMobile ? "stretch" : "center",
            flexDirection:p.isMobile ? "column" : "row", width:p.isMobile ? "100%" : "auto" }}>
            <span style={{ fontSize:12, color:C.textLight }}>Payout month</span>
            <div style={{ display:"flex", gap:8, flexDirection:p.isMobile ? "column" : "row" }}>
              {/* Year — changing it KEEPS the month (payoutMonStr rides
                  through), so stepping back a year lands on the same month
                  last year rather than resetting to January. */}
              <Select value={payoutYearStr}
                onChange={function(e){ setPayoutYearMonth(e.target.value, payoutMonStr); }}
                width={p.isMobile ? "100%" : 90} maxWidth={p.isMobile ? 9999 : 90}
                options={payoutYears.map(function(y){ return { value:String(y), label:String(y) }; })}/>
              {/* Month — all twelve, independent of the year, so the pair can
                  name any month of any offered year. */}
              <Select value={payoutMonStr}
                onChange={function(e){ setPayoutYearMonth(payoutYearStr, e.target.value); }}
                width={p.isMobile ? "100%" : 130} maxWidth={p.isMobile ? 9999 : 130}
                options={payoutMonthNames.map(function(nm, i){
                  return { value:String(i + 1).padStart(2, "0"), label:nm };
                })}/>
            </div>
          </div>
          {payoutLoading && <span style={{ fontSize:11, color:C.textLight }}>Loading payout report…</span>}
          <span style={{ flex:1 }}/>
          {/* THE LABEL SAYS WHAT WILL HAPPEN, per platform. On the app there is
              no print dialog — it builds a PDF and opens the OS share sheet,
              which is where WhatsApp and "Save to Files" live. A label
              promising a dialog that is not coming is how the original silence
              started. */}
          <button onClick={payoutPdfBusy ? undefined : printPayoutReport}
            disabled={(agents.length + brokers.length) === 0 || payoutLoading}
            title={pdfShareSupport() === "print"
              ? "Open the browser print dialog — pick 'Save as PDF' to file the report"
              : "Build a PDF and open the share sheet — save it or send it on WhatsApp"}
            style={{
              padding:"7px 14px", borderRadius:8, fontSize:12, fontWeight:600,
              border:"1px solid "+C.accent,
              background: (agents.length + brokers.length) === 0 ? "#F8FAFC" : C.accent + "12",
              color: (agents.length + brokers.length) === 0 ? C.textLight : C.accent,
              opacity: payoutPdfBusy ? 0.55 : 1,
              cursor: (agents.length + brokers.length) === 0 || payoutLoading || payoutPdfBusy ? "not-allowed" : "pointer"
            }}>{payoutPdfBusy ? "⏳ Preparing PDF…" : (pdfShareSupport() === "print" ? "🖨️ Print to PDF" : "📄 Share PDF")}</button>
        </div>
        {/* NEVER SILENT. `no-print` and `no-pdf` both, so it reaches neither the
            printed page nor the rasterised one. */}
        {payoutPdfErr && <div className="no-print no-pdf" style={{ marginBottom:14, padding:"9px 12px", borderRadius:8, background:"#FEE2E2", border:"1px solid #FCA5A5", color:"#B91C1C", fontSize:12, lineHeight:1.5 }}>{payoutPdfErr}</div>}

        {/* Stats row — Phase R-12 Part 8 changes the middle card from
            "Agents to pay" → "Recipients to pay" so brokers count too.
            Sub-text breaks out (N agents + M brokers) when both exist. */}
        <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
          <div style={{ flex:1, minWidth:160, background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, padding:"12px 14px" }}>
            <div style={{ fontSize:11, color:C.textLight, marginBottom:4 }}>Total payout</div>
            <div style={{ fontSize:18, fontWeight:700, color:C.success }}>{fmtMoney(summary.totalPayout)}</div>
          </div>
          <div style={{ flex:1, minWidth:160, background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, padding:"12px 14px" }}>
            <div style={{ fontSize:11, color:C.textLight, marginBottom:4 }}>Recipients to pay</div>
            <div style={{ fontSize:18, fontWeight:700, color:C.text }}>{recipientCount}</div>
            {brokers.length > 0 && <div style={{ fontSize:10, color:C.textLight, marginTop:2 }}>{summary.agentCount} internal · {brokers.length} broker{brokers.length === 1 ? "" : "s"}</div>}
          </div>
          <div style={{ flex:1, minWidth:160, background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, padding:"12px 14px" }}>
            <div style={{ fontSize:11, color:C.textLight, marginBottom:4 }}>Deals contributing</div>
            <div style={{ fontSize:18, fontWeight:700, color:C.text }}>{summary.dealCount}</div>
          </div>
        </div>

        {/* Per-agent table — expandable rows. Each agent shows totalOwed,
            and their list of contributing deals. Phase R-12 Part 8 appends
            broker rows in their own tbody section, separated by a labelled
            sub-header. Brokers grouped at bottom (judgment call) for clean
            visual split. */}
        {(agents.length + brokers.length) === 0 && !payoutLoading && <div style={{
          padding:"30px 16px", textAlign:"center", color:C.textLight,
          background:"#fff", border:"1px solid #E8ECF1", borderRadius:10
        }}>No payouts in this month.</div>}

        {(agents.length + brokers.length) > 0 && <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
          <table style={{ width:"100%", minWidth:560, borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#F8FAFC", borderBottom:"1px solid #E8ECF1" }}>
                <th className="no-print" style={{ width:32 }}></th>
                <th style={{ textAlign:"start", padding:"10px 12px", fontWeight:700, color:C.textLight }}>Recipient</th>
                <th style={{ textAlign:"start", padding:"10px 12px", fontWeight:700, color:C.textLight }}>Role</th>
                <th style={{ textAlign:"end",   padding:"10px 12px", fontWeight:700, color:C.textLight }}>Deals</th>
                <th style={{ textAlign:"end",   padding:"10px 12px", fontWeight:700, color:C.textLight }}>Total paid (EGP)</th>
              </tr>
            </thead>
            <tbody>
              {agents.map(function(a){
                var expanded = !!expandedPayoutAgents[a.userId];
                return <Fragment key={a.userId}>
                  <tr style={{ borderTop:"1px solid #F1F5F9", cursor:"pointer" }}
                      onClick={function(){
                        setExpandedPayoutAgents(function(prev){
                          var next = Object.assign({}, prev);
                          if (next[a.userId]) delete next[a.userId]; else next[a.userId] = true;
                          return next;
                        });
                      }}>
                    <td className="no-print" style={{ padding:"10px 12px", color:C.text, width:32 }}>
                      <ChevronDown size={16} style={{
                        display:"inline-block", verticalAlign:"middle",
                        transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                        transition:"transform 0.2s ease"
                      }}/>
                    </td>
                    <td style={{ padding:"10px 12px", fontWeight:600 }}>{a.userName}</td>
                    <td style={{ padding:"10px 12px", color:C.textLight }}>{a.role}</td>
                    <td style={{ padding:"10px 12px", textAlign:"end" }}>{a.deals.length}</td>
                    <td style={{ padding:"10px 12px", textAlign:"end", fontWeight:700, color:C.success }}>{fmtMoney(a.totalPaid)}</td>
                  </tr>
                  {expanded && <tr style={{ background:"#F8FAFC" }}>
                    <td colSpan={5} style={{ padding:"6px 12px 12px 44px" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                        <thead>
                          <tr style={{ color:C.textLight }}>
                            <th style={{ textAlign:"start", padding:"6px 8px", fontWeight:700 }}>Customer</th>
                            <th style={{ textAlign:"start", padding:"6px 8px", fontWeight:700 }}>Project</th>
                            <th style={{ textAlign:"start", padding:"6px 8px", fontWeight:700 }}>Deal date</th>
                            <th style={{ textAlign:"end",   padding:"6px 8px", fontWeight:700 }}>Deal total</th>
                            <th style={{ textAlign:"start", padding:"6px 8px", fontWeight:700 }}>Split</th>
                            <th style={{ textAlign:"start", padding:"6px 8px", fontWeight:700 }}>Cycle</th>
                            <th style={{ textAlign:"end",   padding:"6px 8px", fontWeight:700 }}>Paid (EGP)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {a.deals.map(function(d, idx){
                            return <tr key={d.commissionId + ":" + idx} style={{ borderTop:"1px solid #E2E8F0" }}>
                              <td style={{ padding:"6px 8px", color:C.text }}>{d.customerName || "(unknown)"}</td>
                              <td style={{ padding:"6px 8px", color:C.text }}>{d.projectName || "—"}</td>
                              <td style={{ padding:"6px 8px", color:C.textLight }}>{fmtDayOnly(d.dealDate) || "—"}</td>
                              <td style={{ padding:"6px 8px", textAlign:"end", color:C.textLight }}>{fmtMoney(d.dealTotal)}</td>
                              <td style={{ padding:"6px 8px", color: d.isSplit ? "#7C3AED" : C.textLight, fontWeight: d.isSplit ? 600 : 400 }}>{d.splitLabel || "Full deal"}</td>
                              <td style={{ padding:"6px 8px", color:C.textLight }}>{d.cycleRef ? "#" + d.cycleRef : "—"}</td>
                              <td style={{ padding:"6px 8px", textAlign:"end", fontWeight:700, color:C.success }}>{fmtMoney(d.paidThisMonth)}</td>
                            </tr>;
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>}
                </Fragment>;
              })}
              {/* Phase R-12 Part 8 — broker section. Sub-header divider row,
                  then one expandable row per broker. Drill-down omits the
                  Split column (brokers don't have team splits). */}
              {brokers.length > 0 && <tr style={{ background:"#F5F3FF", borderTop:"2px solid #DDD6FE" }}>
                <td colSpan={5} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, color:"#5B21B6", textTransform:"uppercase", letterSpacing:0.4 }}>
                  Brokers ({brokers.length})
                </td>
              </tr>}
              {brokers.map(function(b){
                var bKey = "broker:" + (b.brokerId || b.brokerName);
                var expanded = !!expandedPayoutAgents[bKey];
                return <Fragment key={bKey}>
                  <tr style={{ borderTop:"1px solid #F1F5F9", cursor:"pointer" }}
                      onClick={function(){
                        setExpandedPayoutAgents(function(prev){
                          var next = Object.assign({}, prev);
                          if (next[bKey]) delete next[bKey]; else next[bKey] = true;
                          return next;
                        });
                      }}>
                    <td className="no-print" style={{ padding:"10px 12px", color:C.text, width:32 }}>
                      <ChevronDown size={16} style={{
                        display:"inline-block", verticalAlign:"middle",
                        transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                        transition:"transform 0.2s ease"
                      }}/>
                    </td>
                    <td style={{ padding:"10px 12px", fontWeight:600 }}>
                      {b.brokerName}
                      {/* Violet "Broker" tag — same color family as the
                          External badge on the commission card (Part 6).
                          Renders cleanly in print (background + border
                          both visible — no opacity/shadow tricks). */}
                      <span style={{ marginInlineStart:8, fontSize:10, padding:"2px 8px", borderRadius:10, background:"#EDE9FE", color:"#5B21B6", fontWeight:700, border:"1px solid #DDD6FE" }}>Broker</span>
                    </td>
                    <td style={{ padding:"10px 12px", color:C.textLight }}>—</td>
                    <td style={{ padding:"10px 12px", textAlign:"end" }}>{b.deals.length}</td>
                    <td style={{ padding:"10px 12px", textAlign:"end", fontWeight:700, color:"#5B21B6" }}>{fmtMoney(b.totalPaid)}</td>
                  </tr>
                  {expanded && <tr style={{ background:"#F5F3FF" }}>
                    <td colSpan={5} style={{ padding:"6px 12px 12px 44px" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                        <thead>
                          <tr style={{ color:C.textLight }}>
                            <th style={{ textAlign:"start", padding:"6px 8px", fontWeight:700 }}>Customer</th>
                            <th style={{ textAlign:"start", padding:"6px 8px", fontWeight:700 }}>Project</th>
                            <th style={{ textAlign:"start", padding:"6px 8px", fontWeight:700 }}>Deal date</th>
                            <th style={{ textAlign:"end",   padding:"6px 8px", fontWeight:700 }}>Deal total</th>
                            <th style={{ textAlign:"start", padding:"6px 8px", fontWeight:700 }}>Cycle</th>
                            <th style={{ textAlign:"end",   padding:"6px 8px", fontWeight:700 }}>Paid (EGP)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {b.deals.map(function(d, idx){
                            return <tr key={d.commissionId + ":" + idx} style={{ borderTop:"1px solid #E2E8F0" }}>
                              <td style={{ padding:"6px 8px", color:C.text }}>{d.customerName || "(unknown)"}</td>
                              <td style={{ padding:"6px 8px", color:C.text }}>{d.projectName || "—"}</td>
                              <td style={{ padding:"6px 8px", color:C.textLight }}>{fmtDayOnly(d.dealDate) || "—"}</td>
                              <td style={{ padding:"6px 8px", textAlign:"end", color:C.textLight }}>{fmtMoney(d.dealTotal)}</td>
                              <td style={{ padding:"6px 8px", color:C.textLight }}>{d.cycleRef ? "#" + d.cycleRef : "—"}</td>
                              <td style={{ padding:"6px 8px", textAlign:"end", fontWeight:700, color:"#5B21B6" }}>{fmtMoney(d.paidThisMonth)}</td>
                            </tr>;
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>}
                </Fragment>;
              })}
            </tbody>
            <tfoot>
              <tr style={{ background:"#F8FAFC", borderTop:"2px solid #CBD5E1" }}>
                <td colSpan={3} style={{ padding:"10px 12px", fontWeight:700, color:C.text }}>Total</td>
                <td style={{ padding:"10px 12px", textAlign:"end", fontWeight:700, color:C.text }}>{summary.dealCount}</td>
                <td style={{ padding:"10px 12px", textAlign:"end", fontWeight:700, color:C.success }}>{fmtMoney(summary.totalPayout)}</td>
              </tr>
            </tfoot>
          </table>
        </div>}
      </div>;
    })()}

    {/* Phase R-8 — single unified Edit Collection modal. Sub-API calls inside
        the modal flush sequentially; onSaved swaps the updated commission into
        rows[] in place and refreshes ancillary stats + cash-flow. */}
    {editingCollection && <CommissionEditCollectionModal
      target={editingCollection}
      token={p.token}
      users={p.users}
      cu={p.cu}
      savingFlag={savingFlag}
      onClose={function(){ setEditingCollection(null); }}
      onSaved={function(doc, didAnything){
        if (doc && doc._id) {
          setRows(function(prev){
            if (!Array.isArray(prev)) return prev;
            var idx = prev.findIndex(function(x){ return String(x._id) === String(doc._id); });
            if (idx >= 0) { var next = prev.slice(); next[idx] = doc; return next; }
            return prev;
          });
          var statsUrl = "/api/commissions/stats" + (claimsYearFilter ? "?year=" + encodeURIComponent(claimsYearFilter) : "");
          apiFetch(statsUrl, "GET", null, p.token).then(function(s){ setStats(s || {}); }).catch(function(){});
        }
        if (didAnything !== false) setToast({ kind: "ok", msg: "Deal saved" });
      }}
      onPartialFailure={function(){ setReloadTick(function(v){ return v + 1; }); }}
    />}
    {editingCycleStage && <CommissionCycleStageModal target={editingCycleStage} onClose={function(){ setEditingCycleStage(null); }} writeCommission={writeCommission} savingFlag={savingFlag}/>}
    {addRecipientFor && <Modal show={true} onClose={function(){ setAddRecipientFor(null); }} title={"➕ Add Manual Recipient — " + (addRecipientFor.snapshot && addRecipientFor.snapshot.customerName ? addRecipientFor.snapshot.customerName : "Commission")} w={460}>
      <div style={{ fontSize:11, color:C.textLight, marginBottom:12, lineHeight:1.5 }}>
        Add an extra recipient to this deal with a manual EGP commission. This appears alongside the chain recipients but is per-deal only — global rates are unchanged.
        <br/><b>Tip:</b> picking an existing User links the payout to their User profile (so it shows on the Payout Report). Use the manual-name field below only for off-system recipients.
      </div>
      {/* Phase R-13.1 — User picker. Linking the override to a User lets the
          Payout Report aggregate paid amounts by userId. Selecting a user
          autofills the Name field and overrides any manual name on submit. */}
      <Inp label="User (recommended)" searchable type="select" value={addRecipUserId} onChange={function(e){
        setAddRecipUserId(e.target.value);
        if (e.target.value) {
          var u = (p.users || []).find(function(x){ return String(x._id || gid(x)) === String(e.target.value); });
          if (u && u.name) setAddRecipName(u.name);
        }
      }} options={[{ value:"", label:"— Pick a User (recommended) —" }].concat(
        (p.users || [])
          .filter(function(u){ return u && u.active !== false; })
          .map(function(u){ return { value: String(u._id || gid(u)), label: (u.name || "(unnamed)") + (u.role ? " · " + u.role : "") + (u.title ? " — " + u.title : "") }; })
      )}/>
      <div style={{ fontSize:10, color:C.textLight, margin:"-6px 0 8px 4px" }}>— or enter a name manually below (no User link; appears on Payout Report under the typed name) —</div>
      <Inp label="Name" req value={addRecipName} onChange={function(e){ setAddRecipName(e.target.value); }} placeholder="Full name"/>
      <Inp label="Role" type="select" value={addRecipRole} onChange={function(e){ setAddRecipRole(e.target.value); }} options={[
        { value:"sales", label:"Sales" },
        { value:"team_leader", label:"Team Leader" },
        { value:"manager", label:"Manager" },
        { value:"director", label:"Director" }
      ]}/>
      <Inp label="Amount (EGP)" req value={addRecipAmount} onChange={function(e){
        var raw = e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "");
        var firstDot = raw.indexOf(".");
        if (firstDot >= 0) raw = raw.slice(0, firstDot+1) + raw.slice(firstDot+1).replace(/\./g, "");
        var parts = raw.split(".");
        var intPart = parts[0] || "";
        var hasDec = parts.length > 1;
        var decPart = hasDec ? parts[1].slice(0, 2) : "";
        var formatted = intPart === "" ? (hasDec ? "0." + decPart : "") : Number(intPart).toLocaleString() + (hasDec ? "." + decPart : "");
        setAddRecipAmount(formatted);
      }} placeholder="e.g. 25,000"/>
      <div style={{ display:"flex", gap:10, marginTop:6 }}>
        <Btn outline onClick={function(){ setAddRecipientFor(null); }} style={{ flex:1 }}>Cancel</Btn>
        <Btn onClick={async function(){
          var nm = String(addRecipName || "").trim();
          var amtRaw = String(addRecipAmount || "").replace(/,/g, "").trim();
          var amtNum = Number(amtRaw);
          if (!nm) { alert("Name is required"); return; }
          if (!amtRaw || !isFinite(amtNum) || amtNum <= 0) { alert("Amount must be greater than 0"); return; }
          var body = { name: nm, role: addRecipRole, amount: amtNum };
          if (addRecipUserId) body.userId = addRecipUserId;
          try {
            await writeCommission("POST", "/api/commissions/" + addRecipientFor._id + "/recipients", body, "Manual recipient added");
            setAddRecipientFor(null);
          } catch(_){}
        }} style={{ flex:1 }} loading={savingFlag}>Add</Btn>
      </div>
    </Modal>}
    {/* Phase R-13.1 — Broker Calculation read-only modal. Shows the informal
        broker split breakdown computed from commission.externalSplit + Lead
        external-sales-agent fields. Includes a print-for-broker button that
        injects a print-only stylesheet and triggers window.print(). */}
    {brokerCalcFor && (function(){
      var c = brokerCalcFor;
      var es = c.externalSplit || {};
      var gross = 0;
      // Accumulate withholding per-cycle so per-cycle exemptions apply (a cycle
      // with withholdingExempt contributes 0 withholding). VAT below is still off
      // the full gross — the exemption waives only the 5% withholding, not VAT.
      var withholding = 0;
      if (Array.isArray(c.cycles)) {
        for (var i = 0; i < c.cycles.length; i++) {
          var cy = c.cycles[i];
          if (!cy || cy.state === "cancelled") continue;
          var cca = Number(cy.claimAmount || 0);
          gross += cca;
          withholding += cy.withholdingExempt ? 0 : (cca / 1.14) * 0.05;
        }
      }
      var taxPct = Number(es.commissionTaxPct || 0);
      var taxAmount = gross * (taxPct / 100);
      var grossAfterTax = gross - taxAmount;
      var brokerPct = Number(es.brokerSharePct || 0);
      var aroPct = Math.max(0, 100 - brokerPct);
      // Phase R-13.2 — brokerShare on the modal stays the original/gross
      // amount the broker is entitled to before the optional sales-agent
      // deduction. The "Paid by" branch below shows how that splits.
      var brokerShare = Number(es.brokerOwed || 0);
      var aroTheoretical = Number(es.aroOwed || 0);
      // Phase R-14 — canonical state-tax split off gross. companyNet (below)
      // is what ARO actually keeps in the bank: gross − VAT − withholding −
      // broker share. The VAT term is the R-14 fix — it was previously
      // omitted, so "Company net" overstated by the 14% VAT.
      var vat = gross - (gross / 1.14);
      var netRevenue = gross - vat - withholding;
      // Sales-agent line — only when toggle was ON (snapshot.salesAgent has a
      // positive computedShare). For toggle OFF, hide the line entirely.
      var sa = c.snapshot && c.snapshot.salesAgent;
      var salesAgentName = sa && sa.userName ? sa.userName : "";
      var manualAmount = sa ? Number(sa.computedShare || 0) : 0;
      var hasSales = manualAmount > 0;
      // Phase R-13.2 — paidBy semantics (R-14: company net is off netRevenue
      // = gross − VAT − withholding, the same basis as the P&L Revenue line):
      //   "company": company net = netRevenue − broker − sales.
      //   "broker":  company net = netRevenue − broker (sales is paid by the
      //              broker from their share; that amount stays with company).
      //   Broker NET (the amount the broker actually takes home in the
      //   "broker" mode) is brokerShare − manualAmount; the print view uses
      //   this so the broker on paper sees their real take.
      var paidByBroker = String(es.salesAgentPaidBy || "company") === "broker";
      var brokerNetTake = paidByBroker ? Math.max(0, brokerShare - manualAmount) : brokerShare;
      var companyNet = paidByBroker
        ? (netRevenue - brokerShare)
        : (netRevenue - brokerShare - (hasSales ? manualAmount : 0));
      var fmt = function(n){ return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " EGP"; };
      var leadName = (c.snapshot && c.snapshot.customerName) || "(deal)";
      // Phase R-13.3 — print path. Inject a body-level sibling of #root
      // containing ONLY the broker copy, then hide every other body > *
      // during @media print. Body height collapses to the injected root,
      // so the browser allocates exactly one printed page (the previous
      // visibility-based approach left the underlying scrolled document
      // height intact and produced N pages of repeated/blank output).
      var printNow = function(){
        var ROOT_ID = "broker-print-root";
        var cleanup = function(){
          try { document.body.classList.remove("printing-broker"); } catch(_){}
          try {
            var stale = document.getElementById(ROOT_ID);
            if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
          } catch(_){}
        };
        try {
          cleanup();
          var escapeHtml = function(s){
            return String(s == null ? "" : s).replace(/[&<>"']/g, function(ch){
              return ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" })[ch];
            });
          };
          // Each row uses flex with space-between so the amount is always
          // hard-right; no reliance on grid sizing that has misbehaved
          // on the Broker share row historically.
          var row = function(label, value, opts){
            opts = opts || {};
            var rs = "display:flex;justify-content:space-between;align-items:baseline;gap:6px;padding:6px 0;";
            if (opts.emphasized) rs = "display:flex;justify-content:space-between;align-items:baseline;gap:6px;padding:8px 0;border-top:2px solid #CBD5E1;border-bottom:2px solid #CBD5E1;background:#F8FAFC;";
            if (opts.divider) rs += "border-bottom:1px solid #E2E8F0;";
            if (opts.topBorder) rs += "border-top:1px solid #E2E8F0;";
            if (opts.bottomBorder) rs += "border-bottom:2px solid #CBD5E1;";
            if (opts.indent) rs += "padding-left:16px;font-size:12px;";
            var ls = opts.bold ? "font-weight:600;color:#111;" : "color:#475569;";
            var vs = "font-weight:700;" + (opts.color ? "color:" + opts.color + ";" : "color:#111;");
            return '<div style="' + rs + '"><span style="' + ls + '">' + escapeHtml(label) + '</span><b style="' + vs + '">' + escapeHtml(value) + '</b></div>';
          };
          // Build the broker copy exactly the way the broker should see it.
          // paidBy=broker → full breakdown (gross share, − pays sales, NET take).
          // paidBy=company → single Broker share line. Sales agent and Company
          // net rows are NEVER printed regardless of mode.
          var body = "";
          body += row("Gross Commission:", fmt(gross), { divider: true });
          body += row("Pre-deduction (" + taxPct + "%):", "−" + fmt(taxAmount), { divider: true, color: "#B45309" });
          body += row("Amount after deduction:", fmt(grossAfterTax), { emphasized: true, bold: true });
          body += row("Broker share (" + brokerPct + "%):", fmt(brokerShare), { color: "#5B21B6" });
          if (paidByBroker && hasSales) {
            body += row("Broker pays sales (" + salesAgentName + "):", "−" + fmt(manualAmount), { indent: true, color: "#B45309" });
            body += row("Broker NET take:", fmt(brokerNetTake), { topBorder: true, bold: true, color: "#5B21B6" });
          }
          body += row("ARO theoretical (" + aroPct + "%):", fmt(aroTheoretical), { bottomBorder: true });

          var printRoot = document.createElement("div");
          printRoot.id = ROOT_ID;
          printRoot.innerHTML =
            '<div style="padding:32px;font-family:system-ui,-apple-system,sans-serif;font-size:13px;line-height:1.8;color:#111;background:#fff;">' +
              '<div style="margin-bottom:14px;padding-bottom:10px;border-bottom:2px solid #1F2937;">' +
                '<div style="font-size:18px;font-weight:700;color:#111;">💼 Broker Calculation</div>' +
                '<div style="font-size:12px;color:#374151;margin-top:4px;">Deal: <b>' + escapeHtml(leadName) + '</b> · Date: <b>' + escapeHtml(new Date().toLocaleDateString("en-GB")) + '</b></div>' +
              '</div>' + body +
            '</div>';
          document.body.appendChild(printRoot);

          // ── ONE DOCUMENT, TWO WAYS OF DELIVERING IT (2026-09-11) ──────────
          //
          // `printRoot` above is the broker copy, built exactly as it always
          // was. What changed is that `window.print()` is no longer the only
          // way it can leave the screen: in the iOS and Android WebViews
          // window.print does not exist, so the button did NOTHING — no dialog,
          // no error, no log line.
          //
          // The app path RASTERISES THIS VERY NODE. It does not rebuild the
          // document: a second builder for the same commission figures could
          // drift from the one the website prints, in a place nobody would
          // look. Same node, so same layout, same numbers, same date line —
          // by construction, not by review.
          //
          // pdfShareSupport() owns the branch and lives at module scope in
          // utils/brokerPdf.js so a build gate can execute it. It checks
          // NATIVE FIRST: an app WebView can expose a window.print that exists
          // and does nothing, and trusting that would reproduce the original
          // silent failure.
          var mode = pdfShareSupport();

          if (mode === "print") {
            document.body.classList.add("printing-broker");
            // Defer so the print CSS has a tick to apply before the dialog opens.
            setTimeout(function(){
              try { window.print(); } catch(_){}
              // Cleanup runs after the dialog returns (print or cancel).
              setTimeout(cleanup, 200);
            }, 80);
            return;
          }

          if (mode !== "share") {
            // NEVER SILENT AGAIN. "stale" means the installed app predates the
            // Share/Filesystem plugins — capacitor.config.ts points the WebView
            // at the live site, so this JS is already there while the native
            // half is not, and the only honest thing to do is name the update.
            cleanup();
            setBrokerPrintErr(PRINT_UNSUPPORTED_MSG[mode] || PRINT_UNSUPPORTED_MSG.none);
            return;
          }

          // ── APP: rasterise -> PDF -> native share sheet ──────────────────
          // captureAndShare owns every step that used to be inline here: the
          // off-screen layout (html2canvas cannot photograph a display:none
          // node, and the print stylesheet hides this one), the A4 width that
          // keeps the line breaks identical to the printed page, the two-frame
          // wait, restoring the node, and treating a dismissed share sheet as a
          // dismissal rather than a failure. Four surfaces needed all of it.
          setBrokerPrinting(true);
          setBrokerPrintErr("");
          captureAndShare(printRoot, {
            layout: "fit",                 // one screenful, one page — as printed
            filename: pdfFilename("broker", leadName, Date.now()),
            title: "Broker Calculation — " + leadName
          })
            .then(function(){ setBrokerPrinting(false); cleanup(); })
            .catch(function(ex){
              setBrokerPrinting(false);
              cleanup();
              setBrokerPrintErr(String((ex && ex.message) || "Could not create the PDF."));
            });
        } catch(ex){
          setBrokerPrinting(false);
          cleanup();
          setBrokerPrintErr(String((ex && ex.message) || "Could not create the PDF."));
        }
      };
      return <Modal show={true} onClose={function(){ setBrokerCalcFor(null); setBrokerPrintErr(""); }} title={"💼 Broker Calculation — " + leadName} w={520}>
        <style>{
          // Phase R-13.3 — display-based "print only this element" pattern.
          // The previous visibility:hidden approach hid paint but left the
          // underlying document's full scroll height intact, so the browser
          // still allocated N print pages (one per viewport-worth of hidden
          // content). Switching to display:none on body's direct children
          // collapses body height to the injected #broker-print-root and
          // produces exactly one printed page. #broker-print-root is built
          // in printNow() as a sibling of #root (not nested inside it), so
          // hiding body > * doesn't take the print copy down with it.
          "@media print {" +
          "  body.printing-broker > * { display: none !important; }" +
          "  body.printing-broker > #broker-print-root { display: block !important; }" +
          "  body.printing-broker { background: #fff !important; }" +
          "}" +
          // Off-screen by default; only revealed inside @media print on a
          // .printing-broker body. Belt-and-suspenders against a stray
          // Ctrl+P leaking a half-built print root.
          "#broker-print-root { display: none; }" +
          "@page { margin: 1cm; }"
        }</style>
        <div style={{ fontSize:13, lineHeight:1.8, color:C.text }}>
          {/* Internal title block — visible on screen as the modal's own
              header. The printed broker copy gets its own header built in
              printNow(); this one is screen-only by virtue of living
              inside #root, which is hidden by the print stylesheet. */}
          <div style={{ marginBottom:14, paddingBottom:10, borderBottom:"2px solid #1F2937" }}>
            <div style={{ fontSize:18, fontWeight:700, color:"#111" }}>💼 Broker Calculation</div>
            <div style={{ fontSize:12, color:"#374151", marginTop:4 }}>
              Deal: <b>{leadName}</b> &nbsp;·&nbsp; Date: <b>{new Date().toLocaleDateString("en-GB")}</b>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"6px 0", borderBottom:"1px solid #E2E8F0" }}>
            <span style={{ color:C.textLight }}>Gross Commission:</span><b style={{ textAlign:"right" }}>{fmt(gross)}</b>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"6px 0", borderBottom:"1px solid #E2E8F0" }}>
            <span style={{ color:C.textLight }}>Pre-deduction ({taxPct}%):</span><b style={{ textAlign:"right", color:"#B45309" }}>−{fmt(taxAmount)}</b>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"8px 0", borderTop:"2px solid #CBD5E1", borderBottom:"2px solid #CBD5E1", background:"#F8FAFC" }}>
            <span style={{ color:C.text, fontWeight:600 }}>Amount after deduction:</span><b style={{ textAlign:"right" }}>{fmt(grossAfterTax)}</b>
          </div>
          {/* Phase R-13.3 — Broker share rows on screen. Flex with
              justify-content:space-between hard-rights the amount; the
              earlier grid 1fr/auto rendering left this row's value butted
              against the label. The printed broker copy is built in
              printNow() as a separate body-level DOM tree — these screen
              rows are NOT what's printed. */}
          <div className="screen-only" style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:6, padding:"6px 0", marginTop:4 }}>
            <span style={{ color:C.textLight }}>Broker share ({brokerPct}%):</span><b style={{ color:"#5B21B6" }}>{fmt(brokerShare)}</b>
          </div>
          {paidByBroker && hasSales && <div className="screen-only" style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:6, padding:"2px 0 6px 16px", fontSize:12, color:"#B45309" }}>
            <span>Broker pays sales ({salesAgentName}):</span><b>−{fmt(manualAmount)}</b>
          </div>}
          {paidByBroker && hasSales && <div className="screen-only" style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:6, padding:"6px 0", borderTop:"1px solid #E2E8F0" }}>
            <span style={{ color:C.text, fontWeight:600 }}>Broker NET take:</span><b style={{ color:"#5B21B6" }}>{fmt(brokerNetTake)}</b>
          </div>}
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"6px 0", borderBottom:"2px solid #CBD5E1" }}>
            <span style={{ color:C.textLight }}>ARO theoretical ({aroPct}%):</span><b style={{ textAlign:"right" }}>{fmt(aroTheoretical)}</b>
          </div>
          {/* Sales agent + Company net rows — admin-only (screen). When
              paidBy=broker, the sales line shows the "(paid by broker)"
              suffix so admin reads it correctly against the broker NET. */}
          {hasSales && <div className="screen-only" style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"6px 0", marginTop:8, color:C.textLight, fontSize:12 }}>
            <span>Sales agent ({salesAgentName}){paidByBroker ? " (paid by broker)" : ""}:</span><b style={{ textAlign:"right" }}>{fmt(manualAmount)}</b>
          </div>}
          {p.cu.role === "admin" && <div className="screen-only" style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"10px 0 4px", borderTop:"2px solid #15803D", marginTop:hasSales ? 0 : 8, color:"#15803D", fontWeight:700 }}>
            <span>Company net (after broker{(hasSales && !paidByBroker) ? " + sales" : ""}):</span><span style={{ textAlign:"right" }}>{fmt(companyNet)}</span>
          </div>}
        </div>
        {/* THE BUTTON SAYS WHAT IT WILL DO, per platform. On the app it does not
            open a print dialog — it builds a PDF and opens the OS share sheet,
            which is where WhatsApp and "Save to Files" live. A label promising
            a dialog that is not coming is how the original silence started. */}
        {brokerPrintErr && <div style={{ marginTop:14, padding:"9px 12px", borderRadius:8, background:"#FEE2E2", border:"1px solid #FCA5A5", color:"#B91C1C", fontSize:12, lineHeight:1.5 }}>{brokerPrintErr}</div>}
        <div style={{ display:"flex", gap:10, marginTop:18 }}>
          <Btn outline onClick={brokerPrinting ? undefined : printNow} style={{ flex:1, opacity:brokerPrinting?0.6:1, cursor:brokerPrinting?"wait":"pointer" }}>
            {brokerPrinting ? "⏳ Preparing PDF…" : (pdfShareSupport() === "print" ? "📄 Print for Broker" : "📄 Share PDF for Broker")}
          </Btn>
          <Btn onClick={function(){ setBrokerCalcFor(null); setBrokerPrintErr(""); }} style={{ flex:1 }}>✕ Close</Btn>
        </div>
      </Modal>;
    })()}
    {/* Phase R-14 — Ambassador Calculation read-only modal. Mirrors the Broker
        Calculation modal but for ambassador deals: Gross → Developer Tax → ARO
        Net → Team Total → ARO Keep. No print path (recipients are internal staff,
        so there's no external party needing a printout). Reads the stored
        ambassadorSplit computed fields (single source of truth). */}
    {ambassadorCalcFor && (function(){
      var c = ambassadorCalcFor;
      var amb = c.ambassadorSplit || {};
      var recips = amb.recipients || [];
      var gross = Number(amb.grossCommission || 0);
      var taxEnabled = !!amb.developerTaxEnabled;
      var taxPct = Number(amb.developerTaxRate || 0);
      var taxAmount = Number(amb.developerTaxAmount || 0);
      var aroNet = Number(amb.aroNetTotal || 0);
      var teamTotal = recips.reduce(function(s, r){ return s + Number(r.amount || 0); }, 0);
      var aroKeep = aroNet - teamTotal;
      var fmt = function(n){ return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " EGP"; };
      var leadName = (c.snapshot && c.snapshot.customerName) || "(deal)";
      var roleLbl = function(role){ return role === "team_leader" ? "Team Lead" : role === "manager" ? "Manager" : role === "director" ? "Director" : "Sales"; };
      return <Modal show={true} onClose={function(){ setAmbassadorCalcFor(null); }} title={"🎯 Ambassador Calculation — " + leadName} w={520}>
        <div style={{ fontSize:13, lineHeight:1.8, color:C.text }}>
          <div style={{ marginBottom:14, paddingBottom:10, borderBottom:"2px solid #B45309" }}>
            <div style={{ fontSize:18, fontWeight:700, color:"#111" }}>🎯 Ambassador Calculation</div>
            <div style={{ fontSize:12, color:"#374151", marginTop:4 }}>
              Deal: <b>{leadName}</b> &nbsp;·&nbsp; Commission: <b>{Number(amb.commissionRate || 0)}%</b>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"6px 0", borderBottom:"1px solid #E2E8F0" }}>
            <span style={{ color:C.textLight }}>Gross Commission:</span><b style={{ textAlign:"right" }}>{fmt(gross)}</b>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"6px 0", borderBottom:"1px solid #E2E8F0" }}>
            <span style={{ color:C.textLight }}>Developer Tax{taxEnabled ? " (" + taxPct + "%)" : " (disabled)"}:</span><b style={{ textAlign:"right", color:"#B45309" }}>{taxEnabled ? "−" : ""}{fmt(taxAmount)}</b>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"8px 0", borderTop:"2px solid #FCD34D", borderBottom:"2px solid #FCD34D", background:"#FFFBEB" }}>
            <span style={{ color:C.text, fontWeight:600 }}>ARO Net:</span><b style={{ textAlign:"right", color:"#B45309" }}>{fmt(aroNet)}</b>
          </div>
          {/* Team breakdown */}
          {recips.length > 0 && <div style={{ marginTop:8 }}>
            {recips.map(function(r, idx){
              return <div key={String(r._id) || idx} style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"4px 0 4px 16px", fontSize:12, color:C.textLight }}>
                <span>{r.userName || "(unknown)"} ({roleLbl(r.userRole)}):</span><b style={{ textAlign:"right", color:C.text }}>{fmt(r.amount)}</b>
              </div>;
            })}
          </div>}
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"6px 0", marginTop:4, borderTop:"1px solid #E2E8F0" }}>
            <span style={{ color:C.text, fontWeight:600 }}>Team Total:</span><b style={{ textAlign:"right" }}>{fmt(teamTotal)}</b>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:6, padding:"10px 0 4px", borderTop:"2px solid #15803D", marginTop:4, color: aroKeep < 0 ? "#B91C1C" : "#15803D", fontWeight:700 }}>
            <span>ARO Keep (ARO Net − Team Total):</span><span style={{ textAlign:"right" }}>{fmt(aroKeep)}</span>
          </div>
        </div>
        <div style={{ display:"flex", gap:10, marginTop:18 }}>
          <Btn onClick={function(){ setAmbassadorCalcFor(null); }} style={{ flex:1 }}>✕ Close</Btn>
        </div>
      </Modal>;
    })()}
    {/* Phase R-6 polish — claim-data backfill modal for pre-R-5 cycles. */}
    {backfillTarget && <CommissionClaimBackfillModal
      commission={backfillTarget.commission}
      cycle={backfillTarget.cycle}
      writeCommission={writeCommission}
      onClose={function(){ setBackfillTarget(null); }}/>}
    {/* Phase R-6 modals — Add/Edit Expense + Manage Categories. Both refresh
        the P&L via pnlReloadTick; Manage Categories also writes back the
        latest list so the dropdowns refresh without a roundtrip. */}
    {(addingExpense || editingExpense) && <AddExpenseModal
      target={editingExpense}
      isMobile={p.isMobile}
      categories={expenseCategories}
      token={p.token}
      onSaved={function(){ triggerPnlReload(); }}
      onClose={function(){ setAddingExpense(false); setEditingExpense(null); }}/>}
    {managingCategories && <ManageCategoriesModal
      categories={expenseCategories}
      token={p.token}
      onClose={function(latest){
        setManagingCategories(false);
        if (Array.isArray(latest)) setExpenseCategories(latest);
        // Refetch P&L so any rename/archive reflects in the table category column.
        triggerPnlReload();
      }}/>}

    {/* Phase R-7 — manual revert confirm modal. Type CONFIRM to unlock. Admin
        only; the parent button is gated on role+status already. */}
    {revertTarget && <div className="crm-modal" style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.52)",
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16
    }} onClick={function(){ if (!revertBusy) { setRevertTarget(null); setRevertConfirm(""); } }}>
      <div onClick={function(e){ e.stopPropagation(); }} style={{
        background:"#fff", borderRadius:14, padding:"22px 22px",
        width:"100%", maxWidth:480, boxShadow:"0 24px 64px rgba(0,0,0,0.2)"
      }}>
        <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:8 }}>Revert to Active</div>
        <div style={{ fontSize:13, color:C.text, marginBottom:14, lineHeight:1.55 }}>
          Revert <b>{(revertTarget.snapshot && revertTarget.snapshot.customerName) || "this commission"}</b> from <b>Fully paid</b> back to <b>Active</b>?
          Cycles are NOT modified — only the commission's status. Use only if data was entered incorrectly.
        </div>
        <div style={{ fontSize:12, color:C.textLight, marginBottom:6 }}>Type <b style={{ color:C.danger }}>CONFIRM</b> to enable:</div>
        <input
          type="text"
          value={revertConfirm}
          onChange={function(e){ setRevertConfirm(e.target.value); }}
          autoFocus
          disabled={revertBusy}
          style={{ width:"100%", padding:"10px 12px", border:"1px solid "+C.border, borderRadius:8, fontSize:13, fontFamily:"'SF Mono', 'Consolas', 'Monaco', monospace", boxSizing:"border-box" }}
        />
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:16 }}>
          <button onClick={function(){ setRevertTarget(null); setRevertConfirm(""); }} disabled={revertBusy} style={{
            padding:"8px 16px", borderRadius:8, fontSize:12, fontWeight:600,
            border:"1px solid "+C.border, background:"#fff", color:C.textLight,
            cursor: revertBusy ? "wait" : "pointer"
          }}>Cancel</button>
          <button
            onClick={async function(){
              if (revertConfirm !== "CONFIRM" || revertBusy) return;
              setRevertBusy(true);
              try {
                await writeCommission("PATCH", "/api/commissions/" + revertTarget._id + "/revert-to-active", {}, "Reverted to Active");
                setRevertTarget(null); setRevertConfirm("");
              } catch(_){ /* writeCommission already toasts */ }
              setRevertBusy(false);
            }}
            disabled={revertConfirm !== "CONFIRM" || revertBusy}
            style={{
              padding:"8px 16px", borderRadius:8, fontSize:12, fontWeight:700,
              border:"1px solid "+C.warning,
              background: (revertConfirm === "CONFIRM" && !revertBusy) ? C.warning : "#F8FAFC",
              color: (revertConfirm === "CONFIRM" && !revertBusy) ? "#FFF" : C.textLight,
              cursor: (revertConfirm !== "CONFIRM" || revertBusy) ? "not-allowed" : "pointer"
            }}>{revertBusy ? "Reverting…" : "Revert to Active"}</button>
        </div>
      </div>
    </div>}

    {/* Toast — top-right transient feedback. Auto-dismisses via useEffect. */}
    {toast && <div style={{
      position:"fixed", top:80, right:16, zIndex:800,
      padding:"10px 18px", borderRadius:10,
      background: toast.kind === "err" ? "#FEF2F2" : "#F0FDF4",
      color: toast.kind === "err" ? "#B91C1C" : "#15803D",
      border:"1px solid " + (toast.kind === "err" ? "#FCA5A5" : "#86EFAC"),
      fontSize:13, fontWeight:600,
      boxShadow:"0 8px 24px rgba(0,0,0,0.08)"
    }}>{toast.msg}</div>}
  </div>;
};


// =====================================================================
// Phase R-6 polish #2 — Diagnostics page (admin only). Surfaces Leads that
// should have an active commission but don't (auto-create likely failed at
// the DoneDeal transition because the lead had no agentId, OR a hook
// exception silently dropped the write). Self-service "Create commission"
// button calls POST /api/diagnose/create-commission per row.
// Server-side gate: strictAdminOnly. Client-side gate at the page-switch.
// =====================================================================
var DiagnosticsPage = function(p) {
  var [rows, setRows] = useState(null);     // null=loading, []=empty, [...]=data
  var [err, setErr] = useState("");
  var [busyId, setBusyId] = useState(null); // leadId currently being created
  var [toast, setToast] = useState(null);   // {kind:"ok"|"err", msg}
  // Bulk-backfill state (admin HTTP triggers — same logic as the script files).
  var [bulkRunning, setBulkRunning] = useState(null); // "zombies" | "missing" | null
  var [bulkResult,  setBulkResult]  = useState(null); // { type, summary } or { type, error }
  // Lookup-by-leadId state — feeds the "Delete legacy bogus commissions" flow.
  var [lookupLeadId, setLookupLeadId] = useState("");
  var [lookupResult, setLookupResult] = useState(null); // server response or null
  var [lookupBusy,   setLookupBusy]   = useState(false);
  var [lookupErr,    setLookupErr]    = useState("");
  // Targeted-delete state. deleteIds is a free-text textarea (comma/whitespace
  // separated) so admin can paste IDs from anywhere — the lookup table's
  // "Add" buttons just append to this string. Source of truth for the
  // confirm modal's N-count and for the actual POST body.
  var [deleteIds,    setDeleteIds]    = useState("");
  var [confirmOpen,  setConfirmOpen]  = useState(false);
  var [confirmText,  setConfirmText]  = useState("");
  var [deleteBusy,   setDeleteBusy]   = useState(false);
  var [deleteResult, setDeleteResult] = useState(null);

  var refresh = function(){
    setRows(null); setErr("");
    apiFetch("/api/diagnose/missing-commissions", "GET", null, p.token)
      .then(function(d){ setRows((d && d.data) || []); })
      .catch(function(e){ setRows([]); setErr((e && e.message) || "Load failed"); });
  };
  useEffect(refresh, [p.token]);

  useEffect(function(){
    if (!toast) return;
    var h = setTimeout(function(){ setToast(null); }, 3000);
    return function(){ clearTimeout(h); };
  }, [toast]);

  var createOne = async function(row){
    setBusyId(row.leadId);
    try {
      var r = await apiFetch("/api/diagnose/create-commission", "POST", { leadId: row.leadId }, p.token);
      setRows(function(prev){ return (prev || []).filter(function(x){ return x.leadId !== row.leadId; }); });
      setToast({ kind:"ok", msg:"Commission created for " + (row.customerName || row.leadId) + " (" + String(r.commissionId).slice(-6) + ")" });
    } catch(e) {
      setToast({ kind:"err", msg:(e && e.message) || "Create failed" });
    }
    setBusyId(null);
  };

  // Bulk backfill — calls POST /api/admin/run-backfill?type=<type>. Sync
  // request: the FE waits for the server to finish the entire backfill.
  // After completion: refetch the missing-commissions list (only relevant
  // for type="missing", but cheap to do for the others — the list reflects
  // the new state either way).
  var runBulk = async function(type){
    setBulkRunning(type); setBulkResult(null);
    try {
      var summary = await apiFetch("/api/admin/run-backfill?type=" + encodeURIComponent(type), "POST", {}, p.token);
      setBulkResult({ type: type, summary: summary });
      var msg;
      if (type === "zombies") {
        msg = "Zombie cleanup done — " + (summary.updated || 0) + " flipped to fully_paid";
      } else if (type === "missing") {
        msg = "Missing-commission backfill done — " + (summary.created || 0) + " created, " + ((summary.noAgent && summary.noAgent.length) || 0) + " skipped (no agent)";
      } else if (type === "fully_paid_with_active_cycles") {
        msg = "Fully-paid revert done — " + (summary.updated || 0) + " flipped back to active";
      } else if (type === "active_with_no_workable_cycle") {
        msg = "Revive-broken backfill done — " + (summary.updated || 0) + " fresh cycle(s) added";
      } else {
        msg = "Backfill done";
      }
      setToast({ kind:"ok", msg: msg });
      refresh();
    } catch(e) {
      setBulkResult({ type: type, error: (e && e.message) || "Backfill failed" });
      setToast({ kind:"err", msg:(e && e.message) || "Backfill failed" });
    }
    setBulkRunning(null);
  };

  var fmtDate = function(d){ if (!d) return "—"; try { return new Date(d).toLocaleDateString("en-GB"); } catch(_){ return String(d); } };
  var fmtMoney = function(n){ try { return Math.round(Number(n||0)).toLocaleString(); } catch(_){ return String(n); } };

  // Parse the deleteIds textarea into a deduped list of trimmed tokens.
  // Accepts comma, whitespace, or newline separation; ignores empty tokens.
  var parsedIds = (function(){
    var seen = Object.create(null);
    var out = [];
    String(deleteIds || "").split(/[\s,]+/).forEach(function(t){
      var v = String(t || "").trim();
      if (!v || seen[v]) return;
      seen[v] = true;
      out.push(v);
    });
    return out;
  })();

  var runLookup = async function(){
    var id = String(lookupLeadId || "").trim();
    if (!/^[0-9a-fA-F]{24}$/.test(id)) {
      setLookupErr("Lead ID must be a 24-character hex string");
      return;
    }
    setLookupBusy(true); setLookupErr(""); setLookupResult(null);
    try {
      var r = await apiFetch("/api/diagnose/commissions-for-lead/" + encodeURIComponent(id), "GET", null, p.token);
      setLookupResult(r || null);
      if (!r || !r.data || r.data.length === 0) {
        setToast({ kind:"ok", msg:"No commissions found for that lead." });
      }
    } catch(e) {
      setLookupErr((e && e.message) || "Lookup failed");
    }
    setLookupBusy(false);
  };

  var addIdToSelection = function(id){
    setDeleteIds(function(prev){
      var existing = String(prev || "").trim();
      if (existing.split(/[\s,]+/).indexOf(id) >= 0) return existing; // already present
      return existing ? (existing + ", " + id) : id;
    });
  };

  var openConfirm = function(){
    if (parsedIds.length === 0) {
      setToast({ kind:"err", msg:"Add at least one commission ID to the deletion list." });
      return;
    }
    setConfirmText("");
    setConfirmOpen(true);
  };

  var runDelete = async function(){
    if (confirmText !== "DELETE") return;
    setDeleteBusy(true); setDeleteResult(null);
    try {
      var summary = await apiFetch(
        "/api/admin/run-backfill?type=delete_bogus_legacy_commissions",
        "POST",
        { commissionIds: parsedIds },
        p.token
      );
      setDeleteResult({ summary: summary });
      setToast({ kind:"ok", msg:"Deleted " + ((summary.deleted && summary.deleted.length) || 0) + " · skipped " + ((summary.skipped && summary.skipped.length) || 0) });
      setDeleteIds("");
      setConfirmOpen(false);
      setConfirmText("");
      // Refresh the lookup if the same lead is still on screen — its rows
      // may have just been deleted.
      if (lookupResult && lookupResult.leadId) {
        setLookupLeadId(lookupResult.leadId);
        try { await runLookup(); } catch(_){}
      }
    } catch(e) {
      setDeleteResult({ error: (e && e.message) || "Delete failed" });
      setToast({ kind:"err", msg:(e && e.message) || "Delete failed" });
    }
    setDeleteBusy(false);
  };

  return <div style={{ padding:"24px 16px", maxWidth:1100, margin:"0 auto" }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
      <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:C.text }}>Diagnostics</h1>
      <button onClick={refresh} disabled={rows === null} style={{
        padding:"6px 14px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff",
        cursor: rows === null ? "wait" : "pointer", fontSize:12, fontWeight:600, color:C.textLight
      }}>Refresh</button>
    </div>

    {/* Bulk operations — same logic as the backfill-*.js scripts, exposed
        as admin HTTP triggers since Railway shell access was removed. */}
    <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:14, padding:"16px 18px", marginBottom:14 }}>
      <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:6 }}>Bulk operations</div>
      <div style={{ fontSize:11, color:C.textLight, marginBottom:12 }}>
        Both operations are idempotent — running again finds no work because the previous run already cleared the targets. Spinner runs while the server processes the request; do not navigate away mid-run.
      </div>
      <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
        <button onClick={function(){ runBulk("zombies"); }} disabled={!!bulkRunning}
          title="Flip status=active → fully_paid on commissions whose cycles are all terminal (paid_to_team or cancelled)"
          style={{
            padding:"8px 14px", borderRadius:8, fontSize:12, fontWeight:600,
            border: "1px solid " + C.accent,
            background: bulkRunning === "zombies" ? C.accent + "30" : C.accent + "12",
            color: C.accent,
            cursor: bulkRunning ? "wait" : "pointer"
          }}>
          {bulkRunning === "zombies" ? "Running zombie cleanup…" : "Run zombie cleanup"}
        </button>
        <button onClick={function(){ runBulk("missing"); }} disabled={!!bulkRunning}
          title="Create active commissions for DoneDeal leads that don't have one (skipping leads with no agent)"
          style={{
            padding:"8px 14px", borderRadius:8, fontSize:12, fontWeight:600,
            border: "1px solid " + C.accent,
            background: bulkRunning === "missing" ? C.accent + "30" : C.accent + "12",
            color: C.accent,
            cursor: bulkRunning ? "wait" : "pointer"
          }}>
          {bulkRunning === "missing" ? "Running missing-commission backfill…" : "Run missing-commission backfill"}
        </button>
        <button onClick={function(){ runBulk("fully_paid_with_active_cycles"); }} disabled={!!bulkRunning}
          title="Revert status=fully_paid → active on commissions that have a non-terminal cycle (admin clicked Add Cycle on a fully-paid deal before the forward fix)"
          style={{
            padding:"8px 14px", borderRadius:8, fontSize:12, fontWeight:600,
            border: "1px solid " + C.accent,
            background: bulkRunning === "fully_paid_with_active_cycles" ? C.accent + "30" : C.accent + "12",
            color: C.accent,
            cursor: bulkRunning ? "wait" : "pointer"
          }}>
          {bulkRunning === "fully_paid_with_active_cycles" ? "Running fully-paid revert…" : "Run fully-paid revert"}
        </button>
        <button onClick={function(){ runBulk("active_with_no_workable_cycle"); }} disabled={!!bulkRunning}
          title="Add a fresh pending_claim cycle to active commissions whose cycles are all terminal — typically revived commissions where cascadeCommissionCancel left the cycles cancelled"
          style={{
            padding:"8px 14px", borderRadius:8, fontSize:12, fontWeight:600,
            border: "1px solid " + C.accent,
            background: bulkRunning === "active_with_no_workable_cycle" ? C.accent + "30" : C.accent + "12",
            color: C.accent,
            cursor: bulkRunning ? "wait" : "pointer"
          }}>
          {bulkRunning === "active_with_no_workable_cycle" ? "Running revive-broken backfill…" : "Run revive-broken backfill"}
        </button>
      </div>
      {bulkResult && <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #F1F5F9" }}>
        <div style={{ fontSize:11, color:C.textLight, marginBottom:6 }}>
          Last run · type=<b style={{ color:C.text }}>{bulkResult.type}</b>
        </div>
        {bulkResult.error && <div style={{ padding:"10px 12px", background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:8, color:"#B91C1C", fontSize:12 }}>
          Error: {bulkResult.error}
        </div>}
        {bulkResult.summary && <pre style={{
          margin:0, padding:"10px 12px",
          background:"#0F172A", color:"#E2E8F0", borderRadius:8,
          fontSize:11, lineHeight:1.55, overflow:"auto", maxHeight:360,
          fontFamily:"'SF Mono', 'Consolas', 'Monaco', monospace",
          whiteSpace:"pre-wrap", wordBreak:"break-word"
        }}>{JSON.stringify(bulkResult.summary, null, 2)}</pre>}
      </div>}
    </div>

    {/* Lookup all commissions for a given lead. Surfaces every record
        regardless of status so admin can identify legacy / bogus records
        (e.g. a cancelled doc whose cycles still carry stale claim amounts
        that bleed into Annual Summary). Rows have an "Add to delete list"
        action that appends the commissionId to the textarea below. */}
    <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:14, padding:"16px 18px", marginBottom:14 }}>
      <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>Lookup commissions for lead</div>
      <div style={{ fontSize:11, color:C.textLight, marginBottom:10 }}>
        Paste a lead's ObjectId to list every commission attached to it (active, cancelled, fully_paid). Use this to identify legacy records before adding them to the deletion list.
      </div>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
        <input
          type="text"
          value={lookupLeadId}
          onChange={function(e){ setLookupLeadId(e.target.value); }}
          placeholder="24-char hex lead ID"
          style={{ flex:"1 1 280px", minWidth:240, padding:"8px 12px", border:"1px solid "+C.border, borderRadius:8, fontSize:12, fontFamily:"'SF Mono', 'Consolas', 'Monaco', monospace" }}
        />
        <button onClick={runLookup} disabled={lookupBusy} style={{
          padding:"8px 14px", borderRadius:8, fontSize:12, fontWeight:600,
          border:"1px solid "+C.accent, background: lookupBusy ? C.accent+"30" : C.accent+"12",
          color:C.accent, cursor: lookupBusy ? "wait" : "pointer"
        }}>{lookupBusy ? "Looking up…" : "Lookup"}</button>
      </div>
      {lookupErr && <div style={{ marginTop:10, padding:"8px 12px", background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:8, color:"#B91C1C", fontSize:12 }}>{lookupErr}</div>}
      {lookupResult && lookupResult.data && lookupResult.data.length > 0 && <div style={{ marginTop:12 }}>
        <div style={{ fontSize:11, color:C.textLight, marginBottom:8 }}>
          Lead: <b style={{ color:C.text }}>{lookupResult.leadName || "(unknown)"}</b>
          {lookupResult.leadPhone ? " · " + lookupResult.leadPhone : ""}
          {lookupResult.project ? " · " + lookupResult.project : ""}
          {" · "} <b style={{ color:C.text }}>{lookupResult.count}</b> commission record(s)
        </div>
        <div style={{ border:"1px solid "+C.border, borderRadius:10, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
            <thead>
              <tr style={{ background:"#F8FAFC", borderBottom:"1px solid "+C.border }}>
                <th style={{ textAlign:"start", padding:"8px 10px", fontWeight:700, color:C.textLight }}>Commission ID</th>
                <th style={{ textAlign:"start", padding:"8px 10px", fontWeight:700, color:C.textLight }}>Status</th>
                <th style={{ textAlign:"start", padding:"8px 10px", fontWeight:700, color:C.textLight }}>Created</th>
                <th style={{ textAlign:"start", padding:"8px 10px", fontWeight:700, color:C.textLight }}>Agent</th>
                <th style={{ textAlign:"end",   padding:"8px 10px", fontWeight:700, color:C.textLight }}>Cycles</th>
                <th style={{ textAlign:"end",   padding:"8px 10px", fontWeight:700, color:C.textLight }}>Claim sum</th>
                <th style={{ textAlign:"end",   padding:"8px 10px", fontWeight:700, color:C.textLight }}>Received sum</th>
                <th style={{ textAlign:"end",   padding:"8px 10px", fontWeight:700, color:C.textLight, width:120 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {lookupResult.data.map(function(row){
                var isActive = row.status === "active";
                var selected = parsedIds.indexOf(row.commissionId) >= 0;
                var statusBg = row.status === "active" ? "#DCFCE7" : (row.status === "cancelled" ? "#FEE2E2" : "#E0F2FE");
                var statusFg = row.status === "active" ? "#15803D" : (row.status === "cancelled" ? "#B91C1C" : "#0284C7");
                return <tr key={row.commissionId} style={{ borderTop:"1px solid #F1F5F9" }}>
                  <td style={{ padding:"8px 10px", fontFamily:"'SF Mono', 'Consolas', 'Monaco', monospace", fontSize:10 }}>{row.commissionId}</td>
                  <td style={{ padding:"8px 10px" }}>
                    <span style={{ padding:"2px 8px", borderRadius:999, background:statusBg, color:statusFg, fontSize:10, fontWeight:700 }}>{row.status}</span>
                  </td>
                  <td style={{ padding:"8px 10px", color:C.textLight }}>{fmtDate(row.createdAt)}</td>
                  <td style={{ padding:"8px 10px", color:C.text }}>{row.agentName || "—"}</td>
                  <td style={{ padding:"8px 10px", textAlign:"end" }}>{row.cycleCount}</td>
                  <td style={{ padding:"8px 10px", textAlign:"end", fontFamily:"'SF Mono', 'Consolas', 'Monaco', monospace" }}>{fmtMoney(row.claimSum)}</td>
                  <td style={{ padding:"8px 10px", textAlign:"end", fontFamily:"'SF Mono', 'Consolas', 'Monaco', monospace" }}>{fmtMoney(row.receivedSum)}</td>
                  <td style={{ padding:"8px 10px", textAlign:"end" }}>
                    <button
                      onClick={function(){ addIdToSelection(row.commissionId); }}
                      disabled={isActive || selected}
                      title={isActive ? "Active commissions are protected from deletion" : (selected ? "Already in deletion list" : "Append this ID to the deletion list below")}
                      style={{
                        padding:"4px 10px", borderRadius:6, fontSize:10, fontWeight:600,
                        border:"1px solid "+(isActive ? C.border : C.danger),
                        background:isActive ? "#F8FAFC" : (selected ? "#FEF2F2" : "#FFF"),
                        color:isActive ? C.textLight : (selected ? C.danger : C.danger),
                        cursor: (isActive || selected) ? "not-allowed" : "pointer",
                        opacity: isActive ? 0.6 : 1
                      }}
                    >{isActive ? "Protected" : (selected ? "Queued" : "+ Add to delete")}</button>
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </div>}
      {lookupResult && lookupResult.data && lookupResult.data.length === 0 && <div style={{ marginTop:10, padding:"8px 12px", background:"#F8FAFC", border:"1px solid "+C.border, borderRadius:8, color:C.textLight, fontSize:12 }}>No commissions found for that lead.</div>}
    </div>

    {/* Delete legacy bogus commissions — accepts a free-form list of IDs
        and POSTs to /api/admin/run-backfill?type=delete_bogus_legacy_commissions.
        Two-step confirm: type DELETE in a modal. Active commissions are
        refused server-side; the response surfaces them in `skipped`. */}
    <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:14, padding:"16px 18px", marginBottom:14 }}>
      <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>Delete legacy bogus commissions</div>
      <div style={{ fontSize:11, color:C.textLight, marginBottom:10 }}>
        Permanently removes the listed commission documents. Active commissions are refused server-side; only cancelled / fully_paid records can be deleted via this tool. No undo — use the lookup above to verify each ID first.
      </div>
      <textarea
        value={deleteIds}
        onChange={function(e){ setDeleteIds(e.target.value); }}
        placeholder="Paste commission ObjectIds — comma, space, or newline separated."
        style={{ width:"100%", minHeight:80, padding:"10px 12px", border:"1px solid "+C.border, borderRadius:8, fontSize:11, fontFamily:"'SF Mono', 'Consolas', 'Monaco', monospace", resize:"vertical", boxSizing:"border-box" }}
      />
      <div style={{ display:"flex", gap:8, alignItems:"center", marginTop:10, flexWrap:"wrap" }}>
        <button onClick={openConfirm} disabled={parsedIds.length === 0 || deleteBusy} style={{
          padding:"8px 14px", borderRadius:8, fontSize:12, fontWeight:700,
          border:"1px solid "+C.danger,
          background: parsedIds.length === 0 ? "#F8FAFC" : C.danger,
          color: parsedIds.length === 0 ? C.textLight : "#FFF",
          cursor: (parsedIds.length === 0 || deleteBusy) ? "not-allowed" : "pointer"
        }}>{deleteBusy ? "Deleting…" : ("Delete " + parsedIds.length + " record" + (parsedIds.length === 1 ? "" : "s"))}</button>
        {parsedIds.length > 0 && <button onClick={function(){ setDeleteIds(""); }} disabled={deleteBusy} style={{
          padding:"6px 12px", borderRadius:8, fontSize:11, fontWeight:600,
          border:"1px solid "+C.border, background:"#fff", color:C.textLight,
          cursor: deleteBusy ? "wait" : "pointer"
        }}>Clear list</button>}
        <span style={{ fontSize:11, color:C.textLight }}>{parsedIds.length} unique ID(s) parsed</span>
      </div>
      {deleteResult && <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #F1F5F9" }}>
        {deleteResult.error && <div style={{ padding:"10px 12px", background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:8, color:"#B91C1C", fontSize:12 }}>
          Error: {deleteResult.error}
        </div>}
        {deleteResult.summary && <pre style={{
          margin:0, padding:"10px 12px",
          background:"#0F172A", color:"#E2E8F0", borderRadius:8,
          fontSize:11, lineHeight:1.55, overflow:"auto", maxHeight:360,
          fontFamily:"'SF Mono', 'Consolas', 'Monaco', monospace",
          whiteSpace:"pre-wrap", wordBreak:"break-word"
        }}>{JSON.stringify(deleteResult.summary, null, 2)}</pre>}
      </div>}
    </div>

    <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:14, padding:"16px 18px", marginBottom:14 }}>
      <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>Missing commissions</div>
      <div style={{ fontSize:11, color:C.textLight }}>
        Active DoneDeal leads that have no commission with <b>status="active"</b>. Most common cause: lead had no agent when it transitioned to DoneDeal, so <code>ensureCommissionForLead</code> silently no-op'd. Assign an agent on the lead, then click Create here. Lead-side Active Deals count − this list count = Commissions-side Active Deals count.
      </div>
    </div>

    {rows === null && <div style={{ padding:"40px 16px", textAlign:"center", color:C.textLight }}>Loading…</div>}
    {rows !== null && err && <div style={{ padding:"14px", background:"#FEF2F2", border:"1px solid #FCA5A5", borderRadius:10, color:"#B91C1C", fontSize:13, marginBottom:14 }}>{err}</div>}
    {rows !== null && !err && rows.length === 0 && <div style={{ padding:"40px 16px", textAlign:"center", color:C.textLight, background:"#fff", borderRadius:14, border:"1px solid #E8ECF1" }}>
      ✓ No missing commissions — every active DoneDeal lead has an active commission.
    </div>}
    {rows !== null && rows.length > 0 && <div style={{ background:"#fff", border:"1px solid #E8ECF1", borderRadius:10, overflow:"hidden" }}>
      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
        <thead>
          <tr style={{ background:"#F8FAFC", borderBottom:"1px solid #E8ECF1" }}>
            <th style={{ textAlign:"start", padding:"10px 12px", fontWeight:700, color:C.textLight }}>Customer</th>
            <th style={{ textAlign:"start", padding:"10px 12px", fontWeight:700, color:C.textLight }}>Project</th>
            <th style={{ textAlign:"start", padding:"10px 12px", fontWeight:700, color:C.textLight }}>Deal date</th>
            <th style={{ textAlign:"start", padding:"10px 12px", fontWeight:700, color:C.textLight }}>Agent</th>
            <th style={{ textAlign:"start", padding:"10px 12px", fontWeight:700, color:C.textLight }}>Prior commissions</th>
            <th style={{ textAlign:"end",   padding:"10px 12px", fontWeight:700, color:C.textLight, width:160 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(function(row){
            var blocked = !!row.agentMissing;
            var prior = (row.existingCommissionStatuses && row.existingCommissionStatuses.length > 0)
              ? row.existingCommissionStatuses.join(", ")
              : "—";
            return <tr key={row.leadId} style={{ borderTop:"1px solid #F1F5F9" }}>
              <td style={{ padding:"10px 12px" }}>
                <div style={{ fontWeight:600 }}>{row.customerName || "(unknown)"}</div>
                {row.customerPhone && <div style={{ fontSize:10, color:C.textLight }}>{row.customerPhone}</div>}
              </td>
              <td style={{ padding:"10px 12px", color:C.text }}>{row.projectName || "—"}</td>
              <td style={{ padding:"10px 12px" }}>{fmtDayOnly(row.dealDate) || "—"}</td>
              <td style={{ padding:"10px 12px", color: blocked ? "#B91C1C" : C.text }}>
                {blocked ? <span title="No agent assigned"><b>—</b> <span style={{ fontSize:10 }}>(missing)</span></span> : (row.agentName || "(unknown)")}
              </td>
              <td style={{ padding:"10px 12px", color:C.textLight, fontStyle:"italic" }}>{prior}</td>
              <td style={{ padding:"10px 12px", textAlign:"end" }}>
                <button onClick={function(){ createOne(row); }}
                  disabled={blocked || busyId === row.leadId}
                  title={blocked ? "Assign an agent on this lead first, then retry" : "Create active commission"}
                  style={{
                    padding:"5px 12px", borderRadius:8, fontSize:11, fontWeight:600,
                    border: blocked ? "1px solid #E2E8F0" : "1px solid " + C.accent,
                    background: blocked ? "#F8FAFC" : (busyId === row.leadId ? C.accent + "30" : C.accent + "12"),
                    color: blocked ? C.textLight : C.accent,
                    cursor: blocked ? "not-allowed" : (busyId === row.leadId ? "wait" : "pointer")
                  }}>
                  {busyId === row.leadId ? "Creating…" : "Create commission"}
                </button>
              </td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>}

    {/* Two-step confirm — type DELETE to unlock the destructive button.
        Renders only when admin clicks "Delete N record(s)" above. */}
    {confirmOpen && <div className="crm-modal" style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.52)",
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16
    }} onClick={function(){ if (!deleteBusy) { setConfirmOpen(false); setConfirmText(""); } }}>
      <div onClick={function(e){ e.stopPropagation(); }} style={{
        background:"#fff", borderRadius:14, padding:"22px 22px",
        width:"100%", maxWidth:480, boxShadow:"0 24px 64px rgba(0,0,0,0.2)"
      }}>
        <div style={{ fontSize:16, fontWeight:700, color:C.danger, marginBottom:8 }}>Confirm permanent deletion</div>
        <div style={{ fontSize:13, color:C.text, marginBottom:14, lineHeight:1.55 }}>
          This will permanently delete <b>{parsedIds.length}</b> commission record{parsedIds.length === 1 ? "" : "s"}. No undo.
          Active commissions in this list will be refused server-side.
        </div>
        <div style={{ fontSize:12, color:C.textLight, marginBottom:6 }}>Type <b style={{ color:C.danger }}>DELETE</b> to confirm:</div>
        <input
          type="text"
          value={confirmText}
          onChange={function(e){ setConfirmText(e.target.value); }}
          autoFocus
          disabled={deleteBusy}
          style={{ width:"100%", padding:"10px 12px", border:"1px solid "+C.border, borderRadius:8, fontSize:13, fontFamily:"'SF Mono', 'Consolas', 'Monaco', monospace", boxSizing:"border-box" }}
        />
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:16 }}>
          <button onClick={function(){ setConfirmOpen(false); setConfirmText(""); }} disabled={deleteBusy} style={{
            padding:"8px 16px", borderRadius:8, fontSize:12, fontWeight:600,
            border:"1px solid "+C.border, background:"#fff", color:C.textLight,
            cursor: deleteBusy ? "wait" : "pointer"
          }}>Cancel</button>
          <button onClick={runDelete} disabled={confirmText !== "DELETE" || deleteBusy} style={{
            padding:"8px 16px", borderRadius:8, fontSize:12, fontWeight:700,
            border:"1px solid "+C.danger,
            background: (confirmText === "DELETE" && !deleteBusy) ? C.danger : "#F8FAFC",
            color: (confirmText === "DELETE" && !deleteBusy) ? "#FFF" : C.textLight,
            cursor: (confirmText !== "DELETE" || deleteBusy) ? "not-allowed" : "pointer"
          }}>{deleteBusy ? "Deleting…" : "Permanently delete"}</button>
        </div>
      </div>
    </div>}

    {toast && <div style={{
      position:"fixed", top:80, right:16, zIndex:800,
      padding:"10px 18px", borderRadius:10,
      background: toast.kind === "err" ? "#FEF2F2" : "#F0FDF4",
      color: toast.kind === "err" ? "#B91C1C" : "#15803D",
      border:"1px solid " + (toast.kind === "err" ? "#FCA5A5" : "#86EFAC"),
      fontSize:13, fontWeight:600,
      boxShadow:"0 8px 24px rgba(0,0,0,0.08)"
    }}>{toast.msg}</div>}
  </div>;
};

// ===== MAIN APP =====
export default function CRMApp() {
  var [lang,setLang]=useState((function(){try{return localStorage.getItem("crm_lang")||"en";}catch(e){return "en";}})());
  // Session is restored synchronously from localStorage so the first render
  // already has currentUser/token populated. The previous useEffect-based
  // restore caused a one-render flash of the login screen on every page load,
  // and worse — for deep-link tabs (e.g. /assets/<code>) the chained loadData
  // call could 401 on an expired token and trigger window.location.reload()
  // before the user saw the asset, dropping them at a real login screen on
  // the deep-link URL. Reading synchronously avoids both problems.
  var savedSession = sessionStoreRead();
  var [currentUser,setCurrentUser]=useState(savedSession ? savedSession.user : null);
  var [token,setToken]=useState(savedSession ? savedSession.token : null);
  var [csrfToken,setCsrfToken]=useState(savedSession && savedSession.csrfToken ? savedSession.csrfToken : null);
  // THE ACCESS ENVELOPE — what the server says this caller may see and do.
  // Restored synchronously from the session blob for the same reason the user
  // is: it decides which nav entries and tabs render, so arriving a round-trip
  // late means painting a frame of the wrong UI. Refreshed by the boot /api/me
  // and by the WS events that mean the access rules moved.
  var [access,setAccess]=useState(savedSession && savedSession.access ? savedSession.access : null);
  // Biometric app-lock. Locked on first paint when native + a session exists,
  // so authenticated content never flashes before the prompt. Web => unlocked.
  var BIO_THRESHOLD_MS = 60 * 1000; // re-prompt only after ≥1 min backgrounded
  var [bioLocked,setBioLocked]=useState(isNativePlatformSync() && !!savedSession);
  var [bioPrompting,setBioPrompting]=useState(false);
  var [bioFailed,setBioFailed]=useState(false); // last attempt failed/cancelled → show Retry
  var bgAtRef = useRef(null);     // ms timestamp when app last went to background
  var treeShownRef = useRef(false); // true once the app tree has painted unlocked; a later re-lock covers it instead of unmounting it
  var bioBusyRef = useRef(false); // guard against overlapping authenticate calls
  var bioBusyAtRef = useRef(0);   // when bioBusyRef was set; a flag older than the native timeouts is stale (lib/lockShell.js)
  // Path-based routing for the unauthenticated reset flow. We don't pull in
  // react-router; just observe popstate + a manual dispatch from in-page nav.
  var [authPath,setAuthPath]=useState(function(){ try { return window.location.pathname; } catch(e){ return "/"; } });
  useEffect(function(){
    var onPop = function(){ try { setAuthPath(window.location.pathname); } catch(e){} };
    window.addEventListener("popstate", onPop);
    return function(){ window.removeEventListener("popstate", onPop); };
  }, []);
  // Phase 2 Slice 3 — projectWeights cache rev. Bumped after fetch and after
  // any save so all consumers re-render and read the updated module cache.
  // The actual map lives at module scope (_projWeightsMap) because
  // getProjectWeight is a top-level helper called from many render functions
  // — we just need a state value to trigger re-renders when the cache moves.
  var [projectWeightsRev,setProjectWeightsRev]=useState(0);
  var bumpProjectWeightsRev=useCallback(function(){ setProjectWeightsRev(function(v){return v+1;}); },[]);
  // QR deep-link: when the user lands on /assets/<code> (e.g. from scanning
  // a printed QR with their phone camera) we capture the code into App state
  // ONCE, then pass it as a prop to AssetTrackerPage. Earlier versions of
  // this code re-read window.location.pathname inside AssetTrackerPage's
  // own state initializer, which raced badly with the URL-clearing effect
  // below — for users with a saved session, the URL got cleared before
  // AssetTrackerPage mounted, and the user landed on the list view instead
  // of the detail view. Capturing in App state makes the value survive both
  // the URL clear and the asynchronous saved-session-load round-trip.
  var [deepLinkAssetCode, setDeepLinkAssetCode] = useState(function() {
    try {
      if (typeof window === "undefined" || !window.location) return null;
      var m = (window.location.pathname || "").match(/^\/assets\/([A-Za-z0-9-]+)\/?$/);
      return m ? m[1] : null;
    } catch(e) { return null; }
  });
  // RESTORED PAGE — validated, not trusted.
  //
  // crm_page is written on every nav click and read back synchronously at first
  // paint. It survives a role change, a demotion, and a hand edit in devtools,
  // and until Phase 1c nothing checked it: whatever id it held went straight
  // into renderPage, whose per-case guards were looser than the sidebar on
  // seven pages. That is what made the sidebar/route divergence reachable
  // rather than theoretical.
  //
  // Validated against the SAME rule the sidebar and renderPage use. An id this
  // user may not see — or one that no longer exists — resolves to null, and the
  // dashboard default takes over. renderPage guards again on every render, so
  // this is defence in depth for the FIRST paint, not the only check.
  //
  // savedSession is read a few lines above; currentUser is not in scope yet at
  // initializer time, so the stored user is what we validate against. It is the
  // same object the first paint renders with.
  var [page,setPage]=useState((function(){
    try {
      if (deepLinkAssetCode) return "assets";
      var stored = localStorage.getItem("crm_page");
      if (!stored) return null;
      var env = savedSession && savedSession.access;
      if (!env) return null;                      // no session/envelope -> dashboard default
      // A pre-Phase-5 blob has an envelope with no `pages`. Treat that as "not
      // answered yet" rather than "not allowed": the boot /api/me refreshes it a
      // round-trip later, and renderPage holds on the dashboard until it does.
      if (!accessLoaded(env)) return null;
      return canSeePage(env, stored) ? stored : null;
    } catch(e) { return null; }
  })());
  // Clear the QR deep-link URL once both initializers above have captured
  // their values into state. Without this, a browser refresh after the
  // initial scan would forever bounce back to the asset detail view. Safe
  // to run before AssetTrackerPage mounts because the deep-link code now
  // lives in App state (passed as a prop), not the URL itself.
  useEffect(function() {
    try {
      if (typeof window !== "undefined" && window.location && /^\/assets\/[A-Za-z0-9-]+\/?$/.test(window.location.pathname)) {
        window.history.replaceState(null, "", "/");
      }
    } catch(e) {}
  }, []);
  var [leads,setLeads]=useState([]); var [users,setUsers]=useState([]);
  var [activities,setActivities]=useState([]);
  var [dailyReqs,setDailyReqs]=useState([]);
  var [leadFilter,setLeadFilter]=useState("all");
  var [leadSpecialFilter,setLeadSpecialFilter]=useState(null);
  // The dashboard Meetings tile's period, handed to MeetingsPage on click and
  // consumed there (R7) — the Meetings twin of leadSpecialFilter.
  var [meetingsInit,setMeetingsInit]=useState(null);
  // The Deals tile's period, handed to the Deals page so the click opens the
  // rows the tile counted. Same one-shot contract as meetingsInit: the page
  // consumes it and clears it, and owns its filters from there.
  var [dealsInit,setDealsInit]=useState(null);
  // One-shot initial filter for the Daily Requests page — set from the Sales
  // Dashboard click handlers; the page consumes it on mount and clears it.
  var [drInitFilter,setDrInitFilter]=useState(null);
  // The Daily Requests tile's period, handed to the Daily Requests page the same
  // one-shot way as dealsInit / meetingsInit.
  var [drInit,setDrInit]=useState(null);
  // One-shot initial agent filter for the Leads page — set by Admin Dashboard
  // drill-down clicks (Agent Performance row, Callback Compliance row). Leads
  // page consumes it on mount and clears it.
  var [initAgentFilter,setInitAgentFilter]=useState(null);
  useEffect(function(){ if (page && page!=="leads") { setLeadSpecialFilter(null); setInitAgentFilter(null); } },[page]);
  useEffect(function(){
    // Clear the salary-sheet drilldown when the user navigates away from
    // Attendance entirely. Legacy page ids that now redirect to the
    // attendance "salaries" tab are preserved.
    if (page && page!=="attendance" && page!=="salaries" && page!=="offsiteRequests" && page!=="companyOffDays") {
      setSalaryViewUserId(null);
    }
  },[page]);
  // One-shot migration: rename legacy page ids to "attendance" so the page
  // title + localStorage are consistent. AttendancePage instance keeps its
  // initTab-derived activeTab state across the rename (same component type =
  // React reuses the instance, useState init doesn't re-run).
  useEffect(function(){
    if (page === "salaries" || page === "offsiteRequests" || page === "companyOffDays") {
      setPage("attendance");
      try { localStorage.setItem("crm_page", "attendance"); } catch(e){}
    }
  },[page]);
  var [leadsPage,setLeadsPage]=useState(1); var [leadsTotal,setLeadsTotal]=useState(0); var [leadsTotalPages,setLeadsTotalPages]=useState(0);
  // STEP 4-5 X3 — DR pagination state mirrors leads. drsTotalPages drives
  // the DRPage endReached gate (skip the fetch when already on the last
  // page). drsLoadingMore guards against duplicate firings of endReached
  // while a fetch is in flight.
  var [drsPage,setDrsPage]=useState(1); var [drsTotal,setDrsTotal]=useState(0); var [drsTotalPages,setDrsTotalPages]=useState(0);
  var [leadsLoadingMore,setLeadsLoadingMore]=useState(false);
  var [drsLoadingMore,setDrsLoadingMore]=useState(false);
  // Scroll-jitter fix (Plan C): TableVirtuoso re-fires endReached the instant a
  // page fetch resolves and the new last row is still within its ~256px
  // threshold, kicking off the next page immediately → rapid back-to-back
  // height re-measures → window-scroll-anchor jump. The leadsLoadingMore flag
  // only dedups DURING the fetch; this ref blocks the auto re-fire for 150ms
  // AFTER each fetch completes. Below human-perceptual scroll continuation, so
  // manual scrolling is unaffected (Virtuoso re-fires once the cooldown lapses).
  var leadsLoadMoreBlockedUntilRef = useRef(0);
  // How many lead rows are currently held in `leads`. Read by the WS refresh
  // path (fetchLeadsLight) to re-request exactly the loaded window. A ref, not
  // the state: the WS effect closes over [token] only, so a state read there
  // would be frozen at its first-render value.
  var leadsLoadedCountRef = useRef(0);
  useEffect(function(){ leadsLoadedCountRef.current = Array.isArray(leads) ? leads.length : 0; },[leads]);
  // The rows themselves, for the same reader: fetchLeadsLight re-verifies by id
  // every row held OUTSIDE the refreshed window (mergeLeadsWindowRefresh), and
  // the ids to ask for have to be known BEFORE the setState updater runs.
  var leadsHeldRef = useRef([]);
  useEffect(function(){ leadsHeldRef.current = Array.isArray(leads) ? leads : []; },[leads]);
  // Same idea for Daily Requests — read by the WS refresh path (fetchDRs) so it
  // re-requests only the window the client holds. A ref, not the state: the WS
  // effect closes over [token] only, so a state read there would be frozen at
  // its first-render value.
  var drsLoadedCountRef = useRef(0);
  useEffect(function(){ drsLoadedCountRef.current = Array.isArray(dailyReqs) ? dailyReqs.length : 0; },[dailyReqs]);
  // The rows themselves, for the same reader — fetchDRs re-verifies by id every
  // row held OUTSIDE the refreshed window (mergeLeadsWindowRefresh, shared with
  // fetchLeadsLight), and the ids to ask for must be known before the updater runs.
  var drsHeldRef = useRef([]);
  useEffect(function(){ drsHeldRef.current = Array.isArray(dailyReqs) ? dailyReqs : []; },[dailyReqs]);
  var [activitiesPage,setActivitiesPage]=useState(1); var [activitiesTotal,setActivitiesTotal]=useState(0); var [activitiesTotalPages,setActivitiesTotalPages]=useState(0);
  var [showNotif,setShowNotif]=useState(false);
  // Foreground FCM alert — { title, body, type, leadId, status }. null = unmounted.
  var [pushBanner,setPushBanner]=useState(null);
  // Drives the slide transform: true → translateY(0), false → off-screen (-120%).
  var [bannerShown,setBannerShown]=useState(false);
  var pushTimer=useRef(null);        // auto-dismiss timer handle
  var pushTouchStartY=useRef(null);  // swipe-up gesture start Y
  var [showPendingApproval,setShowPendingApproval]=useState(false);   // BELL 3 popover open-flag
  var [showRotNotif,setShowRotNotif]=useState(false);
  var [showPendingLoc,setShowPendingLoc]=useState(false); // pending-location bell dropdown
  // BELL 4 (off-site approvals) popover open-flag. The bell owns its own rows and
  // its own fetch — this is only the open/closed state, so the other bells can
  // close it when they open. There is deliberately no offSiteNotifs state here:
  // the previous bell kept its list at App level and that is what made a stale
  // copy possible. See OffSiteBell.
  var [showOffSite,setShowOffSite]=useState(false);
  // Bumped by the DR side-panel location resolver: POST
  // /api/daily-requests/:id/resolve-location emits no WS event, so without this
  // the bell would linger up to 60s after the queue empties. (The retired
  // pendingDevRev existed for the same reason.) Also re-fetches the DR forms'
  // Location picker, since resolving by "Add as new" mints a Location that
  // should appear without a reload.
  var [pendingLocRev,setPendingLocRev]=useState(0);
  var bumpPendingLocRev=useCallback(function(){ setPendingLocRev(function(v){return v+1;}); },[]);
  // STEP 4-2 — bumped (debounced) on every lead/DR WS event. CallbackBell +
  // DashboardPage's callback cards read this in their fetch effect
  // deps so they refresh in real time without scanning the (soon-to-shrink)
  // p.leads / p.dailyRequests arrays in memory.
  var [cbBust,setCbBust]=useState(0);
  // THE DR PAGE'S LIVE FEED (2026-10-27). dr_updated docs are queued here and
  // the page drains the queue on `drLiveRev`; an EMPTY dr_updated and a socket
  // reconnect bump `drBurstRev`, on which the page refreshes its own page 1 and
  // re-verifies the rows it holds outside that window (never a wholesale
  // replace). cbBust is untouched — the counts/chips keep their behaviour.
  var drLiveDocsRef = useRef([]);
  var [drLiveRev,setDrLiveRev]=useState(0);
  var [drBurstRev,setDrBurstRev]=useState(0);
  // See applyDrPageMount. fetchDRs lives inside the socket effect, so the
  // catch-up reaches it through a ref the effect fills in.
  var drLiveStateRef = useRef({ mounted: false, skipped: false });
  var fetchDRsRef = useRef(null);
  var setDrPageMounted = useCallback(function(on){
    applyDrPageMount(on, drLiveStateRef.current, function(){ if (fetchDRsRef.current) fetchDRsRef.current(); });
  }, []);
  // Sidebar Leads badge — strict current-owner scope. Separate from leadsTotal
  // (which feeds LeadsPage pagination and keeps the historical scope so sales
  // agents can still scroll rotated-away leads). Null until the first
  // /api/leads/counts response lands; the Sidebar falls back to leadsTotal
  // (then to p.leads filter) in the meantime.
  var [sidebarLeadsTotal,setSidebarLeadsTotal]=useState(null);
  var [rotNotifs,setRotNotifs]=useState([]);
  // Rotation bell paging state. NOTIF_PAGE is deliberately modest: the bell is a
  // "what just happened" surface, not an archive, and at ~422 rotation rows/day
  // one page is roughly 3 hours of activity. Older rows are never CUT OFF — they
  // are one "Load older" click away — which is what "no cap" means for a feed
  // that legitimately holds 35k rows.
  var [rotNotifPage,setRotNotifPage]=useState(1);
  var [rotNotifPages,setRotNotifPages]=useState(1);
  // Off-site request bell — pending rows go to approvers, approved/rejected
  // rows go to the requester. Visibility is enforced server-side; this client
  // just renders whatever the API returns.
  // Deal-bell unread state is per-user, DB-backed via Notification.seenBy[]
  // (see /api/notifications/mark-seen). The earlier localStorage cutoff
  // (lastSeenDealAt + crm_deal_seen_<uid>) was removed because it didn't
  // travel across devices and disagreed with the dropdown header count.
  // Phase: rotation "Clear all" no longer hides notifications client-side —
  // it just marks them seen on the server. rotHiddenBefore state removed.
  // Phase 1b removed the App-level attendanceSettings cache. Its ONLY consumer
  // was the client-side permission mirror (12 call sites), which the access
  // envelope replaced — so every client used to download the company's whole
  // attendance POLICY document just to learn its own four booleans. SettingsPage
  // still fetches it for the Office Location / Permissions / Work Shifts tabs;
  // that fetch is its own and is unaffected.
  // Phase 5B — selected user for salary sheet. null = list view, set = sheet
  // view for that user. Cleared when the user navigates away from /salaries.
  var [salaryViewUserId,setSalaryViewUserId]=useState(null);
  var [loading,setLoading]=useState(false); var [dataError,setDataError]=useState(null);
  // Remount key for the authenticated tree. Changes ONLY when the signed-in
  // identity changes, so switching user throws away every page component's
  // internal state (dealAllData, eoiAllData, filters, selections) the way the
  // old unmount-on-loading did — and a boot spinner no longer does it by
  // accident. Falls back to "anon" so the key is never undefined.
  var authKey = String((currentUser && (currentUser.id || currentUser._id)) || "anon");
  var [isMobile,setIsMobile]=useState(window.innerWidth<768);
  var [sidebarOpen,setSidebarOpen]=useState(false);
  var [initSelected,setInitSelected]=useState(null);
  // One-shot lead-id for the Commissions page to auto-expand on mount.
  // Set by the 💰 button on a Deal row; CommissionsPage reads + clears it.
  var [selectedCommissionLeadId,setSelectedCommissionLeadId]=useState(null);
  var [selectedDealLeadId,setSelectedDealLeadId]=useState(null);
  var [search,setSearch]=useState("");
  var [isOnline,setIsOnline]=useState(navigator.onLine);
  // P0 (2026-11-02): the socket's auth was refused — live updates have stopped
  // and only a fresh login restores them. Rendered as a banner; it used to be a
  // console.error nobody saw.
  var [wsAuthFailed,setWsAuthFailed]=useState(false);

  // Deal/EOI submission record — POSTs a type:"deal" Notification row.
  //
  // This NO LONGER feeds any bell. BELL 3 is now the derived pending-approvals
  // queue (PendingApprovalsBell → GET /api/leads/pending-approvals), so nothing
  // renders these rows and there is no local state to update. The POST is kept
  // because the BACKEND side-effects on it: POST /api/notifications fires the
  // "Your deal/EOI on X is pending approval" push to the submitting agent
  // (server.js, sendPushNotification on type "deal"). Deleting this call would
  // silently kill that push. The row itself remains a submission audit trail.
  //
  // eventTime stamps the moment of the action rather than the row's insert
  // time, which is what the backend copies onto the notification.
  var addDealNotif = function(n){
    var notif = {type:"deal",leadName:n.leadName||"",leadId:n.leadId||"",agentName:n.agentName||"",status:n.status||"",budget:n.budget||"",eventTime:n.eventTime||new Date().toISOString()};
    apiFetch("/api/notifications","POST",notif,token).catch(function(err){
      // Was a bare .catch(){} — a 403 here (the POST gate only allows
      // admin/SA or the lead's own agent, so a TL submitting on behalf of an
      // agent is rejected) vanished with no trace. The bell no longer depends
      // on this row, but the agent's push does, so make the failure visible.
      console.warn("[DEAL_SUBMIT_NOTIF_FAILED]", { leadId: notif.leadId, status: notif.status, error: err && err.message });
    });
  };

  // Rotation notification helper — saves to DB, updates local state
  var notifyRotation = function(lead, fromName, toName, reason){
    // byName — only this (manual) path has a human actor; the auto sweep writes
    // its own rows server-side with no byName. The assignment log uses it to
    // render "Manual reassign — by <name>" instead of showing an admin's action
    // as if the rotation engine had made it.
    var notif = {type:"rotation",leadName:lead.name,leadId:gid(lead),fromName:fromName,toName:toName,reason:reason,
      byName:(currentUser && currentUser.name) || ""};
    apiFetch("/api/notifications","POST",notif,token).then(function(saved){
      if(saved) setRotNotifs(function(prev){return [Object.assign({},saved,{seen:false})].concat(prev).slice(0,50);});
    }).catch(function(){});
  };
  // Fetch notifications from DB. Single combined fetch (was 3 separate
  // calls) — backend supports comma-separated types. Saves 2 HTTP round-
  // trips and 2x the auth-middleware cost during the mount-time burst.
  // Bucket the response by type into the two local state arrays the WS push
  // paths target.
  //
  // "deal" was REMOVED from the requested types. The backend applies the limit
  // to the pooled result BEFORE the client buckets by type, and rotation writes
  // ~450 rows/day against 38 deal rows in total — so deal rows were evicted
  // from the window within ~32h and the old bell rendered "No deals yet" over a
  // real queue. BELL 3 no longer reads Notification rows at all (it derives its
  // queue from live Lead state), so asking for them here would be pure waste.
  // Do NOT add "deal" back without first giving it its own request + limit.
  // PER-TYPE REQUESTS — one query per bell, never a pooled one (2026-08-14).
  //
  // This used to be a SINGLE request for four types under one limit=600, and the
  // server sorts by createdAt and truncates BEFORE the client buckets by type.
  // Rotation writes ~422 rows/day against 35,434 total, so it took the whole
  // window: measured on prod, the live top-600 was 599 rotation + 1
  // offsite_approved, and offsite_rejected (2 rows in the DB) was COMPLETELY
  // INVISIBLE. Exactly the failure CLAUDE.md records for the old deal bell.
  //
  // Two independent requests means rotation volume CANNOT occupy the off-site
  // bell's slots — no shared budget to lose. Page 1 is the first NOTIF_PAGE rows
  // of each type; older rows stay reachable via loadOlderRotNotifs below rather
  // than being cut off.
  var loadNotifications = function(tok){
    // Phase R-4: commission_* notifications were removed from the rotation bell.
    // They surface only on the Commissions page banner via /api/notifications/commission.
    var grab = function(types){
      return apiFetch("/api/notifications?type=" + encodeURIComponent(types) +
                      "&page=1&limit=" + NOTIF_PAGE, "GET", null, tok)
        .then(function(r){
          return {
            rows:  (r && Array.isArray(r.data)) ? r.data : (Array.isArray(r) ? r : []),
            pages: (r && typeof r.totalPages === "number") ? r.totalPages : 1
          };
        })
        .catch(function(e){ console.error("Notifications fetch failed:", e); return { rows: [], pages: 1 }; });
    };
    // ONE type, not two. The second call fetched
    // "offsite_pending,offsite_approved,offsite_rejected" for the header pin
    // (BELL 4), which was removed for all roles on 2026-08-26 — nothing renders
    // those rows any more, so nothing asks for them. This halves the requests
    // loadNotifications makes, on every login and every 60s poll.
    //
    // THE ROUTE STAYS. /api/notifications is shared with the rotation bell and
    // is not dedicated to off-site; the offsite_* ROWS are still written
    // server-side and still reachable through it, they are simply no longer
    // requested. Same for PUT /api/notifications/mark-seen, which the rotation
    // bell still calls — only the offsite-typed call is gone.
    grab("rotation").then(function(rot){
      setRotNotifs(rot.rows);
      setRotNotifPages(rot.pages);
      setRotNotifPage(1);
    });
  };
  // "Load older" for the rotation bell — the only type with the volume to need
  // it (35,434 rows, ~422/day). Appends the next page, deduped by _id.
  var loadOlderRotNotifs = function(){
    if (!token || rotNotifPage >= rotNotifPages) return;
    var next = rotNotifPage + 1;
    apiFetch("/api/notifications?type=rotation&page=" + next + "&limit=" + NOTIF_PAGE, "GET", null, token)
      .then(function(r){
        var rows = (r && Array.isArray(r.data)) ? r.data : [];
        setRotNotifs(function(prev){
          var seen = {};
          (prev || []).forEach(function(n){ if (n && n._id) seen[String(n._id)] = 1; });
          return (prev || []).concat(rows.filter(function(n){ return n && n._id && !seen[String(n._id)]; }));
        });
        setRotNotifPage(next);
        if (r && typeof r.totalPages === "number") setRotNotifPages(r.totalPages);
      })
      .catch(function(e){ console.error("Older rotation notifications failed:", e); });
  };

  useEffect(function(){
    if (!document.getElementById('cairo-font')) {
      var link=document.createElement('link'); link.id='cairo-font'; link.rel='stylesheet';
      link.href='https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(link);
    }
  },[]);
  
  useEffect(function(){
    var goOnline=function(){setIsOnline(true);};
    var goOffline=function(){setIsOnline(false);};
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return function(){ window.removeEventListener('online',goOnline); window.removeEventListener('offline',goOffline); };
  },[]);

  useEffect(function(){
    var fn=function(){setIsMobile(window.innerWidth<768);if(window.innerWidth>=768)setSidebarOpen(false);};
    window.addEventListener("resize",fn); return function(){window.removeEventListener("resize",fn);};
  },[]);

  // Evict the now-inert per-user localStorage keys that used to drive the
  // rotation hide-cutoff and the deal-bell unread cutoff. Both bells now read
  // their unread state from the DB (Notification.seenBy[] via mark-seen),
  // so these keys are dead and we clear them on every login to free the slots.
  useEffect(function(){
    var uid = gid(currentUser); if (!uid) return;
    try { localStorage.removeItem("crm_rot_hidden_"+uid); } catch(e){}
    try { localStorage.removeItem("crm_deal_seen_"+uid); } catch(e){}
  },[currentUser&&gid(currentUser)]);

  var t=TR[lang];

  // NOTE: the sidebar swipe gesture lives inside the Sidebar component now
  // (interactive finger-tracking — it needs direct refs to the panel + overlay).
  // App just wires setSidebarOpen through the onOpen/onClose props below.

  // Server already filters leads by role/hierarchy — frontend just returns as-is
  var getVisibleLeads = function(allLeads, user, allUsers) {
    return allLeads; // server handles all filtering
  };

  // Ref flips to false after the first successful data load. Subsequent silent syncs (reconnect, manual refresh) never show the full-screen spinner.
  var isInitialLoadRef = useRef(true);
  // WHEN THE BOOTSTRAP SETTLED, and how long after it a websocket burst is
  // ignored. null = still in flight. See bumpCbBust.
  var bootSettledAtRef = useRef(null);

  // ===== THE TWO-PHASE BOOTSTRAP =========================================
  //
  // THE PAGE THE APP OPENED ON, captured on the first render and never moved.
  // Navigating away mid-boot must not re-point the gate at a page that has not
  // started fetching — see bootPageMounted, which releases phase 2 in that case
  // instead of re-aiming it.
  var bootPageRef = useRef(null);
  if (bootPageRef.current === null) bootPageRef.current = page || "dashboard";
  var bootPage = bootPageRef.current;
  // The split is a ONE-SHOT on the first non-silent load (a restored session, or
  // handleLogin). Every later call — the dataError retry, any silent sync — takes
  // the single-burst path it takes today.
  var bootTwoPhaseDoneRef = useRef(false);
  // Armed by the restored-session boot only. handleLogin fetches the bell itself
  // (a fresh login has no restored page to wait behind), so leaving this null on
  // that path is what stops the bell being requested twice.
  var bootBellTokenRef = useRef(null);
  // Phase 1 has settled and its lists are applied.
  var [bootPhase1Done,setBootPhase1Done]=useState(false);
  // The open page's OWN list has answered. Only pages in BOOT_PAGE_OWNS_ROWS set
  // this; for every other page the rows ARE phase 1, so phase 1 is the flag.
  var [bootPageRows,setBootPageRows]=useState(false);
  var markBootPageRows = useCallback(function(){ setBootPageRows(true); },[]);
  // Is the page we are waiting on still the one on screen? renderPage falls back
  // to the dashboard when the envelope has not landed or has stopped allowing
  // this page, and a page that is not mounted will never report — so that case
  // releases phase 2 rather than deadlocking it. (A page whose fetch simply
  // fails reports from its own .catch; apiFetch's 30s abort is the floor.)
  var bootPageMounted = ((page || "dashboard") === bootPage) &&
                        accessLoaded(access) && canSeePage(access, bootPage);
  var bootPhase2 = bootPhase1Done &&
                   (bootPageRows || !BOOT_PAGE_OWNS_ROWS[bootPage] || !bootPageMounted);
  // loadData is an async function and cannot await a state flag, so the flag
  // opens a promise. Deliberately NOT a timer and NOT requestIdleCallback: idle
  // arrives while the page is still empty, which is the moment phase 2 exists to
  // stay out of.
  var bootPhase2GateRef = useRef(null);
  if (bootPhase2GateRef.current === null) {
    var _openGate = null;
    var _gatePromise = new Promise(function(resolve){ _openGate = resolve; });
    bootPhase2GateRef.current = { promise: _gatePromise, open: _openGate };
  }
  useEffect(function(){
    if (bootPhase2) bootPhase2GateRef.current.open();
  },[bootPhase2]);

  var loadData=useCallback(async function(tok, userOverride, opts){
    var silent = opts && opts.silent;
    if (!silent && isInitialLoadRef.current) setLoading(true);
    setDataError(null);
    try {
      // STEP 3 — both list fetches request ?fields=summary so the bootstrap
      // payload drops the heavy log arrays (agentHistory[], history[],
      // privateNotes[] on Lead; eoiDocuments[] on DailyRequest). Precomputed
      // fields (_qualifyingMarksCount, _qualifyingMarksFirst, _currentStatus,
      // _currentFeedback, _hasAction) come back on each Lead and the FE
      // helpers fall back to them. Side panels still refetch full docs via
      // /api/leads/:id and /api/daily-requests/:id when needed.
      // STEP 4-5 X3 — bootstrap shrink. Only the first page of leads + DRs
      // is fetched on boot; subsequent pages load via loadMoreLeads /
      // loadMoreDRs (LeadsPage / DRPage endReached). Payload drops from
      // ~16 MB raw / ~1.6 MB gz to ~1.4 MB raw / ~140 KB gz.
      // NO ?sort= HERE, DELIBERATELY (2026-08-20). A sales agent's All tab is
      // ordered by when each lead reached HIM, but that ordering comes from the
      // server's role default, not from a param: `effectiveUser` is resolved
      // BELOW this Promise.all, so the role is not reliably known yet and a
      // conditional param here would be read off stale state on first login.
      // The server takes the role from the JWT, so it always knows. This is why
      // `received` is the sales DEFAULT in receiptSortDir() rather than an
      // opt-in — the bootstrap is precisely the caller that cannot ask for it.
      // ===== DON'T FETCH WHAT YOU CANNOT SHOW (Phase 5) =====================
      // The bootstrap used to pull the lead and daily-request lists for EVERY
      // role, including hr and office_boy, whose entire product is an
      // attendance-only dashboard and who have never been able to open Leads.
      // The rows came back empty anyway — their leadScope is "none" — so this
      // was a round-trip and a payload for nothing.
      //
      // It also had a sharper consequence. THE LEADS FETCH HAS NO .catch(): a
      // 403 rejects the whole Promise.all, sets dataError, and renders the
      // full-screen error page instead of the app. That is why Phase 4b-1 had to
      // leave page:dashboard in the surface set for GET /api/leads — the server
      // could not refuse it without bricking login for those two roles.
      //
      // Skipping the fetch here is what lets that set tighten. The condition is
      // deliberately the SAME set the server declares, so the two cannot drift:
      // if you can see any page that legitimately reads leads, we fetch.
      // The envelope arrives via opts at LOGIN, because `access` state has not
      // re-rendered yet at that moment — the same timing reason userOverride
      // exists beside currentUser two lines down.
      var envNow = (opts && opts.access) || access;
      var canReadLeads = !accessLoaded(envNow) ||
        ["leads","eoi","deals","archive","dailyReq"].some(function(pg){ return canSeePage(envNow, pg); });
      // ===== THE PHASE SPLIT ================================================
      // ONE-SHOT, on the first non-silent load. Every later call takes the
      // single-burst path unchanged, so the WS/manual refresh paths and the
      // dataError retry are untouched by any of this.
      var twoPhase = !silent && !bootTwoPhaseDoneRef.current;
      if (twoPhase) bootTwoPhaseDoneRef.current = true;
      // The three shared lists this landing page's FIRST PAINT reads. A page
      // absent from the table keeps all three, i.e. boots exactly as today.
      var phase1Lists = twoPhase ? bootPhase1Lists(bootPageRef.current) : BOOT_ALL_LISTS;
      var inPhase1 = function(k){ return phase1Lists.indexOf(k) !== -1; };
      var EMPTY_LIST = { data: [], total: 0, totalPages: 0, page: 1 };
      // The three requests, verbatim, hoisted so phase 1 and phase 2 issue the
      // SAME call — a deferred list must not become a different request.
      var getLeadsList = function(){
        return canReadLeads
          ? apiFetch("/api/leads?fields=summary&page=1&limit=100","GET",null,tok)
          : Promise.resolve(EMPTY_LIST);
      };
      var getActivitiesList = function(){
        return apiFetch("/api/activities?page="+activitiesPage+"&limit=1000","GET",null,tok);
      };
      var getDrList = function(){
        return (canReadLeads
          ? apiFetch("/api/daily-requests?fields=summary&page=1&limit=100","GET",null,tok)
          : Promise.resolve(EMPTY_LIST)).catch(function(e){ console.error("DR fetch failed:", e); return []; });
      };
      var results=await Promise.all([
        inPhase1("leads") ? getLeadsList() : Promise.resolve(null),
        apiFetch("/api/users","GET",null,tok),
        inPhase1("activities") ? getActivitiesList() : Promise.resolve(null),
        inPhase1("dailyRequests") ? getDrList() : Promise.resolve(null),
        // IDENTITY REFRESH (P6, 2026-08-21) — the frontend half of the fix that
        // already landed on the server. `currentUser` is restored synchronously
        // from a session blob written at LOGIN, and tokens live 30 days, so a
        // role / title / rank changed since then reached the UI only when the
        // user happened to log out. The server solved its half in auth()
        // (checkUserActive widened to role/title/titleRank + applyFreshIdentity);
        // this is the same idea on the client.
        //
        // PHASE 1, and it could never be anything else: the envelope decides what
        // the first frame may show, and /api/users supplies the agent names it
        // shows. A rank-blind or nameless first frame is not a slower frame, it is
        // a wrong one. .catch to null because a failed refresh must degrade to the
        // stored identity, never blank the boot — a rejection here would take the
        // whole Promise.all down with it.
        apiFetch("/api/me","GET",null,tok).catch(function(e){ console.error("identity refresh failed:", e); return null; })
      ]);
      // Use userOverride if passed (avoids React state timing issue)
      var effectiveUser = userOverride || currentUser;
      // ---- apply the refreshed identity, if it moved ----
      // Only the three fields that decide what the UI may show. Widening this to
      // the whole /api/me document would quietly overwrite local edits (name,
      // phone, language prefs) that other screens own.
      var meDoc = results[4];
      // THE ACCESS ENVELOPE. Applied unconditionally (not only when identity
      // moved): a take-over changes what this user may see without changing a
      // single identity field, so gating this on the role/title/rank diff below
      // would let a stale envelope survive exactly the change it exists for.
      if (meDoc && meDoc.access) {
        setAccess(meDoc.access);
        sessionStoreWriteAccess(meDoc.access);
      }
      // THE LEAD SOURCE REGISTRY (2026-11-02) rides on /api/me. A failed refresh
      // asks its own route; until either answers, the last stored list stays.
      if (meDoc && meDoc.leadSources) setLeadSources(meDoc.leadSources);
      else apiFetch("/api/lead-sources","GET",null,tok).then(setLeadSources).catch(function(){ /* keep the stored list */ });
      if (meDoc && meDoc._id) {
        var fresh = {
          role:      meDoc.role,
          title:     meDoc.title || "",
          titleRank: (typeof meDoc.titleRank === "number") ? meDoc.titleRank : null
        };
        var eu = effectiveUser || {};
        if (eu.role !== fresh.role || eu.title !== fresh.title || eu.titleRank !== fresh.titleRank) {
          effectiveUser = Object.assign({}, eu, fresh);
          setCurrentUser(effectiveUser);
          sessionStorePatchUser(fresh);
        }
      }
      setUsers(results[1]);
      // The three list appliers, extracted verbatim so a list applies IDENTICALLY
      // whichever phase fetched it. Nothing here changed but the indentation.
      var applyLeadsList = function(r){
        // Restore phone2 from cache for leads that are missing it
        var leadsData = r.data||[];
        try {
          var cache = JSON.parse(localStorage.getItem('phone2_cache')||'{}');
          leadsData = leadsData.map(function(l){
            var id = l._id ? String(l._id) : null;
            if (id && cache[id] && !l.phone2) return Object.assign({}, l, {phone2: cache[id]});
            return l;
          });
        } catch(e) {}
        setLeads(getVisibleLeads(leadsData, effectiveUser, results[1]));
        setLeadsTotal(r.total || 0);
        setLeadsTotalPages(r.totalPages || 0);
        setLeadsPage(1);
      };
      var applyActivitiesList = function(r){
        setActivities(r.data || []);
        setActivitiesTotal(r.total || 0);
        setActivitiesTotalPages(r.totalPages || 0);
      };
      // STEP 4-5 X3 — DR now returns the {data,total,page,totalPages} shape
      // because the request includes ?page=1&limit=100. Earlier callers
      // (fetchDRs WS fallback, ArchivePage's own fetch) still use the legacy
      // array shape — preserved by checking Array.isArray.
      var applyDrList = function(drBootResult){
        var drBootRows = (drBootResult && Array.isArray(drBootResult.data)) ? drBootResult.data
                       : Array.isArray(drBootResult) ? drBootResult
                       : [];
        setDailyReqs(drBootRows);
        if (drBootResult && typeof drBootResult.total === "number") {
          setDrsTotal(drBootResult.total);
          setDrsTotalPages(drBootResult.totalPages || 0);
          setDrsPage(1);
        }
      };
      var applyList = { leads: applyLeadsList, activities: applyActivitiesList, dailyRequests: applyDrList };
      if (inPhase1("leads"))         applyLeadsList(results[0]);
      if (inPhase1("activities"))    applyActivitiesList(results[2]);
      if (inPhase1("dailyRequests")) applyDrList(results[3]);

      // ===== PHASE 1 IS OVER — THE LOADER LIFTS HERE ========================
      // Everything the open page's first frame needs is applied. The tree stops
      // being covered now, not when the lists it does not read come back.
      setLoading(false);
      setBootPhase1Done(true);

      // ===== PHASE 2 ========================================================
      // Released by the open page's own loaded flag (bootPhase2 above). Its
      // rejection still lands in the same catch, so a 403 on /api/leads or
      // /api/activities renders the error page exactly as it does today —
      // one beat later, from behind a page instead of from behind a spinner.
      var deferred = BOOT_ALL_LISTS.filter(function(k){ return !inPhase1(k); });
      if (deferred.length) {
        await bootPhase2GateRef.current.promise;
        var getList = { leads: getLeadsList, activities: getActivitiesList, dailyRequests: getDrList };
        var phase2Results = await Promise.all(deferred.map(function(k){ return getList[k](); }));
        deferred.forEach(function(k, i){ applyList[k](phase2Results[i]); });
      }
    } catch(e){setDataError(e.message);}
    setLoading(false);
    isInitialLoadRef.current = false;
    if (bootSettledAtRef.current === null) bootSettledAtRef.current = Date.now();
    // Backfill lastFeedback for existing leads (once per browser, admin only).
    // No post-backfill leads reload — WS lead_updated keeps p.leads fresh, and
    // a second 7.8 MB /api/leads pull was the dominant cost on every admin
    // page (incl. Attendance) where leads aren't even displayed.
    try{
      var bfKey="crm_feedback_backfilled";
      var u = userOverride || currentUser;
      var isAdminUser = u && (u.role === "admin" || u.role === "operation");
      if(!localStorage.getItem(bfKey) && isAdminUser){
        apiFetch("/api/leads/backfill-feedback","GET",null,tok).then(function(){
          localStorage.setItem(bfKey,"1");
        }).catch(function(e){
          console.error("Backfill-feedback fetch failed:", e);
          // Set the gate even on failure so we don't retry every page load
          localStorage.setItem(bfKey, "failed");
        });
      }
    }catch(e){}
  },[activitiesPage, access]);

  // Sidebar Leads badge — fetch the same count the LeadsPage uses so the
  // sidebar and the page agree. For sales role we pass ?scope=historical
  // so the count covers every lead they've ever held a slice on (matches
  // GET /api/leads's sales scope + LeadsPage allVisible filter). For other
  // roles the existing strict-current-owner /counts response is what
  // they're already used to. Debounced 1000ms so WS bursts (cbBust) don't
  // flood the endpoint; matches the LeadsPage fetch debounce.
  //
  // THE SAME URL AS THE PAGE'S UNFILTERED STRIP, byte for byte (2026-10-16):
  // excludeSource=Daily Request (DR-mirror leads live on the Daily Requests
  // page, and the page's All chip never counted them — the badge read one more
  // than the page, 235 vs 234 for Nagy Naeem) and, for the strict roles,
  // derivedStatus=1, which the page sends. Same URL means the burst that wakes
  // both readers costs ONE request through sharedApiGet instead of two.
  useEffect(function(){
    if (!token) return;
    var cancelled = false;
    var isSales = currentUser && positionOf(currentUser) === "sales";
    var url = "/api/leads/counts?excludeSource=" + encodeURIComponent("Daily Request")
            + (isSales ? "&scope=historical" : "&derivedStatus=1");
    var timer = setTimeout(function(){
      if (cancelled) return;
      sharedApiGet(url, token)
        .then(function(r){ if (!cancelled && r && typeof r.total === "number") setSidebarLeadsTotal(r.total); })
        .catch(function(){});
    }, 1000);
    return function(){ cancelled = true; clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[token, cbBust, currentUser && currentUser.role]);

  // STEP 4-5 X3 — load-more helpers for LeadsPage / DRPage. Called by
  // TableVirtuoso's endReached when the user scrolls within ~256px of the
  // bottom. Guarded by leadsLoadingMore / drsLoadingMore (Virtuoso fires
  // endReached repeatedly during smooth scroll; the guard dedups). When
  // there are no more pages we short-circuit so the bottom of the table
  // doesn't kick off a no-op fetch every frame.
  var loadMoreLeads = function(){
    if (leadsLoadingMore) return;
    // Post-resolve cooldown — blocks the immediate endReached re-fire that
    // causes scroll jitter. See leadsLoadMoreBlockedUntilRef declaration.
    if (Date.now() < leadsLoadMoreBlockedUntilRef.current) return;
    if (leadsPage >= leadsTotalPages) return;
    if (!token) return;
    var nextPage = leadsPage + 1;
    setLeadsLoadingMore(true);
    // Same rule as usePagedLeads.loadMore: pages slice the RAW result and the
    // per-slice visibility gate runs server-side AFTER skip/limit, so an empty
    // page does NOT mean the end of the list. Chase forward until a page
    // carries rows, or the pages run out — otherwise the "All" tab stalls with
    // leads still unreached. The page number is threaded through the recursion
    // instead of read back from state, which has not settled yet.
    var MAX_CHAIN = 30;
    var chase = function(pageNo, hops){
      return apiFetch("/api/leads?fields=summary&page=" + pageNo + "&limit=100","GET",null,token)
      .then(function(r){
        var rows = (r && Array.isArray(r.data)) ? r.data : (Array.isArray(r) ? r : []);
        if (rows.length) {
          var scoped = getVisibleLeads(rows, currentUser, users);
          // Append to existing list. Dedup by _id to survive overlap if a
          // mutation upserted a row that's also in the next page response.
          setLeads(function(prev){
            var byId = {};
            prev.forEach(function(l){ if(l && l._id) byId[String(l._id)] = true; });
            var fresh = scoped.filter(function(l){ return l && l._id && !byId[String(l._id)]; });
            return prev.concat(fresh);
          });
        }
        if (r && typeof r.total === "number") setLeadsTotal(r.total);
        var tp = (r && typeof r.totalPages === "number") ? r.totalPages : leadsTotalPages;
        if (r && typeof r.totalPages === "number") setLeadsTotalPages(r.totalPages);
        setLeadsPage(pageNo);
        // CONTRIBUTION, not raw length — same fix and same reasoning as the
        // usePagedLeads chase (see the comment there). This path feeds the All
        // tab via p.leads, which LeadsPage then filters through allVisible; a
        // page whose rows are all archived / Daily-Request-source / EOI /
        // DoneDeal grows p.leads but NOT the rendered list, so endReached never
        // fires again and the tab dead-ends with pages remaining.
        var contributedRows = rows.filter(leadsPageScopeVisible).length;
        if (!contributedRows && pageNo < tp && hops + 1 < MAX_CHAIN) return chase(pageNo + 1, hops + 1);
      });
    };
    chase(nextPage, 0)
      .catch(function(e){ console.error("[loadMoreLeads]", e && e.message); })
      .finally(function(){
        setLeadsLoadingMore(false);
        // Re-arm the 150ms cooldown so endReached can't instantly re-fire.
        leadsLoadMoreBlockedUntilRef.current = Date.now() + 150;
      });
  };
  var loadMoreDRs = function(){
    if (drsLoadingMore) return;
    if (drsPage >= drsTotalPages) return;
    if (!token) return;
    var nextPage = drsPage + 1;
    setDrsLoadingMore(true);
    apiFetch("/api/daily-requests?fields=summary&page=" + nextPage + "&limit=100","GET",null,token)
      .then(function(r){
        var rows = (r && Array.isArray(r.data)) ? r.data : (Array.isArray(r) ? r : []);
        if (rows.length) {
          setDailyReqs(function(prev){
            var byId = {};
            prev.forEach(function(d){ if(d && d._id) byId[String(d._id)] = true; });
            var fresh = rows.filter(function(d){ return d && d._id && !byId[String(d._id)]; });
            return prev.concat(fresh);
          });
        }
        if (r && typeof r.total === "number") setDrsTotal(r.total);
        if (r && typeof r.totalPages === "number") setDrsTotalPages(r.totalPages);
        setDrsPage(nextPage);
      })
      .catch(function(e){ console.error("[loadMoreDRs]", e && e.message); })
      .finally(function(){ setDrsLoadingMore(false); });
  };

  // Phase 2 Slice 3 — hydrate the projectWeights module cache once auth is
  // available, then bump rev so children re-render and read the new values.
  // Re-runs on token change (login of a different user, session restore).
  useEffect(function(){
    if (!token) return;
    fetchProjectWeights(token).then(function(){ bumpProjectWeightsRev(); });
  },[token, bumpProjectWeightsRev]);

  // ===== ACCESS REFRESH =====
  // One function, called from the two WS events that can move a caller's access:
  // a capability take-over, and (while anything still resolves through it) an
  // edit to the legacy permissions document. Refetches /api/me rather than
  // patching locally — the envelope is server-resolved, and a client that could
  // patch it correctly would need the rule, which is the thing being deleted.
  //
  // Persisted so the NEXT first paint is right too, not just this one.
  var refreshAccess = useCallback(function(){
    if (!token) return;
    apiFetch("/api/me","GET",null,token).then(function(me){
      if (me && me.access) { setAccess(me.access); sessionStoreWriteAccess(me.access); }
    }).catch(function(){ /* keep the last known envelope; a failed refresh must not blank access */ });
  },[token]);

  // Data-refresh polling intervals were removed — WebSocket listener below handles real-time updates.
  // ===== REAL-TIME WEBSOCKET SYNC (single source of truth — replaces all data-refresh polling) =====
  useEffect(function(){
    if(!token) return;
    var wsUrl = (process.env.REACT_APP_API_URL||API).replace("https://","wss://").replace("http://","ws://");
    // P0 TRANSPORT (2026-11-02). What was here before, and why each is gone:
    //   maxRetries=20   — after ~8 minutes of backoff the socket stayed dead
    //                     until a focus/visibility event. Backoff is now
    //                     uncapped (30s ceiling, jittered so the office does
    //                     not reconnect in lockstep after a redeploy).
    //   no client ping  — an iOS WebView suspension leaves a half-open socket
    //                     that reports OPEN forever; nothing noticed. The
    //                     client now pings every 25s (WS_PING_MS), the server
    //                     answers pong, and 40s without ANY frame (WS_STALE_MS)
    //                     forces a reconnect. The watchdog arms only once a
    //                     pong has been seen, so an older server never trips it.
    //   no seq          — frames carry a per-socket seq; a gap means frames
    //                     were lost (server restart, proxy) and the client
    //                     resyncs instead of trusting its state.
    //   no fallback     — while the socket is dead/stale and the tab is visible
    //                     the same resync runs every 30s (WS_FALLBACK_MS).
    var ws; var reconnectTimer; var retries=0; var hasConnectedBefore=false; var cancelled=false;
    var lastFrameAt=0; var lastSeq=0; var pongSeen=false; var authFailed=false;
    var WS_PING_MS=25000, WS_STALE_MS=40000, WS_FALLBACK_MS=30000;
    // Silent background refresh — no spinner, keeps existing state while re-fetching.
    var fetchAll = function(){
      try{ loadData(token, currentUser, {silent:true}); }catch(e){}
      try{ loadNotifications(token); }catch(e){}
    };
    var fetchNotifications = function(){ try{ loadNotifications(token); }catch(e){} };
    var fetchUsers = function(){
      apiFetch("/api/users","GET",null,token).then(function(u){ if(Array.isArray(u)) setUsers(u); }).catch(function(){});
    };
    var fetchActivitiesLatest = function(){
      apiFetch("/api/activities?page="+activitiesPage+"&limit=20","GET",null,token).then(function(r){
        if (r && r.data) setActivities(r.data);
      }).catch(function(){});
    };
    var fetchDRs = function(){
      // STEP 3 — same summary projection as loadData so the refetch payload
      // is consistent with what setDailyReqs originally holds.
      //
      // This used to request /api/daily-requests?fields=summary with NO limit,
      // and the backend treats an absent limit as "return the whole scope" —
      // 2.97 MB / 4.1s for an admin against 92.5 KB / 1.3s for one page,
      // measured on prod 2026-08-11. It fired on every WebSocket reconnect and
      // every empty-payload dr_updated (bulk ops, server-side cascades), which
      // is the same shape fbb3dc0 fixed on the Leads side and never applied
      // here. Re-request only the window the client actually holds instead.
      // Capped at 250 for the same reason as Leads: a deep-scrolled session
      // must not be able to reintroduce a multi-MB response.
      // ONE LIST REQUEST PER BURST — see applyDrPageMount (module scope).
      if (drLiveStateRef.current.mounted) { drLiveStateRef.current.skipped = true; return; }
      var drLoaded = drsLoadedCountRef.current || 0;
      var drRows = Math.min(Math.max(drLoaded, 100), 250);
      apiFetch("/api/daily-requests?fields=summary&page=1&limit=" + drRows,"GET",null,token).then(function(d){
        // ?page/?limit switches the response to the object shape; the bare-array
        // branch keeps an older/cached backend working.
        var fresh = (d && Array.isArray(d.data)) ? d.data : (Array.isArray(d) ? d : null);
        if (!fresh) return;
        // THE WINDOW CANNOT CONTAIN A ROW OLDER THAN ITSELF — the same defect
        // fetchLeadsLight had until 2026-10-27, the same mechanism: rows held
        // outside the refreshed window (WS-upserted requests, the deep-scroll
        // tail) are re-verified by id through the same scoped list, never
        // silently evicted. The helpers are shared with the Leads path — ONE
        // merge rule, two callers; see mergeLeadsWindowRefresh (module scope).
        var drAsked = leadsHeldOutsideWindow(drsHeldRef.current, fresh).slice(0, LEADS_REFRESH_VERIFY_MAX);
        if (!drAsked.length) {
          setDailyReqs(function(prev){ return mergeLeadsWindowRefresh(prev, fresh, {}, drAsked); });
          return;
        }
        var drVerifyUrl = "/api/daily-requests?fields=summary&page=1&limit=" + drAsked.length +
                          drAsked.map(function(id){ return "&ids=" + encodeURIComponent(id); }).join("");
        apiFetch(drVerifyUrl, "GET", null, token)
          .then(function(v){ return leadsVerifiedById(drAsked, (v && Array.isArray(v.data)) ? v.data : (Array.isArray(v) ? v : [])); },
                function(){ return undefined; })
          .then(function(verified){
            setDailyReqs(function(prev){ return mergeLeadsWindowRefresh(prev, fresh, verified, drAsked); });
          });
      }).catch(function(){});
    };
    fetchDRsRef.current = fetchDRs;
    // Lightweight fallback for empty-payload lead_updated WS events. Mirrors
    // the existing fetchDRs() fallback used by the dr_updated handler. Without
    // this, server-side cascade emits (e.g. DR.delete cascading to mirror
    // Lead.deleteMany at server.js:8189) that broadcast empty lead_updated
    // payloads silently no-op on the client, leaving the deleted/archived
    // mirror Lead in p.leads — which surfaces as ghost rows on DealsPage,
    // EOIPage, and the dashboard KPI strip.
    var fetchLeadsLight = function(){
      // STEP 3 — same summary projection as loadData. fetchSingleLead below
      // still uses the full /api/leads/:id endpoint for side-panel hydration,
      // so detail data continues to flow when individual rows are opened.
      //
      // This used to request /api/leads?fields=summary with NO limit, and the
      // backend treats an absent limit as "return the whole scope" (server.js
      // ~11535) — ~4.1k rows / ~15 MB for an admin, on EVERY ws reconnect and
      // every empty-payload lead_updated. That silently undid the bootstrap
      // shrink right above (16 MB -> 1.4 MB). Re-request only the window the
      // client actually holds instead: p.leads is the createdAt-desc prefix
      // built by the bootstrap (page 1) plus loadMoreLeads (pages 2..n), so
      // page=1&limit=<rows loaded> re-reads exactly that prefix in one call.
      // Capped so a deep-scrolled session can't reintroduce a multi-MB
      // response. Measured on prod data 2026-08-10: 100 rows = 0.44 MB, 250 =
      // 1.25 MB, 300 = 1.57 MB (rows get heavier further back — older leads
      // carry more assignment slices), against 33.9 MB unbounded. 250 is the
      // largest round cap that stays under the 1.5 MB budget.
      var loaded = leadsLoadedCountRef.current || 0;
      var rows = Math.min(Math.max(loaded, 100), 250);
      // Several empty-payload lead_updated events in one burst (a sweep, a bulk
      // op) each call this; sharedApiGet turns them into one request.
      sharedApiGet("/api/leads?fields=summary&page=1&limit=" + rows, token).then(function(r){
        var fresh = (r && Array.isArray(r.data)) ? r.data : null;
        if (!fresh) return;
        // THE WINDOW CANNOT CONTAIN A ROW OLDER THAN ITSELF. "Refetched at least
        // as many rows as we held, so take the response as-is" was the rule here
        // until 2026-10-27, and it counted WS-upserted rows as if they were part
        // of the window — a lead an agent had just worked (older than the window,
        // top of the Last Activity sort) was evicted by the next unrelated burst.
        // Rows held outside the window are re-verified by id instead; see
        // mergeLeadsWindowRefresh (module scope) for the rule and the measurement.
        var asked = leadsHeldOutsideWindow(leadsHeldRef.current, fresh).slice(0, LEADS_REFRESH_VERIFY_MAX);
        if (!asked.length) {
          setLeads(function(prev){ return mergeLeadsWindowRefresh(prev, fresh, {}, asked); });
          return;
        }
        var verifyUrl = "/api/leads?fields=summary&page=1&limit=" + asked.length +
                        asked.map(function(id){ return "&ids=" + encodeURIComponent(id); }).join("");
        sharedApiGet(verifyUrl, token)
          .then(function(v){ return leadsVerifiedById(asked, (v && Array.isArray(v.data)) ? v.data : []); },
                function(){ return undefined; })
          .then(function(verified){
            setLeads(function(prev){ return mergeLeadsWindowRefresh(prev, fresh, verified, asked); });
          });
      }).catch(function(){});
    };
    var fetchSingleLead = function(leadId){
      if(!leadId) return;
      apiFetch("/api/leads/"+leadId,"GET",null,token).then(function(fresh){
        if(fresh && fresh._id) setLeads(function(prev){
          var found = prev.some(function(l){return gid(l)===String(fresh._id);});
          return found ? prev.map(function(l){return gid(l)===String(fresh._id)?fresh:l;}) : [fresh].concat(prev);
        });
      }).catch(function(){});
    };
    // STEP 4-2 — debounced bump for the callback consumers. Coalesces bursts
    // (e.g. cascade lead_updated emits) into a single refresh tick. 1500ms
    // matches the report-card debounce used elsewhere — fast enough to feel
    // live, slow enough to skip mid-burst churn.
    var cbBumpTimer = null;
    // ── A BURST DURING BOOT IS NOT NEWS ──────────────────────────────────────
    //
    // The websocket connects mid-bootstrap, and the first lead/DR event to
    // arrive bumped cbBust ~1.5s later — which re-ran every cbBust consumer at
    // a moment when all of them had just fetched. Measured in a headless-Chrome
    // HAR against production, 2026-08-26: a Deals cold load issued 26 GETs, of
    // which TEN were a second wave at +3540ms re-requesting endpoints already
    // answered at +855ms. The data had not changed; the socket had merely
    // finished connecting.
    //
    // So a bust is dropped while the bootstrap is still in flight, and for
    // BOOT_QUIET_MS after it settles. Both halves are needed: the burst that
    // caused this arrived AFTER loadData resolved, not during it.
    //
    // THIS ONLY GATES cbBust, which drives badges and bell counts. Live row
    // updates are untouched — the same websocket handlers call loadData /
    // patchDealRow / patchEoiRow directly, and none of those routes through
    // cbBust. The cost of a dropped bust is a badge that waits for the next
    // event or the 60s poll, in a window where it was just fetched anyway.
    var BOOT_QUIET_MS = 5000;
    var bumpCbBust = function(){
      var settled = bootSettledAtRef.current;
      if (settled === null || (Date.now() - settled) < BOOT_QUIET_MS) return;
      if (cbBumpTimer) clearTimeout(cbBumpTimer);
      cbBumpTimer = setTimeout(function(){ setCbBust(function(v){return v+1;}); }, 1500);
    };
    // ONE resync for every "we may have missed frames" moment — reconnect, a
    // seq gap, the dead-socket fallback poll. Re-reads the slices the socket
    // would have patched, bumps the bell/badge consumers, and announces itself
    // (crm:live_resync) so page-level live hooks can refetch their own family.
    var resyncState = function(why){
      console.log("[ws] resync (" + why + ")");
      try{ loadNotifications(token); }catch(e){}
      try{ fetchLeadsLight(); }catch(e){}
      try{ fetchDRs(); }catch(e){}
      try{ fetchActivitiesLatest(); }catch(e){}
      setDrBurstRev(function(v){return v+1;});
      bumpCbBust();
      try { window.dispatchEvent(new CustomEvent("crm:live_resync", { detail: { why: why } })); } catch(e){}
    };
    function connect(){
      if(cancelled) return;
      // Defence in depth for the same race: whatever calls connect(), never
      // abandon a socket by overwriting the only reference to it. The guards in
      // onVis/onFocus should mean there is nothing live here, but the reconnect
      // timer and the initial call share this path too.
      if (ws) {
        try {
          ws.onopen = ws.onmessage = ws.onerror = null;
          ws.onclose = null;                 // detach BEFORE close, or this
          ws.close();                        // close would schedule a reconnect
        } catch(e) {}
      }
      try{ ws = new WebSocket(wsUrl); }catch(e){ return; }
      ws.onopen = function(){
        retries = 0; lastFrameAt = Date.now(); lastSeq = 0;
        // BUG #1 diagnostic — surface the WS lifecycle in DevTools so a user
        // can confirm live updates are wired end-to-end without sniffing the
        // Network → WS frames tab. Three signals total: opened, auth_ok,
        // auth_failed. Plus a one-liner per inbound event (low noise; one
        // line per server push, matches the cadence the user expects).
        console.log("[ws] opened");
        // Authenticate the socket so the server can scope per-client broadcasts
        // (sales must only receive events for leads/DRs they own).
        try { ws.send(JSON.stringify({ type: "auth", token: token })); } catch(e){}
        // On reconnect, refetch the data slices that WS events would have
        // updated during the disconnect window. Any events that fired while
        // the socket was down are otherwise lost permanently. Each slice is
        // wrapped so one failure doesn't block the others. First connection
        // is unchanged — loadData populated initial state already.
        if (hasConnectedBefore) resyncState("reconnect");
        hasConnectedBefore = true;
      };
      ws.onmessage = function(e){
        try{
          var msg = JSON.parse(e.data);
          var data = msg.data || {};
          lastFrameAt = Date.now();
          // Per-socket seq from the server (P0). 1,2,3… for every frame THIS
          // socket was sent; a jump means something between us dropped frames.
          if (typeof msg.seq === "number") {
            if (lastSeq && msg.seq > lastSeq + 1) resyncState("seq gap " + lastSeq + "→" + msg.seq);
            if (msg.seq > lastSeq) lastSeq = msg.seq;
          }
          // BUG #1 diagnostic — log every inbound event so a user reproducing
          // the "admin doesn't see new deal notifications" symptom can confirm
          // whether the WS layer is even firing. Skip "hello" (server greeting,
          // arrives on every connect and would spam the console).
          if (msg.type && msg.type !== "hello") console.log("[ws] event:", msg.type);
          switch(msg.type){
            case "lead_updated":
              if (data.lead && data.lead._id) {
                // Re-apply the backend sales overlay before this doc enters
                // local state — the broadcast payload is the originating
                // request's (admin-scoped) body and would otherwise leak the
                // previous owner's slice/feedback to a sales viewer. No-op for
                // privileged roles (returns the doc unchanged).
                var lead = scopeWsLeadForViewer(data.lead, currentUser);
                // Defense in depth: a lead that just got archived (e.g. via
                // PUT /api/leads/:id/archive) shouldn't be upserted back into
                // the visible list — drop it instead so any page that filters
                // on !archived doesn't have to rely on its render-time guard.
                if (lead.archived === true) {
                  setLeads(function(prev){return prev.filter(function(l){return gid(l)!==String(lead._id);});});
                } else {
                  setLeads(function(prev){
                    var hit = prev.some(function(l){return gid(l)===String(lead._id);});
                    return hit ? prev.map(function(l){return gid(l)===String(lead._id)?lead:l;}) : [lead].concat(prev);
                  });
                }
              } else if (data.leadId) {
                fetchSingleLead(String(data.leadId));
              } else {
                // Empty payload → server-side cascade or bulk op that didn't
                // attach a doc. Mirrors the dr_updated→fetchDRs fallback so
                // the local Lead state can't silently diverge from the DB.
                fetchLeadsLight();
              }
              bumpCbBust();
              break;
            case "lead_deleted":
              if (data.leadId) setLeads(function(prev){return prev.filter(function(l){return gid(l)!==String(data.leadId);});});
              bumpCbBust();
              break;
            case "dr_updated":
              if (data.dr && data.dr._id) {
                var dr = data.dr;
                // Defense in depth: a DR that just got archived shouldn't be
                // upserted back into the visible list — drop it instead so the
                // DR page's render-time !archived guard isn't the only line
                // of defense.
                if (dr.archived === true) {
                  setDailyReqs(function(prev){return prev.filter(function(r){return gid(r)!==String(dr._id);});});
                } else {
                  setDailyReqs(function(prev){
                    var hit = prev.some(function(r){return gid(r)===String(dr._id);});
                    return hit ? prev.map(function(r){return gid(r)===String(dr._id)?dr:r;}) : [dr].concat(prev);
                  });
                }
                drLiveDocsRef.current.push(dr); if (drLiveDocsRef.current.length > 200) drLiveDocsRef.current.splice(0, drLiveDocsRef.current.length - 200);
                setDrLiveRev(function(v){return v+1;});
              } else if (Array.isArray(data.drs) && data.drs.length) {
                // Bulk path — backend now ships the affected DRs (e.g. bulk-reassign
                // returns { drs: [...] }). Upsert each in place so we don't fall
                // through to fetchDRs() and pull the whole list across every connected client.
                // Archived rows in the bulk payload are dropped, mirroring the single-DR path above.
                var byId = {};
                var archivedIds = {};
                data.drs.forEach(function(d){
                  if(!d || !d._id) return;
                  if(d.archived === true) archivedIds[String(d._id)] = true;
                  else byId[String(d._id)] = d;
                });
                setDailyReqs(function(prev){
                  var seen = {};
                  var merged = prev.filter(function(r){return !archivedIds[gid(r)];}).map(function(r){
                    var rid = gid(r);
                    if (byId[rid]) { seen[rid] = true; return byId[rid]; }
                    return r;
                  });
                  Object.keys(byId).forEach(function(k){ if(!seen[k]) merged.unshift(byId[k]); });
                  return merged;
                });
                data.drs.forEach(function(d){ if (d && d._id) drLiveDocsRef.current.push(d); });
                if (drLiveDocsRef.current.length > 200) drLiveDocsRef.current.splice(0, drLiveDocsRef.current.length - 200);
                setDrLiveRev(function(v){return v+1;});
              } else {
                fetchDRs();
                setDrBurstRev(function(v){return v+1;});
              }
              bumpCbBust();
              break;
            case "dr_deleted":
              if (data.drId) setDailyReqs(function(prev){return prev.filter(function(r){return gid(r)!==String(data.drId);});});
              bumpCbBust();
              break;
            case "activity_created":
              // P5 — also announce it as a family change so page-level hooks
              // (the dashboard's activity card) re-read without a poll. The
              // frame already passed the server's lead-owner filter.
              try { window.dispatchEvent(new CustomEvent("crm:entity_changed", { detail: { family: "activity", ids: data.activity && data.activity._id ? [String(data.activity._id)] : null, op: "POST" } })); } catch(e){}
              if (data.activity) setActivities(function(prev){
                if (prev.some(function(a){return gid(a)===gid(data.activity);})) return prev;
                return [data.activity].concat(prev).slice(0,50);
              });
              else fetchActivitiesLatest();
              break;
            case "user_updated":
              if (data.user && data.user._id) {
                var u = data.user;
                setUsers(function(prev){
                  var hit = prev.some(function(x){return gid(x)===String(u._id);});
                  return hit ? prev.map(function(x){return gid(x)===String(u._id)?u:x;}) : [u].concat(prev);
                });
              } else {
                fetchUsers();
              }
              break;
            case "user_deleted":
              if (data.userId) setUsers(function(prev){return prev.filter(function(x){return gid(x)!==String(data.userId);});});
              break;
            case "notification_updated":
              fetchNotifications();
              // Phase 6 — also re-dispatch as window event so subscribers
              // (AttendancePage 5-lates banner) can refresh without prop
              // drilling.
              try { window.dispatchEvent(new CustomEvent("crm:notification_updated")); } catch(e){}
              break;
            case "rotation_updated":
              if (data.leadId) fetchSingleLead(String(data.leadId));
              fetchNotifications();
              break;
            case "attendance_settings_updated":
              // The Owner toggled the legacy permissions or the office location.
              // While ANY capability still resolves through the legacy path this
              // can change what this caller may do, so re-ask the server.
              // SettingsPage keeps its own copy of the document for the tabs that
              // edit it and refetches on its own; the App level no longer caches
              // the policy at all, only the resolved answer.
              refreshAccess();
              try { window.dispatchEvent(new CustomEvent("crm:attendance_settings_updated", { detail: data })); } catch(e){}
              break;
            case "visibility_matrix_updated":
              // A page or tab was taken over (possibly from another session).
              // TWO consumers, and the second is the important one: the matrix
              // screen refetches its grid, and EVERY open session re-asks for its
              // own envelope — a take-over is exactly the moment somebody's nav
              // entries appear or disappear, and waiting for a reload would leave
              // them looking at a menu the server no longer backs.
              refreshAccess();
              try { window.dispatchEvent(new CustomEvent("crm:visibility_matrix_updated")); } catch(e){}
              break;
            case "capability_matrix_updated":
              // The Owner took a capability over (possibly in another session).
              // Two consumers: the Capabilities tab refetches its grid, and EVERY
              // session re-asks for its own envelope — a take-over is exactly the
              // moment somebody's tabs appear or disappear, and waiting for a
              // reload would leave them looking at a UI the server no longer backs.
              refreshAccess();
              try { window.dispatchEvent(new CustomEvent("crm:capability_matrix_updated")); } catch(e){}
              break;
            case "attendance_updated":
              // Phase 3 — re-dispatch as a window event so the widget and
              // AttendancePage Team tab can react without prop drilling.
              // CheckInWidget filters to its own user (cross-tab/device sync);
              // Team tab patches the matching row.
              if (data && data.userId) {
                try { window.dispatchEvent(new CustomEvent("crm:attendance_updated", { detail: data })); } catch(e){}
              }
              break;
            case "offsite_request_updated":
              // Phase 4 — fired on create/cancel/approve/reject. Off-site
              // inbox page listens via crm:offsite_request_updated and
              // refetches the pending list.
              try { window.dispatchEvent(new CustomEvent("crm:offsite_request_updated")); } catch(e){}
              break;
            case "company_offdays_updated":
              // Phase 4 — fired on add/remove. Company Off-Days page refetches.
              try { window.dispatchEvent(new CustomEvent("crm:company_offdays_updated")); } catch(e){}
              break;
            case "salary_finalized":
              // Phase 5E — fired on finalize/unlock for one (user, month).
              // SalarySheetPage refetches if it's currently viewing that user.
              try { window.dispatchEvent(new CustomEvent("crm:salary_finalized", { detail: data })); } catch(e){}
              break;
            case "entity_changed":
              // P1 — an id-only frame for a live family (brokers, developers,
              // closing companies, locations, branches, expense categories,
              // campaign groups, …). Nothing here to patch with: pages that
              // show the family subscribe through src/lib/liveFamily.js and
              // refetch their rows through the REST route.
              // P4 — list-level families the App itself holds: an admin bulk
              // door on leads (bulk-update-source, cleanup, rotation sweeps)
              // refreshes the window the App holds; a users-graph repair
              // re-reads the users list; the two attendance-settings keys
              // re-ask the envelope, exactly as attendance_settings_updated.
              if (data.family === "lead" && !data.ids) { fetchLeadsLight(); bumpCbBust(); }
              if (data.family === "user" && !data.ids) fetchUsers();
              if (data.family === "attendanceSettings") {
                refreshAccess();
                try { window.dispatchEvent(new CustomEvent("crm:attendance_settings_updated", { detail: {} })); } catch(e){}
              }
              try { window.dispatchEvent(new CustomEvent("crm:entity_changed", { detail: data })); } catch(e){}
              break;
            case "settings_updated":
              // Key-only since P0. Settings screens re-read their own document.
              try { window.dispatchEvent(new CustomEvent("crm:settings_updated", { detail: data })); } catch(e){}
              break;
            case "hello": break; // server greeting
            case "pong": pongSeen = true; break; // answers our 25s ping; lastFrameAt already stamped
            case "auth_ok": console.log("[ws] auth ok"); authFailed = false; setWsAuthFailed(false); break;
            case "auth_failed":
              console.error("[ws] auth FAILED — token rejected, real-time updates will not arrive. Logout / login to refresh.");
              authFailed = true; setWsAuthFailed(true);
              break;
            default: break;
          }
        }catch(err){}
      };
      ws.onclose = function(){
        if (cancelled) return;
        retries++;
        // Uncapped: 2s, 4s, 8s, 16s, 30s, 30s … plus up to 1s of jitter.
        var delay = Math.min(1000*Math.pow(2, Math.min(retries, 5)), 30000) + Math.floor(Math.random()*1000);
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = function(){ try{ws.close();}catch(e){} };
    }
    connect();
    // Tab visibility / focus: only reconnect the socket if it was silently dropped.
    // NO data re-fetch, NO loading spinner — events from the live socket keep state fresh.
    // ORPHANED-SOCKET RACE (fixed 2026-08-14). These used to reconnect whenever
    // readyState !== 1 (OPEN) — which INCLUDES 0 (CONNECTING). A focus or
    // visibility event landing during the connect handshake therefore built a
    // SECOND WebSocket and overwrote `ws` with it, leaving the first one live,
    // unreferenced, and impossible to close: its onmessage stayed bound, so every
    // server push was applied twice (two setLeads, two bumpCbBust) and the
    // duplicate survived until the server or network dropped it. Rapid
    // tab-switching multiplied it.
    //
    // Reconnect ONLY from a genuinely dead socket. CONNECTING is already on its
    // way to OPEN and must be left alone; OPEN needs nothing.
    //   0 CONNECTING -> leave  |  1 OPEN -> leave
    //   2 CLOSING    -> reconnect  |  3 CLOSED -> reconnect  |  null -> reconnect
    var socketIsDead = function(){
      if (!ws) return true;
      return ws.readyState === 2 /* CLOSING */ || ws.readyState === 3 /* CLOSED */;
    };
    // OPEN but silent past the stale window, after the server has proven it
    // answers pings: the half-open iOS shape. Treated exactly like dead.
    var socketIsStale = function(){
      return !!ws && ws.readyState === 1 && pongSeen && (Date.now() - lastFrameAt) > WS_STALE_MS;
    };
    var reconnectIfNeeded = function(){
      if (socketIsDead() || socketIsStale()) { retries=0; connect(); }
    };
    var onVis = function(){
      if (document.visibilityState!=="visible") return;
      reconnectIfNeeded();
    };
    var onFocus = function(){ reconnectIfNeeded(); };
    var onOnline = function(){ reconnectIfNeeded(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    // Heartbeat + watchdog, one timer: ping an OPEN socket; reconnect a stale one.
    var pingTimer = setInterval(function(){
      if (cancelled) return;
      if (socketIsStale()) { console.log("[ws] stale, reconnecting"); retries=0; connect(); return; }
      if (ws && ws.readyState === 1) { try { ws.send(JSON.stringify({ type: "ping" })); } catch(e){} }
    }, WS_PING_MS);
    // 30s fallback while the socket cannot carry events — dead, stale, or
    // refused — and only while somebody is looking at the tab.
    var fallbackTimer = setInterval(function(){
      if (cancelled || document.visibilityState === "hidden") return;
      if (socketIsDead() || socketIsStale() || authFailed) resyncState("fallback poll");
    }, WS_FALLBACK_MS);
    return function(){
      cancelled = true;
      clearTimeout(reconnectTimer);
      clearInterval(pingTimer);
      clearInterval(fallbackTimer);
      if (cbBumpTimer) clearTimeout(cbBumpTimer);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      if (ws) try{ws.close();}catch(e){}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshAccess]);

  // Boot-time data fetch when the page loads with an existing session.
  // currentUser/token are already seeded synchronously up above; this effect
  // only fires the async loadData + loadNotifications calls that drive the
  // initial population. Fresh logins go through handleLogin which calls these
  // directly, so this effect runs at most once per app mount and never races
  // with the login path.
  useEffect(function(){
    if (savedSession && savedSession.token && savedSession.user) {
      loadData(savedSession.token, savedSession.user);
      bootBellTokenRef.current = savedSession.token;   // arms the phase-2 effect below
      // The rotation bell moved OUT of here — see the bootPhase2 effect below.
      // It is a badge: it renders 0 until answered and is wrong for nobody while
      // pending, so it waits for the open page like the rest of phase 2.
      // Native push: re-register the device token on app rehydrate. No-op on web.
      try { initPushNotifications(); } catch(e){}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PHASE 2 — the rotation bell. It used to ride a requestIdleCallback, which
  // fires while the page is still empty and therefore still competes with the
  // fetch that draws it; the gate is now the open page's own loaded flag.
  // Login is unchanged: handleLogin calls loadNotifications directly, because a
  // fresh login has no restored page to get out of the way of.
  useEffect(function(){
    if (!bootPhase2) return;
    var tok = bootBellTokenRef.current;
    if (!tok) return;                      // login already fetched it; nothing armed
    bootBellTokenRef.current = null;       // one shot — the 60s poll owns it after this
    try { loadNotifications(tok); } catch(e){}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[bootPhase2]);

  // Cold-launch reflow nudge (iOS). On a WKWebView cold launch the page often
  // paints BEFORE the safe-area insets settle, so the first layout is computed
  // against a wider viewport → a transient horizontal scroll offset that only
  // clears on a force-quit (warm relaunch has insets ready). The CSS clips
  // (overflow-x:hidden on html/body/#root/.crm-app) can't fix a layout already
  // computed against the wrong width, so we force a re-clamp once the viewport
  // settles: reset horizontal scroll + dispatch a synthetic resize so the
  // existing isMobile listeners re-run and React reflows against the real width.
  // Safe no-op on desktop/Android (nothing to correct; visualViewport guarded).
  useEffect(function(){
    if (typeof window === "undefined") return;
    var corrected = 0;
    var nudge = function(){
      try {
        // Native only: re-harden the viewport meta so WebKit re-resolves
        // initial-scale against the SETTLED width → resets a stuck cold-launch
        // zoom. The index.html inline gate can miss if window.Capacitor isn't
        // injected yet at parse time (remote load), so we re-apply it here on
        // each settle signal. Idempotent (same content string). Web/desktop skip
        // this entirely, keeping pinch-zoom for accessibility.
        if (isNativePlatformSync()) {
          var vp = document.querySelector("meta[name=viewport]");
          if (vp) vp.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover");
        }
        if (document.documentElement) document.documentElement.scrollLeft = 0;
        if (document.body) document.body.scrollLeft = 0;
        window.scrollTo(0, window.scrollY || 0);
        window.dispatchEvent(new Event("resize"));
      } catch (e) { /* never let the nudge throw */ }
    };
    // A few early frames catch the first paints before insets settle.
    var raf = (typeof requestAnimationFrame === "function")
      ? requestAnimationFrame(function(){ nudge(); })
      : null;
    var t1 = setTimeout(nudge, 0);
    var t2 = setTimeout(nudge, 300);
    // visualViewport 'resize' fires exactly when iOS insets/visual viewport
    // settle — the authoritative signal. Cap to a couple of corrections so we
    // don't react to every keyboard/scroll-driven viewport change forever.
    var vv = window.visualViewport || null;
    var onVV = function(){ if (corrected < 2) { corrected++; nudge(); } };
    if (vv && typeof vv.addEventListener === "function") vv.addEventListener("resize", onVV);
    return function(){
      try { if (raf && typeof cancelAnimationFrame === "function") cancelAnimationFrame(raf); } catch(e){}
      clearTimeout(t1); clearTimeout(t2);
      if (vv && typeof vv.removeEventListener === "function") vv.removeEventListener("resize", onVV);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One funnel for every biometric unlock attempt (cold launch, resume, retry).
  // SECURITY: a device WITH any lock can open ONLY by passing native auth —
  // there is NO bypass. The app opens without auth ONLY when we positively
  // confirm the device has no lock at all (canAuthKnown && canAuth===false).
  // On failure/cancel/timeout we stay locked and surface a Retry button — and
  // because every native call settles via timeout, there is no permanent spinner.
  var attemptBioUnlock = useCallback(function(){
    if (bioBusyActive(bioBusyRef.current, bioBusyAtRef.current, Date.now())) return;
    bioBusyRef.current = true;
    bioBusyAtRef.current = Date.now();
    setBioFailed(false);
    setBioPrompting(true);
    isBiometricAvailable()
      .then(function(info){
        // Confirmed NO lock on the device → nothing to verify → open.
        if (info && info.canAuthKnown && info.canAuth === false) {
          setBioLocked(false);
          return null;
        }
        // A lock exists (or we couldn't confirm) → REQUIRE device auth. No bypass.
        return runBiometricAuth("Unlock ARO CRM").then(function(ok){
          if (ok) { setBioLocked(false); }
          else { setBioFailed(true); }   // cancelled/failed/timeout → Retry, stay locked
        });
      })
      .catch(function(){ setBioFailed(true); /* stay locked; Retry remains */ })
      .finally(function(){ bioBusyRef.current = false; setBioPrompting(false); });
  }, []);

  // Cold launch: if we mounted locked (native + session), prompt immediately.
  useEffect(function(){
    if (bioLocked) attemptBioUnlock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-lock on resume only if backgrounded ≥1 min. visibilitychange stamps bgAt
  // when hidden and fires maybeRelock when shown; the busy ref stops the auth
  // overlay's own visibility blip from causing a re-lock loop. No-op on web.
  useEffect(function(){
    if (!isNativePlatformSync()) return; // web: never lock
    var maybeRelock = function(){
      if (bioBusyActive(bioBusyRef.current, bioBusyAtRef.current, Date.now())) return;   // a prompt is already showing
      var bgAt = bgAtRef.current;
      if (bgAt && (Date.now() - bgAt) >= BIO_THRESHOLD_MS) {
        bgAtRef.current = null;
        setBioLocked(true);                      // render lock; cold-launch effect re-prompts
        attemptBioUnlock();
      }
    };
    var onVis = function(){
      if (typeof document === "undefined") return;
      if (document.hidden) { if (bgAtRef.current == null) bgAtRef.current = Date.now(); }
      else { maybeRelock(); }
    };
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVis);
    return function(){
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  var handleLogin=function(user,tok,csrfTok,persistent,accessEnv){
    // SECURITY — a user switch invalidates every cached list. Wipe any list
    // state still in memory from a previous user BEFORE the new scoped fetch,
    // and force the initial-load spinner (isInitialLoadRef) so the full-screen
    // loader — not another agent's rows — covers the repopulation. Normally
    // handleLogout / the 401-reload already cleared these, but re-login within
    // the same mount (or any future account-switch path) would otherwise leave
    // the prior user's leads/EOIs/deals visible until loadData resolves.
    setLeads([]); setUsers([]); setActivities([]); setDailyReqs([]); setSidebarLeadsTotal(null);
    isInitialLoadRef.current = true;
    // Re-arm the boot-quiet window with it: a re-login within the same mount
    // runs a fresh bootstrap, and a socket burst against it is as uninteresting
    // as the first one was.
    bootSettledAtRef.current = null;
    // Set the envelope BEFORE the first post-login render, so the very first
    // frame is drawn against the server's answer rather than against `null`.
    // The boot /api/me inside loadData refreshes it a moment later; this is what
    // stops the gap being visible.
    setAccess(accessEnv || null);
    setCurrentUser(user); setToken(tok); setCsrfToken(csrfTok); loadData(tok, user, {access: accessEnv}); loadNotifications(tok);
    // Honour a pending QR deep-link instead of forcing dashboard. Only admins
    // and the Owner can actually see the assets page; non-admins fall through
    // to the dashboard default below via the renderPage guard.
    var defaultPage = deepLinkAssetCode ? "assets" : "dashboard";
    setPage(defaultPage);
    // Persist to localStorage when Remember me is checked or on native; else to
    // sessionStorage (cleared when the tab/browser closes). persistent is
    // undefined for legacy callers -> default to localStorage (prior behavior).
    sessionStoreWrite({user:Object.assign({},user),token:tok,csrfToken:csrfTok,access:accessEnv||null}, persistent !== false);
    // Native push: request permission + register the device token. No-op on web.
    try { initPushNotifications(); } catch(e){}
  };
  // Auto-refresh disabled


  // ===== HEARTBEAT + ACTIVITY TRACKING =====
  useEffect(function(){
    if(!token) return;
    var lastUserAction = Date.now();
    // Track user interactions
    var onAction = function(){ lastUserAction = Date.now(); };
    document.addEventListener("click", onAction);
    document.addEventListener("keydown", onAction);
    document.addEventListener("mousemove", onAction);

    var hb=function(){
      var idleMs = Date.now() - lastUserAction;
      var isActive = idleMs < 2*60*1000; // active if action within 2 min
      try{apiFetch("/api/heartbeat","POST",{isActive:isActive},token);}catch(e){}
    };
    hb();
    var interval=setInterval(hb,60*1000); // every 1 min
    return function(){
      clearInterval(interval);
      document.removeEventListener("click", onAction);
      document.removeEventListener("keydown", onAction);
      document.removeEventListener("mousemove", onAction);
    };
  },[token]);

  // Notification polling removed — the WebSocket "notification_updated" event now triggers loadNotifications in real time.

  // Auto-rotation: server-side cron at crm-aro-backend/server.js:7262 runs
  // every 5 min and is the single source of rotation triggers. The legacy
  // browser-side runChecks cron was removed — it duplicated the server work,
  // refreshed the full 1000-lead list after each rotation, and produced the
  // 409 noise (server cron vs browser cron racing) tracked in MEMORY.md.

  var handleLogout=async function(){
    // Owner ruling 2026-11-03: logout makes this device as if it never existed
    // for the account. ONE request — POST /api/logout — revokes THIS session
    // server-side and drops this device's push token, AWAITED before the
    // session is cleared. (The old shape fired the owner-only revoke and the
    // token DELETE in parallel on the same bearer: the revoke could win and
    // 401 the DELETE, and for a non-owner the revoke 403'd — so a logged-out
    // phone kept receiving pushes.) Raw fetch, not apiFetch, so a 401 here
    // never triggers the reload handler; bounded, so an offline device still
    // logs out locally.
    var _loggedOut = false;
    try {
      if (token) {
        var _pushTok = ""; try { _pushTok = localStorage.getItem("crm_push_token") || ""; } catch(e){}
        var _ctl = (typeof AbortController !== "undefined") ? new AbortController() : null;
        var _tm = _ctl ? setTimeout(function(){ try { _ctl.abort(); } catch(e){} }, 6000) : null;
        var _r = await fetch(API + "/api/logout", { method:"POST", headers:{ "Content-Type":"application/json", "Authorization":"Bearer "+token, "X-CSRF-Token": csrfToken||"" }, body: JSON.stringify({ pushToken: _pushTok }), signal: _ctl ? _ctl.signal : undefined });
        if (_tm) clearTimeout(_tm);
        _loggedOut = !!(_r && _r.ok);
      }
    } catch(e){}
    if (!_loggedOut) {
      // The one-request route did not answer (offline, or an older backend
      // during a deploy): fall back to the pre-ruling pair while the bearer is
      // still valid — token removal first (awaited, explicit bearer), then the
      // owner-only revoke. The server refuses delivery to a token whose
      // session is not live regardless of which path ran.
      try { await removeTokenFromBackend(undefined, token); } catch(e){}
      try {
        var _sid = sidFromToken(token);
        if (_sid && token) { fetch(API + "/api/sessions/" + encodeURIComponent(_sid) + "/revoke", { method:"POST", headers:{ "Content-Type":"application/json", "Authorization":"Bearer "+token, "X-CSRF-Token": csrfToken||"" } }).catch(function(){}); }
      } catch(e){}
    }
    // The token itself is invalidated on the device (native only; no-op on web).
    try { await deleteDeviceToken(); } catch(e){}
    try{disposePushNotifications();}catch(e){}setCurrentUser(null);setToken(null);setCsrfToken(null);setLeads([]);setUsers([]);setActivities([]);setDailyReqs([]);setPage("dashboard");setSidebarOpen(false);setSidebarLeadsTotal(null);setAccess(null);sessionStoreClear();
  };
  var nav=function(pg,initLead){var p2=pg||"dashboard";setPage(p2);if(initLead){setInitSelected(initLead);}else{setInitSelected(null);}try{localStorage.setItem("crm_page",p2);}catch(e){}};

  // Push tap-to-open: consume a pending leadId captured by the native push
  // listener (pushNotifications.js writes crm_pending_lead* on tap). Mirrors
  // the deepLinkAssetCode pattern: read once, navigate, clear. Two paths —
  // (a) cold start / resume: read localStorage on mount; (b) already
  // foreground: react to the crm:open-lead window event. No-op on web (the
  // listener only fires in the native shell, so the keys are never set).
  useEffect(function(){
    var openPendingLead = function(leadId, type, status){
      if (!leadId) return;
      var t = type || "";
      var page = (t.indexOf("eoi") === 0)                          ? "eoi"
               : (t.indexOf("deal") === 0 && status === "EOI")     ? "eoi"
               : (t.indexOf("deal") === 0)                          ? "deals"
               : "leads";
      // Read the LIVE token at call time from the durable session — NOT the
      // closure `token`. This effect has [] deps, so a closure token is frozen
      // at first mount (null when the app mounted on the login screen). Firing
      // apiFetch with a null token hits the global 401 handler, which deletes
      // crm_aro_session and reloads to login — i.e. tapping a push would log the
      // user out. Resolving the token fresh from localStorage every time avoids
      // that and survives login-within-the-same-mount.
      var liveToken = null;
      try {
        var _ss = sessionStoreRead();
        if (_ss && _ss.token) liveToken = _ss.token;
      } catch(e){ liveToken = null; }
      // GUARD: with no valid token, NEVER fire the fetch — a null-token request
      // would 401 and wipe the session. Just navigate to the right page so the
      // tap lands somewhere sensible without ever triggering a logout.
      if (!liveToken) {
        try { nav(page); } catch(e){}
      } else {
        // Mirror the notification bell (App.js ~1777): fetch the full lead first
        // so nav() receives a complete object (name/phone present). A bare {_id}
        // shell is rejected by LeadsPage's hasRenderableSelected guard and the
        // panel never opens. On fetch failure (lead gone / no access) we stay on
        // the page rather than forcing a broken panel.
        try {
          apiFetch("/api/leads/" + String(leadId), "GET", null, liveToken).then(function(fresh){
            if (fresh && fresh._id) { try { nav(page, fresh); } catch(e){} }
          }).catch(function(){ /* lead gone or no access — stay on page */ });
        } catch(e){}
      }
      // Consume the deep-link once regardless of fetch outcome.
      try {
        localStorage.removeItem("crm_pending_lead");
        localStorage.removeItem("crm_pending_lead_type");
        localStorage.removeItem("crm_pending_lead_status");
      } catch(e){}
    };
    // (a) cold start / resume — consume whatever the tap stored.
    try {
      var pendId = localStorage.getItem("crm_pending_lead");
      if (pendId) openPendingLead(pendId, localStorage.getItem("crm_pending_lead_type"), localStorage.getItem("crm_pending_lead_status"));
    } catch(e){}
    // (b) foreground — tap while the app is already open.
    var onOpenLead = function(ev){
      var d = (ev && ev.detail) || {};
      openPendingLead(d.leadId, d.type, d.status);
    };
    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("crm:open-lead", onOpenLead);
    }
    return function(){
      if (typeof window !== "undefined" && window.removeEventListener) {
        window.removeEventListener("crm:open-lead", onOpenLead);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slide the banner up and out, then unmount after the transition (320ms).
  var dismissPushBanner = function(){
    if (pushTimer.current){ clearTimeout(pushTimer.current); pushTimer.current=null; }
    setBannerShown(false);
    setTimeout(function(){ setPushBanner(null); }, 320);
  };
  // Tap → open the lead via the hardened crm:open-lead path, then dismiss.
  var openPushBanner = function(){
    if (!bannerShown) return; // ignore taps once it's sliding out
    if (pushBanner && pushBanner.leadId){
      try { window.dispatchEvent(new CustomEvent("crm:open-lead", { detail:{ leadId:pushBanner.leadId, type:pushBanner.type, status:pushBanner.status } })); } catch(e){}
    }
    dismissPushBanner();
  };

  // Foreground push banner: pushNotifications.js dispatches crm:push-received
  // when an FCM message arrives while the app is open (the OS suppresses its
  // own banner then). Show a full-width slide-down banner. No-op on web — the
  // event is only dispatched from the native shell.
  useEffect(function(){
    var onPush = function(ev){
      var d = (ev && ev.detail) || {};
      if (!d.title && !d.body) return; // nothing to show
      setBannerShown(false); // start above the screen
      setPushBanner({ title:d.title||"", body:d.body||"", type:d.type||"", leadId:d.leadId||"", status:d.status||"" });
    };
    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("crm:push-received", onPush);
    }
    return function(){
      if (typeof window !== "undefined" && window.removeEventListener) {
        window.removeEventListener("crm:push-received", onPush);
      }
    };
  }, []);

  // Slide in on the next frame, then auto-dismiss after 5s.
  useEffect(function(){
    if (!pushBanner) return;
    var raf = (typeof window!=="undefined" && window.requestAnimationFrame)
      ? window.requestAnimationFrame(function(){ setBannerShown(true); })
      : setTimeout(function(){ setBannerShown(true); }, 20);
    pushTimer.current = setTimeout(function(){ dismissPushBanner(); }, 5000);
    return function(){
      if (typeof window!=="undefined" && window.cancelAnimationFrame) window.cancelAnimationFrame(raf); else clearTimeout(raf);
      if (pushTimer.current){ clearTimeout(pushTimer.current); pushTimer.current=null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushBanner]);

  // Public privacy policy — no auth required. Checked before the auth/loading
  // gates so it renders whether logged in or out (app stores fetch /privacy
  // during review). authPath is updated on popstate (see effect above).
  if (authPath === "/privacy") return <PrivacyPolicyPage/>;

  if(!currentUser) {
    // Unauthenticated reset routes. authPath is updated on popstate so
    // browser-back / pushState navigation between /, /forgot-password, and
    // /reset-password swaps the page without a full reload.
    if (authPath === "/forgot-password") return <ForgotPasswordPage/>;
    if (authPath === "/reset-password")  return <ResetPasswordPage/>;
    return <LoginPage t={t} onLogin={handleLogin}/>;
  }
  // ── THE BOOT LOADER OVERLAYS THE TREE; IT NO LONGER REPLACES IT ──────────
  //
  // This was `if(loading) return <loader/>`, an early return that unmounted the
  // whole authenticated tree. loadData calls setLoading(true) AFTER the first
  // render has already mounted the page, so every cold load ran
  // mount -> unmount -> remount and fetched everything twice. Measured in a
  // headless-Chrome HAR against production: 26 GETs on a Deals cold load, TEN of
  // them a second wave at +3540ms re-requesting endpoints answered at +1200ms,
  // /api/deals among them.
  //
  // WHAT THE UNMOUNT WAS SILENTLY DOING, and why this is not a one-line change:
  // it was also the thing that wiped page-local state. DealsPage holds
  // dealAllData, EOIPage holds eoiAllData; handleLogin clears the shared lists
  // but cannot reach inside a child component. Unmounting did it by accident.
  // An overlay does not, so a user switch would have left the previous agent's
  // deals in memory behind the spinner — the exact leak the loader exists to
  // prevent (see handleLogin's SECURITY note).
  //
  // So the reset is now explicit and keyed on IDENTITY, not on the spinner:
  // the page tree below carries key={authKey}. A different user remounts it and
  // every page's own state goes with it; a loading toggle does not.
  var bootOverlay = loading ? <div style={{ position:"fixed", inset:0, zIndex:9000,
      display:"flex", alignItems:"center", justifyContent:"center",
      background:"#F0F2F5", fontFamily:"Cairo,sans-serif" }}><div style={{ textAlign:"center" }}><div style={{ width:40, height:40, borderRadius:"50%", border:"3px solid #E8ECF1", borderTopColor:C.accent, animation:"spin 0.8s linear infinite", margin:"0 auto 16px" }}/><div style={{ color:C.textLight, fontSize:14 }}>{t.loading}</div></div></div> : null;
  if(dataError) return <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"100vh", gap:16, fontFamily:"Cairo,sans-serif" }}><AlertCircle size={48} color={C.danger}/><div style={{ fontSize:16, color:C.danger, fontWeight:700 }}>{t.error}</div><div style={{ color:C.textLight }}>{dataError}</div><button onClick={function(){loadData(token);}} style={{ padding:"10px 24px", borderRadius:10, background:C.accent, border:"none", color:"#fff", fontWeight:700, cursor:"pointer" }}>{t.retry}</button></div>;

  // Biometric lock — only authenticated content is gated. Sits below the
  // !currentUser + loading + dataError gates so login/loading/error screens are
  // never locked, and above renderPage() so no page content renders while locked.
  // A cold launch has nothing to keep, so the lock replaces the tree. A RE-lock covers the
  // mounted tree (see lib/lockShell.js) so every page keeps its scroll, filters and open record.
  if (!bioLocked) treeShownRef.current = true;
  if (lockReplacesTree(bioLocked, treeShownRef.current)) return <BiometricLockScreen onUnlock={attemptBioUnlock} busy={bioPrompting} failed={bioFailed}/>;

  var isAdmin=currentUser.role==="admin"||positionOf(currentUser)==="manager"||positionOf(currentUser)==="team_leader"; var isOnlyAdmin=currentUser.role==="admin"||currentUser.role==="operation";
  var currentPage=page||"dashboard";
  var titles={dashboard:t.dashboard,commissions:"Commissions",brokers:t.brokers,kpis:"KPIs",leads:t.leads,dailyReq:t.dailyReq,deals:t.deals,eoi:"EOI",projects:t.projects,reports:t.reports,team:t.team,users:t.users,archive:t.archive,queue:"Assignment Queue",attendance:"Attendance",offsiteRequests:"Off-site Requests",salaries:"Salaries",companyOffDays:"Company Off-Days",settings:t.settings,assets:"Assets",meetings:"Meetings"};
  // Server already filters users by role — p.users IS the team
  var myId = String(currentUser.id||currentUser._id||"");

  // Team-leader scope (defense in depth) — backend scopes /api/users,
  // /api/leads, /api/activities, and /api/daily-requests for team_leader
  // callers, but the WS broadcaster's per-client filter is sales-only, so a
  // team_leader socket receives every lead_updated/dr_updated/activity_created
  // event in the system. Without this filter, a operation saving a deal
  // for an out-of-team agent would prepend that lead into a TL's local state
  // and surface across LeadsPage, DealsPage, EOIPage, dashboards, KPIs, and
  // the Sales Team cards. tlScope is null for every other role, in which
  // case the raw arrays pass through unchanged.
  var tlScope = getTeamScopeIds(currentUser, users);
  var leadInScope = function(l){
    var aid=String(l&&l.agentId&&l.agentId._id?l.agentId._id:(l&&l.agentId)||"");
    var sid=String(l&&l.splitAgent2Id&&l.splitAgent2Id._id?l.splitAgent2Id._id:(l&&l.splitAgent2Id)||"");
    return tlScope.has(aid)||(!!sid&&tlScope.has(sid));
  };
  var scopedLeads     = tlScope ? leads.filter(leadInScope) : leads;
  var scopedUsers     = tlScope ? users.filter(function(u){return tlScope.has(String((u&&u._id)||""));}) : users;
  var scopedActivities= tlScope ? (activities||[]).filter(function(a){var auid=String(a&&a.userId&&a.userId._id?a.userId._id:(a&&a.userId)||"");return tlScope.has(auid);}) : activities;
  var scopedDailyReqs = tlScope ? (dailyReqs||[]).filter(function(r){var aid=String(r&&r.agentId&&r.agentId._id?r.agentId._id:(r&&r.agentId)||"");return tlScope.has(aid);}) : dailyReqs;
  var myTeamUsers = scopedUsers;

  var navigateToCommission=function(leadId){
    if(!leadId) return;
    try { setSelectedCommissionLeadId(String(leadId)); } catch(e){}
    setPage("commissions");
    try { localStorage.setItem("crm_page","commissions"); } catch(e){}
  };
  // The return journey. Deliberately the SAME shape as navigateToCommission —
  // a lead id parked in state, consumed once by the destination page, cleared.
  //
  // NOT p.nav("deals", lead), which is what goToDeal used to call. nav carries a
  // LEAD OBJECT, and the only place CommissionsPage could find one was p.leads —
  // the 100-row bootstrap. Any deal outside those 100 resolved to null and the
  // Deals page opened with nothing selected. An id costs nothing to carry and
  // the Deals page already holds the whole book to resolve it against.
  var navigateToDeal=function(leadId){
    if(!leadId) return;
    try { setSelectedDealLeadId(String(leadId)); } catch(e){}
    setPage("deals");
    try { localStorage.setItem("crm_page","deals"); } catch(e){}
  };
  var sp={t,leads:scopedLeads,setLeads,users:scopedUsers,setUsers,activities:scopedActivities,setActivities,cu:currentUser,token,csrfToken,nav,setFilter:setLeadFilter,leadFilter,specialFilter:leadSpecialFilter,setSpecialFilter:setLeadSpecialFilter,meetingsInit:meetingsInit,setMeetingsInit:setMeetingsInit,dealsInit:dealsInit,setDealsInit:setDealsInit,drInitFilter:drInitFilter,setDrInitFilter:setDrInitFilter,drInit:drInit,setDrInit:setDrInit,lang,setLang,search,setSearch,isMobile,initSelected,setInitSelected,initAgentFilter,setInitAgentFilter,isOnlyAdmin,myTeamUsers,addDealNotif:addDealNotif,notifyRotation:notifyRotation,rotNotifs:rotNotifs,dailyReqs:scopedDailyReqs,bumpProjectWeightsRev:bumpProjectWeightsRev,projectWeightsRev:projectWeightsRev,bumpPendingLocRev:bumpPendingLocRev,locationRev:pendingLocRev,access:access,salaryViewUserId:salaryViewUserId,setSalaryViewUserId:setSalaryViewUserId,setPage:setPage,navigateToCommission:navigateToCommission,selectedCommissionLeadId:selectedCommissionLeadId,setSelectedCommissionLeadId:setSelectedCommissionLeadId,navigateToDeal:navigateToDeal,selectedDealLeadId:selectedDealLeadId,setSelectedDealLeadId:setSelectedDealLeadId,cbBust:cbBust,loadMoreLeads:loadMoreLeads,loadMoreDRs:loadMoreDRs,leadsLoadingMore:leadsLoadingMore,drsLoadingMore:drsLoadingMore,setDrPageMounted:setDrPageMounted,drLiveRev:drLiveRev,drLiveDocsRef:drLiveDocsRef,drBurstRev:drBurstRev,leadsTotal:leadsTotal,leadsTotalPages:leadsTotalPages,leadsPage:leadsPage,drsTotal:drsTotal,drsTotalPages:drsTotalPages,drsPage:drsPage,onBootRows:markBootPageRows};

  var renderPage=function(){
    // ONE GATE, ABOVE THE SWITCH. Every case used to carry its own role test,
    // and seven of them were looser than the sidebar entry that was supposed to
    // be the same rule. Since Phase 5 the answer comes from the SERVER, off
    // access.pages — so a page cannot be added to this switch and be visible
    // without a surface governing it: an unknown id is absent from the envelope,
    // reads as false, and lands on the dashboard rather than defaulting to
    // "visible" the way a missing case guard did.
    // Until the envelope lands, show the dashboard rather than guessing. Every
    // role can see it, so this is the one page that is never a wrong answer.
    if (!accessLoaded(access)) return <DashboardPage {...sp}/>;
    if (!canSeePage(access, currentPage)) return <DashboardPage {...sp}/>;
    switch(currentPage){
      // The check-in card rides on the dashboard for everyone who checks in.
      // NOT a visibility rule — the dashboard is visible to all — so it stays
      // here rather than becoming a surface.
      // A financeReads role renders the check-in CARD inside its finance-only
      // dashboard (owner ruling 2026-11-02), so the compact strip is not added twice.
      case "dashboard": return (currentUser.role !== "admin" && currentUser.role !== "hr" && currentUser.role !== "office_boy" && !roleHasFinanceReadsFE(currentUser))
        ? <div><CheckInWidget token={token} cu={currentUser} csrfToken={csrfToken} mode="compact" onNavigate={setPage}/><DashboardPage {...sp}/></div>
        : <DashboardPage {...sp}/>;
      case "kpis":        return <KPIsPage {...sp}/>;
      case "leads":       return <LeadsPage {...sp} isRequest={false}/>;
      case "dailyReq":    return <DailyRequestsPage {...sp}/>;
      case "deals":       return <DealsPage {...sp}/>;
      case "eoi":         return <EOIPage {...sp}/>;
      case "commissions": return <CommissionsPage {...sp}/>;
      case "projects":    return <ProjectsPage {...sp}/>;
      case "reports":     return <ReportsPage {...sp}/>;
      case "team":        return <TeamPage {...sp}/>;
      case "users":       return <UsersPage {...sp}/>;
      case "brokers":     return <BrokersPage {...sp}/>;
      case "archive":     return <ArchivePage {...sp}/>;
      case "attendance":  return <AttendancePage {...sp}/>;
      // Legacy page ids (salaries / offsiteRequests / companyOffDays) were once
      // standalone sidebar entries and are now tabs inside AttendancePage. Kept
      // as deep-link migrations for users whose crm_page still points at one.
      // SURFACE_ALIASES resolves all three to Attendance server-side and the
      // envelope carries them, so they no longer let an HR user through a door
      // the Attendance entry itself closes to them.
      case "salaries":        return <AttendancePage {...sp} initTab="salaries"/>;
      case "offsiteRequests": return <AttendancePage {...sp} initTab="offsiteRequests"/>;
      case "companyOffDays":  return <AttendancePage {...sp} initTab="companyOffDays"/>;
      case "settings":    return <SettingsPage {...sp} users={users}/>;
      case "assets":      return <AssetTrackerPage {...sp} users={users} initialAssetCode={deepLinkAssetCode} onInitialConsumed={function(){ setDeepLinkAssetCode(null); }}/>;
      case "meetings":    return <MeetingsPage {...sp}/>;
      case "diagnostics": return <DiagnosticsPage {...sp}/>;
      default: return <DashboardPage {...sp}/>;
    }
  };

  // TEMPORARY — see ShakeDiagnostic. Mounted only for ?diagShake=1 AND an admin
  // caller, so every normal session renders nothing and runs no observers.
  var shakeDiagOn = (function(){
    try { return new URLSearchParams(window.location.search).get("diagShake") === "1" && currentUser.role === "admin"; }
    catch (e) { return false; }
  })();

  // The phase-2 flag reaches the four header bells through context rather than
  // through Header's prop list — they are three components deep and none of the
  // layers between them has any business carrying a boot flag.
  return <BootPhase2Context.Provider value={bootPhase2}>
  <div className="crm-app" {...lockedShellProps(bioLocked)} style={Object.assign({ display:"flex", minHeight:"100vh", background:C.bg, fontFamily:"'Cairo','Segoe UI',Tahoma,sans-serif", direction:t.dir }, lockedShellProps(bioLocked).style)}>
    {/* Opaque, full-viewport, above everything — it must HIDE the tree behind
        it, not merely dim it. That is the whole of what the early return used
        to guarantee visually. */}
    {bootOverlay}
    {shakeDiagOn && <ShakeDiagnostic/>}
    {/* Global base styles + mobile safety-net. Desktop behaviour is unchanged
        — every mobile rule lives inside @media (max-width: 768px). The goal
        of the mobile block is a reliable layer-of-last-resort: no page can
        horizontally scroll, every modal becomes a full-screen sheet, every
        button has a 44 px tap target, inputs use 16 px font so iOS doesn't
        auto-zoom on focus, and wide tables/cards are clamped to the
        viewport width. */}
    <style>{""
+ "* { box-sizing: border-box; margin: 0; padding: 0; }"
+ "::-webkit-scrollbar { width: 4px; height: 4px; }"
+ "::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 3px; }"
+ "input::placeholder, textarea::placeholder { color: #94A3B8; }"
+ "@keyframes spin { to { transform: rotate(360deg); } }"
+ "html, body, #root, .crm-app { overflow-x: hidden; width: 100%; max-width: 100%; }"
+ ".crm-safe-top { padding-top: env(safe-area-inset-top, 0px); }"
+ "@media (max-width: 768px) {"
+   "html, body, #root, .crm-app { max-width: 100%; overflow-x: hidden; }"
+   "/* Notification bell dropdowns: anchor to the viewport (centered card) instead of right:0 against a mid-header bell, which clips off-screen on phones. */"
+   ".crm-notif-panel { position: fixed !important; top: calc(env(safe-area-inset-top, 0px) + 60px) !important; left: 50% !important; right: auto !important; transform: translateX(-50%) !important; width: calc(100vw - 24px) !important; max-width: 420px !important; max-height: calc(100vh - env(safe-area-inset-top, 0px) - 76px) !important; overflow-y: auto !important; }"
+   "/* Sidebar drawer nav items: trim the global 40px button floor a touch so the list fits without scrolling on shorter (Android) viewports; still a usable ~36px tap target. */"
+   ".crm-nav-item { min-height: 36px !important; margin-bottom: 1px !important; }"
+   ".crm-app, .crm-app *, .crm-app *::before, .crm-app *::after { box-sizing: border-box; }"
+   "/* Prevent iOS auto-zoom on focus and keep body text readable. */"
+   ".crm-app input, .crm-app select, .crm-app textarea { font-size: 16px !important; }"
+   "/* Touch targets: buttons need at least a 40 px tap area on phones. Icon buttons and filter chips opt out with .crm-btn-xs. */"
+   ".crm-app button:not(.crm-btn-xs) { min-height: 40px; }"
+   "/* SearchableSelect's panel is portaled to <body>, so it sits OUTSIDE .crm-app and misses the two rules above. Re-apply them by class: 16px stops iOS auto-zooming when the search box focuses, and the 40px floor keeps the multi-select Select all / Clear buttons tappable. */"
+   ".crm-ss-panel input { font-size: 16px !important; }"
+   ".crm-ss-panel button:not(.crm-btn-xs) { min-height: 40px; }"
+   "/* Modals become full-screen sheets on mobile so no content is clipped. */"
+   ".crm-modal { padding: 0 !important; align-items: stretch !important; }"
+   ".crm-modal > .crm-modal-inner { width: 100% !important; max-width: 100% !important; min-height: 100vh !important; max-height: 100vh !important; border-radius: 0 !important; padding: 16px !important; padding-top: calc(16px + env(safe-area-inset-top, 0px)) !important; box-sizing: border-box !important; overflow-y: auto !important; overflow-x: hidden !important; -webkit-overflow-scrolling: touch; }"
+   "/* Dashboard-specific safety net (admin + sales). */"
+   ".crm-dash { width: 100% !important; max-width: 100% !important; overflow-x: hidden !important; padding-left: 12px !important; padding-right: 12px !important; }"
+   ".crm-dash .crm-dash-card { width: 100% !important; max-width: 100% !important; min-width: 0 !important; padding: 14px !important; box-sizing: border-box !important; }"
+   ".crm-dash .crm-dash-card > * { max-width: 100%; }"
+   ".crm-dash .crm-dash-scroll { overflow-x: auto !important; overflow-y: auto !important; max-width: 100% !important; width: 100% !important; -webkit-overflow-scrolling: touch; }"
+   ".crm-dash .crm-dash-header { flex-direction: column !important; align-items: stretch !important; width: 100% !important; }"
+   ".crm-dash .crm-dash-header > * { width: 100% !important; min-width: 0 !important; }"
+   ".crm-dash .crm-dash-filters { overflow-x: visible !important; flex-wrap: wrap !important; gap: 6px !important; }"
+   ".crm-dash .crm-dash-filters > * { flex: 1 1 auto !important; min-width: 0 !important; }"
+   "/* Quarter wrapper opts out of the flex-grow rule above so the inner dropdown's right:0 anchor sits against the button's right edge, not the row-slot edge. Higher specificity (3 classes vs 2+universal) beats the previous rule. */"
+   ".crm-dash .crm-dash-filters > .crm-dash-quarter-wrap { flex: 0 0 auto !important; }"
+   ".crm-dash .crm-dash-kpi { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 8px !important; }"
+   ".crm-dash .crm-dash-row { grid-template-columns: minmax(0, 1fr) !important; gap: 10px !important; }"
+   "/* Long Arabic / English text should wrap instead of stretching a parent. */"
+   ".crm-dash h1, .crm-dash h2, .crm-dash h3 { max-width: 100%; overflow-wrap: anywhere; }"
+   "/* Leads / Daily Request phone toolbar (MobileFilterChrome): compact wrapping status chips, a 3x2 select grid, small toggle pills, a header-row ＋ menu. */"
+   ".crm-m-chiprow > button { min-height: 0 !important; height: auto !important; padding: 4px 8px !important; font-size: 12px !important; line-height: 1.25 !important; border-radius: 14px !important; white-space: nowrap !important; display: inline-flex !important; align-items: center !important; justify-content: center !important; overflow: visible !important; }"
+   ".crm-m-chiprow > span { font-size: 12px !important; white-space: nowrap; }"
+   "[data-mf] > div, [data-mf] > div > div { width: 100% !important; max-width: 100% !important; min-width: 0 !important; }"
+   "[data-mf] button { min-height: 0 !important; height: 34px !important; padding: 0 15px !important; font-size: 12px !important; position: relative !important; justify-content: center !important; text-align: center !important; }"
+   "[data-mf] button > span:first-child { max-width: 100%; }"
+   "[data-mf] button > span:last-child { position: absolute !important; right: 5px; font-size: 10px; top: 50%; transform: translateY(-50%); }"
+   "[data-msort] > div, [data-msort] > div > div { width: auto !important; }"
+   "[data-msort] button { border-radius: 17px !important; padding: 0 26px 0 14px !important; white-space: nowrap; }"
+   "[data-mt] > button { min-height: 0 !important; height: 34px !important; padding: 0 12px !important; font-size: 12px !important; border-radius: 17px !important; white-space: nowrap; justify-content: center; }"
+   "[data-mt][data-on=\"1\"] > button { background: #E8A838 !important; border-color: #E8A838 !important; color: #fff !important; }"
+   "[data-m-active] button, [data-m-actions] > button { min-height: 0 !important; }"
+   "/* Deals phone period row (owner 2026-10-30): the year is a native <select> drawn as a SegmentedControl cell. The two phone rules above (16px on every select, the 40px button floor) would clip \"2026\" inside a 48px cell and draw the native box, so the segment variant opts out here: 12px, transparent, borderless, filling its cell; the ▾ is the cell's own glyph. */"
+   "[data-seg-select] select { font-size: 12px !important; -webkit-appearance: none !important; appearance: none !important; background: transparent !important; border: none !important; box-shadow: none !important; min-height: 0 !important; height: 100% !important; padding: 0 12px 0 2px !important; margin: 0 !important; color: #64748B !important; font-weight: 600 !important; text-align: center !important; text-align-last: center !important; border-radius: 9px !important; }"
+   "[data-seg-select] { align-self: stretch; }"
+   "/* Floating ＋ (MobileFab) stacks 12px above the floating search button; both respect the safe area on a phone. The FAB is hidden under any modal / dropdown / drawer overlay. */"
+   ".crm-qps-btn { bottom: calc(24px + env(safe-area-inset-bottom, 0px)) !important; right: calc(24px + env(safe-area-inset-right, 0px)) !important; }"
+   "body:has([data-overlay-above]) [data-m-fab], body:has([data-m-drawer]) [data-m-fab] { display: none !important; }"
+   "/* Select mode (Bulk Reassign): the sticky bar owns the bottom edge — the floating search button would sit on its Cancel. */"
+   "body:has([data-m-selbar]) .crm-qps-btn { display: none !important; }"
+   "@keyframes crm-fab-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }"
+   ".crm-m-stats { flex-wrap: nowrap !important; gap: 6px !important; margin-bottom: 8px !important; }"
+   ".crm-m-stats > div { min-width: 0 !important; padding: 6px 10px !important; gap: 8px !important; border-radius: 10px !important; }"
+   ".crm-m-stats > div > div:first-child { display: none !important; }"
+   ".crm-m-stats > div > div > div:first-child { font-size: 10px !important; margin-bottom: 1px !important; }"
+   ".crm-m-stats > div > div > div:last-child { font-size: 16px !important; }"
+ "}"
+ "/* iOS: overflow-x:hidden makes overflow-y compute to auto, so body/#root/.crm-app were three extra vertical scroll containers between the touch and the window. A swipe could latch onto one (fractional overflow, no chaining) and rubber-band by a hair instead of scrolling the page. clip clips identically but creates no scroll container. html keeps hidden: it propagates to the viewport, which is the real scroller. iOS < 16 has no clip and keeps hidden above. verify-scroll-ancestors.js runs this block. */"
+ "@supports (overflow: clip) { body, #root, .crm-app { overflow-x: clip; } }"
}</style>
    <Sidebar active={currentPage} setActive={setPage} t={t} cu={currentUser} onLogout={handleLogout} isMobile={isMobile} open={sidebarOpen} onOpen={function(){setSidebarOpen(true);}} onClose={function(){setSidebarOpen(false);}} leads={scopedLeads} leadsTotal={leadsTotal} sidebarLeadsTotal={sidebarLeadsTotal} access={access}/>
    <div style={{ flex:1, marginRight:!isMobile&&t.dir==="rtl"?240:0, marginLeft:!isMobile&&t.dir==="ltr"?240:0, minHeight:"100vh", display:"flex", flexDirection:"column", minWidth:0 }}>
      {currentUser.role!=="hr" && currentUser.role!=="office_boy" && !roleHidesCustomerDataFE(currentUser) && <QuickPhoneSearch isMobile={isMobile} leads={scopedLeads} dailyReqs={scopedDailyReqs} token={token} t={t} onSelect={function(lead){setPage("leads");setInitSelected(lead);}} onSelectDR={function(req){setPage("dailyReq");setInitSelected(req);}}/>}
      {!isOnline&&<div style={{ background:"#FEF3C7", color:"#B45309", padding:"8px 16px", fontSize:12, fontWeight:600, textAlign:"center", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
        ⚠️ You are offline — data will not be saved until connection is restored
      </div>}
      {wsAuthFailed&&<div style={{ background:"#FEE2E2", color:"#B91C1C", padding:"8px 16px", fontSize:12, fontWeight:600, textAlign:"center", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
        ⚠️ Live updates stopped — your session was refused by the server. Log out and log in again.
      </div>}
            <Header title={titles[currentPage]||""} page={currentPage} t={t} leads={scopedLeads} token={token} cbBust={cbBust} lang={lang} setLang={function(l){setLang(l);try{localStorage.setItem("crm_lang",l);}catch(e){}}} showNotif={showNotif} setShowNotif={setShowNotif} search={search} setSearch={setSearch} isMobile={isMobile} onMenu={function(){setSidebarOpen(true);}} onLeadClick={function(l){nav("leads",l);}} onTxnClick={function(pg,row){nav(pg,row);}} onDRClick={function(){setPage("dailyReq");}} onDRItemClick={function(r){nav("dailyReq",r);}} onPendingApprovalClick={function(pg,lead){nav(pg,lead);}} onRotNotifClick={function(lead){nav("leads",lead);}} navigateToCommission={navigateToCommission} showPendingApproval={showPendingApproval} setShowPendingApproval={setShowPendingApproval} cu={currentUser} isAdmin={isAdmin} showRotNotif={showRotNotif} setShowRotNotif={setShowRotNotif} showPendingLoc={showPendingLoc} setShowPendingLoc={setShowPendingLoc} pendingLocRev={pendingLocRev} onPendingLocClick={function(r){nav("dailyReq",r);}} rotNotifs={rotNotifs} setRotNotifs={setRotNotifs} rotHasMore={rotNotifPage < rotNotifPages} onLoadOlderRot={loadOlderRotNotifs} unseenRot={rotNotifs.filter(function(n){return !n.seen;}).length} onRotNotifSeen={function(){apiFetch("/api/notifications/mark-seen","PUT",{type:"rotation"},token).then(function(){loadNotifications(token);}).catch(function(){});}} onRotClearAll={function(){apiFetch("/api/notifications/mark-seen","PUT",{type:"rotation"},token).then(function(){loadNotifications(token);}).catch(function(){});}} dailyRequests={scopedDailyReqs} myTeamUsers={myTeamUsers} access={access} csrfToken={csrfToken} showOffSite={showOffSite} setShowOffSite={setShowOffSite} onOffSiteClick={function(){setPage("offsiteRequests");}}/>
      {/* key={authKey}: a USER SWITCH remounts every page component and takes
          its internal state with it (dealAllData, eoiAllData, filters,
          selections). A loading toggle does not — which is the entire point of
          moving the loader to an overlay. */}
      <div key={authKey} style={{ flex:1 }}>{renderPage()}</div>
    </div>
    {pushBanner && <div
      onClick={openPushBanner}
      onTouchStart={function(e){ try { pushTouchStartY.current = e.touches[0].clientY; } catch(_){ pushTouchStartY.current = null; } }}
      onTouchMove={function(e){
        if (pushTouchStartY.current == null) return;
        try {
          var dy = e.touches[0].clientY - pushTouchStartY.current; // negative = swiping up
          if (dy < -40){ pushTouchStartY.current = null; dismissPushBanner(); }
        } catch(_){}
      }}
      onTouchEnd={function(){ pushTouchStartY.current = null; }}
      style={{
        position:"fixed", top:0, left:0, right:0, zIndex:1000,
        padding:"calc(env(safe-area-inset-top, 0px) + 10px) 12px 0",
        transform: bannerShown ? "translateY(0)" : "translateY(-120%)",
        transition:"transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)",
        willChange:"transform",
        pointerEvents: bannerShown ? "auto" : "none",
        cursor: pushBanner.leadId ? "pointer" : "default"
      }}>
      <div style={{
        maxWidth:560, margin:"0 auto",
        background:"#fff", color:C.text,
        border:"1px solid "+C.border, borderRadius:14,
        boxShadow:"0 10px 30px rgba(0,0,0,0.18)",
        padding:"12px 16px 14px"
      }}>
        {/* grab handle — hints swipe-up-to-dismiss */}
        <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:"0 auto 10px" }}/>
        <div style={{ fontSize:14, fontWeight:700, marginBottom: pushBanner.body ? 4 : 0 }}>{pushBanner.title || "Notification"}</div>
        {pushBanner.body && <div style={{ fontSize:12.5, color:C.textLight, lineHeight:1.45 }}>{pushBanner.body}</div>}
      </div>
    </div>}
  </div>
  {bioLocked && <BiometricLockScreen onUnlock={attemptBioUnlock} busy={bioPrompting} failed={bioFailed}/>}
  </BootPhase2Context.Provider>;
}
